// Code generated by internal/core/api. DO NOT EDIT.
// Source of truth is the Go route registry (CLAUDE.md §7); regenerate, don't hand-edit.

export interface AIAccuracyByTypeDTO {
  rows: AIAccuracyRowDTO[];
  overall: AIAccuracyRowDTO;
}

export interface AIAccuracyQuery {
  capability?: string;
  companyId?: number;
}

export interface AIAccuracyRowDTO {
  recommendationType: string;
  capabilityCode: string;
  samples: number;
  correct: number;
  accuracy: string;
  meanAbsolutePercentageError?: string;
  coverage: AICoverageDTO;
}

export interface AIAgentDTO {
  id: number;
  code: string;
  labelKey: string;
  descriptionKey?: string;
  module: string;
  capabilityCode?: string;
  permissionCode: string;
  defaultCron: string;
  isOrphaned: boolean;
  isEnabled: boolean;
  recipientPositionIds: number[];
  rowVersion: number;
}

export interface AIAgentRunDTO {
  id: number;
  agentCode: string;
  companyId: number;
  businessDate: string;
  status: 'running' | 'complete' | 'degraded' | 'failed';
  errorMessage?: string;
  fallbackApplied?: string;
  model?: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  startedAt: string;
  finishedAt?: string | null;
  durationMs: number;
  briefingId?: number | null;
}

export interface AIAnomalySweepResult {
  auditRowsScanned: number;
  detected: number;
  alreadyKnown: number;
  narrated: number;
}

export interface AIBacktestHistoryPointDTO {
  at: string;
  value: string;
}

export interface AIBacktestPointDTO {
  id: number;
  runId: number;
  horizon: number;
  asOf: string;
  target: string;
  predicted: string;
  actual: string;
  absoluteError: string;
  absolutePercentageError?: string;
}

export interface AIBacktestResultDTO {
  mape?: string;
  mae: string;
  bias: string;
  n: number;
  mapeN: number;
}

export interface AIBacktestRunDTO {
  id: number;
  companyId: number;
  metricCode?: string;
  subjectModule?: string;
  subjectEntity?: string;
  subjectId?: number | null;
  subjectLabel?: string;
  method: 'seasonal_naive' | 'ewma' | 'drift' | 'mean';
  params: Record<string, string>;
  seed: number;
  trainFrom: string;
  trainTo: string;
  scoreFrom: string;
  scoreTo: string;
  horizons: number[];
  results: Record<string, AIBacktestResultDTO>;
  pointCount: number;
  status: string;
  errorMessage?: string;
  startedAt: string;
  finishedAt?: string | null;
  createdAt: string;
}

export interface AIBriefingCitationDTO {
  label: string;
  route?: string;
}

export interface AIBriefingDTO {
  id: number;
  runId: number;
  agentCode: string;
  companyId: number;
  businessDate: string;
  title: string;
  summary?: string;
  priority: 'info' | 'attention' | 'urgent';
  sectionCount: number;
  withheldSectionCount: number;
  readAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface AIBriefingDetailDTO {
  id: number;
  runId: number;
  agentCode: string;
  companyId: number;
  businessDate: string;
  title: string;
  summary?: string;
  priority: 'info' | 'attention' | 'urgent';
  sectionCount: number;
  withheldSectionCount: number;
  readAt?: string | null;
  createdAt: string;
  rowVersion: number;
  sections: AIBriefingSectionDTO[];
  run: AIAgentRunDTO;
}

export interface AIBriefingSectionDTO {
  sectionKey: string;
  sortOrder: number;
  headingKey?: string;
  heading?: string;
  figures: Record<string, unknown>;
  narrative?: string;
  citations: AIBriefingCitationDTO[];
  severity: 'info' | 'attention' | 'urgent';
  permissionCode?: string;
}

export interface AICapabilityDTO {
  id: number;
  code: string;
  module: string;
  labelKey: string;
  descriptionKey?: string;
  kind: 'advice' | 'draft' | 'action';
  permissionCode: string;
  authorityDocType?: string;
  maxAutonomy: 'assistive' | 'supervised' | 'delegated';
  unavailableFallback: 'figures_only' | 'skip' | 'queue';
  measurementHorizonDays: number;
  isOrphaned: boolean;
  autonomy: 'assistive' | 'supervised' | 'delegated';
  confidenceThreshold: string;
  tolerance: AIToleranceDTO;
  isEnabled: boolean;
  effectiveAutonomy: 'assistive' | 'supervised' | 'delegated';
  companyMaxAutonomy: 'assistive' | 'supervised' | 'delegated';
  delegationBlockedReason?: string;
  delegatedAt?: string | null;
  delegatedBy?: number | null;
  delegatedEvidenceN?: number | null;
  delegatedEvidenceAcc?: string;
  rowVersion: number;
}

export interface AICapabilityDetailDTO {
  id: number;
  code: string;
  module: string;
  labelKey: string;
  descriptionKey?: string;
  kind: 'advice' | 'draft' | 'action';
  permissionCode: string;
  authorityDocType?: string;
  maxAutonomy: 'assistive' | 'supervised' | 'delegated';
  unavailableFallback: 'figures_only' | 'skip' | 'queue';
  measurementHorizonDays: number;
  isOrphaned: boolean;
  autonomy: 'assistive' | 'supervised' | 'delegated';
  confidenceThreshold: string;
  tolerance: AIToleranceDTO;
  isEnabled: boolean;
  effectiveAutonomy: 'assistive' | 'supervised' | 'delegated';
  companyMaxAutonomy: 'assistive' | 'supervised' | 'delegated';
  delegationBlockedReason?: string;
  delegatedAt?: string | null;
  delegatedBy?: number | null;
  delegatedEvidenceN?: number | null;
  delegatedEvidenceAcc?: string;
  rowVersion: number;
  evidence: AIAccuracyRowDTO[];
  evidenceSamples: number;
  evidenceAccuracy: string;
  requiredSamples: number;
  requiredAccuracy: string;
}

export interface AIConfigAnomalyCoverageDTO {
  entitiesInStream: AIConfigAnomalyCoverageEntityDTO[];
  directionMappedEntities: string[];
  rulesRegistered: number;
}

export interface AIConfigAnomalyCoverageEntityDTO {
  entity: string;
  changes: number;
  hasDirectionMapping: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface AIConfigAnomalyDTO {
  id: number;
  ruleCode: string;
  companyId: number;
  entity: string;
  entityId?: string;
  actorId?: number | null;
  actorType: string;
  windowFrom: string;
  windowTo: string;
  evidenceAuditIds: number[];
  occurrenceCount: number;
  figures: Record<string, unknown>;
  narrative?: string;
  impactedCount?: number | null;
  impactNote?: string;
  severity: 'info' | 'attention' | 'urgent';
  status: 'open' | 'acknowledged' | 'dismissed';
  acknowledgedAt?: string | null;
  acknowledgedBy?: number | null;
  dispositionReasonCode?: string;
  dispositionNote?: string;
  attentionItemId?: number | null;
  notificationId?: number | null;
  detectedAt: string;
  rowVersion: number;
}

export interface AIConfigAnomalyRuleDTO {
  code: string;
  labelKey: string;
  descriptionKey?: string;
  pattern: 'churn' | 'flip' | 'off_hours' | 'first_time' | 'control_weaken' | 'pre_document' | 'bulk';
  defaultSeverity: 'info' | 'attention' | 'urgent';
  isOrphaned: boolean;
  isEnabled: boolean;
  severity?: 'info' | 'attention' | 'urgent';
  effectiveSeverity: 'info' | 'attention' | 'urgent';
  params: Record<string, unknown>;
  excludedEntities: string[];
  rowVersion: number;
}

export interface AICoverageDTO {
  issued: number;
  realised: number;
  notRealised: number;
  notTaken: number;
  unmeasurable: number;
  unmeasured: number;
  measuredRatio: string;
  notTakenRatio: string;
  unmeasurableRatio: string;
}

export interface AIRecommendationDTO {
  id: number;
  capabilityCode: string;
  recommendationType: string;
  companyId: number;
  agentRunId?: number | null;
  conversationId?: number | null;
  messageId?: number | null;
  toolPreviewId?: number | null;
  subjectModule?: string;
  subjectEntity?: string;
  subjectId?: number | null;
  subjectDocNo?: string;
  figures: string;
  narration?: string;
  confidence?: string;
  amount?: string;
  currency?: string;
  autonomyAtIssue: 'assistive' | 'supervised' | 'delegated';
  withinTolerance: boolean;
  measurementHorizonDays: number;
  disposition: 'pending' | 'accepted' | 'rejected' | 'expired' | 'auto_confirmed' | 'routed';
  disposedAt?: string | null;
  disposedBy?: number | null;
  dispositionNote?: string;
  approvalTaskId?: number | null;
  attentionItemId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface AIRecommendationDetailDTO {
  id: number;
  capabilityCode: string;
  recommendationType: string;
  companyId: number;
  agentRunId?: number | null;
  conversationId?: number | null;
  messageId?: number | null;
  toolPreviewId?: number | null;
  subjectModule?: string;
  subjectEntity?: string;
  subjectId?: number | null;
  subjectDocNo?: string;
  figures: string;
  narration?: string;
  confidence?: string;
  amount?: string;
  currency?: string;
  autonomyAtIssue: 'assistive' | 'supervised' | 'delegated';
  withinTolerance: boolean;
  measurementHorizonDays: number;
  disposition: 'pending' | 'accepted' | 'rejected' | 'expired' | 'auto_confirmed' | 'routed';
  disposedAt?: string | null;
  disposedBy?: number | null;
  dispositionNote?: string;
  approvalTaskId?: number | null;
  attentionItemId?: number | null;
  createdAt: string;
  rowVersion: number;
  proposal: string;
  preview?: PreviewDTO | null;
  outcome?: AIRecommendationOutcomeDTO | null;
}

export interface AIRecommendationOutcomeDTO {
  id: number;
  recommendationId: number;
  recommendationType: string;
  measuredAt: string;
  measuredOn: string;
  result: 'realised' | 'not_realised' | 'not_taken' | 'unmeasurable';
  predictedValue?: string;
  actualValue?: string;
  absolutePercentageError?: string;
  wasCorrect?: boolean | null;
  note?: string;
}

export interface AISettingsDTO {
  companyId: number;
  aiEnabled: boolean;
  maxAutonomy: 'assistive' | 'supervised' | 'delegated';
  instanceEnabled: boolean;
  delegationMinSamples: number;
  delegationMinAccuracy: string;
  narrateWhenProviderUnavailable: boolean;
  redactionAcknowledgedAt?: string | null;
  rowVersion: number;
}

export interface AISettingsQuery {
  companyId: number;
}

export interface AIToleranceDTO {
  pct?: string;
  abs?: string;
  defaultPct?: string;
  defaultAbs?: string;
}

export interface APInvoiceSignalDTO {
  id: number;
  invoiceId: number;
  companyId: number;
  vendorId: number;
  signalCode: string;
  severity: 'info' | 'warn' | 'block';
  detectedAt: string;
  detail?: Record<string, unknown>;
  relatedInvoiceId?: number | null;
  status: 'open' | 'cleared' | 'confirmed';
  queueItemId?: number | null;
  disposition?: string;
  reasonCode?: string;
  note?: string;
  clearedAt?: string | null;
  clearedBy?: number | null;
}

export interface APPaymentExceptionDTO {
  id: number;
  paymentId: number;
  companyId: number;
  kind: 'returned' | 'recalled' | 'rejected' | 'stopped' | 'restored';
  raisedOn: string;
  amount: string;
  bankReasonCode?: string;
  reasonCode: string;
  note?: string;
  reference?: string;
  status: 'open' | 'resolved' | 'cancelled';
  journalEntryId?: number | null;
  replacementPaymentId?: number | null;
  resolvedAt?: string | null;
  resolvedBy?: number | null;
  queueItemId?: number | null;
}

export interface ASNDTO {
  id: number;
  poId: number;
  asnNo?: string;
  carrier?: string;
  trackingNo?: string;
  shippedDate?: string | null;
  expectedDate?: string | null;
  note?: string;
  status: string;
  createdAt: string;
  lines?: ASNLineDTO[];
}

export interface ASNLineDTO {
  poLineId: number;
  quantity: string;
}

export interface ATPLineDTO {
  itemId: number;
  warehouseId: number;
  onHand: string;
  reserved: string;
  inbound: string;
  available: string;
}

export interface ATPRequestQuery {
  warehouseId: number;
  itemIds: string;
  asOf?: string;
}

export interface AbandonCloseRunRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface AbandonPMSNightAuditRunRequest {
  reason: string;
}

export interface AbandonPOSSaleRequest {
  reasonCode?: string;
  note?: string;
}

export interface AbolishRequest {
  effectiveTo: string;
  reasonCode: string;
}

export interface AbsorbLandedCostRequest {
  date?: string;
  reverse?: boolean;
  reason?: string;
}

export interface AcceptAssertionRequest {
  reasonCode: string;
  note?: string;
}

export interface AcceptCloseTaskRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface AcceptEAMPermitReturnRequest {
  note?: string;
}

export interface AcceptEliminationRequest {
  note?: string;
  rowVersion: number;
}

export interface AcceptInviteRequest {
  tenantSlug: string;
  token: string;
  name?: string | null;
  password: string;
}

export interface AcceptProjectVariationRequest {
  customerReference: string;
  customerAttachmentId?: number | null;
  acceptedOn?: string;
  baselineName?: string;
}

export interface AcceptRequest {
  effectiveFrom: string;
  note: string;
  force?: boolean;
  forceReason?: string;
}

export interface Account {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  requiresCostCenter: boolean;
  requiresProfitCenter: boolean;
  requiresBranch: boolean;
  requiresDepartment: boolean;
  requiresProject: boolean;
  requiresDivision: boolean;
  companyId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentId?: number | null;
  isPostable: boolean;
  currency?: string | null;
  description: string;
  sortOrder: number;
  isActive: boolean;
}

export interface AccountDimensionPolicyDTO {
  id: number;
  accountId: number;
  dimension: string;
  requirement: 'required' | 'optional' | 'forbidden';
  note?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface AccountNode {
  id: number;
  companyId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentId?: number | null;
  isPostable: boolean;
  currency?: string | null;
  isActive: boolean;
  sortOrder: number;
  depth: number;
  requiresCostCenter: boolean;
  requiresProfitCenter: boolean;
  requiresBranch: boolean;
  requiresDepartment: boolean;
  requiresProject: boolean;
  requiresDivision: boolean;
  children: AccountNode[];
}

export interface AccountTreeResponse {
  companyId: number;
  accounts: AccountNode[];
}

export interface AccountingMethodDTO {
  id: number;
  code: string;
  name: string;
  description: string;
  isSystem: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface AccrualAgingDTO {
  positionId: number;
  code: string;
  name: string;
  maxAgeDays: number;
  openCount: number;
  remaining: string;
  agedCount: number;
  agedRemaining: string;
}

export interface AccrualAgingQuery {
  companyId?: number;
  asOf?: string;
}

export interface AccrualAgingReportDTO {
  companyId: number;
  asOf: string;
  positions: AccrualAgingDTO[];
  totalRemaining: string;
}

export interface AccrualItemDTO {
  id: number;
  positionId: number;
  companyId: number;
  raisedOn: string;
  amount: string;
  matchedAmount: string;
  remaining: string;
  raisedDocType?: string;
  raisedDocId?: number | null;
  scheduledReversalOn?: string | null;
  intendedReversalOn?: string | null;
  reversalJournalEntryId?: number | null;
  matchedDocType?: string;
  matchedDocId?: number | null;
  status: 'open' | 'partially_matched' | 'matched' | 'reversed' | 'written_off';
  queueItemId?: number | null;
  reasonCode?: string;
  description?: string;
  rowVersion: number;
}

export interface AccrualPositionDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  kind: 'goods_received' | 'services_received' | 'expense_accrued' | 'revenue_unbilled' | 'payroll_accrued' | 'other';
  accountId: number;
  counterAccountId?: number | null;
  ownerPositionId?: number | null;
  reversePolicy: 'on_match' | 'on_date' | 'manual';
  maxAgeDays: number;
  blocksClose: boolean;
  isSystem: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface AccrualRunResult {
  periodYear: number;
  periodMonth: number;
  processedCount: number;
  skippedCount: number;
  generatedAt: string;
}

export interface AccrueRebateRequest {
  asOf?: string;
  note?: string;
}

export interface AckResponse {
  ok: boolean;
}

export interface AcknowledgeAnomalyRequest {
  note: string;
}

export interface AcknowledgeConfigAnomalyRequest {
  reasonCode: string;
  note?: string;
}

export interface ActRequest {
  comment?: string;
}

export interface ActionDTO {
  code: string;
  label: string;
}

export interface ActionDispositionRequest {
  reasonCode?: string;
  toWarehouseId?: number | null;
  note?: string;
}

export interface ActionMRPSuggestionResponse {
  productionOrder?: ProductionOrderDTO | null;
  suggestion: MRPSuggestionDTO;
}

export interface ActionsResponse {
  actions: ActionDTO[];
}

export interface ActivateAgreementRequest {
  note?: string;
}

export interface ActivateBusinessUnitRequest {
  rowVersion: number;
}

export interface ActivateCompanyRequest {
}

export interface ActivateContractRequest {
  note?: string;
}

export interface ActivateMatrixRequest {
  effectiveFrom: string;
  note?: string;
}

export interface ActivatePMSBoardPlanRequest {
  rowVersion: number;
}

export interface ActivatePMSRatePlanRequest {
  rowVersion: number;
  note?: string;
}

export interface ActivatePackRequest {
  packCode: string;
  version?: string;
}

export interface ActivatePlanRequest {
  rowVersion: number;
}

export interface ActivatePrecedenceRequest {
  rowVersion: number;
  effectiveFrom: string;
  note?: string;
}

export interface ActivateRequest {
  country: string;
  effectiveFrom: string;
}

export interface ActivateServiceContractRequest {
  rowVersion: number;
}

export interface ActivateTenantPackRequest {
  country: string;
  effectiveFrom: string;
}

export interface ActivationDTO {
  id: number;
  country: string;
  packId: string;
  packVersion: number;
  contentHash: string;
  status: string;
  effectiveFrom: string;
  activatedAt: string;
  pendingPackVersion?: number | null;
  pendingSeenAt?: string | null;
  sandboxRanAt?: string | null;
  acceptedEffectiveFrom?: string | null;
  lastError?: string;
}

export interface ActivityDTO {
  id: number;
  leadId?: number | null;
  opportunityId?: number | null;
  caseId?: number | null;
  kind: string;
  subject: string;
  body: string;
  isPublic: boolean;
  authorType: string;
  authorUserId?: number | null;
  authorName?: string;
  dueAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
}

export interface AddAssetBookRequest {
  bookId: number;
  method?: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths?: number;
  salvageValue?: string;
  cost?: string;
  inServiceDate?: string;
  assetAccountId?: number | null;
  accumulatedDepreciationAccountId?: number | null;
  depreciationExpenseAccountId?: number | null;
  impairmentExpenseAccountId?: number | null;
  impairmentReversalAccountId?: number | null;
  gainLossAccountId?: number | null;
}

export interface AddCAPAActionRequest {
  description: string;
  ownerUserId?: number | null;
  dueOn?: string;
}

export interface AddClearanceItemRequest {
  ownerDepartmentId?: number | null;
  ownerUserId?: number | null;
  category: string;
  label: string;
  isMandatory?: boolean | null;
  assetId?: number | null;
  userId?: number | null;
  recoverableAmount?: string;
  note?: string;
}

export interface AddCloseTaskRequest {
  code: string;
  name: string;
  seq?: number;
  taskKind?: string;
  readinessKey?: string;
  actionKey?: string;
  obligationKind?: string;
  module?: string;
  ownerPositionId?: number | null;
  assigneeUserId?: number | null;
  dueOffsetDays?: number;
  isMandatory?: boolean | null;
  requiresEvidence?: boolean;
  dependsOn?: string[];
  note?: string;
}

export interface AddDependencyRequest {
  dependsOnTaskId: number;
}

export interface AddDivisionCountryRequest {
  countryCode: string;
  effectiveFrom?: string;
}

export interface AddLaborRequest {
  technicianUserId: number;
  workDate: string;
  hours: string;
  rate?: string;
  billable?: boolean | null;
  description?: string;
}

export interface AddLedgerSetMemberRequest {
  ledgerId: number;
  effectiveFrom: string;
}

export interface AddMarketingSegmentMemberRequest {
  partyId: number;
  excluded?: boolean;
  exclusionReason?: string;
}

export interface AddMemberRequest {
  companyId: number;
  ownershipPct?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  method?: 'full' | 'proportional' | 'equity' | 'excluded';
  note?: string;
  investmentAccountId?: number | null;
}

export interface AddPOSCheckLineRequest {
  itemId?: number;
  menuItemId?: number | null;
  description?: string;
  uom?: string;
  quantity: string;
  revenueCenterId?: number | null;
  seatNo?: number | null;
  courseNo?: number | null;
  stationId?: number | null;
  note?: string;
}

export interface AddPOSSaleLineRequest {
  itemId?: number;
  menuItemId?: number | null;
  quantity: string;
  uom?: string;
  unitPrice?: string | null;
  priceOverride?: POSOverrideRequest | null;
  description?: string;
  revenueCenterId?: number | null;
  courseNo?: number | null;
  lotId?: number | null;
  modifiers?: POSSaleLineModifierInput[];
}

export interface AddPOSTenderRequest {
  tenderTypeId: number;
  amount?: string;
  amountTendered?: string;
  tipAmount?: string;
  cardApprovalCode?: string;
  cardLast4?: string;
  cardScheme?: string;
  providerRef?: string;
  instrumentId?: number | null;
  customerId?: number | null;
  folioRef?: string;
  roomRef?: string;
  override?: POSOverrideRequest | null;
}

export interface AddPartRequest {
  itemId: number;
  warehouseId: number;
  quantity: string;
  billable?: boolean | null;
}

export interface AddReferenceRequest {
  refereeName: string;
  refereeOrganisation?: string;
  refereeContact: string;
  relationship?: string;
}

export interface AddReportingCurrencyRequest {
  currency: string;
  conversionBasis?: string;
  effectiveFrom?: string;
}

export interface AddServiceWorkOrderPartRequest {
  itemId: number;
  warehouseId?: number | null;
  visitId?: number | null;
  quantity: string;
  occurredAt?: string | null;
}

export interface AddSignatoryRequest {
  userId?: number | null;
  positionId?: number | null;
  personName?: string;
  panel?: string;
  maxAmount?: string;
  specimenAttachmentId?: number | null;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface AddressInput {
  country: string;
  line1?: string;
  line2?: string;
  line3?: string;
  localityName?: string;
  subdivisionName?: string;
  postalCode?: string;
  subdivisionGeoRef?: string | null;
  localityGeoRef?: string | null;
}

export interface AddressProblem {
  field: string;
  code: string;
  message: string;
}

export interface AddressResult {
  level: string;
  valid: boolean;
  missing?: string[];
  problems?: AddressProblem[];
  jurisdictionId?: number | null;
}

export interface AddressRulesDTO {
  country: string;
  validationLevel: string;
  addressFormat: Record<string, unknown>;
  postalCodePattern: string;
  postalCodeLabel: string;
  requiresGeoLocality: boolean;
  subdivisionLabel: string;
  localityLabel: string;
}

export interface AdjustPOSInstrumentRequest {
  amount: string;
  reasonCode: string;
  note?: string;
}

export interface AdjustmentLineInput {
  itemId: number;
  binId?: number | null;
  qtyDelta: string;
  unitCost?: string;
  lotNo?: string;
}

export interface AdjustmentReasonCodeDTO {
  id: number;
  code: string;
  description: string;
  requiresApproval: boolean;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface AdjustmentReasonCodeDetailDTO {
  id: number;
  code: string;
  description: string;
  requiresApproval: boolean;
  isActive: boolean;
  requiresAttachment: boolean;
  requiresRootCause: boolean;
  gainLossAccountId?: number | null;
  rowVersion: number;
}

export interface AdminAcceptInviteRequest {
  token: string;
  password: string;
}

export interface AdminCreateRequest {
  email: string;
  name: string;
  role: string;
}

export interface AdminForgotPasswordRequest {
  email: string;
}

export interface AdminLoginRequest {
  email: string;
  password: string;
  totpCode?: string;
}

export interface AdminMFAConfirmRequest {
  code: string;
}

export interface AdminMFAConfirmResponse {
  recoveryCodes: string[];
}

export interface AdminMFAEnrollResponse {
  secret: string;
  otpauthUrl: string;
}

export interface AdminRefreshRequest {
  refreshToken: string;
}

export interface AdminResetPasswordRequest {
  token: string;
  password: string;
}

export interface AdminStepUpRequest {
  code: string;
}

export interface AdminStepUpResponse {
  accessToken: string;
  expiresAt: string;
  stepUpExpiresAt: string;
}

export interface AdminUpdateRequest {
  name?: string | null;
  role?: string | null;
  status?: string | null;
}

export interface AdvancePMSNightAuditRunRequest {
  expectedStep?: string;
}

export interface AdvanceStageRequest {
  stage: string;
  note?: string;
  rowVersion: number;
}

export interface AgingBucket {
  label: string;
  fromDays: number;
  toDays: number;
  amount: string;
}

export interface AgingQuery {
  companyId: number;
  asOf?: string;
  vendorId?: number;
  currency?: string;
}

export interface AgingReport {
  companyId: number;
  asOf: string;
  currency: string;
  rows: AgingRow[];
  totals: AgingBucket[];
  grandTotal: string;
  generatedAt: string;
}

export interface AgingRow {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  currency: string;
  buckets: AgingBucket[];
  total: string;
}

export interface AlignLedgerRequest {
  ledgerId: number;
  documentType: string;
  jurisdictionId?: number | null;
  sequenceId?: number | null;
}

export interface AllocateLandedCostRequest {
  directAmounts?: LandedCostChargeDirectAmountsRequest[];
}

export interface AllocateLineInput {
  orderLineId: number;
  warehouseId: number;
  quantity: string;
}

export interface AllocateRequest {
  allocations: AllocationInput[];
}

export interface AllocateStockRequest {
  lines: AllocateLineInput[];
}

export interface AllocationDTO {
  id: number;
  invoiceId: number;
  invoiceNo?: string;
  amount: string;
  whtAmount: string;
  discountAmount: string;
  fxGainLoss: string;
}

export interface AllocationInput {
  invoiceId: number;
  amount: string;
}

export interface AllowancePMSFolioRequest {
  windowNo?: number;
  amount: string;
  reasonCode: string;
  note?: string;
  description?: string;
  businessDate?: string;
  taxCode?: string;
}

export interface AmendOrderRequest {
  rowVersion: number;
  reasonCode: string;
  note?: string;
  lines?: OrderLineInput[];
  requestedDate?: string;
  promisedDate?: string;
  permitBackorder?: boolean | null;
  backorderReasonCode?: string;
  discountOverrideReason?: string;
  creditOverrideReason?: string;
}

export interface AmendPMSReservationRequest {
  arrival?: string | null;
  departure?: string | null;
  roomTypeId?: number | null;
  ratePlanId?: number | null;
  roomsCount?: number | null;
  adults?: number | null;
  children?: number | null;
  overrideReason?: string;
  overrideNote?: string;
  rowVersion: number;
}

export interface AmendPORequest {
  expectedDate?: string;
  memo?: string | null;
  lines?: POLineInput[];
  reason: string;
  rowVersion: number;
}

export interface AnswerClarificationRequest {
  answer: string;
}

export interface ApCancelWhtRemittanceRequest {
  reason: string;
}

export interface ApCreateSupplierStatementRequest {
  companyId: number;
  vendorId: number;
  currency: string;
  statementDate: string;
  periodFrom?: string;
  statementRef?: string;
  openingBalance?: string;
  reportedBalance: string;
  notes?: string;
  lines: ApSupplierStatementLineInput[];
}

export interface ApCreateWhtRemittanceRequest {
  companyId: number;
  authorityCode: string;
  period: string;
  bankAccountId: number;
  remittanceDate: string;
  allocationIds: number[];
  reference?: string;
  memo?: string;
}

export interface ApCurrencyTotal {
  currency: string;
  total: string;
  paid: string;
  outstanding: string;
}

export interface ApDeleteQuery {
  rowVersion?: number;
}

export interface ApDuplicatePaymentDTO {
  paymentId: number;
  runId: number;
  runNo?: string;
  paymentDate: string;
  reference?: string;
  netAmount: string;
  grossAmount: string;
}

export interface ApDuplicatePaymentGroupDTO {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  currency: string;
  netAmount: string;
  reason: 'same_reference' | 'same_amount_within_window';
  exposureAmount: string;
  payments: ApDuplicatePaymentDTO[];
}

export interface ApDuplicatePaymentPreventedDTO {
  exceptionId: number;
  paymentId: number;
  runId: number;
  runNo?: string;
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  currency: string;
  kind: string;
  status: string;
  raisedOn: string;
  amount: string;
  reference?: string;
  note?: string;
}

export interface ApDuplicatePaymentsDTO {
  companyId: number;
  from: string;
  to: string;
  windowDays: number;
  suspected: ApDuplicatePaymentGroupDTO[];
  prevented: ApDuplicatePaymentPreventedDTO[];
  suspectedCount: number;
  preventedCount: number;
  generatedAt: string;
}

export interface ApDuplicatePaymentsQuery {
  companyId: number;
  from?: string;
  to?: string;
  windowDays?: number;
}

export interface ApInvoiceDTO {
  budgetWarnings?: BudgetWarningDTO[];
  id: number;
  invoiceNo?: string | null;
  vendorInvoiceNo: string;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  docKind: 'invoice' | 'credit_note' | 'debit_note';
  invoiceDate: string;
  dueDate: string;
  postingDate?: string | null;
  currency: string;
  fxRate: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  baseTotal: string;
  whtTotal: string;
  whtWithheld: string;
  amountPaid: string;
  outstanding: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'posted' | 'paid' | 'cancelled' | 'reversed';
  journalEntryId?: number | null;
  apAccountId?: number | null;
  originalInvoiceId?: number | null;
  reversedByInvoiceId?: number | null;
  memo: string;
  description: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  postedAt?: string | null;
  createdAt: string;
  rowVersion: number;
  sourceDocType?: string;
  sourceDocId?: number | null;
  poId?: number | null;
  onHold: boolean;
  holdReason?: string | null;
  heldBy?: number | null;
  heldAt?: string | null;
  dueDateOverrideReason?: string | null;
  lines?: ApInvoiceLineDTO[];
}

export interface ApInvoiceLineDTO {
  id: number;
  lineNo: number;
  description: string;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  quantity: string;
  unitPrice: string;
  lineAmount: string;
  taxCode?: string | null;
  itemGroupId?: number | null;
  taxAmount: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  projectTaskId?: number | null;
  poLineId?: number | null;
}

export interface ApLedgerQuery {
  companyId: number;
  from?: string;
  to?: string;
  openOnly?: boolean;
}

export interface ApPaymentAnomalyDTO {
  code: 'new_bank_account_cooling_off' | 'round_amount' | 'first_payment_to_vendor' | 'above_vendor_average';
  message: string;
}

export interface ApPaymentDTO {
  id: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  vendorBankAccountId?: number | null;
  bankAccountMasked?: string;
  currency: string;
  fxRate: string;
  grossAmount: string;
  whtAmount: string;
  discountAmount: string;
  netAmount: string;
  baseAmount: string;
  reference: string;
  narrative: string;
  exceptionState: 'none' | 'returned' | 'recalled' | 'rejected' | 'stopped' | 'restored';
  allocations?: AllocationDTO[];
  anomalies?: ApPaymentAnomalyDTO[];
}

export interface ApPostWhtRemittanceRequest {
  reference?: string;
}

export interface ApReverseWhtRemittanceRequest {
  reason: string;
  reversalDate?: string;
}

export interface ApStatementLine {
  docNo: string;
  docType: string;
  docId: number;
  date: string;
  dueDate: string;
  currency: string;
  total: string;
  paid: string;
  outstanding: string;
  daysPastDue: number;
}

export interface ApStatementReconDTO {
  statementId: number;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  currency: string;
  statementDate: string;
  periodFrom?: string;
  supplierBalance: string;
  ledgerBalance: string;
  difference: string;
  supplierOpening: string;
  ledgerOpening: string;
  openingDifference: string;
  matched: ApStatementReconMatchedDTO[];
  supplierOnly: ApStatementReconLineDTO[];
  oursOnly: ApStatementReconOursDTO[];
  amountDifferences: ApStatementReconDiffDTO[];
  supplierOnlyTotal: string;
  oursOnlyTotal: string;
  amountDifferencesTotal: string;
  settlementDifferencesTotal: string;
  unexplainedDifference: string;
  generatedAt: string;
}

export interface ApStatementReconDiffDTO {
  line: ApStatementReconLineDTO;
  ours: ApStatementReconOursDTO;
  difference: string;
}

export interface ApStatementReconLineDTO {
  lineId: number;
  lineNo: number;
  date: string;
  reference: string;
  description: string;
  kind: 'invoice' | 'credit_note' | 'debit_note' | 'payment' | 'other';
  amount: string;
}

export interface ApStatementReconMatchedDTO {
  line: ApStatementReconLineDTO;
  ours: ApStatementReconOursDTO;
  basis: 'reference_and_amount' | 'amount_and_date';
  settlementDifference: string;
}

export interface ApStatementReconOursDTO {
  docType: string;
  docId: number;
  paymentId?: number | null;
  docNo?: string;
  reference: string;
  date: string;
  description: string;
  amount: string;
}

export interface ApSupplierStatementDTO {
  id: number;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  currency: string;
  statementDate: string;
  periodFrom?: string | null;
  statementRef: string;
  openingBalance: string;
  reportedBalance: string;
  source: 'json' | 'csv';
  fileName?: string;
  notes?: string;
  lineCount: number;
  linesTotal: string;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
  lines?: ApSupplierStatementLineDTO[];
}

export interface ApSupplierStatementLineDTO {
  id: number;
  lineNo: number;
  date: string;
  reference: string;
  description: string;
  kind: 'invoice' | 'credit_note' | 'debit_note' | 'payment' | 'other';
  amount: string;
}

export interface ApSupplierStatementLineInput {
  date: string;
  reference?: string;
  description?: string;
  kind?: string;
  amount: string;
}

export interface ApSupplierStatementListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vendorId?: number;
  from?: string;
  to?: string;
}

export interface ApWhtRemittanceDTO {
  id: number;
  remittanceNo?: string | null;
  companyId: number;
  authorityCode: string;
  authorityName?: string;
  period: string;
  currency: string;
  amount: string;
  bankAccountId: number;
  remittanceDate: string;
  reference?: string;
  memo?: string;
  status: 'draft' | 'posted' | 'cancelled' | 'reversed';
  journalEntryId?: number | null;
  postedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string;
  createdBy?: number | null;
  postedBy?: number | null;
  reversedAt?: string | null;
  reverseReason?: string;
  reversalEntryId?: number | null;
  createdAt: string;
  rowVersion: number;
  itemCount: number;
  items?: ApWhtRemittanceItemDTO[];
}

export interface ApWhtRemittanceItemDTO {
  id: number;
  allocationId: number;
  paymentId: number;
  invoiceId: number;
  invoiceNo?: string;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  whtCode?: string;
  rate?: string;
  currency: string;
  fxRate: string;
  whtAmount: string;
  baseAmount: string;
  withheldOn: string;
  remitted: boolean;
}

export interface ApWhtRemittanceListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'posted' | 'cancelled' | 'reversed';
  authority?: string;
  period?: string;
}

export interface ApWithholdingRegisterDTO {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  authority?: string;
  status?: string;
  rows: ApWithholdingRegisterRowDTO[];
  totals: ApWithholdingRegisterTotalDTO[];
  grandTotal: ApWithholdingRegisterTotalDTO;
  generatedAt: string;
}

export interface ApWithholdingRegisterQuery {
  companyId: number;
  from?: string;
  to?: string;
  authority?: string;
  status?: 'unremitted' | 'draft' | 'remitted';
}

export interface ApWithholdingRegisterRowDTO {
  allocationId: number;
  paymentId: number;
  runId: number;
  runNo?: string;
  withheldOn: string;
  period: string;
  invoiceId: number;
  invoiceNo?: string;
  vendorInvoiceNo: string;
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorTaxRegistrationNo?: string;
  whtCode?: string;
  rate?: string;
  authorityCode: string;
  authorityName?: string;
  termsRecorded: boolean;
  baseAmount: string;
  whtAmount: string;
  currency: string;
  fxRate: string;
  whtAmountBase: string;
  remitState: 'unremitted' | 'draft' | 'remitted';
  remittanceId?: number | null;
  remittanceNo?: string;
  remittanceDate?: string;
}

export interface ApWithholdingRegisterTotalDTO {
  authorityCode: string;
  authorityName?: string;
  rows: number;
  withheld: string;
  remitted: string;
  inDraft: string;
  outstanding: string;
}

export interface AppliedModifierDTO {
  kind: string;
  sourceId: number;
  sourceCode: string;
  before: string;
  after: string;
  applied: boolean;
  reasonKey: string;
  reasonArgs?: Record<string, string>;
}

export interface ApplyECORequest {
  note?: string;
}

export interface ApplyPMSDepositRequest {
  folioId: number;
  windowNo?: number;
  amount?: string;
  businessDate?: string;
}

export interface ApplyPOSDepositRequest {
  saleId: number;
  amount: string;
}

export interface ApplyPOSDiscountRequest {
  lineId?: number | null;
  kind: 'percent' | 'amount' | 'promotion';
  percent?: string;
  amount?: string;
  promotionId?: number | null;
  reasonCode?: string;
  note?: string;
  override?: POSOverrideRequest | null;
}

export interface ApplyResult {
  enterpriseId: number;
  runId: number;
  status: 'draft' | 'pending' | 'applied' | 'rejected';
  enterpriseStatus: 'draft' | 'pending' | 'active' | 'inactive';
  consumedAt: string;
  consumedBy?: number | null;
  objects: CreatedObject[];
}

export interface ApplyStandardCostRollupsRequest {
  rollupIds: number[];
}

export interface ApprovalChainCycleDTO {
  requestId: number;
  cycle: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'voided';
  origin: string;
  amount?: string;
  currency?: string;
  voidReasonCode?: string;
  submittedAt: string;
  decidedAt?: string | null;
}

export interface ApprovalChainDTO {
  requestId: number;
  docType: string;
  docId: number;
  docNo?: string;
  source: string;
  authorityVersionId?: number | null;
  authorityRuleId?: number | null;
  policyId?: number | null;
  policyName?: string;
  cycle: number;
  steps: ApprovalChainStepDTO[];
  cycles: ApprovalChainCycleDTO[];
}

export interface ApprovalChainStepDTO {
  step: number;
  mode: string;
  name?: string;
  source: string;
  holders: number[];
  holderNames: string[];
  positionId?: number | null;
  position?: string;
  roleId?: number | null;
  role?: string;
  status: string;
}

export interface ApproveAttendanceRequest {
  rowVersion: number;
}

export interface ApproveInstructionRequest {
  note?: string;
  rowVersion: number;
}

export interface ApproveMarketingCampaignRequest {
  rowVersion: number;
}

export interface ApprovePartyMergeRequest {
  comment?: string;
}

export interface ApprovePoolRequest {
  rowVersion: number;
}

export interface ApproveReturnRequest {
  note: string;
}

export interface ApproveServiceGoodwillRequest {
  note?: string;
  rowVersion: number;
}

export interface ApproveTimesheetRequest {
  comment?: string;
}

export interface ArAgingBucket {
  label: string;
  fromDays: number;
  toDays?: number | null;
  amount: string;
}

export interface ArAgingQuery {
  companyId: number;
  asOf?: string;
  customerId?: number;
}

export interface ArAgingReport {
  companyId: number;
  currency: string;
  asOf: string;
  rows: ArAgingRow[];
  totals: ArAgingRow;
  generatedAt: string;
}

export interface ArAgingRow {
  customerId: number;
  customerCode: string;
  customerName: string;
  buckets: ArAgingBucket[];
  total: string;
  unappliedCredit: string;
  netExposure: string;
}

export interface ArAllocationDTO {
  id: number;
  invoiceId: number;
  invoiceNo?: string;
  amount: string;
  allocatedOn: string;
  journalEntryId?: number | null;
}

export interface ArAllocationProposalDTO {
  receiptId: number;
  receiptNo: string;
  customerId: number;
  currency: string;
  unallocated: string;
  basis: 'reference' | 'exact_amount' | 'oldest_first' | 'none';
  confidence: 'high' | 'medium' | 'low' | 'none';
  lines: SuggestionDTO[];
  unproposed: string;
}

export interface ArCeiQuery {
  companyId: number;
  from: string;
  to: string;
  customerId?: number;
}

export interface ArCeiReport {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  beginningReceivables: string;
  creditSales: string;
  endingReceivables: string;
  endingCurrentReceivables: string;
  numerator: string;
  denominator: string;
  cashCollected: string;
  ceiPct?: string;
  formula: string;
  generatedAt: string;
}

export interface ArCollectionsItemDTO {
  rank: number;
  customerId: number;
  customerCode: string;
  customerName: string;
  currency: string;
  overdueAmount: string;
  valueAtRisk: string;
  score: string;
  overdueInvoices: number;
  oldestDaysOverdue: number;
  oldestBucket: string;
  dunningLevel: number;
  lastDunningSentOn?: string | null;
  dunningPaused: boolean;
  onHold: boolean;
  brokenPromises: number;
  openPromiseDate?: string | null;
  openPromiseAmount?: string | null;
  openPromiseCurrency?: string | null;
}

export interface ArCollectionsSettingsDTO {
  companyId: number;
  currency: string;
  unallocatedThreshold: string;
  unallocatedMaxAgeDays: number;
  configured: boolean;
  rowVersion: number;
}

export interface ArCollectionsSettingsQuery {
  companyId: number;
}

export interface ArCollectionsSettingsRequest {
  companyId: number;
  unallocatedThreshold?: string;
  unallocatedMaxAgeDays: number;
  rowVersion?: number;
}

export interface ArCollectionsWorklistQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  asOf?: string;
}

export interface ArCreateInvoiceRequest {
  companyId: number;
  customerId: number;
  invoiceDate?: string;
  dueDate?: string;
  currency?: string;
  memo?: string;
  reference?: string;
  originalInvoiceId?: number | null;
  sourceDocType?: string;
  sourceDocId?: number | null;
  channel?: string;
  supplyType?: string;
  priceBasis?: string;
  deliveryChannel?: 'email' | 'portal' | 'post' | 'einvoice';
  lines: ArInvoiceLineInput[];
}

export interface ArCreditExposureDTO {
  customerId: number;
  customerCode: string;
  customerName: string;
  currency: string;
  limit: string;
  exposure: string;
  overdue: string;
  available?: string | null;
  overLimit: boolean;
  onHold: boolean;
  holdId?: number | null;
  holdReason?: string;
  holdSource?: 'manual' | 'dunning' | 'limit';
  holdAppliedAt?: string | null;
  lastDunningLevel: number;
  customerRowVersion: number;
}

export interface ArCreditExposureQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  onHold?: boolean;
  overLimit?: boolean;
}

export interface ArCreditHoldActionRequest {
  reason: string;
  rowVersion: number;
}

export interface ArCreditHoldDTO {
  id: number;
  customerId: number;
  source: 'manual' | 'dunning' | 'limit';
  reason: string;
  dunningInvoiceId?: number | null;
  appliedBy?: number | null;
  appliedAt: string;
  releasedBy?: number | null;
  releasedAt?: string | null;
  releaseReason?: string;
  active: boolean;
  customerStatus: 'active' | 'on_hold' | 'blocked';
  customerRowVersion: number;
}

export interface ArCreditLimitRequestDTO {
  id: number;
  customerId: number;
  currency: string;
  currentLimit: string;
  newLimit: string;
  reason: string;
  status: 'pending' | 'applied' | 'rejected';
  requestedBy?: number | null;
  requestedAt: string;
  approvalRequestId?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  appliedAt?: string | null;
  rowVersion: number;
}

export interface ArCreditLimitRequestInput {
  newLimit: string;
  reason: string;
}

export interface ArCreditLimitRequestListQuery {
  cursor?: string;
  limit?: number;
}

export interface ArDeleteQuery {
  rowVersion?: number;
}

export interface ArDsoGroupDTO {
  groupId: number;
  code: string;
  name: string;
  creditSales: string;
  closingReceivables: string;
  dso?: string;
}

export interface ArDunningEffectivenessQuery {
  companyId?: number;
  from?: string;
  to?: string;
  windowDays?: number;
}

export interface ArDunningEffectivenessReport {
  from?: string;
  to?: string;
  windowDays: number;
  rows: ArDunningEffectivenessRowDTO[];
  totals: ArDunningEffectivenessTotalDTO[];
}

export interface ArDunningEffectivenessRowDTO {
  levelNo: number;
  levelName: string;
  channel: 'email' | 'phone';
  currency: string;
  noticesSent: number;
  noticesResponded: number;
  noticesNoResponse: number;
  noticesPaid: number;
  responseRatePct: string;
  amountNoticed: string;
  amountRecovered: string;
  recoveryRatePct: string;
}

export interface ArDunningEffectivenessTotalDTO {
  currency: string;
  noticesSent: number;
  noticesResponded: number;
  noticesPaid: number;
  noticesNoResponse: number;
  responseRatePct: string;
  amountNoticed: string;
  amountRecovered: string;
  recoveryRatePct: string;
}

export interface ArDunningEventDTO {
  id: number;
  invoiceId: number;
  invoiceNo: string;
  customerId: number;
  customerName: string;
  levelId?: number | null;
  levelNo: number;
  levelName: string;
  channel: 'email' | 'phone';
  sentOn: string;
  daysOverdue: number;
  amountDue: string;
  currency: string;
  recipient: string;
  nextActionDate?: string;
  respondedAt?: string | null;
  response?: 'promise_to_pay' | 'dispute' | 'no_response' | 'other';
  responseNote?: string;
  paidAt?: string | null;
  promiseId?: number | null;
  promisedDate?: string;
  promisedAmount?: string;
  promiseStatus?: 'open' | 'kept' | 'broken' | 'cancelled';
}

export interface ArDunningEventListQuery {
  cursor?: string;
  limit?: number;
  customerId?: number;
  invoiceId?: number;
  levelId?: number;
  channel?: 'email' | 'phone';
  responded?: string;
  from?: string;
  to?: string;
}

export interface ArDunningLevelCreateRequest {
  levelNo: number;
  code: string;
  name: string;
  minDaysOverdue: number;
  template?: string;
  channel?: 'email' | 'phone';
  isFinal: boolean;
  isActive?: boolean | null;
}

export interface ArDunningLevelDTO {
  id: number;
  levelNo: number;
  code: string;
  name: string;
  minDaysOverdue: number;
  template: string;
  channel: 'email' | 'phone';
  isFinal: boolean;
  isActive: boolean;
  inUse: boolean;
  rowVersion: number;
}

export interface ArDunningLevelListQuery {
  cursor?: string;
  limit?: number;
  activeOnly?: boolean;
}

export interface ArDunningLevelUpdateRequest {
  rowVersion: number;
  levelNo?: number | null;
  code?: string | null;
  name?: string | null;
  minDaysOverdue?: number | null;
  template?: string | null;
  channel?: 'email' | 'phone';
  isFinal?: boolean | null;
  isActive?: boolean | null;
}

export interface ArDunningNoticeDTO {
  eventId?: number | null;
  invoiceId: number;
  invoiceNo: string;
  customerId: number;
  customerName: string;
  levelId: number;
  levelNo: number;
  levelName: string;
  channel: 'email' | 'phone';
  daysOverdue: number;
  amountDue: string;
  currency: string;
  recipient: string;
  isFinal: boolean;
  nextActionDate?: string | null;
}

export interface ArDunningResponseRequest {
  response: 'promise_to_pay' | 'dispute' | 'no_response' | 'other';
  note?: string;
  promisedDate?: string;
  promisedAmount?: string;
}

export interface ArDunningRunRequest {
  asOf?: string;
  customerIds?: number[];
  levelIds?: number[];
  preview: boolean;
}

export interface ArDunningRunResultDTO {
  asOf: string;
  preview: boolean;
  noticed: number;
  skipped: number;
  notices: ArDunningNoticeDTO[];
  skippedItems: ArDunningSkipDTO[];
  totals: ArDunningRunTotalDTO[];
}

export interface ArDunningRunTotalDTO {
  currency: string;
  noticed: number;
  amountDue: string;
}

export interface ArDunningSkipDTO {
  invoiceId: number;
  invoiceNo: string;
  customerId: number;
  customerName: string;
  reason: 'customer_paused' | 'no_recipient' | 'already_sent';
}

export interface ArInvoiceDTO {
  id: number;
  docKind: 'invoice' | 'credit_note';
  invoiceNo?: string | null;
  companyId: number;
  customerId: number;
  customerName?: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  fxRate: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  allocated: string;
  balance: string;
  status: 'draft' | 'confirmed' | 'posted' | 'cancelled';
  settlement: 'unpaid' | 'partial' | 'paid';
  arAccountId?: number | null;
  memo: string;
  reference: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  originalInvoiceId?: number | null;
  creditOverrideReason?: string;
  creditOverrideBy?: number | null;
  creditOverrideAt?: string | null;
  creditOverridePending?: boolean;
  dunningLevel: number;
  dunningLastSentOn?: string | null;
  deliveryChannel?: 'email' | 'portal' | 'post' | 'einvoice';
  deliveryStatus: 'pending' | 'sent' | 'delivered' | 'failed';
  deliveryAttempts: number;
  deliveryLastError?: string;
  deliveryLastAttemptAt?: string | null;
  deliverySentAt?: string | null;
  deliveredAt?: string | null;
  deliveryReference?: string;
  journalEntryId?: number | null;
  confirmedAt?: string | null;
  postedAt?: string | null;
  createdAt: string;
  rowVersion: number;
  lines?: ArInvoiceLineDTO[];
}

export interface ArInvoiceDeliverRequest {
  channel?: 'email' | 'portal' | 'post' | 'einvoice';
  reference?: string;
}

export interface ArInvoiceLineDTO {
  id: number;
  lineNo: number;
  description: string;
  revenueAccountId: number;
  accountCode?: string;
  accountName?: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  lineAmount: string;
  taxAmount: string;
  taxCode?: string | null;
  itemTaxGroupId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
}

export interface ArInvoiceLineInput {
  description?: string;
  revenueAccountId: number;
  quantity?: string;
  unitPrice?: string;
  discountPercent?: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
}

export interface ArLedgerEntry {
  date: string;
  docType: string;
  docId: number;
  docNo: string;
  memo: string;
  debit: string;
  credit: string;
  balance: string;
  currency: string;
  docCurrency: string;
  docAmount: string;
  journalEntryId?: number | null;
}

export interface ArLedgerPage {
  customerId: number;
  companyId: number;
  currency: string;
  from?: string;
  to?: string;
  opening: string;
  closing: string;
  entries: ArLedgerEntry[];
}

export interface ArLedgerQuery {
  companyId?: number;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface ArListInvoicesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  customerId?: number;
  status?: string;
  openOnly?: boolean;
  from?: string;
  to?: string;
}

export interface ArPromiseCreateRequest {
  companyId: number;
  customerId: number;
  invoiceId?: number | null;
  promisedAmount: string;
  promisedDate: string;
  note?: string;
}

export interface ArPromiseDTO {
  id: number;
  companyId: number;
  customerId: number;
  customerName: string;
  invoiceId?: number | null;
  invoiceNo?: string;
  currency: string;
  promisedAmount: string;
  madeOn: string;
  promisedDate: string;
  status: 'open' | 'kept' | 'broken' | 'cancelled';
  note: string;
  receivedAmount?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ArPromiseListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  invoiceId?: number;
  status?: 'open' | 'kept' | 'broken' | 'cancelled';
  from?: string;
  to?: string;
}

export interface ArPromiseUpdateRequest {
  rowVersion: number;
  promisedAmount?: string | null;
  promisedDate?: string | null;
  note?: string | null;
  status?: 'open' | 'kept' | 'broken' | 'cancelled';
}

export interface ArReceiptAllocationHistoryDTO {
  receiptId: number;
  receiptNo: string;
  currency: string;
  fxRate: string;
  amount: string;
  allocated: string;
  unallocated: string;
  entries: ArReceiptAllocationHistoryEntryDTO[];
}

export interface ArReceiptAllocationHistoryEntryDTO {
  id: number;
  kind: 'allocation' | 'reversal' | 'reallocation';
  invoiceId: number;
  invoiceNo?: string;
  amount: string;
  openAmount?: string | null;
  allocatedOn: string;
  reversalOf?: number | null;
  reallocatedFrom?: number | null;
  movedTo?: number | null;
  reason?: string;
  fxGainLoss: string;
  journalEntryId?: number | null;
  actorId?: number | null;
  createdAt: string;
}

export interface ArReceiptReallocateRequest {
  fromAllocationId?: number | null;
  fromInvoiceId?: number | null;
  toInvoiceId: number;
  amount?: string;
  reallocatedOn?: string;
  reason: string;
}

export interface ArReceiptReallocationDTO {
  receipt: ReceiptDTO;
  moves: ArReceiptReallocationMoveDTO[];
  reversalJournalEntryId?: number | null;
  allocationJournalEntryId?: number | null;
  reason: string;
  reallocatedOn: string;
}

export interface ArReceiptReallocationMoveDTO {
  reversalId: number;
  newAllocationId: number;
  originalAllocationId: number;
  fromInvoiceId: number;
  toInvoiceId: number;
  amount: string;
  fxReleased: string;
  fxRecognised: string;
}

export interface ArStatement {
  customerId: number;
  customerCode: string;
  customerName: string;
  companyId: number;
  currency: string;
  asOf: string;
  openOnly: boolean;
  lines: ArStatementLine[];
  totalDue: string;
  unappliedCredit: string;
  aging: ArAgingBucket[];
  generatedAt: string;
}

export interface ArStatementLine {
  date: string;
  dueDate: string;
  docType: string;
  docNo: string;
  currency: string;
  amount: string;
  balance: string;
  daysDue: number;
}

export interface ArUnallocatedReceiptDTO {
  receiptId: number;
  receiptNo: string;
  customerId: number;
  customerName: string;
  receiptDate: string;
  ageDays: number;
  currency: string;
  amount: string;
  unallocated: string;
  baseUnallocated: string;
  reference: string;
  overMaxAge: boolean;
  proposal: ArAllocationProposalDTO;
}

export interface ArUnallocatedReceiptsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  customerId?: number;
  minAgeDays?: number;
}

export interface ArUpdateInvoiceRequest {
  invoiceDate?: string;
  dueDate?: string;
  currency?: string;
  memo?: string | null;
  reference?: string | null;
  lines?: ArInvoiceLineInput[];
  deliveryChannel?: 'email' | 'portal' | 'post' | 'einvoice';
  rowVersion: number;
}

export interface ArWriteOffCreateRequest {
  companyId: number;
  customerId: number;
  writeOffDate?: string;
  reasonCode: 'insolvency' | 'untraceable' | 'uncollectable' | 'disputed' | 'statute_barred' | 'other';
  reason?: string;
  lines: ArWriteOffLineInput[];
}

export interface ArWriteOffDTO {
  id: number;
  writeOffNo?: string;
  companyId: number;
  customerId: number;
  customerCode: string;
  customerName: string;
  status: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected' | 'reversed';
  writeOffDate: string;
  currency: string;
  totalBase: string;
  reasonCode: 'insolvency' | 'untraceable' | 'uncollectable' | 'disputed' | 'statute_barred' | 'other';
  reason: string;
  dualRequired: boolean;
  committeeRequired: boolean;
  provisionUsed: string;
  expenseAmount: string;
  taxReliefAmount: string;
  proposedBy?: number | null;
  approvalRequestId?: number | null;
  submittedAt?: string | null;
  submittedBy?: number | null;
  approver1Id?: number | null;
  approver2Id?: number | null;
  approvedAt?: string | null;
  decisionNote?: string;
  rejectedAt?: string | null;
  approverUserIds: number[];
  journalEntryId?: number | null;
  postedAt?: string | null;
  postedBy?: number | null;
  reversalEntryId?: number | null;
  reversedAt?: string | null;
  reversedBy?: number | null;
  reversedOn?: string | null;
  reverseReason?: string;
  createdAt: string;
  rowVersion: number;
  lines?: ArWriteOffLineDTO[];
  evidence?: ArWriteOffEvidenceDTO | null;
}

export interface ArWriteOffEvidenceDTO {
  attachmentCount: number;
  dunningEffort: boolean;
  invoicesWithoutDunning: number[];
  satisfied: boolean;
}

export interface ArWriteOffLineDTO {
  id: number;
  lineNo: number;
  invoiceId: number;
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  amount: string;
  fxRate: string;
  baseAmount: string;
  taxReliefBase: string;
  invoiceBalance: string;
  dunningNotices: number;
}

export interface ArWriteOffLineInput {
  invoiceId: number;
  amount?: string;
}

export interface ArWriteOffListQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  customerId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected' | 'reversed';
}

export interface ArWriteOffPreviewDTO {
  lines: ArWriteOffPreviewLineDTO[];
  totals: ArWriteOffPreviewTotalsDTO;
  provisionAvailable: string;
  currency: string;
}

export interface ArWriteOffPreviewLineDTO {
  invoiceId: number;
  amount: string;
  provisionUsed: string;
  badDebt: string;
  taxRelief: string;
  baseAmount: string;
}

export interface ArWriteOffPreviewRequest {
  companyId: number;
  customerId: number;
  writeOffDate?: string;
  writeOffId?: number;
  lines: ArWriteOffLineInput[];
}

export interface ArWriteOffPreviewTotalsDTO {
  provisionUsed: string;
  badDebt: string;
  taxRelief: string;
  baseAmount: string;
}

export interface ArWriteOffReasonTotal {
  reasonCode: 'insolvency' | 'untraceable' | 'uncollectable' | 'disputed' | 'statute_barred' | 'other';
  count: number;
  totalBase: string;
}

export interface ArWriteOffReport {
  companyId: number;
  currency: string;
  from?: string;
  to: string;
  status: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected' | 'reversed';
  rows: ArWriteOffReportRow[];
  byReason: ArWriteOffReasonTotal[];
  totalBase: string;
  provisionUsed: string;
  expenseAmount: string;
  taxReliefAmount: string;
  committeeCount: number;
  committeeTotal: string;
  generatedAt: string;
}

export interface ArWriteOffReportQuery {
  companyId: number;
  customerId?: number;
  from?: string;
  to?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected' | 'reversed';
}

export interface ArWriteOffReportRow {
  id: number;
  writeOffNo: string;
  writeOffDate: string;
  customerId: number;
  customerCode: string;
  customerName: string;
  reasonCode: 'insolvency' | 'untraceable' | 'uncollectable' | 'disputed' | 'statute_barred' | 'other';
  status: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected' | 'reversed';
  totalBase: string;
  provisionUsed: string;
  expenseAmount: string;
  taxReliefAmount: string;
  dualRequired: boolean;
  committeeRequired: boolean;
  proposedBy?: number | null;
  approver1Id?: number | null;
  approver2Id?: number | null;
  postedBy?: number | null;
  journalEntryId?: number | null;
}

export interface ArWriteOffReverseRequest {
  reason: string;
  reversalDate?: string;
}

export interface ArWriteOffSettingsDTO {
  companyId: number;
  currency: string;
  writeOffDualAbove?: string | null;
  writeOffCommitteeAbove?: string | null;
  badDebtAccountId?: number | null;
  taxReliefEnabled: boolean;
  pending?: ArWriteOffSettingsPendingDTO | null;
  configured: boolean;
  rowVersion: number;
}

export interface ArWriteOffSettingsPendingDTO {
  writeOffDualAbove?: string | null;
  writeOffCommitteeAbove?: string | null;
  requestedBy: number;
  requestedAt: string;
  approvalRequestId: number;
}

export interface ArWriteOffSettingsQuery {
  companyId: number;
}

export interface ArWriteOffSettingsRequest {
  companyId: number;
  writeOffDualAbove?: string | null;
  clearWriteOffDualAbove?: boolean;
  writeOffCommitteeAbove?: string | null;
  clearWriteOffCommitteeAbove?: boolean;
  badDebtAccountId?: number | null;
  clearBadDebtAccountId?: boolean;
  taxReliefEnabled?: boolean | null;
  rowVersion?: number;
}

export interface ArWriteOffUpdateRequest {
  rowVersion: number;
  writeOffDate?: string | null;
  reasonCode?: 'insolvency' | 'untraceable' | 'uncollectable' | 'disputed' | 'statute_barred' | 'other';
  reason?: string | null;
  lines?: ArWriteOffLineInput[];
}

export interface ArchiveKBArticleRequest {
  rowVersion: number;
}

export interface ArrangeNodesRequest {
  operations: NodeOperation[];
}

export interface ArrangeNodesResult {
  versionId: number;
  applied: number;
  nodes: HierarchyNodeDTO[];
  validation: ValidationResult;
}

export interface AsAtAssignmentQuery {
  on?: string;
}

export interface AssessImpairmentIndicatorRequest {
  assetId?: number;
  bookId: number;
  recoverableAmount: string;
  outcome?: 'pending' | 'no_impairment' | 'impaired' | 'reversed';
  reasonCode?: string;
  note?: string;
}

export interface AssetBookComparisonQuery {
  companyId: number;
  bookAId: number;
  bookBId: number;
  classId?: number;
  asOf: string;
}

export interface AssetBookComparisonReport {
  companyId: number;
  bookAId: number;
  bookACode: string;
  bookBId: number;
  bookBCode: string;
  asOf: string;
  rows: AssetBookComparisonRow[];
  totalBookACarrying: string;
  totalBookBCarrying: string;
  totalDifference: string;
  generatedAt: string;
}

export interface AssetBookComparisonRow {
  assetId: number;
  assetNo: string;
  assetName: string;
  bookACost: string;
  bookAAccumDepreciation: string;
  bookAAccumImpairment: string;
  bookACarrying: string;
  bookBCost: string;
  bookBAccumDepreciation: string;
  bookBAccumImpairment: string;
  bookBCarrying: string;
  difference: string;
}

export interface AssetBookDTO {
  id: number;
  assetId: number;
  bookId: number;
  bookCode?: string;
  bookName?: string;
  method: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths: number;
  salvageValue: string;
  cost: string;
  accumulatedDepreciation: string;
  accumulatedImpairment: string;
  carryingAmount: string;
  periodsDepreciated: number;
  inServiceDate: string;
  assetAccountId?: number | null;
  accumulatedDepreciationAccountId?: number | null;
  depreciationExpenseAccountId?: number | null;
  impairmentExpenseAccountId?: number | null;
  impairmentReversalAccountId?: number | null;
  gainLossAccountId?: number | null;
  status: 'in_service' | 'fully_depreciated' | 'disposed';
  closedOn?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface AssetBookScheduleDTO {
  assetBookId: number;
  assetId: number;
  assetNo: string;
  bookId: number;
  bookCode: string;
  method: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths: number;
  salvageValue: string;
  cost: string;
  rows: ScheduleRow[];
  disclaimer: string;
  generatedAt: string;
}

export interface AssetDTO {
  id: number;
  assetNo: string;
  companyId: number;
  classId: number;
  classCode?: string;
  className?: string;
  name: string;
  description: string;
  location: string;
  custodian: string;
  branchId?: number | null;
  costCenterId?: number | null;
  method: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths: number;
  salvageValue: string;
  cost: string;
  accumulatedDepreciation: string;
  accumulatedImpairment: string;
  netBookValue: string;
  periodsDepreciated: number;
  inServiceDate: string;
  acquisitionSource: 'manual' | 'ap_invoice_line' | 'project' | 'cip' | 'ic_transfer';
  sourceInvoiceLineId?: number | null;
  sourceInvoiceId?: number | null;
  sourceInvoiceNo?: string;
  acquisitionJournalEntryId?: number | null;
  sourceProjectId?: number | null;
  sourceAccountId?: number | null;
  assetAccountId: number;
  assetAccountCode?: string;
  accumulatedDepreciationAccountId: number;
  depreciationExpenseAccountId: number;
  gainLossAccountId: number;
  status: 'in_service' | 'disposed';
  disposalDate?: string | null;
  disposalProceeds?: string | null;
  disposalGainLoss?: string | null;
  disposalMethod?: 'sale' | 'scrap' | 'write_off' | 'donation' | 'ic_transfer';
  disposalReceiptRef?: string;
  pendingDisposalId?: number | null;
  serialNo: string;
  tagNo: string;
  insurancePolicy: string;
  insuranceExpiry?: string | null;
  warrantyExpiry?: string | null;
  sourceRef: string;
  parentAssetId?: number | null;
  componentKind: 'whole' | 'parent' | 'component';
  componentSeq?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface AssetDepreciationSchedule {
  assetId: number;
  assetNo: string;
  method: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths: number;
  salvageValue: string;
  cost: string;
  rows: ScheduleRow[];
  disclaimer: string;
  generatedAt: string;
}

export interface AssetsCapexVsBudgetQuery {
  companyId: number;
  from: string;
  to: string;
}

export interface AssetsCapexVsBudgetReport {
  companyId: number;
  from: string;
  to: string;
  capitalBudgetConfigured: boolean;
  basis: string;
  rows: AssetsCapexVsBudgetRow[];
  totalBudget: string;
  totalAdditions: string;
  totalVariance: string;
  generatedAt: string;
}

export interface AssetsCapexVsBudgetRow {
  accountId: number;
  accountCode: string;
  accountName: string;
  budgetAmount: string;
  additionsAmount: string;
  additionsCount: number;
  variance: string;
  utilisationPercent?: string;
}

export interface AssetsCapitalizableSourceDTO {
  kind: 'purchase' | 'project' | 'cip';
  sourceId: number;
  companyId: number;
  code: string;
  name: string;
  accountId: number;
  accountCode: string;
  balance: string;
}

export interface AssetsCapitalizableSourcesQuery {
  cursor?: string;
  limit?: number;
  kind: 'purchase' | 'project' | 'cip';
  companyId?: number;
  q?: string;
}

export interface AssetsDepreciationForecastCell {
  period: string;
  amount: string;
}

export interface AssetsDepreciationForecastGroup {
  key: string;
  label: string;
  id?: number | null;
  assetCount: number;
  total: string;
  cells: AssetsDepreciationForecastCell[];
}

export interface AssetsDepreciationForecastQuery {
  companyId: number;
  groupBy?: string;
  from: string;
  to: string;
  bookId?: number;
  classId?: number;
}

export interface AssetsDepreciationForecastReport {
  companyId: number;
  bookId: number;
  bookCode: string;
  groupBy: string;
  from: string;
  to: string;
  periods: string[];
  periodTotals: AssetsDepreciationForecastCell[];
  groups: AssetsDepreciationForecastGroup[];
  assetCount: number;
  totalCharge: string;
  basis: string;
  generatedAt: string;
}

export interface AssetsDepreciationPreviewBook {
  bookId: number;
  bookCode: string;
  isPrimary: boolean;
  postsToGl: boolean;
  chargeCount: number;
  totalCharge: string;
  rows: AssetsDepreciationPreviewRow[];
  skipped: DepreciationSkip[];
}

export interface AssetsDepreciationPreviewDTO {
  companyId: number;
  fiscalPeriodId: number;
  periodLabel: string;
  runDate: string;
  books: AssetsDepreciationPreviewBook[];
  chargeCount: number;
  totalCharge: string;
  generatedAt: string;
}

export interface AssetsDepreciationPreviewJournalLine {
  accountId: number;
  accountCode: string;
  accountName: string;
  debit: string;
  credit: string;
  costCenterId?: number | null;
  description: string;
}

export interface AssetsDepreciationPreviewRequest {
  companyId: number;
  on?: string;
  bookId?: number;
}

export interface AssetsDepreciationPreviewRow {
  assetId: number;
  assetNo: string;
  assetName: string;
  bookId: number;
  bookCode: string;
  method: 'SL' | 'DB' | 'SYD' | 'UOP';
  periodNo: number;
  openingNbv: string;
  charge: string;
  closingNbv: string;
  journalLines: AssetsDepreciationPreviewJournalLine[];
}

export interface AssetsDepreciationSchedulePreviewDTO {
  companyId: number;
  classId: number;
  bookId?: number | null;
  bookCode?: string;
  method: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths: number;
  cost: string;
  salvageValue: string;
  inServiceDate: string;
  totalDepreciation: string;
  residualNbv: string;
  rows: AssetsDepreciationSchedulePreviewRow[];
  basis: string;
  generatedAt: string;
}

export interface AssetsDepreciationSchedulePreviewRequest {
  companyId: number;
  classId: number;
  cost: string;
  salvageValue?: string;
  salvagePercent?: string;
  usefulLifeMonths?: number;
  method?: 'SL' | 'DB' | 'SYD' | 'UOP';
  inServiceDate: string;
  bookId?: number;
}

export interface AssetsDepreciationSchedulePreviewRow {
  periodNo: number;
  period: string;
  periodEnd: string;
  periodName?: string;
  estimated: boolean;
  openingNbv: string;
  charge: string;
  closingNbv: string;
  accumulatedDepreciation: string;
}

export interface AssetsDisposalDTO {
  id: number;
  assetId: number;
  assetNo: string;
  assetName: string;
  classCode?: string;
  companyId: number;
  disposalDate: string;
  method: 'sale' | 'scrap' | 'write_off' | 'donation' | 'ic_transfer';
  status: 'pending_approval' | 'posted' | 'rejected' | 'cancelled' | 'lapsed';
  proceeds: string;
  proceedsAccountId?: number | null;
  proceedsReceiptRef: string;
  reason: string;
  cost: string;
  accumulatedDepreciation: string;
  accumulatedImpairment: string;
  netBookValue: string;
  gainLoss: string;
  journalId?: number | null;
  approvalRequestId?: number | null;
  requestedBy?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  postedAt?: string | null;
  toCompanyId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface AssetsDisposalGainLossQuery {
  companyId: number;
  from?: string;
  to?: string;
  classId?: number;
  method?: 'sale' | 'scrap' | 'write_off' | 'donation' | 'ic_transfer';
}

export interface AssetsDisposalGainLossReport {
  companyId: number;
  from?: string;
  to?: string;
  rows: AssetsDisposalGainLossRow[];
  count: number;
  totalCost: string;
  totalAccumulatedDepreciation: string;
  totalAccumulatedImpairment: string;
  totalNetBookValue: string;
  totalProceeds: string;
  totalGain: string;
  totalLoss: string;
  netGainLoss: string;
  generatedAt: string;
}

export interface AssetsDisposalGainLossRow {
  disposalId: number;
  assetId: number;
  assetNo: string;
  assetName: string;
  classId: number;
  classCode: string;
  method: 'sale' | 'scrap' | 'write_off' | 'donation' | 'ic_transfer';
  disposalDate: string;
  cost: string;
  accumulatedDepreciation: string;
  accumulatedImpairment: string;
  netBookValue: string;
  proceeds: string;
  gainLoss: string;
  journalId?: number | null;
  proceedsReceiptRef: string;
}

export interface AssetsDisposalListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  assetId?: number;
  status?: 'pending_approval' | 'posted' | 'rejected' | 'cancelled' | 'lapsed';
  method?: 'sale' | 'scrap' | 'write_off' | 'donation' | 'ic_transfer';
  from?: string;
  to?: string;
  q?: string;
}

export interface AssetsEventDTO {
  id: number;
  assetId: number;
  companyId: number;
  eventType: 'acquire' | 'capitalise' | 'depreciate' | 'revalue' | 'transfer' | 'ic_transfer' | 'impair' | 'impair_reverse' | 'dispose' | 'update' | 'componentise' | 'book_change';
  eventDate: string;
  amount?: string | null;
  fromValue: string;
  toValue: string;
  reason: string;
  approvedBy?: number | null;
  journalEntryId?: number | null;
  createdAt: string;
  createdBy?: number | null;
}

export interface AssetsEventListQuery {
  cursor?: string;
  limit?: number;
  eventType?: 'acquire' | 'capitalise' | 'depreciate' | 'revalue' | 'transfer' | 'ic_transfer' | 'impair' | 'impair_reverse' | 'dispose' | 'update' | 'componentise' | 'book_change';
}

export interface AssetsImpairmentSettingsPendingChangeDTO {
  impairmentApprovalAbove?: string | null;
  requestedBy?: number | null;
  requestedAt?: string | null;
  approvalRequestId?: number | null;
}

export interface AssetsIntercompanyTransferDTO {
  status: 'pending_approval' | 'posted' | 'rejected' | 'cancelled' | 'lapsed';
  pendingDisposalId?: number | null;
  id: number;
  fromCompanyId: number;
  toCompanyId: number;
  fromAssetId: number;
  fromAssetNo: string;
  toAssetId: number;
  toAssetNo: string;
  transferDate: string;
  price: string;
  toPrice: string;
  cost: string;
  accumulatedDepreciation: string;
  accumulatedImpairment: string;
  netBookValue: string;
  gainLoss: string;
  fromJournalEntryId: number;
  toJournalEntryId: number;
  disposalId?: number | null;
  reason: string;
  fromAsset: AssetDTO;
  toAsset: AssetDTO;
  createdAt: string;
}

export interface AssetsIntercompanyTransferRequest {
  toCompanyId: number;
  toClassId?: number;
  date: string;
  price: string;
  reason?: string;
  rowVersion?: number;
}

export interface AssetsSettingsDTO {
  companyId: number;
  disposalApprovalAbove?: string | null;
  impairmentApprovalAbove?: string | null;
  cipAccountId?: number | null;
  rowVersion: number;
  pendingChange?: AssetsSettingsPendingChangeDTO | null;
  pendingImpairmentChange?: AssetsImpairmentSettingsPendingChangeDTO | null;
}

export interface AssetsSettingsPendingChangeDTO {
  disposalApprovalAbove?: string | null;
  requestedBy?: number | null;
  requestedAt?: string | null;
  approvalRequestId?: number | null;
}

export interface AssetsSettingsUpdateRequest {
  disposalApprovalAbove?: string | null;
  clearDisposalApprovalAbove?: boolean;
  impairmentApprovalAbove?: string | null;
  clearImpairmentApprovalAbove?: boolean;
  cipAccountId?: number | null;
  clearCipAccountId?: boolean;
  rowVersion?: number;
}

export interface AssignBalancingSegmentsRequest {
  segments: BalancingSegmentInput[];
}

export interface AssignBusinessUnitsRequest {
  determinantType: string;
  businessUnitIds: number[];
  effectiveFrom: string;
  effectiveTo: string;
  reassign: boolean;
  confirmedBy: number | null;
}

export interface AssignCaseRequest {
  assignedTo: number;
  rowVersion: number;
}

export interface AssignCloseTaskRequest {
  assigneeUserId?: number | null;
  start?: boolean;
  rowVersion: number;
}

export interface AssignFunctionRequest {
  functionCode: string;
  effectiveFrom?: string;
}

export interface AssignPMSRoomRequest {
  roomId: number;
  reason: 'requested' | 'upgrade' | 'accessible' | 'connecting' | 'only_available' | 'room_move';
  note?: string;
  rowVersion: number;
}

export interface AssignReferenceSetRequest {
  determinantType: string;
  referenceSetId: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface AssignReferenceSetsRequest {
  lines: AssignReferenceSetRequest[];
}

export interface AssignRequest {
  positionId: number;
  employeeId: number;
  assignmentType?: string;
  fte?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  effectiveFrom: string;
  effectiveTo?: string;
  reasonCode?: string;
  note?: string;
  departmentId?: number | null;
  legalEmployerId?: number | null;
  gradeId?: number | null;
  salary?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
}

export interface AssignTicketRequest {
  assignedTo: number;
  rowVersion: number;
}

export interface AssignmentDTO {
  id: number;
  positionId: number;
  positionCode?: string;
  positionName?: string;
  employeeId: number;
  employeeName?: string;
  assignmentType: string;
  fte: string;
  reasonCode?: string;
  note?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface AssignmentFieldChangeDTO {
  field: string;
  from?: string | null;
  to?: string | null;
}

export interface AssignmentRuleDTO {
  id: number;
  name: string;
  sortOrder: number;
  matchCategory: string;
  matchPriority: string;
  assignToUserId: number;
  isActive: boolean;
  rowVersion: number;
}

export interface AssociateDivisionLegalEntityRequest {
  companyId: number;
  effectiveFrom?: string;
}

export interface AttachCRMContactRequest {
  contactId: number;
  roleId?: number | null;
  isPrimary?: boolean;
  startedOn?: string;
}

export interface AttachOpportunityCompetitorRequest {
  competitorCode: string;
  isWinner?: boolean;
  note?: string;
}

export interface AttachReceiptLineRequest {
  stockLedgerEntryId: number;
  weightKg?: string;
  volumeM3?: string;
  tariffClassificationId?: number | null;
  originCountry?: string;
}

export interface AttachReceiptsRequest {
  sourceDocType?: string;
  sourceDocId?: number;
  stockLedgerEntryIds?: number[];
  lines?: AttachReceiptLineRequest[];
}

export interface AttachmentDTO {
  id: number;
  module: string;
  entity: string;
  entityId: number;
  filename: string;
  mime: string;
  sizeBytes: number;
  sha256: string;
  note?: string;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
  portalVisible: boolean;
}

export interface AttachmentsDeleteQuery {
  rowVersion?: number;
}

export interface AttendanceDTO {
  id: number;
  employeeId: number;
  employeeName?: string;
  workDate: string;
  hoursWorked: string;
  overtimeHours: string;
  overtimeRuleId?: number | null;
  overtimeRuleCode?: string;
  source: string;
  approved: boolean;
  approvedBy?: number | null;
  approvedAt?: string | null;
  notes?: string;
  createdAt: string;
  rowVersion: number;
  rosterLineId?: number | null;
  clockInAt?: string | null;
  clockOutAt?: string | null;
  breakMinutes?: number;
  locationId?: number | null;
  overtimeBucket?: string;
}

export interface AttendanceExceptionDTO {
  id: number;
  employeeId: number;
  employeeNo?: string;
  businessDate: string;
  rosterLineId?: number | null;
  kind: string;
  expectedHours: string;
  observedHours: string;
  status: string;
  resolutionReasonCode?: string;
  resolutionNote?: string;
  resolvedBy?: number | null;
  resolvedAt?: string | null;
  attendanceId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface AttendanceLockDTO {
  id: number;
  companyId: number;
  payGroupId?: number | null;
  fromDate: string;
  toDate: string;
  lockedByRunId?: number | null;
  status: string;
  lockedAt: string;
  lockedBy?: number | null;
  releasedAt?: string | null;
  releasedBy?: number | null;
  releaseReasonCode?: string;
  releaseNote?: string;
  rowVersion: number;
}

export interface AttentionCounts {
  total: number;
  byKind: Record<string, number>;
  bySeverity: Record<string, number>;
  overdue: number;
}

export interface AttentionItemDTO {
  id: number;
  kind: 'approval' | 'exception' | 'task' | 'alert';
  sourceModule: string;
  sourceType: string;
  sourceId: number;
  companyId?: number | null;
  docType?: string;
  docId?: number | null;
  docNo?: string;
  amount?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency?: string;
  titleKey: string;
  titleArgs?: Record<string, unknown>;
  link?: string;
  priority: number;
  openedAt: string;
  dueAt?: string | null;
  escalatedAt?: string | null;
  status: 'open' | 'acted' | 'resolved' | 'expired';
  rowVersion: number;
  severity: 'info' | 'warning' | 'critical';
  explanationKey?: string;
  recommendedActionKey?: string;
  acknowledgedAt?: string | null;
  acknowledgedBy?: number | null;
  resolvedAt?: string | null;
  resolvedBy?: number | null;
  resolutionCode?: 'action_taken' | 'false_positive' | 'no_action_needed' | 'duplicate' | 'dismissed';
  resolutionOutcome?: string;
}

export interface AttentionItemResolveRequest {
  code: 'action_taken' | 'false_positive' | 'no_action_needed' | 'duplicate' | 'dismissed';
  outcome?: string;
}

export interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: User;
}

export interface AuthorisePOSOverrideRequest {
  ruleCode: string;
  docType: string;
  docId: number;
  docNo?: string;
  companyId?: number | null;
  amount?: string;
  currency?: string;
  detail?: string;
  reasonCode: string;
  note?: string;
  authorisedByUserId?: number | null;
}

export interface AuthoriseReopenRequest {
  decline?: boolean;
  declineReason?: string;
  rowVersion: number;
}

export interface AuthoriseReturnLineInput {
  lineNo: number;
  authorisedQuantity?: string;
}

export interface AuthoriseReturnRequest {
  rowVersion: number;
  expiresOn?: string;
  reasonCode?: string;
  lines?: AuthoriseReturnLineInput[];
}

export interface AuthorityAbsenceDTO {
  id: number;
  employeeId: number;
  employeeName?: string;
  fromDate: string;
  toDate: string;
  kind: string;
  source: string;
  sourceId?: number | null;
  reassignToPositionId?: number | null;
  reassignToPosition?: string;
  status: string;
  note?: string;
  rowVersion: number;
}

export interface AuthorityAbsenceRequest {
  employeeId: number;
  fromDate: string;
  toDate: string;
  kind?: string;
  reassignToPositionId?: number | null;
  note?: string;
}

export interface AuthorityAssessRequest {
  docType: string;
  companyId?: number | null;
  before: string;
  after: string;
  currency?: string;
  changedFields?: string[];
  category?: string;
  dimensions?: Record<string, string>;
  on?: string;
}

export interface AuthorityAssessment {
  material: boolean;
  reason: string;
  pctMove: string;
  absMove: string;
  field?: string;
  ruleBefore?: number | null;
  ruleAfter?: number | null;
  policyId?: number | null;
}

export interface AuthorityChainPreview {
  matched: boolean;
  source: string;
  versionId?: number | null;
  versionCode?: string;
  ruleId?: number | null;
  specificity: number;
  on: string;
  steps: AuthorityPreviewStep[];
  rejected: AuthorityRejectedRule[];
  problems: string[];
  candidates: AuthorityRuleSummaryVW[];
}

export interface AuthorityDelegationDTO {
  id: number;
  fromPositionId: number;
  fromPosition?: string;
  toPositionId?: number | null;
  toPosition?: string;
  toEmployeeId?: number | null;
  docType?: string;
  maxAmount?: string;
  currency?: string;
  effectiveFrom: string;
  effectiveTo: string;
  reasonCode: string;
  note?: string;
  status: string;
  revokeReasonCode?: string;
  revokedAt?: string | null;
  rowVersion: number;
}

export interface AuthorityPreviewRequest {
  docType: string;
  companyId?: number | null;
  amount?: string;
  currency?: string;
  category?: string;
  dimensions?: Record<string, string>;
  on?: string;
  submitterUserId?: number | null;
}

export interface AuthorityPreviewStep {
  stepNo: number;
  mode: string;
  name?: string;
  source: string;
  positionIds: number[];
  positions: string[];
  holderIds: number[];
  holders: string[];
  unresolvable?: string;
  slaHours?: number;
}

export interface AuthorityRejectedRule {
  ruleId: number;
  reason: string;
}

export interface AuthorityRuleDTO {
  id: number;
  versionId: number;
  docType: string;
  companyId?: number | null;
  scopeType: string;
  scopeId?: number | null;
  category?: string;
  currency?: string;
  minAmount?: string;
  maxAmount?: string;
  priority: number;
  specificity: number;
  isActive: boolean;
  note?: string;
  steps: AuthorityStepDTO[];
  rowVersion: number;
}

export interface AuthorityRuleRequest {
  versionId: number;
  docType: string;
  companyId?: number | null;
  scopeType?: string;
  scopeId?: number | null;
  category?: string;
  currency?: string;
  minAmount?: string;
  maxAmount?: string;
  priority?: number;
  isActive?: boolean | null;
  note?: string;
  rowVersion?: number;
}

export interface AuthorityRuleSummaryVW {
  ruleId: number;
  scopeType: string;
  scopeId?: number | null;
  category?: string;
  currency?: string;
  minAmount?: string;
  maxAmount?: string;
  specificity: number;
  priority: number;
}

export interface AuthorityStepDTO {
  stepNo: number;
  mode: string;
  name?: string;
  positionId?: number | null;
  positionName?: string;
  jobId?: number | null;
  minGradeLevel?: number | null;
  relativeTo?: string;
  relativeLevels?: number;
  slaHours?: number;
  escalateToPositionId?: number | null;
  allowSelfApproval?: boolean;
}

export interface AutoMatchCardStatementResponse {
  suggestions: CardMatchSuggestionDTO[];
}

export interface AutoMatchResultDTO {
  considered: number;
  matchedExactRef: number;
  matchedAmountDate: number;
}

export interface AwardRequest {
  vendorId: number;
  orderDate?: string;
  expectedDate?: string;
  rationale?: string;
  overrideReason?: string;
}

export interface BOMAlternateDTO {
  id: number;
  alternateItemId: number;
  quantityPer: string;
  priority: number;
  notes?: string;
}

export interface BOMAlternateInput {
  alternateItemId: number;
  quantityPer: string;
  priority?: number;
  notes?: string;
}

export interface BOMDTO {
  id: number;
  companyId: number;
  itemId: number;
  version: number;
  status: 'draft' | 'active' | 'obsolete';
  name?: string;
  notes?: string;
  lines: BOMLineDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface BOMExplosionLine {
  itemId: number;
  quantityPer: string;
  uomCode: string;
  fromBomLineId: number;
}

export interface BOMLineDTO {
  id: number;
  lineNo: number;
  componentItemId: number;
  quantityPer: string;
  uomCode: string;
  scrapPct: string;
  isPhantom: boolean;
  notes?: string;
  alternates?: BOMAlternateDTO[];
}

export interface BOMLineEffectivityDTO {
  id: number;
  bomLineId: number;
  ecoId?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  effectiveFromOrderId?: number | null;
  effectiveToOrderId?: number | null;
  quantityPer?: string | null;
  scrapPct?: string | null;
  isPhantom?: boolean | null;
  notes?: string;
  rowVersion: number;
}

export interface BOMLineEffectivityInput {
  effectiveFrom: string;
  effectiveTo?: string;
  effectiveFromOrderId?: number | null;
  effectiveToOrderId?: number | null;
  quantityPer?: string | null;
  scrapPct?: string | null;
  isPhantom?: boolean | null;
  notes?: string;
}

export interface BOMLineInput {
  componentItemId: number;
  quantityPer: string;
  uomCode: string;
  scrapPct?: string;
  isPhantom?: boolean;
  notes?: string;
  alternates?: BOMAlternateInput[];
}

export interface BOMOutputDTO {
  id: number;
  bomId: number;
  seqNo: number;
  itemId: number;
  kind: 'co_product' | 'by_product' | 'scrap_output';
  quantityPer: string;
  uomCode?: string;
  costSplitBasis: 'physical_quantity' | 'relative_value' | 'fixed_percent';
  costSharePercent?: string | null;
  standardCreditRate?: string | null;
  warehouseId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface BOMOutputInput {
  itemId: number;
  kind: string;
  quantityPer: string;
  uomCode?: string;
  costSplitBasis?: string;
  costSharePercent?: string | null;
  standardCreditRate?: string | null;
  warehouseId?: number | null;
  isActive?: boolean | null;
}

export interface BUFunctionDTO {
  functionCode: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface BUSecurityScopeDTO {
  principalId: number;
  scopeType: string;
  source: string;
  sourceRefId?: number | null;
  expiresAt?: string;
}

export interface BUSetAssignmentDTO {
  determinantType: string;
  referenceSetId: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface BackorderDTO {
  id: number;
  orderId: number;
  orderLineId: number;
  itemId: number;
  warehouseId: number;
  quantity: string;
  status: 'open' | 'fulfilled' | 'cancelled';
  origin: 'confirm' | 'shipment';
  promisedDate?: string | null;
  reasonCode?: string;
  createdAt: string;
}

export interface BalanceCheck {
  debit: string;
  credit: string;
  difference: string;
  balanced: boolean;
}

export interface BalanceSheetQuery {
  companyId: number;
  asOf: string;
  basis?: 'entry' | 'posting';
  compareTo?: '' | 'prior_period' | 'prior_year';
  compareThru?: string;
  includeZero?: boolean;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
}

export interface BalancingSegmentDTO {
  id: number;
  companyId: number;
  ledgerId: number;
  coaSegmentValueId: number;
  value?: string;
  isDerived: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface BalancingSegmentInput {
  ledgerId: number;
  coaSegmentValueId: number;
  isDerived?: boolean;
  effectiveFrom: string;
}

export interface BankAccountDTO {
  id: number;
  vendorId: number;
  label: string;
  bankName: string;
  branch: string;
  accountName: string;
  accountNoMasked: string;
  ibanMasked: string;
  swift: string;
  currency: string;
  isPrimary: boolean;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
  pendingChangeId?: number | null;
}

export interface BankAccountRequest {
  label?: string;
  bankName: string;
  branch?: string;
  accountName: string;
  accountNo: string;
  iban?: string;
  swift?: string;
  currency: string;
  isPrimary?: boolean;
  reason: string;
}

export interface BankBalanceObservationDTO {
  id: number;
  bankAccountId: number;
  bankAccountCode: string;
  bankAccountName: string;
  asOfDate: string;
  bookedBalance: string;
  valueDatedBalance?: string | null;
  availableBalance?: string | null;
  currency: string;
  source: string;
  sourceRef?: string;
  importId?: number | null;
  note?: string;
  observedAt: string;
  createdAt: string;
  rowVersion: number;
}

export interface BankChangeDTO {
  id: number;
  vendorId: number;
  bankAccountId?: number | null;
  action: 'create' | 'update' | 'deactivate';
  status: 'pending' | 'approved' | 'applied' | 'rejected' | 'cancelled';
  label: string;
  bankName: string;
  branch: string;
  accountName: string;
  accountNoMasked: string;
  ibanMasked: string;
  swift: string;
  currency?: string;
  isPrimary: boolean;
  reason: string;
  requestedBy?: number | null;
  requestedAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  appliedAt?: string | null;
  approvalRequestId?: number | null;
  effectiveAt?: string | null;
  coolingOffHours?: number;
  releasedEarlyBy?: number | null;
  releasedEarlyAt?: string | null;
  releasedEarlyReasonCode?: string;
  notifiedAt?: string | null;
}

export interface BankConnectorRefDTO {
  key: string;
  name: string;
  description?: string;
  canFetchStatements: boolean;
  canFetchBalances: boolean;
  configured: boolean;
  formats: string[];
}

export interface BankFileProfileDTO {
  id: number;
  code: string;
  name: string;
  bankCode: string;
  format: 'csv' | 'fixed_width';
  delimiter: string;
  includeHeader: boolean;
  lineEnding: string;
  fileExtension: string;
  columns: ColumnSpec[];
  notes: string;
  isActive: boolean;
  rowVersion: number;
}

export interface BankFileResponse {
  runId: number;
  runNo: string;
  profileCode: string;
  profileName: string;
  filename: string;
  contentType: string;
  content: string;
  sha256: string;
  rows: number;
  disclaimer: string;
}

export interface BankMandateDTO {
  id: number;
  bankAccountId: number;
  bankAccountCode?: string;
  bankAccountName?: string;
  companyId: number;
  code: string;
  name: string;
  signingRule: 'any' | 'a_plus_b' | 'all';
  requiredSignatures: number;
  minAmount: string;
  maxAmount?: string | null;
  currency?: string;
  documentRef?: string;
  attachmentId?: number | null;
  notes?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  approvalStatus: 'pending_approval' | 'approved';
  preparedBy?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  supersedesId?: number | null;
  signatories: BankMandateSignatoryDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface BankMandateSignatoryDTO {
  id: number;
  mandateId: number;
  userId?: number | null;
  positionId?: number | null;
  personName: string;
  panel: string;
  maxAmount?: string | null;
  specimenAttachmentId?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  canSignInApp: boolean;
  rowVersion: number;
}

export interface BankPOSDepositRequest {
  bankReference?: string;
  rowVersion: number;
}

export interface BankrecAutoMatchRequest {
  sessionId: number;
}

export interface BankrecAutoMatchResultDTO {
  statementLineId: number;
  threshold?: string | null;
  applied: boolean;
  reason?: string;
  match?: MatchDTO | null;
  candidates: MatchCandidateDTO[];
}

export interface BankrecCertificateDTO {
  id: number;
  sessionId: number;
  certificateNo: string;
  certifiedAt: string;
  certifiedBy?: number | null;
  certifiedByName?: string;
  status: string;
  supersededAt?: string | null;
  supersededByReopenId?: number | null;
  supersededReason?: string;
  balancePerBank?: string | null;
  unpresentedTotal: string;
  unrecordedTotal: string;
  balancePerLedger?: string | null;
  unmatchedCount: number;
  overrideReasonCode?: string;
  certificateSha256: string;
  matchedSetSha256: string;
  matchedSetCount: number;
}

export interface BankrecConnectionAccountDTO {
  bankAccountId: number;
  bankAccountCode?: string;
  bankAccountName?: string;
  precedence: number;
}

export interface BankrecConnectionAccountInput {
  bankAccountId: number;
  precedence?: number;
}

export interface BankrecConnectionCreateRequest {
  name: string;
  bankId: number;
  channel: 'manual_upload' | 'storage_inbox';
  endpoint?: string;
  credentialRef?: string;
  servicePrincipalId?: number | null;
  credentialRotatesOn?: string;
  accounts?: BankrecConnectionAccountInput[];
}

export interface BankrecConnectionDTO {
  id: number;
  name: string;
  bankId: number;
  bankName?: string;
  channel: 'manual_upload' | 'storage_inbox';
  endpoint: string;
  inboxPath?: string;
  credentialRef: string;
  servicePrincipalId?: number | null;
  servicePrincipalName?: string;
  unassignedFiles: number;
  servicePrincipalIsNamedUser: boolean;
  credentialRotatesOn?: string | null;
  daysToRotation?: number | null;
  status: 'draft' | 'active' | 'disabled';
  canFetch: boolean;
  lastTestAt?: string | null;
  lastTestResult: string;
  lastTestDetail: string;
  lastFetchAt?: string | null;
  lastFetchResult: string;
  lastFetchDetail: string;
  consecutiveFailures: number;
  health: 'inactive' | 'healthy' | 'degraded' | 'failing' | 'rotation_due';
  predictedRisk: 'unknown' | 'low' | 'elevated' | 'high';
  expectedBy?: string | null;
  predictionBasis: string;
  accounts: BankrecConnectionAccountDTO[];
  activatedAt?: string | null;
  disabledAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface BankrecConnectionFetchResult {
  connectionId: number;
  imported: number;
  duplicates: number;
  rejected: number;
  failed: number;
  settled: number;
  unassigned: number;
  failure?: string;
}

export interface BankrecConnectionHealthDTO {
  asOf: string;
  total: number;
  healthy: number;
  degraded: number;
  failing: number;
  rotationDue: number;
  inactive: number;
  items: BankrecConnectionHealthItemDTO[];
}

export interface BankrecConnectionHealthItemDTO {
  connectionId: number;
  name: string;
  bankName?: string;
  channel: 'manual_upload' | 'storage_inbox';
  status: 'draft' | 'active' | 'disabled';
  health: 'inactive' | 'healthy' | 'degraded' | 'failing' | 'rotation_due';
  consecutiveFailures: number;
  lastTestAt?: string | null;
  lastFetchAt?: string | null;
  lastDetail: string;
  credentialRotatesOn?: string | null;
  daysToRotation?: number | null;
  accountCount: number;
  predictedRisk: 'unknown' | 'low' | 'elevated' | 'high';
  expectedBy?: string | null;
  unassignedFiles: number;
}

export interface BankrecConnectionListQuery {
  cursor?: string;
  limit?: number;
  bankId?: number;
  status?: 'draft' | 'active' | 'disabled';
  channel?: 'manual_upload' | 'storage_inbox';
}

export interface BankrecConnectionTestDTO {
  connectionId: number;
  channel: 'manual_upload' | 'storage_inbox';
  result: string;
  detail: string;
  filesWaiting: number;
  filesUnassigned: number;
  testedAt: string;
}

export interface BankrecConnectionTransitionRequest {
  rowVersion: number;
  reason?: string;
}

export interface BankrecConnectionUpdateRequest {
  name?: string | null;
  endpoint?: string | null;
  credentialRef?: string | null;
  servicePrincipalId?: number | null;
  credentialRotatesOn?: string | null;
  accounts?: BankrecConnectionAccountInput[] | null;
  rowVersion: number;
}

export interface BankrecContinuityDTO {
  importId: number;
  bankAccountId: number;
  bankAccountName?: string;
  sequenceNo: string;
  priorImportId?: number | null;
  priorStatementNo?: string;
  priorClosingBalance?: string | null;
  openingBalance: string;
  closingBalance: string;
  continuityStatus: 'unchecked' | 'first' | 'ok' | 'balance_break' | 'sequence_gap' | 'overlap';
  continuityGap: string;
  continuityNote: string;
  acknowledgedBy?: number | null;
  acknowledgedByName?: string;
  acknowledgedAt?: string | null;
  reasonCode: string;
  rowVersion: number;
}

export interface BankrecDashboardAccountDTO {
  bankAccountId: number;
  companyId: number;
  bankAccountCode: string;
  bankAccountName: string;
  currency: string;
  state: 'in_progress' | 'reconciled' | 'never_reconciled';
  openSessionId?: number | null;
  lastReconciledTo?: string | null;
  daysSinceReconciled?: number | null;
  daysToReconcile?: string | null;
  completedSampleCount: number;
  unmatchedStatement: BankrecDashboardAgeingDTO;
  unmatchedLedger: BankrecDashboardAgeingDTO;
  openExceptions: number;
  investigatingExceptions: number;
  escalatedExceptions: number;
  unownedExceptions: number;
  staleExceptions: number;
}

export interface BankrecDashboardAgeingBucketDTO {
  bucket: string;
  count: number;
  amount: string;
}

export interface BankrecDashboardAgeingDTO {
  count: number;
  amount: string;
  buckets: BankrecDashboardAgeingBucketDTO[];
}

export interface BankrecDashboardDTO {
  asOf: string;
  lastSweepOn?: string | null;
  accounts: BankrecDashboardAccountDTO[];
  accountsInProgress: number;
  accountsNeverReconciled: number;
  openExceptions: number;
  escalatedExceptions: number;
  unownedExceptions: number;
  staleExceptions: number;
}

export interface BankrecDashboardQuery {
  companyId?: number;
  bankAccountId?: number;
}

export interface BankrecDeleteQuery {
  rowVersion?: number;
}

export interface BankrecExceptionAssignRequest {
  ownerUserId: number;
  note?: string;
  rowVersion: number;
}

export interface BankrecExceptionDTO {
  id: number;
  companyId: number;
  bankAccountId: number;
  bankAccountCode?: string;
  bankAccountName?: string;
  statementLineId?: number | null;
  ledgerLineId?: number | null;
  journalEntryId?: number | null;
  entryNo?: string;
  reference?: string;
  description?: string;
  kind: 'unmatched_bank' | 'unmatched_ledger' | 'unpresented_payment' | 'stale_deposit';
  direction: 'in' | 'out';
  amount: string;
  currency: string;
  itemDate: string;
  ageDays: number;
  openedOn: string;
  ownerUserId?: number | null;
  ownerName?: string;
  ownerSource: 'declared' | 'account' | 'assigned' | 'none';
  status: 'open' | 'investigating' | 'resolved' | 'written_off';
  escalationLevel: number;
  escalatedOn?: string | null;
  staleOn?: string | null;
  investigationNote?: string;
  investigatingBy?: number | null;
  investigatingAt?: string | null;
  resolutionCode?: string;
  resolutionNote?: string;
  resolutionMatchSetId?: number | null;
  resolvedBy?: number | null;
  resolvedAt?: string | null;
  reopenedCount: number;
  lastReopenedOn?: string | null;
  hypothesis?: 'timing_difference' | 'bank_charge' | 'duplicate' | 'fx_difference' | 'unrecorded_receipt' | 'unrecorded_payment' | 'unpresented_item';
  hypothesisBasis?: string;
  hypothesisConfidence?: string;
  hypothesisDecision?: 'accepted' | 'overridden';
  hypothesisDecisionCode?: 'timing_difference' | 'bank_charge' | 'duplicate' | 'fx_difference' | 'unrecorded_receipt' | 'unrecorded_payment' | 'unpresented_item';
  hypothesisOfferedCode?: 'timing_difference' | 'bank_charge' | 'duplicate' | 'fx_difference' | 'unrecorded_receipt' | 'unrecorded_payment' | 'unpresented_item';
  hypothesisDecidedBy?: number | null;
  hypothesisDecidedAt?: string | null;
  rowVersion: number;
}

export interface BankrecExceptionDetailDTO {
  exception: BankrecExceptionDTO;
  ledgerCandidates: MatchCandidateDTO[];
  statementCandidates: StatementLineDTO[];
  trail: BankrecExceptionTrailDTO[];
}

export interface BankrecExceptionInvestigateRequest {
  note: string;
  rowVersion: number;
  hypothesisDecision?: 'accepted' | 'overridden';
  hypothesisOverrideCode?: 'timing_difference' | 'bank_charge' | 'duplicate' | 'fx_difference' | 'unrecorded_receipt' | 'unrecorded_payment' | 'unpresented_item';
}

export interface BankrecExceptionListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  bankAccountId?: number;
  status?: 'open' | 'investigating' | 'resolved' | 'written_off';
  kind?: 'unmatched_bank' | 'unmatched_ledger' | 'unpresented_payment' | 'stale_deposit';
  ownerUserId?: number;
  minEscalationLevel?: number;
  unowned?: boolean;
}

export interface BankrecExceptionResolveRequest {
  resolutionCode: 'matched' | 'written_off' | 'reversed_at_source';
  note: string;
  matchId?: number | null;
  rowVersion: number;
}

export interface BankrecExceptionTrailDTO {
  at: string;
  action: string;
  actorId?: string;
  actorType: string;
  note?: string;
}

export interface BankrecForceMatchAuthoriseRequest {
  decline?: boolean;
  declineReason?: string;
  rowVersion: number;
}

export interface BankrecForceMatchDTO {
  id: number;
  sessionId: number;
  bankAccountId: number;
  statementLineId: number;
  ledgerLineIds: number[];
  statementAmount: string;
  ledgerTotal: string;
  difference: string;
  value: string;
  band: string;
  residualTolerance: string;
  requiresAuthorisation: boolean;
  reasonCode: string;
  reason: string;
  status: 'pending' | 'applied' | 'declined';
  requestedBy?: number | null;
  requestedByName?: string;
  requestedAt: string;
  decidedBy?: number | null;
  decidedByName?: string;
  decidedAt?: string | null;
  declineReason?: string;
  matchSetId?: number | null;
  rowVersion: number;
}

export interface BankrecForceMatchListQuery {
  cursor?: string;
  limit?: number;
  sessionId?: number;
  status?: 'pending' | 'applied' | 'declined';
}

export interface BankrecForceMatchRequest {
  sessionId: number;
  ledgerLineIds: number[];
  reasonCode: string;
  reason: string;
}

export interface BankrecImportDTO {
  id: number;
  companyId: number;
  bankAccountId: number;
  sourceFormat: 'csv' | 'mt940' | 'camt053';
  profileId?: number | null;
  filename: string;
  statementNo?: string;
  currency: string;
  openingBalance: string;
  openingDate: string;
  closingBalance: string;
  closingDate: string;
  lineCount: number;
  importedCount: number;
  duplicateCount: number;
  status: 'quarantined' | 'released' | 'accepted' | 'rejected';
  statementKind: 'end_of_day' | 'intraday';
  integrityStatus: 'verified' | 'unverified' | 'failed';
  fileSha256: string;
  quarantineReasons: BankrecStatementQuarantineReasonDTO[];
  duplicateOfImportId?: number | null;
  accountIdentifier?: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionReason?: string;
  lateAcknowledgedBy?: number | null;
  lateAcknowledgedAt?: string | null;
  lateAcknowledgement?: string;
  releaseProposedBy?: number | null;
  releaseProposedAt?: string | null;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface BankrecListImportsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  bankAccountId?: number;
  status?: 'quarantined' | 'released' | 'accepted' | 'rejected';
}

export interface BankrecListLinesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  importId?: number;
  bankAccountId?: number;
  status?: 'unmatched' | 'matched';
  from?: string;
  to?: string;
}

export interface BankrecMatchSetDTO {
  id: number;
  sessionId: number;
  bankAccountId: number;
  setNo: string;
  statementLineCount: number;
  journalLineCount: number;
  statementTotal: string;
  journalTotal: string;
  difference: string;
  status: 'active' | 'released';
  matchType: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  confidence: string;
  note?: string;
  matchedAt: string;
  matchedBy?: number | null;
  matchedByName?: string;
  releasedAt?: string | null;
  releasedBy?: number | null;
  releasedByName?: string;
  rowVersion: number;
}

export interface BankrecMatchSetDetailDTO {
  id: number;
  sessionId: number;
  bankAccountId: number;
  setNo: string;
  statementLineCount: number;
  journalLineCount: number;
  statementTotal: string;
  journalTotal: string;
  difference: string;
  status: 'active' | 'released';
  matchType: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  confidence: string;
  note?: string;
  matchedAt: string;
  matchedBy?: number | null;
  matchedByName?: string;
  releasedAt?: string | null;
  releasedBy?: number | null;
  releasedByName?: string;
  rowVersion: number;
  statementLineIds: number[];
  journalLineIds: number[];
}

export interface BankrecMatchSignalDTO {
  signal: string;
  score: string;
  weight: string;
  contribution: string;
  detail: string;
}

export interface BankrecMatchTierDTO {
  matchType: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  label: string;
  total: number;
  autoApplied: number;
  confirmed: number;
  forced: number;
  averageConfidence: string;
  minimumConfidence: string;
}

export interface BankrecMatchTiersQuery {
  bankAccountId?: number;
  sessionId?: number;
  from?: string;
  to?: string;
}

export interface BankrecMatchTiersReportDTO {
  autoApplyConfidence?: string | null;
  tiers: BankrecMatchTierDTO[];
  totalMatches: number;
  totalAutoApplied: number;
  autoAppliedShare: string;
}

export interface BankrecSessionAutoMatchSummaryDTO {
  sessionId: number;
  autoApplyEnabled: boolean;
  threshold?: string | null;
  examined: number;
  applied: number;
  skippedAmbiguous: number;
  belowThreshold: number;
  noCandidate: number;
  refused: number;
}

export interface BankrecSessionDTO {
  id: number;
  companyId: number;
  bankAccountId: number;
  fromDate: string;
  toDate: string;
  openingBalanceStatement: string;
  closingBalanceStatement?: string | null;
  closingBalanceLedger?: string | null;
  status: 'open' | 'closed';
  openedAt: string;
  openedBy?: number | null;
  closedAt?: string | null;
  closedBy?: number | null;
  reopenedAt?: string | null;
  reopenedBy?: number | null;
  note: string;
  totalStatementLines: number;
  matchedStatementLines: number;
  unmatchedStatementLines: number;
  legacyClosed: boolean;
  certificateCurrent: boolean;
  certifiedAt?: string | null;
  certifiedBy?: number | null;
  certificateNo?: string;
  unpresentedTotal?: string;
  unrecordedTotal?: string;
  unmatchedCount: number;
  pendingReopen: BankrecSessionReopenDTO | null;
  rowVersion: number;
}

export interface BankrecSessionReopenDTO {
  id: number;
  sessionId: number;
  reasonCode: string;
  reason: string;
  requestedBy?: number | null;
  requestedByName?: string;
  requestedAt: string;
  authorisedBy?: number | null;
  authorisedByName?: string;
  authorisedAt?: string | null;
  status: 'pending' | 'authorised' | 'declined';
  declineReason?: string;
  rowVersion: number;
}

export interface BankrecSessionReopenListQuery {
  cursor?: string;
  limit?: number;
  sessionId?: number;
  status?: 'pending' | 'authorised' | 'declined';
}

export interface BankrecSettingsDTO {
  id?: number | null;
  companyId: number;
  bankAccountId?: number | null;
  scope: string;
  autoApplyEnabled: boolean;
  autoApplyConfidence?: string | null;
  forceMatchBand: string;
  forceMatchResidualTolerance: string;
  ruleProposalAfter: number;
  rulePreviewLines: number;
  exceptionAgeDays: number;
  outstandingStalenessDays: number;
  escalationLadderDays: number[];
  exceptionPolicyScope: string;
  exceptionOwnerUserId?: number | null;
  exceptionOwnerScope?: string;
  rowVersion: number;
}

export interface BankrecSettingsQuery {
  companyId?: number;
  bankAccountId?: number;
}

export interface BankrecSettingsUpsertRequest {
  companyId: number;
  bankAccountId?: number | null;
  autoApplyConfidence?: string | null;
  forceMatchBand: string;
  forceMatchResidualTolerance?: string;
  ruleProposalAfter: number;
  rulePreviewLines: number;
  exceptionAgeDays?: number | null;
  outstandingStalenessDays?: number | null;
  escalationLadderDays?: number[];
  exceptionOwnerUserId?: number | null;
  rowVersion?: number;
}

export interface BankrecStandingRuleApproveRequest {
  rowVersion: number;
  note?: string;
}

export interface BankrecStandingRuleDTO {
  id: number;
  companyId: number;
  bankAccountId?: number | null;
  bankAccountName?: string;
  code: string;
  name: string;
  priority: number;
  direction?: string;
  bankTransactionCode?: string;
  descriptionPattern?: string;
  referencePattern?: string;
  minAmount?: string | null;
  maxAmount?: string | null;
  offsetAccountId: number;
  offsetAccountCode?: string;
  taxCode?: string;
  taxInclusive: boolean;
  costCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  descriptionTemplate?: string;
  actionMode: 'suggest' | 'auto';
  autoCeiling?: string | null;
  hitCount: number;
  lastHitAt?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  isActive: boolean;
  rowVersion: number;
  pendingApproval: boolean;
  source: string;
  proposalCount?: number;
  supersedesId?: number | null;
  counterpartyPattern?: string;
  preview?: BankrecStandingRulePreviewDTO | null;
  createdBy?: number | null;
  createdByName?: string;
  approvedBy?: number | null;
  approvedByName?: string;
  approvedAt?: string | null;
  declinedAt?: string | null;
  declineReason?: string;
}

export interface BankrecStandingRuleDeclineRequest {
  reason: string;
  rowVersion: number;
}

export interface BankrecStandingRuleHitDTO {
  statementLineId: number;
  importId: number;
  valueDate: string;
  direction: 'in' | 'out';
  reference: string;
  description: string;
  currency: string;
  grossAmount: string;
  taxAmount: string;
  netAmount: string;
  offsetAccountCode?: string;
  wouldAutoPost: boolean;
  blockedReason?: string;
}

export interface BankrecStandingRulePreviewDTO {
  previewedAt: string;
  windowLines: number;
  linesExamined: number;
  linesMatched: number;
  totalGross: string;
  summary: string;
}

export interface BankrecStatementGapDTO {
  id: number;
  companyId: number;
  bankAccountId: number;
  bankAccountName?: string;
  fromDate: string;
  toDate: string;
  kind: 'continuity' | 'sequence' | 'coverage' | 'stale';
  status: 'open' | 'resolved' | 'waived';
  detail: string;
  detectedBy?: number | null;
  detectedAt: string;
  sourceImportId?: number | null;
  connectionId?: number | null;
  reason?: string;
  resolvedBy?: number | null;
  resolvedAt?: string | null;
  resolvedByImportId?: number | null;
  waivedBy?: number | null;
  waivedAt?: string | null;
  waiveProposedBy?: number | null;
  waiveProposedAt?: string | null;
  rowVersion: number;
}

export interface BankrecStatementGapListQuery {
  cursor?: string;
  limit?: number;
  bankAccountId?: number;
  status?: 'open' | 'resolved' | 'waived';
  kind?: 'continuity' | 'sequence' | 'coverage' | 'stale';
}

export interface BankrecStatementGapResolveRequest {
  reason: string;
  importId: number | null;
}

export interface BankrecStatementGapWaiveRequest {
  reason: string;
}

export interface BankrecStatementLineDecisionDTO {
  line: StatementLineDTO;
  decision?: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  reason?: string;
  proposedBy?: number | null;
}

export interface BankrecStatementLineDecisionRequest {
  reason: string;
}

export interface BankrecStatementQuarantineReasonDTO {
  code: string;
  rule: string;
  message: string;
}

export interface BankrecStatementRejectRequest {
  reason: string;
}

export interface BankrecStatementReleaseRequest {
  reason: string;
}

export interface BankrecStatementStalenessDTO {
  bankAccountId: number;
  companyId: number;
  bankAccountCode: string;
  bankAccountName: string;
  lastStatementDate?: string | null;
  daysStale?: number | null;
  thresholdDays: number;
  stale: boolean;
  openGapCount: number;
  quarantinedCount: number;
  connectionId?: number | null;
}

export interface BankrecStatementStalenessQuery {
  thresholdDays?: number;
  bankAccountId?: number;
}

export interface BankrecStatementStalenessReportDTO {
  asOf: string;
  thresholdDays?: number;
  items: BankrecStatementStalenessDTO[];
}

export interface BeneficiaryChangeDTO {
  id: number;
  beneficiaryId?: number | null;
  companyId: number;
  action: 'create' | 'update' | 'deactivate';
  status: 'pending' | 'approved' | 'applied' | 'rejected';
  beneficiaryCode?: string;
  beneficiaryName?: string;
  changedFields: string[];
  touchesBankDetail: boolean;
  proposedAccountNumberMasked?: string;
  proposedIban?: string;
  proposedBankName?: string;
  requestedBy?: number | null;
  requestedByName?: string;
  requestedAt: string;
  decidedBy?: number | null;
  decidedByName?: string;
  decidedAt?: string | null;
  coolingOffHours: number;
  effectiveAt?: string | null;
  coolingOff: boolean;
  releasedEarlyBy?: number | null;
  releasedEarlyAt?: string | null;
  releasedEarlyReasonCode?: string;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface BeneficiaryRevealDTO {
  beneficiaryId: number;
  accountNumber: string;
  revealedAt: string;
}

export interface BeneficiaryVerificationDTO {
  id: number;
  beneficiaryId: number;
  method: 'penny' | 'callback' | 'document' | 'existing_relationship' | 'manual';
  outcome: 'pending' | 'passed' | 'failed';
  pennyAmount?: string | null;
  pennyCurrency?: string;
  pennyReference?: string;
  pennyTransferId?: number | null;
  pennyTransferStatus?: string;
  contactName?: string;
  contactNumberMasked?: string;
  contactSource?: string;
  changeId?: number | null;
  attachmentId?: number | null;
  note?: string;
  performedBy?: number | null;
  performedByName?: string;
  performedAt: string;
  rowVersion: number;
}

export interface BillLandedCostChargeRequest {
  apInvoiceLineId: number;
  rowVersion: number;
}

export interface BillProjectCostPlusRequest {
  through?: string;
  invoiceDate?: string;
  upliftPercent?: string;
  taxCode?: string;
}

export interface BillProjectRetainerRequest {
  periodStart: string;
  periodEnd?: string;
  amount?: string;
  invoiceDate?: string;
  taxCode?: string;
}

export interface BillServiceWorkOrderRequest {
  invoiceDate?: string;
  labourTaxCode?: string;
}

export interface BillTicketRequest {
  invoiceDate?: string;
  laborTaxCode?: string;
}

export interface BillTimeAndMaterialsRequest {
  through?: string;
  invoiceDate?: string;
  taxCode?: string;
}

export interface BillingDTO {
  id: number;
  projectId: number;
  method: 'fixed_price_milestone' | 'time_and_materials' | 'percent_complete' | 'cost_plus' | 'retainer';
  billedOn: string;
  amount: string;
  arInvoiceId: number;
  arInvoiceNo?: string;
  percentComplete?: string;
  costToDate?: string;
  note?: string;
  createdAt: string;
}

export interface BinDTO {
  id: number;
  warehouseId: number;
  code: string;
  name: string;
  isActive: boolean;
  zoneId?: number | null;
  storageClass: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  maxQty?: string | null;
  maxWeightKg?: string | null;
  maxVolumeM3?: string | null;
  binKind: 'storage' | 'picking' | 'bulk' | 'staging' | 'quarantine';
  pickSequence: number;
  isBlocked: boolean;
  blockedReason?: string;
  createdAt: string;
  rowVersion: number;
}

export interface BlockBeneficiaryRequest {
  block: boolean;
  reason: string;
  note?: string;
  rowVersion: number;
}

export interface BoardQuery {
  companyId: number;
  on?: string;
}

export interface BottleneckReportDTO {
  rows: BottleneckRowDTO[];
}

export interface BottleneckReportQuery {
  companyId: number;
  from: string;
  weeks?: number;
}

export interface BottleneckRowDTO {
  weekStart: string;
  workCenterId: number;
  workCenterCode: string;
  loadPct: string;
  runnerUpWorkCenterId?: number | null;
  runnerUpWorkCenterCode?: string;
  runnerUpLoadPct?: string;
}

export interface BrandingResponse {
  displayName: string;
  logoUrl?: string;
  color?: string;
  slug?: string;
}

export interface BucketItemsQuery {
  cursor?: string;
  limit?: number;
}

export interface BudgetAvailabilityDTO {
  companyId: number;
  accountId: number;
  costCenterId?: number | null;
  period: number;
  fiscalYear: number;
  budget: string;
  commitments: string;
  actuals: string;
  available: string;
  controlLevel: 'none' | 'advisory' | 'warn' | 'block';
  tolerancePct: string;
  controlSource: 'cost_center' | 'company_default';
  unbudgeted: boolean;
}

export interface BudgetAvailabilityQuery {
  companyId: number;
  accountId: number;
  costCenterId?: number | null;
  period: number;
}

export interface BudgetControlLevelChangeDTO {
  id: number;
  companyId: number;
  costCenterId?: number | null;
  costCenterCode?: string;
  costCenterName?: string;
  previousLevel: 'none' | 'advisory' | 'warn' | 'block';
  previousTolerancePct: string;
  previousSource: 'cost_center' | 'company_default';
  level: 'none' | 'advisory' | 'warn' | 'block';
  tolerancePct: string;
  previousNoLinePolicy: 'unlimited' | 'zero';
  noLinePolicy: 'unlimited' | 'zero';
  direction: 'loosen' | 'tighten';
  status: 'pending' | 'applied' | 'rejected';
  reason: string;
  requestedBy?: number | null;
  requestedAt: string;
  approvalRequestId?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  appliedAt?: string | null;
  rowVersion: number;
}

export interface BudgetControlLevelDTO {
  id: number;
  companyId: number;
  costCenterId?: number | null;
  costCenterCode?: string;
  costCenterName?: string;
  level: 'none' | 'advisory' | 'warn' | 'block';
  tolerancePct: string;
  source: 'cost_center' | 'company_default';
  rowVersion: number;
  updatedAt?: string | null;
}

export interface BudgetControlLevelSetRequest {
  companyId: number;
  costCenterId?: number | null;
  level: 'none' | 'advisory' | 'warn' | 'block';
  tolerancePct?: string;
  noLinePolicy?: 'unlimited' | 'zero';
  reason: string;
  rowVersion?: number;
}

export interface BudgetControlLevelsDTO {
  companyId: number;
  companyDefault: BudgetControlLevelDTO;
  noLinePolicy: 'unlimited' | 'zero';
  costCentres: BudgetControlLevelDTO[];
  pendingChanges: BudgetControlLevelChangeDTO[];
}

export interface BudgetControlLevelsQuery {
  companyId: number;
}

export interface BudgetCycleOwnerAssignRequest {
  costCenterId: number;
  ownerUserId: number;
}

export interface BudgetCycleOwnerReassignRequest {
  ownerUserId: number;
  rowVersion: number;
}

export interface BudgetCycleOwnerRemoveQuery {
  rowVersion: number;
}

export interface BudgetCycleOwnerReopenRequest {
  rowVersion: number;
}

export interface BudgetCycleOwnerSubmitRequest {
  rowVersion: number;
}

export interface BudgetCycleSubmissionDTO {
  id: number;
  modelId: number;
  costCenterId: number;
  costCenterCode: string;
  costCenterName: string;
  ownerUserId: number;
  ownerName: string;
  state: 'pending' | 'submitted';
  submittedAt?: string | null;
  rowVersion: number;
}

export interface BudgetCycleTransitionRequest {
  rowVersion: number;
}

export interface BudgetCycleVersionDTO {
  id: number;
  modelId: number;
  modelName: string;
  companyId: number;
  fiscalYear: number;
  versionNo: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'locked' | 'superseded';
  totalAmount: string;
  kind: 'budget' | 'forecast';
  submittedAt?: string | null;
  approvedAt?: string | null;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface BudgetCycleVersionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  fiscalYear?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'locked' | 'superseded';
  kind?: 'budget' | 'forecast';
}

export interface BudgetForecastReviseRequest {
  name?: string;
  note?: string;
  assumptions?: string;
  asOfDate?: string;
}

export interface BudgetForecastVsBudgetQuery {
  companyId: number;
  fiscalYear: number;
  forecastVersionId?: number | null;
  modelId?: number | null;
}

export interface BudgetForecastVsBudgetReportDTO {
  companyId: number;
  fiscalYear: number;
  forecast?: BudgetForecastVsBudgetVersionDTO | null;
  budgetVersionId?: number | null;
  baseline?: BudgetVersionBaselineDTO | null;
  rows: BudgetForecastVsBudgetRowDTO[];
  totalBudget: string;
  totalForecast: string;
  totalVariance: string;
  totalBaseline?: string;
  generatedAt: string;
}

export interface BudgetForecastVsBudgetRowDTO {
  accountId: number;
  accountCode: string;
  accountName: string;
  fiscalPeriodId: number;
  periodNo: number;
  periodName: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  budget: string;
  forecast: string;
  variance: string;
  variancePercent?: string;
  baseline?: string;
  changeSinceBaseline?: string;
  forecastDriverQty?: string;
  forecastDriverRate?: string;
  budgetDriverQty?: string;
  budgetDriverRate?: string;
  justification?: string;
  actualToDate: boolean;
}

export interface BudgetForecastVsBudgetVersionDTO {
  versionId: number;
  modelId: number;
  modelName: string;
  versionNo: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'locked' | 'superseded';
  kind: 'budget' | 'forecast';
  sourceVersionId?: number | null;
  asOf: string;
  assumptions: string;
}

export interface BudgetImportResult {
  valid: boolean;
  lines: BudgetLineDTO[];
  rowCount: number;
  total: string;
  errors: BudgetRowError[];
}

export interface BudgetIndicatorDTO {
  accountId: number;
  budget: string;
  available: string;
  requested: string;
  exceeded: boolean;
}

export interface BudgetLineDTO {
  id: number;
  versionId: number;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  fiscalPeriodId: number;
  periodNo?: number;
  periodName?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  amount: string;
  note: string;
  rowVersion: number;
  driverQty?: string;
  driverRate?: string;
  justification: string;
  actualToDate: boolean;
}

export interface BudgetListLinesQuery {
  cursor?: string;
  limit?: number;
  accountId?: number;
  fiscalPeriodId?: number;
}

export interface BudgetModelDTO {
  id: number;
  companyId: number;
  fiscalYear: number;
  name: string;
  purpose: string;
  createdAt: string;
  rowVersion: number;
  methodology: 'incremental' | 'zero_based' | 'driver_based' | 'rolling';
  submissionDeadline?: string | null;
  status: 'draft' | 'open' | 'closed';
  openedAt?: string | null;
  openedBy?: number | null;
  closedAt?: string | null;
}

export interface BudgetMovementKeyDTO {
  accountId: number;
  accountCode?: string;
  accountName?: string;
  fiscalPeriodId: number;
  periodNo?: number;
  periodName?: string;
  costCenterId?: number | null;
  costCenterCode?: string;
  costCenterName?: string;
  profitCenterId?: number | null;
  branchId?: number | null;
}

export interface BudgetMovementKeyInput {
  accountId: number;
  fiscalPeriodId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
}

export interface BudgetRowError {
  lineNo: number;
  column?: string;
  code: string;
  message: string;
}

export interface BudgetSettingsDTO {
  companyId: number;
  materialityPct?: string;
  materialityAmount?: string;
  isDefault: boolean;
  rowVersion: number;
  pending?: BudgetSettingsPendingDTO | null;
}

export interface BudgetSettingsPendingDTO {
  materialityPct?: string;
  materialityAmount?: string;
  requestedBy: number;
  requestedAt: string;
  approvalRequestId: number;
}

export interface BudgetSettingsQuery {
  companyId: number;
}

export interface BudgetSettingsSetRequest {
  companyId: number;
  materialityPct?: string;
  materialityAmount?: string;
  rowVersion?: number;
}

export interface BudgetSupplementaryDTO {
  id: number;
  requestNo: string;
  companyId: number;
  versionId: number;
  key: BudgetMovementKeyDTO;
  amount: string;
  justification: string;
  status: 'pending' | 'approved' | 'rejected';
  budgetBefore?: string | null;
  budgetAfter?: string | null;
  requestedBy?: number | null;
  requestedAt: string;
  approvalRequestId?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  failureCode?: string;
  appliedAt?: string | null;
  rowVersion: number;
}

export interface BudgetSupplementaryListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'pending' | 'approved' | 'rejected';
}

export interface BudgetSupplementaryRequest {
  companyId: number;
  versionId?: number | null;
  key: BudgetMovementKeyInput;
  amount: string;
  justification: string;
}

export interface BudgetTransferDTO {
  id: number;
  transferNo: string;
  companyId: number;
  versionId: number;
  from: BudgetMovementKeyDTO;
  to: BudgetMovementKeyDTO;
  amount: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  availableAtRequest: string;
  availableAtApproval?: string | null;
  fromBudgetBefore?: string | null;
  fromBudgetAfter?: string | null;
  toBudgetBefore?: string | null;
  toBudgetAfter?: string | null;
  requestedBy?: number | null;
  requestedAt: string;
  approvalRequestId?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  failureCode?: string;
  appliedAt?: string | null;
  rowVersion: number;
}

export interface BudgetTransferListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'pending' | 'approved' | 'rejected';
}

export interface BudgetTransferRegisterDTO {
  companyId: number;
  rows: BudgetTransferDTO[];
  count: number;
  approvedAmount: string;
  pendingAmount: string;
  rejectedAmount: string;
  truncated: boolean;
  generatedAt: string;
}

export interface BudgetTransferRegisterQuery {
  companyId: number;
  status?: 'pending' | 'approved' | 'rejected';
  costCenterId?: number;
  accountId?: number;
  fiscalYear?: number;
  limit?: number;
}

export interface BudgetTransferRequest {
  companyId: number;
  versionId?: number | null;
  from: BudgetMovementKeyInput;
  to: BudgetMovementKeyInput;
  amount: string;
  reason: string;
}

export interface BudgetVersionBaselineDTO {
  id: number;
  versionId: number;
  baselineNo: number;
  label: string;
  lineCount: number;
  totalAmount: string;
  takenAt: string;
  takenBy?: number | null;
}

export interface BudgetVersionBaselineRequest {
  label?: string;
}

export interface BudgetVersionCompareDTO {
  a: BudgetVersionCompareHeaderDTO;
  b: BudgetVersionCompareHeaderDTO;
  lines: BudgetVersionCompareLineDTO[];
  totalA: string;
  totalB: string;
  totalDelta: string;
  changedCount: number;
}

export interface BudgetVersionCompareHeaderDTO {
  versionId: number;
  modelId: number;
  modelName: string;
  fiscalYear: number;
  versionNo: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'locked' | 'superseded';
  totalAmount: string;
}

export interface BudgetVersionCompareLineDTO {
  accountId: number;
  accountCode: string;
  accountName: string;
  fiscalPeriodId: number;
  periodNo: number;
  periodName: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  amountA: string;
  amountB: string;
  delta: string;
  change: 'unchanged' | 'changed' | 'added' | 'removed';
}

export interface BudgetVersionCompareQuery {
  a: number;
  b: number;
}

export interface BudgetVersionDTO {
  id: number;
  modelId: number;
  versionNo: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'locked' | 'superseded';
  note: string;
  totalAmount: string;
  kind: 'budget' | 'forecast';
  sourceVersionId?: number | null;
  forecastAsOf?: string | null;
  assumptions: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  lockedAt?: string | null;
  supersededAt?: string | null;
  approvalRequestId?: number | null;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface BudgetVersionUpdateRequest {
  name?: string | null;
  note?: string | null;
  assumptions?: string | null;
  rowVersion: number;
}

export interface BudgetWarningDTO {
  accountId: number;
  budget: string;
  committed: string;
  actuals: string;
  available: string;
  requested: string;
}

export interface BuildDailyOperatingRequest {
  companyId: number;
  unitType: string;
  unitId: number;
  businessDate: string;
  force?: boolean;
  publish?: boolean;
}

export interface BuildPriceSetVersionRequest {
  itemIds?: number[];
  categoryCode?: string;
}

export interface BulkApproveRequest {
  ids: number[];
  comment?: string;
}

export interface BulkApproveResponse {
  results: BulkApproveResult[];
  approved: number;
  failed: number;
}

export interface BulkApproveResult {
  requestId: number;
  ok: boolean;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'voided';
  code?: string;
  message?: string;
}

export interface BusinessDay {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  calendarId: number;
  companyId: number;
  unitType: 'company' | 'branch' | 'location';
  unitId: number;
  businessDate: string;
  status: 'scheduled' | 'open' | 'closing' | 'closed' | 'reopened';
  fiscalPeriodId?: number | null;
  openedAt?: string | null;
  openedBy?: number | null;
  closingStartedAt?: string | null;
  closingStartedBy?: number | null;
  closedAt?: string | null;
  closedBy?: number | null;
  closeAttemptNo: number;
  closedForced: boolean;
  forceReasonCode?: string;
  reopenedAt?: string | null;
  reopenReasonCode?: string;
  reopenReason?: string;
  reopenAuthorisedBy?: number | null;
}

export interface BusinessDayDetailDTO {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  calendarId: number;
  companyId: number;
  unitType: 'company' | 'branch' | 'location';
  unitId: number;
  businessDate: string;
  status: 'scheduled' | 'open' | 'closing' | 'closed' | 'reopened';
  fiscalPeriodId?: number | null;
  openedAt?: string | null;
  openedBy?: number | null;
  closingStartedAt?: string | null;
  closingStartedBy?: number | null;
  closedAt?: string | null;
  closedBy?: number | null;
  closeAttemptNo: number;
  closedForced: boolean;
  forceReasonCode?: string;
  reopenedAt?: string | null;
  reopenReasonCode?: string;
  reopenReason?: string;
  reopenAuthorisedBy?: number | null;
  modules: BusinessDayModule[];
}

export interface BusinessDayModule {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  businessDayId: number;
  module: string;
  status: 'pending' | 'ready' | 'blocked' | 'complete';
  blockingCount: number;
  message?: string;
  detail?: Record<string, unknown>;
  assertedAt?: string | null;
  assertedBy?: number | null;
}

export interface BusinessFunction {
  code: string;
  nameKey: string;
  descriptionKey: string;
  module: string;
  owningPermission?: string;
}

export interface BusinessHourWindowInput {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

export interface BusinessUnitDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  status: string;
  primaryLegalEntityId?: number | null;
  defaultLocationId?: number | null;
  managerPositionId?: number | null;
  divisionId?: number | null;
  ledgerId?: number | null;
  activatedAt?: string;
  deactivatedOn?: string;
  functions: string[];
  referenceSetAssignments: BUSetAssignmentDTO[];
  rowVersion: number;
}

export interface BusinessUnitStep {
  legalEntityRef: string;
  code: string;
  name: string;
}

export interface CAPAActionDTO {
  id: number;
  capaId: number;
  seqNo: number;
  description: string;
  ownerUserId?: number | null;
  dueOn?: string;
  status: 'open' | 'done' | 'cancelled';
  completedAt?: string;
  completedBy?: number | null;
  completionNote: string;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface CAPADTO {
  id: number;
  capaNo?: string;
  companyId: number;
  title: string;
  description: string;
  kind: 'corrective' | 'preventive';
  severity: 'critical' | 'major' | 'minor';
  sourceKind: 'inspection' | 'ncr' | 'supplier_scorecard' | 'recall' | 'audit' | 'customer_complaint' | 'manual';
  sourceDocType?: string;
  sourceDocId?: number | null;
  vendorId?: number | null;
  itemId?: number | null;
  status: 'open' | 'investigating' | 'actions_planned' | 'actions_complete' | 'verifying' | 'closed' | 'cancelled';
  rootCause: string;
  ownerUserId?: number | null;
  dueOn?: string;
  effectivenessDueOn?: string;
  effectivenessMethod: string;
  effectivenessOutcome: '' | 'effective' | 'not_effective';
  effectivenessNote: string;
  verifiedBy?: number | null;
  verifiedAt?: string;
  closedAt?: string;
  cancelReason?: string;
  openActionCount: number;
  totalActionCount: number;
  rowVersion: number;
}

export interface CAPADetailDTO {
  id: number;
  capaNo?: string;
  companyId: number;
  title: string;
  description: string;
  kind: 'corrective' | 'preventive';
  severity: 'critical' | 'major' | 'minor';
  sourceKind: 'inspection' | 'ncr' | 'supplier_scorecard' | 'recall' | 'audit' | 'customer_complaint' | 'manual';
  sourceDocType?: string;
  sourceDocId?: number | null;
  vendorId?: number | null;
  itemId?: number | null;
  status: 'open' | 'investigating' | 'actions_planned' | 'actions_complete' | 'verifying' | 'closed' | 'cancelled';
  rootCause: string;
  ownerUserId?: number | null;
  dueOn?: string;
  effectivenessDueOn?: string;
  effectivenessMethod: string;
  effectivenessOutcome: '' | 'effective' | 'not_effective';
  effectivenessNote: string;
  verifiedBy?: number | null;
  verifiedAt?: string;
  closedAt?: string;
  cancelReason?: string;
  openActionCount: number;
  totalActionCount: number;
  rowVersion: number;
  actions: CAPAActionDTO[];
}

export interface COATemplate {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  description: string;
  country?: string | null;
  industry: string;
  isSystem: boolean;
}

export interface COATemplateLine {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  requiresCostCenter: boolean;
  requiresProfitCenter: boolean;
  requiresBranch: boolean;
  requiresDepartment: boolean;
  requiresProject: boolean;
  requiresDivision: boolean;
  templateId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentCode?: string | null;
  isPostable: boolean;
  currency?: string | null;
  description: string;
  sortOrder: number;
}

export interface CRMAccountContactDTO {
  id: number;
  accountId: number;
  contactId: number;
  contactName: string;
  roleId?: number | null;
  roleName?: string;
  isPrimary: boolean;
  startedOn?: string;
  endedOn?: string;
  rowVersion: number;
}

export interface CRMAccountDTO {
  id: number;
  accountNo?: string;
  partyId: number;
  name: string;
  parentId?: number | null;
  segmentId?: number | null;
  segmentCode?: string;
  ownerUserId?: number | null;
  ownerName?: string;
  accountType: string;
  status: string;
  industry: string;
  territory: string;
  website: string;
  employeeCount?: number | null;
  annualRevenue?: string;
  annualRevenueCurrency?: string;
  companyId?: number | null;
  notes: string;
  createdAt: string;
  rowVersion: number;
}

export interface CRMAccountDetailDTO {
  id: number;
  accountNo?: string;
  partyId: number;
  name: string;
  parentId?: number | null;
  segmentId?: number | null;
  segmentCode?: string;
  ownerUserId?: number | null;
  ownerName?: string;
  accountType: string;
  status: string;
  industry: string;
  territory: string;
  website: string;
  employeeCount?: number | null;
  annualRevenue?: string;
  annualRevenueCurrency?: string;
  companyId?: number | null;
  notes: string;
  createdAt: string;
  rowVersion: number;
  parentName?: string;
  childCount: number;
  contacts: CRMAccountContactDTO[];
}

export interface CRMAccountSegmentDTO {
  id: number;
  code: string;
  name: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface CRMAccountTreeNodeDTO {
  account: CRMAccountDTO;
  children: CRMAccountTreeNodeDTO[];
}

export interface CRMCloseReasonDTO {
  id: number;
  code: string;
  name: string;
  outcome: string;
  requiresCompetitor: boolean;
  requiresNote: boolean;
  sortOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface CRMCompetitorDTO {
  id: number;
  code: string;
  name: string;
  notes: string;
  isActive: boolean;
  rowVersion: number;
}

export interface CRMContactDTO {
  id: number;
  partyId: number;
  displayName: string;
  email?: string;
  phone?: string;
  jobTitle: string;
  department: string;
  reportsToContactId?: number | null;
  ownerUserId?: number | null;
  status: string;
  notes: string;
  createdAt: string;
  rowVersion: number;
}

export interface CRMContactRoleDTO {
  id: number;
  code: string;
  name: string;
  isDecisionMaker: boolean;
  isBilling: boolean;
  sortOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface CRMForecastCategoryDTO {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  countsInCommit: boolean;
  countsInBestCase: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface CRMForecastSnapshotDTO {
  id: number;
  snapshotOn: string;
  companyId?: number | null;
  ownerUserId?: number | null;
  periodCode: string;
  currency: string;
  pipelineAmount: string;
  bestCaseAmount: string;
  commitAmount: string;
  weightedAmount: string;
  closedWonAmount: string;
  opportunityCount: number;
  lines: ForecastSnapshotLine[];
  createdAt: string;
}

export interface CSATReport {
  from: string;
  to: string;
  responses: number;
  satisfied: number;
  satisfactionPercent: string;
}

export interface CSATRequest {
  satisfied: boolean;
}

export interface CSATTicketRequest {
  satisfied: boolean;
}

export interface CSVMapping {
  hasHeader: boolean;
  dateFormat: string;
  decimalSeparator?: string;
  thousandsSeparator?: string;
  amountMode: 'signed' | 'debit_credit';
  columns: Record<string, string>;
}

export interface CalcQuery {
  calendarId: number;
  from: string;
  days: number;
}

export interface CalcResult {
  isBusinessDay: boolean;
  next: string;
  endDate: string;
  count: number;
}

export interface CalculateCostRunRequest {
  cycleId: number;
  fiscalPeriodId: number;
  note?: string;
  recalculate?: boolean;
}

export interface CalculateECLRunRequest {
  companyId: number;
  asOf?: string;
}

export interface CalculateRunRequest {
  employeeIds?: number[];
  asAtDate?: string;
}

export interface Calendar {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  companyId: number;
  code: string;
  name: string;
  timezone: string;
  weekStart: number;
  workingDays: string;
  dayStart: string;
  isDefault: boolean;
  isActive: boolean;
}

export interface CalendarDTO {
  id: number;
  companyId: number;
  name: string;
  startMonth: number;
  frequency: string;
  adjustingPeriods: number;
  isActive: boolean;
  rowVersion: number;
}

export interface CalendarRequest {
  companyId: number;
  code: string;
  name: string;
  timezone?: string;
  weekStart?: number;
  workingDays?: string;
  dayStart?: string;
  isDefault?: boolean | null;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface CalendarStep {
  legalEntityRef: string;
  name: string;
  startMonth: number;
  generateYear?: number;
  periodCount?: number;
}

export interface CampaignAttributionDTO {
  id: number;
  campaignId: number;
  sendId?: number | null;
  partyId: number;
  objectType: string;
  objectId: number;
  touchAt: string;
  objectAt: string;
  windowDaysApplied: number;
  amount: string;
  currency?: string;
  baseAmount: string;
  baseCurrency?: string;
  recordedByRule: string;
  share?: string;
  createdAt: string;
}

export interface CampaignAttributionQuery {
  model?: string;
}

export interface CampaignAttributionReportDTO {
  campaignId: number;
  model: string;
  totalAttributed: string;
  baseCurrency?: string;
  touches: CampaignAttributionDTO[];
}

export interface CampaignROIDTO {
  campaignId: number;
  model: string;
  attributedRevenue: string;
  budget: string;
  net: string;
  roiPercent: string;
  currency?: string;
}

export interface CampaignRecipientDTO {
  id: number;
  sendId: number;
  campaignId: number;
  partyId: number;
  partyName?: string;
  state: string;
  exclusionReason?: string;
  consentId?: number | null;
  suppressionId?: number | null;
  destinationMasked?: string;
  sentAt?: string;
  failedReason?: string;
  createdAt: string;
}

export interface CampaignResponseDTO {
  id: number;
  campaignId: number;
  sendId?: number | null;
  partyId: number;
  partyName?: string;
  kind: string;
  respondedAt: string;
  respondedOn: string;
  note?: string;
  leadId?: number | null;
  opportunityId?: number | null;
  caseId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface CampaignSendDTO {
  id: number;
  campaignId: number;
  segmentId: number;
  sendNo: number;
  status: string;
  evaluatedAt?: string;
  evaluatedOn?: string;
  audienceTotal: number;
  sentCount: number;
  excludedCount: number;
  failedCount: number;
  isTest: boolean;
  startedAt?: string;
  completedAt?: string;
  exclusionBreakdown?: Record<string, number>;
  rowVersion: number;
}

export interface CancelCountRequest {
  reason?: string;
  note?: string;
  rowVersion: number;
}

export interface CancelCountSessionRequest {
  reason?: string;
}

export interface CancelEAMPermitRequest {
  reasonCode: string;
  note?: string;
}

export interface CancelECORequest {
  reason: string;
}

export interface CancelInstructionRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface CancelLeaveRequestRequest {
  rowVersion: number;
}

export interface CancelOrderRequest {
  reason: string;
}

export interface CancelPMSKeyIssueRequest {
  note?: string;
}

export interface CancelPMSReservationRequest {
  rowVersion: number;
  reason: string;
  note?: string;
}

export interface CancelPMSServiceBookingRequest {
  reason?: string;
  rowVersion: number;
}

export interface CancelPartyMergeRequest {
  reason: string;
}

export interface CancelPaymentRunRequest {
  reason: string;
}

export interface CancelProductionOrderRequest {
  reason: string;
}

export interface CancelRecallRequest {
  reason: string;
}

export interface CancelRequest {
  reason: string;
}

export interface CancelRequisitionRequest {
  reason?: string;
}

export interface CancelRosterRequest {
  rowVersion: number;
  reasonCode: string;
  note?: string;
}

export interface CancelRunRequest {
  reason: string;
}

export interface CancelServiceWorkOrderRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface CancelShipmentRequest {
  reason?: string;
}

export interface CancelSubscriptionRequest {
  reason: string;
  atPeriodEnd: boolean;
}

export interface CancelTransferRequest {
  reason?: string;
}

export interface CandidateDTO {
  id: number;
  candidateNo: string;
  requisitionId?: number | null;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  phone?: string;
  source?: string;
  stage: string;
  rating?: number | null;
  notes?: string;
  convertedEmployeeId?: number | null;
  convertedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface CandidateReferenceDTO {
  id: number;
  candidateId: number;
  refereeName: string;
  refereeOrganisation?: string;
  refereeContactMasked?: string;
  relationship?: string;
  status: string;
  outcome?: string;
  response?: string;
  requestedAt?: string | null;
  receivedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface CandidateScorecardDTO {
  id: number;
  candidateId: number;
  stage: string;
  interviewerUserId: number;
  criteria: ScorecardCriterionScore[];
  overallScore?: number | null;
  recommendation: string;
  note?: string;
  submittedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface CandidateStageEventDTO {
  id: number;
  stage: string;
  note?: string;
  actorId?: number | null;
  createdAt: string;
}

export interface CapacityLine {
  workCenterId: number;
  workCenterCode: string;
  weekStart: string;
  plannedHours: string;
  capacityHours: string;
  loadPct: string;
}

export interface CapacityReportDTO {
  lines: CapacityLine[];
}

export interface CapacityReportQuery {
  companyId: number;
  weekStart: string;
  weeks?: number;
}

export interface CapitalizableLineDTO {
  invoiceLineId: number;
  invoiceId: number;
  invoiceNo?: string;
  vendorInvoiceNo: string;
  companyId: number;
  invoiceDate: string;
  description: string;
  lineAmount: string;
  accountId: number;
  accountCode?: string;
}

export interface CapitalizeRequest {
  classId: number;
  sourceKind?: 'purchase' | 'project' | 'cip';
  invoiceLineId?: number;
  sourceId?: number;
  name: string;
  description?: string;
  location?: string;
  custodian?: string;
  branchId?: number | null;
  costCenterId?: number | null;
  serialNo?: string;
  tagNo?: string;
  insurancePolicy?: string;
  insuranceExpiry?: string;
  warrantyExpiry?: string;
  sourceRef?: string;
  method?: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths?: number;
  salvageValue?: string;
  cost?: string;
  inServiceDate?: string;
}

export interface CaptureConsentRequest {
  partyId: number;
  channelCode: string;
  purposeCode: string;
  state?: string;
  basis?: string;
  effectiveFrom?: string;
  expiresOn?: string;
  source?: string;
  capturedAt?: string;
  capturedIp?: string;
  capturedUserAgent?: string;
  evidenceRef?: string;
  evidenceText?: string;
  evidenceAttachmentId?: number | null;
}

export interface CapturePOSSerialsRequest {
  serialNos: string[];
  warrantyMonths?: number | null;
}

export interface CardAccountDTO {
  id: number;
  companyId: number;
  code: string;
  issuer: string;
  maskedPan: string;
  holderEmployeeId?: number | null;
  holderName?: string;
  currency: string;
  clearingAccountId: number;
  issuerVendorId?: number | null;
  isActive: boolean;
  creditLimit?: string;
  rowVersion: number;
}

export interface CardMatchSuggestionDTO {
  cardLineId: number;
  claimLineId: number;
  kind: string;
  confidence: string;
}

export interface CardStatementDTO {
  id: number;
  cardAccountId: number;
  cardAccountCode?: string;
  companyId: number;
  periodFrom: string;
  periodTo: string;
  currency: string;
  openingBalance: string;
  closingBalance: string;
  totalCharges: string;
  totalCredits: string;
  lineCount: number;
  matchedCount: number;
  status: string;
  importedAt?: string;
  attachmentId?: number | null;
  closedAt?: string;
  apInvoiceId?: number | null;
  rowVersion: number;
}

export interface CardStatementLineDTO {
  id: number;
  statementId: number;
  lineNo: number;
  transactedOn: string;
  merchant?: string;
  reference?: string;
  amount: string;
  currency: string;
  status: string;
  claimLineId?: number | null;
  matchConfidence?: string;
  matchKind?: string;
  disposition?: string;
  reasonCode?: string;
  note?: string;
  matchedAt?: string;
  rowVersion: number;
}

export interface CarryForwardRunRequest {
  on?: string;
}

export interface CarryForwardRunResult {
  leaveYear: number;
  carriedCount: number;
  lapsedCount: number;
  expiredCount: number;
  skippedCount: number;
  generatedAt: string;
}

export interface CaseClockEventDTO {
  id: number;
  kind: string;
  occurredAt: string;
  reasonCode: string;
  note?: string;
  elapsedMinutes?: number | null;
  correctsEventId?: number | null;
  createdAt: string;
  createdBy?: number | null;
}

export interface CaseClockTrailDTO {
  events: CaseClockEventDTO[];
  escalations: CaseEscalationDTO[];
}

export interface CaseDTO {
  id: number;
  caseNo: string;
  subject: string;
  description: string;
  origin: string;
  category: string;
  priority: string;
  status: string;
  customerId?: number | null;
  customerName?: string;
  companyId?: number | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  assignedTo?: number | null;
  assignedName?: string;
  slaPolicyId?: number | null;
  slaPolicyName?: string;
  responseDueAt?: string | null;
  resolutionDueAt?: string | null;
  firstResponseAt?: string | null;
  responseBreachedAt?: string | null;
  resolutionBreachedAt?: string | null;
  escalatedAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  reopenedCount: number;
  csatSatisfied?: boolean | null;
  csatSubmittedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface CaseEscalationDTO {
  id: number;
  level: number;
  which: string;
  firedAt: string;
  notifiedUserId?: number | null;
}

export interface CaseSLAStateDTO {
  caseId: number;
  caseNo: string;
  status: string;
  priority: string;
  pausedAt?: string | null;
  pausedMinutesTotal: number;
  responseDueAt?: string | null;
  resolutionDueAt?: string | null;
  responseBreachedAt?: string | null;
  resolutionBreachedAt?: string | null;
  escalationLevel: number;
  predictedBreachAt?: string | null;
  rowVersion: number;
}

export interface CaseVolumeReport {
  from: string;
  to: string;
  casesCreated: number;
  casesResolved: number;
  casesClosed: number;
  responseSlaTotal: number;
  responseSlaMet: number;
  responseCompliancePercent: string;
  resolutionSlaTotal: number;
  resolutionSlaMet: number;
  resolutionCompliancePercent: string;
  avgFirstResponseMinutes?: number | null;
  avgResolutionMinutes?: number | null;
}

export interface CashBankAccountActivateRequest {
  rowVersion: number;
}

export interface CashBankAccountAmendmentCreateRequest {
  bankId?: number | null;
  bankName?: string | null;
  branchName?: string | null;
  iban?: string | null;
  swift?: string | null;
  accountNumber?: string | null;
  reason: string;
}

export interface CashBankAccountAmendmentDTO {
  id: number;
  bankAccountId: number;
  status: 'pending' | 'approved' | 'rejected';
  bankId?: number | null;
  bankName?: string | null;
  branchName?: string | null;
  iban?: string | null;
  swift?: string | null;
  accountNumberMasked?: string | null;
  changesAccountNumber: boolean;
  reason: string;
  requestedBy?: number | null;
  requestedAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionReason: string;
  rowVersion: number;
}

export interface CashBankAccountAmendmentDecideRequest {
  decision: string;
  reason: string;
  rowVersion: number;
}

export interface CashBankAccountCloseAuthoriseRequest {
  rowVersion: number;
}

export interface CashBankAccountCloseCreateRequest {
  reason: string;
  rowVersion: number;
}

export interface CashBankAccountCloseRequestDTO {
  id: number;
  bankAccountId: number;
  status: 'requested' | 'authorised' | 'cancelled';
  reason: string;
  requestedBy?: number | null;
  requestedAt: string;
  authorisedBy?: number | null;
  authorisedAt?: string | null;
  rowVersion: number;
}

export interface CashBankAccountDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  glAccountId: number;
  glAccount?: string;
  kind: 'bank' | 'cash';
  currency: string;
  bankName: string;
  branchName: string;
  accountNumberMasked: string;
  hasAccountNumber: boolean;
  iban: string;
  swift: string;
  openingBalance: string;
  openingDate?: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
  status: 'draft' | 'pending_approval' | 'active' | 'suspended' | 'dormant' | 'closing' | 'closed';
  statusReason: string;
  submittedBy?: number | null;
  submittedAt?: string | null;
  activatedBy?: number | null;
  activatedAt?: string | null;
  closedAt?: string | null;
  verificationStatus: 'unverified' | 'verified' | 'failed';
  verifiedBy?: number | null;
  verifiedAt?: string | null;
  verificationChannel: string;
  verificationReference: string;
  coolingOffUntil?: string | null;
  liquidityClass: 'unrestricted' | 'restricted' | 'pledged' | 'escrowed' | 'trapped';
  valueDateToleranceDays: number;
  dormancyDays: number;
  statementStalenessDays: number;
  lastMovementOn?: string | null;
  bankId?: number | null;
}

export interface CashBankAccountReactivateRequest {
  rowVersion: number;
}

export interface CashBankAccountSubmitRequest {
  rowVersion: number;
}

export interface CashBankAccountSuspendRequest {
  reason: string;
  rowVersion: number;
}

export interface CashBankAccountVerifyRequest {
  channel: string;
  reference: string;
  rowVersion: number;
}

export interface CashBankChargeCreateRequest {
  bankAccountId: number;
  feeType: string;
  chargeDate: string;
  quantity?: number | null;
  basisAmount?: string;
  amount: string;
  reference?: string;
  note?: string;
}

export interface CashBankChargeDTO {
  id: number;
  bankAccountId: number;
  bankAccountCode?: string;
  bankId: number;
  feeType: string;
  chargeDate: string;
  currency: string;
  quantity: number;
  basisAmount: string;
  amount: string;
  expectedAmount?: string | null;
  variance?: string | null;
  reference: string;
  note: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface CashBankChargeListQuery {
  cursor?: string;
  limit?: number;
  bankId?: number;
  bankAccountId?: number;
  feeType?: string;
  from?: string;
  to?: string;
}

export interface CashBankChargesVarianceLineDTO {
  bankId: number;
  bankName: string;
  bankAccountId: number;
  bankAccountCode: string;
  feeType: string;
  currency: string;
  chargeCount: number;
  charged: string;
  expected: string;
  variance: string;
  untariffedCount: number;
  untariffedCharged: string;
  status: string;
}

export interface CashBankChargesVarianceQuery {
  from: string;
  to: string;
  bankId?: number;
  bankAccountId?: number;
  feeType?: string;
}

export interface CashBankChargesVarianceReportDTO {
  from: string;
  to: string;
  lines: CashBankChargesVarianceLineDTO[];
  totals: CashBankChargesVarianceTotalDTO[];
  generatedAt: string;
}

export interface CashBankChargesVarianceTotalDTO {
  currency: string;
  charged: string;
  expected: string;
  variance: string;
  untariffedCharged: string;
}

export interface CashBankConcentrationLineDTO {
  bankId: number;
  bankName: string;
  accountCount: number;
  balance: string;
  balanceSharePercent: string;
  facilityCount: number;
  facilityLimit: string;
  facilityDrawn: string;
  facilitySharePercent: string;
  limitAmount?: string | null;
  limitCurrency?: string;
  limitUtilisationPercent?: string | null;
  breached: boolean;
}

export interface CashBankConcentrationQuery {
  currency?: string;
  asOf?: string;
}

export interface CashBankConcentrationReportDTO {
  asOf: string;
  reportingCurrency: string;
  totalBalance: string;
  totalFacilityLimit: string;
  totalFacilityDrawn: string;
  facilityExposureAvailable: boolean;
  lines: CashBankConcentrationLineDTO[];
  unconvertedCurrencies: string[];
  generatedAt: string;
}

export interface CashBankContactCreateRequest {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  isPrimary?: boolean;
  notes?: string;
}

export interface CashBankContactDTO {
  id: number;
  bankId: number;
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  notes: string;
  rowVersion: number;
}

export interface CashBankContactListQuery {
  cursor?: string;
  limit?: number;
}

export interface CashBankContactUpdateRequest {
  name?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary?: boolean | null;
  notes?: string | null;
  rowVersion: number;
}

export interface CashBankCreateRequest {
  name: string;
  bic?: string;
  countryCode?: string;
}

export interface CashBankDTO {
  id: number;
  name: string;
  bic: string;
  countryCode: string;
  createdAt: string;
  rowVersion: number;
}

export interface CashBankLimitChangeDTO {
  id: number;
  bankId: number;
  bankName?: string;
  currency: string;
  previousAmount?: string | null;
  proposedAmount?: string | null;
  direction: 'set' | 'lower' | 'raise' | 'clear';
  status: 'pending' | 'applied' | 'rejected' | 'cancelled';
  reason: string;
  approvalRequestId?: number | null;
  requestedBy: number;
  requestedAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  approverUserIds: number[];
  rowVersion: number;
}

export interface CashBankLimitChangeListQuery {
  cursor?: string;
  limit?: number;
  status?: 'pending' | 'applied' | 'rejected' | 'cancelled';
}

export interface CashBankLimitChangeRequest {
  amount?: string;
  currency?: string;
  clear?: boolean;
  reason: string;
}

export interface CashBankLimitDTO {
  currency: string;
  amount: string;
  balance: string;
  headroom: string;
  utilisationPercent: string;
  breached: boolean;
  breachedSince?: string | null;
  unconvertedCurrencies: string[];
  pending?: CashBankLimitChangeDTO | null;
  rowVersion: number;
}

export interface CashBankLimitWithdrawRequest {
  note: string;
}

export interface CashBankListQuery {
  cursor?: string;
  limit?: number;
  q?: string;
}

export interface CashBankRelationshipAccountDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  currency: string;
  status: 'draft' | 'pending_approval' | 'active' | 'suspended' | 'dormant' | 'closing' | 'closed';
  balance: string;
  reportingCurrency: string;
  balanceReporting?: string | null;
}

export interface CashBankRelationshipAccountListQuery {
  cursor?: string;
  limit?: number;
  currency?: string;
  asOf?: string;
}

export interface CashBankRelationshipDTO {
  id: number;
  name: string;
  bic: string;
  countryCode: string;
  rowVersion: number;
  contactCount: number;
  primaryContact?: CashBankContactDTO | null;
  tariffCount: number;
  accountCount: number;
  activeAccountCount: number;
  reportingCurrency: string;
  balance: string;
  balanceSharePercent: string;
  facilityExposureAvailable: boolean;
  facilityCount: number;
  facilityLimit: string;
  facilityDrawn: string;
  facilityUndrawn: string;
  facilitySharePercent: string;
  limit?: CashBankLimitDTO | null;
  unconvertedCurrencies: string[];
}

export interface CashBankRelationshipFacilityDTO {
  facilityId: number;
  facilityCode: string;
  currency: string;
  facilityLimit: string;
  facilityDrawn: string;
  facilityUndrawn: string;
  reportingCurrency: string;
  limitReporting?: string | null;
  drawnReporting?: string | null;
}

export interface CashBankRelationshipFacilityListQuery {
  cursor?: string;
  limit?: number;
  currency?: string;
  asOf?: string;
}

export interface CashBankRelationshipGetQuery {
  currency?: string;
  asOf?: string;
}

export interface CashBankRelationshipListQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  currency?: string;
  asOf?: string;
}

export interface CashBankTariffCreateRequest {
  feeType: string;
  description?: string;
  basis: 'flat' | 'per_item' | 'percent';
  currency: string;
  amount?: string;
  ratePercent?: string;
  minAmount?: string;
  maxAmount?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CashBankTariffDTO {
  id: number;
  bankId: number;
  feeType: string;
  description: string;
  basis: 'flat' | 'per_item' | 'percent';
  currency: string;
  amount: string;
  ratePercent: string;
  minAmount?: string | null;
  maxAmount?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  createdBy?: number | null;
  rowVersion: number;
}

export interface CashBankTariffListQuery {
  cursor?: string;
  limit?: number;
  feeType?: string;
  inForceOn?: string;
}

export interface CashBankTariffUpdateRequest {
  description?: string | null;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface CashBankUpdateRequest {
  name?: string | null;
  bic?: string | null;
  countryCode?: string | null;
  rowVersion: number;
}

export interface CashDeleteQuery {
  rowVersion?: number;
}

export interface CashExchangeControlRuleDTO {
  id: number;
  countryCode: string;
  currency?: string;
  rule: string;
  threshold?: string | null;
  description?: string;
  active: boolean;
}

export interface CashExchangeControlRuleInput {
  countryCode: string;
  currency?: string;
  rule: string;
  threshold?: string;
  description?: string;
  active?: boolean | null;
}

export interface CashExchangeControlRulesRequest {
  expectedSetVersion: number;
  rules: CashExchangeControlRuleInput[];
}

export interface CashExchangeControlSetDTO {
  setVersion: number;
  rules: CashExchangeControlRuleDTO[];
}

export interface CashForecastAccuracyDTO {
  runId: number;
  bucketId: number;
  horizonDays: number;
  measuredOn: string;
  forecastClosing: string;
  actualClosing: string;
  errorAmount: string;
  actualPositionRunId?: number | null;
  absolutePctError: string | null;
}

export interface CashForecastAccuracyReportDTO {
  companyId: number;
  from?: string;
  to?: string;
  baseCurrency: string;
  bands: ForecastAccuracyBandDTO[];
  rows: CashForecastAccuracyDTO[];
  overallMape: string | null;
  measuredCount: number;
  undefinedCount: number;
}

export interface CashForecastAdjustment {
  date: string;
  amount: string;
  direction: string;
  description?: string;
}

export interface CashForecastBucketDTO {
  id: number;
  seqNo: number;
  granularity: 'day' | 'week' | 'month';
  fromDate: string;
  toDate: string;
  opening: string;
  inflows: string;
  outflows: string;
  net: string;
  closing: string;
  belowMinimum: boolean;
}

export interface CashForecastItemDTO {
  id: number;
  source: string;
  direction: string;
  documentType?: string;
  documentId?: number | null;
  reference?: string;
  counterparty?: string;
  description?: string;
  dueDate: string;
  adjustedDate?: string | null;
  currency: string;
  amount: string;
  fxRate: string;
  baseAmount: string;
  adjustmentAmount: string;
  overdue: boolean;
  sourceClass: 'open_item' | 'commitment' | 'calendar' | 'contract' | 'modelled';
  confidence: string;
  lineClass: 'sourced' | 'modelled' | 'override';
  drivers: TreasuryForecastDriverDTO[];
  overrideId?: number | null;
  originalAmount?: string | null;
}

export interface CashForecastRunDTO {
  id: number;
  companyId: number;
  companyName?: string;
  scenarioId?: number | null;
  scenarioName?: string;
  asOfDate: string;
  version: number;
  supersedesRunId?: number | null;
  isCurrent: boolean;
  status: 'draft' | 'published' | 'superseded';
  horizonWeeks: number;
  dailyDays: number;
  baseCurrency: string;
  horizonKind: 'operational' | 'strategic';
  horizonMonths: number;
  method: string;
  granularity: string;
  degraded: boolean;
  degradedInputs: TreasuryForecastDegradedInputDTO[];
  openingBalance: string;
  closingBalance: string;
  totalInflows: string;
  totalOutflows: string;
  minClosingBalance: string;
  minClosingOn?: string | null;
  liquidityGap: string;
  minimumBalance: string;
  openingPositionRunId?: number | null;
  contentHash?: string;
  origin: string;
  publishedAt?: string | null;
  publishedBy?: number | null;
  publishedByName?: string;
  revisionReason?: string;
  alreadyPublished?: boolean;
  sources: CashForecastSourceDTO[];
  published: boolean;
  rowVersion: number;
}

export interface CashForecastRunDetailDTO {
  id: number;
  companyId: number;
  companyName?: string;
  scenarioId?: number | null;
  scenarioName?: string;
  asOfDate: string;
  version: number;
  supersedesRunId?: number | null;
  isCurrent: boolean;
  status: 'draft' | 'published' | 'superseded';
  horizonWeeks: number;
  dailyDays: number;
  baseCurrency: string;
  horizonKind: 'operational' | 'strategic';
  horizonMonths: number;
  method: string;
  granularity: string;
  degraded: boolean;
  degradedInputs: TreasuryForecastDegradedInputDTO[];
  openingBalance: string;
  closingBalance: string;
  totalInflows: string;
  totalOutflows: string;
  minClosingBalance: string;
  minClosingOn?: string | null;
  liquidityGap: string;
  minimumBalance: string;
  openingPositionRunId?: number | null;
  contentHash?: string;
  origin: string;
  publishedAt?: string | null;
  publishedBy?: number | null;
  publishedByName?: string;
  revisionReason?: string;
  alreadyPublished?: boolean;
  sources: CashForecastSourceDTO[];
  published: boolean;
  rowVersion: number;
  buckets: CashForecastBucketDTO[];
  modelledItems: number;
  overriddenItems: number;
}

export interface CashForecastScenarioDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  receivableDelayDays: number;
  receivableHaircutPct: string;
  payableDelayDays: number;
  recurringMultiplierPct: string;
  includeOverdue: boolean;
  manualAdjustments: CashForecastAdjustment[];
  scenarioType: 'base' | 'upside' | 'downside' | 'stress' | 'what_if';
  overriddenDrivers: string[];
  inheritsFromScenarioId?: number | null;
  supersededAt?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  pendingPromotion?: TreasuryScenarioPromotionDTO | null;
  rowVersion: number;
}

export interface CashForecastSourceDTO {
  name: string;
  available: boolean;
  items: number;
  note?: string;
}

export interface CashIcInstrumentCloseRequest {
  note: string;
  rowVersion: number;
}

export interface CashIcInstrumentCreateRequest {
  lenderCompanyId: number;
  borrowerCompanyId: number;
  kind: 'loan' | 'advance' | 'current_account';
  currency: string;
  principalLimit: string;
  pricingBasis?: 'tp_policy' | 'at_cost' | 'interest_free';
  effectiveFrom: string;
  maturityDate?: string;
  description?: string;
}

export interface CashIcInstrumentDTO {
  id: number;
  instrumentNo: string;
  lenderCompanyId: number;
  lenderCompanyCode?: string;
  borrowerCompanyId: number;
  borrowerCompanyCode?: string;
  intercompanyRuleId: number;
  kind: 'loan' | 'advance' | 'current_account';
  currency: string;
  principalLimit: string;
  tpPolicyId?: number | null;
  pricingBasis: 'tp_policy' | 'at_cost' | 'interest_free';
  status: 'draft' | 'pending_approval' | 'active' | 'matured' | 'closed';
  effectiveFrom: string;
  maturityDate?: string | null;
  description?: string;
  outstanding: string;
  inFlight: string;
  headroom: string;
  submittedBy?: number | null;
  submittedAt?: string | null;
  approvalRequestId?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  rejectionNote?: string;
  closedAt?: string | null;
  closeNote?: string;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface CashIcInstrumentListQuery {
  cursor?: string;
  limit?: number;
  lenderCompanyId?: number;
  borrowerCompanyId?: number;
  status?: 'draft' | 'pending_approval' | 'active' | 'matured' | 'closed';
  kind?: 'loan' | 'advance' | 'current_account';
}

export interface CashIcInstrumentSubmitRequest {
  rowVersion: number;
}

export interface CashIcInstrumentUpdateRequest {
  kind?: 'loan' | 'advance' | 'current_account';
  currency?: string | null;
  principalLimit?: string | null;
  pricingBasis?: 'tp_policy' | 'at_cost' | 'interest_free';
  effectiveFrom?: string | null;
  maturityDate?: string | null;
  description?: string | null;
  rowVersion: number;
}

export interface CashIcLoanReportDTO {
  asOf: string;
  rows: CashIcLoanReportRowDTO[];
  totals: CashIcLoanReportTotalDTO[];
}

export interface CashIcLoanReportQuery {
  asOf?: string;
  lenderCompanyId?: number;
  borrowerCompanyId?: number;
  includeClosed?: boolean;
}

export interface CashIcLoanReportRowDTO {
  instrumentId: number;
  instrumentNo: string;
  kind: 'loan' | 'advance' | 'current_account';
  status: 'draft' | 'pending_approval' | 'active' | 'matured' | 'closed';
  lenderCompanyId: number;
  lenderCompanyCode?: string;
  borrowerCompanyId: number;
  borrowerCompanyCode?: string;
  currency: string;
  principalLimit: string;
  advanced: string;
  repaid: string;
  outstanding: string;
  inFlight: string;
  headroom: string;
  maturityDate?: string | null;
  tpPolicyId?: number | null;
}

export interface CashIcLoanReportTotalDTO {
  currency: string;
  advanced: string;
  repaid: string;
  outstanding: string;
  inFlight: string;
}

export interface CashListAccountsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  kind?: 'bank' | 'cash';
  currency?: string;
  isActive?: boolean | null;
  status?: 'draft' | 'pending_approval' | 'active' | 'suspended' | 'dormant' | 'closing' | 'closed';
  verificationStatus?: 'unverified' | 'verified' | 'failed';
  liquidityClass?: 'unrestricted' | 'restricted' | 'pledged' | 'escrowed' | 'trapped';
  bankId?: number;
}

export interface CashMovementAnomaliesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  bankAccountId?: number;
  kind?: string;
  severity?: string;
  status?: string;
}

export interface CashMovementAnomalyDTO {
  id: number;
  companyId: number;
  bankAccountId: number;
  bankAccount?: string;
  movementId: number;
  entryId: number;
  scanVersion: number;
  kind: 'amount_outlier' | 'first_counterparty' | 'unusual_time';
  severity: 'high' | 'medium' | 'low';
  driverKey: string;
  movementDate: string;
  direction: 'in' | 'out';
  amount: string;
  currency: string;
  expectedAmount?: string;
  zScore?: string;
  counterpartyKey?: string;
  counterpartyName?: string;
  comparison: string;
  detail?: Record<string, unknown>;
  recorderIds: number[];
  status: 'open' | 'dispositioned';
  outcome?: 'explained' | 'false_positive';
  reason?: string;
  dispositionedBy?: number | null;
  dispositionedAt?: string;
  raisedAt: string;
  raisedBy?: number | null;
  rowVersion: number;
}

export interface CashMovementAnomalyDispositionRequest {
  outcome: 'explained' | 'false_positive';
  reason: string;
  rowVersion: number;
}

export interface CashMovementAnomalyScanDTO {
  asOf: string;
  scanVersion: number;
  scanned: number;
  detected: number;
  newFindings: number;
  newHigh: number;
}

export interface CashMovementAnomalyScanRequest {
  companyId?: number;
  lookbackDays?: number;
}

export interface CashRevealRequest {
  reason: string;
}

export interface CashRevealResponse {
  bankAccountId: number;
  accountNumber: string;
  revealedAt: string;
}

export interface CashScreeningEntryCreateRequest {
  entryType?: string;
  name: string;
  aliases?: string[];
  bic?: string;
  countryCode?: string;
  listName?: string;
  source?: string;
}

export interface CashScreeningEntryDTO {
  id: number;
  entryType: string;
  name: string;
  aliases: string[];
  bic?: string;
  countryCode?: string;
  listName?: string;
  source: string;
  active: boolean;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface CashScreeningEntryListQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  entryType?: string;
  listName?: string;
  active?: boolean | null;
}

export interface CashScreeningEntryUpdateRequest {
  name?: string | null;
  aliases?: string[] | null;
  bic?: string | null;
  countryCode?: string | null;
  listName?: string | null;
  active?: boolean | null;
  rowVersion: number;
}

export interface CashScreeningImportErrorDTO {
  row: number;
  field: string;
  code: string;
}

export interface CashScreeningImportResultDTO {
  imported: number;
  errors: CashScreeningImportErrorDTO[];
}

export interface CashTransferApprovalAssigneeDTO {
  userId?: number | null;
  userName?: string;
  roleId?: number | null;
  roleName?: string;
  status: string;
  actedBy?: number | null;
}

export interface CashTransferApprovalRouteDTO {
  transferId: number;
  docType: string;
  preview: boolean;
  resolvable: boolean;
  source?: string;
  requestId?: number | null;
  requestStatus?: string;
  amount: string;
  currency: string;
  problem?: string;
  steps: CashTransferApprovalStepDTO[];
}

export interface CashTransferApprovalStepDTO {
  step: number;
  mode: string;
  status: string;
  assignees: CashTransferApprovalAssigneeDTO[];
}

export interface CashTransferReportDTO {
  id: number;
  transferId: number;
  countryCode: string;
  reference: string;
  reportedOn: string;
  note?: string;
  screeningRound: number;
  recordedAt: string;
  recordedBy?: number | null;
}

export interface CashTransferReportRecordRequest {
  reference: string;
  reportedOn: string;
  note?: string;
  rowVersion: number;
}

export interface CashTransferScreeningClearRequest {
  reason: string;
  screeningRound: number;
  rowVersion: number;
}

export interface CashTransferScreeningHitDTO {
  id: number;
  round: number;
  kind: string;
  entryId?: number | null;
  listName?: string;
  matchedValue?: string;
  score?: string | null;
  rule?: string;
  detail: string;
}

export interface CatalogueItemDTO {
  itemId: number;
  sku: string;
  name: string;
  description: string;
  uom: string;
  listPrice?: string;
  unitPrice?: string;
}

export interface CategoryRequest {
  code: string;
  name: string;
  parentCode?: string | null;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface Certificate {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  partyKind: string;
  partyId: number;
  companyId?: number | null;
  jurisdictionId: number;
  regimeId?: number | null;
  certificateNo: string;
  scope: 'full' | 'item_group' | 'tax_code';
  itemGroupId?: number | null;
  taxCode?: string | null;
  exemptTreatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  exemptTaxCode?: string | null;
  validFrom: string;
  validTo?: string | null;
  status: 'draft' | 'active' | 'revoked' | 'expired';
  attachmentId?: number | null;
  issuedBy: string;
  notes: string;
}

export interface CertificateDTO {
  id: number;
  partyKind: string;
  partyId: number;
  companyId?: number | null;
  jurisdictionId: number;
  regimeId?: number | null;
  certificateNo: string;
  scope: string;
  itemGroupId?: number | null;
  taxCode?: string | null;
  exemptTreatment: string;
  exemptTaxCode?: string | null;
  validFrom: string;
  validTo?: string | null;
  status: string;
  attachmentId?: number | null;
  issuedBy: string;
  notes: string;
  rowVersion: number;
}

export interface CertifyCloseReconciliationRequest {
  reconType: string;
  evidenceAttachmentId: number;
  note?: string;
}

export interface CertifyCloseRunRequest {
  statement: string;
  note?: string;
  rowVersion: number;
}

export interface CertifyMetricRequest {
  companyId: number;
  basis: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  positionId?: number | null;
}

export interface CertifyRequest {
  on: string;
  note?: string;
}

export interface CertifySessionRequest {
  override?: ContinuityOverride | null;
  rowVersion: number;
}

export interface CertifyStatementRequest {
  note?: string;
  rowVersion: number;
}

export interface ChainResult {
  rows: number;
  ok: boolean;
  firstBadId?: number;
  reason?: string;
}

export interface ChangeBookMethodRequest {
  newMethod?: 'SL' | 'DB' | 'SYD' | 'UOP';
  newUsefulLifeMonths?: number;
  newSalvageValue?: string;
  reason: string;
  rowVersion: number;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePlanRequest {
  planId: string;
  effective: string;
}

export interface ChangeStageRequest {
  stageCode: string;
  probabilityPercent?: string;
  rowVersion: number;
}

export interface ChangeSubscriptionRequest {
  quantity?: string;
  unitPrice?: string;
  manualPriceReason?: string;
}

export interface ChargeListQuery {
  companyId?: number | null;
  from?: string;
  to?: string;
  poolKey?: string;
  taxCode?: string;
  cursor?: string;
  limit?: number;
}

export interface ChargePool {
  poolKey: string;
  currency: string;
  taxCode: string;
  baseAmount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  lineCount: number;
  documentCount: number;
}

export interface CheckInPMSReservationRequest {
  roomId?: number | null;
  assignmentReason?: 'requested' | 'upgrade' | 'accessible' | 'connecting' | 'only_available' | 'room_move';
  assignmentNote?: string;
  guestId?: number | null;
  attachmentId?: number | null;
  extraction?: Record<string, unknown>;
  confirmedFields?: string[];
  signatureCaptured?: boolean;
  authHold?: CreatePMSAuthHoldRequest | null;
  issueKey?: boolean | null;
  rowVersion: number;
}

export interface CheckLineDTO {
  accountId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  amount: string;
}

export interface CheckOutPMSReservationRequest {
  overrideReason?: string;
  overrideNote?: string;
  rowVersion: number;
}

export interface CheckRequestDTO {
  companyId: number;
  date: string;
  docType: string;
  docId?: number;
  lines: CheckLineDTO[];
}

export interface CheckResultDTO {
  outcome: 'ok' | 'warn' | 'blocked';
  lines: LineResultDTO[];
}

export interface Checklist {
  complete: boolean;
  done: number;
  total: number;
  items: ChecklistItem[];
}

export interface ChecklistItem {
  key: string;
  title: string;
  done: boolean;
  detail: string;
  link: string;
  required: boolean;
}

export interface Citation {
  docType: string;
  docId?: number;
  docNo?: string;
  label: string;
}

export interface CityLedgerPMSFolioRequest {
  windowNo?: number;
  businessDate?: string;
  amount?: string;
  dueDate?: string;
  memo?: string;
  creditOverrideReason?: string;
  creditOverrideNote?: string;
}

export interface ClassDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  defaultMethod: 'SL' | 'DB' | 'SYD' | 'UOP';
  defaultUsefulLifeMonths: number;
  defaultSalvagePercent: string;
  assetAccountId: number;
  assetAccountCode?: string;
  accumulatedDepreciationAccountId: number;
  accumulatedDepreciationAccountCode?: string;
  depreciationExpenseAccountId: number;
  depreciationExpenseAccountCode?: string;
  gainLossAccountId: number;
  gainLossAccountCode?: string;
  impairmentExpenseAccountId?: number | null;
  impairmentReversalAccountId?: number | null;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface ClearItemRequest {
  rowVersion: number;
  note?: string;
}

export interface ClearReconRequest {
  note?: string;
  rowVersion: number;
}

export interface ClearSuspenseItemRequest {
  entryDate: string;
  amount?: string;
  clearingAccountId: number;
  clearingDimensions?: DimensionsInput;
  reasonCode?: string;
  note?: string;
}

export interface ClockEventDTO {
  id: number;
  employeeId: number;
  employeeNo?: string;
  clientEventId: string;
  kind: string;
  happenedAt: string;
  businessDate: string;
  locationId?: number | null;
  rosterLineId?: number | null;
  latitude?: string;
  longitude?: string;
  accuracyM?: string;
  channel: string;
  correctsEventId?: number | null;
  correctedByEventId?: number | null;
  reasonCode?: string;
  note?: string;
  createdAt: string;
}

export interface CloseAccrualRequest {
  note?: string;
}

export interface CloseAnomalyDispositionRequest {
  outcome: 'explained' | 'corrected' | 'false_positive';
  reason: string;
}

export interface CloseAnomalyFindingDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  scanVersion: number;
  kind: 'balance_spike' | 'sign_flip' | 'dormant_activity' | 'round_amount_journal' | 'unusual_preparer' | 'weekend_post';
  severity: 'high' | 'medium' | 'low';
  driverKey: string;
  accountId?: number | null;
  accountCode?: string;
  accountName?: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  entryDate?: string;
  currency?: string;
  amount: string;
  expectedAmount?: string;
  zScore?: string;
  detail?: Record<string, unknown>;
  status: 'open' | 'dispositioned';
  outcome?: 'explained' | 'corrected' | 'false_positive';
  reason?: string;
  dispositionedBy?: number | null;
  dispositionedAt?: string;
  preparerIds: number[];
  raisedAt: string;
  raisedBy?: number | null;
  rowVersion: number;
}

export interface CloseAnomalyFindingsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  periodId?: number;
  kind?: string;
  status?: string;
  severity?: string;
  accountId?: number;
  journalEntryId?: number;
}

export interface CloseAnomalyKindCountDTO {
  kind: 'balance_spike' | 'sign_flip' | 'dormant_activity' | 'round_amount_journal' | 'unusual_preparer' | 'weekend_post';
  open: number;
  dispositioned: number;
}

export interface CloseAnomalyScanDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  scanVersion: number;
  scanCount: number;
  historyPeriods: number;
  firstScannedAt: string;
  lastScannedAt: string;
  scannedBy?: number | null;
  parameters: Record<string, string>;
  detected: number;
  newFindings: number;
  refreshedFindings: number;
  openFindings: number;
  dispositionedFindings: number;
  byKind: CloseAnomalyKindCountDTO[];
  rowVersion: number;
}

export interface CloseAnomalyScanRequest {
  companyId: number;
  periodId: number;
}

export interface CloseBoardDTO {
  rows: CloseBoardRowDTO[];
  generatedAt: string;
}

export interface CloseBoardQuery {
  companyId?: number;
  fiscalYear?: number;
}

export interface CloseBoardRowDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  calendarId: number;
  calendarCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  runNo?: string;
  status: string;
  openedAt?: string;
  openedBy?: number | null;
  targetDate?: string;
  completedAt?: string;
  taskCount: number;
  completedCount: number;
  blockedCount: number;
  overdueCount: number;
  certifiedAt?: string;
  certifiedBy?: number | null;
  certificationStatement?: string;
  evidenceHash?: string;
  evidenceAttachmentId?: number | null;
  approvalRequestId?: number | null;
  note?: string;
  rowVersion: number;
  nextTask?: string;
  blockingReadiness?: string[];
}

export interface CloseCalendarDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  code: string;
  name: string;
  scope: string;
  targetWorkingDays: number;
  ownerPositionId?: number | null;
  isActive: boolean;
  templateCount: number;
  rowVersion: number;
}

export interface CloseCalendarDetailDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  code: string;
  name: string;
  scope: string;
  targetWorkingDays: number;
  ownerPositionId?: number | null;
  isActive: boolean;
  templateCount: number;
  rowVersion: number;
  templates: CloseTaskTemplateDTO[];
}

export interface CloseCardStatementRequest {
  postingDate?: string;
}

export interface CloseCaseRequest {
  rowVersion: number;
}

export interface CloseCertificationDTO {
  id: number;
  runId: number;
  runNo?: string;
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  reconType: string;
  certifiedBy: number;
  certifiedByName?: string;
  positionCode?: string;
  certifiedAt: string;
  statement?: string;
  evidenceAttachmentId?: number | null;
  evidenceFilename?: string;
  evidenceSha256?: string;
}

export interface CloseCertificationsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  periodId?: number;
  reconType?: string;
}

export interface CloseCycleAnalysisBottleneckDTO {
  taskCode: string;
  taskName: string;
  runs: number;
  averageDaysAfterPeriodEnd: string;
  lateCount: number;
  averageDaysLate: string;
}

export interface CloseCycleAnalysisDTO {
  companyId?: number;
  fiscalYear?: number;
  periods: CloseCycleAnalysisPeriodDTO[];
  closedCount: number;
  onTargetCount: number;
  averageDaysToClose?: string;
  averageTargetDays?: string;
  trend: string;
  trendDeltaDays?: string;
  bottlenecks: CloseCycleAnalysisBottleneckDTO[];
  generatedAt: string;
}

export interface CloseCycleAnalysisPeriodDTO {
  runId: number;
  runNo?: string;
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  fiscalYear: number;
  periodEnd: string;
  runStatus: string;
  openedAt?: string;
  certifiedAt?: string;
  targetDate?: string;
  daysToClose?: number | null;
  daysElapsed: number;
  targetDays?: number | null;
  varianceDays?: number | null;
  onTarget?: boolean | null;
  taskCount: number;
  overdueCount: number;
}

export interface CloseCycleAnalysisQuery {
  companyId?: number;
  fiscalYear?: number;
}

export interface CloseDayRequest {
  force?: boolean;
  forceReasonCode?: string;
}

export interface CloseECORequest {
  note?: string;
}

export interface CloseEvidenceDTO {
  id: number;
  runId: number;
  taskId?: number | null;
  taskCode?: string;
  kind: string;
  label: string;
  attachmentId?: number | null;
  storageKey?: string;
  sha256?: string;
  rowCount: number;
  generatedAt: string;
  note?: string;
}

export interface CloseEvidencePackDTO {
  runId: number;
  runNo?: string;
  manifestHash: string;
  entries: CloseEvidencePackEntryDTO[];
  totalBytes: number;
  attachmentId?: number | null;
  storageKey?: string;
  chainVerified: boolean;
  chainRows: number;
  chainReason?: string;
  hashMatchesCertification: boolean;
  certifiedHash?: string;
  generatedAt: string;
}

export interface CloseEvidencePackEntryDTO {
  name: string;
  sha256: string;
  bytes: number;
}

export interface CloseEvidencePackSummaryDTO {
  runId: number;
  runNo?: string;
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  runStatus: string;
  certifiedAt?: string;
  certifiedBy?: number | null;
  manifestHash?: string;
  archiveAttachmentId?: number | null;
  archiveFilename?: string;
  archiveSha256?: string;
  archiveBytes?: number;
  generatedAt?: string;
  partCount: number;
  reconciliationCertifications: number;
  downloadPath: string;
}

export interface CloseEvidencePacksQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  periodId?: number;
}

export interface CloseLineRequest {
  reason: string;
}

export interface ClosePOSCashSessionRequest {
  witnessedBy: number;
  override?: POSOverrideRequest | null;
  rowVersion: number;
}

export interface ClosePeriodRequest {
  reason: string;
  soft?: boolean;
}

export interface ClosePlan {
  companyId: number;
  fiscalYear: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  entryDate: string;
  memo: string;
  retainedEarningsAccountId: number;
  retainedEarningsAccountCode: string;
  retainedEarningsAccountName: string;
  totalIncome: string;
  totalExpense: string;
  netIncome: string;
  lines: ClosePlanLine[];
  totalDebit: string;
  totalCredit: string;
  check: BalanceCheck;
  executor: string;
  generatedAt: string;
}

export interface ClosePlanLine {
  lineNo: number;
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  debit: string;
  credit: string;
  description: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  divisionId?: number | null;
  counterpartyType?: string;
  counterpartyId?: number | null;
}

export interface CloseReadinessDTO {
  companyId: number;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  fromDate: string;
  toDate: string;
  currency: string;
  ready: boolean;
  blockingCount: number;
  advisoryCount: number;
  items: CloseReadinessItemDTO[];
  generatedAt: string;
}

export interface CloseReadinessItemDTO {
  key: string;
  module?: string;
  severity: string;
  ready: boolean;
  skipped?: boolean;
  blocks: boolean;
  count: number;
  amount?: string;
  messageKey?: string;
  link?: string;
  detail?: Record<string, unknown>;
  error?: string;
}

export interface CloseReadinessQuery {
  companyId: number;
  fiscalPeriodId: number;
}

export interface CloseRecallRequest {
  note?: string;
}

export interface CloseRunDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  calendarId: number;
  calendarCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  runNo?: string;
  status: string;
  openedAt?: string;
  openedBy?: number | null;
  targetDate?: string;
  completedAt?: string;
  taskCount: number;
  completedCount: number;
  blockedCount: number;
  overdueCount: number;
  certifiedAt?: string;
  certifiedBy?: number | null;
  certificationStatement?: string;
  evidenceHash?: string;
  evidenceAttachmentId?: number | null;
  approvalRequestId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface CloseRunDetailDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  calendarId: number;
  calendarCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  runNo?: string;
  status: string;
  openedAt?: string;
  openedBy?: number | null;
  targetDate?: string;
  completedAt?: string;
  taskCount: number;
  completedCount: number;
  blockedCount: number;
  overdueCount: number;
  certifiedAt?: string;
  certifiedBy?: number | null;
  certificationStatement?: string;
  evidenceHash?: string;
  evidenceAttachmentId?: number | null;
  approvalRequestId?: number | null;
  note?: string;
  rowVersion: number;
  tasks: CloseTaskDTO[];
  signoffs: CloseSignoffDTO[];
}

export interface CloseServiceWorkOrderRequest {
  override?: ServiceDispatchOverrideDTO | null;
  rowVersion: number;
}

export interface CloseSessionRequest {
  closingBalanceStatement: string;
  rowVersion: number;
}

export interface CloseSignoffDTO {
  id: number;
  runId: number;
  taskId?: number | null;
  taskCode?: string;
  kind: string;
  signedBy: number;
  signedByName?: string;
  positionId?: number | null;
  positionCode?: string;
  signedAt: string;
  statement?: string;
  reconType?: string;
  evidenceAttachmentId?: number | null;
}

export interface CloseStatementDistributionDTO {
  id: number;
  audience: string;
  recipientIds: number[];
  recipientCount: number;
  sentAt: string;
  sentBy: number;
}

export interface CloseStatementSetDTO {
  id: number;
  setNo: string;
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  version: number;
  status: 'generated' | 'published' | 'superseded';
  compareMode?: string;
  basis: string;
  fromDate: string;
  toDate: string;
  currency: string;
  contentHash: string;
  generatedAt: string;
  generatedBy?: number | null;
  pdfAttachmentId?: number | null;
  pdfSha256?: string;
  xlsxAttachmentId?: number | null;
  xlsxSha256?: string;
  pdfDownloadPath: string;
  xlsxDownloadPath: string;
  audience?: string;
  publishedAt?: string;
  publishedBy?: number | null;
  supersededBySetId?: number | null;
  supersededAt?: string;
  evidenceRunId?: number | null;
  evidencePackPath?: string;
  rowVersion: number;
  statements?: StatementSetEntity | null;
  distribution?: CloseStatementDistributionDTO | null;
}

export interface CloseStatementSetDownloadQuery {
  format: string;
}

export interface CloseStatementSetGenerateRequest {
  companyId: number;
  periodId: number;
  compare?: '' | 'prior_period' | 'prior_year';
}

export interface CloseStatementSetPublishRequest {
  audience: 'executive' | 'finance' | 'audit';
}

export interface CloseStatementSetsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  periodId?: number;
  status?: string;
}

export interface CloseSubledgerReconAccountDTO {
  accountId: number;
  accountCode?: string;
  accountName?: string;
  role?: string;
  subLedgerBalance?: string;
  controlBalance: string;
  difference?: string;
  drillDown: string;
}

export interface CloseSubledgerReconDTO {
  companyId: number;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  fromDate: string;
  toDate: string;
  currency: string;
  reconciled: boolean;
  differenceCount: number;
  items: CloseSubledgerReconItemDTO[];
  generatedAt: string;
}

export interface CloseSubledgerReconItemDTO {
  subledger: string;
  reconType: string;
  key: string;
  module?: string;
  severity: string;
  ready: boolean;
  skipped?: boolean;
  blocks: boolean;
  basis?: string;
  currency?: string;
  subLedgerBalance?: string;
  controlBalance?: string;
  difference?: string;
  differenceCount: number;
  messageKey?: string;
  link?: string;
  accounts: CloseSubledgerReconAccountDTO[];
  certification?: CloseCertificationDTO | null;
  error?: string;
}

export interface CloseSubledgerReconQuery {
  companyId: number;
  periodId: number;
}

export interface CloseTaskDTO {
  id: number;
  runId: number;
  templateId?: number | null;
  code: string;
  name: string;
  seq: number;
  taskKind: string;
  readinessKey?: string;
  actionKey?: string;
  obligationKind?: string;
  module?: string;
  ownerPositionId?: number | null;
  assigneeUserId?: number | null;
  dueOn?: string;
  isMandatory: boolean;
  requiresEvidence: boolean;
  status: string;
  computedReady?: boolean | null;
  computedAt?: string;
  computedDetail?: Record<string, unknown>;
  readinessCount?: number;
  readinessAmount?: string;
  readinessMessageKey?: string;
  readinessLink?: string;
  blockedBy?: string[];
  dependsOn?: string[];
  completedAt?: string;
  completedBy?: number | null;
  acceptedReasonCode?: string;
  acceptedNote?: string;
  resultDocType?: string;
  resultDocId?: number | null;
  note?: string;
  runnable: boolean;
  rowVersion: number;
}

export interface CloseTaskTemplateDTO {
  id: number;
  calendarId: number;
  code: string;
  name: string;
  seq: number;
  taskKind: string;
  readinessKey?: string;
  actionKey?: string;
  obligationKind?: string;
  module?: string;
  ownerPositionId?: number | null;
  dueOffsetDays: number;
  isMandatory: boolean;
  requiresEvidence: boolean;
  instructions?: string;
  isActive: boolean;
  dependsOn: string[];
  rowVersion: number;
}

export interface CloseTemplateInput {
  code: string;
  name: string;
  seq?: number;
  taskKind: string;
  readinessKey?: string;
  actionKey?: string;
  obligationKind?: string;
  module?: string;
  ownerPositionId?: number | null;
  dueOffsetDays?: number;
  isMandatory?: boolean | null;
  requiresEvidence?: boolean;
  instructions?: string;
  isActive?: boolean | null;
  dependsOn?: string[];
}

export interface CloseTicketRequest {
  rowVersion: number;
}

export interface CloseTrialBalanceAnomaliesDTO {
  companyId: number;
  companyCode?: string;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  periodStatus?: string;
  currency: string;
  fromDate: string;
  toDate: string;
  scanVersion: number;
  scanned: boolean;
  lastScannedAt?: string;
  openFindings: number;
  dispositionedFindings: number;
  flaggedAccounts: number;
  rows: CloseTrialBalanceAnomalyRowDTO[];
  journalFindings: CloseAnomalyFindingDTO[];
  byKind: CloseAnomalyKindCountDTO[];
  generatedAt: string;
}

export interface CloseTrialBalanceAnomaliesQuery {
  companyId: number;
  periodId: number;
}

export interface CloseTrialBalanceAnomalyRowDTO {
  accountId: number;
  code: string;
  name: string;
  type: string;
  opening: string;
  periodDebit: string;
  periodCredit: string;
  closing: string;
  flagged: boolean;
  flags: CloseAnomalyFindingDTO[];
}

export interface CloseWalletRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface ClosureCoverage {
  nodeCount: number;
  selfRows: number;
  expectedRows: number;
  actualRows: number;
  maxDepth: number;
}

export interface CoaSegmentStep {
  sequence: number;
  prompt: string;
  qualifier?: string;
  binding: string;
  length?: number;
}

export interface CoaStructureStep {
  name: string;
  effectiveFrom?: string;
  segments: CoaSegmentStep[];
}

export interface CodeDTO {
  id: number;
  code: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE';
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  basis: 'percent' | 'per_unit';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  unitCurrency?: string | null;
  unitLabel?: string | null;
  jurisdiction: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  inputAccountCode?: string | null;
  outputAccountCode?: string | null;
  payableAccountCode?: string | null;
  rounding: 'line' | 'document';
  roundingMode: 'half_up' | 'half_even';
  isRecoverable: boolean;
  description: string;
  isActive: boolean;
  rowVersion: number;
}

export interface CollectPoolRequest {
  employerTopup?: string;
  rowVersion: number;
}

export interface ColumnSpec {
  header: string;
  source: string;
  width?: number;
  align?: string;
  pad?: string;
  format?: string;
  literal?: string;
}

export interface CombinationResult {
  segmentValues: string;
  combinationHash: string;
  isValid: boolean;
  violatedRuleId?: number | null;
  violatedRuleName?: string;
  offendingValues?: string[];
  cached: boolean;
}

export interface CommissionEAMAssetRequest {
  commissionedOn?: string;
  note?: string;
  rowVersion: number;
}

export interface CommitPartyMergeRequest {
}

export interface CommitmentDTO {
  id: number;
  companyId: number;
  fiscalPeriodId: number;
  accountId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  kind: 'soft' | 'hard';
  status: 'open' | 'released';
  docType: string;
  docId: number;
  docLineNo: number;
  docNo: string;
  amount: string;
  releasedAmount: string;
  remaining: string;
  note: string;
  createdAt: string;
  rowVersion: number;
}

export interface Company {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  legalName: string;
  name: string;
  country: string;
  jurisdictionId?: number | null;
  baseCurrency: string;
  taxRegistrations: unknown[];
  address: Record<string, unknown>;
  phone: string;
  email: string;
  logoKey?: string | null;
  isActive: boolean;
  openingBalancesSkipped: boolean;
  openingBalancesSkippedReason: string;
  activatedAt?: string | null;
  activatedBy?: number | null;
  dissolvedOn?: string | null;
  dissolutionReasonCode?: string;
  locationId?: number | null;
}

export interface CompanyDTO {
  id: number;
  name: string;
}

export interface CompareSetsQuery {
  leftId: number;
  rightId: number;
  configurationType: string;
  asOf: string;
}

export interface CompareSetsResult {
  leftId: number;
  leftCode: string;
  rightId: number;
  rightCode: string;
  asOf: string;
  uniqueToLeft: ValueRef[];
  uniqueToRight: ValueRef[];
  inCommon: ValueRef[];
  overlapRatio: number;
}

export interface CompareVersionsQuery {
  fromVersionId: number;
  toVersionId: number;
}

export interface CompareVersionsResult {
  hierarchyId: number;
  fromVersionId: number;
  fromVersionNo: number;
  toVersionId: number;
  toVersionNo: number;
  addedCount: number;
  movedCount: number;
  removedCount: number;
  unchangedCount: number;
  added: NodeChange[];
  moved: NodeChange[];
  removed: NodeChange[];
}

export interface ComparisonDTO {
  rfqId: number;
  quotes: QuoteDTO[];
  recommendedVendorId?: number | null;
}

export interface CompleteBeneficiaryVerificationRequest {
  outcome: 'pending' | 'passed' | 'failed';
  pennyTransferId?: number | null;
  pennyReference?: string;
  contactName?: string;
  contactNumber?: string;
  contactSource?: string;
  attachmentId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface CompleteCAPAActionRequest {
  completionNote?: string;
  attachmentId?: number | null;
  cancelled?: boolean;
  rowVersion: number;
}

export interface CompleteCloseTaskRequest {
  note?: string;
  statement?: string;
  evidenceAttachmentIds?: number[];
  rowVersion: number;
}

export interface CompleteDSRTaskRequest {
  status?: string;
  note?: string;
  rowVersion: number;
}

export interface CompleteECODispositionRequest {
  action?: string;
  status?: string;
  reason?: string;
  outcomeDocType?: string;
  outcomeDocId?: number | null;
  rowVersion: number;
}

export interface CompletePickRequest {
  note?: string;
}

export interface CompleteRecallActionRequest {
  status: 'open' | 'done' | 'waived';
  waivedReason?: string;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface CompleteServiceVisitRequest {
  outcome: string;
  incompleteReasonCode?: string;
  notes?: string;
  signerName?: string;
  signatureAttachmentId?: number | null;
  departedAt?: string | null;
  occurredAt?: string | null;
  rowVersion: number;
}

export interface CompleteServiceWorkOrderRequest {
  note?: string;
  occurredAt?: string | null;
  rowVersion: number;
}

export interface CompleteVerificationRequest {
  rowVersion: number;
}

export interface ComponentDTO {
  id: number;
  code: string;
  name: string;
  componentType: 'earning' | 'deduction' | 'employer_cost';
  formulaType: 'fixed' | 'percent_of_base' | 'per_unit_overtime' | 'slab' | 'pool_share';
  fixedAmount: string;
  percentOfBase?: string | null;
  base?: 'basic_salary' | 'pensionable_wage';
  overtimeBucket?: string;
  perUnitRate?: string | null;
  slabBands?: SlabBandDTO[];
  expenseAccountCode?: string;
  liabilityAccountCode?: string;
  sequence: number;
  isActive: boolean;
  description: string;
  companyId?: number | null;
  payGroupId?: number | null;
  jurisdictionId?: number | null;
  wageClass: 'basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other';
  poolCode?: string;
  settlementBasis: 'expense' | 'liability_drawdown';
  effectiveFrom: string;
  effectiveTo?: string | null;
  source: 'tenant' | 'pack' | 'legacy';
  sourceCitation?: string;
  reviewDueOn?: string | null;
  statutoryEquivalentCode?: string;
  isSuppressed?: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface ComponentSpec {
  name: string;
  costShare: string;
  method?: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths?: number;
  salvageValue?: string;
}

export interface ComponentiseRequest {
  effectiveOn: string;
  components: ComponentSpec[];
  reasonCode?: string;
  note?: string;
}

export interface Computation {
  lineNo: number;
  gross: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  net: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  steps: StepResult[];
  inclusive: boolean;
}

export interface ComputeStandardCostRollupRequest {
  companyId: number;
  itemIds?: number[];
  effectiveDate: string;
  componentBasis?: string;
  batchSize?: string;
  levels?: number;
  notes?: string;
}

export interface ComputeUsageVarianceRequest {
  companyId: number;
  warehouseId: number;
  periodFrom: string;
  periodTo: string;
  openingCountSessionId?: number | null;
  closingCountSessionId?: number | null;
}

export interface ConfirmConsentRequest {
  token: string;
}

export interface ConfirmInstructionRequest {
  receiptDate?: string;
  creditedAmount?: string;
  bankReference?: string;
  fxAccountId?: number | null;
  debitStatementLineId?: number | null;
  creditStatementLineId?: number | null;
  rowVersion: number;
}

export interface ConfirmInvoiceRequest {
  creditOverrideReason?: string;
}

export interface ConfirmMatchRequest {
  sessionId: number;
  journalLineId: number;
  matchType?: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  confidence?: string;
}

export interface ConfirmOrderRequest {
  creditOverrideReason?: string;
  discountOverrideReason?: string;
  permitBackorder?: boolean | null;
  backorderReasonCode?: string;
  promisedDate?: string;
}

export interface ConfirmPMSGroupBlockRequest {
  rowVersion: number;
  note?: string;
}

export interface ConfirmPMSReservationRequest {
  rowVersion: number;
  overrideReason?: string;
  overrideNote?: string;
}

export interface ConfirmPORequest {
  expectedDate?: string;
  reference?: string;
}

export interface ConnectRoomRequest {
  connectedRoomId: number;
}

export interface ConsentChannelDTO {
  id: number;
  code: string;
  name: string;
  medium: 'email' | 'sms' | 'voice' | 'whatsapp' | 'postal' | 'in_app';
  requiresDoubleOptIn: boolean;
  isActive: boolean;
  sortOrder: number;
  isDeleted: boolean;
  rowVersion: number;
}

export interface ConsentImportRowError {
  lineNo: number;
  column?: string;
  code: string;
  message: string;
}

export interface ConsentMatrixQuery {
  asOf?: string;
}

export interface ConsentOutcomeEntry {
  channelCode: string;
  purposeCode: string;
  survivor?: ConsentOutcomeSide | null;
  source?: ConsentOutcomeSide | null;
  winner: string;
  rule: string;
  action: string;
}

export interface ConsentOutcomeSide {
  consentId: number;
  state: string;
  basis: string;
  expiresOn?: string | null;
  capturedAt: string;
}

export interface ConsentPurposeDTO {
  id: number;
  code: string;
  name: string;
  description?: string;
  defaultBasis: 'consent' | 'contract' | 'legal_obligation' | 'legitimate_interest' | 'vital_interest' | 'public_task';
  isEssential: boolean;
  defaultValidityDays?: number | null;
  isActive: boolean;
  sortOrder: number;
  isDeleted: boolean;
  rowVersion: number;
}

export interface ConsolAccountMapDTO {
  id: number;
  groupId: number;
  companyId: number;
  companyCode: string;
  localAccountId: number;
  localAccountCode: string;
  localAccountName: string;
  groupAccountId: number;
  groupAccountCode: string;
  groupAccountName: string;
  effectiveFrom: string;
  effectiveTo?: string;
  version: number;
  isCurrent: boolean;
}

export interface ConsolAccountMapListQuery {
  cursor?: string;
  limit?: number;
  asOf?: string;
  companyId?: number;
  history?: boolean | null;
}

export interface ConsolAccountMapRowInput {
  companyId: number;
  localAccountId: number;
  groupAccountId?: number | null;
}

export interface ConsolAccountMapSuggestRequest {
  companyId?: number;
  asOf?: string;
}

export interface ConsolAccountMapSuggestionDTO {
  companyId: number;
  companyCode: string;
  localAccountId: number;
  localAccountCode: string;
  localAccountName: string;
  groupAccountId: number;
  groupAccountCode: string;
  groupAccountName: string;
  method: 'code' | 'name';
  score: string;
}

export interface ConsolAccountMapSuggestionsDTO {
  groupId: number;
  asOf: string;
  items: ConsolAccountMapSuggestionDTO[];
  unmatchedCount: number;
}

export interface ConsolAccountMapsPutRequest {
  setVersion: number;
  effectiveFrom: string;
  rows: ConsolAccountMapRowInput[];
}

export interface ConsolAccountMapsPutResult {
  groupId: number;
  setVersion: number;
  effectiveFrom: string;
  opened: number;
  closed: number;
  unchanged: number;
}

export interface ConsolAdjustmentCreateRequest {
  runId: number;
  description: string;
  justification: string;
  lines: ConsolAdjustmentLineInput[];
}

export interface ConsolAdjustmentDTO {
  id: number;
  adjustmentNo?: string;
  runId: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'withdrawn';
  description: string;
  justification: string;
  totalAmount: string;
  currency: string;
  lines: ConsolAdjustmentLineDTO[];
  carriedFromAdjustmentId?: number | null;
  approvalRequestId?: number | null;
  createdAt: string;
  createdBy?: number | null;
  submittedAt?: string | null;
  submittedBy?: number | null;
  approverId?: number | null;
  approvedAt?: string | null;
  decisionNote: string;
  rejectedAt?: string | null;
  withdrawnAt?: string | null;
  withdrawnBy?: number | null;
  rowVersion: number;
}

export interface ConsolAdjustmentLineDTO {
  lineNo: number;
  groupAccountId: number;
  groupAccountCode: string;
  groupAccountName: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  debit: string;
  credit: string;
  description: string;
}

export interface ConsolAdjustmentLineInput {
  groupAccountId: number;
  debit?: string;
  credit?: string;
  description?: string;
}

export interface ConsolAdjustmentListQuery {
  cursor?: string;
  limit?: number;
  runId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'withdrawn';
}

export interface ConsolAdjustmentUpdateRequest {
  description?: string | null;
  justification?: string | null;
  lines?: ConsolAdjustmentLineInput[];
  rowVersion: number;
}

export interface ConsolBalanceCheck {
  debit: string;
  credit: string;
  difference: string;
  balanced: boolean;
}

export interface ConsolCashFlowFxDTO {
  companyId: number;
  companyCode: string;
  baseCurrency: string;
  openingRate: string;
  closingRate: string;
  averageRate: string;
  openingCashLocal: string;
  closingCashLocal: string;
  openingCash: string;
  closingCash: string;
  fxEffect: string;
}

export interface ConsolCashFlowLineDTO {
  key: string;
  group: string;
  label: string;
  companyId?: number;
  companyCode?: string;
  accountCode?: string;
  amount: string;
}

export interface ConsolCashFlowSectionDTO {
  key: string;
  title: string;
  lines: ConsolCashFlowLineDTO[];
  total: string;
}

export interface ConsolCashFlowStatementDTO {
  method: string;
  netProfit: string;
  sections: ConsolCashFlowSectionDTO[];
  netChange: string;
  fxEffect: string;
  netChangeInCash: string;
  openingCash: string;
  closingCash: string;
  cashMovement: string;
  reconciles: boolean;
  difference: string;
  roundingAllowance: string;
  fxByEntity: ConsolCashFlowFxDTO[];
  notes: string[];
}

export interface ConsolContributionRowDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  isParent: boolean;
  method?: 'full' | 'proportional' | 'equity' | 'excluded';
  ownershipPct?: string;
  revenue: string;
  expenses: string;
  profit: string;
  profitMinority: string;
  profitAttributable: string;
  assets: string;
  liabilities: string;
  assetsAfterEliminations: string;
  liabilitiesAfterEliminations: string;
  netAssets: string;
  revenueSharePct: string;
  profitSharePct: string;
  netAssetsSharePct: string;
}

export interface ConsolCreateGroupRequest {
  code: string;
  name: string;
  parentCompanyId: number;
  presentationCurrency: string;
  description?: string;
  intercoTolerance?: string;
  groupCoaId?: number | null;
}

export interface ConsolCurrencyTranslationDTO {
  runId: number;
  groupId: number;
  presentationCurrency: string;
  fromDate: string;
  asOfDate: string;
  status: 'draft' | 'ready' | 'published' | 'superseded';
  source: 'frozen' | 'computed';
  entities: ConsolTranslationEntityDTO[];
  balanceTypes: ConsolTranslationBalanceTypeDTO[];
  reserve: ConsolTranslationReserveDTO;
  trialBalanceBalanced: boolean;
  trialBalanceDifference: string;
  generatedAt: string;
}

export interface ConsolEntityContributionDTO {
  runId: number;
  groupId: number;
  presentationCurrency: string;
  asOfDate: string;
  status: 'draft' | 'ready' | 'published' | 'superseded';
  source: 'frozen' | 'computed';
  entities: ConsolContributionRowDTO[];
  adjustments: ConsolContributionRowDTO;
  total: ConsolContributionRowDTO;
  reconcilesToStatements: boolean;
  difference: string;
  generatedAt: string;
}

export interface ConsolEquityMovementDTO {
  method: string;
  rows: ConsolEquityRowDTO[];
  openingEquity: string;
  closingEquity: string;
  balanceSheetEquity: string;
  balanceSheetDifference: string;
  reconciles: boolean;
  difference: string;
  roundingAllowance: string;
  notes: string[];
}

export interface ConsolEquityRowDTO {
  key: string;
  label: string;
  postedEquity: string;
  retainedEarnings: string;
  translationReserve: string;
  consolidationEntries: string;
  total: string;
  minorityInterest: string;
  ownersOfParent: string;
}

export interface ConsolGroupDTO {
  id: number;
  code: string;
  name: string;
  parentCompanyId: number;
  parentCompanyCode?: string;
  presentationCurrency: string;
  description: string;
  isActive: boolean;
  intercoTolerance: string;
  rowVersion: number;
  groupCoaId?: number | null;
  accountMapSetVersion: number;
  icDifferenceThreshold: string;
  icDifferenceThresholdPending?: ConsolIcThresholdPendingDTO | null;
  intercoTolerancePending?: ConsolIcThresholdPendingDTO | null;
}

export interface ConsolIcThresholdPendingDTO {
  threshold: string;
  requestedBy: number;
  requestedAt: string;
  approvalRequestId: number;
}

export interface ConsolIcThresholdRequest {
  threshold: string;
  rowVersion: number;
}

export interface ConsolLine {
  companyId: number;
  companyCode: string;
  accountId?: number;
  accountCode?: string;
  accountName: string;
  amount: string;
  synthetic?: boolean;
  groupAccountCode?: string;
  groupAccountName?: string;
  adjustment?: boolean;
  adjustmentNo?: string;
}

export interface ConsolListGroupsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  isActive?: boolean | null;
}

export interface ConsolListRunsQuery {
  cursor?: string;
  limit?: number;
  groupId?: number;
  status?: 'draft' | 'ready' | 'published' | 'superseded';
}

export interface ConsolMemberDTO {
  id: number;
  groupId: number;
  companyId: number;
  companyCode?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  method: 'full' | 'proportional' | 'equity' | 'excluded';
  note: string;
  rowVersion: number;
  investmentAccountId?: number | null;
}

export interface ConsolMinorityEntityDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  method: 'full' | 'proportional' | 'equity' | 'excluded';
  baseCurrency: string;
  ownershipPct: string;
  minorityPct: string;
  netAssets: string;
  minorityEquity: string;
  netIncome: string;
  minorityIncome: string;
  ownersIncome: string;
  reconciles: boolean;
  periods: RunEntityPeriodDTO[];
}

export interface ConsolMinorityInterestDTO {
  runId: number;
  groupId: number;
  presentationCurrency: string;
  asOfDate: string;
  status: 'draft' | 'ready' | 'published' | 'superseded';
  source: 'frozen' | 'computed';
  entities: ConsolMinorityEntityDTO[];
  totalMinorityEquity: string;
  totalMinorityIncome: string;
  reconciles: boolean;
  generatedAt: string;
}

export interface ConsolReadinessDTO {
  groupId: number;
  fiscalPeriodId: number;
  asOfDate: string;
  intercoTolerance: string;
  icDifferenceThreshold: string;
  members: ConsolReadinessMemberDTO[];
  ready: boolean;
  groupCoaId?: number | null;
  unmappedBalanceCount: number;
  warnings: string[];
}

export interface ConsolReadinessMemberDTO {
  companyId: number;
  companyCode?: string;
  method: 'full' | 'proportional' | 'equity' | 'excluded';
  fiscalPeriodId?: number | null;
  periodStatus: string;
  closeRunCertified: boolean;
  intercoUnreconciled: string;
  unmappedBalances: number;
  unmappedAccounts: ConsolReadinessUnmappedAccountDTO[];
  unmappedTruncated: boolean;
  icDifferencesOpen: number;
  icDifferencesAmount: string;
  blockers: string[];
}

export interface ConsolReadinessQuery {
  periodId: number;
}

export interface ConsolReadinessUnmappedAccountDTO {
  companyId: number;
  accountId: number;
  accountCode: string;
  accountName: string;
  balance: string;
  currency: string;
}

export interface ConsolRunDTO {
  id: number;
  groupId: number;
  fiscalPeriodId?: number | null;
  fromDate: string;
  asOfDate: string;
  presentationCurrency: string;
  note: string;
  totalAssets: string;
  totalLiabilities: string;
  totalEquityAttributable: string;
  totalMinorityInterest: string;
  totalCta: string;
  netIncomeAttributable: string;
  netIncomeMinority: string;
  check: ConsolBalanceCheck;
  groupCoaId?: number | null;
  accountMapSetVersion?: number | null;
  createdAt: string;
  createdBy?: number | null;
  entities?: RunEntityDTO[];
  status: 'draft' | 'ready' | 'published' | 'superseded';
  restatesRunId?: number | null;
  restatementReason?: string;
  publishedAt?: string | null;
  publishedBy?: number | null;
  supersededByRunId?: number | null;
  supersededAt?: string | null;
  rowVersion: number;
  approvedAdjustmentCount: number;
  pendingAdjustmentCount: number;
}

export interface ConsolRunRestateRequest {
  reason: string;
}

export interface ConsolSection {
  key: string;
  title: string;
  lines: ConsolLine[];
  total: string;
}

export interface ConsolStatement {
  kind: 'income_statement' | 'balance_sheet';
  runId: number;
  groupId: number;
  presentationCurrency: string;
  from?: string;
  to: string;
  sections: ConsolSection[];
  summary: ConsolStatementTotal[];
  check?: ConsolBalanceCheck | null;
  generatedAt: string;
}

export interface ConsolStatementSetDTO {
  runId: number;
  groupId: number;
  presentationCurrency: string;
  from: string;
  to: string;
  status: 'draft' | 'ready' | 'published' | 'superseded';
  source: 'frozen' | 'computed';
  incomeStatement: ConsolStatement;
  balanceSheet: ConsolStatement;
  cashFlow: ConsolCashFlowStatementDTO;
  equityMovement: ConsolEquityMovementDTO;
  generatedAt: string;
}

export interface ConsolStatementTotal {
  key: string;
  label: string;
  amount: string;
}

export interface ConsolTBRow {
  companyId: number;
  companyCode: string;
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  rateKind: 'closing' | 'opening' | 'average' | 'identity';
  rateUsed: string;
  localAmount: string;
  grossTranslated: string;
  eliminationAdjustment: string;
  netAmount: string;
  groupAccountId?: number | null;
  groupAccountCode?: string;
  groupAccountName?: string;
  consolidationAdjustment: string;
  adjustment?: boolean;
  adjustmentNo?: string;
}

export interface ConsolTranslationBalanceTypeDTO {
  key: string;
  label: string;
  translated: string;
  translationEffect: string;
}

export interface ConsolTranslationEntityDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  isParent: boolean;
  method: 'full' | 'proportional' | 'equity' | 'excluded';
  baseCurrency: string;
  closingRate: string;
  averageRate: string;
  openingRate: string;
  rows: ConsolTranslationRowDTO[];
  cta: string;
  ctaRecomputed: string;
  ctaMatches: boolean;
  effectsTotal: string;
  roundingResidual: string;
}

export interface ConsolTranslationReserveDTO {
  opening: string;
  movement: string;
  closing: string;
}

export interface ConsolTranslationRowDTO {
  key: string;
  label: string;
  balanceType: string;
  rateKind: 'closing' | 'opening' | 'average' | 'identity';
  rate: string;
  local: string;
  translated: string;
  translationEffect: string;
}

export interface ConsolTrialBalance {
  runId: number;
  groupId: number;
  presentationCurrency: string;
  fromDate: string;
  asOfDate: string;
  rows: ConsolTBRow[];
  check: ConsolBalanceCheck;
  generatedAt: string;
}

export interface ConsolidationPathDTO {
  companyId: number;
  parentCompanyId: number;
  route: string;
  detail: string;
}

export interface ConsolidationPathQuery {
  parentCompanyId: number;
}

export interface ContentPartInput {
  kind: string;
  refId: number;
  refVersion?: number;
  refHash?: string;
  byteSize?: number;
}

export interface Context {
  companyId: number;
  direction: 'input' | 'output' | 'any';
  supplyType: 'goods' | 'services' | 'accommodation' | 'fnb' | 'digital' | 'transport' | 'other';
  channel: '' | 'pos' | 'portal' | 'web' | 'b2b' | 'hospitality' | 'retail' | 'wholesale';
  date: string;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  shipFrom: JurisdictionRef;
  shipTo: JurisdictionRef;
  propertyJurisdiction: JurisdictionRef;
  party: PartyRef;
  docType: string;
  docId: number;
  docNo: string;
  regimes?: string[];
  chargePoolKey: string;
  allowLegacyFallback: boolean;
  company: TaxCompany;
  from: Jurisdiction;
  to: Jurisdiction;
  partyJurisdiction: Jurisdiction;
  certificates: Certificate[];
  basis: 'exclusive' | 'inclusive';
  today: string;
}

export interface ContinuityOverride {
  reasonCode: string;
  note?: string;
}

export interface ContractCommitmentDTO {
  id: number;
  seqNo: number;
  direction: string;
  itemId?: number | null;
  itemCategoryId?: number | null;
  basis: string;
  committedQuantity: string;
  committedValue: string;
  periodStart: string;
  periodEnd: string;
  releasedQuantity: string;
  releasedValue: string;
  receivedQuantity: string;
  invoicedValue: string;
  remainingCommitment: string;
  utilisationPercent: string;
  overCommitmentPolicy: string;
  shortfallPenaltyValue: string;
  rowVersion: number;
}

export interface ContractCommitmentInput {
  direction?: string;
  itemId?: number | null;
  itemCategoryId?: number | null;
  basis: string;
  committedQuantity?: string;
  committedValue?: string;
  periodStart: string;
  periodEnd: string;
  overCommitmentPolicy?: string;
  shortfallPenaltyValue?: string;
}

export interface ContractConsumptionDTO {
  contractId: number;
  contractNo?: string;
  kind: string;
  status: string;
  currency: string;
  committedValue: string;
  releasedValue: string;
  invoicedValue: string;
  openReleaseValue: string;
  releaseCount: number;
  lines: ContractConsumptionLineDTO[];
}

export interface ContractConsumptionLineDTO {
  commitmentId: number;
  seqNo: number;
  direction: string;
  basis: string;
  itemId?: number | null;
  periodStart: string;
  periodEnd: string;
  committed: string;
  released: string;
  received: string;
  invoiced: string;
  remainingCommitment: string;
  utilisationPercent: string;
  isOverCommitted: boolean;
}

export interface ContractExpiryRowDTO {
  contractId: number;
  contractNo?: string;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  kind: string;
  title: string;
  status: string;
  endsOn?: string | null;
  daysToExpiry?: number | null;
  notifyOn?: string;
  autoRenew: boolean;
  committedValue: string;
  releasedValue: string;
  utilisationPercent: string;
}

export interface ContractObligationInput {
  seq: number;
  kind: string;
  description?: string;
  targetValue?: string;
  periodStart?: string;
  periodEnd?: string;
  dueOn?: string;
}

export interface ContractPriceLineInput {
  itemId?: number | null;
  description?: string;
  uom?: string;
  minQuantity?: string;
  unitPrice: string;
  currency?: string;
  priceBasis?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  leadTimeDays?: number | null;
  minShelfLifeDays?: number | null;
  minOrderQuantity?: string;
  orderMultiple?: string;
  priceReviewOn?: string;
}

export interface ContractRebateDTO {
  id: number;
  seqNo: number;
  name: string;
  kind: string;
  itemCategoryId?: number | null;
  basis: string;
  thresholdQuantity: string;
  thresholdValue: string;
  rebatePercent: string;
  rebateAmount: string;
  isRetrospective: boolean;
  periodStart: string;
  periodEnd: string;
  receivableAccountId?: number | null;
  incomeAccountId?: number | null;
  postsJournal: boolean;
  accruedValue: string;
  settledValue: string;
  tiers: ContractRebateTierDTO[];
  rowVersion: number;
}

export interface ContractRebateInput {
  name: string;
  kind: string;
  itemCategoryId?: number | null;
  basis?: string;
  thresholdQuantity?: string;
  thresholdValue?: string;
  rebatePercent?: string;
  rebateAmount?: string;
  isRetrospective?: boolean | null;
  periodStart: string;
  periodEnd: string;
  receivableAccountId?: number | null;
  incomeAccountId?: number | null;
  tiers?: ContractRebateTierInput[];
}

export interface ContractRebateTierDTO {
  id: number;
  seqNo: number;
  thresholdFrom: string;
  thresholdTo?: string | null;
  rebatePercent: string;
  rebateAmount: string;
}

export interface ContractRebateTierInput {
  thresholdFrom: string;
  thresholdTo?: string;
  rebatePercent?: string;
  rebateAmount?: string;
}

export interface ContractReleaseDTO {
  id: number;
  contractId: number;
  commitmentId?: number | null;
  poId: number;
  poNo?: string;
  poLineId?: number | null;
  releaseNo: number;
  quantity: string;
  value: string;
  contractLineId?: number | null;
  unitPrice: string;
  releasedOn: string;
  status: string;
  rowVersion: number;
}

export interface ContractScopeInput {
  itemId?: number | null;
  itemCategoryId?: number | null;
  expenseAccountId?: number | null;
  branchId?: number | null;
  locationId?: number | null;
  isExclusive?: boolean | null;
  notes?: string;
}

export interface ContractorInvoiceDTO {
  id: number;
  projectId?: number | null;
  amount: string;
  currency: string;
  invoiceDate: string;
  description?: string;
  status: string;
  createdAt: string;
  rowVersion: number;
}

export interface ContractorTaskDTO {
  id: number;
  projectId: number;
  wbsCode: string;
  name: string;
  description: string;
  status: string;
  startDate?: string;
  endDate?: string;
  isMilestone: boolean;
  rowVersion: number;
}

export interface ControlReconciliation {
  accountId: number;
  accountCode: string;
  subLedgerTotal: string;
  controlBalance: string;
  difference: string;
  reconciles: boolean;
}

export interface ControlRuleDTO {
  id: number;
  code: string;
  module: string;
  classification: string;
  severity: string;
  tenantSeverity?: string | null;
  effectiveSeverity: string;
  reasonSetCode?: string;
  authorityDocType?: string;
  queueCode?: string;
  isEnabled: boolean;
  isOrphaned: boolean;
  rowVersion: number;
}

export interface ConversationDTO {
  id: number;
  title?: string;
  createdAt: string;
  lastMessageAt?: string | null;
  rowVersion: number;
}

export interface ConversionReport {
  from: string;
  to: string;
  totalLeads: number;
  convertedLeads: number;
  conversionRatePercent: string;
  bySource: SourceConversionRow[];
}

export interface ConvertCandidateRequest {
  companyId: number;
  departmentId?: number | null;
  branchId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  title?: string;
  joinedOn: string;
  basicSalary: string;
  currency: string;
  pensionable?: boolean | null;
  rowVersion: number;
}

export interface ConvertCandidateResult {
  candidate: CandidateDTO;
  employee: EmployeeDTO;
}

export interface ConvertLeadRequest {
  customerCode: string;
  currency: string;
  paymentTermsDays?: number;
  creditLimit?: string;
  creditLimitCurrency?: string;
  portalAccess?: boolean;
  rowVersion: number;
}

export interface ConvertLeadResult {
  customer: CustomerDTO;
  lead: LeadDTO;
}

export interface ConvertQuoteRequest {
  orderDate?: string;
  requestedDate?: string;
  memo?: string;
  reference?: string;
  lines: OrderLineInput[];
}

export interface ConvertSuggestionsRequest {
  suggestionIds: number[];
  needByDate: string;
  departmentId?: number | null;
  costCenterId?: number | null;
  note?: string;
}

export interface CoolingOffPolicyDTO {
  id: number;
  subject: string;
  companyId?: number | null;
  companyCode?: string;
  hours: number;
  isActive: boolean;
  isDefault: boolean;
  subjectDefinition: CoolingOffSubjectDTO;
  rowVersion: number;
}

export interface CoolingOffSubjectDTO {
  key: string;
  module: string;
  label: string;
  approvePermission: string;
  releasePermission: string;
  reasonSet: string;
  defaultHours: number;
}

export interface CopyPriorYearRequest {
  sourceVersionId: number;
  upliftPercent?: string;
}

export interface CopyResult {
  linesCopied: number;
  lines: BudgetLineDTO[];
  skipped?: string[];
}

export interface CorrectAssignmentRequest {
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  clearManager?: boolean;
  positionId?: number | null;
  clearPosition?: boolean;
  locationId?: number | null;
  clearLocation?: boolean;
  employmentType?: string | null;
  employmentStatus?: string | null;
  payGroupId?: number | null;
  shiftPatternId?: number | null;
  basicSalary?: string | null;
  currency?: string | null;
  pensionable?: boolean | null;
  fte?: string | null;
  probationEndOn?: string | null;
  noticeDays?: number | null;
  effectiveFrom?: string | null;
  note?: string;
  rowVersion: number;
}

export interface CorrectClockEventRequest {
  kind: string;
  happenedAt: string;
  rosterLineId?: number | null;
  locationId?: number | null;
  reasonCode: string;
  note?: string;
}

export interface CostAbsorptionRateDTO {
  id: number;
  companyId: number;
  poolId: number;
  poolCode: string;
  driverId: number;
  driverCode: string;
  fiscalPeriodId: number;
  periodName: string;
  budgetedCost: string;
  budgetedQuantity: string;
  rate: string;
  varianceAccountId: number;
  varianceAccountCode?: string;
  status: 'draft' | 'active' | 'superseded';
  note: string;
  rowVersion: number;
}

export interface CostAbsorptionVarianceDTO {
  id: number;
  runId: number;
  runNo?: string;
  poolId: number;
  poolCode: string;
  rateId: number;
  actualCost: string;
  actualQuantity: string;
  appliedCost: string;
  totalVariance: string;
  spendVariance: string;
  volumeVariance: string;
  journalEntryId?: number | null;
}

export interface CostAbsorptionVarianceReportDTO {
  companyId: number;
  fiscalPeriodId: number;
  periodName: string;
  currency: string;
  rows: CostAbsorptionVarianceDTO[];
  totalApplied: string;
  totalActual: string;
  totalVariance: string;
  totalSpendVariance: string;
  totalVolumeVariance: string;
}

export interface CostCycleDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  method: 'direct' | 'step_down' | 'reciprocal';
  status: 'draft' | 'active' | 'archived';
  journalTypeId?: number | null;
  journalTypeCode?: string;
  maxIterations: number;
  convergenceTolerance: string;
  postAutomatically: boolean;
  isActive: boolean;
  stepCount: number;
  rowVersion: number;
}

export interface CostCycleDetailDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  method: 'direct' | 'step_down' | 'reciprocal';
  status: 'draft' | 'active' | 'archived';
  journalTypeId?: number | null;
  journalTypeCode?: string;
  maxIterations: number;
  convergenceTolerance: string;
  postAutomatically: boolean;
  isActive: boolean;
  stepCount: number;
  rowVersion: number;
  steps: CostCycleStepDTO[];
}

export interface CostCycleStepDTO {
  id: number;
  cycleId: number;
  seq: number;
  poolId: number;
  poolCode: string;
  poolName: string;
  basis: 'driver' | 'fixed_percent';
  driverId?: number | null;
  driverCode?: string;
  excludeSelf: boolean;
  absorbedAccountId?: number | null;
  description: string;
  receivers: CostStepReceiverDTO[];
}

export interface CostCycleStepInput {
  seq: number;
  poolId: number;
  basis?: string;
  driverId?: number | null;
  excludeSelf?: boolean | null;
  absorbedAccountId?: number | null;
  description?: string;
  receivers?: CostStepReceiverInput[];
}

export interface CostCycleValidateQuery {
  fiscalPeriodId?: number;
}

export interface CostCycleValidationDTO {
  cycleId: number;
  method: 'direct' | 'step_down' | 'reciprocal';
  valid: boolean;
  fiscalPeriodId?: number | null;
  findings: CostCycleValidationFindingD[];
}

export interface CostCycleValidationFindingD {
  rule: number;
  code: string;
  message: string;
  stepSeq?: number | null;
  poolIds?: number[];
}

export interface CostDeleteQuery {
  rowVersion?: number;
}

export interface CostDriverDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  uom: string;
  description: string;
  isActive: boolean;
  rowVersion: number;
}

export interface CostDriverQuantityDTO {
  id: number;
  driverId: number;
  fiscalPeriodId: number;
  object: CostObjectRefDTO;
  quantity: string;
  source: 'manual' | 'import' | 'api';
  note: string;
  rowVersion: number;
}

export interface CostDriverQuantityInput {
  objectType: string;
  objectId: number;
  quantity: string;
  note?: string;
}

export interface CostImportRejectedDTO {
  line: number;
  reason: string;
  raw: string;
}

export interface CostObjectProfitabilityDTO {
  companyId: number;
  from: string;
  to: string;
  currency: string;
  rows: CostObjectProfitabilityRowDTO[];
  totalRevenue: string;
  totalDirectCost: string;
  totalAllocatedCost: string;
  totalMargin: string;
}

export interface CostObjectProfitabilityRowDTO {
  object: CostObjectRefDTO;
  revenue: string;
  directCost: string;
  allocatedCost: string;
  totalCost: string;
  margin: string;
  marginPercent?: string;
}

export interface CostObjectRefDTO {
  type: 'cost_center' | 'profit_center' | 'department' | 'business_unit' | 'division' | 'branch' | 'project' | 'item' | 'company' | 'pool';
  id: number;
  code?: string;
  name?: string;
}

export interface CostPoolBalanceDTO {
  poolId: number;
  poolCode: string;
  fiscalPeriodId: number;
  periodName: string;
  startDate: string;
  endDate: string;
  primaryCost: string;
  lines: CostPoolBalanceLineDTO[];
  currency: string;
}

export interface CostPoolBalanceLineDTO {
  sourceId: number;
  accountId: number;
  accountCode: string;
  accountName: string;
  sign: number;
  movement: string;
  amount: string;
}

export interface CostPoolBalanceQuery {
  fiscalPeriodId?: number;
}

export interface CostPoolDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  poolKind: 'service' | 'overhead' | 'primary';
  object?: CostObjectRefDTO | null;
  clearingAccountId: number;
  clearingAccountCode?: string;
  absorbedAccountId: number;
  absorbedAccountCode?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface CostPoolDetailDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  poolKind: 'service' | 'overhead' | 'primary';
  object?: CostObjectRefDTO | null;
  clearingAccountId: number;
  clearingAccountCode?: string;
  absorbedAccountId: number;
  absorbedAccountCode?: string;
  isActive: boolean;
  rowVersion: number;
  sources: CostPoolSourceDTO[];
}

export interface CostPoolSourceDTO {
  id: number;
  poolId: number;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  projectId?: number | null;
  sign: number;
}

export interface CostPoolSourceInput {
  accountId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  projectId?: number | null;
  sign?: number;
}

export interface CostProfitabilityQuery {
  companyId?: number;
  from?: string;
  to?: string;
  objectType?: string;
  objectId?: number;
}

export interface CostQuantityImportResultDTO {
  imported: number;
  replaced: number;
  rejected: CostImportRejectedDTO[];
  quantities: CostDriverQuantityDTO[];
}

export interface CostRunAbsorptionDTO {
  receiver: CostObjectRefDTO;
  exact: string;
  amount: string;
}

export interface CostRunAllocationDTO {
  id: number;
  runId: number;
  stepSeq: number;
  senderPoolId: number;
  senderPoolCode: string;
  receiver: CostObjectRefDTO;
  receiverPoolId?: number | null;
  isInternal: boolean;
  driverQuantity: string;
  share: string;
  exactAmount: string;
  amount: string;
}

export interface CostRunDTO {
  id: number;
  companyId: number;
  cycleId: number;
  cycleCode: string;
  fiscalPeriodId: number;
  periodName: string;
  runNo?: string;
  status: 'draft' | 'calculated' | 'posted' | 'reversed' | 'failed';
  method: 'direct' | 'step_down' | 'reciprocal';
  iterations: number;
  converged: boolean;
  maxDelta: string;
  totalPrimary: string;
  totalAbsorbed: string;
  residual: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  reversesRunId?: number | null;
  reversedByRunId?: number | null;
  calculatedAt?: string;
  postedAt?: string;
  note: string;
  rowVersion: number;
}

export interface CostRunDetailDTO {
  id: number;
  companyId: number;
  cycleId: number;
  cycleCode: string;
  fiscalPeriodId: number;
  periodName: string;
  runNo?: string;
  status: 'draft' | 'calculated' | 'posted' | 'reversed' | 'failed';
  method: 'direct' | 'step_down' | 'reciprocal';
  iterations: number;
  converged: boolean;
  maxDelta: string;
  totalPrimary: string;
  totalAbsorbed: string;
  residual: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  reversesRunId?: number | null;
  reversedByRunId?: number | null;
  calculatedAt?: string;
  postedAt?: string;
  note: string;
  rowVersion: number;
  pools: CostRunPoolDTO[];
  absorbed: CostRunAbsorptionDTO[];
  variances: CostAbsorptionVarianceDTO[];
}

export interface CostRunExportQuery {
  includeInternal?: boolean;
}

export interface CostRunIterationDTO {
  iterationNo: number;
  poolId: number;
  poolCode: string;
  total: string;
  delta: string;
}

export interface CostRunPoolDTO {
  poolId: number;
  poolCode: string;
  poolName: string;
  seq: number;
  primaryCost: string;
  receivedCost: string;
  reciprocalTotal: string;
  allocatedOut: string;
}

export interface CostStepReceiverDTO {
  id: number;
  stepId: number;
  lineNo: number;
  object: CostObjectRefDTO;
  fixedPercent: string;
  description: string;
}

export interface CostStepReceiverInput {
  objectType: string;
  objectId: number;
  fixedPercent: string;
  description?: string;
}

export interface CostVarianceReportQuery {
  companyId?: number;
  fiscalPeriodId?: number;
  poolId?: number;
}

export interface CountRootCauseCodeDTO {
  id: number;
  code: string;
  description: string;
  category: 'process' | 'people' | 'system' | 'physical' | 'external';
  requiresAttachment: boolean;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface CountSessionRequest {
  companyId: number;
  warehouseId: number;
  countDate: string;
  blind?: boolean | null;
  itemIds?: number[];
  countMethod?: string;
  zoneId?: number | null;
  freezeScope?: boolean;
  recountThresholdPct?: string;
  recountThresholdValue?: string;
  requireDifferentCounter?: boolean | null;
}

export interface CountVarianceReportDTO {
  sessionId: number;
  sessionNo?: string | null;
  warehouseId: number;
  countDate: string;
  countMethod: 'full' | 'abc' | 'zone' | 'item' | 'expiring' | 'spot';
  status: 'open' | 'counting' | 'recount' | 'review' | 'posted' | 'cancelled';
  totalVarianceValue: string;
  lines: CountVarianceReportLineDTO[];
  linesCounted: number;
  linesRecounted: number;
}

export interface CountVarianceReportLineDTO {
  itemId: number;
  itemSku?: string;
  systemQty: string;
  acceptedQty: string;
  varianceQty: string;
  varianceValue: string;
  acceptedSource?: string;
  varianceReasonCode?: string;
  rootCauseCode?: string;
}

export interface Country {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  iso2: string;
  iso3: string;
  numericCode: string;
  name: string;
  officialName: string;
  defaultCurrency: string;
  phoneCc: string;
  defaultTimezone: string;
  addressValidation: 'enforce' | 'warn' | 'allow';
  subdivisionLabel: string;
  localityLabel: string;
  addressFormat: Record<string, unknown>;
  postalCodePattern: string;
  postalCodeLabel: string;
  requiresGeoLocality: boolean;
  isActive: boolean;
}

export interface CountryRequest {
  iso2: string;
  iso3: string;
  numericCode?: string;
  name: string;
  officialName?: string;
  defaultCurrency: string;
  phoneCc?: string;
  defaultTimezone?: string;
  addressValidation?: string;
  subdivisionLabel?: string;
  localityLabel?: string;
  addressFormat?: Record<string, unknown>;
  postalCodePattern?: string;
  postalCodeLabel?: string;
  requiresGeoLocality?: boolean;
}

export interface CountryUpdateRequest {
  name?: string | null;
  officialName?: string | null;
  defaultCurrency?: string | null;
  phoneCc?: string | null;
  defaultTimezone?: string | null;
  addressValidation?: string | null;
  subdivisionLabel?: string | null;
  localityLabel?: string | null;
  isActive?: boolean | null;
  addressFormat?: Record<string, unknown> | null;
  postalCodePattern?: string | null;
  postalCodeLabel?: string | null;
  requiresGeoLocality?: boolean | null;
  rowVersion: number;
}

export interface Coverage {
  providers: string[];
  metricsRequested: string[];
  metricsAnswered: string[];
  metricsUnanswered: string[];
  datesWithNoSignal: string[];
}

export interface CoverageMinimumDTO {
  id: number;
  companyId: number;
  departmentId?: number | null;
  locationId?: number | null;
  minOnDuty: number;
  weekdayMask: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface CreateAIBacktestRequest {
  companyId: number;
  metricCode?: string;
  subjectModule?: string;
  subjectEntity?: string;
  subjectId?: number | null;
  subjectLabel?: string;
  method: 'seasonal_naive' | 'ewma' | 'drift' | 'mean';
  params?: Record<string, string>;
  seed?: number;
  trainFrom: string;
  trainTo: string;
  scoreFrom: string;
  scoreTo: string;
  horizons?: number[];
  history: AIBacktestHistoryPointDTO[];
}

export interface CreateAccountRequest {
  companyId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentId?: number | null;
  isPostable?: boolean | null;
  currency?: string | null;
  description?: string;
  sortOrder?: number;
  requiresCostCenter: boolean;
  requiresProfitCenter: boolean;
  requiresBranch: boolean;
  requiresDepartment: boolean;
  requiresProject: boolean;
  requiresDivision: boolean;
}

export interface CreateAccountingMethodRequest {
  code: string;
  name: string;
  description: string;
}

export interface CreateAccrualPositionRequest {
  companyId: number;
  code: string;
  name: string;
  kind: string;
  accountId: number;
  counterAccountId?: number | null;
  ownerPositionId?: number | null;
  reversePolicy?: string;
  maxAgeDays?: number;
  blocksClose?: boolean | null;
}

export interface CreateActivityRequest {
  leadId?: number | null;
  opportunityId?: number | null;
  caseId?: number | null;
  kind: string;
  subject?: string;
  body?: string;
  isPublic?: boolean;
  dueAt?: string | null;
}

export interface CreateAdjustmentRequest {
  companyId: number;
  warehouseId: number;
  reasonCode: string;
  adjustmentDate: string;
  memo?: string;
  lines: AdjustmentLineInput[];
}

export interface CreateAmendmentRequest {
  reason: string;
  expectedDate?: string;
  memo?: string | null;
  lines: POLineInput[];
}

export interface CreateAssetRequest {
  companyId: number;
  classId: number;
  name: string;
  description?: string;
  location?: string;
  custodian?: string;
  branchId?: number | null;
  costCenterId?: number | null;
  serialNo?: string;
  tagNo?: string;
  insurancePolicy?: string;
  insuranceExpiry?: string;
  warrantyExpiry?: string;
  sourceRef?: string;
  method?: 'SL' | 'DB' | 'SYD' | 'UOP';
  usefulLifeMonths?: number;
  salvageValue?: string;
  cost: string;
  inServiceDate: string;
  creditAccountId: number;
  memo?: string;
}

export interface CreateAssignmentRequest {
  companyId: number;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  positionId?: number | null;
  locationId?: number | null;
  employmentType: string;
  employmentStatus?: string;
  payGroupId?: number | null;
  shiftPatternId?: number | null;
  basicSalary: string;
  currency: string;
  pensionable?: boolean | null;
  fte?: string;
  probationEndOn?: string;
  noticeDays?: number | null;
  effectiveFrom: string;
  effectiveTo?: string;
  reasonCode: string;
  note?: string;
}

export interface CreateAssignmentRuleRequest {
  name: string;
  sortOrder: number;
  matchCategory?: string;
  matchPriority?: string;
  assignToUserId: number;
}

export interface CreateAttendanceRequest {
  employeeId: number;
  workDate: string;
  hoursWorked: string;
  notes?: string;
}

export interface CreateAuthorityDelegationRequest {
  fromPositionId: number;
  toPositionId?: number | null;
  toEmployeeId?: number | null;
  docType?: string;
  maxAmount?: string;
  currency?: string;
  effectiveFrom: string;
  effectiveTo: string;
  reasonCode: string;
  note?: string;
}

export interface CreateBOMRequest {
  companyId: number;
  itemId: number;
  name?: string;
  notes?: string;
  lines: BOMLineInput[];
}

export interface CreateBankAccountRequest {
  companyId: number;
  code: string;
  name: string;
  glAccountId: number;
  kind?: 'bank' | 'cash';
  currency?: string;
  bankName?: string;
  branchName?: string;
  accountNumber?: string;
  iban?: string;
  swift?: string;
  openingBalance?: string;
  openingDate?: string;
  bankId?: number | null;
  liquidityClass?: 'unrestricted' | 'restricted' | 'pledged' | 'escrowed' | 'trapped';
  valueDateToleranceDays?: number | null;
  dormancyDays?: number | null;
  statementStalenessDays?: number | null;
}

export interface CreateBeneficiaryRequest {
  companyId: number;
  code: string;
  name: string;
  counterpartyKind?: 'vendor' | 'employee' | 'customer' | 'tax_authority' | 'related_company' | 'other';
  counterpartyId?: number | null;
  vendorBankAccountId?: number | null;
  bankName?: string;
  branchName?: string;
  accountName?: string;
  accountNumber?: string;
  iban?: string;
  swift?: string;
  currency?: string;
  countryCode?: string;
  note?: string;
}

export interface CreateBinRequest {
  code: string;
  name?: string;
  zoneId?: number | null;
  storageClass?: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  maxQty?: string;
  maxWeightKg?: string;
  maxVolumeM3?: string;
  binKind?: 'storage' | 'picking' | 'bulk' | 'staging' | 'quarantine';
  pickSequence?: number;
}

export interface CreateBookRequest {
  companyId: number;
  code: string;
  name: string;
  bookKind?: 'accounting' | 'tax' | 'management' | 'other';
  convention?: 'full_month' | 'half_year' | 'mid_month' | 'mid_quarter' | 'actual_days';
  currency?: string;
  description?: string;
  postsToGl?: boolean;
  ledgerId?: number | null;
}

export interface CreateBusinessUnitRequest {
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  primaryLegalEntityId: number;
  defaultLocationId: number;
  managerPositionId: number;
  divisionId?: number | null;
  ledgerId?: number | null;
}

export interface CreateCAPARequest {
  companyId: number;
  title: string;
  description?: string;
  kind: string;
  severity?: string;
  sourceKind: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  vendorId?: number | null;
  itemId?: number | null;
  ownerUserId?: number | null;
  dueOn?: string;
  effectivenessDueOn?: string;
}

export interface CreateCRMAccountRequest {
  partyId?: number | null;
  name: string;
  parentId?: number | null;
  segmentId?: number | null;
  ownerUserId?: number | null;
  accountType?: string;
  status?: string;
  industry?: string;
  territory?: string;
  website?: string;
  employeeCount?: number | null;
  annualRevenue?: string;
  annualRevenueCurrency?: string;
  companyId?: number | null;
  notes?: string;
}

export interface CreateCRMAccountSegmentRequest {
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
}

export interface CreateCRMCloseReasonRequest {
  code: string;
  name: string;
  outcome: string;
  requiresCompetitor?: boolean;
  requiresNote?: boolean;
  sortOrder?: number;
}

export interface CreateCRMCompetitorRequest {
  code: string;
  name: string;
  notes?: string;
}

export interface CreateCRMContactRequest {
  partyId?: number | null;
  givenName?: string;
  familyName?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  reportsToContactId?: number | null;
  ownerUserId?: number | null;
  status?: string;
  notes?: string;
}

export interface CreateCRMContactRoleRequest {
  code: string;
  name: string;
  isDecisionMaker?: boolean;
  isBilling?: boolean;
  sortOrder?: number;
}

export interface CreateCRMForecastCategoryRequest {
  code: string;
  name: string;
  sortOrder?: number;
  countsInCommit?: boolean;
  countsInBestCase?: boolean;
}

export interface CreateCampaignAttributionRequest {
  campaignId: number;
  partyId: number;
  objectType: string;
  objectId: number;
  touchAt?: string;
}

export interface CreateCampaignResponseRequest {
  campaignId: number;
  sendId?: number | null;
  partyId: number;
  kind: string;
  respondedAt?: string;
  note?: string;
  leadId?: number | null;
  opportunityId?: number | null;
  caseId?: number | null;
}

export interface CreateCandidateRequest {
  requisitionId?: number | null;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  source?: string;
  notes?: string;
}

export interface CreateCapturePolicyRequest {
  companyId: number;
  walletId?: number | null;
  categoryCode: string;
  categoryName: string;
  expenseAccountId?: number | null;
  defaultTaxCode?: string;
  perTxnLimit?: string;
  dailyLimit?: string;
  monthlyLimit?: string;
  requiresEvidence?: boolean | null;
  evidenceFreeCeiling?: string;
  requiresApproval?: boolean | null;
  isBlocked?: boolean;
  effectiveFrom?: string;
}

export interface CreateCardAccountRequest {
  companyId: number;
  code: string;
  issuer: string;
  maskedPan: string;
  holderEmployeeId?: number | null;
  currency: string;
  clearingAccountId: number;
  issuerVendorId?: number | null;
}

export interface CreateCaseRequest {
  subject: string;
  description?: string;
  origin?: string;
  category?: string;
  priority?: string;
  customerId?: number | null;
  companyId?: number | null;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  assignedTo?: number | null;
  slaPolicyId?: number | null;
}

export interface CreateCertificateRequest {
  partyKind: string;
  partyId: number;
  companyId?: number | null;
  jurisdictionId: number;
  regimeId?: number | null;
  certificateNo: string;
  scope: string;
  itemGroupId?: number | null;
  taxCode?: string | null;
  exemptTreatment: string;
  exemptTaxCode?: string | null;
  validFrom: string;
  validTo?: string | null;
  status: string;
  attachmentId?: number | null;
  issuedBy: string;
  notes: string;
}

export interface CreateCharacteristicRequest {
  seqNo?: number;
  code: string;
  name: string;
  kind: string;
  specMin?: string;
  specMax?: string;
  specTarget?: string;
  uomCode?: string;
  decimals?: number | null;
  allowedValues?: string[];
  severity?: string;
  isMandatory?: boolean | null;
}

export interface CreateCheckpointRequest {
  code: string;
  name: string;
  companyId: number;
  warehouseId?: number | null;
  zoneId?: number | null;
  kind?: 'temperature' | 'humidity' | 'cleaning' | 'receiving' | 'cooking' | 'cooling' | 'visual';
  minValue?: string;
  maxValue?: string;
  unit?: string;
  frequency?: 'per_shift' | 'daily' | 'twice_daily' | 'weekly' | 'on_receipt';
  correctiveAction?: string;
  isActive?: boolean | null;
}

export interface CreateClarificationRequest {
  question: string;
}

export interface CreateClassRequest {
  companyId: number;
  code: string;
  name: string;
  defaultMethod?: 'SL' | 'DB' | 'SYD' | 'UOP';
  defaultUsefulLifeMonths: number;
  defaultSalvagePercent?: string;
  assetAccountId: number;
  accumulatedDepreciationAccountId: number;
  depreciationExpenseAccountId: number;
  gainLossAccountId: number;
  impairmentExpenseAccountId?: number;
  impairmentReversalAccountId?: number;
}

export interface CreateCloseCalendarRequest {
  companyId: number;
  code: string;
  name: string;
  scope?: string;
  targetWorkingDays?: number;
  ownerPositionId?: number | null;
  seedDefaults?: boolean;
}

export interface CreateCodeRequest {
  code: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE';
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  basis: 'percent' | 'per_unit';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  unitCurrency: string | null;
  unitLabel: string | null;
  jurisdiction: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  inputAccountCode: string | null;
  outputAccountCode: string | null;
  payableAccountCode: string | null;
  rounding: 'line' | 'document';
  roundingMode: 'half_up' | 'half_even';
  isRecoverable: boolean | null;
  description: string;
  isActive: boolean | null;
}

export interface CreateCompanyRequest {
  code: string;
  legalName: string;
  name: string;
  country: string;
  baseCurrency: string;
  taxRegistrations?: unknown[];
  address?: Record<string, unknown>;
  phone?: string;
  email?: string;
}

export interface CreateComplianceDocumentRequest {
  documentTypeCode: string;
  reference?: string;
  issuedOn?: string;
  expiresOn?: string;
  attachmentId?: number | null;
  notes?: string;
}

export interface CreateComponentRequest {
  code: string;
  name: string;
  componentType: 'earning' | 'deduction' | 'employer_cost';
  formulaType: 'fixed' | 'percent_of_base' | 'per_unit_overtime' | 'slab' | 'pool_share';
  fixedAmount?: string;
  percentOfBase?: string;
  base?: 'basic_salary' | 'pensionable_wage';
  overtimeBucket?: string;
  perUnitRate?: string;
  slabBands?: SlabBandDTO[];
  expenseAccountCode?: string;
  liabilityAccountCode?: string;
  sequence?: number;
  description?: string;
  companyId?: number | null;
  payGroupId?: number | null;
  wageClass?: 'basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other';
  poolCode?: string;
  settlementBasis?: 'expense' | 'liability_drawdown';
  effectiveFrom?: string;
}

export interface CreateConsentChannelRequest {
  code: string;
  name: string;
  medium: string;
  requiresDoubleOptIn?: boolean;
  isActive?: boolean | null;
  sortOrder?: number;
}

export interface CreateConsentPurposeRequest {
  code: string;
  name: string;
  description?: string;
  defaultBasis?: string;
  isEssential?: boolean;
  defaultValidityDays?: number | null;
  isActive?: boolean | null;
  sortOrder?: number;
}

export interface CreateContractorTimesheetRequest {
  weekStartDate: string;
  lines: PortalTimesheetLineInput[];
}

export interface CreateConversationRequest {
  title?: string;
}

export interface CreateCostCycleRequest {
  companyId: number;
  code: string;
  name: string;
  method?: string;
  journalTypeCode?: string;
  maxIterations?: number;
  convergenceTolerance?: string;
  postAutomatically?: boolean;
  steps?: CostCycleStepInput[];
}

export interface CreateCostDriverRequest {
  companyId: number;
  code: string;
  name: string;
  uom?: string;
  description?: string;
}

export interface CreateCostPoolRequest {
  companyId: number;
  code: string;
  name: string;
  poolKind?: string;
  objectType?: string;
  objectId?: number | null;
  clearingAccountId: number;
  absorbedAccountId: number;
  sources?: CostPoolSourceInput[];
}

export interface CreateCovenantRequest {
  code: string;
  name: string;
  covenantKind?: 'financial' | 'information' | 'affirmative' | 'negative';
  metricKey?: string;
  operator?: string;
  threshold?: string;
  testFrequency?: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'at_maturity';
  graceDays?: number;
  description?: string;
}

export interface CreateCoverageMinimumRequest {
  companyId: number;
  departmentId?: number | null;
  locationId?: number | null;
  minOnDuty: number;
  weekdayMask?: string;
  effectiveFrom: string;
}

export interface CreateCrossChargeRuleRequest {
  toDivisionId: number;
  pricingMethod: string;
  rate?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  recoveryAccountId?: number | null;
  effectiveFrom?: string;
}

export interface CreateCrossValidationRuleRequest {
  ruleName: string;
  includeCondition?: SegmentCondition[];
  excludeCondition: SegmentCondition[];
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface CreateCurrencyRequest {
  code: string;
  name: string;
  symbol: string;
  minorUnits: number | null;
  isActive: boolean | null;
}

export interface CreateCustomerAssetRequest {
  customerId: number;
  name: string;
  category?: string;
  modelNo?: string;
  serialNo?: string;
  itemSerialId?: number | null;
  location?: string;
  purchasedAt?: string;
  warrantyExpiresAt?: string;
  notes?: string;
}

export interface CreateCustomerRequest {
  code: string;
  name: string;
  legalName?: string;
  taxGroupId?: number | null;
  priceTier?: string;
  currency: string;
  paymentTermsDays?: number;
  paymentTermsCode?: string;
  creditLimit?: string;
  creditLimitCurrency?: string;
  overdueBlockDays?: number;
  portalAccess?: boolean;
  email?: string;
  phone?: string;
  address?: string | null;
  notes?: string;
}

export interface CreateDSRPolicyRequest {
  jurisdiction: string;
  kind: string;
  responseDays: number;
  extensionDays: number;
  warnDaysBefore: number;
  identityVerificationRequired?: boolean | null;
  statuteRef?: string;
}

export interface CreateDSRRequest {
  partyId: number;
  kind: string;
  channel?: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterIsSubject?: boolean | null;
  requestText?: string;
  receivedAt?: string;
  companyId?: number | null;
}

export interface CreateDelegationRequest {
  toUserId: number;
  docType?: string;
  startsOn: string;
  endsOn: string;
  reason?: string;
}

export interface CreateDesignationRequest {
  companyId: number;
  kind: 'PSU' | 'TRU' | 'LE';
  jurisdictionId: number;
  registrationId?: number | null;
  identifier?: string;
  effectiveFrom: string;
  note?: string;
}

export interface CreateDeviceRequest {
  companyId: number;
  code: string;
  name: string;
  surface: string;
  bindingKind: string;
  bindingId: number;
}

export interface CreateDispositionRequest {
  kind: string;
  quantity: string;
  reasonCode?: string;
  reason?: string;
  outcomeDocType?: string;
  outcomeDocId?: number | null;
  mfgNcrId?: number | null;
}

export interface CreateDivisionFNDRequest {
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  enterpriseId?: number | null;
  purpose?: string;
  ownerId?: number | null;
  defaultCurrency?: string;
}

export interface CreateDrawdownRequest {
  valueDate: string;
  amount: string;
  currency?: string;
  fixedRate?: string;
  bankAccountId?: number | null;
  transferId?: number | null;
  reference?: string;
  memo?: string;
  override?: DebtOverride | null;
}

export interface CreateEAMAssetLinkRequest {
  linkKind: 'pms.room' | 'mfg.work_center' | 'service.resource' | 'inventory.warehouse';
  linkRef: number;
  linkLabel?: string;
}

export interface CreateEAMAssetRequest {
  companyId: number;
  name: string;
  description?: string;
  functionalLocationId?: number | null;
  parentAssetId?: number | null;
  financeAssetId?: number | null;
  classCode?: string;
  manufacturer?: string;
  modelNo?: string;
  serialNo?: string;
  criticality?: number;
  ownership?: 'owned' | 'leased' | 'customer';
  warehouseId?: number | null;
  costCenterId?: number | null;
}

export interface CreateEAMComplianceRequirementRequest {
  companyId: number;
  assetId: number;
  code: string;
  name: string;
  authority?: string;
  jurisdictionId?: number | null;
  taskListId?: number | null;
  intervalMonths: number;
  nextDueOn: string;
  certificateRef?: string;
  certificateAttachmentId?: number | null;
  blocksOperation?: boolean | null;
  warnDays?: number | null;
  isActive?: boolean | null;
}

export interface CreateEAMDowntimeRequest {
  assetId: number;
  kind: 'planned' | 'unplanned' | 'compliance';
  state?: 'unavailable' | 'degraded';
  reasonCode: string;
  note?: string;
  startedAt?: string;
  endedAt?: string;
  workOrderId?: number | null;
}

export interface CreateEAMFailureRequest {
  assetId: number;
  detectedAt?: string;
  failureModeCode: string;
  causeCode?: string;
  severity?: 'minor' | 'major' | 'critical';
  description?: string;
}

export interface CreateEAMFunctionalLocationRequest {
  companyId: number;
  parentId?: number | null;
  locationId?: number | null;
  code: string;
  name: string;
  description?: string;
  isActive?: boolean | null;
}

export interface CreateEAMLifecycleEventRequest {
  kind: 'commissioned' | 'baselined' | 'modified' | 'relocated' | 'decommissioned';
  occurredOn?: string;
  note?: string;
  attachmentId?: number | null;
  financeDocType?: string;
  financeDocId?: number | null;
}

export interface CreateEAMMeterReadingRequest {
  readOn: string;
  reading: string;
  note?: string;
  overrideReason?: string;
  overrideNote?: string;
}

export interface CreateEAMMeterRequest {
  assetId: number;
  code: string;
  name: string;
  unit: string;
  rollsOver?: boolean;
  rolloverAt?: string;
}

export interface CreateEAMPMPlanRequest {
  companyId: number;
  assetId: number;
  strategyId?: number | null;
  name: string;
  defaultTaskListId?: number | null;
  intervalDays?: number | null;
  nextDueOn?: string;
  meterId?: number | null;
  intervalMeterUnits?: string;
  leadDays?: number | null;
  isActive?: boolean | null;
}

export interface CreateEAMPermitRequest {
  companyId: number;
  assetId: number;
  kind: 'hot_work' | 'confined_space' | 'electrical' | 'height' | 'lifting' | 'general';
  description?: string;
  hazards?: string;
  precautions?: string;
  validFrom: string;
  validTo: string;
}

export interface CreateEAMStrategyRequest {
  companyId: number;
  code: string;
  name: string;
  kind: 'run_to_failure' | 'time_based' | 'usage_based' | 'condition_based';
  description?: string;
  isActive?: boolean | null;
}

export interface CreateEAMTaskListRequest {
  companyId: number;
  code: string;
  name: string;
  description?: string;
  estimatedHours?: string;
  requiresPermit?: boolean;
  permitKind?: string;
  isActive?: boolean | null;
}

export interface CreateECLAssessmentRequest {
  companyId: number;
  customerId: number;
  asOf?: string;
  eclAmount: string;
  basis: string;
  reasonCode: string;
  note?: string;
  attachmentId?: number | null;
}

export interface CreateECLMatrixRequest {
  companyId: number;
  segmentId: number;
  code: string;
  macroFactor?: string;
  macroBasis?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CreateECLSegmentRequest {
  companyId: number;
  code: string;
  name: string;
  matchKind: string;
  matchValue?: string;
  priority?: number;
}

export interface CreateECORequest {
  companyId: number;
  title: string;
  description?: string;
  reasonKind: 'design' | 'cost_reduction' | 'quality' | 'obsolescence' | 'supplier_change' | 'regulatory';
  urgency?: 'routine' | 'expedited' | 'emergency';
  effectiveFrom?: string;
  effectiveFromOrderId?: number | null;
  estimatedCostImpact?: string;
  ownerUserId?: number | null;
  items?: ECOChangeItemInput[];
}

export interface CreateEliminationRuleRequest {
  companyId: number;
  counterpartyCompanyId: number;
  consolGroupId?: number | null;
  ruleType: string;
  accounts: eliminationAccountEntry[];
  effectiveFrom: string;
}

export interface CreateEmployeeRequest {
  companyId: number;
  departmentId?: number | null;
  branchId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  userId?: number | null;
  firstName: string;
  lastName: string;
  preferredName?: string;
  title?: string;
  email?: string;
  phone?: string;
  personalEmail?: string;
  employmentType: string;
  joinedOn: string;
  basicSalary: string;
  currency: string;
  pensionable?: boolean | null;
  nationality?: string;
  isExpatriate?: boolean;
  workPermitNo?: string;
  workPermitExpiry?: string;
  notes?: string;
}

export interface CreateEnterpriseRequest {
  code: string;
  enterpriseType: string;
  residencyRegion?: string;
  legalName: string;
  brandName?: string;
  primaryCountry: string;
  countries?: string[];
  defaultCurrency: string;
  defaultTimezone: string;
  defaultLanguage: string;
  dateFormat: string;
  numberFormat: string;
  weekStart: string;
  fiscalAnchorMonth?: number | null;
  effectiveFrom: string;
  changeSummary?: string;
}

export interface CreateEntryRequest {
  companyId: number;
  entryDate: string;
  postingDate?: string;
  currency?: string;
  memo?: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  journalTypeCode?: string;
  documentDate?: string;
  reversalDate?: string;
  rateType?: string;
  rate?: string;
  post?: boolean;
  lines: EntryLineInput[];
}

export interface CreateExpenseCategoryRequest {
  companyId: number;
  code: string;
  name: string;
  expenseAccountId: number;
  taxGroupId?: number | null;
  defaultTaxCode?: string;
  isMileage?: boolean;
  requiresReceiptAbove?: string;
}

export interface CreateExpenseClaimRequest {
  companyId: number;
  employeeId?: number;
  title: string;
  claimDate: string;
  periodFrom?: string;
  periodTo?: string;
  currency?: string;
  travelRequestId?: number | null;
  note?: string;
  settlementMethod?: string;
}

export interface CreateExpenseMileageRateRequest {
  companyId: number;
  vehicleClass: string;
  ratePerUnit: string;
  uom?: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CreateExpensePolicyRequest {
  companyId: number;
  categoryId: number;
  gradeId?: number | null;
  limitKind: string;
  limitAmount: string;
  currency: string;
  breachAction: string;
  requiresPreApproval?: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CreateFNDCalendarRequest {
  companyId: number;
  name: string;
  startMonth: number;
  frequency?: string;
  adjustingPeriods?: number | null;
  generateYear?: number;
  periods?: FNDPeriodInput[];
}

export interface CreateFNDCalendarResult {
  calendar: CalendarDTO;
  periods?: PeriodDTO[];
}

export interface CreateFacilityRequest {
  companyId: number;
  code: string;
  name: string;
  facilityKind: 'term_loan' | 'revolving' | 'overdraft' | 'guarantee' | 'demand_loan' | 'lease_liability' | 'bond' | 'intercompany_loan';
  bankId?: number | null;
  lenderName?: string;
  lenderVendorId?: number | null;
  icInstrumentId?: number | null;
  securityDescription?: string;
  agreementRef?: string;
  attachmentId?: number | null;
  currency: string;
  facilityLimit: string;
  rateKind?: 'fixed' | 'floating';
  baseRateCode?: string;
  fixedRate?: string;
  marginRate?: string;
  rateFloor?: string;
  rateCap?: string;
  dayCount?: 'act_365' | 'act_360' | 'thirty_360';
  interestFrequency?: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'at_maturity';
  commitmentFeeRate?: string;
  arrangementFeeTreatment?: 'expense' | 'amortise';
  arrangementFeeAmount?: string;
  startDate: string;
  maturityDate: string;
  defaultBankAccountId?: number | null;
  principalAccountId?: number | null;
  principalCurrentAccountId?: number | null;
  interestExpenseAccountId?: number | null;
  interestPayableAccountId?: number | null;
  feeExpenseAccountId?: number | null;
  costCenterId?: number | null;
  notes?: string;
}

export interface CreateForecastSnapshotRequest {
  companyId?: number | null;
  ownerUserId?: number | null;
  periodCode: string;
  currency: string;
  snapshotOn?: string;
}

export interface CreateGRNRequest {
  poId: number;
  receiptDate?: string;
  memo?: string;
  lines: GRNLineInput[];
}

export interface CreateGroupRequest {
  code: string;
  name: string;
  appliesTo: 'party' | 'item';
  description: string;
  isActive: boolean | null;
}

export interface CreateHACCPRecordRequest {
  checkpointId: number;
  recordedOn: string;
  shiftCode?: string;
  reading?: string;
  outcome?: 'pass' | 'fail' | 'not_applicable';
  correctiveActionTaken?: string;
  lotId?: number | null;
  attachmentId?: number | null;
  note?: string;
}

export interface CreateHRSkillRequest {
  companyId: number;
  code: string;
  name: string;
  category?: string;
  description?: string;
  expires?: boolean;
  isStatutory?: boolean;
}

export interface CreateHierarchyRequest {
  enterpriseId: number;
  code: string;
  name: string;
  purpose: string;
  permittedNodeTypes: string[];
  depthLimit: number;
  isDefault: boolean;
}

export interface CreateHierarchyVersionRequest {
  effectiveFrom: string;
  effectiveTo: string;
  seedFromActive: boolean;
  changeSummary: string;
}

export interface CreateICReconPairRequest {
  companyId: number;
  counterpartyCompanyId: number;
  ownerPositionId?: number | null;
  tolerance?: string;
  toleranceCurrency: string;
  maxAgeDays?: number;
  blocksClose?: boolean | null;
  requiresTp?: boolean | null;
}

export interface CreateImpairmentIndicatorRequest {
  companyId: number;
  assetId?: number;
  classId?: number;
  assessedOn: string;
  indicator: 'market_value_decline' | 'physical_damage' | 'obsolescence' | 'idle' | 'disposal_planned' | 'adverse_regulation' | 'economic_performance' | 'reversal_indicator' | 'other';
  note?: string;
}

export interface CreateImpairmentRequest {
  assetId: number;
  bookId: number;
  indicatorId?: number;
  impairedOn: string;
  recoverableAmount: string;
  reasonCode?: string;
  note?: string;
}

export interface CreateInspectionPlanRequest {
  companyId: number;
  code: string;
  name: string;
  description: string;
  originKind: string;
  itemId?: number | null;
  itemCategoryId?: number | null;
  vendorId?: number | null;
  samplingPlanId?: number | null;
  quarantineOnReceipt?: boolean | null;
  isBlocking?: boolean | null;
  requiresIndependentInspector?: boolean | null;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface CreateInstructionRequest {
  companyId: number;
  fromBankAccountId: number;
  toBankAccountId: number;
  beneficiaryId?: number | null;
  sendDate: string;
  receiptDate?: string;
  amount: string;
  toAmount?: string;
  fxAccountId?: number | null;
  icInstrumentId?: number | null;
  purposeCode?: string;
  reference?: string;
  memo?: string;
}

export interface CreateIntercompanyRuleRequest {
  companyId: number;
  counterpartyCompanyId: number;
  counterpartySegmentValue?: string;
  icReceivableAccountId: number;
  icPayableAccountId: number;
  balancingRule?: string;
  effectiveFrom: string;
}

export interface CreateInvoiceFromShipmentRequest {
  invoiceDate?: string;
  autoPost?: boolean | null;
}

export interface CreateInvoiceRequest {
  companyId: number;
  vendorId: number;
  vendorInvoiceNo: string;
  docKind?: 'invoice' | 'credit_note' | 'debit_note';
  invoiceDate: string;
  dueDate?: string;
  currency?: string;
  description?: string;
  memo?: string;
  originalInvoiceId?: number | null;
  lines: InvoiceLineInput[];
  poId?: number | null;
  dueDateOverrideReason?: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
}

export interface CreateItemRequest {
  sku: string;
  name: string;
  description?: string;
  type?: 'stocked' | 'non_stock' | 'service';
  baseUom: string;
  barcode?: string;
  taxGroupId?: number | null;
  defaultTaxCode?: string;
  trackBatch?: boolean;
  trackSerial?: boolean;
  reorderMin?: string;
  reorderMax?: string;
  reorderPoint?: string;
  lotPickStrategy?: 'fifo' | 'fefo' | 'manual';
  shelfLifeDays?: number | null;
  minRemainingShelfLifeDays?: number | null;
  requiresExpiry?: boolean;
  storageClass?: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  netWeightKg?: string;
  volumeM3?: string;
}

export interface CreateJournalTypeRequest {
  companyId: number;
  code: string;
  name: string;
  allowedSources?: string[];
  allowsManual?: boolean;
  reversalPolicy?: string;
  numberPrefix?: string;
  requiresApproval?: boolean;
  attachmentRequiredAbove?: string | null;
}

export interface CreateKBArticleRequest {
  slug?: string;
  title: string;
  category?: string;
  tags?: string[];
  body?: string;
}

export interface CreateLandedCostChargeRequest {
  elementId: number;
  description?: string;
  vendorId?: number | null;
  currency: string;
  amount: string;
  fxRate?: string;
  basis?: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  tariffClassificationId?: number | null;
  originCountry?: string;
  customsValue?: string;
  directAmounts?: LandedCostDirectAmountRequest[];
}

export interface CreateLandedCostElementRequest {
  companyId: number;
  code: string;
  name: string;
  chargeKind: 'fee' | 'disbursement';
  defaultBasis?: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  isDuty?: boolean;
  isAbsorbable?: boolean | null;
  expenseAccountId?: number | null;
}

export interface CreateLandedCostShipmentRequest {
  companyId: number;
  reference?: string;
  mode?: 'sea' | 'air' | 'road' | 'courier';
  vesselOrFlight?: string;
  billOfLading?: string;
  customsEntryNo?: string;
  originCountry?: string;
  etdOn?: string;
  etaOn?: string;
  clearedOn?: string;
  absorptionDate?: string;
  defaultBasis?: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  memo?: string;
}

export interface CreateLeadRequest {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  title?: string;
  email?: string;
  phone?: string;
  source?: string;
  ownerUserId?: number | null;
  notes?: string;
  countryCode?: string;
  campaignId?: number | null;
}

export interface CreateLeadResult {
  lead: LeadDTO;
  duplicateCandidates: DuplicateCandidate[];
}

export interface CreateLeadRoutingRuleRequest {
  seq: number;
  name: string;
  matchBand?: string;
  matchSource?: string;
  matchCountry?: string;
  matchSegmentId?: number | null;
  minScore?: number | null;
  assignUserId?: number | null;
  assignRoleCode?: string;
  assignStrategy?: string;
}

export interface CreateLeadScoreModelRequest {
  code: string;
  name: string;
  hotThreshold?: number;
  warmThreshold?: number;
}

export interface CreateLeadScoreRuleRequest {
  seq: number;
  field: string;
  operator: string;
  value?: string;
  points: number;
  label?: string;
}

export interface CreateLeaveBlackoutRequest {
  companyId: number;
  leaveTypeId?: number | null;
  departmentId?: number | null;
  locationId?: number | null;
  fromDate: string;
  toDate: string;
  severity?: string;
  reason: string;
}

export interface CreateLeaveRequestRequest {
  employeeId: number;
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  isUnpaid?: boolean;
  reason?: string;
}

export interface CreateLeaveTypeRequest {
  code: string;
  name: string;
  accrualDaysPerMonth?: string;
  maxBalanceDays?: string | null;
  isPaid?: boolean | null;
  requiresApproval?: boolean | null;
}

export interface CreateLedgerRequest {
  companyId: number;
  code: string;
  name: string;
  coaStructureId: number;
  calendarId: number;
  currency: string;
  accountingMethodId: number;
}

export interface CreateLedgerResult {
  ledger: LedgerDTO;
  warnings?: string[];
}

export interface CreateLedgerSetRequest {
  code: string;
  name: string;
  coaStructureId: number;
  calendarId?: number | null;
}

export interface CreateLegalEntityRequest {
  code: string;
  legalName: string;
  name: string;
  country: string;
  baseCurrency: string;
  entityType: string;
  incorporationDate: string;
  taxRegistrations?: unknown[];
  address?: Record<string, unknown>;
  phone?: string;
  email?: string;
}

export interface CreateLegislativeDataGroupRequest {
  code: string;
  name: string;
  country: string;
  currency: string;
  payrollCalendarId?: number | null;
}

export interface CreateLocationRequest {
  code: string;
  name: string;
  companyId?: number | null;
  type: 'office' | 'warehouse' | 'outlet' | 'property' | 'plant' | 'site' | 'virtual';
  parentLocationId?: number | null;
  country: string;
  line1: string;
  line2?: string;
  line3?: string;
  localityName?: string;
  subdivisionName?: string;
  postalCode?: string;
  subdivisionGeoRef?: string | null;
  localityGeoRef?: string | null;
  timezone?: string;
  isInventoryEnabled?: boolean;
  openedOn?: string;
  note?: string;
  waiverReasonCode?: string;
}

export interface CreateLotRecallRequest {
  companyId: number;
  title: string;
  severity: 'class_1' | 'class_2' | 'class_3' | 'withdrawal';
  reason?: string;
  lotIds: number[];
  initiatedOn: string;
  containBy?: string | null;
}

export interface CreateLoyaltyAccrualRequest {
  sourceType?: string;
  sourceId?: number;
  sourceKey?: string;
  eligibleAmount: string;
  currency?: string;
  businessOn?: string;
  note?: string;
}

export interface CreateLoyaltyProgramRequest {
  code: string;
  name: string;
  companyId?: number | null;
  pointsPerUnit?: string;
  currency: string;
  rounding?: string;
  status?: string;
  startsOn?: string;
  endsOn?: string;
}

export interface CreateLoyaltyTierRequest {
  code: string;
  name: string;
  sortOrder: number;
  minPoints?: number | null;
  minSpend?: string;
  earnMultiplier?: string;
}

export interface CreateMandateRequest {
  bankAccountId: number;
  code: string;
  name: string;
  signingRule?: 'any' | 'a_plus_b' | 'all';
  requiredSignatures?: number;
  minAmount?: string;
  maxAmount?: string;
  currency?: string;
  documentRef?: string;
  attachmentId?: number | null;
  notes?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CreateMarketingCampaignRequest {
  name: string;
  code: string;
  channelCode: string;
  purposeCode: string;
  companyId?: number | null;
  ownerUserId?: number | null;
  startsOn?: string;
  endsOn?: string;
  budgetAmount?: string;
  targetRevenue?: string;
  currency?: string;
  subject?: string;
  body?: string;
  templateKey?: string;
  locale?: string;
  attributionWindowDays?: number;
}

export interface CreateMarketingSegmentRequest {
  code: string;
  name: string;
  description?: string;
  kind?: string;
  criteria?: MarketingSegmentCriteria | null;
  ownerUserId?: number | null;
}

export interface CreateMatchSetRequest {
  sessionId: number;
  statementLineIds: number[];
  journalLineIds: number[];
  matchType?: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  note?: string;
}

export interface CreateMatrixVersionRequest {
  code: string;
  name: string;
  note?: string;
  cloneFromId?: number | null;
}

export interface CreateMenuVersionRequest {
  effectiveFrom: string;
  effectiveTo?: string;
  note?: string;
}

export interface CreateMfgToolRequest {
  companyId: number;
  code: string;
  name: string;
  workCenterId?: number | null;
  locationId?: number | null;
  lifeCycles?: number | null;
  nextServiceOn?: string | null;
  notes?: string;
}

export interface CreateMilestoneRequest {
  phaseId?: number | null;
  taskId?: number | null;
  name: string;
  sequence?: number;
  amount: string;
  dueDate?: string;
}

export interface CreateModelRequest {
  companyId: number;
  fiscalYear: number;
  name: string;
  purpose?: string;
  methodology?: 'incremental' | 'zero_based' | 'driver_based' | 'rolling';
  submissionDeadline?: string;
}

export interface CreateOfferRequest {
  candidateId: number;
  requisitionId?: number | null;
  proposedTitle: string;
  proposedSalary: string;
  currency: string;
  startDate: string;
}

export interface CreateOpportunityRequest {
  customerId?: number | null;
  companyId?: number | null;
  name: string;
  stageCode?: string;
  amount?: string;
  currency: string;
  probabilityPercent?: string;
  expectedCloseDate?: string;
  ownerUserId?: number | null;
  source?: string;
  forecastCategoryCode?: string;
  accountId?: number | null;
  primaryContactId?: number | null;
  campaignId?: number | null;
  nextStep?: string;
  nextStepDueOn?: string;
}

export interface CreateOrderRequest {
  companyId: number;
  customerId: number;
  quoteId?: number | null;
  orderDate?: string;
  requestedDate?: string;
  currency?: string;
  memo?: string;
  reference?: string;
  channel?: string;
  supplyType?: string;
  dayPartCode?: string;
  locationId?: number | null;
  lines?: OrderLineInput[];
}

export interface CreateOvertimeRuleRequest {
  code: string;
  name: string;
  dailyThresholdHours: string;
  rateMultiplier?: string;
}

export interface CreatePMSAuthHoldRequest {
  folioId?: number;
  reservationId?: number | null;
  amount: string;
  currency?: string;
  approvalCode?: string;
  providerReference?: string;
  cardScheme?: string;
  cardLast4?: string;
  expiresAt?: string;
}

export interface CreatePMSBoardPlanRequest {
  propertyId: number;
  code: string;
  name: string;
}

export interface CreatePMSCancellationPolicyRequest {
  propertyId: number;
  code: string;
  name: string;
  tiers?: PMSCancellationTierDTO[];
  noShowChargeKind?: 'charge_first_night' | 'charge_full_stay' | 'no_charge';
}

export interface CreatePMSChannelRequest {
  companyId: number;
  propertyId?: number | null;
  code: string;
  name: string;
  kind: 'ota' | 'wholesaler' | 'tour_operator' | 'direct' | 'gds' | 'corporate';
  adapter?: string;
  vendorId?: number | null;
  customerId?: number | null;
  defaultSourceCode?: string;
  defaultSegmentCode?: string;
  defaultMarketCode?: string;
  parityReferencePlanId?: number | null;
  parityTolerancePercent?: string;
}

export interface CreatePMSCommissionAgreementRequest {
  companyId: number;
  channelId: number;
  code: string;
  name: string;
}

export interface CreatePMSCommissionStatementRequest {
  channelId: number;
  statementNo: string;
  periodFrom: string;
  periodTo: string;
  currency: string;
  declaredTotal?: string;
  filename?: string;
  lines: PMSCommissionStatementLineInput[];
}

export interface CreatePMSCommissionVersionRequest {
  effectiveFrom: string;
  effectiveTo?: string | null;
  percentBasis?: 'room_net' | 'room_gross' | 'stay_net';
  percent?: string;
  flatPerBooking?: string;
  flatPerNight?: string;
  currency: string;
  settlementMode?: 'invoiced' | 'net_remittance';
}

export interface CreatePMSDepositRequest {
  propertyId: number;
  reservationId?: number | null;
  folioId?: number | null;
  guestId?: number | null;
  customerId?: number | null;
  amount: string;
  currency?: string;
  method: 'cash' | 'card' | 'bank_transfer';
  bankAccountId: number;
  reference?: string;
  receivedOn?: string;
}

export interface CreatePMSFolioRequest {
  propertyId: number;
  kind?: 'guest' | 'group_master' | 'house' | 'walk_in';
  reservationId?: number | null;
  guestId?: number | null;
  customerId?: number | null;
  currency?: string;
  note?: string;
}

export interface CreatePMSFolioWindowRequest {
  windowNo?: number;
  name?: string;
  customerId?: number | null;
  guestId?: number | null;
  isPackage?: boolean;
}

export interface CreatePMSGroupBlockRequest {
  propertyId: number;
  name: string;
  customerId?: number | null;
  ratePlanId?: number | null;
  arrival: string;
  departure: string;
  cutoffDays?: number;
  attritionPercent?: string;
  note?: string;
}

export interface CreatePMSGuestMessageRequest {
  guestId: number;
  reservationId?: number | null;
  channel: 'email' | 'sms' | 'in_app' | 'print';
  purpose: 'confirmation' | 'pre_arrival' | 'in_stay' | 'departure' | 'marketing';
  subject?: string;
  body?: string;
}

export interface CreatePMSGuestRequest {
  companyId: number;
  givenName?: string;
  familyName?: string;
  displayName?: string;
  title?: string;
  email?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressRegion?: string;
  addressPostcode?: string;
  addressCountry?: string;
  documentKind?: '' | 'passport' | 'national_id' | 'driving_licence';
  documentCountry?: string;
  documentNumber?: string;
  documentExpiresOn?: string;
  vipCode?: string;
  segmentCode?: string;
  language?: string;
  notes?: string;
}

export interface CreatePMSGuestRequestRequest {
  propertyId: number;
  reservationId?: number | null;
  guestId?: number | null;
  roomId?: number | null;
  kind: 'request' | 'complaint';
  category: string;
  severity?: 'low' | 'medium' | 'high';
  description?: string;
}

export interface CreatePMSHousekeepingBoardRequest {
  propertyId: number;
  businessDate: string;
  employeeId: number;
  sectionCode?: string;
  creditTarget?: string;
}

export interface CreatePMSKeyIssueRequest {
  reservationId: number;
  roomId?: number | null;
  guestId?: number | null;
  kind?: 'guest' | 'duplicate' | 'staff' | 'master';
  validFrom?: string;
  validTo?: string;
}

export interface CreatePMSNightAuditRunRequest {
  propertyId: number;
  date?: string;
}

export interface CreatePMSOutOfOrderRequest {
  roomId: number;
  kind: 'out_of_order' | 'out_of_service';
  fromDate: string;
  toDate: string;
  reasonCode: string;
  note?: string;
}

export interface CreatePMSParityObservationRequest {
  channelId: number;
  propertyId: number;
  roomTypeId: number;
  stayDate: string;
  observedOn: string;
  observedAmount: string;
  currency: string;
}

export interface CreatePMSPropertyRequest {
  companyId: number;
  code: string;
  name: string;
  locationId: number;
  branchId?: number | null;
  kind: 'resort' | 'hotel' | 'guesthouse' | 'vessel' | 'villa';
  taxChannel?: string;
  accommodationItemClass?: string;
  currency: string;
  checkInTime?: string;
  checkOutTime?: string;
  overbookingRooms?: number | null;
}

export interface CreatePMSRatePlanRequest {
  propertyId: number;
  code: string;
  name: string;
  description?: string;
  currency: string;
  priceBasis?: string;
  boardPlanId?: number | null;
  cancellationPolicyId?: number | null;
  guaranteeKind?: 'none' | 'token' | 'deposit' | 'company' | 'travel_agent';
  minNights?: number | null;
  maxNights?: number | null;
  minAdvanceDays?: number | null;
  maxAdvanceDays?: number | null;
  channelCodes?: string[];
  segmentCodes?: string[];
  commissionable?: boolean | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

export interface CreatePMSReservationRequest {
  propertyId: number;
  roomTypeId: number;
  ratePlanId: number;
  arrival: string;
  departure: string;
  guestId?: number | null;
  customerId?: number | null;
  groupBlockId?: number | null;
  roomsCount?: number;
  adults?: number;
  children?: number;
  guaranteeKind?: 'none' | 'token' | 'deposit' | 'company' | 'travel_agent';
  guaranteeReference?: string;
  channelId?: number | null;
  channelReference?: string;
  sourceCode?: string;
  segmentCode?: string;
  marketCode?: string;
  note?: string;
}

export interface CreatePMSRevenueCenterRequest {
  propertyId: number;
  code: string;
  name: string;
  kind?: 'rooms' | 'fnb' | 'spa' | 'activities' | 'transfers' | 'minibar' | 'other';
  revenueAccountId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
}

export interface CreatePMSRoomFaultRequest {
  propertyId: number;
  roomId: number;
  category: 'plumbing' | 'electrical' | 'hvac' | 'furniture' | 'appliance' | 'structural' | 'safety' | 'other';
  severity?: 'minor' | 'major' | 'blocking';
  description?: string;
  attachmentId?: number | null;
}

export interface CreatePMSRoomRequest {
  propertyId: number;
  roomTypeId: number;
  number: string;
  name?: string;
  floor?: string;
  wing?: string;
  sectionCode?: string;
}

export interface CreatePMSRoomTypeRequest {
  propertyId: number;
  code: string;
  name: string;
  description?: string;
  baseOccupancy?: number | null;
  maxOccupancy?: number | null;
  maxAdults?: number | null;
  maxChildren?: number | null;
  itemId: number;
  revenueCenterId?: number | null;
  overbookingRooms?: number | null;
  cleanCreditDeparture?: string;
  cleanCreditStayover?: string;
  sortOrder?: number;
}

export interface CreatePMSServiceBookingRequest {
  propertyId: number;
  serviceId: number;
  reservationId?: number | null;
  guestId?: number | null;
  scheduledAt: string;
  pax?: number;
  quantity?: string;
  unitPrice?: string;
  note?: string;
}

export interface CreatePMSServiceRequest {
  propertyId: number;
  code: string;
  name: string;
  description?: string;
  itemId: number;
  kind?: 'activity' | 'spa' | 'dining' | 'transfer' | 'rental' | 'other';
  revenueCenterId?: number | null;
  durationMinutes?: number;
  capacityPerSlot?: number;
  leadTimeHours?: number;
  cancelWindowHours?: number;
  mealPeriod?: string;
  requirements?: PMSServiceRequirementDTO[];
  dependencies?: PMSServiceDependencyDTO[];
}

export interface CreatePMSServiceResourceRequest {
  propertyId: number;
  code: string;
  name: string;
  kind?: 'staff' | 'equipment' | 'vehicle' | 'room';
  capacity?: number;
  employeeId?: number | null;
}

export interface CreatePMSTransferLegBookingsRequest {
  bookings: PMSTransferBookingInput[];
  splitReason?: string;
}

export interface CreatePMSTransferLegRequest {
  propertyId: number;
  legNo: string;
  direction: 'arrival' | 'departure' | 'internal';
  departAt: string;
  arriveAt: string;
  origin?: string;
  destination?: string;
  resourceId?: number | null;
  capacityPax?: number;
}

export interface CreatePMSWaitlistEntryRequest {
  propertyId: number;
  roomTypeId: number;
  ratePlanId?: number | null;
  guestId?: number | null;
  arrival: string;
  departure: string;
  roomsCount?: number;
  priority?: number;
  note?: string;
}

export interface CreatePMScheduleRequest {
  name: string;
  customerAssetId: number;
  companyId: number;
  frequencyDays: number;
  nextDueDate: string;
  subjectTemplate: string;
  category?: string;
  priority?: string;
  slaPolicyId?: number | null;
  assignedTo?: number | null;
}

export interface CreatePORequest {
  companyId: number;
  vendorId: number;
  prId?: number | null;
  rfqId?: number | null;
  currency?: string;
  orderDate: string;
  expectedDate?: string;
  memo?: string;
  lines: POLineInput[];
}

export interface CreatePOSDepositRequest {
  outletId: number;
  bankAccountId: number;
  amount: string;
  currency: string;
  businessDate?: string;
  witnessedBy: number;
  note?: string;
  sessions: POSDepositSessionInput[];
}

export interface CreatePOSEmployeePurchaseRequest {
  employeeId: number;
  note?: string;
}

export interface CreatePOSFloorAreaRequest {
  outletId: number;
  code: string;
  name: string;
  seqNo?: number;
}

export interface CreatePOSMenuRequest {
  companyId: number;
  outletId: number;
  code: string;
  name: string;
  channel?: string;
  dayPartCode?: string;
}

export interface CreatePOSOutletRequest {
  companyId: number;
  code: string;
  name: string;
  locationId: number;
  warehouseId?: number | null;
  branchId?: number | null;
  kind: 'retail' | 'hospitality' | 'wholesale';
  taxChannel?: string;
  supplyType?: string;
  currency: string;
  defaultRevenueCenterId?: number | null;
  priceSetId?: number | null;
  cashRoundingIncrement?: string;
}

export interface CreatePOSRefundRequest {
  terminalId: number;
  refundsSaleId?: number | null;
  lines?: POSRefundLineInput[];
  tenderTypeId?: number | null;
  reasonCode: string;
  reason?: string;
  override?: POSOverrideRequest | null;
  businessDate?: string;
  note?: string;
}

export interface CreatePOSRevenueCenterRequest {
  companyId: number;
  code: string;
  name: string;
  revenueAccountId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  departmentId?: number | null;
}

export interface CreatePOSSaleRequest {
  terminalId: number;
  businessDate?: string;
  shiftCode?: string;
  dayPartCode?: string;
  channelMode?: string;
  customerId?: number | null;
  loyaltyMemberId?: number | null;
  checkId?: number | null;
  menuVersionId?: number | null;
  priceSetVersionId?: number | null;
  reference?: string;
  note?: string;
}

export interface CreatePOSStationRequest {
  outletId: number;
  code: string;
  name: string;
  seqNo?: number;
  targetMinutes?: number;
}

export interface CreatePOSTenderTypeRequest {
  companyId: number;
  code: string;
  name: string;
  kind: 'cash' | 'card' | 'gift_card' | 'store_credit' | 'account' | 'room_charge';
  clearingAccountId?: number | null;
  opensDrawer?: boolean | null;
  allowsChange?: boolean | null;
  allowsPartial?: boolean | null;
  allowsRefund?: boolean | null;
  surchargePercent?: string;
  surchargeItemId?: number | null;
  provider?: string;
}

export interface CreatePOSTerminalRequest {
  outletId: number;
  code: string;
  name: string;
  cashAccountId?: number | null;
  offlineEnabled?: boolean | null;
  contentMaxAgeHours?: number | null;
}

export interface CreatePRRequest {
  companyId: number;
  departmentId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  needBy: string;
  currency?: string;
  memo?: string;
  lines: PRLineInput[];
}

export interface CreatePackRequest {
  country: string;
  baseVersion?: number | null;
  title?: string;
}

export interface CreatePartyIdentifierRequest {
  kind: string;
  issuingCountry?: string;
  value: string;
  validFrom?: string;
  validTo?: string;
  verified?: boolean;
}

export interface CreatePartyLinkRequest {
  linkType: string;
  recordId: number;
  isPrimary?: boolean;
  rule?: string;
}

export interface CreatePartyMergeRequest {
  sourcePartyId: number;
  survivorPartyId: number;
  candidateId?: number | null;
}

export interface CreatePartyRequest {
  kind: string;
  displayName?: string;
  legalName?: string;
  givenName?: string;
  familyName?: string;
  salutation?: string;
  email?: string;
  phone?: string;
  address?: Record<string, unknown>;
  countryCode?: string;
  preferredLocale?: string;
  status?: string;
}

export interface CreatePayGroupRequest {
  companyId: number;
  code: string;
  name: string;
  frequency?: string;
  periodAnchorDay?: number | null;
  paymentLagDays?: number | null;
  calendarId?: number | null;
  bankAccountId?: number | null;
  isDefault?: boolean;
  note?: string;
}

export interface CreatePaymentExceptionRequest {
  paymentId: number;
  kind: string;
  bankReasonCode?: string;
  reasonCode: string;
  note?: string;
  reference?: string;
}

export interface CreatePeriodsRequest {
  calendarId: number;
  fiscalYear: number;
  periodCount?: number;
  periods?: FNDPeriodInput[];
}

export interface CreatePermitQuotaRequest {
  companyId: number;
  jurisdictionId: number;
  quotaCategory: string;
  cap: number;
  sourceCitation?: string;
  effectiveFrom: string;
  note?: string;
}

export interface CreatePhaseRequest {
  name: string;
  sequence?: number;
  startDate?: string;
  endDate?: string;
}

export interface CreatePickRequest {
  orderId: number;
  warehouseId: number;
  pickDate?: string;
  memo?: string;
  lines: PickLineInput[];
}

export interface CreatePolicyRequest {
  docType: string;
  companyId?: number | null;
  name: string;
  rules: Rule[];
  priority?: number;
  isActive?: boolean | null;
}

export interface CreatePortalOrderRequest {
  companyId: number;
  quoteId?: number | null;
  reference?: string;
  lines?: PortalOrderLineInput[];
}

export interface CreatePortalUserRequest {
  email: string;
  name: string;
  counterpartyType: 'customer' | 'supplier' | 'contractor';
  counterpartyId?: number | null;
}

export interface CreatePositionRequest {
  code: string;
  name: string;
  companyId: number;
  jobId: number;
  gradeId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  locationId?: number | null;
  reportsToPositionId?: number | null;
  headcount?: number;
  status?: string;
  effectiveFrom: string;
  note?: string;
}

export interface CreatePrecedenceRequest {
  code: string;
  name: string;
  effectiveFrom: string;
  cloneFromId?: number | null;
  note?: string;
}

export interface CreatePriceListRequest {
  companyId: number;
  code: string;
  name: string;
  kind: 'base' | 'tier' | 'contract' | 'segment' | 'channel';
  currency: string;
  customerId?: number | null;
  priceTier?: string;
  priority?: number;
  validFrom?: string;
  validTo?: string;
  channel?: string;
  dayPartCode?: string;
  segmentCode?: string;
  locationId?: number | null;
  lines: PriceListLineInput[];
}

export interface CreateProblemRequest {
  companyId: number;
  title: string;
  description?: string;
  itemId?: number | null;
  assetCategory?: string;
  ownerUserId?: number | null;
}

export interface CreateProductionOrderRequest {
  companyId: number;
  itemId: number;
  bomId?: number | null;
  routingId?: number | null;
  warehouseId: number;
  quantityPlanned: string;
  backflush?: boolean;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  notes?: string;
}

export interface CreateProfileRequest {
  companyId: number;
  bankAccountId?: number | null;
  name: string;
  mapping: CSVMapping;
  notes?: string;
}

export interface CreateProjectAdvanceRequest {
  projectId: number;
  amount: string;
  recoupmentPercent: string;
  invoiceDate?: string;
  taxCode?: string;
}

export interface CreateProjectAllocationRequest {
  resourceId: number;
  projectId: number;
  taskId?: number | null;
  weekStartDate: string;
  allocatedHours: string;
  overrideReasonCode?: string;
  overrideNote?: string;
}

export interface CreateProjectBaselineRequest {
  name: string;
  variationId?: number | null;
}

export interface CreateProjectChargeabilityOverrideRequest {
  reasonCode: string;
  note?: string;
  validTo: string;
}

export interface CreateProjectRequest {
  companyId: number;
  code: string;
  name: string;
  description?: string;
  customerId?: number | null;
  managerUserId?: number | null;
  startDate?: string;
  endDate?: string;
  currency: string;
  contractValue?: string;
  revenueTaxCode?: string;
}

export interface CreateProjectResourceRequest {
  companyId: number;
  employeeId: number;
  planningCostRate?: string;
  currency?: string;
}

export interface CreateProjectRetentionRequest {
  projectId: number;
  claimInvoiceId: number;
  percent: string;
  releaseDueOn: string;
  withheldOn?: string;
}

export interface CreateProjectRiskRequest {
  projectId: number;
  title: string;
  description?: string;
  category?: string;
  probability: number;
  impact: number;
  costImpact?: string;
  response?: 'avoid' | 'mitigate' | 'transfer' | 'accept';
  mitigation?: string;
  ownerUserId?: number | null;
  reviewOn?: string;
}

export interface CreateProjectSubcontractRequest {
  projectId: number;
  vendorId: number;
  title: string;
  purchaseOrderId?: number | null;
  contractValue?: string;
  retentionPercent?: string;
  startsOn?: string;
  endsOn?: string;
}

export interface CreateProjectVariationRequest {
  projectId: number;
  title: string;
  description?: string;
  origin?: 'internal' | 'customer' | 'site';
  costImpact?: string;
  revenueImpact?: string;
  scheduleImpactDays?: number;
  impactNote?: string;
}

export interface CreateProjectWIPRunRequest {
  companyId: number;
  periodId: number;
}

export interface CreatePromotionRequest {
  companyId: number;
  code: string;
  name: string;
  kind: 'percent' | 'amount' | 'bundle';
  value: string;
  currency?: string;
  bundleMinQty?: string;
  startDate: string;
  endDate: string;
  customerIds?: number[];
  customerTiers?: string[];
  itemIds?: number[];
  minQty?: string;
  channel?: string;
  dayPartCode?: string;
  segmentCode?: string;
  isStackable?: boolean | null;
}

export interface CreateQuoteRequest {
  companyId: number;
  customerId: number;
  quoteDate?: string;
  expiryDate?: string;
  currency?: string;
  memo?: string;
  reference?: string;
  channel?: string;
  supplyType?: string;
  dayPartCode?: string;
  locationId?: number | null;
  lines: SalesQuoteLineInput[];
}

export interface CreateRFQRequest {
  companyId: number;
  prId?: number | null;
  dueDate?: string;
  currency?: string;
  memo?: string;
  weightPrice?: string;
  weightLeadTime?: string;
  weightTerms?: string;
  lines?: RFQLineInput[];
  supplierIds?: number[];
}

export interface CreateRVMAccuracyRunRequest {
  forecastId: number;
  scoredFrom: string;
  scoredTo: string;
}

export interface CreateRVMCompetitorRateRequest {
  propertyId: number;
  competitorName: string;
  stayDate: string;
  roomTypeLabel?: string;
  rate: string;
  currency?: string;
  observedOn?: string;
  note?: string;
}

export interface CreateRVMForecastRequest {
  propertyId: number;
  code: string;
  name: string;
  kind: 'budget' | 'forecast';
  periodFrom: string;
  periodTo: string;
  currency?: string;
}

export interface CreateRVMForecastVersionRequest {
  name?: string;
  note?: string;
  copyFromVersionId?: number | null;
}

export interface CreateRVMHurdleRequest {
  propertyId: number;
  segmentId?: number | null;
  roomTypeId?: number | null;
  dateFrom?: string;
  dateTo?: string;
  dowMask?: string;
  minAdr: string;
  minTotalContribution?: string;
  currency?: string;
  note?: string;
}

export interface CreateRVMSegmentMappingRequest {
  companyId: number;
  segmentId: number;
  priority?: number | null;
  matchSegmentCode?: string;
  matchMarketCode?: string;
  matchSourceCode?: string;
  matchChannelId?: number | null;
}

export interface CreateRVMSegmentRequest {
  companyId: number;
  code: string;
  name: string;
  family: 'transient' | 'group' | 'contract' | 'wholesale' | 'complimentary' | 'house';
  displacementRank?: number | null;
  sortOrder?: number | null;
}

export interface CreateRVMStreamRequest {
  companyId: number;
  code: 'room' | 'fnb' | 'ancillary' | 'other';
  name: string;
  actualSources?: StreamActualSources | null;
  sortOrder?: number | null;
}

export interface CreateReasonCodeRequest {
  code: string;
  description: string;
  requiresApproval?: boolean;
}

export interface CreateRecallActionRequest {
  actionKind: 'quarantine' | 'notify_customer' | 'notify_authority' | 'return_to_vendor' | 'dispose' | 'block_sales' | 'verify_disposal';
  targetKind?: string;
  targetId?: number | null;
  description?: string;
  dueOn?: string | null;
}

export interface CreateReceiptRequest {
  companyId: number;
  customerId: number;
  receiptDate?: string;
  currency?: string;
  amount: string;
  depositAccountId: number;
  method?: 'bank_transfer' | 'cash' | 'cheque' | 'card' | 'other';
  reference?: string;
  memo?: string;
  allocations?: AllocationInput[];
}

export interface CreateRecipeRequest {
  code: string;
  name: string;
  companyId: number;
  outputItemId: number;
  yieldQty?: string;
  yieldUom?: string;
  kind?: 'plated' | 'batch' | 'sub_recipe' | 'modifier';
  defaultWarehouseId?: number | null;
  isActive?: boolean | null;
  effectiveFrom: string;
  note?: string;
  lines?: RecipeLineRequest[];
}

export interface CreateRecurringItemRequest {
  companyId: number;
  bankAccountId?: number | null;
  name: string;
  direction: 'inflow' | 'outflow';
  amount: string;
  currency?: string;
  cadence: 'once' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'annually';
  nextDate: string;
  endDate?: string;
  notes?: string;
  kind?: string;
}

export interface CreateReferenceSetRequest {
  enterpriseId: number;
  code: string;
  name: string;
  purpose: string;
  ownerPositionId: number | null;
  effectiveFrom: string;
  effectiveTo: string;
  regions: ReferenceSetScopeInput[];
}

export interface CreateRegistrationRequest {
  companyId: number;
  jurisdictionId: number;
  regimeId: number;
  registrationNo: string;
  scheme: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  returnFrequency: string;
  firstPeriodStart?: string | null;
  filingCurrency: string;
  entityDesignation: string;
  isPrimary: boolean;
  notes: string;
}

export interface CreateRemittanceRequest {
  returnId: number;
  bankAccountId: number;
  profileId?: number | null;
  paymentDate: string;
  reference?: string;
  note?: string;
}

export interface CreateReorganisationRequest {
  code: string;
  effectiveOn: string;
  changeSummary?: string;
  items: ReorganisationItemRequest[];
}

export interface CreateRepaymentRequest {
  statementLineId: number;
  valueDate?: string;
  principalAmount?: string;
  interestAmount?: string;
  feeAmount?: string;
  penaltyAmount?: string;
  currency?: string;
  scheduleLineId?: number | null;
  bankAccountId?: number | null;
  transferId?: number | null;
  reference?: string;
  memo?: string;
}

export interface CreateReportExceptionViewRequest {
  companyId: number;
  code: string;
  name: string;
  description?: string;
  roleCode: string;
  sources: ('attention' | 'recon_queue' | 'approvals' | 'ai_recommendations')[];
  filters?: Record<string, unknown>;
  refreshMinutes: number;
  sortOrder?: number;
}

export interface CreateRequisitionLineRequest {
  itemId: number;
  requestedQty: string;
  note?: string;
}

export interface CreateRequisitionRequest {
  fromWarehouseId: number;
  toWarehouseId: number;
  requiredOn: string;
  origin?: 'par' | 'manual' | 'event' | 'recipe_forecast';
  shiftCode?: string;
  memo?: string;
  lines: CreateRequisitionLineRequest[];
}

export interface CreateReservationRequest {
  itemId: number;
  warehouseId: number;
  kind: 'soft' | 'hard';
  qty: string;
  docType: string;
  docId: number;
  docLineNo?: number | null;
  note?: string;
}

export interface CreateRetentionRuleRequest {
  linkType: string;
  tableName: string;
  columnName: string;
  treatment: string;
  basis: string;
  retainYears?: number | null;
  anchor?: string;
  statuteRef?: string;
  note?: string;
}

export interface CreateReturnRequest {
  companyId: number;
  customerId: number;
  orderId?: number | null;
  shipmentId?: number | null;
  warehouseId: number;
  returnDate?: string;
  reason?: string;
  memo?: string;
  lines: ReturnLineInput[];
}

export interface CreateRootCauseCodeRequest {
  code: string;
  description: string;
  category?: string;
  requiresAttachment?: boolean;
}

export interface CreateRosterRequest {
  companyId: number;
  locationId?: number | null;
  departmentId?: number | null;
  fromDate: string;
  toDate: string;
  note?: string;
}

export interface CreateRoutingRequest {
  companyId: number;
  itemId: number;
  name?: string;
  notes?: string;
  operations: RoutingOperationInput[];
}

export interface CreateRunRequest {
  companyId: number;
  paymentDate: string;
  currency: string;
  method?: string;
  bankGlAccountId: number;
  bankFileProfileId?: number | null;
  dueOnOrBefore?: string;
  reference?: string;
  memo?: string;
  takeDiscounts?: boolean;
  vendorIds?: number[];
  allocations?: RunAllocationInput[];
  bankOverrideReasonCode?: string;
  bankOverrideNote?: string;
}

export interface CreateSLAPolicyRequest {
  name: string;
  priority: string;
  responseMinutes: number;
  resolutionMinutes: number;
  businessHoursOnly?: boolean;
  escalateToUserId?: number | null;
  isDefault?: boolean;
}

export interface CreateSSOProviderRequest {
  code: string;
  name: string;
  issuer: string;
  clientId: string;
  clientSecret: string;
  allowedEmailDomains?: string[];
  jitProvisioning?: boolean;
  defaultRoleCodes?: string[];
}

export interface CreateSamplingPlanRequest {
  companyId: number;
  code: string;
  name: string;
  method: string;
  samplePercent?: string;
  minSampleSize?: number | null;
  maxSampleSize?: number | null;
  acceptNumber?: number | null;
  levels?: SamplingLevelInput[];
}

export interface CreateSavedFilterRequest {
  reportKey: string;
  name: string;
  params: Record<string, unknown>;
  isShared: boolean;
}

export interface CreateScenarioRequest {
  companyId: number;
  code: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  receivableDelayDays?: number;
  receivableHaircutPct?: string;
  payableDelayDays?: number;
  recurringMultiplierPct?: string;
  includeOverdue?: boolean | null;
  scenarioType?: 'base' | 'upside' | 'downside' | 'stress' | 'what_if';
  driverOverrides?: string[];
  manualAdjustments?: CashForecastAdjustment[];
}

export interface CreateScheduleRequest {
  reportKey: string;
  name: string;
  params: Record<string, unknown>;
  format: string;
  frequency: string;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  hourLocal: number;
  recipients: string[];
  companyId?: number | null;
}

export interface CreateScreeningRequest {
  listKind: string;
  screenedName: string;
  countryCode?: string;
  outcome: string;
  matchCount?: number;
  evidence?: string;
  attachmentId?: number | null;
  reference?: string;
  validUntil?: string;
}

export interface CreateSegmentSecurityRuleRequest {
  coaSegmentId: number;
  valueRange: string;
  roleId?: number | null;
  businessUnitId?: number | null;
  accessType?: string;
}

export interface CreateServiceChargePointRequest {
  schemeId: number;
  gradeId?: number | null;
  positionId?: number | null;
  employeeId?: number | null;
  points: string;
  effectiveFrom?: string;
}

export interface CreateServiceChargePoolRequest {
  companyId: number;
  poolKey?: string;
  locationId?: number | null;
  periodStart: string;
  periodEnd: string;
  schemeId: number;
  note?: string;
}

export interface CreateServiceChargeSchemeRequest {
  companyId: number;
  code: string;
  name: string;
  basis?: 'points' | 'hours';
  minDaysWorked?: number;
  includeEmploymentTypes?: string[];
  excludeOnProbation?: boolean;
  prorateByDays?: boolean | null;
  forfeitOnTerminationForCause?: boolean | null;
  forfeitAfterUnapprovedAbsenceDays?: number | null;
  retentionPercent?: string;
  contributesToClasses?: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  componentCode: string;
  effectiveFrom?: string;
  note?: string;
}

export interface CreateServiceContractRequest {
  companyId: number;
  customerId: number;
  name: string;
  startsOn: string;
  endsOn: string;
  autoRenew?: boolean;
  renewalNoticeDays?: number | null;
  slaPolicyId?: number | null;
  rateCardId?: number | null;
  feeAmount?: string;
  feeCycle?: string;
  revenueAccountId?: number | null;
  taxCode?: string;
  entitlements?: ServiceEntitlementInput[];
}

export interface CreateServicePrincipalRequest {
  name: string;
}

export interface CreateServiceProviderRelationshipRequest {
  providerBuId: number;
  clientBuId: number;
  functionCode: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface CreateServiceRateCardRequest {
  companyId: number;
  code: string;
  name: string;
  currency?: string;
}

export interface CreateServiceRateCardVersionRequest {
  effectiveFrom: string;
  partsMarkupPercent?: string;
  travelRate?: string;
  minimumCharge?: string;
  preApprovalThreshold?: string;
  copyFromVersionId?: number | null;
  lines?: ServiceRateCardLineInput[];
}

export interface CreateServiceWorkOrderRequest {
  companyId: number;
  kind: string;
  summary: string;
  ticketId?: number | null;
  eamAssetId?: number | null;
  customerAssetId?: number | null;
  originKind?: string;
  originDocId?: number | null;
  originDueOn?: string | null;
  priority?: string;
  instructions?: string;
  taskListId?: number | null;
  appointmentOn?: string | null;
  windowStartMinute?: number | null;
  windowEndMinute?: number | null;
  assignedToUserId?: number | null;
  warehouseId?: number | null;
  costAccountId?: number | null;
  estimatedHours?: string;
  skills?: ServiceWorkOrderSkillInput[];
}

export interface CreateSessionRequest {
  bankAccountId: number;
  fromDate: string;
  toDate: string;
  openingBalanceStatement?: string;
  note?: string;
}

export interface CreateSetVersionRequest {
  effectiveFrom: string;
  cloneFromVersionNo?: number | null;
  note?: string;
}

export interface CreateShiftDefinitionRequest {
  companyId: number;
  code: string;
  name: string;
  startsAt: string;
  endsAt: string;
  crossesMidnight: boolean;
  unpaidBreakMinutes: number;
  departmentId?: number | null;
  colour?: string;
  isNight: boolean;
}

export interface CreateShiftPatternRequest {
  companyId: number;
  code: string;
  name: string;
  cycleDays: number;
  calendarId?: number | null;
  note?: string;
  slots: ShiftPatternSlotDTO[];
}

export interface CreateShipmentRequest {
  orderId: number;
  warehouseId: number;
  shipDate?: string;
  memo?: string;
  lines: ShipmentLineInput[];
}

export interface CreateStaffingRuleRequest {
  companyId: number;
  locationId?: number | null;
  departmentId?: number | null;
  shiftDefinitionId?: number | null;
  metric: string;
  unitsPerHead: string;
  minHeadcount?: number;
  maxHeadcount?: number | null;
  dayPart?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CreateStandingRuleRequest {
  companyId: number;
  bankAccountId?: number | null;
  code: string;
  name: string;
  priority?: number;
  direction?: string;
  bankTransactionCode?: string;
  descriptionPattern?: string;
  referencePattern?: string;
  counterpartyPattern?: string;
  minAmount?: string;
  maxAmount?: string;
  offsetAccountId: number;
  taxCode?: string;
  taxInclusive?: boolean | null;
  costCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  descriptionTemplate?: string;
  actionMode?: 'suggest' | 'auto';
  autoCeiling?: string;
  effectiveFrom: string;
}

export interface CreateStatutoryMappingRequest {
  companyId: number;
  ruleCode: string;
  expenseAccountCode?: string;
  liabilityAccountCode?: string;
  remittanceBankAccountId?: number | null;
  effectiveFrom?: string;
  note?: string;
  rate?: string;
  percent?: string;
  bands?: SlabBandDTO[];
}

export interface CreateStatutoryReturnRequest {
  companyId: number;
  returnCode: string;
  periodStart: string;
  periodEnd: string;
  dueOn?: string;
}

export interface CreateStockLotRequest {
  itemId: number;
  lotNo: string;
  expiryDate?: string | null;
  bestBeforeDate?: string | null;
  manufacturedOn?: string | null;
  supplierLotRef?: string;
  vendorId?: number | null;
  countryOfOrigin?: string;
  attributes?: Record<string, unknown>;
}

export interface CreateStructureRequest {
  name: string;
  effectiveFrom?: string;
  segments?: SegmentRequest[];
}

export interface CreateSubscriptionPlanRequest {
  companyId: number;
  code: string;
  name: string;
  itemId?: number | null;
  revenueAccountId: number;
  currency: string;
  billingCycle: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  price: string;
  revenueTaxCode?: string;
}

export interface CreateSubscriptionRequest {
  planId: string;
  seats?: number | null;
  trialDays?: number | null;
}

export interface CreateSubstituteRequest {
  itemId: number;
  substituteItemId: number;
  warehouseId?: number | null;
  conversionFactor?: string;
  preference?: number;
  appliesTo?: 'both' | 'outbound' | 'production';
  requiresApproval?: boolean;
  note?: string;
}

export interface CreateSupplierContractRequest {
  companyId: number;
  vendorId: number;
  kind: string;
  title: string;
  description?: string;
  vendorReference?: string;
  currency?: string;
  startsOn: string;
  endsOn?: string;
  autoRenew?: boolean | null;
  noticePeriodDays?: number | null;
  renewalTermMonths?: number | null;
  incotermCode?: string;
  incotermPlace?: string;
  paymentTermsCode?: string;
  leadTimeDays?: number | null;
  ownerUserId?: number | null;
  attachmentId?: number | null;
}

export interface CreateSupplierReturnRequest {
  companyId: number;
  vendorId: number;
  poId?: number | null;
  grnId?: number | null;
  returnDate?: string;
  reasonKind: string;
  reason?: string;
  replacementExpected?: boolean;
  carrierRef?: string;
  lines: SupplierReturnLineInput[];
}

export interface CreateSuppressionRequest {
  channelCode: string;
  destination: string;
  reason: string;
  scope?: string;
  purposeCode?: string;
  partyId?: number | null;
  note?: string;
}

export interface CreateSuspensePositionRequest {
  companyId: number;
  code: string;
  name: string;
  accountId: number;
  ownerPositionId?: number | null;
  maxAgeDays?: number;
  blocksClose?: boolean | null;
}

export interface CreateTariffRequest {
  code: string;
  description: string;
  country: string;
  dutyRatePct?: string;
  dutyPerUnit?: string;
  dutyUom?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CreateTaskRequest {
  phaseId?: number | null;
  wbsCode?: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  isMilestone?: boolean;
  assigneeUserId?: number | null;
  accountId?: number | null;
  costCenterId?: number | null;
  budgetAmount?: string;
  dependsOn?: number[];
}

export interface CreateTaxReportingUnitRequest {
  name: string;
  registrationId?: string;
  jurisdictionId: number;
  effectiveFrom: string;
}

export interface CreateTemplateRequest {
  code: string;
  name: string;
  description?: string;
  country?: string | null;
  industry?: string;
  lines: TemplateLineInput[];
}

export interface CreateTenantRequest {
  name: string;
  slug: string;
  region?: string;
  timezone?: string;
  planId?: string | null;
  adminEmail: string;
  adminName: string;
}

export interface CreateTenantUserRequest {
  email: string;
  name: string;
  roleCodes?: string[];
  overrideReason?: string;
  credentialMode?: string;
  password?: string;
  mustChangePassword?: boolean | null;
}

export interface CreateTenantUserResponse {
  user: TenantUserSummary;
  generatedPassword?: string | null;
}

export interface CreateTicketRequest {
  subject: string;
  description?: string;
  origin?: string;
  category?: string;
  priority?: string;
  companyId: number;
  customerId?: number | null;
  customerAssetId?: number | null;
  warehouseId?: number | null;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  assignedTo?: number | null;
  slaPolicyId?: number | null;
  billable?: boolean;
  isRevisit?: boolean;
}

export interface CreateTimesheetRequest {
  employeeUserId?: number;
  contractorPortalUserId?: number | null;
  weekStartDate: string;
}

export interface CreateTravelAdvanceRequest {
  companyId: number;
  employeeId?: number;
  travelRequestId?: number | null;
  currency?: string;
  amount: string;
  dueBy?: string;
  note?: string;
}

export interface CreateTravelRequestRequest {
  companyId: number;
  employeeId?: number;
  purpose: string;
  destination: string;
  departOn: string;
  returnOn: string;
  currency?: string;
  estimatedCost?: string;
  costCenterId?: number | null;
  projectId?: number | null;
  note?: string;
}

export interface CreateUnitRequest {
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  sortOrder?: number;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  roleCodes?: string[];
  overrideReason?: string;
  credentialMode?: string;
  password?: string;
  mustChangePassword?: boolean | null;
}

export interface CreateUserResponse {
  user: UserDTO;
  generatedPassword?: string | null;
}

export interface CreateValueSetRequest {
  code: string;
  name: string;
  kind?: string;
  parentValueSetId?: number | null;
}

export interface CreateVanRequest {
  companyId: number;
  warehouseCode: string;
  warehouseName: string;
  registration?: string;
  technicianUserId?: number | null;
  eamAssetId?: number | null;
  sourceWarehouseId?: number | null;
}

export interface CreateVariancePolicyRequest {
  companyId: number;
  absoluteThreshold: string;
  percentThreshold: string;
  includeJoinersLeavers?: boolean;
  effectiveFrom: string;
}

export interface CreateVendorRequest {
  code: string;
  name: string;
  legalName?: string;
  taxGroupId?: number | null;
  whtTaxCode?: string;
  paymentTermsDays?: number;
  discountPercent?: string;
  discountDays?: number;
  defaultCurrency: string;
  allowedCurrencies?: string[];
  email?: string;
  phone?: string;
  address?: string;
  taxRegistrationNo?: string;
  notes?: string;
}

export interface CreateVerificationRunRequest {
  companyId: number;
  code: string;
  name: string;
  scheduledOn: string;
  classId?: number;
  location?: string;
  custodian?: string;
  isBlind?: boolean | null;
  note?: string;
}

export interface CreateVersionRequest {
  name?: string;
  note?: string;
  assumptions?: string;
}

export interface CreateWageBaseRequest {
  code: string;
  name: string;
  jurisdictionId?: number | null;
  seedSource?: 'none' | 'assignment_basic';
  includeClasses?: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  excludeClasses?: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  gate?: string;
  floorAmount?: string;
  ceilingAmount?: string;
  effectiveFrom?: string;
  sourceCitation?: string;
}

export interface CreateWalletRequest {
  companyId: number;
  code: string;
  name: string;
  currency: string;
  kind?: 'imprest' | 'fluctuating';
  floatAmount?: string;
  glAccountId?: number | null;
  clearingAccountId?: number | null;
  varianceAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  locationId?: number | null;
  custodianUserId?: number | null;
  custodianEmployeeId?: number | null;
  deputyUserId?: number | null;
  deputyEmployeeId?: number | null;
  holderPositionId?: number | null;
  fundingBankAccountId?: number | null;
  dormancyDays?: number | null;
  cashBooks?: WalletCashBookInput[];
  countIntervalDays?: number;
}

export interface CreateWarehouseRequest {
  companyId: number;
  code: string;
  name: string;
  negativeStockPolicy?: 'block' | 'allow';
  type?: 'main' | 'outlet_store' | 'in_transit' | 'quarantine' | 'staging' | 'consignment' | 'van';
  locationId?: number | null;
  stockModel?: 'perpetual' | 'expense_on_issue';
  inventoryAccountOverrideId?: number | null;
  costCenterId?: number | null;
  defaultPutawayStrategy?: 'fixed_then_any' | 'nearest_empty' | 'consolidate' | 'single_bin' | 'none';
  defaultPickStrategy?: 'lot_strategy' | 'fixed_bin_first' | 'nearest' | 'single_bin';
  requiresBin?: boolean;
}

export interface CreateWasteReasonRequest {
  code: string;
  description: string;
  category: 'spoilage' | 'preparation' | 'breakage' | 'staff_meal' | 'complimentary' | 'training' | 'expiry' | 'over_production' | 'theft';
  expenseAccountId?: number;
  requiresApproval?: boolean;
  requiresAttachment?: boolean;
  countsAsShrinkage?: boolean | null;
}

export interface CreateWasteRecordLineRequest {
  itemId: number;
  binId?: number | null;
  lotId?: number | null;
  qty: string;
  note?: string;
}

export interface CreateWasteRecordRequest {
  companyId: number;
  warehouseId: number;
  recordedOn: string;
  shiftCode?: string;
  reasonCode: string;
  memo?: string;
  attachmentId?: number | null;
  lines: CreateWasteRecordLineRequest[];
}

export interface CreateWebhookEndpointRequest {
  name: string;
  url: string;
  topics?: string[];
  isActive?: boolean | null;
}

export interface CreateWebhookEndpointResponse {
  id: number;
  name: string;
  url: string;
  topics: string[];
  isActive: boolean;
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  failureCount: number;
  createdAt: string;
  rowVersion: number;
  secret: string;
}

export interface CreateWorkCenterExceptionRequest {
  onDate: string;
  kind: string;
  startMinute?: number | null;
  endMinute?: number | null;
  capacityMinutesOverride?: number | null;
  reason?: string;
}

export interface CreateWorkCenterRequest {
  companyId: number;
  code: string;
  name: string;
  description?: string;
  costRatePerHour?: string;
  overheadRatePerHour?: string;
  capacityHoursPerWeek?: string;
}

export interface CreateWorkPermitRequest {
  employeeId: number;
  jurisdictionId: number;
  permitKind: string;
  quotaCategory?: string;
  permitNo: string;
  issuedOn: string;
  expiresOn: string;
  sponsorName?: string;
  status?: string;
  note?: string;
}

export interface CreateWorkingTimeRuleRequest {
  code: string;
  name: string;
  companyId?: number | null;
  jurisdictionId?: number | null;
  payGroupId?: number | null;
  employmentType?: string;
  ruleKind: string;
  threshold: string;
  windowUnits?: number;
  nightStartsAt?: string;
  nightEndsAt?: string;
  severity?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  note?: string;
}

export interface CreateYearEndCloseRequest {
  companyId: number;
  fiscalYear: number;
  retainedEarningsAccountId?: number;
}

export interface CreateZoneRequest {
  code: string;
  name?: string;
  storageClass?: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  pickSequence?: number;
  minTemperatureC?: string;
  maxTemperatureC?: string;
}

export interface CreatedObject {
  stepRef: string;
  kind: 'create_legal_entity' | 'ensure_primary_ledger' | 'create_division' | 'create_business_unit' | 'create_coa_structure' | 'create_calendar';
  entity: string;
  id: number;
  code?: string;
  name?: string;
}

export interface CreditCheckDTO {
  customerId: number;
  currency: string;
  creditLimit: string;
  exposure: string;
  available?: string | null;
  overdueAmount: string;
  overdueDays: number;
  overdueBlockDays: number;
  conditions: ('credit_limit' | 'overdue' | 'customer_status')[];
  passed: boolean;
  overrideGranted: boolean;
  onHold?: boolean;
  holdReason?: string;
}

export interface CrmDeleteQuery {
  rowVersion: number;
}

export interface CrmForecastAccuracyQuery {
  periodCode: string;
}

export interface CrossChargeRuleDTO {
  id: number;
  fromDivisionId: number;
  toDivisionId: number;
  pricingMethod: string;
  rate?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  recoveryAccountId?: number | null;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string;
  rowVersion: number;
}

export interface CrossChargeSimulationResult {
  ruleId: number;
  runAt: string;
  boundaries: crossChargeBoundary[];
  isBalanced: boolean;
  residual: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
}

export interface CrossValidationRuleDTO {
  id: number;
  coaStructureId: number;
  ruleName: string;
  includeCondition: SegmentCondition[];
  excludeCondition: SegmentCondition[];
  effectiveFrom: string;
  effectiveTo?: string;
  rowVersion: number;
  scan?: RuleScanDTO | null;
}

export interface CurrencyDTO {
  id: number;
  code: string;
  name: string;
  symbol: string;
  minorUnits: number;
  isActive: boolean;
  rowVersion: number;
}

export interface CurrencyTotal {
  currency: string;
  outputBase: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  outputTax: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  inputBase: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  inputTax: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  recoverableInputTax: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  netPayable: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
}

export interface CurrentPositionQuery {
  companyId: number;
  asOf?: string;
}

export interface CurrentQuery {
  asOf?: string;
}

export interface CursorQuery {
  cursor?: string;
  limit?: number;
}

export interface CustomerAssetDTO {
  id: number;
  customerId: number;
  customerName?: string;
  name: string;
  category: string;
  modelNo: string;
  serialNo: string;
  itemSerialId?: number | null;
  location: string;
  purchasedAt?: string;
  warrantyExpiresAt?: string;
  notes: string;
  createdAt: string;
  rowVersion: number;
}

export interface CustomerContractDTO {
  id: number;
  companyId: number;
  customerId: number;
  code: string;
  name: string;
  tradeAgreementId?: number | null;
  status: string;
  startsOn: string;
  endsOn?: string | null;
  renewalNoticeDays: number;
  priceReviewOn?: string | null;
  autoRenew: boolean;
  ownerPositionId?: number | null;
  currency: string;
  note?: string;
  approvalRequestId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface CustomerContractDetailDTO {
  id: number;
  companyId: number;
  customerId: number;
  code: string;
  name: string;
  tradeAgreementId?: number | null;
  status: string;
  startsOn: string;
  endsOn?: string | null;
  renewalNoticeDays: number;
  priceReviewOn?: string | null;
  autoRenew: boolean;
  ownerPositionId?: number | null;
  currency: string;
  note?: string;
  approvalRequestId?: number | null;
  createdAt: string;
  rowVersion: number;
  obligations: CustomerContractObligationDTO[];
}

export interface CustomerContractObligationDTO {
  id: number;
  seq: number;
  kind: string;
  description?: string;
  targetValue: string;
  measuredValue: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  dueOn?: string | null;
  status: string;
}

export interface CustomerContractRequest {
  companyId: number;
  customerId: number;
  code: string;
  name: string;
  tradeAgreementId?: number | null;
  startsOn: string;
  endsOn?: string;
  renewalNoticeDays?: number;
  priceReviewOn?: string;
  autoRenew?: boolean | null;
  ownerPositionId?: number | null;
  currency: string;
  note?: string;
  rowVersion?: number;
}

export interface CustomerDTO {
  id: number;
  code: string;
  name: string;
  legalName: string;
  taxGroupId?: number | null;
  priceTier: string;
  currency: string;
  paymentTermsDays: number;
  paymentTermsCode: string;
  creditLimit: string;
  creditLimitCurrency: string;
  overdueBlockDays: number;
  portalAccess: boolean;
  status: 'active' | 'on_hold' | 'blocked';
  email: string;
  phone: string;
  notes: string;
  dunningPaused: boolean;
  dunningPausedReason?: string;
  dunningPausedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface CustomerPositionDTO {
  customerId: number;
  customerCode: string;
  customerName: string;
  currency: string;
  asOf: string;
  sources: PositionSourceDTO[];
  totalExposure: string;
  creditLimit: string;
  creditLimitCurrency: string;
  overdueBlockDays: number;
  overdueAmount: string;
  overdueDays: number;
  currentEcl: string;
  eclRunId?: number | null;
  eclAsOf?: string | null;
  headroom?: string | null;
  generatedAt: string;
}

export interface CutoffPMSGroupBlockRequest {
  note?: string;
}

export interface CycleCountLineDetailDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  binId?: number | null;
  lotId?: number | null;
  systemQty?: string | null;
  countedQty?: string | null;
  varianceQty?: string | null;
  unitCost?: string;
  countedBy?: number | null;
  countedAt?: string | null;
  recountQty?: string | null;
  recountBy?: number | null;
  recountAt?: string | null;
  recountRequired: boolean;
  acceptedSource?: string;
  varianceReasonCode?: string;
  rootCauseCode?: string;
  attachmentId?: number | null;
  note?: string;
  gainLossAccountId?: number | null;
}

export interface CycleCountSessionDetailDTO {
  id: number;
  sessionNo?: string | null;
  companyId: number;
  warehouseId: number;
  countDate: string;
  blind: boolean;
  status: 'open' | 'counting' | 'recount' | 'review' | 'posted' | 'cancelled';
  countMethod: 'full' | 'abc' | 'zone' | 'item' | 'expiring' | 'spot';
  zoneId?: number | null;
  recountThresholdPct?: string | null;
  recountThresholdValue?: string | null;
  requireDifferentCounter: boolean;
  freezeScope: boolean;
  frozenAt?: string | null;
  unfrozenAt?: string | null;
  cancelledReason?: string;
  varianceQty?: string;
  varianceValue?: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  lines?: CycleCountLineDetailDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface DSOQuery {
  companyId: number;
  from: string;
  to: string;
  customerId?: number;
  groupBy?: 'business_unit' | 'segment';
}

export interface DSOReport {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  days: number;
  creditSales: string;
  closingReceivables: string;
  dso?: string;
  formula: string;
  generatedAt: string;
  groupBy?: 'business_unit' | 'segment';
  groups?: ArDsoGroupDTO[];
}

export interface DSRErasurePreview {
  dsrId: number;
  partyId: number;
  partyName: string;
  identityVerified: boolean;
  fields: DSRErasurePreviewFieldDTO[];
  columnsErased: number;
  columnsPseudonymised: number;
  columnsRetained: number;
  alreadyErased: boolean;
  attachmentCount: number;
  uncoveredLinkTypes: string[];
}

export interface DSRErasurePreviewFieldDTO {
  linkType: string;
  tableName: string;
  column: string;
  records: number;
  treatment: string;
  basis: string;
  retainedUntil?: string;
  why?: string;
  unruled: boolean;
}

export interface DSRErasureReport {
  dsrId: number;
  partyId: number;
  fields: DSRErasurePreviewFieldDTO[];
  columnsErased: number;
  columnsPseudonymised: number;
  columnsRetained: number;
  recordsTouched: number;
  unruledColumnsErased: string[];
  attachmentsQueued: number;
  alreadyErased: boolean;
}

export interface DSRExportBundle {
  dsrId: number;
  partyId: number;
  attachmentId: number;
  manifestHash: string;
  sections: DSRExportSectionInfo[];
  generatedAtUtc: string;
}

export interface DSRExportSectionInfo {
  code: string;
  title: string;
  rows: number;
}

export interface DailyOperatingBoardDTO {
  companyId: number;
  from: string;
  to: string;
  dueHour: number;
  rows: DailyOperatingReportDTO[];
  lateCount: number;
  missingCount: number;
}

export interface DailyOperatingBoardQuery {
  companyId: number;
  from: string;
  to: string;
}

export interface DailyOperatingDetailDTO {
  report: DailyOperatingReportDTO;
  sections: DailyOperatingSectionDTO[];
}

export interface DailyOperatingLineDTO {
  family: 'revenue' | 'controllable_cost' | 'labour' | 'cash' | 'exception';
  stream?: string;
  labelKey?: string;
  label?: string;
  quantity?: string | null;
  amount?: string | null;
  currency?: string;
  metricCode?: string | null;
  isMemo: boolean;
  memoCountedIn?: string;
  drillLink?: string;
  sortOrder: number;
}

export interface DailyOperatingReportDTO {
  id: number;
  companyId: number;
  unitType: string;
  unitId: number;
  unitName?: string;
  businessDate: string;
  businessDayId?: number | null;
  currency: string;
  status: string;
  publishedAt?: string | null;
  publishedBy?: number | null;
  publishedOnTime?: boolean | null;
  dueAt?: string | null;
  revenueTotal: string;
  controllableCostTotal: string;
  labourCostTotal: string;
  cashMovementTotal: string;
  exceptionCount: number;
  ledgerRevenueTotal: string;
  ledgerDifference: string;
  ledgerScope: string;
  tiesToLedger: boolean;
  failedSections: string[];
  rowVersion: number;
}

export interface DailyOperatingSectionDTO {
  sectionKey: string;
  module: string;
  status: string;
  errorMessage?: string;
  durationMs: number;
  sortOrder: number;
  lines: DailyOperatingLineDTO[];
}

export interface DashboardQuery {
  companyId: number;
}

export interface DashboardResponse {
  tenantsByStatus: Record<string, number>;
  tenantsByCluster: Record<string, number>;
  signupsByDay: DaySignups[];
  failingMigrations: number;
  totalTenants: number;
  dbHealthy: boolean;
  redisHealthy: boolean;
}

export interface DataClassification {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  dataDomain: string;
  sensitivity: string;
  readAuditRequired: boolean;
  retentionYears?: number | null;
  residencyConstraint?: string;
  maskingRule?: string;
}

export interface DateRangeQuery {
  from: string;
  to: string;
}

export interface DayBoardDTO {
  companyId: number;
  on: string;
  units: DayBoardUnit[];
}

export interface DayBoardUnit {
  unitType: 'company' | 'branch' | 'location';
  unitId: number;
  day?: BusinessDay | null;
  blockingModules?: string[];
}

export interface DayPart {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  calendarId: number;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  crossesMidnight: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface DaySignups {
  day: string;
  count: number;
}

export interface DeactivateBankAccountRequest {
  reason: string;
}

export interface DeactivateBusinessUnitRequest {
  rowVersion: number;
  effectiveTo?: string;
}

export interface DebtAccrualResult {
  companyId: number;
  fiscalPeriodId: number;
  periodLabel: string;
  runDate: string;
  runId?: number;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  processedCount: number;
  interestTotal: string;
  feeTotal: string;
  skipped?: DebtAccrualSkipDTO[];
  generatedAt: string;
}

export interface DebtAccrualRunDTO {
  id: number;
  companyId: number;
  fiscalPeriodId: number;
  periodName?: string;
  runDate: string;
  periodFrom: string;
  periodTo: string;
  facilityCount: number;
  interestTotal: string;
  feeTotal: string;
  interestExpense: string;
  entryId?: number | null;
  createdAt: string;
}

export interface DebtAccrualRunDetailDTO {
  id: number;
  companyId: number;
  fiscalPeriodId: number;
  periodName?: string;
  runDate: string;
  periodFrom: string;
  periodTo: string;
  facilityCount: number;
  interestTotal: string;
  feeTotal: string;
  interestExpense: string;
  entryId?: number | null;
  createdAt: string;
  accruals: DebtInterestAccrualDTO[];
  skipped: DebtAccrualSkipDTO[];
}

export interface DebtAccrualSegmentDTO {
  seqNo: number;
  from: string;
  to: string;
  days: number;
  principal: string;
  rate: string;
  exactInterest: string;
  exactFee: string;
  undrawnBalance: string;
}

export interface DebtAccrualSkipDTO {
  facilityId: number;
  facilityCode: string;
  reason: string;
}

export interface DebtCovenantDTO {
  id: number;
  facilityId: number;
  facilityCode?: string;
  facilityName?: string;
  code: string;
  name: string;
  covenantKind: 'financial' | 'information' | 'affirmative' | 'negative';
  metricKey?: string;
  operator?: string;
  threshold?: string | null;
  testFrequency: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'at_maturity';
  graceDays: number;
  description?: string;
  isActive: boolean;
  latestOutcome?: 'pending' | 'pass' | 'fail' | 'waived' | 'not_tested';
  latestTest?: DebtCovenantTestDTO | null;
  rowVersion: number;
  approvalStatus: 'pending_approval' | 'approved' | 'rejected';
  approvedBy?: number | null;
  approvedAt?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  computable: boolean;
  pendingChange?: TreasuryDebtChangeRequestDTO | null;
}

export interface DebtCovenantTestDTO {
  id: number;
  covenantId: number;
  covenantCode?: string;
  covenantName?: string;
  facilityId?: number;
  facilityCode?: string;
  operator?: string;
  fiscalPeriodId?: number | null;
  testDate: string;
  dueDate?: string | null;
  actualValue?: string | null;
  threshold?: string | null;
  outcome: 'pending' | 'pass' | 'fail' | 'waived' | 'not_tested';
  computation?: string;
  valueSource?: string;
  headroom?: string | null;
  waiverRef?: string;
  waiverExpiresOn?: string | null;
  attachmentId?: number | null;
  note?: string;
  testedBy?: number | null;
  testedByName?: string;
  testedAt?: string | null;
  rowVersion: number;
}

export interface DebtDrawdownDTO {
  id: number;
  facilityId: number;
  facilityCode?: string;
  bankAccount?: string;
  drawdownNo: string;
  valueDate: string;
  amount: string;
  currency: string;
  fixedRate?: string | null;
  bankAccountId?: number | null;
  transferId?: number | null;
  status: 'draft' | 'pending_approval' | 'in_transit' | 'posted' | 'reversed' | 'cancelled';
  entryId?: number | null;
  reversalEntryId?: number | null;
  reference?: string;
  memo?: string;
  createdAt: string;
  rowVersion: number;
  source: 'bank' | 'rollover' | 'legacy';
  requestedValueDate?: string;
  statementLineId?: number | null;
  confirmedBy?: number | null;
  confirmedAt?: string | null;
  cancelReason?: string;
  approvalRequestId?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  decisionNote?: string;
  createdBy?: number | null;
}

export interface DebtFacilityDTO {
  id: number;
  companyId: number;
  companyName?: string;
  code: string;
  name: string;
  facilityKind: 'term_loan' | 'revolving' | 'overdraft' | 'guarantee' | 'demand_loan' | 'lease_liability' | 'bond' | 'intercompany_loan';
  lenderName: string;
  lenderVendorId?: number | null;
  bankId?: number | null;
  icInstrumentId?: number | null;
  securityDescription?: string;
  agreementRef?: string;
  attachmentId?: number | null;
  currency: string;
  facilityLimit: string;
  rateKind: 'fixed' | 'floating';
  baseRateCode?: string;
  fixedRate: string;
  marginRate: string;
  rateFloor?: string | null;
  rateCap?: string | null;
  dayCount: 'act_365' | 'act_360' | 'thirty_360';
  interestFrequency: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'at_maturity';
  commitmentFeeRate: string;
  arrangementFeeTreatment: 'expense' | 'amortise';
  arrangementFeeAmount: string;
  startDate: string;
  maturityDate: string;
  defaultBankAccountId?: number | null;
  principalAccountId?: number | null;
  principalCurrentAccountId?: number | null;
  interestExpenseAccountId?: number | null;
  interestPayableAccountId?: number | null;
  feeExpenseAccountId?: number | null;
  costCenterId?: number | null;
  status: 'draft' | 'pending_approval' | 'active' | 'in_default' | 'matured' | 'closed';
  activatedAt?: string | null;
  closedAt?: string | null;
  notes?: string;
  createdBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  approvalNote?: string;
  rejectionNote?: string;
  termsEditedBy?: number | null;
  supersedesFacilityId?: number | null;
  supersededByFacilityId?: number | null;
  rolloverReason?: string;
  closeRequestedBy?: number | null;
  closeRequestedAt?: string | null;
  closeReason?: string;
  closedBy?: number | null;
  drawnAmount: string;
  inTransitAmount: string;
  awaitingApprovalAmount: string;
  undrawnAmount: string;
  accrualDegraded: boolean;
  missedAccrualPeriods: string[];
  outstandingPrincipal: string;
  accruedInterest: string;
  currentPortion: string;
  nonCurrentPortion: string;
  effectiveRate?: string;
  createdAt: string;
  rowVersion: number;
}

export interface DebtFacilityDetailDTO {
  facility: DebtFacilityDTO;
  schedule?: DebtScheduleDTO | null;
  drawdowns: DebtDrawdownDTO[];
  repayments: DebtRepaymentDTO[];
  accruals: DebtInterestAccrualDTO[];
  covenants: DebtCovenantDTO[];
  openCovenantFailCount: number;
  securities: TreasuryDebtSecurityDTO[];
  changes: TreasuryDebtChangeRequestDTO[];
}

export interface DebtInterestAccrualDTO {
  id: number;
  runId: number;
  facilityId: number;
  facilityCode?: string;
  facilityName?: string;
  fiscalPeriodId: number;
  periodFrom: string;
  periodTo: string;
  interestAmount: string;
  feeAmount: string;
  currency: string;
  dayCount: 'act_365' | 'act_360' | 'thirty_360';
  days: number;
  averagePrincipal: string;
  effectiveRate: string;
  interestExpense: string;
  segments: DebtAccrualSegmentDTO[];
}

export interface DebtMaturityBandDTO {
  band: 'months0to3' | 'months3to12' | 'years1to2' | 'years2to5' | 'over5';
  principal: string;
  interest: string;
}

export interface DebtMaturityFacilityDTO {
  facilityId: number;
  facilityCode: string;
  facilityName: string;
  facilityKind: 'term_loan' | 'revolving' | 'overdraft' | 'guarantee' | 'demand_loan' | 'lease_liability' | 'bond' | 'intercompany_loan';
  lenderName: string;
  currency: string;
  maturityDate: string;
  outstandingPrincipal: string;
  currentPortion: string;
  nonCurrentPortion: string;
  baseOutstanding: string;
  accrualDegraded: boolean;
  missedAccrualPeriods: string[];
}

export interface DebtMaturityQuery {
  companyId: number;
  asOf?: string;
}

export interface DebtMaturityReportDTO {
  companyId: number;
  asOf: string;
  baseCurrency: string;
  totalDrawn: string;
  currentPortion: string;
  nonCurrentPortion: string;
  facilities: DebtMaturityFacilityDTO[];
  ladder: DebtMaturityBandDTO[];
  generatedAt: string;
  degraded: boolean;
}

export interface DebtOverride {
  reasonCode: string;
  note?: string;
}

export interface DebtRateObservationDTO {
  id: number;
  rateCode: string;
  asOf: string;
  rate: string;
  source: string;
  note?: string;
}

export interface DebtRepaymentDTO {
  id: number;
  facilityId: number;
  facilityCode?: string;
  bankAccount?: string;
  repaymentNo: string;
  valueDate: string;
  scheduleLineId?: number | null;
  principalAmount: string;
  interestAmount: string;
  feeAmount: string;
  penaltyAmount: string;
  currency: string;
  bankAccountId?: number | null;
  transferId?: number | null;
  status: 'draft' | 'pending_approval' | 'in_transit' | 'posted' | 'reversed' | 'cancelled';
  entryId?: number | null;
  reversalEntryId?: number | null;
  reference?: string;
  memo?: string;
  createdAt: string;
  rowVersion: number;
  source: 'bank' | 'rollover' | 'legacy';
  statementLineId?: number | null;
  interestExpenseAmount: string;
  approvalRequestId?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  decisionNote?: string;
  createdBy?: number | null;
}

export interface DebtScheduleDTO {
  facilityId: number;
  scheduleVersion: number;
  currency: string;
  committed: boolean;
  lines: DebtScheduleLineDTO[];
  totalPrincipal: string;
  totalInterest: string;
  totalFee: string;
}

export interface DebtScheduleLineDTO {
  id?: number;
  seqNo: number;
  dueDate: string;
  principalDue: string;
  interestDue: string;
  feeDue: string;
  closingPrincipal: string;
  status: 'scheduled' | 'paid' | 'partly_paid' | 'waived' | 'superseded';
  paidPrincipal: string;
  paidInterest: string;
  paidFee: string;
}

export interface DecideAmendmentRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideBeneficiaryChangeRequest {
  approve: boolean;
  note?: string;
  rowVersion: number;
}

export interface DecideCaptureRequest {
  approve: boolean;
  hasTaxInvoice?: boolean;
  recoverableTaxAmount?: string;
  expenseAccountId?: number | null;
  reasonCode?: 'duplicate' | 'no_evidence' | 'not_business_expense' | 'over_limit' | 'other';
  note?: string;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface DecideChangeRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideContractRequest {
  approved: boolean | null;
  comment?: string;
}

export interface DecideDetailChangeRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideECORequest {
  decision: string;
  comment?: string;
}

export interface DecideExpenseClaimRequest {
  comment?: string;
}

export interface DecideHoldRequest {
  accept: boolean;
  reasonCode: string;
  note?: string;
}

export interface DecideInvoiceRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideLeaveRequestRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideOfferRequest {
  action: string;
  rowVersion: number;
}

export interface DecidePORequest {
  reject?: boolean;
  comment?: string;
}

export interface DecidePRRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideProjectBaselineRequest {
  comment?: string;
}

export interface DecideRVMDisplacementRequest {
  decision: 'draft' | 'evaluated' | 'accepted' | 'declined' | 'countered' | 'withdrawn' | 'lapsed';
  reasonCode: string;
  note?: string;
  counteredAdr?: string;
  hurdleOverrideReasonCode?: string;
  hurdleOverrideNote?: string;
}

export interface DecideRVMForecastVersionRequest {
  note?: string;
}

export interface DecideRatificationRequest {
  ratify: boolean;
  note?: string;
  compensatingDocType?: string;
  compensatingDocId?: number | null;
}

export interface DecideReplenishmentRequest {
  decision?: string;
  reason?: string;
  approve: boolean;
  approvedAmount?: string;
  fundingBankAccountId?: number | null;
  reasonCode?: string;
  note?: string;
  rowVersion: number;
}

export interface DecideRequest {
  approve: boolean;
  note?: string;
}

export interface DecideRequisitionRequest {
  reject?: boolean;
  comment?: string;
}

export interface DecideRunRequest {
  reject?: boolean;
  comment?: string;
}

export interface DeclareConflictRequest {
  vendorId: number;
  note: string;
}

export interface DeclareDeterminantRequest {
  configurationType: string;
  determinantType: string;
  effectiveFrom: string;
  note: string;
}

export interface DeclineRFQRequest {
  reason?: string;
}

export interface DecommissionEAMAssetRequest {
  decommissionedOn?: string;
  attachmentId: number;
  note?: string;
  rowVersion: number;
}

export interface DelegateRequest {
  toUserId: number;
  comment?: string;
}

export interface DelegationDTO {
  id: number;
  fromUserId: number;
  toUserId: number;
  toUserName?: string;
  docType?: string;
  startsOn: string;
  endsOn: string;
  reason?: string;
}

export interface DeleteAuthorityRuleRequest {
  rowVersion: number;
}

export interface DeleteCharacteristicQuery {
  rowVersion: number;
}

export interface DeletePMSGuestPreferenceRequest {
  category: 'room' | 'housekeeping' | 'dining' | 'transport' | 'other';
  code: string;
}

export interface DeleteQuery {
  rowVersion?: number;
}

export interface DeliverPMSServiceBookingRequest {
  rowVersion: number;
}

export interface DeliveryScheduleRowInput {
  seqNo: number;
  scheduledDate: string;
  quantity: string;
  confirmedDate?: string;
  shipToWarehouseId?: number | null;
  notes?: string;
}

export interface DemandHistoryQuery {
  cursor?: string;
  limit?: number;
  itemId: number;
  warehouseId: number;
}

export interface DemandInputDTO {
  id: number;
  companyId: number;
  locationId: number;
  businessDate: string;
  dayPart?: string;
  metric: 'occupied_rooms' | 'arrivals' | 'departures' | 'covers' | 'transactions' | 'revenue' | 'headcount';
  value: string;
  source: string;
  note?: string;
  rowVersion: number;
}

export interface DemandPeriodDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  periodStart: string;
  periodEnd: string;
  issuedQty: string;
  excludedQty: string;
  orderCount: number;
  computedAt: string;
}

export interface DependencyDTO {
  id: number;
  taskId: number;
  dependsOnTaskId: number;
  kind: string;
}

export interface DependencyPosture {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  fromModule: string;
  toModule: string;
  posture: string;
  continuesDuringOutage: boolean;
  note?: string;
}

export interface DependencyStatus {
  name: string;
  status: string;
  error?: string;
}

export interface DepletionComponentDTO {
  saleLineNo: number;
  componentItemId: number;
  componentItemSku?: string;
  lotId?: number | null;
  lotNo?: string;
  theoreticalQty: string;
  issuedQty: string;
  unitCost: string;
  value: string;
  stockLedgerEntryId?: number | null;
  shortfallQty: string;
}

export interface DepletionLineDTO {
  id?: number;
  saleLineNo: number;
  soldItemId: number;
  soldItemSku?: string;
  soldQty: string;
  recipeId?: number | null;
  resolution: 'resolved' | 'unresolved' | 'skipped';
  unresolvedReason?: string;
  theoreticalValue: string;
  actualValue: string;
}

export interface DepletionRequest {
  companyId: number;
  warehouseId: number;
  date: string;
  businessDayId?: number | null;
  shiftCode?: string;
  docType: string;
  docId: number;
  docNo?: string;
  mode?: 'post' | 'preview';
  reference?: string;
  lines: DepletionRequestLine[];
}

export interface DepletionRequestLine {
  saleLineNo: number;
  itemId: number;
  quantity: string;
  recipeId?: number | null;
  modifierCodes?: string[];
  lotId?: number | null;
}

export interface DepletionRunDTO {
  id: number;
  companyId: number;
  warehouseId: number;
  docType: string;
  docId: number;
  docNo?: string;
  businessDate: string;
  businessDayId?: number | null;
  shiftCode?: string;
  status: 'posted' | 'reversed';
  journalEntryId?: number | null;
  reversedByRunId?: number | null;
  totalValue: string;
  unresolvedLines: number;
  skippedLines: number;
  reference?: string;
  alreadyRecorded: boolean;
  preview: boolean;
  unknownModifiers?: string[];
  lines?: DepletionLineDTO[];
  components?: DepletionComponentDTO[];
  shortfalls?: DepletionShortfallDTO[];
}

export interface DepletionRunDetailDTO {
  id: number;
  companyId: number;
  warehouseId: number;
  docType: string;
  docId: number;
  docNo?: string;
  businessDate: string;
  businessDayId?: number | null;
  shiftCode?: string;
  status: 'posted' | 'reversed';
  journalEntryId?: number | null;
  reversedByRunId?: number | null;
  totalValue: string;
  unresolvedLines: number;
  skippedLines: number;
  reference?: string;
  alreadyRecorded: boolean;
  preview: boolean;
  unknownModifiers?: string[];
  lines?: DepletionLineDTO[];
  components?: DepletionComponentDTO[];
  shortfalls?: DepletionShortfallDTO[];
}

export interface DepletionShortfallDTO {
  saleLineNo: number;
  componentItemId: number;
  itemSku?: string;
  requiredQty: string;
  shortfallQty: string;
}

export interface DepreciationBookRunResult {
  bookId: number;
  bookCode: string;
  isPrimary: boolean;
  postsToGl: boolean;
  runId: number;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  processedCount: number;
  totalAmount: string;
  skipped: DepreciationSkip[];
}

export interface DepreciationEntryDTO {
  id: number;
  assetId: number;
  runId: number;
  fiscalPeriodId: number;
  periodLabel?: string;
  periodNo: number;
  openingNbv: string;
  amount: string;
  closingNbv: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  createdAt: string;
}

export interface DepreciationRunDTO {
  id: number;
  companyId: number;
  fiscalPeriodId: number;
  periodLabel?: string;
  runDate: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  assetCount: number;
  totalAmount: string;
  skippedCount: number;
  skipped: DepreciationSkip[];
  createdAt: string;
}

export interface DepreciationRunResult {
  runId?: number;
  companyId: number;
  fiscalPeriodId: number;
  periodLabel: string;
  runDate: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  processedCount: number;
  totalAmount: string;
  skipped: DepreciationSkip[];
  bookRuns: DepreciationBookRunResult[];
  generatedAt: string;
}

export interface DepreciationSkip {
  assetId: number;
  assetNo: string;
  reason: string;
  bookId?: number;
  bookCode?: string;
}

export interface DesignationDTO {
  id: number;
  companyId: number;
  kind: 'PSU' | 'TRU' | 'LE';
  jurisdictionId: number;
  registrationId?: number | null;
  identifier: string;
  note: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface DesignationsResult {
  legalEmployer?: DesignationDTO | null;
  psu?: PSUDesignationDTO | null;
}

export interface DespatchRequest {
  carrierCode?: string;
  carrierName?: string;
  trackingNo?: string;
  despatchedAt?: string;
}

export interface DespatchReturnRequest {
  despatchDate?: string;
}

export interface DespatchTransferLineRequest {
  transferLineId: number;
  despatchedQty?: string;
  lotId?: number | null;
  binId?: number | null;
}

export interface DespatchTransferRequest {
  date: string;
  expectedArrivalOn?: string;
  carrierName?: string;
  carrierReference?: string;
  vehicleReference?: string;
  driverName?: string;
  autoReceive?: boolean;
  overrideReasonCode?: string;
  overrideNote?: string;
  serials?: DespatchTransferSerialLine[];
  lines?: DespatchTransferLineRequest[];
}

export interface DespatchTransferSerialLine {
  transferLineId: number;
  serialNos: string[];
}

export interface DetailChangeDTO {
  id: number;
  employeeId: number;
  kind: string;
  status: string;
  reason: string;
  bankName?: string;
  bankBranch?: string;
  bankAccountName?: string;
  bankAccountNoMasked?: string;
  bankIbanMasked?: string;
  bankSwift?: string;
  bankCurrency?: string;
  nationalIdMasked?: string;
  taxIdMasked?: string;
  requestedBy?: number | null;
  requestedAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  appliedAt?: string | null;
  approvalRequestId?: number | null;
}

export interface DeterminantDTO {
  id: number;
  configurationType: string;
  determinantType: string;
  effectiveFrom: string;
  effectiveTo?: string;
  declaredBy?: number | null;
  note?: string;
  superseded: boolean;
}

export interface Determination {
  context: Context;
  place: Place;
  computations: Computation[];
  lines: TaxLine[];
  warnings: Warning[];
}

export interface DeviceContentAssignmentDTO {
  id: number;
  deviceId: number;
  setId: number;
  setCode: string;
  publishedVersionId?: number | null;
  publishedVersionNo: number;
  heldVersionId?: number | null;
  heldVersionNo: number;
  heldHash: string;
  hashMismatch: boolean;
  heldReportedAt?: string | null;
  stale: boolean;
}

export interface DeviceContentCheckDTO {
  versionId: number;
  ok: boolean;
  missingKinds: string[];
  findings: DeviceContentFindingDTO[];
}

export interface DeviceContentFindingDTO {
  checker: string;
  code: string;
  ref: string;
  message: string;
}

export interface DeviceContentManifestDTO {
  setId: number;
  setCode: string;
  versionId: number;
  versionNo: number;
  contentHash: string;
  validFrom?: string | null;
  validUntil?: string | null;
  parts: DeviceContentSetPartDTO[];
  serverTime: string;
}

export interface DeviceContentPartResponse {
  partId: number;
  kind: 'price_set_version' | 'tax_pack' | 'menu_version' | 'operator_roster' | 'terminal_policy' | 'housekeeping_board' | 'work_order_pack';
  refId: number;
  refHash: string;
  byteSize: number;
  payload: Record<string, unknown>;
  serverTime: string;
}

export interface DeviceContentSetDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  surface: string;
  bindingKind: string;
  bindingId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface DeviceContentSetPartDTO {
  id: number;
  seqNo: number;
  kind: 'price_set_version' | 'tax_pack' | 'menu_version' | 'operator_roster' | 'terminal_policy' | 'housekeeping_board' | 'work_order_pack';
  refId: number;
  refVersion: number;
  refHash: string;
  byteSize: number;
}

export interface DeviceContentSetVersionDTO {
  id: number;
  setId: number;
  versionNo: number;
  status: 'draft' | 'published' | 'withdrawn';
  contentHash: string;
  partCount: number;
  validFrom?: string | null;
  validUntil?: string | null;
  publishedAt?: string | null;
  withdrawnAt?: string | null;
  note: string;
  parts: DeviceContentSetPartDTO[];
  rowVersion: number;
}

export interface DeviceDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  surface: string;
  bindingKind: string;
  bindingId: number;
  status: 'pending' | 'active' | 'suspended' | 'revoked';
  enrolled: boolean;
  enrolledAt?: string | null;
  attestedPlatform: string;
  attestedAppVersion: string;
  attestedAt?: string | null;
  installationId: string;
  lastSeenAt?: string | null;
  lastSeenIp: string;
  revokedAt?: string | null;
  revokeReason: string;
  rowVersion: number;
  createdAt: string;
}

export interface DeviceDetailDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  surface: string;
  bindingKind: string;
  bindingId: number;
  status: 'pending' | 'active' | 'suspended' | 'revoked';
  enrolled: boolean;
  enrolledAt?: string | null;
  attestedPlatform: string;
  attestedAppVersion: string;
  attestedAt?: string | null;
  installationId: string;
  lastSeenAt?: string | null;
  lastSeenIp: string;
  revokedAt?: string | null;
  revokeReason: string;
  rowVersion: number;
  createdAt: string;
  assignments: DeviceContentAssignmentDTO[];
  pendingMutations: number;
  lastSyncAt?: string | null;
  trading: DeviceTradingStateDTO;
}

export interface DeviceEnrolRequest {
  code: string;
  installationId: string;
  platform?: string;
  appVersion?: string;
}

export interface DeviceEnrolResponse {
  deviceId: number;
  deviceSecret: string;
  tenantSlug: string;
  code: string;
  surface: string;
  bindingKind: string;
  bindingId: number;
}

export interface DeviceEnrolmentCodeResponse {
  deviceId: number;
  code: string;
  expiresAt: string;
}

export interface DeviceFailedSignIn {
  userId: number;
  attempts: number;
}

export interface DeviceHeartbeatRequest {
  heldVersionId?: number | null;
  heldHash?: string;
  queueDepth?: number;
  appVersion?: string;
  failedSignIns?: DeviceFailedSignIn[];
}

export interface DeviceHeartbeatResponse {
  serverTime: string;
  publishedVersionId?: number | null;
  publishedHash: string;
  contentStale: boolean;
  deviceStatus: 'pending' | 'active' | 'suspended' | 'revoked';
  queueDrained: boolean;
}

export interface DeviceMenuResponse {
  versionId: number;
  hash: string;
  menu: Record<string, unknown>;
}

export interface DeviceMutationInput {
  localSeq: number;
  mutationKind: string;
  operatorUserId: number;
  businessDate: string;
  occurredAt: string;
  payload: Record<string, unknown>;
  payloadHash?: string;
  offlineAuths?: DeviceOfflineAuthInput[];
  contentSetVersionId?: number | null;
}

export interface DeviceMutationResultDTO {
  localSeq: number;
  status: 'pending' | 'applied' | 'replayed' | 'conflict' | 'failed';
  targetKind: string;
  targetId?: number | null;
  errorCode: string;
  errorDetail: string;
  conflictId?: number | null;
  conflictRule?: 'server_wins_config' | 'client_wins_completed' | 'queued_for_review';
  warnings: DeviceMutationWarningDTO[];
}

export interface DeviceMutationWarningDTO {
  code: string;
  message: string;
  field?: string;
}

export interface DeviceOfflineAuthInput {
  controlCode: string;
  policy: string;
  reasonCode?: string;
  note?: string;
  authorisedByUserId?: number;
  limitApplied?: string;
  amount?: string;
}

export interface DeviceOfflinePolicyDTO {
  id: number;
  companyId: number;
  bindingKind: string;
  bindingId?: number | null;
  controlCode: string;
  policy: 'permit_within_limit' | 'defer_ratification' | 'refuse';
  limitAmount?: string;
  limitPercent?: string;
  limitCount?: number | null;
  reasonSet: string;
  effectiveFrom: string;
  effectiveTo?: string;
  rowVersion: number;
  supersededBy?: number | null;
  note?: string | null;
}

export interface DeviceOperatorRosterDTO {
  id: number;
  companyId: number;
  bindingKind: string;
  bindingId: number;
  versionNo: number;
  contentHash: string;
  validUntil: string;
  entryCount: number;
  publishedAt?: string | null;
  entries: DeviceOperatorRosterEntryDTO[];
}

export interface DeviceOperatorRosterEntryDTO {
  userId: number;
  displayName: string;
  pinSalt?: string;
  pinVerifier?: string;
  permissions: string[];
  offlineLimits: Record<string, string>;
  isSupervisor: boolean;
}

export interface DevicePriceManifestResponse {
  versionId: number;
  hash: string;
  manifest: Record<string, unknown>;
}

export interface DevicePublishRosterRequest {
  companyId: number;
  bindingKind: string;
  bindingId: number;
  deviceId: number;
  userIds: number[];
  validHours?: number;
}

export interface DeviceRatificationDTO {
  id: number;
  mutationId: number;
  deviceId: number;
  deviceCode: string;
  controlCode: string;
  reasonCode: string;
  note: string;
  amount?: string;
  status: 'pending' | 'ratified' | 'refused';
  dueAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote: string;
  compensatingDocType: string;
  compensatingDocId?: number | null;
  rowVersion: number;
}

export interface DeviceSyncBatchDTO {
  id: number;
  deviceId: number;
  deviceCode: string;
  batchUuid: string;
  clientAssembledAt?: string | null;
  receivedAt: string;
  completedAt?: string | null;
  mutationCount: number;
  appliedCount: number;
  replayedCount: number;
  conflictCount: number;
  failedCount: number;
  status: 'received' | 'applying' | 'completed' | 'failed';
  clockSkewSeconds?: number | null;
}

export interface DeviceSyncBatchDetailDTO {
  id: number;
  deviceId: number;
  deviceCode: string;
  batchUuid: string;
  clientAssembledAt?: string | null;
  receivedAt: string;
  completedAt?: string | null;
  mutationCount: number;
  appliedCount: number;
  replayedCount: number;
  conflictCount: number;
  failedCount: number;
  status: 'received' | 'applying' | 'completed' | 'failed';
  clockSkewSeconds?: number | null;
  mutations: DeviceSyncMutationDTO[];
}

export interface DeviceSyncConflictDTO {
  id: number;
  mutationId: number;
  deviceId: number;
  deviceCode: string;
  localSeq: number;
  mutationKind: string;
  rule: 'server_wins_config' | 'client_wins_completed' | 'queued_for_review';
  field: string;
  clientValue: string;
  serverValue: string;
  detail: string;
  status: 'open' | 'resolved' | 'dismissed';
  ownerUserId?: number | null;
  resolution: string;
  resolutionNote: string;
  resolvedAt?: string | null;
  createdAt: string;
  ageHours: number;
  rowVersion: number;
}

export interface DeviceSyncMutationDTO {
  id: number;
  batchId: number;
  deviceId: number;
  bindingKind: string;
  bindingId: number;
  localSeq: number;
  mutationKind: string;
  operatorUserId: number;
  businessDate: string;
  occurredAt: string;
  status: 'pending' | 'applied' | 'replayed' | 'conflict' | 'failed';
  targetKind: string;
  targetId?: number | null;
  errorCode: string;
  errorDetail: string;
  appliedAt?: string | null;
  contentSetVersionId?: number | null;
}

export interface DeviceSyncRequest {
  batchUuid: string;
  clientAssembledAt?: string | null;
  heldVersionId?: number | null;
  heldHash?: string;
  mutations: DeviceMutationInput[];
}

export interface DeviceSyncResultDTO {
  batchUuid: string;
  batchId: number;
  status: 'received' | 'applying' | 'completed' | 'failed';
  appliedCount: number;
  replayedCount: number;
  conflictCount: number;
  failedCount: number;
  results: DeviceMutationResultDTO[];
  serverTime: string;
  publishedVersionId?: number | null;
  publishedHash: string;
  queueDrained: boolean;
}

export interface DeviceTelemetryRequest {
  appVersion: string;
  platform?: string;
  errorCount?: number;
  crashCount?: number;
  failedSignIns?: number;
  queueDepth?: number;
  offlineSeconds?: number;
}

export interface DeviceTokenRequest {
  deviceId: number;
  deviceSecret: string;
  installationId: string;
  appVersion?: string;
}

export interface DeviceTokenResponse {
  accessToken: string;
  expiresAt: string;
  deviceId: number;
  tenantSlug: string;
  surface: string;
  bindingKind: string;
  bindingId: number;
}

export interface DeviceTradingStateDTO {
  mayTrade: boolean;
  reason: string;
  hasActiveVersion: boolean;
  hashVerified: boolean;
  validUntil?: string | null;
  lastSyncAt?: string | null;
  contentMaxAgeHours: number;
}

export interface DiffQuery {
  againstVersion: number;
}

export interface DimensionsInput {
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  divisionId?: number | null;
  taxCode?: string;
  counterpartyType?: string;
  counterpartyId?: number | null;
}

export interface DisburseRunRequest {
  bankAccountId?: number | null;
}

export interface DiscountPolicyDTO {
  id: number;
  companyId: number;
  channel?: string;
  segmentCode?: string;
  categoryCode?: string;
  thresholdPercent: string;
  thresholdAmount?: string | null;
  thresholdCurrency?: string | null;
  marginFloorPolicy: string;
  specificity: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface DiscountPolicyRequest {
  companyId: number;
  channel?: string;
  segmentCode?: string;
  categoryCode?: string;
  thresholdPercent: string;
  thresholdAmount?: string;
  thresholdCurrency?: string;
  marginFloorPolicy?: string;
  effectiveFrom: string;
  rowVersion?: number;
}

export interface DismissAttendanceExceptionRequest {
  resolutionReasonCode: string;
  resolutionNote?: string;
  rowVersion: number;
}

export interface DismissMRPSuggestionRequest {
  reason: string;
}

export interface DismissPartyMatchRequest {
  reason: string;
  rowVersion: number;
}

export interface DismissRetroEventRequest {
  reasonCode: string;
  note?: string;
}

export interface DispatchEntryDTO {
  operationId: number;
  productionOrderId: number;
  orderNo?: string | null;
  seqNo: number;
  name: string;
  status: string;
  dispatchSeq?: number | null;
  isFrozen: boolean;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
}

export interface DispatchListDTO {
  workCenterId: number;
  entries: DispatchEntryDTO[];
}

export interface DispatchServiceWorkOrderRequest {
  assignedToUserId?: number | null;
  override?: ServiceDispatchOverrideDTO | null;
  rowVersion: number;
}

export interface DisposeCardLineRequest {
  disposition: string;
  reasonCode: string;
  note?: string;
}

export interface DisposeRequest {
  disposalDate: string;
  proceeds?: string;
  proceedsAccountId?: number | null;
  disposalMethod?: 'sale' | 'scrap' | 'write_off' | 'donation' | 'ic_transfer';
  proceedsReceiptRef?: string;
  reason: string;
  rowVersion: number;
}

export interface DisposeSignalRequest {
  disposition: string;
  reasonCode: string;
  note?: string;
}

export interface DisposeTransactionRequest {
  disposition: string;
  reasonCode: string;
  note?: string;
}

export interface DisputeDTO {
  id: number;
  matchId: number;
  matchLineId?: number | null;
  reason: string;
  status: 'open' | 'resolved';
  openedAt: string;
  resolvedAt?: string | null;
  resolutionNote?: string;
}

export interface DisqualifyLeadRequest {
  reason: string;
  rowVersion: number;
}

export interface DissolveLegalEntityRequest {
  dissolvedOn: string;
  reasonCode: string;
}

export interface DissolveRequest {
  dissolvedOn: string;
  reasonCode: string;
}

export interface DistributePoolRequest {
  runId: number;
  rowVersion: number;
}

export interface DivisionCountryDTO {
  id: number;
  divisionId: number;
  countryCode: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface DivisionFNDDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  managerPositionId?: number | null;
  enterpriseId?: number | null;
  purpose?: string;
  ownerId?: number | null;
  ownerName?: string;
  defaultCurrency?: string;
  isActive: boolean;
  rowVersion: number;
  approvalRequestId?: number | null;
  approvalStatus?: string;
}

export interface DivisionLegalEntityDTO {
  id: number;
  divisionId: number;
  companyId: number;
  companyCode?: string;
  companyName?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface DivisionRollupDTO {
  divisionId: number;
  asAt: string;
  countries: DivisionCountryDTO[];
  legalEntities: DivisionLegalEntityDTO[];
  costCentres: RollupCostCentre[];
}

export interface DivisionSegmentMapDTO {
  id?: number;
  divisionId: number;
  coaStructureId: number;
  segmentSequence: number;
  segmentValue: string;
  isBalancing: boolean;
  approvedBy?: number | null;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface DivisionStep {
  legalEntityRef: string;
  code: string;
  name: string;
}

export interface DocumentSequenceBinding {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  businessUnitId: number;
  ledgerId: number;
  documentType: string;
  jurisdictionId?: number | null;
  sequenceId?: number | null;
}

export interface DownloadDTO {
  url: string;
  expiresAt: string;
}

export interface DraftTransferLineRequest {
  itemId: number;
  quantity: string;
  fromBinId?: number | null;
  toBinId?: number | null;
  lotId?: number | null;
}

export interface DraftTransferRequest {
  fromWarehouseId: number;
  toWarehouseId: number;
  memo?: string;
  transferDate?: string;
  expectedArrivalOn?: string;
  carrierName?: string;
  carrierReference?: string;
  vehicleReference?: string;
  driverName?: string;
  requisitionId?: number | null;
  lines: DraftTransferLineRequest[];
}

export interface DrillQuery {
  companyId: number;
  cursor?: string;
  limit?: number;
}

export interface DrillResponse {
  key: string;
  columns: string[];
  rows: string[][];
  nextCursor: string | null;
  hasMore: boolean;
  generatedAt: string;
}

export interface DryRunImpactDTO {
  structureType: string;
  structureId: number;
  currentValue?: unknown;
  inherits: boolean;
}

export interface DryRunStandingRuleQuery {
  limit?: number;
}

export interface DuplicateCandidate {
  leadId: number;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phone: string;
  status: string;
  matchedField: string;
  similarity: number;
}

export interface EAMAssetDTO {
  id: number;
  companyId: number;
  assetNo: string;
  name: string;
  description?: string;
  functionalLocationId?: number | null;
  parentAssetId?: number | null;
  financeAssetId?: number | null;
  classCode?: string;
  manufacturer?: string;
  modelNo?: string;
  serialNo?: string;
  criticality: number;
  ownership: 'owned' | 'leased' | 'customer';
  lifecycleState: 'planned' | 'commissioned' | 'in_service' | 'standby' | 'decommissioned';
  commissionedOn?: string;
  decommissionedOn?: string;
  warehouseId?: number | null;
  costCenterId?: number | null;
  rowVersion: number;
}

export interface EAMAssetDetailDTO {
  id: number;
  companyId: number;
  assetNo: string;
  name: string;
  description?: string;
  functionalLocationId?: number | null;
  parentAssetId?: number | null;
  financeAssetId?: number | null;
  classCode?: string;
  manufacturer?: string;
  modelNo?: string;
  serialNo?: string;
  criticality: number;
  ownership: 'owned' | 'leased' | 'customer';
  lifecycleState: 'planned' | 'commissioned' | 'in_service' | 'standby' | 'decommissioned';
  commissionedOn?: string;
  decommissionedOn?: string;
  warehouseId?: number | null;
  costCenterId?: number | null;
  rowVersion: number;
  links: EAMAssetLinkDTO[];
  lifecycleEvents: EAMLifecycleEventDTO[];
}

export interface EAMAssetLinkDTO {
  id: number;
  assetId: number;
  linkKind: 'pms.room' | 'mfg.work_center' | 'service.resource' | 'inventory.warehouse';
  linkRef: number;
  linkLabel?: string;
  rowVersion: number;
}

export interface EAMAvailabilityDTO {
  assetId: number;
  assetNo: string;
  assetName: string;
  on: string;
  available: boolean;
  degraded: boolean;
  blockers?: EAMDowntimeDTO[];
}

export interface EAMAvailabilityEventDTO {
  id: number;
  assetId: number;
  assetNo?: string;
  assetName?: string;
  downtimeId?: number | null;
  state: 'unavailable' | 'available';
  reasonCode: string;
  effectiveFrom: string;
  effectiveTo?: string;
  links: EAMAvailabilityLinkDTO[];
  publishedAt: string;
  receipts: EAMAvailabilityReceiptDTO[];
}

export interface EAMAvailabilityLinkDTO {
  kind: string;
  ref: number;
  label?: string;
}

export interface EAMAvailabilityQuery {
  assetIds?: string;
  companyId?: number;
  on?: string;
}

export interface EAMAvailabilityReceiptDTO {
  id: number;
  eventId: number;
  consumer: string;
  linkKind: string;
  linkRef: number;
  outcome: string;
  detail?: string;
  targetDocType?: string;
  targetDocId?: number | null;
  appliedAt: string;
}

export interface EAMComplianceCalendarDTO {
  companyId: number;
  from: string;
  to: string;
  asOf: string;
  overdue: number;
  dueSoon: number;
  entries: EAMComplianceCalendarEntryDTO[];
}

export interface EAMComplianceCalendarEntryDTO {
  requirementId: number;
  assetId: number;
  assetNo: string;
  assetName: string;
  code: string;
  name: string;
  authority?: string;
  dueOn: string;
  standing: string;
  blocksOperation: boolean;
  daysUntilDue: number;
  assetUnavailable: boolean;
}

export interface EAMComplianceCalendarQuery {
  companyId: number;
  from?: string;
  to?: string;
  assetId?: number | null;
}

export interface EAMComplianceRequirementDTO {
  id: number;
  companyId: number;
  assetId: number;
  assetNo?: string;
  assetName?: string;
  code: string;
  name: string;
  authority?: string;
  jurisdictionId?: number | null;
  taskListId?: number | null;
  intervalMonths: number;
  nextDueOn: string;
  lastPassedOn?: string;
  certificateRef?: string;
  certificateAttachmentId?: number | null;
  blocksOperation: boolean;
  warnDays: number;
  isActive: boolean;
  daysUntilDue: number;
  standing: string;
  rowVersion: number;
}

export interface EAMComplianceSweepOutcome {
  requirementId: number;
  assetId: number;
  code: string;
  action: string;
  downtimeId?: number | null;
  eventId?: number | null;
}

export interface EAMComplianceSweepResult {
  companyId: number;
  on: string;
  blocked: number;
  exception: number;
  warned: number;
  outcomes: EAMComplianceSweepOutcome[];
}

export interface EAMDowntimeDTO {
  id: number;
  assetId: number;
  assetNo?: string;
  assetName?: string;
  kind: 'planned' | 'unplanned' | 'compliance';
  state: 'unavailable' | 'degraded';
  reasonCode: string;
  note?: string;
  startedAt: string;
  endedAt?: string;
  source: 'manual' | 'compliance' | 'work_order' | 'failure';
  sourceRef?: string;
  workOrderId?: number | null;
  requirementId?: number | null;
  failureId?: number | null;
  releasedBy?: number | null;
  releaseNote?: string;
  rowVersion: number;
}

export interface EAMFailureDTO {
  id: number;
  assetId: number;
  failureNo: string;
  detectedAt: string;
  failureModeCode: string;
  causeCode?: string;
  severity: 'minor' | 'major' | 'critical';
  description?: string;
  workOrderId?: number | null;
  restoredAt?: string;
  rowVersion: number;
}

export interface EAMFunctionalLocationDTO {
  id: number;
  companyId: number;
  parentId?: number | null;
  locationId?: number | null;
  code: string;
  name: string;
  description?: string;
  path: string;
  depth: number;
  isActive: boolean;
  rowVersion: number;
}

export interface EAMFunctionalLocationTreeDTO {
  id: number;
  companyId: number;
  parentId?: number | null;
  locationId?: number | null;
  code: string;
  name: string;
  description?: string;
  path: string;
  depth: number;
  isActive: boolean;
  rowVersion: number;
  children?: EAMFunctionalLocationTreeDTO[];
}

export interface EAMInspectionDTO {
  id: number;
  requirementId: number;
  inspectionNo: string;
  inspectedOn: string;
  workOrderId?: number | null;
  result: 'pass' | 'pass_with_conditions' | 'fail';
  inspector?: string;
  certificateRef?: string;
  validUntil?: string;
  findings?: string;
  attachmentId?: number | null;
  nextDueOn: string;
  releasedDowntimeId?: number | null;
  availabilityPublished: boolean;
  rowVersion: number;
}

export interface EAMLifecycleEventDTO {
  id: number;
  assetId: number;
  kind: 'commissioned' | 'baselined' | 'modified' | 'relocated' | 'decommissioned';
  occurredOn: string;
  note?: string;
  toState: 'planned' | 'commissioned' | 'in_service' | 'standby' | 'decommissioned';
  attachmentId?: number | null;
  financeDocType?: string;
  financeDocId?: number | null;
  createdAt: string;
}

export interface EAMMeterDTO {
  id: number;
  assetId: number;
  code: string;
  name: string;
  unit: string;
  rollsOver: boolean;
  rolloverAt?: string;
  currentReading: string;
  cumulativeReading: string;
  readAt?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface EAMMeterReadingDTO {
  id: number;
  meterId: number;
  readOn: string;
  reading: string;
  delta: string;
  source: string;
  note?: string;
  regressionReasonCode?: string;
  createdAt: string;
}

export interface EAMPMPlanDTO {
  id: number;
  companyId: number;
  assetId: number;
  strategyId?: number | null;
  name: string;
  defaultTaskListId?: number | null;
  intervalDays?: number | null;
  nextDueOn?: string;
  lastCompletedOn?: string;
  meterId?: number | null;
  intervalMeterUnits?: string;
  lastCompletedReading?: string;
  leadDays?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface EAMPMPlanPreviewDTO {
  planId: number;
  due: boolean;
  dueReason?: string;
  nextDueOn?: string;
  effectiveLeadDays: number;
  asOf: string;
}

export interface EAMPermitDTO {
  id: number;
  companyId: number;
  permitNo: string;
  assetId: number;
  assetNo?: string;
  assetName?: string;
  kind: 'hot_work' | 'confined_space' | 'electrical' | 'height' | 'lifting' | 'general';
  status: 'requested' | 'issued' | 'active' | 'returned' | 'closed' | 'cancelled' | 'expired';
  description?: string;
  hazards?: string;
  precautions?: string;
  validFrom: string;
  validTo: string;
  issuedBy?: number | null;
  issuedAt?: string;
  executedBy?: number | null;
  executedAt?: string;
  acceptedBy?: number | null;
  acceptedAt?: string;
  cancelledAt?: string;
  cancelReasonCode?: string;
  isolationRef?: string;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface EAMReconciliationResponse {
  rows: EAMReconciliationRowDTO[];
}

export interface EAMReconciliationRowDTO {
  eamAssetId?: number | null;
  eamAssetNo?: string;
  eamAssetName?: string;
  eamLifecycleState?: string;
  financeAssetId?: number | null;
  financeAssetNo?: string;
  financeAssetName?: string;
  financeStatus?: string;
  differenceClass: string;
}

export interface EAMReliabilityDTO {
  assetId: number;
  asOf: string;
  windowDays: number;
  failures: number;
  mtbfHours?: string;
  mttrHours?: string;
  downtimeHours: string;
  availabilityPercent?: string;
}

export interface EAMSettingsDTO {
  companyId: number;
  maintenanceExpenseAccountId?: number | null;
  labourClearingAccountId?: number | null;
  defaultWarehouseId?: number | null;
  pmGenerationLeadDays: number;
  enforceComplianceBlock: boolean;
  rowVersion: number;
}

export interface EAMStrategyDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  kind: 'run_to_failure' | 'time_based' | 'usage_based' | 'condition_based';
  description?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface EAMTaskListDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description?: string;
  estimatedHours: string;
  requiresPermit: boolean;
  permitKind?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface EAMTaskListDetailDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description?: string;
  estimatedHours: string;
  requiresPermit: boolean;
  permitKind?: string;
  isActive: boolean;
  rowVersion: number;
  steps: EAMTaskListStepDTO[];
  skills: EAMTaskListSkillDTO[];
}

export interface EAMTaskListSkillDTO {
  skillId: number;
  skillCode?: string;
  skillName?: string;
  isStatutory: boolean;
  minLevel: number;
}

export interface EAMTaskListSkillInput {
  skillId: number;
  minLevel?: number;
}

export interface EAMTaskListStepDTO {
  id: number;
  taskListId: number;
  stepNo: number;
  instruction: string;
  measurementUnit?: string;
  minValue?: string;
  maxValue?: string;
  isMandatory: boolean;
}

export interface EAMTaskListStepInput {
  stepNo: number;
  instruction: string;
  measurementUnit?: string;
  minValue?: string;
  maxValue?: string;
  isMandatory?: boolean | null;
}

export interface ECLAssessmentDTO {
  id: number;
  companyId: number;
  customerId: number;
  asOf: string;
  eclAmount: string;
  basis: string;
  reasonCode: string;
  note?: string;
  attachmentId?: number | null;
  approvalRequestId?: number | null;
  approvedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ECLMatrixDTO {
  id: number;
  companyId: number;
  segmentId: number;
  code: string;
  status: string;
  macroFactor: string;
  macroBasis: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  approvedAt?: string | null;
  approvalRequestId?: number | null;
  createdAt: string;
  rowVersion: number;
  rates?: ECLMatrixRateDTO[];
}

export interface ECLMatrixRateDTO {
  bucket: string;
  bucketSeq: number;
  lossRate: string;
}

export interface ECLMatrixRateInput {
  bucket: string;
  lossRate: string;
}

export interface ECLMovementQuery {
  companyId: number;
  from?: string;
  to?: string;
}

export interface ECLMovementReport {
  companyId: number;
  currency: string;
  rows: ECLMovementRow[];
  closingAllowance: string;
  writeOffUse: string;
  writeOffs: ECLMovementWriteOffRow[];
  generatedAt: string;
}

export interface ECLMovementRow {
  runId: number;
  fiscalPeriodId: number;
  asOf: string;
  priorAllowance: string;
  totalEcl: string;
  movement: string;
  journalEntryId?: number | null;
  postedAt?: string | null;
  writeOffUse: string;
}

export interface ECLMovementWriteOffRow {
  kind: string;
  movementDate: string;
  writeOffId: number;
  writeOffNo: string;
  writeOffDate: string;
  customerId: number;
  customerCode: string;
  customerName: string;
  totalBase: string;
  provisionUsed: string;
  journalEntryId?: number | null;
}

export interface ECLRunDTO {
  id: number;
  companyId: number;
  fiscalPeriodId: number;
  asOf: string;
  status: string;
  currency: string;
  grossReceivables: string;
  collectiveEcl: string;
  individualEcl: string;
  totalEcl: string;
  priorAllowance: string;
  movement: string;
  allowanceAccountId: number;
  expenseAccountId: number;
  journalEntryId?: number | null;
  approvalRequestId?: number | null;
  postedAt?: string | null;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface ECLRunLineDTO {
  id: number;
  runId: number;
  customerId: number;
  customerCode?: string;
  customerName?: string;
  segmentId?: number | null;
  matrixId?: number | null;
  bucket: string;
  bucketSeq: number;
  grossAmount: string;
  creditApplied: string;
  exposure: string;
  lossRate: string;
  macroFactor: string;
  eclAmount: string;
  isIndividual: boolean;
}

export interface ECLSegmentDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  matchKind: string;
  matchValue: string;
  priority: number;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface ECLSettingsDTO {
  id: number;
  companyId: number;
  allowanceAccountId: number;
  expenseAccountId: number;
  creditApplication: string;
  isEnabled: boolean;
  rowVersion: number;
}

export interface ECOChangeItemDTO {
  id: number;
  seqNo: number;
  changeKind: 'bom_line_add' | 'bom_line_change' | 'bom_line_remove' | 'routing_op_change' | 'item_attribute' | 'alternate_add' | 'alternate_remove';
  itemId?: number | null;
  bomId?: number | null;
  bomLineId?: number | null;
  routingId?: number | null;
  routingOperationId?: number | null;
  beforeValue: Record<string, unknown>;
  afterValue: Record<string, unknown>;
  notes?: string;
  appliedAt?: string | null;
}

export interface ECOChangeItemInput {
  changeKind: 'bom_line_add' | 'bom_line_change' | 'bom_line_remove' | 'routing_op_change' | 'item_attribute' | 'alternate_add' | 'alternate_remove';
  itemId?: number | null;
  bomId?: number | null;
  bomLineId?: number | null;
  routingId?: number | null;
  routingOperationId?: number | null;
  afterValue?: Record<string, unknown>;
  notes?: string;
}

export interface ECODispositionDTO {
  id: number;
  seqNo: number;
  subjectKind: 'stock' | 'wip' | 'production_order' | 'purchase_order' | 'inspection_plan';
  itemId?: number | null;
  warehouseId?: number | null;
  docType?: string;
  docId?: number | null;
  docNo?: string;
  affectedQuantity: string;
  affectedValue: string;
  action: 'use_up' | 'rework' | 'scrap' | 'return' | 'amend' | 'no_action';
  reason?: string;
  status: 'open' | 'done' | 'waived';
  outcomeDocType?: string;
  outcomeDocId?: number | null;
  completedAt?: string | null;
  rowVersion: number;
}

export interface EInvoicingConfig {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  country: string;
  gateway: string;
  endpoint: string;
  credentialMasked?: string;
  pollIntervalS: number;
  status: string;
  lastTestAt?: string | null;
  lastTestResult?: Record<string, unknown> | null;
}

export interface EffectiveDefaultDTO {
  key: string;
  value?: unknown;
  level: string;
  source?: string;
  effectiveFrom: string;
}

export interface EliminationDTO {
  id: number;
  runId: number;
  companyId: number;
  companyCode?: string;
  counterpartyCompanyId: number;
  counterpartyCompanyCode?: string;
  dueFromAccountId: number;
  dueFromAccountCode?: string;
  dueToAccountId?: number | null;
  dueToAccountCode?: string;
  mirrorConfigured: boolean;
  companyCurrency: string;
  companyBalanceLocal: string;
  companyRate: string;
  companyBalanceTranslated: string;
  counterpartyCurrency: string;
  counterpartyBalanceLocal: string;
  counterpartyRate: string;
  counterpartyBalanceTranslated: string;
  mismatchTranslated: string;
  eliminatedAmountTranslated: string;
  status: 'proposed' | 'accepted' | 'rejected';
  decidedAt?: string | null;
  decidedBy?: number | null;
  decisionNote: string;
  note: string;
  rowVersion: number;
}

export interface EliminationRuleDTO {
  id: number;
  companyId: number;
  counterpartyCompanyId: number;
  consolGroupId?: number | null;
  ruleType: string;
  accounts: unknown[];
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface EmergencyApprovalRequest {
  reasonCode: string;
  reason: string;
  ratifyWithinHours?: number;
}

export interface EmployeeDTO {
  id: number;
  employeeNo: string;
  companyId: number;
  userId?: number | null;
  departmentId?: number | null;
  branchId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  managerName?: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  fullName: string;
  title?: string;
  email?: string;
  phone?: string;
  personalEmail?: string;
  employmentType: string;
  employmentStatus: string;
  joinedOn: string;
  leftOn?: string | null;
  basicSalary: string;
  currency: string;
  pensionable: boolean;
  nationality?: string;
  isExpatriate: boolean;
  workPermitNoMasked?: string;
  workPermitExpiry?: string | null;
  nationalIdMasked?: string;
  taxIdMasked?: string;
  bankName?: string;
  bankBranch?: string;
  bankAccountName?: string;
  bankAccountNoMasked?: string;
  bankIbanMasked?: string;
  bankSwift?: string;
  bankCurrency?: string;
  notes?: string;
  currentAssignment?: EmploymentAssignmentDTO | null;
  createdAt: string;
  rowVersion: number;
}

export interface EmployeeDocumentDTO {
  id: number;
  filename: string;
  mime: string;
  sizeBytes: number;
  sha256: string;
  note?: string;
  createdAt: string;
}

export interface EmployeeDocumentDownloadDTO {
  url: string;
  expiresAt: string;
}

export interface EmploymentAssignmentDTO {
  id: number;
  employeeId: number;
  companyId: number;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  positionId?: number | null;
  locationId?: number | null;
  employmentType: string;
  employmentStatus: string;
  payGroupId?: number | null;
  shiftPatternId?: number | null;
  basicSalary?: string | null;
  currency: string;
  pensionable: boolean;
  fte: string;
  probationEndOn?: string | null;
  noticeDays: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  isCurrent: boolean;
  reasonCode?: string;
  note?: string;
  rowVersion: number;
}

export interface EmptyResponse {
  ok: boolean;
}

export interface EncashLeaveRequest {
  days: string;
  runId: number;
  reasonCode?: string;
  note?: string;
}

export interface EndAssignmentRequest {
  effectiveTo: string;
  reasonCode?: string;
}

export interface EndAssignmentResultDTO {
  assignment: EmploymentAssignmentDTO;
  retroEventRaised: boolean;
  retroNote?: string;
}

export interface EndDateDivisionFNDRequest {
  rowVersion: number;
  reasonCode?: string;
}

export interface EndDateSetRequest {
  effectiveTo: string;
  retirementReason: string;
}

export interface EndDateSetResult {
  set: ReferenceSetDTO;
  archivedValueCount: number;
  endedAssignmentIds: number[];
  assignedUnitIdsAtEnd: number[];
}

export interface EndSignatoryRequest {
  effectiveTo?: string;
  rowVersion: number;
}

export interface EngineeringChangeOrderDTO {
  id: number;
  ecoNo?: string | null;
  companyId: number;
  title: string;
  description?: string;
  reasonKind: 'design' | 'cost_reduction' | 'quality' | 'obsolescence' | 'supplier_change' | 'regulatory';
  urgency: 'routine' | 'expedited' | 'emergency';
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'effective' | 'closed' | 'cancelled';
  effectiveFrom?: string | null;
  effectiveFromOrderId?: number | null;
  approvalRequestId?: number | null;
  submittedAt?: string | null;
  decidedAt?: string | null;
  appliedAt?: string | null;
  closedAt?: string | null;
  cancelReason?: string;
  estimatedCostImpact?: string | null;
  realisedCostImpact?: string | null;
  ownerUserId?: number | null;
  createdAt: string;
  rowVersion: number;
  openDispositionCount: number;
}

export interface EngineeringChangeOrderDetailDTO {
  id: number;
  ecoNo?: string | null;
  companyId: number;
  title: string;
  description?: string;
  reasonKind: 'design' | 'cost_reduction' | 'quality' | 'obsolescence' | 'supplier_change' | 'regulatory';
  urgency: 'routine' | 'expedited' | 'emergency';
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'effective' | 'closed' | 'cancelled';
  effectiveFrom?: string | null;
  effectiveFromOrderId?: number | null;
  approvalRequestId?: number | null;
  submittedAt?: string | null;
  decidedAt?: string | null;
  appliedAt?: string | null;
  closedAt?: string | null;
  cancelReason?: string;
  estimatedCostImpact?: string | null;
  realisedCostImpact?: string | null;
  ownerUserId?: number | null;
  createdAt: string;
  rowVersion: number;
  openDispositionCount: number;
  items: ECOChangeItemDTO[];
  dispositions: ECODispositionDTO[];
}

export interface EnrolLoyaltyMemberRequest {
  programId: number;
  partyId: number;
  membershipNo?: string;
  tierId?: number | null;
  enrolledOn?: string;
}

export interface EnterpriseCountryDTO {
  countryCode: string;
  isPrimary: boolean;
  effectiveFrom: string;
}

export interface EnterpriseCurrentDTO {
  enterprise: EnterpriseDTO;
  countries: EnterpriseCountryDTO[];
  defaults: EffectiveDefaultDTO[];
  industryPacks: IndustryPackDTO[];
}

export interface EnterpriseDTO {
  id: number;
  code: string;
  enterpriseType: 'production' | 'non_production';
  residencyRegion: string;
  status: 'draft' | 'pending' | 'active' | 'inactive';
  legalName: string;
  brandName: string;
  primaryCountry: string;
  defaultCurrency: string;
  defaultTimezone: string;
  defaultLanguage: string;
  dateFormat: string;
  numberFormat: string;
  weekStart: string;
  fiscalAnchorMonth?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  versionId: number;
  versionNo: number;
  versionStatus: 'draft' | 'pending' | 'active' | 'superseded';
  submittedAt?: string | null;
  submittedBy?: number | null;
  activatedAt?: string | null;
  activatedBy?: number | null;
  rowVersion: number;
}

export interface EntryDTO {
  id: number;
  entryNo?: string | null;
  companyId: number;
  entryDate: string;
  postingDate: string;
  fiscalPeriodId?: number | null;
  source: 'manual' | 'recurring' | 'reversal' | 'allocation' | 'opening_balance' | 'ap' | 'ar' | 'cash' | 'inventory' | 'payroll' | 'assets' | 'sales' | 'projects' | 'service' | 'mfg' | 'cost' | 'expense' | 'interco' | 'treasury' | 'pos' | 'pms' | 'year_end_close' | 'template';
  sourceDocType?: string;
  sourceDocId?: number | null;
  journalTypeId: number;
  journalTypeCode?: string;
  memo: string;
  currency: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'posted' | 'reversed';
  reversedByEntryId?: number | null;
  reversesEntryId?: number | null;
  reversalOfSource?: string;
  postedAt?: string | null;
  postedBy?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  totalDebit: string;
  totalCredit: string;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
  documentDate?: string;
  reversalDate?: string;
  rateType?: string;
  rateOverride?: string | null;
  autoReversalStatus?: string;
  autoReversalEntryId?: number | null;
  lines?: LineDTO[];
  warnings?: GlPostWarningDTO[];
}

export interface EntryLineInput {
  accountId: number;
  debit?: string;
  credit?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  taxCode?: string;
  counterpartyType?: string;
  counterpartyId?: number | null;
  description?: string;
  divisionId?: number | null;
}

export interface EvaluateRVMDisplacementRequest {
  propertyId: number;
  segmentId: number;
  groupBlockId?: number | null;
  customerId?: number | null;
  enquiryLabel?: string;
  arrivalDate: string;
  departureDate: string;
  offeredAdr: string;
  rooms: RVMDisplacementRoomRequest[];
  groupAncillaryRate?: string;
  groupDirectCostRate?: string;
  displacedAncillaryRate?: string;
  displacedDirectCostRate?: string;
  groupAncillaryRevenue?: string;
  groupDirectCost?: string;
}

export interface EvaluateRosterResultDTO {
  rosterId: number;
  evaluations: RuleEvaluationDTO[];
  breaches: WorkingTimeBreachDTO[];
  blocking: boolean;
  statutoryBlocking: boolean;
}

export interface EvaluateServiceEntitlementRequest {
  contractId?: number | null;
  on?: string;
}

export interface Exception {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  calendarId: number;
  fromDate: string;
  toDate: string;
  kind: 'closure' | 'seasonal_hours' | 'special_hours';
  openTime?: string | null;
  closeTime?: string | null;
  reasonCode?: string;
  note?: string;
}

export interface ExceptionRequest {
  fromDate: string;
  toDate: string;
  kind: 'closure' | 'seasonal_hours' | 'special_hours';
  openTime?: string | null;
  closeTime?: string | null;
  reasonCode?: string;
  note?: string;
}

export interface ExecuteErasureRequest {
  overrideReasonCode?: string;
  overrideNote?: string;
  note?: string;
  rowVersion: number;
}

export interface ExecuteRevaluationRequest {
  approvalRequestId: number;
  base: string;
  quote: string;
  rateType?: 'spot' | 'corporate' | 'historical';
  asOf: string;
  newRate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  reasonCode: string;
  note?: string;
}

export interface ExecuteRunRequest {
  reference?: string;
  bankOverrideReasonCode?: string;
  bankOverrideNote?: string;
}

export interface ExitBlockerDTO {
  code: string;
  message: string;
  refType?: string;
  refId?: number;
}

export interface ExitClearanceItemDTO {
  id: number;
  exitId: number;
  ownerDepartmentId?: number | null;
  ownerUserId?: number | null;
  category: string;
  label: string;
  isMandatory: boolean;
  status: string;
  assetId?: number | null;
  userId?: number | null;
  recoverableAmount: string;
  clearedBy?: number | null;
  clearedAt?: string | null;
  waiverReasonCode?: string;
  note?: string;
  rowVersion: number;
}

export interface ExitDTO {
  id: number;
  exitNo: string;
  employeeId: number;
  assignmentId: number;
  companyId: number;
  exitType: string;
  noticeGivenOn?: string | null;
  lastWorkingDay: string;
  terminationDate: string;
  noticePaidInLieuDays: string;
  reasonCode?: string;
  reasonNote?: string;
  status: string;
  finalRunId?: number | null;
  rehireEligible: boolean;
  rehireNote?: string;
  exitInterviewAt?: string | null;
  exitInterviewNote?: string;
  createdAt: string;
  rowVersion: number;
  clearanceItems?: ExitClearanceItemDTO[];
  exitBlockers?: ExitBlockerDTO[];
}

export interface ExpenseCategoryDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  expenseAccountId: number;
  expenseAccountCode?: string;
  taxGroupId?: number | null;
  defaultTaxCode?: string;
  isMileage: boolean;
  requiresReceiptAbove: string;
  isActive: boolean;
  rowVersion: number;
}

export interface ExpenseClaimDTO {
  id: number;
  companyId: number;
  employeeId: number;
  employeeName?: string;
  claimNo?: string;
  title: string;
  claimDate: string;
  periodFrom?: string;
  periodTo?: string;
  currency: string;
  travelRequestId?: number | null;
  advanceApplied: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  reimbursable: string;
  cardPaid: string;
  status: string;
  approvalRequestId?: number | null;
  approvedAt?: string;
  apInvoiceId?: number | null;
  apInvoiceNo?: string;
  journalEntryId?: number | null;
  postedAt?: string;
  note?: string;
  rowVersion: number;
  settlementMethod: string;
  payrollStatus?: string;
  payrollRunId?: number | null;
  payrollSettledAt?: string;
  approvedBy?: number | null;
  createdBy?: number | null;
  claimantUserId?: number | null;
  approverUserIds: number[];
}

export interface ExpenseClaimDeleteQuery {
  rowVersion?: number;
}

export interface ExpenseClaimDetailDTO {
  id: number;
  companyId: number;
  employeeId: number;
  employeeName?: string;
  claimNo?: string;
  title: string;
  claimDate: string;
  periodFrom?: string;
  periodTo?: string;
  currency: string;
  travelRequestId?: number | null;
  advanceApplied: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  reimbursable: string;
  cardPaid: string;
  status: string;
  approvalRequestId?: number | null;
  approvedAt?: string;
  apInvoiceId?: number | null;
  apInvoiceNo?: string;
  journalEntryId?: number | null;
  postedAt?: string;
  note?: string;
  rowVersion: number;
  settlementMethod: string;
  payrollStatus?: string;
  payrollRunId?: number | null;
  payrollSettledAt?: string;
  approvedBy?: number | null;
  createdBy?: number | null;
  claimantUserId?: number | null;
  approverUserIds: number[];
  lines: ExpenseClaimLineDTO[];
}

export interface ExpenseClaimLineDTO {
  id: number;
  claimId: number;
  lineNo: number;
  categoryId: number;
  categoryCode?: string;
  incurredOn: string;
  description?: string;
  merchant?: string;
  currency: string;
  fxRate: string;
  amount: string;
  taxAmount: string;
  baseAmount: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  taxRecoverable: boolean;
  distance?: string;
  mileageRateId?: number | null;
  vehicleClass?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  divisionId?: number | null;
  projectId?: number | null;
  receiptAttachmentId?: number | null;
  cardLineId?: number | null;
  paidBy: string;
  policyOutcome: string;
  policyLimit?: string;
  violationReasonCode?: string;
  violationNote?: string;
}

export interface ExpenseClaimLineInput {
  categoryId: number;
  incurredOn: string;
  description?: string;
  merchant?: string;
  currency?: string;
  amount?: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  taxRecoverable?: boolean;
  distance?: string;
  vehicleClass?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  divisionId?: number | null;
  projectId?: number | null;
  receiptAttachmentId?: number | null;
  cardLineId?: number | null;
  paidBy?: string;
}

export interface ExpenseMileageRateDTO {
  id: number;
  companyId: number;
  vehicleClass: string;
  ratePerUnit: string;
  uom: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo?: string;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface ExpensePayrollInclusionDTO {
  id: number;
  kind: string;
  claimId?: number | null;
  advanceId?: number | null;
  runId: number;
  companyId: number;
  employeeId: number;
  employeeName?: string;
  componentCode: string;
  amount: string;
  status: string;
  includedAt: string;
  settledAt?: string;
  settledJournalEntryId?: number | null;
}

export interface ExpensePayrollRecoveryRequest {
  enabled: boolean;
  rowVersion: number;
}

export interface ExpensePayrollSettlementRequest {
  settlementMethod: string;
  rowVersion: number;
}

export interface ExpensePolicyDTO {
  id: number;
  companyId: number;
  categoryId: number;
  categoryCode?: string;
  gradeId?: number | null;
  limitKind: string;
  limitAmount: string;
  currency: string;
  breachAction: string;
  requiresPreApproval: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface ExpenseSettingsDTO {
  id: number;
  companyId: number;
  advanceAccountId: number;
  employeeReceivableAccountId: number;
  defaultClaimCurrency: string;
  requireReceiptAbove: string;
  advanceOverdueDays: number;
  rowVersion: number;
}

export interface ExpiringLotDTO {
  lotId: number;
  itemId: number;
  itemSku?: string;
  lotNo: string;
  expiryDate: string;
  daysToExpiry: number;
  qtyOnHand: string;
  value: string;
  status: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
}

export interface ExpiringLotsQuery {
  cursor?: string;
  limit?: number;
  withinDays?: number;
  warehouseId?: number;
  itemId?: number;
  includeZeroBalance?: boolean;
}

export interface ExplainRVMVarianceRequest {
  stayDate: string;
  segmentId: number;
  streamId: number;
  reasonCode: string;
  note?: string;
}

export interface ExplainReconRequest {
  causeCode: string;
  note?: string;
  rowVersion: number;
}

export interface ExplainVarianceBulkRequest {
  varianceIds: number[];
  code: string;
  note?: string;
}

export interface ExplainVarianceBulkResult {
  explained: number;
}

export interface ExplainVarianceRequest {
  reasonCode?: string;
  rootCauseCode?: string;
  attachmentId?: number | null;
  note?: string;
}

export interface ExplodeBOMQuery {
  quantity?: string;
  asOf?: string;
  orderId?: number;
}

export interface ExplorerQuery {
  entity?: string;
  entityId?: string;
  docNo?: string;
  action?: string;
  actorId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface ExportEntriesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'posted' | 'reversed';
  source?: 'manual' | 'recurring' | 'reversal' | 'allocation' | 'opening_balance' | 'ap' | 'ar' | 'cash' | 'inventory' | 'payroll' | 'assets' | 'sales' | 'projects' | 'service' | 'mfg' | 'cost' | 'expense' | 'interco' | 'treasury' | 'pos' | 'pms' | 'year_end_close' | 'template';
  fiscalPeriodId?: number;
  accountId?: number;
  from?: string;
  to?: string;
  journalTypeCode?: string;
  createdBy?: number;
  approvedBy?: number;
  format?: string;
}

export interface ExportExplanationsQuery {
  companyId: number;
  from: string;
  to: string;
}

export interface ExportQuery {
  entity?: string;
  entityId?: string;
  docNo?: string;
  action?: string;
  actorId?: string;
  from?: string;
  to?: string;
}

export interface ExportReturnFileQuery {
  remittanceId: number;
}

export interface ExtendDSRRequest {
  extensionDays: number;
  reason: string;
  rowVersion: number;
  overrideReasonCode?: string;
  overrideNote?: string;
}

export interface ExtractedField {
  value: string;
  confidence: number;
}

export interface ExtractionDTO {
  id: number;
  kind: string;
  filename: string;
  status: string;
  model: string;
  header: Record<string, ExtractedField>;
  lines: Record<string, ExtractedField>[];
  overallConfidence?: number | null;
  createdAt: string;
  confirmedAt?: string | null;
  rowVersion: number;
}

export interface FABookDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  bookKind: 'accounting' | 'tax' | 'management' | 'other';
  isPrimary: boolean;
  postsToGl: boolean;
  ledgerId?: number | null;
  source: 'tenant' | 'pack';
  convention: 'full_month' | 'half_year' | 'mid_month' | 'mid_quarter' | 'actual_days';
  currency?: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface FAComponentisationDTO {
  id: number;
  parentAssetId: number;
  parentAssetNo?: string;
  effectiveOn: string;
  originalCost: string;
  originalAccumulated: string;
  componentCount: number;
  reasonCode: string;
  note: string;
  components: AssetDTO[];
  createdAt: string;
}

export interface FAImpairmentDTO {
  id: number;
  assetId: number;
  assetNo?: string;
  bookId: number;
  bookCode?: string;
  indicatorId?: number | null;
  impairedOn: string;
  kind: 'loss' | 'reversal';
  carryingAmount: string;
  recoverableAmount: string;
  amount: string;
  notionalCarryingAmount?: string | null;
  reversesId?: number | null;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  reasonCode: string;
  note: string;
  approvalStatus: 'none' | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'lapsed';
  approvalRequestId?: number | null;
  requestedBy?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  createdAt: string;
  rowVersion: number;
}

export interface FAImpairmentIndicatorDTO {
  id: number;
  companyId: number;
  assetId?: number | null;
  assetNo?: string;
  classId?: number | null;
  classCode?: string;
  assessedOn: string;
  indicator: 'market_value_decline' | 'physical_damage' | 'obsolescence' | 'idle' | 'disposal_planned' | 'adverse_regulation' | 'economic_performance' | 'reversal_indicator' | 'other';
  outcome: 'pending' | 'no_impairment' | 'impaired' | 'reversed';
  note: string;
  raisedByJob: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface FAVerificationLineDTO {
  id: number;
  runId: number;
  assetId: number;
  assetNo?: string;
  expectedLocation?: string;
  expectedCustodian?: string;
  found?: boolean | null;
  actualLocation?: string;
  actualCustodian?: string;
  condition?: string;
  disposition: 'none' | 'relocated' | 'recustodied' | 'impair' | 'dispose' | 'write_off' | 'investigate';
  reasonCode?: string;
  note?: string;
  verifiedAt?: string | null;
  impairmentId?: number | null;
  rowVersion: number;
}

export interface FAVerificationRunDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  scheduledOn: string;
  classId?: number | null;
  location: string;
  custodian: string;
  isBlind: boolean;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  completedAt?: string | null;
  note: string;
  lineCount: number;
  foundCount: number;
  missingCount: number;
  createdAt: string;
  rowVersion: number;
}

export interface FNDPeriodInput {
  periodNo: number;
  name?: string;
  startDate: string;
  endDate: string;
  quarter?: number | null;
  isAdjusting?: boolean;
}

export interface FetchStatementsRequest {
  from?: string;
  to?: string;
}

export interface FetchStatementsResultDTO {
  bankAccountId: number;
  connectorKey: string;
  fetched: number;
  importIds: number[];
  note?: string;
}

export interface FieldChangeDTO {
  field: string;
  before?: unknown;
  after: unknown;
}

export interface FieldError {
  field: string;
  code: string;
  message: string;
  params?: Record<string, string>;
}

export interface FileReturnRequest {
  filedOn: string;
  reference: string;
  acknowledgement: string;
}

export interface FilingDTO {
  id: number;
  companyId: number;
  registrationId: number;
  returnDefCode: string;
  periodStart: string;
  periodEnd: string;
  dueOn: string;
  status: 'pending' | 'prepared' | 'filed' | 'paid' | 'waived' | 'overdue';
  taxReturnId?: number | null;
  waivedReasonCode?: string;
  remindedAt?: string | null;
  rowVersion: number;
}

export interface FinalPayRequest {
  netPayPayableAccountId: number;
  periodStart?: string;
  paymentDate?: string;
  notes?: string;
}

export interface FinalPayResponse {
  exit: ExitDTO;
  runId: number;
}

export interface FirePOSCourseRequest {
  courseNo?: number | null;
}

export interface FirstTimeFixReport {
  from: string;
  to: string;
  resolvedCount: number;
  firstTimeFixCount: number;
  ratePercent: string;
}

export interface FiscalCalendar {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  companyId: number;
  name: string;
  startMonth: number;
  isActive: boolean;
  frequency: string;
  adjustingPeriods: number;
}

export interface FlagDTO {
  key: string;
  value: boolean;
  priorValue?: boolean | null;
  changedBy?: number | null;
  changedAt: string;
  approverId?: number | null;
  secondApproverId?: number | null;
}

export interface FndAssignmentValidationRequest {
  positionId: number;
  employeeId?: number | null;
  assignmentId?: number | null;
  assignmentType?: string;
  departmentId?: number | null;
  legalEmployerId?: number | null;
  gradeId?: number | null;
  salary?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  effectiveFrom: string;
  effectiveTo?: string;
  overrideReason?: string;
}

export interface FndAssignmentValidationResult {
  valid: boolean;
  violations: FndAssignmentViolation[];
  canProceedWithOverride: boolean;
}

export interface FndAssignmentViolation {
  code: string;
  field: string;
  message: string;
  overridable: boolean;
}

export interface FndListBusinessUnitsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  legalEntityId?: number;
  q?: string;
}

export interface FndListCrossChargesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  divisionId?: number;
}

export interface FndListSecurityScopesQuery {
  cursor?: string;
  limit?: number;
  businessUnitId?: number;
  userId?: number;
  asAt?: string;
}

export interface FndListServiceProviderRelationshipsQuery {
  cursor?: string;
  limit?: number;
  businessUnitId?: number;
  function?: string;
  asAt?: string;
}

export interface FndSecurityScopeGrantDTO {
  id: number;
  businessUnitId: number;
  principalId: number;
  scopeType: string;
  source: string;
  isDerived: boolean;
  sourceRefId?: number | null;
  grantedAt: string;
  expiresAt?: string;
  effective: boolean;
  asAt: string;
}

export interface Footprint {
  legalEntities?: number;
  divisions?: number;
  businessUnits?: number;
}

export interface ForcePMSNightAuditRunRequest {
  reasonCode: string;
  note?: string;
}

export interface ForecastAccuracyBandDTO {
  band: string;
  horizonDays: number;
  count: number;
  mapePct: string | null;
  meanSignedError: string;
}

export interface ForecastAccuracyDTO {
  periodCode: string;
  rows: ForecastAccuracyRow[];
}

export interface ForecastAccuracyQuery {
  companyId: number;
  from?: string;
  to?: string;
}

export interface ForecastAccuracyRow {
  companyId?: number | null;
  ownerUserId?: number | null;
  snapshotOn: string;
  committedAmount: string;
  actualAmount: string;
  absoluteError: string;
  errorPercent: string;
}

export interface ForecastBucket {
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'converted' | 'superseded';
  probability: string;
  value: string;
  weighted: string;
}

export interface ForecastQuery {
  companyId: number;
  from: string;
  to: string;
}

export interface ForecastReport {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  buckets: ForecastBucket[];
  committedTotal: string;
  weightedTotal: string;
  generatedAt: string;
}

export interface ForecastSnapshotLine {
  opportunityId: number;
  name: string;
  stageCode: string;
  forecastCategory?: string;
  amount: string;
  probabilityPercent: string;
  weightedAmount: string;
  ownerUserId?: number | null;
}

export interface ForfeitProjectRetentionRequest {
  kind: 'write_off' | 'price_adjustment';
  amount: string;
  reasonCode: string;
  note?: string;
  forfeitDate?: string;
}

export interface ForgotPasswordRequest {
  tenantSlug: string;
  email: string;
}

export interface FoundationPlan {
  enterpriseId: number;
  steps: PlanStep[];
}

export interface FromTemplateRequest {
  templateId: number;
}

export interface FulfilDSRRequest {
  outcome?: string;
  rowVersion: number;
  note?: string;
  overrideReasonCode?: string;
  overrideNote?: string;
}

export interface FundsInTransitAgingBandDTO {
  band: string;
  count: number;
  amount: string;
}

export interface FundsInTransitAgingDTO {
  companyId: number;
  asOf: string;
  baseCurrency: string;
  bands: FundsInTransitAgingBandDTO[];
  registerTotal: string;
  ledgerBalance: string;
  difference: string;
  reconciled: boolean;
  fundsInTransitAccountId?: number | null;
  alertDays: number;
  items: FundsInTransitDTO[];
}

export interface FundsInTransitAgingQuery {
  companyId: number;
  asOf?: string;
}

export interface FundsInTransitDTO {
  transferId: number;
  transferNo: string;
  companyId: number;
  status: 'draft' | 'submitted' | 'approved' | 'screening_hold' | 'released' | 'confirmed' | 'rejected' | 'cancelled' | 'recall_requested' | 'recalled' | 'returned';
  fromBankAccountId: number;
  fromBankAccount?: string;
  toBankAccountId: number;
  toBankAccount?: string;
  beneficiaryName?: string;
  sendDate: string;
  releasedAt?: string | null;
  ageDays: number;
  band: string;
  alerted: boolean;
  fromAmount: string;
  fromCurrency: string;
  baseAmount: string;
  baseCurrency: string;
}

export interface FundsInTransitQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  bankAccountId?: number;
  asOf?: string;
  minAgeDays?: number;
}

export interface FxrevalAdjustmentDTO {
  accountId: number;
  accountCode: string;
  accountName: string;
  currency: string;
  rate: string;
  foreignAmount: string;
  carriedBase: string;
  revaluedBase: string;
  unrealised: string;
}

export interface FxrevalCreateRunRequest {
  companyId: number;
  periodId: number;
  rateType?: string;
}

export interface FxrevalDiscardRunRequest {
  reason: string;
}

export interface FxrevalExposureCurrencyDTO {
  currency: string;
  rate: FxrevalRateDTO;
  assets: string;
  liabilities: string;
  netExposure: string;
  carriedBase: string;
  revaluedBase: string;
  unrealised: string;
}

export interface FxrevalExposureDTO {
  companyId: number;
  baseCurrency: string;
  asOf: string;
  rateType: string;
  currencies: FxrevalExposureCurrencyDTO[];
  lines: FxrevalExposureLineDTO[];
  totalUnrealised: string;
}

export interface FxrevalExposureLineDTO {
  source: string;
  accountId: number;
  accountCode: string;
  accountName: string;
  currency: string;
  counterpartyType?: string;
  counterpartyId?: number | null;
  counterpartyName?: string;
  itemCount: number;
  foreignAmount: string;
  rate: string;
  carriedBase: string;
  revaluedBase: string;
  unrealised: string;
}

export interface FxrevalExposureQuery {
  companyId: number;
  asOf: string;
  rateType?: string;
}

export interface FxrevalListRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
}

export interface FxrevalRateDTO {
  currency: string;
  rate: string;
  method: string;
  rateAsOf?: string;
  source?: string;
  observationId?: number | null;
  derivation: string;
}

export interface FxrevalRunDTO {
  id: number;
  companyId: number;
  fiscalPeriodId: number;
  periodName: string;
  asOf: string;
  rateType: string;
  baseCurrency: string;
  status: string;
  gainAccountId: number;
  lossAccountId: number;
  totalGain: string;
  totalLoss: string;
  netAmount: string;
  lineCount: number;
  journalEntryId?: number | null;
  reversalEntryId?: number | null;
  reversalDate?: string;
  reversalStatus?: string;
  createdBy?: number | null;
  postedBy?: number | null;
  postedAt?: string;
  discardedBy?: number | null;
  discardReason?: string;
  createdAt: string;
  rowVersion: number;
  rates: FxrevalRateDTO[];
  adjustments: FxrevalAdjustmentDTO[];
}

export interface FxrevalRunLineDTO {
  id: number;
  lineNo: number;
  source: string;
  accountId: number;
  accountCode: string;
  currency: string;
  counterpartyType?: string;
  counterpartyId?: number | null;
  counterpartyName?: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  docNo?: string;
  foreignAmount: string;
  originalRate?: string;
  carriedBase: string;
  rate: string;
  revaluedBase: string;
  unrealised: string;
}

export interface FxrevalRunLinesQuery {
  cursor?: string;
  limit?: number;
}

export interface FxrevalSettingsDTO {
  companyId: number;
  configured: boolean;
  unrealisedGainAccountId?: number | null;
  unrealisedLossAccountId?: number | null;
  defaultRateType: string;
  monetaryAccountIds: number[];
  updatedBy?: number | null;
  rowVersion: number;
}

export interface FxrevalSettingsQuery {
  companyId: number;
}

export interface FxrevalUpdateSettingsRequest {
  companyId: number;
  unrealisedGainAccountId: number;
  unrealisedLossAccountId: number;
  defaultRateType?: string;
  monetaryAccountIds: number[];
  rowVersion: number;
}

export interface GRIRQuery {
  companyId: number;
  asOf?: string;
  vendorId?: number;
  poId?: number;
  openOnly?: boolean;
}

export interface GRIRReconciliation {
  ledgerAccountId: number;
  ledgerAccountCode: string;
  ledgerAccountName: string;
  ledgerBalance: string;
  procurementBalance: string;
  difference: string;
  reconciled: boolean;
}

export interface GRIRReport {
  companyId: number;
  asOf: string;
  rows: GRIRRow[];
  totalReceived: string;
  totalMatched: string;
  totalBalance: string;
  reconciliation: GRIRReconciliation;
  generatedAt: string;
}

export interface GRIRRow {
  poId: number;
  poNo?: string;
  poLineId: number;
  lineNo: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  description: string;
  receivedQuantity: string;
  receivedValue: string;
  matchedQuantity: string;
  matchedValue: string;
  balance: string;
  poLineStatus: string;
  oldestReceiptAgeDays?: number;
}

export interface GRNDTO {
  id: number;
  grnNo?: string | null;
  poId: number;
  poNo?: string | null;
  companyId: number;
  receiptDate: string;
  status: 'draft' | 'posted' | 'reversed' | 'cancelled';
  memo: string;
  postedAt?: string | null;
  reversedAt?: string | null;
  reversalReason?: string;
  createdAt: string;
  rowVersion: number;
  lines?: GRNLineDTO[];
}

export interface GRNLineDTO {
  id: number;
  lineNo: number;
  poLineId: number;
  itemId: number;
  warehouseId: number;
  binId?: number | null;
  quantity: string;
  unitCost: string;
  lotNo?: string | null;
  serialNos?: string[];
  expiryDate?: string | null;
  status: 'received' | 'accepted' | 'quarantined' | 'rejected' | 'returned';
  rejectReason?: string;
  qtyAccepted: string;
  qtyQuarantined: string;
  qtyRejected: string;
  qtyReturned: string;
  inspectionLotId?: number | null;
  stockLotId?: number | null;
}

export interface GRNLineDispositionDTO {
  grnLineId: number;
  lineNo: number;
  itemId: number;
  quantity: string;
  status: 'received' | 'accepted' | 'quarantined' | 'rejected' | 'returned';
  qtyAccepted: string;
  qtyQuarantined: string;
  qtyRejected: string;
  qtyReturned: string;
  inspectionLotId?: number | null;
  inspectionLotNo?: string;
  inspectionStatus?: string;
  inspectionIsBlocking?: boolean;
  stockLotId?: number | null;
  quarantined: boolean;
}

export interface GRNLineInput {
  poLineId: number;
  quantity: string;
  binId?: number | null;
  lotNo?: string;
  serialNos?: string[];
  expiryDate?: string;
  reject?: boolean;
  rejectReason?: string;
  overrideReasonCode?: string;
  overrideNote?: string;
}

export interface GanttDTO {
  projectId: number;
  phases: PhaseDTO[];
  tasks: ProjectsTaskDTO[];
}

export interface GenerateEAMPMPlansOutcome {
  planId: number;
  generated: boolean;
  workOrderId?: number;
  reason?: string;
}

export interface GenerateEAMPMPlansRequest {
  companyId: number;
  on?: string;
}

export interface GenerateEAMPMPlansResult {
  generated: GenerateEAMPMPlansOutcome[];
}

export interface GenerateInvoicesRequest {
  periodStart: string;
  periodEnd: string;
  tenantId?: string | null;
}

export interface GenerateInvoicesResponse {
  periodStart: string;
  periodEnd: string;
  created: InvoiceDTO[];
  createdCount: number;
  skipped: number;
}

export interface GenerateRunRequest {
  groupId: number;
  fiscalPeriodId?: number;
  fromDate?: string;
  asOfDate?: string;
  note?: string;
}

export interface GenerateScheduleRequest {
  shape: string;
  periods: number;
  startDate?: string;
  principal?: string;
  rate?: string;
  frequency?: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'at_maturity';
  commit?: boolean;
  rowVersion: number;
}

export interface GenerateStatementRequest {
  pairId: number;
  fiscalPeriodId: number;
}

export interface GeoImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors?: string[];
}

export interface GeoTree {
  country: Country;
  subdivisions: Subdivision[];
  localities: Locality[];
}

export interface GeoTreeQuery {
  countryId: string;
}

export interface GetDivisionRollupQuery {
  asAt?: string;
}

export interface GetDivisionSegmentMapQuery {
  asAt?: string;
}

export interface GetECLSettingsQuery {
  companyId: number;
}

export interface GetExpenseSettingsQuery {
  companyId: number;
}

export interface GetExplanationQuery {
  companyId: number;
}

export interface GetLegalEntityQuery {
  asAt?: string;
}

export interface GlCashFlowLineDTO {
  key: string;
  group: string;
  label: string;
  accountId?: number | null;
  accountCode?: string;
  lineCode?: string;
  amount: string;
  compareAmount?: string;
}

export interface GlCashFlowMappingDTO {
  accountId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  category: 'operating' | 'investing' | 'financing' | 'cash' | 'non_cash';
  lineCode: string;
  source: string;
  rowVersion: number;
}

export interface GlCashFlowMappingQuery {
  companyId: number;
}

export interface GlCashFlowMappingRequest {
  category: 'operating' | 'investing' | 'financing' | 'cash' | 'non_cash';
  lineCode?: string;
  rowVersion: number;
}

export interface GlCashFlowQuery {
  companyId: number;
  from: string;
  to: string;
  basis?: 'entry' | 'posting';
  compareTo?: '' | 'prior_period' | 'prior_year';
  compareFrom?: string;
  compareThru?: string;
}

export interface GlCashFlowSectionDTO {
  key: string;
  title: string;
  lines: GlCashFlowLineDTO[];
  total: string;
  compareTotal?: string;
}

export interface GlCashFlowStatementDTO {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  basis: 'entry' | 'posting';
  comparative: boolean;
  compareFrom?: string;
  compareTo?: string;
  compareLabel?: string;
  netProfit: string;
  sections: GlCashFlowSectionDTO[];
  netChange: string;
  openingCash: string;
  closingCash: string;
  cashMovement: string;
  reconciles: boolean;
  difference: string;
  compareNetChange?: string;
  compareOpeningCash?: string;
  compareClosingCash?: string;
  compareReconciles?: boolean | null;
  unmappedAccounts: GlCashFlowUnmappedAccountDTO[];
  generatedAt: string;
}

export interface GlCashFlowUnmappedAccountDTO {
  accountId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  defaultCategory: 'operating' | 'investing' | 'financing' | 'cash' | 'non_cash';
  movement: string;
}

export interface GlCreateTemplateRequest {
  companyId: number;
  code: string;
  name: string;
  memo?: string;
  currency?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  intervalCount?: number;
  dayOfMonth?: number | null;
  startDate: string;
  endDate?: string;
  postAutomatically?: boolean;
  lines: GlTemplateLineInput[];
}

export interface GlDeleteQuery {
  rowVersion?: number;
}

export interface GlEquityMovementDTO {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  basis: 'entry' | 'posting';
  rows: GlEquityMovementRowDTO[];
  openingEquity: string;
  unclosedResultOpening: string;
  netProfit: string;
  otherMovements: string;
  yearEndTransfer: string;
  closingEquity: string;
  balanceSheetEquity: string;
  reconciles: boolean;
  difference: string;
  generatedAt: string;
}

export interface GlEquityMovementQuery {
  companyId: number;
  from: string;
  to: string;
  basis?: 'entry' | 'posting';
  includeZero?: boolean;
}

export interface GlEquityMovementRowDTO {
  accountId: number;
  code: string;
  name: string;
  opening: string;
  movement: string;
  yearEndTransfer: string;
  closing: string;
}

export interface GlJournalIssue {
  code: string;
  lineNo: number;
  field?: string;
  message: string;
}

export interface GlJournalLineImportErrorDTO {
  row: number;
  field: string;
  code: string;
}

export interface GlJournalLineImportResultDTO {
  imported: number;
  errors: GlJournalLineImportErrorDTO[];
}

export interface GlJournalValidationDTO {
  ok: boolean;
  issues: GlJournalIssue[];
  warnings?: GlPostWarningDTO[];
}

export interface GlListTemplatesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  isActive?: boolean | null;
}

export interface GlManualJournalAnalysisDTO {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  basis: 'entry' | 'posting';
  groupBy: 'preparer' | 'account';
  rows: GlManualJournalAnalysisRowDTO[];
  manualEntryCount: number;
  manualLineCount: number;
  manualDebit: string;
  reversedCount: number;
  allEntryCount: number;
  manualSharePct: string;
  generatedAt: string;
}

export interface GlManualJournalAnalysisQuery {
  companyId: number;
  from: string;
  to: string;
  groupBy?: 'preparer' | 'account';
  basis?: 'entry' | 'posting';
}

export interface GlManualJournalAnalysisRowDTO {
  key: string;
  label: string;
  entryCount: number;
  lineCount: number;
  totalDebit: string;
  totalCredit: string;
  averageDebit: string;
  reversedCount: number;
  sharePct: string;
}

export interface GlPostWarningDTO {
  code: string;
  message: string;
  details?: Record<string, string>;
}

export interface GlTemplateDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  memo: string;
  currency: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  intervalCount: number;
  dayOfMonth?: number | null;
  startDate: string;
  endDate?: string | null;
  nextRunDate: string;
  lastRunDate?: string | null;
  runCount: number;
  postAutomatically: boolean;
  isActive: boolean;
  rowVersion: number;
  lines?: TemplateLineDTO[];
}

export interface GlTemplateLineInput {
  accountId: number;
  debit?: string;
  credit?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  taxCode?: string;
  description?: string;
}

export interface GradeRequest {
  code: string;
  name: string;
  level: number;
  currency: string;
  minPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  midPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  maxPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  effectiveFrom: string;
}

export interface GrantHRSkillRequest {
  skillId: number;
  level?: number;
  validFrom: string;
  validTo?: string;
  reference?: string;
  issuedBy?: string;
  attachmentId?: number | null;
}

export interface GroupDTO {
  id: number;
  code: string;
  name: string;
  appliesTo: 'party' | 'item';
  description: string;
  isActive: boolean;
  rowVersion: number;
}

export interface GroupDetail {
  id: number;
  code: string;
  name: string;
  parentCompanyId: number;
  parentCompanyCode?: string;
  presentationCurrency: string;
  description: string;
  isActive: boolean;
  intercoTolerance: string;
  rowVersion: number;
  groupCoaId?: number | null;
  accountMapSetVersion: number;
  icDifferenceThreshold: string;
  icDifferenceThresholdPending?: ConsolIcThresholdPendingDTO | null;
  intercoTolerancePending?: ConsolIcThresholdPendingDTO | null;
  members: ConsolMemberDTO[];
}

export interface GuidedFinding {
  stepIndex: number;
  stepRef?: string;
  kind?: 'create_legal_entity' | 'ensure_primary_ledger' | 'create_division' | 'create_business_unit' | 'create_coa_structure' | 'create_calendar';
  field?: string;
  severity: 'blocking' | 'warning';
  message: string;
}

export interface GuidedRunDTO {
  id: number;
  enterpriseId: number;
  status: 'draft' | 'pending' | 'applied' | 'rejected';
  source: string;
  stepCount: number;
  submittedAt?: string | null;
  submittedBy?: number | null;
  consumedAt?: string | null;
  consumedBy?: number | null;
  rejectReason?: string;
  rowVersion: number;
}

export interface GuidedValidationResult {
  enterpriseId: number;
  stepCount: number;
  valid: boolean;
  blockingCount: number;
  findings: GuidedFinding[];
}

export interface HACCPCheckpointDTO {
  id: number;
  code: string;
  name: string;
  companyId: number;
  warehouseId?: number | null;
  zoneId?: number | null;
  kind: 'temperature' | 'humidity' | 'cleaning' | 'receiving' | 'cooking' | 'cooling' | 'visual';
  minValue?: string | null;
  maxValue?: string | null;
  unit: string;
  frequency: 'per_shift' | 'daily' | 'twice_daily' | 'weekly' | 'on_receipt';
  correctiveAction?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface HACCPComplianceDTO {
  companyId: number;
  from: string;
  to: string;
  dueCount: number;
  takenCount: number;
  missedCount: number;
  failedCount: number;
  shiftsPerDay: number;
  shiftFallback: boolean;
  note?: string;
  lines: HACCPComplianceLineDTO[];
}

export interface HACCPComplianceLineDTO {
  checkpointId: number;
  checkpointCode: string;
  checkpointName: string;
  frequency: 'per_shift' | 'daily' | 'twice_daily' | 'weekly' | 'on_receipt';
  dueCount: number;
  takenCount: number;
  missedCount: number;
  failedCount: number;
  compliancePct: string;
}

export interface HACCPComplianceQuery {
  companyId: number;
  warehouseId?: number;
  from: string;
  to: string;
}

export interface HACCPImportRequest {
  companyId: number;
  csv: string;
  dryRun?: boolean;
}

export interface HACCPImportResultDTO {
  imported: number;
  failed: number;
  dryRun: boolean;
  errors?: HACCPImportRowErrorDTO[];
}

export interface HACCPImportRowErrorDTO {
  rowNo: number;
  reason: string;
}

export interface HACCPRecordDTO {
  id: number;
  checkpointId: number;
  checkpointCode?: string;
  recordedOn: string;
  recordedAt: string;
  businessDayId?: number | null;
  shiftCode?: string;
  reading?: string | null;
  outcome: 'pass' | 'fail' | 'not_applicable';
  correctiveActionTaken?: string;
  lotId?: number | null;
  attachmentId?: number | null;
  note?: string;
  recordedBy?: number | null;
  source: 'manual' | 'csv';
}

export interface HRPersonSkillDTO {
  id: number;
  employeeId: number;
  skillId: number;
  skillCode: string;
  skillName: string;
  level: number;
  validFrom: string;
  validTo?: string;
  reference?: string;
  issuedBy?: string;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface HRSkillDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  category?: string;
  description?: string;
  expires: boolean;
  isStatutory: boolean;
  source: string;
  rowVersion: number;
}

export interface HRSkillMatchDTO {
  employeeId: number;
  qualified: boolean;
  missing?: HRSkillShortfallDTO[];
}

export interface HRSkillRequirementDTO {
  skillId: number;
  minLevel?: number;
}

export interface HRSkillShortfallDTO {
  skillId: number;
  skillCode: string;
  isStatutory: boolean;
  reason: string;
}

export interface HeadcountEventDTO {
  id: number;
  positionId: number;
  eventType: string;
  deltaFte: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  requisitionRef?: string;
  overrideReasonCode?: string;
  overrideReason?: string;
  approverId?: number | null;
  approvalRequestId?: number | null;
  createdAt: string;
  createdBy?: number | null;
  effectiveFrom: string;
  newApprovedHeadcount: number;
}

export interface HeadcountEventRequest {
  eventType: string;
  deltaFte: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  effectiveFrom: string;
  requisitionRef?: string;
  overrideReasonCode?: string;
  overrideReason?: string;
  approverId?: number | null;
}

export interface HeadcountStatusDTO {
  positionId: number;
  approved: number;
  filled: number;
  available: number;
  asAt: string;
}

export interface HealthStatus {
  status: string;
  checks?: DependencyStatus[];
}

export interface Hierarchy {
  companyId: number;
  branches: UnitNode[];
  departments: UnitNode[];
  costCenters: UnitNode[];
  profitCenters: UnitNode[];
  businessUnits: UnitNode[];
}

export interface HierarchyDTO {
  id: number;
  enterpriseId: number;
  code: string;
  name: string;
  purpose: string;
  permittedNodeTypes: string[];
  depthLimit: number;
  isDefault: boolean;
  status: string;
  rowVersion: number;
}

export interface HierarchyNodeDTO {
  id: number;
  versionId: number;
  orgType: string;
  orgId: number;
  parentNodeId?: number | null;
  level: number;
  sortOrder: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface HierarchyVersionDTO {
  id: number;
  hierarchyId: number;
  versionNo: number;
  status: string;
  changeSummary?: string;
  routedTo?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  publishedAt?: string;
  approvedBy?: number | null;
  submittedBy?: number | null;
  seededFromId?: number | null;
  supersededBy?: number | null;
  nodeCount: number;
  rowVersion: number;
}

export interface HistoryEntry {
  at: string;
  action: string;
  actorId?: number | null;
  actorName?: string;
  step?: number;
  comment?: string;
  onBehalfOfUserId?: number | null;
}

export interface Hold {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  base: string;
  quote: string;
  rateType: 'spot' | 'corporate' | 'historical';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  asOf: string;
  priorRate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  movePct: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  source: 'manual' | 'api';
  provider?: string | null;
  status: 'held' | 'accepted' | 'rejected';
  reasonCode?: string;
  note: string;
  decidedAt?: string | null;
  decidedBy?: number | null;
  fxRateId?: number | null;
}

export interface HoldInvoiceRequest {
  reason: string;
}

export interface HoldPMSReservationRequest {
  holdMinutes?: number;
  rowVersion: number;
}

export interface Holiday {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  calendarId: number;
  holidayDate: string;
  name: string;
  kind: 'public' | 'religious' | 'company' | 'closure';
  isWorkingDay: boolean;
  isHalfDay: boolean;
  jurisdictionId?: number | null;
  source: string;
}

export interface HolidayRequest {
  holidayDate: string;
  name: string;
  kind?: 'public' | 'religious' | 'company' | 'closure';
  isWorkingDay?: boolean;
  isHalfDay?: boolean;
  jurisdictionId?: number | null;
}

export interface HrCreateRequisitionRequest {
  companyId: number;
  departmentId?: number | null;
  title: string;
  headcount?: number;
  justification?: string;
}

export interface HrDeleteQuery {
  rowVersion: number;
}

export interface HrEndAssignmentRequest {
  effectiveTo: string;
  reasonCode?: string;
  note?: string;
}

export interface HrListAssignmentsQuery {
  cursor?: string;
  limit?: number;
  currentOnly?: boolean;
}

export interface HrListRequisitionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
}

export interface HrRunAccrualRequest {
  on?: string;
}

export interface HrUpdateRequisitionRequest {
  title?: string | null;
  headcount?: number | null;
  justification?: string | null;
  rowVersion: number;
}

export interface ICAgeingDTO {
  pairId: number;
  companyId: number;
  companyCode?: string;
  counterpartyCompanyId: number;
  counterpartyCompanyCode?: string;
  openCount: number;
  openAmount: string;
  current: string;
  days30: string;
  days60: string;
  days90Plus: string;
  oldestOpenDays: number;
}

export interface ICAgeingReportDTO {
  asOf: string;
  rows: ICAgeingDTO[];
}

export interface ICAgeingReportQuery {
  companyId?: number;
  asOf?: string;
}

export interface ICAssignDifferenceRequest {
  ownerUserId: number;
  note?: string;
  rowVersion: number;
}

export interface ICDifferenceDTO {
  id: number;
  pairId: number;
  companyId: number;
  companyCode?: string;
  counterpartyCompanyId: number;
  counterpartyCompanyCode?: string;
  kind: string;
  transactionId?: number | null;
  statementId?: number | null;
  currency: string;
  amount: string;
  agedFrom: string;
  ageDays: number;
  status: string;
  ownerUserId?: number | null;
  ownerName?: string;
  assignedBy?: number | null;
  assignedAt?: string;
  investigationStartedAt?: string;
  resolvedBy?: number | null;
  resolvedAt?: string;
  resolutionNote?: string;
  evidenceCount: number;
  createdBy?: number | null;
  createdAt: string;
  updatedBy?: number | null;
  rowVersion: number;
  history?: ICDifferenceEventDTO[];
}

export interface ICDifferenceEventDTO {
  id: number;
  action: string;
  fromStatus?: string;
  toStatus: string;
  ownerUserId?: number | null;
  ownerName?: string;
  actorUserId?: number | null;
  actorName?: string;
  note?: string;
  at: string;
}

export interface ICInvestigateDifferenceRequest {
  note?: string;
  rowVersion: number;
}

export interface ICPairDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  counterpartyCompanyId: number;
  counterpartyCompanyCode?: string;
  companyPairId: number;
  ownerPositionId?: number | null;
  tolerance: string;
  toleranceCurrency: string;
  maxAgeDays: number;
  blocksClose: boolean;
  isActive: boolean;
  mirrorConfigured: boolean;
  requiresTp: boolean;
  createdBy?: number | null;
  updatedBy?: number | null;
  rowVersion: number;
}

export interface ICResolveDifferenceRequest {
  resolutionNote: string;
  rowVersion: number;
}

export interface ICStatementDTO {
  id: number;
  pairId: number;
  fiscalPeriodId: number;
  fiscalPeriodName?: string;
  asOf: string;
  currency: string;
  dueFromBalance: string;
  counterpartyDueToBalance: string;
  difference: string;
  openItemCount: number;
  oldestOpenDays: number;
  status: string;
  certifiedAt?: string;
  certifiedBy?: number | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  note?: string;
  rowVersion: number;
}

export interface ICTransactionDTO {
  id: number;
  companyId: number;
  counterpartyCompanyId: number;
  pairId: number;
  direction: string;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  docDate: string;
  docType: string;
  docId?: number | null;
  docNo?: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  journalLineId?: number | null;
  currency: string;
  amount: string;
  baseAmount: string;
  status: string;
  matchedTransactionId?: number | null;
  matchedAt?: string;
  matchedBy?: number | null;
  matchKind?: string;
  disposition?: string;
  reasonCode?: string;
  note?: string;
  queueItemId?: number | null;
  mirrorJournalEntryId?: number | null;
  tpPolicyId?: number | null;
  tpMethod?: string;
  tpCategory?: string;
  tpBasisNote?: string;
  tpException?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  ageDays?: number;
}

export interface IdentifierInput {
  kind: string;
  issuingCountry?: string;
  value: string;
}

export interface IdentityUserDirectoryEntryDTO {
  id: number;
  name: string;
  email: string;
  principalKind: string;
  status: string;
  jobTitle?: string | null;
}

export interface IdentityUserDirectoryQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  principalKind?: string;
  status?: string;
}

export interface ImportAttendanceRequest {
  csv: string;
}

export interface ImportAttendanceResult {
  imported: number;
  skipped: ImportAttendanceSkip[];
}

export interface ImportAttendanceSkip {
  row: number;
  reason: string;
}

export interface ImportCardStatementLineInput {
  transactedOn: string;
  merchant?: string;
  reference?: string;
  amount: string;
  originalAmount?: string;
  originalCurrency?: string;
}

export interface ImportCardStatementRequest {
  cardAccountId: number;
  periodFrom: string;
  periodTo: string;
  currency?: string;
  openingBalance?: string;
  closingBalance?: string;
  attachmentId?: number | null;
  lines: ImportCardStatementLineInput[];
}

export interface ImportConsentsResult {
  rowCount: number;
  imported: PartyConsentDTO[];
  errors: ConsentImportRowError[];
  valid: boolean;
}

export interface ImportCostDriverQuantitiesRequest {
  fiscalPeriodId: number;
  csv: string;
  replace?: boolean;
}

export interface ImportDTO {
  id: number;
  companyId: number;
  filename: string;
  asOfDate: string;
  currency: string;
  status: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected';
  totalDebit: string;
  totalCredit: string;
  lineCount: number;
  note: string;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
  journalEntryId?: number | null;
  postedAt?: string | null;
  lines?: ImportLineDTO[];
  approvalRequestId?: number | null;
  reconciliation?: ReconciliationDTO | null;
}

export interface ImportDemandRequest {
  companyId: number;
  rows: ImportDemandRow[];
}

export interface ImportDemandResultDTO {
  rowsWritten: number;
}

export interface ImportDemandRow {
  locationId: number;
  businessDate: string;
  dayPart?: string;
  metric: string;
  value: string;
  note?: string;
}

export interface ImportEAMMeterReadingsRequest {
  csv: string;
}

export interface ImportEAMMeterReadingsResult {
  imported: number;
  skipped: ImportEAMMeterReadingsSkip[];
}

export interface ImportEAMMeterReadingsSkip {
  row: number;
  reason: string;
}

export interface ImportHolidaysRequest {
  year: number;
  csv: string;
}

export interface ImportLineDTO {
  lineNo: number;
  accountId: number;
  accountCode: string;
  accountName?: string;
  debit: string;
  credit: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  description: string;
}

export interface ImportPMSRoomingListRequest {
  filename?: string;
  apply?: boolean;
  rows: PMSRoomingListRowInput[];
}

export interface ImportRVMCompetitorRatesRequest {
  propertyId: number;
  mapping: RVMCompetitorColumnMapping;
  csv: string;
  hasHeader?: boolean;
  delimiter?: string;
  currency?: string;
  observedOn?: string;
  commit?: boolean;
  importBatch?: string;
}

export interface ImportRequest {
  countries: CountryRequest[];
}

export interface ImportResult {
  valid: boolean;
  import?: ImportDTO | null;
  lines: ImportLineDTO[];
  rowCount: number;
  totalDebit: string;
  totalCredit: string;
  difference: string;
  errors: RowError[];
}

export interface ImportSuppressionsResult {
  rowCount: number;
  imported: PartySuppressionDTO[];
  errors: ConsentImportRowError[];
  valid: boolean;
}

export interface InTransitQuery {
  companyId: number;
}

export interface InTransitSummaryDTO {
  companyId: number;
  totalValue: string;
  transfers: InTransitTransferSummaryDTO[];
}

export interface InTransitTransferSummaryDTO {
  transferId: number;
  transferNo?: string | null;
  fromWarehouseId: number;
  toWarehouseId: number;
  despatchedOn: string;
  ageDays: number;
  value: string;
  overdue: boolean;
}

export interface InboxItem {
  taskId: number;
  requestId: number;
  docType: string;
  docId: number;
  docNo?: string;
  companyId?: number | null;
  amount?: string;
  currency?: string;
  submittedBy: number;
  submitterName?: string;
  submitterEmail?: string;
  submittedAt: string;
  step: number;
  stepName?: string;
  mode: string;
  policyName?: string;
  dueAt?: string | null;
  overdue: boolean;
  onBehalfOfUserId?: number | null;
  onBehalfOfName?: string;
  positionId?: number | null;
  positionName?: string;
  viaDelegation?: string;
  cycle: number;
  origin?: string;
}

export interface IncomeStatementQuery {
  companyId: number;
  from: string;
  to: string;
  basis?: 'entry' | 'posting';
  compareTo?: '' | 'prior_period' | 'prior_year';
  compareFrom?: string;
  compareThru?: string;
  includeZero?: boolean;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
}

export interface IncotermDTO {
  code: string;
  name: string;
  freightBorneBy: string;
  insuranceBorneBy: string;
  dutyBorneBy: string;
  riskTransferPoint: string;
  requiresNamedPlace: boolean;
  source: string;
  isActive: boolean;
  rowVersion: number;
}

export interface IndustryPackDTO {
  packCode: string;
  version?: string;
  status: 'active' | 'inactive';
  activatedAt?: string | null;
  deactivatedAt?: string | null;
}

export interface InitiateExitRequest {
  employeeId: number;
  exitType: string;
  noticeGivenOn?: string;
  lastWorkingDay: string;
  terminationDate: string;
  noticePaidInLieuDays?: string;
  reasonCode?: string;
  reasonNote?: string;
  rehireEligible?: boolean | null;
  rehireNote?: string;
}

export interface InspectPMSHousekeepingTaskRequest {
  rowVersion: number;
  overrideReason?: string;
  overrideNote?: string;
}

export interface InspectionCharacteristicDTO {
  id: number;
  planId: number;
  seqNo: number;
  code: string;
  name: string;
  kind: 'numeric' | 'attribute' | 'visual';
  specMin?: string;
  specMax?: string;
  specTarget?: string;
  uomCode: string;
  decimals: number;
  allowedValues: string[];
  severity: 'critical' | 'major' | 'minor';
  isMandatory: boolean;
  rowVersion: number;
}

export interface InspectionPlanDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description: string;
  originKind: 'goods_receipt' | 'production' | 'shipment';
  itemId?: number | null;
  itemSku?: string;
  itemCategoryId?: number | null;
  itemCategory?: string;
  vendorId?: number | null;
  samplingPlanId?: number | null;
  samplingPlanCode?: string;
  quarantineOnReceipt: boolean;
  isBlocking: boolean;
  requiresIndependentInspector: boolean;
  status: 'draft' | 'active' | 'obsolete';
  effectiveFrom: string;
  effectiveTo?: string;
  supersededBy?: number | null;
  characteristicCount: number;
  rowVersion: number;
}

export interface InspectionPlanDetailDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description: string;
  originKind: 'goods_receipt' | 'production' | 'shipment';
  itemId?: number | null;
  itemSku?: string;
  itemCategoryId?: number | null;
  itemCategory?: string;
  vendorId?: number | null;
  samplingPlanId?: number | null;
  samplingPlanCode?: string;
  quarantineOnReceipt: boolean;
  isBlocking: boolean;
  requiresIndependentInspector: boolean;
  status: 'draft' | 'active' | 'obsolete';
  effectiveFrom: string;
  effectiveTo?: string;
  supersededBy?: number | null;
  characteristicCount: number;
  rowVersion: number;
  characteristics: InspectionCharacteristicDTO[];
  samplingPlan?: SamplingPlanDTO | null;
}

export interface InstructionOverride {
  reasonCode: string;
  note?: string;
}

export interface IntercompanyRuleDTO {
  id: number;
  companyId: number;
  counterpartyCompanyId: number;
  counterpartySegmentValue?: string;
  icReceivableAccountId: number;
  icPayableAccountId: number;
  balancingRule: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface IntercompanySimulationResult {
  isBalanced: boolean;
  residual: string;
  findings?: string[];
}

export interface InventoryClassificationLineDTO {
  id: number;
  runId: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  lotId?: number | null;
  lotNo?: string;
  classification: string;
  qty: string;
  value: string;
  daysSinceLastIssue?: number | null;
  coverageDays?: number | null;
  expiryDate?: string | null;
  daysToExpiry?: number | null;
  disposition: string;
  dispositionStatus: string;
  dispositionDocType?: string;
  dispositionDocId?: number | null;
  note?: string;
}

export interface InventoryClassificationRunDTO {
  id: number;
  companyId: number;
  asOf: string;
  slowDays: number;
  obsoleteDays: number;
  expiringHorizonDays: number;
  linesTotal: number;
  valueAtRisk: string;
  runAt: string;
}

export interface InventoryCompanySettingsDTO {
  companyId: number;
  inTransitAccountId?: number | null;
  inTransitAccountCode?: string;
  landedCostClearingAccountId?: number | null;
  landedCostClearingAccountCode?: string;
  landedCostAccrualAccountId?: number | null;
  landedCostAccrualAccountCode?: string;
  wasteAccountId?: number | null;
  wasteAccountCode?: string;
  outletConsumptionAccountId?: number | null;
  outletConsumptionAccountCode?: string;
  inTransitWarehouseId?: number | null;
  allowAutoReceive: boolean;
  transferOverdueDays: number;
  countRecountThresholdPct: string;
  countRecountThresholdValue: string;
  countRequireDifferentCounter: boolean;
  shrinkageTargetPct: string;
  depletionMissingRecipePolicy: 'record' | 'block';
  rowVersion: number;
}

export interface InventoryControlReconciliation {
  accountId: number;
  accountCode: string;
  subLedgerTotal: string;
  controlBalance: string;
  difference: string;
  reconciles: boolean;
}

export interface InventoryDeleteQuery {
  rowVersion?: number;
}

export interface InventoryListItemsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  type?: 'stocked' | 'non_stock' | 'service';
  isActive?: boolean | null;
}

export interface InventoryReconLineDTO {
  id: number;
  runId: number;
  accountId: number;
  accountCode: string;
  subledgerTotal: string;
  controlTotal: string;
  difference: string;
  status: string;
  causeCode?: string;
  ownerPositionId?: number | null;
  reconciliationItemId?: number | null;
  note?: string;
  clearedAt?: string | null;
  clearedBy?: number | null;
  rowVersion: number;
}

export interface InventoryReconRunDTO {
  id: number;
  companyId: number;
  asOf: string;
  status: string;
  subledgerTotal: string;
  controlTotal: string;
  difference: string;
  accountsChecked: number;
  accountsDiffering: number;
  errorDetail?: string;
  runAt: string;
  blocksClose: boolean;
}

export interface InventoryReconRunDetailDTO {
  id: number;
  companyId: number;
  asOf: string;
  status: string;
  subledgerTotal: string;
  controlTotal: string;
  difference: string;
  accountsChecked: number;
  accountsDiffering: number;
  errorDetail?: string;
  runAt: string;
  blocksClose: boolean;
  lines: InventoryReconLineDTO[];
}

export interface InventoryRecordCountLinesRequest {
  lines: RecordCountLineRequest[];
}

export interface InventoryRevalueRequest {
  itemId: number;
  warehouseId: number;
  companyId: number;
  newUnitCost: string;
  date: string;
  reason: string;
}

export interface InviteSuppliersRequest {
  vendorIds: number[];
}

export interface InvoiceDTO {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  subscriptionId?: string | null;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  issueDate: string;
  dueDate: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  subtotal: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  tax: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  total: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amountPaid: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amountDue: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  pdfStorageKey?: string | null;
  sentAt?: string | null;
  voidReason?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  rowVersion: number;
}

export interface InvoiceDateRequest {
  invoiceDate?: string;
  taxCode?: string;
}

export interface InvoiceDetailDTO {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  subscriptionId?: string | null;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  issueDate: string;
  dueDate: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  subtotal: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  tax: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  total: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amountPaid: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amountDue: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  pdfStorageKey?: string | null;
  sentAt?: string | null;
  voidReason?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  rowVersion: number;
  lines: InvoiceLineDTO[];
  payments: PaymentDTO[];
}

export interface InvoiceLineDTO {
  id: string;
  invoiceId: string;
  lineNo: number;
  kind: 'plan' | 'metered' | 'adjustment';
  description: string;
  quantity: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  unitPrice: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  meta: Record<string, unknown>;
}

export interface InvoiceLineInput {
  description?: string;
  accountId: number;
  quantity?: string;
  unitPrice?: string;
  lineAmount?: string;
  taxCode?: string;
  itemGroupId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  projectTaskId?: number | null;
  poLineId?: number | null;
}

export interface InvoiceListQuery {
  tenantId?: string;
  status?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface IssueEAMPermitRequest {
  hazards?: string;
  precautions?: string;
  isolationRef?: string;
  attachmentId?: number | null;
}

export interface IssueLineInput {
  itemId: number;
  warehouseId: number;
  binId?: number | null;
  quantity: string;
  lotNo?: string;
  serialNos?: string[];
}

export interface IssueMaterialLineInputC7 {
  itemId: number;
  quantity: string;
  lotNo?: string;
  substituteItemId?: number | null;
  serialNos?: string[];
  override?: MaterialIssueOverride | null;
}

export interface IssueMaterialsRequestC7 {
  date?: string;
  lines: IssueMaterialLineInputC7[];
}

export interface IssueMaterialsResultC7 {
  order: ProductionOrderDTO | null;
  overIssue?: OverIssueCheckDTO[];
  substitutions?: MaterialSubstitutionDTO[];
}

export interface IssuePOSInstrumentRequest {
  companyId: number;
  kind: 'gift_card' | 'store_credit';
  code: string;
  currency: string;
  issuedAmount: string;
  customerId?: number | null;
  issuedBySaleId?: number | null;
  expiresOn?: string;
}

export interface IssueRequisitionLineRequest {
  requisitionLineId: number;
  qty: string;
}

export interface IssueRequisitionRequest {
  date: string;
  lines?: IssueRequisitionLineRequest[];
  carrierName?: string;
  carrierReference?: string;
  vehicleReference?: string;
  driverName?: string;
}

export interface IssueResultDTO {
  entries: StockLedgerEntryDTO[];
  journalEntryId: number;
  journalEntryNo: string;
}

export interface IssueStockRequest {
  companyId: number;
  date: string;
  docType: string;
  docId?: number;
  docNo?: string;
  lines: IssueLineInput[];
}

export interface IssueYearEndStatementRequest {
  companyId: number;
  employeeId: number;
  taxYear: number;
}

export interface ItemCompanySettingDTO {
  itemId: number;
  companyId: number;
  costingMethod: 'MA' | 'FIFO';
  inventoryAccountId: number;
  inventoryAccountCode?: string;
  cogsAccountId: number;
  cogsAccountCode?: string;
  adjustmentGainLossAccountId: number;
  adjustmentGainLossAccountCode?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface ItemDTO {
  id: number;
  sku: string;
  name: string;
  description: string;
  type: 'stocked' | 'non_stock' | 'service';
  baseUom: string;
  barcode?: string;
  taxGroupId?: number | null;
  defaultTaxCode?: string;
  trackBatch: boolean;
  trackSerial: boolean;
  reorderMin?: string | null;
  reorderMax?: string | null;
  reorderPoint?: string | null;
  isActive: boolean;
  lotPickStrategy: 'fifo' | 'fefo' | 'manual';
  shelfLifeDays?: number | null;
  minRemainingShelfLifeDays?: number | null;
  requiresExpiry: boolean;
  storageClass: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  netWeightKg?: string | null;
  volumeM3?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ItemFixedBinDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  binId: number;
  binCode?: string;
  role: 'primary' | 'overflow';
  targetQty?: string | null;
  minQty?: string | null;
  rowVersion: number;
}

export interface ItemSettingDTO {
  companyId: number;
  itemId: number;
  standardCost: string;
  marginFloorPercent: string;
  categoryCode?: string;
  rowVersion: number;
}

export interface ItemShippingMeasuresDTO {
  itemId: number;
  sku: string;
  netWeightKg?: string | null;
  grossWeightKg?: string | null;
  volumeM3?: string | null;
  tariffClassificationId?: number | null;
  countryOfOrigin?: string | null;
}

export interface ItemStandardCostDTO {
  id: number;
  companyId: number;
  itemId: number;
  effectiveDate: string;
  standardCost: string;
  isCurrent: boolean;
  notes?: string;
  createdAt: string;
}

export interface ItemSubstituteDTO {
  id: number;
  itemId: number;
  substituteItemId: number;
  warehouseId?: number | null;
  conversionFactor: string;
  preference: number;
  appliesTo: 'both' | 'outbound' | 'production';
  requiresApproval: boolean;
  isActive: boolean;
  note?: string;
  rowVersion: number;
  createdAt: string;
}

export interface ItemUOMDTO {
  itemId: number;
  uomCode: string;
  conversionFactor: string;
  barcode?: string;
  isPurchaseDefault: boolean;
  isSalesDefault: boolean;
}

export interface Job {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  family: string;
  description: string;
  defaultGradeId?: number | null;
  isActive: boolean;
}

export interface JobGradeDTO {
  id: number;
  code: string;
  name: string;
  level: number;
  currency: string;
  minPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  midPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  maxPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface JobRequest {
  code: string;
  name: string;
  family?: string;
  description?: string;
  defaultGradeId?: number | null;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface JointOutputInput {
  bomOutputId: number;
  quantity: string;
  lotNo?: string;
  expiryDate?: string;
  notes?: string;
}

export interface JointOutputReceiptDTO {
  receiptId: number;
  bomOutputId?: number | null;
  itemId: number;
  outputKind: 'primary' | 'co_product' | 'by_product' | 'scrap_output';
  warehouseId: number;
  quantity: string;
  unitCost: string;
  value: string;
  splitWeight: string;
  costSharePercent?: string | null;
  lotNo?: string;
  stockLotId?: number | null;
  journalEntryId?: number | null;
}

export interface JournalTypeDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  allowedSources?: string[];
  allowsManual: boolean;
  reversalPolicy: 'manual' | 'next_period' | 'on_match';
  numberPrefix?: string;
  requiresApproval: boolean;
  isSystem: boolean;
  isActive: boolean;
  rowVersion: number;
  attachmentRequiredAbove?: string | null;
}

export interface Jurisdiction {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  level: 'country' | 'subdivision' | 'locality';
  parentId?: number | null;
  country: string;
  geoRef?: string | null;
  sortOrder: number;
  isActive: boolean;
  source: 'pack' | 'tenant' | 'legacy';
  packId?: string | null;
  packVersion?: number | null;
  contentHash?: string | null;
}

export interface JurisdictionDTO {
  id: number;
  code: string;
  name: string;
  level: string;
  parentId?: number | null;
  country: string;
  sortOrder: number;
  isActive: boolean;
  source: string;
  packVersion?: number | null;
}

export interface JurisdictionRef {
  id: number;
  code: string;
  country: string;
}

export interface JurisdictionTreeDTO {
  country: string;
  roots: JurisdictionTreeNode[];
}

export interface JurisdictionTreeNode {
  id: number;
  code: string;
  name: string;
  level: string;
  parentId?: number | null;
  country: string;
  sortOrder: number;
  isActive: boolean;
  source: string;
  packVersion?: number | null;
  children?: JurisdictionTreeNode[];
}

export interface JurisdictionTreeQuery {
  country?: string;
}

export interface KBApplicabilityDTO {
  id: number;
  itemId?: number | null;
  category?: string;
  modelNo?: string;
}

export interface KBApplicabilityInput {
  itemId?: number | null;
  category?: string;
  modelNo?: string;
}

export interface KBArticleDTO {
  id: number;
  slug: string;
  title: string;
  category: string;
  tags: string[];
  body: string;
  status: string;
  authorId?: number | null;
  authorName?: string;
  publishedAt?: string | null;
  rank?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface KBDeflectionReport {
  lines: KBDeflectionReportLine[];
}

export interface KBDeflectionReportLine {
  articleId: number;
  slug: string;
  title: string;
  views: number;
  resolved: number;
  deflectionRatePercent: string;
}

export interface KBReviewDTO {
  articleId: number;
  reviewIntervalDays: number;
  lastReviewedOn: string;
  nextReviewOn: string;
}

export interface KPICard {
  key: string;
  dashboard: string;
  label: string;
  unit: string;
  value: string;
  currency?: string;
  periodStart?: string;
  periodEnd: string;
  computedAt: string;
  source: string;
  extra?: Record<string, unknown>;
  drillable: boolean;
}

export interface KPIDefDTO {
  key: string;
  dashboard: string;
  label: string;
  unit: string;
  drillable: boolean;
}

export interface LabourCostBucketDTO {
  bucket: string;
  locationId?: number | null;
  departmentId?: number | null;
  rosteredHours: string;
  actualHours: string;
  overtimeHours: string;
  labourCost: string;
  costBasis: string;
  revenue: string;
  revenueBasis: string;
  costPercent: string | null;
  salesPerManHour: string | null;
  headcount: number;
  currency: string;
}

export interface LabourCostQuery {
  companyId: number;
  from: string;
  to: string;
  locationId?: number | null;
  departmentId?: number | null;
  groupBy?: string;
}

export interface LabourCostReportDTO {
  companyId: number;
  from: string;
  to: string;
  groupBy: string;
  buckets: LabourCostBucketDTO[];
  total: LabourCostBucketDTO;
}

export interface LandedCostAgingDTO {
  companyId: number;
  asOf: string;
  clearingAccountId?: number | null;
  ledgerBalance: string;
  openBalance: string;
  difference: string;
  lines: LandedCostAgingLineDTO[];
}

export interface LandedCostAgingLineDTO {
  shipmentId: number;
  shipmentNo?: string | null;
  reference?: string;
  status: 'draft' | 'allocated' | 'absorbed' | 'closed' | 'cancelled';
  chargesBooked: string;
  absorbedToStock: string;
  absorbedToCogs: string;
  clearingBalance: string;
  ageDays: number;
}

export interface LandedCostAgingQuery {
  companyId: number;
  asOf?: string;
  includeClosed?: boolean;
}

export interface LandedCostAllocationDTO {
  id: number;
  chargeId: number;
  shipmentLineId: number;
  basisQty: string;
  basisShare: string;
  amount: string;
}

export interface LandedCostAllocationPreviewDTO {
  shipmentId: number;
  status: 'draft' | 'allocated' | 'absorbed' | 'closed' | 'cancelled';
  stale: boolean;
  lines: LandedCostShipmentLineDTO[];
  charges: LandedCostChargeDTO[];
  allocations: LandedCostAllocationDTO[];
  perLine: LandedCostLineTotalDTO[];
  perCharge: LandedCostChargeTotalDTO[];
  total: string;
}

export interface LandedCostChargeDTO {
  id: number;
  shipmentId: number;
  elementId: number;
  elementCode?: string;
  description?: string;
  state: 'estimated' | 'billed' | 'cancelled';
  vendorId?: number | null;
  vendorName?: string;
  currency: string;
  amount: string;
  fxRate: string;
  baseAmount: string;
  recoverableTaxAmount: string;
  absorbableAmount: string;
  chargeKind: 'fee' | 'disbursement';
  basis: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  tariffClassificationId?: number | null;
  originCountry?: string | null;
  customsValue?: string | null;
  apInvoiceId?: number | null;
  apInvoiceLineId?: number | null;
  accrualItemId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface LandedCostChargeDirectAmountsRequest {
  chargeId: number;
  amounts: LandedCostDirectAmountRequest[];
}

export interface LandedCostChargeTotalDTO {
  chargeId: number;
  amount: string;
}

export interface LandedCostDirectAmountRequest {
  shipmentLineId: number;
  amount: string;
}

export interface LandedCostElementDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  chargeKind: 'fee' | 'disbursement';
  defaultBasis: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  isDuty: boolean;
  isAbsorbable: boolean;
  expenseAccountId?: number | null;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface LandedCostLineTotalDTO {
  shipmentLineId: number;
  itemId: number;
  sku?: string;
  amount: string;
}

export interface LandedCostShipmentDTO {
  id: number;
  shipmentNo?: string | null;
  companyId: number;
  reference?: string;
  status: 'draft' | 'allocated' | 'absorbed' | 'closed' | 'cancelled';
  mode: 'sea' | 'air' | 'road' | 'courier';
  vesselOrFlight?: string;
  billOfLading?: string;
  customsEntryNo?: string;
  originCountry?: string | null;
  etdOn?: string | null;
  etaOn?: string | null;
  clearedOn?: string | null;
  absorptionDate?: string | null;
  defaultBasis: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  allocationStale: boolean;
  goodsValue: string;
  chargesTotal: string;
  absorbableTotal: string;
  recoverableTaxTotal: string;
  absorbedTotal: string;
  absorbedToCogs: string;
  clearingBalance: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  absorbedAt?: string | null;
  absorbedBy?: number | null;
  cancelReason?: string;
  memo?: string;
  createdAt: string;
  rowVersion: number;
}

export interface LandedCostShipmentDetailDTO {
  id: number;
  shipmentNo?: string | null;
  companyId: number;
  reference?: string;
  status: 'draft' | 'allocated' | 'absorbed' | 'closed' | 'cancelled';
  mode: 'sea' | 'air' | 'road' | 'courier';
  vesselOrFlight?: string;
  billOfLading?: string;
  customsEntryNo?: string;
  originCountry?: string | null;
  etdOn?: string | null;
  etaOn?: string | null;
  clearedOn?: string | null;
  absorptionDate?: string | null;
  defaultBasis: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  allocationStale: boolean;
  goodsValue: string;
  chargesTotal: string;
  absorbableTotal: string;
  recoverableTaxTotal: string;
  absorbedTotal: string;
  absorbedToCogs: string;
  clearingBalance: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  absorbedAt?: string | null;
  absorbedBy?: number | null;
  cancelReason?: string;
  memo?: string;
  createdAt: string;
  rowVersion: number;
  lines: LandedCostShipmentLineDTO[];
  charges: LandedCostChargeDTO[];
  allocations: LandedCostAllocationDTO[];
}

export interface LandedCostShipmentLineDTO {
  id: number;
  stockLedgerEntryId: number;
  itemId: number;
  sku?: string;
  warehouseId: number;
  lotId?: number | null;
  fifoLayerId?: number | null;
  receivedQty: string;
  goodsValue: string;
  weightKg: string;
  volumeM3: string;
  tariffClassificationId?: number | null;
  originCountry?: string | null;
  allocatedAmount: string;
  absorbedToInventory: string;
  absorbedToCogs: string;
  absorptionEntryId?: number | null;
}

export interface LeadDTO {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  companyName: string;
  title: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  ownerUserId?: number | null;
  ownerName?: string;
  notes: string;
  disqualifyReason?: string;
  convertedCustomerId?: number | null;
  convertedAt?: string | null;
  mergedIntoLeadId?: number | null;
  mergedAt?: string | null;
  score?: number | null;
  scoreBand?: string | null;
  scoredAt?: string | null;
  routingRuleId?: number | null;
  campaignId?: number | null;
  countryCode?: string;
  createdAt: string;
  rowVersion: number;
}

export interface LeadRoutingResult {
  lead: LeadDTO;
  routingRuleId?: number | null;
  assignedTo?: number | null;
  matched: boolean;
}

export interface LeadRoutingRuleDTO {
  id: number;
  seq: number;
  name: string;
  matchBand?: string;
  matchSource?: string;
  matchCountry?: string;
  matchSegmentId?: number | null;
  minScore?: number | null;
  assignUserId?: number | null;
  assignRoleCode?: string;
  assignStrategy?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface LeadScoreContribution {
  seq: number;
  field: string;
  operator: string;
  value: string;
  points: number;
  label: string;
  fired: boolean;
  running: number;
}

export interface LeadScoreModelDTO {
  id: number;
  code: string;
  name: string;
  version: number;
  kind: string;
  isActive: boolean;
  hotThreshold: number;
  warmThreshold: number;
  rowVersion: number;
}

export interface LeadScoreRuleDTO {
  id: number;
  modelId: number;
  seq: number;
  field: string;
  operator: string;
  value: string;
  points: number;
  label: string;
  rowVersion: number;
}

export interface LeadScoreRunDTO {
  id: number;
  leadId: number;
  modelId: number;
  modelVersion: number;
  score: number;
  band: string;
  clamped: boolean;
  contributions: LeadScoreContribution[];
  scorer: string;
  scoredAt: string;
}

export interface LeaseChangeDTO {
  id: number;
  contractId: number;
  leaseNo?: string | null;
  kind: 'remeasure' | 'terminate';
  status: 'pending_approval' | 'applied' | 'rejected' | 'cancelled' | 'lapsed';
  effectiveDate: string;
  reason: string;
  newPaymentAmount?: string | null;
  newEndDate?: string | null;
  newDiscountRate?: string | null;
  newEscalationPercent?: string | null;
  penalty: string;
  fromVersion: number;
  toVersion?: number | null;
  carryingLiability: string;
  newLiability?: string | null;
  adjustment?: string | null;
  rouCarrying?: string | null;
  gainLoss?: string | null;
  rouAssetCarrying?: string | null;
  journalEntryId?: number | null;
  rouAssetId?: number | null;
  apInvoiceId?: number | null;
  approvalRequestId?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  approverUserIds?: number[];
  decisionNote: string;
  createdBy?: number | null;
  createdAt: string;
  updatedBy?: number | null;
  updatedAt?: string | null;
  rowVersion: number;
}

export interface LeaseChangeListQuery {
  cursor?: string;
  limit?: number;
}

export interface LeaseContractCreateRequest {
  companyId: number;
  name: string;
  description?: string;
  lessorVendorId: number;
  assetClassId?: number | null;
  commencementDate: string;
  endDate: string;
  paymentAmount: string;
  frequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  timing: 'advance' | 'arrears';
  escalationPercent?: string;
  discountRate?: string;
  shortTerm?: boolean;
  lowValue?: boolean;
  liabilityAccountId: number;
  interestExpenseAccountId?: number | null;
  rentExpenseAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  notes?: string;
}

export interface LeaseContractDTO {
  id: number;
  leaseNo?: string | null;
  companyId: number;
  name: string;
  description: string;
  lessorVendorId: number;
  lessorVendorCode?: string;
  lessorVendorName?: string;
  assetClassId?: number | null;
  commencementDate: string;
  endDate: string;
  termMonths: number;
  paymentAmount: string;
  frequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  timing: 'advance' | 'arrears';
  escalationPercent: string;
  discountRate: string;
  shortTerm: boolean;
  lowValue: boolean;
  exempt: boolean;
  liabilityAccountId: number;
  interestExpenseAccountId?: number | null;
  rentExpenseAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  status: 'draft' | 'pending_approval' | 'active' | 'rejected' | 'lapsed' | 'ended' | 'terminated';
  presentValue?: string | null;
  totalPayments?: string | null;
  rouAssetId?: number | null;
  activationJournalEntryId?: number | null;
  approvalRequestId?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  approverUserIds?: number[];
  decisionNote: string;
  notes: string;
  totalPeriods: number;
  postedPeriods: number;
  scheduleVersion: number;
  terminatedOn?: string | null;
  pendingChangeId?: number | null;
  pendingChangeKind?: 'remeasure' | 'terminate';
  createdBy?: number | null;
  createdAt: string;
  updatedBy?: number | null;
  updatedAt?: string | null;
  rowVersion: number;
}

export interface LeaseContractListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'pending_approval' | 'active' | 'rejected' | 'lapsed' | 'ended' | 'terminated';
  lessorVendorId?: number;
  q?: string;
  kind?: 'recognised' | 'exempt';
}

export interface LeaseContractUpdateRequest {
  name?: string | null;
  description?: string | null;
  lessorVendorId?: number | null;
  assetClassId?: number | null;
  commencementDate?: string | null;
  endDate?: string | null;
  paymentAmount?: string | null;
  frequency?: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  timing?: 'advance' | 'arrears';
  escalationPercent?: string | null;
  discountRate?: string | null;
  shortTerm?: boolean | null;
  lowValue?: boolean | null;
  liabilityAccountId?: number | null;
  interestExpenseAccountId?: number | null;
  rentExpenseAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  notes?: string | null;
  rowVersion: number;
}

export interface LeaseLiabilityMaturityQuery {
  companyId: number;
  asOf?: string;
}

export interface LeaseLiabilityMaturityReport {
  companyId: number;
  currency: string;
  asOf: string;
  rows: LeaseLiabilityMaturityRowDTO[];
  totals: LeaseLiabilityMaturityRowDTO;
}

export interface LeaseLiabilityMaturityRowDTO {
  contractId: number;
  leaseNo?: string | null;
  name: string;
  within1Y: string;
  y1To2: string;
  y2To5: string;
  over5Y: string;
  totalUndiscounted: string;
  liability: string;
  currentPortion: string;
  nonCurrentPortion: string;
}

export interface LeaseRemeasureChange {
  paymentAmount?: string;
  endDate?: string;
  discountRate?: string;
  escalationPercent?: string;
}

export interface LeaseRemeasureRequest {
  effectiveDate: string;
  change: LeaseRemeasureChange;
  reason: string;
}

export interface LeaseRollForwardQuery {
  companyId: number;
  from?: string;
  to?: string;
}

export interface LeaseRollForwardReport {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  rows: LeaseRollForwardRowDTO[];
  totals: LeaseRollForwardRowDTO;
}

export interface LeaseRollForwardRowDTO {
  contractId: number;
  leaseNo?: string | null;
  name: string;
  opening: string;
  additions: string;
  interest: string;
  remeasurements: string;
  payments: string;
  terminations: string;
  closing: string;
  difference: string;
}

export interface LeaseRunDTO {
  id?: number;
  companyId: number;
  asOf: string;
  fiscalPeriodId?: number | null;
  processedCount: number;
  draftedCount: number;
  interestTotal: string;
  expenseTotal: string;
  rouDepreciationTotal: string;
  skipped: LeaseRunSkipDTO[];
  triggeredBy: string;
  createdBy?: number | null;
  createdAt?: string | null;
}

export interface LeaseRunListQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface LeaseRunRequest {
  companyId: number;
  asOf?: string;
}

export interface LeaseRunSkipDTO {
  contractId: number;
  leaseNo?: string;
  periodNo: number;
  reason: string;
}

export interface LeaseScheduleDTO {
  contractId: number;
  leaseNo?: string | null;
  status: 'draft' | 'pending_approval' | 'active' | 'rejected' | 'lapsed' | 'ended' | 'terminated';
  preview: boolean;
  termMonths: number;
  presentValue: string;
  totalPayments: string;
  totalInterest: string;
  totalExpense: string;
  lines: LeaseScheduleLineDTO[];
  scheduleVersion: number;
}

export interface LeaseScheduleLineDTO {
  periodNo: number;
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  paymentAmount: string;
  openingLiability: string;
  interest: string;
  principal: string;
  closingLiability: string;
  expense: string;
  posted: boolean;
  postedAt?: string | null;
  postedJournalEntryId?: number | null;
  postedRunId?: number | null;
  apInvoiceId?: number | null;
  scheduleVersion: number;
}

export interface LeaseScheduleVersionDTO {
  version: number;
  kind: string;
  effectiveFrom: string;
  changeId?: number | null;
  liability: string;
  adjustment?: string | null;
  createdAt: string;
  current: boolean;
}

export interface LeaseScheduleVersionsDTO {
  contractId: number;
  leaseNo?: string | null;
  currentVersion: number;
  versions: LeaseScheduleVersionDTO[];
}

export interface LeaseTerminateRequest {
  date: string;
  penalty?: string;
  reason: string;
}

export interface LeaveBalanceDTO {
  employeeId: number;
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  balanceDays: string;
}

export interface LeaveBlackoutDTO {
  id: number;
  companyId: number;
  leaveTypeId?: number | null;
  departmentId?: number | null;
  locationId?: number | null;
  fromDate: string;
  toDate: string;
  severity: string;
  reason: string;
  rowVersion: number;
}

export interface LeaveMovementDTO {
  id: number;
  employeeId: number;
  leaveTypeId: number;
  kind: string;
  days: string;
  leaveYear: number;
  effectiveOn: string;
  runId?: number | null;
  amount?: string | null;
  currency?: string | null;
  reasonCode?: string;
  note?: string;
  createdAt: string;
}

export interface LeaveRequestDTO {
  id: number;
  requestNo: string;
  employeeId: number;
  employeeName?: string;
  leaveTypeId: number;
  leaveTypeName?: string;
  startDate: string;
  endDate: string;
  days: string;
  isUnpaid: boolean;
  reason?: string;
  status: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  approvalRequestId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface LeaveRequestPreviewRequest {
  employeeId: number;
  leaveTypeId: number;
  startDate: string;
  endDate: string;
}

export interface LeaveRequestPreviewResponse {
  days: string;
  calendarDays: string;
  dayBasis: string;
  blackoutHit: boolean;
  blackoutSeverity?: string;
  blackoutReason?: string;
  coverageBreach: boolean;
  coverageMinimum?: number;
  coverageOnDuty?: number;
}

export interface LeaveTypeDTO {
  id: number;
  code: string;
  name: string;
  accrualDaysPerMonth: string;
  maxBalanceDays?: string | null;
  isPaid: boolean;
  requiresApproval: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface LeaveTypePolicyDTO {
  id: number;
  code: string;
  companyId?: number | null;
  dayBasis: string;
  calendarId?: number | null;
  leaveClass: string;
  carryForwardEnabled: boolean;
  carryForwardMaxDays?: string | null;
  carryForwardExpiresAfterMonths?: number | null;
  encashable: boolean;
  encashmentMaxDays?: string | null;
  encashmentComponentCode?: string;
  qualifyingMonths: number;
  certificateAfterDays?: string | null;
  maxDaysPerInstance?: string | null;
  coverageCheck: string;
  statutoryMinDays?: string | null;
  statutoryRuleCode?: string;
  sourceCitation?: string;
}

export interface Ledger {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  companyId: number;
  code: string;
  name: string;
  role: 'primary' | 'secondary';
  currency: string;
  isActive: boolean;
  coaStructureId?: number | null;
  calendarId?: number | null;
  accountingMethodId?: number | null;
  coaStructure?: LedgerComponentRef | null;
  calendar?: LedgerComponentRef | null;
  accountingMethod?: LedgerComponentRef | null;
  legalEntity?: LedgerComponentRef | null;
}

export interface LedgerAccount {
  id: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  isPostable: boolean;
}

export interface LedgerComponentRef {
  id: number;
  code?: string;
  name: string;
}

export interface LedgerDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  role: string;
  currency: string;
  coaStructureId?: number | null;
  calendarId?: number | null;
  accountingMethodId?: number | null;
  rowVersion: number;
}

export interface LedgerEntry {
  date: string;
  docNo: string;
  docType: string;
  docId: number;
  description: string;
  currency: string;
  debit: string;
  credit: string;
  balance: string;
  journalEntryId?: number | null;
}

export interface LedgerLineDTO {
  journalLineId: number;
  journalEntryId: number;
  entryNo?: string;
  entryDate: string;
  description?: string;
  memo?: string;
  debit: string;
  credit: string;
  matched: boolean;
}

export interface LedgerLinesQuery {
  status?: string;
}

export interface LedgerMapDTO {
  id: number;
  companyId: number;
  ledgerId: number;
  ledgerCode?: string;
  ledgerRole: string;
  currency?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface LedgerMapInput {
  ledgerId: number;
  ledgerRole: string;
  effectiveFrom: string;
}

export interface LedgerPage {
  companyId: number;
  currency: string;
  account: LedgerAccount;
  from: string;
  to: string;
  basis: 'entry' | 'posting';
  openingBalance: string;
  pageOpeningBalance: string;
  rows: LedgerRow[];
  rangeDebit: string;
  rangeCredit: string;
  closingBalance: string;
  nextCursor: string | null;
  hasMore: boolean;
  filtered: boolean;
  generatedAt: string;
}

export interface LedgerQuery {
  companyId: number;
  accountId: number;
  from?: string;
  to?: string;
  basis?: 'entry' | 'posting';
  source?: string;
  cursor?: string;
  limit?: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
}

export interface LedgerResolutionDTO {
  ledgerId: number;
  ledgerCode: string;
  currency: string;
  on: string;
  periodId?: number | null;
  periodName?: string;
  periodStatus?: string;
  allowed: boolean;
  warning?: boolean;
  noOpenPeriod?: boolean;
}

export interface LedgerRow {
  lineId: number;
  lineNo: number;
  entryId: number;
  entryNo: string;
  entryDate: string;
  postingDate: string;
  fiscalPeriodId?: number | null;
  source: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  memo: string;
  description: string;
  debit: string;
  credit: string;
  runningBalance: string;
  currency: string;
  fxRate: string;
  txnDebit: string;
  txnCredit: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  taxCode?: string;
  counterpartyType?: string;
  counterpartyId?: number | null;
}

export interface LedgerSetDTO {
  id: number;
  code: string;
  name: string;
  coaStructureId: number;
  calendarId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface LedgerSetMemberDTO {
  id: number;
  ledgerSetId: number;
  ledgerId: number;
  ledgerCode?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface LedgerSetWithMembers {
  id: number;
  code: string;
  name: string;
  coaStructureId: number;
  calendarId?: number | null;
  isActive: boolean;
  rowVersion: number;
  members: LedgerSetMemberDTO[];
}

export interface LegalEntityDTO {
  legalEntityId: number;
  code: string;
  legalName: string;
  name: string;
  country: string;
  baseCurrency: string;
  jurisdictionId?: number | null;
  entityType: string;
  incorporationDate?: string | null;
  isActive: boolean;
  activatedAt?: string | null;
  dissolvedOn?: string | null;
  dissolutionReasonCode?: string;
  rowVersion: number;
  asAt?: string;
  ledgers?: LedgerMapDTO[];
  balancingSegments?: BalancingSegmentDTO[];
  designations?: DesignationDTO[];
  approvalRequestId?: number | null;
  approvalStatus?: string;
}

export interface LegalEntityStep {
  code: string;
  legalName: string;
  name: string;
  country: string;
  baseCurrency: string;
  entityType: string;
  incorporationDate: string;
}

export interface LegislativeDataGroupDTO {
  id: number;
  code: string;
  name: string;
  country: string;
  currency: string;
  payrollCalendarId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface LiabilityBreakageReportDTO {
  companyId?: number | null;
  from?: string;
  to?: string;
  breakagePolicy?: 'none' | 'at_expiry';
  breakageIncomeAccountId?: number | null;
  rows: LiabilityBreakageRowDTO[];
  totals: LiabilityBreakageRowDTO[];
}

export interface LiabilityBreakageReportQuery {
  companyId?: number;
  kind?: string;
  from?: string;
  to?: string;
}

export interface LiabilityBreakageRowDTO {
  issuePeriod?: string;
  kind: 'gift_card' | 'store_credit' | 'guest_deposit' | 'ar_on_account';
  currency: string;
  instrumentCount: number;
  issuedAmount: string;
  adjustedAmount: string;
  redeemedAmount: string;
  refundedAmount: string;
  expiredAmount: string;
  breakageAmount: string;
  unrecognisedBreakageAmount: string;
  outstandingAmount: string;
  breakageRatePercent: string;
}

export interface LiabilityInstrumentDTO {
  source: string;
  sourceId: number;
  kind: 'gift_card' | 'store_credit' | 'guest_deposit' | 'ar_on_account';
  reference: string;
  companyId: number;
  customerId?: number | null;
  currency: string;
  issuedOn: string;
  expiresOn?: string;
  status: 'active' | 'exhausted' | 'expired' | 'void';
  issuedAmount: string;
  adjustedAmount: string;
  redeemedAmount: string;
  refundedAmount: string;
  expiredAmount: string;
  breakageAmount: string;
  unrecognisedBreakageAmount: string;
  outstandingAmount: string;
}

export interface LiabilityInstrumentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  kind?: string;
  status?: string;
  currency?: string;
}

export interface LiabilitySettingsDTO {
  companyId: number;
  breakagePolicy: 'none' | 'at_expiry';
  breakageIncomeAccountId?: number | null;
  rowVersion: number;
  updatedBy?: number | null;
}

export interface LiabilityUpdateSettingsRequest {
  breakagePolicy: 'none' | 'at_expiry';
  breakageIncomeAccountId?: number | null;
  rowVersion: number;
}

export interface LineDTO {
  id: number;
  entryId: number;
  lineNo: number;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  debit: string;
  credit: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  divisionId?: number | null;
  taxCode?: string | null;
  counterpartyType?: string | null;
  counterpartyId?: number | null;
  description: string;
  currency: string;
  fxRate: string;
  baseDebit: string;
  baseCredit: string;
  baseOnly: boolean;
}

export interface LineResultDTO {
  accountId: number;
  budget: string;
  commitments: string;
  actuals: string;
  available: string;
  requested: string;
  exceeded: boolean;
  level: 'none' | 'advisory' | 'warn' | 'block';
  tolerancePct: string;
  toleranceAmount: string;
  source: 'cost_center' | 'company_default';
}

export interface LinkProblemCaseRequest {
  caseId: number;
}

export interface LinkedRecordDescription {
  recordId: number;
  title: string;
  subtitle?: string;
  href?: string;
  status?: string;
}

export interface LiquidityGapQuery {
  companyId: number;
  scenarioId?: number;
  asOf?: string;
  asBase?: boolean;
}

export interface LiquidityGapReportDTO {
  companyId: number;
  runId?: number;
  asOfDate: string;
  baseCurrency: string;
  minimumBalance: string;
  minClosingBalance: string;
  minClosingOn?: string | null;
  liquidityGap: string;
  breachedBuckets: number;
  firstBreachOn?: string | null;
  scenarioId?: number | null;
  basis: string;
  approvedBase: boolean;
  openingBasis: string;
  currencies: TreasuryLiquidityGapCurrencyDTO[];
}

export interface ListAIAgentRunsQuery {
  cursor?: string;
  limit?: number;
  agentCode?: string;
  companyId?: number | null;
  status?: string;
  from?: string;
  to?: string;
}

export interface ListAIAgentsQuery {
  cursor?: string;
  limit?: number;
  module?: string;
  includeOrphaned?: boolean;
}

export interface ListAIBacktestPointsQuery {
  cursor?: string;
  limit?: number;
  horizon?: number;
}

export interface ListAIBacktestRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  metricCode?: string;
  status?: string;
}

export interface ListAIBriefingsQuery {
  cursor?: string;
  limit?: number;
  agentCode?: string;
  companyId?: number | null;
  priority?: string;
  unreadOnly?: boolean;
}

export interface ListAICapabilitiesQuery {
  cursor?: string;
  limit?: number;
  module?: string;
  kind?: 'advice' | 'draft' | 'action';
  autonomy?: 'assistive' | 'supervised' | 'delegated';
  includeOrphaned?: boolean;
}

export interface ListAIConfigAnomaliesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  ruleCode?: string;
  entity?: string;
  status?: string;
  severity?: string;
}

export interface ListAIRecommendationOutcomesQuery {
  cursor?: string;
  limit?: number;
  capability?: string;
  type?: string;
  result?: 'realised' | 'not_realised' | 'not_taken' | 'unmeasurable';
  companyId?: number;
  measuredFrom?: string;
  measuredTo?: string;
}

export interface ListAIRecommendationsQuery {
  cursor?: string;
  limit?: number;
  capability?: string;
  type?: string;
  disposition?: 'pending' | 'accepted' | 'rejected' | 'expired' | 'auto_confirmed' | 'routed';
  companyId?: number;
}

export interface ListAccountProfilesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  accountType?: 'operating' | 'collection' | 'disbursement' | 'payroll' | 'escrow' | 'pool_header' | 'deposit';
  poolRole?: 'none' | 'header' | 'participant';
  q?: string;
}

export interface ListAccountingMethodsQuery {
  activeOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListAccountsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  type?: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  isPostable?: boolean | null;
  isActive?: boolean | null;
}

export interface ListAccrualItemsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  positionId?: number;
  status?: string;
}

export interface ListAccrualPositionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  isActive?: boolean | null;
}

export interface ListAccrualRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListAccrualsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  programId?: number;
  customerId?: number;
  status?: string;
  periodStart?: string;
}

export interface ListActivitiesQuery {
  cursor?: string;
  limit?: number;
  leadId?: number;
  opportunityId?: number;
  caseId?: number;
}

export interface ListAdjustmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'pending_approval' | 'posted' | 'rejected';
}

export interface ListAgreementsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  segmentCode?: string;
  status?: string;
  activeOn?: string;
  q?: string;
}

export interface ListAllocationsQuery {
  cursor?: string;
  limit?: number;
  orderId?: number;
  orderLineId?: number;
  warehouseId?: number;
  status?: 'allocated' | 'picked' | 'released';
}

export interface ListAmendmentsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListAnomaliesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListAssetBooksQuery {
  cursor?: string;
  limit?: number;
}

export interface ListAssetsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  classId?: number;
  status?: 'in_service' | 'disposed';
  location?: string;
  custodian?: string;
  serialNo?: string;
  tagNo?: string;
  insuranceExpiringBefore?: string;
  warrantyExpiringBefore?: string;
}

export interface ListAssignmentRulesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListAssignmentsQuery {
  cursor?: string;
  limit?: number;
  positionId?: number | null;
  employeeId?: number | null;
  asAt?: string;
  type?: string;
}

export interface ListAttendanceExceptionsQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number;
  status?: string;
  kind?: string;
  from?: string;
  to?: string;
}

export interface ListAttendanceLocksQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  payGroupId?: number;
  status?: string;
}

export interface ListAttendanceQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number;
  from?: string;
  to?: string;
  approvedOnly?: boolean;
}

export interface ListAttentionQuery {
  kind?: 'approval' | 'exception' | 'task' | 'alert';
  module?: string;
  severity?: 'info' | 'warning' | 'critical';
  companyId?: number | null;
  overdueOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListAuthorityAbsencesQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number | null;
  asAt?: string;
  status?: string;
}

export interface ListAuthorityDelegationsQuery {
  cursor?: string;
  limit?: number;
  fromPositionId?: number | null;
  toPositionId?: number | null;
  status?: string;
  asAt?: string;
}

export interface ListAuthorityRulesQuery {
  cursor?: string;
  limit?: number;
  versionId?: number | null;
  docType?: string;
  companyId?: number | null;
}

export interface ListBOMQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  itemId?: number;
  status?: string;
}

export interface ListBackordersQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  orderId?: number;
  status?: 'open' | 'fulfilled' | 'cancelled';
  origin?: 'confirm' | 'shipment';
  promisedBefore?: string;
}

export interface ListBankAccountsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListBankFileProfilesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListBeneficiariesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  verificationStatus?: 'unverified' | 'pending' | 'verified' | 'failed' | 'blocked';
  counterpartyKind?: 'vendor' | 'employee' | 'customer' | 'tax_authority' | 'related_company' | 'other';
  isActive?: boolean | null;
  payableOnly?: boolean;
  q?: string;
}

export interface ListBeneficiaryChangesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  beneficiaryId?: number;
  status?: 'pending' | 'approved' | 'applied' | 'rejected';
  action?: 'create' | 'update' | 'deactivate';
  dueOnly?: boolean;
}

export interface ListBinsQuery {
  cursor?: string;
  limit?: number;
  includeInactive?: boolean;
}

export interface ListBooksQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListCAPAQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
  kind?: string;
  severity?: string;
  sourceKind?: string;
  vendorId?: number;
  itemId?: number;
  ownerUserId?: number;
  open?: boolean;
  overdueOn?: string;
  q?: string;
}

export interface ListCRMAccountsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  accountType?: string;
  segmentId?: number;
  ownerUserId?: number;
  companyId?: number;
  parentId?: number;
  q?: string;
}

export interface ListCRMContactsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  ownerUserId?: number;
  accountId?: number;
  q?: string;
}

export interface ListCalendarsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListCampaignRecipientsQuery {
  cursor?: string;
  limit?: number;
  state?: string;
  exclusionReason?: string;
}

export interface ListCampaignResponsesQuery {
  cursor?: string;
  limit?: number;
  campaignId?: number;
  partyId?: number;
  kind?: string;
}

export interface ListCampaignSendsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListCandidatesQuery {
  cursor?: string;
  limit?: number;
  requisitionId?: number;
  stage?: string;
  q?: string;
}

export interface ListCapitalizableLinesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  q?: string;
}

export interface ListCapturePoliciesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  walletId?: number;
  categoryCode?: string;
  currentOnly?: boolean;
}

export interface ListCardAccountsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListCardStatementLinesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
}

export interface ListCardStatementsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  cardAccountId?: number;
  status?: string;
}

export interface ListCasesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  priority?: string;
  assignedTo?: number;
  customerId?: number;
  openOnly?: boolean;
  q?: string;
}

export interface ListCatalogueQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId: number;
}

export interface ListCategoriesQuery {
  cursor?: string;
  limit?: number;
  isActive?: boolean | null;
  q?: string;
}

export interface ListCertificatesQuery {
  partyKind?: string;
  partyId?: number;
  status?: string;
  cursor?: string;
  limit?: number;
}

export interface ListChangesQuery {
  cursor?: string;
  limit?: number;
  vendorId?: number;
  status?: 'pending' | 'approved' | 'applied' | 'rejected' | 'cancelled';
}

export interface ListCheckpointsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  kind?: 'temperature' | 'humidity' | 'cleaning' | 'receiving' | 'cooking' | 'cooling' | 'visual';
  activeOnly?: boolean;
}

export interface ListClassesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  q?: string;
}

export interface ListClassificationQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  runId?: number;
  classification?: string;
  warehouseId?: number;
}

export interface ListClockEventsQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number;
  from?: string;
  to?: string;
  kind?: string;
}

export interface ListCloseCalendarsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  scope?: string;
  isActive?: boolean | null;
}

export interface ListCloseRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  calendarId?: number;
  fiscalPeriodId?: number;
  status?: string;
}

export interface ListCloseTasksQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  taskKind?: string;
  overdue?: boolean;
}

export interface ListCodesQuery {
  code?: string;
  kind?: string;
  jurisdiction?: string;
  on?: string;
  q?: string;
  activeOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListCommitmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  accountId?: number;
  fiscalPeriodId?: number;
  docType?: string;
  status?: 'open' | 'released';
}

export interface ListCompaniesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  isActive?: boolean | null;
}

export interface ListComponentsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  isActive?: boolean | null;
}

export interface ListConsentChannelsQuery {
  cursor?: string;
  limit?: number;
  includeInactive?: string;
}

export interface ListConsentPurposesQuery {
  cursor?: string;
  limit?: number;
  includeInactive?: string;
}

export interface ListConsentsQuery {
  cursor?: string;
  limit?: number;
  partyId?: number | null;
  channelCode?: string;
  purposeCode?: string;
  state?: string;
  asOf?: string;
}

export interface ListContentAssignmentsQuery {
  cursor?: string;
  limit?: number;
  setId?: number;
  deviceId?: number;
  staleOnly?: boolean;
}

export interface ListContentSetsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListContractExpiryQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  withinDays?: number;
}

export interface ListContractReleasesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  poId?: number;
}

export interface ListContractorTimesheetsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListContractsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  status?: string;
  expiringBefore?: string;
  reviewBefore?: string;
  q?: string;
}

export interface ListControlsQuery {
  module?: string;
  classification?: string;
  isEnabled?: boolean | null;
  cursor?: string;
  limit?: number;
}

export interface ListConversationsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListCoolingOffQuery {
  cursor?: string;
  limit?: number;
  subject?: string;
  module?: string;
  companyId?: number;
}

export interface ListCostAbsorptionRatesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  poolId?: number;
  fiscalPeriodId?: number;
  status?: string;
}

export interface ListCostCyclesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
  method?: string;
  q?: string;
}

export interface ListCostDriverQuantitiesQuery {
  cursor?: string;
  limit?: number;
  fiscalPeriodId?: number;
  objectType?: string;
}

export interface ListCostDriversQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  isActive?: boolean | null;
  q?: string;
}

export interface ListCostPoolsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  poolKind?: string;
  isActive?: boolean | null;
  q?: string;
}

export interface ListCostRunAllocationsQuery {
  cursor?: string;
  limit?: number;
  stepSeq?: number;
  senderPoolId?: number;
  objectType?: string;
  objectId?: number;
  internalOnly?: boolean;
  externalOnly?: boolean;
}

export interface ListCostRunIterationsQuery {
  cursor?: string;
  limit?: number;
  poolId?: number;
}

export interface ListCostRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  cycleId?: number;
  fiscalPeriodId?: number;
  status?: string;
}

export interface ListCountSessionsDetailQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  status?: string;
}

export interface ListCountriesQuery {
  q?: string;
  isActive?: boolean | null;
  cursor?: string;
  limit?: number;
}

export interface ListCoverageMinimumsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListCrossChargeRulesQuery {
  asAt?: string;
}

export interface ListCurrenciesQuery {
  activeOnly?: boolean;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListCustomerAssetsQuery {
  cursor?: string;
  limit?: number;
  customerId?: number;
  q?: string;
}

export interface ListCustomersQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  status?: string;
}

export interface ListDSRPoliciesQuery {
  cursor?: string;
  limit?: number;
  jurisdiction?: string;
  kind?: string;
}

export interface ListDSRQuery {
  cursor?: string;
  limit?: number;
  partyId?: number | null;
  kind?: string;
  status?: string;
  dueBefore?: string;
  overdueOnly?: boolean;
}

export interface ListDailyOperatingQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  unitType?: string;
  unitId?: number;
  from?: string;
  to?: string;
  status?: string;
  lateOnly?: boolean;
}

export interface ListDaysQuery {
  cursor?: string;
  limit?: number;
  unitType?: 'company' | 'branch' | 'location';
  unitId?: number | null;
  from?: string;
  to?: string;
  status?: 'scheduled' | 'open' | 'closing' | 'closed' | 'reopened';
}

export interface ListDemandQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  locationId?: number | null;
  metric?: string;
  from?: string;
  to?: string;
}

export interface ListDepletionRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  docType?: string;
  status?: 'posted' | 'reversed';
  from?: string;
  to?: string;
}

export interface ListDepreciationEntriesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListDepreciationRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListDesignationsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  kind?: 'PSU' | 'TRU' | 'LE';
  asAt?: string;
}

export interface ListDevicesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  surface?: string;
  status?: string;
  bindingKind?: string;
  bindingId?: number;
  q?: string;
}

export interface ListDiscountPoliciesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  channel?: string;
  segmentCode?: string;
  categoryCode?: string;
  asAt?: string;
}

export interface ListDivisionCountriesQuery {
  asAt?: string;
}

export interface ListDivisionLegalEntitiesQuery {
  asAt?: string;
}

export interface ListDivisionsFNDQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  enterpriseId?: number;
  countryCode?: string;
  legalEntityId?: number;
  asAt?: string;
}

export interface ListEAMAssetReconciliationQuery {
  companyId: number;
  format?: string;
}

export interface ListEAMAssetsQuery {
  companyId?: number;
  functionalLocationId?: number | null;
  criticality?: number | null;
  state?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMAvailabilityEventsQuery {
  companyId?: number;
  assetId?: number | null;
  state?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMComplianceRequirementsQuery {
  companyId?: number;
  assetId?: number | null;
  standing?: string;
  blockingOnly?: boolean;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMDowntimeQuery {
  companyId?: number;
  assetId?: number | null;
  openOnly?: boolean;
  kind?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMFailuresQuery {
  assetId?: number;
  open?: boolean | null;
  cursor?: string;
  limit?: number;
}

export interface ListEAMFunctionalLocationsQuery {
  companyId?: number;
  parentId?: number | null;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMInspectionsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListEAMMeterReadingsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListEAMMetersQuery {
  assetId?: number;
  cursor?: string;
  limit?: number;
}

export interface ListEAMPMPlansQuery {
  companyId?: number;
  assetId?: number | null;
  dueBefore?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMPermitsQuery {
  companyId?: number;
  assetId?: number | null;
  status?: string;
  openOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListEAMReliabilityQuery {
  companyId?: number;
  assetId?: number | null;
  windowDays?: number;
  cursor?: string;
  limit?: number;
}

export interface ListEAMStrategiesQuery {
  companyId?: number;
  kind?: string;
  cursor?: string;
  limit?: number;
}

export interface ListEAMTaskListsQuery {
  companyId?: number;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListECLAssessmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
}

export interface ListECLMatricesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  segmentId?: number;
}

export interface ListECLRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
}

export interface ListECLSegmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListECOQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  status?: string;
  itemId?: number;
  q?: string;
}

export interface ListEliminationRulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  asAt?: string;
}

export interface ListEliminationsQuery {
  cursor?: string;
  limit?: number;
  status?: 'proposed' | 'accepted' | 'rejected';
}

export interface ListEmployeesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  departmentId?: number;
  managerId?: number;
  status?: string;
  q?: string;
  permitExpiringBefore?: string;
}

export interface ListEnterprisesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  type?: string;
  q?: string;
}

export interface ListEntriesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'posted' | 'reversed';
  source?: 'manual' | 'recurring' | 'reversal' | 'allocation' | 'opening_balance' | 'ap' | 'ar' | 'cash' | 'inventory' | 'payroll' | 'assets' | 'sales' | 'projects' | 'service' | 'mfg' | 'cost' | 'expense' | 'interco' | 'treasury' | 'pos' | 'pms' | 'year_end_close' | 'template';
  fiscalPeriodId?: number;
  accountId?: number;
  from?: string;
  to?: string;
  journalTypeCode?: string;
  createdBy?: number;
  approvedBy?: number;
}

export interface ListExitsQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number;
  companyId?: number;
  status?: string;
}

export interface ListExpenseCategoriesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  q?: string;
}

export interface ListExpenseClaimsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  employeeId?: number;
  status?: string;
  q?: string;
}

export interface ListExpenseMileageRatesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vehicleClass?: string;
  asAt?: string;
}

export interface ListExpensePayrollInclusionsQuery {
  cursor?: string;
  limit?: number;
  runId?: number;
  companyId?: number;
  employeeId?: number;
  kind?: string;
  status?: string;
}

export interface ListExpensePoliciesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  categoryId?: number;
  asAt?: string;
}

export interface ListExpiringHRSkillsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  within?: number;
}

export interface ListExplanationsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  docType?: string;
  docId?: number;
  itemId?: number;
  customerId?: number;
  winnerSource?: string;
  marginOutcome?: string;
  from?: string;
  to?: string;
  supersededOnly?: boolean;
}

export interface ListExtractionsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
}

export interface ListFacilitiesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'pending_approval' | 'active' | 'in_default' | 'matured' | 'closed';
  facilityKind?: 'term_loan' | 'revolving' | 'overdraft' | 'guarantee' | 'demand_loan' | 'lease_liability' | 'bond' | 'intercompany_loan';
  bankId?: number;
  currency?: string;
  q?: string;
}

export interface ListFilingsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'pending' | 'prepared' | 'filed' | 'paid' | 'waived' | 'overdue';
  dueBefore?: string;
}

export interface ListFixedBinsQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
}

export interface ListForecastRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  scenarioId?: number;
  horizon?: 'operational' | 'strategic';
}

export interface ListForecastSnapshotsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  ownerUserId?: number;
  periodCode?: string;
}

export interface ListFreezesQuery {
  cursor?: string;
  limit?: number;
  warehouseId?: number;
  sessionId?: number;
  activeOnly?: boolean;
}

export interface ListGRNQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  poId?: number;
  status?: 'draft' | 'posted' | 'reversed' | 'cancelled';
}

export interface ListGradesQuery {
  cursor?: string;
  limit?: number;
  asAt?: string;
  isActive?: boolean | null;
}

export interface ListGroupsQuery {
  appliesTo?: string;
  activeOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListHACCPRecordsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  checkpointId?: number;
  warehouseId?: number;
  outcome?: 'pass' | 'fail' | 'not_applicable';
  from?: string;
  to?: string;
}

export interface ListHRSkillsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  category?: string;
  q?: string;
}

export interface ListHierarchiesQuery {
  cursor?: string;
  limit?: number;
  enterpriseId?: number;
  purpose?: string;
  status?: string;
  q?: string;
}

export interface ListHoldsQuery {
  status?: 'held' | 'accepted' | 'rejected';
  cursor?: string;
  limit?: number;
}

export interface ListHolidaysQuery {
  cursor?: string;
  limit?: number;
  from?: string;
  to?: string;
  kind?: 'public' | 'religious' | 'company' | 'closure';
}

export interface ListICDifferencesQuery {
  cursor?: string;
  limit?: number;
  pairId?: number;
  companyId?: number;
  status?: string;
  kind?: string;
  ownerUserId?: number;
  unresolved?: boolean | null;
}

export interface ListICReconPairsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  isActive?: boolean | null;
}

export interface ListICStatementsQuery {
  cursor?: string;
  limit?: number;
  pairId?: number;
  fiscalPeriodId?: number;
}

export interface ListICTransactionsQuery {
  cursor?: string;
  limit?: number;
  pairId?: number;
  companyId?: number;
  status?: string;
  direction?: string;
  fromDate?: string;
  toDate?: string;
  q?: string;
}

export interface ListImpairmentIndicatorsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  outcome?: 'pending' | 'no_impairment' | 'impaired' | 'reversed';
}

export interface ListImpairmentsQuery {
  cursor?: string;
  limit?: number;
  assetId?: number;
  bookId?: number;
}

export interface ListImportsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'posted' | 'rejected';
}

export interface ListInboxQuery {
  cursor?: string;
  limit?: number;
  docType?: string;
}

export interface ListInspectionLotsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  originKind?: string;
  status?: string;
  itemId?: number;
  warehouseId?: number;
  planId?: number;
  originDocType?: string;
  originDocId?: number;
  open?: boolean;
  q?: string;
}

export interface ListInspectionPlansQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  originKind?: string;
  status?: string;
  itemId?: number;
  vendorId?: number;
  asOf?: string;
  q?: string;
}

export interface ListInstructionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'screening_hold' | 'released' | 'confirmed' | 'rejected' | 'cancelled' | 'recall_requested' | 'recalled' | 'returned';
  bankAccountId?: number;
  beneficiaryId?: number;
  openOnly?: boolean;
  from?: string;
  to?: string;
  q?: string;
}

export interface ListIntercompanyRulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  asAt?: string;
}

export interface ListInvoicesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  vendorId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'posted' | 'paid' | 'cancelled' | 'reversed';
  docKind?: 'invoice' | 'credit_note' | 'debit_note';
  from?: string;
  to?: string;
  openOnly?: boolean;
  onHold?: boolean | null;
}

export interface ListItemsQuery {
  status?: 'open' | 'in_progress' | 'resolved' | 'written_off' | 'escalated';
  olderThanDays?: number;
  companyId?: number | null;
  cursor?: string;
  limit?: number;
}

export interface ListJobsQuery {
  cursor?: string;
  limit?: number;
  family?: string;
  isActive?: boolean | null;
  q?: string;
}

export interface ListJournalTypesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  isActive?: boolean | null;
  q?: string;
}

export interface ListJurisdictionsQuery {
  country?: string;
  level?: string;
  cursor?: string;
  limit?: number;
}

export interface ListKBArticlesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  category?: string;
  q?: string;
}

export interface ListKDSTicketsQuery {
  cursor?: string;
  limit?: number;
  stationId: number;
  sinceId?: number;
  state?: 'new' | 'in_progress' | 'ready' | 'bumped' | 'recalled' | 'cancelled';
}

export interface ListLandedCostElementsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  isActive?: boolean | null;
}

export interface ListLandedCostShipmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
  reference?: string;
}

export interface ListLeadScoreRunsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListLeadsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  ownerUserId?: number;
  q?: string;
}

export interface ListLeaveBlackoutsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListLeaveMovementsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListLeaveRequestsQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number;
  leaveTypeId?: number;
  status?: string;
  from?: string;
  to?: string;
}

export interface ListLedgerQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
  docType?: string;
  docId?: number;
  from?: string;
  to?: string;
}

export interface ListLedgerSetsQuery {
  activeOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListLedgersQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListLegalEntitiesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  isActive?: boolean | null;
  entityType?: string;
}

export interface ListLegislativeDataGroupsQuery {
  cursor?: string;
  limit?: number;
  country?: string;
}

export interface ListLinesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListLocalitiesQuery {
  countryId?: string;
  subdivisionId?: string;
  isActive?: boolean | null;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListLocationsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  type?: string;
  status?: string;
  country?: string;
  q?: string;
}

export interface ListLotRecallsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'tracing' | 'contained' | 'closed' | 'cancelled';
  severity?: 'class_1' | 'class_2' | 'class_3' | 'withdrawal';
  q?: string;
}

export interface ListLoyaltyAccrualsQuery {
  cursor?: string;
  limit?: number;
  sourceType?: string;
  from?: string;
  to?: string;
}

export interface ListLoyaltyMembersQuery {
  cursor?: string;
  limit?: number;
  programId?: number | null;
  partyId?: number | null;
  tierId?: number | null;
  status?: string;
  q?: string;
}

export interface ListLoyaltyProgramsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  companyId?: number | null;
  q?: string;
}

export interface ListMRPSuggestionsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  status?: string;
}

export interface ListMandatesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  bankAccountId?: number;
  effectiveOn?: string;
  q?: string;
}

export interface ListManualLinesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListMarketingCampaignsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  ownerUserId?: number;
  companyId?: number;
  q?: string;
}

export interface ListMarketingSegmentMembersQuery {
  cursor?: string;
  limit?: number;
  excluded?: boolean | null;
}

export interface ListMarketingSegmentsQuery {
  cursor?: string;
  limit?: number;
  kind?: string;
  q?: string;
}

export interface ListMatchCausesQuery {
  includeInactive?: boolean;
}

export interface ListMatchExceptionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vendorId?: number;
  poId?: number;
  causeCode?: string;
  status?: string;
  mine?: boolean;
  overdueOnly?: boolean;
}

export interface ListMatchQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  poId?: number;
  status?: 'matched' | 'disputed';
}

export interface ListMatchSetsQuery {
  cursor?: string;
  limit?: number;
  sessionId?: number;
  status?: 'active' | 'released';
}

export interface ListMaterialityQuery {
  cursor?: string;
  limit?: number;
  docType?: string;
  companyId?: number | null;
}

export interface ListMatrixVersionsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  code?: string;
}

export interface ListMessagesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListMetricCertificationsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  metricCode?: string;
  status?: string;
}

export interface ListMetricDefinitionsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  module?: string;
  dashboard?: string;
  certificationState?: string;
  ownerPositionId?: number;
  includeOrphaned?: boolean;
}

export interface ListMfgToolsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  status?: string;
}

export interface ListMinimumBalanceChangesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  currency?: string;
  status?: 'pending' | 'applied' | 'rejected' | 'cancelled';
}

export interface ListModelsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  fiscalYear?: number;
}

export interface ListMyServiceChargeAllocationsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListNamingTemplatesQuery {
  cursor?: string;
  limit?: number;
  scopeType?: string;
  scopeId?: number | null;
  objectType?: string;
}

export interface ListNotificationPoliciesQuery {
  eventKind?: string;
  cursor?: string;
  limit?: number;
}

export interface ListNotificationsQuery {
  cursor?: string;
  limit?: number;
  unreadOnly: boolean;
}

export interface ListObligationsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  contractId?: number;
  method?: string;
  status?: string;
  dueBefore?: string;
}

export interface ListObservationsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  bankAccountId?: number;
  source?: string;
  from?: string;
  to?: string;
}

export interface ListOffersQuery {
  cursor?: string;
  limit?: number;
  candidateId?: number;
  status?: string;
}

export interface ListOfflinePoliciesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListOnHandQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
}

export interface ListOpportunitiesQuery {
  cursor?: string;
  limit?: number;
  stageCode?: string;
  ownerUserId?: number;
  openOnly?: boolean;
  q?: string;
}

export interface ListOrdersQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  customerId?: number;
  status?: 'draft' | 'pending_approval' | 'confirmed' | 'partially_shipped' | 'shipped' | 'invoiced' | 'closed' | 'cancelled';
}

export interface ListOverridesQuery {
  ruleCode?: string;
  companyId?: number | null;
  from?: string;
  to?: string;
  reasonCode?: string;
  overriddenBy?: number | null;
  cursor?: string;
  limit?: number;
}

export interface ListOwnershipQuery {
  cursor?: string;
  limit?: number;
  parentCompanyId?: number;
  childCompanyId?: number;
  asAt?: string;
}

export interface ListPMSAuthHoldsQuery {
  cursor?: string;
  limit?: number;
  folioId?: number;
  reservationId?: number;
  status?: string;
}

export interface ListPMSAvailabilityQuery {
  propertyId: number;
  from: string;
  to: string;
  roomTypeId?: number;
}

export interface ListPMSBoardAllowancesQuery {
  reservationId: number;
}

export interface ListPMSBoardPlansQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
}

export interface ListPMSCancellationPoliciesQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
}

export interface ListPMSChannelExchangesQuery {
  cursor?: string;
  limit?: number;
  channelId: number;
  direction?: string;
}

export interface ListPMSChannelsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  propertyId?: number;
  status?: string;
  kind?: string;
  q?: string;
}

export interface ListPMSCommissionAccrualsQuery {
  cursor?: string;
  limit?: number;
  channelId?: number;
  reservationId?: number;
  status?: string;
}

export interface ListPMSCommissionAgreementsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  channelId?: number;
}

export interface ListPMSCommissionStatementsQuery {
  cursor?: string;
  limit?: number;
  channelId?: number;
  status?: string;
}

export interface ListPMSDepositsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  reservationId?: number;
  folioId?: number;
  q?: string;
}

export interface ListPMSFoliosQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  reservationId?: number;
  guestId?: number;
  q?: string;
}

export interface ListPMSFrontDeskQuery {
  propertyId: number;
  businessDate?: string;
}

export interface ListPMSGroupBlocksQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  q?: string;
}

export interface ListPMSGuestMessagesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  guestId?: number;
  status?: 'pending' | 'rendered' | 'sent' | 'suppressed' | 'failed';
  channel?: 'email' | 'sms' | 'in_app' | 'print';
}

export interface ListPMSGuestRequestsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  kind?: string;
}

export interface ListPMSGuestsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  q?: string;
  restricted?: boolean | null;
}

export interface ListPMSHousekeepingBoardsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  businessDate?: string;
  employeeId?: number;
}

export interface ListPMSHousekeepingTasksQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  businessDate?: string;
  boardId?: number;
  roomId?: number;
  status?: string;
}

export interface ListPMSKeyIssuesQuery {
  cursor?: string;
  limit?: number;
  reservationId?: number;
  roomId?: number;
  status?: string;
}

export interface ListPMSNightAuditRunsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  propertyId?: number;
  status?: string;
  from?: string;
  to?: string;
}

export interface ListPMSOutOfOrderQuery {
  cursor?: string;
  limit?: number;
  roomId?: number;
  openOnly?: boolean;
}

export interface ListPMSParityObservationsQuery {
  cursor?: string;
  limit?: number;
  channelId?: number;
  propertyId?: number;
  breached?: boolean | null;
}

export interface ListPMSPropertiesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  q?: string;
}

export interface ListPMSRatePlanRatesQuery {
  from?: string;
  to?: string;
  roomTypeId?: number;
}

export interface ListPMSRatePlansQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  q?: string;
}

export interface ListPMSRateRestrictionsQuery {
  propertyId: number;
  from: string;
  to: string;
  roomTypeId?: number;
  ratePlanId?: number;
}

export interface ListPMSReservationsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  guestId?: number;
  groupBlockId?: number;
  arrivalFrom?: string;
  arrivalTo?: string;
  q?: string;
}

export interface ListPMSRevenueCentersQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  q?: string;
}

export interface ListPMSRoomFaultsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  roomId?: number;
  status?: string;
  severity?: string;
}

export interface ListPMSRoomTypesQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  q?: string;
}

export interface ListPMSRoomsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  roomTypeId?: number;
  status?: string;
  q?: string;
}

export interface ListPMSServiceBookingsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  reservationId?: number;
  status?: string;
  scheduledDate?: string;
}

export interface ListPMSServiceResourcesQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  kind?: string;
}

export interface ListPMSServicesQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  kind?: string;
  q?: string;
}

export interface ListPMSTransferLegsQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  direction?: string;
}

export interface ListPMSUnconsumedBoardAllowancesQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
}

export interface ListPMSWaitlistQuery {
  cursor?: string;
  limit?: number;
  propertyId: number;
  status?: string;
  roomTypeId?: number;
}

export interface ListPMSchedulesQuery {
  cursor?: string;
  limit?: number;
  customerAssetId?: number;
}

export interface ListPOQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  vendorId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'issued' | 'confirmed' | 'partially_received' | 'received' | 'closed' | 'cancelled';
}

export interface ListPOSAllergenCodesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
}

export interface ListPOSAnomaliesQuery {
  cursor?: string;
  limit?: number;
  outletId?: number;
  operatorId?: number;
  code?: string;
  status?: string;
}

export interface ListPOSAvailabilityQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  state?: string;
}

export interface ListPOSCashSessionsQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  status?: string;
}

export interface ListPOSChecksQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  status?: 'open' | 'settling' | 'settled' | 'transferred' | 'voided';
  tableId?: number;
}

export interface ListPOSCustomerDepositsQuery {
  cursor?: string;
  limit?: number;
  customerId?: number;
  outletId?: number;
  status?: string;
}

export interface ListPOSDayClosesQuery {
  cursor?: string;
  limit?: number;
  outletId?: number;
  status?: string;
  from?: string;
  to?: string;
}

export interface ListPOSDenominationsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  currency?: string;
}

export interface ListPOSDepositsQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  status?: string;
}

export interface ListPOSEmployeePurchasesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  employeeId?: number;
  status?: string;
}

export interface ListPOSFiscalReceiptsQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  status?: string;
  fiscalYear?: number;
  from?: string;
  to?: string;
  q?: string;
}

export interface ListPOSFloorAreasQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
}

export interface ListPOSInstrumentsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  kind?: string;
  status?: string;
  customerId?: number;
}

export interface ListPOSItemAllergensQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  itemId?: number;
}

export interface ListPOSMenuVersionsQuery {
  cursor?: string;
  limit?: number;
  menuId: number;
  status?: string;
}

export interface ListPOSMenusQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  q?: string;
}

export interface ListPOSOperatorStatsQuery {
  cursor?: string;
  limit?: number;
  outletId?: number;
  operatorId?: number;
  businessDate?: string;
  from?: string;
  to?: string;
  breachedOnly?: boolean;
}

export interface ListPOSOutletsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  q?: string;
}

export interface ListPOSRevenueCentersQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  q?: string;
}

export interface ListPOSSalesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  outletId?: number;
  terminalId?: number;
  status?: string;
  docKind?: string;
  businessDate?: string;
  from?: string;
  to?: string;
  operatorId?: number;
  customerId?: number;
  unclosed?: boolean;
  q?: string;
}

export interface ListPOSSerialSalesQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  serialNo?: string;
  customerId?: number;
}

export interface ListPOSStationsQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
}

export interface ListPOSTablesQuery {
  cursor?: string;
  limit?: number;
  areaId?: number;
  outletId?: number;
  status?: 'free' | 'seated' | 'ordered' | 'printed' | 'paid' | 'dirty';
}

export interface ListPOSTenderTypesQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  kind?: string;
}

export interface ListPOSTerminalsQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  q?: string;
}

export interface ListPOSTipsQuery {
  cursor?: string;
  limit?: number;
  outletId: number;
  operatorId?: number;
  businessDate?: string;
  from?: string;
  to?: string;
}

export interface ListPRQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  requesterId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'cancelled' | 'closed';
}

export interface ListPacksQuery {
  cursor?: string;
  limit?: number;
}

export interface ListPacksRequest {
  country?: string;
  status?: string;
  cursor?: string;
  limit?: number;
}

export interface ListParLevelsQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
  asOf?: string;
}

export interface ListPartiesQuery {
  cursor?: string;
  limit?: number;
  kind?: string;
  status?: string;
  q?: string;
  linkType?: string;
  hasLinkType?: string;
  countryCode?: string;
  includeMerged?: string;
}

export interface ListPartyMatchCandidatesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  method?: string;
  partyId?: number | null;
  minScore?: string;
}

export interface ListPartyMergesQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  partyId?: number | null;
}

export interface ListPartyTimelineQuery {
  cursor?: string;
  limit?: number;
}

export interface ListPayGroupsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  q?: string;
  activeOnly?: boolean;
}

export interface ListPaymentClaimsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListPaymentExceptionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  paymentId?: number;
  status?: 'open' | 'resolved' | 'cancelled';
}

export interface ListPayslipsQuery {
  cursor?: string;
  limit?: number;
  hasException?: boolean | null;
}

export interface ListPeriodsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  calendarId?: number;
  fiscalYear?: number;
  status?: 'open' | 'soft_closed' | 'closed' | 'permanently_closed';
}

export interface ListPermitQuotasQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  jurisdictionId?: number;
}

export interface ListPicksQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  orderId?: number;
  warehouseId?: number;
  status?: 'open' | 'picking' | 'picked' | 'cancelled';
  pickerUserId?: number;
}

export interface ListPlanningParametersQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
}

export interface ListPoliciesQuery {
  cursor?: string;
  limit?: number;
  docType?: string;
  isActive?: boolean | null;
}

export interface ListPortalUsersQuery {
  cursor?: string;
  limit?: number;
  counterpartyType?: 'customer' | 'supplier' | 'contractor';
  counterpartyId?: number;
}

export interface ListPositionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  jobId?: number | null;
  gradeId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  status?: string;
  vacantOnly?: boolean;
  asAt?: string;
  q?: string;
}

export interface ListPostingAssertionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  module?: string;
  status?: string;
  from?: string;
  to?: string;
}

export interface ListPrecedenceQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  asAt?: string;
}

export interface ListPreviewsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListPriceListsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  kind?: 'base' | 'tier' | 'contract' | 'segment' | 'channel';
  currency?: string;
  customerId?: number;
  priceTier?: string;
  channel?: string;
  segmentCode?: string;
  isActive?: boolean | null;
  q?: string;
}

export interface ListPriceSetsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  channel?: string;
  locationId?: number;
  isActive?: boolean | null;
}

export interface ListProblemsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
}

export interface ListProductionOrderQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  status?: string;
  itemId?: number;
}

export interface ListProfilesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  bankAccountId?: number;
}

export interface ListProjectAdvancesQuery {
  cursor?: string;
  limit?: number;
  projectId?: number;
  companyId?: number;
  status?: 'outstanding' | 'recouped' | 'written_off';
}

export interface ListProjectAllocationsQuery {
  cursor?: string;
  limit?: number;
  resourceId?: number | null;
  projectId?: number | null;
  from?: string;
  to?: string;
}

export interface ListProjectBaselinesQuery {
  cursor?: string;
  limit?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'superseded';
}

export interface ListProjectCapacityQuery {
  from?: string;
  to?: string;
}

export interface ListProjectEVMQuery {
  from?: string;
  to?: string;
}

export interface ListProjectResourcesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  isActive?: boolean | null;
}

export interface ListProjectRetentionsQuery {
  cursor?: string;
  limit?: number;
  projectId?: number;
  companyId?: number;
  status?: 'withheld' | 'part_released' | 'released' | 'forfeited';
  dueBefore?: string;
}

export interface ListProjectRisksQuery {
  cursor?: string;
  limit?: number;
  projectId?: number;
  companyId?: number;
  status?: 'open' | 'mitigating' | 'realised' | 'closed';
  minExposure?: number;
}

export interface ListProjectSubcontractsQuery {
  cursor?: string;
  limit?: number;
  projectId?: number;
  companyId?: number;
  vendorId?: number;
  status?: 'draft' | 'active' | 'completed' | 'terminated';
}

export interface ListProjectVariationsQuery {
  cursor?: string;
  limit?: number;
  projectId?: number | null;
  status?: 'draft' | 'submitted' | 'approved' | 'accepted' | 'rejected' | 'withdrawn';
  origin?: 'internal' | 'customer' | 'site';
}

export interface ListProjectWIPRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'posted' | 'reversed' | 'cancelled';
}

export interface ListProjectsQuery {
  companyId?: number | null;
  status?: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListPromotionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  kind?: 'percent' | 'amount' | 'bundle';
  isActive?: boolean | null;
  activeOn?: string;
  channel?: string;
  q?: string;
}

export interface ListQuery {
  module?: string;
  entity: string;
  entityId: number;
  cursor?: string;
  limit?: number;
}

export interface ListQueuesQuery {
  module?: string;
  cursor?: string;
  limit?: number;
}

export interface ListQuotesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  customerId?: number;
  status?: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'converted' | 'superseded';
}

export interface ListRFQQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'issued' | 'closed' | 'awarded' | 'cancelled';
}

export interface ListRVMAccuracyLinesQuery {
  horizon?: number;
}

export interface ListRVMAccuracyRunsQuery {
  cursor?: string;
  limit?: number;
  forecastId?: number;
}

export interface ListRVMCompetitorRatesQuery {
  cursor?: string;
  limit?: number;
  propertyId?: number;
  competitorName?: string;
  from?: string;
  to?: string;
  importBatch?: string;
}

export interface ListRVMDisplacementsQuery {
  cursor?: string;
  limit?: number;
  propertyId?: number;
  segmentId?: number;
  groupBlockId?: number;
  status?: string;
  arrivalFrom?: string;
  arrivalTo?: string;
}

export interface ListRVMForecastVersionsQuery {
  cursor?: string;
  limit?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'published' | 'locked' | 'superseded';
}

export interface ListRVMForecastsQuery {
  cursor?: string;
  limit?: number;
  propertyId?: number;
  kind?: 'budget' | 'forecast';
  activeOnly?: boolean;
}

export interface ListRVMHurdlesQuery {
  cursor?: string;
  limit?: number;
  propertyId?: number;
  segmentId?: number;
  roomTypeId?: number;
  activeOnly?: boolean;
}

export interface ListRVMSegmentMappingsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  segmentId?: number;
}

export interface ListRVMSegmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  family?: 'transient' | 'group' | 'contract' | 'wholesale' | 'complimentary' | 'house';
  activeOnly?: boolean;
  q?: string;
}

export interface ListRVMSnapshotsQuery {
  cursor?: string;
  limit?: number;
  propertyId?: number;
  from?: string;
  to?: string;
}

export interface ListRVMStreamsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  activeOnly?: boolean;
}

export interface ListRateObservationsQuery {
  cursor?: string;
  limit?: number;
  rateCode?: string;
}

export interface ListRatesQuery {
  base?: string;
  quote?: string;
  from?: string;
  to?: string;
  rateType?: 'spot' | 'corporate' | 'historical';
  cursor?: string;
  limit?: number;
}

export interface ListRatificationsQuery {
  cursor?: string;
  limit?: number;
  deviceId?: number;
  status?: string;
  controlCode?: string;
  overdueOnly?: boolean;
}

export interface ListReasonCodesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListReasonSetsQuery {
  module?: string;
  cursor?: string;
  limit?: number;
}

export interface ListReasonsQuery {
  setCode?: string;
  isActive?: boolean | null;
  asAt?: string;
  cursor?: string;
  limit?: number;
}

export interface ListRebateProgramsQuery {
  cursor?: string;
  limit?: number;
  agreementId?: number;
  isActive?: boolean | null;
}

export interface ListRecallTracesQuery {
  cursor?: string;
  limit?: number;
  positionKind?: 'on_hand' | 'issued' | 'transferred' | 'sold' | 'consumed' | 'wasted' | 'returned';
  lotId?: number;
}

export interface ListReceiptsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  customerId?: number;
  openOnly?: boolean;
  from?: string;
  to?: string;
}

export interface ListRecipesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  outputItemId?: number;
  kind?: 'plated' | 'batch' | 'sub_recipe' | 'modifier';
  code?: string;
  asOf?: string;
  activeOnly?: boolean;
}

export interface ListReconRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
}

export interface ListRecurringQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  direction?: 'inflow' | 'outflow';
  isActive?: boolean | null;
  loanReview?: boolean | null;
}

export interface ListReferenceSetsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  status?: string;
}

export interface ListReferencesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListRegimesQuery {
  jurisdictionId?: number;
  kind?: string;
  cursor?: string;
  limit?: number;
}

export interface ListRegistrationsQuery {
  companyId?: number;
  regimeId?: number;
  status?: string;
  cursor?: string;
  limit?: number;
}

export interface ListRemittancesQuery {
  cursor?: string;
  limit?: number;
  returnId?: number;
  companyId?: number;
  status?: 'prepared' | 'confirmed' | 'cancelled';
}

export interface ListReplenishmentPoliciesQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
  companyId?: number;
  asOf?: string;
}

export interface ListReplenishmentSuggestionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
  method?: string;
}

export interface ListReportExceptionViewsQuery {
  companyId: number;
  roleCode?: string;
  mineOnly?: boolean;
}

export interface ListReportOutputsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  reportKey?: string;
  certificationState?: string;
  emittedBy?: number;
}

export interface ListReportingCurrenciesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListRequisitionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'issued' | 'partially_issued' | 'closed' | 'cancelled';
}

export interface ListReservationsQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
  docType?: string;
  docId?: number;
}

export interface ListRetentionRulesQuery {
  cursor?: string;
  limit?: number;
  linkType?: string;
  unruledOnly?: boolean;
}

export interface ListRetroEventsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'open' | 'applied' | 'superseded' | 'dismissed';
  employeeId?: number;
}

export interface ListRetroLinesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListReturnsQuery {
  registrationId?: number;
  status?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface ListRevRecSchedulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'active' | 'completed' | 'cancelled';
}

export interface ListRevisionsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListRootCauseCodesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListRostersQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  locationId?: number | null;
  status?: string;
  from?: string;
  to?: string;
}

export interface ListRoutingQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  itemId?: number;
  status?: string;
}

export interface ListRuleEvaluationsQuery {
  cursor?: string;
  limit?: number;
  rosterId?: number | null;
  outcome?: string;
}

export interface ListRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'executed' | 'cancelled';
}

export interface ListSLABreachForecastsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListSLAPoliciesQuery {
  cursor?: string;
  limit?: number;
  priority?: string;
}

export interface ListSSOProvidersQuery {
  cursor?: string;
  limit?: number;
}

export interface ListSSPQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  itemId?: number;
  currency?: string;
  asAt?: string;
}

export interface ListSamplingPlansQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  method?: string;
  q?: string;
}

export interface ListSavedFiltersQuery {
  reportKey?: string;
}

export interface ListScenarioPromotionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  scenarioId?: number;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

export interface ListScenariosQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListScheduleRunsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
}

export interface ListScorecardQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vendorId?: number;
}

export interface ListScorecardsQuery {
  cursor?: string;
  limit?: number;
  stage?: string;
}

export interface ListScreeningsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListServiceChargeAllocationsQuery {
  cursor?: string;
  limit?: number;
  eligibility?: 'eligible' | 'ineligible' | 'forfeited';
}

export interface ListServiceChargePointsQuery {
  cursor?: string;
  limit?: number;
  schemeId?: number;
}

export interface ListServiceChargePoolsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'collected' | 'allocated' | 'approved' | 'distributed' | 'cancelled';
  poolKey?: string;
}

export interface ListServiceChargeSchemesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListServiceContractsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  status?: string;
  activeOn?: string;
  expiringWithinDays?: number;
  q?: string;
}

export interface ListServiceGoodwillQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  workOrderId?: number;
  status?: string;
  causeCode?: string;
}

export interface ListServiceRateCardsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  activeOnly?: boolean;
  q?: string;
}

export interface ListServiceWorkOrdersQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
  kind?: string;
  priority?: string;
  assignedToUserId?: number;
  ticketId?: number;
  eamAssetId?: number;
  billability?: string;
  appointmentOn?: string;
  openOnly?: boolean;
  q?: string;
}

export interface ListSessionsQuery {
  cursor?: string;
  limit?: number;
  bankAccountId?: number;
  status?: 'open' | 'closed';
}

export interface ListSetVersionsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
}

export interface ListSettlementsQuery {
  cursor?: string;
  limit?: number;
  status?: string;
}

export interface ListShiftDefinitionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  activeOnly?: boolean;
}

export interface ListShiftPatternsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  activeOnly?: boolean;
}

export interface ListShipmentsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  orderId?: number;
  status?: 'draft' | 'posted' | 'despatched' | 'delivered' | 'reversed' | 'cancelled';
}

export interface ListShortagesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  itemId?: number;
  warehouseId?: number;
  cause?: 'no_stock' | 'expired_lot' | 'quarantined_lot' | 'reserved_elsewhere' | 'shelf_life' | 'bin_blocked' | 'in_transit';
  status?: 'open' | 'substituted' | 'backordered' | 'cancelled' | 'resolved';
  docType?: string;
  docId?: number;
}

export interface ListSignalSettingsQuery {
  companyId: number;
}

export interface ListSignalsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vendorId?: number;
  invoiceId?: number;
  signalCode?: string;
  status?: 'open' | 'cleared' | 'confirmed';
}

export interface ListSpendFactsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  vendorId?: number;
  itemId?: number;
  stage?: string;
  fromDate?: string;
  toDate?: string;
}

export interface ListStaffingRulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  locationId?: number | null;
}

export interface ListStandardCostRollupsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  itemId?: number;
  status?: string;
}

export interface ListStandardCostsQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  itemId?: number;
}

export interface ListStandingRulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  pendingApproval?: boolean;
}

export interface ListStatutoryMappingsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  ruleCode?: string;
}

export interface ListStatutoryReturnLinesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListStatutoryReturnsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  returnCode?: string;
  status?: 'draft' | 'prepared' | 'reconciled' | 'approved' | 'filed' | 'superseded';
}

export interface ListStatutoryRulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  onDate?: string;
  componentKind?: string;
}

export interface ListStockBalancesQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
  binId?: number;
  lotId?: number;
}

export interface ListStockLotsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  itemId?: number;
  status?: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
  warehouseId?: number;
  vendorId?: number;
  expiresFrom?: string;
  expiresTo?: string;
  includeDeleted?: boolean;
}

export interface ListStockSerialsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  itemId?: number;
  warehouseId?: number;
  lotId?: number;
  status?: string;
}

export interface ListStockTransfersQuery {
  cursor?: string;
  limit?: number;
  fromWarehouseId?: number;
  toWarehouseId?: number;
  status?: string;
  overdue?: boolean;
}

export interface ListStructuresQuery {
  cursor?: string;
  limit?: number;
  status?: string;
}

export interface ListSubdivisionsQuery {
  countryId?: string;
  parentId?: string;
  isActive?: boolean | null;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListSubscriptionPlansQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListSubscriptionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  status?: 'active' | 'paused' | 'cancelled' | 'expired';
}

export interface ListSubstitutesQuery {
  cursor?: string;
  limit?: number;
  itemId?: number;
  warehouseId?: number;
  includeDeleted?: boolean;
}

export interface ListSupplierContractsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vendorId?: number;
  kind?: string;
  status?: string;
  activeOn?: string;
  q?: string;
}

export interface ListSupplierInvoicesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListSupplierReturnsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  vendorId?: number;
  status?: string;
}

export interface ListSupplierRiskQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListSuppressionsQuery {
  cursor?: string;
  limit?: number;
  channelCode?: string;
  reason?: string;
  scope?: string;
  partyId?: number | null;
  includeReleased?: string;
}

export interface ListSuspenseItemsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  positionId?: number;
  status?: string;
}

export interface ListSuspensePositionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  isActive?: boolean | null;
}

export interface ListSyncBatchesQuery {
  cursor?: string;
  limit?: number;
  deviceId?: number;
  status?: string;
}

export interface ListSyncConflictsQuery {
  cursor?: string;
  limit?: number;
  deviceId?: number;
  status?: string;
  rule?: string;
}

export interface ListTariffsQuery {
  cursor?: string;
  limit?: number;
  country?: string;
  code?: string;
  isActive?: boolean | null;
}

export interface ListTasksQuery {
  phaseId?: number | null;
  cursor?: string;
  limit?: number;
}

export interface ListTaxReportingUnitsQuery {
  cursor?: string;
  limit?: number;
  psuId?: number;
  asAt?: string;
}

export interface ListTemplatesQuery {
  cursor?: string;
  limit?: number;
  country?: string;
}

export interface ListTicketsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: string;
  priority?: string;
  assignedTo?: number;
  customerId?: number;
  invoiceStatus?: string;
  openOnly?: boolean;
  q?: string;
}

export interface ListTimesheetsQuery {
  employeeUserId?: number | null;
  contractorPortalUserId?: number | null;
  projectId?: number | null;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected';
  cursor?: string;
  limit?: number;
}

export interface ListTolerancesQuery {
  base?: string;
  quote?: string;
  cursor?: string;
  limit?: number;
}

export interface ListTravelAdvancesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  employeeId?: number;
  status?: string;
}

export interface ListTravelRequestsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  employeeId?: number;
  status?: string;
}

export interface ListTreasuryPositionsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  from?: string;
  to?: string;
  includeSuperseded?: boolean;
  status?: 'draft' | 'published' | 'superseded';
}

export interface ListUnitsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  companyId?: number;
  parentId?: number | null;
  isActive?: boolean | null;
}

export interface ListUsageVarianceQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  status?: 'open' | 'closed';
}

export interface ListUsersQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  status?: string;
}

export interface ListValidationRunsQuery {
  country?: string;
}

export interface ListValueSetsQuery {
  cursor?: string;
  limit?: number;
  kind?: string;
}

export interface ListVansQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  technicianUserId?: number;
}

export interface ListVariancePoliciesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface ListVariancesQuery {
  cursor?: string;
  limit?: number;
  materialOnly?: boolean;
  unexplainedOnly?: boolean;
}

export interface ListVendorsQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  status?: 'active' | 'blocked';
}

export interface ListVerificationLinesQuery {
  cursor?: string;
  limit?: number;
}

export interface ListVerificationRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'in_progress' | 'completed' | 'cancelled';
}

export interface ListVersionsQuery {
  cursor?: string;
  limit?: number;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected' | 'locked' | 'superseded';
  kind?: 'budget' | 'forecast';
}

export interface ListWageBasesQuery {
  cursor?: string;
  limit?: number;
  code?: string;
  source?: 'tenant' | 'pack' | 'legacy';
}

export interface ListWalletCountsQuery {
  cursor?: string;
  limit?: number;
  walletId?: number;
  status?: 'draft' | 'counted' | 'posted' | 'cancelled';
}

export interface ListWalletTxnsQuery {
  cursor?: string;
  limit?: number;
  kind?: 'spend' | 'approval' | 'rejection' | 'replenish' | 'return' | 'count_variance' | 'transfer_in' | 'transfer_out' | 'reversal';
  status?: 'captured' | 'submitted' | 'approved' | 'rejected' | 'posted';
  from?: string;
  to?: string;
}

export interface ListWalletsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'active' | 'suspended' | 'in_handover' | 'dormant' | 'closing' | 'closed';
  mineOnly?: boolean;
  q?: string;
}

export interface ListWarehousesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  q?: string;
  includeSystem?: boolean;
}

export interface ListWasteReasonsQuery {
  cursor?: string;
  limit?: number;
  category?: 'spoilage' | 'preparation' | 'breakage' | 'staff_meal' | 'complimentary' | 'training' | 'expiry' | 'over_production' | 'theft';
  isActive?: boolean | null;
  q?: string;
}

export interface ListWasteRecordsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  reasonCode?: string;
  status?: 'draft' | 'pending_approval' | 'posted' | 'rejected';
  from?: string;
  to?: string;
}

export interface ListWebhookDeliveriesQuery {
  status?: string;
  cursor?: string;
  limit?: number;
}

export interface ListWebhookEndpointsQuery {
  cursor?: string;
  limit?: number;
}

export interface ListWorkCenterExceptionsQuery {
  from?: string;
  to?: string;
}

export interface ListWorkCenterLoadQuery {
  from: string;
  days?: number;
}

export interface ListWorkCentersQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
  q?: string;
}

export interface ListWorkPermitsQuery {
  cursor?: string;
  limit?: number;
  employeeId?: number;
  status?: string;
  quotaCategory?: string;
  expiringBefore?: string;
}

export interface ListWorkingTimeRulesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  ruleKind?: string;
  source?: string;
  activeOnly?: boolean;
}

export interface ListYearEndClosesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  fiscalYear?: number;
  status?: string;
}

export interface ListYearEndStatementsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  employeeId?: number;
  taxYear?: number;
}

export interface ListZonesQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  isActive?: boolean | null;
}

export interface Locality {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  countryId: string;
  subdivisionId?: string | null;
  code: string;
  name: string;
  kind: 'city' | 'town' | 'island' | 'zone' | 'free_zone' | 'district' | 'village' | 'other';
  postalCode: string;
  timezone: string;
  sortOrder: number;
  isActive: boolean;
}

export interface LocalityRequest {
  countryId: string;
  subdivisionId?: string;
  code: string;
  name: string;
  kind?: string;
  postalCode?: string;
  timezone?: string;
  sortOrder?: number;
}

export interface LocalityUpdateRequest {
  subdivisionId?: string | null;
  name?: string | null;
  kind?: string | null;
  postalCode?: string | null;
  timezone?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface Location {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  companyId?: number | null;
  type: string;
  parentLocationId?: number | null;
  country: string;
  subdivisionGeoRef?: string | null;
  localityGeoRef?: string | null;
  jurisdictionId?: number | null;
  line1: string;
  line2: string;
  line3: string;
  localityName: string;
  subdivisionName: string;
  postalCode: string;
  addressStatus: string;
  addressValidatedAt?: string | null;
  addressWaiverReasonCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  timezone: string;
  isInventoryEnabled: boolean;
  openedOn?: string | null;
  closedOn?: string | null;
  status: string;
  note: string;
}

export interface LocationTreeDTO {
  companyId?: number | null;
  locations: LocationTreeNode[];
}

export interface LocationTreeNode {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  companyId?: number | null;
  type: string;
  parentLocationId?: number | null;
  country: string;
  subdivisionGeoRef?: string | null;
  localityGeoRef?: string | null;
  jurisdictionId?: number | null;
  line1: string;
  line2: string;
  line3: string;
  localityName: string;
  subdivisionName: string;
  postalCode: string;
  addressStatus: string;
  addressValidatedAt?: string | null;
  addressWaiverReasonCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  timezone: string;
  isInventoryEnabled: boolean;
  openedOn?: string | null;
  closedOn?: string | null;
  status: string;
  note: string;
  children: LocationTreeNode[];
}

export interface LocationTreeQuery {
  companyId?: number | null;
}

export interface LockRosterRequest {
  rowVersion: number;
  note?: string;
}

export interface LogEntry {
  id: number;
  entity: string;
  entityId?: string;
  docNo?: string;
  action: 'create' | 'update' | 'delete' | 'restore' | 'status_change' | 'submit' | 'post' | 'reverse' | 'approve' | 'reject' | 'delegate' | 'login_success' | 'login_failure' | 'logout' | 'password_change' | 'mfa_change' | 'session_revoke' | 'role_change' | 'permission_change' | 'api_key_create' | 'api_key_revoke' | 'config_change' | 'export' | 'print' | 'sensitive_reveal' | 'support_session_start' | 'support_session_end' | 'assistant_tool_call';
  actorId?: string;
  actorType: string;
  at: string;
  ip?: string;
  userAgent?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  note?: string;
  hash: string;
  prevHash?: string;
}

export interface LogServiceWorkOrderLabourRequest {
  technicianUserId: number;
  visitId?: number | null;
  workDate: string;
  hours: string;
  costRate?: string;
  isOvertime?: boolean;
  description?: string;
}

export interface LoginRequest {
  tenantSlug: string;
  email: string;
  password: string;
  totpCode?: string;
  deviceName?: string;
}

export interface LoginResult {
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  user: UserDTO;
}

export interface LookupPOSInstrumentRequest {
  companyId: number;
  code: string;
}

export interface LoseOpportunityRequest {
  reason: string;
  rowVersion: number;
  closeReasonCode?: string;
  competitorCode?: string;
}

export interface LotBalanceDTO {
  warehouseId: number;
  warehouseCode?: string;
  binId?: number | null;
  qty: string;
}

export interface LotGenealogyDTO {
  id: number;
  parentLotId: number;
  parentLotNo?: string;
  parentItemId?: number;
  childLotId: number;
  childLotNo?: string;
  childItemId?: number;
  relation: 'repack' | 'split' | 'merge' | 'transform' | 'production' | 'rework';
  qtyConsumed: string;
  docType?: string;
  docId?: number | null;
  occurredOn: string;
  createdAt: string;
}

export interface LotGenealogyQuery {
  maxDepth?: number;
}

export interface LotRecallActionDTO {
  id: number;
  recallId: number;
  actionKind: 'quarantine' | 'notify_customer' | 'notify_authority' | 'return_to_vendor' | 'dispose' | 'block_sales' | 'verify_disposal';
  targetKind?: string;
  targetId?: number | null;
  targetLabel?: string;
  description?: string;
  dueOn?: string | null;
  status: 'open' | 'done' | 'waived';
  waivedReason?: string;
  completedAt?: string | null;
  completedBy?: number | null;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface LotRecallDTO {
  id: number;
  recallNo?: string | null;
  companyId: number;
  title: string;
  severity: 'class_1' | 'class_2' | 'class_3' | 'withdrawal';
  reason?: string;
  status: 'draft' | 'tracing' | 'contained' | 'closed' | 'cancelled';
  initiatedOn: string;
  containBy?: string | null;
  containedAt?: string | null;
  closedAt?: string | null;
  closedBy?: number | null;
  cancelReason?: string;
  tracedAt?: string | null;
  affectedLots: number;
  affectedQty: string;
  affectedValue: string;
  evidenceKey?: string;
  evidenceSha256?: string;
  openActions: number;
  createdAt: string;
  rowVersion: number;
}

export interface LotRecallDetailDTO {
  id: number;
  recallNo?: string | null;
  companyId: number;
  title: string;
  severity: 'class_1' | 'class_2' | 'class_3' | 'withdrawal';
  reason?: string;
  status: 'draft' | 'tracing' | 'contained' | 'closed' | 'cancelled';
  initiatedOn: string;
  containBy?: string | null;
  containedAt?: string | null;
  closedAt?: string | null;
  closedBy?: number | null;
  cancelReason?: string;
  tracedAt?: string | null;
  affectedLots: number;
  affectedQty: string;
  affectedValue: string;
  evidenceKey?: string;
  evidenceSha256?: string;
  openActions: number;
  createdAt: string;
  rowVersion: number;
  lots: LotTraceNodeDTO[];
  traces: LotRecallTraceDTO[];
  actions: LotRecallActionDTO[];
}

export interface LotRecallTraceDTO {
  id: number;
  lotId: number;
  lotNo?: string;
  itemId?: number;
  itemSku?: string;
  positionKind: 'on_hand' | 'issued' | 'transferred' | 'sold' | 'consumed' | 'wasted' | 'returned';
  warehouseId?: number | null;
  warehouseCode?: string;
  binId?: number | null;
  qty: string;
  value: string;
  docType?: string;
  docId?: number | null;
  docNo?: string;
  occurredOn?: string | null;
  counterpartyType?: string;
  counterpartyId?: number | null;
}

export interface LotTraceNodeDTO {
  lotId: number;
  itemId: number;
  itemSku?: string;
  lotNo: string;
  status: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
  depth: number;
  qtyConsumed: string;
}

export interface LotTraceResult {
  rootLotIds: number[];
  maxDepth: number;
  depthCapReached: boolean;
  nodes: LotTraceNodeDTO[];
  edges: LotGenealogyDTO[];
}

export interface LoyaltyAccrualDTO {
  id: number;
  memberId: number;
  sourceType: 'ar.invoice' | 'sales.order' | 'pos.sale' | 'pms.folio' | 'manual';
  sourceId: number;
  sourceKey: string;
  eligibleAmount: string;
  currency: string;
  rateApplied: string;
  multiplierApplied: string;
  points: number;
  accruedAt: string;
  businessOn: string;
  tierIdAtAccrual?: number | null;
}

export interface LoyaltyMemberDTO {
  id: number;
  programId: number;
  programCode?: string;
  partyId: number;
  partyName?: string;
  membershipNo: string;
  tierId?: number | null;
  tierCode?: string;
  status: 'active' | 'suspended' | 'closed';
  pointsBalance: number;
  pointsLifetime: number;
  spendRolling12: string;
  enrolledOn: string;
  tierReviewedOn?: string;
  rowVersion: number;
}

export interface LoyaltyProgramDTO {
  id: number;
  code: string;
  name: string;
  companyId?: number | null;
  pointsPerUnit: string;
  currency: string;
  rounding: 'down' | 'nearest';
  status: 'draft' | 'active' | 'closed';
  startsOn?: string;
  endsOn?: string;
  tierCount: number;
  rowVersion: number;
}

export interface LoyaltyTierDTO {
  id: number;
  programId: number;
  code: string;
  name: string;
  sortOrder: number;
  minPoints?: number | null;
  minSpend?: string;
  earnMultiplier: string;
  rowVersion: number;
}

export interface MFAConfirmRequest {
  code: string;
}

export interface MFAConfirmResponse {
  recoveryCodes: string[];
}

export interface MFADisableRequest {
  password: string;
  code: string;
}

export interface MFAEnrollResponse {
  secret: string;
  otpauthUrl: string;
}

export interface MRPExceptionDTO {
  id: number;
  runId: number;
  companyId: number;
  itemId: number;
  warehouseId?: number | null;
  kind: string;
  detail?: string;
  shortfallQuantity: string;
  needBy?: string | null;
  earliestPossible?: string | null;
}

export interface MRPPeggingDTO {
  id: number;
  suggestionId: number;
  demandKind: string;
  demandDocType?: string;
  demandDocId?: number | null;
  demandDocLineNo?: number | null;
  demandDocNo?: string;
  parentSuggestionId?: number | null;
  quantity: string;
  needBy?: string | null;
  level: number;
}

export interface MRPPeggingNodeDTO {
  suggestionId: number;
  itemId: number;
  suggestionType: string;
  suggestedQty: string;
  level: number;
  demands: MRPPeggingDTO[];
  parents?: MRPPeggingNodeDTO[];
}

export interface MRPRunDTO {
  id: number;
  companyId: number;
  runAt: string;
  horizonDays: number;
  suggestionsCreated: number;
}

export interface MRPSuggestionDTO {
  id: number;
  runId: number;
  companyId: number;
  itemId: number;
  warehouseId: number;
  suggestionType: 'production' | 'purchase';
  suggestedQty: string;
  needByDate?: string | null;
  reason: string;
  sourceDemand?: Record<string, unknown>;
  status: 'open' | 'dismissed' | 'actioned';
  createdAt: string;
}

export interface MRRQuery {
  companyId: number;
  asOf?: string;
}

export interface MRRReport {
  companyId: number;
  currency: string;
  asOf: string;
  activeSubscriptions: number;
  mrr: string;
  arr: string;
  generatedAt: string;
}

export interface MandateCandidateDTO {
  mandateId: number;
  code: string;
  name: string;
  minAmount: string;
  maxAmount?: string | null;
  currency?: string;
  applies: boolean;
  why: string;
}

export interface MandateResolutionDTO {
  bankAccountId: number;
  amount: string;
  currency: string;
  on: string;
  mandate?: BankMandateDTO | null;
  candidates: MandateCandidateDTO[];
  eligibleSignatories: BankMandateSignatoryDTO[];
  explanation: string;
}

export interface ManualLineDTO {
  id: number;
  runId: number;
  employeeId: number;
  componentId: number;
  componentCode?: string;
  amount: string;
  note: string;
}

export interface MapLedgersRequest {
  ledgers: LedgerMapInput[];
}

export interface MarginOutcomeDTO {
  status: string;
  floorPercent: string;
  minPrice: string;
  standardCost: string;
}

export interface MarketingCampaignDTO {
  id: number;
  campaignNo?: string;
  name: string;
  code: string;
  status: string;
  channelId: number;
  channelCode?: string;
  purposeId: number;
  purposeCode?: string;
  isEssential: boolean;
  companyId?: number | null;
  ownerUserId?: number | null;
  startsOn?: string;
  endsOn?: string;
  budgetAmount: string;
  targetRevenue: string;
  currency?: string;
  subject?: string;
  body?: string;
  templateKey?: string;
  locale?: string;
  attributionWindowDays: number;
  approvedAt?: string;
  approvedBy?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface MarketingCampaignDetailDTO {
  id: number;
  campaignNo?: string;
  name: string;
  code: string;
  status: string;
  channelId: number;
  channelCode?: string;
  purposeId: number;
  purposeCode?: string;
  isEssential: boolean;
  companyId?: number | null;
  ownerUserId?: number | null;
  startsOn?: string;
  endsOn?: string;
  budgetAmount: string;
  targetRevenue: string;
  currency?: string;
  subject?: string;
  body?: string;
  templateKey?: string;
  locale?: string;
  attributionWindowDays: number;
  approvedAt?: string;
  approvedBy?: number | null;
  createdAt: string;
  rowVersion: number;
  sendCount: number;
  responseCount: number;
}

export interface MarketingSegmentCriteria {
  all?: MarketingSegmentCriteriaRule[];
  none?: MarketingSegmentCriteriaRule[];
}

export interface MarketingSegmentCriteriaRule {
  field: string;
  op: string;
  value: unknown;
}

export interface MarketingSegmentDTO {
  id: number;
  code: string;
  name: string;
  description: string;
  kind: string;
  criteria: MarketingSegmentCriteria;
  lastBuiltAt?: string;
  lastBuiltCount: number;
  ownerUserId?: number | null;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface MarketingSegmentMemberDTO {
  id: number;
  segmentId: number;
  partyId: number;
  partyName?: string;
  addedByRule: string;
  excluded: boolean;
  exclusionReason?: string;
  createdAt: string;
  rowVersion: number;
}

export interface MastershipEntry {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  dataDomain: string;
  masteringModule: string;
  enforcement: string;
  ownerPositionId?: number | null;
}

export interface Match {
  minAmount?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  maxAmount?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  category?: string;
  dimension?: Record<string, string>;
}

export interface MatchCandidateDTO {
  journalLineId: number;
  journalEntryId: number;
  entryNo?: string;
  entryDate: string;
  description?: string;
  memo?: string;
  debit: string;
  credit: string;
  matchType: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  confidence: string;
  dateDistanceDays: number;
  signals: BankrecMatchSignalDTO[];
}

export interface MatchCardLineRequest {
  claimLineId: number;
  kind?: string;
}

export interface MatchCauseDTO {
  code: string;
  name: string;
  description?: string;
  ownerRoleCode?: string | null;
  ownerUserId?: number | null;
  ownerResolution: 'buyer' | 'requester' | 'warehouse_manager' | 'role' | 'user' | 'finance';
  slaHours: number;
  isAutoResolvable: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface MatchDTO {
  id: number;
  sessionId: number;
  statementLineId: number;
  journalLineId: number;
  journalEntryId: number;
  entryNo?: string;
  matchType: 'exact_reference' | 'amount_date' | 'fuzzy_name' | 'manual' | 'created';
  confidence: string;
  status: string;
  matchedAt: string;
  matchedBy?: number | null;
  releasedAt?: string | null;
  rowVersion: number;
  setId: number;
}

export interface MatchExceptionDTO {
  id: number;
  matchId: number;
  matchLineId?: number | null;
  causeCode: string;
  causeName?: string;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  poId?: number | null;
  poNo?: string | null;
  poLineId?: number | null;
  detail?: string;
  expectedValue?: string | null;
  actualValue?: string | null;
  varianceValue?: string | null;
  variancePercent?: string | null;
  ownerUserId?: number | null;
  ownerRoleCode?: string | null;
  dueAt?: string | null;
  status: 'open' | 'resolved' | 'accepted' | 'rejected';
  resolutionNote?: string;
  resolvedBy?: number | null;
  resolvedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface MatchLineDTO {
  id: number;
  invoiceLineId?: number | null;
  poLineId: number;
  grnLineId?: number | null;
  invoiceQty: string;
  poQty: string;
  grnQty: string;
  invoicePrice: string;
  poPrice: string;
  qtyVariance: string;
  qtyVariancePercent: string;
  priceVariance: string;
  priceVariancePercent: string;
  withinTolerance: boolean;
  matchedQuantity: string;
  matchedAmount: string;
}

export interface MatchPMSCommissionStatementLineRequest {
  accrualId: number;
  rowVersion: number;
}

export interface MatchTransactionRequest {
  counterpartyTransactionId: number;
}

export interface MaterialIssueOverride {
  reasonCode: string;
  note?: string;
}

export interface MaterialSerialDTO {
  id: number;
  productionOrderId: number;
  materialId: number;
  itemId: number;
  serialNo: string;
  stockLotId?: number | null;
  stockLedgerEntryId?: number | null;
  issuedOn: string;
}

export interface MaterialSubstitutionDTO {
  id: number;
  productionOrderId: number;
  materialId: number;
  bomAlternateId?: number | null;
  originalItemId: number;
  substituteItemId: number;
  quantityOriginal: string;
  quantitySubstitute: string;
  conversionFactor: string;
  reasonCode?: string;
  reason?: string;
  originalStandardCost?: string | null;
  substituteActualValue: string;
  costImpact: string;
  issuedOn: string;
  createdAt: string;
}

export interface MaterialityPolicyDTO {
  id: number;
  docType: string;
  companyId?: number | null;
  pctThreshold?: string;
  absThreshold?: string;
  currency?: string;
  materialFields: string[];
  bandCrossingIsMaterial: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface MaterialityRequest {
  docType: string;
  companyId?: number | null;
  pctThreshold?: string;
  absThreshold?: string;
  currency?: string;
  materialFields?: string[];
  bandCrossingIsMaterial?: boolean | null;
  isActive?: boolean | null;
}

export interface MatrixValidation {
  versionId: number;
  ok: boolean;
  problems: MatrixValidationRow[];
}

export interface MatrixValidationRow {
  severity: string;
  kind: string;
  ruleId?: number | null;
  stepNo?: number | null;
  docType?: string;
  detail: string;
}

export interface MatrixVersionDTO {
  id: number;
  code: string;
  name: string;
  status: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  activatedBy?: number | null;
  activatedAt?: string | null;
  note?: string;
  ruleCount: number;
  rowVersion: number;
}

export interface MatrixVersionDetailDTO {
  id: number;
  code: string;
  name: string;
  status: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  activatedBy?: number | null;
  activatedAt?: string | null;
  note?: string;
  ruleCount: number;
  rowVersion: number;
  rules: AuthorityRuleDTO[];
}

export interface MaverickSpendQuery {
  companyId: number;
  stage?: string;
  fromDate?: string;
  toDate?: string;
}

export interface MaverickSpendRowDTO {
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  contractStatus: 'on_contract' | 'off_contract' | 'no_contract' | 'contract_alternate';
  availableContractId: number;
  availableContractNo?: string;
  amount: string;
  priceVarianceVsContract: string;
  factCount: number;
}

export interface MeResponse {
  user: UserDTO;
  permissions: string[];
  roleCodes: string[];
  rolesHash: string;
}

export interface MeasureRVMDisplacementOutcomesRequest {
  propertyId?: number;
}

export interface MemberDTO {
  id: number;
  projectId: number;
  userId: number;
  role: string;
  costRate: string;
  billRate: string;
  currency: string;
  isActive: boolean;
  rowVersion: number;
}

export interface MergeFieldChoiceInput {
  fieldName: string;
  chosenFrom: string;
  chosenValue?: string | null;
}

export interface MergeLeadRequest {
  survivingLeadId: number;
  rowVersion: number;
}

export interface MergeLotsRequest {
  parentLotIds: number[];
  childLotNo: string;
  occurredOn: string;
  docType?: string;
  docId?: number | null;
}

export interface MessageDTO {
  id: number;
  role: string;
  content: string;
  toolCalls: ToolCallRecord[];
  citations: Citation[];
  model?: string;
  inputTokens: number;
  outputTokens: number;
  createdAt: string;
}

export interface MetricCertificationDTO {
  id: number;
  metricCode: string;
  companyId: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  status: string;
  certifiedAt: string;
  certifiedBy: number;
  certifiedByName?: string;
  certifiedPositionId?: number | null;
  basis: string;
  certifiedContentHash: string;
  isStale: boolean;
  revokedAt?: string | null;
  revokedBy?: number | null;
  revocationReasonCode?: string;
  revocationNote?: string;
  rowVersion: number;
}

export interface MetricDefinitionDTO {
  code: string;
  module: string;
  labelKey: string;
  descriptionKey?: string;
  formulaKey: string;
  sourceKind: 'module_function' | 'read_model' | 'ledger' | 'derived';
  sourceRef?: string;
  grain: string;
  unit: string;
  dashboard?: string;
  permissionCode: string;
  refreshMinutes: number;
  effectiveRefreshMinutes: number;
  isCertifiable: boolean;
  isOrphaned: boolean;
  isPublished: boolean;
  ownerPositionId?: number | null;
  ownerPositionName?: string;
  note?: string;
  contentHash: string;
  certificationState: 'certified' | 'ad_hoc' | 'mixed';
  certificationId?: number | null;
  isStale: boolean;
  rowVersion: number;
}

export interface MetricDefinitionDetailDTO {
  definition: MetricDefinitionDTO;
  owners: MetricOwnerDTO[];
  certifications: MetricCertificationDTO[];
  recentOutputs: ReportOutputDTO[];
}

export interface MetricDetailQuery {
  companyId: number;
}

export interface MetricOwnerDTO {
  id: number;
  metricCode: string;
  positionId: number;
  positionName?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  note?: string;
  createdAt: string;
}

export interface MetricSyncResultDTO {
  metrics: number;
  orphaned: number;
}

export interface MfgCostingAccountsDTO {
  companyId: number;
  yieldVarianceAccountId?: number | null;
  overheadSpendVarianceAccountId?: number | null;
  overheadVolumeVarianceAccountId?: number | null;
  byProductCreditAccountId?: number | null;
  rowVersion: number;
}

export interface MfgSettingsDTO {
  companyId: number;
  wipAccountId?: number | null;
  appliedLaborAccountId?: number | null;
  appliedOverheadAccountId?: number | null;
  scrapAccountId?: number | null;
  materialPriceVarianceAccountId?: number | null;
  materialUsageVarianceAccountId?: number | null;
  laborRateVarianceAccountId?: number | null;
  laborEfficiencyVarianceAccountId?: number | null;
  overheadVarianceAccountId?: number | null;
  rowVersion: number;
}

export interface MfgToolDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  status: string;
  workCenterId?: number | null;
  locationId?: number | null;
  lifeCycles?: number | null;
  cyclesUsed: number;
  nextServiceOn?: string | null;
  notes?: string;
  rowVersion: number;
}

export interface MilestoneDTO {
  id: number;
  projectId: number;
  phaseId?: number | null;
  taskId?: number | null;
  name: string;
  sequence: number;
  amount: string;
  dueDate?: string;
  status: 'pending' | 'ready' | 'invoiced' | 'cancelled';
  achievedAt?: string | null;
  invoicedAt?: string | null;
  arInvoiceId?: number | null;
  rowVersion: number;
}

export interface MismatchReport {
  runId: number;
  generatedAt: string;
  items: EliminationDTO[];
}

export interface MissingEntryRequest {
  sessionId: number;
  offsetAccountId: number;
  description?: string;
}

export interface MovePMSRoomRequest {
  toRoomId: number;
  reasonCode: string;
  note?: string;
  repriceRemainingNights?: boolean;
  rowVersion: number;
}

export interface MovementReport {
  companyId: number;
  from: string;
  to: string;
  rows: MovementRow[];
  totalOpeningCost: string;
  totalOpeningAccumDep: string;
  totalAdditionsCost: string;
  totalDepreciationCharge: string;
  totalDisposalsCost: string;
  totalDisposalsAccumDep: string;
  totalClosingCost: string;
  totalClosingAccumDep: string;
  totalClosingNbv: string;
  totalOpeningAccumImpairment: string;
  totalImpairmentCharge: string;
  totalDisposalsAccumImpairment: string;
  totalClosingAccumImpairment: string;
  reconciliations: ControlReconciliation[];
  generatedAt: string;
}

export interface MovementReportQuery {
  companyId: number;
  from: string;
  to: string;
  classId?: number;
}

export interface MovementRow {
  classId: number;
  classCode: string;
  className: string;
  openingCost: string;
  openingAccumDep: string;
  additionsCost: string;
  depreciationCharge: string;
  disposalsCost: string;
  disposalsAccumDep: string;
  closingCost: string;
  closingAccumDep: string;
  openingAccumImpairment: string;
  impairmentCharge: string;
  disposalsAccumImpairment: string;
  closingAccumImpairment: string;
  closingNbv: string;
}

export interface MyClockStateDTO {
  today: ClockEventDTO[];
  openKind?: string;
}

export interface MyRosterDTO {
  employeeId: number;
  from: string;
  to: string;
  lines: RosterLineDTO[];
}

export interface MyRosterQuery {
  from?: string;
  to?: string;
}

export interface MyServiceChargeAllocationDTO {
  poolId: number;
  poolNo: string;
  companyId: number;
  poolKey: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  basis: 'points' | 'hours';
  weight: string;
  rawShare: string;
  roundingCents: number;
  amount: string;
  distributable: string;
  totalWeight: string;
  payslipId?: number | null;
  payslipLineId?: number | null;
}

export interface NCRDTO {
  id: number;
  productionOrderId: number;
  productionOrderOperationId?: number | null;
  itemId: number;
  quantity: string;
  reason: string;
  status: 'open' | 'closed';
  disposition?: 'rework' | 'scrap' | 'use_as_is';
  resolutionNote?: string;
  createdAt: string;
  closedAt?: string | null;
}

export interface NamingTemplate {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  ScopeType: string;
  ScopeID: number;
  ObjectType: string;
  AttributeSequence: string[];
  Separator: string;
  IsActive: boolean;
}

export interface NamingTemplateRequest {
  scopeType: string;
  scopeId: number;
  objectType: string;
  attributeSequence: string[];
  separator?: string;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface NodeChange {
  orgType: string;
  orgId: number;
  fromNodeId?: number | null;
  toNodeId?: number | null;
  fromParent?: string;
  toParent?: string;
  fromLevel?: number | null;
  toLevel?: number | null;
}

export interface NodeOperation {
  op: string;
  nodeId: number | null;
  orgType: string;
  orgId: number;
  parentNodeId: number | null;
  clearParent: boolean;
  sortOrder: number | null;
  effectiveFrom: string;
  effectiveTo: string;
  clearEffectiveTo: boolean;
}

export interface NoteRequest {
  note?: string;
}

export interface NotificationDTO {
  id: number;
  kind: string;
  title: string;
  body: string;
  link?: string;
  entity?: string;
  entityId?: number;
  severity: string;
  readAt?: string | null;
  createdAt: string;
}

export interface NotificationPolicyDTO {
  id: number;
  eventKind: string;
  channels: string;
  digest: string;
  escalateAfterHours: number;
  suppressDuplicatesForMinutes: number;
  isActive: boolean;
  rowVersion: number;
}

export interface ObligationDTO {
  id: number;
  companyId: number;
  customerId: number;
  contractId?: number | null;
  sourceDocType: string;
  sourceDocId: number;
  sourceLineNo: number;
  itemId?: number | null;
  description?: string;
  method: 'point_in_time' | 'over_time' | 'milestone';
  transactionPrice: string;
  sspAmount: string;
  sspMethod: 'observable' | 'adjusted_market' | 'expected_cost_plus' | 'residual';
  allocatedAmount: string;
  recognisedAmount: string;
  currency: string;
  startDate: string;
  endDate?: string | null;
  satisfiedAt?: string | null;
  deferredAccountId: number;
  revenueAccountId: number;
  status: 'pending' | 'in_progress' | 'satisfied' | 'cancelled';
  rowVersion: number;
}

export interface ObligationDetailDTO {
  id: number;
  companyId: number;
  customerId: number;
  contractId?: number | null;
  sourceDocType: string;
  sourceDocId: number;
  sourceLineNo: number;
  itemId?: number | null;
  description?: string;
  method: 'point_in_time' | 'over_time' | 'milestone';
  transactionPrice: string;
  sspAmount: string;
  sspMethod: 'observable' | 'adjusted_market' | 'expected_cost_plus' | 'residual';
  allocatedAmount: string;
  recognisedAmount: string;
  currency: string;
  startDate: string;
  endDate?: string | null;
  satisfiedAt?: string | null;
  deferredAccountId: number;
  revenueAccountId: number;
  status: 'pending' | 'in_progress' | 'satisfied' | 'cancelled';
  rowVersion: number;
  milestones?: ObligationMilestoneDTO[];
  allocation?: RevRecAllocationDTO | null;
  schedule?: RevRecScheduleDTO | null;
}

export interface ObligationMilestoneDTO {
  id: number;
  seq: number;
  name: string;
  dueOn?: string | null;
  percent: string;
  amount: string;
  certifiedAt?: string | null;
  certifiedBy?: number | null;
  journalEntryId?: number | null;
  rowVersion: number;
}

export interface OfferDTO {
  id: number;
  offerNo: string;
  candidateId: number;
  requisitionId?: number | null;
  proposedTitle: string;
  proposedSalary: string;
  currency: string;
  startDate: string;
  status: string;
  sentAt?: string | null;
  decidedAt?: string | null;
  rowVersion: number;
}

export interface OfferPMSWaitlistEntryRequest {
  offerMinutes?: number;
  rowVersion: number;
}

export interface OfferPositionDTO {
  id: number;
  gradeId?: number | null;
  positionId?: number | null;
  payGroupId?: number | null;
  rowVersion: number;
}

export interface OfflinePolicyInput {
  controlCode: string;
  policy: string;
  limitAmount?: string;
  limitPercent?: string;
  limitCount?: number | null;
  reasonSet?: string;
}

export interface OpcalDeleteQuery {
  rowVersion?: number;
}

export interface OpcalImportResult {
  imported: number;
  skipped: number;
  errors?: string[];
}

export interface OpcalListCalendarsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number | null;
  isActive?: boolean | null;
}

export interface OpenCloseRunRequest {
  companyId: number;
  fiscalPeriodId: number;
  calendarId?: number;
  note?: string;
}

export interface OpenCustodyTransferRequest {
  toCustodianUserId?: number | null;
  toCustodianEmployeeId?: number | null;
  effectiveOn?: string;
  note?: string;
}

export interface OpenDayRequest {
  unitType: 'company' | 'branch' | 'location';
  unitId: number;
  businessDate: string;
}

export interface OpenItemsQuery {
  companyId?: number;
  amount?: string;
  reference?: string;
}

export interface OpenPOSCashSessionRequest {
  outletId: number;
  terminalId: number;
  operatorUserId?: number | null;
  openingFloat: string;
  businessDate?: string;
}

export interface OpenPOSCheckRequest {
  outletId: number;
  tableId?: number | null;
  covers?: number;
  customerId?: number | null;
  checkNo?: string | null;
  note?: string;
}

export interface OpenRFQRequest {
  witnessUserId: number;
}

export interface OpenServiceVisitRequest {
  technicianUserId: number;
  travelStartedAt?: string | null;
  arrivedAt?: string | null;
  occurredAt?: string | null;
  returnForVisitId?: number | null;
}

export interface OpenWalletCountRequest {
  kind?: 'periodic' | 'handover' | 'surprise' | 'closing';
  countDate?: string;
  isBlind?: boolean | null;
}

export interface OperationTimeLogDTO {
  id: number;
  productionOrderOperationId: number;
  startedAt: string;
  endedAt: string;
  laborMinutes: string;
  machineMinutes: string;
  actualLaborRate: string;
  actualOverheadRate: string;
  laborCost: string;
  overheadCost: string;
  notes?: string;
}

export interface OpportunityCompetitorDTO {
  id: number;
  opportunityId: number;
  competitorId: number;
  competitorCode: string;
  competitorName: string;
  isWinner: boolean;
  note: string;
  rowVersion: number;
}

export interface OpportunityDTO {
  id: number;
  leadId?: number | null;
  customerId?: number | null;
  companyId?: number | null;
  name: string;
  stageId: number;
  stageCode: string;
  stageName: string;
  amount: string;
  currency: string;
  probabilityPercent: string;
  weightedAmount: string;
  expectedCloseDate?: string | null;
  ownerUserId?: number | null;
  ownerName?: string;
  source: string;
  isOpen: boolean;
  wonAt?: string | null;
  lostAt?: string | null;
  lostReason?: string;
  forecastCategoryId?: number | null;
  closeReasonId?: number | null;
  accountId?: number | null;
  primaryContactId?: number | null;
  campaignId?: number | null;
  nextStep?: string;
  nextStepDueOn?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface OpportunityStageDTO {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  probabilityPercent: string;
  isWon: boolean;
  isLost: boolean;
}

export interface OrderAmendmentDTO {
  id: number;
  orderId: number;
  amendmentNo: number;
  reasonCode: string;
  note?: string;
  priorTotal: string;
  newTotal: string;
  currency: string;
  changedFields?: string;
  isMaterial: boolean;
  materialReason?: string;
  voidedRequestId?: number | null;
  newRequestId?: number | null;
  approvalRequestId?: number | null;
  repricedLines: number;
  creditRecheck?: string;
  atpRecheck?: string;
  amendedAt: string;
  amendedBy?: number | null;
}

export interface OrderDTO {
  id: number;
  orderNo?: string | null;
  companyId: number;
  customerId: number;
  customerName?: string;
  quoteId?: number | null;
  currency: string;
  fxRate: string;
  orderDate: string;
  requestedDate?: string | null;
  status: 'draft' | 'pending_approval' | 'confirmed' | 'partially_shipped' | 'shipped' | 'invoiced' | 'closed' | 'cancelled';
  subtotal: string;
  discountTotal: string;
  total: string;
  memo: string;
  reference: string;
  channel?: string;
  supplyType?: string;
  dayPartCode?: string;
  priceBasis: string;
  locationId?: number | null;
  creditOverrideReason?: string;
  discountOverrideReason?: string;
  cancellationReason?: string;
  approvalRequestId?: number | null;
  approvalStatus?: string;
  approvalNote?: string;
  discountPolicyId?: number | null;
  discountAuthoritySource?: string;
  discountAuthorityPositionId?: number | null;
  discountAmountTotal?: string;
  backorderPermitted: boolean;
  promisedDate?: string | null;
  promiseSource?: string;
  promiseCount: number;
  amendmentNo: number;
  backorders?: BackorderDTO[];
  createdAt: string;
  rowVersion: number;
  lines?: OrderLineDTO[];
}

export interface OrderLineDTO {
  id: number;
  lineNo: number;
  itemId: number;
  warehouseId: number;
  description: string;
  uom: string;
  quantity: string;
  listPrice: string;
  unitPrice: string;
  discountPercent: string;
  discountAmount: string;
  promotionId?: number | null;
  priceRule?: string;
  priceExplanation?: string;
  priceResolutionLogId?: number | null;
  lineAmount: string;
  taxCode?: string | null;
  itemTaxGroupId?: number | null;
  shippedQuantity: string;
  invoicedQuantity: string;
  reservedQuantity: string;
  status: string;
  revRecTermMonths?: number | null;
  revenueAccountId?: number | null;
  orderedQuantity: string;
  backorderedQuantity: string;
  promisedDate?: string | null;
}

export interface OrderLineInput {
  itemId: number;
  warehouseId: number;
  description?: string;
  uom?: string;
  quantity: string;
  discountPercent?: string;
  unitPrice?: string;
  taxCode?: string;
  revRecTermMonths?: number | null;
  revenueAccountId: number;
}

export interface OverIssueCheckDTO {
  materialId: number;
  itemId: number;
  quantityRequired: string;
  quantityBefore: string;
  thisIssue: string;
  proposedTotal: string;
  excessPercent: string;
  warnPercent: string;
  approvePercent: string;
  band: string;
}

export interface OverrideBreakdown {
  key: string;
  count: number;
}

export interface OverrideDTO {
  id: number;
  ruleCode: string;
  companyId?: number | null;
  docType: string;
  docId: number;
  docNo?: string;
  amount?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency?: string;
  reasonCode: string;
  note?: string;
  detail?: string;
  overriddenBy: number;
  overriddenAt: string;
  authorisedBy?: number | null;
  authoritySource: string;
}

export interface OverrideExportQuery {
  format: string;
  companyId?: number | null;
  from?: string;
  to?: string;
}

export interface OverrideReport {
  from: string;
  to: string;
  total: number;
  byRule: OverrideBreakdown[];
  byReason: OverrideBreakdown[];
  byActor: OverrideBreakdown[];
}

export interface OverrideReportQuery {
  companyId?: number | null;
  from: string;
  to: string;
}

export interface OvertimeRuleDTO {
  id: number;
  code: string;
  name: string;
  dailyThresholdHours: string;
  rateMultiplier: string;
  isActive: boolean;
  rowVersion: number;
}

export interface OwnershipDTO {
  id: number;
  parentCompanyId: number;
  childCompanyId: number;
  holdingPct: string;
  holdingType: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface PMRunResult {
  generated: number;
  skipped: number;
}

export interface PMSAllotmentDTO {
  id: number;
  groupBlockId: number;
  roomTypeId: number;
  stayDate: string;
  contracted: number;
  pickedUp: number;
  released: number;
  remaining: number;
  unitPrice?: string | null;
  rowVersion: number;
}

export interface PMSAllotmentInput {
  roomTypeId: number;
  stayDate: string;
  contracted: number;
  unitPrice?: string;
}

export interface PMSAmendmentDTO {
  reservationId: number;
  previousArrival: string;
  previousDeparture: string;
  previousRoomTypeId: number;
  previousRoomsCount: number;
  previousGross: string;
  nightsReleased: number;
  nightsCommitted: number;
  reservation: PMSReservationDetailDTO;
}

export interface PMSAuthHoldDTO {
  id: number;
  folioId: number;
  reservationId?: number | null;
  amount: string;
  currency: string;
  provider: string;
  providerReference: string;
  approvalCode: string;
  cardScheme: string;
  cardLast4: string;
  status: 'held' | 'captured' | 'released' | 'expired' | 'failed';
  heldAt: string;
  expiresAt?: string | null;
  releasedAt?: string | null;
  rowVersion: number;
}

export interface PMSAvailabilityCalendarDTO {
  propertyId: number;
  from: string;
  to: string;
  days: PMSAvailabilityDayDTO[];
}

export interface PMSAvailabilityDayDTO {
  propertyId: number;
  roomTypeId: number;
  stayDate: string;
  physicalRooms: number;
  outOfOrder: number;
  committed: number;
  blocked: number;
  overbooking: number;
  available: number;
}

export interface PMSBoardAllowanceDTO {
  id: number;
  reservationId: number;
  stayDate: string;
  mealPeriod: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  entitlementId: number;
  grantedCovers: string;
  drawnCovers: string;
  remainingCovers: string;
  grantedValue: string;
  drawnValue: string;
  remainingValue: string;
  rowVersion: number;
}

export interface PMSBoardEntitlementDTO {
  id: number;
  boardPlanId: number;
  mealPeriod: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  coversPerPersonPerNight: string;
  transferPrice: string;
  targetRevenueCenterId?: number | null;
  allowanceCap?: string | null;
  rowVersion: number;
}

export interface PMSBoardEntitlementInput {
  mealPeriod: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  coversPerPersonPerNight?: string;
  transferPrice: string;
  targetRevenueCenterId?: number | null;
  allowanceCap?: string;
}

export interface PMSBoardPlanDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  status: 'draft' | 'active' | 'closed';
  entitlements: PMSBoardEntitlementDTO[];
  rowVersion: number;
}

export interface PMSCancellationOutcomeDTO {
  policyId: number;
  policyCode: string;
  chargeKind: string;
  tierHoursBeforeArrival: number;
  hoursBeforeArrival: string;
  chargeAmount: string;
  currency: string;
  tiers: PMSCancellationTierDTO[];
}

export interface PMSCancellationPolicyDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  tiers: PMSCancellationTierDTO[];
  noShowChargeKind: 'charge_first_night' | 'charge_full_stay' | 'no_charge';
  rowVersion: number;
}

export interface PMSCancellationTierDTO {
  hoursBeforeArrival: number;
  chargeKind: string;
}

export interface PMSChannelDTO {
  id: number;
  companyId: number;
  propertyId?: number | null;
  code: string;
  name: string;
  kind: 'ota' | 'wholesaler' | 'tour_operator' | 'direct' | 'gds' | 'corporate';
  adapter: string;
  vendorId?: number | null;
  customerId?: number | null;
  defaultSourceCode?: string;
  defaultSegmentCode?: string;
  defaultMarketCode?: string;
  parityReferencePlanId?: number | null;
  parityTolerancePercent: string;
  source: string;
  status: 'active' | 'suspended';
  rowVersion: number;
}

export interface PMSChannelExchangeDTO {
  id: number;
  channelId: number;
  direction: 'push' | 'pull';
  payloadKind: 'rates' | 'restrictions' | 'availability' | 'bookings' | 'commission';
  adapter: string;
  attachmentId?: number | null;
  rowCount: number;
  acceptedCount: number;
  rejectedCount: number;
  contentHash?: string;
  status: 'pending' | 'complete' | 'failed';
  detail?: string;
  createdAt: string;
}

export interface PMSCheckInDTO {
  reservation: PMSReservationDetailDTO;
  roomId: number;
  roomNumber: string;
  folioId: number;
  folioNo: string;
  registrationCard: PMSRegistrationCardDTO;
  authHold?: PMSAuthHoldDTO | null;
  keyIssue?: PMSKeyIssueDTO | null;
}

export interface PMSCheckOutDTO {
  reservation: PMSReservationDetailDTO;
  roomId?: number | null;
  roomNumber: string;
  folioId?: number | null;
  folioNo: string;
  folioBalance: string;
  keysCancelled: number;
  holdsReleased: number;
}

export interface PMSCommissionAccrualDTO {
  id: number;
  companyId: number;
  propertyId: number;
  reservationId: number;
  channelId: number;
  agreementVersionId: number;
  businessDate: string;
  basisAmount: string;
  percent: string;
  flatAmount: string;
  accruedAmount: string;
  currency: string;
  journalEntryId?: number | null;
  status: 'accrued' | 'matched' | 'disputed' | 'settled' | 'reversed';
  matchedLineId?: number | null;
  varianceAmount: string;
  rowVersion: number;
}

export interface PMSCommissionAgreementDTO {
  id: number;
  companyId: number;
  channelId: number;
  code: string;
  name: string;
  status: 'draft' | 'active' | 'closed';
  rowVersion: number;
}

export interface PMSCommissionMatchDTO {
  statementLineId?: number;
  accrualId: number;
  reservationId: number;
  reservationNo: string;
  guestName?: string;
  accruedAmount: string;
  matchType: string;
  confidence: string;
  dateDistanceDays: number;
  varianceAmount: string;
}

export interface PMSCommissionStatementDTO {
  id: number;
  companyId: number;
  channelId: number;
  statementNo: string;
  periodFrom: string;
  periodTo: string;
  currency: string;
  declaredTotal: string;
  matchedTotal: string;
  filename?: string;
  status: 'imported' | 'matching' | 'reconciled' | 'disputed' | 'closed';
  reconciledAt?: string | null;
  reconciledBy?: number | null;
  lines?: PMSCommissionStatementLineDTO[];
  rowVersion: number;
}

export interface PMSCommissionStatementLineDTO {
  id: number;
  statementId: number;
  lineNo: number;
  channelReference: string;
  guestName?: string;
  arrivalDate?: string | null;
  departureDate?: string | null;
  roomRevenue: string;
  commissionAmount: string;
  currency: string;
  status: 'unmatched' | 'matched' | 'disputed' | 'written_off';
  accrualId?: number | null;
  matchConfidence: string;
  matchType?: string;
  varianceAmount: string;
  disputeReason?: string;
  writeOffReason?: string;
  rowVersion: number;
}

export interface PMSCommissionStatementLineInput {
  channelReference: string;
  guestName?: string;
  arrivalDate?: string;
  departureDate?: string;
  roomRevenue?: string;
  commissionAmount: string;
}

export interface PMSCommissionVersionDTO {
  id: number;
  agreementId: number;
  versionNo: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  percentBasis: 'room_net' | 'room_gross' | 'stay_net';
  percent: string;
  flatPerBooking: string;
  flatPerNight: string;
  currency: string;
  settlementMode: 'invoiced' | 'net_remittance';
  status: 'draft' | 'published' | 'superseded';
  publishedAt?: string | null;
  publishedBy?: number | null;
  rowVersion: number;
}

export interface PMSDayPackDTO {
  runId: number;
  runNo: string;
  propertyId: number;
  businessDate: string;
  hash: string;
  parts: PMSDayPackPartDTO[];
  attachmentId?: number | null;
}

export interface PMSDayPackPartDTO {
  name: string;
  contentType: string;
  rowCount: number;
  bytes: number;
  sha256: string;
}

export interface PMSDepositDTO {
  id: number;
  companyId: number;
  propertyId: number;
  depositNo: string;
  reservationId?: number | null;
  folioId?: number | null;
  guestId?: number | null;
  customerId?: number | null;
  amount: string;
  appliedAmount: string;
  refundedAmount: string;
  remaining: string;
  currency: string;
  method: 'cash' | 'card' | 'bank_transfer';
  bankAccountId?: number | null;
  reference: string;
  receivedOn: string;
  journalEntryId?: number | null;
  status: 'held' | 'applied' | 'refunded' | 'forfeited';
  rowVersion: number;
}

export interface PMSDeviceBoardResponse {
  board?: PMSHousekeepingBoardDTO | null;
  propertyId: number;
  businessDate: string;
  rooms: PMSDeviceRoomDTO[];
  tasks: PMSDeviceTaskDTO[];
  faultCategories: string[];
  faultSeverities: string[];
}

export interface PMSDeviceFaultReasonsResponse {
  categories: string[];
  severities: string[];
}

export interface PMSDeviceOperatorQuery {
  operatorUserId: number;
}

export interface PMSDeviceReportFaultRequest {
  operatorUserId: number;
  roomId: number;
  category: 'plumbing' | 'electrical' | 'hvac' | 'furniture' | 'appliance' | 'structural' | 'safety' | 'other';
  severity?: 'minor' | 'major' | 'blocking';
  description?: string;
  attachmentId?: number | null;
}

export interface PMSDeviceRoomDTO {
  roomId: number;
  number: string;
  roomTypeId: number;
  sectionCode: string;
  housekeepingStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  occupancyStatus: 'vacant' | 'occupied';
}

export interface PMSDeviceRoomsResponse {
  rooms: PMSDeviceRoomDTO[];
}

export interface PMSDeviceSetRoomStatusRequest {
  operatorUserId: number;
  roomId: number;
  toStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  note?: string;
}

export interface PMSDeviceSummaryResponse {
  boardId?: number | null;
  assignedCredit: string;
  completedCredit: string;
  remainingCredit: string;
  tasksTotal: number;
  tasksDone: number;
}

export interface PMSDeviceTaskDTO {
  taskId: number;
  roomId: number;
  roomNumber: string;
  taskKind: 'departure' | 'stayover' | 'turndown' | 'deep_clean' | 'inspection';
  credit: string;
  priority: number;
  status: 'pending' | 'in_progress' | 'done' | 'skipped';
}

export interface PMSFolioChargeDTO {
  id: number;
  folioId: number;
  windowId: number;
  windowNo: number;
  seqNo: number;
  businessDate: string;
  postedAt: string;
  kind: 'room' | 'tax' | 'service_charge' | 'levy' | 'ancillary' | 'pos' | 'deposit' | 'payment' | 'allowance' | 'transfer_in' | 'transfer_out' | 'package_draw' | 'package_relief';
  revenueCenterId?: number | null;
  description: string;
  quantity: string;
  unitPrice: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  currency: string;
  taxCode: string;
  itemTaxGroupId?: number | null;
  postingSource: 'pms' | 'pos' | 'channel' | 'import';
  sourceDocType: string;
  sourceDocId?: number | null;
  reservationNightId?: number | null;
  nightAuditRunId?: number | null;
  status: 'posted' | 'reversed' | 'reversal';
  reversesChargeId?: number | null;
  reversedByChargeId?: number | null;
  reversalReason: string;
  rowVersion: number;
}

export interface PMSFolioDTO {
  id: number;
  companyId: number;
  propertyId: number;
  folioNo: string;
  reservationId?: number | null;
  guestId?: number | null;
  kind: 'guest' | 'group_master' | 'house' | 'walk_in';
  currency: string;
  status: 'open' | 'settled' | 'transferred' | 'disputed';
  balance: string;
  openedAt: string;
  settledAt?: string | null;
  settledBy?: number | null;
  invoiceNo?: string | null;
  note: string;
  rowVersion: number;
}

export interface PMSFolioDetailDTO {
  id: number;
  companyId: number;
  propertyId: number;
  folioNo: string;
  reservationId?: number | null;
  guestId?: number | null;
  kind: 'guest' | 'group_master' | 'house' | 'walk_in';
  currency: string;
  status: 'open' | 'settled' | 'transferred' | 'disputed';
  balance: string;
  openedAt: string;
  settledAt?: string | null;
  settledBy?: number | null;
  invoiceNo?: string | null;
  note: string;
  rowVersion: number;
  windows: PMSFolioWindowDTO[];
  charges: PMSFolioChargeDTO[];
  settlements: PMSFolioSettlementDTO[];
  routingRules: PMSFolioRoutingRuleDTO[];
  deposits: PMSDepositDTO[];
}

export interface PMSFolioRoutingRuleDTO {
  id: number;
  folioId: number;
  seqNo: number;
  chargeKind: 'room' | 'tax' | 'service_charge' | 'levy' | 'ancillary' | 'pos' | 'deposit' | 'payment' | 'allowance' | 'transfer_in' | 'transfer_out' | 'package_draw' | 'package_relief';
  revenueCenterId?: number | null;
  outletId?: number | null;
  targetWindowNo: number;
  capAmount?: string | null;
  consumedAmount: string;
  rowVersion: number;
}

export interface PMSFolioRoutingRuleInput {
  seqNo?: number;
  chargeKind?: 'room' | 'tax' | 'service_charge' | 'levy' | 'ancillary' | 'pos' | 'deposit' | 'payment' | 'allowance' | 'transfer_in' | 'transfer_out' | 'package_draw' | 'package_relief';
  revenueCenterId?: number | null;
  outletId?: number | null;
  targetWindowNo: number;
  capAmount?: string;
}

export interface PMSFolioSettlementDTO {
  id: number;
  folioId: number;
  windowId: number;
  windowNo: number;
  seqNo: number;
  businessDate: string;
  method: 'cash' | 'card' | 'bank_transfer' | 'deposit_applied' | 'city_ledger' | 'allowance' | 'transfer_to_folio';
  amount: string;
  currency: string;
  bankAccountId?: number | null;
  glAccountId?: number | null;
  arInvoiceId?: number | null;
  targetFolioId?: number | null;
  depositId?: number | null;
  allowanceReason: string;
  reference: string;
  journalEntryId?: number | null;
  status: 'posted' | 'reversed' | 'reversal';
  rowVersion: number;
}

export interface PMSFolioSettlementInput {
  windowNo?: number;
  method: 'cash' | 'card' | 'bank_transfer' | 'deposit_applied' | 'city_ledger' | 'allowance' | 'transfer_to_folio';
  amount?: string;
  bankAccountId?: number | null;
  depositId?: number | null;
  reference?: string;
}

export interface PMSFolioWindowDTO {
  id: number;
  folioId: number;
  windowNo: number;
  name: string;
  customerId?: number | null;
  guestId?: number | null;
  isPackage: boolean;
  chargedTotal: string;
  creditedTotal: string;
  balance: string;
  status: 'open' | 'settled' | 'transferred';
  rowVersion: number;
}

export interface PMSFrontDeskBoardDTO {
  propertyId: number;
  kind: 'arrivals' | 'departures' | 'in_house';
  businessDate: string;
  rows: PMSFrontDeskRowDTO[];
}

export interface PMSFrontDeskRowDTO {
  reservationId: number;
  reservationNo?: string | null;
  guestId?: number | null;
  guestName: string;
  guestRestricted: boolean;
  arrivalDate: string;
  departureDate: string;
  nights: number;
  adults: number;
  children: number;
  roomTypeId: number;
  roomTypeCode: string;
  roomId?: number | null;
  roomNumber?: string | null;
  housekeepingStatus?: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  occupancyStatus?: 'vacant' | 'occupied';
  status: 'quote' | 'held' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | 'waitlisted';
  folioId?: number | null;
  folioNo: string;
  folioBalance: string;
  currency: string;
}

export interface PMSGroupBlockDTO {
  id: number;
  companyId: number;
  propertyId: number;
  blockNo?: string | null;
  name: string;
  customerId?: number | null;
  ratePlanId?: number | null;
  arrivalDate: string;
  departureDate: string;
  cutoffDays: number;
  cutoffAppliedAt?: string | null;
  attritionPercent: string;
  status: 'tentative' | 'definite' | 'cancelled' | 'closed';
  note: string;
  rowVersion: number;
}

export interface PMSGroupBlockDetailDTO {
  id: number;
  companyId: number;
  propertyId: number;
  blockNo?: string | null;
  name: string;
  customerId?: number | null;
  ratePlanId?: number | null;
  arrivalDate: string;
  departureDate: string;
  cutoffDays: number;
  cutoffAppliedAt?: string | null;
  attritionPercent: string;
  status: 'tentative' | 'definite' | 'cancelled' | 'closed';
  note: string;
  rowVersion: number;
  allotments: PMSAllotmentDTO[];
  totalContracted: number;
  totalPickedUp: number;
  totalReleased: number;
  allowedShortfall: string;
  attritionRooms: string;
}

export interface PMSGuestDTO {
  id: number;
  companyId: number;
  code: string;
  givenName: string;
  familyName: string;
  displayName: string;
  title?: string;
  email?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressRegion?: string;
  addressPostcode?: string;
  addressCountry?: string;
  documentKind?: '' | 'passport' | 'national_id' | 'driving_licence';
  documentCountry?: string;
  documentLast4?: string;
  documentExpiresOn?: string;
  hasDocument: boolean;
  vipCode?: string;
  segmentCode?: string;
  language?: string;
  isRestricted: boolean;
  restrictedReason?: 'non_payment' | 'property_damage' | 'abusive_behaviour' | 'security_concern' | 'fraud' | 'health_and_safety' | 'other';
  restrictedNote?: string;
  restrictedAt?: string | null;
  notes?: string;
  partyId?: number;
  rowVersion: number;
}

export interface PMSGuestDetailDTO {
  id: number;
  companyId: number;
  code: string;
  givenName: string;
  familyName: string;
  displayName: string;
  title?: string;
  email?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressRegion?: string;
  addressPostcode?: string;
  addressCountry?: string;
  documentKind?: '' | 'passport' | 'national_id' | 'driving_licence';
  documentCountry?: string;
  documentLast4?: string;
  documentExpiresOn?: string;
  hasDocument: boolean;
  vipCode?: string;
  segmentCode?: string;
  language?: string;
  isRestricted: boolean;
  restrictedReason?: 'non_payment' | 'property_damage' | 'abusive_behaviour' | 'security_concern' | 'fraud' | 'health_and_safety' | 'other';
  restrictedNote?: string;
  restrictedAt?: string | null;
  notes?: string;
  partyId?: number;
  rowVersion: number;
  preferences: PMSGuestPreferenceDTO[];
}

export interface PMSGuestDocumentExtractionDTO {
  extractionId: number;
  guestId: number;
  overallConfidence: number;
  fields: PMSGuestExtractedFieldDTO[];
}

export interface PMSGuestDocumentRevealDTO {
  guestId: number;
  documentKind: '' | 'passport' | 'national_id' | 'driving_licence';
  documentNumber: string;
  documentCountry?: string;
  documentExpiresOn?: string;
  revealedAt: string;
}

export interface PMSGuestExtractedFieldDTO {
  name: string;
  value: string;
  confidence: number;
}

export interface PMSGuestMessageDTO {
  id: number;
  companyId: number;
  guestId: number;
  reservationId?: number | null;
  channel: 'email' | 'sms' | 'in_app' | 'print';
  purpose: 'confirmation' | 'pre_arrival' | 'in_stay' | 'departure' | 'marketing';
  subject?: string;
  body?: string;
  status: 'pending' | 'rendered' | 'sent' | 'suppressed' | 'failed';
  suppressedReason?: string;
  consentDecision?: Record<string, unknown>;
  sentAt?: string | null;
  rowVersion: number;
}

export interface PMSGuestPreferenceDTO {
  id: number;
  guestId: number;
  category: 'room' | 'housekeeping' | 'dining' | 'transport' | 'other';
  code: string;
  value?: string;
  note?: string;
  rowVersion: number;
}

export interface PMSGuestRequestDTO {
  id: number;
  propertyId: number;
  reservationId?: number | null;
  guestId?: number | null;
  roomId?: number | null;
  requestNo: string;
  kind: 'request' | 'complaint';
  category: string;
  causeCode?: string;
  severity: 'low' | 'medium' | 'high';
  description?: string;
  status: 'open' | 'assigned' | 'resolved' | 'escalated' | 'closed';
  assignedTo?: number | null;
  resolvedAt?: string | null;
  resolutionNote?: string;
  recoveryKind: 'none' | 'allowance' | 'comp_service' | 'points' | 'gift';
  recoveryValue: string;
  recoveryChargeId?: number | null;
  recoveryApprovedBy?: number | null;
  rowVersion: number;
}

export interface PMSHousekeepingBoardDTO {
  id: number;
  propertyId: number;
  businessDate: string;
  employeeId: number;
  sectionCode: string;
  creditTarget: string;
  assignedCredit: string;
  completedCredit: string;
  status: 'planned' | 'released' | 'in_progress' | 'complete';
  releasedAt?: string | null;
  releasedBy?: number | null;
  rowVersion: number;
}

export interface PMSHousekeepingBoardRebalanceResultDTO {
  from: PMSHousekeepingBoardDTO;
  to: PMSHousekeepingBoardDTO;
}

export interface PMSHousekeepingTaskDTO {
  id: number;
  propertyId: number;
  businessDate: string;
  roomId: number;
  boardId?: number | null;
  taskKind: 'departure' | 'stayover' | 'turndown' | 'deep_clean' | 'inspection';
  credit: string;
  priority: number;
  status: 'pending' | 'in_progress' | 'done' | 'skipped';
  targetRoomStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  startedAt?: string | null;
  completedAt?: string | null;
  completedBy?: number | null;
  skippedReason?: string;
  note?: string;
  deviceId?: number | null;
  rowVersion: number;
}

export interface PMSInHouseRowDTO {
  reservationId: number;
  reservationNo?: string;
  roomId?: number | null;
  roomNumber?: string;
  roomTypeCode: string;
  guestId?: number | null;
  arrivalDate: string;
  departureDate: string;
  adults: number;
  children: number;
  folioId?: number | null;
  folioBalance: string;
  nightPosted: boolean;
}

export interface PMSItineraryConflictDTO {
  firstBookingId: number;
  secondBookingId: number;
  detail: string;
}

export interface PMSItineraryDTO {
  reservationId: number;
  bookings: PMSServiceBookingDTO[];
  conflicts: PMSItineraryConflictDTO[];
}

export interface PMSKeyIssueDTO {
  id: number;
  reservationId: number;
  roomId: number;
  roomNumber: string;
  guestId?: number | null;
  validFrom: string;
  validTo: string;
  kind: 'guest' | 'duplicate' | 'staff' | 'master';
  issuer: string;
  status: 'issued' | 'cancelled' | 'expired';
  cancelledAt?: string | null;
  cancelledBy?: number | null;
  createdAt: string;
  createdBy?: number | null;
}

export interface PMSLedgerRowDTO {
  folioId: number;
  folioNo: string;
  reservationId?: number | null;
  openingBalance: string;
  charges: string;
  settlements: string;
  closingBalance: string;
  status: string;
}

export interface PMSManifestDTO {
  legId: number;
  legNo: string;
  bookedPax: number;
  rows: PMSManifestRowDTO[];
}

export interface PMSManifestRowDTO {
  reservationId: number;
  guestName: string;
  pax: number;
  partyKey: string;
  status: string;
}

export interface PMSNightAuditCheckDTO {
  key: string;
  blocking: boolean;
  passed: boolean;
  count: number;
  messageKey: string;
  detail?: Record<string, unknown>;
}

export interface PMSNightAuditLineDTO {
  seqNo: number;
  kind: 'guest_ledger' | 'revenue' | 'tax' | 'service_charge' | 'levy' | 'package_ledger' | 'package_relief' | 'allowance' | 'rounding';
  revenueCenterId?: number | null;
  taxCode?: string;
  accountId: number;
  debit: string;
  credit: string;
  description?: string;
}

export interface PMSNightAuditReadinessDTO {
  propertyId: number;
  businessDate: string;
  ready: boolean;
  blockingCount: number;
  checks: PMSNightAuditCheckDTO[];
}

export interface PMSNightAuditReadinessQuery {
  propertyId: number;
  date?: string;
}

export interface PMSNightAuditRunDTO {
  id: number;
  companyId: number;
  propertyId: number;
  runNo: string;
  businessDate: string;
  nextBusinessDate: string;
  businessDayId?: number | null;
  status: 'pending' | 'running' | 'blocked' | 'completed' | 'abandoned' | 'reversed';
  currentStep?: string;
  completedSteps: string[];
  startedAt?: string;
  lastHeartbeatAt?: string;
  completedAt?: string;
  abandonedAt?: string;
  abandonedReason?: string;
  forced: boolean;
  forcedReason?: string;
  roomsInHouse: number;
  roomsPosted: number;
  noShowsProcessed: number;
  chargesPosted: number;
  revenueNet: string;
  revenueTax: string;
  revenueGross: string;
  journalEntryId?: number | null;
  reversalEntryId?: number | null;
  dayPackHash?: string;
  rowVersion: number;
}

export interface PMSNightAuditRunDetailDTO {
  id: number;
  companyId: number;
  propertyId: number;
  runNo: string;
  businessDate: string;
  nextBusinessDate: string;
  businessDayId?: number | null;
  status: 'pending' | 'running' | 'blocked' | 'completed' | 'abandoned' | 'reversed';
  currentStep?: string;
  completedSteps: string[];
  startedAt?: string;
  lastHeartbeatAt?: string;
  completedAt?: string;
  abandonedAt?: string;
  abandonedReason?: string;
  forced: boolean;
  forcedReason?: string;
  roomsInHouse: number;
  roomsPosted: number;
  noShowsProcessed: number;
  chargesPosted: number;
  revenueNet: string;
  revenueTax: string;
  revenueGross: string;
  journalEntryId?: number | null;
  reversalEntryId?: number | null;
  dayPackHash?: string;
  rowVersion: number;
  steps: PMSNightAuditStepDTO[];
  lines: PMSNightAuditLineDTO[];
  readiness: PMSNightAuditReadinessDTO;
}

export interface PMSNightAuditStepDTO {
  step: string;
  seqNo: number;
  status: 'pending' | 'running' | 'done' | 'skipped' | 'failed';
  attemptCount: number;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  result?: Record<string, unknown>;
  error?: string;
}

export interface PMSOccupancyReportDTO {
  propertyId: number;
  businessDate: string;
  currency: string;
  roomsAvailable: number;
  roomsOccupied: number;
  roomsOutOfOrder: number;
  arrivals: number;
  departures: number;
  noShows: number;
  stayovers: number;
  occupancyPercent: string;
  roomRevenue: string;
  adr: string;
  revpar: string;
  rows: PMSOccupancyRowDTO[];
}

export interface PMSOccupancyRowDTO {
  roomTypeId: number;
  roomTypeCode: string;
  roomsAvailable: number;
  roomsOccupied: number;
  roomsOutOfOrder: number;
  arrivals: number;
  departures: number;
  noShows: number;
  stayovers: number;
  occupancyPercent: string;
  adr: string;
  revpar: string;
}

export interface PMSOutOfOrderDTO {
  id: number;
  roomId: number;
  kind: 'out_of_order' | 'out_of_service';
  fromDate: string;
  toDate: string;
  reasonCode: string;
  note: string;
  source: string;
  sourceRef?: string;
  releasedAt?: string | null;
  releasedBy?: number | null;
  rowVersion: number;
}

export interface PMSParityObservationDTO {
  id: number;
  channelId: number;
  propertyId: number;
  roomTypeId: number;
  stayDate: string;
  observedOn: string;
  observedAmount: string;
  referenceAmount: string;
  deviationPercent: string;
  currency: string;
  breached: boolean;
  source: 'manual' | 'import';
  createdAt: string;
}

export interface PMSPropertyDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  locationId: number;
  branchId?: number | null;
  dayUnitType: string;
  dayUnitId: number;
  kind: 'resort' | 'hotel' | 'guesthouse' | 'vessel' | 'villa';
  taxChannel: string;
  accommodationItemClass: string;
  currency: string;
  checkInTime: string;
  checkOutTime: string;
  overbookingRooms: number;
  status: 'active' | 'suspended';
  rowVersion: number;
}

export interface PMSQuoteConsiderationDTO {
  seq: number;
  source: string;
  sourceCode?: string;
  outcome: string;
  unitPrice?: string | null;
  reasonKey: string;
  reasonArgs?: Record<string, string>;
}

export interface PMSQuoteDTO {
  propertyId: number;
  roomTypeId: number;
  ratePlanId: number;
  arrival: string;
  departure: string;
  nights: number;
  roomsCount: number;
  adults: number;
  children: number;
  currency: string;
  sellable: boolean;
  quotedNet: string;
  quotedTax: string;
  quotedGross: string;
  nightDetail: PMSQuoteNightDTO[];
}

export interface PMSQuoteLevyDTO {
  taxCode: string;
  name: string;
  currency: string;
  amount: string;
  units: string;
}

export interface PMSQuoteNightDTO {
  stayDate: string;
  outcome: string;
  available: number;
  rateGross: string;
  packageAmount: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  unitPrice: string;
  currency: string;
  levies: PMSQuoteLevyDTO[];
  reasonKey?: string;
  reasonArgs?: Record<string, string>;
  waterfall: PMSQuoteConsiderationDTO[];
}

export interface PMSRateDerivationDTO {
  id: number;
  ratePlanId: number;
  parentPlanId: number;
  deltaAmount: string;
  deltaPercent: string;
  roundingIncrement: string;
  floorAmount?: string | null;
  ceilingAmount?: string | null;
  rowVersion: number;
}

export interface PMSRatePlanDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  description: string;
  currency: string;
  priceBasis?: string;
  boardPlanId?: number | null;
  cancellationPolicyId?: number | null;
  guaranteeKind: 'none' | 'token' | 'deposit' | 'company' | 'travel_agent';
  minNights: number;
  maxNights?: number | null;
  minAdvanceDays: number;
  maxAdvanceDays?: number | null;
  channelCodes: string[];
  segmentCodes: string[];
  commissionable: boolean;
  status: 'draft' | 'active' | 'closed';
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface PMSRatePlanRateDTO {
  id: number;
  ratePlanId: number;
  roomTypeId: number;
  stayDate: string;
  occupancy: number;
  amount: string;
  currency: string;
  extraAdult: string;
  extraChild: string;
  source: 'manual' | 'import' | 'channel' | 'derived';
  rowVersion: number;
}

export interface PMSRatePlanRateInput {
  roomTypeId: number;
  stayDate: string;
  occupancy?: number;
  amount: string;
  extraAdult?: string;
  extraChild?: string;
}

export interface PMSRateRestrictionDTO {
  id: number;
  propertyId: number;
  roomTypeId?: number | null;
  ratePlanId?: number | null;
  stayDate: string;
  closed: boolean;
  closedToArrival: boolean;
  closedToDeparture: boolean;
  minLengthOfStay?: number | null;
  maxLengthOfStay?: number | null;
  source: 'manual' | 'import' | 'channel';
  rowVersion: number;
}

export interface PMSRateRestrictionInput {
  roomTypeId?: number | null;
  ratePlanId?: number | null;
  stayDate: string;
  closed?: boolean;
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
  minLengthOfStay?: number | null;
  maxLengthOfStay?: number | null;
}

export interface PMSRegistrationCardDTO {
  id: number;
  reservationId: number;
  guestId: number;
  checkedInAt: string;
  checkedInBy?: number | null;
  attachmentId?: number | null;
  extraction: Record<string, unknown>;
  confirmedFields: string[];
  signatureCaptured: boolean;
}

export interface PMSReportQuery {
  propertyId: number;
  date?: string;
  format?: string;
}

export interface PMSReservationDTO {
  id: number;
  companyId: number;
  propertyId: number;
  reservationNo?: string | null;
  guestId?: number | null;
  customerId?: number | null;
  groupBlockId?: number | null;
  arrivalDate: string;
  departureDate: string;
  nights: number;
  roomTypeId: number;
  ratePlanId: number;
  roomsCount: number;
  adults: number;
  children: number;
  roomId?: number | null;
  currency: string;
  quotedNet: string;
  quotedTax: string;
  quotedGross: string;
  status: 'quote' | 'held' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | 'waitlisted';
  guaranteeKind: 'none' | 'token' | 'deposit' | 'company' | 'travel_agent';
  guaranteeReference: string;
  channelReference: string;
  sourceCode: string;
  segmentCode: string;
  marketCode: string;
  heldUntil?: string | null;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason: string;
  note: string;
  rowVersion: number;
}

export interface PMSReservationDetailDTO {
  id: number;
  companyId: number;
  propertyId: number;
  reservationNo?: string | null;
  guestId?: number | null;
  customerId?: number | null;
  groupBlockId?: number | null;
  arrivalDate: string;
  departureDate: string;
  nights: number;
  roomTypeId: number;
  ratePlanId: number;
  roomsCount: number;
  adults: number;
  children: number;
  roomId?: number | null;
  currency: string;
  quotedNet: string;
  quotedTax: string;
  quotedGross: string;
  status: 'quote' | 'held' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | 'waitlisted';
  guaranteeKind: 'none' | 'token' | 'deposit' | 'company' | 'travel_agent';
  guaranteeReference: string;
  channelReference: string;
  sourceCode: string;
  segmentCode: string;
  marketCode: string;
  heldUntil?: string | null;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason: string;
  note: string;
  rowVersion: number;
  nightDetail: PMSReservationNightDTO[];
  cancellationSnapshot: PMSCancellationTierDTO[];
  cancellation?: PMSCancellationOutcomeDTO | null;
}

export interface PMSReservationNightDTO {
  id: number;
  stayDate: string;
  roomTypeId: number;
  ratePlanId: number;
  roomId?: number | null;
  unitPrice: string;
  currency: string;
  rateGross: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  packageAmount: string;
  priceResolutionLogId?: number | null;
  rateOverridden: boolean;
  rateOverrideReason: string;
  postedAt?: string | null;
  postedChargeId?: number | null;
}

export interface PMSRevenueCenterDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  kind: 'rooms' | 'fnb' | 'spa' | 'activities' | 'transfers' | 'minibar' | 'other';
  revenueAccountId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  rowVersion: number;
}

export interface PMSRevenueReportDTO {
  propertyId: number;
  businessDate: string;
  currency: string;
  runId?: number | null;
  runStatus?: string;
  totalDebit: string;
  totalCredit: string;
  rows: PMSRevenueRowDTO[];
}

export interface PMSRevenueRowDTO {
  kind: 'guest_ledger' | 'revenue' | 'tax' | 'service_charge' | 'levy' | 'package_ledger' | 'package_relief' | 'allowance' | 'rounding';
  revenueCenterId?: number | null;
  revenueCenterCode?: string;
  taxCode?: string;
  accountId: number;
  debit: string;
  credit: string;
}

export interface PMSRoomConnectionDTO {
  roomAId: number;
  roomBId: number;
}

export interface PMSRoomDTO {
  id: number;
  propertyId: number;
  roomTypeId: number;
  number: string;
  name: string;
  floor: string;
  wing: string;
  features: string;
  housekeepingStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  occupancyStatus: 'vacant' | 'occupied';
  sectionCode: string;
  status: 'active' | 'retired';
  rowVersion: number;
}

export interface PMSRoomFaultDTO {
  id: number;
  propertyId: number;
  roomId: number;
  reportedOn: string;
  category: 'plumbing' | 'electrical' | 'hvac' | 'furniture' | 'appliance' | 'structural' | 'safety' | 'other';
  severity: 'minor' | 'major' | 'blocking';
  description: string;
  attachmentId?: number | null;
  status: 'open' | 'triaged' | 'in_repair' | 'resolved' | 'rejected';
  outOfOrderId?: number | null;
  serviceRef?: string;
  deviceId?: number | null;
  resolvedAt?: string | null;
  resolvedBy?: number | null;
  resolutionNote?: string;
  requiresGuestMove?: boolean;
  rowVersion: number;
}

export interface PMSRoomMoveDTO {
  id: number;
  reservationId: number;
  fromRoomId: number;
  fromRoomNumber: string;
  toRoomId: number;
  toRoomNumber: string;
  movedOn: string;
  reasonCode: string;
  note: string;
  rateChanged: boolean;
  nightsRepriced: number;
  nightsLeftAlone: number;
  createdAt: string;
  createdBy?: number | null;
}

export interface PMSRoomStatusEventDTO {
  id: number;
  roomId: number;
  businessDate: string;
  fromStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  toStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  taskId?: number | null;
  deviceId?: number | null;
  occurredAt: string;
  recordedAt: string;
}

export interface PMSRoomStatusResultDTO {
  room: PMSRoomDTO;
  event: PMSRoomStatusEventDTO;
  task?: PMSHousekeepingTaskDTO | null;
}

export interface PMSRoomTypeDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  description: string;
  baseOccupancy: number;
  maxOccupancy: number;
  maxAdults: number;
  maxChildren: number;
  itemId: number;
  revenueCenterId?: number | null;
  overbookingRooms?: number | null;
  cleanCreditDeparture: string;
  cleanCreditStayover: string;
  sortOrder: number;
  rowVersion: number;
}

export interface PMSRoomingListDTO {
  id: number;
  groupBlockId: number;
  filename: string;
  status: 'draft' | 'validated' | 'applied' | 'failed';
  rowCount: number;
  acceptedCount: number;
  rejectedCount: number;
  appliedAt?: string | null;
  rows: PMSRoomingListRowDTO[];
}

export interface PMSRoomingListRowDTO {
  id: number;
  lineNo: number;
  givenName: string;
  familyName: string;
  email: string;
  roomTypeCode: string;
  arrivalDate?: string | null;
  departureDate?: string | null;
  adults: number;
  children: number;
  outcome: 'pending' | 'accepted' | 'rejected';
  reason: string;
  reservationId?: number | null;
  guestId?: number | null;
}

export interface PMSRoomingListRowInput {
  givenName?: string;
  familyName?: string;
  email?: string;
  roomTypeCode?: string;
  arrival?: string;
  departure?: string;
  adults?: number;
  children?: number;
}

export interface PMSServiceAllocationDTO {
  id: number;
  resourceId: number;
  fromAt: string;
  toAt: string;
  units: number;
}

export interface PMSServiceBookingDTO {
  id: number;
  propertyId: number;
  serviceId: number;
  reservationId?: number | null;
  folioId?: number | null;
  guestId?: number | null;
  bookingNo: string;
  scheduledAt: string;
  scheduledDate: string;
  durationMinutes: number;
  pax: number;
  quantity: string;
  unitPrice: string;
  currency: string;
  status: 'booked' | 'confirmed' | 'delivered' | 'cancelled' | 'no_show';
  folioChargeId?: number | null;
  boardDrawdownId?: number | null;
  allocations?: PMSServiceAllocationDTO[];
  note?: string;
  rowVersion: number;
}

export interface PMSServiceDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  description: string;
  itemId: number;
  revenueCenterId?: number | null;
  kind: 'activity' | 'spa' | 'dining' | 'transfer' | 'rental' | 'other';
  durationMinutes: number;
  capacityPerSlot: number;
  leadTimeHours: number;
  cancelWindowHours: number;
  mealPeriod?: string;
  requirements: PMSServiceRequirementDTO[];
  dependencies: PMSServiceDependencyDTO[];
  rowVersion: number;
}

export interface PMSServiceDependencyDTO {
  dependsOnServiceId: number;
  kind: 'requires' | 'excludes';
}

export interface PMSServiceRequirementDTO {
  resourceId: number;
  units: number;
}

export interface PMSServiceResourceDTO {
  id: number;
  propertyId: number;
  code: string;
  name: string;
  kind: 'staff' | 'equipment' | 'vehicle' | 'room';
  capacity: number;
  employeeId?: number | null;
  rowVersion: number;
}

export interface PMSSettingsDTO {
  companyId: number;
  guestLedgerAccountId?: number | null;
  depositLiabilityAccountId?: number | null;
  packageLedgerAccountId?: number | null;
  allowanceAccountId?: number | null;
  commissionExpenseAccountId?: number | null;
  commissionPayableAccountId?: number | null;
  defaultRevenueAccountId?: number | null;
  cashRoundingAccountId?: number | null;
  noShowPolicy: 'charge_first_night' | 'charge_full_stay' | 'no_charge';
  auditResumeWindowHours: number;
  rowVersion: number;
}

export interface PMSTransferBookingDTO {
  id: number;
  legId: number;
  reservationId: number;
  pax: number;
  partyKey: string;
  splitReason?: string;
  serviceBookingId?: number | null;
  status: 'booked' | 'boarded' | 'no_show' | 'cancelled';
}

export interface PMSTransferBookingInput {
  reservationId: number;
  pax?: number;
  partyKey?: string;
}

export interface PMSTransferLegDTO {
  id: number;
  propertyId: number;
  legNo: string;
  direction: 'arrival' | 'departure' | 'internal';
  departAt: string;
  arriveAt: string;
  origin?: string;
  destination?: string;
  resourceId?: number | null;
  capacityPax: number;
  bookedPax: number;
  status: 'planned' | 'confirmed' | 'departed' | 'completed' | 'cancelled';
  rowVersion: number;
}

export interface PMSWaitlistEntryDTO {
  id: number;
  propertyId: number;
  guestId?: number | null;
  roomTypeId: number;
  ratePlanId?: number | null;
  arrivalDate: string;
  departureDate: string;
  roomsCount: number;
  priority: number;
  status: 'waiting' | 'offered' | 'converted' | 'expired' | 'withdrawn';
  offeredAt?: string | null;
  offerExpiresAt?: string | null;
  reservationId?: number | null;
  note: string;
  rowVersion: number;
}

export interface PMScheduleDTO {
  id: number;
  name: string;
  customerAssetId: number;
  companyId: number;
  frequencyDays: number;
  nextDueDate: string;
  lastGeneratedDate?: string;
  subjectTemplate: string;
  category: string;
  priority: string;
  slaPolicyId?: number | null;
  assignedTo?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface POAmendmentDTO {
  id: number;
  poId: number;
  version: number;
  reason: string;
  totalBefore: string;
  totalAfter: string;
  crossesAuthorityBand: boolean;
  approvalRequestId?: number | null;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'applied';
  lines?: POAmendmentLineDTO[];
  submittedAt?: string | null;
  decidedAt?: string | null;
  appliedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface POAmendmentLineDTO {
  id: number;
  lineNo: number;
  poLineId?: number | null;
  changeKind: 'add' | 'update' | 'cancel';
  fromQuantity?: string | null;
  toQuantity?: string | null;
  fromUnitPrice?: string | null;
  toUnitPrice?: string | null;
  fromDescription?: string;
  toDescription?: string;
}

export interface PODRequest {
  deliveredOn: string;
  podSignedBy?: string;
  podAttachmentId?: number | null;
  podNote?: string;
}

export interface PODTO {
  id: number;
  poNo?: string | null;
  version: number;
  prId?: number | null;
  rfqId?: number | null;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  companyId: number;
  currency: string;
  fxRate: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'issued' | 'confirmed' | 'partially_received' | 'received' | 'closed' | 'cancelled';
  orderDate: string;
  expectedDate?: string | null;
  subtotal: string;
  taxTotal: string;
  total: string;
  baseTotal: string;
  memo: string;
  amendmentReason?: string;
  approvalRequestId?: number | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  issuedAt?: string | null;
  confirmedAt?: string | null;
  closedAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  rowVersion: number;
  lines?: POLineDTO[];
}

export interface PODeliveryScheduleDTO {
  id?: number;
  poLineId: number;
  seqNo: number;
  scheduledDate: string;
  quantity: string;
  receivedQuantity: string;
  confirmedDate?: string | null;
  shipToWarehouseId?: number | null;
  notes?: string;
  status: 'open' | 'partially_received' | 'received' | 'cancelled';
  rowVersion: number;
}

export interface POLineDTO {
  id: number;
  lineNo: number;
  prLineId?: number | null;
  itemId?: number | null;
  description: string;
  uom: string;
  quantity: string;
  unitPrice: string;
  lineAmount: string;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  warehouseId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  projectId?: number | null;
  projectTaskId?: number | null;
  taxCode?: string | null;
  receivedQuantity: string;
  matchedQuantity: string;
  matchedAmount: string;
  status: string;
  grIrRemaining: string;
}

export interface POLineInput {
  prLineId?: number | null;
  itemId?: number | null;
  description?: string;
  uom?: string;
  quantity: string;
  unitPrice: string;
  accountId: number;
  warehouseId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  projectId?: number | null;
  projectTaskId?: number | null;
  taxCode?: string;
}

export interface POPriceConsiderationDTO {
  seq: number;
  source: string;
  sourceId?: number | null;
  sourceCode?: string;
  outcome: string;
  unitPrice?: string | null;
  minQty?: string | null;
  reasonKey: string;
  reasonArgs?: Record<string, string>;
}

export interface POPriceResultDTO {
  companyId: number;
  vendorId: number;
  itemId: number;
  quantity: string;
  currency: string;
  unitPrice: string;
  listPrice: string;
  priceBasis: string;
  source: string;
  sourceId?: number | null;
  sourceCode?: string;
  contractLineId?: number | null;
  contractId?: number | null;
  marginOutcome: string;
  precedenceVersionId: number;
  precedenceDefaulted: boolean;
  fingerprint: string;
  waterfall: POPriceConsiderationDTO[];
}

export interface POSAgeVerificationDTO {
  id: number;
  saleId: number;
  lineId?: number | null;
  minimumAge: number;
  method: string;
  verifiedBy: number;
  verifiedAt: string;
  refused: boolean;
  note?: string;
}

export interface POSAllergenCodeDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  source: string;
  rowVersion: number;
}

export interface POSAnomalyExceptionDTO {
  id: number;
  outletId: number;
  operatorUserId: number;
  businessDate: string;
  code: string;
  observedValue: string;
  thresholdValue: string;
  detail?: Record<string, unknown>;
  status: 'open' | 'investigating' | 'resolved' | 'dismissed';
  ownerUserId?: number | null;
  dueAt?: string | null;
  resolution?: string;
  resolutionNote?: string;
  resolvedBy?: number | null;
  resolvedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface POSCashCountLineDTO {
  id: number;
  denominationId?: number | null;
  faceValue: string;
  unitCount: number;
  lineTotal: string;
}

export interface POSCashCountLineInput {
  denominationId?: number | null;
  faceValue: string;
  unitCount: number;
}

export interface POSCashDenominationDTO {
  id: number;
  companyId: number;
  currency: string;
  faceValue: string;
  kind: 'note' | 'coin';
  sortOrder: number;
  isActive: boolean;
  source: string;
  rowVersion: number;
}

export interface POSCashMovementDTO {
  id: number;
  sessionId: number;
  seqNo: number;
  kind: 'float_in' | 'float_out' | 'drop' | 'paid_in' | 'paid_out' | 'no_sale';
  amount: string;
  counterAccountId?: number | null;
  reasonCode?: string;
  note?: string;
  witnessedBy?: number | null;
  occurredAt: string;
}

export interface POSCashSessionDTO {
  id: number;
  outletId: number;
  terminalId: number;
  businessDate: string;
  businessDayId?: number | null;
  sessionNo?: string | null;
  operatorUserId: number;
  openedAt: string;
  openedBy?: number | null;
  openingFloat: string;
  status: 'open' | 'counting' | 'closed' | 'reconciled';
  closedAt?: string | null;
  closedBy?: number | null;
  countedBy?: number | null;
  witnessedBy?: number | null;
  countedAt?: string | null;
  varianceReasonCode?: string;
  varianceNote?: string;
  createdAt: string;
  rowVersion: number;
}

export interface POSCashSessionDetailDTO {
  id: number;
  outletId: number;
  terminalId: number;
  businessDate: string;
  businessDayId?: number | null;
  sessionNo?: string | null;
  operatorUserId: number;
  openedAt: string;
  openedBy?: number | null;
  openingFloat: string;
  status: 'open' | 'counting' | 'closed' | 'reconciled';
  closedAt?: string | null;
  closedBy?: number | null;
  countedBy?: number | null;
  witnessedBy?: number | null;
  countedAt?: string | null;
  varianceReasonCode?: string;
  varianceNote?: string;
  createdAt: string;
  rowVersion: number;
  countedBalance?: string | null;
  expectedBalance?: string | null;
  variance?: string | null;
  movements: POSCashMovementDTO[];
  countLines: POSCashCountLineDTO[];
}

export interface POSCheckDTO {
  id: number;
  outletId: number;
  checkNo?: string | null;
  tableId?: number | null;
  covers: number;
  businessDate: string;
  businessDayId?: number | null;
  openedAt: string;
  openedBy: number;
  serverUserId?: number | null;
  status: 'open' | 'settling' | 'settled' | 'transferred' | 'voided';
  settledAt?: string | null;
  transferredToCheckId?: number | null;
  customerId?: number | null;
  declaredAllergens: string[];
  note?: string;
  rowVersion: number;
}

export interface POSCheckDetailDTO {
  id: number;
  outletId: number;
  checkNo?: string | null;
  tableId?: number | null;
  covers: number;
  businessDate: string;
  businessDayId?: number | null;
  openedAt: string;
  openedBy: number;
  serverUserId?: number | null;
  status: 'open' | 'settling' | 'settled' | 'transferred' | 'voided';
  settledAt?: string | null;
  transferredToCheckId?: number | null;
  customerId?: number | null;
  declaredAllergens: string[];
  note?: string;
  rowVersion: number;
  lines: POSCheckLineDTO[];
}

export interface POSCheckLineDTO {
  id: number;
  checkId: number;
  lineNo: number;
  parentLineId?: number | null;
  itemId: number;
  menuItemId?: number | null;
  description: string;
  uom: string;
  quantity: string;
  unitPrice: string;
  grossAmount: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  itemClass?: string;
  revenueCenterId?: number | null;
  seatNo?: number | null;
  courseNo?: number | null;
  stationId?: number | null;
  state: 'pending' | 'fired' | 'prepared' | 'served' | 'voided';
  voidReasonCode?: string;
  settledSaleId?: number | null;
  settledSaleLineId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface POSCheckSplitResultDTO {
  kind: string;
  checks?: POSCheckDetailDTO[];
  shares?: string[];
}

export interface POSCustomerDepositDTO {
  id: number;
  outletId: number;
  customerId: number;
  depositNo?: string | null;
  businessDate: string;
  amount: string;
  applied: string;
  refunded: string;
  remaining: string;
  currency: string;
  takenBySaleId?: number | null;
  status: 'open' | 'applied' | 'refunded' | 'expired';
  expiresOn?: string | null;
  note?: string;
  rowVersion: number;
}

export interface POSDayBoardDTO {
  companyId: number;
  businessDate: string;
  outlets: POSDayBoardRowDTO[];
}

export interface POSDayBoardQuery {
  companyId: number;
  businessDate?: string;
}

export interface POSDayBoardRowDTO {
  outletId: number;
  outletCode: string;
  outletName: string;
  dayStatus: string;
  closeStatus?: 'draft' | 'posted' | 'reversed';
  closeId?: number | null;
  unpostedSales: number;
  unpostedGross: string;
  blockingCount: number;
  blockers: POSDayCloseBlockerDTO[];
}

export interface POSDayCloseBlockerDTO {
  kind: string;
  id: number;
  ref: string;
  status: string;
  ruleCode: string;
}

export interface POSDayCloseDTO {
  id: number;
  outletId: number;
  businessDate: string;
  businessDayId: number;
  closeNo?: string | null;
  status: 'draft' | 'posted' | 'reversed';
  saleCount: number;
  voidCount: number;
  refundCount: number;
  grossSales: string;
  discountTotal: string;
  netRevenue: string;
  taxTotal: string;
  serviceChargeTotal: string;
  tipTotal: string;
  roundingTotal: string;
  accountGross: string;
  accountNet: string;
  accountTax: string;
  instrumentIssued: string;
  instrumentRedeemed: string;
  cashVariance: string;
  paidOutTotal: string;
  paidInTotal: string;
  journalEntryId?: number | null;
  reversalEntryId?: number | null;
  closedAt?: string | null;
  closedBy?: number | null;
  reopenedAt?: string | null;
  reopenedBy?: number | null;
  reopenReason?: string;
  createdAt: string;
  rowVersion: number;
}

export interface POSDayCloseDetailDTO {
  id: number;
  outletId: number;
  businessDate: string;
  businessDayId: number;
  closeNo?: string | null;
  status: 'draft' | 'posted' | 'reversed';
  saleCount: number;
  voidCount: number;
  refundCount: number;
  grossSales: string;
  discountTotal: string;
  netRevenue: string;
  taxTotal: string;
  serviceChargeTotal: string;
  tipTotal: string;
  roundingTotal: string;
  accountGross: string;
  accountNet: string;
  accountTax: string;
  instrumentIssued: string;
  instrumentRedeemed: string;
  cashVariance: string;
  paidOutTotal: string;
  paidInTotal: string;
  journalEntryId?: number | null;
  reversalEntryId?: number | null;
  closedAt?: string | null;
  closedBy?: number | null;
  reopenedAt?: string | null;
  reopenedBy?: number | null;
  reopenReason?: string;
  createdAt: string;
  rowVersion: number;
  lines: POSDayCloseLineDTO[];
  totalDebit: string;
  totalCredit: string;
  blockers: POSDayCloseBlockerDTO[];
}

export interface POSDayCloseLineDTO {
  id: number;
  seqNo: number;
  kind: 'revenue' | 'tax' | 'service_charge' | 'tender' | 'instrument_issue' | 'instrument_redeem' | 'tip' | 'rounding' | 'cash_variance' | 'paid_in' | 'paid_out';
  keyCode?: string;
  revenueCenterId?: number | null;
  tenderTypeId?: number | null;
  taxCode?: string;
  accountId: number;
  debit: string;
  credit: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  departmentId?: number | null;
}

export interface POSDayReconciliationCheckDTO {
  code: string;
  labelKey: string;
  expected: string;
  actual: string;
  difference: string;
  passed: boolean;
}

export interface POSDayReconciliationDTO {
  closeId: number;
  outletId: number;
  businessDate: string;
  status: 'draft' | 'posted' | 'reversed';
  currency: string;
  ties: boolean;
  assertions: POSDayReconciliationCheckDTO[];
}

export interface POSDepositDTO {
  id: number;
  outletId: number;
  businessDate: string;
  depositNo?: string | null;
  bankAccountId: number;
  amount: string;
  currency: string;
  status: 'draft' | 'banked' | 'confirmed';
  bankedAt?: string | null;
  bankedBy?: number | null;
  bankReference?: string;
  confirmedAt?: string | null;
  journalEntryId?: number | null;
  preparedBy?: number | null;
  witnessedBy?: number | null;
  note?: string;
  sessions: POSDepositSessionDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface POSDepositSessionDTO {
  sessionId: number;
  amount: string;
}

export interface POSDepositSessionInput {
  sessionId: number;
  amount: string;
}

export interface POSEmployeePurchaseDTO {
  id: number;
  saleId: number;
  employeeId: number;
  amount: string;
  businessDate: string;
  status: 'pending' | 'deducted' | 'waived' | 'cancelled';
  payrollRunId?: number | null;
  deductedAt?: string | null;
  note?: string;
  rowVersion: number;
}

export interface POSExportQuery {
  outletId: number;
  from: string;
  to: string;
}

export interface POSFiscalGapDTO {
  outletId: number;
  outletCode: string;
  docType: string;
  fiscalYear: number;
  gaps: POSFiscalGapRangeDTO[];
  unexplained: number;
  issuedCount: number;
  voidedCount: number;
}

export interface POSFiscalGapQuery {
  outletId: number;
  fiscalYear?: number;
}

export interface POSFiscalGapRangeDTO {
  from: number;
  to: number;
  explained: boolean;
  reason?: string;
}

export interface POSFiscalReceiptDTO {
  id: number;
  outletId: number;
  saleId?: number | null;
  receiptNo: string;
  seqNo: number;
  fiscalYear: number;
  issuedAt: string;
  issuedBy?: number | null;
  status: 'issued' | 'voided';
  voidedAt?: string;
  voidedBy?: number | null;
  voidReasonCode?: string;
  grossTotal: string;
  rowVersion: number;
}

export interface POSFloorAreaDTO {
  id: number;
  outletId: number;
  code: string;
  name: string;
  seqNo: number;
  isActive: boolean;
  rowVersion: number;
}

export interface POSInstrumentDTO {
  id: number;
  companyId: number;
  kind: 'gift_card' | 'store_credit';
  codeLast4: string;
  currency: string;
  issuedAmount: string;
  balance: string;
  customerId?: number | null;
  issuedBySaleId?: number | null;
  expiresOn?: string;
  status: 'active' | 'expired' | 'void';
  voidReason?: string;
  rowVersion: number;
}

export interface POSInstrumentMovementDTO {
  id: number;
  instrumentId: number;
  kind: 'issue' | 'topup' | 'redeem' | 'refund_grant' | 'expire' | 'void_reverse' | 'adjust';
  amount: string;
  balanceAfter: string;
  saleId?: number | null;
  tenderId?: number | null;
  businessDate: string;
  reasonCode?: string;
  note?: string;
}

export interface POSItemAllergenDTO {
  id: number;
  companyId: number;
  itemId: number;
  allergenCode: string;
  severity: 'contains' | 'may_contain';
  note?: string;
  source: string;
  rowVersion: number;
}

export interface POSItemAvailabilityDTO {
  id: number;
  outletId: number;
  itemId: number;
  state: 'available' | 'eighty_six' | 'limited';
  remainingQty?: string | null;
  reasonCode?: string;
  note?: string;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  setBy?: number | null;
  rowVersion: number;
}

export interface POSKDSTicketDTO {
  id: number;
  outletId: number;
  stationId: number;
  checkId: number;
  courseNo?: number | null;
  tableCode?: string;
  checkNo?: string;
  covers: number;
  businessDate: string;
  firedAt: string;
  firedBy?: number | null;
  state: 'new' | 'in_progress' | 'ready' | 'bumped' | 'recalled' | 'cancelled';
  startedAt?: string | null;
  readyAt?: string | null;
  bumpedAt?: string | null;
  bumpedBy?: number | null;
  recalledAt?: string | null;
  recalledBy?: number | null;
  priority: number;
  allergens: string[];
  note?: string;
  lines: POSKDSTicketLineDTO[];
  rowVersion: number;
}

export interface POSKDSTicketLineDTO {
  id: number;
  ticketId: number;
  checkLineId: number;
  seqNo: number;
  description: string;
  quantity: string;
  modifiers: string[];
  note?: string;
  state: string;
}

export interface POSMenuDTO {
  id: number;
  companyId: number;
  outletId: number;
  code: string;
  name: string;
  channel?: string;
  dayPartCode?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface POSMenuItemDTO {
  id: number;
  versionId: number;
  sectionId: number;
  itemId: number;
  seqNo: number;
  displayName?: string;
  uom?: string;
  quantityPerPress: string;
  revenueCenterId?: number | null;
  recipeId?: number | null;
  requiresAgeCheck: boolean;
  minimumAge?: number | null;
  requiresSerial: boolean;
  courseNo?: number | null;
  stationId?: number | null;
  isAvailable: boolean;
  colour?: string;
  modifierGroupCodes?: string[];
}

export interface POSMenuPublishCheckDTO {
  versionId: number;
  ready: boolean;
  offenders: POSMenuPublishOffenderDTO[];
}

export interface POSMenuPublishOffenderDTO {
  check: string;
  sectionId?: number | null;
  sectionCode?: string;
  menuItemId?: number | null;
  itemId?: number | null;
  groupId?: number | null;
  groupCode?: string;
  message: string;
}

export interface POSMenuSectionDTO {
  id: number;
  versionId: number;
  code: string;
  name: string;
  seqNo: number;
  revenueCenterId?: number | null;
  colour?: string;
}

export interface POSMenuVersionDTO {
  id: number;
  menuId: number;
  versionNo: number;
  status: 'draft' | 'published' | 'withdrawn';
  effectiveFrom: string;
  effectiveTo?: string | null;
  contentHash?: string | null;
  itemCount: number;
  publishedAt?: string | null;
  publishedBy?: number | null;
  withdrawnAt?: string | null;
  withdrawnBy?: number | null;
  note?: string;
  rowVersion: number;
}

export interface POSMenuVersionDetailDTO {
  id: number;
  menuId: number;
  versionNo: number;
  status: 'draft' | 'published' | 'withdrawn';
  effectiveFrom: string;
  effectiveTo?: string | null;
  contentHash?: string | null;
  itemCount: number;
  publishedAt?: string | null;
  publishedBy?: number | null;
  withdrawnAt?: string | null;
  withdrawnBy?: number | null;
  note?: string;
  rowVersion: number;
  sections: POSMenuSectionDTO[];
  items: POSMenuItemDTO[];
  modifierGroups: POSModifierGroupDTO[];
}

export interface POSModifierDTO {
  id: number;
  groupId: number;
  code: string;
  name: string;
  seqNo: number;
  isDefault: boolean;
  chargeItemId?: number | null;
  recipeModifierCode?: string;
}

export interface POSModifierGroupDTO {
  id: number;
  versionId: number;
  code: string;
  name: string;
  minSelect: number;
  maxSelect?: number | null;
  seqNo: number;
  modifiers: POSModifierDTO[];
}

export interface POSOperatorDayStatsDTO {
  id: number;
  outletId: number;
  operatorUserId: number;
  businessDate: string;
  saleCount: number;
  grossSales: string;
  voidCount: number;
  voidAmount: string;
  lineVoidCount: number;
  refundCount: number;
  refundAmount: string;
  discountCount: number;
  discountAmount: string;
  noSaleCount: number;
  overrideCount: number;
  cashVariance: string;
  voidRatePercent: string;
  refundRatePercent: string;
  discountRatePercent: string;
  breached: string[];
  computedAt: string;
}

export interface POSOutletDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  locationId: number;
  warehouseId?: number | null;
  branchId?: number | null;
  dayUnitType: string;
  dayUnitId: number;
  kind: 'retail' | 'hospitality' | 'wholesale';
  taxChannel: string;
  supplyType: string;
  currency: string;
  defaultRevenueCenterId?: number | null;
  priceSetId?: number | null;
  cashRoundingIncrement: string;
  isActive: boolean;
  rowVersion: number;
}

export interface POSOverrideGrantDTO {
  overrideId: number;
  ruleCode: string;
  docType: string;
  docId: number;
  docNo?: string;
  overriddenByUserId: number;
  authorisedByUserId?: number | null;
  authoritySource: string;
  authorisedAt: string;
  expiresAt: string;
}

export interface POSOverrideRequest {
  reasonCode: string;
  note?: string;
  authorisedBy?: number | null;
}

export interface POSPriceConsiderationDTO {
  seq: number;
  source: string;
  sourceId?: number | null;
  sourceCode?: string;
  outcome: string;
  unitPrice?: string | null;
  minQty?: string | null;
  reasonKey: string;
  reasonArgs?: Record<string, string>;
}

export interface POSRefundLineInput {
  originalLineId?: number | null;
  itemId?: number;
  quantity: string;
  unitPrice?: string;
  uom?: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  revenueCenterId?: number | null;
  description?: string;
}

export interface POSRevenueCenterDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  revenueAccountId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  departmentId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface POSSaleDTO {
  id: number;
  companyId: number;
  outletId: number;
  terminalId: number;
  saleNo?: string | null;
  receiptNo?: string | null;
  localSeq?: number | null;
  deviceId?: number | null;
  syncMutationId?: number | null;
  businessDate: string;
  businessDayId?: number | null;
  shiftCode: string;
  dayPartCode: string;
  openedAt: string;
  settledAt?: string;
  operatorUserId: number;
  checkId?: number | null;
  customerId?: number | null;
  loyaltyMemberId?: number | null;
  channelMode: 'dine_in' | 'takeaway' | 'delivery' | 'room_charge' | 'counter';
  docKind: 'sale' | 'refund';
  refundsSaleId?: number | null;
  status: 'open' | 'settled' | 'voided' | 'abandoned';
  voidReasonCode?: string;
  voidReason?: string;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  discountTotal: string;
  serviceChargeTotal: string;
  tipTotal: string;
  roundingAdjustment: string;
  tenderedTotal: string;
  changeTotal: string;
  priceSetVersionId?: number | null;
  pricedFromSupersededSet: boolean;
  menuVersionId?: number | null;
  depletionRunId?: number | null;
  arInvoiceId?: number | null;
  cashSessionId?: number | null;
  dayCloseId?: number | null;
  reference?: string;
  note?: string;
  rowVersion: number;
}

export interface POSSaleDepletionComponentDTO {
  saleLineNo: number;
  itemId: number;
  lotId?: number | null;
  lotNo?: string;
  theoreticalQty: string;
  issuedQty: string;
  unitCost: string;
  value: string;
  stockLedgerEntryId?: number | null;
  shortfallQty: string;
}

export interface POSSaleDepletionDTO {
  saleId: number;
  runId: number;
  preview: boolean;
  alreadyRecorded: boolean;
  journalEntryId?: number | null;
  totalValue: string;
  lines: POSSaleDepletionLineDTO[];
  components: POSSaleDepletionComponentDTO[];
  shortfalls: POSSaleDepletionShortfallDTO[];
  unresolvedLines: number;
  skippedLines: number;
  unknownModifiers: string[];
}

export interface POSSaleDepletionLineDTO {
  saleLineNo: number;
  itemId: number;
  quantity: string;
  recipeId?: number | null;
  resolution: string;
  unresolvedReason?: string;
  theoreticalValue: string;
  actualValue: string;
}

export interface POSSaleDepletionShortfallDTO {
  saleLineNo: number;
  itemId: number;
  requiredQty: string;
  shortfallQty: string;
}

export interface POSSaleDetailDTO {
  id: number;
  companyId: number;
  outletId: number;
  terminalId: number;
  saleNo?: string | null;
  receiptNo?: string | null;
  localSeq?: number | null;
  deviceId?: number | null;
  syncMutationId?: number | null;
  businessDate: string;
  businessDayId?: number | null;
  shiftCode: string;
  dayPartCode: string;
  openedAt: string;
  settledAt?: string;
  operatorUserId: number;
  checkId?: number | null;
  customerId?: number | null;
  loyaltyMemberId?: number | null;
  channelMode: 'dine_in' | 'takeaway' | 'delivery' | 'room_charge' | 'counter';
  docKind: 'sale' | 'refund';
  refundsSaleId?: number | null;
  status: 'open' | 'settled' | 'voided' | 'abandoned';
  voidReasonCode?: string;
  voidReason?: string;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  discountTotal: string;
  serviceChargeTotal: string;
  tipTotal: string;
  roundingAdjustment: string;
  tenderedTotal: string;
  changeTotal: string;
  priceSetVersionId?: number | null;
  pricedFromSupersededSet: boolean;
  menuVersionId?: number | null;
  depletionRunId?: number | null;
  arInvoiceId?: number | null;
  cashSessionId?: number | null;
  dayCloseId?: number | null;
  reference?: string;
  note?: string;
  rowVersion: number;
  lines: POSSaleLineDTO[];
  discounts: POSSaleDiscountDTO[];
}

export interface POSSaleDiscountDTO {
  id: number;
  saleId: number;
  lineId?: number | null;
  kind: 'percent' | 'amount' | 'promotion';
  promotionId?: number | null;
  percent: string;
  amount: string;
  reasonCode?: string;
  note?: string;
  ruleOverrideId?: number | null;
  authorisedBy?: number | null;
}

export interface POSSaleLineDTO {
  id: number;
  saleId: number;
  lineNo: number;
  parentLineId?: number | null;
  itemId: number;
  menuItemId?: number | null;
  description: string;
  uom: string;
  quantity: string;
  unitPrice: string;
  grossAmount: string;
  discountAmount: string;
  netAmount: string;
  taxAmount: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  itemClass?: string;
  revenueCenterId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  priceSource?: string;
  priceSetVersionId?: number | null;
  pricedFromSupersededSet: boolean;
  currentUnitPrice?: string | null;
  priceVarianceGross: string;
  courseNo?: number | null;
  stationId?: number | null;
  lotId?: number | null;
  voided: boolean;
  voidReasonCode?: string;
  modifiers: POSSaleLineModifierDTO[];
  rowVersion: number;
}

export interface POSSaleLineModifierDTO {
  id: number;
  lineId: number;
  modifierId?: number | null;
  groupCode: string;
  modifierCode: string;
  name: string;
  recipeModifierCode?: string;
  seqNo: number;
}

export interface POSSaleLineModifierInput {
  modifierId?: number;
  code?: string;
  quantity?: string;
}

export interface POSSalePriceDTO {
  saleId: number;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  discountTotal: string;
  serviceChargeTotal: string;
  lines: POSSalePriceLineDTO[];
  warnings?: string[];
  persisted: boolean;
}

export interface POSSalePriceLineDTO {
  lineNo: number;
  lineId: number;
  itemId: number;
  quantity: string;
  unitPrice: string;
  currentUnitPrice: string;
  priceSource: string;
  setSuperseded: boolean;
  grossAmount: string;
  discountAmount: string;
  netAmount: string;
  taxAmount: string;
  taxLines: POSSaleTaxLineDTO[];
}

export interface POSSaleSettlementDTO {
  id: number;
  companyId: number;
  outletId: number;
  terminalId: number;
  saleNo?: string | null;
  receiptNo?: string | null;
  localSeq?: number | null;
  deviceId?: number | null;
  syncMutationId?: number | null;
  businessDate: string;
  businessDayId?: number | null;
  shiftCode: string;
  dayPartCode: string;
  openedAt: string;
  settledAt?: string;
  operatorUserId: number;
  checkId?: number | null;
  customerId?: number | null;
  loyaltyMemberId?: number | null;
  channelMode: 'dine_in' | 'takeaway' | 'delivery' | 'room_charge' | 'counter';
  docKind: 'sale' | 'refund';
  refundsSaleId?: number | null;
  status: 'open' | 'settled' | 'voided' | 'abandoned';
  voidReasonCode?: string;
  voidReason?: string;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  discountTotal: string;
  serviceChargeTotal: string;
  tipTotal: string;
  roundingAdjustment: string;
  tenderedTotal: string;
  changeTotal: string;
  priceSetVersionId?: number | null;
  pricedFromSupersededSet: boolean;
  menuVersionId?: number | null;
  depletionRunId?: number | null;
  arInvoiceId?: number | null;
  cashSessionId?: number | null;
  dayCloseId?: number | null;
  reference?: string;
  note?: string;
  rowVersion: number;
  lines: POSSaleLineDTO[];
  discounts: POSSaleDiscountDTO[];
  tenders: POSTenderDTO[];
  depletion?: POSSaleDepletionDTO | null;
  receipt?: POSFiscalReceiptDTO | null;
}

export interface POSSaleTaxLineDTO {
  sequenceNo: number;
  taxCode: string;
  kind: string;
  regimeCode?: string;
  rate: string;
  baseAmount: string;
  taxAmount: string;
  currency: string;
}

export interface POSSaleTenderStateDTO {
  id: number;
  companyId: number;
  outletId: number;
  terminalId: number;
  saleNo?: string | null;
  receiptNo?: string | null;
  localSeq?: number | null;
  deviceId?: number | null;
  syncMutationId?: number | null;
  businessDate: string;
  businessDayId?: number | null;
  shiftCode: string;
  dayPartCode: string;
  openedAt: string;
  settledAt?: string;
  operatorUserId: number;
  checkId?: number | null;
  customerId?: number | null;
  loyaltyMemberId?: number | null;
  channelMode: 'dine_in' | 'takeaway' | 'delivery' | 'room_charge' | 'counter';
  docKind: 'sale' | 'refund';
  refundsSaleId?: number | null;
  status: 'open' | 'settled' | 'voided' | 'abandoned';
  voidReasonCode?: string;
  voidReason?: string;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  discountTotal: string;
  serviceChargeTotal: string;
  tipTotal: string;
  roundingAdjustment: string;
  tenderedTotal: string;
  changeTotal: string;
  priceSetVersionId?: number | null;
  pricedFromSupersededSet: boolean;
  menuVersionId?: number | null;
  depletionRunId?: number | null;
  arInvoiceId?: number | null;
  cashSessionId?: number | null;
  dayCloseId?: number | null;
  reference?: string;
  note?: string;
  rowVersion: number;
  lines: POSSaleLineDTO[];
  discounts: POSSaleDiscountDTO[];
  tenders: POSTenderDTO[];
}

export interface POSSaleWaterfallDTO {
  saleId: number;
  lines: POSSaleWaterfallLineDTO[];
}

export interface POSSaleWaterfallLineDTO {
  lineNo: number;
  itemId: number;
  listPrice: string;
  unitPrice: string;
  currency: string;
  priceBasis: string;
  winnerSource: string;
  winnerSourceCode?: string;
  precedenceVersionId: number;
  precedenceCode?: string;
  priceSetVersionId?: number | null;
  setSuperseded: boolean;
  marginOutcome: string;
  fingerprint: string;
  resolvedAt: string;
  considerations: POSPriceConsiderationDTO[];
}

export interface POSSalesSummaryDTO {
  outletId: number;
  from: string;
  to: string;
  byDay: POSSalesSummaryLineDTO[];
  byTender: POSSalesSummaryTenderDTO[];
}

export interface POSSalesSummaryLineDTO {
  businessDate: string;
  revenueCenterId?: number | null;
  saleCount: number;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  discountTotal: string;
}

export interface POSSalesSummaryQuery {
  outletId: number;
  from: string;
  to: string;
}

export interface POSSalesSummaryTenderDTO {
  businessDate: string;
  tenderTypeId: number;
  tenderCode: string;
  tenderAmount: string;
}

export interface POSSerialSaleDTO {
  id: number;
  saleId: number;
  lineId: number;
  itemId: number;
  serialNo: string;
  stockSerialId?: number | null;
  customerId?: number | null;
  warrantyMonths?: number | null;
  warrantyStartsOn?: string | null;
  warrantyEndsOn?: string | null;
  createdAt: string;
}

export interface POSSettingsDTO {
  companyId: number;
  cashOnHandAccountId?: number | null;
  cardClearingAccountId?: number | null;
  giftCardLiabilityAccountId?: number | null;
  storeCreditLiabilityAccountId?: number | null;
  customerDepositLiabilityAccountId?: number | null;
  tipPayableAccountId?: number | null;
  cashOverShortAccountId?: number | null;
  cashRoundingAccountId?: number | null;
  defaultRevenueAccountId?: number | null;
  defaultCashRoundingIncrement: string;
  voidWindowMinutes: number;
  anomalyVoidRatePercent: string;
  anomalyDiscountRatePercent: string;
  anomalyRefundRatePercent: string;
  anomalyNoSaleCount: number;
  rowVersion: number;
}

export interface POSStationDTO {
  id: number;
  outletId: number;
  code: string;
  name: string;
  seqNo: number;
  targetMinutes: number;
  isActive: boolean;
  rowVersion: number;
}

export interface POSSupersededPricingDTO {
  outletId: number;
  from: string;
  to: string;
  lines: POSSupersededPricingLineDTO[];
}

export interface POSSupersededPricingLineDTO {
  saleId: number;
  saleNo?: string;
  businessDate: string;
  lineNo: number;
  itemId: number;
  description: string;
  priceSetVersionId?: number | null;
  unitPrice: string;
  currentUnitPrice?: string;
  priceVarianceGross: string;
}

export interface POSSupersededPricingQuery {
  outletId: number;
  from: string;
  to: string;
}

export interface POSTableDTO {
  id: number;
  areaId: number;
  code: string;
  name: string;
  seats: number;
  gridX: number;
  gridY: number;
  shape: string;
  status: 'free' | 'seated' | 'ordered' | 'printed' | 'paid' | 'dirty';
  isActive: boolean;
  rowVersion: number;
}

export interface POSTableInput {
  code: string;
  name: string;
  seats: number;
  gridX?: number;
  gridY?: number;
  shape?: string;
}

export interface POSTenderDTO {
  id: number;
  saleId: number;
  seqNo: number;
  tenderTypeId: number;
  kind: 'cash' | 'card' | 'gift_card' | 'store_credit' | 'account' | 'room_charge';
  amountApplied: string;
  amountTendered: string;
  changeGiven: string;
  roundingAdjustment: string;
  tipAmount: string;
  cardLast4?: string;
  cardScheme?: string;
  cardApprovalCode?: string;
  provider?: string;
  providerRef?: string;
  instrumentId?: number | null;
  customerId?: number | null;
  folioRef?: string;
  roomRef?: string;
  voided: boolean;
  voidReasonCode?: string;
  rowVersion: number;
}

export interface POSTenderTypeDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  kind: 'cash' | 'card' | 'gift_card' | 'store_credit' | 'account' | 'room_charge';
  clearingAccountId?: number | null;
  opensDrawer: boolean;
  allowsChange: boolean;
  allowsPartial: boolean;
  allowsRefund: boolean;
  surchargePercent: string;
  surchargeItemId?: number | null;
  provider: string;
  source: string;
  sortOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface POSTerminalDTO {
  id: number;
  outletId: number;
  code: string;
  name: string;
  localSeqHighWater: number;
  cashAccountId?: number | null;
  offlineEnabled: boolean;
  contentMaxAgeHours: number;
  status: 'active' | 'suspended' | 'retired';
  rowVersion: number;
}

export interface POSTipDTO {
  id: number;
  saleId: number;
  tenderId?: number | null;
  outletId: number;
  businessDate: string;
  operatorUserId: number;
  amount: string;
  method: 'cash' | 'card';
  distributedAt?: string;
  distributionRef?: string;
}

export interface POSTipPoolDTO {
  outletId: number;
  from: string;
  to: string;
  total: string;
  cashTotal: string;
  cardTotal: string;
  operators: POSTipPoolOperatorDTO[];
}

export interface POSTipPoolOperatorDTO {
  operatorUserId: number;
  cashAmount: string;
  cardAmount: string;
  total: string;
}

export interface POSTipPoolQuery {
  outletId: number;
  from: string;
  to: string;
}

export interface POSVoidDiscountDTO {
  outletId: number;
  from: string;
  to: string;
  rows: POSOperatorDayStatsDTO[];
}

export interface POSVoidDiscountQuery {
  outletId: number;
  from: string;
  to: string;
  breachedOnly?: boolean;
}

export interface PRDTO {
  id: number;
  prNo?: string | null;
  companyId: number;
  requesterId: number;
  requesterName?: string;
  departmentId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  needBy: string;
  currency: string;
  subtotal: string;
  total: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'cancelled' | 'closed';
  memo: string;
  approvalRequestId?: number | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  closedAt?: string | null;
  createdAt: string;
  rowVersion: number;
  lines?: PRLineDTO[];
}

export interface PRLineDTO {
  id: number;
  lineNo: number;
  itemId?: number | null;
  description: string;
  uom: string;
  quantity: string;
  estimatedPrice: string;
  lineAmount: string;
  accountId: number;
  accountCode?: string;
  accountName?: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  projectId?: number | null;
  needBy?: string | null;
  status: string;
  orderedQuantity: string;
  remainingQuantity: string;
  lastPriceHint?: string;
  budget?: BudgetIndicatorDTO | null;
}

export interface PRLineInput {
  itemId?: number | null;
  description?: string;
  uom?: string;
  quantity: string;
  estimatedPrice?: string;
  accountId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  projectId?: number | null;
  needBy?: string;
}

export interface PSUDesignationDTO {
  id: number;
  companyId: number;
  jurisdictionId: number;
  legislativeDataGroupId?: number | null;
  identifier: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface PSURedesignationDTO {
  id: number;
  psuId: number;
  fromLegislativeDataGroupId: number;
  toLegislativeDataGroupId: number;
  effectiveOn: string;
  impactReport: Record<string, unknown>;
  approvalRequestId?: number | null;
  status: string;
  reason?: string;
  rowVersion: number;
}

export interface Pack {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  countryId: string;
  country: string;
  version: number;
  status: 'draft' | 'published' | 'withdrawn';
  title: string;
  changelog: string;
  contentHash?: string | null;
  baseVersion?: number | null;
  submittedBy?: string | null;
  submittedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  withdrawnBy?: string | null;
  withdrawnAt?: string | null;
  withdrawReason: string;
  isSystem: boolean;
}

export interface PackContent {
  jurisdictions: PackJurisdiction[];
  regimes: PackRegime[];
  taxCodes: PackTaxCode[];
  rules: RuleContent[];
  posRules: PackPOSRule[];
  priceBasis: PackPriceBasis[];
  returnDefs: PackReturnDef[];
  returnBoxes: PackReturnBox[];
  docRules: PackDocRule[];
  payrollRules: PackPayrollRule[];
}

export interface PackDetailDTO {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  countryId: string;
  country: string;
  version: number;
  status: 'draft' | 'published' | 'withdrawn';
  title: string;
  changelog: string;
  contentHash?: string | null;
  baseVersion?: number | null;
  submittedBy?: string | null;
  submittedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  withdrawnBy?: string | null;
  withdrawnAt?: string | null;
  withdrawReason: string;
  isSystem: boolean;
  rowCounts: Record<string, number>;
  validation?: PackValidation | null;
}

export interface PackDiff {
  jurisdictions: SectionDiff;
  regimes: SectionDiff;
  taxCodes: SectionDiff;
  rules: SectionDiff;
  posRules: SectionDiff;
  priceBasis: SectionDiff;
  returnDefs: SectionDiff;
  returnBoxes: SectionDiff;
  docRules: SectionDiff;
  payrollRules: SectionDiff;
}

export interface PackDocRule {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  jurisdictionCode: string;
  docType: string;
  gapless: boolean;
  voidConsumesNumber: boolean;
  statutory: boolean;
  numberFormat: string;
  numberPrefix: string;
  numberPadding: number;
  statutoryLanguage: string;
  einvoiceRequired: boolean;
  einvoiceProvider: string;
  retentionYears: number;
  note: string;
}

export interface PackJurisdiction {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  code: string;
  name: string;
  level: 'country' | 'subdivision' | 'locality';
  parentCode?: string | null;
  geoCountryId?: string | null;
  geoSubdivisionId?: string | null;
  geoLocalityId?: string | null;
  sortOrder: number;
}

export interface PackPOSRule {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  regimeCode: string;
  supplyType: 'goods' | 'services' | 'accommodation' | 'fnb' | 'digital' | 'transport' | 'other';
  basis: 'origin' | 'destination' | 'hybrid';
  fallback: 'origin' | 'out_of_scope';
  note: string;
}

export interface PackPayrollRule {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  jurisdictionCode: string;
  code: string;
  name: string;
  componentKind: 'earning' | 'deduction' | 'employer_cost' | 'leave_minimum' | 'threshold';
  formulaKind: 'fixed' | 'percent' | 'slab' | 'per_unit';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency?: string | null;
  params: Record<string, unknown>;
  effectiveFrom: string;
  effectiveTo?: string | null;
  sourceCitation: string;
  reviewPeriodMonths: number;
  note: string;
}

export interface PackPriceBasis {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  regimeCode: string;
  channelClass: 'default' | 'pos' | 'hospitality' | 'retail' | 'wholesale' | 'b2b' | 'portal' | 'web';
  priceBasis: 'inclusive' | 'exclusive';
  note: string;
}

export interface PackRegime {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  code: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE' | 'SALES_TAX';
  jurisdictionCode: string;
  filingCurrency: string;
  registrationPattern: string;
  registrationHint: string;
  unregisteredTaxCode?: string | null;
  sortOrder: number;
  description: string;
}

export interface PackReturnBox {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  returnDefCode: string;
  boxNo: string;
  label: string;
  sequenceNo: number;
  valueKind: 'base' | 'tax' | 'computed';
  predicate?: Record<string, unknown> | null;
  formula: string;
  sign: number;
  note: string;
}

export interface PackReturnDef {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  regimeCode: string;
  code: string;
  name: string;
  frequency: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  dueRule: string;
  filingCurrency: string;
  formReference: string;
  note: string;
}

export interface PackRuleEntry {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  ruleId: string;
  sequenceNo: number;
  taxCode: string;
  baseKind: 'net' | 'net_plus_prior';
  note: string;
}

export interface PackTaxCode {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  regimeCode: string;
  jurisdictionCode: string;
  code: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE';
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  basis: 'percent' | 'per_unit';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  unitCurrency?: string | null;
  unitLabel?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  inputAccountCode?: string | null;
  outputAccountCode?: string | null;
  payableAccountCode?: string | null;
  rounding: 'line' | 'document';
  roundingMode: 'half_up' | 'half_even';
  isRecoverable: boolean;
  isOverridable: boolean;
  description: string;
  sourceCitation: string;
  reviewDue?: string | null;
}

export interface PackUpdateRequest {
  title?: string | null;
  changelog?: string | null;
  rowVersion: number;
}

export interface PackValidation {
  valid: boolean;
  issues: ValidationIssue[];
}

export interface PackValidationRun {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  country: string;
  packId: string;
  fromVersion: string;
  toVersion: string;
  sampleSize: number;
  differenceSummary?: Record<string, unknown> | null;
  status: string;
  executedAt: string;
  executedBy?: number | null;
  nonProductionEnterpriseId?: number | null;
}

export interface PageAIAgentDTO {
  items: AIAgentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIAgentRunDTO {
  items: AIAgentRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIBacktestPointDTO {
  items: AIBacktestPointDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIBacktestRunDTO {
  items: AIBacktestRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIBriefingDTO {
  items: AIBriefingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAICapabilityDTO {
  items: AICapabilityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIConfigAnomalyDTO {
  items: AIConfigAnomalyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIRecommendationDTO {
  items: AIRecommendationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAIRecommendationOutcomeDTO {
  items: AIRecommendationOutcomeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAPInvoiceSignalDTO {
  items: APInvoiceSignalDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAPPaymentExceptionDTO {
  items: APPaymentExceptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAccount {
  items: Account[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAccountingMethodDTO {
  items: AccountingMethodDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAccrualItemDTO {
  items: AccrualItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAccrualPositionDTO {
  items: AccrualPositionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageActivationDTO {
  items: ActivationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageActivityDTO {
  items: ActivityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAdjustmentReasonCodeDTO {
  items: AdjustmentReasonCodeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageApSupplierStatementDTO {
  items: ApSupplierStatementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageApWhtRemittanceDTO {
  items: ApWhtRemittanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArCollectionsItemDTO {
  items: ArCollectionsItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArCreditExposureDTO {
  items: ArCreditExposureDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArCreditLimitRequestDTO {
  items: ArCreditLimitRequestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArDunningEventDTO {
  items: ArDunningEventDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArDunningLevelDTO {
  items: ArDunningLevelDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArPromiseDTO {
  items: ArPromiseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArUnallocatedReceiptDTO {
  items: ArUnallocatedReceiptDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageArWriteOffDTO {
  items: ArWriteOffDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssetBookDTO {
  items: AssetBookDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssetDTO {
  items: AssetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssetsCapitalizableSourceDTO {
  items: AssetsCapitalizableSourceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssetsDisposalDTO {
  items: AssetsDisposalDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssetsEventDTO {
  items: AssetsEventDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssignmentDTO {
  items: AssignmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAssignmentRuleDTO {
  items: AssignmentRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAttachmentDTO {
  items: AttachmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAttendanceDTO {
  items: AttendanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAttendanceExceptionDTO {
  items: AttendanceExceptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAttendanceLockDTO {
  items: AttendanceLockDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAttentionItemDTO {
  items: AttentionItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAuthorityAbsenceDTO {
  items: AuthorityAbsenceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAuthorityDelegationDTO {
  items: AuthorityDelegationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageAuthorityRuleDTO {
  items: AuthorityRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBOMDTO {
  items: BOMDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBackorderDTO {
  items: BackorderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankAccountDTO {
  items: BankAccountDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankAccountDTO2 {
  items: CashBankAccountDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankBalanceObservationDTO {
  items: BankBalanceObservationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankChangeDTO {
  items: BankChangeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankFileProfileDTO {
  items: BankFileProfileDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankFileProfileDTO2 {
  items: PayrollBankFileProfileDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankMandateDTO {
  items: BankMandateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecConnectionDTO {
  items: BankrecConnectionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecExceptionDTO {
  items: BankrecExceptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecForceMatchDTO {
  items: BankrecForceMatchDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecMatchSetDTO {
  items: BankrecMatchSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecSessionReopenDTO {
  items: BankrecSessionReopenDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecStandingRuleDTO {
  items: BankrecStandingRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBankrecStatementGapDTO {
  items: BankrecStatementGapDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBeneficiaryChangeDTO {
  items: BeneficiaryChangeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBillingDTO {
  items: BillingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBinDTO {
  items: BinDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBudgetCycleVersionDTO {
  items: BudgetCycleVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBudgetLineDTO {
  items: BudgetLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBudgetModelDTO {
  items: BudgetModelDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBudgetSupplementaryDTO {
  items: BudgetSupplementaryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBudgetTransferDTO {
  items: BudgetTransferDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBudgetVersionDTO {
  items: BudgetVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBusinessDay {
  items: BusinessDay[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageBusinessUnitDTO {
  items: BusinessUnitDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCAPADTO {
  items: CAPADTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCOATemplate {
  items: COATemplate[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCRMAccountDTO {
  items: CRMAccountDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCRMContactDTO {
  items: CRMContactDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCRMForecastSnapshotDTO {
  items: CRMForecastSnapshotDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCalendar {
  items: Calendar[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCampaignRecipientDTO {
  items: CampaignRecipientDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCampaignResponseDTO {
  items: CampaignResponseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCampaignSendDTO {
  items: CampaignSendDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCandidateDTO {
  items: CandidateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCandidateReferenceDTO {
  items: CandidateReferenceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCandidateScorecardDTO {
  items: CandidateScorecardDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCapitalizableLineDTO {
  items: CapitalizableLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCardAccountDTO {
  items: CardAccountDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCardStatementDTO {
  items: CardStatementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCardStatementLineDTO {
  items: CardStatementLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCaseDTO {
  items: CaseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankChargeDTO {
  items: CashBankChargeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankContactDTO {
  items: CashBankContactDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankDTO {
  items: CashBankDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankLimitChangeDTO {
  items: CashBankLimitChangeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankRelationshipAccountDTO {
  items: CashBankRelationshipAccountDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankRelationshipDTO {
  items: CashBankRelationshipDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankRelationshipFacilityDTO {
  items: CashBankRelationshipFacilityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashBankTariffDTO {
  items: CashBankTariffDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashForecastItemDTO {
  items: CashForecastItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashForecastRunDTO {
  items: CashForecastRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashForecastScenarioDTO {
  items: CashForecastScenarioDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashIcInstrumentDTO {
  items: CashIcInstrumentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashMovementAnomalyDTO {
  items: CashMovementAnomalyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCashScreeningEntryDTO {
  items: CashScreeningEntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCatalogueItemDTO {
  items: CatalogueItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCertificateDTO {
  items: CertificateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageChargePool {
  items: ChargePool[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageClassDTO {
  items: ClassDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageClockEventDTO {
  items: ClockEventDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseAnomalyFindingDTO {
  items: CloseAnomalyFindingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseCalendarDTO {
  items: CloseCalendarDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseCertificationDTO {
  items: CloseCertificationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseEvidenceDTO {
  items: CloseEvidenceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseEvidencePackSummaryDTO {
  items: CloseEvidencePackSummaryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseRunDTO {
  items: CloseRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseStatementSetDTO {
  items: CloseStatementSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCloseTaskDTO {
  items: CloseTaskDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCodeDTO {
  items: CodeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCommitmentDTO {
  items: CommitmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCompany {
  items: Company[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageComponentDTO {
  items: ComponentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageConsentChannelDTO {
  items: ConsentChannelDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageConsentPurposeDTO {
  items: ConsentPurposeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageConsolAccountMapDTO {
  items: ConsolAccountMapDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageConsolAdjustmentDTO {
  items: ConsolAdjustmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageContractExpiryRowDTO {
  items: ContractExpiryRowDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageContractReleaseDTO {
  items: ContractReleaseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageContractorInvoiceDTO {
  items: ContractorInvoiceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageContractorTaskDTO {
  items: ContractorTaskDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageControlRuleDTO {
  items: ControlRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageConversationDTO {
  items: ConversationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCoolingOffPolicyDTO {
  items: CoolingOffPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostAbsorptionRateDTO {
  items: CostAbsorptionRateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostCycleDTO {
  items: CostCycleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostDriverDTO {
  items: CostDriverDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostDriverQuantityDTO {
  items: CostDriverQuantityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostPoolDTO {
  items: CostPoolDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostRunAllocationDTO {
  items: CostRunAllocationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostRunDTO {
  items: CostRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCostRunIterationDTO {
  items: CostRunIterationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCountRootCauseCodeDTO {
  items: CountRootCauseCodeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCountry {
  items: Country[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCoverageMinimumDTO {
  items: CoverageMinimumDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCrossChargeRuleDTO {
  items: CrossChargeRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCrossValidationRuleDTO {
  items: CrossValidationRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCurrencyDTO {
  items: CurrencyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCustomerAssetDTO {
  items: CustomerAssetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCustomerContractDTO {
  items: CustomerContractDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCustomerDTO {
  items: CustomerDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageCycleCountSessionDetailDTO {
  items: CycleCountSessionDetailDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDailyOperatingReportDTO {
  items: DailyOperatingReportDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDebtAccrualRunDTO {
  items: DebtAccrualRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDebtFacilityDTO {
  items: DebtFacilityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDebtRateObservationDTO {
  items: DebtRateObservationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDemandInputDTO {
  items: DemandInputDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDemandPeriodDTO {
  items: DemandPeriodDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDepletionRunDTO {
  items: DepletionRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDepreciationEntryDTO {
  items: DepreciationEntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDepreciationRunDTO {
  items: DepreciationRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDesignationDTO {
  items: DesignationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceContentAssignmentDTO {
  items: DeviceContentAssignmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceContentSetDTO {
  items: DeviceContentSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceContentSetVersionDTO {
  items: DeviceContentSetVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceDTO {
  items: DeviceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceOfflinePolicyDTO {
  items: DeviceOfflinePolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceRatificationDTO {
  items: DeviceRatificationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceSyncBatchDTO {
  items: DeviceSyncBatchDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDeviceSyncConflictDTO {
  items: DeviceSyncConflictDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDiscountPolicyDTO {
  items: DiscountPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageDivisionFNDDTO {
  items: DivisionFNDDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMAssetDTO {
  items: EAMAssetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMAvailabilityEventDTO {
  items: EAMAvailabilityEventDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMComplianceRequirementDTO {
  items: EAMComplianceRequirementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMDowntimeDTO {
  items: EAMDowntimeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMFailureDTO {
  items: EAMFailureDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMFunctionalLocationDTO {
  items: EAMFunctionalLocationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMInspectionDTO {
  items: EAMInspectionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMMeterDTO {
  items: EAMMeterDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMMeterReadingDTO {
  items: EAMMeterReadingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMPMPlanDTO {
  items: EAMPMPlanDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMPermitDTO {
  items: EAMPermitDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMReliabilityDTO {
  items: EAMReliabilityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMStrategyDTO {
  items: EAMStrategyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEAMTaskListDTO {
  items: EAMTaskListDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageECLAssessmentDTO {
  items: ECLAssessmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageECLMatrixDTO {
  items: ECLMatrixDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageECLRunDTO {
  items: ECLRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageECLSegmentDTO {
  items: ECLSegmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEliminationDTO {
  items: EliminationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEliminationRuleDTO {
  items: EliminationRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEmployeeDTO {
  items: EmployeeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEmployeeDocumentDTO {
  items: EmployeeDocumentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEmploymentAssignmentDTO {
  items: EmploymentAssignmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEngineeringChangeOrderDTO {
  items: EngineeringChangeOrderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEnterpriseDTO {
  items: EnterpriseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageEntryDTO {
  items: EntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExitDTO {
  items: ExitDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExpenseCategoryDTO {
  items: ExpenseCategoryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExpenseClaimDTO {
  items: ExpenseClaimDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExpenseMileageRateDTO {
  items: ExpenseMileageRateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExpensePayrollInclusionDTO {
  items: ExpensePayrollInclusionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExpensePolicyDTO {
  items: ExpensePolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExpiringLotDTO {
  items: ExpiringLotDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageExtractionDTO {
  items: ExtractionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFABookDTO {
  items: FABookDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFAImpairmentDTO {
  items: FAImpairmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFAImpairmentIndicatorDTO {
  items: FAImpairmentIndicatorDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFAVerificationLineDTO {
  items: FAVerificationLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFAVerificationRunDTO {
  items: FAVerificationRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFilingDTO {
  items: FilingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFiscalCalendar {
  items: FiscalCalendar[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFndSecurityScopeGrantDTO {
  items: FndSecurityScopeGrantDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFundsInTransitDTO {
  items: FundsInTransitDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFxrevalRunDTO {
  items: FxrevalRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageFxrevalRunLineDTO {
  items: FxrevalRunLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageGRNDTO {
  items: GRNDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageGroupDTO {
  items: GroupDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageGroupDTO2 {
  items: ConsolGroupDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHACCPCheckpointDTO {
  items: HACCPCheckpointDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHACCPRecordDTO {
  items: HACCPRecordDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHRPersonSkillDTO {
  items: HRPersonSkillDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHRSkillDTO {
  items: HRSkillDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHierarchyDTO {
  items: HierarchyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHold {
  items: Hold[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageHoliday {
  items: Holiday[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageICDifferenceDTO {
  items: ICDifferenceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageICPairDTO {
  items: ICPairDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageICStatementDTO {
  items: ICStatementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageICTransactionDTO {
  items: ICTransactionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageIdentityUserDirectoryEntryDTO {
  items: IdentityUserDirectoryEntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageImportDTO {
  items: ImportDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageImportDTO2 {
  items: BankrecImportDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInboxItem {
  items: InboxItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInspectionPlanDTO {
  items: InspectionPlanDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageIntercompanyRuleDTO {
  items: IntercompanyRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInventoryClassificationLineDTO {
  items: InventoryClassificationLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInventoryReconRunDTO {
  items: InventoryReconRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInvoiceDTO {
  items: InvoiceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInvoiceDTO2 {
  items: ApInvoiceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageInvoiceDTO3 {
  items: ArInvoiceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageItemDTO {
  items: ItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageItemFixedBinDTO {
  items: ItemFixedBinDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageItemStandardCostDTO {
  items: ItemStandardCostDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageItemSubstituteDTO {
  items: ItemSubstituteDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageJob {
  items: Job[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageJobGradeDTO {
  items: JobGradeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageJournalTypeDTO {
  items: JournalTypeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageJurisdictionDTO {
  items: JurisdictionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageKBArticleDTO {
  items: KBArticleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLandedCostElementDTO {
  items: LandedCostElementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLandedCostShipmentDTO {
  items: LandedCostShipmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeadDTO {
  items: LeadDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeadScoreRunDTO {
  items: LeadScoreRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeaseChangeDTO {
  items: LeaseChangeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeaseContractDTO {
  items: LeaseContractDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeaseRunDTO {
  items: LeaseRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeaveBlackoutDTO {
  items: LeaveBlackoutDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeaveMovementDTO {
  items: LeaveMovementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLeaveRequestDTO {
  items: LeaveRequestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLedger {
  items: Ledger[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLedgerSetDTO {
  items: LedgerSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLegalEntityDTO {
  items: LegalEntityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLegislativeDataGroupDTO {
  items: LegislativeDataGroupDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLiabilityInstrumentDTO {
  items: LiabilityInstrumentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLineDTO {
  items: LineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLocality {
  items: Locality[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLocation {
  items: Location[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLogEntry {
  items: LogEntry[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLotRecallDTO {
  items: LotRecallDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLotRecallTraceDTO {
  items: LotRecallTraceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLoyaltyAccrualDTO {
  items: LoyaltyAccrualDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLoyaltyMemberDTO {
  items: LoyaltyMemberDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageLoyaltyProgramDTO {
  items: LoyaltyProgramDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMRPSuggestionDTO {
  items: MRPSuggestionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageManualLineDTO {
  items: ManualLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMarketingCampaignDTO {
  items: MarketingCampaignDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMarketingSegmentDTO {
  items: MarketingSegmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMarketingSegmentMemberDTO {
  items: MarketingSegmentMemberDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMatchDTO {
  items: ProcurementMatchDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMatchExceptionDTO {
  items: MatchExceptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMaterialityPolicyDTO {
  items: MaterialityPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMatrixVersionDTO {
  items: MatrixVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMemberDTO {
  items: MemberDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMessageDTO {
  items: MessageDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMetricCertificationDTO {
  items: MetricCertificationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMetricDefinitionDTO {
  items: MetricDefinitionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMfgToolDTO {
  items: MfgToolDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMilestoneDTO {
  items: MilestoneDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageMyServiceChargeAllocationDTO {
  items: MyServiceChargeAllocationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageNamingTemplate {
  items: NamingTemplate[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageNotificationDTO {
  items: NotificationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageNotificationPolicyDTO {
  items: NotificationPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageObligationDTO {
  items: ObligationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageOfferDTO {
  items: OfferDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageOpportunityDTO {
  items: OpportunityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageOrderAmendmentDTO {
  items: OrderAmendmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageOrderDTO {
  items: OrderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageOverrideDTO {
  items: OverrideDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageOwnershipDTO {
  items: OwnershipDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSAuthHoldDTO {
  items: PMSAuthHoldDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSBoardAllowanceDTO {
  items: PMSBoardAllowanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSBoardPlanDTO {
  items: PMSBoardPlanDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSCancellationPolicyDTO {
  items: PMSCancellationPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSChannelDTO {
  items: PMSChannelDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSChannelExchangeDTO {
  items: PMSChannelExchangeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSCommissionAccrualDTO {
  items: PMSCommissionAccrualDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSCommissionAgreementDTO {
  items: PMSCommissionAgreementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSCommissionStatementDTO {
  items: PMSCommissionStatementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSDepositDTO {
  items: PMSDepositDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSFolioDTO {
  items: PMSFolioDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSGroupBlockDTO {
  items: PMSGroupBlockDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSGuestDTO {
  items: PMSGuestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSGuestMessageDTO {
  items: PMSGuestMessageDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSGuestRequestDTO {
  items: PMSGuestRequestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSHousekeepingBoardDTO {
  items: PMSHousekeepingBoardDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSHousekeepingTaskDTO {
  items: PMSHousekeepingTaskDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSKeyIssueDTO {
  items: PMSKeyIssueDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSNightAuditRunDTO {
  items: PMSNightAuditRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSOutOfOrderDTO {
  items: PMSOutOfOrderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSParityObservationDTO {
  items: PMSParityObservationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSPropertyDTO {
  items: PMSPropertyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSReservationDTO {
  items: PMSReservationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSRevenueCenterDTO {
  items: PMSRevenueCenterDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSRoomDTO {
  items: PMSRoomDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSRoomFaultDTO {
  items: PMSRoomFaultDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSRoomTypeDTO {
  items: PMSRoomTypeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSServiceBookingDTO {
  items: PMSServiceBookingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSServiceDTO {
  items: PMSServiceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSServiceResourceDTO {
  items: PMSServiceResourceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSTransferLegDTO {
  items: PMSTransferLegDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMSWaitlistEntryDTO {
  items: PMSWaitlistEntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePMScheduleDTO {
  items: PMScheduleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePODTO {
  items: PODTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSAllergenCodeDTO {
  items: POSAllergenCodeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSAnomalyExceptionDTO {
  items: POSAnomalyExceptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSCashDenominationDTO {
  items: POSCashDenominationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSCashSessionDTO {
  items: POSCashSessionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSCheckDTO {
  items: POSCheckDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSCustomerDepositDTO {
  items: POSCustomerDepositDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSDayCloseDTO {
  items: POSDayCloseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSDepositDTO {
  items: POSDepositDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSEmployeePurchaseDTO {
  items: POSEmployeePurchaseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSFiscalReceiptDTO {
  items: POSFiscalReceiptDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSFloorAreaDTO {
  items: POSFloorAreaDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSInstrumentDTO {
  items: POSInstrumentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSInstrumentMovementDTO {
  items: POSInstrumentMovementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSItemAllergenDTO {
  items: POSItemAllergenDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSItemAvailabilityDTO {
  items: POSItemAvailabilityDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSKDSTicketDTO {
  items: POSKDSTicketDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSMenuDTO {
  items: POSMenuDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSMenuVersionDTO {
  items: POSMenuVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSOperatorDayStatsDTO {
  items: POSOperatorDayStatsDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSOutletDTO {
  items: POSOutletDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSRevenueCenterDTO {
  items: POSRevenueCenterDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSSaleDTO {
  items: POSSaleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSSerialSaleDTO {
  items: POSSerialSaleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSStationDTO {
  items: POSStationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSTableDTO {
  items: POSTableDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSTenderTypeDTO {
  items: POSTenderTypeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSTerminalDTO {
  items: POSTerminalDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePOSTipDTO {
  items: POSTipDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePRDTO {
  items: PRDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePack {
  items: Pack[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageParLevelDTO {
  items: ParLevelDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyConsentDTO {
  items: PartyConsentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyDSRPolicyDTO {
  items: PartyDSRPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyDSRRequestDTO {
  items: PartyDSRRequestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyDTO {
  items: PartyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyMatchCandidateDTO {
  items: PartyMatchCandidateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyMergeDTO {
  items: PartyMergeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyRetentionRuleDTO {
  items: PartyRetentionRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartySuppressionDTO {
  items: PartySuppressionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePartyTimelineEntryDTO {
  items: PartyTimelineEntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePayGroupDTO {
  items: PayGroupDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePaymentBeneficiaryDTO {
  items: PaymentBeneficiaryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePaymentClaimDTO {
  items: PaymentClaimDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePaymentDTO {
  items: PaymentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePayslipDTO {
  items: PayslipDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePeriodDTO {
  items: PeriodDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePermitQuotaDTO {
  items: PermitQuotaDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePhaseDTO {
  items: PhaseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePickDTO {
  items: PickDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePlanningParameterDTO {
  items: PlanningParameterDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePolicyDTO {
  items: PolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePositionDTO {
  items: PositionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePostingAssertionDTO {
  items: PostingAssertionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePrecedenceVersionDTO {
  items: PrecedenceVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePreviewDTO {
  items: PreviewDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePriceExplanationSummaryDTO {
  items: PriceExplanationSummaryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePriceListDTO {
  items: PriceListDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePriceSetDTO {
  items: PriceSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePriceSetVersionDTO {
  items: PriceSetVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePricingCategoryDTO {
  items: PricingCategoryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProblemDTO {
  items: ProblemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProductionOrderDTO {
  items: ProductionOrderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProductionSettlementDTO {
  items: ProductionSettlementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProfileDTO {
  items: ProfileDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectAdvanceDTO {
  items: ProjectAdvanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectAdvanceRecoupmentDTO {
  items: ProjectAdvanceRecoupmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectAllocationDTO {
  items: ProjectAllocationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectBaselineDTO {
  items: ProjectBaselineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectDTO {
  items: ProjectDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectDependencyDTO {
  items: ProjectDependencyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectResourceDTO {
  items: ProjectResourceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectRetentionDTO {
  items: ProjectRetentionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectRiskDTO {
  items: ProjectRiskDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectSubcontractDTO {
  items: ProjectSubcontractDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectVariationDTO {
  items: ProjectVariationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageProjectWIPRunDTO {
  items: ProjectWIPRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PagePromotionDTO {
  items: PromotionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageQualityInspectionDTO {
  items: QualityInspectionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageQuery {
  cursor?: string;
  limit?: number;
}

export interface PageQueueDTO {
  items: QueueDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageQueueItemDTO {
  items: QueueItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageQuoteDTO {
  items: SalesQuoteDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageQuoteRevisionDTO {
  items: QuoteRevisionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRFQDTO {
  items: RFQDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMAccuracyRunDTO {
  items: RVMAccuracyRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMCompetitorRateDTO {
  items: RVMCompetitorRateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMDisplacementDTO {
  items: RVMDisplacementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMForecastDTO {
  items: RVMForecastDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMForecastVersionDTO {
  items: RVMForecastVersionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMHurdleDTO {
  items: RVMHurdleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMOnTheBooksSnapshotDTO {
  items: RVMOnTheBooksSnapshotDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMSegmentDTO {
  items: RVMSegmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMSegmentMappingDTO {
  items: RVMSegmentMappingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRVMStreamDTO {
  items: RVMStreamDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRateDTO {
  items: RateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReasonCodeDTO {
  items: ReasonCodeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReasonSetDTO {
  items: ReasonSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRebateAccrualDTO {
  items: RebateAccrualDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRebateProgramDTO {
  items: RebateProgramDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReceiptDTO {
  items: ReceiptDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRecipeDTO {
  items: RecipeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRecurringItemDTO {
  items: RecurringItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReferenceDataPageItem {
  items: ReferenceDataPageItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReferenceSetDTO {
  items: ReferenceSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRegimeDTO {
  items: RegimeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRegistrationDTO {
  items: RegistrationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRemittanceDTO {
  items: RemittanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReplenishmentPolicyDTO {
  items: ReplenishmentPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReplenishmentSuggestionDTO {
  items: ReplenishmentSuggestionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReportOutputDTO {
  items: ReportOutputDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReportingCurrencyDTO {
  items: ReportingCurrencyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRequisitionDTO {
  items: RequisitionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReservationDetailDTO {
  items: ReservationDetailDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRetroEventDTO {
  items: RetroEventDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRetroLineDTO {
  items: RetroLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageReturnDTO {
  items: ReturnDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRevRecScheduleDTO {
  items: RevRecScheduleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRevpolicyPolicyDTO {
  items: RevpolicyPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRosterDTO {
  items: RosterDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRoutingDTO {
  items: RoutingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRuleEvaluationDTO {
  items: RuleEvaluationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRunAnomalyDTO {
  items: RunAnomalyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRunDTO {
  items: RunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRunDTO2 {
  items: PayrollRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRunDTO3 {
  items: ConsolRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageRunVarianceDTO {
  items: RunVarianceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSLABreachForecastDTO {
  items: SLABreachForecastDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSLAPolicyDTO {
  items: SLAPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSSOProviderDTO {
  items: SSOProviderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSSPDTO {
  items: SSPDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSamplingPlanDTO {
  items: SamplingPlanDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageScheduleRunDTO {
  items: ScheduleRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageScorecardDTO {
  items: ScorecardDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSegmentSecurityRuleDTO {
  items: SegmentSecurityRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSegmentValueDTO {
  items: SegmentValueDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceChargeAllocationDTO {
  items: ServiceChargeAllocationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceChargePointDTO {
  items: ServiceChargePointDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceChargePoolDTO {
  items: ServiceChargePoolDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceChargeSchemeDTO {
  items: ServiceChargeSchemeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceContractDTO {
  items: ServiceContractDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceGoodwillDTO {
  items: ServiceGoodwillDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceProviderRelationshipDTO {
  items: ServiceProviderRelationshipDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceRateCardDTO {
  items: ServiceRateCardDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageServiceWorkOrderDTO {
  items: ServiceWorkOrderDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSessionDTO {
  items: BankrecSessionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageShiftDefinitionDTO {
  items: ShiftDefinitionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageShiftPatternDTO {
  items: ShiftPatternDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageShipmentDTO {
  items: ShipmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSpendFactDTO {
  items: SpendFactDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStaffingRuleDTO {
  items: StaffingRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStandardCostRollupDTO {
  items: StandardCostRollupDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStatementLineDTO {
  items: StatementLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStatutoryMappingDTO {
  items: StatutoryMappingDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStatutoryReturnDTO {
  items: StatutoryReturnDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStatutoryReturnLineDTO {
  items: StatutoryReturnLineDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStatutoryRuleDTO {
  items: StatutoryRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockAdjustmentDTO {
  items: StockAdjustmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockAllocationDTO {
  items: StockAllocationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockBalanceDTO {
  items: StockBalanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockCountFreezeDTO {
  items: StockCountFreezeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockLedgerEntryDTO {
  items: StockLedgerEntryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockLotDTO {
  items: StockLotDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockRequisitionDTO {
  items: StockRequisitionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockSerialDTO {
  items: StockSerialDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockShortageDTO {
  items: StockShortageDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStockTransferDetailDTO {
  items: StockTransferDetailDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageStructureDTO {
  items: StructureDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSubdivision {
  items: Subdivision[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSubscriptionDTO {
  items: SubscriptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSubscriptionDTO2 {
  items: SalesSubscriptionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSubscriptionPlanDTO {
  items: SubscriptionPlanDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSupplierContractDTO {
  items: SupplierContractDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSupplierReturnDTO {
  items: SupplierReturnDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSupplierRiskRowDTO {
  items: SupplierRiskRowDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSupplierScreeningDTO {
  items: SupplierScreeningDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSuspenseItemDTO {
  items: SuspenseItemDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageSuspensePositionDTO {
  items: SuspensePositionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTariffClassificationDTO {
  items: TariffClassificationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTaskDTO {
  items: ProjectsTaskDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTaxReportingUnitDTO {
  items: TaxReportingUnitDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTaxReturnDTO {
  items: TaxReturnDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTemplateDTO {
  items: GlTemplateDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTenantActivationDTO {
  items: TenantActivationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTenantSummary {
  items: TenantSummary[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTenantUserSummary {
  items: TenantUserSummary[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTicketDTO {
  items: TicketDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTicketLaborDTO {
  items: TicketLaborDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTicketPartDTO {
  items: TicketPartDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTimesheetDTO {
  items: TimesheetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTolerance {
  items: Tolerance[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTpPolicyDTO {
  items: TpPolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTradeAgreementDTO {
  items: TradeAgreementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTransferInstructionDTO {
  items: TransferInstructionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTravelAdvanceDTO {
  items: TravelAdvanceDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTravelRequestDTO {
  items: TravelRequestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryAccountProfileDTO {
  items: TreasuryAccountProfileDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryDebtChangeRequestDTO {
  items: TreasuryDebtChangeRequestDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryMinimumBalanceChangeDTO {
  items: TreasuryMinimumBalanceChangeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryPositionRunDTO {
  items: TreasuryPositionRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryScenarioPromotionDTO {
  items: TreasuryScenarioPromotionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasurySweepSimulationDTO {
  items: TreasurySweepSimulationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasurySweepStructureDTO {
  items: TreasurySweepStructureDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryWalletDTO {
  items: TreasuryWalletDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryWalletReconciliationDTO {
  items: TreasuryWalletReconciliationDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageTreasuryWalletSplitFlagDTO {
  items: TreasuryWalletSplitFlagDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageUnit {
  items: Unit[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageUnresolvedDepletionDTO {
  items: UnresolvedDepletionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageUsageVarianceRunDTO {
  items: UsageVarianceRunDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageUserDTO {
  items: UserDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageUserDTO2 {
  items: PortalUserDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageValueSetDTO {
  items: ValueSetDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageVanDTO {
  items: VanDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageVariancePolicyDTO {
  items: VariancePolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageVendorDTO {
  items: VendorDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWageBaseDTO {
  items: WageBaseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWalletCapturePolicyDTO {
  items: WalletCapturePolicyDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWalletCountDTO {
  items: WalletCountDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWalletReplenishmentDTO {
  items: WalletReplenishmentDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWalletTransactionDTO {
  items: WalletTransactionDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWarehouseDTO {
  items: WarehouseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWarehouseZoneDTO {
  items: WarehouseZoneDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWasteReasonCodeDTO {
  items: WasteReasonCodeDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWasteRecordDTO {
  items: WasteRecordDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWebhookDeliveryDTO {
  items: WebhookDeliveryDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWebhookEndpointDTO {
  items: WebhookEndpointDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWorkCenterDTO {
  items: WorkCenterDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWorkPermitDTO {
  items: WorkPermitDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageWorkingTimeRuleDTO {
  items: WorkingTimeRuleDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageYearEndCloseDTO {
  items: YearEndCloseDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PageYearEndStatementDTO {
  items: YearEndStatementDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ParLevelDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  parQty: string;
  minQty?: string | null;
  orderMultiple?: string | null;
  replenishWeekday?: number | null;
  sourceWarehouseId?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  note?: string;
  rowVersion: number;
}

export interface ParLevelInput {
  itemId: number;
  parQty: string;
  minQty?: string;
}

export interface ParRunLineDTO {
  itemId: number;
  itemSku?: string;
  parQty: string;
  onHandQty: string;
  onOrderQty: string;
  shortfallQty: string;
  suggestedQty: string;
  sourceWarehouseId?: number | null;
}

export interface ParRunRequest {
  companyId: number;
  warehouseId: number;
  on?: string;
  itemIds?: number[];
  includeOnOrder?: boolean | null;
}

export interface ParRunResultDTO {
  warehouseId: number;
  on: string;
  lines: ParRunLineDTO[];
  unresolved?: ParRunUnresolvedDTO[];
}

export interface ParRunToRequisitionRequest {
  companyId: number;
  warehouseId: number;
  on?: string;
  itemIds?: number[];
  includeOnOrder?: boolean | null;
  requiredOn: string;
  sourceWarehouseId?: number;
  memo?: string;
}

export interface ParRunUnresolvedDTO {
  itemId: number;
  itemSku?: string;
  reason: string;
}

export interface PartyConsentDTO {
  id: number;
  partyId: number;
  channelId: number;
  channelCode?: string;
  purposeId: number;
  purposeCode?: string;
  state: 'granted' | 'withdrawn' | 'pending' | 'expired' | 'not_asked';
  basis: 'consent' | 'contract' | 'legal_obligation' | 'legitimate_interest' | 'vital_interest' | 'public_task';
  effectiveFrom: string;
  effectiveTo?: string;
  expiresOn?: string;
  supersededBy?: number | null;
  source: 'portal' | 'web_form' | 'import' | 'verbal' | 'contract' | 'api' | 'staff' | 'merge' | 'system';
  capturedAt: string;
  capturedIpMasked?: string;
  evidenceRef?: string;
  evidenceText?: string;
  confirmedAt?: string | null;
  withdrawnAt?: string | null;
  withdrawnReason?: string;
  derivedFromConsentId?: number | null;
  mergeId?: number | null;
  rowVersion: number;
}

export interface PartyConsentMatrixCellDTO {
  channelCode: string;
  channelName: string;
  purposeCode: string;
  purposeName: string;
  state: 'granted' | 'withdrawn' | 'pending' | 'expired' | 'not_asked';
  basis?: 'consent' | 'contract' | 'legal_obligation' | 'legitimate_interest' | 'vital_interest' | 'public_task';
  consentId?: number;
  expiresOn?: string;
  permitted: boolean;
}

export interface PartyDSRCoverageDTO {
  linkType: string;
  label: string;
  recordCount: number;
  taskStatus: string;
}

export interface PartyDSRDetailDTO {
  id: number;
  dsrNo: string;
  partyId: number;
  kind: string;
  status: string;
  channel: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterIsSubject: boolean;
  partyName?: string;
  receivedOn: string;
  dueOn: string;
  extensionGrantedDays: number;
  effectiveDueOn: string;
  overdueDays?: number;
  daysRemaining: number;
  identityVerifiedAt?: string | null;
  closedAt?: string | null;
  outcome?: string;
  warnedAt?: string | null;
  breachedAt?: string | null;
  rowVersion: number;
  requestText?: string;
  policyId?: number | null;
  responseDaysApplied: number;
  extensionReason?: string;
  refusalReasonCode?: string;
  refusalNote?: string;
  identityVerificationNote?: string;
  closingNote?: string;
  erasureNote?: string;
  identityEvidenceIdentifierId?: number | null;
  exportAttachmentId?: number | null;
  bundleSha256?: string;
  tasks: PartyDSRTaskDTO[];
  coverage: PartyDSRCoverageDTO[];
  policy?: PartyDSRPolicyDTO | null;
  identifiers: PartyIdentifierDTO[];
}

export interface PartyDSRPolicyDTO {
  id: number;
  jurisdiction: string;
  kind: string;
  responseDays: number;
  extensionDays: number;
  warnDaysBefore: number;
  identityVerificationRequired: boolean;
  source: string;
  statuteRef: string;
  rowVersion: number;
}

export interface PartyDSRRequestDTO {
  id: number;
  dsrNo: string;
  partyId: number;
  kind: string;
  status: string;
  channel: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterIsSubject: boolean;
  partyName?: string;
  receivedOn: string;
  dueOn: string;
  extensionGrantedDays: number;
  effectiveDueOn: string;
  overdueDays?: number;
  daysRemaining: number;
  identityVerifiedAt?: string | null;
  closedAt?: string | null;
  outcome?: string;
  warnedAt?: string | null;
  breachedAt?: string | null;
  rowVersion: number;
}

export interface PartyDSRTaskDTO {
  id: number;
  dsrId: number;
  seq: number;
  code: string;
  title: string;
  linkType?: string;
  assignedTo?: number | null;
  status: string;
  completedAt?: string | null;
  completedBy?: number | null;
  note?: string;
  rowVersion: number;
}

export interface PartyDTO {
  id: number;
  kind: 'person' | 'organisation';
  displayName: string;
  legalName?: string;
  givenName?: string;
  familyName?: string;
  salutation?: string;
  email?: string;
  phone?: string;
  address?: Record<string, unknown>;
  countryCode?: string;
  preferredLocale?: string;
  status: 'active' | 'inactive' | 'blocked';
  linkCount: number;
  mergedIntoPartyId?: number | null;
  mergedAt?: string | null;
  erasedAt?: string | null;
  isDeleted: boolean;
  rowVersion: number;
}

export interface PartyDeleteQuery {
  rowVersion: number;
}

export interface PartyDetailDTO {
  party: PartyDTO;
  links: PartyLinkDTO[];
  identifiers: PartyIdentifierDTO[];
  openMatchCandidates: PartyMatchCandidateDTO[];
}

export interface PartyIdentifierDTO {
  id: number;
  partyId: number;
  kind: 'national_id' | 'passport' | 'tax_id' | 'driving_licence' | 'company_registration' | 'loyalty_number' | 'external_key';
  issuingCountry?: string;
  valueMasked: string;
  verifiedAt?: string | null;
  verifiedBy?: number | null;
  validFrom?: string;
  validTo?: string;
  rowVersion: number;
}

export interface PartyLinkDTO {
  id: number;
  partyId: number;
  linkType: string;
  linkLabel?: string;
  module?: string;
  recordId: number;
  isPrimary: boolean;
  title?: string;
  subtitle?: string;
  href?: string;
  status?: string;
  isOrphaned: boolean;
  linkedByRule: 'manual' | 'convert' | 'backfill' | 'merge' | 'auto_exact' | 'import';
  mergeId?: number | null;
  rowVersion: number;
}

export interface PartyLinkTypeDTO {
  code: string;
  label: string;
  module: string;
  retentionRuleCount: number;
  linkCount: number;
}

export interface PartyMatchCandidateDTO {
  id: number;
  partyAId: number;
  partyAName: string;
  partyBId: number;
  partyBName: string;
  method: 'identifier' | 'email' | 'phone' | 'name_address';
  score: string;
  signals: string[];
  status: 'open' | 'dismissed' | 'merged';
  dismissedReason?: string;
  dismissedAt?: string | null;
  mergeId?: number | null;
  detectedBy: string;
  detectedOn: string;
  rowVersion: number;
}

export interface PartyMergeDTO {
  id: number;
  status: 'draft' | 'submitted' | 'approved' | 'committed' | 'rejected' | 'cancelled' | 'reversed';
  sourcePartyId: number;
  sourcePartyName?: string;
  survivorPartyId: number;
  survivorPartyName?: string;
  candidateId?: number | null;
  candidateScore?: string;
  mergeNo?: string;
  reasonCode?: string;
  reasonNote?: string;
  linksMoved: number;
  consentsWritten: number;
  fieldsTaken: number;
  submittedAt?: string | null;
  approvedAt?: string | null;
  committedAt?: string | null;
  reversedAt?: string | null;
  reversibleUntil?: string;
  rowVersion: number;
}

export interface PartyMergeDetailDTO {
  merge: PartyMergeDTO;
  fieldChoices: PartyMergeFieldChoiceDTO[];
  consentOutcome: ConsentOutcomeEntry[];
  linksPreview?: PartyMergeLinkPreviewDTO[];
  records?: PartyMergeRecordDTO[];
}

export interface PartyMergeFieldChoiceDTO {
  fieldName: string;
  survivorValueBefore: string;
  sourceValue: string;
  chosenValue: string;
  chosenFrom: 'survivor' | 'source' | 'manual';
}

export interface PartyMergeLinkPreviewDTO {
  linkType: string;
  recordId: number;
  title?: string;
  isPrimary: boolean;
  wouldCollide: boolean;
}

export interface PartyMergeRecordDTO {
  linkType: string;
  recordId: number;
  fromPartyId: number;
  toPartyId: number;
  wasPrimary: boolean;
  outcome: 'moved' | 'collided' | 'skipped';
}

export interface PartyMergeSettingsDTO {
  companyId: number;
  reversalWindowDays: number;
  requireApproval: boolean;
  autoProposeThreshold: string;
  rowVersion: number;
}

export interface PartyProfileDTO {
  party: PartyDTO;
  identifiers: PartyIdentifierDTO[];
  sections: PartyProfileSectionDTO[];
  openDsrCount: number;
}

export interface PartyProfileSectionDTO {
  code: string;
  label: string;
  count: number;
  items: LinkedRecordDescription[];
  href?: string;
}

export interface PartyRef {
  kind: string;
  id: number;
  class: 'registered' | 'unregistered' | 'export' | 'government' | 'exempt' | 'non_resident';
  groupId?: number | null;
  jurisdiction: JurisdictionRef;
  registrationNo: string;
  isRegistered: boolean;
  certificateId?: number | null;
}

export interface PartyRetentionRuleDTO {
  id: number;
  linkType: string;
  tableName: string;
  columnName: string;
  treatment: string;
  basis: string;
  retainYears?: number | null;
  anchor: string;
  statuteRef?: string;
  note?: string;
  source: string;
  rowVersion: number;
  isOrphaned: boolean;
}

export interface PartySuppressionDTO {
  id: number;
  channelId: number;
  channelCode?: string;
  destinationMasked: string;
  reason: 'unsubscribe' | 'hard_bounce' | 'spam_complaint' | 'manual' | 'dsr_objection' | 'invalid';
  scope: 'global' | 'purpose';
  purposeId?: number | null;
  purposeCode?: string;
  partyId?: number | null;
  note?: string;
  suppressedAt: string;
  releasedAt?: string | null;
  releasedBy?: number | null;
  releaseReason?: string;
  rowVersion: number;
}

export interface PartyTimelineEntryDTO {
  at: string;
  kind: string;
  title: string;
  detail?: string;
  href?: string;
}

export interface PatchAIAgentRequest {
  isEnabled?: boolean | null;
  recipientPositionIds?: number[] | null;
  rowVersion?: number;
}

export interface PatchAICapabilityRequest {
  confidenceThreshold?: string | null;
  tolerancePct?: string | null;
  toleranceAbs?: string | null;
  isEnabled?: boolean | null;
}

export interface PatchAIConfigAnomalyRuleRequest {
  isEnabled?: boolean | null;
  severity?: string | null;
  params?: Record<string, unknown> | null;
  excludedEntities?: string[] | null;
  rowVersion?: number;
}

export interface PatchAISettingsRequest {
  companyId: number;
  aiEnabled?: boolean | null;
  maxAutonomy?: 'assistive' | 'supervised' | 'delegated';
  delegationMinSamples?: number | null;
  delegationMinAccuracy?: string | null;
  acknowledgeRedaction?: boolean;
}

export interface PatchTenantRequest {
  name?: string | null;
  timezone?: string | null;
  planId?: string | null;
  featureFlags?: Record<string, unknown>;
  customLimits?: Record<string, unknown>;
}

export interface PauseCaseRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface PauseDunningRequest {
  reason: string;
}

export interface PayGroupDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  frequency: string;
  periodAnchorDay: number;
  paymentLagDays: number;
  calendarId?: number | null;
  bankAccountId?: number | null;
  isDefault: boolean;
  isActive: boolean;
  note?: string;
  rowVersion: number;
}

export interface PayReturnRequest {
  paidOn: string;
  paymentDocType: string;
  paymentDocId: number;
}

export interface PayTravelAdvanceRequest {
  paymentDate?: string;
}

export interface PaymentBeneficiaryDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  counterpartyKind: 'vendor' | 'employee' | 'customer' | 'tax_authority' | 'related_company' | 'other';
  counterpartyId?: number | null;
  counterpartyName?: string;
  vendorBankAccountId?: number | null;
  delegatesToVendorBank: boolean;
  bankName?: string;
  branchName?: string;
  accountName?: string;
  accountNumberMasked?: string;
  hasAccountNumber: boolean;
  iban?: string;
  swift?: string;
  currency?: string;
  countryCode?: string;
  bankDetailVisible: boolean;
  verificationStatus: 'unverified' | 'pending' | 'verified' | 'failed' | 'blocked';
  verificationMethod?: 'penny' | 'callback' | 'document' | 'existing_relationship' | 'manual';
  verificationSource?: string;
  verifiedAt?: string | null;
  verifiedBy?: number | null;
  verificationNote?: string;
  detailsEffectiveAt?: string | null;
  detailsChangedAt?: string | null;
  coolingOff: boolean;
  payable: boolean;
  payableBlockedBy?: string;
  structuralWarning?: string;
  isActive: boolean;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface PaymentClaimDTO {
  id: number;
  companyId: number;
  amount: string;
  currency: string;
  bankReference?: string;
  paidOn: string;
  note?: string;
  attachmentId?: number | null;
  status: string;
  receiptId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface PaymentDTO {
  id: string;
  tenantId: string;
  tenantSlug: string;
  invoiceId?: string | null;
  invoiceNumber: string;
  provider: string;
  providerRef?: string | null;
  method: 'bank_transfer' | 'cheque' | 'cash' | 'card';
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency: string;
  receivedOn: string;
  recordedBy?: string | null;
  note?: string | null;
  createdAt: string;
}

export interface PaymentListQuery {
  tenantId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface PaymentStatusResponse {
  ledger?: VendorLedger | null;
  invoices?: PageContractorInvoiceDTO | null;
}

export interface PayrollBankFileProfileDTO {
  id: number;
  code: string;
  name: string;
  bankCode: string;
  format: 'csv' | 'fixed_width';
  delimiter: string;
  includeHeader: boolean;
  lineEnding: string;
  fileExtension: string;
  columns: PayrollColumnSpec[];
  notes: string;
  isActive: boolean;
  rowVersion: number;
}

export interface PayrollBankFileResponse {
  runId: number;
  runNo?: string;
  profileCode: string;
  profileName: string;
  filename: string;
  contentType: string;
  content: string;
  sha256: string;
  rows: number;
  disclaimer: string;
}

export interface PayrollColumnSpec {
  header: string;
  source: string;
  width?: number;
  align?: string;
  pad?: string;
  format?: string;
  literal?: string;
}

export interface PayrollCreateRunRequest {
  companyId: number;
  runType?: 'regular' | 'off_cycle';
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  netPayPayableAccountId: number;
  bankFileProfileId?: number | null;
  payGroupId?: number | null;
  notes?: string;
}

export interface PayrollDecideRunRequest {
  reject?: boolean;
  comment?: string;
}

export interface PayrollExplainVarianceRequest {
  code: string;
  note?: string;
}

export interface PayrollFileReturnRequest {
  filingReference?: string;
}

export interface PayrollListBankFileProfilesQuery {
  cursor?: string;
  limit?: number;
}

export interface PayrollListRunsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  status?: 'draft' | 'calculated' | 'reviewed' | 'approved' | 'posted' | 'disbursed';
  runType?: 'regular' | 'off_cycle';
}

export interface PayrollRunDTO {
  id: number;
  runNo?: string | null;
  companyId: number;
  runType: 'regular' | 'off_cycle';
  status: 'draft' | 'calculated' | 'reviewed' | 'approved' | 'posted' | 'disbursed';
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  netPayPayableAccountId: number;
  bankFileProfileId?: number | null;
  fiscalPeriodId?: number | null;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  grossTotal: string;
  deductionTotal: string;
  employerCostTotal: string;
  netTotal: string;
  employeeCount: number;
  exceptionCount: number;
  notes: string;
  payGroupId?: number | null;
  jurisdictionId?: number | null;
  packId?: string | null;
  packVersion?: number | null;
  bankAccountId?: number | null;
  disbursementJournalEntryId?: number | null;
  varianceStatus: string;
  anomalyStatus: string;
  createdAt: string;
  rowVersion: number;
  payslips?: PayslipDTO[];
}

export interface PayslipDTO {
  id: number;
  runId: number;
  runNo?: string | null;
  runStatus?: 'draft' | 'calculated' | 'reviewed' | 'approved' | 'posted' | 'disbursed';
  employeeId: number;
  employeeNo: string;
  employeeName: string;
  periodStart: string;
  periodEnd: string;
  periodDays: number;
  payableDays: string;
  unpaidLeaveDays: string;
  grossAmount: string;
  deductionTotal: string;
  employerCostTotal: string;
  netAmount: string;
  hasException: boolean;
  exceptionNotes?: string;
  segmentCount: number;
  segments?: PayslipSegment[];
  payGroupId?: number | null;
  jurisdictionId?: number | null;
  locationId?: number | null;
  lines?: PayslipLineDTO[];
}

export interface PayslipLineDTO {
  componentId: number;
  componentCode: string;
  componentName: string;
  componentType: 'earning' | 'deduction' | 'employer_cost';
  formulaType: 'fixed' | 'percent_of_base' | 'per_unit_overtime' | 'slab' | 'pool_share';
  amount: string;
  trace: Record<string, unknown>;
  lineClass: 'current' | 'retro' | 'pool_share';
  ruleSource: 'tenant' | 'pack' | 'legacy';
  ruleCode?: string;
  packId?: string | null;
  packVersion?: number | null;
  sourceCitation?: string;
  ruleEffectiveFrom?: string | null;
  reviewDueOn?: string | null;
  segmentIndex: number;
  originPeriodStart?: string | null;
  originPeriodEnd?: string | null;
  originRunId?: number | null;
}

export interface PayslipSegment {
  index: number;
  assignmentId: number;
  from: string;
  to: string;
  days: number;
  payableDays: string;
  basicSalary: string;
  proratedBasic: string;
  pensionable: boolean;
  companyId: number;
  payGroupId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  locationId?: number | null;
}

export interface PeriodDTO {
  id: number;
  calendarId: number;
  companyId: number;
  fiscalYear: number;
  periodNo: number;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'soft_closed' | 'closed' | 'permanently_closed';
  closedAt?: string | null;
  closedBy?: number | null;
  closeReason: string;
  reopenedAt?: string | null;
  reopenedBy?: number | null;
  rowVersion: number;
  isAdjusting: boolean;
  quarter?: number | null;
  permanentlyClosedAt?: string | null;
  permanentlyClosedBy?: number | null;
  permanentCloseRequestId?: number | null;
  closeReasonCode?: string;
  reopenReasonCode?: string;
  reopenReason?: string;
  reopenExpectedUntil?: string | null;
  reopenAuthorisedBy?: number | null;
  reopenPositionId?: number | null;
}

export interface PeriodsCreated {
  periods: PeriodDTO[];
}

export interface PermanentCloseRequest {
  reasonCode: string;
  note?: string;
}

export interface PermitQuotaDTO {
  id: number;
  companyId: number;
  jurisdictionId: number;
  quotaCategory: string;
  cap: number;
  consumed: number;
  available: number;
  source: string;
  sourceCitation?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  note?: string;
  rowVersion: number;
}

export interface PhaseDTO {
  id: number;
  projectId: number;
  name: string;
  sequence: number;
  startDate?: string;
  endDate?: string;
  status: 'planned' | 'active' | 'completed';
  budgetAmount: string;
  rowVersion: number;
}

export interface PhaseProfitDTO {
  phaseId: number;
  name: string;
  budget: string;
  actualTimesheet: string;
}

export interface PickDTO {
  id: number;
  pickNo?: string | null;
  companyId: number;
  orderId: number;
  warehouseId: number;
  status: 'open' | 'picking' | 'picked' | 'cancelled';
  pickerUserId?: number | null;
  pickDate: string;
  memo?: string;
  completedAt?: string | null;
  completedBy?: number | null;
  createdAt: string;
  rowVersion: number;
  lines?: PickLineDTO[];
}

export interface PickLineDTO {
  id: number;
  lineNo: number;
  allocationId: number;
  orderLineId: number;
  itemId: number;
  warehouseId: number;
  quantity: string;
  pickedQuantity: string;
  shortReasonCode?: string;
}

export interface PickLineInput {
  allocationId: number;
  quantity: string;
}

export interface PickPlanDTO {
  strategy: 'lot_strategy' | 'fixed_bin_first' | 'nearest' | 'single_bin';
  lines: PickPlanLineDTO[];
}

export interface PickPlanLine {
  itemId: number;
  qty: string;
}

export interface PickPlanLineDTO {
  itemId: number;
  qty: string;
  sources: PickSourceDTO[];
  shortfall?: string;
}

export interface PickPlanRequest {
  warehouseId: number;
  lines: PickPlanLine[];
}

export interface PickSourceDTO {
  binId: number;
  binCode: string;
  lotId?: number | null;
  lotNo?: string;
  qty: string;
}

export interface PipelineQuery {
  ownerUserId?: number;
}

export interface PipelineReport {
  stages: PipelineStageRow[];
  totalAmount: string;
  totalWeighted: string;
}

export interface PipelineStage {
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'converted' | 'superseded';
  count: number;
  value: string;
}

export interface PipelineStageRow {
  stageCode: string;
  stageName: string;
  count: number;
  amount: string;
  weightedAmount: string;
}

export interface Place {
  governing: Jurisdiction;
  chain: Jurisdiction[];
  bindings: RegimeBinding[];
  fallback: boolean;
}

export interface Plan {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  code: string;
  name: string;
  description: string;
  price: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency: string;
  billingInterval: 'monthly' | 'annual';
  trialDays: number;
  maxUsers?: number | null;
  modules: unknown[];
  limits: Record<string, unknown>;
  isActive: boolean;
  sortOrder: number;
}

export interface PlanCreateRequest {
  code: string;
  name: string;
  description: string;
  price: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency: string;
  billingInterval: string;
  trialDays: number;
  maxUsers?: number | null;
  modules?: string[];
  limits?: Record<string, unknown>;
  sortOrder: number;
}

export interface PlanStep {
  ref: string;
  kind: 'create_legal_entity' | 'ensure_primary_ledger' | 'create_division' | 'create_business_unit' | 'create_coa_structure' | 'create_calendar';
  explanation?: string;
  legalEntity?: LegalEntityStep | null;
  primaryLedger?: PrimaryLedgerStep | null;
  division?: DivisionStep | null;
  businessUnit?: BusinessUnitStep | null;
  coaStructure?: CoaStructureStep | null;
  calendar?: CalendarStep | null;
}

export interface PlanUpdateRequest {
  name?: string | null;
  description?: string | null;
  price?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency?: string | null;
  billingInterval?: string | null;
  trialDays?: number | null;
  maxUsers?: number | null;
  modules?: string[];
  limits?: Record<string, unknown>;
  isActive?: boolean | null;
  sortOrder?: number | null;
}

export interface PlanningParameterDTO {
  id: number;
  companyId: number;
  itemId: number;
  lotSizingRule: string;
  fixedOrderQuantity?: string | null;
  minQuantity?: string | null;
  maxQuantity?: string | null;
  periodOfSupplyDays?: number | null;
  orderMultiple?: string | null;
  planningTimeFenceDays: number;
  demandTimeFenceDays: number;
  sourcePolicy: string;
  leadTimeDays?: number | null;
  safetyLeadTimeDays: number;
  isActive: boolean;
  rowVersion: number;
}

export interface PolicyDTO {
  id: number;
  docType: string;
  companyId?: number | null;
  name: string;
  rules: Rule[];
  priority: number;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface PolicyRequest {
  eventKind: string;
  channels?: string;
  digest?: string;
  escalateAfterHours?: number;
  suppressDuplicatesForMinutes?: number;
  isActive?: boolean | null;
}

export interface PortalCSATRequest {
  satisfied: boolean;
}

export interface PortalConfirmPORequest {
  expectedDate?: string;
  reference?: string;
}

export interface PortalCreateCaseRequest {
  subject: string;
  description?: string;
  category?: string;
  priority?: string;
}

export interface PortalLoginResult {
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  user: PortalUserDTO;
}

export interface PortalMeResponse {
  user: PortalUserDTO;
  companies: CompanyDTO[];
}

export interface PortalOrderLineInput {
  itemId: number;
  quantity: string;
}

export interface PortalRefreshRequest {
  refreshToken: string;
}

export interface PortalRejectQuoteRequest {
  reason: string;
}

export interface PortalRespondCaseRequest {
  body: string;
  rowVersion: number;
}

export interface PortalSettingsDTO {
  companyId: number;
  defaultWarehouseId?: number | null;
  defaultRevenueAccountId?: number | null;
  rowVersion: number;
}

export interface PortalTimesheetLineInput {
  projectId: number;
  taskId?: number | null;
  workDate: string;
  hours: string;
  note?: string;
}

export interface PortalUserDTO {
  id: number;
  email: string;
  name: string;
  status: 'invited' | 'active' | 'suspended';
  counterpartyType: 'customer' | 'supplier' | 'contractor';
  counterpartyId?: number | null;
  counterpartyName?: string;
  lastLoginAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface PositionChartDTO {
  companyId: number;
  asAt: string;
  roots: PositionChartNode[];
}

export interface PositionChartNode {
  id: number;
  code: string;
  name: string;
  companyId: number;
  jobId: number;
  jobName?: string;
  gradeId?: number | null;
  gradeCode?: string;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  locationId?: number | null;
  reportsToPositionId?: number | null;
  headcount: number;
  status: string;
  note: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
  asAt: string;
  holders: PositionHolderDTO[];
  vacantSlots: number;
  children: PositionChartNode[];
}

export interface PositionChartQuery {
  companyId: number;
  rootPositionId?: number | null;
  asAt?: string;
}

export interface PositionDTO {
  id: number;
  code: string;
  name: string;
  companyId: number;
  jobId: number;
  jobName?: string;
  gradeId?: number | null;
  gradeCode?: string;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  locationId?: number | null;
  reportsToPositionId?: number | null;
  headcount: number;
  status: string;
  note: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
  asAt: string;
  holders: PositionHolderDTO[];
  vacantSlots: number;
}

export interface PositionDiffQuery {
  against?: number;
}

export interface PositionGetQuery {
  asAt?: string;
}

export interface PositionHolderDTO {
  assignmentId: number;
  employeeId: number;
  employeeName: string;
  assignmentType: string;
  fte: string;
  effectiveFrom: string;
}

export interface PositionPreviewQuery {
  companyId: number;
  asOf?: string;
  baseCurrency?: string;
  includeInactive?: boolean | null;
}

export interface PositionSourceDTO {
  source: string;
  label: string;
  amount: string;
}

export interface PostCostRunRequest {
  note?: string;
}

export interface PostExpenseClaimRequest {
  postingDate?: string;
}

export interface PostInvoiceRequest {
  postingDate?: string;
}

export interface PostPMSFolioChargeRequest {
  kind: 'room' | 'tax' | 'service_charge' | 'levy' | 'ancillary' | 'pos' | 'deposit' | 'payment' | 'allowance' | 'transfer_in' | 'transfer_out' | 'package_draw' | 'package_relief';
  businessDate?: string;
  revenueCenterId?: number | null;
  outletId?: number | null;
  description?: string;
  quantity?: string;
  unitPrice?: string;
  netAmount?: string;
  taxAmount?: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  postingSource?: 'pms' | 'pos' | 'channel' | 'import';
  sourceDocType?: string;
  sourceDocId?: number | null;
  windowNo?: number;
  lateChargeReason?: string;
  lateChargeNote?: string;
}

export interface PostPOSDayCloseRequest {
  outletId: number;
  businessDate?: string;
}

export interface PostReturnRequest {
  rowVersion: number;
}

export interface PostSuspenseItemRequest {
  positionCode: string;
  companyId: number;
  entryDate: string;
  amount: string;
  counterAccountId: number;
  counterDimensions?: DimensionsInput;
  sourceModule?: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  reasonCode?: string;
  description?: string;
}

export interface PostWalletCountRequest {
  witnessedBy: number;
  varianceReasonCode?: string;
  varianceOwnerUserId?: number | null;
  varianceNote?: string;
  rowVersion: number;
}

export interface PostingAssertionDTO {
  id: number;
  companyId: number;
  module: string;
  businessDate: string;
  status: 'pass' | 'fail' | 'not_run' | 'accepted';
  expectedCount: number;
  postedCount: number;
  unpostedCount: number;
  expectedAmount: string;
  postedAmount: string;
  difference: string;
  explanation?: string;
  acceptedReasonCode?: string;
  acceptedNote?: string;
  rowVersion: number;
}

export interface PrecedenceEntryDTO {
  id: number;
  seq: number;
  source: string;
  isEnabled: boolean;
  stopOnMatch: boolean;
  note?: string;
  evaluatorAvailable: boolean;
}

export interface PrecedenceEntryInput {
  seq: number;
  source: string;
  isEnabled?: boolean | null;
  stopOnMatch?: boolean | null;
  note?: string;
}

export interface PrecedenceVersionDTO {
  id: number;
  code: string;
  name: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  activatedBy?: number | null;
  activatedAt?: string | null;
  note?: string;
  entryCount: number;
  createdAt: string;
  rowVersion: number;
}

export interface PrecedenceVersionDetailDTO {
  id: number;
  code: string;
  name: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  activatedBy?: number | null;
  activatedAt?: string | null;
  note?: string;
  entryCount: number;
  createdAt: string;
  rowVersion: number;
  entries: PrecedenceEntryDTO[];
}

export interface PrepareReturnRequest {
  registrationId: number;
  returnDefCode: string;
  periodStart: string;
  periodEnd: string;
}

export interface PreviewDTO {
  id: number;
  conversationId?: number | null;
  tool: string;
  summary: string;
  preview: string;
  status: string;
  result?: string;
  createdAt: string;
  expiresAt: string;
  consumedAt?: string | null;
}

export interface PreviewNamingTemplateRequest {
  values: Record<string, string>;
  excludeId?: number;
}

export interface PreviewNamingTemplateResponse {
  composedName: string;
  collides: boolean;
  collidingEntityId?: number | null;
  collidingEntityCode?: string;
  attributeSequence: string[];
}

export interface PreviewPOSDayCloseRequest {
  outletId: number;
  businessDate?: string;
}

export interface PriceConsiderationDTO {
  seq: number;
  source: string;
  sourceId?: number | null;
  sourceCode?: string;
  outcome: string;
  unitPrice?: string | null;
  minQty?: string | null;
  reasonKey: string;
  reasonArgs?: Record<string, string>;
}

export interface PriceExplanationDTO {
  id: number;
  companyId: number;
  docType?: string;
  docId?: number | null;
  docLineNo?: number | null;
  businessDate: string;
  customerId?: number | null;
  itemId?: number | null;
  quantity: string;
  currency: string;
  channel?: string;
  listPrice: string;
  unitPrice: string;
  priceBasis: string;
  winnerSource: string;
  winnerSourceCode?: string;
  marginOutcome: string;
  setSuperseded: boolean;
  fingerprint: string;
  resolvedAt: string;
  uom?: string;
  dayPartCode?: string;
  segmentCode?: string;
  supplyType?: string;
  locationId?: number | null;
  precedenceVersionId: number;
  precedenceVersionCode?: string;
  priceSetVersionId?: number | null;
  winnerSourceId?: number | null;
  promotionId?: number | null;
  tradeAgreementLineId?: number | null;
  marginFloorPercent?: string;
  marginMinPrice?: string;
  standardCost?: string;
  waterfall: PriceConsiderationDTO[];
}

export interface PriceExplanationSummaryDTO {
  id: number;
  companyId: number;
  docType?: string;
  docId?: number | null;
  docLineNo?: number | null;
  businessDate: string;
  customerId?: number | null;
  itemId?: number | null;
  quantity: string;
  currency: string;
  channel?: string;
  listPrice: string;
  unitPrice: string;
  priceBasis: string;
  winnerSource: string;
  winnerSourceCode?: string;
  marginOutcome: string;
  setSuperseded: boolean;
  fingerprint: string;
  resolvedAt: string;
}

export interface PriceListDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  kind: 'base' | 'tier' | 'contract' | 'segment' | 'channel';
  currency: string;
  customerId?: number | null;
  priceTier?: string;
  priority: number;
  channel?: string;
  dayPartCode?: string;
  segmentCode?: string;
  locationId?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
  lines?: PriceListLineDTO[];
}

export interface PriceListLineDTO {
  id: number;
  itemId: number;
  minQty: string;
  unitPrice: string;
}

export interface PriceListLineInput {
  itemId: number;
  minQty?: string;
  unitPrice: string;
}

export interface PricePORequest {
  companyId: number;
  vendorId: number;
  itemId: number;
  quantity?: string;
  uom?: string;
  currency?: string;
  on?: string;
}

export interface PriceResolutionDTO {
  listPrice: string;
  unitPrice: string;
  currency: string;
  priceBasis: string;
  winner: PriceConsiderationDTO;
  waterfall: PriceConsiderationDTO[];
  promotion?: AppliedModifierDTO | null;
  tradeDiscount?: AppliedModifierDTO | null;
  margin: MarginOutcomeDTO;
  precedenceVersionId: number;
  precedenceDefaulted: boolean;
  priceSetVersionId?: number | null;
  setSuperseded: boolean;
  fingerprint: string;
  priceRule: string;
  priceExplanation: string;
}

export interface PriceSetDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  channel?: string;
  locationId?: number | null;
  currency: string;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface PriceSetEntryDTO {
  id: number;
  itemId: number;
  uom?: string;
  minQty: string;
  unitPrice: string;
  dayPartCode?: string;
  segmentCode?: string;
  taxCode?: string;
  itemTaxGroupId?: number | null;
  priceBasis: string;
}

export interface PriceSetEntryInput {
  itemId: number;
  uom?: string;
  minQty?: string;
  unitPrice: string;
  dayPartCode?: string;
  segmentCode?: string;
  taxCode?: string;
  itemTaxGroupId?: number;
  priceBasis?: string;
}

export interface PriceSetManifestDTO {
  versionId: number;
  versionNo: number;
  contentHash: string;
  entryCount: number;
  entries: PriceSetEntryDTO[];
}

export interface PriceSetRequest {
  companyId?: number;
  code?: string;
  name?: string;
  channel?: string;
  locationId?: number;
  currency?: string;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface PriceSetVersionDTO {
  id: number;
  setId: number;
  versionNo: number;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  contentHash?: string;
  entryCount: number;
  publishedAt?: string | null;
  publishedBy?: number | null;
  withdrawnAt?: string | null;
  withdrawnBy?: number | null;
  note?: string;
  createdAt: string;
  rowVersion: number;
  entries?: PriceSetEntryDTO[];
}

export interface PricingCategoryDTO {
  id: number;
  code: string;
  name: string;
  parentCode?: string | null;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface PrimaryLedgerStep {
  legalEntityRef: string;
}

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  code: string;
  errors?: FieldError[];
  traceId?: string;
}

export interface ProblemDTO {
  id: number;
  companyId: number;
  problemNo: string;
  title: string;
  description: string;
  status: string;
  rootCause: string;
  workaround: string;
  kbArticleId?: number | null;
  itemId?: number | null;
  assetCategory?: string;
  ownerUserId?: number | null;
  resolvedAt?: string | null;
  caseIds?: number[];
  createdAt: string;
  rowVersion: number;
}

export interface ProcurementDeleteQuery {
  rowVersion?: number;
}

export interface ProcurementMatchDTO {
  id: number;
  invoiceId: number;
  poId: number;
  companyId: number;
  vendorId: number;
  status: 'matched' | 'disputed';
  matchedAt: string;
  lines: MatchLineDTO[];
  disputes?: DisputeDTO[];
}

export interface ProcurementSettingsDTO {
  companyId: number;
  grIrAccountId?: number | null;
  resolvedGrIrAccountCode?: string;
  qtyTolerancePercent: string;
  priceTolerancePercent: string;
  priceToleranceAmount: string;
  overReceiptTolerancePercent: string;
  cumulativeInvoiceTolerancePercent: string;
  twoWayMatchForServices: boolean;
  fourWayMatchWhenInspected: boolean;
  duplicateInvoiceWindowDays: number;
  rowVersion: number;
}

export interface ProcurementSettingsQuery {
  companyId: number;
}

export interface ProcurementUpsertSettingsRequest {
  companyId: number;
  grIrAccountId?: number | null;
  qtyTolerancePercent?: string;
  priceTolerancePercent?: string;
  priceToleranceAmount?: string;
  overReceiptTolerancePercent?: string;
  cumulativeInvoiceTolerancePercent?: string;
  twoWayMatchForServices?: boolean | null;
  fourWayMatchWhenInspected?: boolean | null;
  duplicateInvoiceWindowDays?: number | null;
  rowVersion: number;
}

export interface ProductionOrderDTO {
  id: number;
  companyId: number;
  orderNo?: string | null;
  itemId: number;
  bomId?: number | null;
  routingId?: number | null;
  warehouseId: number;
  quantityPlanned: string;
  quantityCompleted: string;
  quantityScrapped: string;
  status: 'draft' | 'released' | 'in_progress' | 'completed' | 'closed' | 'cancelled';
  backflush: boolean;
  hold: boolean;
  wipBalance: string;
  standardUnitCost?: string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  closedAt?: string | null;
  source: 'manual' | 'mrp';
  sourceDocType?: string;
  sourceDocId?: number | null;
  notes?: string;
  materials?: ProductionOrderMaterialDTO[];
  operations?: ProductionOrderOperationDTO[];
  receipts?: ProductionOrderReceiptDTO[];
  timeLogs?: OperationTimeLogDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface ProductionOrderMaterialDTO {
  id: number;
  bomLineId?: number | null;
  itemId: number;
  isSubstitute: boolean;
  quantityRequired: string;
  quantityIssued: string;
  uomCode?: string;
  standardUnitCost?: string | null;
  actualValueIssued: string;
  status: 'pending' | 'partially_issued' | 'issued';
}

export interface ProductionOrderOperationDTO {
  id: number;
  seqNo: number;
  workCenterId: number;
  name: string;
  plannedSetupMinutes: string;
  plannedRunMinutes: string;
  status: 'pending' | 'in_progress' | 'complete';
  actualLaborMinutes: string;
  actualMachineMinutes: string;
  appliedLaborCost: string;
  appliedOverheadCost: string;
  startedAt?: string | null;
  endedAt?: string | null;
}

export interface ProductionOrderReceiptDTO {
  id: number;
  productionOrderId: number;
  quantity: string;
  unitCost: string;
  costingMode: 'standard' | 'actual';
  value: string;
  receivedAt: string;
  notes?: string;
}

export interface ProductionSettlementDTO {
  id: number;
  productionOrderId: number;
  companyId: number;
  status: 'preview' | 'posted' | 'superseded';
  settlementDate: string;
  wipBalanceBefore: string;
  quantityPlanned: string;
  quantityCompleted: string;
  quantityScrapped: string;
  materialPriceVariance: string;
  materialUsageVariance: string;
  yieldVariance: string;
  laborRateVariance: string;
  laborEfficiencyVariance: string;
  overheadSpendVariance: string;
  overheadVolumeVariance: string;
  residualAmount: string;
  isShortClose: boolean;
  shortQuantity: string;
  residualDisposition: 'write_off' | 'reallocate_to_output' | 'carry_to_new_order';
  residualAccountId?: number | null;
  carryToOrderId?: number | null;
  shortCloseReason?: string;
  blockers: SettlementBlockerDTO[];
  computedAt: string;
  computedBy?: number | null;
  postedAt?: string | null;
  postedBy?: number | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface ProfileDTO {
  id: number;
  companyId: number;
  bankAccountId?: number | null;
  name: string;
  mapping: CSVMapping;
  notes: string;
  createdAt: string;
  rowVersion: number;
}

export interface ProfileUpdateRequest {
  name?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  locale?: string | null;
  timezone?: string | null;
}

export interface ProfitabilityDTO {
  projectId: number;
  budget: string;
  commitment: string;
  actualTimesheet: string;
  actualAp: string;
  actualTotal: string;
  eac: string;
  variance: string;
  contractValue: string;
  billedToDate: string;
  revenueRecognized: string;
  phases: PhaseProfitDTO[];
  tasks: TaskProfitDTO[];
}

export interface ProjectAdvanceDTO {
  id: number;
  projectId: number;
  projectCode?: string;
  advanceInvoiceId: number;
  advanceInvoiceNo?: string;
  amount: string;
  recoupmentPercent: string;
  recoupedAmount: string;
  outstanding: string;
  status: 'outstanding' | 'recouped' | 'written_off';
  rowVersion: number;
}

export interface ProjectAdvanceRecoupmentDTO {
  id: number;
  advanceId: number;
  claimInvoiceId: number;
  claimInvoiceNo?: string;
  noteId: number;
  noteNo?: string;
  amount: string;
  taxAmount: string;
  recoupedOn: string;
}

export interface ProjectAllocationBoardCellDTO {
  weekStartDate: string;
  allocatedHours: string;
  availableHours: string;
  isOverAllocated: boolean;
}

export interface ProjectAllocationBoardDTO {
  from: string;
  to: string;
  rows: ProjectAllocationBoardRowDTO[];
}

export interface ProjectAllocationBoardQuery {
  companyId?: number | null;
  from: string;
  to: string;
}

export interface ProjectAllocationBoardRowDTO {
  resourceId: number;
  employeeId: number;
  employeeName?: string;
  weeks: ProjectAllocationBoardCellDTO[];
}

export interface ProjectAllocationDTO {
  id: number;
  resourceId: number;
  employeeId?: number;
  employeeName?: string;
  projectId: number;
  projectCode?: string;
  taskId?: number | null;
  weekStartDate: string;
  allocatedHours: string;
  isOverAllocated: boolean;
  rowVersion: number;
}

export interface ProjectBaselineComparisonDTO {
  baselineId: number;
  baselineNo: string;
  projectId: number;
  baselineBudget: string;
  currentBudget: string;
  budgetDelta: string;
  lines: ProjectBaselineComparisonLineDTO[];
}

export interface ProjectBaselineComparisonLineDTO {
  wbsCode: string;
  name: string;
  state: 'unchanged' | 'changed' | 'added' | 'removed';
  baselineStartDate?: string;
  baselineEndDate?: string;
  baselineBudget?: string;
  currentStartDate?: string;
  currentEndDate?: string;
  currentBudget?: string;
  budgetDelta?: string;
  endDateDeltaDays?: number | null;
}

export interface ProjectBaselineDTO {
  id: number;
  projectId: number;
  baselineNo: string;
  version: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'superseded';
  budgetTotal: string;
  contractValue: string;
  startDate?: string;
  endDate?: string;
  variationId?: number | null;
  approvalRequestId?: number | null;
  approvedAt?: string;
  approvedBy?: number | null;
  supersededAt?: string;
  supersededByBaselineId?: number | null;
  isEditable: boolean;
  rowVersion: number;
}

export interface ProjectBaselineDetailDTO {
  id: number;
  projectId: number;
  baselineNo: string;
  version: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'superseded';
  budgetTotal: string;
  contractValue: string;
  startDate?: string;
  endDate?: string;
  variationId?: number | null;
  approvalRequestId?: number | null;
  approvedAt?: string;
  approvedBy?: number | null;
  supersededAt?: string;
  supersededByBaselineId?: number | null;
  isEditable: boolean;
  rowVersion: number;
  lines: ProjectBaselineLineDTO[];
}

export interface ProjectBaselineLineDTO {
  id: number;
  baselineId: number;
  taskId?: number | null;
  wbsCode: string;
  name: string;
  parentWbsCode?: string;
  costCategory: 'labour' | 'material' | 'subcontract' | 'expense' | 'overhead';
  startDate?: string;
  endDate?: string;
  budgetAmount: string;
  budgetHours: string;
}

export interface ProjectCapacityDTO {
  id: number;
  resourceId: number;
  weekStartDate: string;
  availableHours: string;
  rowVersion: number;
}

export interface ProjectCapacityWeekInput {
  weekStartDate: string;
  availableHours: string;
}

export interface ProjectChargeabilityDTO {
  projectId: number;
  projectCode: string;
  chargeable: boolean;
  reason: 'gate_disabled' | 'baseline_approved' | 'override' | 'no_approved_baseline';
  on: string;
  baselineId?: number | null;
  baselineNo?: string;
  overrideId?: number | null;
  validTo?: string;
}

export interface ProjectChargeabilityOverrideDTO {
  id: number;
  projectId: number;
  reasonCode: string;
  note?: string;
  validFrom: string;
  validTo: string;
  ruleOverrideId: number;
  authorisedBy: number;
  revokedAt?: string;
  revokedBy?: number | null;
  rowVersion: number;
}

export interface ProjectCriticalPathDTO {
  projectId: number;
  computedAt?: string;
  usesBusinessDays: boolean;
  tasks: ProjectScheduleDTO[];
  criticalTaskIds: number[];
}

export interface ProjectDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description: string;
  customerId?: number | null;
  managerUserId?: number | null;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  startDate?: string;
  endDate?: string;
  billingMethod?: 'fixed_price_milestone' | 'time_and_materials' | 'percent_complete' | 'cost_plus' | 'retainer';
  currency: string;
  contractValue: string;
  revenueAccountId?: number | null;
  revenueRecognized: string;
  revenueTaxCode?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ProjectDependencyDTO {
  id: number;
  taskId: number;
  dependsOnTaskId: number;
  kind: 'FS' | 'SS' | 'FF' | 'SF';
  lagDays: number;
}

export interface ProjectDependencyInput {
  dependsOnTaskId: number;
  kind?: 'FS' | 'SS' | 'FF' | 'SF';
  lagDays?: number;
}

export interface ProjectEVMDTO {
  id: number;
  projectId: number;
  baselineId: number;
  asOf: string;
  bcws: string;
  bcwp: string;
  acwp: string;
  cpi?: string;
  spi?: string;
  eac?: string;
}

export interface ProjectMoneyDTO {
  projectId: number;
  estimateAtCompletion?: string;
  costPlusPercent: string;
  retainerAmount: string;
}

export interface ProjectMoneySettingsDTO {
  companyId: number;
  contractAssetAccountId?: number | null;
  contractLiabilityAccountId?: number | null;
  retentionReceivableAccountId?: number | null;
  contractAdvanceAccountId?: number | null;
  lossProvisionAccountId?: number | null;
  contractLossAccountId?: number | null;
  rowVersion: number;
}

export interface ProjectResourceDTO {
  id: number;
  companyId: number;
  employeeId: number;
  employeeNo?: string;
  employeeName?: string;
  planningCostRate: string;
  currency?: string;
  isActive: boolean;
  rowVersion: number;
}

export interface ProjectRetentionDTO {
  id: number;
  projectId: number;
  projectCode?: string;
  claimInvoiceId: number;
  claimInvoiceNo?: string;
  percent: string;
  amount: string;
  outstanding: string;
  withheldNoteId: number;
  withheldOn: string;
  releaseDueOn: string;
  status: 'withheld' | 'part_released' | 'released' | 'forfeited';
  releaseInvoiceId?: number | null;
  releasedOn?: string;
  forfeitKind?: 'write_off' | 'price_adjustment';
  forfeitAmount: string;
  forfeitJournalId?: number | null;
  forfeitNoteId?: number | null;
  forfeitReasonCode?: string;
  rowVersion: number;
}

export interface ProjectRiskDTO {
  id: number;
  projectId: number;
  riskNo: string;
  title: string;
  description?: string;
  category?: string;
  probability: number;
  impact: number;
  exposure: number;
  costImpact: string;
  status: 'open' | 'mitigating' | 'realised' | 'closed';
  response: 'avoid' | 'mitigate' | 'transfer' | 'accept';
  mitigation?: string;
  ownerUserId?: number | null;
  reviewOn?: string;
  closedAt?: string;
  rowVersion: number;
}

export interface ProjectScheduleDTO {
  taskId: number;
  wbsCode: string;
  name: string;
  parentTaskId?: number | null;
  costCategory: 'labour' | 'material' | 'subcontract' | 'expense' | 'overhead';
  startDate?: string;
  endDate?: string;
  isMilestone: boolean;
  earlyStart?: string;
  earlyFinish?: string;
  lateStart?: string;
  lateFinish?: string;
  totalFloatDays?: number | null;
  isCritical: boolean;
  dependsOn: ProjectDependencyDTO[];
}

export interface ProjectSubcontractDTO {
  id: number;
  projectId: number;
  vendorId: number;
  vendorName?: string;
  subcontractNo: string;
  title: string;
  purchaseOrderId?: number | null;
  purchaseOrderNo?: string;
  contractValue: string;
  retentionPercent: string;
  certifiedAmount: string;
  status: 'draft' | 'active' | 'completed' | 'terminated';
  startsOn?: string;
  endsOn?: string;
  rowVersion: number;
}

export interface ProjectTaskCostDTO {
  taskId: number;
  wbsCode: string;
  name: string;
  costCategory: 'labour' | 'material' | 'subcontract' | 'expense' | 'overhead';
  budgetAmount: string;
  actualAp: string;
  commitment: string;
}

export interface ProjectTaskSkillDTO {
  skillId: number;
  skillCode?: string;
  skillName?: string;
  isStatutory: boolean;
  minLevel: number;
}

export interface ProjectTaskSkillInput {
  skillId: number;
  minLevel?: number;
}

export interface ProjectUtilisationDTO {
  resourceId: number;
  employeeId: number;
  employeeName?: string;
  availableHours: string;
  plannedHours: string;
  actualHours: string;
  plannedUtilisation: string;
  actualUtilisation: string;
}

export interface ProjectUtilisationQuery {
  companyId?: number | null;
  from: string;
  to: string;
}

export interface ProjectVariationDTO {
  id: number;
  projectId: number;
  variationNo: string;
  title: string;
  description: string;
  status: 'draft' | 'submitted' | 'approved' | 'accepted' | 'rejected' | 'withdrawn';
  origin: 'internal' | 'customer' | 'site';
  costImpact: string;
  revenueImpact: string;
  scheduleImpactDays: number;
  impactNote: string;
  magnitude: string;
  submittedAt?: string;
  approvalRequestId?: number | null;
  customerAcceptedAt?: string;
  customerReference?: string;
  customerAttachmentId?: number | null;
  rejectedReason?: string;
  resultingBaselineId?: number | null;
  rowVersion: number;
}

export interface ProjectWIPLineDTO {
  id: number;
  runId: number;
  projectId: number;
  projectCode?: string;
  projectName?: string;
  baselineId?: number | null;
  contractValue: string;
  costToDate: string;
  estimateAtCompletion: string;
  percentComplete: string;
  revenueToDate: string;
  billedToDate: string;
  positionPrior: string;
  positionNow: string;
  lossExpected: string;
  lossProvidedPrior: string;
  lossProvisionMovement: string;
  contractAssetMovement: string;
  contractLiabilityMovement: string;
  revenueMovement: string;
}

export interface ProjectWIPRunDTO {
  id: number;
  companyId: number;
  periodId: number;
  periodName?: string;
  asOf: string;
  status: 'draft' | 'posted' | 'reversed' | 'cancelled';
  projectCount: number;
  contractAssetMovement: string;
  contractLiabilityMovement: string;
  lossProvisionMovement: string;
  revenueMovement: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  postedAt?: string;
  postedBy?: number | null;
  reversesRunId?: number | null;
  rowVersion: number;
}

export interface ProjectWIPRunDetailDTO {
  id: number;
  companyId: number;
  periodId: number;
  periodName?: string;
  asOf: string;
  status: 'draft' | 'posted' | 'reversed' | 'cancelled';
  projectCount: number;
  contractAssetMovement: string;
  contractLiabilityMovement: string;
  lossProvisionMovement: string;
  revenueMovement: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  postedAt?: string;
  postedBy?: number | null;
  reversesRunId?: number | null;
  rowVersion: number;
  lines: ProjectWIPLineDTO[];
}

export interface ProjectsBudgetIndicatorDTO {
  accountId: number;
  budget: string;
  available: string;
  requested: string;
  exceeded: boolean;
}

export interface ProjectsSettingsDTO {
  companyId: number;
  wipAccountId?: number | null;
  payrollClearingAccountId?: number | null;
  rowVersion: number;
}

export interface ProjectsTaskDTO {
  id: number;
  projectId: number;
  phaseId?: number | null;
  wbsCode: string;
  name: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
  startDate?: string;
  endDate?: string;
  isMilestone: boolean;
  assigneeUserId?: number | null;
  accountId?: number | null;
  costCenterId?: number | null;
  budgetAmount: string;
  budget?: ProjectsBudgetIndicatorDTO | null;
  dependsOn: number[];
  rowVersion: number;
}

export interface ProjectsUpsertSettingsRequest {
  wipAccountId?: number | null;
  payrollClearingAccountId?: number | null;
  rowVersion: number;
}

export interface PromiseBreachQuery {
  companyId: number;
}

export interface PromiseBreachReportDTO {
  companyId: number;
  currency: string;
  rows: PromiseBreachRow[];
  generatedAt: string;
}

export interface PromiseBreachRow {
  orderId: number;
  orderNo?: string | null;
  customerId: number;
  promisedDate: string;
  daysOverdue: number;
  promiseCount: number;
  total: string;
}

export interface PromiseDTO {
  atpDate?: string | null;
  promisedDate?: string | null;
  source?: string;
  breachRisk: boolean;
}

export interface PromotionDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  kind: 'percent' | 'amount' | 'bundle';
  value: string;
  currency?: string;
  bundleMinQty?: string;
  startDate: string;
  endDate: string;
  customerIds?: number[];
  customerTiers?: string[];
  itemIds?: number[];
  minQty?: string;
  channel?: string;
  dayPartCode?: string;
  segmentCode?: string;
  isStackable: boolean;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface ProposeInput {
  industry?: string;
  primaryCountry?: string;
  footprint?: Footprint;
  blank?: boolean;
}

export interface ProposeReplenishmentRequest {
  on?: string;
}

export interface ProposeResult {
  run: GuidedRunDTO;
  plan: FoundationPlan;
  blank: boolean;
}

export interface ProposeRosterRequest {
  basis: string;
  departmentIds?: number[];
  replace?: boolean;
  rowVersion: number;
}

export interface ProposeRosterResultDTO {
  roster: RosterDTO;
  linesCreated: number;
  openLines: number;
  assignedLines: number;
  coverage: Coverage;
}

export interface ProvisioningStep {
  id: number;
  tenantId: string;
  seq: number;
  step: string;
  status: string;
  attempts: number;
  lastError?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface PublicSSOProvidersQuery {
  tenantSlug: string;
}

export interface PublishContentRequest {
  validFrom?: string | null;
  validUntil?: string | null;
  note?: string;
}

export interface PublishDailyOperatingRequest {
  rowVersion: number;
}

export interface PublishForecastRequest {
  companyId: number;
  scenarioId?: number | null;
  asOfDate?: string;
  horizon?: 'operational' | 'strategic';
  horizonWeeks?: number;
  dailyDays?: number | null;
  baseCurrency?: string;
  minimumBalance?: string;
}

export interface PublishHierarchyVersionRequest {
  note: string;
}

export interface PublishHierarchyVersionResult {
  version: HierarchyVersionDTO;
  supersededVersionId?: number | null;
  closure: ClosureCoverage;
  scopeRows: number;
  scopeGenerationId?: number | null;
}

export interface PublishKBArticleRequest {
  rowVersion: number;
}

export interface PublishPMSCommissionVersionRequest {
  rowVersion: number;
}

export interface PublishPOSMenuRequest {
  note?: string;
}

export interface PublishPositionRequest {
  companyId: number;
  positionDate?: string;
  baseCurrency?: string;
  includeInactive?: boolean | null;
  note?: string;
}

export interface PublishPriceSetVersionRequest {
  note?: string;
}

export interface PublishRVMForecastVersionRequest {
  note?: string;
  reasonCode?: string;
  reasonNote?: string;
}

export interface PublishRosterRequest {
  rowVersion: number;
  workingTimeOverride?: RuleOverrideInput | null;
  openLinesOverride?: RuleOverrideInput | null;
}

export interface PublishRosterResultDTO {
  roster: RosterDTO;
  warnings: WorkingTimeBreachDTO[];
  overridden: WorkingTimeBreachDTO[];
}

export interface PublishServiceRateCardVersionRequest {
  rowVersion: number;
}

export interface PushPMSChannelRequest {
  payloadKind: string;
  from: string;
  to: string;
}

export interface PutPMSAllotmentsRequest {
  rows: PMSAllotmentInput[];
}

export interface PutPMSRateDerivationRequest {
  parentPlanId: number;
  deltaAmount?: string;
  deltaPercent?: string;
  roundingIncrement?: string;
  floorAmount?: string | null;
  ceilingAmount?: string | null;
}

export interface PutPMSRateRestrictionsRequest {
  propertyId: number;
  from: string;
  to: string;
  rows: PMSRateRestrictionInput[];
}

export interface PutawayPlacementDTO {
  binId: number;
  binCode: string;
  qty: string;
  reason: string;
}

export interface PutawayPlanDTO {
  strategy: 'fixed_then_any' | 'nearest_empty' | 'consolidate' | 'single_bin' | 'none';
  lines: PutawayPlanLineDTO[];
  unplaced: PutawayUnplacedDTO[];
}

export interface PutawayPlanLine {
  itemId: number;
  lotId?: number | null;
  qty: string;
  fromBinId?: number | null;
}

export interface PutawayPlanLineDTO {
  itemId: number;
  lotId?: number | null;
  qty: string;
  placements: PutawayPlacementDTO[];
}

export interface PutawayPlanRequest {
  warehouseId: number;
  lines: PutawayPlanLine[];
}

export interface PutawayUnplacedDTO {
  itemId: number;
  lotId?: number | null;
  qty: string;
  reason: string;
}

export interface QCInspectionDTO {
  id: number;
  productionOrderId: number;
  productionOrderOperationId?: number | null;
  inspectionType: 'in_process' | 'final';
  quantityInspected: string;
  quantityPassed: string;
  quantityFailed: string;
  result: 'pass' | 'fail' | 'partial';
  notes?: string;
  ncrId?: number | null;
  createdAt: string;
}

export interface QMSDispositionDTO {
  id: number;
  inspectionLotId: number;
  kind: 'accept' | 'accept_with_concession' | 'rework' | 'reject_return' | 'reject_scrap' | 'hold';
  quantity: string;
  reasonCode?: string;
  reason?: string;
  outcomeDocType?: string;
  outcomeDocId?: number | null;
  mfgNcrId?: number | null;
  decidedBy?: number | null;
  decidedAt: string;
}

export interface QMSSettingsDTO {
  companyId: number;
  quarantineWarehouseId?: number | null;
  receiptScrapAccountId?: number | null;
  defaultReceiptPolicy: 'none' | 'quarantine';
  quarantineSlaDays: number;
  capaDefaultDueDays: number;
  rowVersion: number;
}

export interface QualifiedForRequest {
  employeeIds: number[];
  requirements: HRSkillRequirementDTO[];
  on?: string;
}

export interface QualifiedForResponse {
  matches: HRSkillMatchDTO[];
}

export interface QualifyLeadRequest {
  name?: string;
  companyId?: number | null;
  stageCode?: string;
  amount?: string;
  currency: string;
  probabilityPercent?: string;
  expectedCloseDate?: string;
  ownerUserId?: number | null;
  rowVersion: number;
  forecastCategoryCode?: string;
  accountId?: number | null;
  primaryContactId?: number | null;
  nextStep?: string;
  nextStepDueOn?: string;
}

export interface QualityInspectionDTO {
  id: number;
  lotNo?: string;
  companyId: number;
  originKind: 'goods_receipt' | 'production' | 'shipment';
  originDocType: string;
  originDocId: number;
  originLineId?: number | null;
  originDocNo: string;
  planId: number;
  planCode: string;
  itemId: number;
  itemSku: string;
  itemName: string;
  stockLotId?: number | null;
  stockLotNo?: string;
  warehouseId?: number | null;
  lotQuantity: string;
  sampleSize: number;
  acceptNumber: number;
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'waived';
  defectsFound: number;
  criticalFound: number;
  qtyAccepted: string;
  qtyRejected: string;
  qtyRework: string;
  qtyUndisposed: string;
  originActorId?: number | null;
  inspectorId?: number | null;
  inspectedAt?: string;
  waivedReason?: string;
  waivedBy?: number | null;
  notes: string;
  requiresIndependence: boolean;
  isBlocking: boolean;
  rowVersion: number;
}

export interface QualityInspectionDetailDTO {
  id: number;
  lotNo?: string;
  companyId: number;
  originKind: 'goods_receipt' | 'production' | 'shipment';
  originDocType: string;
  originDocId: number;
  originLineId?: number | null;
  originDocNo: string;
  planId: number;
  planCode: string;
  itemId: number;
  itemSku: string;
  itemName: string;
  stockLotId?: number | null;
  stockLotNo?: string;
  warehouseId?: number | null;
  lotQuantity: string;
  sampleSize: number;
  acceptNumber: number;
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'waived';
  defectsFound: number;
  criticalFound: number;
  qtyAccepted: string;
  qtyRejected: string;
  qtyRework: string;
  qtyUndisposed: string;
  originActorId?: number | null;
  inspectorId?: number | null;
  inspectedAt?: string;
  waivedReason?: string;
  waivedBy?: number | null;
  notes: string;
  requiresIndependence: boolean;
  isBlocking: boolean;
  rowVersion: number;
  characteristics: InspectionCharacteristicDTO[];
  results: QualityInspectionResultDTO[];
  dispositions: QMSDispositionDTO[];
}

export interface QualityInspectionResultDTO {
  id: number;
  characteristicId: number;
  characteristicCode: string;
  characteristicName: string;
  sampleNo: number;
  numericValue?: string;
  attributeValue?: string;
  passed: boolean;
  defectSeverity?: string;
  notes: string;
  attachmentId?: number | null;
  recordedBy?: number | null;
  recordedAt: string;
}

export interface QualitySummaryDTO {
  companyId: number;
  fromDate?: string;
  toDate?: string;
  lotsTotal: number;
  lotsPassed: number;
  lotsFailed: number;
  lotsWaived: number;
  lotsOpen: number;
  passRatePct: string;
  qtyAccepted: string;
  qtyRejected: string;
  qtyRework: string;
  byCharacteristic: QualitySummaryRowDTO[];
  byVendor: QualitySummaryRowDTO[];
  byItem: QualitySummaryRowDTO[];
}

export interface QualitySummaryQuery {
  companyId?: number;
  fromDate?: string;
  toDate?: string;
  originKind?: string;
  vendorId?: number;
  itemId?: number;
}

export interface QualitySummaryRowDTO {
  key: string;
  label: string;
  readings: number;
  failures: number;
  critical: number;
  defectRatePct: string;
}

export interface QueueDTO {
  id: number;
  code: string;
  nameKey: string;
  module: string;
  ownerPositionId?: number | null;
  slaHours: number;
  escalateToPositionId?: number | null;
  isActive: boolean;
}

export interface QueueItemDTO {
  id: number;
  queueId: number;
  subjectType: string;
  subjectId: number;
  subjectNo?: string;
  companyId?: number | null;
  amount?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency?: string;
  summaryKey?: string;
  openedAt: string;
  dueAt?: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'written_off' | 'escalated';
  disposition?: string;
  dispositionReasonCode?: string;
  resolvedAt?: string | null;
}

export interface QuoteDTO {
  id: number;
  rfqId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  status: 'submitted' | 'withdrawn';
  currency: string;
  leadTimeDays: number;
  terms: string;
  totalAmount: string;
  priceScore?: string;
  leadTimeScore?: string;
  termsScore?: string;
  weightedScore?: string;
  recommended?: boolean;
  submittedAt: string;
  lines?: QuoteLineDTO[];
}

export interface QuoteLineDTO {
  rfqLineId: number;
  description?: string;
  quantity?: string;
  unitPrice: string;
  leadTimeDays?: number | null;
  lineAmount: string;
}

export interface QuoteLineInput {
  rfqLineId: number;
  unitPrice: string;
  leadTimeDays?: number | null;
}

export interface QuotePMSStayRequest {
  propertyId: number;
  roomTypeId: number;
  ratePlanId: number;
  arrival: string;
  departure: string;
  roomsCount?: number;
  adults?: number;
  children?: number;
  customerId?: number | null;
  segmentCode?: string;
}

export interface QuoteRevisionDTO {
  id: number;
  quoteId: number;
  revisionNo: number;
  snapshot: SalesQuoteDTO;
  reasonCode?: string;
  note?: string;
  issuedAt: string;
  issuedBy?: number | null;
}

export interface RFQAwardDTO {
  id: number;
  rfqId: number;
  quoteId: number;
  vendorId: number;
  rank: number;
  weightedScore?: string | null;
  landedTotal: string;
  lowestLandedTotal: string;
  rationale: string;
  overrideReason?: string;
  poId?: number | null;
}

export interface RFQClarificationDTO {
  id: number;
  seqNo: number;
  question: string;
  askedAt: string;
  answer?: string;
  answeredAt?: string | null;
  publishedAt?: string | null;
  rowVersion: number;
}

export interface RFQCriterionDTO {
  id?: number;
  seqNo: number;
  code: string;
  name: string;
  kind: 'price' | 'landed_cost' | 'lead_time' | 'terms' | 'quality_score' | 'otif' | 'manual';
  weight: string;
  lowerIsBetter: boolean;
  isGate: boolean;
  notes?: string;
  rowVersion: number;
}

export interface RFQCriterionInput {
  seqNo: number;
  code: string;
  name: string;
  kind: string;
  weight?: string;
  lowerIsBetter?: boolean | null;
  isGate?: boolean;
  notes?: string;
}

export interface RFQDTO {
  id: number;
  rfqNo?: string | null;
  prId?: number | null;
  companyId: number;
  status: 'draft' | 'issued' | 'closed' | 'awarded' | 'cancelled';
  dueDate?: string | null;
  currency: string;
  memo: string;
  weightPrice: string;
  weightLeadTime: string;
  weightTerms: string;
  issuedAt?: string | null;
  closedAt?: string | null;
  awardedAt?: string | null;
  awardedVendorId?: number | null;
  awardedPoId?: number | null;
  createdAt: string;
  rowVersion: number;
  lines?: RFQLineDTO[];
  suppliers?: RFQSupplierDTO[];
}

export interface RFQEvaluatorDTO {
  id?: number;
  userId: number;
  role: string;
  conflictStatus: 'none' | 'declared';
  conflictVendorId?: number | null;
  conflictNote?: string;
  rowVersion: number;
}

export interface RFQEvaluatorInput {
  userId: number;
  role?: string;
}

export interface RFQLineDTO {
  id: number;
  lineNo: number;
  prLineId?: number | null;
  itemId?: number | null;
  description: string;
  uom: string;
  quantity: string;
}

export interface RFQLineInput {
  prLineId?: number | null;
  itemId?: number | null;
  description?: string;
  uom?: string;
  quantity: string;
}

export interface RFQQuoteViewDTO {
  id: number;
  rfqId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  status: 'submitted' | 'withdrawn';
  currency: string;
  leadTimeDays: number;
  isLate?: boolean;
  submittedAt: string;
  totalAmount?: string | null;
  freightAmount?: string | null;
  dutyAmount?: string | null;
  otherCostAmount?: string | null;
  landedTotal?: string | null;
}

export interface RFQSupplierDTO {
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  status: 'invited' | 'quoted' | 'declined';
  invitedAt: string;
}

export interface RVMAccuracyByHorizonDTO {
  horizon: number;
  n: number;
  mapeN: number;
  mape: string;
  mae: string;
  bias: string;
}

export interface RVMAccuracyLineDTO {
  id: number;
  runId: number;
  stayDate: string;
  horizon: number;
  segmentId?: number | null;
  streamId?: number | null;
  asPublishedVersionId: number;
  asPublishedAt: string;
  forecastRoomNights: string;
  forecastRevenueNet: string;
  actualRoomNights: string;
  actualRevenueNet: string;
  absoluteError: string;
  absolutePercentageError?: string | null;
  bias: string;
}

export interface RVMAccuracyRunDTO {
  id: number;
  companyId: number;
  propertyId: number;
  forecastId: number;
  scoredFrom: string;
  scoredTo: string;
  horizons: number[];
  results: RVMAccuracyByHorizonDTO[];
  lineCount: number;
  status: string;
  errorMessage?: string;
  startedAt: string;
  finishedAt?: string | null;
}

export interface RVMCommercialReportDTO {
  propertyId: number;
  businessDate: string;
  currency: string;
  actualRoomsOccupied: number;
  actualRoomsCapacity: number;
  actualOccupancyPercent: string;
  actualAdr: string;
  actualRevpar: string;
  onTheBooks: RVMCommercialRowDTO[];
  paceVsLastYear?: RVMPaceRowDTO | null;
  revenueBySegment: RVMCommercialSegmentRowDTO[];
  revenueByStream: RVMCommercialStreamRowDTO[];
  acquisitionCost: string;
  netAdr?: string | null;
  forecastRevenueNet?: string | null;
  varianceVsForecast?: string | null;
}

export interface RVMCommercialReportQuery {
  propertyId: number;
  businessDate: string;
}

export interface RVMCommercialRowDTO {
  label: string;
  roomsOtb: string;
  revenueNetOtb: string;
  adr: string;
}

export interface RVMCommercialSegmentRowDTO {
  segmentId: number;
  segmentCode: string;
  roomsOtb: string;
  revenueNetOtb: string;
}

export interface RVMCommercialStreamRowDTO {
  streamId: number;
  streamCode: 'room' | 'fnb' | 'ancillary' | 'other';
  streamName: string;
  configured: boolean;
  pmsRevenueCenterCount: number;
  posOutletCount: number;
  revenueNet: string;
  forecastRevenueNet?: string | null;
  varianceVsForecast?: string | null;
}

export interface RVMCompetitorColumnMapping {
  competitorName: number;
  stayDate: number;
  rate: number;
  roomTypeLabel?: number;
  currency?: number;
  observedOn?: number;
}

export interface RVMCompetitorImportErrorDTO {
  line: number;
  column: string;
  message: string;
}

export interface RVMCompetitorImportResultDTO {
  importBatch: string;
  committed: boolean;
  rowsRead: number;
  rowsValid: number;
  rowsWritten: number;
  rowsDuplicate: number;
  errors: RVMCompetitorImportErrorDTO[];
  preview: RVMCompetitorRateDTO[];
}

export interface RVMCompetitorRateDTO {
  id: number;
  companyId: number;
  propertyId: number;
  competitorName: string;
  stayDate: string;
  roomTypeLabel: string;
  rate: string;
  currency: string;
  observedOn: string;
  source: 'manual' | 'csv';
  importBatch: string;
  note: string;
  rowVersion: number;
}

export interface RVMDisplacementDTO {
  id: number;
  companyId: number;
  propertyId: number;
  evaluationNo?: string | null;
  groupBlockId?: number | null;
  customerId?: number | null;
  enquiryLabel: string;
  segmentId: number;
  arrivalDate: string;
  departureDate: string;
  currency: string;
  otbSnapshotId?: number | null;
  forecastVersionId?: number | null;
  basisNote: string;
  groupRoomNights: string;
  groupRoomRevenue: string;
  groupAncillaryRevenue: string;
  groupDirectCost: string;
  groupNetValue: string;
  displacedRoomNights: string;
  displacedRoomRevenue: string;
  displacedAncillaryRevenue: string;
  displacedDirectCost: string;
  displacedNetValue: string;
  groupAncillaryRate: string;
  groupDirectCostRate: string;
  displacedAncillaryRate: string;
  displacedDirectCostRate: string;
  netBenefit: string;
  hurdleId?: number | null;
  hurdleMinAdr?: string | null;
  offeredAdr?: string | null;
  hurdleMet?: boolean | null;
  status: 'draft' | 'evaluated' | 'accepted' | 'declined' | 'countered' | 'withdrawn' | 'lapsed';
  decidedAt?: string | null;
  decidedBy?: number | null;
  decisionReasonCode: string;
  decisionNote: string;
  counteredAdr?: string | null;
  outcomeDueOn: string;
  rowVersion: number;
}

export interface RVMDisplacementDetailDTO {
  evaluation: RVMDisplacementDTO;
  lines: RVMDisplacementLineDTO[];
  hurdle?: RVMHurdleDTO | null;
  outcome?: RVMDisplacementOutcomeDTO | null;
}

export interface RVMDisplacementLineDTO {
  id: number;
  evaluationId: number;
  stayDate: string;
  roomTypeId: number;
  roomsCapacity: number;
  roomsOtb: string;
  roomsRequested: string;
  roomsAccommodated: string;
  roomsDisplaced: string;
  displacedAdr: string;
  displacedAdrBasis: 'otb' | 'forecast' | 'rate_plan';
  groupAdr: string;
  displacedRoomRevenue: string;
  displacedAncillaryRevenue: string;
}

export interface RVMDisplacementOutcomeDTO {
  id: number;
  evaluationId: number;
  measuredAt: string;
  measuredOn: string;
  actualGroupRoomNights: string;
  actualGroupRoomRevenue: string;
  actualGroupAncillaryRevenue: string;
  actualTotalRoomNights: string;
  actualTotalRoomRevenue: string;
  actualOccupancyPct?: string | null;
  result: 'materialised' | 'partly_materialised' | 'no_show' | 'declined_and_filled' | 'declined_and_unsold' | 'unmeasurable';
  predictedNetBenefit: string;
  realisedNetBenefit: string;
  variance: string;
  note: string;
}

export interface RVMDisplacementRoomRequest {
  roomTypeId: number;
  rooms: string;
}

export interface RVMDisplacementSweepDTO {
  measuredOn: string;
  considered: number;
  measured: number;
  skipped: number;
  outcomes: RVMDisplacementOutcomeDTO[];
}

export interface RVMForecastDTO {
  id: number;
  companyId: number;
  propertyId: number;
  code: string;
  name: string;
  kind: 'budget' | 'forecast';
  periodFrom: string;
  periodTo: string;
  currency: string;
  isActive: boolean;
  publishedVersionId?: number | null;
  rowVersion: number;
}

export interface RVMForecastLineDTO {
  id: number;
  versionId: number;
  stayDate: string;
  segmentId: number;
  streamId: number;
  roomTypeId?: number | null;
  channelId?: number | null;
  roomNights: string;
  revenueNet: string;
  adr?: string | null;
  note: string;
}

export interface RVMForecastLineInput {
  stayDate: string;
  segmentId: number;
  streamId: number;
  roomTypeId?: number | null;
  channelId?: number | null;
  roomNights?: string;
  revenueNet?: string;
  note?: string;
}

export interface RVMForecastLinesDTO {
  version: RVMForecastVersionDTO;
  lines: RVMForecastLineDTO[];
}

export interface RVMForecastVersionDTO {
  id: number;
  forecastId: number;
  versionNo: number;
  name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'published' | 'locked' | 'superseded';
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  publishedAt?: string | null;
  lockedAt?: string | null;
  supersededAt?: string | null;
  supersedesVersionId?: number | null;
  note: string;
  roomNightsTotal: string;
  revenueTotal: string;
  lineCount: number;
  rowVersion: number;
}

export interface RVMHurdleDTO {
  id: number;
  companyId: number;
  propertyId: number;
  segmentId?: number | null;
  roomTypeId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  dowMask: string;
  minAdr: string;
  minTotalContribution?: string | null;
  currency: string;
  isActive: boolean;
  note: string;
  tier: number;
  rowVersion: number;
}

export interface RVMHurdleResolutionDTO {
  propertyId: number;
  stayDate: string;
  segmentId?: number | null;
  roomTypeId?: number | null;
  hurdle?: RVMHurdleDTO | null;
  candidates: RVMHurdleDTO[];
  explanation: string;
}

export interface RVMOnTheBooksLineDTO {
  id: number;
  snapshotId: number;
  stayDate: string;
  segmentId: number;
  roomTypeId: number;
  channelId?: number | null;
  ratePlanId?: number | null;
  roomsOtb: string;
  revenueNetOtb: string;
  revenueGrossOtb: string;
  packageAmountOtb: string;
  roomsCapacity: number;
  arrivals: number;
  cancellationsToDate: number;
  adr?: string | null;
}

export interface RVMOnTheBooksQuery {
  propertyId: number;
  asOf?: string;
  stayDateFrom?: string;
  stayDateTo?: string;
  segmentId?: number;
  roomTypeId?: number;
}

export interface RVMOnTheBooksSnapshotDTO {
  id: number;
  companyId: number;
  propertyId: number;
  asOfDate: string;
  takenAt: string;
  horizonTo: string;
  currency: string;
  roomNightsOtb: string;
  revenueNetOtb: string;
  lineCount: number;
  definition: SnapshotDefinition;
}

export interface RVMOnTheBooksSurfaceDTO {
  snapshot: RVMOnTheBooksSnapshotDTO;
  stayDateFrom: string;
  stayDateTo: string;
  roomsOtb: string;
  revenueNetOtb: string;
  roomsCapacity: number;
  occupancy: string;
  occupancyPercent: string;
  adr: string;
  revpar: string;
  lines: RVMOnTheBooksLineDTO[];
}

export interface RVMPaceDTO {
  propertyId: number;
  asOfSnapshotId: number;
  asOfDate: string;
  priorYearSnapshotId: number;
  priorYearAsOfDate: string;
  rows: RVMPaceRowDTO[];
}

export interface RVMPaceQuery {
  propertyId: number;
  asOf: string;
  stayDateFrom: string;
  stayDateTo: string;
  segmentId?: number;
  roomTypeId?: number;
  channelId?: number;
}

export interface RVMPaceRowDTO {
  stayDate: string;
  priorYearStayDate: string;
  roomsOtb: string;
  roomsOtbPriorYear: string;
  roomsVariance: string;
  roomsVariancePercent?: string | null;
  revenueNetOtb: string;
  revenueNetOtbPriorYear: string;
  revenueVariance: string;
  revenueVariancePercent?: string | null;
  adr: string;
  adrPriorYear: string;
  adrVariance: string;
  adrVariancePercent?: string | null;
}

export interface RVMPickupDTO {
  propertyId: number;
  stayDateFrom: string;
  stayDateTo: string;
  fromSnapshotId: number;
  fromAsOfDate: string;
  toSnapshotId: number;
  toAsOfDate: string;
  fromRoomsOtb: string;
  toRoomsOtb: string;
  pickupRooms: string;
  fromRevenueNetOtb: string;
  toRevenueNetOtb: string;
  pickupRevenueNet: string;
  pickupAdr?: string | null;
}

export interface RVMPickupQuery {
  propertyId: number;
  stayDateFrom: string;
  stayDateTo: string;
  fromAsOf: string;
  toAsOf: string;
  segmentId?: number;
  roomTypeId?: number;
  channelId?: number;
}

export interface RVMSegmentDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  family: 'transient' | 'group' | 'contract' | 'wholesale' | 'complimentary' | 'house';
  displacementRank: number;
  isActive: boolean;
  sortOrder: number;
  rowVersion: number;
}

export interface RVMSegmentMappingDTO {
  id: number;
  companyId: number;
  segmentId: number;
  priority: number;
  matchSegmentCode: string;
  matchMarketCode: string;
  matchSourceCode: string;
  matchChannelId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface RVMSegmentResolutionDTO {
  segmentId: number;
  segmentCode: string;
  segmentName: string;
  family: 'transient' | 'group' | 'contract' | 'wholesale' | 'complimentary' | 'house';
  matched: boolean;
}

export interface RVMSettingsDTO {
  id: number;
  companyId: number;
  propertyId: number;
  snapshotHorizonDays: number;
  forecastHorizonDays: number;
  varianceMaterialPct: string;
  varianceMaterialAbs: string;
  accuracyHorizons: number[];
  unmappedSegmentCode: string;
  hurdleEnforcement: 'warn' | 'block';
  rowVersion: number;
}

export interface RVMSettingsQuery {
  propertyId: number;
}

export interface RVMStreamDTO {
  id: number;
  companyId: number;
  code: 'room' | 'fnb' | 'ancillary' | 'other';
  name: string;
  actualSources: StreamActualSources;
  isActive: boolean;
  sortOrder: number;
  rowVersion: number;
}

export interface RVMVarianceDTO {
  versionId: number;
  forecastId: number;
  priorVersionId?: number | null;
  materialCount: number;
  unexplainedCount: number;
  rows: RVMVarianceRowDTO[];
}

export interface RVMVarianceExplanationDTO {
  id: number;
  versionId: number;
  stayDate: string;
  segmentId: number;
  streamId: number;
  priorRoomNights: string;
  priorRevenueNet: string;
  newRoomNights: string;
  newRevenueNet: string;
  deltaRevenueNet: string;
  reasonCode: string;
  note: string;
  explainedAt: string;
  explainedBy: number;
}

export interface RVMVarianceQuery {
  versionId: number;
}

export interface RVMVarianceRowDTO {
  stayDate: string;
  segmentId: number;
  streamId: number;
  priorRoomNights: string;
  priorRevenueNet: string;
  newRoomNights: string;
  newRevenueNet: string;
  deltaRevenueNet: string;
  material: boolean;
  explained: boolean;
  reasonCode?: string | null;
  note?: string | null;
}

export interface RaiseDebitNoteRequest {
  taxCode?: string;
}

export interface RangeCheckRequest {
  override?: RangeOverrideInput | null;
}

export interface RangeCheckResponse {
  withinRange: boolean;
  rangeCheck: string;
  gradeCode?: string;
  minPay?: string | null;
  midPay?: string | null;
  maxPay?: string | null;
  overridden: boolean;
}

export interface RangeOverrideInput {
  reasonCode: string;
  note?: string;
}

export interface RateDTO {
  id: number;
  base: string;
  quote: string;
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  asOf: string;
  source: 'manual' | 'api';
  provider?: string | null;
  note: string;
  rateType: 'spot' | 'corporate' | 'historical';
  rowVersion: number;
}

export interface RatifyRequest {
  decision: 'approve' | 'reject';
  comment?: string;
}

export interface ReasonCodeDTO {
  id: number;
  setId: number;
  code: string;
  labelKey?: string;
  label?: string;
  requiresNote: boolean;
  requiresAttachment: boolean;
  causeClass?: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface ReasonCodeRequest {
  setCode: string;
  code: string;
  labelKey?: string;
  label?: string;
  requiresNote?: boolean;
  requiresAttachment?: boolean;
  causeClass?: string;
  sortOrder?: number;
  effectiveFrom?: string;
}

export interface ReasonSetDTO {
  id: number;
  code: string;
  name: string;
  module: string;
  description?: string;
  isSystem: boolean;
}

export interface ReassignMatchExceptionRequest {
  ownerUserId?: number | null;
  ownerRoleCode?: string | null;
  note?: string;
  rowVersion: number;
}

export interface RebalancePMSHousekeepingBoardRequest {
  taskIds: number[];
  toBoardId: number;
  rowVersion: number;
}

export interface RebateAccrualDTO {
  id: number;
  programId: number;
  companyId: number;
  customerId: number;
  periodStart: string;
  periodEnd: string;
  qualifyingBase: string;
  accruedAmount: string;
  postedAmount: string;
  settledAmount: string;
  tierSeq?: number | null;
  currency: string;
  status: string;
  journalEntryId?: number | null;
  closedAt?: string | null;
  rowVersion: number;
}

export interface RebateAccrualDetailDTO {
  id: number;
  programId: number;
  companyId: number;
  customerId: number;
  periodStart: string;
  periodEnd: string;
  qualifyingBase: string;
  accruedAmount: string;
  postedAmount: string;
  settledAmount: string;
  tierSeq?: number | null;
  currency: string;
  status: string;
  journalEntryId?: number | null;
  closedAt?: string | null;
  rowVersion: number;
  lines: RebateAccrualLineDTO[];
}

export interface RebateAccrualLineDTO {
  sourceDocType: string;
  sourceDocId: number;
  sourceLineNo: number;
  itemId?: number | null;
  qualifyingAmount: string;
  qualifyingQty: string;
}

export interface RebateProgramDTO {
  id: number;
  agreementId: number;
  code: string;
  name: string;
  basis: string;
  periodKind: string;
  tierKind: string;
  rebateExpenseAccountId: number;
  rebateLiabilityAccountId: number;
  settlementMethod: string;
  isActive: boolean;
  tiers: RebateTierDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface RebateProgramRequest {
  agreementId: number;
  code: string;
  name: string;
  basis?: string;
  periodKind?: string;
  tierKind?: string;
  rebateExpenseAccountId: number;
  rebateLiabilityAccountId: number;
  settlementMethod?: string;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface RebateSettlementDTO {
  id: number;
  accrualId: number;
  settlementNo?: string | null;
  amount: string;
  currency: string;
  method: string;
  creditNoteId?: number | null;
  journalEntryId?: number | null;
  approvalRequestId?: number | null;
  settledAt?: string | null;
  settledBy?: number | null;
  status: string;
  rowVersion: number;
}

export interface RebateTierDTO {
  seq: number;
  minValue: string;
  maxValue?: string | null;
  ratePercent: string;
  rateAmount: string;
}

export interface RebateTierInput {
  seq: number;
  minValue: string;
  maxValue?: string;
  ratePercent?: string;
  rateAmount?: string;
}

export interface RebuildDemandRequest {
  itemId: number;
  warehouseId: number;
  weeks?: number;
}

export interface RebuildDemandResultDTO {
  itemId: number;
  warehouseId: number;
  periodsWritten: number;
}

export interface RebuildRequest {
  companyId: number;
}

export interface RebuildResponse {
  companyId: number;
  cardsWritten: number;
  generatedAt: string;
}

export interface RecallEvidencePackDTO {
  recallId: number;
  recallNo?: string;
  manifestHash: string;
  entries: RecallEvidencePackEntryDTO[];
  totalBytes: number;
  generatedAt: string;
  storageKey?: string;
}

export interface RecallEvidencePackEntryDTO {
  name: string;
  sha256: string;
  bytes: number;
}

export interface RecallInstructionRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface RecallOutcomeRequest {
  outcome: 'recalled' | 'failed';
  outcomeDate?: string;
  fxAccountId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface ReceiptDTO {
  id: number;
  receiptNo: string;
  companyId: number;
  customerId: number;
  receiptDate: string;
  currency: string;
  fxRate: string;
  amount: string;
  allocated: string;
  unallocated: string;
  depositAccountId: number;
  arAccountId: number;
  method: 'bank_transfer' | 'cash' | 'cheque' | 'card' | 'other';
  reference: string;
  memo: string;
  journalEntryId?: number | null;
  postedAt: string;
  createdAt: string;
  rowVersion: number;
  allocations?: ArAllocationDTO[];
}

export interface ReceiptResultDTO {
  entries: StockLedgerEntryDTO[];
  journalEntryId: number;
  journalEntryNo: string;
}

export interface ReceiveFinishedGoodsRequestC7 {
  quantity: string;
  date?: string;
  notes?: string;
  lotNo?: string;
  expiryDate?: string;
  serialNos?: string[];
}

export interface ReceiveGoodsRequest {
  companyId: number;
  date: string;
  docType: string;
  docId?: number;
  docNo?: string;
  lines: ReceiveLineInput[];
}

export interface ReceiveJointOutputsRequest {
  quantity: string;
  date?: string;
  notes?: string;
  lotNo?: string;
  expiryDate?: string;
  serialNos?: string[];
  outputs: JointOutputInput[];
}

export interface ReceiveJointOutputsResultDTO {
  order: ProductionOrderDTO | null;
  byProductCredit: string;
  poolSplit: string;
  outputs: JointOutputReceiptDTO[];
}

export interface ReceiveLineInput {
  itemId: number;
  warehouseId: number;
  binId?: number | null;
  quantity: string;
  unitCost: string;
  grirAccountId: number;
  lotNo?: string;
  serialNos?: string[];
}

export interface ReceiveReturnLineInput {
  lineNo: number;
  receivedQuantity: string;
  conditionCode: string;
  disposition: string;
  restockingFeePercent?: string;
}

export interface ReceiveReturnRequest {
  rowVersion: number;
  receivedAt?: string;
  lines: ReceiveReturnLineInput[];
}

export interface ReceiveTransferLineRequest {
  transferLineId: number;
  receivedQty: string;
  shortageQty?: string;
  shortageReasonCode?: string;
  toBinId?: number | null;
  serialNos?: string[];
}

export interface ReceiveTransferRequest {
  date: string;
  overrideReasonCode?: string;
  overrideNote?: string;
  lines: ReceiveTransferLineRequest[];
}

export interface RecipeCostDTO {
  recipeId: number;
  outputQty: string;
  warehouseId?: number | null;
  date: string;
  totalCost: string;
  lines: RecipeCostLineDTO[];
}

export interface RecipeCostLineDTO {
  itemId: number;
  itemSku?: string;
  itemName?: string;
  qty: string;
  unitCost: string;
  value: string;
  path: number[];
  optional?: boolean;
  modifierCode?: string;
}

export interface RecipeCostQuery {
  warehouseId?: number;
  date?: string;
  persist?: boolean;
}

export interface RecipeDTO {
  id: number;
  code: string;
  name: string;
  companyId: number;
  outputItemId: number;
  outputItemSku?: string;
  yieldQty: string;
  yieldUom?: string;
  kind: 'plated' | 'batch' | 'sub_recipe' | 'modifier';
  defaultWarehouseId?: number | null;
  theoreticalCost: string;
  theoreticalCostAt?: string | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  note?: string;
  rowVersion: number;
}

export interface RecipeDetailDTO {
  id: number;
  code: string;
  name: string;
  companyId: number;
  outputItemId: number;
  outputItemSku?: string;
  yieldQty: string;
  yieldUom?: string;
  kind: 'plated' | 'batch' | 'sub_recipe' | 'modifier';
  defaultWarehouseId?: number | null;
  theoreticalCost: string;
  theoreticalCostAt?: string | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  note?: string;
  rowVersion: number;
  lines: RecipeLineDTO[];
}

export interface RecipeLineDTO {
  id: number;
  lineNo: number;
  componentItemId?: number | null;
  componentItemSku?: string;
  componentRecipeId?: number | null;
  componentRecipeCode?: string;
  qty: string;
  wastePercent: string;
  grossQty: string;
  isOptional: boolean;
  modifierCode?: string;
  note?: string;
}

export interface RecipeLineRequest {
  lineNo: number;
  componentItemId?: number | null;
  componentRecipeId?: number | null;
  qty: string;
  wastePercent?: string;
  isOptional?: boolean;
  modifierCode?: string;
  note?: string;
}

export interface RecognizePercentCompleteRequest {
  invoiceDate?: string;
  taxCode?: string;
}

export interface ReconCertificateDTO {
  sessionId: number;
  bankAccountId: number;
  bankAccountName?: string;
  currency: string;
  fromDate: string;
  toDate: string;
  certificateNo: string;
  certifiedAt: string;
  certifiedBy?: number | null;
  certifiedByName?: string;
  balancePerBank: string;
  unpresentedTotal: string;
  unrecordedTotal: string;
  balancePerLedger: string;
  unmatchedCount: number;
  overrideReasonCode: string;
  certificateSha256: string;
  matchedSetSha256: string;
  matchedSetCount: number;
}

export interface ReconcileOpeningBalancesRequest {
  sourceTotal: string;
  note?: string;
}

export interface ReconcileRetentionRulesResult {
  inserted: number;
  unchanged: number;
  loosened?: string[];
  orphaned?: string[];
  unruledDiscovered?: string[];
}

export interface ReconcileReturnResponse {
  controlMovement: string;
  returnTotal: string;
  difference: string;
  return: StatutoryReturnDTO;
}

export interface ReconciliationDTO {
  importId: number;
  companyId: number;
  status: string;
  sourceTotal?: string | null;
  loadedTotal?: string | null;
  variance?: string | null;
  reconciledBy?: number | null;
  reconciledAt?: string | null;
  signedOffBy?: number | null;
  signedOffAt?: string | null;
  rowVersion: number;
}

export interface RecordClockEventForRequest {
  clientEventId: string;
  kind: string;
  happenedAt?: string;
  rosterLineId?: number | null;
  locationId?: number | null;
  latitude?: string | null;
  longitude?: string | null;
  accuracyM?: string | null;
  note?: string;
  employeeId: number;
  reasonCode: string;
}

export interface RecordClockEventRequest {
  clientEventId: string;
  kind: string;
  happenedAt?: string;
  rosterLineId?: number | null;
  locationId?: number | null;
  latitude?: string | null;
  longitude?: string | null;
  accuracyM?: string | null;
  note?: string;
}

export interface RecordCountLineRequest {
  lineId: number;
  countedQty: string;
  lotId?: number | null;
  binId?: number | null;
  note?: string;
}

export interface RecordCountLinesRequest {
  lines: WalletCountLineInput[];
  rowVersion: number;
}

export interface RecordEAMInspectionRequest {
  inspectedOn: string;
  result: 'pass' | 'pass_with_conditions' | 'fail';
  workOrderId?: number | null;
  inspector?: string;
  certificateRef?: string;
  validUntil?: string;
  findings?: string;
  attachmentId?: number | null;
}

export interface RecordObservationRequest {
  bankAccountId: number;
  asOfDate: string;
  bookedBalance: string;
  valueDatedBalance?: string;
  availableBalance?: string;
  currency?: string;
  source?: string;
  sourceRef?: string;
  note?: string;
}

export interface RecordOwnershipRequest {
  parentCompanyId: number;
  childCompanyId: number;
  holdingPct: string;
  holdingType: string;
  effectiveFrom: string;
  note?: string;
}

export interface RecordPOSAgeCheckRequest {
  lineId?: number | null;
  minimumAge: number;
  refused?: boolean;
  method?: string;
  note?: string;
  override?: POSOverrideRequest | null;
}

export interface RecordPOSCashMovementRequest {
  kind: 'float_in' | 'float_out' | 'drop' | 'paid_in' | 'paid_out' | 'no_sale';
  amount?: string;
  counterAccountId?: number | null;
  reasonCode?: string;
  note?: string;
  witnessedBy?: number | null;
}

export interface RecordPaymentRequest {
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  receivedOn: string;
  method: 'bank_transfer' | 'cheque' | 'cash' | 'card';
  providerRef: string;
  note?: string;
}

export interface RecordPermitClearanceRequest {
  medicalClearedOn: string;
  medicalExpiresOn: string;
  medicalReference?: string;
  rowVersion: number;
}

export interface RecordPickLineInput {
  lineNo: number;
  pickedQuantity: string;
  shortReasonCode?: string;
}

export interface RecordPickRequest {
  lines: RecordPickLineInput[];
}

export interface RecordQCInspectionRequest {
  productionOrderOperationId?: number | null;
  inspectionType: string;
  quantityInspected: string;
  quantityPassed?: string;
  quantityFailed?: string;
  notes?: string;
}

export interface RecordQuoteLogisticsRequest {
  freightAmount?: string;
  dutyAmount?: string;
  otherCostAmount?: string;
  incotermCode?: string;
  incotermPlace?: string;
  validityDate?: string;
}

export interface RecordRateObservationRequest {
  rateCode: string;
  asOf: string;
  rate: string;
  source?: string;
  note?: string;
}

export interface RecordRecountLineRequest {
  lineId: number;
  recountQty: string;
  note?: string;
}

export interface RecordRecountRequest {
  lines: RecordRecountLineRequest[];
}

export interface RecordResultInput {
  characteristicId: number;
  sampleNo?: number;
  numericValue?: string;
  attributeValue?: string;
  passed?: boolean | null;
  notes?: string;
  attachmentId?: number | null;
}

export interface RecordResultsRequest {
  results: RecordResultInput[];
  notes?: string;
}

export interface RecordScoreRequest {
  criterionId: number;
  rawScore: string;
  note?: string;
}

export interface RecordVerificationLinesRequest {
  lines: VerificationLineInput[];
}

export interface RecoupProjectAdvanceRequest {
  claimInvoiceId: number;
  amount?: string;
  recoupedOn?: string;
  taxCode?: string;
}

export interface RecoverPMSGuestRequestRequest {
  recoveryKind: 'none' | 'allowance' | 'comp_service' | 'points' | 'gift';
  recoveryValue?: string;
  causeCode?: string;
  note?: string;
  folioId?: number | null;
  resolutionStatus?: string;
  resolutionNote?: string;
  rowVersion: number;
}

export interface RecurringItemDTO {
  id: number;
  companyId: number;
  bankAccountId?: number | null;
  name: string;
  direction: 'inflow' | 'outflow';
  amount: string;
  currency: string;
  cadence: 'once' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'annually';
  nextDate: string;
  endDate?: string | null;
  notes: string;
  isActive: boolean;
  kind: string;
  loanReview: boolean;
  rowVersion: number;
}

export interface RedesignatePSURequest {
  toLegislativeDataGroupId: number;
  effectiveOn: string;
  reason?: string;
}

export interface ReferenceDataPageItem {
  configurationType: string;
  valueId: number;
  referenceSetId: number;
  membershipId: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface ReferenceDataQuery {
  businessUnitId: number;
  asOf?: string;
  cursor?: string;
  limit?: number;
}

export interface ReferenceSetDTO {
  id: number;
  enterpriseId: number;
  code: string;
  name: string;
  purpose: string;
  ownerPositionId?: number | null;
  status: string;
  isSystem: boolean;
  retirementReason?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  regions: ReferenceSetScopeInput[];
  rowVersion: number;
}

export interface ReferenceSetScopeInput {
  regionCode: string;
  companyId: number | null;
}

export interface ReferenceSetVersionDTO {
  id: number;
  referenceSetId: number;
  versionNo: number;
  changeReason?: string;
  retroactiveFlag: boolean;
  effectiveDate?: string;
  approvedBy?: number | null;
  setStatus: string;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface RefundPMSDepositRequest {
  amount?: string;
  bankAccountId?: number | null;
  businessDate?: string;
  reference?: string;
}

export interface RefundPOSDepositRequest {
  amount?: string;
  note?: string;
}

export interface RefuseDSRRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface Regime {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE' | 'SALES_TAX';
  jurisdictionId: number;
  filingCurrency: string;
  registrationPattern: string;
  registrationHint: string;
  unregisteredTaxCode?: string | null;
  sortOrder: number;
  description: string;
  isActive: boolean;
  source: 'pack' | 'tenant' | 'legacy';
  packId?: string | null;
  packVersion?: number | null;
  contentHash?: string | null;
}

export interface RegimeBinding {
  regime: Regime;
  basis: 'origin' | 'destination' | 'hybrid';
  jurisdiction: Jurisdiction;
  governing: Jurisdiction;
  chain: Jurisdiction[];
  registration?: Registration | null;
  unregistered: boolean;
  fallback: boolean;
}

export interface RegimeDTO {
  id: number;
  code: string;
  name: string;
  kind: string;
  jurisdictionId: number;
  filingCurrency: string;
  registrationPattern: string;
  registrationHint: string;
  unregisteredTaxCode?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface Registration {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  companyId: number;
  jurisdictionId: number;
  regimeId: number;
  registrationNo: string;
  registrationNoNormalised: string;
  scheme: string;
  status: 'pending' | 'active' | 'suspended' | 'deregistered';
  effectiveFrom: string;
  effectiveTo?: string | null;
  returnFrequency: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  firstPeriodStart?: string | null;
  filingCurrency: string;
  entityDesignation: string;
  isPrimary: boolean;
  notes: string;
}

export interface RegistrationDTO {
  id: number;
  companyId: number;
  jurisdictionId: number;
  regimeId: number;
  registrationNo: string;
  registrationNoNormalised: string;
  scheme: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  returnFrequency: string;
  firstPeriodStart?: string | null;
  filingCurrency: string;
  entityDesignation: string;
  isPrimary: boolean;
  notes: string;
  rowVersion: number;
}

export interface RejectAIRecommendationRequest {
  reason: string;
}

export interface RejectAdjustmentRequest {
  reason: string;
}

export interface RejectEliminationRequest {
  note: string;
  rowVersion: number;
}

export interface RejectEnterpriseRequest {
  reason: string;
}

export interface RejectGuidedRequest {
  reason: string;
}

export interface RejectInstructionRequest {
  returned?: boolean;
  reasonCode: string;
  outcomeDate?: string;
  fxAccountId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface RejectPartyMergeRequest {
  reason: string;
}

export interface RejectProjectVariationRequest {
  reason: string;
}

export interface RejectQuarantineRequest {
  note?: string;
}

export interface RejectQuoteRequest {
  reason: string;
}

export interface RejectRequest {
  comment: string;
}

export interface RejectReturnRequest {
  rowVersion: number;
  reasonCode: string;
  note?: string;
}

export interface RejectTimesheetRequest {
  reason: string;
}

export interface RejectWasteRequest {
  reason: string;
}

export interface ReleaseAllocationRequest {
  reasonCode?: string;
  note?: string;
}

export interface ReleaseAttendanceLockRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface ReleaseBankChangeEarlyRequest {
  reasonCode: string;
  note?: string;
}

export interface ReleaseEAMDowntimeRequest {
  endedAt?: string;
  note?: string;
}

export interface ReleaseEarlyRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface ReleaseInstructionRequest {
  valueDate?: string;
  bankReference?: string;
  override?: InstructionOverride | null;
  rowVersion: number;
}

export interface ReleaseInvoiceHoldRequest {
  reason: string;
}

export interface ReleasePMSAuthHoldRequest {
  status?: 'held' | 'captured' | 'released' | 'expired' | 'failed';
  note?: string;
  rowVersion: number;
}

export interface ReleasePMSHousekeepingBoardRequest {
  rowVersion: number;
}

export interface ReleaseProductionOrderRequest {
  override?: ToolGateOverride | null;
}

export interface ReleaseProjectRetentionRequest {
  releaseDate?: string;
  reasonCode?: string;
  note?: string;
}

export interface ReleaseQuarantineRequest {
  note?: string;
}

export interface ReleaseReservationRequest {
  reason?: string;
}

export interface ReleaseSuppressionRequest {
  reason: string;
  rowVersion: number;
  overrideReasonCode?: string;
  overrideNote?: string;
}

export interface RemittanceDTO {
  id: number;
  returnId: number;
  companyId: number;
  bankAccountId: number;
  profileId?: number | null;
  amount: string;
  currency: string;
  paymentDate: string;
  status: 'prepared' | 'confirmed' | 'cancelled';
  journalEntryId?: number | null;
  journalEntryNo?: string;
  reference?: string;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface RemittanceFileResponse {
  remittanceId: number;
  returnId: number;
  profileCode?: string;
  profileName?: string;
  filename: string;
  contentType: string;
  content: string;
  sha256: string;
  rows: number;
  disclaimer: string;
}

export interface RemoveFunctionQuery {
  effectiveTo?: string;
}

export interface RenderStatusDTO {
  docType: string;
  docId: number;
  version: string;
  status: string;
  attachmentId?: number | null;
  error?: string;
  requestedAt?: string | null;
  completedAt?: string | null;
}

export interface RenewServiceContractRequest {
  startsOn?: string;
  endsOn?: string;
  note?: string;
}

export interface ReopenCaseRequest {
  reason: string;
  rowVersion: number;
}

export interface ReopenCloseTaskRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface ReopenDayRequest {
  reasonCode: string;
  note: string;
}

export interface ReopenPeriodRequest {
  reason: string;
}

export interface ReopenTicketRequest {
  reason: string;
  rowVersion: number;
}

export interface ReorderConversionLineErrorDTO {
  suggestionId: number;
  reason: string;
}

export interface ReorderConversionResultDTO {
  requestId?: number;
  requestNo?: string;
  converted: number;
  lineErrors?: ReorderConversionLineErrorDTO[];
}

export interface ReorderRequest {
  kind: 'branch' | 'department' | 'cost_center' | 'profit_center' | 'business_unit' | 'division';
  id: number;
  parentId?: number | null;
  sortOrder: number;
  rowVersion?: number;
}

export interface ReorgAffectedAssignmentDTO {
  positionId: number;
  positionCode: string;
  employeeId: number;
  reason: string;
}

export interface ReorgCostCentreImpactDTO {
  positionId: number;
  fromCostCenterId?: string;
  toCostCenterId?: string;
}

export interface ReorganisationItemRequest {
  objectType: string;
  objectId: number;
  field: string;
  toValue: string;
}

export interface ReorganisationSimulationDTO {
  items: ResolvedReorgItem[];
  affectedAssignments: ReorgAffectedAssignmentDTO[];
  costCentreImpact: ReorgCostCentreImpactDTO[];
}

export interface ReorganiseDivisionFNDRequest {
  toDivisionId: number;
  companyIds: number[];
  effectiveOn: string;
  reasonCode?: string;
}

export interface ReorganiseDivisionFNDResult {
  fromDivisionId: number;
  toDivisionId: number;
  effectiveOn: string;
  entities: ReorganisedEntityResult[];
}

export interface ReorganisedEntityResult {
  companyId: number;
  moved: boolean;
}

export interface RepackLotRequest {
  parentLotId: number;
  childItemId?: number | null;
  childLotNo: string;
  qtyConsumed: string;
  occurredOn: string;
  expiryDate?: string | null;
  docType?: string;
  docId?: number | null;
}

export interface ReplaceLinesRequest {
  lines: TimesheetLineInput[];
  rowVersion: number;
}

export interface ReplacePMSRatePlanRatesRequest {
  from: string;
  to: string;
  rows: PMSRatePlanRateInput[];
}

export interface ReplaceRVMForecastLinesRequest {
  lines: RVMForecastLineInput[];
  rowVersion: number;
}

export interface ReplaceRosterLinesRequest {
  lines: RosterLineInput[];
  rowVersion: number;
}

export interface ReplenishmentPolicyDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  warehouseId?: number | null;
  companyId: number;
  method: string;
  leadTimeDays: number;
  leadTimeStddevDays: string;
  reviewPeriodDays: number;
  serviceLevel: string;
  safetyStockOverride?: string | null;
  minOrderQty?: string | null;
  orderMultiple?: string | null;
  maxOrderQty?: string | null;
  orderCost: string;
  holdingCostPct: string;
  preferredVendorId?: number | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  note?: string;
  rowVersion: number;
}

export interface ReplenishmentSuggestionDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  companyId: number;
  method: string;
  onHandQty: string;
  reservedQty: string;
  inboundQty: string;
  averageDemand: string;
  demandStddev: string;
  leadTimeDays: number;
  safetyStock: string;
  computedRop: string;
  eoq?: string | null;
  reorderPoint: string;
  reorderMax: string;
  suggestedQty: string;
  preferredVendorId?: number | null;
  policyId?: number | null;
  status: string;
  generatedAt: string;
  convertedPrId?: number | null;
  convertedPrNo?: string;
}

export interface Report {
  from: string;
  to: string;
  companyId?: number | null;
  output: ReportRow[];
  input: ReportRow[];
  totals: CurrencyTotal[];
  generatedAt: string;
}

export interface ReportExceptionRowDTO {
  source: 'attention' | 'recon_queue' | 'approvals' | 'ai_recommendations';
  sourceId: number;
  kind?: string;
  module?: string;
  docType?: string;
  docNo?: string;
  titleKey?: string;
  amount?: string | null;
  currency?: string;
  openedAt: string;
  dueAt?: string | null;
  ageDays: number;
  link?: string;
}

export interface ReportExceptionRowsDTO {
  viewId: number;
  asAt: string;
  refreshMinutes: number;
  ageSeconds: number;
  isLive: boolean;
  items: ReportExceptionRowDTO[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ReportExceptionRowsQuery {
  cursor?: string;
  limit?: number;
}

export interface ReportExceptionViewDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description?: string;
  roleCode: string;
  sources: ('attention' | 'recon_queue' | 'approvals' | 'ai_recommendations')[];
  filters?: Record<string, unknown>;
  refreshMinutes: number;
  cachedAt?: string | null;
  cachedCount?: number | null;
  isActive: boolean;
  sortOrder: number;
  rowVersion: number;
}

export interface ReportOutputDTO {
  id: number;
  companyId: number;
  reportKey: string;
  format: string;
  certificationState: 'certified' | 'ad_hoc' | 'mixed';
  metricsIncluded: ReportOutputMetricDTO[];
  asOfDate?: string | null;
  scheduleId?: number | null;
  attachmentId?: number | null;
  rowCount: number;
  emittedAt: string;
  emittedBy?: number | null;
  emittedActorType: string;
  banner?: string;
}

export interface ReportOutputMetricDTO {
  code: string;
  certified: boolean;
  certificationId?: number | null;
  stale: boolean;
}

export interface ReportQuery {
  companyId: number | null;
  from: string;
  to: string;
}

export interface ReportRangeQuery {
  from?: string;
  to?: string;
  companyId?: number;
}

export interface ReportRow {
  direction: 'input' | 'output' | 'any';
  taxCode: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE';
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency: string;
  isRecoverable: boolean;
  baseAmount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  taxAmount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  lineCount: number;
  documentCount: number;
}

export interface ReportingCurrencyDTO {
  id: number;
  ledgerId: number;
  currency: string;
  conversionBasis: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface ReportingDashboardResponse {
  dashboard: string;
  companyId: number;
  cards: KPICard[];
  generatedAt: string;
}

export interface ReportingDeleteQuery {
  rowVersion?: number;
}

export interface ReportingExportQuery {
  reportKey: string;
  companyId: number;
  format: string;
}

export interface ReportingSettingsDTO {
  companyId: number;
  dailyOperatingDueHour: number;
  allowPartialDailyOperating: boolean;
  dailyOperatingDetailDays: number;
  markUncertifiedOutputs: boolean;
  rowVersion: number;
}

export interface ReportingSettingsQuery {
  companyId: number;
}

export interface RepricePOSSaleRequest {
  persist?: boolean;
}

export interface RepriceReviewDTO {
  subscriptionId: number;
  priceSource: string;
  storedPrice: string;
  enginePrice?: string;
  difference?: string;
  unpricedReason?: string;
  currency: string;
  waterfall?: PriceConsiderationDTO[];
  priceResolutionLogId?: number | null;
  reviewedOn: string;
  changed: boolean;
  rowVersion: number;
}

export interface RepriceReviewRequest {
  adopt?: boolean;
  applyPrice?: boolean;
  confirmManual?: boolean;
  manualPriceReason?: string;
  rowVersion: number;
}

export interface RepromiseRequest {
  promisedDate: string;
  reasonCode: string;
  note?: string;
  lineNos?: number[];
}

export interface RepublishPositionRequest {
  reasonCode: string;
  reason: string;
  baseCurrency?: string;
  includeInactive?: boolean | null;
}

export interface RequestDTO {
  id: number;
  docType: string;
  docId: number;
  docNo?: string;
  companyId?: number | null;
  amount?: string;
  currency?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'voided';
  currentStep: number;
  policyId?: number | null;
  policyName?: string;
  submittedBy: number;
  submitterName?: string;
  submittedAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  comment?: string;
  tasks: TaskDTO[];
  history: HistoryEntry[];
  cycle: number;
  origin: string;
  source: string;
  authorityVersionId?: number | null;
  authorityRuleId?: number | null;
  voidedRequestId?: number | null;
  voidReasonCode?: string;
  ratificationDueAt?: string | null;
  ratifiedAt?: string | null;
  ratifiedBy?: number | null;
}

export interface RequestDetailChangeRequest {
  kind: string;
  reason: string;
  bankName?: string;
  bankBranch?: string;
  bankAccountName?: string;
  bankAccountNo?: string;
  bankIban?: string;
  bankSwift?: string;
  bankCurrency?: string;
  nationalId?: string;
  taxId?: string;
}

export interface RequestReopenRequest {
  reasonCode: string;
  reason: string;
}

export interface RequestReplenishmentRequest {
  requestedOn?: string;
  requestedAmount?: string;
  amount?: string;
  fundingBankAccountId: number;
  note?: string;
  justification?: string;
}

export interface RequestServiceGoodwillRequest {
  companyId: number;
  customerId: number;
  workOrderId?: number | null;
  ticketId?: number | null;
  amount: string;
  causeCode: string;
  note?: string;
  taxCode?: string;
  override?: ServiceDispatchOverrideDTO | null;
}

export interface RequisitionDTO {
  id: number;
  requisitionNo: string;
  companyId: number;
  departmentId?: number | null;
  title: string;
  headcount: number;
  justification?: string;
  status: string;
  rowVersion: number;
}

export interface RequisitionPositionDTO {
  id: number;
  positionId?: number | null;
  gradeId?: number | null;
  locationId?: number | null;
  scorecardCriteria?: string[];
  rowVersion: number;
}

export interface RescoreLeadRequest {
}

export interface ResequenceDispatchListRequest {
  operationIds: number[];
}

export interface ReservationDetailDTO {
  id: number;
  itemId: number;
  warehouseId: number;
  kind: 'soft' | 'hard';
  qty: string;
  docType: string;
  docId: number;
  docLineNo?: number | null;
  status: 'active' | 'released' | 'consumed';
  note?: string;
  lotId?: number | null;
  binId?: number | null;
  serialNos?: string[];
  priority: number;
  expiresAt?: string | null;
  releasedReason?: string;
  consumedByEntryId?: number | null;
  createdAt: string;
  releasedAt?: string | null;
  rowVersion: number;
}

export interface ResetPasswordRequest {
  tenantSlug: string;
  token: string;
  newPassword: string;
}

export interface Resolution {
  base: string;
  quote: string;
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  on: string;
  asOf?: string | null;
  method: 'identity' | 'direct' | 'inverse';
  source?: 'manual' | 'api';
  observationId?: number | null;
  derivation: string;
}

export interface ResolveAttendanceExceptionRequest {
  resolutionReasonCode: string;
  resolutionNote?: string;
  writeHours: boolean;
  rowVersion: number;
}

export interface ResolveBatchRequest {
  lines: ResolvePriceQuery[];
}

export interface ResolveBatchResponse {
  lines: PriceResolutionDTO[];
}

export interface ResolveCaseRequest {
  resolution?: string;
  rowVersion: number;
}

export interface ResolveClientQuery {
  clientBusinessUnitId: number;
  functionCode: string;
  asOf?: string;
}

export interface ResolveClientResult {
  allowed: boolean;
  providerBuId: number;
  clientBuId: number;
  functionCode: string;
  relationshipId?: number | null;
  clientPrimaryLegalEntityId?: number | null;
}

export interface ResolveDisputeRequest {
  accept: boolean;
  note: string;
}

export interface ResolveHierarchyQuery {
  orgType: string;
  orgId: number;
  asAt?: string;
}

export interface ResolveHierarchyResult {
  hierarchyId: number;
  versionId: number;
  versionNo: number;
  versionStatus: string;
  asAt: string;
  node: HierarchyNodeDTO;
  ancestors: HierarchyNodeDTO[];
  descendants: HierarchyNodeDTO[];
  rollupPath: HierarchyNodeDTO[];
}

export interface ResolveInput {
  kind?: string;
  displayName?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  postcode?: string;
  identifiers?: IdentifierInput[];
  excludePartyId?: number;
  limit?: number;
}

export interface ResolveItemRequest {
  disposition: string;
  reasonCode?: string;
  note?: string;
  docType?: string;
  docId?: number | null;
}

export interface ResolveLedgerQuery {
  companyId: number;
  on?: string;
}

export interface ResolveMandateQuery {
  bankAccountId: number;
  amount: string;
  currency?: string;
  on?: string;
}

export interface ResolveMatchDTO {
  partyId: number;
  displayName: string;
  kind: 'person' | 'organisation';
  email?: string;
  phone?: string;
  method: 'identifier' | 'email' | 'phone' | 'name_address';
  score: string;
  signals: string[];
}

export interface ResolveMatchExceptionRequest {
  reject?: boolean;
  note: string;
  rowVersion: number;
}

export interface ResolveNCRRequest {
  disposition: string;
  resolutionNote?: string;
}

export interface ResolvePMSRoomFaultRequest {
  resolutionNote?: string;
  rowVersion: number;
}

export interface ResolvePOSAnomalyRequest {
  resolution: string;
  note?: string;
  dismiss?: boolean;
}

export interface ResolvePaymentExceptionRequest {
  note?: string;
}

export interface ResolvePriceQuery {
  companyId: number;
  customerId?: number;
  itemId: number;
  quantity?: string;
  uom?: string;
  currency?: string;
  on?: string;
  channel?: string;
  supplyType?: string;
  dayPart?: string;
  segmentCode?: string;
  locationId?: number;
  warehouseId?: number;
  priceSetVersionId?: number;
}

export interface ResolveRVMHurdleQuery {
  propertyId: number;
  stayDate: string;
  segmentId?: number;
  roomTypeId?: number;
}

export interface ResolveRVMSegmentQuery {
  companyId: number;
  segmentCode?: string;
  marketCode?: string;
  sourceCode?: string;
  channelId?: number;
}

export interface ResolveRateQuery {
  base: string;
  quote: string;
  on: string;
  rateType?: 'spot' | 'corporate' | 'historical';
}

export interface ResolveRecipeQuery {
  itemId: number;
  date?: string;
}

export interface ResolveResult {
  exactPartyId?: number;
  exactMethod?: 'identifier' | 'email' | 'phone' | 'name_address';
  candidates: ResolveMatchDTO[];
}

export interface ResolveShortageRequest {
  status: 'open' | 'substituted' | 'backordered' | 'cancelled' | 'resolved';
  note?: string;
  releaseLowerPriority?: boolean;
}

export interface ResolveSyncConflictRequest {
  resolution: string;
  note?: string;
  dismiss?: boolean;
}

export interface ResolveTicketRequest {
  resolution?: string;
  rowVersion: number;
}

export interface ResolvedReorgItem {
  objectType: string;
  objectId: number;
  field: string;
  fromValue?: string;
  toValue?: string;
}

export interface RespondCaseRequest {
  body: string;
  isPublic: boolean;
  rowVersion: number;
}

export interface RespondTicketRequest {
  body: string;
  isPublic: boolean;
  rowVersion: number;
}

export interface RestoreEAMFailureRequest {
  workOrderId?: number | null;
  restoredAt?: string;
  rowVersion: number;
}

export interface RestrictPMSGuestRequest {
  reason: 'non_payment' | 'property_damage' | 'abusive_behaviour' | 'security_concern' | 'fraud' | 'health_and_safety' | 'other';
  note?: string;
}

export interface ResumeCaseRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface RetroEventDTO {
  id: number;
  companyId: number;
  employeeId: number;
  kind: 'assignment' | 'component' | 'attendance' | 'leave' | 'statutory_rule';
  field: string;
  oldValue: string;
  newValue: string;
  effectiveFrom: string;
  affectedFrom: string;
  affectedTo?: string | null;
  status: 'open' | 'applied' | 'superseded' | 'dismissed';
  appliedRunId?: number | null;
  dismissedReasonCode?: string;
  sourceEntity?: string;
  sourceEntityId?: number;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface RetroLineDTO {
  id: number;
  runId: number;
  retroEventId: number;
  employeeId: number;
  originRunId: number;
  originPeriodStart: string;
  originPeriodEnd: string;
  componentId?: number | null;
  componentCode: string;
  componentType: string;
  ruleSource: 'tenant' | 'pack' | 'legacy';
  ruleCode?: string;
  originalAmount: string;
  recomputedAmount: string;
  deltaAmount: string;
  trace?: Record<string, unknown>;
  createdAt: string;
}

export interface ReturnDTO {
  id: number;
  returnNo?: string | null;
  companyId: number;
  customerId: number;
  orderId?: number | null;
  shipmentId?: number | null;
  warehouseId: number;
  returnDate: string;
  status: 'draft' | 'authorised' | 'received' | 'posted' | 'rejected' | 'cancelled';
  reason: string;
  memo: string;
  creditNoteId?: number | null;
  createdAt: string;
  rowVersion: number;
  lines?: ReturnLineDTO[];
  authorisedAt?: string | null;
  authorisedBy?: number | null;
  authorisationExpiresOn?: string | null;
  receivedAt?: string | null;
  receivedBy?: number | null;
  rejectedAt?: string | null;
  rejectedBy?: number | null;
  rejectionReasonCode?: string;
  reasonCode?: string;
  restockingFeeTotal?: string;
  approvalRequestId?: number | null;
}

export interface ReturnDetailDTO {
  id: number;
  registrationId: number;
  returnDefCode: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  status: string;
  returnNo?: string | null;
  filingCurrency: string;
  netPayable: string;
  rowVersion: number;
  totalOutputBase: string;
  totalOutputTax: string;
  totalInputBase: string;
  totalInputTax: string;
  totalRecoverable: string;
  glControlMovement: string;
  reconciliationDifference: string;
  supersededById?: number | null;
  supersedeReason?: string;
  lines: TaxReturnLineDTO[];
}

export interface ReturnLineDTO {
  id: number;
  lineNo: number;
  shipmentLineId?: number | null;
  itemId: number;
  warehouseId: number;
  quantity: string;
  unitCost: string;
  unitPrice: string;
  restock: boolean;
  lineAmount: string;
  authorisedQuantity: string;
  receivedQuantity?: string;
  conditionCode?: string;
  disposition?: string;
  restockingFeePercent?: string;
  restockingFeeAmount?: string;
  revenueAccountId?: number | null;
}

export interface ReturnLineInput {
  shipmentLineId?: number | null;
  itemId: number;
  warehouseId: number;
  quantity: string;
  unitCost?: string;
  unitPrice: string;
  restock?: boolean | null;
}

export interface RevRecAllocationDTO {
  obligationId: number;
  sspAmount: string;
  sspShare: string;
  allocatedAmount: string;
  isResidualHolder: boolean;
  currency: string;
}

export interface RevRecLineDTO {
  id: number;
  periodStart: string;
  periodEnd: string;
  amount: string;
  recognized: boolean;
  recognizedAt?: string | null;
}

export interface RevRecScheduleDTO {
  id: number;
  companyId: number;
  sourceDocType: string;
  sourceDocId: number;
  customerId: number;
  totalAmount: string;
  deferredAccountId: number;
  revenueAccountId: number;
  currency: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'completed' | 'cancelled';
  lines?: RevRecLineDTO[];
}

export interface RevaluationRunDTO {
  id: number;
  companyId?: number | null;
  base: string;
  quote: string;
  rateType: string;
  fromDate: string;
  toDate: string;
  priorRate: string;
  newRate: string;
  impactStatement: string;
  approvalRequestId?: number | null;
  firstApproverId?: number | null;
  secondApproverId?: number | null;
  status: string;
  reasonCode: string;
  executedAt?: string | null;
  executedBy?: number | null;
}

export interface RevaluationSubmittedDTO {
  requestId: number;
  status: string;
  base: string;
  quote: string;
  asOf: string;
}

export interface RevalueRequest {
  newCost?: string;
  newUsefulLifeMonths?: number;
  newSalvageValue?: string;
  on: string;
  reason: string;
  rowVersion: number;
}

export interface RevealBankRequest {
  reason: string;
}

export interface RevealBankResponse {
  employeeId: number;
  bankAccountName: string;
  bankAccountNo: string;
  bankIban?: string;
  bankName: string;
  bankSwift?: string;
  bankCurrency: string;
  revealedAt: string;
}

export interface RevealBeneficiaryRequest {
  reason: string;
}

export interface RevealIdentifierRequest {
  reason: string;
}

export interface RevealIdentifierResult {
  identifierId: number;
  partyId: number;
  kind: 'national_id' | 'passport' | 'tax_id' | 'driving_licence' | 'company_registration' | 'loyalty_number' | 'external_key';
  issuingCountry?: string;
  value: string;
  revealedAt: string;
}

export interface RevealPMSGuestDocumentRequest {
  reason: string;
}

export interface RevealPermitRequest {
  reason: string;
}

export interface RevealPermitResponse {
  id: number;
  employeeId: number;
  permitNo: string;
  revealedAt: string;
}

export interface RevealRequest {
  reason: string;
}

export interface RevealResponse {
  bankAccountId: number;
  vendorId: number;
  accountName: string;
  accountNo: string;
  iban?: string;
  bankName: string;
  swift: string;
  currency: string;
  revealedAt: string;
}

export interface ReverseAccrualItemRequest {
  on?: string;
  reasonCode?: string;
  note?: string;
}

export interface ReverseCostRunRequest {
  reason: string;
  on?: string;
}

export interface ReverseDepletionRequest {
  reason: string;
  date?: string;
}

export interface ReverseDrawdownRequest {
  reason: string;
  valueDate?: string;
  note?: string;
  rowVersion: number;
}

export interface ReverseEntryRequest {
  reversalDate?: string;
  reason: string;
}

export interface ReverseGRNRequest {
  reason: string;
}

export interface ReverseImpairmentRequest {
  on: string;
  recoverableAmount: string;
  reasonCode?: string;
  note?: string;
}

export interface ReverseInvoiceRequest {
  reversalDate?: string;
  reason: string;
}

export interface ReverseMergeResult {
  mergeId: number;
  status: 'draft' | 'submitted' | 'approved' | 'committed' | 'rejected' | 'cancelled' | 'reversed';
  linksRestored: number;
  consentRestored: number;
  fieldsRestored: number;
  retainedBySurvivor?: Record<string, number>;
  retainedEdited?: string[];
}

export interface ReversePMSFolioChargeRequest {
  reasonCode: string;
  note?: string;
  businessDate?: string;
}

export interface ReversePartyMergeRequest {
  reason: string;
}

export interface ReverseProjectWIPRunRequest {
  reason: string;
  reversalDate?: string;
}

export interface ReverseReceiptRequest {
  docType: string;
  docId: number;
  reason: string;
}

export interface ReverseReorganisationRequest {
  reasonCode?: string;
}

export interface ReverseRepaymentRequest {
  reason: string;
  valueDate?: string;
  note?: string;
  rowVersion: number;
}

export interface ReverseShipmentRequest {
  reason: string;
}

export interface ReverseTxnRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface ReverseYearEndCloseRequest {
  reason: string;
  rowVersion: number;
}

export interface ReviewExtractionRequest {
  note?: string;
}

export interface ReviewKBArticleRequest {
  reviewIntervalDays: number;
}

export interface ReviewLoyaltyTierResult {
  memberId: number;
  fromTierId?: number | null;
  fromTierCode?: string;
  toTierId?: number | null;
  toTierCode?: string;
  changed: boolean;
  reviewedOn: string;
  reason: string;
}

export interface ReviewRunRequest {
  comment?: string;
}

export interface ReviewScreeningRequest {
  reviewOutcome: string;
  reviewNote: string;
}

export interface ReviseForecastRequest {
  reason: string;
}

export interface ReviseQuoteRequest {
  reasonCode: string;
  note?: string;
  lines?: SalesQuoteLineInput[];
}

export interface RevokeAuthorityDelegationRequest {
  reasonCode: string;
  note?: string;
}

export interface RevokeDeviceRequest {
  reason: string;
}

export interface RevokeHRSkillQuery {
  skillId: number;
}

export interface RevokeMetricCertificationRequest {
  reasonCode: string;
  note?: string;
}

export interface RevpolicyCreateRequest {
  name: string;
  description?: string;
  requestedEffectiveFrom: string;
  rules?: RevpolicyRuleInput[];
}

export interface RevpolicyCurrentDTO {
  inForce?: RevpolicyPolicyDTO | null;
  openDraft?: RevpolicyPolicyDTO | null;
}

export interface RevpolicyDepartureDTO {
  scope: 'sales' | 'subscription' | 'projects' | 'service' | 'pos' | 'pms';
  sourceDocType: string;
  sourceDocId: number;
  docNo: string;
  companyId: number;
  projectId?: number | null;
  docDate: string;
  amount?: string;
  currency?: string;
  actualRecognition: 'point_in_time' | 'over_time' | 'milestone' | 'as_billed';
  actualTrigger: 'control_transfer' | 'service_completed' | 'day_close' | 'night_audit' | 'invoice' | 'time_incurred' | 'period_elapsed' | 'progress_measured' | 'milestone_certified';
  actualSspMethod?: 'observable' | 'adjusted_market' | 'expected_cost_plus' | 'residual' | 'not_applicable';
  policyId?: number | null;
  policyVersionNo?: number | null;
  permittedRecognitions?: ('point_in_time' | 'over_time' | 'milestone' | 'as_billed')[];
  reason: string;
  detail: string;
}

export interface RevpolicyDeparturesQuery {
  companyId?: number;
  from: string;
  to: string;
  scope?: 'sales' | 'subscription' | 'projects' | 'service' | 'pos' | 'pms';
}

export interface RevpolicyDeparturesReportDTO {
  from: string;
  to: string;
  companyId?: number | null;
  scope?: 'sales' | 'subscription' | 'projects' | 'service' | 'pos' | 'pms';
  scopes: RevpolicyScopeSummaryDTO[];
  items: RevpolicyDepartureDTO[];
  examined: number;
  truncated: boolean;
}

export interface RevpolicyDiscardRequest {
  rowVersion: number;
}

export interface RevpolicyListQuery {
  cursor?: string;
  limit?: number;
  status?: 'draft' | 'submitted' | 'in_force' | 'superseded' | 'discarded';
  asAt?: string;
}

export interface RevpolicyPolicyDTO {
  id: number;
  versionNo: number;
  policyNo: string;
  name: string;
  description: string;
  status: 'draft' | 'submitted' | 'in_force' | 'superseded' | 'discarded';
  requestedEffectiveFrom: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  supersedesId?: number | null;
  approvalRequestId?: number | null;
  rules: RevpolicyRuleDTO[];
  createdBy?: number | null;
  createdAt: string;
  updatedBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  supersededAt?: string | null;
  rejectionNote?: string;
  approverUserIds?: number[];
  rowVersion: number;
}

export interface RevpolicyRuleDTO {
  scope: 'sales' | 'subscription' | 'projects' | 'service' | 'pos' | 'pms';
  recognition: 'point_in_time' | 'over_time' | 'milestone' | 'as_billed';
  trigger: 'control_transfer' | 'service_completed' | 'day_close' | 'night_audit' | 'invoice' | 'time_incurred' | 'period_elapsed' | 'progress_measured' | 'milestone_certified';
  sspMethod: 'observable' | 'adjusted_market' | 'expected_cost_plus' | 'residual' | 'not_applicable';
  note?: string;
}

export interface RevpolicyRuleInput {
  scope: 'sales' | 'subscription' | 'projects' | 'service' | 'pos' | 'pms';
  recognition: 'point_in_time' | 'over_time' | 'milestone' | 'as_billed';
  trigger: 'control_transfer' | 'service_completed' | 'day_close' | 'night_audit' | 'invoice' | 'time_incurred' | 'period_elapsed' | 'progress_measured' | 'milestone_certified';
  sspMethod?: 'observable' | 'adjusted_market' | 'expected_cost_plus' | 'residual' | 'not_applicable';
  note?: string;
}

export interface RevpolicyScopeSummaryDTO {
  scope: 'sales' | 'subscription' | 'projects' | 'service' | 'pos' | 'pms';
  examined: number;
  departures: number;
  truncated: boolean;
}

export interface RevpolicySubmitRequest {
  rowVersion: number;
}

export interface RevpolicySupersedeRequest {
  requestedEffectiveFrom: string;
  name?: string | null;
  description?: string | null;
  rules?: RevpolicyRuleInput[];
  rowVersion: number;
}

export interface RevpolicyUpdateRequest {
  name?: string | null;
  description?: string | null;
  requestedEffectiveFrom?: string | null;
  rules?: RevpolicyRuleInput[];
  rowVersion: number;
}

export interface RightToWorkDTO {
  candidateId: number;
  status: string;
  evidenceId?: number | null;
  expiresOn?: string | null;
  checkedBy?: number | null;
  checkedAt?: string | null;
  rowVersion: number;
}

export interface RoleDTO {
  code: string;
  name: string;
  description: string;
  isSystem: boolean;
}

export interface RollbackHierarchyRequest {
  versionId: number;
  effectiveFrom: string;
  effectiveTo: string;
  changeSummary: string;
}

export interface RollupCostCentre {
  id: number;
  code: string;
  name: string;
}

export interface RosterDTO {
  id: number;
  rosterNo?: string;
  companyId: number;
  locationId?: number | null;
  departmentId?: number | null;
  fromDate: string;
  toDate: string;
  status: string;
  basis: string;
  plannedHours: string;
  plannedCost: string;
  currency: string;
  note?: string;
  lineCount: number;
  openLineCount: number;
  demandCoverage: Coverage;
  publishedAt?: string;
  lockedAt?: string;
  rowVersion: number;
}

export interface RosterDetailDTO {
  roster: RosterDTO;
  lines: RosterLineDTO[];
  evaluations: RuleEvaluationDTO[];
  breaches: WorkingTimeBreachDTO[];
}

export interface RosterLineDTO {
  id: number;
  rosterId: number;
  employeeId?: number | null;
  employeeNo?: string;
  employeeName?: string;
  assignmentId?: number | null;
  workDate: string;
  shiftDefinitionId?: number | null;
  shiftCode?: string;
  startsAt?: string;
  endsAt?: string;
  plannedHours: string;
  costRateHourly: string;
  plannedCost: string;
  lineStatus: string;
  isRestDay: boolean;
  note?: string;
  rowVersion: number;
}

export interface RosterLineInput {
  employeeId?: number | null;
  workDate: string;
  shiftDefinitionId?: number | null;
  startsAt?: string;
  endsAt?: string;
  lineStatus?: string;
  note?: string;
}

export interface RosterSettingsDTO {
  companyId: number;
  standardHoursPerMonth: string;
  defaultCalendarId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface RosterSettingsQuery {
  companyId: number;
}

export interface RotateSecretResponse {
  secret: string;
}

export interface RouteLeadRequest {
}

export interface RoutingDTO {
  id: number;
  companyId: number;
  itemId: number;
  version: number;
  status: 'draft' | 'active' | 'obsolete';
  name?: string;
  notes?: string;
  operations: RoutingOperationDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface RoutingOperationDTO {
  id: number;
  seqNo: number;
  workCenterId: number;
  name: string;
  setupTimeMinutes: string;
  runTimeMinutesPerUnit: string;
  description?: string;
}

export interface RoutingOperationInput {
  seqNo: number;
  workCenterId: number;
  name: string;
  setupTimeMinutes?: string;
  runTimeMinutesPerUnit?: string;
  description?: string;
}

export interface RoutingOperationToolDTO {
  id: number;
  routingOperationId: number;
  toolId: number;
  isMandatory: boolean;
}

export interface RoutingOperationToolInput {
  toolId: number;
  isMandatory?: boolean;
}

export interface RowDiff {
  key: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export interface RowError {
  lineNo: number;
  column?: string;
  code: string;
  message: string;
}

export interface RowVersionRequest {
  rowVersion: number;
}

export interface Rule {
  match: Match;
  steps: Step[];
}

export interface RuleContent {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  packId: string;
  regimeCode: string;
  jurisdictionCode: string;
  direction: 'input' | 'output' | 'any';
  supplyType?: 'goods' | 'services' | 'accommodation' | 'fnb' | 'digital' | 'transport' | 'other';
  partyClass?: 'registered' | 'unregistered' | 'export' | 'government' | 'exempt' | 'non_resident';
  itemClass?: string | null;
  channel?: string | null;
  priority: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isOverridable: boolean;
  note: string;
  entries: PackRuleEntry[];
}

export interface RuleEvaluationDTO {
  id: number;
  rosterId: number;
  employeeId: number;
  ruleId: number;
  ruleCode: string;
  ruleKind: string;
  threshold: string;
  observed: string;
  windowFrom: string;
  windowTo: string;
  outcome: string;
  overrideId?: number | null;
  evaluatedAt: string;
}

export interface RuleOverrideInput {
  reasonCode: string;
  note?: string;
}

export interface RuleProvenanceDTO {
  ruleSource: 'tenant' | 'pack' | 'legacy';
  ruleCode?: string;
  componentCode: string;
  componentName: string;
  packId?: string | null;
  packVersion?: number | null;
  sourceCitation?: string;
  ruleEffectiveFrom?: string | null;
  reviewDueOn?: string | null;
  lineCount: number;
  totalAmount: string;
}

export interface RuleScanDTO {
  scanned: number;
  limit: number;
  violatingCombinations: number;
  affectedLines: number;
  violations: RuleViolationDTO[];
}

export interface RuleViolationDTO {
  segmentValues: string;
  combinationHash: string;
  lineCount: number;
  offendingValues: string[];
}

export interface RunAIAgentRequest {
  companyId: number;
  businessDate?: string;
}

export interface RunAccrualRequest {
  companyId: number;
  on?: string;
}

export interface RunAllocationInput {
  invoiceId: number;
  amount?: string;
  bankAccountId?: number | null;
  reference?: string;
}

export interface RunAnomalyDTO {
  id: number;
  runId: number;
  employeeId?: number | null;
  employeeNo?: string;
  employeeName?: string;
  kind: string;
  severity: 'low' | 'medium' | 'high';
  observed?: string | null;
  expected?: string | null;
  score?: string | null;
  facts?: Record<string, unknown>;
  status: 'open' | 'acknowledged' | 'dismissed';
  acknowledgedBy?: number | null;
  acknowledgedAt?: string | null;
  acknowledgementNote?: string;
  createdAt: string;
}

export interface RunClassificationRequest {
  companyId: number;
  on?: string;
  slowDays?: number;
  obsoleteDays?: number;
  expiringHorizonDays?: number;
}

export interface RunCloseActionRequest {
  note?: string;
  rowVersion: number;
}

export interface RunDSRClockRequest {
  on?: string;
}

export interface RunDSRClockResult {
  on: string;
  scanned: number;
  warned: number;
  breached: number;
  alreadyWarned: number;
}

export interface RunDTO {
  id: number;
  runNo?: string | null;
  companyId: number;
  paymentDate: string;
  currency: string;
  method: 'bank_transfer' | 'cheque' | 'cash';
  bankGlAccountId: number;
  bankFileProfileId?: number | null;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'executed' | 'cancelled';
  dueOnOrBefore?: string | null;
  reference: string;
  memo: string;
  grossAmount: string;
  whtAmount: string;
  discountAmount: string;
  netAmount: string;
  journalEntryId?: number | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  executedAt?: string | null;
  createdAt: string;
  rowVersion: number;
  payments?: ApPaymentDTO[];
  submittedBy?: number | null;
  approvedBy?: number | null;
  approverUserIds: number[];
  anomalyCount: number;
  anomalyAvgMultiple?: string;
  payingAccountBalance?: string | null;
  insufficientFunds?: boolean | null;
  fundsCheckNote?: string;
}

export interface RunDepreciationRequest {
  companyId: number;
  on?: string;
  bookId?: number;
}

export interface RunEAMComplianceSweepRequest {
  companyId: number;
  on?: string;
}

export interface RunEntityDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  isParent: boolean;
  method: 'full' | 'proportional' | 'equity' | 'excluded';
  baseCurrency: string;
  ownershipPct: string;
  closingRate: string;
  averageRate: string;
  openingRate: string;
  assetsLocal: string;
  assetsTranslated: string;
  liabilitiesLocal: string;
  liabilitiesTranslated: string;
  equityPostedLocal: string;
  equityPostedTranslated: string;
  priorPnlLocal: string;
  priorPnlTranslated: string;
  netAssetsLocal: string;
  netAssetsTranslated: string;
  netIncomeLocal: string;
  netIncomeTranslated: string;
  cta: string;
  minorityInterestEquity: string;
  minorityInterestIncome: string;
  periods: RunEntityPeriodDTO[];
}

export interface RunEntityPeriodDTO {
  fromDate: string;
  toDate: string;
  ownershipPct: string;
  netIncomeLocal: string;
  minorityIncomeLocal: string;
}

export interface RunMRPDepthRequest {
  companyId: number;
  warehouseId: number;
  horizonDays?: number;
  mode?: string;
  includeForecast?: boolean;
  includeSafetyStock?: boolean | null;
}

export interface RunMRPRequest {
  companyId: number;
  warehouseId: number;
  horizonDays?: number;
}

export interface RunPMSchedulesRequest {
  on?: string | null;
}

export interface RunPartyMatchRequest {
  on?: string;
  partyId?: number | null;
  limit?: number;
}

export interface RunPartyMatchResult {
  on: string;
  partiesScanned: number;
  proposed: number;
  alreadyOpen: number;
  suppressed: number;
}

export interface RunPostingAssertionsRequest {
  companyId: number;
  businessDate?: string;
}

export interface RunProvenanceDTO {
  runId: number;
  runNo?: string;
  companyId: number;
  jurisdictionId?: number | null;
  packId?: string | null;
  packVersion?: number | null;
  periodStart: string;
  periodEnd: string;
  asAtDate: string;
  rules: RuleProvenanceDTO[];
  suppressedComponents: string[];
  disabledRules: string[];
}

export interface RunReconRequest {
  companyId: number;
  on?: string;
}

export interface RunReorderRequest {
  companyId: number;
  on?: string;
}

export interface RunReorderResultDTO {
  companyId: number;
  on: string;
  itemsEvaluated: number;
  suggestionsCreated: number;
  suggestionsUpdated: number;
  suggestionsOpen: number;
  requisitionsProposed: number;
}

export interface RunResult {
  entry: EntryDTO;
  amount: string;
  fiscalPeriodId?: number | null;
}

export interface RunSLASweepRequest {
  on?: string | null;
}

export interface RunScheduleRequest {
  companyId: number;
  direction?: string;
  anchorDate: string;
  horizonDays?: number;
  mode?: string;
  includeApprovedSuggestions?: boolean;
}

export interface RunTemplateRequest {
  on?: string;
}

export interface RunValidationRequest {
  country: string;
  sampleSize?: number;
  nonProductionEnterpriseId?: number | null;
}

export interface RunVarianceDTO {
  id: number;
  runId: number;
  priorRunId?: number | null;
  employeeId?: number | null;
  employeeNo?: string;
  employeeName?: string;
  componentCode?: string;
  measure: 'gross' | 'deduction' | 'employer_cost' | 'net' | 'headcount' | 'component';
  priorAmount: string;
  currentAmount: string;
  deltaAmount: string;
  deltaPercent?: string | null;
  isMaterial: boolean;
  explanationSource: '' | 'auto' | 'human';
  explanationCode?: string;
  explanationNote?: string;
  explainedBy?: number | null;
  explainedAt?: string | null;
  createdAt: string;
}

export interface SLABreachForecastDTO {
  caseId: number;
  caseNo: string;
  subject: string;
  priority: string;
  status: string;
  assignedTo?: number | null;
  resolutionDueAt?: string | null;
  predictedBreachAt: string;
}

export interface SLAComplianceReport {
  from: string;
  to: string;
  ticketCount: number;
  responseBreachedCount: number;
  resolutionBreachedCount: number;
  compliancePercent: string;
}

export interface SLAEscalationLevelDTO {
  id: number;
  policyId: number;
  level: number;
  afterMinutes: number;
  notifyUserId?: number | null;
  notifyPositionId?: number | null;
  rowVersion: number;
}

export interface SLAEscalationLevelInput {
  level: number;
  afterMinutes: number;
  notifyUserId?: number | null;
  notifyPositionId?: number | null;
}

export interface SLAPolicyDTO {
  id: number;
  name: string;
  priority: string;
  responseMinutes: number;
  resolutionMinutes: number;
  businessHoursOnly: boolean;
  escalateToUserId?: number | null;
  isDefault: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface SLASweepResult {
  responseBreaches: number;
  resolutionBreaches: number;
  escalations: number;
}

export interface SSOCallbackQuery {
  code?: string;
  state?: string;
  error?: string;
}

export interface SSOCompleteRequest {
  ticket: string;
  browserNonce?: string;
}

export interface SSOProviderDTO {
  id: number;
  code: string;
  name: string;
  issuer: string;
  clientId: string;
  allowedEmailDomains: string[];
  jitProvisioning: boolean;
  defaultRoleCodes: string[];
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface SSOProviderPublicDTO {
  code: string;
  name: string;
}

export interface SSOStartRequest {
  tenantSlug: string;
  providerCode: string;
  deviceName?: string;
  browserNonce: string;
}

export interface SSOStartResponse {
  authorizationUrl: string;
}

export interface SSPDTO {
  id: number;
  companyId: number;
  itemId: number;
  currency: string;
  sspAmount: string;
  sspMethod: 'observable' | 'adjusted_market' | 'expected_cost_plus' | 'residual';
  note?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  rowVersion: number;
}

export interface SSPRequest {
  companyId: number;
  itemId: number;
  currency: string;
  sspAmount: string;
  sspMethod?: string;
  effectiveFrom: string;
  note?: string;
}

export interface SalesByQuery {
  companyId: number;
  from: string;
  to: string;
  groupBy: string;
}

export interface SalesByReport {
  companyId: number;
  currency: string;
  from: string;
  to: string;
  groupBy: string;
  rows: SalesByRow[];
  total: string;
  generatedAt: string;
}

export interface SalesByRow {
  key: string;
  label?: string;
  quantity?: string;
  amount: string;
}

export interface SalesCancelRequest {
  reason: string;
}

export interface SalesCreateSubscriptionRequest {
  companyId: number;
  customerId: number;
  planId: number;
  quantity?: string;
  unitPrice?: string;
  manualPriceReason?: string;
  currency?: string;
  startDate?: string;
  endDate?: string;
}

export interface SalesDeleteQuery {
  rowVersion?: number;
}

export interface SalesListReturnsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  customerId?: number;
  status?: 'draft' | 'authorised' | 'received' | 'posted' | 'rejected' | 'cancelled';
}

export interface SalesPipelineQuery {
  companyId: number;
}

export interface SalesPipelineReport {
  companyId: number;
  currency: string;
  stages: PipelineStage[];
  generatedAt: string;
}

export interface SalesQuoteDTO {
  id: number;
  quoteNo?: string | null;
  companyId: number;
  customerId: number;
  customerName?: string;
  currency: string;
  fxRate: string;
  quoteDate: string;
  expiryDate?: string | null;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'converted' | 'superseded';
  subtotal: string;
  discountTotal: string;
  total: string;
  memo: string;
  reference: string;
  channel?: string;
  supplyType?: string;
  dayPartCode?: string;
  priceBasis: string;
  locationId?: number | null;
  rejectionReason?: string;
  convertedOrderId?: number | null;
  revisionNo: number;
  rootQuoteId: number;
  supersededByQuoteId?: number | null;
  revisionReasonCode?: string;
  approvalRequestId?: number | null;
  approvalStatus?: string;
  approvalNote?: string;
  createdAt: string;
  rowVersion: number;
  lines?: SalesQuoteLineDTO[];
}

export interface SalesQuoteLineDTO {
  id: number;
  lineNo: number;
  itemId: number;
  description: string;
  uom: string;
  quantity: string;
  listPrice: string;
  unitPrice: string;
  discountPercent: string;
  discountAmount: string;
  promotionId?: number | null;
  priceRule?: string;
  priceExplanation?: string;
  priceResolutionLogId?: number | null;
  lineAmount: string;
  warehouseId?: number | null;
}

export interface SalesQuoteLineInput {
  itemId: number;
  description?: string;
  uom?: string;
  quantity: string;
  warehouseId?: number | null;
  discountPercent?: string;
  unitPrice?: string;
}

export interface SalesSettingsDTO {
  companyId: number;
  deferredRevenueAccountId?: number | null;
  defaultWarehouseId?: number | null;
  discountApprovalThresholdPercent: string;
  marginFloorPolicy: 'block' | 'warn' | 'ignore';
  rowVersion: number;
}

export interface SalesSettingsQuery {
  companyId: number;
}

export interface SalesSubscriptionDTO {
  id: number;
  subscriptionNo?: string | null;
  companyId: number;
  customerId: number;
  planId: number;
  quantity: string;
  unitPrice: string;
  currency: string;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextBillingDate?: string | null;
  endDate?: string | null;
  status: 'active' | 'paused' | 'cancelled' | 'expired';
  priceSource: 'engine' | 'manual' | 'manual_legacy';
  priceResolutionLogId?: number | null;
  priceRule?: string;
  priceExplanation?: string;
  manualPriceReason?: string;
  priceReviewedOn?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface SalesUpsertSettingsRequest {
  companyId: number;
  deferredRevenueAccountId?: number | null;
  defaultWarehouseId?: number | null;
  discountApprovalThresholdPercent?: string;
  marginFloorPolicy?: string;
  rowVersion: number;
}

export interface SalesWinRateReport {
  companyId: number;
  from: string;
  to: string;
  accepted: number;
  rejected: number;
  expired: number;
  decided: number;
  winRate: string;
  formula: string;
  generatedAt: string;
}

export interface SamplePreviewDTO {
  samplingPlanId: number;
  method: 'fixed_percent' | 'aql_table' | 'hundred_percent';
  lotQuantity: string;
  sampleSize: number;
  acceptNumber: number;
  bandId?: number | null;
  explanation: string;
}

export interface SamplePreviewRequest {
  lotQuantity: string;
}

export interface SamplingLevelDTO {
  id: number;
  lotSizeFrom: string;
  lotSizeTo?: string;
  sampleSize: number;
  acceptNumber: number;
  rejectNumber: number;
}

export interface SamplingLevelInput {
  lotSizeFrom: string;
  lotSizeTo?: string;
  sampleSize: number;
  acceptNumber: number;
  rejectNumber?: number | null;
}

export interface SamplingPlanDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  method: 'fixed_percent' | 'aql_table' | 'hundred_percent';
  samplePercent: string;
  minSampleSize: number;
  maxSampleSize?: number | null;
  acceptNumber: number;
  levels: SamplingLevelDTO[];
  rowVersion: number;
}

export interface SandboxDifference {
  docType: string;
  docId: number;
  docNo?: string;
  taxCode: string;
  oldTreatment: string;
  newTreatment: string;
  oldTaxAmount: string;
  newTaxAmount: string;
  reason: string;
}

export interface SandboxResult {
  ranAt: string;
  sampleSize: number;
  differences: SandboxDifference[];
  ok: boolean;
}

export interface SatisfyRequest {
  on: string;
  note?: string;
}

export interface SavedFilterDTO {
  id: number;
  reportKey: string;
  name: string;
  params: Record<string, unknown>;
  isShared: boolean;
  createdAt: string;
  updatedAt?: string | null;
  rowVersion: number;
}

export interface ScheduleDTO {
  id: number;
  reportKey: string;
  name: string;
  params: Record<string, unknown>;
  format: string;
  frequency: string;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  hourLocal: number;
  recipients: string[];
  companyId?: number | null;
  isActive: boolean;
  lastRunAt?: string | null;
  lastRunStatus?: string;
  lastRunError?: string;
  nextRunAt: string;
  rowVersion: number;
}

export interface ScheduleQuery {
  version?: number;
}

export interface ScheduleRow {
  periodNo: number;
  status: string;
  openingNbv: string;
  amount: string;
  closingNbv: string;
  fiscalPeriodId?: number | null;
  periodLabel?: string;
  date?: string | null;
  estimatedDate?: string | null;
}

export interface ScheduleRunDTO {
  id: number;
  companyId: number;
  runAt: string;
  direction: string;
  anchorDate: string;
  horizonDays: number;
  mode: string;
  operationsScheduled: number;
  operationsLate: number;
  ordersScheduled: number;
}

export interface ScheduleServiceWorkOrderRequest {
  appointmentOn: string;
  windowStartMinute?: number | null;
  windowEndMinute?: number | null;
  assignedToUserId: number;
  rowVersion: number;
}

export interface ScopeAssignment {
  scopeType: 'company' | 'branch' | 'department' | 'division' | 'location';
  scopeId: number;
  effectiveFrom: string;
}

export interface ScorecardCriterionScore {
  criterion: string;
  score?: number;
  note?: string;
}

export interface ScorecardDTO {
  id: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  periodStart: string;
  periodEnd: string;
  ordersCount: number;
  onTimeCount: number;
  inFullCount: number;
  otifPercent: string;
  priceVariancePercent: string;
  qualityScore: string;
  computedAt: string;
}

export interface SealRFQRequest {
  sealedUntil: string;
}

export interface SectionDiff {
  added: RowDiff[];
  changed: RowDiff[];
  withdrawn: RowDiff[];
}

export interface SectionListQuery {
  cursor?: string;
  limit?: number;
}

export interface SecurityPreviewDTO {
  roleId?: number | null;
  roleCode?: string;
  ranges?: string[];
  visibleValues: string[];
  hiddenValues: string[];
  truncated: boolean;
}

export interface SegmentBuildResult {
  segmentId: number;
  memberCount: number;
  addedCount: number;
  removedCount: number;
  builtAt: string;
}

export interface SegmentCondition {
  segment: string;
  from?: string;
  to?: string;
}

export interface SegmentDTO {
  id: number;
  coaStructureId: number;
  sequence: number;
  prompt: string;
  qualifier: string;
  binding: string;
  segmentKey: string;
  valueSetId?: number | null;
  length: number;
  displayOrder: number;
  isSecured: boolean;
  rowVersion: number;
}

export interface SegmentRequest {
  sequence: number;
  prompt: string;
  qualifier?: string;
  binding: string;
  valueSetId?: number | null;
  length?: number;
  displayOrder?: number;
  isSecured?: boolean;
}

export interface SegmentSecurityRuleDTO {
  id: number;
  coaStructureId: number;
  coaSegmentId: number;
  segment: string;
  valueRange: string;
  roleId?: number | null;
  businessUnitId?: number | null;
  accessType: string;
  rowVersion: number;
  preview?: SecurityPreviewDTO[];
}

export interface SegmentValueDTO {
  id: number;
  coaValueSetId: number;
  value: string;
  description: string;
  parentValueId?: number | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  rowVersion: number;
}

export interface SegmentValueRequest {
  value: string;
  description?: string;
  parentValueId?: number | null;
  isActive?: boolean | null;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface SendCampaignRequest {
  segmentId: number;
  isTest?: boolean;
  testRecipients?: string[];
}

export interface SendMessageRequest {
  message: string;
}

export interface SendPreviewResult {
  campaignId: number;
  segmentId: number;
  evaluatedAt: string;
  audienceTotal: number;
  allowedCount: number;
  excludedCount: number;
  exclusionBreakdown: Record<string, number>;
}

export interface ServiceCSATReport {
  from: string;
  to: string;
  submittedCount: number;
  satisfiedCount: number;
  satisfiedPercent: string;
}

export interface ServiceChargeAllocationDTO {
  id: number;
  poolId: number;
  employeeId: number;
  employeeNo: string;
  employeeName: string;
  assignmentId: number;
  eligibility: 'eligible' | 'ineligible' | 'forfeited';
  ineligibleReasonCode?: string;
  eligibleDays: number;
  points?: string;
  hours?: string;
  weight: string;
  rawShare: string;
  roundingCents: number;
  amount: string;
  payslipLineId?: number | null;
}

export interface ServiceChargePointDTO {
  id: number;
  schemeId: number;
  gradeId?: number | null;
  positionId?: number | null;
  employeeId?: number | null;
  points: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ServiceChargePoolDTO {
  id: number;
  poolNo?: string;
  companyId: number;
  poolKey: string;
  locationId?: number | null;
  periodStart: string;
  periodEnd: string;
  currency: string;
  schemeId: number;
  status: 'draft' | 'collected' | 'allocated' | 'approved' | 'distributed' | 'cancelled';
  grossCollected: string;
  retainedAmount: string;
  employerTopup: string;
  distributable: string;
  allocatedAmount: string;
  totalWeight: string;
  eligibleCount: number;
  forfeitedCount: number;
  runId?: number | null;
  sources?: ServiceChargePoolSourceDTO[];
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface ServiceChargePoolSourceDTO {
  taxCode: string;
  currency: string;
  baseAmount: string;
  amount: string;
  lineCount: number;
  documentCount: number;
  capturedAt: string;
}

export interface ServiceChargeSchemeDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  basis: 'points' | 'hours';
  minDaysWorked: number;
  includeEmploymentTypes: string[];
  excludeOnProbation: boolean;
  prorateByDays: boolean;
  forfeitOnTerminationForCause: boolean;
  forfeitAfterUnapprovedAbsenceDays?: number | null;
  retentionPercent: string;
  contributesToClasses: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  componentCode: string;
  advisoryWarning?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface ServiceContractDTO {
  id: number;
  companyId: number;
  contractNo?: string;
  customerId: number;
  customerName?: string;
  name: string;
  status: 'draft' | 'active' | 'suspended' | 'expired' | 'cancelled';
  startsOn: string;
  endsOn: string;
  autoRenew: boolean;
  renewalNoticeDays: number;
  renewedFromContractId?: number | null;
  renewedToContractId?: number | null;
  slaPolicyId?: number | null;
  rateCardId?: number | null;
  feeAmount: string;
  feeCycle: string;
  revenueAccountId?: number | null;
  taxCode?: string;
  entitlements?: ServiceEntitlementDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface ServiceCostToServeDTO {
  companyId: number;
  customerId: number;
  customerName?: string;
  contractId?: number | null;
  contractNo?: string;
  workOrders: number;
  labourHours: string;
  labourCost: string;
  partsCost: string;
  totalCost: string;
  revenue: string;
  goodwill: string;
  margin: string;
  fromDate: string;
  toDate: string;
}

export interface ServiceCostToServeQuery {
  companyId: number;
  fromDate: string;
  toDate: string;
  customerId?: number;
  contractId?: number;
}

export interface ServiceDeviceCompleteRequest {
  operatorUserId: number;
  workOrderId: number;
  visitId?: number | null;
  outcome: string;
  incompleteReasonCode?: string;
  notes?: string;
  signerName?: string;
  signatureAttachmentId?: number | null;
}

export interface ServiceDeviceConsumeRequest {
  operatorUserId: number;
  workOrderId: number;
  itemId: number;
  visitId?: number | null;
  quantity: string;
}

export interface ServiceDeviceSkillDTO {
  skillId: number;
  code: string;
  name: string;
  minLevel?: number;
  level?: number;
  isStatutory: boolean;
  validTo?: string;
}

export interface ServiceDeviceVanStockLineDTO {
  warehouseId: number;
  itemId: number;
  sku: string;
  name: string;
  qtyOnHand: string;
}

export interface ServiceDeviceVanStockResponse {
  lines: ServiceDeviceVanStockLineDTO[];
}

export interface ServiceDeviceVocabulariesResponse {
  completionOutcomes: string[];
  incompleteReasons: string[];
}

export interface ServiceDeviceWorkOrderDTO {
  workOrderId: number;
  workOrderNo: string;
  status: 'draft' | 'scheduled' | 'dispatched' | 'in_progress' | 'incomplete' | 'completed' | 'cancelled' | 'closed';
  kind: 'customer' | 'internal';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  summary: string;
  instructions?: string;
  appointmentOn?: string;
  windowStartMinute?: number | null;
  windowEndMinute?: number | null;
  customerName?: string;
  siteLocation?: string;
  customerAssetName?: string;
  warehouseId?: number | null;
  requiredSkills: ServiceDeviceSkillDTO[];
  openVisitId?: number | null;
  rowVersion: number;
}

export interface ServiceDeviceWorkOrderListResponse {
  technicianUserId: number;
  companyId: number;
  businessDate: string;
  workOrders: ServiceDeviceWorkOrderDTO[];
  vanStock: ServiceDeviceVanStockLineDTO[];
  heldSkills: ServiceDeviceSkillDTO[];
  completionOutcomes: string[];
  incompleteReasons: string[];
}

export interface ServiceDispatchAssignRequest {
  workOrderId: number;
  assignedToUserId: number;
  appointmentOn?: string;
  override?: ServiceDispatchOverrideDTO | null;
}

export interface ServiceDispatchBoardDTO {
  companyId: number;
  date: string;
  workOrderId?: number | null;
  slots: ServiceDispatchSlotDTO[];
  rosterDataAvailable: boolean;
}

export interface ServiceDispatchBoardQuery {
  companyId: number;
  workOrderId?: number;
  date?: string;
  technicianIds?: string;
}

export interface ServiceDispatchOverrideDTO {
  reasonCode: string;
  note?: string;
}

export interface ServiceDispatchRefusalDTO {
  ruleCode: string;
  overridable: boolean;
  detail: string;
  skillId?: number | null;
  skillCode?: string;
}

export interface ServiceDispatchSlotDTO {
  technicianUserId: number;
  technicianName?: string;
  employeeId?: number | null;
  eligible: boolean;
  statutoryBlocked: boolean;
  refusals: ServiceDispatchRefusalDTO[];
  assignedCount: number;
  rosteredMinutes: number;
  withinShift: boolean;
}

export interface ServiceEntitlementConsiderationDTO {
  entitlementId?: number | null;
  scope?: string;
  quotaHours?: string;
  remainingHours?: string;
  drawnHours?: string;
  drawnAmount?: string;
  reasonKey: string;
}

export interface ServiceEntitlementDTO {
  id: number;
  contractId: number;
  scope: 'labour' | 'parts' | 'travel' | 'response';
  customerAssetId?: number | null;
  assetCategory?: string;
  priority?: string;
  quotaHours?: string;
  quotaAmount?: string;
  usedHours: string;
  usedAmount: string;
  remainingHours?: string;
  remainingAmount?: string;
  exhaustedPolicy: 'chargeable_overage' | 'chargeable_all';
  rowVersion: number;
}

export interface ServiceEntitlementDecisionDTO {
  id: number;
  workOrderId: number;
  contractId?: number | null;
  contractNo?: string;
  decidedAt: string;
  workDate: string;
  decision: 'covered' | 'partially_covered' | 'chargeable' | 'out_of_contract';
  considerations: ServiceEntitlementConsiderationDTO[];
  coveredAmount: string;
  chargeableAmount: string;
  reasonKey?: string;
  supersededById?: number | null;
}

export interface ServiceEntitlementInput {
  scope: string;
  customerAssetId?: number | null;
  assetCategory?: string;
  priority?: string;
  quotaHours?: string | null;
  quotaAmount?: string | null;
  exhaustedPolicy?: string;
}

export interface ServiceGoodwillDTO {
  id: number;
  companyId: number;
  customerId: number;
  workOrderId?: number | null;
  ticketId?: number | null;
  amount: string;
  causeCode: string;
  note?: string;
  status: 'requested' | 'approved' | 'rejected' | 'posted' | 'cancelled';
  approvalRequestId?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  arCreditNoteId?: number | null;
  postedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ServiceProviderRelationshipDTO {
  id: number;
  providerBuId: number;
  clientBuId: number;
  functionCode: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface ServiceRateCardDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  currency: string;
  isActive: boolean;
  versions?: ServiceRateCardVersionDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface ServiceRateCardLineDTO {
  id: number;
  versionId: number;
  skillId?: number | null;
  skillCode?: string;
  isOvertime: boolean;
  hourlyRate: string;
}

export interface ServiceRateCardLineInput {
  skillId?: number | null;
  isOvertime?: boolean;
  hourlyRate: string;
}

export interface ServiceRateCardVersionDTO {
  id: number;
  rateCardId: number;
  version: number;
  status: 'draft' | 'published' | 'superseded';
  effectiveFrom: string;
  effectiveTo?: string;
  partsMarkupPercent: string;
  travelRate: string;
  minimumCharge: string;
  preApprovalThreshold: string;
  publishedAt?: string | null;
  publishedBy?: number | null;
  lines?: ServiceRateCardLineDTO[];
  rowVersion: number;
}

export interface ServiceSettingsDTO {
  companyId: number;
  defaultWarehouseId?: number | null;
  serviceRevenueAccountId?: number | null;
  rowVersion: number;
}

export interface ServiceSettingsQuery {
  companyId: number;
}

export interface ServiceUpsertSettingsRequest {
  rowVersion: number;
  companyId: number;
  defaultWarehouseId?: number | null;
  serviceRevenueAccountId?: number | null;
}

export interface ServiceVisitDTO {
  id: number;
  workOrderId: number;
  visitNo: number;
  technicianUserId: number;
  technicianName?: string;
  travelStartedAt?: string | null;
  arrivedAt?: string | null;
  departedAt?: string | null;
  outcome?: 'completed' | 'incomplete' | 'no_access' | 'cancelled';
  incompleteReasonCode?: string;
  notes?: string;
  signerName?: string;
  signatureAttachmentId?: number | null;
  returnForVisitId?: number | null;
  occurredAt: string;
  rowVersion: number;
}

export interface ServiceWorkOrderDTO {
  id: number;
  companyId: number;
  workOrderNo: string;
  kind: 'customer' | 'internal';
  status: 'draft' | 'scheduled' | 'dispatched' | 'in_progress' | 'incomplete' | 'completed' | 'cancelled' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  summary: string;
  ticketId?: number | null;
  eamAssetId?: number | null;
  customerAssetId?: number | null;
  originKind: 'service.ticket' | 'service.pm_schedule' | 'eam.pm_plan' | 'eam.compliance' | 'pms.room_fault';
  originDocId?: number | null;
  originDueOn?: string;
  taskListId?: number | null;
  permitId?: number | null;
  appointmentOn?: string;
  windowStartMinute?: number | null;
  windowEndMinute?: number | null;
  assignedToUserId?: number | null;
  assignedToName?: string;
  warehouseId?: number | null;
  costAccountId?: number | null;
  estimatedHours: string;
  contractId?: number | null;
  entitlementDecision?: 'covered' | 'partially_covered' | 'chargeable' | 'out_of_contract';
  entitlementDecidedAt?: string | null;
  billability: 'undecided' | 'not_billable' | 'partially_billable' | 'billable';
  arInvoiceId?: number | null;
  invoicedAt?: string | null;
  journalEntryId?: number | null;
  completedAt?: string | null;
  closedAt?: string | null;
  visitCount: number;
  isFirstTimeFix: boolean;
  instructions?: string;
  createdAt: string;
  rowVersion: number;
}

export interface ServiceWorkOrderDetailDTO {
  id: number;
  companyId: number;
  workOrderNo: string;
  kind: 'customer' | 'internal';
  status: 'draft' | 'scheduled' | 'dispatched' | 'in_progress' | 'incomplete' | 'completed' | 'cancelled' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  summary: string;
  ticketId?: number | null;
  eamAssetId?: number | null;
  customerAssetId?: number | null;
  originKind: 'service.ticket' | 'service.pm_schedule' | 'eam.pm_plan' | 'eam.compliance' | 'pms.room_fault';
  originDocId?: number | null;
  originDueOn?: string;
  taskListId?: number | null;
  permitId?: number | null;
  appointmentOn?: string;
  windowStartMinute?: number | null;
  windowEndMinute?: number | null;
  assignedToUserId?: number | null;
  assignedToName?: string;
  warehouseId?: number | null;
  costAccountId?: number | null;
  estimatedHours: string;
  contractId?: number | null;
  entitlementDecision?: 'covered' | 'partially_covered' | 'chargeable' | 'out_of_contract';
  entitlementDecidedAt?: string | null;
  billability: 'undecided' | 'not_billable' | 'partially_billable' | 'billable';
  arInvoiceId?: number | null;
  invoicedAt?: string | null;
  journalEntryId?: number | null;
  completedAt?: string | null;
  closedAt?: string | null;
  visitCount: number;
  isFirstTimeFix: boolean;
  instructions?: string;
  createdAt: string;
  rowVersion: number;
  visits: ServiceVisitDTO[];
  skills: ServiceWorkOrderSkillDTO[];
  parts: ServiceWorkOrderPartDTO[];
  labour: ServiceWorkOrderLabourDTO[];
  totalCost?: string;
}

export interface ServiceWorkOrderLabourDTO {
  id: number;
  workOrderId: number;
  visitId?: number | null;
  technicianUserId: number;
  technicianName?: string;
  workDate: string;
  hours: string;
  costRate?: string;
  costAmount?: string;
  billRate: string;
  billAmount: string;
  coveredAmount: string;
  chargeableAmount: string;
  isOvertime: boolean;
  description?: string;
  status: 'logged' | 'voided';
  rowVersion: number;
}

export interface ServiceWorkOrderPartDTO {
  id: number;
  workOrderId: number;
  visitId?: number | null;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  quantity: string;
  unitCost?: string;
  costAmount?: string;
  billAmount: string;
  coveredAmount: string;
  chargeableAmount: string;
  status: 'issued' | 'voided';
  voidReason?: string;
  occurredAt: string;
  rowVersion: number;
}

export interface ServiceWorkOrderSkillDTO {
  id: number;
  workOrderId: number;
  skillId: number;
  skillCode?: string;
  skillName?: string;
  minLevel: number;
  isStatutory: boolean;
}

export interface ServiceWorkOrderSkillInput {
  skillId: number;
  minLevel?: number;
}

export interface SessionDTO {
  id: string;
  deviceName?: string;
  userAgent?: string;
  ip?: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  current: boolean;
}

export interface SessionsResponse {
  sessions: SessionDTO[];
}

export interface SetAdjustmentLineEvidenceRequest {
  lotId?: number | null;
  rootCauseCode?: string;
  attachmentId?: number | null;
}

export interface SetAgreementLinesRequest {
  lines: TradeAgreementLineInput[];
  rowVersion: number;
}

export interface SetAssignmentDTO {
  id: number;
  businessUnitId: number;
  determinantType: string;
  referenceSetId: number;
  effectiveFrom: string;
  effectiveTo?: string;
  confirmedBy?: number | null;
  priorReferenceSetId?: number | null;
  gained: ValueRef[];
  lost: ValueRef[];
}

export interface SetAuthorityStepsRequest {
  steps: AuthorityStepDTO[];
}

export interface SetAutonomyRequest {
  autonomy: 'assistive' | 'supervised' | 'delegated';
  note?: string;
}

export interface SetBOMExpectedYieldRequest {
  expectedYieldPercent: string;
}

export interface SetBOMLineEffectivityRequest {
  windows: BOMLineEffectivityInput[];
}

export interface SetBOMOutputsRequest {
  outputs: BOMOutputInput[];
}

export interface SetBusinessHoursRequest {
  windows: BusinessHourWindowInput[];
}

export interface SetCloseTemplatesRequest {
  templates: CloseTemplateInput[];
}

export interface SetContentPartsRequest {
  parts: ContentPartInput[];
}

export interface SetContractCommitmentsRequest {
  commitments: ContractCommitmentInput[];
  rowVersion: number;
}

export interface SetContractLinesRequest {
  lines: ContractPriceLineInput[];
  rowVersion: number;
}

export interface SetContractRebatesRequest {
  rebates: ContractRebateInput[];
  rowVersion: number;
}

export interface SetContractScopesRequest {
  scopes: ContractScopeInput[];
  rowVersion: number;
}

export interface SetCostAbsorptionRateRequest {
  poolId: number;
  driverId: number;
  fiscalPeriodId: number;
  budgetedCost: string;
  budgetedQuantity: string;
  rate?: string;
  varianceAccountId: number;
  activate?: boolean;
  note?: string;
}

export interface SetCostCycleStepsRequest {
  steps: CostCycleStepInput[];
}

export interface SetCostDriverQuantitiesRequest {
  fiscalPeriodId: number;
  quantities: CostDriverQuantityInput[];
}

export interface SetCostPoolSourcesRequest {
  sources: CostPoolSourceInput[];
}

export interface SetDayPartsRequest {
  dayParts: DayPart[];
}

export interface SetDefaultRequest {
  key: string;
  value: string;
  effectiveFrom: string;
}

export interface SetDefaultResponse {
  default?: EffectiveDefaultDTO | null;
  impact: DryRunImpactDTO[];
}

export interface SetDeliveryScheduleRequest {
  rows: DeliveryScheduleRowInput[];
}

export interface SetDesignationsRequest {
  jurisdictionId: number;
  isLegalEmployer: boolean;
  isPsu: boolean;
  legislativeDataGroupId?: number | null;
  effectiveFrom: string;
  note?: string;
}

export interface SetDimensionPoliciesRequest {
  policies: SetDimensionPolicyEntry[];
}

export interface SetDimensionPolicyEntry {
  dimension: string;
  requirement: string;
  effectiveFrom?: string;
  note?: string;
}

export interface SetDispositionRequest {
  disposition: string;
  note?: string;
}

export interface SetDivisionSegmentMapRequest {
  coaStructureId: number;
  segmentSequence: number;
  segmentValue: string;
  effectiveFrom?: string;
  confirm?: boolean;
}

export interface SetDivisionSegmentMapResult {
  saved: boolean;
  map?: DivisionSegmentMapDTO | null;
  requiresFinanceConfirmation?: boolean;
  warning?: string;
}

export interface SetEAMTaskListSkillsRequest {
  skills: EAMTaskListSkillInput[];
}

export interface SetEAMTaskListStepsRequest {
  steps: EAMTaskListStepInput[];
}

export interface SetECLMatrixRatesRequest {
  rates: ECLMatrixRateInput[];
}

export interface SetECLSettingsRequest {
  companyId: number;
  allowanceAccountId: number;
  expenseAccountId: number;
  creditApplication?: string;
  isEnabled?: boolean;
  rowVersion: number;
}

export interface SetECOItemsRequest {
  items: ECOChangeItemInput[];
}

export interface SetExpenseClaimLinesRequest {
  lines: ExpenseClaimLineInput[];
  rowVersion: number;
}

export interface SetFixedBinRequest {
  itemId: number;
  warehouseId: number;
  binId: number;
  role?: 'primary' | 'overflow';
  targetQty?: string;
  minQty?: string;
}

export interface SetInventoryCompanySettingsRequest {
  inTransitAccountId?: number | null;
  landedCostClearingAccountId?: number | null;
  landedCostAccrualAccountId?: number | null;
  wasteAccountId?: number | null;
  outletConsumptionAccountId?: number | null;
  allowAutoReceive?: boolean | null;
  transferOverdueDays?: number | null;
  countRecountThresholdPct?: string;
  countRecountThresholdValue?: string;
  countRequireDifferentCounter?: boolean | null;
  shrinkageTargetPct?: string;
  depletionMissingRecipePolicy?: 'record' | 'block';
  rowVersion: number;
}

export interface SetItemCompanySettingRequest {
  costingMethod: 'MA' | 'FIFO';
  inventoryAccountId: number;
  cogsAccountId: number;
  adjustmentGainLossAccountId: number;
}

export interface SetItemMeasuresRequest {
  netWeightKg?: string | null;
  grossWeightKg?: string | null;
  volumeM3?: string | null;
  tariffClassificationId?: number | null;
  countryOfOrigin?: string | null;
}

export interface SetKBArticleApplicabilityRequest {
  lines: KBApplicabilityInput[];
}

export interface SetLotStatusRequest {
  status: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
  note?: string;
  rowVersion: number;
}

export interface SetMetricOwnerRequest {
  positionId: number;
  effectiveFrom?: string;
  note?: string;
}

export interface SetObligationsRequest {
  obligations: ContractObligationInput[];
  rowVersion: number;
}

export interface SetOfferPositionRequest {
  gradeId?: number | null;
  positionId?: number | null;
  payGroupId?: number | null;
  rowVersion: number;
}

export interface SetOfflinePoliciesRequest {
  companyId: number;
  bindingKind?: string;
  bindingId?: number | null;
  effectiveFrom: string;
  policies: OfflinePolicyInput[];
  reason?: string;
}

export interface SetOperatorPinRequest {
  userId: number;
  pin: string;
}

export interface SetPMSBoardEntitlementsRequest {
  entitlements: PMSBoardEntitlementInput[];
  rowVersion: number;
}

export interface SetPMSFolioRoutingRulesRequest {
  rules: PMSFolioRoutingRuleInput[];
}

export interface SetPMSGuestPreferenceRequest {
  category: 'room' | 'housekeeping' | 'dining' | 'transport' | 'other';
  code: string;
  value?: string;
  note?: string;
}

export interface SetPMSHousekeepingTaskStatusRequest {
  status: 'pending' | 'in_progress' | 'done' | 'skipped';
  skippedReason?: string;
  note?: string;
  rowVersion: number;
}

export interface SetPMSRoomStatusRequest {
  toStatus: 'dirty' | 'cleaning' | 'clean' | 'inspected';
  note?: string;
  overrideReason?: string;
  overrideNote?: string;
  rowVersion: number;
}

export interface SetPOSAvailabilityRequest {
  outletId: number;
  itemId: number;
  state: 'available' | 'eighty_six' | 'limited';
  remainingQty?: string;
  reasonCode?: string;
  note?: string;
  effectiveUntil?: string;
}

export interface SetPOSCheckAllergensRequest {
  allergenCodes: string[];
}

export interface SetPOSDenominationLineRequest {
  faceValue: string;
  kind: 'note' | 'coin';
  sortOrder?: number;
  isActive?: boolean | null;
}

export interface SetPOSDenominationsRequest {
  companyId: number;
  currency: string;
  lines: SetPOSDenominationLineRequest[];
}

export interface SetPOSItemAllergenLineInput {
  allergenCode: string;
  severity: 'contains' | 'may_contain';
  note?: string;
}

export interface SetPOSItemAllergensRequest {
  companyId: number;
  allergens: SetPOSItemAllergenLineInput[];
}

export interface SetPOSMenuItemInput {
  sectionCode: string;
  itemId: number;
  seqNo: number;
  displayName?: string;
  uom?: string;
  quantityPerPress?: string;
  revenueCenterId?: number | null;
  recipeId?: number | null;
  requiresAgeCheck?: boolean;
  minimumAge?: number | null;
  requiresSerial?: boolean;
  courseNo?: number | null;
  stationId?: number | null;
  isAvailable?: boolean | null;
  colour?: string;
  modifierGroupCodes?: string[];
}

export interface SetPOSMenuItemsRequest {
  items: SetPOSMenuItemInput[];
}

export interface SetPOSMenuSectionInput {
  code: string;
  name: string;
  seqNo: number;
  revenueCenterId?: number | null;
  colour?: string;
}

export interface SetPOSMenuSectionsRequest {
  sections: SetPOSMenuSectionInput[];
}

export interface SetPOSModifierGroupInput {
  code: string;
  name: string;
  minSelect?: number;
  maxSelect?: number | null;
  seqNo?: number;
  modifiers: SetPOSModifierInput[];
}

export interface SetPOSModifierGroupsRequest {
  groups: SetPOSModifierGroupInput[];
}

export interface SetPOSModifierInput {
  code: string;
  name: string;
  seqNo?: number;
  isDefault?: boolean;
  chargeItemId?: number | null;
  recipeModifierCode?: string;
}

export interface SetPOSTableStatusRequest {
  status: 'free' | 'seated' | 'ordered' | 'printed' | 'paid' | 'dirty';
  rowVersion: number;
}

export interface SetPOSTablesRequest {
  tables: POSTableInput[];
}

export interface SetParLevelRequest {
  itemId: number;
  warehouseId: number;
  parQty: string;
  minQty?: string;
  orderMultiple?: string;
  replenishWeekday?: number | null;
  sourceWarehouseId?: number | null;
  effectiveFrom: string;
  note?: string;
}

export interface SetPartyMergeSettingsRequest {
  reversalWindowDays?: number | null;
  requireApproval?: boolean | null;
  autoProposeThreshold?: string | null;
  rowVersion: number;
}

export interface SetPlanningParameterRequest {
  companyId: number;
  lotSizingRule?: string;
  fixedOrderQuantity?: string;
  minQuantity?: string;
  maxQuantity?: string;
  periodOfSupplyDays?: number | null;
  orderMultiple?: string;
  planningTimeFenceDays?: number | null;
  demandTimeFenceDays?: number | null;
  sourcePolicy?: string;
  leadTimeDays?: number | null;
  safetyLeadTimeDays?: number | null;
  isActive?: boolean | null;
}

export interface SetPortalVisibilityRequest {
  portalVisible: boolean;
  rowVersion?: number;
}

export interface SetPrecedenceEntriesRequest {
  rowVersion: number;
  entries: PrecedenceEntryInput[];
}

export interface SetPriceSetEntriesRequest {
  rowVersion: number;
  entries: PriceSetEntryInput[];
}

export interface SetProjectDependenciesRequest {
  dependencies: ProjectDependencyInput[];
}

export interface SetProjectTaskParentRequest {
  parentTaskId: number | null;
  costCategory?: 'labour' | 'material' | 'subcontract' | 'expense' | 'overhead';
}

export interface SetProjectTaskSkillsRequest {
  skills: ProjectTaskSkillInput[];
}

export interface SetRFQCriteriaRequest {
  criteria: RFQCriterionInput[];
}

export interface SetRFQEvaluatorsRequest {
  evaluators: RFQEvaluatorInput[];
}

export interface SetReasonCodeControlsRequest {
  requiresAttachment?: boolean | null;
  requiresRootCause?: boolean | null;
  gainLossAccountId?: number | null;
  clearGainLossAccount?: boolean;
  rowVersion: number;
}

export interface SetRecipeLinesRequest {
  lines: RecipeLineRequest[];
  rowVersion: number;
}

export interface SetReplenishmentPolicyRequest {
  itemId: number;
  warehouseId?: number | null;
  companyId: number;
  method: string;
  leadTimeDays?: number;
  leadTimeStddevDays?: string;
  reviewPeriodDays?: number;
  serviceLevel?: string;
  safetyStockOverride?: string;
  minOrderQty?: string;
  orderMultiple?: string;
  maxOrderQty?: string;
  orderCost?: string;
  holdingCostPct?: string;
  preferredVendorId?: number | null;
  effectiveFrom: string;
  note?: string;
}

export interface SetRequisitionPositionRequest {
  positionId?: number | null;
  gradeId?: number | null;
  locationId?: number | null;
  scorecardCriteria?: string[];
  rowVersion: number;
}

export interface SetRoutingOperationToolsRequest {
  tools: RoutingOperationToolInput[];
}

export interface SetSLAEscalationLevelsRequest {
  levels: SLAEscalationLevelInput[];
}

export interface SetSamplingLevelsRequest {
  levels: SamplingLevelInput[];
  rowVersion: number;
}

export interface SetScopesRequest {
  scopes: ScopeAssignment[];
}

export interface SetServiceEntitlementsRequest {
  entitlements: ServiceEntitlementInput[];
}

export interface SetServiceRateCardLinesRequest {
  lines: ServiceRateCardLineInput[];
}

export interface SetServiceWorkOrderSkillsRequest {
  skills: ServiceWorkOrderSkillInput[];
}

export interface SetShiftsRequest {
  shifts: Shift[];
}

export interface SetSignalSettingsRequest {
  companyId: number;
  settings: SignalSettingInput[];
}

export interface SetStandardCostRequest {
  companyId: number;
  itemId: number;
  effectiveDate: string;
  standardCost: string;
  notes?: string;
}

export interface SetSupplierPolicyRequest {
  unqualifiedSupplierPolicy?: string;
  expiredDocumentPolicy?: string;
  unscreenedSupplierPolicy?: string;
  screeningValidityDays?: number | null;
  requalificationMonths?: number | null;
  rowVersion: number;
}

export interface SetTenantUserPasswordRequest {
  credentialMode: string;
  password?: string;
  mustChangePassword?: boolean | null;
}

export interface SetTenantUserPasswordResponse {
  ok: boolean;
  generatedPassword?: string | null;
}

export interface SetTiersRequest {
  tiers: RebateTierInput[];
  rowVersion: number;
}

export interface SetVanParLevelsRequest {
  lines: ParLevelInput[];
}

export interface SetWorkCenterCalendarRequest {
  opcalCalendarId?: number | null;
  effectiveFrom: string;
  efficiencyPercent?: string;
  utilisationPercent?: string;
  parallelCapacity?: number;
  shifts?: WorkCenterShiftInput[];
}

export interface SetWorkCenterShiftsRequest {
  shifts: WorkCenterShiftInput[];
}

export interface SettingsDTO {
  companyId: number;
  apControlAccountId?: number | null;
  whtPayableAccountId?: number | null;
  fxGainAccountId?: number | null;
  fxLossAccountId?: number | null;
  discountAccountId?: number | null;
  settlementTolerance: string;
  attachmentRequiredAbove?: string | null;
  paymentAnomalyAvgMultiple: string;
  rowVersion: number;
  resolvedApControlCode?: string;
  resolvedWhtPayableCode?: string;
  resolvedFxGainCode?: string;
  resolvedFxLossCode?: string;
  resolvedDiscountCode?: string;
  bankChangeCoolingOffHours: number;
  bankChangeCoolingOffActive: boolean;
  coolingOffRowVersion: number;
}

export interface SettingsQuery {
  companyId: number;
}

export interface SettleAccrualRequest {
  amount: string;
  method?: string;
  note?: string;
}

export interface SettleExitRequest {
  rowVersion: number;
  reasonCode?: string;
  note?: string;
}

export interface SettlePMSFolioRequest {
  businessDate?: string;
  settlements?: PMSFolioSettlementInput[];
  keepOpen?: boolean;
}

export interface SettlePOSCheckRequest {
  terminalId: number;
}

export interface SettlePOSSaleRequest {
  note?: string;
  reference?: string;
}

export interface SettleSupplierRebateRequest {
  invoiceId: number;
  amount: string;
  note?: string;
}

export interface SettlementBlockerDTO {
  code: string;
  message: string;
  detail?: string;
}

export interface SettlementOverride {
  reasonCode: string;
  note?: string;
}

export interface SettlementPreviewDTO {
  settlement: ProductionSettlementDTO;
  blockers: SettlementBlockerDTO[];
  variances: SettlementVarianceLineDTO[];
  quantityStarted?: string;
  expectedOutput?: string;
  expectedYieldPercent: string;
  canPost: boolean;
}

export interface SettlementVarianceLineDTO {
  varianceType: 'material_price' | 'material_usage' | 'labor_rate' | 'labor_efficiency' | 'overhead' | 'yield' | 'overhead_spend' | 'overhead_volume';
  itemId?: number | null;
  workCenterId?: number | null;
  amount: string;
  accountId?: number | null;
  wouldPost: boolean;
  notes?: string;
}

export interface Shift {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  calendarId: number;
  code: string;
  name: string;
  startTime: string;
  durationMinutes: number;
  breakMinutes: number;
  dayPartCode?: string;
  isActive: boolean;
}

export interface ShiftDefinitionDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  startsAt: string;
  endsAt: string;
  crossesMidnight: boolean;
  unpaidBreakMinutes: number;
  paidHours: string;
  departmentId?: number | null;
  colour?: string;
  isNight: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface ShiftPatternDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  cycleDays: number;
  calendarId?: number | null;
  isActive: boolean;
  note?: string;
  slots: ShiftPatternSlotDTO[];
  hoursPerCycle: string;
  rowVersion: number;
}

export interface ShiftPatternSlotDTO {
  dayIndex: number;
  shiftCode: string;
}

export interface ShipmentDTO {
  id: number;
  shipmentNo?: string | null;
  companyId: number;
  orderId: number;
  warehouseId: number;
  shipDate: string;
  status: 'draft' | 'posted' | 'despatched' | 'delivered' | 'reversed' | 'cancelled';
  memo: string;
  reversalReason?: string;
  createdAt: string;
  rowVersion: number;
  lines?: ShipmentLineDTO[];
  pickId?: number | null;
  carrierCode?: string;
  carrierName?: string;
  trackingNo?: string;
  despatchedAt?: string | null;
  despatchedBy?: number | null;
  deliveredOn?: string | null;
  podReceivedAt?: string | null;
  podSignedBy?: string;
  podAttachmentId?: number | null;
  podNote?: string;
}

export interface ShipmentLineDTO {
  id: number;
  lineNo: number;
  orderLineId: number;
  itemId: number;
  warehouseId: number;
  quantity: string;
  unitCost: string;
}

export interface ShipmentLineInput {
  orderLineId: number;
  quantity: string;
}

export interface ShortCloseRequest {
  residualDisposition?: string;
  carryToOrderId?: number | null;
  shortCloseReason?: string;
  override?: SettlementOverride | null;
}

export interface ShrinkageReportDTO {
  companyId: number;
  warehouseId?: number;
  from: string;
  to: string;
  targetPct?: string;
  lines: ShrinkageReportWarehouseLineDTO[];
  itemLines?: ShrinkageReportLineDTO[];
  totalIssuesValue: string;
  totalShrinkageValue: string;
  shrinkagePct?: string;
}

export interface ShrinkageReportLineDTO {
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  openingQty: string;
  receivedQty: string;
  closingQty: string;
  actualUsageQty: string;
  theoreticalUsageQty: string;
  shrinkageWasteQty: string;
  shrinkageWasteValue: string;
  nonShrinkageWasteQty: string;
  nonShrinkageWasteValue: string;
  unexplainedVarianceQty: string;
  shrinkagePercent?: string | null;
}

export interface ShrinkageReportQuery {
  companyId: number;
  warehouseId?: number;
  from: string;
  to: string;
}

export interface ShrinkageReportWarehouseLineDTO {
  warehouseId: number;
  warehouseCode?: string;
  issuesValue: string;
  shrinkageValue: string;
  shrinkagePct?: string;
  targetPct?: string;
  overTarget: boolean;
}

export interface SignInstructionRequest {
  signatoryId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface SignalSettingDTO {
  id?: number;
  companyId: number;
  signalCode: string;
  isEnabled: boolean;
  severity: 'info' | 'warn' | 'block';
  params?: Record<string, unknown>;
  rowVersion: number;
}

export interface SignalSettingInput {
  signalCode: string;
  isEnabled: boolean;
  severity: 'info' | 'warn' | 'block';
  params?: Record<string, unknown>;
  rowVersion: number;
}

export interface SimulateContext {
  companyId: number;
  direction: 'input' | 'output' | 'any';
  supplyType: 'goods' | 'services' | 'accommodation' | 'fnb' | 'digital' | 'transport' | 'other';
  channel: '' | 'pos' | 'portal' | 'web' | 'b2b' | 'hospitality' | 'retail' | 'wholesale';
  date: string;
  currency: string;
  priceBasis: 'exclusive' | 'inclusive';
  shipFrom: SimulateJurisdictionRef;
  shipTo: SimulateJurisdictionRef;
  propertyJurisdiction: SimulateJurisdictionRef;
  party: SimulateParty;
  docType: string;
  docId: number;
  docNo: string;
  regimes: string[];
  chargePoolKey: string;
}

export interface SimulateCrossChargeRequest {
  ruleId: number;
  baseAmount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
}

export interface SimulateIntercompanyRequest {
  ruleId?: number | null;
  companyId: number;
  counterpartyCompanyId: number;
  icReceivableAccountId: number;
  icPayableAccountId: number;
}

export interface SimulateJurisdictionRef {
  id: number;
  code: string;
  country: string;
}

export interface SimulateLine {
  lineNo: number;
  itemId: number | null;
  itemGroupId: number | null;
  itemClass: string;
  taxCode: string;
  taxCodes: string[];
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  quantity: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  levyUnits: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  chargePoolKey: string;
  suppressRegimes: string[];
}

export interface SimulateParty {
  kind: string;
  id: number;
  class: 'registered' | 'unregistered' | 'export' | 'government' | 'exempt' | 'non_resident';
  groupId: number | null;
  jurisdiction: SimulateJurisdictionRef;
  registrationNo: string;
  isRegistered: boolean;
  certificateId: number | null;
}

export interface SimulatePrecedenceRequest {
  companyId: number;
  from: string;
  to: string;
  limit?: number;
}

export interface SimulateRequest {
  context: SimulateContext;
  lines: SimulateLine[];
}

export interface SimulationDiffDTO {
  logId: number;
  docType?: string;
  docId?: number | null;
  docLineNo?: number | null;
  itemId?: number | null;
  customerId?: number | null;
  businessDate: string;
  quantity: string;
  currency: string;
  oldSource: string;
  oldSourceCode?: string;
  oldUnitPrice: string;
  newSource?: string;
  newSourceCode?: string;
  newUnitPrice?: string;
  delta?: string;
  failureCode?: string;
}

export interface SimulationReportDTO {
  versionId: number;
  sampled: number;
  unchanged: number;
  changed: number;
  failed: number;
  totalDelta: string;
  diffs: SimulationDiffDTO[];
}

export interface SlabBandDTO {
  upToAmount?: string | null;
  rate: string;
}

export interface SnapshotDefinition {
  statuses: string[];
  includesHeld: boolean;
  includesGroupBlockUnpicked: boolean;
  includesComplimentary: boolean;
  revenueBasis: string;
}

export interface SourceConversionRow {
  source: string;
  total: number;
  converted: number;
  ratePercent: string;
}

export interface SpendAnalysisQuery {
  companyId: number;
  groupBy?: string;
  stage?: string;
  fromDate?: string;
  toDate?: string;
}

export interface SpendAnalysisRowDTO {
  groupKey: string;
  groupLabel?: string;
  onContractAmount: string;
  offContractAmount: string;
  contractAlternateAmount: string;
  noContractAmount: string;
  totalAmount: string;
  factCount: number;
}

export interface SpendFactDTO {
  id: number;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  stage: string;
  docType: string;
  docId: number;
  docLineNo?: number | null;
  docNo?: string;
  itemId?: number | null;
  expenseAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  projectId?: number | null;
  requesterId?: number | null;
  buyerId?: number | null;
  quantity: string;
  unitPrice: string;
  currency: string;
  fxRate: string;
  amount: string;
  baseAmount: string;
  contractStatus: 'on_contract' | 'off_contract' | 'no_contract' | 'contract_alternate';
  contractId?: number | null;
  contractLineId?: number | null;
  availableContractId?: number | null;
  availableContractNo?: string | null;
  priceVarianceVsContract: string;
  sourcingRoute: 'direct' | 'rfq' | 'contract' | 'catalogue';
  businessDate: string;
  createdAt: string;
}

export interface SplitLotPart {
  lotNo: string;
  qty: string;
  status?: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
  note?: string;
}

export interface SplitLotRequest {
  parentLotId: number;
  parts: SplitLotPart[];
  occurredOn: string;
  docType?: string;
  docId?: number | null;
}

export interface SplitPOSCheckRequest {
  kind: string;
  groups?: number[][];
  partyCount?: number;
  reasonCode?: string;
  note?: string;
}

export interface SpreadRequest {
  accountId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  total: string;
  profile?: string[];
}

export interface SpreadResult {
  lines: BudgetLineDTO[];
  total: string;
}

export interface StaffingRuleDTO {
  id: number;
  companyId: number;
  locationId?: number | null;
  departmentId?: number | null;
  shiftDefinitionId?: number | null;
  shiftCode?: string;
  metric: 'occupied_rooms' | 'arrivals' | 'departures' | 'covers' | 'transactions' | 'revenue' | 'headcount';
  unitsPerHead: string;
  minHeadcount: number;
  maxHeadcount?: number | null;
  dayPart?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  rowVersion: number;
}

export interface StandardCostRollupDTO {
  id: number;
  companyId: number;
  itemId: number;
  bomId?: number | null;
  routingId?: number | null;
  status: 'draft' | 'applied' | 'superseded';
  effectiveDate: string;
  componentBasis: 'standard' | 'moving_average';
  materialCost: string;
  laborCost: string;
  overheadCost: string;
  subcontractCost: string;
  byProductCredit: string;
  totalCost: string;
  previousCost?: string | null;
  costChangePercent?: string | null;
  yieldPercent: string;
  levelsExploded: number;
  notes?: string;
  appliedAt?: string | null;
  appliedBy?: number | null;
  supersededBy?: number | null;
  createdAt: string;
  rowVersion: number;
  lines: StandardCostRollupLineDTO[];
}

export interface StandardCostRollupLineDTO {
  id: number;
  seqNo: number;
  costElement: 'material' | 'labor' | 'overhead' | 'subcontract' | 'by_product_credit';
  componentItemId?: number | null;
  workCenterId?: number | null;
  bomLineId?: number | null;
  routingOperationId?: number | null;
  quantityPer: string;
  unitCost: string;
  extendedCost: string;
  costSource: 'component_standard' | 'component_moving_average' | 'work_center_rate' | 'nested_rollup' | 'manual';
  level: number;
  scrapPct: string;
}

export interface StandingRuleDryRunDTO {
  ruleId: number;
  statementsExamined: number;
  linesExamined: number;
  matched: BankrecStandingRuleHitDTO[];
  totalGross: string;
  totalTax: string;
  totalNet: string;
  note?: string;
}

export interface StartBeneficiaryVerificationRequest {
  method: 'penny' | 'callback' | 'document' | 'existing_relationship' | 'manual';
  changeId?: number | null;
  pennyAmount?: string;
  pennyCurrency?: string;
  contactName?: string;
  contactNumber?: string;
  contactSource?: string;
  attachmentId?: number | null;
  note?: string;
}

export interface StartServiceWorkOrderRequest {
  technicianUserId?: number | null;
  occurredAt?: string | null;
  rowVersion: number;
}

export interface Statement {
  kind: 'income_statement' | 'balance_sheet';
  companyId: number;
  currency: string;
  from: string;
  to: string;
  basis: 'entry' | 'posting';
  comparative: boolean;
  compareFrom?: string;
  compareTo?: string;
  compareLabel?: string;
  sections: StatementSection[];
  summary: StatementTotal[];
  check?: BalanceCheck | null;
  filtered: boolean;
  generatedAt: string;
}

export interface StatementLine {
  accountId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  isPostable: boolean;
  depth: number;
  amount: string;
  compareAmount?: string;
  variance?: string;
  variancePct?: string | null;
  synthetic?: boolean;
  children: StatementLine[];
}

export interface StatementLineDTO {
  id: number;
  importId: number;
  bankAccountId: number;
  lineNo: number;
  valueDate: string;
  entryDate: string;
  direction: 'in' | 'out';
  amount: string;
  currency: string;
  reference?: string;
  counterpartyName?: string;
  description?: string;
  bankTransactionCode?: string;
  status: 'unmatched' | 'matched';
  quarantined: boolean;
  intraday: boolean;
  duplicateOfLineId?: number | null;
  quarantineReason?: string;
  lineDecision?: string;
  lineDecidedBy?: number | null;
  lineDecidedAt?: string | null;
  lineDecisionReason?: string;
  lineDecisionProposedBy?: number | null;
}

export interface StatementQuery {
  companyId: number;
  asOf?: string;
  openOnly?: boolean;
}

export interface StatementSection {
  key: string;
  title: string;
  lines: StatementLine[];
  total: string;
  compareTotal?: string;
  variance?: string;
  variancePct?: string | null;
}

export interface StatementSetEntity {
  companyId: number;
  currency: string;
  incomeStatement: Statement | null;
  balanceSheet: Statement | null;
  cashFlow: GlCashFlowStatementDTO | null;
  equityMovement: GlEquityMovementDTO | null;
}

export interface StatementTotal {
  key: string;
  label: string;
  amount: string;
  compareAmount?: string;
  variance?: string;
  variancePct?: string | null;
}

export interface StatutoryDocumentLanguage {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  country: string;
  documentClass: string;
  language: string;
}

export interface StatutoryMappingDTO {
  id: number;
  companyId: number;
  jurisdictionId: number;
  ruleCode: string;
  expenseAccountCode: string;
  liabilityAccountCode: string;
  remittanceBankAccountId?: number | null;
  isEnabled: boolean;
  disabledReasonCode?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface StatutoryReturnDTO {
  id: number;
  returnNo?: string;
  companyId: number;
  jurisdictionId: number;
  returnCode: string;
  periodStart: string;
  periodEnd: string;
  dueOn?: string;
  status: 'draft' | 'prepared' | 'reconciled' | 'approved' | 'filed' | 'superseded';
  currency: string;
  employeeCount: number;
  employeeAmount: string;
  employerAmount: string;
  totalAmount: string;
  controlAccountCode?: string;
  controlMovement: string;
  reconciledDifference: string;
  filingReference?: string;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface StatutoryReturnLineDTO {
  id: number;
  employeeId: number;
  employeeNo: string;
  employeeName: string;
  statutoryIdMasked?: string;
  baseAmount: string;
  employeeAmount: string;
  employerAmount: string;
  runId?: number | null;
  payslipId?: number | null;
  originPeriodStart?: string;
  originPeriodEnd?: string;
}

export interface StatutoryRuleDTO {
  code: string;
  name: string;
  jurisdictionId: number;
  componentKind: string;
  formulaKind: string;
  rate: string;
  amount: string;
  baseCode?: string;
  bands?: SlabBandDTO[];
  source: 'tenant' | 'pack' | 'legacy';
  sourceCitation: string;
  packId?: string | null;
  packVersion?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  reviewPeriodMonths: number;
  reviewDueOn?: string | null;
  isReviewDue: boolean;
  isOverridable: boolean;
  mappedExpenseAccountCode?: string;
  mappedLiabilityAccountCode?: string;
  isEnabledForCompany: boolean;
  suppressesComponentCode?: string;
}

export interface StatutoryRuleHistoryQuery {
  companyId?: number;
}

export interface Step {
  seq: number;
  mode: string;
  name?: string;
  roleCode?: string;
  userId?: number | null;
  positionId?: number | null;
  jobId?: number | null;
  minGradeLevel?: number | null;
  relativeTo?: string;
  relativeLevels?: number;
  slaHours?: number;
  escalateTo?: number | null;
  escalateToPositionId?: number | null;
  allowSelfApproval?: boolean;
}

export interface StepResult {
  sequenceNo: number;
  code: TaxCode;
  baseKind: 'net' | 'net_plus_prior';
  regime: Regime;
  jurisdiction: Jurisdiction;
  registration?: Registration | null;
  ruleId?: number | null;
  via: 'explicit' | 'rule' | 'regime' | 'certificate' | 'unregistered';
  certificate?: Certificate | null;
  base: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  exact: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  amount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency: string;
  rounded: boolean;
}

export interface StepUpRequest {
  code: string;
}

export interface StepUpResponse {
  accessToken: string;
  expiresAt: string;
  stepUpExpiresAt: string;
}

export interface StockAdjustmentDTO {
  id: number;
  adjustmentNo?: string | null;
  companyId: number;
  warehouseId: number;
  reasonCode: string;
  memo: string;
  adjustmentDate: string;
  status: 'pending_approval' | 'posted' | 'rejected';
  journalEntryId?: number | null;
  journalEntryNo?: string;
  approvedBy?: number | null;
  approvedAt?: string | null;
  rejectedReason?: string;
  lines?: StockAdjustmentLineDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface StockAdjustmentDetailDTO {
  id: number;
  adjustmentNo?: string | null;
  companyId: number;
  warehouseId: number;
  reasonCode: string;
  memo?: string;
  adjustmentDate: string;
  status: 'pending_approval' | 'posted' | 'rejected';
  journalEntryId?: number | null;
  journalEntryNo?: string;
  lines?: StockAdjustmentLineDetailDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface StockAdjustmentLineDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  binId?: number | null;
  qtyDelta: string;
  unitCost: string;
  lotNo?: string;
  gainLossAccountId: number;
}

export interface StockAdjustmentLineDetailDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  binId?: number | null;
  qtyDelta: string;
  unitCost: string;
  lotNo?: string;
  lotId?: number | null;
  rootCauseCode?: string;
  attachmentId?: number | null;
  gainLossAccountId: number;
}

export interface StockAllocationDTO {
  id: number;
  orderId: number;
  orderLineId: number;
  itemId: number;
  warehouseId: number;
  quantity: string;
  reservationId?: number | null;
  status: 'allocated' | 'picked' | 'released';
  allocatedAt: string;
  allocatedBy?: number | null;
  releasedAt?: string | null;
  releasedBy?: number | null;
  rowVersion: number;
}

export interface StockBalanceDTO {
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  binId?: number | null;
  binCode?: string;
  lotId?: number | null;
  lotNo?: string;
  qty: string;
}

export interface StockCountFreezeDTO {
  id: number;
  sessionId: number;
  warehouseId: number;
  zoneId?: number | null;
  binId?: number | null;
  itemId?: number | null;
  frozenAt: string;
  frozenBy?: number | null;
  releasedAt?: string | null;
  releasedBy?: number | null;
}

export interface StockLedgerEntryDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  binId?: number | null;
  movementType: 'receipt' | 'issue' | 'transfer_out' | 'transfer_in' | 'adjustment_in' | 'adjustment_out' | 'revaluation' | 'reversal' | 'landed_cost' | 'waste';
  docType: string;
  docId?: number | null;
  docLineNo?: number | null;
  lotNo?: string;
  serialNos?: string[];
  qty: string;
  unitCost: string;
  value: string;
  runningBalanceQty: string;
  runningBalanceValue: string;
  businessDate: string;
  journalEntryId?: number | null;
  journalEntryNo?: string;
  reasonCode?: string | null;
  fifoLayerId?: number | null;
  createdAt: string;
}

export interface StockLotDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  lotNo: string;
  status: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
  statusNote?: string;
  statusChangedAt?: string | null;
  statusChangedBy?: number | null;
  expiryDate?: string | null;
  bestBeforeDate?: string | null;
  manufacturedOn?: string | null;
  receivedFirstOn?: string | null;
  daysToExpiry?: number | null;
  supplierLotRef?: string;
  vendorId?: number | null;
  vendorName?: string;
  countryOfOrigin?: string | null;
  source: 'receipt' | 'production' | 'repack' | 'backfill' | 'manual' | 'transfer';
  attributes?: Record<string, unknown>;
  qtyOnHand: string;
  isDeleted: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface StockLotDetailDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  lotNo: string;
  status: 'available' | 'quarantine' | 'blocked' | 'recalled' | 'expired';
  statusNote?: string;
  statusChangedAt?: string | null;
  statusChangedBy?: number | null;
  expiryDate?: string | null;
  bestBeforeDate?: string | null;
  manufacturedOn?: string | null;
  receivedFirstOn?: string | null;
  daysToExpiry?: number | null;
  supplierLotRef?: string;
  vendorId?: number | null;
  vendorName?: string;
  countryOfOrigin?: string | null;
  source: 'receipt' | 'production' | 'repack' | 'backfill' | 'manual' | 'transfer';
  attributes?: Record<string, unknown>;
  qtyOnHand: string;
  isDeleted: boolean;
  createdAt: string;
  rowVersion: number;
  balances: LotBalanceDTO[];
  parents: LotGenealogyDTO[];
  children: LotGenealogyDTO[];
}

export interface StockOnHandDTO {
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  qtyOnHand: string;
  avgUnitCost: string;
  value: string;
  qtyReservedSoft: string;
  qtyReservedHard: string;
  updatedAt: string;
}

export interface StockRequisitionDTO {
  id: number;
  requisitionNo?: string | null;
  companyId: number;
  fromWarehouseId: number;
  toWarehouseId: number;
  requiredOn: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'issued' | 'partially_issued' | 'closed' | 'cancelled';
  origin: 'par' | 'manual' | 'event' | 'recipe_forecast';
  businessDayId?: number | null;
  shiftCode?: string;
  memo?: string;
  requestedBy?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  rejectedReason?: string;
  firstTransferId?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface StockRequisitionDetailDTO {
  id: number;
  requisitionNo?: string | null;
  companyId: number;
  fromWarehouseId: number;
  toWarehouseId: number;
  requiredOn: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'issued' | 'partially_issued' | 'closed' | 'cancelled';
  origin: 'par' | 'manual' | 'event' | 'recipe_forecast';
  businessDayId?: number | null;
  shiftCode?: string;
  memo?: string;
  requestedBy?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  rejectedReason?: string;
  firstTransferId?: number | null;
  toWarehouseStockModel?: 'perpetual' | 'expense_on_issue';
  lines: StockRequisitionLineDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface StockRequisitionLineDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  requestedQty: string;
  approvedQty?: string | null;
  issuedQty: string;
  parQty?: string | null;
  onHandAtRequest?: string | null;
  lotId?: number | null;
  note?: string;
}

export interface StockSerialDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  serialNo: string;
  warehouseId: number;
  lotNo?: string;
  lotId?: number | null;
  status: string;
  createdAt: string;
  updatedAt?: string | null;
}

export interface StockShortageDTO {
  id: number;
  companyId: number;
  itemId: number;
  warehouseId: number;
  lotId?: number | null;
  docType: string;
  docId?: number | null;
  docLineNo?: number | null;
  reservationId?: number | null;
  requestedQty: string;
  availableQty: string;
  shortfallQty: string;
  cause: 'no_stock' | 'expired_lot' | 'quarantined_lot' | 'reserved_elsewhere' | 'shelf_life' | 'bin_blocked' | 'in_transit';
  status: 'open' | 'substituted' | 'backordered' | 'cancelled' | 'resolved';
  detectedOn: string;
  resolvedAt?: string | null;
  note?: string;
  createdAt: string;
}

export interface StockTransferDetailDTO {
  id: number;
  transferNo?: string | null;
  status: 'draft' | 'in_transit' | 'received' | 'cancelled';
  companyId: number;
  fromWarehouseId: number;
  fromWarehouseCode?: string;
  toWarehouseId: number;
  toWarehouseCode?: string;
  inTransitWarehouseId?: number | null;
  transferDate: string;
  memo?: string;
  despatchedOn?: string | null;
  despatchedAt?: string | null;
  despatchedBy?: number | null;
  receivedOn?: string | null;
  receivedAt?: string | null;
  receivedBy?: number | null;
  expectedArrivalOn?: string | null;
  carrierName?: string;
  carrierReference?: string;
  vehicleReference?: string;
  driverName?: string;
  overdueFlaggedAt?: string | null;
  cancelReason?: string;
  requisitionId?: number | null;
  fromCostCenterId?: number | null;
  toCostCenterId?: number | null;
  despatchJournalEntryId?: number | null;
  receiveJournalEntryId?: number | null;
  lineCount: number;
  inTransitValue: string;
  lines?: StockTransferLineDetailDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface StockTransferLineDetailDTO {
  id: number;
  itemId: number;
  sku?: string;
  quantity: string;
  despatchedQty: string;
  receivedQty: string;
  shortageQty: string;
  shortageReasonCode?: string;
  unitCost: string;
  fromBinId?: number | null;
  toBinId?: number | null;
  lotId?: number | null;
  toLotId?: number | null;
  lotNo?: string;
}

export interface StopOperationRequest {
  laborMinutes: string;
  machineMinutes?: string;
  actualLaborRatePerHour?: string | null;
  actualOverheadRatePerHour?: string | null;
  final?: boolean;
  notes?: string;
}

export interface StreamActualSources {
  pmsRevenueCenterIds: number[];
  posOutletIds: number[];
}

export interface StructureDTO {
  id: number;
  name: string;
  status: string;
  segmentCount: number;
  effectiveFrom: string;
  firstPostingDate?: string;
  frozen: boolean;
  segments?: SegmentDTO[];
  rowVersion: number;
}

export interface Subdivision {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  countryId: string;
  code: string;
  name: string;
  kind: 'state' | 'province' | 'atoll' | 'region' | 'emirate' | 'district' | 'county' | 'prefecture' | 'territory' | 'other';
  level: number;
  parentId?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface SubdivisionRequest {
  countryId: string;
  code: string;
  name: string;
  kind?: string;
  level: number;
  parentId?: string;
  sortOrder?: number;
}

export interface SubdivisionUpdateRequest {
  name?: string | null;
  kind?: string | null;
  parentId?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface SubmitASNLineInput {
  poLineId: number;
  quantity: string;
}

export interface SubmitASNRequest {
  carrier?: string;
  trackingNo?: string;
  shippedDate?: string;
  expectedDate?: string;
  note?: string;
  lines: SubmitASNLineInput[];
}

export interface SubmitContractRequest {
  note?: string;
  rowVersion: number;
}

export interface SubmitContractorInvoiceRequest {
  projectId?: number | null;
  amount: string;
  currency: string;
  invoiceDate: string;
  description?: string;
  attachmentId?: number | null;
}

export interface SubmitECORequest {
  note?: string;
}

export interface SubmitExpenseClaimRequest {
  policyOverrideReasonCode?: string;
  policyOverrideNote?: string;
  authorisedBy?: number | null;
}

export interface SubmitGuidedRequest {
  plan: FoundationPlan;
}

export interface SubmitHierarchyVersionRequest {
  changeSummary: string;
}

export interface SubmitInstructionRequest {
  override?: InstructionOverride | null;
  rowVersion: number;
}

export interface SubmitMatrixVersionRequest {
  note?: string;
}

export interface SubmitPOSCashCountRequest {
  lines?: POSCashCountLineInput[];
  total?: string;
  rowVersion: number;
}

export interface SubmitPartyMergeRequest {
  reasonCode: string;
  reasonNote?: string;
  rowVersion: number;
}

export interface SubmitPaymentClaimRequest {
  companyId: number;
  amount: string;
  currency: string;
  bankReference?: string;
  paidOn: string;
  note?: string;
  attachmentId?: number | null;
}

export interface SubmitPrecedenceRequest {
  rowVersion: number;
  note?: string;
}

export interface SubmitQuoteRequest {
  vendorId: number;
  currency?: string;
  leadTimeDays?: number;
  terms?: string;
  termsScore?: number | null;
  lines: QuoteLineInput[];
}

export interface SubmitRFQQuoteLineInput {
  rfqLineId: number;
  unitPrice: string;
  leadTimeDays?: number | null;
}

export interface SubmitRFQQuoteRequest {
  currency?: string;
  leadTimeDays?: number;
  terms?: string;
  lines: SubmitRFQQuoteLineInput[];
}

export interface SubmitRVMForecastVersionRequest {
  note?: string;
}

export interface SubmitRequisitionRequest {
  rowVersion: number;
}

export interface SubmitReturnRequest {
  note: string;
}

export interface SubmitRevaluationRequest {
  companyId: number;
  base: string;
  quote: string;
  rateType?: 'spot' | 'corporate' | 'historical';
  asOf: string;
  reasonCode: string;
  note?: string;
}

export interface SubmitScorecardRequest {
  stage: string;
  criteria?: ScorecardCriterionScore[];
  overallScore?: number | null;
  recommendation: string;
  note?: string;
  submit?: boolean;
}

export interface SubmitSupplierInvoiceRequest {
  poId: number;
  vendorInvoiceNo: string;
  invoiceDate: string;
}

export interface SubscriptionDTO {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  planId: string;
  planCode: string;
  planName: string;
  status: 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled';
  currency: string;
  seats: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string | null;
  cancelAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
  pastDueSince?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  rowVersion: number;
}

export interface SubscriptionListQuery {
  status?: string;
  tenantId?: string;
  cursor?: string;
  limit?: number;
}

export interface SubscriptionPlanDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  itemId?: number | null;
  revenueAccountId: number;
  currency: string;
  billingCycle: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  price: string;
  revenueTaxCode?: string | null;
  isActive: boolean;
  rowVersion: number;
}

export interface SubstituteOption {
  itemId: number;
  conversionFactor: string;
  priority: number;
  requiresApproval: boolean;
  available: string;
  wouldNeed: string;
}

export interface SubstituteShortageRequest {
  substituteItemId: number;
  substituteLotId?: number | null;
  qty: string;
  reasonCode?: string;
  note?: string;
  approvedByUserId?: number | null;
}

export interface SuggestionDTO {
  invoiceId: number;
  invoiceNo: string;
  dueDate: string;
  balance: string;
  suggested: string;
  reason: string;
}

export interface SuggestionsResponse {
  statementLineId: number;
  candidates: MatchCandidateDTO[];
  generatedAt: string;
}

export interface SupersedeAssignmentPreviewDTO {
  employeeId: number;
  currentAssignmentId: number;
  effectiveFrom: string;
  closesCurrentAt: string;
  changes: AssignmentFieldChangeDTO[];
  landsInAPostedPeriod?: boolean | null;
  retroNote?: string;
}

export interface SupersedeAssignmentRequest {
  effectiveFrom: string;
  reasonCode: string;
  note?: string;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  clearManager?: boolean;
  positionId?: number | null;
  clearPosition?: boolean;
  locationId?: number | null;
  clearLocation?: boolean;
  employmentType?: string | null;
  employmentStatus?: string | null;
  payGroupId?: number | null;
  shiftPatternId?: number | null;
  basicSalary?: string | null;
  currency?: string | null;
  pensionable?: boolean | null;
  fte?: string | null;
  probationEndOn?: string | null;
  noticeDays?: number | null;
}

export interface SupersedeAssignmentResultDTO {
  assignment: EmploymentAssignmentDTO;
  previousAssignmentId: number;
  retroEventRaised: boolean;
  retroNote?: string;
}

export interface SupersedeCapturePolicyRequest {
  categoryName?: string;
  expenseAccountId?: number | null;
  defaultTaxCode?: string | null;
  perTxnLimit?: string | null;
  dailyLimit?: string | null;
  monthlyLimit?: string | null;
  requiresEvidence?: boolean | null;
  evidenceFreeCeiling?: string | null;
  requiresApproval?: boolean | null;
  isBlocked?: boolean | null;
  effectiveFrom: string;
  rowVersion: number;
}

export interface SupersedeDesignationRequest {
  effectiveFrom: string;
  registrationId?: number | null;
  identifier?: string;
  note?: string;
}

export interface SupersedeDiscountPolicyRequest {
  effectiveFrom: string;
  thresholdPercent: string;
  thresholdAmount?: string;
  thresholdCurrency?: string;
  marginFloorPolicy?: string;
}

export interface SupersedeGradeRequest {
  effectiveFrom: string;
  name?: string | null;
  level?: number | null;
  minPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  midPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  maxPay?: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  note?: string;
}

export interface SupersedeMandateRequest {
  effectiveFrom: string;
  name?: string;
  signingRule?: 'any' | 'a_plus_b' | 'all';
  requiredSignatures?: number;
  minAmount?: string;
  maxAmount?: string;
  currency?: string;
  documentRef?: string;
  attachmentId?: number | null;
  notes?: string;
  copySignatories?: boolean | null;
  rowVersion: number;
}

export interface SupersedePositionRequest {
  effectiveFrom: string;
  name?: string | null;
  jobId?: number | null;
  gradeId?: number | null;
  clearGrade?: boolean;
  branchId?: number | null;
  departmentId?: number | null;
  costCenterId?: number | null;
  businessUnitId?: number | null;
  divisionId?: number | null;
  locationId?: number | null;
  reportsToPositionId?: number | null;
  clearReportsTo?: boolean;
  headcount?: number | null;
  status?: string | null;
  note?: string | null;
  reasonCode?: string;
}

export interface SupersedeReasonRequest {
  effectiveFrom: string;
  labelKey?: string;
  label?: string;
  requiresNote?: boolean | null;
  requiresAttachment?: boolean | null;
  isActive?: boolean | null;
  note?: string;
}

export interface SupersedeRecipeRequest {
  effectiveFrom: string;
  name?: string;
  yieldQty?: string;
  yieldUom?: string;
  kind?: 'plated' | 'batch' | 'sub_recipe' | 'modifier';
  defaultWarehouseId?: number | null;
  note?: string;
  lines?: RecipeLineRequest[];
}

export interface SupersedeRequest {
  reason: string;
}

export interface SupersedeSSPRequest {
  effectiveFrom: string;
  sspAmount: string;
  sspMethod?: string;
}

export interface SupersedeStandingRuleRequest {
  effectiveFrom: string;
  changes: CreateStandingRuleRequest;
  rowVersion: number;
}

export interface SupplierComplianceDocDTO {
  id: number;
  vendorId: number;
  documentTypeCode: string;
  reference?: string;
  issuedOn?: string | null;
  expiresOn?: string | null;
  attachmentId?: number | null;
  status: string;
  verifiedBy?: number | null;
  verifiedAt?: string | null;
  rejectReason?: string;
  notes?: string;
  createdAt: string;
  rowVersion: number;
}

export interface SupplierContractDTO {
  id: number;
  contractNo?: string | null;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  kind: string;
  title: string;
  description?: string;
  vendorReference?: string;
  currency: string;
  startsOn: string;
  endsOn?: string | null;
  autoRenew: boolean;
  noticePeriodDays: number;
  renewalTermMonths?: number | null;
  notifyOn?: string;
  incotermCode?: string | null;
  incotermPlace?: string;
  paymentTermsCode?: string;
  leadTimeDays?: number | null;
  status: string;
  committedValue: string;
  releasedValue: string;
  invoicedValue: string;
  approvalRequestId?: number | null;
  ownerUserId?: number | null;
  attachmentId?: number | null;
  terminationReason?: string;
  cancelReason?: string;
  createdAt: string;
  rowVersion: number;
}

export interface SupplierContractDetailDTO {
  id: number;
  contractNo?: string | null;
  companyId: number;
  vendorId: number;
  vendorCode?: string;
  vendorName?: string;
  kind: string;
  title: string;
  description?: string;
  vendorReference?: string;
  currency: string;
  startsOn: string;
  endsOn?: string | null;
  autoRenew: boolean;
  noticePeriodDays: number;
  renewalTermMonths?: number | null;
  notifyOn?: string;
  incotermCode?: string | null;
  incotermPlace?: string;
  paymentTermsCode?: string;
  leadTimeDays?: number | null;
  status: string;
  committedValue: string;
  releasedValue: string;
  invoicedValue: string;
  approvalRequestId?: number | null;
  ownerUserId?: number | null;
  attachmentId?: number | null;
  terminationReason?: string;
  cancelReason?: string;
  createdAt: string;
  rowVersion: number;
  scopes: SupplierContractScopeDTO[];
  lines: SupplierContractLineDTO[];
  commitments: ContractCommitmentDTO[];
  rebates: ContractRebateDTO[];
}

export interface SupplierContractLineDTO {
  id: number;
  lineNo: number;
  itemId?: number | null;
  description?: string;
  uom?: string;
  minQuantity: string;
  unitPrice: string;
  currency: string;
  priceBasis: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  leadTimeDays?: number | null;
  minShelfLifeDays?: number | null;
  minOrderQuantity?: string | null;
  orderMultiple?: string | null;
  priceReviewOn?: string | null;
  rowVersion: number;
}

export interface SupplierContractScopeDTO {
  id: number;
  seqNo: number;
  itemId?: number | null;
  itemCategoryId?: number | null;
  expenseAccountId?: number | null;
  branchId?: number | null;
  locationId?: number | null;
  isExclusive: boolean;
  notes?: string;
}

export interface SupplierDocumentTypeDTO {
  code: string;
  name: string;
  isBlocking: boolean;
  requiredForCriticality?: string;
  warnDaysBeforeExpiry: number;
  source: string;
  isActive: boolean;
  rowVersion: number;
}

export interface SupplierPolicyDTO {
  companyId: number;
  unqualifiedSupplierPolicy: string;
  expiredDocumentPolicy: string;
  unscreenedSupplierPolicy: string;
  screeningValidityDays: number;
  requalificationMonths: number;
  rowVersion: number;
}

export interface SupplierQualificationDTO {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  qualificationStatus: string;
  criticality: string;
  qualifiedUntil?: string | null;
  qualificationNote?: string;
  lastScreenedAt?: string | null;
  screeningOutcome?: string;
  rowVersion: number;
  history: SupplierQualificationEventDTO[];
}

export interface SupplierQualificationEventDTO {
  id: number;
  fromStatus: string;
  toStatus: string;
  reason?: string;
  assessmentScore?: string | null;
  assessmentNote?: string;
  validUntil?: string | null;
  approvalRequestId?: number | null;
  decidedBy?: number | null;
  decidedAt: string;
}

export interface SupplierRebateAccrualDTO {
  id: number;
  rebateId: number;
  contractId: number;
  companyId: number;
  periodStart: string;
  periodEnd: string;
  asOf: string;
  qualifyingQuantity: string;
  qualifyingValue: string;
  tierSeqNo?: number | null;
  accruedThisRun: string;
  accruedToDate: string;
  journalEntryId?: number | null;
  supersededBy?: number | null;
  settledByInvoiceId?: number | null;
  createdAt: string;
}

export interface SupplierReturnDTO {
  id: number;
  rtsNo?: string | null;
  companyId: number;
  vendorId: number;
  poId?: number | null;
  grnId?: number | null;
  returnDate: string;
  reasonKind: string;
  reason?: string;
  status: 'draft' | 'submitted' | 'approved' | 'despatched' | 'completed' | 'cancelled';
  replacementExpected: boolean;
  debitNoteInvoiceId?: number | null;
  carrierRef?: string;
  totalValue: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  despatchedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string;
  createdAt: string;
  rowVersion: number;
  lines?: SupplierReturnLineDTO[];
}

export interface SupplierReturnLineDTO {
  id: number;
  lineNo: number;
  grnLineId?: number | null;
  poLineId?: number | null;
  itemId: number;
  warehouseId: number;
  stockLotId?: number | null;
  quantity: string;
  unitCost: string;
  lineValue: string;
  notes?: string;
}

export interface SupplierReturnLineInput {
  grnLineId: number;
  quantity: string;
  notes?: string;
}

export interface SupplierRiskRowDTO {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  qualificationStatus: string;
  criticality: string;
  expiringDocumentCount: number;
  expiredDocumentCount: number;
  lastScreenedAt?: string | null;
  screeningOutcome?: string;
  openCapaCount: number;
}

export interface SupplierScreeningDTO {
  id: number;
  vendorId: number;
  provider: string;
  listKind: string;
  screenedName: string;
  countryCode?: string;
  outcome: string;
  matchCount: number;
  evidence?: string;
  attachmentId?: number | null;
  reference?: string;
  reviewOutcome?: string;
  reviewNote?: string;
  reviewedBy?: number | null;
  reviewedAt?: string | null;
  screenedBy?: number | null;
  screenedAt: string;
  validUntil?: string | null;
}

export interface SupportSessionRequest {
  reason: string;
}

export interface SupportSessionResponse {
  supportSessionId: string;
  token: string;
  expiresAt: string;
}

export interface SuspendContractRequest {
  reason: string;
  resume?: boolean | null;
}

export interface SuspendTenantRequest {
  reason: string;
}

export interface SuspenseItemDTO {
  id: number;
  positionId: number;
  companyId: number;
  journalLineId: number;
  entryDate: string;
  amount: string;
  clearedAmount: string;
  remaining: string;
  sourceModule?: string;
  sourceDocType?: string;
  sourceDocId?: number | null;
  reasonCode?: string;
  description?: string;
  status: 'open' | 'partially_cleared' | 'cleared' | 'written_off';
  queueItemId?: number | null;
  clearedAt?: string | null;
  clearingJournalEntryId?: number | null;
  disposition?: string;
  dispositionNote?: string;
  rowVersion: number;
}

export interface SuspensePositionDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  accountId: number;
  ownerPositionId?: number | null;
  maxAgeDays: number;
  blocksClose: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface SuspensePositionReportRowDTO {
  positionId: number;
  code: string;
  name: string;
  accountId: number;
  ownerPositionId?: number | null;
  maxAgeDays: number;
  blocksClose: boolean;
  openCount: number;
  remaining: string;
  agedCount: number;
  agedRemaining: string;
}

export interface SuspenseReportDTO {
  companyId: number;
  asOf: string;
  positions: SuspensePositionReportRowDTO[];
  totalRemaining: string;
}

export interface SuspenseReportQuery {
  companyId?: number;
  asOf?: string;
}

export interface TakePOSCustomerDepositRequest {
  outletId: number;
  customerId: number;
  amount: string;
  currency: string;
  businessDate?: string;
  takenBySaleId?: number | null;
  expiresOn?: string;
  note?: string;
}

export interface TakeRVMSnapshotRequest {
  propertyId: number;
}

export interface TariffClassificationDTO {
  id: number;
  code: string;
  description: string;
  country: string;
  dutyRatePct?: string | null;
  dutyPerUnit?: string | null;
  dutyUom?: string;
  source: 'tenant' | 'pack';
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface TaskDTO {
  id: number;
  step: number;
  mode: string;
  status: 'pending' | 'approved' | 'rejected' | 'delegated' | 'skipped' | 'expired' | 'voided';
  stepName?: string;
  assigneeUserId?: number | null;
  assigneeName?: string;
  assigneeRoleId?: number | null;
  assigneeRole?: string;
  actedBy?: number | null;
  actedByName?: string;
  actedAt?: string | null;
  comment?: string;
  dueAt?: string | null;
  remindedAt?: string | null;
  escalatedAt?: string | null;
  escalatedTo?: number | null;
  delegatedFrom?: number | null;
  delegatedFromName?: string;
  active: boolean;
  assigneePositionId?: number | null;
  assigneePosition?: string;
  viaDelegationId?: number | null;
  voidReasonCode?: string;
}

export interface TaskProfitDTO {
  taskId: number;
  wbsCode: string;
  name: string;
  budget: string;
  actualTimesheet: string;
}

export interface TaskProgressRequest {
  status: string;
  note?: string;
  rowVersion: number;
}

export interface TaxCode {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE';
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  basis: 'percent' | 'per_unit';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  unitCurrency?: string | null;
  unitLabel?: string | null;
  jurisdiction: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  inputAccountCode?: string | null;
  outputAccountCode?: string | null;
  payableAccountCode?: string | null;
  rounding: 'line' | 'document';
  roundingMode: 'half_up' | 'half_even';
  isRecoverable: boolean;
  description: string;
  isActive: boolean;
  regimeId?: number | null;
  jurisdictionId?: number | null;
  source: 'pack' | 'tenant' | 'legacy';
  packId?: string | null;
  packVersion?: number | null;
  contentHash?: string | null;
  sourceCitation: string;
  reviewDue?: string | null;
  isOverridable: boolean;
}

export interface TaxCompany {
  id: number;
  code: string;
  country: string;
  baseCurrency: string;
  jurisdiction: Jurisdiction;
}

export interface TaxLine {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  companyId?: number | null;
  docType: string;
  docId: number;
  docNo?: string | null;
  docLineNo: number;
  direction: 'input' | 'output' | 'any';
  taxDate: string;
  taxCode: string;
  taxCodeId?: number | null;
  kind: 'VAT' | 'WHT' | 'LEVY' | 'CHARGE';
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  basis: 'percent' | 'per_unit';
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  currency: string;
  quantity: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  baseAmount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  taxAmount: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  isRecoverable: boolean;
  jurisdictionId?: number | null;
  regimeId?: number | null;
  regimeCode: string;
  sequenceNo: number;
  baseKind: 'net' | 'net_plus_prior';
  registrationId?: number | null;
  inclusive: boolean;
  packId?: string | null;
  packVersion?: number | null;
  source: 'pack' | 'tenant' | 'legacy';
  chargePoolKey: string;
  reverseChargePairNo?: number | null;
}

export interface TaxReportingUnitDTO {
  id: number;
  psuId: number;
  name: string;
  registrationId: string;
  jurisdictionId: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface TaxReturnDTO {
  id: number;
  registrationId: number;
  returnDefCode: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  status: string;
  returnNo?: string | null;
  filingCurrency: string;
  netPayable: string;
  rowVersion: number;
}

export interface TaxReturnLineDTO {
  boxNo: string;
  label: string;
  sequenceNo: number;
  valueKind: string;
  baseAmount: string;
  taxAmount: string;
  value: string;
  lineCount: number;
  predicate?: Record<string, unknown> | null;
  formula?: string;
}

export interface TaxValidateRequest {
  sampleSize: number;
}

export interface TemplateDTO {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  code: string;
  name: string;
  description: string;
  country?: string | null;
  industry: string;
  isSystem: boolean;
  lines: COATemplateLine[];
}

export interface TemplateFromEntryRequest {
  name: string;
  code?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  startOn?: string;
  endOn?: string;
}

export interface TemplateLineDTO {
  lineNo: number;
  accountId: number;
  accountCode?: string;
  debit: string;
  credit: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
  taxCode?: string | null;
  description: string;
}

export interface TemplateLineInput {
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentCode?: string | null;
  isPostable?: boolean | null;
  currency?: string | null;
  description?: string;
  sortOrder?: number;
  requiresCostCenter: boolean;
  requiresProfitCenter: boolean;
  requiresBranch: boolean;
  requiresDepartment: boolean;
  requiresProject: boolean;
  requiresDivision: boolean;
}

export interface Tenant {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  slug: string;
  name: string;
  schemaName: string;
  dbCluster: string;
  status: string;
  planId?: string | null;
  region: string;
  timezone: string;
  country: string;
  locale: string;
  baseCurrency: string;
  storagePrefix: string;
  featureFlags: Record<string, unknown>;
  customLimits: Record<string, unknown>;
  settings: Record<string, unknown>;
  trialEndsAt?: string | null;
  activatedAt?: string | null;
  suspendedAt?: string | null;
  suspendedReason?: string | null;
  archivedAt?: string | null;
  plan?: Plan | null;
}

export interface TenantActivationDTO {
  country: string;
  packId: string;
  packVersion: number;
  contentHash: string;
  status: string;
  effectiveFrom: string;
  activatedAt: string;
  pendingPackVersion?: number | null;
  pendingSeenAt?: string | null;
  sandboxRanAt?: string | null;
  acceptedEffectiveFrom?: string | null;
  lastError?: string;
}

export interface TenantAuditQuery {
  entity?: string;
  action?: string;
  actorId?: string;
  docNo?: string;
  cursor?: string;
  limit?: number;
}

export interface TenantDetail {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  slug: string;
  name: string;
  schemaName: string;
  dbCluster: string;
  status: string;
  planId?: string | null;
  region: string;
  timezone: string;
  country: string;
  locale: string;
  baseCurrency: string;
  storagePrefix: string;
  featureFlags: Record<string, unknown>;
  customLimits: Record<string, unknown>;
  settings: Record<string, unknown>;
  trialEndsAt?: string | null;
  activatedAt?: string | null;
  suspendedAt?: string | null;
  suspendedReason?: string | null;
  archivedAt?: string | null;
  plan?: Plan | null;
  planName?: string;
  schemaVersion: number;
  schemaLatestVersion: number;
  schemaDirty: boolean;
  schemaBehind: boolean;
}

export interface TenantListQuery {
  status?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface TenantMigrateResponse {
  schemaVersion: number;
  schemaLatestVersion: number;
  schemaDirty: boolean;
  schemaBehind: boolean;
}

export interface TenantSummary {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  slug: string;
  name: string;
  schemaName: string;
  dbCluster: string;
  status: string;
  planId?: string | null;
  region: string;
  timezone: string;
  country: string;
  locale: string;
  baseCurrency: string;
  storagePrefix: string;
  featureFlags: Record<string, unknown>;
  customLimits: Record<string, unknown>;
  settings: Record<string, unknown>;
  trialEndsAt?: string | null;
  activatedAt?: string | null;
  suspendedAt?: string | null;
  suspendedReason?: string | null;
  archivedAt?: string | null;
  plan?: Plan | null;
  planName?: string;
  userCount: number;
  lastActivityAt?: string | null;
}

export interface TenantTaxPacksQuery {
  cursor?: string;
  limit?: number;
}

export interface TenantUsageQuery {
  days?: number;
}

export interface TenantUserSummary {
  id: number;
  email: string;
  name: string;
  status: string;
  mfaEnabled: boolean;
  lastLoginAt?: string | null;
  lockedUntil?: string | null;
  createdAt: string;
}

export interface TenantUsersQuery {
  cursor?: string;
  limit?: number;
}

export interface TerminateContractRequest {
  reason: string;
  effectiveOn?: string;
}

export interface TerminateDivisionCountryRequest {
  effectiveTo: string;
}

export interface TerminateDivisionLegalEntityRequest {
  effectiveTo: string;
}

export interface TerminateEmployeeRequest {
  leftOn: string;
  rowVersion: number;
}

export interface TerminateParLevelRequest {
  effectiveTo: string;
  note?: string;
}

export interface TerminatePolicyRequest {
  effectiveTo: string;
  note?: string;
}

export interface TicketDTO {
  id: number;
  caseId: number;
  caseNo: string;
  subject: string;
  description: string;
  origin: string;
  category: string;
  priority: string;
  status: string;
  companyId: number;
  customerId?: number | null;
  customerName?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  assignedTo?: number | null;
  assignedName?: string;
  slaPolicyId?: number | null;
  slaPolicyName?: string;
  responseDueAt?: string | null;
  resolutionDueAt?: string | null;
  firstResponseAt?: string | null;
  responseBreachedAt?: string | null;
  resolutionBreachedAt?: string | null;
  escalatedAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  reopenedCount: number;
  csatSatisfied?: boolean | null;
  csatSubmittedAt?: string | null;
  customerAssetId?: number | null;
  warehouseId?: number | null;
  pmScheduleId?: number | null;
  isRevisit: boolean;
  billable: boolean;
  invoiceStatus: string;
  arInvoiceId?: number | null;
  createdAt: string;
  rowVersion: number;
  ticketRowVersion: number;
}

export interface TicketLaborDTO {
  id: number;
  ticketId: number;
  technicianUserId: number;
  technicianName?: string;
  workDate: string;
  hours: string;
  rate: string;
  billable: boolean;
  description: string;
  status: string;
  createdAt: string;
  rowVersion: number;
}

export interface TicketPartDTO {
  id: number;
  ticketId: number;
  itemId: number;
  itemSku?: string;
  warehouseId: number;
  quantity: string;
  unitCost: string;
  billable: boolean;
  status: string;
  voidReason?: string;
  createdAt: string;
  rowVersion: number;
}

export interface TimelineQuery {
  entity: string;
  entityId: string;
  limit?: number;
}

export interface TimesheetDTO {
  id: number;
  employeeUserId?: number | null;
  contractorPortalUserId?: number | null;
  weekStartDate: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  totalHours: string;
  totalCost: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: number | null;
  rejectionReason?: string;
  journalEntryId?: number | null;
  lines: TimesheetLineDTO[];
  rowVersion: number;
}

export interface TimesheetLineDTO {
  id: number;
  projectId: number;
  taskId?: number | null;
  workDate: string;
  hours: string;
  billable: boolean;
  description: string;
  costRate?: string;
  billRate?: string;
  costAmount?: string;
  billAmount?: string;
  billedAt?: string | null;
  arInvoiceId?: number | null;
  rowVersion: number;
}

export interface TimesheetLineInput {
  projectId: number;
  taskId?: number | null;
  workDate: string;
  hours: string;
  billable?: boolean | null;
  description?: string;
}

export interface ToggleFlagRequest {
  value: boolean;
  approverId?: number;
  secondApproverId?: number | null;
  reason?: string;
}

export interface Tolerance {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  base: string;
  quote: string;
  rateType: 'spot' | 'corporate' | 'historical';
  maxMovePct: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  action: 'hold' | 'warn';
  isActive: boolean;
}

export interface ToolCallRecord {
  name: string;
  toolUseId: string;
  input: string;
  resultSummary: string;
  isError: boolean;
  previewId?: number | null;
}

export interface ToolGateOverride {
  reasonCode: string;
  justification?: string;
}

export interface TpCreatePolicyRequest {
  intercompanyRuleId: number;
  category?: string;
  method: 'cup' | 'cost_plus' | 'resale_minus' | 'tnmm' | 'profit_split';
  markupPct?: string | null;
  rangeLow?: string | null;
  rangeHigh?: string | null;
  basisNote?: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

export interface TpDepartureDTO {
  transactionId: number;
  companyId: number;
  counterpartyCompanyId: number;
  docDate: string;
  docType: string;
  docNo?: string;
  journalEntryId?: number | null;
  direction: string;
  currency: string;
  amount: string;
  reason: string;
}

export interface TpDeparturesQuery {
  companyId?: number;
  from: string;
  to: string;
}

export interface TpDeparturesReportDTO {
  from: string;
  to: string;
  companyId?: number | null;
  items: TpDepartureDTO[];
  examined: number;
  truncated: boolean;
}

export interface TpDiscardPolicyRequest {
  reason: string;
  rowVersion: number;
}

export interface TpListPoliciesQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  counterpartyCompanyId?: number;
  intercompanyRuleId?: number;
  status?: string;
  method?: string;
  category?: string;
  asAt?: string;
}

export interface TpPolicyDTO {
  id: number;
  intercompanyRuleId: number;
  companyId: number;
  companyCode?: string;
  counterpartyCompanyId: number;
  counterpartyCompanyCode?: string;
  category: string;
  method: 'cup' | 'cost_plus' | 'resale_minus' | 'tnmm' | 'profit_split';
  markupPct?: string | null;
  rangeLow?: string | null;
  rangeHigh?: string | null;
  basisNote: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  status: 'draft' | 'pending_approval' | 'approved' | 'superseded' | 'discarded';
  supersedesId?: number | null;
  createdBy?: number | null;
  submittedBy?: number | null;
  submittedAt?: string | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  rejectionNote?: string;
  discardedBy?: number | null;
  discardedAt?: string | null;
  discardReason?: string;
  evidenceCount: number;
  rowVersion: number;
}

export interface TpPriceQuoteDTO {
  policyId: number;
  intercompanyRuleId: number;
  method: 'cup' | 'cost_plus' | 'resale_minus' | 'tnmm' | 'profit_split';
  category: string;
  date: string;
  base: string;
  price: string;
  markupPct?: string | null;
  priceLow?: string | null;
  priceHigh?: string | null;
  basisNote?: string;
  evidenceCount: number;
}

export interface TpPriceRequest {
  intercompanyRuleId: number;
  date: string;
  base: string;
  category?: string;
}

export interface TpSubmitPolicyRequest {
  rowVersion: number;
}

export interface TpSupersedePolicyRequest {
  effectiveFrom: string;
  effectiveTo?: string | null;
  method?: 'cup' | 'cost_plus' | 'resale_minus' | 'tnmm' | 'profit_split';
  markupPct?: string | null;
  rangeLow?: string | null;
  rangeHigh?: string | null;
  basisNote?: string | null;
  rowVersion: number;
}

export interface TpUpdatePolicyRequest {
  category?: string | null;
  method?: 'cup' | 'cost_plus' | 'resale_minus' | 'tnmm' | 'profit_split';
  markupPct?: string | null;
  rangeLow?: string | null;
  rangeHigh?: string | null;
  basisNote?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface TraceRecallRequest {
  maxDepth?: number;
}

export interface TradeAgreementDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  customerId?: number | null;
  segmentCode?: string;
  currency: string;
  status: string;
  startsOn: string;
  endsOn?: string | null;
  ownerPositionId?: number | null;
  note?: string;
  approvalRequestId?: number | null;
  activatedAt?: string | null;
  activatedBy?: number | null;
  lines: TradeAgreementLineDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface TradeAgreementLineDTO {
  id: number;
  lineNo: number;
  itemId?: number | null;
  categoryCode?: string;
  minQty: string;
  kind: string;
  value: string;
  priority: number;
}

export interface TradeAgreementLineInput {
  lineNo: number;
  itemId?: number | null;
  categoryCode?: string;
  minQty?: string;
  kind: string;
  value: string;
  priority?: number;
}

export interface TradeAgreementRequest {
  companyId: number;
  code: string;
  name: string;
  customerId?: number | null;
  segmentCode?: string;
  currency: string;
  startsOn: string;
  endsOn?: string;
  ownerPositionId?: number | null;
  note?: string;
  rowVersion?: number;
}

export interface TransferEventDTO {
  id: number;
  transferId: number;
  fromStatus?: string;
  toStatus: string;
  reasonCode?: string;
  note?: string;
  actorId?: number | null;
  actorName?: string;
  occurredAt: string;
}

export interface TransferInstructionDTO {
  id: number;
  transferNo: string;
  isDraftNumber: boolean;
  companyId: number;
  status: 'draft' | 'submitted' | 'approved' | 'screening_hold' | 'released' | 'confirmed' | 'rejected' | 'cancelled' | 'recall_requested' | 'recalled' | 'returned';
  fromBankAccountId: number;
  fromBankAccount?: string;
  toBankAccountId: number;
  toBankAccount?: string;
  destinationKind: string;
  destinationWalletId?: number | null;
  destinationLedgerAccountId?: number | null;
  beneficiaryId?: number | null;
  beneficiaryCode?: string;
  beneficiaryName?: string;
  mandateId?: number | null;
  mandateCode?: string;
  mandateName?: string;
  purposeCode?: string;
  sendDate: string;
  receiptDate: string;
  fromAmount: string;
  fromCurrency: string;
  toAmount: string;
  toCurrency: string;
  fxRate: string;
  fxRateAsOf?: string | null;
  fxDerivation: string;
  baseSendAmount?: string | null;
  baseCurrency?: string;
  inTransitAccountId?: number | null;
  fxAccountId?: number | null;
  sendEntryId?: number | null;
  receiptEntryId?: number | null;
  reversalEntryId?: number | null;
  journalCurrency: string;
  bankReference?: string;
  reference?: string;
  memo?: string;
  submittedAt?: string | null;
  submittedBy?: number | null;
  submittedByName?: string;
  approvedAt?: string | null;
  approvedBy?: number | null;
  approvedByName?: string;
  releasedAt?: string | null;
  releasedBy?: number | null;
  releasedByName?: string;
  confirmedAt?: string | null;
  confirmedBy?: number | null;
  confirmedByName?: string;
  exceptionReasonCode?: string;
  exceptionNote?: string;
  signatures: TransferSignatureDTO[];
  signaturesRequired: number;
  signaturesSatisfied: boolean;
  signingRule?: string;
  signatureShortfall?: string;
  instructionFingerprint?: string;
  counterpartyCompanyId?: number | null;
  icInstrumentId?: number | null;
  icCounterpartyEntryId?: number | null;
  confirmDebitStatementLineId?: number | null;
  confirmCreditStatementLineId?: number | null;
  approvalRequestId?: number | null;
  screeningStatus?: string;
  screeningHits?: CashTransferScreeningHitDTO[];
  screeningHeldBy?: number | null;
  screeningClearedBy?: number | null;
  screeningClearedAt?: string | null;
  screeningClearReason?: string;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface TransferPMSFolioRequest {
  windowNo?: number;
  targetFolioId: number;
  targetWindowNo?: number;
  amount?: string;
  businessDate?: string;
  reference?: string;
}

export interface TransferPOSCheckRequest {
  kind: 'table_move' | 'line_move' | 'merge' | 'split';
  toTableId?: number | null;
  toCheckId?: number | null;
  lineIds?: number[];
  reasonCode?: string;
  note?: string;
}

export interface TransferRequest {
  location?: string | null;
  custodian?: string | null;
  branchId?: number | null;
  costCenterId?: number | null;
  reason: string;
  rowVersion: number;
}

export interface TransferSignatureDTO {
  id: number;
  transferId: number;
  mandateId: number;
  signatoryId?: number | null;
  signedBy?: number | null;
  personName?: string;
  panel: string;
  signedAt: string;
  note?: string;
}

export interface TransitionQualificationRequest {
  toStatus: string;
  reason?: string;
  assessmentScore?: string;
  assessmentNote?: string;
  validUntil?: string;
  criticality?: string;
}

export interface TransitionRequest {
  reason: string;
}

export interface TravelAdvanceDTO {
  id: number;
  companyId: number;
  employeeId: number;
  employeeName?: string;
  travelRequestId?: number | null;
  advanceNo?: string;
  requestedOn: string;
  currency: string;
  amount: string;
  settledAmount: string;
  outstanding: string;
  advanceAccountId: number;
  apInvoiceId?: number | null;
  status: string;
  approvalRequestId?: number | null;
  dueBy?: string;
  closedAt?: string;
  note?: string;
  rowVersion: number;
  payrollRecovery: boolean;
  payrollRunId?: number | null;
  createdBy?: number | null;
  employeeUserId?: number | null;
}

export interface TravelRequestDTO {
  id: number;
  companyId: number;
  employeeId: number;
  employeeName?: string;
  requestNo?: string;
  purpose: string;
  destination: string;
  departOn: string;
  returnOn: string;
  currency: string;
  estimatedCost: string;
  costCenterId?: number | null;
  projectId?: number | null;
  status: string;
  approvalRequestId?: number | null;
  approvedAt?: string;
  closedAt?: string;
  note?: string;
  rowVersion: number;
}

export interface TreasuryAccountProfileDTO {
  id: number;
  bankAccountId: number;
  bankAccountCode: string;
  bankAccountName: string;
  companyId: number;
  currency: string;
  accountType: 'operating' | 'collection' | 'disbursement' | 'payroll' | 'escrow' | 'pool_header' | 'deposit';
  overdraftLimit: string;
  minimumBalance: string;
  pendingMinimumChangeId?: number | null;
  blockedAmount: string;
  blockedNote?: string;
  clearingPolicy: 'immediate' | 'next_day' | 'value_date' | 'manual';
  poolRole: 'none' | 'header' | 'participant';
  poolHeaderAccountId?: number | null;
  poolHeaderName?: string;
  maturityDate?: string;
  restrictionNote?: string;
  connectorKey: string;
  rowVersion: number;
}

export interface TreasuryBasisDeltaDTO {
  basis: 'ledger' | 'bank_booked' | 'value_dated' | 'available';
  left?: string | null;
  right?: string | null;
  delta?: string | null;
  changed: boolean;
  leftSource?: string;
  rightSource?: string;
}

export interface TreasuryBasisNoteDTO {
  basis: 'ledger' | 'bank_booked' | 'value_dated' | 'available';
  amount?: string | null;
  baseAmount: string;
  source: string;
  available: boolean;
}

export interface TreasuryDebtChangeDecisionRequest {
  note?: string;
  rowVersion: number;
}

export interface TreasuryDebtChangeListQuery {
  cursor?: string;
  limit?: number;
  facilityId?: number;
  companyId?: number;
  status?: 'pending' | 'approved' | 'rejected';
}

export interface TreasuryDebtChangeRequestDTO {
  id: number;
  facilityId: number;
  facilityCode?: string;
  covenantId?: number | null;
  covenantCode?: string;
  kind: 'limit_increase' | 'covenant_add' | 'covenant_amend';
  status: 'pending' | 'approved' | 'rejected';
  proposed: Record<string, string>;
  current: Record<string, string>;
  reason: string;
  requestedBy?: number | null;
  requestedAt: string;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string;
  rowVersion: number;
}

export interface TreasuryDebtCovenantAmendRequest {
  name?: string | null;
  metricKey?: string | null;
  operator?: string | null;
  threshold?: string | null;
  testFrequency?: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'at_maturity';
  graceDays?: number | null;
  description?: string | null;
  isActive?: boolean | null;
  reason: string;
  rowVersion: number;
}

export interface TreasuryDebtCovenantTestRequest {
  testDate?: string;
  actualValue?: string;
  outcome?: 'pending' | 'pass' | 'fail' | 'waived' | 'not_tested';
  note?: string;
}

export interface TreasuryDebtDrawdownConfirmRequest {
  statementLineId: number;
  rowVersion: number;
}

export interface TreasuryDebtFacilityActivateRequest {
  note?: string;
  rowVersion: number;
}

export interface TreasuryDebtFacilityCloseRequest {
  reason?: string;
  rowVersion: number;
}

export interface TreasuryDebtFacilityRejectRequest {
  note: string;
  rowVersion: number;
}

export interface TreasuryDebtFacilityRolloverRequest {
  code: string;
  name?: string;
  startDate?: string;
  maturityDate: string;
  facilityLimit?: string;
  rateKind?: 'fixed' | 'floating';
  baseRateCode?: string;
  fixedRate?: string;
  marginRate?: string;
  reason: string;
  rowVersion: number;
}

export interface TreasuryDebtFacilitySubmitRequest {
  note?: string;
  rowVersion: number;
}

export interface TreasuryDebtInterestExpenseLineDTO {
  facilityId: number;
  facilityCode: string;
  facilityName: string;
  facilityKind: 'term_loan' | 'revolving' | 'overdraft' | 'guarantee' | 'demand_loan' | 'lease_liability' | 'bond' | 'intercompany_loan';
  lenderName: string;
  currency: string;
  accruedInterest: string;
  expensedAtRepayment: string;
  interestExpense: string;
  fees: string;
  paidInterest: string;
  averagePrincipal: string;
  days: number;
  effectiveRate?: string;
}

export interface TreasuryDebtInterestExpenseQuery {
  companyId: number;
  from: string;
  to: string;
}

export interface TreasuryDebtInterestExpenseReportDTO {
  companyId: number;
  from: string;
  to: string;
  lines: TreasuryDebtInterestExpenseLineDTO[];
  generatedAt: string;
}

export interface TreasuryDebtLimitChangeRequest {
  facilityLimit: string;
  reason: string;
  rowVersion: number;
}

export interface TreasuryDebtMovementDecisionRequest {
  note?: string;
  rowVersion: number;
}

export interface TreasuryDebtSecurityDTO {
  id: number;
  facilityId: number;
  bankAccountId?: number | null;
  bankAccount?: string;
  description: string;
  status: 'pledged' | 'released';
  priorLiquidityClass?: string;
  pledgedBy?: number | null;
  pledgedAt: string;
  releasedBy?: number | null;
  releasedAt?: string | null;
  releaseReason?: string;
  rowVersion: number;
}

export interface TreasuryDebtSecurityReleaseRequest {
  reason: string;
  rowVersion: number;
}

export interface TreasuryDebtSecurityRequest {
  bankAccountId?: number | null;
  description: string;
}

export interface TreasuryForecastClassTotalDTO {
  class: 'open_item' | 'commitment' | 'calendar' | 'contract' | 'modelled';
  items: number;
  inflows: string;
  outflows: string;
}

export interface TreasuryForecastDegradedInputDTO {
  name: string;
  reason: string;
}

export interface TreasuryForecastDriverDTO {
  name: string;
  value: string;
}

export interface TreasuryForecastOverrideDTO {
  id: number;
  companyId: number;
  runId: number;
  itemId?: number | null;
  source: string;
  sourceClass: 'open_item' | 'commitment' | 'calendar' | 'contract' | 'modelled';
  direction: string;
  documentType?: string;
  documentId?: number | null;
  reference?: string;
  description?: string;
  dueDate: string;
  currency: string;
  originalAmount: string;
  overrideAmount: string;
  reason: string;
  expiresOn: string;
  status: 'active' | 'expired' | 'superseded';
  overriddenBy?: number | null;
  overriddenByName?: string;
  overriddenAt: string;
  expiredAt?: string | null;
  appliedInRun: boolean;
  rowVersion: number;
}

export interface TreasuryForecastOverrideRequest {
  amount: string;
  reason: string;
  expiresOn: string;
}

export interface TreasuryForecastOverrideTotalDTO {
  items: number;
  inflows: string;
  outflows: string;
}

export interface TreasuryForecastRunOverridesDTO {
  runId: number;
  appliedCount: number;
  overrides: TreasuryForecastOverrideDTO[];
}

export interface TreasuryForecastSeriesDTO {
  runId: number;
  companyId: number;
  horizonKind: 'operational' | 'strategic';
  method: string;
  granularity: string;
  asOfDate: string;
  publishedAt?: string | null;
  baseCurrency: string;
  minimumBalance: string;
  degraded: boolean;
  degradedInputs: TreasuryForecastDegradedInputDTO[];
  points: TreasuryForecastSeriesPointDTO[];
}

export interface TreasuryForecastSeriesPointDTO {
  seqNo: number;
  granularity: 'day' | 'week' | 'month';
  fromDate: string;
  toDate: string;
  opening: string;
  inflows: string;
  outflows: string;
  net: string;
  closing: string;
  belowMinimum: boolean;
  bandLow: string;
  bandHigh: string;
  classes: TreasuryForecastClassTotalDTO[];
  overrides: TreasuryForecastOverrideTotalDTO;
}

export interface TreasuryFundingActionDTO {
  rank: number;
  kind: 'overdraft' | 'committed_facility';
  reference: string;
  name: string;
  currency: string;
  capacity: string;
  coversAmount: string;
  costPct?: string | null;
  costBasis: string;
  settleDays: number;
  availableOn: string;
  inTime: boolean;
}

export interface TreasuryGroupPositionCurrencyDTO {
  currency: string;
  holders: number;
  ledgerBalance: string;
  bankBookedBalance: string;
  valueDatedBalance: string;
  availableBalance: string;
  baseLedger: string;
  baseBankBooked: string;
  baseValueDated: string;
  baseAvailable: string;
  rate: TreasuryRateProvenanceDTO;
}

export interface TreasuryGroupPositionDTO {
  asOf: string;
  reportingCurrency: string;
  source: string;
  computedAt: string;
  perimeterDeclared: boolean;
  perimeterId?: number | null;
  perimeterCode?: string;
  perimeterName?: string;
  rateToleranceDays: number;
  translationStale: boolean;
  watermark?: TreasuryPositionWatermarkDTO | null;
  totalLedger: string;
  totalBankBooked: string;
  totalValueDated: string;
  totalAvailable: string;
  sources: TreasuryPositionSourceDTO[];
  entities: TreasuryGroupPositionEntityDTO[];
  outsidePerimeter: TreasuryGroupPositionExcludedDTO[];
  unpublished: TreasuryGroupPositionExcludedDTO[];
  byCurrency: TreasuryGroupPositionCurrencyDTO[];
  rates: TreasuryRateProvenanceDTO[];
  liquidity?: TreasuryLiquiditySummaryDTO | null;
  inTransit: TreasuryLiquidityInTransitDTO[];
  inTransitTotal: string;
  notes: string[];
}

export interface TreasuryGroupPositionEntityDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  companyCurrency: string;
  runId?: number | null;
  version?: number | null;
  contentHash?: string;
  publishedAt?: string | null;
  consolidationMethod?: string;
  ownershipPct: string;
  totalLedger: string;
  totalBankBooked: string;
  totalValueDated: string;
  totalAvailable: string;
  groupLedger: string;
  groupBankBooked: string;
  groupValueDated: string;
  groupAvailable: string;
  sources: TreasuryPositionSourceDTO[];
  lines: TreasuryGroupPositionLineDTO[];
  rates: TreasuryRateProvenanceDTO[];
  liquidity?: TreasuryLiquiditySummaryDTO | null;
}

export interface TreasuryGroupPositionExcludedDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  reason: string;
  detail: string;
}

export interface TreasuryGroupPositionLineDTO {
  id: number;
  runId: number;
  holderKind: 'bank_account' | 'wallet';
  holderId: number;
  code: string;
  name: string;
  accountKind?: string;
  accountType?: string;
  currency: string;
  glAccountId?: number | null;
  glAccountCode?: string;
  ledgerBalance: string;
  bankBookedBalance?: string | null;
  valueDatedBalance?: string | null;
  availableBalance?: string | null;
  bases: TreasuryBasisNoteDTO[];
  unpresentedTotal: string;
  unrecordedTotal: string;
  unclearedTotal: string;
  overdraftLimit: string;
  blockedAmount: string;
  observationId?: number | null;
  statementImportId?: number | null;
  baseLedger: string;
  baseBankBooked: string;
  baseValueDated: string;
  baseAvailable: string;
  fxRate: string;
  fxRateAsOf?: string | null;
  fxDerivation: string;
  sortOrder: number;
  rate: TreasuryRateProvenanceDTO;
  functionalCurrency: string;
  functionalLedger: string;
  functionalBankBooked?: string | null;
  functionalValueDated?: string | null;
  functionalAvailable?: string | null;
  functionalRate: TreasuryRateProvenanceDTO;
}

export interface TreasuryGroupPositionQuery {
  perimeterId?: number;
  companyIds?: string;
  reportingCurrency?: string;
  asOf?: string;
  source?: string;
}

export interface TreasuryIdleCashLineDTO {
  kind: string;
  sourceId: number;
  code: string;
  name: string;
  currency: string;
  status: string;
  balance: string;
  commitments: string;
  recoverable: string;
  baseRecoverable: string;
  restrictedReason?: string;
  idleDays: number;
  inactivityPeriodDays: number;
  lastMovementOn?: string | null;
  proposal: string;
}

export interface TreasuryIdleCashQuery {
  companyId: number;
  minIdleDays?: number;
}

export interface TreasuryIdleCashReportDTO {
  companyId: number;
  baseCurrency: string;
  asOf: string;
  lines: TreasuryIdleCashLineDTO[];
  totalRecoverableBase: string;
  bankAccounts: number;
  wallets: number;
}

export interface TreasuryKPIDTO {
  code: string;
  key: string;
  unit: string;
  value: string | null;
  currency?: string | null;
  sampleSize: number;
  basis: string;
  cycle: string;
  drill: TreasuryKPIDrillDTO;
}

export interface TreasuryKPIDrillDTO {
  path: string;
  query: string;
  exact: boolean;
}

export interface TreasuryKPIQuery {
  companyId: number;
  windowDays?: number;
}

export interface TreasuryKPIReportDTO {
  companyId: number;
  baseCurrency: string;
  asOf: string;
  windowFrom: string;
  windowTo: string;
  cycle: string;
  generatedAt: string;
  kpis: TreasuryKPIDTO[];
}

export interface TreasuryLiquidityDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  asOf: string;
  reportingCurrency: string;
  computedAt: string;
  rateToleranceDays: number;
  accessibleCash: string;
  deductions: TreasuryLiquidityKindTotalDTO[];
  totalDeductions: string;
  netCash: string;
  undrawnCommittedFacilities: string;
  availableLiquidity: string;
  overdraftHeadroom: string;
  inTransitTotal: string;
  thresholdBreached: boolean;
  lines: TreasuryLiquidityLineDTO[];
  facilities: TreasuryLiquidityFacilityDTO[];
  inTransit: TreasuryLiquidityInTransitDTO[];
  rates: TreasuryRateProvenanceDTO[];
  unavailableHolders: number;
  notes: string[];
}

export interface TreasuryLiquidityDeductionDTO {
  kind: 'restricted' | 'pledged' | 'escrowed' | 'in_transit' | 'blocked' | 'minimum';
  amount: string;
  baseAmount: string;
  currency: string;
  reason: string;
}

export interface TreasuryLiquidityFacilityDTO {
  facilityId: number;
  code: string;
  name: string;
  kind: string;
  currency: string;
  limit: string;
  drawn: string;
  inTransit: string;
  awaitingApproval: string;
  undrawn: string;
  undrawnBase: string;
  rate: TreasuryRateProvenanceDTO;
}

export interface TreasuryLiquidityGapCurrencyDTO {
  currency: string;
  declaredMinimum: string;
  minimumSource: string;
  availableOpening: string;
  beforeMitigations: TreasuryLiquidityGapOutcomeDTO;
  afterMitigations: TreasuryLiquidityGapOutcomeDTO;
  mitigations: TreasuryLiquidityMitigationDTO[];
  days: TreasuryLiquidityGapDayDTO[];
}

export interface TreasuryLiquidityGapDayDTO {
  date: string;
  liquidity: string;
  gap: string;
  liquidityAfter: string;
  gapAfter: string;
}

export interface TreasuryLiquidityGapOutcomeDTO {
  breached: boolean;
  firstBreachOn?: string | null;
  firstBreachShortfall: string;
  deepestOn?: string | null;
  deepestShortfall: string;
  minimumLiquidity: string;
  minimumLiquidityOn: string;
}

export interface TreasuryLiquidityInTransitDTO {
  kind: string;
  refId: number;
  refNo: string;
  status: string;
  currency: string;
  amount: string;
  baseAmount: string;
  fromBankAccountId?: number | null;
  toBankAccountId?: number | null;
  ageDays: number;
  note?: string;
}

export interface TreasuryLiquidityKindTotalDTO {
  kind: 'restricted' | 'pledged' | 'escrowed' | 'in_transit' | 'blocked' | 'minimum';
  amount: string;
  holders: number;
}

export interface TreasuryLiquidityLineDTO {
  holderKind: 'bank_account' | 'wallet';
  holderId: number;
  code: string;
  name: string;
  currency: string;
  accountType?: string;
  liquidityClass: string;
  available: boolean;
  availableSource: string;
  gross?: string | null;
  grossBase: string;
  deductions: TreasuryLiquidityDeductionDTO[];
  net?: string | null;
  netBase: string;
  overdraftLimit: string;
  overdraftDrawn: string;
  overdraftHeadroom: string;
  overdraftHeadroomBase: string;
  rate: TreasuryRateProvenanceDTO;
}

export interface TreasuryLiquidityMitigationDTO {
  kind: 'overdraft' | 'committed_facility';
  reference: string;
  name: string;
  currency: string;
  amount: string;
  availableOn: string;
  availableUntil?: string | null;
  settleDays: number;
  costPct?: string | null;
  costBasis: string;
}

export interface TreasuryLiquidityQuery {
  companyId: number;
  asOf?: string;
  reportingCurrency?: string;
}

export interface TreasuryLiquiditySummaryDTO {
  accessibleCash: string;
  deductions: TreasuryLiquidityKindTotalDTO[];
  totalDeductions: string;
  netCash: string;
  undrawnCommittedFacilities: string;
  availableLiquidity: string;
  overdraftHeadroom: string;
  inTransitTotal: string;
  thresholdBreached: boolean;
}

export interface TreasuryMandateApproveRequest {
  note?: string;
  rowVersion: number;
}

export interface TreasuryMinimumBalanceChangeDTO {
  id: number;
  companyId: number;
  currency: string;
  bankAccountId?: number | null;
  previousAmount: string;
  proposedAmount: string;
  direction: 'raise' | 'lower';
  status: 'pending' | 'applied' | 'rejected' | 'cancelled';
  reason: string;
  approvalRequestId?: number | null;
  requestedBy: number;
  requestedByName?: string;
  requestedAt: string;
  decidedBy?: number | null;
  decidedByName?: string;
  decidedAt?: string | null;
  decisionNote?: string;
  rowVersion: number;
}

export interface TreasuryMinimumBalanceChangeRequest {
  companyId: number;
  currency: string;
  amount: string;
  reason: string;
}

export interface TreasuryMinimumBalanceDTO {
  companyId: number;
  currency: string;
  amount: string;
  source: string;
  updatedBy?: number | null;
  pending?: TreasuryMinimumBalanceChangeDTO | null;
}

export interface TreasuryMinimumBalanceListDTO {
  companyId: number;
  items: TreasuryMinimumBalanceDTO[];
}

export interface TreasuryMinimumBalanceListQuery {
  companyId: number;
}

export interface TreasuryMinimumBalanceWithdrawRequest {
  note: string;
}

export interface TreasuryPositionDetailDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  companyName?: string;
  positionDate: string;
  version: number;
  supersedesRunId?: number | null;
  supersededByRunId?: number | null;
  isCurrent: boolean;
  status: 'draft' | 'published' | 'superseded';
  baseCurrency: string;
  companyCurrency: string;
  calcVersion: number;
  totalLedger: string;
  totalBankBooked: string;
  totalValueDated: string;
  totalAvailable: string;
  contentHash: string;
  watermark: TreasuryPositionWatermarkDTO;
  correctionReason?: string;
  correctionReasonCode?: string;
  origin: 'scheduled' | 'manual';
  publishedAt?: string | null;
  publishedBy?: number | null;
  publishedByName?: string;
  published: boolean;
  alreadyPublished?: boolean;
  sources: TreasuryPositionSourceDTO[];
  rowVersion: number;
  lines: TreasuryPositionLineDTO[];
  totals: TreasuryPositionTotalDTO[];
}

export interface TreasuryPositionDiffDTO {
  left: TreasuryPositionRunDTO;
  right: TreasuryPositionRunDTO;
  totals: TreasuryBasisDeltaDTO[];
  lines: TreasuryPositionLineDiffDTO[];
  changedLines: number;
  identical: boolean;
}

export interface TreasuryPositionLineDTO {
  id: number;
  runId: number;
  holderKind: 'bank_account' | 'wallet';
  holderId: number;
  code: string;
  name: string;
  accountKind?: string;
  accountType?: string;
  currency: string;
  glAccountId?: number | null;
  glAccountCode?: string;
  ledgerBalance: string;
  bankBookedBalance?: string | null;
  valueDatedBalance?: string | null;
  availableBalance?: string | null;
  bases: TreasuryBasisNoteDTO[];
  unpresentedTotal: string;
  unrecordedTotal: string;
  unclearedTotal: string;
  overdraftLimit: string;
  blockedAmount: string;
  observationId?: number | null;
  statementImportId?: number | null;
  baseLedger: string;
  baseBankBooked: string;
  baseValueDated: string;
  baseAvailable: string;
  fxRate: string;
  fxRateAsOf?: string | null;
  fxDerivation: string;
  sortOrder: number;
}

export interface TreasuryPositionLineDiffDTO {
  holderKind: 'bank_account' | 'wallet';
  holderId: number;
  code: string;
  name: string;
  currency: string;
  presence: string;
  bases: TreasuryBasisDeltaDTO[];
  changed: boolean;
}

export interface TreasuryPositionMismatchDTO {
  holderKind: 'bank_account' | 'wallet';
  holderId: number;
  code: string;
  name: string;
  basis: 'ledger' | 'bank_booked' | 'value_dated' | 'available';
  published?: string | null;
  recomputed?: string | null;
  field?: string;
  note: string;
}

export interface TreasuryPositionRunDTO {
  id: number;
  companyId: number;
  companyCode?: string;
  companyName?: string;
  positionDate: string;
  version: number;
  supersedesRunId?: number | null;
  supersededByRunId?: number | null;
  isCurrent: boolean;
  status: 'draft' | 'published' | 'superseded';
  baseCurrency: string;
  companyCurrency: string;
  calcVersion: number;
  totalLedger: string;
  totalBankBooked: string;
  totalValueDated: string;
  totalAvailable: string;
  contentHash: string;
  watermark: TreasuryPositionWatermarkDTO;
  correctionReason?: string;
  correctionReasonCode?: string;
  origin: 'scheduled' | 'manual';
  publishedAt?: string | null;
  publishedBy?: number | null;
  publishedByName?: string;
  published: boolean;
  alreadyPublished?: boolean;
  sources: TreasuryPositionSourceDTO[];
  rowVersion: number;
}

export interface TreasuryPositionSourceDTO {
  basis: 'ledger' | 'bank_booked' | 'value_dated' | 'available';
  available: boolean;
  holders: number;
  missing: number;
  note?: string;
}

export interface TreasuryPositionToleranceChangeDTO {
  days: number;
  requestedBy: number;
  requestedAt: string;
  approvalRequestId: number;
}

export interface TreasuryPositionToleranceDTO {
  companyId: number;
  days: number;
  pending?: TreasuryPositionToleranceChangeDTO | null;
  rowVersion: number;
}

export interface TreasuryPositionToleranceRequest {
  companyId: number;
  days: number | null;
  rowVersion: number;
}

export interface TreasuryPositionTotalDTO {
  currency: string;
  holders: number;
  ledgerBalance: string;
  bankBookedBalance: string;
  valueDatedBalance: string;
  availableBalance: string;
  bankBookedHolders: number;
  valueDatedHolders: number;
  availableHolders: number;
  baseLedger: string;
  baseBankBooked: string;
  baseValueDated: string;
  baseAvailable: string;
  fxRate: string;
  fxDerivation: string;
}

export interface TreasuryPositionVerifyDTO {
  runId: number;
  companyId: number;
  positionDate: string;
  version: number;
  matched: boolean;
  code?: string;
  publishedHash: string;
  recomputedHash: string;
  watermark: TreasuryPositionWatermarkDTO;
  mismatches: TreasuryPositionMismatchDTO[];
  explanation?: string;
  verifiedAt: string;
}

export interface TreasuryPositionWatermarkDTO {
  journalLineId: number;
  movementId: number;
  statementLineId: number;
  observationId: number;
  fxRateId: number;
}

export interface TreasuryRateProvenanceDTO {
  fromCurrency: string;
  toCurrency: string;
  rate: string;
  rateType: string;
  rateDate?: string | null;
  source: string;
  inverted: boolean;
  derivation: string;
  ageDays?: number | null;
  toleranceDays: number;
  stale: boolean;
}

export interface TreasuryScenarioCategoryDeltaDTO {
  currency: string;
  category: string;
  baseInflows: string;
  baseOutflows: string;
  candidateInflows: string;
  candidateOutflows: string;
  baseNet: string;
  candidateNet: string;
  delta: string;
}

export interface TreasuryScenarioCompareQuery {
  companyId: number;
  candidateId: number;
  baseId?: number;
  asOf?: string;
}

export interface TreasuryScenarioComparisonDTO {
  companyId: number;
  candidateScenarioId: number;
  candidateCode: string;
  candidateName: string;
  candidateRunId: number;
  candidateAsOfDate: string;
  baseScenarioId?: number | null;
  baseCode?: string;
  baseRunId?: number | null;
  baseAsOfDate?: string | null;
  drivers: TreasuryScenarioDriverDiffDTO[];
  categories: TreasuryScenarioCategoryDeltaDTO[];
  days: TreasuryScenarioDayDeltaDTO[];
  currencies: TreasuryScenarioCurrencyCompareDTO[];
}

export interface TreasuryScenarioCurrencyCompareDTO {
  currency: string;
  declaredMinimum: string;
  baseMinimumLiquidity: string;
  candidateMinimumLiquidity: string;
  baseFirstBreachOn?: string | null;
  candidateFirstBreachOn?: string | null;
  baseDeepestShortfall: string;
  candidateDeepestShortfall: string;
}

export interface TreasuryScenarioDayDeltaDTO {
  date: string;
  currency: string;
  baseNet: string;
  candidateNet: string;
  delta: string;
}

export interface TreasuryScenarioDriverDiffDTO {
  driver: string;
  baseValue: string;
  candidateValue: string;
  candidateOwnsDriver: boolean;
}

export interface TreasuryScenarioPromoteRequest {
  reason: string;
  rowVersion: number;
}

export interface TreasuryScenarioPromotionDTO {
  id: number;
  companyId: number;
  scenarioId: number;
  scenarioCode: string;
  scenarioName: string;
  scenarioRowVersion: number;
  previousType: 'base' | 'upside' | 'downside' | 'stress' | 'what_if';
  supersededScenarioId?: number | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reason: string;
  approvalRequestId?: number | null;
  requestedBy: number;
  requestedByName?: string;
  requestedAt: string;
  decidedBy?: number | null;
  decidedByName?: string;
  decidedAt?: string | null;
  decisionNote?: string;
  scenarioCreatedBy?: number | null;
  scenarioUpdatedBy?: number | null;
  scenarioEditorIds: number[];
  comparison?: TreasuryScenarioComparisonDTO | null;
  rowVersion: number;
}

export interface TreasuryScenarioPromotionWithdrawRequest {
  note: string;
}

export interface TreasurySettingsDTO {
  companyId: number;
  companyCode: string;
  companyName: string;
  fundsInTransitAccountId?: number | null;
  walletFloatAccountId?: number | null;
  walletClearingAccountId?: number | null;
  walletVarianceAccountId?: number | null;
  interestExpenseAccountId?: number | null;
  interestPayableAccountId?: number | null;
  facilityFeeAccountId?: number | null;
  borrowingsAccountId?: number | null;
  borrowingsCurrentAccountId?: number | null;
  accounts?: Record<string, string>;
  duplicateWindowHours: number;
  inTransitAlertDays: number;
  autoPublishPosition: boolean;
  walletCountIntervalDays: number;
  positionRateToleranceDays: number;
  pendingPositionRateTolerance?: TreasuryPositionToleranceChangeDTO | null;
  rowVersion: number;
}

export interface TreasuryShortfallDTO {
  companyId: number;
  companyName?: string;
  runId: number;
  scenarioId?: number | null;
  asOfDate: string;
  currency: string;
  declaredMinimum: string;
  firstBreachOn: string;
  firstBreachShortfall: string;
  leadDays: number;
  deepestOn: string;
  deepestShortfall: string;
  mitigated: boolean;
  residualShortfall: string;
  firstBreachAfterMitigationsOn?: string | null;
  actions: TreasuryFundingActionDTO[];
  unfundedAmount: string;
}

export interface TreasuryShortfallsDTO {
  items: TreasuryShortfallDTO[];
}

export interface TreasuryShortfallsQuery {
  companyId?: number;
  scenarioId?: number;
}

export interface TreasurySweepActivateRequest {
  note?: string;
  rowVersion: number;
}

export interface TreasurySweepCreateRequest {
  code: string;
  name: string;
  kind: string;
  headerAccountId: number;
  currency?: string;
  cutoffTime: string;
  calendarId: number;
  setoffEvidenceAttachmentId?: number | null;
  notes?: string;
  participants?: TreasurySweepParticipantInput[];
}

export interface TreasurySweepDeferralDTO {
  deferredTo: string;
  reasonCode: string;
  reason: string;
  recordedAt?: string | null;
  recordedBy?: number | null;
}

export interface TreasurySweepExecuteRequest {
  simulationId: number;
}

export interface TreasurySweepExecutionDTO {
  outcome: string;
  code?: string;
  message?: string;
  simulation: TreasurySweepSimulationDTO;
  instructions: TreasurySweepInstructionDTO[];
}

export interface TreasurySweepFaultDTO {
  code: string;
  field?: string;
  message: string;
}

export interface TreasurySweepInstructionDTO {
  id: number;
  transferNo: string;
  status: string;
  companyId: number;
  fromBankAccountId: number;
  toBankAccountId: number;
  amount: string;
  currency: string;
  sendDate: string;
  icInstrumentId?: number | null;
}

export interface TreasurySweepListQuery {
  cursor?: string;
  limit?: number;
  status?: string;
  kind?: string;
  companyId?: number;
  q?: string;
}

export interface TreasurySweepMovementDTO {
  id: number;
  bankAccountId: number;
  bankAccount: string;
  companyId: number;
  companyName: string;
  balanceBefore: string;
  direction: string;
  amount: string;
  balanceAfter: string;
  outcome: string;
  reasonCode?: string;
  reason?: string;
  crossEntity: boolean;
  icInstrumentId?: number | null;
  mandateId?: number | null;
  instructionId?: number | null;
  deferredTo?: string | null;
}

export interface TreasurySweepParticipantDTO {
  bankAccountId: number;
  bankAccount: string;
  companyId: number;
  companyName: string;
  currency: string;
  targetAmount?: string | null;
  thresholdAmount?: string | null;
  icInstrumentId?: number | null;
  crossEntity: boolean;
  sortOrder: number;
}

export interface TreasurySweepParticipantInput {
  bankAccountId: number;
  targetAmount?: string | null;
  thresholdAmount?: string | null;
  icInstrumentId?: number | null;
}

export interface TreasurySweepPoolDTO {
  net: string;
  crossEntity: boolean;
  setoffEvidenceAttachmentId?: number | null;
  entities: TreasurySweepPoolEntityDTO[];
}

export interface TreasurySweepPoolEntityDTO {
  companyId: number;
  companyName: string;
  net: string;
}

export interface TreasurySweepSimulateRequest {
  date: string;
}

export interface TreasurySweepSimulationDTO {
  id: number;
  structureId: number;
  structureCode: string;
  structureRowVersion: number;
  kind: string;
  currency: string;
  headerAccountId: number;
  headerAccount: string;
  headerCompanyId: number;
  headerCompanyName: string;
  status: string;
  valueDate: string;
  effectiveDate: string;
  shiftReason?: string;
  cutoffAt: string;
  cutoffFeasible: boolean;
  nextCycleDate: string;
  expiresAt: string;
  expired: boolean;
  executable: boolean;
  headerBalanceBefore: string;
  headerBalanceAfter: string;
  totalToHeader: string;
  totalFromHeader: string;
  movements: TreasurySweepMovementDTO[];
  pool?: TreasurySweepPoolDTO | null;
  deferral?: TreasurySweepDeferralDTO | null;
  simulatedBy?: number | null;
  simulatedAt: string;
  executedBy?: number | null;
  executedAt?: string | null;
  rowVersion: number;
}

export interface TreasurySweepSimulationListQuery {
  cursor?: string;
  limit?: number;
  status?: string;
}

export interface TreasurySweepStructureDTO {
  id: number;
  code: string;
  name: string;
  kind: string;
  status: string;
  companyId: number;
  companyName: string;
  headerAccountId: number;
  headerAccount: string;
  currency: string;
  cutoffTime: string;
  calendarId: number;
  calendarCode: string;
  calendarTimezone: string;
  setoffEvidenceAttachmentId?: number | null;
  crossEntity: boolean;
  notes?: string;
  participants: TreasurySweepParticipantDTO[];
  faults: TreasurySweepFaultDTO[];
  createdBy?: number | null;
  termsEditedBy?: number | null;
  activatedBy?: number | null;
  activatedAt?: string | null;
  activationNote?: string;
  suspendedBy?: number | null;
  suspendedAt?: string | null;
  suspendReason?: string;
  createdAt: string;
  rowVersion: number;
}

export interface TreasurySweepSuspendRequest {
  reason: string;
  rowVersion: number;
}

export interface TreasurySweepUpdateRequest {
  name?: string | null;
  kind?: string | null;
  headerAccountId?: number | null;
  cutoffTime?: string | null;
  calendarId?: number | null;
  notes?: string | null;
  setoffEvidenceAttachmentId?: number | null;
  clearSetoffEvidence?: boolean;
  participants?: TreasurySweepParticipantInput[];
  rowVersion: number;
}

export interface TreasuryWalletAgeBucketDTO {
  label: string;
  fromDays: number;
  toDays?: number | null;
  count: number;
  amount: string;
}

export interface TreasuryWalletAgeingItemDTO {
  transactionId: number;
  txnNo: string;
  walletId: number;
  walletCode: string;
  currency: string;
  businessDate: string;
  categoryCode?: string;
  amount: string;
  ageDays: number;
  bucket: string;
  recordedBy?: number | null;
  recordedName?: string;
}

export interface TreasuryWalletAgeingLineDTO {
  walletId: number;
  walletCode: string;
  walletName: string;
  currency: string;
  ownerUserId?: number | null;
  ownerName?: string;
  count: number;
  total: string;
  oldestAgeDays: number;
  buckets: TreasuryWalletAgeBucketDTO[];
}

export interface TreasuryWalletAgeingQuery {
  companyId?: number;
  walletId?: number;
  asOf?: string;
}

export interface TreasuryWalletAgeingReportDTO {
  asOf: string;
  lines: TreasuryWalletAgeingLineDTO[];
  items: TreasuryWalletAgeingItemDTO[];
  totals: TreasuryWalletAgeingTotalDTO[];
}

export interface TreasuryWalletAgeingTotalDTO {
  currency: string;
  count: number;
  total: string;
  buckets: TreasuryWalletAgeBucketDTO[];
}

export interface TreasuryWalletCashBookDTO {
  id: number;
  code: string;
  name: string;
  isPrimary: boolean;
  recordedBalance: string;
}

export interface TreasuryWalletCountVarianceCauseDTO {
  causeCode: string;
  currency: string;
  count: number;
  netVariance: string;
  absVariance: string;
}

export interface TreasuryWalletCountVarianceItemDTO {
  countId: number;
  countNo: string;
  walletId: number;
  walletCode: string;
  currency: string;
  kind: string;
  countDate: string;
  systemTotal: string;
  countedTotal: string;
  variance: string;
  causeCode: string;
  ownerUserId?: number | null;
  ownerName?: string;
  countedBy?: number | null;
  countedByName?: string;
}

export interface TreasuryWalletCountVarianceQuery {
  companyId?: number;
  walletId?: number;
  from?: string;
  to?: string;
}

export interface TreasuryWalletCountVarianceReportDTO {
  from: string;
  to: string;
  countsPosted: number;
  countsWithVariance: number;
  items: TreasuryWalletCountVarianceItemDTO[];
  byCause: TreasuryWalletCountVarianceCauseDTO[];
  byWallet: TreasuryWalletCountVarianceWalletDTO[];
}

export interface TreasuryWalletCountVarianceWalletDTO {
  walletId: number;
  walletCode: string;
  walletName: string;
  currency: string;
  custodianName?: string;
  countsPosted: number;
  withVariance: number;
  netVariance: string;
  absVariance: string;
  lastCountOn?: string | null;
}

export interface TreasuryWalletDTO {
  id: number;
  companyId: number;
  companyName?: string;
  code: string;
  name: string;
  currency: string;
  kind: 'imprest' | 'fluctuating';
  floatAmount: string;
  glAccountId: number;
  glAccountCode?: string;
  glAccountLabel?: string;
  clearingAccountId: number;
  clearingAccountLabel?: string;
  varianceAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  locationId?: number | null;
  custodianUserId?: number | null;
  custodianEmployeeId?: number | null;
  custodianName?: string;
  custodianSince?: string | null;
  status: 'active' | 'suspended' | 'in_handover' | 'dormant' | 'closing' | 'closed';
  closedAt?: string | null;
  lastCountedOn?: string | null;
  lastReplenishedOn?: string | null;
  countIntervalDays: number;
  countOverdue: boolean;
  floatBalance: string;
  clearingBalance: string;
  openCaptureCount: number;
  shortfall: string;
  deputyUserId?: number | null;
  deputyEmployeeId?: number | null;
  deputyName?: string;
  personalDataMasked: boolean;
  holderPositionId?: number | null;
  composition: 'single' | 'group';
  fundingBankAccountId?: number | null;
  dormancyDays: number;
  lastMovementAt?: string | null;
  statusReason?: string;
  statusSince?: string | null;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface TreasuryWalletDetailDTO {
  id: number;
  companyId: number;
  companyName?: string;
  code: string;
  name: string;
  currency: string;
  kind: 'imprest' | 'fluctuating';
  floatAmount: string;
  glAccountId: number;
  glAccountCode?: string;
  glAccountLabel?: string;
  clearingAccountId: number;
  clearingAccountLabel?: string;
  varianceAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  locationId?: number | null;
  custodianUserId?: number | null;
  custodianEmployeeId?: number | null;
  custodianName?: string;
  custodianSince?: string | null;
  status: 'active' | 'suspended' | 'in_handover' | 'dormant' | 'closing' | 'closed';
  closedAt?: string | null;
  lastCountedOn?: string | null;
  lastReplenishedOn?: string | null;
  countIntervalDays: number;
  countOverdue: boolean;
  floatBalance: string;
  clearingBalance: string;
  openCaptureCount: number;
  shortfall: string;
  deputyUserId?: number | null;
  deputyEmployeeId?: number | null;
  deputyName?: string;
  personalDataMasked: boolean;
  holderPositionId?: number | null;
  composition: 'single' | 'group';
  fundingBankAccountId?: number | null;
  dormancyDays: number;
  lastMovementAt?: string | null;
  statusReason?: string;
  statusSince?: string | null;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
  openCaptureTotal: string;
  openCount?: WalletCountDTO | null;
  openCustodyTransfer?: WalletCustodyTransferDTO | null;
  openReplenishment?: WalletReplenishmentDTO | null;
  cashBooks: TreasuryWalletCashBookDTO[];
  lastReconciliation?: TreasuryWalletReconciliationDTO | null;
  policies: WalletCapturePolicyDTO[];
}

export interface TreasuryWalletExpenditureLineDTO {
  categoryCode: string;
  categoryName?: string;
  costCenterId?: number | null;
  costCenter?: string;
  expenseAccountId?: number | null;
  expenseAccount?: string;
  entries: number;
  recordedAmount: string;
  approvedAmount: string;
  procurementAmount: string | null;
  walletSharePct: string | null;
}

export interface TreasuryWalletExpenditureQuery {
  companyId: number;
  walletId?: number;
  from?: string;
  to?: string;
}

export interface TreasuryWalletExpenditureReportDTO {
  companyId: number;
  baseCurrency: string;
  from: string;
  to: string;
  lines: TreasuryWalletExpenditureLineDTO[];
  totalRecorded: string;
  totalApproved: string;
  procurementComparison: boolean;
}

export interface TreasuryWalletFloatUtilisationLineDTO {
  walletId: number;
  code: string;
  name: string;
  companyId: number;
  currency: string;
  kind: 'imprest' | 'fluctuating';
  status: 'active' | 'suspended' | 'in_handover' | 'dormant' | 'closing' | 'closed';
  custodianName?: string;
  authorisedFloat?: string | null;
  confirmedBalance: string;
  utilisationPct: string | null;
  windowSpend: string;
  avgDailySpend: string;
  daysOfCover: string | null;
  justifiedFloat: string;
  level: string;
  excessAmount: string;
  shortfallAmount: string;
}

export interface TreasuryWalletFloatUtilisationQuery {
  companyId?: number;
  walletId?: number;
  windowDays?: number;
  targetCoverDays?: number;
  tolerancePercent?: string;
}

export interface TreasuryWalletFloatUtilisationReportDTO {
  asOf: string;
  windowDays: number;
  targetCoverDays: number;
  tolerancePercent: string;
  lines: TreasuryWalletFloatUtilisationLineDTO[];
  totals: TreasuryWalletFloatUtilisationTotalDTO[];
}

export interface TreasuryWalletFloatUtilisationTotalDTO {
  currency: string;
  wallets: number;
  authorisedFloat: string;
  confirmedBalance: string;
  windowSpend: string;
  utilisationPct: string | null;
  above: number;
  below: number;
}

export interface TreasuryWalletFundingDTO {
  walletId: number;
  code: string;
  currency: string;
  kind: 'imprest' | 'fluctuating';
  floatAmount: string;
  confirmedBalance: string;
  inTransit: string;
  resultingBalance: string;
  headroom?: string | null;
  derivedAmount?: string | null;
  maxBalance?: string | null;
  replenishmentThreshold?: string | null;
  fundingBankAccountId?: number | null;
  openReplenishment?: WalletReplenishmentDTO | null;
  rowVersion: number;
}

export interface TreasuryWalletFundingTermsRequest {
  maxBalance?: string;
  replenishmentThreshold?: string;
  fundingBankAccountId?: number | null;
  rowVersion: number;
}

export interface TreasuryWalletHandoverAcknowledgeRequest {
  countId?: number | null;
  note?: string;
  decline?: boolean;
  declineReason?: string;
  rowVersion: number;
}

export interface TreasuryWalletIdleBalancesLineDTO {
  walletId: number;
  code: string;
  name: string;
  companyId: number;
  currency: string;
  status: 'active' | 'suspended' | 'in_handover' | 'dormant' | 'closing' | 'closed';
  custodianName?: string;
  confirmedBalance: string;
  commitments: string;
  recoverable: string;
  idleDays: number;
  inactivityPeriodDays: number;
  lastMovementOn?: string | null;
  proposal: string;
}

export interface TreasuryWalletIdleBalancesQuery {
  companyId?: number;
  walletId?: number;
  minIdleDays?: number;
}

export interface TreasuryWalletIdleBalancesReportDTO {
  asOf: string;
  lines: TreasuryWalletIdleBalancesLineDTO[];
  totals: TreasuryWalletIdleTotalDTO[];
}

export interface TreasuryWalletIdleTotalDTO {
  currency: string;
  wallets: number;
  confirmedBalance: string;
  commitments: string;
  recoverable: string;
}

export interface TreasuryWalletLoadsInTransitLineDTO {
  replenishmentId: number;
  requestNo: string;
  walletId: number;
  walletCode: string;
  walletName: string;
  currency: string;
  amount: string;
  approvedOn: string;
  ageDays: number;
  bucket: string;
  transferId?: number | null;
  transferNo?: string;
  transferStatus?: string;
  leftBank: boolean;
}

export interface TreasuryWalletLoadsInTransitQuery {
  companyId?: number;
  walletId?: number;
}

export interface TreasuryWalletLoadsInTransitReportDTO {
  asOf: string;
  lines: TreasuryWalletLoadsInTransitLineDTO[];
  totals: TreasuryWalletLoadsInTransitTotalDTO[];
}

export interface TreasuryWalletLoadsInTransitTotalDTO {
  currency: string;
  count: number;
  total: string;
  leftBank: string;
  buckets: TreasuryWalletAgeBucketDTO[];
}

export interface TreasuryWalletPersonDTO {
  userId?: number | null;
  employeeId?: number | null;
  name: string;
  employeeNo?: string;
  email?: string;
  phone?: string;
}

export interface TreasuryWalletReconcileBookInput {
  cashBookId: number;
  observedBalance: string;
  causeCode?: string;
  ownerUserId?: number | null;
  note?: string;
}

export interface TreasuryWalletReconcileRequest {
  books: TreasuryWalletReconcileBookInput[];
  note?: string;
}

export interface TreasuryWalletReconciliationDTO {
  id: number;
  walletId: number;
  asOf: string;
  status: 'reconciled' | 'unreconciled';
  recordedTotal: string;
  observedTotal: string;
  aggregateDifference: string;
  aggregateAgrees: boolean;
  maskedByAggregate: boolean;
  unreconciledBooks: number;
  note?: string;
  createdAt: string;
  createdBy?: number | null;
  lines: TreasuryWalletReconciliationLineDTO[];
}

export interface TreasuryWalletReconciliationLineDTO {
  cashBookId: number;
  cashBookCode: string;
  cashBookName: string;
  recordedBalance: string;
  observedBalance: string;
  difference: string;
  causeCode?: string;
  ownerUserId?: number | null;
  ownerName?: string;
  note?: string;
}

export interface TreasuryWalletReconciliationListQuery {
  cursor?: string;
  limit?: number;
}

export interface TreasuryWalletRecoverRequest {
  amount?: string;
  reasonCode: string;
  note?: string;
  fundingBankAccountId?: number | null;
  cashBookId?: number | null;
}

export interface TreasuryWalletRecoveryDTO {
  id: number;
  walletId: number;
  amount: string;
  currency: string;
  fundingBankAccountId: number;
  fundingBankAccount?: string;
  cashBookId?: number | null;
  reasonCode: string;
  note?: string;
  notifiedUserId?: number | null;
  walletTransactionId?: number | null;
  entryId?: number | null;
  balanceAfter: string;
  createdAt: string;
  createdBy?: number | null;
}

export interface TreasuryWalletReplenishmentListQuery {
  cursor?: string;
  limit?: number;
  walletId?: number;
  companyId?: number;
  status?: 'draft' | 'proposed' | 'submitted' | 'approved' | 'funded' | 'rejected' | 'failed';
}

export interface TreasuryWalletRevealDTO {
  walletId: number;
  custodian?: TreasuryWalletPersonDTO | null;
  deputy?: TreasuryWalletPersonDTO | null;
  revealedAt: string;
}

export interface TreasuryWalletRevealRequest {
  reason: string;
}

export interface TreasuryWalletSplitFlagDTO {
  id: number;
  companyId: number;
  walletId: number;
  walletCode?: string;
  walletName?: string;
  categoryCode: string;
  categoryName?: string;
  payee?: string;
  currency: string;
  limitAmount: string;
  groupTotal: string;
  excessOverLimit: string;
  memberCount: number;
  windowDays: number;
  windowFrom: string;
  windowTo: string;
  members: TreasuryWalletSplitMemberDTO[];
  comparison: string;
  undecidedMembers: number;
  detectedAt: string;
  detectedBy?: number | null;
  rowVersion: number;
}

export interface TreasuryWalletSplitFlagsQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  walletId?: number;
  txnId?: number;
  undecidedOnly?: boolean;
}

export interface TreasuryWalletSplitMemberDTO {
  txnId: number;
  txnNo: string;
  businessDate: string;
  amount: string;
  status: 'captured' | 'submitted' | 'approved' | 'rejected' | 'posted';
  payee?: string;
  createdBy?: number | null;
}

export interface TreeEAMFunctionalLocationsQuery {
  companyId: number;
}

export interface TriagePMSRoomFaultRequest {
  category?: 'plumbing' | 'electrical' | 'hvac' | 'furniture' | 'appliance' | 'structural' | 'safety' | 'other';
  severity?: 'minor' | 'major' | 'blocking';
  reject?: boolean;
  note?: string;
  rowVersion: number;
}

export interface TrialBalanceQuery {
  companyId: number;
  periodId?: number;
  from?: string;
  asOf?: string;
  basis?: 'entry' | 'posting';
  includeZero?: boolean;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  businessUnitId?: number | null;
  projectId?: number | null;
}

export interface TrialBalanceReport {
  companyId: number;
  currency: string;
  from: string;
  asOf: string;
  basis: 'entry' | 'posting';
  periodId?: number | null;
  rows: TrialBalanceRow[];
  totals: TrialBalanceTotals;
  check: BalanceCheck;
  openingCheck: BalanceCheck;
  movementCheck: BalanceCheck;
  filtered: boolean;
  generatedAt: string;
}

export interface TrialBalanceRow {
  accountId: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  opening: string;
  openingDebit: string;
  openingCredit: string;
  periodDebit: string;
  periodCredit: string;
  closing: string;
  closingDebit: string;
  closingCredit: string;
}

export interface TrialBalanceTotals {
  openingDebit: string;
  openingCredit: string;
  periodDebit: string;
  periodCredit: string;
  closingDebit: string;
  closingCredit: string;
}

export interface Unit {
  id: number;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  rowVersion: number;
  isDeleted: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  sortOrder: number;
  isActive: boolean;
  jurisdictionId?: number | null;
  managerPositionId?: number | null;
  kind: 'branch' | 'department' | 'cost_center' | 'profit_center' | 'business_unit' | 'division';
}

export interface UnitNode {
  id: number;
  kind: 'branch' | 'department' | 'cost_center' | 'profit_center' | 'business_unit' | 'division';
  companyId: number;
  code: string;
  name: string;
  parentId?: number | null;
  sortOrder: number;
  isActive: boolean;
  depth: number;
  children: UnitNode[];
}

export interface UnlockRosterRequest {
  rowVersion: number;
  reasonCode: string;
  note?: string;
}

export interface UnreadCountResponse {
  count: number;
}

export interface UnresolvedDepletionDTO {
  soldItemId: number;
  itemSku?: string;
  itemName?: string;
  warehouseId: number;
  lineCount: number;
  soldQty: string;
  firstSeenOn: string;
  lastSeenOn: string;
  lastReason?: string;
}

export interface UnresolvedDepletionQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
  warehouseId?: number;
  from?: string;
  to?: string;
}

export interface UnrestrictPMSGuestRequest {
  note: string;
}

export interface UpdateAccountProfileRequest {
  rowVersion: number;
  accountType?: 'operating' | 'collection' | 'disbursement' | 'payroll' | 'escrow' | 'pool_header' | 'deposit';
  overdraftLimit?: string | null;
  minimumBalance?: string | null;
  minimumBalanceReason?: string | null;
  blockedAmount?: string | null;
  blockedNote?: string | null;
  clearingPolicy?: 'immediate' | 'next_day' | 'value_date' | 'manual';
  poolRole?: 'none' | 'header' | 'participant';
  poolHeaderAccountId?: number | null;
  maturityDate?: string | null;
  restrictionNote?: string | null;
  connectorKey?: string | null;
}

export interface UpdateAccountRequest {
  name?: string | null;
  code?: string | null;
  type?: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  parentId?: number | null;
  isPostable?: boolean | null;
  currency?: string | null;
  description?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  requiresCostCenter?: boolean | null;
  requiresProfitCenter?: boolean | null;
  requiresBranch?: boolean | null;
  requiresDepartment?: boolean | null;
  requiresProject?: boolean | null;
  rowVersion: number;
}

export interface UpdateAssetBookRequest {
  assetAccountId?: number | null;
  accumulatedDepreciationAccountId?: number | null;
  depreciationExpenseAccountId?: number | null;
  impairmentExpenseAccountId?: number | null;
  impairmentReversalAccountId?: number | null;
  gainLossAccountId?: number | null;
  rowVersion: number;
}

export interface UpdateAssetRequest {
  name?: string | null;
  description?: string | null;
  serialNo?: string | null;
  tagNo?: string | null;
  insurancePolicy?: string | null;
  insuranceExpiry?: string | null;
  warrantyExpiry?: string | null;
  sourceRef?: string | null;
  rowVersion: number;
}

export interface UpdateAssignmentRuleRequest {
  name?: string | null;
  sortOrder?: number | null;
  matchCategory?: string | null;
  matchPriority?: string | null;
  assignToUserId?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateAttendanceRequest {
  hoursWorked?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateBOMRequest {
  name?: string | null;
  notes?: string | null;
  lines?: BOMLineInput[];
  rowVersion: number;
}

export interface UpdateBankAccountRequest {
  name?: string | null;
  bankName?: string | null;
  branchName?: string | null;
  bankId?: number | null;
  accountNumber?: string | null;
  iban?: string | null;
  swift?: string | null;
  liquidityClass?: 'unrestricted' | 'restricted' | 'pledged' | 'escrowed' | 'trapped';
  valueDateToleranceDays?: number | null;
  dormancyDays?: number | null;
  statementStalenessDays?: number | null;
  rowVersion: number;
}

export interface UpdateBeneficiaryRequest {
  name?: string | null;
  bankName?: string | null;
  branchName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  iban?: string | null;
  swift?: string | null;
  currency?: string | null;
  deactivate?: boolean;
  note?: string;
  rowVersion: number;
}

export interface UpdateBinRequest {
  name?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
  zoneId?: number | null;
  storageClass?: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  maxQty?: string | null;
  maxWeightKg?: string | null;
  maxVolumeM3?: string | null;
  binKind?: 'storage' | 'picking' | 'bulk' | 'staging' | 'quarantine';
  pickSequence?: number | null;
  isBlocked?: boolean | null;
  blockedReason?: string | null;
}

export interface UpdateBookRequest {
  name?: string | null;
  convention?: 'full_month' | 'half_year' | 'mid_month' | 'mid_quarter' | 'actual_days';
  description?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCAPARequest {
  title?: string | null;
  description?: string | null;
  severity?: string | null;
  rootCause?: string | null;
  ownerUserId?: number | null;
  dueOn?: string | null;
  effectivenessDueOn?: string | null;
  effectivenessMethod?: string | null;
  status?: string | null;
  cancelReason?: string | null;
  rowVersion: number;
}

export interface UpdateCRMAccountRequest {
  name?: string | null;
  parentId?: number | null;
  segmentId?: number | null;
  ownerUserId?: number | null;
  accountType?: string | null;
  status?: string | null;
  industry?: string | null;
  territory?: string | null;
  website?: string | null;
  employeeCount?: number | null;
  annualRevenue?: string | null;
  annualRevenueCurrency?: string | null;
  companyId?: number | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateCRMAccountSegmentRequest {
  name?: string | null;
  description?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCRMCloseReasonRequest {
  name?: string | null;
  requiresCompetitor?: boolean | null;
  requiresNote?: boolean | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCRMCompetitorRequest {
  name?: string | null;
  notes?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCRMContactRequest {
  jobTitle?: string | null;
  department?: string | null;
  reportsToContactId?: number | null;
  ownerUserId?: number | null;
  status?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateCRMContactRoleRequest {
  name?: string | null;
  isDecisionMaker?: boolean | null;
  isBilling?: boolean | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCRMForecastCategoryRequest {
  name?: string | null;
  sortOrder?: number | null;
  countsInCommit?: boolean | null;
  countsInBestCase?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCandidateRequest {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  rating?: number | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateCardAccountRequest {
  holderEmployeeId?: number | null;
  creditLimit?: string;
  status?: string;
  rowVersion: number;
}

export interface UpdateCaseRequest {
  subject?: string | null;
  description?: string | null;
  category?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  rowVersion: number;
}

export interface UpdateCertificateRequest {
  validTo?: string | null;
  status?: string | null;
  attachmentId?: number | null;
  issuedBy?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateCharacteristicRequest {
  name?: string | null;
  specMin?: string | null;
  specMax?: string | null;
  specTarget?: string | null;
  uomCode?: string | null;
  decimals?: number | null;
  allowedValues?: string[];
  severity?: string | null;
  isMandatory?: boolean | null;
  rowVersion: number;
}

export interface UpdateCheckpointRequest {
  name?: string | null;
  warehouseId?: number | null;
  zoneId?: number | null;
  kind?: 'temperature' | 'humidity' | 'cleaning' | 'receiving' | 'cooking' | 'cooling' | 'visual';
  minValue?: string | null;
  maxValue?: string | null;
  unit?: string | null;
  frequency?: 'per_shift' | 'daily' | 'twice_daily' | 'weekly' | 'on_receipt';
  correctiveAction?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateClassRequest {
  name?: string | null;
  defaultMethod?: 'SL' | 'DB' | 'SYD' | 'UOP';
  defaultUsefulLifeMonths?: number | null;
  defaultSalvagePercent?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCloseCalendarRequest {
  name?: string;
  scope?: string;
  targetWorkingDays?: number | null;
  ownerPositionId?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateCodeRequest {
  name: string | null;
  treatment: 'standard' | 'zero_rated' | 'exempt' | 'reverse_charge' | 'out_of_scope';
  basis: 'percent' | 'per_unit';
  rate: string | null; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  unitCurrency: string | null;
  unitLabel: string | null;
  effectiveTo: string | null;
  inputAccountCode: string | null;
  outputAccountCode: string | null;
  payableAccountCode: string | null;
  rounding: 'line' | 'document';
  roundingMode: 'half_up' | 'half_even';
  isRecoverable: boolean | null;
  description: string | null;
  isActive: boolean | null;
  rowVersion: number;
}

export interface UpdateCompanyRequest {
  legalName?: string | null;
  name?: string | null;
  country?: string | null;
  baseCurrency?: string | null;
  taxRegistrations?: unknown[] | null;
  address?: Record<string, unknown> | null;
  phone?: string | null;
  email?: string | null;
  isActive?: boolean | null;
  openingBalancesSkipped?: boolean | null;
  openingBalancesSkippedReason?: string | null;
  rowVersion: number;
}

export interface UpdateComponentRequest {
  name?: string | null;
  fixedAmount?: string | null;
  percentOfBase?: string | null;
  base?: 'basic_salary' | 'pensionable_wage';
  overtimeBucket?: string | null;
  perUnitRate?: string | null;
  slabBands?: SlabBandDTO[];
  expenseAccountCode?: string | null;
  liabilityAccountCode?: string | null;
  sequence?: number | null;
  description?: string | null;
  isActive?: boolean | null;
  companyId?: number | null;
  payGroupId?: number | null;
  wageClass?: 'basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other';
  poolCode?: string | null;
  settlementBasis?: 'expense' | 'liability_drawdown';
  rowVersion: number;
}

export interface UpdateConsentChannelRequest {
  code?: string | null;
  name?: string | null;
  requiresDoubleOptIn?: boolean | null;
  isActive?: boolean | null;
  sortOrder?: number | null;
  rowVersion: number;
}

export interface UpdateConsentPurposeRequest {
  code?: string | null;
  name?: string | null;
  description?: string | null;
  isEssential?: boolean | null;
  defaultValidityDays?: number | null;
  isActive?: boolean | null;
  sortOrder?: number | null;
  rowVersion: number;
}

export interface UpdateControlRequest {
  isEnabled?: boolean | null;
  tenantSeverity?: string | null;
}

export interface UpdateCoolingOffRequest {
  companyId?: number | null;
  hours: number;
  isActive: boolean;
  rowVersion: number;
}

export interface UpdateCostCycleRequest {
  rowVersion: number;
  name?: string | null;
  method?: string | null;
  journalTypeCode?: string | null;
  maxIterations?: number | null;
  convergenceTolerance?: string | null;
  postAutomatically?: boolean | null;
  isActive?: boolean | null;
  status?: string | null;
}

export interface UpdateCostDriverRequest {
  rowVersion: number;
  name?: string | null;
  uom?: string | null;
  description?: string | null;
  isActive?: boolean | null;
}

export interface UpdateCostPoolRequest {
  rowVersion: number;
  name?: string | null;
  poolKind?: string | null;
  objectType?: string | null;
  objectId?: number | null;
  clearingAccountId?: number | null;
  absorbedAccountId?: number | null;
  isActive?: boolean | null;
}

export interface UpdateCoverageMinimumRequest {
  minOnDuty?: number | null;
  weekdayMask?: string | null;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface UpdateCustomerAssetRequest {
  name?: string | null;
  category?: string | null;
  modelNo?: string | null;
  serialNo?: string | null;
  itemSerialId?: number | null;
  location?: string | null;
  purchasedAt?: string | null;
  warrantyExpiresAt?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateCustomerRequest {
  name?: string | null;
  legalName?: string | null;
  taxGroupId?: number | null;
  priceTier?: string | null;
  currency?: string | null;
  paymentTermsDays?: number | null;
  paymentTermsCode?: string | null;
  creditLimit?: string | null;
  creditLimitCurrency?: string | null;
  overdueBlockDays?: number | null;
  portalAccess?: boolean | null;
  status?: 'active' | 'on_hold' | 'blocked';
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateDSRPolicyRequest {
  responseDays?: number | null;
  extensionDays?: number | null;
  warnDaysBefore?: number | null;
  identityVerificationRequired?: boolean | null;
  statuteRef?: string | null;
  rowVersion: number;
}

export interface UpdateDSRRequest {
  requesterName?: string | null;
  requesterEmail?: string | null;
  requestText?: string | null;
  rowVersion: number;
}

export interface UpdateDivisionFNDRequest {
  enterpriseId?: number | null;
  purpose?: string | null;
  ownerId?: number | null;
  defaultCurrency?: string | null;
  rowVersion: number;
}

export interface UpdateEAMAssetRequest {
  name?: string | null;
  description?: string | null;
  functionalLocationId?: number | null | null;
  parentAssetId?: number | null | null;
  financeAssetId?: number | null | null;
  classCode?: string | null;
  manufacturer?: string | null;
  modelNo?: string | null;
  serialNo?: string | null;
  criticality?: number | null;
  ownership?: 'owned' | 'leased' | 'customer';
  lifecycleState?: 'planned' | 'commissioned' | 'in_service' | 'standby' | 'decommissioned';
  warehouseId?: number | null | null;
  costCenterId?: number | null | null;
  rowVersion: number;
}

export interface UpdateEAMComplianceRequirementRequest {
  name?: string | null;
  authority?: string | null;
  jurisdictionId?: number | null | null;
  taskListId?: number | null | null;
  intervalMonths?: number | null;
  blocksOperation?: boolean | null;
  warnDays?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateEAMFunctionalLocationRequest {
  name?: string | null;
  description?: string | null;
  parentId?: number | null | null;
  locationId?: number | null | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateEAMPMPlanRequest {
  strategyId?: number | null | null;
  name?: string | null;
  defaultTaskListId?: number | null | null;
  intervalDays?: number | null | null;
  nextDueOn?: string | null;
  meterId?: number | null | null;
  intervalMeterUnits?: string | null;
  leadDays?: number | null | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateEAMSettingsRequest {
  maintenanceExpenseAccountId?: number | null | null;
  labourClearingAccountId?: number | null | null;
  defaultWarehouseId?: number | null | null;
  pmGenerationLeadDays?: number | null;
  enforceComplianceBlock?: boolean | null;
  rowVersion: number;
}

export interface UpdateEAMTaskListRequest {
  name?: string | null;
  description?: string | null;
  estimatedHours?: string | null;
  requiresPermit?: boolean | null;
  permitKind?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateECLSegmentRequest {
  name?: string | null;
  matchKind?: string | null;
  matchValue?: string | null;
  priority?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateECORequest {
  title?: string | null;
  description?: string | null;
  reasonKind?: string | null;
  urgency?: string | null;
  effectiveFrom?: string | null;
  effectiveFromOrderId?: number | null;
  estimatedCostImpact?: string | null;
  ownerUserId?: number | null;
  rowVersion: number;
}

export interface UpdateEmployeeRequest {
  departmentId?: number | null;
  branchId?: number | null;
  costCenterId?: number | null;
  managerId?: number | null;
  clearManager?: boolean;
  userId?: number | null;
  clearUserId?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  preferredName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  personalEmail?: string | null;
  employmentType?: string | null;
  employmentStatus?: string | null;
  basicSalary?: string | null;
  currency?: string | null;
  pensionable?: boolean | null;
  nationality?: string | null;
  isExpatriate?: boolean | null;
  workPermitNo?: string | null;
  workPermitExpiry?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateEnterpriseRequest {
  legalName?: string | null;
  brandName?: string | null;
  primaryCountry?: string | null;
  defaultCurrency?: string | null;
  defaultTimezone?: string | null;
  defaultLanguage?: string | null;
  dateFormat?: string | null;
  numberFormat?: string | null;
  weekStart?: string | null;
  fiscalAnchorMonth?: number | null;
  residencyRegion?: string | null;
  effectiveFrom: string;
  changeSummary?: string;
  rowVersion: number;
}

export interface UpdateEntryRequest {
  entryDate?: string;
  postingDate?: string;
  currency?: string;
  memo?: string | null;
  lines?: EntryLineInput[];
  documentDate?: string;
  reversalDate?: string;
  rateType?: string;
  rate?: string;
  clearRate?: boolean;
  rowVersion: number;
}

export interface UpdateExpenseCategoryRequest {
  name?: string;
  expenseAccountId?: number;
  taxGroupId?: number | null;
  defaultTaxCode?: string | null;
  requiresReceiptAbove?: string;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateExpenseClaimRequest {
  title?: string;
  claimDate?: string;
  periodFrom?: string | null;
  periodTo?: string | null;
  note?: string | null;
  settlementMethod?: string | null;
  rowVersion: number;
}

export interface UpdateFacilityRequest {
  rowVersion: number;
  name?: string | null;
  lenderName?: string | null;
  lenderVendorId?: number | null;
  bankId?: number | null;
  securityDescription?: string | null;
  agreementRef?: string | null;
  attachmentId?: number | null;
  facilityLimit?: string | null;
  commitmentFeeRate?: string | null;
  maturityDate?: string | null;
  fixedRate?: string | null;
  marginRate?: string | null;
  rateFloor?: string | null;
  rateCap?: string | null;
  principalAccountId?: number | null;
  principalCurrentAccountId?: number | null;
  interestExpenseAccountId?: number | null;
  interestPayableAccountId?: number | null;
  feeExpenseAccountId?: number | null;
  costCenterId?: number | null;
  notes?: string | null;
}

export interface UpdateGroupRequest {
  name?: string | null;
  description?: string | null;
  isActive?: boolean | null;
  intercoTolerance?: string | null;
  groupCoaId?: number | null;
  rowVersion: number;
}

export interface UpdateHRSkillRequest {
  name?: string | null;
  category?: string | null;
  description?: string | null;
  expires?: boolean | null;
  isStatutory?: boolean | null;
  rowVersion: number;
}

export interface UpdateICReconPairRequest {
  ownerPositionId?: number | null;
  tolerance?: string;
  toleranceCurrency?: string;
  maxAgeDays?: number | null;
  blocksClose?: boolean | null;
  isActive?: boolean | null;
  requiresTp?: boolean | null;
  rowVersion: number;
}

export interface UpdateInspectionPlanRequest {
  name?: string | null;
  description?: string | null;
  samplingPlanId?: number | null;
  quarantineOnReceipt?: boolean | null;
  isBlocking?: boolean | null;
  requiresIndependentInspector?: boolean | null;
  effectiveTo?: string | null;
  status?: string | null;
  rowVersion: number;
}

export interface UpdateInstructionRequest {
  toBankAccountId?: number | null;
  beneficiaryId?: number | null;
  sendDate?: string | null;
  receiptDate?: string | null;
  amount?: string | null;
  toAmount?: string | null;
  fxAccountId?: number | null;
  icInstrumentId?: number | null;
  purposeCode?: string | null;
  reference?: string | null;
  memo?: string | null;
  rowVersion: number;
}

export interface UpdateInvoiceRequest {
  vendorInvoiceNo?: string | null;
  invoiceDate?: string;
  dueDate?: string;
  currency?: string;
  description?: string | null;
  memo?: string | null;
  lines?: InvoiceLineInput[];
  rowVersion: number;
  poId?: number | null;
  clearPo?: boolean;
  dueDateOverrideReason?: string;
}

export interface UpdateItemRequest {
  name?: string | null;
  description?: string | null;
  barcode?: string | null;
  taxGroupId?: number | null;
  defaultTaxCode?: string | null;
  reorderMin?: string | null;
  reorderMax?: string | null;
  reorderPoint?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
  lotPickStrategy?: 'fifo' | 'fefo' | 'manual';
  shelfLifeDays?: number | null;
  minRemainingShelfLifeDays?: number | null;
  requiresExpiry?: boolean | null;
  storageClass?: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  netWeightKg?: string | null;
  volumeM3?: string | null;
}

export interface UpdateJournalTypeRequest {
  name?: string | null;
  allowedSources?: string[];
  allowsManual?: boolean | null;
  reversalPolicy?: string | null;
  requiresApproval?: boolean | null;
  isActive?: boolean | null;
  attachmentRequiredAbove?: string | null;
  rowVersion: number;
}

export interface UpdateKBArticleRequest {
  title?: string | null;
  category?: string | null;
  tags?: string[] | null;
  body?: string | null;
  rowVersion: number;
}

export interface UpdateLandedCostChargeRequest {
  description?: string | null;
  vendorId?: number | null;
  amount?: string | null;
  fxRate?: string | null;
  basis?: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  customsValue?: string | null;
  directAmounts?: LandedCostDirectAmountRequest[];
  rowVersion: number;
}

export interface UpdateLandedCostElementRequest {
  name?: string | null;
  defaultBasis?: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  isAbsorbable?: boolean | null;
  expenseAccountId?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateLandedCostShipmentRequest {
  reference?: string | null;
  mode?: 'sea' | 'air' | 'road' | 'courier';
  vesselOrFlight?: string | null;
  billOfLading?: string | null;
  customsEntryNo?: string | null;
  originCountry?: string | null;
  etdOn?: string | null;
  etaOn?: string | null;
  clearedOn?: string | null;
  absorptionDate?: string | null;
  defaultBasis?: 'value' | 'quantity' | 'weight' | 'volume' | 'direct';
  memo?: string | null;
  rowVersion: number;
}

export interface UpdateLeadRequest {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  ownerUserId?: number | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateLeadRoutingRuleRequest {
  seq?: number | null;
  name?: string | null;
  matchBand?: string | null;
  matchSource?: string | null;
  matchCountry?: string | null;
  matchSegmentId?: number | null;
  minScore?: number | null;
  assignUserId?: number | null;
  assignRoleCode?: string | null;
  assignStrategy?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateLeadScoreModelRequest {
  name?: string | null;
  hotThreshold?: number | null;
  warmThreshold?: number | null;
  rowVersion: number;
}

export interface UpdateLeadScoreRuleRequest {
  seq?: number | null;
  field?: string | null;
  operator?: string | null;
  value?: string | null;
  points?: number | null;
  label?: string | null;
  rowVersion: number;
}

export interface UpdateLeaveBlackoutRequest {
  fromDate?: string | null;
  toDate?: string | null;
  severity?: string | null;
  reason?: string | null;
  rowVersion: number;
}

export interface UpdateLeaveTypePolicyRequest {
  companyId?: number | null;
  dayBasis?: string | null;
  calendarId?: number | null;
  leaveClass?: string | null;
  carryForwardEnabled?: boolean | null;
  carryForwardMaxDays?: string | null;
  carryForwardExpiresAfterMonths?: number | null;
  encashable?: boolean | null;
  encashmentMaxDays?: string | null;
  encashmentComponentCode?: string | null;
  qualifyingMonths?: number | null;
  certificateAfterDays?: string | null;
  maxDaysPerInstance?: string | null;
  coverageCheck?: string | null;
  rowVersion: number;
}

export interface UpdateLeaveTypeRequest {
  name?: string | null;
  accrualDaysPerMonth?: string | null;
  maxBalanceDays?: string | null;
  isPaid?: boolean | null;
  requiresApproval?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateLegalEntityRequest {
  entityType?: string | null;
  incorporationDate?: string | null;
  rowVersion: number;
}

export interface UpdateLocationRequest {
  name?: string | null;
  parentLocationId?: number | null;
  country?: string | null;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  localityName?: string | null;
  subdivisionName?: string | null;
  postalCode?: string | null;
  subdivisionGeoRef?: string | null;
  localityGeoRef?: string | null;
  timezone?: string | null;
  isInventoryEnabled?: boolean | null;
  status?: string | null;
  openedOn?: string | null;
  closedOn?: string | null;
  note?: string | null;
  waiverReasonCode?: string;
  rowVersion: number;
}

export interface UpdateLoyaltyProgramRequest {
  name?: string | null;
  pointsPerUnit?: string | null;
  rounding?: string | null;
  status?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
  rowVersion: number;
}

export interface UpdateLoyaltyTierRequest {
  name?: string | null;
  sortOrder?: number | null;
  minPoints?: number | null;
  minSpend?: string | null;
  earnMultiplier?: string | null;
  rowVersion: number;
}

export interface UpdateMarketingCampaignRequest {
  name?: string | null;
  status?: string | null;
  companyId?: number | null;
  ownerUserId?: number | null;
  startsOn?: string | null;
  endsOn?: string | null;
  budgetAmount?: string | null;
  targetRevenue?: string | null;
  currency?: string | null;
  subject?: string | null;
  body?: string | null;
  templateKey?: string | null;
  locale?: string | null;
  attributionWindowDays?: number | null;
  rowVersion: number;
}

export interface UpdateMarketingSegmentRequest {
  name?: string | null;
  description?: string | null;
  criteria?: MarketingSegmentCriteria | null;
  ownerUserId?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateMemberRequest {
  note?: string | null;
  method?: 'full' | 'proportional' | 'equity' | 'excluded';
  effectiveTo?: string | null;
  ownershipPct?: string;
  investmentAccountId?: number | null;
  rowVersion: number;
}

export interface UpdateMetricDefinitionRequest {
  tenantRefreshMinutes?: number | null;
  isPublished?: boolean | null;
  note?: string | null;
  ownerPositionId?: number | null;
  rowVersion: number;
  module?: string | null;
  labelKey?: string | null;
  descriptionKey?: string | null;
  formulaKey?: string | null;
  sourceKind?: string | null;
  sourceRef?: string | null;
  grain?: string | null;
  unit?: string | null;
  dashboard?: string | null;
  permissionCode?: string | null;
  refreshMinutes?: number | null;
  isCertifiable?: boolean | null;
}

export interface UpdateMfgCostingAccountsRequest {
  yieldVarianceAccountId?: number | null;
  overheadSpendVarianceAccountId?: number | null;
  overheadVolumeVarianceAccountId?: number | null;
  byProductCreditAccountId?: number | null;
  rowVersion: number;
}

export interface UpdateMfgToolRequest {
  name?: string | null;
  status?: string | null;
  workCenterId?: number | null;
  locationId?: number | null;
  lifeCycles?: number | null;
  cyclesUsed?: number | null;
  nextServiceOn?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateModelRequest {
  name?: string | null;
  purpose?: string | null;
  methodology?: 'incremental' | 'zero_based' | 'driver_based' | 'rolling';
  submissionDeadline?: string | null;
  rowVersion: number;
}

export interface UpdateOpportunityRequest {
  name?: string | null;
  amount?: string | null;
  probabilityPercent?: string | null;
  expectedCloseDate?: string | null;
  ownerUserId?: number | null;
  rowVersion: number;
  forecastCategoryCode?: string | null;
  accountId?: number | null;
  primaryContactId?: number | null;
  campaignId?: number | null;
  nextStep?: string | null;
  nextStepDueOn?: string | null;
}

export interface UpdateOrderRequest {
  requestedDate?: string;
  memo?: string | null;
  reference?: string | null;
  lines?: OrderLineInput[];
  rowVersion: number;
}

export interface UpdateOvertimeRuleRequest {
  name?: string | null;
  dailyThresholdHours?: string | null;
  rateMultiplier?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdatePMSChannelRequest {
  name?: string | null;
  adapter?: string | null;
  status?: string | null;
  vendorId?: number | null | null;
  customerId?: number | null | null;
  defaultSourceCode?: string | null;
  defaultSegmentCode?: string | null;
  defaultMarketCode?: string | null;
  parityReferencePlanId?: number | null | null;
  parityTolerancePercent?: string | null;
  rowVersion: number;
}

export interface UpdatePMSGroupBlockRequest {
  name?: string | null;
  customerId?: number | null | null;
  ratePlanId?: number | null | null;
  cutoffDays?: number | null;
  attritionPercent?: string | null;
  note?: string | null;
  status?: 'tentative' | 'definite' | 'cancelled' | 'closed';
  rowVersion: number;
}

export interface UpdatePMSGuestRequest {
  givenName?: string | null;
  familyName?: string | null;
  displayName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  nationality?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressRegion?: string | null;
  addressPostcode?: string | null;
  addressCountry?: string | null;
  documentKind?: '' | 'passport' | 'national_id' | 'driving_licence';
  documentCountry?: string | null;
  documentNumber?: string | null;
  documentExpiresOn?: string | null;
  vipCode?: string | null;
  segmentCode?: string | null;
  language?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdatePMSPropertyRequest {
  name?: string | null;
  kind?: 'resort' | 'hotel' | 'guesthouse' | 'vessel' | 'villa';
  taxChannel?: string | null;
  accommodationItemClass?: string | null;
  branchId?: number | null | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  overbookingRooms?: number | null;
  status?: 'active' | 'suspended';
  rowVersion: number;
}

export interface UpdatePMSRatePlanRequest {
  name?: string | null;
  description?: string | null;
  priceBasis?: string | null;
  boardPlanId?: number | null | null;
  cancellationPolicyId?: number | null | null;
  guaranteeKind?: 'none' | 'token' | 'deposit' | 'company' | 'travel_agent';
  minNights?: number | null;
  maxNights?: number | null | null;
  minAdvanceDays?: number | null;
  maxAdvanceDays?: number | null | null;
  channelCodes?: string[] | null;
  segmentCodes?: string[] | null;
  commissionable?: boolean | null;
  effectiveTo?: string | null | null;
  status?: 'draft' | 'active' | 'closed';
  rowVersion: number;
}

export interface UpdatePMSRevenueCenterRequest {
  name?: string | null;
  kind?: 'rooms' | 'fnb' | 'spa' | 'activities' | 'transfers' | 'minibar' | 'other';
  revenueAccountId?: number | null | null;
  costCenterId?: number | null | null;
  profitCenterId?: number | null | null;
  rowVersion: number;
}

export interface UpdatePMSRoomRequest {
  name?: string | null;
  floor?: string | null;
  wing?: string | null;
  sectionCode?: string | null;
  roomTypeId?: number | null;
  status?: 'active' | 'retired';
  rowVersion: number;
}

export interface UpdatePMSRoomTypeRequest {
  name?: string | null;
  description?: string | null;
  baseOccupancy?: number | null;
  maxOccupancy?: number | null;
  maxAdults?: number | null;
  maxChildren?: number | null;
  revenueCenterId?: number | null | null;
  overbookingRooms?: number | null | null;
  cleanCreditDeparture?: string | null;
  cleanCreditStayover?: string | null;
  sortOrder?: number | null;
  rowVersion: number;
}

export interface UpdatePMSServiceRequest {
  name?: string | null;
  description?: string | null;
  revenueCenterId?: number | null;
  kind?: 'activity' | 'spa' | 'dining' | 'transfer' | 'rental' | 'other';
  durationMinutes?: number | null;
  capacityPerSlot?: number | null;
  leadTimeHours?: number | null;
  cancelWindowHours?: number | null;
  mealPeriod?: string | null;
  requirements?: PMSServiceRequirementDTO[] | null;
  dependencies?: PMSServiceDependencyDTO[] | null;
  rowVersion: number;
}

export interface UpdatePMSSettingsRequest {
  guestLedgerAccountId?: number | null | null;
  depositLiabilityAccountId?: number | null | null;
  packageLedgerAccountId?: number | null | null;
  allowanceAccountId?: number | null | null;
  commissionExpenseAccountId?: number | null | null;
  commissionPayableAccountId?: number | null | null;
  defaultRevenueAccountId?: number | null | null;
  cashRoundingAccountId?: number | null | null;
  noShowPolicy?: 'charge_first_night' | 'charge_full_stay' | 'no_charge';
  auditResumeWindowHours?: number | null;
  rowVersion: number;
}

export interface UpdatePMScheduleRequest {
  name?: string | null;
  frequencyDays?: number | null;
  nextDueDate?: string | null;
  subjectTemplate?: string | null;
  category?: string | null;
  slaPolicyId?: number | null;
  assignedTo?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdatePORequest {
  vendorId?: number | null;
  currency?: string;
  orderDate?: string;
  expectedDate?: string;
  memo?: string | null;
  lines?: POLineInput[];
  rowVersion: number;
}

export interface UpdatePOSOutletRequest {
  name?: string | null;
  kind?: 'retail' | 'hospitality' | 'wholesale';
  taxChannel?: string | null;
  supplyType?: string | null;
  warehouseId?: number | null | null;
  branchId?: number | null | null;
  defaultRevenueCenterId?: number | null | null;
  priceSetId?: number | null | null;
  cashRoundingIncrement?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdatePOSRevenueCenterRequest {
  name?: string | null;
  revenueAccountId?: number | null | null;
  costCenterId?: number | null | null;
  profitCenterId?: number | null | null;
  departmentId?: number | null | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdatePOSSaleLineRequest {
  quantity?: string | null;
  unitPrice?: string | null;
  priceOverride?: POSOverrideRequest | null;
  description?: string | null;
  revenueCenterId?: number | null | null;
  courseNo?: number | null | null;
  lotId?: number | null | null;
  rowVersion: number;
}

export interface UpdatePOSSettingsRequest {
  cashOnHandAccountId?: number | null | null;
  cardClearingAccountId?: number | null | null;
  giftCardLiabilityAccountId?: number | null | null;
  storeCreditLiabilityAccountId?: number | null | null;
  customerDepositLiabilityAccountId?: number | null | null;
  tipPayableAccountId?: number | null | null;
  cashOverShortAccountId?: number | null | null;
  cashRoundingAccountId?: number | null | null;
  defaultRevenueAccountId?: number | null | null;
  defaultCashRoundingIncrement?: string | null;
  voidWindowMinutes?: number | null;
  anomalyVoidRatePercent?: string | null;
  anomalyDiscountRatePercent?: string | null;
  anomalyRefundRatePercent?: string | null;
  anomalyNoSaleCount?: number | null;
  rowVersion: number;
}

export interface UpdatePOSTenderTypeRequest {
  name?: string | null;
  clearingAccountId?: number | null | null;
  opensDrawer?: boolean | null;
  allowsChange?: boolean | null;
  allowsPartial?: boolean | null;
  allowsRefund?: boolean | null;
  surchargePercent?: string | null;
  surchargeItemId?: number | null | null;
  provider?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdatePOSTerminalRequest {
  name?: string | null;
  cashAccountId?: number | null | null;
  offlineEnabled?: boolean | null;
  contentMaxAgeHours?: number | null;
  status?: 'active' | 'suspended' | 'retired';
  rowVersion: number;
}

export interface UpdatePRRequest {
  departmentId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  needBy?: string;
  currency?: string;
  memo?: string | null;
  lines?: PRLineInput[];
  rowVersion: number;
}

export interface UpdatePartyIdentifierRequest {
  value?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  verified?: boolean | null;
  rowVersion: number;
}

export interface UpdatePartyMergeRequest {
  fieldChoices: MergeFieldChoiceInput[];
  rowVersion: number;
}

export interface UpdatePartyRequest {
  displayName?: string | null;
  legalName?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  salutation?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: Record<string, unknown> | null;
  countryCode?: string | null;
  preferredLocale?: string | null;
  status?: string | null;
  rowVersion: number;
}

export interface UpdatePayGroupRequest {
  name?: string | null;
  frequency?: string | null;
  periodAnchorDay?: number | null;
  paymentLagDays?: number | null;
  calendarId?: number | null;
  clearCalendar?: boolean;
  bankAccountId?: number | null;
  clearBankAccount?: boolean;
  isDefault?: boolean | null;
  isActive?: boolean | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdatePermitQuotaRequest {
  cap: number;
  effectiveFrom: string;
  sourceCitation?: string;
  note?: string;
}

export interface UpdatePhaseRequest {
  name?: string;
  sequence?: number;
  startDate?: string;
  endDate?: string;
  status?: 'planned' | 'active' | 'completed';
  rowVersion: number;
}

export interface UpdatePolicyRequest {
  name?: string | null;
  rules?: Rule[] | null;
  priority?: number | null;
  isActive?: boolean | null;
  companyId?: number | null;
  rowVersion: number;
}

export interface UpdatePriceListRequest {
  name?: string | null;
  priority?: number | null;
  validFrom?: string;
  validTo?: string;
  isActive?: boolean | null;
  lines?: PriceListLineInput[];
  rowVersion: number;
}

export interface UpdateProblemRequest {
  title?: string | null;
  description?: string | null;
  status?: string | null;
  rootCause?: string | null;
  workaround?: string | null;
  kbArticleId?: number | null;
  itemId?: number | null;
  assetCategory?: string | null;
  ownerUserId?: number | null;
  rowVersion: number;
}

export interface UpdateProductionOrderRequest {
  bomId?: number | null;
  routingId?: number | null;
  warehouseId?: number | null;
  quantityPlanned?: string | null;
  backflush?: boolean | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateProfileRequest {
  name?: string | null;
  mapping?: CSVMapping | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateProjectMoneyRequest {
  estimateAtCompletion?: string;
  costPlusPercent?: string;
  retainerAmount?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string | null;
  customerId?: number | null;
  managerUserId?: number | null;
  status?: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  startDate?: string;
  endDate?: string;
  billingMethod?: 'fixed_price_milestone' | 'time_and_materials' | 'percent_complete' | 'cost_plus' | 'retainer';
  contractValue?: string;
  revenueAccountId?: number | null;
  revenueTaxCode?: string | null;
  rowVersion: number;
}

export interface UpdateProjectResourceRequest {
  planningCostRate?: string | null;
  currency?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateProjectRiskRequest {
  title?: string;
  description?: string | null;
  category?: string | null;
  probability?: number | null;
  impact?: number | null;
  costImpact?: string;
  status?: 'open' | 'mitigating' | 'realised' | 'closed';
  response?: 'avoid' | 'mitigate' | 'transfer' | 'accept';
  mitigation?: string | null;
  ownerUserId?: number | null;
  reviewOn?: string;
  rowVersion: number;
}

export interface UpdateProjectSubcontractRequest {
  title?: string;
  purchaseOrderId?: number | null;
  contractValue?: string;
  retentionPercent?: string;
  certifiedAmount?: string;
  status?: 'draft' | 'active' | 'completed' | 'terminated';
  startsOn?: string;
  endsOn?: string;
  rowVersion: number;
}

export interface UpdateProjectVariationRequest {
  title?: string;
  description?: string | null;
  origin?: 'internal' | 'customer' | 'site';
  costImpact?: string;
  revenueImpact?: string;
  scheduleImpactDays?: number | null;
  impactNote?: string | null;
  amendmentReason?: string;
  rowVersion: number;
}

export interface UpdatePromotionRequest {
  name?: string | null;
  isActive?: boolean | null;
  isStackable?: boolean | null;
  endDate?: string;
  rowVersion: number;
}

export interface UpdateQMSSettingsRequest {
  quarantineWarehouseId?: number | null;
  receiptScrapAccountId?: number | null;
  defaultReceiptPolicy?: string | null;
  quarantineSlaDays?: number | null;
  capaDefaultDueDays?: number | null;
  rowVersion: number;
}

export interface UpdateQuoteRequest {
  expiryDate?: string;
  memo?: string | null;
  reference?: string | null;
  lines?: SalesQuoteLineInput[];
  rowVersion: number;
}

export interface UpdateRVMCompetitorRateRequest {
  rate?: string | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdateRVMForecastRequest {
  name?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateRVMHurdleRequest {
  segmentId?: number | null;
  roomTypeId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  dowMask?: string | null;
  minAdr?: string | null;
  minTotalContribution?: string | null;
  currency?: string | null;
  isActive?: boolean | null;
  note?: string | null;
  clearSegment?: boolean;
  clearRoomType?: boolean;
  clearWindow?: boolean;
  rowVersion: number;
}

export interface UpdateRVMSegmentMappingRequest {
  segmentId?: number | null;
  priority?: number | null;
  matchSegmentCode?: string | null;
  matchMarketCode?: string | null;
  matchSourceCode?: string | null;
  matchChannelId?: number | null | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateRVMSegmentRequest {
  name?: string | null;
  family?: 'transient' | 'group' | 'contract' | 'wholesale' | 'complimentary' | 'house';
  displacementRank?: number | null;
  isActive?: boolean | null;
  sortOrder?: number | null;
  rowVersion: number;
}

export interface UpdateRVMSettingsRequest {
  propertyId: number;
  snapshotHorizonDays?: number | null;
  forecastHorizonDays?: number | null;
  varianceMaterialPct?: string | null;
  varianceMaterialAbs?: string | null;
  accuracyHorizons?: number[];
  unmappedSegmentCode?: string | null;
  hurdleEnforcement?: 'warn' | 'block';
  rowVersion: number;
}

export interface UpdateRVMStreamRequest {
  name?: string | null;
  actualSources?: StreamActualSources | null;
  isActive?: boolean | null;
  sortOrder?: number | null;
  rowVersion: number;
}

export interface UpdateReasonCodeRequest {
  description?: string | null;
  requiresApproval?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateRecipeRequest {
  name?: string | null;
  yieldQty?: string | null;
  yieldUom?: string | null;
  kind?: 'plated' | 'batch' | 'sub_recipe' | 'modifier';
  defaultWarehouseId?: number | null;
  isActive?: boolean | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdateRecurringItemRequest {
  name?: string | null;
  direction?: 'inflow' | 'outflow';
  amount?: string | null;
  cadence?: 'once' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'annually';
  nextDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  isActive?: boolean | null;
  kind?: string | null;
  rowVersion: number;
}

export interface UpdateReferenceRequest {
  rowVersion: number;
  status?: string;
  outcome?: string;
  response?: string;
}

export interface UpdateRegistrationRequest {
  status?: string | null;
  scheme?: string | null;
  effectiveTo?: string | null;
  returnFrequency?: string | null;
  firstPeriodStart?: string | null;
  entityDesignation?: string | null;
  isPrimary?: boolean | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateReportExceptionViewRequest {
  name: string;
  description?: string;
  roleCode: string;
  sources: ('attention' | 'recon_queue' | 'approvals' | 'ai_recommendations')[];
  filters?: Record<string, unknown>;
  refreshMinutes: number;
  isActive: boolean;
  sortOrder?: number;
  rowVersion: number;
}

export interface UpdateReportingSettingsRequest {
  companyId: number;
  dailyOperatingDueHour?: number | null;
  allowPartialDailyOperating?: boolean | null;
  dailyOperatingDetailDays?: number | null;
  rowVersion: number;
}

export interface UpdateRequisitionRequest {
  requiredOn?: string | null;
  shiftCode?: string | null;
  memo?: string | null;
  lines?: CreateRequisitionLineRequest[] | null;
  rowVersion: number;
}

export interface UpdateReservationRequest {
  priority?: number | null;
  expiresAt?: string | null;
  clearExpiresAt?: boolean;
  rowVersion: number;
}

export interface UpdateRetentionRuleRequest {
  treatment?: string | null;
  basis?: string | null;
  retainYears?: number | null;
  anchor?: string | null;
  statuteRef?: string | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdateRootCauseCodeRequest {
  description?: string | null;
  category?: string | null;
  requiresAttachment?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateRosterSettingsRequest {
  standardHoursPerMonth: string;
  defaultCalendarId?: number | null;
  note?: string;
  rowVersion: number;
}

export interface UpdateRoutingRequest {
  name?: string | null;
  notes?: string | null;
  operations?: RoutingOperationInput[];
  rowVersion: number;
}

export interface UpdateSLAPolicyRequest {
  name?: string | null;
  responseMinutes?: number | null;
  resolutionMinutes?: number | null;
  businessHoursOnly?: boolean | null;
  escalateToUserId?: number | null;
  isDefault?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateSSOProviderRequest {
  name?: string | null;
  issuer?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  allowedEmailDomains?: string[] | null;
  jitProvisioning?: boolean | null;
  defaultRoleCodes?: string[] | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateSavedFilterRequest {
  name: string;
  params: Record<string, unknown>;
  isShared: boolean;
  rowVersion: number;
}

export interface UpdateScenarioRequest {
  rowVersion: number;
  name?: string | null;
  description?: string | null;
  isDefault?: boolean | null;
  isActive?: boolean | null;
  receivableDelayDays?: number | null;
  receivableHaircutPct?: string | null;
  payableDelayDays?: number | null;
  recurringMultiplierPct?: string | null;
  includeOverdue?: boolean | null;
  manualAdjustments?: CashForecastAdjustment[] | null;
  scenarioType?: 'base' | 'upside' | 'downside' | 'stress' | 'what_if';
  driverOverrides?: string[];
  inheritDrivers?: string[];
}

export interface UpdateScheduleRequest {
  name: string;
  params: Record<string, unknown>;
  format: string;
  frequency: string;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  hourLocal: number;
  recipients: string[];
  companyId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface UpdateSegmentRequest {
  sequence?: number | null;
  prompt?: string | null;
  qualifier?: string | null;
  length?: number | null;
  displayOrder?: number | null;
  isSecured?: boolean | null;
}

export interface UpdateSegmentValueRequest {
  description?: string | null;
  isActive?: boolean | null;
  effectiveTo?: string | null;
}

export interface UpdateServiceChargePointRequest {
  points?: string | null;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface UpdateServiceChargeSchemeRequest {
  name?: string;
  minDaysWorked?: number | null;
  includeEmploymentTypes?: string[];
  excludeOnProbation?: boolean | null;
  prorateByDays?: boolean | null;
  forfeitOnTerminationForCause?: boolean | null;
  forfeitAfterUnapprovedAbsenceDays?: number | null;
  retentionPercent?: string | null;
  contributesToClasses?: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  isActive?: boolean | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdateServiceContractRequest {
  name?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
  autoRenew?: boolean | null;
  renewalNoticeDays?: number | null;
  slaPolicyId?: number | null;
  rateCardId?: number | null;
  feeAmount?: string | null;
  feeCycle?: string | null;
  revenueAccountId?: number | null;
  taxCode?: string | null;
  status?: string | null;
  rowVersion: number;
}

export interface UpdateServiceWorkOrderRequest {
  summary?: string | null;
  instructions?: string | null;
  priority?: string | null;
  taskListId?: number | null;
  warehouseId?: number | null;
  costAccountId?: number | null;
  estimatedHours?: string | null;
  rowVersion: number;
}

export interface UpdateSettingsRequest {
  wipAccountId?: number | null;
  appliedLaborAccountId?: number | null;
  appliedOverheadAccountId?: number | null;
  scrapAccountId?: number | null;
  materialPriceVarianceAccountId?: number | null;
  materialUsageVarianceAccountId?: number | null;
  laborRateVarianceAccountId?: number | null;
  laborEfficiencyVarianceAccountId?: number | null;
  overheadVarianceAccountId?: number | null;
  rowVersion: number;
}

export interface UpdateShiftDefinitionRequest {
  name?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  crossesMidnight?: boolean | null;
  unpaidBreakMinutes?: number | null;
  departmentId?: number | null;
  colour?: string | null;
  isNight?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateShiftPatternRequest {
  name?: string | null;
  cycleDays?: number | null;
  calendarId?: number | null;
  note?: string | null;
  isActive?: boolean | null;
  slots?: ShiftPatternSlotDTO[];
  rowVersion: number;
}

export interface UpdateStaffingRuleRequest {
  unitsPerHead?: string | null;
  minHeadcount?: number | null;
  maxHeadcount?: number | null;
  effectiveTo?: string | null;
  rowVersion: number;
}

export interface UpdateStatutoryMappingRequest {
  expenseAccountCode?: string | null;
  liabilityAccountCode?: string | null;
  remittanceBankAccountId?: number | null;
  isEnabled?: boolean | null;
  disabledReasonCode?: string | null;
  note?: string | null;
  rate?: string;
  percent?: string;
  bands?: SlabBandDTO[];
  rowVersion: number;
}

export interface UpdateStockLotRequest {
  expiryDate?: string | null;
  bestBeforeDate?: string | null;
  manufacturedOn?: string | null;
  supplierLotRef?: string | null;
  vendorId?: number | null;
  countryOfOrigin?: string | null;
  attributes?: Record<string, unknown>;
  rowVersion: number;
}

export interface UpdateStructureRequest {
  name?: string | null;
  status?: string | null;
  effectiveFrom?: string | null;
}

export interface UpdateSubstituteRequest {
  conversionFactor?: string | null;
  preference?: number | null;
  appliesTo?: 'both' | 'outbound' | 'production';
  requiresApproval?: boolean | null;
  isActive?: boolean | null;
  note?: string | null;
  rowVersion?: number;
}

export interface UpdateSupplierContractRequest {
  title?: string;
  description?: string;
  vendorReference?: string;
  startsOn?: string;
  endsOn?: string;
  autoRenew?: boolean | null;
  noticePeriodDays?: number | null;
  renewalTermMonths?: number | null;
  incotermCode?: string;
  incotermPlace?: string;
  paymentTermsCode?: string;
  leadTimeDays?: number | null;
  ownerUserId?: number | null;
  attachmentId?: number | null;
  rowVersion: number;
}

export interface UpdateTariffRequest {
  description?: string | null;
  dutyRatePct?: string | null;
  dutyPerUnit?: string | null;
  dutyUom?: string | null;
  effectiveTo?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateTaskRequest {
  phaseId?: number | null;
  name?: string;
  description?: string | null;
  status?: 'not_started' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
  startDate?: string;
  endDate?: string;
  isMilestone?: boolean | null;
  assigneeUserId?: number | null;
  accountId?: number | null;
  costCenterId?: number | null;
  budgetAmount?: string;
  rowVersion: number;
}

export interface UpdateTemplateRequest {
  name?: string | null;
  memo?: string | null;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  clearFrequency?: boolean;
  intervalCount?: number | null;
  dayOfMonth?: number | null;
  endDate?: string | null;
  nextRunDate?: string | null;
  postAutomatically?: boolean | null;
  isActive?: boolean | null;
  lines?: GlTemplateLineInput[];
  rowVersion: number;
}

export interface UpdateTicketRequest {
  customerAssetId?: number | null;
  warehouseId?: number | null;
  billable?: boolean | null;
  ticketRowVersion: number;
}

export interface UpdateTransferRequest {
  memo?: string | null;
  transferDate?: string | null;
  expectedArrivalOn?: string | null;
  carrierName?: string | null;
  carrierReference?: string | null;
  vehicleReference?: string | null;
  driverName?: string | null;
  lines?: DraftTransferLineRequest[];
  rowVersion: number;
}

export interface UpdateTreasurySettingsRequest {
  rowVersion: number;
  fundsInTransitAccountId?: number | null;
  walletFloatAccountId?: number | null;
  walletClearingAccountId?: number | null;
  walletVarianceAccountId?: number | null;
  interestExpenseAccountId?: number | null;
  interestPayableAccountId?: number | null;
  facilityFeeAccountId?: number | null;
  borrowingsAccountId?: number | null;
  borrowingsCurrentAccountId?: number | null;
  clearAccounts?: string[];
  duplicateWindowHours?: number | null;
  inTransitAlertDays?: number | null;
  autoPublishPosition?: boolean | null;
  walletCountIntervalDays?: number | null;
}

export interface UpdateUnitRequest {
  code?: string | null;
  name?: string | null;
  parentId?: number | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  managerPositionId?: number | null;
  rowVersion: number;
}

export interface UpdateUserRequest {
  name?: string | null;
  locale?: string | null;
  timezone?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  rowVersion: number;
}

export interface UpdateUserRolesRequest {
  roleCodes: string[];
  overrideReason?: string;
}

export interface UpdateVanRequest {
  registration?: string | null;
  technicianUserId?: number | null;
  eamAssetId?: number | null;
  sourceWarehouseId?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateVariancePolicyRequest {
  absoluteThreshold?: string | null;
  percentThreshold?: string | null;
  includeJoinersLeavers?: boolean | null;
  rowVersion: number;
}

export interface UpdateVendorRequest {
  name?: string | null;
  legalName?: string | null;
  taxGroupId?: number | null;
  whtTaxCode?: string | null;
  paymentTermsDays?: number | null;
  discountPercent?: string | null;
  discountDays?: number | null;
  defaultCurrency?: string | null;
  allowedCurrencies?: string[];
  status?: 'active' | 'blocked';
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  taxRegistrationNo?: string | null;
  notes?: string | null;
  rowVersion: number;
}

export interface UpdateWageBaseRequest {
  name?: string | null;
  includeClasses?: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  excludeClasses?: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  gate?: string | null;
  floorAmount?: string | null;
  ceilingAmount?: string | null;
  effectiveTo?: string | null;
  sourceCitation?: string | null;
  rowVersion: number;
}

export interface UpdateWalletRequest {
  name?: string | null;
  status?: string | null;
  floatAmount?: string | null;
  varianceAccountId?: number | null;
  costCenterId?: number | null;
  branchId?: number | null;
  departmentId?: number | null;
  locationId?: number | null;
  countIntervalDays?: number | null;
  deputyUserId?: number | null;
  deputyEmployeeId?: number | null;
  clearDeputy?: boolean;
  holderPositionId?: number | null;
  clearHolderPosition?: boolean;
  dormancyDays?: number | null;
  statusNote?: string;
  rowVersion: number;
}

export interface UpdateWarehouseRequest {
  name?: string | null;
  negativeStockPolicy?: 'block' | 'allow';
  isActive?: boolean | null;
  rowVersion: number;
  locationId?: number | null;
  stockModel?: 'perpetual' | 'expense_on_issue';
  inventoryAccountOverrideId?: number | null;
  costCenterId?: number | null;
  defaultPutawayStrategy?: 'fixed_then_any' | 'nearest_empty' | 'consolidate' | 'single_bin' | 'none';
  defaultPickStrategy?: 'lot_strategy' | 'fixed_bin_first' | 'nearest' | 'single_bin';
  requiresBin?: boolean | null;
}

export interface UpdateWasteReasonRequest {
  description?: string | null;
  expenseAccountId?: number | null;
  requiresApproval?: boolean | null;
  requiresAttachment?: boolean | null;
  countsAsShrinkage?: boolean | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateWebhookEndpointRequest {
  name?: string | null;
  url?: string | null;
  topics?: string[] | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateWorkCenterRequest {
  name?: string | null;
  description?: string | null;
  costRatePerHour?: string | null;
  overheadRatePerHour?: string | null;
  capacityHoursPerWeek?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpdateWorkPermitRequest {
  status?: string | null;
  expiresOn?: string | null;
  sponsorName?: string | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdateWorkingTimeRuleRequest {
  name?: string | null;
  threshold?: string | null;
  windowUnits?: number | null;
  nightStartsAt?: string | null;
  nightEndsAt?: string | null;
  severity?: string | null;
  effectiveTo?: string | null;
  isActive?: boolean | null;
  note?: string | null;
  rowVersion: number;
}

export interface UpdateZoneRequest {
  name?: string | null;
  storageClass?: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  pickSequence?: number | null;
  minTemperatureC?: string | null;
  maxTemperatureC?: string | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpsertClassificationRequest {
  dataDomain: string;
  sensitivity: string;
  readAuditRequired: boolean;
  retentionYears?: number | null;
  residencyConstraint?: string;
  maskingRule?: string;
  effectiveFrom: string;
}

export interface UpsertDemandRequest {
  companyId: number;
  locationId: number;
  businessDate: string;
  dayPart?: string;
  metric: string;
  value: string;
  note?: string;
}

export interface UpsertDependencyPostureRequest {
  fromModule: string;
  toModule: string;
  posture: string;
  continuesDuringOutage: boolean;
  note?: string;
}

export interface UpsertDocumentTypeRequest {
  name: string;
  isBlocking?: boolean | null;
  requiredForCriticality?: string;
  warnDaysBeforeExpiry?: number | null;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpsertEInvoicingConfigRequest {
  country: string;
  gateway: string;
  endpoint: string;
  credential?: string;
  pollIntervalS?: number;
}

export interface UpsertExpenseSettingsRequest {
  companyId: number;
  advanceAccountId: number;
  employeeReceivableAccountId: number;
  defaultClaimCurrency: string;
  requireReceiptAbove?: string;
  advanceOverdueDays?: number;
  rowVersion: number;
}

export interface UpsertIncotermRequest {
  name: string;
  freightBorneBy: string;
  insuranceBorneBy: string;
  dutyBorneBy: string;
  riskTransferPoint?: string;
  requiresNamedPlace?: boolean | null;
  isActive?: boolean | null;
  rowVersion?: number;
}

export interface UpsertItemSettingRequest {
  companyId: number;
  itemId: number;
  standardCost: string;
  marginFloorPercent?: string;
  categoryCode?: string;
}

export interface UpsertItemUOMRequest {
  conversionFactor: string;
  barcode?: string;
  isPurchaseDefault?: boolean;
  isSalesDefault?: boolean;
}

export interface UpsertLineRequest {
  accountId: number;
  fiscalPeriodId: number;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  amount?: string;
  note?: string;
  driverQty?: string;
  driverRate?: string;
  justification?: string;
  rowVersion?: number;
}

export interface UpsertManualLineRequest {
  amount: string;
  note?: string;
}

export interface UpsertMastershipRequest {
  dataDomain: string;
  masteringModule: string;
  enforcement: string;
  ownerPositionId?: number | null;
  effectiveFrom: string;
}

export interface UpsertMatchCauseRequest {
  name: string;
  description?: string;
  ownerRoleCode?: string | null;
  ownerUserId?: number | null;
  ownerResolution: string;
  slaHours: number;
  isAutoResolvable?: boolean;
  isActive?: boolean | null;
  rowVersion: number;
}

export interface UpsertMemberRequest {
  userId: number;
  role?: string;
  costRate?: string;
  billRate?: string;
  currency?: string;
}

export interface UpsertPortalSettingsRequest {
  companyId: number;
  defaultWarehouseId?: number | null;
  defaultRevenueAccountId?: number | null;
  rowVersion?: number;
}

export interface UpsertProjectCapacityRequest {
  weeks: ProjectCapacityWeekInput[];
}

export interface UpsertProjectMoneySettingsRequest {
  contractAssetAccountId?: number | null;
  contractLiabilityAccountId?: number | null;
  retentionReceivableAccountId?: number | null;
  contractAdvanceAccountId?: number | null;
  lossProvisionAccountId?: number | null;
  contractLossAccountId?: number | null;
  rowVersion: number;
}

export interface UpsertRateRequest {
  base: string;
  quote: string;
  rate: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  asOf: string;
  note: string;
  rateType?: 'spot' | 'corporate' | 'historical';
}

export interface UpsertRateResult {
  rate?: RateDTO | null;
  hold?: Hold | null;
}

export interface UpsertSettingsRequest {
  companyId: number;
  apControlAccountId?: number | null;
  whtPayableAccountId?: number | null;
  fxGainAccountId?: number | null;
  fxLossAccountId?: number | null;
  discountAccountId?: number | null;
  settlementTolerance?: string;
  attachmentRequiredAbove?: string | null;
  paymentAnomalyAvgMultiple?: string | null;
  rowVersion?: number;
  bankChangeCoolingOffHours?: number | null;
  bankChangeCoolingOffActive?: boolean | null;
  coolingOffRowVersion?: number;
}

export interface UpsertStatutoryLanguageRequest {
  country: string;
  documentClass: string;
  language: string;
  effectiveFrom: string;
}

export interface UpsertToleranceRequest {
  base: string;
  quote: string;
  rateType?: 'spot' | 'corporate' | 'historical';
  maxMovePct: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  action?: 'hold' | 'warn';
  isActive?: boolean | null;
}

export interface UsageDaily {
  id: number;
  tenantId: string;
  day: string;
  activeUsers: number;
  apiCalls: number;
  storageBytes: number;
  documentsPosted: number;
  aiTokens: number;
  emailsSent: number;
  computedAt: string;
}

export interface UsageVarianceLineDTO {
  itemId: number;
  itemSku?: string;
  openingQty: string;
  receivedQty: string;
  theoreticalQty: string;
  wasteQty: string;
  closingQty: string;
  actualQty: string;
  varianceQty: string;
  unitCost: string;
  varianceValue: string;
  explainedReasonCode?: string;
  note?: string;
}

export interface UsageVarianceRunDTO {
  id: number;
  companyId: number;
  warehouseId: number;
  periodFrom: string;
  periodTo: string;
  status: 'open' | 'closed';
  openingCountSessionId?: number | null;
  closingCountSessionId?: number | null;
  theoreticalValue: string;
  actualValue: string;
  varianceValue: string;
  variancePct: string;
  shrinkageTargetPct: string;
  overTarget: boolean;
  computedAt?: string | null;
  rowVersion: number;
}

export interface UsageVarianceRunDetailDTO {
  id: number;
  companyId: number;
  warehouseId: number;
  periodFrom: string;
  periodTo: string;
  status: 'open' | 'closed';
  openingCountSessionId?: number | null;
  closingCountSessionId?: number | null;
  theoreticalValue: string;
  actualValue: string;
  varianceValue: string;
  variancePct: string;
  shrinkageTargetPct: string;
  overTarget: boolean;
  computedAt?: string | null;
  rowVersion: number;
  lines: UsageVarianceLineDTO[];
}

export interface User {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  rowVersion: number;
  email: string;
  name: string;
  role: 'owner' | 'support' | 'billing';
  status: 'invited' | 'active' | 'disabled';
  mfaEnabled: boolean;
  mfaEnrolledAt?: string | null;
  lastLoginAt?: string | null;
  lockedUntil?: string | null;
}

export interface UserDTO {
  id: number;
  email: string;
  name: string;
  status: string;
  locale: string;
  timezone?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  avatarKey?: string | null;
  mfaEnabled: boolean;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
  passwordChangedAt?: string | null;
  createdAt: string;
  rowVersion: number;
  roleCodes?: string[];
  mustChangePassword: boolean;
  principalKind: string;
}

export interface UserProfileDTO {
  id: number;
  email: string;
  name: string;
  status: string;
  locale: string;
  timezone?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  avatarKey?: string | null;
  mfaEnabled: boolean;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
  passwordChangedAt?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface ValidateCombinationQuery {
  coaStructureId: number;
  journalLineId?: number;
  segmentValues?: string;
  on?: string;
}

export interface ValidateRequest {
  plan: FoundationPlan;
}

export interface ValidationFinding {
  rule: string;
  code: string;
  severity: string;
  message: string;
  nodeIds?: number[];
}

export interface ValidationHandle {
  country: string;
  ranAt: string;
  ok: boolean;
}

export interface ValidationIssue {
  section: string;
  rowKey?: string;
  field?: string;
  code: string;
  message: string;
}

export interface ValidationResult {
  versionId: number;
  valid: boolean;
  nodeCount: number;
  maxDepth: number;
  depthLimit: number;
  findings: ValidationFinding[];
}

export interface ValuationReport {
  companyId: number;
  asOf: string;
  totalValue: string;
  reconciliations: InventoryControlReconciliation[];
  generatedAt: string;
}

export interface ValuationReportQuery {
  companyId: number;
}

export interface ValueRef {
  configurationType: string;
  valueId: number;
}

export interface ValueSetDTO {
  id: number;
  code: string;
  name: string;
  kind: string;
  parentValueSetId?: number | null;
  rowVersion: number;
}

export interface VanDTO {
  id: number;
  companyId: number;
  warehouseId: number;
  warehouseCode?: string;
  registration: string;
  technicianUserId?: number | null;
  eamAssetId?: number | null;
  sourceWarehouseId?: number | null;
  isActive: boolean;
  rowVersion: number;
}

export interface VanParLevelDTO {
  itemId: number;
  sku?: string;
  name?: string;
  parQty: string;
  minQty: string;
}

export interface VanReplenishmentDTO {
  id: number;
  vanId: number;
  runOn: string;
  status: string;
  transferDocType?: string;
  transferDocId?: number | null;
  lines: VanReplenishmentLineDTO[];
  rowVersion: number;
}

export interface VanReplenishmentLineDTO {
  itemId: number;
  sku?: string;
  onHandQty: string;
  parQty: string;
  proposedQty: string;
  issuedQty: string;
}

export interface VanStockLineDTO {
  itemId: number;
  sku: string;
  name: string;
  qtyOnHand: string;
}

export interface VanStockResponse {
  lines: VanStockLineDTO[];
}

export interface VarianceAnalysisQuery {
  companyId: number;
  from?: string;
  to?: string;
}

export interface VarianceAnalysisReportDTO {
  companyId: number;
  from: string;
  to: string;
  rows: VarianceAnalysisRowDTO[];
  totalAmount: string;
}

export interface VarianceAnalysisRowDTO {
  varianceType: 'material_price' | 'material_usage' | 'labor_rate' | 'labor_efficiency' | 'overhead' | 'yield' | 'overhead_spend' | 'overhead_volume';
  count: number;
  totalAmount: string;
  postedAmount: string;
}

export interface VarianceCell {
  accountId: number;
  accountCode: string;
  accountName: string;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  fiscalPeriodId?: number;
  periodNo?: number;
  periodName?: string;
  budget: string;
  commitment: string;
  actual: string;
  available: string;
  variance: string;
  variancePercent?: string;
  unbudgeted: boolean;
}

export interface VarianceDTO {
  id: number;
  productionOrderId: number;
  varianceType: 'material_price' | 'material_usage' | 'labor_rate' | 'labor_efficiency' | 'overhead' | 'yield' | 'overhead_spend' | 'overhead_volume';
  itemId?: number | null;
  workCenterId?: number | null;
  expectedQty?: string | null;
  actualQty?: string | null;
  expectedRate?: string | null;
  actualRate?: string | null;
  expectedAmount?: string | null;
  actualAmount?: string | null;
  varianceAmount: string;
  accountId?: number | null;
  journalEntryId?: number | null;
  computedAt: string;
  notes?: string;
}

export interface VariancePolicyDTO {
  id: number;
  companyId: number;
  absoluteThreshold: string;
  percentThreshold: string;
  includeJoinersLeavers: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  createdAt: string;
  rowVersion: number;
}

export interface VarianceQuery {
  companyId: number;
  fiscalYear: number;
  accountId?: number | null;
  costCenterId?: number | null;
  profitCenterId?: number | null;
  branchId?: number | null;
  fromPeriodNo?: number;
  toPeriodNo?: number;
}

export interface VarianceReport {
  companyId: number;
  fiscalYear: number;
  rows: VarianceCell[];
  totals: VarianceCell[];
  generatedAt: string;
}

export interface VendorDTO {
  id: number;
  code: string;
  name: string;
  legalName: string;
  taxGroupId?: number | null;
  whtTaxCode?: string | null;
  paymentTermsDays: number;
  discountPercent: string;
  discountDays: number;
  defaultCurrency: string;
  allowedCurrencies: string[];
  status: 'active' | 'blocked';
  email: string;
  phone: string;
  address: string;
  taxRegistrationNo: string;
  notes: string;
  createdAt: string;
  rowVersion: number;
}

export interface VendorLedger {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  companyId: number;
  currency: string;
  from: string;
  to: string;
  opening: string;
  entries: LedgerEntry[];
  closing: string;
  totalDebit: string;
  totalCredit: string;
  generatedAt: string;
}

export interface VendorStatement {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  companyId: number;
  asOf: string;
  lines: ApStatementLine[];
  totals: ApCurrencyTotal[];
  generatedAt: string;
}

export interface VerificationLineInput {
  assetId: number;
  found: boolean;
  actualLocation?: string;
  actualCustodian?: string;
  condition?: string;
  disposition?: 'none' | 'relocated' | 'recustodied' | 'impair' | 'dispose' | 'write_off' | 'investigate';
  reasonCode?: string;
  note?: string;
}

export interface VerifyCAPARequest {
  outcome: string;
  note?: string;
  rowVersion: number;
}

export interface VerifyComplianceDocumentRequest {
  approve: boolean;
  rejectReason?: string;
}

export interface VerifyIdentityRequest {
  identifierId: number;
  rowVersion: number;
  note?: string;
}

export interface VerifyRightToWorkRequest {
  status: string;
  evidenceId?: number | null;
  expiresOn?: string;
  rowVersion: number;
}

export interface VersionHistoryDTO {
  id: number;
  versionNo: number;
  status: 'draft' | 'pending' | 'active' | 'superseded';
  legalName: string;
  createdBy?: number | null;
  approvedBy?: number | null;
  createdAt: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  changeSummary: string;
  changes: FieldChangeDTO[];
}

export interface VersionReferenceSetRequest {
  effectiveDate: string;
  changeReason: string;
  approvedBy: number | null;
  activate: boolean;
}

export interface VisibilityQuery {
  configurationType: string;
  valueId: number;
  asOf: string;
}

export interface VisibilityResult {
  referenceSetId: number;
  referenceSetCode: string;
  configurationType: string;
  valueId: number;
  determinantType: string;
  asOf: string;
  inSet: boolean;
  units: VisibleUnit[];
}

export interface VisibleUnit {
  businessUnitId: number;
  code: string;
  name: string;
  via: string;
}

export interface VoidInvoiceRequest {
  reason: string;
}

export interface VoidLaborRequest {
}

export interface VoidPOSLineRequest {
  reasonCode: string;
  note?: string;
}

export interface VoidPOSSaleRequest {
  reasonCode: string;
  reason?: string;
  override?: POSOverrideRequest | null;
}

export interface VoidPOSTenderRequest {
  reasonCode?: string;
  note?: string;
}

export interface VoidPartRequest {
  reason: string;
}

export interface WIPClearingLine {
  productionOrderId: number;
  orderNo?: string | null;
  itemId: number;
  status: 'draft' | 'released' | 'in_progress' | 'completed' | 'closed' | 'cancelled';
  maintainedBalance: string;
  ledgerBalance: string;
  difference: string;
}

export interface WIPClearingReportDTO {
  asOf: string;
  lines: WIPClearingLine[];
}

export interface WIPClearingReportQuery {
  companyId: number;
  status?: string;
}

export interface WageBaseDTO {
  id: number;
  code: string;
  name: string;
  jurisdictionId?: number | null;
  seedSource: 'none' | 'assignment_basic';
  includeClasses: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  excludeClasses: ('basic_salary' | 'allowance' | 'cost_of_living_allowance' | 'overtime' | 'bonus' | 'service_charge' | 'benefit_in_kind' | 'reimbursement' | 'statutory' | 'other')[];
  gate?: string;
  floorAmount?: string | null;
  ceilingAmount?: string | null;
  source: 'tenant' | 'pack' | 'legacy';
  sourceCitation?: string;
  packId?: string | null;
  packVersion?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isOverridable: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface WaiveCovenantRequest {
  waiverRef: string;
  waiverExpiresOn?: string;
  attachmentId?: number | null;
  note: string;
  rowVersion: number;
}

export interface WaiveFilingRequest {
  reasonCode: string;
  note?: string;
}

export interface WaiveInspectionRequest {
  reason: string;
  rowVersion: number;
}

export interface WaiveItemRequest {
  rowVersion: number;
  waiverReasonCode: string;
  note?: string;
}

export interface WalletBalanceReportDTO {
  companyId: number;
  asOf: string;
  baseCurrency: string;
  lines: WalletBalanceReportLineDTO[];
  totalFloat: string;
  totalBalance: string;
  totalHeldReceipts: string;
  controlBalance: string;
  difference: string;
  accounts: WalletControlAccountDTO[];
  generatedAt: string;
}

export interface WalletBalanceReportLineDTO {
  walletId: number;
  code: string;
  name: string;
  currency: string;
  kind: 'imprest' | 'fluctuating';
  custodianName?: string;
  glAccountId: number;
  glAccountCode: string;
  floatAmount: string;
  floatBalance: string;
  heldReceipts: string;
  baseBalance: string;
  lastCountedOn?: string | null;
  countOverdue: boolean;
}

export interface WalletBalanceReportQuery {
  companyId?: number;
  asOf?: string;
}

export interface WalletCapturePolicyDTO {
  id: number;
  companyId: number;
  walletId?: number | null;
  categoryCode: string;
  categoryName: string;
  expenseAccountId?: number | null;
  expenseAccountCode?: string;
  defaultTaxCode?: string;
  perTxnLimit?: string | null;
  dailyLimit?: string | null;
  monthlyLimit?: string | null;
  requiresEvidence: boolean;
  evidenceFreeCeiling: string;
  requiresApproval: boolean;
  isBlocked: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  supersededBy?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface WalletCaptureRequest {
  businessDate?: string;
  amount: string;
  categoryCode: string;
  taxCode?: string;
  taxAmount?: string;
  hasTaxInvoice?: boolean;
  supplierTaxId?: string;
  payee?: string;
  description?: string;
  attachmentId?: number | null;
  costCenterId?: number | null;
  projectId?: number | null;
  override?: WalletOverride | null;
  cashBookId?: number | null;
}

export interface WalletCaptureResultDTO {
  transaction: WalletTransactionDTO;
  warnings?: string[];
}

export interface WalletCashBookInput {
  code: string;
  name?: string;
}

export interface WalletControlAccountDTO {
  accountId: number;
  accountLabel: string;
  walletTotal: string;
  glBalance: string;
  difference: string;
  heldReceipts: string;
}

export interface WalletCountDTO {
  id: number;
  walletId: number;
  walletName?: string;
  countNo: string;
  kind: 'periodic' | 'handover' | 'surprise' | 'closing';
  countDate: string;
  shiftCode?: string;
  status: 'draft' | 'counted' | 'posted' | 'cancelled';
  isBlind: boolean;
  countedBy?: number | null;
  countedByName?: string;
  countedAt?: string | null;
  witnessedBy?: number | null;
  witnessedByName?: string;
  postedBy?: number | null;
  postedByName?: string;
  postedAt?: string | null;
  countedTotal: string;
  systemTotal?: string | null;
  varianceTotal?: string | null;
  unapprovedCaptureTotal: string;
  varianceReasonCode?: string;
  varianceNote?: string;
  varianceEntryId?: number | null;
  attachmentId?: number | null;
  varianceOwnerUserId?: number | null;
  varianceOwnerName?: string;
  lines?: WalletCountLineDTO[];
  createdAt: string;
  rowVersion: number;
}

export interface WalletCountLineDTO {
  id: number;
  kind: 'note' | 'coin' | 'voucher' | 'receipt_held' | 'other';
  denomination: string;
  quantity: number;
  lineTotal: string;
  note?: string;
  sortOrder: number;
}

export interface WalletCountLineInput {
  kind: 'note' | 'coin' | 'voucher' | 'receipt_held' | 'other';
  denomination: string;
  quantity: number;
  note?: string;
}

export interface WalletCountSheetDTO {
  id: number;
  walletId: number;
  walletName?: string;
  countNo: string;
  kind: 'periodic' | 'handover' | 'surprise' | 'closing';
  countDate: string;
  shiftCode?: string;
  status: 'draft' | 'counted' | 'posted' | 'cancelled';
  isBlind: boolean;
  countedBy?: number | null;
  countedByName?: string;
  countedAt?: string | null;
  witnessedBy?: number | null;
  witnessedByName?: string;
  postedBy?: number | null;
  postedByName?: string;
  postedAt?: string | null;
  countedTotal: string;
  systemTotal?: string | null;
  varianceTotal?: string | null;
  unapprovedCaptureTotal: string;
  varianceReasonCode?: string;
  varianceNote?: string;
  varianceEntryId?: number | null;
  attachmentId?: number | null;
  varianceOwnerUserId?: number | null;
  varianceOwnerName?: string;
  lines?: WalletCountLineDTO[];
  createdAt: string;
  rowVersion: number;
  currency: string;
  denominations: string[];
}

export interface WalletCustodyTransferDTO {
  id: number;
  walletId: number;
  walletName?: string;
  fromCustodianUserId?: number | null;
  toCustodianUserId?: number | null;
  fromCustodianEmployeeId?: number | null;
  toCustodianEmployeeId?: number | null;
  fromCustodianName?: string;
  toCustodianName?: string;
  effectiveOn: string;
  countId?: number | null;
  countNo?: string;
  status: 'draft' | 'outgoing_signed' | 'accepted' | 'declined';
  outgoingSignedAt?: string | null;
  outgoingSignedBy?: number | null;
  outgoingSignedByName?: string;
  acceptedAt?: string | null;
  acceptedBy?: number | null;
  acceptedByName?: string;
  declinedReason?: string;
  note?: string;
  createdAt: string;
  rowVersion: number;
}

export interface WalletCustodyTransferListDTO {
  items: WalletCustodyTransferDTO[];
}

export interface WalletOverride {
  reasonCode: string;
  note?: string;
}

export interface WalletReplenishmentDTO {
  id: number;
  walletId: number;
  walletName?: string;
  requestNo: string;
  requestedOn: string;
  approvedSpendTotal: string;
  floatShortfall: string;
  requestedAmount: string;
  approvedAmount?: string | null;
  fundingBankAccountId?: number | null;
  fundingBankAccount?: string;
  transferId?: number | null;
  transferNo?: string;
  walletTransactionId?: number | null;
  status: 'draft' | 'proposed' | 'submitted' | 'approved' | 'funded' | 'rejected' | 'failed';
  approvedBy?: number | null;
  approvedByName?: string;
  approvedAt?: string | null;
  rejectReasonCode?: string;
  note?: string;
  justification?: string;
  failedReason?: string;
  fundedAt?: string | null;
  requestedBy?: number | null;
  createdAt: string;
  rowVersion: number;
}

export interface WalletTransactionDTO {
  id: number;
  walletId: number;
  txnNo: string;
  kind: 'spend' | 'approval' | 'rejection' | 'replenish' | 'return' | 'count_variance' | 'transfer_in' | 'transfer_out' | 'reversal';
  businessDate: string;
  shiftCode?: string;
  amount: string;
  currency: string;
  signedAmount: string;
  taxAmount: string;
  recoverableTaxAmount: string;
  taxCode?: string;
  hasTaxInvoice: boolean;
  supplierTaxId?: string;
  categoryCode?: string;
  categoryName?: string;
  expenseAccountId?: number | null;
  expenseAccountCode?: string;
  costCenterId?: number | null;
  projectId?: number | null;
  payee?: string;
  description?: string;
  attachmentId?: number | null;
  cashBookId?: number | null;
  status: 'captured' | 'submitted' | 'approved' | 'rejected' | 'posted';
  approvedBy?: number | null;
  approvedByName?: string;
  approvedAt?: string | null;
  rejectReasonCode?: string;
  note?: string;
  entryId?: number | null;
  reversesId?: number | null;
  sourceDocType?: string;
  sourceDocId?: number | null;
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface WarehouseDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  negativeStockPolicy: 'block' | 'allow';
  isActive: boolean;
  type: 'main' | 'outlet_store' | 'in_transit' | 'quarantine' | 'staging' | 'consignment' | 'van';
  isSystem: boolean;
  locationId?: number | null;
  stockModel: 'perpetual' | 'expense_on_issue';
  inventoryAccountOverrideId?: number | null;
  costCenterId?: number | null;
  defaultPutawayStrategy: 'fixed_then_any' | 'nearest_empty' | 'consolidate' | 'single_bin' | 'none';
  defaultPickStrategy: 'lot_strategy' | 'fixed_bin_first' | 'nearest' | 'single_bin';
  requiresBin: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface WarehouseZoneDTO {
  id: number;
  warehouseId: number;
  code: string;
  name: string;
  storageClass: 'ambient' | 'chilled' | 'frozen' | 'dry' | 'hazardous' | 'bonded' | 'secure';
  pickSequence: number;
  minTemperatureC?: string | null;
  maxTemperatureC?: string | null;
  isActive: boolean;
  rowVersion: number;
}

export interface Warning {
  code: string;
  lineNo: number;
  detail: string;
}

export interface WasteAnalysisDTO {
  companyId: number;
  from: string;
  to: string;
  lines: WasteAnalysisLineDTO[];
  totalValue: string;
  shrinkageValue: string;
  nonShrinkageValue: string;
}

export interface WasteAnalysisLineDTO {
  reasonCode: string;
  reasonDescription?: string;
  category: 'spoilage' | 'preparation' | 'breakage' | 'staff_meal' | 'complimentary' | 'training' | 'expiry' | 'over_production' | 'theft';
  warehouseId: number;
  warehouseCode?: string;
  shiftCode?: string;
  qty: string;
  value: string;
  recordCount: number;
  countsAsShrinkage: boolean;
}

export interface WasteAnalysisQuery {
  companyId: number;
  warehouseId?: number;
  from: string;
  to: string;
}

export interface WasteReasonCodeDTO {
  id: number;
  code: string;
  description: string;
  category: 'spoilage' | 'preparation' | 'breakage' | 'staff_meal' | 'complimentary' | 'training' | 'expiry' | 'over_production' | 'theft';
  expenseAccountId?: number | null;
  expenseAccountCode?: string;
  requiresApproval: boolean;
  requiresAttachment: boolean;
  countsAsShrinkage: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface WasteRecordDTO {
  id: number;
  wasteNo?: string | null;
  companyId: number;
  warehouseId: number;
  warehouseCode?: string;
  recordedOn: string;
  businessDayId?: number | null;
  shiftCode?: string;
  reasonCode: string;
  status: 'draft' | 'pending_approval' | 'posted' | 'rejected';
  memo?: string;
  journalEntryId?: number | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  rejectedReason?: string;
  attachmentId?: number | null;
  totalValue: string;
  countsAsShrinkage: boolean;
  lines?: WasteRecordLineDTO[];
  createdAt: string;
  createdBy?: number | null;
  rowVersion: number;
}

export interface WasteRecordLineDTO {
  id: number;
  itemId: number;
  itemSku?: string;
  binId?: number | null;
  lotId?: number | null;
  qty: string;
  unitCost: string;
  value: string;
  expenseAccountId?: number | null;
  note?: string;
}

export interface WebhookDeliveryDTO {
  id: number;
  endpointId: number;
  outboxId: string;
  topic: string;
  status: string;
  attempts: number;
  responseCode?: number | null;
  error?: string | null;
  createdAt: string;
  deliveredAt?: string | null;
}

export interface WebhookEndpointDTO {
  id: number;
  name: string;
  url: string;
  topics: string[];
  isActive: boolean;
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  failureCount: number;
  createdAt: string;
  rowVersion: number;
}

export interface WinLossReportDTO {
  from: string;
  to: string;
  rows: WinLossReportRow[];
}

export interface WinLossReportRow {
  closeReasonCode: string;
  closeReasonName: string;
  outcome: string;
  count: number;
  amount: string;
}

export interface WinOpportunityRequest {
  rowVersion: number;
  closeReasonCode?: string;
  note?: string;
}

export interface WinRateQuery {
  companyId: number;
  from: string;
  to: string;
}

export interface WinRateReport {
  from: string;
  to: string;
  wonCount: number;
  lostCount: number;
  winRatePercent: string;
  wonAmount: string;
  lostAmount: string;
  winRateByAmountPercent: string;
}

export interface WithdrawConsentRequest {
  reason?: string;
  effectiveFrom?: string;
}

export interface WithdrawPriceSetVersionRequest {
  reasonCode: string;
  note?: string;
}

export interface WithdrawRequest {
  reason: string;
}

export interface WithdrawYearEndCloseRequest {
  reason: string;
  rowVersion: number;
}

export interface WorkCenterCalendarDTO {
  id: number;
  workCenterId: number;
  opcalCalendarId?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  efficiencyPercent: string;
  utilisationPercent: string;
  parallelCapacity: number;
  shifts: WorkCenterShiftDTO[];
  rowVersion: number;
}

export interface WorkCenterDTO {
  id: number;
  companyId: number;
  code: string;
  name: string;
  description?: string;
  costRatePerHour: string;
  overheadRatePerHour: string;
  capacityHoursPerWeek: string;
  isActive: boolean;
  createdAt: string;
  rowVersion: number;
}

export interface WorkCenterExceptionDTO {
  id: number;
  workCenterId: number;
  onDate: string;
  kind: string;
  startMinute?: number | null;
  endMinute?: number | null;
  capacityMinutesOverride?: number | null;
  reason?: string;
  blockedByDocType?: string;
  blockedByDocId?: number | null;
}

export interface WorkCenterLoadDTO {
  workCenterId: number;
  lines: WorkCenterLoadLine[];
}

export interface WorkCenterLoadLine {
  date: string;
  availableMinutes: string;
  plannedMinutes: string;
  loadPct: string;
}

export interface WorkCenterShiftDTO {
  id: number;
  calendarId: number;
  weekday: number;
  shiftCode: string;
  startMinute: number;
  endMinute: number;
  breakMinutes: number;
}

export interface WorkCenterShiftInput {
  weekday: number;
  shiftCode: string;
  startMinute: number;
  endMinute: number;
  breakMinutes?: number;
}

export interface WorkPermitDTO {
  id: number;
  employeeId: number;
  companyId: number;
  jurisdictionId: number;
  permitKind: string;
  quotaCategory?: string;
  permitNoMasked: string;
  issuedOn: string;
  expiresOn: string;
  status: string;
  sponsorName?: string;
  medicalClearedOn?: string | null;
  medicalExpiresOn?: string | null;
  medicalReference?: string;
  note?: string;
  rowVersion: number;
}

export interface WorkforceReorganisationDTO {
  id: number;
  code: string;
  effectiveOn: string;
  status: string;
  changeSummary?: string;
  approvalRequestId?: number | null;
  appliedAt?: string | null;
  rowVersion: number;
  items: WorkforceReorganisationItem[];
}

export interface WorkforceReorganisationItem {
  id: number;
  reorganisationId: number;
  objectType: string;
  objectId: number;
  field: string;
  fromValue?: string | null;
  toValue?: string | null;
  createdAt: string;
}

export interface WorkingTimeBreachDTO {
  employeeId: number;
  employeeName?: string;
  ruleCode: string;
  ruleName: string;
  ruleKind: string;
  threshold: string;
  observed: string;
  windowFrom: string;
  windowTo: string;
  statutory: boolean;
  overridable: boolean;
  severity: string;
  citation?: string;
}

export interface WorkingTimeRuleDTO {
  id: number;
  code: string;
  name: string;
  companyId?: number | null;
  jurisdictionId?: number | null;
  payGroupId?: number | null;
  employmentType?: string;
  ruleKind: string;
  threshold: string;
  windowUnits: number;
  nightStartsAt?: string;
  nightEndsAt?: string;
  source: string;
  sourceCitation?: string;
  packVersion?: number | null;
  severity: string;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  note?: string;
  editable: boolean;
  rowVersion: number;
}

export interface WriteOffAccrualItemRequest {
  reasonCode: string;
  note?: string;
}

export interface WriteOffPMSCommissionStatementLineRequest {
  reasonCode: string;
  note?: string;
  rowVersion: number;
}

export interface WriteOffSuspenseItemRequest {
  entryDate: string;
  expenseAccountId: number;
  expenseDimensions?: DimensionsInput;
  reasonCode: string;
  note?: string;
}

export interface YearEndCloseDTO {
  id: number;
  companyId: number;
  fiscalYear: number;
  status: 'pending_approval' | 'posted' | 'rejected' | 'withdrawn' | 'reversal_pending' | 'reversed';
  journalEntryId?: number | null;
  reversalJournalEntryId?: number | null;
  retainedEarningsAccountId: number;
  preparedBy?: number | null;
  approvedBy?: number | null;
  reverseReason?: string;
  reversalRequestedBy?: number | null;
  reversalApprovedBy?: number | null;
  decisionNote?: string;
  approvalRequestId?: number | null;
  reversalApprovalRequestId?: number | null;
  createdAt: string;
  updatedAt?: string | null;
  rowVersion: number;
}

export interface YearEndCloseQuery {
  companyId: number;
  fiscalYear: number;
  retainedEarningsAccountId?: number;
  basis?: 'entry' | 'posting';
}

export interface YearEndStatementDTO {
  id: number;
  companyId: number;
  jurisdictionId: number;
  employeeId: number;
  taxYear: number;
  periodStart: string;
  periodEnd: string;
  currency: string;
  grossTotal: string;
  deductionTotal: string;
  employerTotal: string;
  netTotal: string;
  statutoryTotals?: Record<string, string>;
  status: 'draft' | 'issued' | 'superseded';
  attachmentId?: number | null;
  issuedAt?: string | null;
  rowVersion: number;
}

export interface crossChargeBoundary {
  fromCompanyId: number;
  toCompanyId: number;
  debit: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  credit: string; // money: server-computed decimal string — never parse as a JS number (CLAUDE.md §5)
  balanced: boolean;
}

export interface eliminationAccountEntry {
  accountId: number;
  side: string;
}

export interface matchInvoiceLineWire {
  invoiceLineId: number;
  poLineId: number;
}

export interface matchInvoiceRequestDTO {
  invoiceId: number;
  poId: number;
  lines: matchInvoiceLineWire[];
}

export interface portalAcceptInviteRequest {
  tenantSlug: string;
  token: string;
  name?: string | null;
  password: string;
}

export interface portalCompanyListQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
}

export interface portalForgotPasswordRequest {
  tenantSlug: string;
  email: string;
}

export interface portalLoginRequest {
  tenantSlug: string;
  email: string;
  password: string;
  deviceName?: string;
}

export interface portalOrderListQuery {
  cursor?: string;
  limit?: number;
  companyId: number;
}

export interface portalPaymentStatusQuery {
  cursor?: string;
  limit?: number;
  companyId?: number;
}

export interface portalResetPasswordRequest {
  tenantSlug: string;
  token: string;
  newPassword: string;
}

export interface portalStatementQuery {
  companyId: number;
  asOf?: string;
  openOnly?: boolean;
}

export type TokenGetter = () => string | null | undefined;

export interface ApiClientOptions {
  baseUrl: string;
  getToken: TokenGetter;
  fetch?: typeof fetch;
}

export interface RequestInit_ {
  query?: Record<string, string | number | boolean | undefined> | undefined;
  body?: unknown | undefined;
  idempotencyKey?: string | undefined;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenGetter;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.getToken = options.getToken;
    // Bound deliberately: a bare fetch detached from globalThis throws
    // "Illegal invocation" in browsers when called as a method.
    this.fetchImpl = options.fetch ?? ((...args) => fetch(...args));
  }

  async request<TResponse>(method: string, path: string, init?: RequestInit_): Promise<TResponse> {
    const url = new URL(this.baseUrl + path);
    if (init?.query) {
      for (const [key, value] of Object.entries(init.query)) {
        // null means "no filter" exactly like undefined: sending the literal string "null"
        // made the server refuse the whole query (422) for every wrapper that passed null.
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      }
    }

    const headers: Record<string, string> = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (init?.idempotencyKey) {
      headers['Idempotency-Key'] = init.idempotencyKey;
    }

    // Built incrementally rather than with an inline conditional. Under
    // exactOptionalPropertyTypes an explicit undefined is not assignable to an
    // optional property, so the key must be absent rather than present-and-undefined.
    const request: RequestInit = { method, headers };
    if (init?.body !== undefined) {
      if (init.body instanceof FormData) {
        // A multipart upload (CLAUDE.md §7's generated client, api.MultipartBody):
        // the body IS the FormData, sent as-is. Content-Type is deliberately left
        // unset — the fetch spec requires the caller not to set it for a FormData
        // body, because only the runtime knows the multipart boundary it is about
        // to generate; setting it here would omit that boundary and the server
        // could never parse the parts back apart.
        request.body = init.body;
      } else {
        headers['Content-Type'] = 'application/json';
        request.body = JSON.stringify(init.body);
      }
    }

    const response = await this.fetchImpl(url.toString(), request);

    if (!response.ok) {
      const problem = (await response.json().catch(() => undefined)) as Problem | undefined;
      throw problem ?? new Error(`request failed: ${response.status}`);
    }
    if (response.status === 204) {
      return undefined as TResponse;
    }
    return (await response.json()) as TResponse;
  }
}

export async function systemHealthLive(
  client: ApiClient,
): Promise<HealthStatus> {
  return client.request<HealthStatus>('GET', `/healthz`, {
  });
}

export async function systemHealthReady(
  client: ApiClient,
): Promise<HealthStatus> {
  return client.request<HealthStatus>('GET', `/readyz`, {
  });
}

export async function authLogin(
  client: ApiClient,
  body: LoginRequest,
): Promise<LoginResult> {
  return client.request<LoginResult>('POST', `/api/v1/auth/login`, {
    body,
  });
}

export async function authRefresh(
  client: ApiClient,
  body: RefreshRequest,
): Promise<LoginResult> {
  return client.request<LoginResult>('POST', `/api/v1/auth/refresh`, {
    body,
  });
}

export async function authRevoke(
  client: ApiClient,
  body: RefreshRequest,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/auth/revoke`, {
    body,
  });
}

export async function authForgotPassword(
  client: ApiClient,
  body: ForgotPasswordRequest,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/auth/forgot-password`, {
    body,
  });
}

export async function authResetPassword(
  client: ApiClient,
  body: ResetPasswordRequest,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/auth/reset-password`, {
    body,
  });
}

export async function authAcceptInvite(
  client: ApiClient,
  body: AcceptInviteRequest,
): Promise<LoginResult> {
  return client.request<LoginResult>('POST', `/api/v1/auth/accept-invite`, {
    body,
  });
}

export async function authMe(
  client: ApiClient,
): Promise<MeResponse> {
  return client.request<MeResponse>('GET', `/api/v1/auth/me`, {
  });
}

export async function authLogout(
  client: ApiClient,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/auth/logout`, {
  });
}

export async function authSessionsList(
  client: ApiClient,
): Promise<SessionsResponse> {
  return client.request<SessionsResponse>('GET', `/api/v1/auth/sessions`, {
  });
}

export async function authSessionsRevoke(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/auth/sessions/${encodeURIComponent(params.id)}`, {
  });
}

export async function authChangePassword(
  client: ApiClient,
  body: ChangePasswordRequest,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/auth/change-password`, {
    body,
  });
}

export async function authStepUp(
  client: ApiClient,
  body: StepUpRequest,
): Promise<StepUpResponse> {
  return client.request<StepUpResponse>('POST', `/api/v1/auth/step-up`, {
    body,
  });
}

export async function authMfaEnroll(
  client: ApiClient,
): Promise<MFAEnrollResponse> {
  return client.request<MFAEnrollResponse>('POST', `/api/v1/auth/mfa/enroll`, {
  });
}

export async function authMfaConfirm(
  client: ApiClient,
  body: MFAConfirmRequest,
): Promise<MFAConfirmResponse> {
  return client.request<MFAConfirmResponse>('POST', `/api/v1/auth/mfa/confirm`, {
    body,
  });
}

export async function authMfaDisable(
  client: ApiClient,
  body: MFADisableRequest,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/auth/mfa`, {
    body,
  });
}

export async function usersList(
  client: ApiClient,
  query: ListUsersQuery,
): Promise<PageUserDTO> {
  return client.request<PageUserDTO>('GET', `/api/v1/users`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function usersDirectory(
  client: ApiClient,
  query: IdentityUserDirectoryQuery,
): Promise<PageIdentityUserDirectoryEntryDTO> {
  return client.request<PageIdentityUserDirectoryEntryDTO>('GET', `/api/v1/users/directory`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function usersCreate(
  client: ApiClient,
  body: CreateUserRequest,
  idempotencyKey?: string,
): Promise<CreateUserResponse> {
  return client.request<CreateUserResponse>('POST', `/api/v1/users`, {
    body,
    idempotencyKey,
  });
}

export async function usersServicePrincipalsCreate(
  client: ApiClient,
  body: CreateServicePrincipalRequest,
  idempotencyKey?: string,
): Promise<UserDTO> {
  return client.request<UserDTO>('POST', `/api/v1/service-principals`, {
    body,
    idempotencyKey,
  });
}

export async function usersGet(
  client: ApiClient,
  params: { id: string },
): Promise<UserDTO> {
  return client.request<UserDTO>('GET', `/api/v1/users/${encodeURIComponent(params.id)}`, {
  });
}

export async function usersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUserRequest,
): Promise<UserDTO> {
  return client.request<UserDTO>('PATCH', `/api/v1/users/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function usersDisable(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/users/${encodeURIComponent(params.id)}/disable`, {
  });
}

export async function usersEnable(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/users/${encodeURIComponent(params.id)}/enable`, {
  });
}

export async function usersResendInvite(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/users/${encodeURIComponent(params.id)}/resend-invite`, {
  });
}

export async function usersRolesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUserRolesRequest,
): Promise<void> {
  return client.request<void>('PUT', `/api/v1/users/${encodeURIComponent(params.id)}/roles`, {
    body,
  });
}

export async function rolesList(
  client: ApiClient,
): Promise<RoleDTO[]> {
  return client.request<RoleDTO[]>('GET', `/api/v1/roles`, {
  });
}

export async function authSsoProviders(
  client: ApiClient,
  query: PublicSSOProvidersQuery,
): Promise<SSOProviderPublicDTO[]> {
  return client.request<SSOProviderPublicDTO[]>('GET', `/api/v1/auth/sso/providers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authSsoStart(
  client: ApiClient,
  body: SSOStartRequest,
): Promise<SSOStartResponse> {
  return client.request<SSOStartResponse>('POST', `/api/v1/auth/sso/start`, {
    body,
  });
}

export async function authSsoCallback(
  client: ApiClient,
  query: SSOCallbackQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/auth/sso/callback`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authSsoComplete(
  client: ApiClient,
  body: SSOCompleteRequest,
): Promise<LoginResult> {
  return client.request<LoginResult>('POST', `/api/v1/auth/sso/complete`, {
    body,
  });
}

export async function ssoProvidersList(
  client: ApiClient,
  query: ListSSOProvidersQuery,
): Promise<PageSSOProviderDTO> {
  return client.request<PageSSOProviderDTO>('GET', `/api/v1/sso-providers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function ssoProvidersCreate(
  client: ApiClient,
  body: CreateSSOProviderRequest,
  idempotencyKey?: string,
): Promise<SSOProviderDTO> {
  return client.request<SSOProviderDTO>('POST', `/api/v1/sso-providers`, {
    body,
    idempotencyKey,
  });
}

export async function ssoProvidersGet(
  client: ApiClient,
  params: { id: string },
): Promise<SSOProviderDTO> {
  return client.request<SSOProviderDTO>('GET', `/api/v1/sso-providers/${encodeURIComponent(params.id)}`, {
  });
}

export async function ssoProvidersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSSOProviderRequest,
): Promise<SSOProviderDTO> {
  return client.request<SSOProviderDTO>('PATCH', `/api/v1/sso-providers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function ssoProvidersDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/sso-providers/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminAuthLogin(
  client: ApiClient,
  body: AdminLoginRequest,
): Promise<AuthTokenResponse> {
  return client.request<AuthTokenResponse>('POST', `/admin/api/v1/auth/login`, {
    body,
  });
}

export async function adminAuthRefresh(
  client: ApiClient,
  body: AdminRefreshRequest,
): Promise<AuthTokenResponse> {
  return client.request<AuthTokenResponse>('POST', `/admin/api/v1/auth/refresh`, {
    body,
  });
}

export async function adminAuthLogout(
  client: ApiClient,
): Promise<void> {
  return client.request<void>('POST', `/admin/api/v1/auth/logout`, {
  });
}

export async function adminAuthAcceptInvite(
  client: ApiClient,
  body: AdminAcceptInviteRequest,
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/auth/accept-invite`, {
    body,
  });
}

export async function adminAuthForgotPassword(
  client: ApiClient,
  body: AdminForgotPasswordRequest,
): Promise<void> {
  return client.request<void>('POST', `/admin/api/v1/auth/forgot-password`, {
    body,
  });
}

export async function adminAuthResetPassword(
  client: ApiClient,
  body: AdminResetPasswordRequest,
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/auth/reset-password`, {
    body,
  });
}

export async function adminAuthMe(
  client: ApiClient,
): Promise<User> {
  return client.request<User>('GET', `/admin/api/v1/auth/me`, {
  });
}

export async function adminAuthStepUp(
  client: ApiClient,
  body: AdminStepUpRequest,
): Promise<AdminStepUpResponse> {
  return client.request<AdminStepUpResponse>('POST', `/admin/api/v1/auth/step-up`, {
    body,
  });
}

export async function adminAuthMfaEnroll(
  client: ApiClient,
): Promise<AdminMFAEnrollResponse> {
  return client.request<AdminMFAEnrollResponse>('POST', `/admin/api/v1/auth/mfa/enroll`, {
  });
}

export async function adminAuthMfaConfirm(
  client: ApiClient,
  body: AdminMFAConfirmRequest,
): Promise<AdminMFAConfirmResponse> {
  return client.request<AdminMFAConfirmResponse>('POST', `/admin/api/v1/auth/mfa/confirm`, {
    body,
  });
}

export async function adminTenantsList(
  client: ApiClient,
  query: TenantListQuery,
): Promise<PageTenantSummary> {
  return client.request<PageTenantSummary>('GET', `/admin/api/v1/tenants`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTenantsCreate(
  client: ApiClient,
  body: CreateTenantRequest,
  idempotencyKey?: string,
): Promise<Tenant> {
  return client.request<Tenant>('POST', `/admin/api/v1/tenants`, {
    body,
    idempotencyKey,
  });
}

export async function adminTenantsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TenantDetail> {
  return client.request<TenantDetail>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminTenantsProvisioning(
  client: ApiClient,
  params: { id: string },
): Promise<ProvisioningStep[]> {
  return client.request<ProvisioningStep[]>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/provisioning`, {
  });
}

export async function adminTenantsResume(
  client: ApiClient,
  params: { id: string },
): Promise<Tenant> {
  return client.request<Tenant>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/resume`, {
  });
}

export async function adminTenantsSuspend(
  client: ApiClient,
  params: { id: string },
  body: SuspendTenantRequest,
): Promise<Tenant> {
  return client.request<Tenant>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/suspend`, {
    body,
  });
}

export async function adminTenantsReactivate(
  client: ApiClient,
  params: { id: string },
): Promise<Tenant> {
  return client.request<Tenant>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/reactivate`, {
  });
}

export async function adminTenantsUpdate(
  client: ApiClient,
  params: { id: string },
  body: PatchTenantRequest,
): Promise<Tenant> {
  return client.request<Tenant>('PATCH', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminTenantsMigrate(
  client: ApiClient,
  params: { id: string },
): Promise<TenantMigrateResponse> {
  return client.request<TenantMigrateResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/migrate`, {
  });
}

export async function adminTenantsUsage(
  client: ApiClient,
  params: { id: string },
  query: TenantUsageQuery,
): Promise<UsageDaily[]> {
  return client.request<UsageDaily[]>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/usage`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminPlansList(
  client: ApiClient,
): Promise<Plan[]> {
  return client.request<Plan[]>('GET', `/admin/api/v1/plans`, {
  });
}

export async function adminPlansCreate(
  client: ApiClient,
  body: PlanCreateRequest,
): Promise<Plan> {
  return client.request<Plan>('POST', `/admin/api/v1/plans`, {
    body,
  });
}

export async function adminPlansGet(
  client: ApiClient,
  params: { id: string },
): Promise<Plan> {
  return client.request<Plan>('GET', `/admin/api/v1/plans/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminPlansUpdate(
  client: ApiClient,
  params: { id: string },
  body: PlanUpdateRequest,
): Promise<Plan> {
  return client.request<Plan>('PATCH', `/admin/api/v1/plans/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminSupportSessionsCreate(
  client: ApiClient,
  params: { id: string },
  body: SupportSessionRequest,
): Promise<SupportSessionResponse> {
  return client.request<SupportSessionResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/support-session`, {
    body,
  });
}

export async function adminSupportSessionsRevoke(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/admin/api/v1/support-sessions/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminTenantUsersList(
  client: ApiClient,
  params: { id: string },
  query: TenantUsersQuery,
): Promise<PageTenantUserSummary> {
  return client.request<PageTenantUserSummary>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTenantUsersCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateTenantUserRequest,
  idempotencyKey?: string,
): Promise<CreateTenantUserResponse> {
  return client.request<CreateTenantUserResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users`, {
    body,
    idempotencyKey,
  });
}

export async function adminTenantUsersSetPassword(
  client: ApiClient,
  params: { id: string; userId: string },
  body: SetTenantUserPasswordRequest,
): Promise<SetTenantUserPasswordResponse> {
  return client.request<SetTenantUserPasswordResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/set-password`, {
    body,
  });
}

export async function adminTenantUsersResetPassword(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/reset-password`, {
  });
}

export async function adminTenantUsersUnlock(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/unlock`, {
  });
}

export async function adminTenantUsersResetMfa(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/reset-mfa`, {
  });
}

export async function adminTenantUsersResendInvite(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/resend-invite`, {
  });
}

export async function adminTenantUsersDeactivate(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/deactivate`, {
  });
}

export async function adminTenantUsersRevokeSessions(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<AckResponse> {
  return client.request<AckResponse>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/users/${encodeURIComponent(params.userId)}/revoke-sessions`, {
  });
}

export async function adminTenantAuditList(
  client: ApiClient,
  params: { id: string },
  query: TenantAuditQuery,
): Promise<PageLogEntry> {
  return client.request<PageLogEntry>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/audit`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminAdminsList(
  client: ApiClient,
): Promise<User[]> {
  return client.request<User[]>('GET', `/admin/api/v1/admins`, {
  });
}

export async function adminAdminsCreate(
  client: ApiClient,
  body: AdminCreateRequest,
): Promise<User> {
  return client.request<User>('POST', `/admin/api/v1/admins`, {
    body,
  });
}

export async function adminAdminsUpdate(
  client: ApiClient,
  params: { id: string },
  body: AdminUpdateRequest,
): Promise<User> {
  return client.request<User>('PATCH', `/admin/api/v1/admins/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminAdminsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/admin/api/v1/admins/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminDashboardGet(
  client: ApiClient,
): Promise<DashboardResponse> {
  return client.request<DashboardResponse>('GET', `/admin/api/v1/dashboard`, {
  });
}

export async function adminGeoCountriesList(
  client: ApiClient,
  query: ListCountriesQuery,
): Promise<PageCountry> {
  return client.request<PageCountry>('GET', `/admin/api/v1/geo/countries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminGeoCountriesCreate(
  client: ApiClient,
  body: CountryRequest,
  idempotencyKey?: string,
): Promise<Country> {
  return client.request<Country>('POST', `/admin/api/v1/geo/countries`, {
    body,
    idempotencyKey,
  });
}

export async function adminGeoCountriesGet(
  client: ApiClient,
  params: { id: string },
): Promise<Country> {
  return client.request<Country>('GET', `/admin/api/v1/geo/countries/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminGeoCountriesUpdate(
  client: ApiClient,
  params: { id: string },
  body: CountryUpdateRequest,
): Promise<Country> {
  return client.request<Country>('PATCH', `/admin/api/v1/geo/countries/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminGeoCountriesImport(
  client: ApiClient,
  body: ImportRequest,
  idempotencyKey?: string,
): Promise<GeoImportResult> {
  return client.request<GeoImportResult>('POST', `/admin/api/v1/geo/countries/import`, {
    body,
    idempotencyKey,
  });
}

export async function adminGeoSubdivisionsList(
  client: ApiClient,
  query: ListSubdivisionsQuery,
): Promise<PageSubdivision> {
  return client.request<PageSubdivision>('GET', `/admin/api/v1/geo/subdivisions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminGeoSubdivisionsCreate(
  client: ApiClient,
  body: SubdivisionRequest,
): Promise<Subdivision> {
  return client.request<Subdivision>('POST', `/admin/api/v1/geo/subdivisions`, {
    body,
  });
}

export async function adminGeoSubdivisionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: SubdivisionUpdateRequest,
): Promise<Subdivision> {
  return client.request<Subdivision>('PATCH', `/admin/api/v1/geo/subdivisions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminGeoLocalitiesList(
  client: ApiClient,
  query: ListLocalitiesQuery,
): Promise<PageLocality> {
  return client.request<PageLocality>('GET', `/admin/api/v1/geo/localities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminGeoLocalitiesCreate(
  client: ApiClient,
  body: LocalityRequest,
): Promise<Locality> {
  return client.request<Locality>('POST', `/admin/api/v1/geo/localities`, {
    body,
  });
}

export async function adminGeoLocalitiesUpdate(
  client: ApiClient,
  params: { id: string },
  body: LocalityUpdateRequest,
): Promise<Locality> {
  return client.request<Locality>('PATCH', `/admin/api/v1/geo/localities/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminGeoTree(
  client: ApiClient,
  query: GeoTreeQuery,
): Promise<GeoTree> {
  return client.request<GeoTree>('GET', `/admin/api/v1/geo/tree`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTaxPacksList(
  client: ApiClient,
  query: ListPacksRequest,
): Promise<PagePack> {
  return client.request<PagePack>('GET', `/admin/api/v1/tax-packs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTaxPacksCreate(
  client: ApiClient,
  body: CreatePackRequest,
  idempotencyKey?: string,
): Promise<Pack> {
  return client.request<Pack>('POST', `/admin/api/v1/tax-packs`, {
    body,
    idempotencyKey,
  });
}

export async function adminTaxPacksGet(
  client: ApiClient,
  params: { id: string },
): Promise<PackDetailDTO> {
  return client.request<PackDetailDTO>('GET', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminTaxPacksUpdate(
  client: ApiClient,
  params: { id: string },
  body: PackUpdateRequest,
): Promise<Pack> {
  return client.request<Pack>('PATCH', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function adminTaxPacksDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminTaxPacksContentGet(
  client: ApiClient,
  params: { id: string },
): Promise<PackContent> {
  return client.request<PackContent>('GET', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/content`, {
  });
}

export async function adminTaxPacksContentPut(
  client: ApiClient,
  params: { id: string },
  body: PackContent,
): Promise<PackValidation> {
  return client.request<PackValidation>('PUT', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/content`, {
    body,
  });
}

export async function adminTaxPacksDiff(
  client: ApiClient,
  params: { id: string },
  query: DiffQuery,
): Promise<PackDiff> {
  return client.request<PackDiff>('GET', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/diff`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTaxPacksValidate(
  client: ApiClient,
  params: { id: string },
): Promise<PackValidation> {
  return client.request<PackValidation>('POST', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/validate`, {
  });
}

export async function adminTaxPacksSubmit(
  client: ApiClient,
  params: { id: string },
  body: NoteRequest,
): Promise<Pack> {
  return client.request<Pack>('POST', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function adminTaxPacksPublish(
  client: ApiClient,
  params: { id: string },
  body: NoteRequest,
): Promise<Pack> {
  return client.request<Pack>('POST', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/publish`, {
    body,
  });
}

export async function adminTaxPacksWithdraw(
  client: ApiClient,
  params: { id: string },
  body: WithdrawRequest,
): Promise<Pack> {
  return client.request<Pack>('POST', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/withdraw`, {
    body,
  });
}

export async function adminTaxPacksSectionsList(
  client: ApiClient,
  params: { id: string; section: string },
  query: SectionListQuery,
): Promise<Record<string, unknown>[]> {
  return client.request<Record<string, unknown>[]>('GET', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/sections/${encodeURIComponent(params.section)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTaxPacksSectionsCreate(
  client: ApiClient,
  params: { id: string; section: string },
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return client.request<Record<string, unknown>>('POST', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/sections/${encodeURIComponent(params.section)}`, {
    body,
  });
}

export async function adminTaxPacksSectionsUpdate(
  client: ApiClient,
  params: { id: string; section: string; rowId: string },
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return client.request<Record<string, unknown>>('PATCH', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/sections/${encodeURIComponent(params.section)}/${encodeURIComponent(params.rowId)}`, {
    body,
  });
}

export async function adminTaxPacksSectionsDelete(
  client: ApiClient,
  params: { id: string; section: string; rowId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/admin/api/v1/tax-packs/${encodeURIComponent(params.id)}/sections/${encodeURIComponent(params.section)}/${encodeURIComponent(params.rowId)}`, {
  });
}

export async function adminTenantsTaxPacksList(
  client: ApiClient,
  params: { id: string },
  query: TenantTaxPacksQuery,
): Promise<PageTenantActivationDTO> {
  return client.request<PageTenantActivationDTO>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/tax-packs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminTenantsTaxPacksActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateTenantPackRequest,
  idempotencyKey?: string,
): Promise<TenantActivationDTO> {
  return client.request<TenantActivationDTO>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/tax-packs/activate`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsInbox(
  client: ApiClient,
  query: ListInboxQuery,
): Promise<PageInboxItem> {
  return client.request<PageInboxItem>('GET', `/api/v1/approvals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function approvalsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RequestDTO> {
  return client.request<RequestDTO>('GET', `/api/v1/approvals/${encodeURIComponent(params.id)}`, {
  });
}

export async function approvalsApprove(
  client: ApiClient,
  params: { id: string },
  body: ActRequest,
  idempotencyKey?: string,
): Promise<RequestDTO> {
  return client.request<RequestDTO>('POST', `/api/v1/approvals/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectRequest,
  idempotencyKey?: string,
): Promise<RequestDTO> {
  return client.request<RequestDTO>('POST', `/api/v1/approvals/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsBulkApprove(
  client: ApiClient,
  body: BulkApproveRequest,
  idempotencyKey?: string,
): Promise<BulkApproveResponse> {
  return client.request<BulkApproveResponse>('POST', `/api/v1/approvals/bulk-approve`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsDelegate(
  client: ApiClient,
  params: { id: string },
  body: DelegateRequest,
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/approvals/${encodeURIComponent(params.id)}/delegate`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsEmergency(
  client: ApiClient,
  params: { id: string },
  body: EmergencyApprovalRequest,
  idempotencyKey?: string,
): Promise<RequestDTO> {
  return client.request<RequestDTO>('POST', `/api/v1/approvals/${encodeURIComponent(params.id)}/emergency`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsRatify(
  client: ApiClient,
  params: { id: string },
  body: RatifyRequest,
  idempotencyKey?: string,
): Promise<RequestDTO> {
  return client.request<RequestDTO>('POST', `/api/v1/approvals/${encodeURIComponent(params.id)}/ratify`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsChain(
  client: ApiClient,
  params: { id: string },
): Promise<ApprovalChainDTO> {
  return client.request<ApprovalChainDTO>('GET', `/api/v1/approvals/${encodeURIComponent(params.id)}/chain`, {
  });
}

export async function approvalsDelegationsList(
  client: ApiClient,
): Promise<DelegationDTO[]> {
  return client.request<DelegationDTO[]>('GET', `/api/v1/approval-delegations`, {
  });
}

export async function approvalsDelegationsCreate(
  client: ApiClient,
  body: CreateDelegationRequest,
  idempotencyKey?: string,
): Promise<DelegationDTO> {
  return client.request<DelegationDTO>('POST', `/api/v1/approval-delegations`, {
    body,
    idempotencyKey,
  });
}

export async function approvalsDelegationsRevoke(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/approval-delegations/${encodeURIComponent(params.id)}`, {
  });
}

export async function approvalPoliciesList(
  client: ApiClient,
  query: ListPoliciesQuery,
): Promise<PagePolicyDTO> {
  return client.request<PagePolicyDTO>('GET', `/api/v1/approval-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function approvalPoliciesCreate(
  client: ApiClient,
  body: CreatePolicyRequest,
  idempotencyKey?: string,
): Promise<PolicyDTO> {
  return client.request<PolicyDTO>('POST', `/api/v1/approval-policies`, {
    body,
    idempotencyKey,
  });
}

export async function approvalPoliciesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PolicyDTO> {
  return client.request<PolicyDTO>('GET', `/api/v1/approval-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function approvalPoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePolicyRequest,
): Promise<PolicyDTO> {
  return client.request<PolicyDTO>('PATCH', `/api/v1/approval-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function approvalPoliciesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/approval-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function auditLogList(
  client: ApiClient,
  query: ExplorerQuery,
): Promise<PageLogEntry> {
  return client.request<PageLogEntry>('GET', `/api/v1/audit`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function auditLogTimeline(
  client: ApiClient,
  query: TimelineQuery,
): Promise<LogEntry[]> {
  return client.request<LogEntry[]>('GET', `/api/v1/audit/timeline`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function auditLogActions(
  client: ApiClient,
): Promise<ActionsResponse> {
  return client.request<ActionsResponse>('GET', `/api/v1/audit/actions`, {
  });
}

export async function auditLogVerify(
  client: ApiClient,
): Promise<ChainResult> {
  return client.request<ChainResult>('GET', `/api/v1/audit/verify`, {
  });
}

export async function auditLogExport(
  client: ApiClient,
  query: ExportQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/audit/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authProfileUpdate(
  client: ApiClient,
  body: ProfileUpdateRequest,
): Promise<UserProfileDTO> {
  return client.request<UserProfileDTO>('PATCH', `/api/v1/auth/profile`, {
    body,
  });
}

export async function notificationsList(
  client: ApiClient,
  query: ListNotificationsQuery,
): Promise<PageNotificationDTO> {
  return client.request<PageNotificationDTO>('GET', `/api/v1/notifications`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function notificationsUnreadCount(
  client: ApiClient,
): Promise<UnreadCountResponse> {
  return client.request<UnreadCountResponse>('GET', `/api/v1/notifications/unread-count`, {
  });
}

export async function notificationsMarkRead(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/notifications/${encodeURIComponent(params.id)}/read`, {
  });
}

export async function notificationsMarkAllRead(
  client: ApiClient,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/notifications/read-all`, {
  });
}

export async function notificationsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/notifications/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminBillingSubscriptionsList(
  client: ApiClient,
  query: SubscriptionListQuery,
): Promise<PageSubscriptionDTO> {
  return client.request<PageSubscriptionDTO>('GET', `/admin/api/v1/subscriptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminBillingSubscriptionsGetForTenant(
  client: ApiClient,
  params: { id: string },
): Promise<SubscriptionDTO> {
  return client.request<SubscriptionDTO>('GET', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/subscription`, {
  });
}

export async function adminBillingSubscriptionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateSubscriptionRequest,
  idempotencyKey?: string,
): Promise<SubscriptionDTO> {
  return client.request<SubscriptionDTO>('POST', `/admin/api/v1/tenants/${encodeURIComponent(params.id)}/subscription`, {
    body,
    idempotencyKey,
  });
}

export async function adminBillingSubscriptionsChangePlan(
  client: ApiClient,
  params: { id: string },
  body: ChangePlanRequest,
): Promise<SubscriptionDTO> {
  return client.request<SubscriptionDTO>('POST', `/admin/api/v1/subscriptions/${encodeURIComponent(params.id)}/change-plan`, {
    body,
  });
}

export async function adminBillingSubscriptionsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelSubscriptionRequest,
): Promise<SubscriptionDTO> {
  return client.request<SubscriptionDTO>('POST', `/admin/api/v1/subscriptions/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function adminBillingSubscriptionsReactivate(
  client: ApiClient,
  params: { id: string },
): Promise<SubscriptionDTO> {
  return client.request<SubscriptionDTO>('POST', `/admin/api/v1/subscriptions/${encodeURIComponent(params.id)}/reactivate`, {
  });
}

export async function adminBillingInvoicesList(
  client: ApiClient,
  query: InvoiceListQuery,
): Promise<PageInvoiceDTO> {
  return client.request<PageInvoiceDTO>('GET', `/admin/api/v1/invoices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function adminBillingInvoicesGet(
  client: ApiClient,
  params: { id: string },
): Promise<InvoiceDetailDTO> {
  return client.request<InvoiceDetailDTO>('GET', `/admin/api/v1/invoices/${encodeURIComponent(params.id)}`, {
  });
}

export async function adminBillingInvoicesGenerate(
  client: ApiClient,
  body: GenerateInvoicesRequest,
  idempotencyKey?: string,
): Promise<GenerateInvoicesResponse> {
  return client.request<GenerateInvoicesResponse>('POST', `/admin/api/v1/invoices/generate`, {
    body,
    idempotencyKey,
  });
}

export async function adminBillingInvoicesVoid(
  client: ApiClient,
  params: { id: string },
  body: VoidInvoiceRequest,
): Promise<InvoiceDTO> {
  return client.request<InvoiceDTO>('POST', `/admin/api/v1/invoices/${encodeURIComponent(params.id)}/void`, {
    body,
  });
}

export async function adminBillingPaymentsRecord(
  client: ApiClient,
  params: { id: string },
  body: RecordPaymentRequest,
  idempotencyKey?: string,
): Promise<PaymentDTO> {
  return client.request<PaymentDTO>('POST', `/admin/api/v1/invoices/${encodeURIComponent(params.id)}/payments`, {
    body,
    idempotencyKey,
  });
}

export async function adminBillingPaymentsList(
  client: ApiClient,
  query: PaymentListQuery,
): Promise<PagePaymentDTO> {
  return client.request<PagePaymentDTO>('GET', `/admin/api/v1/payments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgCompaniesList(
  client: ApiClient,
  query: ListCompaniesQuery,
): Promise<PageCompany> {
  return client.request<PageCompany>('GET', `/api/v1/companies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgCompaniesCreate(
  client: ApiClient,
  body: CreateCompanyRequest,
  idempotencyKey?: string,
): Promise<Company> {
  return client.request<Company>('POST', `/api/v1/companies`, {
    body,
    idempotencyKey,
  });
}

export async function orgCompaniesGet(
  client: ApiClient,
  params: { id: string },
): Promise<Company> {
  return client.request<Company>('GET', `/api/v1/companies/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgCompaniesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCompanyRequest,
): Promise<Company> {
  return client.request<Company>('PATCH', `/api/v1/companies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgCompaniesDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/companies/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgCompaniesHierarchy(
  client: ApiClient,
  params: { id: string },
): Promise<Hierarchy> {
  return client.request<Hierarchy>('GET', `/api/v1/companies/${encodeURIComponent(params.id)}/hierarchy`, {
  });
}

export async function orgUnitsReorder(
  client: ApiClient,
  body: ReorderRequest,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/org/reorder`, {
    body,
  });
}

export async function orgFiscalPeriodsList(
  client: ApiClient,
  query: ListPeriodsQuery,
): Promise<PagePeriodDTO> {
  return client.request<PagePeriodDTO>('GET', `/api/v1/fiscal-periods`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFiscalPeriodsGenerate(
  client: ApiClient,
  body: CreatePeriodsRequest,
  idempotencyKey?: string,
): Promise<PeriodsCreated> {
  return client.request<PeriodsCreated>('POST', `/api/v1/fiscal-periods`, {
    body,
    idempotencyKey,
  });
}

export async function orgFiscalPeriodsReopen(
  client: ApiClient,
  params: { id: string },
  body: ReopenPeriodRequest,
): Promise<PeriodDTO> {
  return client.request<PeriodDTO>('POST', `/api/v1/fiscal-periods/${encodeURIComponent(params.id)}/reopen`, {
    body,
  });
}

export async function orgAccountsList(
  client: ApiClient,
  query: ListAccountsQuery,
): Promise<PageAccount> {
  return client.request<PageAccount>('GET', `/api/v1/accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgAccountsCreate(
  client: ApiClient,
  body: CreateAccountRequest,
  idempotencyKey?: string,
): Promise<Account> {
  return client.request<Account>('POST', `/api/v1/accounts`, {
    body,
    idempotencyKey,
  });
}

export async function orgAccountsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Account> {
  return client.request<Account>('GET', `/api/v1/accounts/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgAccountsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateAccountRequest,
): Promise<Account> {
  return client.request<Account>('PATCH', `/api/v1/accounts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgAccountsTree(
  client: ApiClient,
  params: { id: string },
): Promise<AccountTreeResponse> {
  return client.request<AccountTreeResponse>('GET', `/api/v1/companies/${encodeURIComponent(params.id)}/accounts/tree`, {
  });
}

export async function orgAccountsFromTemplate(
  client: ApiClient,
  params: { id: string },
  body: FromTemplateRequest,
  idempotencyKey?: string,
): Promise<AccountTreeResponse> {
  return client.request<AccountTreeResponse>('POST', `/api/v1/companies/${encodeURIComponent(params.id)}/accounts/from-template`, {
    body,
    idempotencyKey,
  });
}

export async function orgCoaTemplatesList(
  client: ApiClient,
  query: ListTemplatesQuery,
): Promise<PageCOATemplate> {
  return client.request<PageCOATemplate>('GET', `/api/v1/coa-templates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgCoaTemplatesCreate(
  client: ApiClient,
  body: CreateTemplateRequest,
  idempotencyKey?: string,
): Promise<TemplateDTO> {
  return client.request<TemplateDTO>('POST', `/api/v1/coa-templates`, {
    body,
    idempotencyKey,
  });
}

export async function orgCoaTemplatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<TemplateDTO> {
  return client.request<TemplateDTO>('GET', `/api/v1/coa-templates/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgOpeningBalancesImport(
  client: ApiClient,
  file: File | Blob,
  fields: { companyId: number; asOfDate?: string; currency?: string; note?: string },
): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('companyId', String(fields.companyId));
  if (fields.asOfDate !== undefined) {
    form.append('asOfDate', String(fields.asOfDate));
  }
  if (fields.currency !== undefined) {
    form.append('currency', String(fields.currency));
  }
  if (fields.note !== undefined) {
    form.append('note', String(fields.note));
  }
  return client.request<ImportResult>('POST', `/api/v1/opening-balances/import`, {
    body: form,
  });
}

export async function orgOpeningBalancesSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ImportDTO> {
  return client.request<ImportDTO>('POST', `/api/v1/opening-balances/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function orgOpeningBalancesList(
  client: ApiClient,
  query: ListImportsQuery,
): Promise<PageImportDTO> {
  return client.request<PageImportDTO>('GET', `/api/v1/opening-balances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgOpeningBalancesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ImportDTO> {
  return client.request<ImportDTO>('GET', `/api/v1/opening-balances/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgGoLiveChecklistGet(
  client: ApiClient,
): Promise<Checklist> {
  return client.request<Checklist>('GET', `/api/v1/go-live-checklist`, {
  });
}

export async function orgDivisionsList(
  client: ApiClient,
  query: ListUnitsQuery,
): Promise<PageUnit> {
  return client.request<PageUnit>('GET', `/api/v1/divisions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsCreate(
  client: ApiClient,
  body: CreateUnitRequest,
  idempotencyKey?: string,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/divisions`, {
    body,
    idempotencyKey,
  });
}

export async function orgDivisionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Unit> {
  return client.request<Unit>('GET', `/api/v1/divisions/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgDivisionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUnitRequest,
): Promise<Unit> {
  return client.request<Unit>('PATCH', `/api/v1/divisions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgDivisionsDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/divisions/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLedgersList(
  client: ApiClient,
  query: ListLedgersQuery,
): Promise<PageLedger> {
  return client.request<PageLedger>('GET', `/api/v1/ledgers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDesignationsList(
  client: ApiClient,
  query: ListDesignationsQuery,
): Promise<PageDesignationDTO> {
  return client.request<PageDesignationDTO>('GET', `/api/v1/company-designations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDesignationsCreate(
  client: ApiClient,
  body: CreateDesignationRequest,
  idempotencyKey?: string,
): Promise<DesignationDTO> {
  return client.request<DesignationDTO>('POST', `/api/v1/company-designations`, {
    body,
    idempotencyKey,
  });
}

export async function orgDesignationsSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeDesignationRequest,
): Promise<DesignationDTO> {
  return client.request<DesignationDTO>('POST', `/api/v1/company-designations/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function orgFilingsList(
  client: ApiClient,
  query: ListFilingsQuery,
): Promise<PageFilingDTO> {
  return client.request<PageFilingDTO>('GET', `/api/v1/filing-obligations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFilingsWaive(
  client: ApiClient,
  params: { id: string },
  body: WaiveFilingRequest,
): Promise<FilingDTO> {
  return client.request<FilingDTO>('POST', `/api/v1/filing-obligations/${encodeURIComponent(params.id)}/waive`, {
    body,
  });
}

export async function orgCompaniesActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateCompanyRequest,
): Promise<Company> {
  return client.request<Company>('POST', `/api/v1/companies/${encodeURIComponent(params.id)}/activate`, {
    body,
  });
}

export async function orgCompaniesDissolve(
  client: ApiClient,
  params: { id: string },
  body: DissolveRequest,
): Promise<Company> {
  return client.request<Company>('POST', `/api/v1/companies/${encodeURIComponent(params.id)}/dissolve`, {
    body,
  });
}

export async function orgBranchesList(
  client: ApiClient,
  query: ListUnitsQuery,
): Promise<PageUnit> {
  return client.request<PageUnit>('GET', `/api/v1/branches`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgBranchesCreate(
  client: ApiClient,
  body: CreateUnitRequest,
  idempotencyKey?: string,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/branches`, {
    body,
    idempotencyKey,
  });
}

export async function orgBranchesGet(
  client: ApiClient,
  params: { id: string },
): Promise<Unit> {
  return client.request<Unit>('GET', `/api/v1/branches/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgBranchesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUnitRequest,
): Promise<Unit> {
  return client.request<Unit>('PATCH', `/api/v1/branches/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgBranchesDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/branches/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDepartmentsList(
  client: ApiClient,
  query: ListUnitsQuery,
): Promise<PageUnit> {
  return client.request<PageUnit>('GET', `/api/v1/departments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDepartmentsCreate(
  client: ApiClient,
  body: CreateUnitRequest,
  idempotencyKey?: string,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/departments`, {
    body,
    idempotencyKey,
  });
}

export async function orgDepartmentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Unit> {
  return client.request<Unit>('GET', `/api/v1/departments/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgDepartmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUnitRequest,
): Promise<Unit> {
  return client.request<Unit>('PATCH', `/api/v1/departments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgDepartmentsDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/departments/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgCostCentersList(
  client: ApiClient,
  query: ListUnitsQuery,
): Promise<PageUnit> {
  return client.request<PageUnit>('GET', `/api/v1/cost-centers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgCostCentersCreate(
  client: ApiClient,
  body: CreateUnitRequest,
  idempotencyKey?: string,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/cost-centers`, {
    body,
    idempotencyKey,
  });
}

export async function orgCostCentersGet(
  client: ApiClient,
  params: { id: string },
): Promise<Unit> {
  return client.request<Unit>('GET', `/api/v1/cost-centers/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgCostCentersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUnitRequest,
): Promise<Unit> {
  return client.request<Unit>('PATCH', `/api/v1/cost-centers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgCostCentersDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/cost-centers/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgProfitCentersList(
  client: ApiClient,
  query: ListUnitsQuery,
): Promise<PageUnit> {
  return client.request<PageUnit>('GET', `/api/v1/profit-centers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgProfitCentersCreate(
  client: ApiClient,
  body: CreateUnitRequest,
  idempotencyKey?: string,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/profit-centers`, {
    body,
    idempotencyKey,
  });
}

export async function orgProfitCentersGet(
  client: ApiClient,
  params: { id: string },
): Promise<Unit> {
  return client.request<Unit>('GET', `/api/v1/profit-centers/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgProfitCentersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUnitRequest,
): Promise<Unit> {
  return client.request<Unit>('PATCH', `/api/v1/profit-centers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgProfitCentersDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/profit-centers/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgBusinessUnitsList(
  client: ApiClient,
  query: ListUnitsQuery,
): Promise<PageUnit> {
  return client.request<PageUnit>('GET', `/api/v1/business-units`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgBusinessUnitsCreate(
  client: ApiClient,
  body: CreateUnitRequest,
  idempotencyKey?: string,
): Promise<Unit> {
  return client.request<Unit>('POST', `/api/v1/business-units`, {
    body,
    idempotencyKey,
  });
}

export async function orgBusinessUnitsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Unit> {
  return client.request<Unit>('GET', `/api/v1/business-units/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgBusinessUnitsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateUnitRequest,
): Promise<Unit> {
  return client.request<Unit>('PATCH', `/api/v1/business-units/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgBusinessUnitsDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/business-units/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLocationsList(
  client: ApiClient,
  query: ListLocationsQuery,
): Promise<PageLocation> {
  return client.request<PageLocation>('GET', `/api/v1/locations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLocationsCreate(
  client: ApiClient,
  body: CreateLocationRequest,
  idempotencyKey?: string,
): Promise<Location> {
  return client.request<Location>('POST', `/api/v1/locations`, {
    body,
    idempotencyKey,
  });
}

export async function orgLocationsTree(
  client: ApiClient,
  query: LocationTreeQuery,
): Promise<LocationTreeDTO> {
  return client.request<LocationTreeDTO>('GET', `/api/v1/locations/tree`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLocationsValidateAddress(
  client: ApiClient,
  body: AddressInput,
): Promise<AddressResult> {
  return client.request<AddressResult>('POST', `/api/v1/locations/validate-address`, {
    body,
  });
}

export async function orgAddressRulesGet(
  client: ApiClient,
  params: { country: string },
): Promise<AddressRulesDTO> {
  return client.request<AddressRulesDTO>('GET', `/api/v1/address-rules/${encodeURIComponent(params.country)}`, {
  });
}

export async function orgLocationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Location> {
  return client.request<Location>('GET', `/api/v1/locations/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgLocationsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLocationRequest,
): Promise<Location> {
  return client.request<Location>('PATCH', `/api/v1/locations/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgLocationsDelete(
  client: ApiClient,
  params: { id: string },
  query: DeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/locations/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgJobsList(
  client: ApiClient,
  query: ListJobsQuery,
): Promise<PageJob> {
  return client.request<PageJob>('GET', `/api/v1/jobs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgJobsCreate(
  client: ApiClient,
  body: JobRequest,
  idempotencyKey?: string,
): Promise<Job> {
  return client.request<Job>('POST', `/api/v1/jobs`, {
    body,
    idempotencyKey,
  });
}

export async function orgJobsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Job> {
  return client.request<Job>('GET', `/api/v1/jobs/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgJobsUpdate(
  client: ApiClient,
  params: { id: string },
  body: JobRequest,
): Promise<Job> {
  return client.request<Job>('PATCH', `/api/v1/jobs/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgJobGradesList(
  client: ApiClient,
  query: ListGradesQuery,
): Promise<PageJobGradeDTO> {
  return client.request<PageJobGradeDTO>('GET', `/api/v1/job-grades`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgJobGradesCreate(
  client: ApiClient,
  body: GradeRequest,
  idempotencyKey?: string,
): Promise<JobGradeDTO> {
  return client.request<JobGradeDTO>('POST', `/api/v1/job-grades`, {
    body,
    idempotencyKey,
  });
}

export async function orgJobGradesSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeGradeRequest,
): Promise<JobGradeDTO> {
  return client.request<JobGradeDTO>('POST', `/api/v1/job-grades/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function orgJobGradesHistory(
  client: ApiClient,
  params: { id: string },
): Promise<JobGradeDTO[]> {
  return client.request<JobGradeDTO[]>('GET', `/api/v1/job-grades/${encodeURIComponent(params.id)}/history`, {
  });
}

export async function orgPositionsList(
  client: ApiClient,
  query: ListPositionsQuery,
): Promise<PagePositionDTO> {
  return client.request<PagePositionDTO>('GET', `/api/v1/positions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgPositionsCreate(
  client: ApiClient,
  body: CreatePositionRequest,
  idempotencyKey?: string,
): Promise<PositionDTO> {
  return client.request<PositionDTO>('POST', `/api/v1/positions`, {
    body,
    idempotencyKey,
  });
}

export async function orgPositionsChart(
  client: ApiClient,
  query: PositionChartQuery,
): Promise<PositionChartDTO> {
  return client.request<PositionChartDTO>('GET', `/api/v1/positions/chart`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgPositionsGet(
  client: ApiClient,
  params: { id: string },
  query: PositionGetQuery,
): Promise<PositionDTO> {
  return client.request<PositionDTO>('GET', `/api/v1/positions/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgPositionsSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedePositionRequest,
): Promise<PositionDTO> {
  return client.request<PositionDTO>('POST', `/api/v1/positions/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function orgPositionsAbolish(
  client: ApiClient,
  params: { id: string },
  body: AbolishRequest,
): Promise<PositionDTO> {
  return client.request<PositionDTO>('POST', `/api/v1/positions/${encodeURIComponent(params.id)}/abolish`, {
    body,
  });
}

export async function orgPositionsHistory(
  client: ApiClient,
  params: { id: string },
): Promise<PositionDTO[]> {
  return client.request<PositionDTO[]>('GET', `/api/v1/positions/${encodeURIComponent(params.id)}/history`, {
  });
}

export async function orgAssignmentsList(
  client: ApiClient,
  query: ListAssignmentsQuery,
): Promise<PageAssignmentDTO> {
  return client.request<PageAssignmentDTO>('GET', `/api/v1/position-assignments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgAssignmentsCreate(
  client: ApiClient,
  body: AssignRequest,
  idempotencyKey?: string,
): Promise<AssignmentDTO> {
  return client.request<AssignmentDTO>('POST', `/api/v1/position-assignments`, {
    body,
    idempotencyKey,
  });
}

export async function orgAssignmentsEnd(
  client: ApiClient,
  params: { id: string },
  body: EndAssignmentRequest,
): Promise<AssignmentDTO> {
  return client.request<AssignmentDTO>('POST', `/api/v1/position-assignments/${encodeURIComponent(params.id)}/end`, {
    body,
  });
}

export async function foundationEnterpriseCreate(
  client: ApiClient,
  body: CreateEnterpriseRequest,
  idempotencyKey?: string,
): Promise<EnterpriseDTO> {
  return client.request<EnterpriseDTO>('POST', `/api/v1/foundation/enterprises`, {
    body,
    idempotencyKey,
  });
}

export async function foundationEnterpriseList(
  client: ApiClient,
  query: ListEnterprisesQuery,
): Promise<PageEnterpriseDTO> {
  return client.request<PageEnterpriseDTO>('GET', `/api/v1/foundation/enterprises`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationEnterpriseCurrent(
  client: ApiClient,
  query: CurrentQuery,
): Promise<EnterpriseCurrentDTO> {
  return client.request<EnterpriseCurrentDTO>('GET', `/api/v1/foundation/enterprises/current`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationEnterpriseUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEnterpriseRequest,
): Promise<EnterpriseDTO> {
  return client.request<EnterpriseDTO>('PUT', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function foundationEnterpriseDefaultsGet(
  client: ApiClient,
  params: { id: string },
): Promise<EffectiveDefaultDTO[]> {
  return client.request<EffectiveDefaultDTO[]>('GET', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/defaults`, {
  });
}

export async function foundationEnterpriseDefaultsSet(
  client: ApiClient,
  params: { id: string },
  body: SetDefaultRequest,
): Promise<SetDefaultResponse> {
  return client.request<SetDefaultResponse>('PUT', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/defaults`, {
    body,
  });
}

export async function foundationEnterpriseFlagToggle(
  client: ApiClient,
  params: { id: string; key: string },
  body: ToggleFlagRequest,
): Promise<FlagDTO> {
  return client.request<FlagDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/flags/${encodeURIComponent(params.key)}`, {
    body,
  });
}

export async function foundationEnterpriseFlagsList(
  client: ApiClient,
  params: { id: string },
): Promise<FlagDTO[]> {
  return client.request<FlagDTO[]>('GET', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/flags`, {
  });
}

export async function foundationEnterprisePackActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivatePackRequest,
  idempotencyKey?: string,
): Promise<IndustryPackDTO> {
  return client.request<IndustryPackDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/industry-packs`, {
    body,
    idempotencyKey,
  });
}

export async function foundationEnterprisePacksList(
  client: ApiClient,
  params: { id: string },
): Promise<IndustryPackDTO[]> {
  return client.request<IndustryPackDTO[]>('GET', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/industry-packs`, {
  });
}

export async function foundationEnterprisePackDeactivate(
  client: ApiClient,
  params: { id: string; code: string },
): Promise<IndustryPackDTO> {
  return client.request<IndustryPackDTO>('DELETE', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/industry-packs/${encodeURIComponent(params.code)}`, {
  });
}

export async function foundationEnterpriseSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<EnterpriseDTO> {
  return client.request<EnterpriseDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function foundationEnterpriseActivate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<EnterpriseDTO> {
  return client.request<EnterpriseDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/activate`, {
    idempotencyKey,
  });
}

export async function foundationEnterpriseReject(
  client: ApiClient,
  params: { id: string },
  body: RejectEnterpriseRequest,
): Promise<EnterpriseDTO> {
  return client.request<EnterpriseDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/reject`, {
    body,
  });
}

export async function foundationEnterpriseVersions(
  client: ApiClient,
  params: { id: string },
): Promise<VersionHistoryDTO[]> {
  return client.request<VersionHistoryDTO[]>('GET', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/versions`, {
  });
}

export async function foundationGuidedConfigGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProposeResult> {
  return client.request<ProposeResult>('GET', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/guided-config`, {
  });
}

export async function foundationGuidedConfigPropose(
  client: ApiClient,
  params: { id: string },
  body: ProposeInput,
  idempotencyKey?: string,
): Promise<ProposeResult> {
  return client.request<ProposeResult>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/guided-config/propose`, {
    body,
    idempotencyKey,
  });
}

export async function foundationGuidedConfigValidate(
  client: ApiClient,
  params: { id: string },
  body: ValidateRequest,
): Promise<GuidedValidationResult> {
  return client.request<GuidedValidationResult>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/guided-config/validate`, {
    body,
  });
}

export async function foundationGuidedConfigSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitGuidedRequest,
  idempotencyKey?: string,
): Promise<GuidedRunDTO> {
  return client.request<GuidedRunDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/guided-config/submit`, {
    body,
    idempotencyKey,
  });
}

export async function foundationGuidedConfigApply(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ApplyResult> {
  return client.request<ApplyResult>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/guided-config/apply`, {
    idempotencyKey,
  });
}

export async function foundationGuidedConfigReject(
  client: ApiClient,
  params: { id: string },
  body: RejectGuidedRequest,
): Promise<GuidedRunDTO> {
  return client.request<GuidedRunDTO>('POST', `/api/v1/foundation/enterprises/${encodeURIComponent(params.id)}/guided-config/reject`, {
    body,
  });
}

export async function foundationReferenceSetsCreate(
  client: ApiClient,
  body: CreateReferenceSetRequest,
  idempotencyKey?: string,
): Promise<ReferenceSetDTO> {
  return client.request<ReferenceSetDTO>('POST', `/api/v1/foundation/reference-sets`, {
    body,
    idempotencyKey,
  });
}

export async function foundationReferenceSetsList(
  client: ApiClient,
  query: ListReferenceSetsQuery,
): Promise<PageReferenceSetDTO> {
  return client.request<PageReferenceSetDTO>('GET', `/api/v1/foundation/reference-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationReferenceSetsVersion(
  client: ApiClient,
  params: { id: string },
  body: VersionReferenceSetRequest,
  idempotencyKey?: string,
): Promise<ReferenceSetVersionDTO> {
  return client.request<ReferenceSetVersionDTO>('POST', `/api/v1/foundation/reference-sets/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function foundationDeterminantsDeclare(
  client: ApiClient,
  body: DeclareDeterminantRequest,
  idempotencyKey?: string,
): Promise<DeterminantDTO> {
  return client.request<DeterminantDTO>('POST', `/api/v1/foundation/determinants`, {
    body,
    idempotencyKey,
  });
}

export async function foundationReferenceSetsAssign(
  client: ApiClient,
  params: { id: string },
  body: AssignBusinessUnitsRequest,
  idempotencyKey?: string,
): Promise<SetAssignmentDTO[]> {
  return client.request<SetAssignmentDTO[]>('POST', `/api/v1/foundation/reference-sets/${encodeURIComponent(params.id)}/assignments`, {
    body,
    idempotencyKey,
  });
}

export async function foundationReferenceDataList(
  client: ApiClient,
  params: { type: string },
  query: ReferenceDataQuery,
): Promise<PageReferenceDataPageItem> {
  return client.request<PageReferenceDataPageItem>('GET', `/api/v1/foundation/reference-data/${encodeURIComponent(params.type)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationReferenceSetsCompare(
  client: ApiClient,
  query: CompareSetsQuery,
): Promise<CompareSetsResult> {
  return client.request<CompareSetsResult>('GET', `/api/v1/foundation/reference-sets/compare`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationReferenceSetsVisibility(
  client: ApiClient,
  params: { id: string },
  query: VisibilityQuery,
): Promise<VisibilityResult> {
  return client.request<VisibilityResult>('GET', `/api/v1/foundation/reference-sets/${encodeURIComponent(params.id)}/visibility`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationReferenceSetsEndDate(
  client: ApiClient,
  params: { id: string },
  body: EndDateSetRequest,
): Promise<EndDateSetResult> {
  return client.request<EndDateSetResult>('POST', `/api/v1/foundation/reference-sets/${encodeURIComponent(params.id)}/end-date`, {
    body,
  });
}

export async function foundationHierarchiesCreate(
  client: ApiClient,
  body: CreateHierarchyRequest,
  idempotencyKey?: string,
): Promise<HierarchyDTO> {
  return client.request<HierarchyDTO>('POST', `/api/v1/foundation/hierarchies`, {
    body,
    idempotencyKey,
  });
}

export async function foundationHierarchiesCreateVersion(
  client: ApiClient,
  params: { id: string },
  body: CreateHierarchyVersionRequest,
  idempotencyKey?: string,
): Promise<HierarchyVersionDTO> {
  return client.request<HierarchyVersionDTO>('POST', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function foundationHierarchiesArrangeNodes(
  client: ApiClient,
  params: { id: string; versionId: string },
  body: ArrangeNodesRequest,
  idempotencyKey?: string,
): Promise<ArrangeNodesResult> {
  return client.request<ArrangeNodesResult>('POST', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions/${encodeURIComponent(params.versionId)}/nodes`, {
    body,
    idempotencyKey,
  });
}

export async function foundationHierarchiesValidate(
  client: ApiClient,
  params: { id: string; versionId: string },
): Promise<ValidationResult> {
  return client.request<ValidationResult>('POST', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions/${encodeURIComponent(params.versionId)}/validate`, {
  });
}

export async function foundationHierarchiesSubmitVersion(
  client: ApiClient,
  params: { id: string; versionId: string },
  body: SubmitHierarchyVersionRequest,
  idempotencyKey?: string,
): Promise<HierarchyVersionDTO> {
  return client.request<HierarchyVersionDTO>('POST', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions/${encodeURIComponent(params.versionId)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function foundationHierarchiesCompare(
  client: ApiClient,
  params: { id: string },
  query: CompareVersionsQuery,
): Promise<CompareVersionsResult> {
  return client.request<CompareVersionsResult>('GET', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/compare`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationHierarchiesPublish(
  client: ApiClient,
  params: { id: string; versionId: string },
  body: PublishHierarchyVersionRequest,
  idempotencyKey?: string,
): Promise<PublishHierarchyVersionResult> {
  return client.request<PublishHierarchyVersionResult>('POST', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions/${encodeURIComponent(params.versionId)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function foundationHierarchiesResolve(
  client: ApiClient,
  params: { id: string },
  query: ResolveHierarchyQuery,
): Promise<ResolveHierarchyResult> {
  return client.request<ResolveHierarchyResult>('GET', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationHierarchiesRollback(
  client: ApiClient,
  params: { id: string },
  body: RollbackHierarchyRequest,
  idempotencyKey?: string,
): Promise<HierarchyVersionDTO> {
  return client.request<HierarchyVersionDTO>('POST', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/rollback`, {
    body,
    idempotencyKey,
  });
}

export async function foundationHierarchiesList(
  client: ApiClient,
  query: ListHierarchiesQuery,
): Promise<PageHierarchyDTO> {
  return client.request<PageHierarchyDTO>('GET', `/api/v1/foundation/hierarchies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationHierarchiesGet(
  client: ApiClient,
  params: { id: string },
): Promise<HierarchyDTO> {
  return client.request<HierarchyDTO>('GET', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}`, {
  });
}

export async function foundationHierarchiesListVersions(
  client: ApiClient,
  params: { id: string },
): Promise<HierarchyVersionDTO[]> {
  return client.request<HierarchyVersionDTO[]>('GET', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions`, {
  });
}

export async function foundationHierarchiesListNodes(
  client: ApiClient,
  params: { id: string; versionId: string },
): Promise<HierarchyNodeDTO[]> {
  return client.request<HierarchyNodeDTO[]>('GET', `/api/v1/foundation/hierarchies/${encodeURIComponent(params.id)}/versions/${encodeURIComponent(params.versionId)}/nodes`, {
  });
}

export async function foundationMastershipList(
  client: ApiClient,
): Promise<MastershipEntry[]> {
  return client.request<MastershipEntry[]>('GET', `/api/v1/foundation/mastership-register`, {
  });
}

export async function foundationMastershipUpsert(
  client: ApiClient,
  body: UpsertMastershipRequest,
  idempotencyKey?: string,
): Promise<MastershipEntry> {
  return client.request<MastershipEntry>('PUT', `/api/v1/foundation/mastership-register`, {
    body,
    idempotencyKey,
  });
}

export async function foundationClassificationList(
  client: ApiClient,
): Promise<DataClassification[]> {
  return client.request<DataClassification[]>('GET', `/api/v1/foundation/data-classifications`, {
  });
}

export async function foundationClassificationUpsert(
  client: ApiClient,
  body: UpsertClassificationRequest,
  idempotencyKey?: string,
): Promise<DataClassification> {
  return client.request<DataClassification>('PUT', `/api/v1/foundation/data-classifications`, {
    body,
    idempotencyKey,
  });
}

export async function foundationDependencyPostureList(
  client: ApiClient,
): Promise<DependencyPosture[]> {
  return client.request<DependencyPosture[]>('GET', `/api/v1/foundation/dependency-postures`, {
  });
}

export async function foundationDependencyPostureUpsert(
  client: ApiClient,
  body: UpsertDependencyPostureRequest,
  idempotencyKey?: string,
): Promise<DependencyPosture> {
  return client.request<DependencyPosture>('PUT', `/api/v1/foundation/dependency-postures`, {
    body,
    idempotencyKey,
  });
}

export async function coaStructuresCreate(
  client: ApiClient,
  body: CreateStructureRequest,
  idempotencyKey?: string,
): Promise<StructureDTO> {
  return client.request<StructureDTO>('POST', `/api/v1/foundation/charts-of-accounts`, {
    body,
    idempotencyKey,
  });
}

export async function coaStructuresList(
  client: ApiClient,
  query: ListStructuresQuery,
): Promise<PageStructureDTO> {
  return client.request<PageStructureDTO>('GET', `/api/v1/foundation/charts-of-accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function coaStructuresGet(
  client: ApiClient,
  params: { id: string },
): Promise<StructureDTO> {
  return client.request<StructureDTO>('GET', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}`, {
  });
}

export async function coaStructuresUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateStructureRequest,
): Promise<StructureDTO> {
  return client.request<StructureDTO>('PATCH', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function coaStructuresActivate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<StructureDTO> {
  return client.request<StructureDTO>('POST', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/activate`, {
    idempotencyKey,
  });
}

export async function coaSegmentsCreate(
  client: ApiClient,
  params: { id: string },
  body: SegmentRequest,
  idempotencyKey?: string,
): Promise<SegmentDTO> {
  return client.request<SegmentDTO>('POST', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/segments`, {
    body,
    idempotencyKey,
  });
}

export async function coaSegmentsList(
  client: ApiClient,
  params: { id: string },
): Promise<SegmentDTO[]> {
  return client.request<SegmentDTO[]>('GET', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/segments`, {
  });
}

export async function coaSegmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSegmentRequest,
): Promise<SegmentDTO> {
  return client.request<SegmentDTO>('PATCH', `/api/v1/foundation/coa-segments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function coaSegmentsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/foundation/coa-segments/${encodeURIComponent(params.id)}`, {
  });
}

export async function coaCrossValidationRulesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateCrossValidationRuleRequest,
  idempotencyKey?: string,
): Promise<CrossValidationRuleDTO> {
  return client.request<CrossValidationRuleDTO>('POST', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/cross-validation-rules`, {
    body,
    idempotencyKey,
  });
}

export async function coaCrossValidationRulesList(
  client: ApiClient,
  params: { id: string },
  query: PageQuery,
): Promise<PageCrossValidationRuleDTO> {
  return client.request<PageCrossValidationRuleDTO>('GET', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/cross-validation-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function coaCrossValidationRulesScan(
  client: ApiClient,
  params: { id: string; rid: string },
  idempotencyKey?: string,
): Promise<RuleScanDTO> {
  return client.request<RuleScanDTO>('POST', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/cross-validation-rules/${encodeURIComponent(params.rid)}/scan`, {
    idempotencyKey,
  });
}

export async function coaSegmentSecurityCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateSegmentSecurityRuleRequest,
  idempotencyKey?: string,
): Promise<SegmentSecurityRuleDTO> {
  return client.request<SegmentSecurityRuleDTO>('POST', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/segment-security`, {
    body,
    idempotencyKey,
  });
}

export async function coaSegmentSecurityList(
  client: ApiClient,
  params: { id: string },
  query: PageQuery,
): Promise<PageSegmentSecurityRuleDTO> {
  return client.request<PageSegmentSecurityRuleDTO>('GET', `/api/v1/foundation/charts-of-accounts/${encodeURIComponent(params.id)}/segment-security`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function coaSegmentSecurityPreview(
  client: ApiClient,
  params: { id: string },
): Promise<SecurityPreviewDTO[]> {
  return client.request<SecurityPreviewDTO[]>('GET', `/api/v1/foundation/coa-segments/${encodeURIComponent(params.id)}/security-preview`, {
  });
}

export async function coaValueSetsCreate(
  client: ApiClient,
  body: CreateValueSetRequest,
  idempotencyKey?: string,
): Promise<ValueSetDTO> {
  return client.request<ValueSetDTO>('POST', `/api/v1/foundation/value-sets`, {
    body,
    idempotencyKey,
  });
}

export async function coaValueSetsList(
  client: ApiClient,
  query: ListValueSetsQuery,
): Promise<PageValueSetDTO> {
  return client.request<PageValueSetDTO>('GET', `/api/v1/foundation/value-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function coaValueSetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ValueSetDTO> {
  return client.request<ValueSetDTO>('GET', `/api/v1/foundation/value-sets/${encodeURIComponent(params.id)}`, {
  });
}

export async function coaSegmentValuesCreate(
  client: ApiClient,
  params: { id: string },
  body: SegmentValueRequest,
  idempotencyKey?: string,
): Promise<SegmentValueDTO> {
  return client.request<SegmentValueDTO>('POST', `/api/v1/foundation/value-sets/${encodeURIComponent(params.id)}/values`, {
    body,
    idempotencyKey,
  });
}

export async function coaSegmentValuesList(
  client: ApiClient,
  params: { id: string },
  query: PageQuery,
): Promise<PageSegmentValueDTO> {
  return client.request<PageSegmentValueDTO>('GET', `/api/v1/foundation/value-sets/${encodeURIComponent(params.id)}/values`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function coaSegmentValuesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSegmentValueRequest,
): Promise<SegmentValueDTO> {
  return client.request<SegmentValueDTO>('PATCH', `/api/v1/foundation/segment-values/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function coaCombinationsValidate(
  client: ApiClient,
  query: ValidateCombinationQuery,
): Promise<CombinationResult> {
  return client.request<CombinationResult>('GET', `/api/v1/foundation/account-combinations/validate`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLegalEntitiesList(
  client: ApiClient,
  query: ListLegalEntitiesQuery,
): Promise<PageLegalEntityDTO> {
  return client.request<PageLegalEntityDTO>('GET', `/api/v1/foundation/legal-entities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLegalEntitiesCreate(
  client: ApiClient,
  body: CreateLegalEntityRequest,
  idempotencyKey?: string,
): Promise<LegalEntityDTO> {
  return client.request<LegalEntityDTO>('POST', `/api/v1/foundation/legal-entities`, {
    body,
    idempotencyKey,
  });
}

export async function orgLegalEntitiesGet(
  client: ApiClient,
  params: { id: string },
  query: GetLegalEntityQuery,
): Promise<LegalEntityDTO> {
  return client.request<LegalEntityDTO>('GET', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLegalEntitiesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLegalEntityRequest,
): Promise<LegalEntityDTO> {
  return client.request<LegalEntityDTO>('PATCH', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgLegalEntitiesMapLedgers(
  client: ApiClient,
  params: { id: string },
  body: MapLedgersRequest,
  idempotencyKey?: string,
): Promise<LedgerMapDTO[]> {
  return client.request<LedgerMapDTO[]>('POST', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/ledgers`, {
    body,
    idempotencyKey,
  });
}

export async function orgLegalEntitiesAssignBalancingSegments(
  client: ApiClient,
  params: { id: string },
  body: AssignBalancingSegmentsRequest,
  idempotencyKey?: string,
): Promise<BalancingSegmentDTO[]> {
  return client.request<BalancingSegmentDTO[]>('POST', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/balancing-segments`, {
    body,
    idempotencyKey,
  });
}

export async function orgLegalEntitiesSetDesignations(
  client: ApiClient,
  params: { id: string },
  body: SetDesignationsRequest,
  idempotencyKey?: string,
): Promise<DesignationsResult> {
  return client.request<DesignationsResult>('POST', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/designations`, {
    body,
    idempotencyKey,
  });
}

export async function orgLegalEntitiesSubmit(
  client: ApiClient,
  params: { id: string },
): Promise<LegalEntityDTO> {
  return client.request<LegalEntityDTO>('POST', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/submit`, {
  });
}

export async function orgLegalEntitiesActivate(
  client: ApiClient,
  params: { id: string },
): Promise<LegalEntityDTO> {
  return client.request<LegalEntityDTO>('POST', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/activate`, {
  });
}

export async function orgLegalEntitiesDissolve(
  client: ApiClient,
  params: { id: string },
  body: DissolveLegalEntityRequest,
): Promise<LegalEntityDTO> {
  return client.request<LegalEntityDTO>('POST', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/dissolve`, {
    body,
  });
}

export async function orgLegalEntitiesConsolidationPath(
  client: ApiClient,
  params: { id: string },
  query: ConsolidationPathQuery,
): Promise<ConsolidationPathDTO> {
  return client.request<ConsolidationPathDTO>('GET', `/api/v1/foundation/legal-entities/${encodeURIComponent(params.id)}/consolidation-path`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLegislativeDataGroupsList(
  client: ApiClient,
  query: ListLegislativeDataGroupsQuery,
): Promise<PageLegislativeDataGroupDTO> {
  return client.request<PageLegislativeDataGroupDTO>('GET', `/api/v1/foundation/legislative-data-groups`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgLegislativeDataGroupsCreate(
  client: ApiClient,
  body: CreateLegislativeDataGroupRequest,
  idempotencyKey?: string,
): Promise<LegislativeDataGroupDTO> {
  return client.request<LegislativeDataGroupDTO>('POST', `/api/v1/foundation/legislative-data-groups`, {
    body,
    idempotencyKey,
  });
}

export async function orgPsusCreateTaxReportingUnit(
  client: ApiClient,
  params: { id: string },
  body: CreateTaxReportingUnitRequest,
  idempotencyKey?: string,
): Promise<TaxReportingUnitDTO> {
  return client.request<TaxReportingUnitDTO>('POST', `/api/v1/foundation/psus/${encodeURIComponent(params.id)}/tax-reporting-units`, {
    body,
    idempotencyKey,
  });
}

export async function orgPsusListTaxReportingUnits(
  client: ApiClient,
  params: { id: string },
  query: ListTaxReportingUnitsQuery,
): Promise<PageTaxReportingUnitDTO> {
  return client.request<PageTaxReportingUnitDTO>('GET', `/api/v1/foundation/psus/${encodeURIComponent(params.id)}/tax-reporting-units`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgPsusRedesignateLdg(
  client: ApiClient,
  params: { id: string },
  body: RedesignatePSURequest,
  idempotencyKey?: string,
): Promise<PSURedesignationDTO> {
  return client.request<PSURedesignationDTO>('POST', `/api/v1/foundation/psus/${encodeURIComponent(params.id)}/redesignate-ldg`, {
    body,
    idempotencyKey,
  });
}

export async function orgOwnershipList(
  client: ApiClient,
  query: ListOwnershipQuery,
): Promise<PageOwnershipDTO> {
  return client.request<PageOwnershipDTO>('GET', `/api/v1/foundation/ownership`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgOwnershipRecord(
  client: ApiClient,
  body: RecordOwnershipRequest,
  idempotencyKey?: string,
): Promise<OwnershipDTO> {
  return client.request<OwnershipDTO>('PUT', `/api/v1/foundation/ownership`, {
    body,
    idempotencyKey,
  });
}

export async function orgIntercompanySimulate(
  client: ApiClient,
  body: SimulateIntercompanyRequest,
): Promise<IntercompanySimulationResult> {
  return client.request<IntercompanySimulationResult>('POST', `/api/v1/foundation/intercompany/simulate`, {
    body,
  });
}

export async function orgIntercompanyRulesList(
  client: ApiClient,
  query: ListIntercompanyRulesQuery,
): Promise<PageIntercompanyRuleDTO> {
  return client.request<PageIntercompanyRuleDTO>('GET', `/api/v1/foundation/intercompany-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgIntercompanyRulesCreate(
  client: ApiClient,
  body: CreateIntercompanyRuleRequest,
  idempotencyKey?: string,
): Promise<IntercompanyRuleDTO> {
  return client.request<IntercompanyRuleDTO>('POST', `/api/v1/foundation/intercompany-rules`, {
    body,
    idempotencyKey,
  });
}

export async function orgEliminationRulesList(
  client: ApiClient,
  query: ListEliminationRulesQuery,
): Promise<PageEliminationRuleDTO> {
  return client.request<PageEliminationRuleDTO>('GET', `/api/v1/foundation/elimination-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgEliminationRulesCreate(
  client: ApiClient,
  body: CreateEliminationRuleRequest,
  idempotencyKey?: string,
): Promise<EliminationRuleDTO> {
  return client.request<EliminationRuleDTO>('POST', `/api/v1/foundation/elimination-rules`, {
    body,
    idempotencyKey,
  });
}

export async function foundationBusinessUnitsCreate(
  client: ApiClient,
  body: CreateBusinessUnitRequest,
  idempotencyKey?: string,
): Promise<BusinessUnitDTO> {
  return client.request<BusinessUnitDTO>('POST', `/api/v1/foundation/business-units`, {
    body,
    idempotencyKey,
  });
}

export async function foundationBusinessUnitsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BusinessUnitDTO> {
  return client.request<BusinessUnitDTO>('GET', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}`, {
  });
}

export async function foundationBusinessUnitsAssignFunction(
  client: ApiClient,
  params: { id: string },
  body: AssignFunctionRequest,
  idempotencyKey?: string,
): Promise<BUFunctionDTO> {
  return client.request<BUFunctionDTO>('POST', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/functions`, {
    body,
    idempotencyKey,
  });
}

export async function foundationBusinessUnitsRemoveFunction(
  client: ApiClient,
  params: { id: string; code: string },
  query: RemoveFunctionQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/functions/${encodeURIComponent(params.code)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationServiceProviderRelationshipsCreate(
  client: ApiClient,
  body: CreateServiceProviderRelationshipRequest,
  idempotencyKey?: string,
): Promise<ServiceProviderRelationshipDTO> {
  return client.request<ServiceProviderRelationshipDTO>('POST', `/api/v1/foundation/service-provider-relationships`, {
    body,
    idempotencyKey,
  });
}

export async function foundationBusinessUnitsResolveClient(
  client: ApiClient,
  params: { id: string },
  query: ResolveClientQuery,
): Promise<ResolveClientResult> {
  return client.request<ResolveClientResult>('GET', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/resolve-client`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationBusinessUnitsAssignReferenceSets(
  client: ApiClient,
  params: { id: string },
  body: AssignReferenceSetsRequest,
  idempotencyKey?: string,
): Promise<BUSetAssignmentDTO[]> {
  return client.request<BUSetAssignmentDTO[]>('POST', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/reference-sets`, {
    body,
    idempotencyKey,
  });
}

export async function foundationBusinessUnitsPreviewSecurityScope(
  client: ApiClient,
  params: { id: string },
): Promise<BUSecurityScopeDTO[]> {
  return client.request<BUSecurityScopeDTO[]>('GET', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/security-scope-preview`, {
  });
}

export async function foundationBusinessUnitsActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateBusinessUnitRequest,
): Promise<BusinessUnitDTO> {
  return client.request<BusinessUnitDTO>('POST', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/activate`, {
    body,
  });
}

export async function foundationBusinessUnitsDeactivate(
  client: ApiClient,
  params: { id: string },
  body: DeactivateBusinessUnitRequest,
): Promise<BusinessUnitDTO> {
  return client.request<BusinessUnitDTO>('POST', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/deactivate`, {
    body,
  });
}

export async function foundationBusinessFunctionsList(
  client: ApiClient,
): Promise<BusinessFunction[]> {
  return client.request<BusinessFunction[]>('GET', `/api/v1/foundation/business-functions`, {
  });
}

export async function foundationBusinessUnitsAlignLedger(
  client: ApiClient,
  params: { id: string },
  body: AlignLedgerRequest,
  idempotencyKey?: string,
): Promise<DocumentSequenceBinding> {
  return client.request<DocumentSequenceBinding>('POST', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/ledger-alignment`, {
    body,
    idempotencyKey,
  });
}

export async function foundationBusinessUnitsListLedgerAlignments(
  client: ApiClient,
  params: { id: string },
): Promise<DocumentSequenceBinding[]> {
  return client.request<DocumentSequenceBinding[]>('GET', `/api/v1/foundation/business-units/${encodeURIComponent(params.id)}/ledger-alignment`, {
  });
}

export async function orgNamingTemplatesList(
  client: ApiClient,
  query: ListNamingTemplatesQuery,
): Promise<PageNamingTemplate> {
  return client.request<PageNamingTemplate>('GET', `/api/v1/foundation/naming-templates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgNamingTemplatesCreate(
  client: ApiClient,
  body: NamingTemplateRequest,
  idempotencyKey?: string,
): Promise<NamingTemplate> {
  return client.request<NamingTemplate>('POST', `/api/v1/foundation/naming-templates`, {
    body,
    idempotencyKey,
  });
}

export async function orgNamingTemplatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<NamingTemplate> {
  return client.request<NamingTemplate>('GET', `/api/v1/foundation/naming-templates/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgNamingTemplatesUpdate(
  client: ApiClient,
  params: { id: string },
  body: NamingTemplateRequest,
): Promise<NamingTemplate> {
  return client.request<NamingTemplate>('PATCH', `/api/v1/foundation/naming-templates/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgNamingTemplatesPreview(
  client: ApiClient,
  params: { id: string },
  body: PreviewNamingTemplateRequest,
): Promise<PreviewNamingTemplateResponse> {
  return client.request<PreviewNamingTemplateResponse>('POST', `/api/v1/foundation/naming-templates/${encodeURIComponent(params.id)}/preview`, {
    body,
  });
}

export async function orgPositionsHeadcount(
  client: ApiClient,
  params: { id: string },
  query: PositionGetQuery,
): Promise<HeadcountStatusDTO> {
  return client.request<HeadcountStatusDTO>('GET', `/api/v1/foundation/positions/${encodeURIComponent(params.id)}/headcount`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgPositionsHeadcountEventsCreate(
  client: ApiClient,
  params: { id: string },
  body: HeadcountEventRequest,
  idempotencyKey?: string,
): Promise<HeadcountEventDTO> {
  return client.request<HeadcountEventDTO>('POST', `/api/v1/foundation/positions/${encodeURIComponent(params.id)}/headcount-events`, {
    body,
    idempotencyKey,
  });
}

export async function orgWorkforceReorganisationsSimulate(
  client: ApiClient,
  body: CreateReorganisationRequest,
): Promise<ReorganisationSimulationDTO> {
  return client.request<ReorganisationSimulationDTO>('POST', `/api/v1/foundation/workforce/reorganisations/simulate`, {
    body,
  });
}

export async function orgWorkforceReorganisationsCreate(
  client: ApiClient,
  body: CreateReorganisationRequest,
  idempotencyKey?: string,
): Promise<WorkforceReorganisationDTO> {
  return client.request<WorkforceReorganisationDTO>('POST', `/api/v1/foundation/workforce/reorganisations`, {
    body,
    idempotencyKey,
  });
}

export async function orgWorkforceReorganisationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<WorkforceReorganisationDTO> {
  return client.request<WorkforceReorganisationDTO>('GET', `/api/v1/foundation/workforce/reorganisations/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgWorkforceReorganisationsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseReorganisationRequest,
): Promise<WorkforceReorganisationDTO> {
  return client.request<WorkforceReorganisationDTO>('POST', `/api/v1/foundation/workforce/reorganisations/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function orgDivisionsFndList(
  client: ApiClient,
  query: ListDivisionsFNDQuery,
): Promise<PageDivisionFNDDTO> {
  return client.request<PageDivisionFNDDTO>('GET', `/api/v1/foundation/divisions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsFndCreate(
  client: ApiClient,
  body: CreateDivisionFNDRequest,
  idempotencyKey?: string,
): Promise<DivisionFNDDTO> {
  return client.request<DivisionFNDDTO>('POST', `/api/v1/foundation/divisions`, {
    body,
    idempotencyKey,
  });
}

export async function orgDivisionsFndGet(
  client: ApiClient,
  params: { id: string },
): Promise<DivisionFNDDTO> {
  return client.request<DivisionFNDDTO>('GET', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgDivisionsFndUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateDivisionFNDRequest,
): Promise<DivisionFNDDTO> {
  return client.request<DivisionFNDDTO>('PUT', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function orgDivisionsFndSubmit(
  client: ApiClient,
  params: { id: string },
): Promise<DivisionFNDDTO> {
  return client.request<DivisionFNDDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/submit`, {
  });
}

export async function orgDivisionsFndActivate(
  client: ApiClient,
  params: { id: string },
): Promise<DivisionFNDDTO> {
  return client.request<DivisionFNDDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/activate`, {
  });
}

export async function orgDivisionsFndEndDate(
  client: ApiClient,
  params: { id: string },
  body: EndDateDivisionFNDRequest,
): Promise<DivisionFNDDTO> {
  return client.request<DivisionFNDDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/end-date`, {
    body,
  });
}

export async function orgDivisionsFndReorganise(
  client: ApiClient,
  params: { id: string },
  body: ReorganiseDivisionFNDRequest,
): Promise<ReorganiseDivisionFNDResult> {
  return client.request<ReorganiseDivisionFNDResult>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/reorganise`, {
    body,
  });
}

export async function orgDivisionsFndRollup(
  client: ApiClient,
  params: { id: string },
  query: GetDivisionRollupQuery,
): Promise<DivisionRollupDTO> {
  return client.request<DivisionRollupDTO>('GET', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/rollup`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsFndCountriesList(
  client: ApiClient,
  params: { id: string },
  query: ListDivisionCountriesQuery,
): Promise<DivisionCountryDTO[]> {
  return client.request<DivisionCountryDTO[]>('GET', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/countries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsFndCountriesAdd(
  client: ApiClient,
  params: { id: string },
  body: AddDivisionCountryRequest,
  idempotencyKey?: string,
): Promise<DivisionCountryDTO> {
  return client.request<DivisionCountryDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/countries`, {
    body,
    idempotencyKey,
  });
}

export async function orgDivisionsFndCountriesTerminate(
  client: ApiClient,
  params: { id: string; countryId: string },
  body: TerminateDivisionCountryRequest,
): Promise<DivisionCountryDTO> {
  return client.request<DivisionCountryDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/countries/${encodeURIComponent(params.countryId)}/terminate`, {
    body,
  });
}

export async function orgDivisionsFndLegalEntitiesList(
  client: ApiClient,
  params: { id: string },
  query: ListDivisionLegalEntitiesQuery,
): Promise<DivisionLegalEntityDTO[]> {
  return client.request<DivisionLegalEntityDTO[]>('GET', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/legal-entities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsFndLegalEntitiesAssociate(
  client: ApiClient,
  params: { id: string },
  body: AssociateDivisionLegalEntityRequest,
  idempotencyKey?: string,
): Promise<DivisionLegalEntityDTO> {
  return client.request<DivisionLegalEntityDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/legal-entities`, {
    body,
    idempotencyKey,
  });
}

export async function orgDivisionsFndLegalEntitiesTerminate(
  client: ApiClient,
  params: { id: string; entityId: string },
  body: TerminateDivisionLegalEntityRequest,
): Promise<DivisionLegalEntityDTO> {
  return client.request<DivisionLegalEntityDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/legal-entities/${encodeURIComponent(params.entityId)}/terminate`, {
    body,
  });
}

export async function orgDivisionsFndSegmentMapGet(
  client: ApiClient,
  params: { id: string },
  query: GetDivisionSegmentMapQuery,
): Promise<DivisionSegmentMapDTO> {
  return client.request<DivisionSegmentMapDTO>('GET', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/segment-map`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsFndSegmentMapSet(
  client: ApiClient,
  params: { id: string },
  body: SetDivisionSegmentMapRequest,
): Promise<SetDivisionSegmentMapResult> {
  return client.request<SetDivisionSegmentMapResult>('PUT', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/segment-map`, {
    body,
  });
}

export async function orgDivisionsFndCrossChargesList(
  client: ApiClient,
  params: { id: string },
  query: ListCrossChargeRulesQuery,
): Promise<CrossChargeRuleDTO[]> {
  return client.request<CrossChargeRuleDTO[]>('GET', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/cross-charges`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgDivisionsFndCrossChargesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateCrossChargeRuleRequest,
  idempotencyKey?: string,
): Promise<CrossChargeRuleDTO> {
  return client.request<CrossChargeRuleDTO>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/cross-charges`, {
    body,
    idempotencyKey,
  });
}

export async function orgDivisionsFndCrossChargesSimulate(
  client: ApiClient,
  params: { id: string },
  body: SimulateCrossChargeRequest,
): Promise<CrossChargeSimulationResult> {
  return client.request<CrossChargeSimulationResult>('POST', `/api/v1/foundation/divisions/${encodeURIComponent(params.id)}/cross-charges/simulate`, {
    body,
  });
}

export async function orgCrossChargesActivate(
  client: ApiClient,
  params: { ruleId: string },
): Promise<CrossChargeRuleDTO> {
  return client.request<CrossChargeRuleDTO>('POST', `/api/v1/foundation/cross-charges/${encodeURIComponent(params.ruleId)}/activate`, {
  });
}

export async function orgFoundationCalendarsCreate(
  client: ApiClient,
  body: CreateFNDCalendarRequest,
  idempotencyKey?: string,
): Promise<CreateFNDCalendarResult> {
  return client.request<CreateFNDCalendarResult>('POST', `/api/v1/foundation/calendars`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationCalendarsList(
  client: ApiClient,
  query: ListCalendarsQuery,
): Promise<PageFiscalCalendar> {
  return client.request<PageFiscalCalendar>('GET', `/api/v1/foundation/calendars`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationLedgersCreate(
  client: ApiClient,
  body: CreateLedgerRequest,
  idempotencyKey?: string,
): Promise<CreateLedgerResult> {
  return client.request<CreateLedgerResult>('POST', `/api/v1/foundation/ledgers`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationLedgersList(
  client: ApiClient,
  query: ListLedgersQuery,
): Promise<PageLedger> {
  return client.request<PageLedger>('GET', `/api/v1/foundation/ledgers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationLedgersResolve(
  client: ApiClient,
  query: ResolveLedgerQuery,
): Promise<LedgerResolutionDTO> {
  return client.request<LedgerResolutionDTO>('GET', `/api/v1/foundation/ledgers/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationLedgersAddReportingCurrency(
  client: ApiClient,
  params: { id: string },
  body: AddReportingCurrencyRequest,
  idempotencyKey?: string,
): Promise<ReportingCurrencyDTO> {
  return client.request<ReportingCurrencyDTO>('POST', `/api/v1/foundation/ledgers/${encodeURIComponent(params.id)}/reporting-currencies`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationLedgersListReportingCurrencies(
  client: ApiClient,
  params: { id: string },
  query: ListReportingCurrenciesQuery,
): Promise<PageReportingCurrencyDTO> {
  return client.request<PageReportingCurrencyDTO>('GET', `/api/v1/foundation/ledgers/${encodeURIComponent(params.id)}/reporting-currencies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationLedgerSetsCreate(
  client: ApiClient,
  body: CreateLedgerSetRequest,
  idempotencyKey?: string,
): Promise<LedgerSetDTO> {
  return client.request<LedgerSetDTO>('POST', `/api/v1/foundation/ledger-sets`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationLedgerSetsList(
  client: ApiClient,
  query: ListLedgerSetsQuery,
): Promise<PageLedgerSetDTO> {
  return client.request<PageLedgerSetDTO>('GET', `/api/v1/foundation/ledger-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationLedgerSetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LedgerSetWithMembers> {
  return client.request<LedgerSetWithMembers>('GET', `/api/v1/foundation/ledger-sets/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgFoundationLedgerSetsAddMember(
  client: ApiClient,
  params: { id: string },
  body: AddLedgerSetMemberRequest,
  idempotencyKey?: string,
): Promise<LedgerSetMemberDTO> {
  return client.request<LedgerSetMemberDTO>('POST', `/api/v1/foundation/ledger-sets/${encodeURIComponent(params.id)}/members`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationAccountingMethodsCreate(
  client: ApiClient,
  body: CreateAccountingMethodRequest,
  idempotencyKey?: string,
): Promise<AccountingMethodDTO> {
  return client.request<AccountingMethodDTO>('POST', `/api/v1/foundation/accounting-methods`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationAccountingMethodsList(
  client: ApiClient,
  query: ListAccountingMethodsQuery,
): Promise<PageAccountingMethodDTO> {
  return client.request<PageAccountingMethodDTO>('GET', `/api/v1/foundation/accounting-methods`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationCurrencyRatesUpsert(
  client: ApiClient,
  body: UpsertRateRequest,
  idempotencyKey?: string,
): Promise<UpsertRateResult> {
  return client.request<UpsertRateResult>('POST', `/api/v1/foundation/currency-rates`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationCurrencyRatesList(
  client: ApiClient,
  query: ListRatesQuery,
): Promise<PageRateDTO> {
  return client.request<PageRateDTO>('GET', `/api/v1/foundation/currency-rates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgFoundationCurrencyRatesRevalue(
  client: ApiClient,
  body: ExecuteRevaluationRequest,
  idempotencyKey?: string,
): Promise<RevaluationRunDTO> {
  return client.request<RevaluationRunDTO>('POST', `/api/v1/foundation/currency-rates/revalue`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationOpeningBalancesStage(
  client: ApiClient,
  file: File | Blob,
  fields: { companyId: number; asOfDate?: string; currency?: string; note?: string },
): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('companyId', String(fields.companyId));
  if (fields.asOfDate !== undefined) {
    form.append('asOfDate', String(fields.asOfDate));
  }
  if (fields.currency !== undefined) {
    form.append('currency', String(fields.currency));
  }
  if (fields.note !== undefined) {
    form.append('note', String(fields.note));
  }
  return client.request<ImportResult>('POST', `/api/v1/foundation/opening-balances`, {
    body: form,
  });
}

export async function orgFoundationOpeningBalancesReconcile(
  client: ApiClient,
  params: { id: string },
  body: ReconcileOpeningBalancesRequest,
  idempotencyKey?: string,
): Promise<ReconciliationDTO> {
  return client.request<ReconciliationDTO>('POST', `/api/v1/foundation/opening-balances/${encodeURIComponent(params.id)}/reconcile`, {
    body,
    idempotencyKey,
  });
}

export async function orgFoundationOpeningBalancesSignOff(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ReconciliationDTO> {
  return client.request<ReconciliationDTO>('POST', `/api/v1/foundation/opening-balances/${encodeURIComponent(params.id)}/sign-off`, {
    idempotencyKey,
  });
}

export async function orgFoundationOpeningBalancesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ImportDTO> {
  return client.request<ImportDTO>('GET', `/api/v1/foundation/opening-balances/${encodeURIComponent(params.id)}`, {
  });
}

export async function orgFoundationPeriodsClose(
  client: ApiClient,
  params: { id: string },
  body: ClosePeriodRequest,
): Promise<PeriodDTO> {
  return client.request<PeriodDTO>('POST', `/api/v1/foundation/periods/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function orgFoundationPeriodsPermanentlyClose(
  client: ApiClient,
  params: { id: string },
  body: PermanentCloseRequest,
  idempotencyKey?: string,
): Promise<PeriodDTO> {
  return client.request<PeriodDTO>('POST', `/api/v1/foundation/periods/${encodeURIComponent(params.id)}/permanently-close`, {
    body,
    idempotencyKey,
  });
}

export async function orgStatutoryLanguagesList(
  client: ApiClient,
): Promise<StatutoryDocumentLanguage[]> {
  return client.request<StatutoryDocumentLanguage[]>('GET', `/api/v1/org/statutory-document-languages`, {
  });
}

export async function orgStatutoryLanguagesUpsert(
  client: ApiClient,
  body: UpsertStatutoryLanguageRequest,
  idempotencyKey?: string,
): Promise<StatutoryDocumentLanguage> {
  return client.request<StatutoryDocumentLanguage>('PUT', `/api/v1/org/statutory-document-languages`, {
    body,
    idempotencyKey,
  });
}

export async function orgCrossChargesList(
  client: ApiClient,
  query: FndListCrossChargesQuery,
): Promise<PageCrossChargeRuleDTO> {
  return client.request<PageCrossChargeRuleDTO>('GET', `/api/v1/foundation/cross-charges`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationBusinessUnitsList(
  client: ApiClient,
  query: FndListBusinessUnitsQuery,
): Promise<PageBusinessUnitDTO> {
  return client.request<PageBusinessUnitDTO>('GET', `/api/v1/foundation/business-units`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationServiceProviderRelationshipsList(
  client: ApiClient,
  query: FndListServiceProviderRelationshipsQuery,
): Promise<PageServiceProviderRelationshipDTO> {
  return client.request<PageServiceProviderRelationshipDTO>('GET', `/api/v1/foundation/service-provider-relationships`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function foundationSecurityScopesList(
  client: ApiClient,
  query: FndListSecurityScopesQuery,
): Promise<PageFndSecurityScopeGrantDTO> {
  return client.request<PageFndSecurityScopeGrantDTO>('GET', `/api/v1/foundation/security-scopes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function orgPositionAssignmentsValidate(
  client: ApiClient,
  body: FndAssignmentValidationRequest,
): Promise<FndAssignmentValidationResult> {
  return client.request<FndAssignmentValidationResult>('POST', `/api/v1/foundation/position-assignments/validate`, {
    body,
  });
}

export async function geotaxEinvoicingList(
  client: ApiClient,
): Promise<EInvoicingConfig[]> {
  return client.request<EInvoicingConfig[]>('GET', `/api/v1/geotax/einvoicing-configs`, {
  });
}

export async function geotaxEinvoicingUpsert(
  client: ApiClient,
  body: UpsertEInvoicingConfigRequest,
  idempotencyKey?: string,
): Promise<EInvoicingConfig> {
  return client.request<EInvoicingConfig>('PUT', `/api/v1/geotax/einvoicing-configs`, {
    body,
    idempotencyKey,
  });
}

export async function geotaxEinvoicingTestConnectivity(
  client: ApiClient,
  params: { id: string },
): Promise<EInvoicingConfig> {
  return client.request<EInvoicingConfig>('POST', `/api/v1/geotax/einvoicing-configs/${encodeURIComponent(params.id)}/test-connectivity`, {
  });
}

export async function geotaxSandboxRunList(
  client: ApiClient,
  query: ListValidationRunsQuery,
): Promise<PackValidationRun[]> {
  return client.request<PackValidationRun[]>('GET', `/api/v1/geotax/pack-validation-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function geotaxSandboxRunGet(
  client: ApiClient,
  params: { id: string },
): Promise<PackValidationRun> {
  return client.request<PackValidationRun>('GET', `/api/v1/geotax/pack-validation-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function geotaxSandboxRunExecute(
  client: ApiClient,
  body: RunValidationRequest,
): Promise<PackValidationRun> {
  return client.request<PackValidationRun>('POST', `/api/v1/geotax/pack-validation-runs`, {
    body,
  });
}

export async function taxCodesList(
  client: ApiClient,
  query: ListCodesQuery,
): Promise<PageCodeDTO> {
  return client.request<PageCodeDTO>('GET', `/api/v1/tax-codes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxCodesCreate(
  client: ApiClient,
  body: CreateCodeRequest,
  idempotencyKey?: string,
): Promise<CodeDTO> {
  return client.request<CodeDTO>('POST', `/api/v1/tax-codes`, {
    body,
    idempotencyKey,
  });
}

export async function taxCodesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CodeDTO> {
  return client.request<CodeDTO>('GET', `/api/v1/tax-codes/${encodeURIComponent(params.id)}`, {
  });
}

export async function taxCodesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCodeRequest,
): Promise<CodeDTO> {
  return client.request<CodeDTO>('PATCH', `/api/v1/tax-codes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function taxGroupsList(
  client: ApiClient,
  query: ListGroupsQuery,
): Promise<PageGroupDTO> {
  return client.request<PageGroupDTO>('GET', `/api/v1/tax-groups`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxGroupsCreate(
  client: ApiClient,
  body: CreateGroupRequest,
  idempotencyKey?: string,
): Promise<GroupDTO> {
  return client.request<GroupDTO>('POST', `/api/v1/tax-groups`, {
    body,
    idempotencyKey,
  });
}

export async function taxReport(
  client: ApiClient,
  query: ReportQuery,
): Promise<Report> {
  return client.request<Report>('GET', `/api/v1/tax/report`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxReportExport(
  client: ApiClient,
  query: ReportQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/tax/report/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxJurisdictionsList(
  client: ApiClient,
  query: ListJurisdictionsQuery,
): Promise<PageJurisdictionDTO> {
  return client.request<PageJurisdictionDTO>('GET', `/api/v1/tax/jurisdictions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxJurisdictionsTree(
  client: ApiClient,
  query: JurisdictionTreeQuery,
): Promise<JurisdictionTreeDTO> {
  return client.request<JurisdictionTreeDTO>('GET', `/api/v1/tax/jurisdictions/tree`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxRegimesList(
  client: ApiClient,
  query: ListRegimesQuery,
): Promise<PageRegimeDTO> {
  return client.request<PageRegimeDTO>('GET', `/api/v1/tax/regimes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxPacksList(
  client: ApiClient,
  query: ListPacksQuery,
): Promise<PageActivationDTO> {
  return client.request<PageActivationDTO>('GET', `/api/v1/tax/packs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxPacksActivate(
  client: ApiClient,
  body: ActivateRequest,
  idempotencyKey?: string,
): Promise<ActivationDTO> {
  return client.request<ActivationDTO>('POST', `/api/v1/tax/packs/activate`, {
    body,
    idempotencyKey,
  });
}

export async function taxPacksDiff(
  client: ApiClient,
  params: { country: string },
): Promise<PackDiff> {
  return client.request<PackDiff>('GET', `/api/v1/tax/packs/${encodeURIComponent(params.country)}/diff`, {
  });
}

export async function taxPacksValidate(
  client: ApiClient,
  params: { country: string },
  body: TaxValidateRequest,
): Promise<ValidationHandle> {
  return client.request<ValidationHandle>('POST', `/api/v1/tax/packs/${encodeURIComponent(params.country)}/validate`, {
    body,
  });
}

export async function taxPacksValidation(
  client: ApiClient,
  params: { country: string },
): Promise<SandboxResult> {
  return client.request<SandboxResult>('GET', `/api/v1/tax/packs/${encodeURIComponent(params.country)}/validation`, {
  });
}

export async function taxPacksAccept(
  client: ApiClient,
  params: { country: string },
  body: AcceptRequest,
  idempotencyKey?: string,
): Promise<ActivationDTO> {
  return client.request<ActivationDTO>('POST', `/api/v1/tax/packs/${encodeURIComponent(params.country)}/accept`, {
    body,
    idempotencyKey,
  });
}

export async function taxRegistrationsList(
  client: ApiClient,
  query: ListRegistrationsQuery,
): Promise<PageRegistrationDTO> {
  return client.request<PageRegistrationDTO>('GET', `/api/v1/tax/registrations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxRegistrationsCreate(
  client: ApiClient,
  body: CreateRegistrationRequest,
  idempotencyKey?: string,
): Promise<RegistrationDTO> {
  return client.request<RegistrationDTO>('POST', `/api/v1/tax/registrations`, {
    body,
    idempotencyKey,
  });
}

export async function taxRegistrationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RegistrationDTO> {
  return client.request<RegistrationDTO>('GET', `/api/v1/tax/registrations/${encodeURIComponent(params.id)}`, {
  });
}

export async function taxRegistrationsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRegistrationRequest,
): Promise<RegistrationDTO> {
  return client.request<RegistrationDTO>('PATCH', `/api/v1/tax/registrations/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function taxCertificatesList(
  client: ApiClient,
  query: ListCertificatesQuery,
): Promise<PageCertificateDTO> {
  return client.request<PageCertificateDTO>('GET', `/api/v1/tax/exemption-certificates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxCertificatesCreate(
  client: ApiClient,
  body: CreateCertificateRequest,
  idempotencyKey?: string,
): Promise<CertificateDTO> {
  return client.request<CertificateDTO>('POST', `/api/v1/tax/exemption-certificates`, {
    body,
    idempotencyKey,
  });
}

export async function taxCertificatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CertificateDTO> {
  return client.request<CertificateDTO>('GET', `/api/v1/tax/exemption-certificates/${encodeURIComponent(params.id)}`, {
  });
}

export async function taxCertificatesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCertificateRequest,
): Promise<CertificateDTO> {
  return client.request<CertificateDTO>('PATCH', `/api/v1/tax/exemption-certificates/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function taxCertificatesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/tax/exemption-certificates/${encodeURIComponent(params.id)}`, {
  });
}

export async function taxSimulate(
  client: ApiClient,
  body: SimulateRequest,
): Promise<Determination> {
  return client.request<Determination>('POST', `/api/v1/tax/simulate`, {
    body,
  });
}

export async function taxReturnsList(
  client: ApiClient,
  query: ListReturnsQuery,
): Promise<PageTaxReturnDTO> {
  return client.request<PageTaxReturnDTO>('GET', `/api/v1/tax/returns`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function taxReturnsCreate(
  client: ApiClient,
  body: PrepareReturnRequest,
  idempotencyKey?: string,
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns`, {
    body,
    idempotencyKey,
  });
}

export async function taxReturnsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('GET', `/api/v1/tax/returns/${encodeURIComponent(params.id)}`, {
  });
}

export async function taxReturnsReconcile(
  client: ApiClient,
  params: { id: string },
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/reconcile`, {
  });
}

export async function taxReturnsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitReturnRequest,
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function taxReturnsApprove(
  client: ApiClient,
  params: { id: string },
  body: ApproveReturnRequest,
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/approve`, {
    body,
  });
}

export async function taxReturnsFile(
  client: ApiClient,
  params: { id: string },
  body: FileReturnRequest,
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/file`, {
    body,
  });
}

export async function taxReturnsPay(
  client: ApiClient,
  params: { id: string },
  body: PayReturnRequest,
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/pay`, {
    body,
  });
}

export async function taxReturnsSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeRequest,
): Promise<ReturnDetailDTO> {
  return client.request<ReturnDetailDTO>('POST', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function taxReturnsExport(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/export`, {
  });
}

export async function taxReturnsEvidence(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/tax/returns/${encodeURIComponent(params.id)}/evidence`, {
  });
}

export async function taxChargesList(
  client: ApiClient,
  query: ChargeListQuery,
): Promise<PageChargePool> {
  return client.request<PageChargePool>('GET', `/api/v1/tax/charges`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxCurrenciesList(
  client: ApiClient,
  query: ListCurrenciesQuery,
): Promise<PageCurrencyDTO> {
  return client.request<PageCurrencyDTO>('GET', `/api/v1/currencies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxCurrenciesCreate(
  client: ApiClient,
  body: CreateCurrencyRequest,
  idempotencyKey?: string,
): Promise<CurrencyDTO> {
  return client.request<CurrencyDTO>('POST', `/api/v1/currencies`, {
    body,
    idempotencyKey,
  });
}

export async function fxRatesList(
  client: ApiClient,
  query: ListRatesQuery,
): Promise<PageRateDTO> {
  return client.request<PageRateDTO>('GET', `/api/v1/fx-rates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxRatesUpsert(
  client: ApiClient,
  body: UpsertRateRequest,
  idempotencyKey?: string,
): Promise<UpsertRateResult> {
  return client.request<UpsertRateResult>('POST', `/api/v1/fx-rates`, {
    body,
    idempotencyKey,
  });
}

export async function fxRateResolve(
  client: ApiClient,
  query: ResolveRateQuery,
): Promise<Resolution> {
  return client.request<Resolution>('GET', `/api/v1/fx/rate`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxTolerancesList(
  client: ApiClient,
  query: ListTolerancesQuery,
): Promise<PageTolerance> {
  return client.request<PageTolerance>('GET', `/api/v1/fx/tolerances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxTolerancesUpsert(
  client: ApiClient,
  body: UpsertToleranceRequest,
  idempotencyKey?: string,
): Promise<Tolerance> {
  return client.request<Tolerance>('POST', `/api/v1/fx/tolerances`, {
    body,
    idempotencyKey,
  });
}

export async function fxHoldsList(
  client: ApiClient,
  query: ListHoldsQuery,
): Promise<PageHold> {
  return client.request<PageHold>('GET', `/api/v1/fx/holds`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxHoldsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideHoldRequest,
): Promise<Hold> {
  return client.request<Hold>('POST', `/api/v1/fx/holds/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function fxRevaluationsSubmit(
  client: ApiClient,
  body: SubmitRevaluationRequest,
  idempotencyKey?: string,
): Promise<RevaluationSubmittedDTO> {
  return client.request<RevaluationSubmittedDTO>('POST', `/api/v1/fx/revaluations`, {
    body,
    idempotencyKey,
  });
}

export async function opcalCalendarsList(
  client: ApiClient,
  query: OpcalListCalendarsQuery,
): Promise<PageCalendar> {
  return client.request<PageCalendar>('GET', `/api/v1/operating-calendars`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function opcalCalendarsCreate(
  client: ApiClient,
  body: CalendarRequest,
  idempotencyKey?: string,
): Promise<Calendar> {
  return client.request<Calendar>('POST', `/api/v1/operating-calendars`, {
    body,
    idempotencyKey,
  });
}

export async function opcalCalendarsGet(
  client: ApiClient,
  params: { id: string },
): Promise<Calendar> {
  return client.request<Calendar>('GET', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}`, {
  });
}

export async function opcalCalendarsUpdate(
  client: ApiClient,
  params: { id: string },
  body: CalendarRequest,
): Promise<Calendar> {
  return client.request<Calendar>('PATCH', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function opcalCalendarsDelete(
  client: ApiClient,
  params: { id: string },
  query: OpcalDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function opcalCalendarsSetScopes(
  client: ApiClient,
  params: { id: string },
  body: SetScopesRequest,
): Promise<Calendar> {
  return client.request<Calendar>('PUT', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/scopes`, {
    body,
  });
}

export async function opcalDayPartsList(
  client: ApiClient,
  params: { id: string },
): Promise<DayPart[]> {
  return client.request<DayPart[]>('GET', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/day-parts`, {
  });
}

export async function opcalDayPartsPut(
  client: ApiClient,
  params: { id: string },
  body: SetDayPartsRequest,
): Promise<DayPart[]> {
  return client.request<DayPart[]>('PUT', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/day-parts`, {
    body,
  });
}

export async function opcalShiftsList(
  client: ApiClient,
  params: { id: string },
): Promise<Shift[]> {
  return client.request<Shift[]>('GET', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/shifts`, {
  });
}

export async function opcalShiftsPut(
  client: ApiClient,
  params: { id: string },
  body: SetShiftsRequest,
): Promise<Shift[]> {
  return client.request<Shift[]>('PUT', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/shifts`, {
    body,
  });
}

export async function opcalHolidaysList(
  client: ApiClient,
  params: { id: string },
  query: ListHolidaysQuery,
): Promise<PageHoliday> {
  return client.request<PageHoliday>('GET', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/holidays`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function opcalHolidaysCreate(
  client: ApiClient,
  params: { id: string },
  body: HolidayRequest,
  idempotencyKey?: string,
): Promise<Holiday> {
  return client.request<Holiday>('POST', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/holidays`, {
    body,
    idempotencyKey,
  });
}

export async function opcalHolidaysDelete(
  client: ApiClient,
  params: { id: string; hid: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/holidays/${encodeURIComponent(params.hid)}`, {
  });
}

export async function opcalHolidaysImport(
  client: ApiClient,
  params: { id: string },
  body: ImportHolidaysRequest,
  idempotencyKey?: string,
): Promise<OpcalImportResult> {
  return client.request<OpcalImportResult>('POST', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/holidays/import`, {
    body,
    idempotencyKey,
  });
}

export async function opcalExceptionsList(
  client: ApiClient,
  params: { id: string },
): Promise<Exception[]> {
  return client.request<Exception[]>('GET', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/exceptions`, {
  });
}

export async function opcalExceptionsCreate(
  client: ApiClient,
  params: { id: string },
  body: ExceptionRequest,
  idempotencyKey?: string,
): Promise<Exception> {
  return client.request<Exception>('POST', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/exceptions`, {
    body,
    idempotencyKey,
  });
}

export async function opcalExceptionsDelete(
  client: ApiClient,
  params: { id: string; eid: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/operating-calendars/${encodeURIComponent(params.id)}/exceptions/${encodeURIComponent(params.eid)}`, {
  });
}

export async function opcalDaysList(
  client: ApiClient,
  query: ListDaysQuery,
): Promise<PageBusinessDay> {
  return client.request<PageBusinessDay>('GET', `/api/v1/business-days`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function opcalDaysBoard(
  client: ApiClient,
  query: BoardQuery,
): Promise<DayBoardDTO> {
  return client.request<DayBoardDTO>('GET', `/api/v1/business-days/board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function opcalCalc(
  client: ApiClient,
  query: CalcQuery,
): Promise<CalcResult> {
  return client.request<CalcResult>('GET', `/api/v1/business-days/calc`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function opcalDaysOpen(
  client: ApiClient,
  body: OpenDayRequest,
  idempotencyKey?: string,
): Promise<BusinessDay> {
  return client.request<BusinessDay>('POST', `/api/v1/business-days/open`, {
    body,
    idempotencyKey,
  });
}

export async function opcalDaysGet(
  client: ApiClient,
  params: { id: string },
): Promise<BusinessDayDetailDTO> {
  return client.request<BusinessDayDetailDTO>('GET', `/api/v1/business-days/${encodeURIComponent(params.id)}`, {
  });
}

export async function opcalDaysBeginClose(
  client: ApiClient,
  params: { id: string },
): Promise<BusinessDayDetailDTO> {
  return client.request<BusinessDayDetailDTO>('POST', `/api/v1/business-days/${encodeURIComponent(params.id)}/begin-close`, {
  });
}

export async function opcalDaysClose(
  client: ApiClient,
  params: { id: string },
  body: CloseDayRequest,
): Promise<BusinessDay> {
  return client.request<BusinessDay>('POST', `/api/v1/business-days/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function opcalDaysReopen(
  client: ApiClient,
  params: { id: string },
  body: ReopenDayRequest,
): Promise<BusinessDay> {
  return client.request<BusinessDay>('POST', `/api/v1/business-days/${encodeURIComponent(params.id)}/reopen`, {
    body,
  });
}

export async function authorityVersionsList(
  client: ApiClient,
  query: ListMatrixVersionsQuery,
): Promise<PageMatrixVersionDTO> {
  return client.request<PageMatrixVersionDTO>('GET', `/api/v1/authority/matrix-versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authorityVersionsCreate(
  client: ApiClient,
  body: CreateMatrixVersionRequest,
  idempotencyKey?: string,
): Promise<MatrixVersionDetailDTO> {
  return client.request<MatrixVersionDetailDTO>('POST', `/api/v1/authority/matrix-versions`, {
    body,
    idempotencyKey,
  });
}

export async function authorityVersionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<MatrixVersionDetailDTO> {
  return client.request<MatrixVersionDetailDTO>('GET', `/api/v1/authority/matrix-versions/${encodeURIComponent(params.id)}`, {
  });
}

export async function authorityVersionsValidate(
  client: ApiClient,
  params: { id: string },
): Promise<MatrixValidation> {
  return client.request<MatrixValidation>('GET', `/api/v1/authority/matrix-versions/${encodeURIComponent(params.id)}/validate`, {
  });
}

export async function authorityVersionsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitMatrixVersionRequest,
): Promise<MatrixVersionDTO> {
  return client.request<MatrixVersionDTO>('POST', `/api/v1/authority/matrix-versions/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function authorityVersionsActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateMatrixRequest,
  idempotencyKey?: string,
): Promise<MatrixVersionDTO> {
  return client.request<MatrixVersionDTO>('POST', `/api/v1/authority/matrix-versions/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function authorityRulesList(
  client: ApiClient,
  query: ListAuthorityRulesQuery,
): Promise<PageAuthorityRuleDTO> {
  return client.request<PageAuthorityRuleDTO>('GET', `/api/v1/authority/rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authorityRulesCreate(
  client: ApiClient,
  body: AuthorityRuleRequest,
  idempotencyKey?: string,
): Promise<AuthorityRuleDTO> {
  return client.request<AuthorityRuleDTO>('POST', `/api/v1/authority/rules`, {
    body,
    idempotencyKey,
  });
}

export async function authorityRulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: AuthorityRuleRequest,
): Promise<AuthorityRuleDTO> {
  return client.request<AuthorityRuleDTO>('PATCH', `/api/v1/authority/rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function authorityRulesDelete(
  client: ApiClient,
  params: { id: string },
  body: DeleteAuthorityRuleRequest,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/authority/rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function authorityRulesSetSteps(
  client: ApiClient,
  params: { id: string },
  body: SetAuthorityStepsRequest,
): Promise<AuthorityRuleDTO> {
  return client.request<AuthorityRuleDTO>('PUT', `/api/v1/authority/rules/${encodeURIComponent(params.id)}/steps`, {
    body,
  });
}

export async function authorityPreview(
  client: ApiClient,
  body: AuthorityPreviewRequest,
): Promise<AuthorityChainPreview> {
  return client.request<AuthorityChainPreview>('POST', `/api/v1/authority/preview`, {
    body,
  });
}

export async function authorityAssess(
  client: ApiClient,
  body: AuthorityAssessRequest,
): Promise<AuthorityAssessment> {
  return client.request<AuthorityAssessment>('POST', `/api/v1/authority/assess`, {
    body,
  });
}

export async function authorityDelegationsList(
  client: ApiClient,
  query: ListAuthorityDelegationsQuery,
): Promise<PageAuthorityDelegationDTO> {
  return client.request<PageAuthorityDelegationDTO>('GET', `/api/v1/authority/delegations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authorityDelegationsCreate(
  client: ApiClient,
  body: CreateAuthorityDelegationRequest,
  idempotencyKey?: string,
): Promise<AuthorityDelegationDTO> {
  return client.request<AuthorityDelegationDTO>('POST', `/api/v1/authority/delegations`, {
    body,
    idempotencyKey,
  });
}

export async function authorityDelegationsRevoke(
  client: ApiClient,
  params: { id: string },
  body: RevokeAuthorityDelegationRequest,
  idempotencyKey?: string,
): Promise<AuthorityDelegationDTO> {
  return client.request<AuthorityDelegationDTO>('POST', `/api/v1/authority/delegations/${encodeURIComponent(params.id)}/revoke`, {
    body,
    idempotencyKey,
  });
}

export async function authorityAbsencesList(
  client: ApiClient,
  query: ListAuthorityAbsencesQuery,
): Promise<PageAuthorityAbsenceDTO> {
  return client.request<PageAuthorityAbsenceDTO>('GET', `/api/v1/authority/absences`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authorityAbsencesCreate(
  client: ApiClient,
  body: AuthorityAbsenceRequest,
  idempotencyKey?: string,
): Promise<AuthorityAbsenceDTO> {
  return client.request<AuthorityAbsenceDTO>('POST', `/api/v1/authority/absences`, {
    body,
    idempotencyKey,
  });
}

export async function authorityAbsencesCancel(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/authority/absences/${encodeURIComponent(params.id)}`, {
  });
}

export async function authorityMaterialityList(
  client: ApiClient,
  query: ListMaterialityQuery,
): Promise<PageMaterialityPolicyDTO> {
  return client.request<PageMaterialityPolicyDTO>('GET', `/api/v1/authority/materiality`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function authorityMaterialityPut(
  client: ApiClient,
  body: MaterialityRequest,
): Promise<MaterialityPolicyDTO> {
  return client.request<MaterialityPolicyDTO>('PUT', `/api/v1/authority/materiality`, {
    body,
  });
}

export async function rulesControlsList(
  client: ApiClient,
  query: ListControlsQuery,
): Promise<PageControlRuleDTO> {
  return client.request<PageControlRuleDTO>('GET', `/api/v1/control-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesControlsGet(
  client: ApiClient,
  params: { code: string },
): Promise<ControlRuleDTO> {
  return client.request<ControlRuleDTO>('GET', `/api/v1/control-rules/${encodeURIComponent(params.code)}`, {
  });
}

export async function rulesControlsUpdate(
  client: ApiClient,
  params: { code: string },
  body: UpdateControlRequest,
): Promise<ControlRuleDTO> {
  return client.request<ControlRuleDTO>('PATCH', `/api/v1/control-rules/${encodeURIComponent(params.code)}`, {
    body,
  });
}

export async function rulesReasonSetsList(
  client: ApiClient,
  query: ListReasonSetsQuery,
): Promise<PageReasonSetDTO> {
  return client.request<PageReasonSetDTO>('GET', `/api/v1/reason-code-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesReasonsList(
  client: ApiClient,
  query: ListReasonsQuery,
): Promise<PageReasonCodeDTO> {
  return client.request<PageReasonCodeDTO>('GET', `/api/v1/reason-codes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesReasonsCreate(
  client: ApiClient,
  body: ReasonCodeRequest,
  idempotencyKey?: string,
): Promise<ReasonCodeDTO> {
  return client.request<ReasonCodeDTO>('POST', `/api/v1/reason-codes`, {
    body,
    idempotencyKey,
  });
}

export async function rulesReasonsSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeReasonRequest,
): Promise<ReasonCodeDTO> {
  return client.request<ReasonCodeDTO>('POST', `/api/v1/reason-codes/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function rulesOverridesList(
  client: ApiClient,
  query: ListOverridesQuery,
): Promise<PageOverrideDTO> {
  return client.request<PageOverrideDTO>('GET', `/api/v1/rule-overrides`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesOverridesReport(
  client: ApiClient,
  query: OverrideReportQuery,
): Promise<OverrideReport> {
  return client.request<OverrideReport>('GET', `/api/v1/rule-overrides/report`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesOverridesExport(
  client: ApiClient,
  query: OverrideExportQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/rule-overrides/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesQueuesList(
  client: ApiClient,
  query: ListQueuesQuery,
): Promise<PageQueueDTO> {
  return client.request<PageQueueDTO>('GET', `/api/v1/reconciliation-queues`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesQueueItemsList(
  client: ApiClient,
  params: { code: string },
  query: ListItemsQuery,
): Promise<PageQueueItemDTO> {
  return client.request<PageQueueItemDTO>('GET', `/api/v1/reconciliation-queues/${encodeURIComponent(params.code)}/items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rulesQueueItemsResolve(
  client: ApiClient,
  params: { code: string; id: string },
  body: ResolveItemRequest,
): Promise<QueueItemDTO> {
  return client.request<QueueItemDTO>('POST', `/api/v1/reconciliation-queues/${encodeURIComponent(params.code)}/items/${encodeURIComponent(params.id)}/resolve`, {
    body,
  });
}

export async function attentionList(
  client: ApiClient,
  query: ListAttentionQuery,
): Promise<PageAttentionItemDTO> {
  return client.request<PageAttentionItemDTO>('GET', `/api/v1/attention`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function attentionCounts(
  client: ApiClient,
): Promise<AttentionCounts> {
  return client.request<AttentionCounts>('GET', `/api/v1/attention/counts`, {
  });
}

export async function attentionAcknowledge(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<AttentionItemDTO> {
  return client.request<AttentionItemDTO>('POST', `/api/v1/attention/${encodeURIComponent(params.id)}/acknowledge`, {
    idempotencyKey,
  });
}

export async function attentionResolve(
  client: ApiClient,
  params: { id: string },
  body: AttentionItemResolveRequest,
  idempotencyKey?: string,
): Promise<AttentionItemDTO> {
  return client.request<AttentionItemDTO>('POST', `/api/v1/attention/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function attentionPoliciesList(
  client: ApiClient,
  query: ListNotificationPoliciesQuery,
): Promise<PageNotificationPolicyDTO> {
  return client.request<PageNotificationPolicyDTO>('GET', `/api/v1/notification-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function attentionPoliciesPut(
  client: ApiClient,
  body: PolicyRequest,
  idempotencyKey?: string,
): Promise<NotificationPolicyDTO> {
  return client.request<NotificationPolicyDTO>('PUT', `/api/v1/notification-policies`, {
    body,
    idempotencyKey,
  });
}

export async function glJournalEntriesList(
  client: ApiClient,
  query: ListEntriesQuery,
): Promise<PageEntryDTO> {
  return client.request<PageEntryDTO>('GET', `/api/v1/journal-entries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glJournalEntriesExport(
  client: ApiClient,
  query: ExportEntriesQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/journal-entries/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glJournalEntriesCreate(
  client: ApiClient,
  body: CreateEntryRequest,
  idempotencyKey?: string,
): Promise<EntryDTO> {
  return client.request<EntryDTO>('POST', `/api/v1/journal-entries`, {
    body,
    idempotencyKey,
  });
}

export async function glJournalEntriesGet(
  client: ApiClient,
  params: { id: string },
): Promise<EntryDTO> {
  return client.request<EntryDTO>('GET', `/api/v1/journal-entries/${encodeURIComponent(params.id)}`, {
  });
}

export async function glJournalEntriesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEntryRequest,
): Promise<EntryDTO> {
  return client.request<EntryDTO>('PATCH', `/api/v1/journal-entries/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function glJournalEntriesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/journal-entries/${encodeURIComponent(params.id)}`, {
  });
}

export async function glJournalEntriesPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<EntryDTO> {
  return client.request<EntryDTO>('POST', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function glJournalEntriesValidate(
  client: ApiClient,
  params: { id: string },
): Promise<GlJournalValidationDTO> {
  return client.request<GlJournalValidationDTO>('POST', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/validate`, {
  });
}

export async function glJournalEntriesSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<EntryDTO> {
  return client.request<EntryDTO>('POST', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function glJournalEntriesReturnToDraft(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<EntryDTO> {
  return client.request<EntryDTO>('POST', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/return-to-draft`, {
    idempotencyKey,
  });
}

export async function glJournalEntriesReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseEntryRequest,
  idempotencyKey?: string,
): Promise<EntryDTO> {
  return client.request<EntryDTO>('POST', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function glJournalEntriesLines(
  client: ApiClient,
  params: { id: string },
  query: ListLinesQuery,
): Promise<PageLineDTO> {
  return client.request<PageLineDTO>('GET', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glJournalEntriesImportLines(
  client: ApiClient,
  params: { id: string },
  file: File | Blob,
  idempotencyKey?: string,
): Promise<GlJournalLineImportResultDTO> {
  const form = new FormData();
  form.append('file', file);
  return client.request<GlJournalLineImportResultDTO>('POST', `/api/v1/journal-entries/${encodeURIComponent(params.id)}/import-lines`, {
    body: form,
    idempotencyKey,
  });
}

export async function glRecurringTemplatesList(
  client: ApiClient,
  query: GlListTemplatesQuery,
): Promise<PageTemplateDTO> {
  return client.request<PageTemplateDTO>('GET', `/api/v1/recurring-templates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glRecurringTemplatesCreate(
  client: ApiClient,
  body: GlCreateTemplateRequest,
  idempotencyKey?: string,
): Promise<GlTemplateDTO> {
  return client.request<GlTemplateDTO>('POST', `/api/v1/recurring-templates`, {
    body,
    idempotencyKey,
  });
}

export async function glRecurringTemplatesFromEntry(
  client: ApiClient,
  params: { entryId: string },
  body: TemplateFromEntryRequest,
  idempotencyKey?: string,
): Promise<GlTemplateDTO> {
  return client.request<GlTemplateDTO>('POST', `/api/v1/recurring-templates/from-entry/${encodeURIComponent(params.entryId)}`, {
    body,
    idempotencyKey,
  });
}

export async function glRecurringTemplatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<GlTemplateDTO> {
  return client.request<GlTemplateDTO>('GET', `/api/v1/recurring-templates/${encodeURIComponent(params.id)}`, {
  });
}

export async function glRecurringTemplatesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateTemplateRequest,
): Promise<GlTemplateDTO> {
  return client.request<GlTemplateDTO>('PATCH', `/api/v1/recurring-templates/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function glRecurringTemplatesDelete(
  client: ApiClient,
  params: { id: string },
  query: GlDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/recurring-templates/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glRecurringTemplatesRun(
  client: ApiClient,
  params: { id: string },
  body: RunTemplateRequest,
  idempotencyKey?: string,
): Promise<RunResult> {
  return client.request<RunResult>('POST', `/api/v1/recurring-templates/${encodeURIComponent(params.id)}/run`, {
    body,
    idempotencyKey,
  });
}

export async function glOpeningBalancesPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<RunResult> {
  return client.request<RunResult>('POST', `/api/v1/opening-balances/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function glJournalTypesList(
  client: ApiClient,
  query: ListJournalTypesQuery,
): Promise<PageJournalTypeDTO> {
  return client.request<PageJournalTypeDTO>('GET', `/api/v1/gl-journal-types`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glJournalTypesCreate(
  client: ApiClient,
  body: CreateJournalTypeRequest,
  idempotencyKey?: string,
): Promise<JournalTypeDTO> {
  return client.request<JournalTypeDTO>('POST', `/api/v1/gl-journal-types`, {
    body,
    idempotencyKey,
  });
}

export async function glJournalTypesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateJournalTypeRequest,
): Promise<JournalTypeDTO> {
  return client.request<JournalTypeDTO>('PATCH', `/api/v1/gl-journal-types/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function glJournalTypesDelete(
  client: ApiClient,
  params: { id: string },
  query: GlDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/gl-journal-types/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glDimensionPoliciesList(
  client: ApiClient,
  params: { id: string },
): Promise<AccountDimensionPolicyDTO[]> {
  return client.request<AccountDimensionPolicyDTO[]>('GET', `/api/v1/accounts/${encodeURIComponent(params.id)}/dimension-policies`, {
  });
}

export async function glDimensionPoliciesSet(
  client: ApiClient,
  params: { id: string },
  body: SetDimensionPoliciesRequest,
): Promise<AccountDimensionPolicyDTO[]> {
  return client.request<AccountDimensionPolicyDTO[]>('PUT', `/api/v1/accounts/${encodeURIComponent(params.id)}/dimension-policies`, {
    body,
  });
}

export async function glSuspenseListPositions(
  client: ApiClient,
  query: ListSuspensePositionsQuery,
): Promise<PageSuspensePositionDTO> {
  return client.request<PageSuspensePositionDTO>('GET', `/api/v1/gl-suspense-positions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glSuspenseCreatePosition(
  client: ApiClient,
  body: CreateSuspensePositionRequest,
  idempotencyKey?: string,
): Promise<SuspensePositionDTO> {
  return client.request<SuspensePositionDTO>('POST', `/api/v1/gl-suspense-positions`, {
    body,
    idempotencyKey,
  });
}

export async function glSuspenseListItems(
  client: ApiClient,
  query: ListSuspenseItemsQuery,
): Promise<PageSuspenseItemDTO> {
  return client.request<PageSuspenseItemDTO>('GET', `/api/v1/gl-suspense-items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glSuspensePost(
  client: ApiClient,
  body: PostSuspenseItemRequest,
  idempotencyKey?: string,
): Promise<SuspenseItemDTO> {
  return client.request<SuspenseItemDTO>('POST', `/api/v1/gl-suspense-items`, {
    body,
    idempotencyKey,
  });
}

export async function glSuspenseClear(
  client: ApiClient,
  params: { id: string },
  body: ClearSuspenseItemRequest,
  idempotencyKey?: string,
): Promise<SuspenseItemDTO> {
  return client.request<SuspenseItemDTO>('POST', `/api/v1/gl-suspense-items/${encodeURIComponent(params.id)}/clear`, {
    body,
    idempotencyKey,
  });
}

export async function glSuspenseWriteOff(
  client: ApiClient,
  params: { id: string },
  body: WriteOffSuspenseItemRequest,
  idempotencyKey?: string,
): Promise<SuspenseItemDTO> {
  return client.request<SuspenseItemDTO>('POST', `/api/v1/gl-suspense-items/${encodeURIComponent(params.id)}/write-off`, {
    body,
    idempotencyKey,
  });
}

export async function glReportsSuspense(
  client: ApiClient,
  query: SuspenseReportQuery,
): Promise<SuspenseReportDTO> {
  return client.request<SuspenseReportDTO>('GET', `/api/v1/reports/gl-suspense`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glAccrualsListPositions(
  client: ApiClient,
  query: ListAccrualPositionsQuery,
): Promise<PageAccrualPositionDTO> {
  return client.request<PageAccrualPositionDTO>('GET', `/api/v1/accrual-positions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glAccrualsCreatePosition(
  client: ApiClient,
  body: CreateAccrualPositionRequest,
  idempotencyKey?: string,
): Promise<AccrualPositionDTO> {
  return client.request<AccrualPositionDTO>('POST', `/api/v1/accrual-positions`, {
    body,
    idempotencyKey,
  });
}

export async function glAccrualsListItems(
  client: ApiClient,
  query: ListAccrualItemsQuery,
): Promise<PageAccrualItemDTO> {
  return client.request<PageAccrualItemDTO>('GET', `/api/v1/accrual-items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glAccrualsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseAccrualItemRequest,
  idempotencyKey?: string,
): Promise<AccrualItemDTO> {
  return client.request<AccrualItemDTO>('POST', `/api/v1/accrual-items/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function glAccrualsWriteOff(
  client: ApiClient,
  params: { id: string },
  body: WriteOffAccrualItemRequest,
  idempotencyKey?: string,
): Promise<AccrualItemDTO> {
  return client.request<AccrualItemDTO>('POST', `/api/v1/accrual-items/${encodeURIComponent(params.id)}/write-off`, {
    body,
    idempotencyKey,
  });
}

export async function glReportsAccrualAging(
  client: ApiClient,
  query: AccrualAgingQuery,
): Promise<AccrualAgingReportDTO> {
  return client.request<AccrualAgingReportDTO>('GET', `/api/v1/reports/accrual-ageing`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glAssertionsList(
  client: ApiClient,
  query: ListPostingAssertionsQuery,
): Promise<PagePostingAssertionDTO> {
  return client.request<PagePostingAssertionDTO>('GET', `/api/v1/posting-assertions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glAssertionsRun(
  client: ApiClient,
  body: RunPostingAssertionsRequest,
  idempotencyKey?: string,
): Promise<PostingAssertionDTO[]> {
  return client.request<PostingAssertionDTO[]>('POST', `/api/v1/posting-assertions/run`, {
    body,
    idempotencyKey,
  });
}

export async function glAssertionsAccept(
  client: ApiClient,
  params: { id: string },
  body: AcceptAssertionRequest,
  idempotencyKey?: string,
): Promise<PostingAssertionDTO> {
  return client.request<PostingAssertionDTO>('POST', `/api/v1/posting-assertions/${encodeURIComponent(params.id)}/accept`, {
    body,
    idempotencyKey,
  });
}

export async function glYearEndClosesCreate(
  client: ApiClient,
  body: CreateYearEndCloseRequest,
  idempotencyKey?: string,
): Promise<YearEndCloseDTO> {
  return client.request<YearEndCloseDTO>('POST', `/api/v1/year-end-closes`, {
    body,
    idempotencyKey,
  });
}

export async function glYearEndClosesList(
  client: ApiClient,
  query: ListYearEndClosesQuery,
): Promise<PageYearEndCloseDTO> {
  return client.request<PageYearEndCloseDTO>('GET', `/api/v1/year-end-closes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glYearEndClosesGet(
  client: ApiClient,
  params: { id: string },
): Promise<YearEndCloseDTO> {
  return client.request<YearEndCloseDTO>('GET', `/api/v1/year-end-closes/${encodeURIComponent(params.id)}`, {
  });
}

export async function glYearEndClosesReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseYearEndCloseRequest,
  idempotencyKey?: string,
): Promise<YearEndCloseDTO> {
  return client.request<YearEndCloseDTO>('POST', `/api/v1/year-end-closes/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function glYearEndClosesWithdraw(
  client: ApiClient,
  params: { id: string },
  body: WithdrawYearEndCloseRequest,
  idempotencyKey?: string,
): Promise<YearEndCloseDTO> {
  return client.request<YearEndCloseDTO>('POST', `/api/v1/year-end-closes/${encodeURIComponent(params.id)}/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function glReportsTrialBalance(
  client: ApiClient,
  query: TrialBalanceQuery,
): Promise<TrialBalanceReport> {
  return client.request<TrialBalanceReport>('GET', `/api/v1/reports/trial-balance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsTrialBalanceExport(
  client: ApiClient,
  query: TrialBalanceQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/trial-balance/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsIncomeStatement(
  client: ApiClient,
  query: IncomeStatementQuery,
): Promise<Statement> {
  return client.request<Statement>('GET', `/api/v1/reports/income-statement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsIncomeStatementExport(
  client: ApiClient,
  query: IncomeStatementQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/income-statement/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsBalanceSheet(
  client: ApiClient,
  query: BalanceSheetQuery,
): Promise<Statement> {
  return client.request<Statement>('GET', `/api/v1/reports/balance-sheet`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsBalanceSheetExport(
  client: ApiClient,
  query: BalanceSheetQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/balance-sheet/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsLedger(
  client: ApiClient,
  query: LedgerQuery,
): Promise<LedgerPage> {
  return client.request<LedgerPage>('GET', `/api/v1/reports/ledger`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsLedgerExport(
  client: ApiClient,
  query: LedgerQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/ledger/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsYearEndClosePlan(
  client: ApiClient,
  query: YearEndCloseQuery,
): Promise<ClosePlan> {
  return client.request<ClosePlan>('GET', `/api/v1/reports/year-end-close-plan`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsYearEndClosePlanExport(
  client: ApiClient,
  query: YearEndCloseQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/year-end-close-plan/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsCashFlow(
  client: ApiClient,
  query: GlCashFlowQuery,
): Promise<GlCashFlowStatementDTO> {
  return client.request<GlCashFlowStatementDTO>('GET', `/api/v1/reports/cash-flow`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsCashFlowExport(
  client: ApiClient,
  query: GlCashFlowQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/cash-flow/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsEquityMovement(
  client: ApiClient,
  query: GlEquityMovementQuery,
): Promise<GlEquityMovementDTO> {
  return client.request<GlEquityMovementDTO>('GET', `/api/v1/reports/equity-movement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsEquityMovementExport(
  client: ApiClient,
  query: GlEquityMovementQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/equity-movement/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glReportsManualJournals(
  client: ApiClient,
  query: GlManualJournalAnalysisQuery,
): Promise<GlManualJournalAnalysisDTO> {
  return client.request<GlManualJournalAnalysisDTO>('GET', `/api/v1/reports/manual-journals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glCashFlowMappingsList(
  client: ApiClient,
  query: GlCashFlowMappingQuery,
): Promise<GlCashFlowMappingDTO[]> {
  return client.request<GlCashFlowMappingDTO[]>('GET', `/api/v1/cash-flow-mappings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function glCashFlowMappingsSet(
  client: ApiClient,
  params: { accountId: string },
  body: GlCashFlowMappingRequest,
): Promise<GlCashFlowMappingDTO> {
  return client.request<GlCashFlowMappingDTO>('PUT', `/api/v1/cash-flow-mappings/${encodeURIComponent(params.accountId)}`, {
    body,
  });
}

export async function apVendorsList(
  client: ApiClient,
  query: ListVendorsQuery,
): Promise<PageVendorDTO> {
  return client.request<PageVendorDTO>('GET', `/api/v1/vendors`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apVendorsCreate(
  client: ApiClient,
  body: CreateVendorRequest,
  idempotencyKey?: string,
): Promise<VendorDTO> {
  return client.request<VendorDTO>('POST', `/api/v1/vendors`, {
    body,
    idempotencyKey,
  });
}

export async function apVendorsGet(
  client: ApiClient,
  params: { id: string },
): Promise<VendorDTO> {
  return client.request<VendorDTO>('GET', `/api/v1/vendors/${encodeURIComponent(params.id)}`, {
  });
}

export async function apVendorsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateVendorRequest,
): Promise<VendorDTO> {
  return client.request<VendorDTO>('PATCH', `/api/v1/vendors/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function apVendorsDelete(
  client: ApiClient,
  params: { id: string },
  query: ApDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/vendors/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apVendorBankAccountsList(
  client: ApiClient,
  params: { id: string },
  query: ListBankAccountsQuery,
): Promise<PageBankAccountDTO> {
  return client.request<PageBankAccountDTO>('GET', `/api/v1/vendors/${encodeURIComponent(params.id)}/bank-accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apVendorBankAccountsRequest(
  client: ApiClient,
  params: { id: string },
  body: BankAccountRequest,
  idempotencyKey?: string,
): Promise<BankChangeDTO> {
  return client.request<BankChangeDTO>('POST', `/api/v1/vendors/${encodeURIComponent(params.id)}/bank-accounts`, {
    body,
    idempotencyKey,
  });
}

export async function apVendorBankAccountsRequestUpdate(
  client: ApiClient,
  params: { id: string; baId: string },
  body: BankAccountRequest,
  idempotencyKey?: string,
): Promise<BankChangeDTO> {
  return client.request<BankChangeDTO>('PATCH', `/api/v1/vendors/${encodeURIComponent(params.id)}/bank-accounts/${encodeURIComponent(params.baId)}`, {
    body,
    idempotencyKey,
  });
}

export async function apVendorBankAccountsRequestDeactivation(
  client: ApiClient,
  params: { id: string; baId: string },
  body: DeactivateBankAccountRequest,
): Promise<BankChangeDTO> {
  return client.request<BankChangeDTO>('DELETE', `/api/v1/vendors/${encodeURIComponent(params.id)}/bank-accounts/${encodeURIComponent(params.baId)}`, {
    body,
  });
}

export async function apVendorBankAccountsReveal(
  client: ApiClient,
  params: { id: string; baId: string },
  body: RevealRequest,
): Promise<RevealResponse> {
  return client.request<RevealResponse>('POST', `/api/v1/vendors/${encodeURIComponent(params.id)}/bank-accounts/${encodeURIComponent(params.baId)}/reveal`, {
    body,
  });
}

export async function apVendorBankChangesList(
  client: ApiClient,
  query: ListChangesQuery,
): Promise<PageBankChangeDTO> {
  return client.request<PageBankChangeDTO>('GET', `/api/v1/vendor-bank-changes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apVendorBankChangesDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideChangeRequest,
  idempotencyKey?: string,
): Promise<BankChangeDTO> {
  return client.request<BankChangeDTO>('POST', `/api/v1/vendor-bank-changes/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesList(
  client: ApiClient,
  query: ListInvoicesQuery,
): Promise<PageInvoiceDTO2> {
  return client.request<PageInvoiceDTO2>('GET', `/api/v1/ap-invoices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apInvoicesCreate(
  client: ApiClient,
  body: CreateInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('GET', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}`, {
  });
}

export async function apInvoicesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateInvoiceRequest,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('PATCH', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function apInvoicesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}`, {
  });
}

export async function apInvoicesSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function apInvoicesApprove(
  client: ApiClient,
  params: { id: string },
  body: DecideInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesPost(
  client: ApiClient,
  params: { id: string },
  body: PostInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/post`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesHold(
  client: ApiClient,
  params: { id: string },
  body: HoldInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/hold`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesReleaseHold(
  client: ApiClient,
  params: { id: string },
  body: ReleaseInvoiceHoldRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/release-hold`, {
    body,
    idempotencyKey,
  });
}

export async function apCreditNotesCreate(
  client: ApiClient,
  body: CreateInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/api/v1/ap-credit-notes`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentRunsList(
  client: ApiClient,
  query: ListRunsQuery,
): Promise<PageRunDTO> {
  return client.request<PageRunDTO>('GET', `/api/v1/payment-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apPaymentRunsCreate(
  client: ApiClient,
  body: CreateRunRequest,
  idempotencyKey?: string,
): Promise<RunDTO> {
  return client.request<RunDTO>('POST', `/api/v1/payment-runs`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RunDTO> {
  return client.request<RunDTO>('GET', `/api/v1/payment-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function apPaymentRunsApprove(
  client: ApiClient,
  params: { id: string },
  body: DecideRunRequest,
  idempotencyKey?: string,
): Promise<RunDTO> {
  return client.request<RunDTO>('POST', `/api/v1/payment-runs/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentRunsExecute(
  client: ApiClient,
  params: { id: string },
  body: ExecuteRunRequest,
  idempotencyKey?: string,
): Promise<RunDTO> {
  return client.request<RunDTO>('POST', `/api/v1/payment-runs/${encodeURIComponent(params.id)}/execute`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentRunsBankFile(
  client: ApiClient,
  params: { id: string },
): Promise<BankFileResponse> {
  return client.request<BankFileResponse>('POST', `/api/v1/payment-runs/${encodeURIComponent(params.id)}/bank-file`, {
  });
}

export async function apBankFileProfilesList(
  client: ApiClient,
  query: ListBankFileProfilesQuery,
): Promise<PageBankFileProfileDTO> {
  return client.request<PageBankFileProfileDTO>('GET', `/api/v1/bank-file-profiles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apReportsAging(
  client: ApiClient,
  query: AgingQuery,
): Promise<AgingReport> {
  return client.request<AgingReport>('GET', `/api/v1/reports/ap-aging`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apReportsVendorLedger(
  client: ApiClient,
  params: { id: string },
  query: ApLedgerQuery,
): Promise<VendorLedger> {
  return client.request<VendorLedger>('GET', `/api/v1/vendors/${encodeURIComponent(params.id)}/ledger`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apReportsVendorStatement(
  client: ApiClient,
  params: { id: string },
  query: ApLedgerQuery,
): Promise<VendorStatement> {
  return client.request<VendorStatement>('GET', `/api/v1/vendors/${encodeURIComponent(params.id)}/statement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apSettingsGet(
  client: ApiClient,
  query: SettingsQuery,
): Promise<SettingsDTO> {
  return client.request<SettingsDTO>('GET', `/api/v1/ap-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apSettingsUpsert(
  client: ApiClient,
  body: UpsertSettingsRequest,
  idempotencyKey?: string,
): Promise<SettingsDTO> {
  return client.request<SettingsDTO>('PUT', `/api/v1/ap-settings`, {
    body,
    idempotencyKey,
  });
}

export async function apReportsWithholding(
  client: ApiClient,
  query: ApWithholdingRegisterQuery,
): Promise<ApWithholdingRegisterDTO> {
  return client.request<ApWithholdingRegisterDTO>('GET', `/api/v1/reports/withholding`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apReportsWithholdingExport(
  client: ApiClient,
  query: ApWithholdingRegisterQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/withholding/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apWhtRemittancesList(
  client: ApiClient,
  query: ApWhtRemittanceListQuery,
): Promise<PageApWhtRemittanceDTO> {
  return client.request<PageApWhtRemittanceDTO>('GET', `/api/v1/ap-wht-remittances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apWhtRemittancesCreate(
  client: ApiClient,
  body: ApCreateWhtRemittanceRequest,
  idempotencyKey?: string,
): Promise<ApWhtRemittanceDTO> {
  return client.request<ApWhtRemittanceDTO>('POST', `/api/v1/ap-wht-remittances`, {
    body,
    idempotencyKey,
  });
}

export async function apWhtRemittancesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ApWhtRemittanceDTO> {
  return client.request<ApWhtRemittanceDTO>('GET', `/api/v1/ap-wht-remittances/${encodeURIComponent(params.id)}`, {
  });
}

export async function apWhtRemittancesPost(
  client: ApiClient,
  params: { id: string },
  body: ApPostWhtRemittanceRequest,
  idempotencyKey?: string,
): Promise<ApWhtRemittanceDTO> {
  return client.request<ApWhtRemittanceDTO>('POST', `/api/v1/ap-wht-remittances/${encodeURIComponent(params.id)}/post`, {
    body,
    idempotencyKey,
  });
}

export async function apWhtRemittancesReverse(
  client: ApiClient,
  params: { id: string },
  body: ApReverseWhtRemittanceRequest,
  idempotencyKey?: string,
): Promise<ApWhtRemittanceDTO> {
  return client.request<ApWhtRemittanceDTO>('POST', `/api/v1/ap-wht-remittances/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function apWhtRemittancesCancel(
  client: ApiClient,
  params: { id: string },
  body: ApCancelWhtRemittanceRequest,
  idempotencyKey?: string,
): Promise<ApWhtRemittanceDTO> {
  return client.request<ApWhtRemittanceDTO>('POST', `/api/v1/ap-wht-remittances/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function apSupplierStatementsList(
  client: ApiClient,
  query: ApSupplierStatementListQuery,
): Promise<PageApSupplierStatementDTO> {
  return client.request<PageApSupplierStatementDTO>('GET', `/api/v1/ap-supplier-statements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apSupplierStatementsCreate(
  client: ApiClient,
  body: ApCreateSupplierStatementRequest,
  idempotencyKey?: string,
): Promise<ApSupplierStatementDTO> {
  return client.request<ApSupplierStatementDTO>('POST', `/api/v1/ap-supplier-statements`, {
    body,
    idempotencyKey,
  });
}

export async function apSupplierStatementsImport(
  client: ApiClient,
  file: File | Blob,
  fields: { companyId: number; vendorId: number; currency: string; statementDate: string; reportedBalance: string; periodFrom?: string; openingBalance?: string; statementRef?: string; notes?: string },
  idempotencyKey?: string,
): Promise<ApSupplierStatementDTO> {
  const form = new FormData();
  form.append('file', file);
  form.append('companyId', String(fields.companyId));
  form.append('vendorId', String(fields.vendorId));
  form.append('currency', String(fields.currency));
  form.append('statementDate', String(fields.statementDate));
  form.append('reportedBalance', String(fields.reportedBalance));
  if (fields.periodFrom !== undefined) {
    form.append('periodFrom', String(fields.periodFrom));
  }
  if (fields.openingBalance !== undefined) {
    form.append('openingBalance', String(fields.openingBalance));
  }
  if (fields.statementRef !== undefined) {
    form.append('statementRef', String(fields.statementRef));
  }
  if (fields.notes !== undefined) {
    form.append('notes', String(fields.notes));
  }
  return client.request<ApSupplierStatementDTO>('POST', `/api/v1/ap-supplier-statements/import`, {
    body: form,
    idempotencyKey,
  });
}

export async function apSupplierStatementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ApSupplierStatementDTO> {
  return client.request<ApSupplierStatementDTO>('GET', `/api/v1/ap-supplier-statements/${encodeURIComponent(params.id)}`, {
  });
}

export async function apSupplierStatementsReconciliation(
  client: ApiClient,
  params: { id: string },
): Promise<ApStatementReconDTO> {
  return client.request<ApStatementReconDTO>('GET', `/api/v1/ap-supplier-statements/${encodeURIComponent(params.id)}/reconciliation`, {
  });
}

export async function apReportsDuplicatePayments(
  client: ApiClient,
  query: ApDuplicatePaymentsQuery,
): Promise<ApDuplicatePaymentsDTO> {
  return client.request<ApDuplicatePaymentsDTO>('GET', `/api/v1/reports/duplicate-payments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apSignalsList(
  client: ApiClient,
  query: ListSignalsQuery,
): Promise<PageAPInvoiceSignalDTO> {
  return client.request<PageAPInvoiceSignalDTO>('GET', `/api/v1/ap-invoice-signals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apInvoicesListSignals(
  client: ApiClient,
  params: { id: string },
): Promise<APInvoiceSignalDTO[]> {
  return client.request<APInvoiceSignalDTO[]>('GET', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/signals`, {
  });
}

export async function apSignalsDispose(
  client: ApiClient,
  params: { id: string },
  body: DisposeSignalRequest,
  idempotencyKey?: string,
): Promise<APInvoiceSignalDTO> {
  return client.request<APInvoiceSignalDTO>('POST', `/api/v1/ap-invoice-signals/${encodeURIComponent(params.id)}/dispose`, {
    body,
    idempotencyKey,
  });
}

export async function apInvoicesRescanSignals(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<APInvoiceSignalDTO[]> {
  return client.request<APInvoiceSignalDTO[]>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/rescan-signals`, {
    idempotencyKey,
  });
}

export async function apSignalsListSettings(
  client: ApiClient,
  query: ListSignalSettingsQuery,
): Promise<SignalSettingDTO[]> {
  return client.request<SignalSettingDTO[]>('GET', `/api/v1/ap-signal-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apSignalsSetSettings(
  client: ApiClient,
  body: SetSignalSettingsRequest,
): Promise<SignalSettingDTO[]> {
  return client.request<SignalSettingDTO[]>('PUT', `/api/v1/ap-signal-settings`, {
    body,
  });
}

export async function apBankChangesReleaseEarly(
  client: ApiClient,
  params: { id: string },
  body: ReleaseBankChangeEarlyRequest,
  idempotencyKey?: string,
): Promise<BankChangeDTO> {
  return client.request<BankChangeDTO>('POST', `/api/v1/ap-bank-changes/${encodeURIComponent(params.id)}/release-early`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentExceptionsList(
  client: ApiClient,
  query: ListPaymentExceptionsQuery,
): Promise<PageAPPaymentExceptionDTO> {
  return client.request<PageAPPaymentExceptionDTO>('GET', `/api/v1/ap-payment-exceptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apPaymentExceptionsCreate(
  client: ApiClient,
  body: CreatePaymentExceptionRequest,
  idempotencyKey?: string,
): Promise<APPaymentExceptionDTO> {
  return client.request<APPaymentExceptionDTO>('POST', `/api/v1/ap-payment-exceptions`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentExceptionsResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolvePaymentExceptionRequest,
  idempotencyKey?: string,
): Promise<APPaymentExceptionDTO> {
  return client.request<APPaymentExceptionDTO>('POST', `/api/v1/ap-payment-exceptions/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function apPaymentRunsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelPaymentRunRequest,
  idempotencyKey?: string,
): Promise<RunDTO> {
  return client.request<RunDTO>('POST', `/api/v1/payment-runs/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function arCustomersList(
  client: ApiClient,
  query: ListCustomersQuery,
): Promise<PageCustomerDTO> {
  return client.request<PageCustomerDTO>('GET', `/api/v1/customers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersCreate(
  client: ApiClient,
  body: CreateCustomerRequest,
  idempotencyKey?: string,
): Promise<CustomerDTO> {
  return client.request<CustomerDTO>('POST', `/api/v1/customers`, {
    body,
    idempotencyKey,
  });
}

export async function arCustomersGet(
  client: ApiClient,
  params: { id: string },
): Promise<CustomerDTO> {
  return client.request<CustomerDTO>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}`, {
  });
}

export async function arCustomersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCustomerRequest,
): Promise<CustomerDTO> {
  return client.request<CustomerDTO>('PATCH', `/api/v1/customers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function arCustomersDelete(
  client: ApiClient,
  params: { id: string },
  query: ArDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/customers/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersLedger(
  client: ApiClient,
  params: { id: string },
  query: ArLedgerQuery,
): Promise<ArLedgerPage> {
  return client.request<ArLedgerPage>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/ledger`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersStatement(
  client: ApiClient,
  params: { id: string },
  query: StatementQuery,
): Promise<ArStatement> {
  return client.request<ArStatement>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/statement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersOpenItems(
  client: ApiClient,
  params: { id: string },
  query: OpenItemsQuery,
): Promise<SuggestionDTO[]> {
  return client.request<SuggestionDTO[]>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/open-items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersCreditCheck(
  client: ApiClient,
  params: { id: string },
): Promise<CreditCheckDTO> {
  return client.request<CreditCheckDTO>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/credit-check`, {
  });
}

export async function arCustomersDunningPause(
  client: ApiClient,
  params: { id: string },
  body: PauseDunningRequest,
): Promise<CustomerDTO> {
  return client.request<CustomerDTO>('POST', `/api/v1/customers/${encodeURIComponent(params.id)}/dunning/pause`, {
    body,
  });
}

export async function arCustomersDunningResume(
  client: ApiClient,
  params: { id: string },
): Promise<CustomerDTO> {
  return client.request<CustomerDTO>('POST', `/api/v1/customers/${encodeURIComponent(params.id)}/dunning/resume`, {
  });
}

export async function arCreditExposureList(
  client: ApiClient,
  query: ArCreditExposureQuery,
): Promise<PageArCreditExposureDTO> {
  return client.request<PageArCreditExposureDTO>('GET', `/api/v1/ar-credit-exposure`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersCreditHold(
  client: ApiClient,
  params: { id: string },
  body: ArCreditHoldActionRequest,
  idempotencyKey?: string,
): Promise<ArCreditHoldDTO> {
  return client.request<ArCreditHoldDTO>('POST', `/api/v1/customers/${encodeURIComponent(params.id)}/credit-hold`, {
    body,
    idempotencyKey,
  });
}

export async function arCustomersCreditRelease(
  client: ApiClient,
  params: { id: string },
  body: ArCreditHoldActionRequest,
  idempotencyKey?: string,
): Promise<ArCreditHoldDTO> {
  return client.request<ArCreditHoldDTO>('POST', `/api/v1/customers/${encodeURIComponent(params.id)}/credit-release`, {
    body,
    idempotencyKey,
  });
}

export async function arCustomersRequestCreditLimit(
  client: ApiClient,
  params: { id: string },
  body: ArCreditLimitRequestInput,
  idempotencyKey?: string,
): Promise<ArCreditLimitRequestDTO> {
  return client.request<ArCreditLimitRequestDTO>('POST', `/api/v1/customers/${encodeURIComponent(params.id)}/credit-limit-requests`, {
    body,
    idempotencyKey,
  });
}

export async function arCustomersCreditLimitRequests(
  client: ApiClient,
  params: { id: string },
  query: ArCreditLimitRequestListQuery,
): Promise<PageArCreditLimitRequestDTO> {
  return client.request<PageArCreditLimitRequestDTO>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/credit-limit-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arInvoicesList(
  client: ApiClient,
  query: ArListInvoicesQuery,
): Promise<PageInvoiceDTO3> {
  return client.request<PageInvoiceDTO3>('GET', `/api/v1/ar-invoices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arInvoicesCreate(
  client: ApiClient,
  body: ArCreateInvoiceRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-invoices`, {
    body,
    idempotencyKey,
  });
}

export async function arInvoicesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('GET', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}`, {
  });
}

export async function arInvoicesUpdate(
  client: ApiClient,
  params: { id: string },
  body: ArUpdateInvoiceRequest,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('PATCH', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function arInvoicesDelete(
  client: ApiClient,
  params: { id: string },
  query: ArDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arInvoicesConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmInvoiceRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function arInvoicesPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function arCreditNotesList(
  client: ApiClient,
  query: ArListInvoicesQuery,
): Promise<PageInvoiceDTO3> {
  return client.request<PageInvoiceDTO3>('GET', `/api/v1/ar-credit-notes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCreditNotesCreate(
  client: ApiClient,
  body: ArCreateInvoiceRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-credit-notes`, {
    body,
    idempotencyKey,
  });
}

export async function arCreditNotesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('GET', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}`, {
  });
}

export async function arCreditNotesUpdate(
  client: ApiClient,
  params: { id: string },
  body: ArUpdateInvoiceRequest,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('PATCH', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function arCreditNotesDelete(
  client: ApiClient,
  params: { id: string },
  query: ArDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCreditNotesConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmInvoiceRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function arCreditNotesPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function arCreditNotesAllocate(
  client: ApiClient,
  params: { id: string },
  body: AllocateRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}/allocate`, {
    body,
    idempotencyKey,
  });
}

export async function arReceiptsList(
  client: ApiClient,
  query: ListReceiptsQuery,
): Promise<PageReceiptDTO> {
  return client.request<PageReceiptDTO>('GET', `/api/v1/ar-receipts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arReceiptsCreate(
  client: ApiClient,
  body: CreateReceiptRequest,
  idempotencyKey?: string,
): Promise<ReceiptDTO> {
  return client.request<ReceiptDTO>('POST', `/api/v1/ar-receipts`, {
    body,
    idempotencyKey,
  });
}

export async function arReceiptsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ReceiptDTO> {
  return client.request<ReceiptDTO>('GET', `/api/v1/ar-receipts/${encodeURIComponent(params.id)}`, {
  });
}

export async function arReceiptsAllocate(
  client: ApiClient,
  params: { id: string },
  body: AllocateRequest,
  idempotencyKey?: string,
): Promise<ReceiptDTO> {
  return client.request<ReceiptDTO>('POST', `/api/v1/ar-receipts/${encodeURIComponent(params.id)}/allocate`, {
    body,
    idempotencyKey,
  });
}

export async function arReceiptsReallocate(
  client: ApiClient,
  params: { id: string },
  body: ArReceiptReallocateRequest,
  idempotencyKey?: string,
): Promise<ArReceiptReallocationDTO> {
  return client.request<ArReceiptReallocationDTO>('POST', `/api/v1/ar-receipts/${encodeURIComponent(params.id)}/reallocate`, {
    body,
    idempotencyKey,
  });
}

export async function arReceiptsAllocationHistory(
  client: ApiClient,
  params: { id: string },
): Promise<ArReceiptAllocationHistoryDTO> {
  return client.request<ArReceiptAllocationHistoryDTO>('GET', `/api/v1/ar-receipts/${encodeURIComponent(params.id)}/allocation-history`, {
  });
}

export async function arReportsAging(
  client: ApiClient,
  query: ArAgingQuery,
): Promise<ArAgingReport> {
  return client.request<ArAgingReport>('GET', `/api/v1/reports/ar-aging`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arReportsDso(
  client: ApiClient,
  query: DSOQuery,
): Promise<DSOReport> {
  return client.request<DSOReport>('GET', `/api/v1/reports/dso`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arDunningLevelsList(
  client: ApiClient,
  query: ArDunningLevelListQuery,
): Promise<PageArDunningLevelDTO> {
  return client.request<PageArDunningLevelDTO>('GET', `/api/v1/ar-dunning-levels`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arDunningLevelsCreate(
  client: ApiClient,
  body: ArDunningLevelCreateRequest,
  idempotencyKey?: string,
): Promise<ArDunningLevelDTO> {
  return client.request<ArDunningLevelDTO>('POST', `/api/v1/ar-dunning-levels`, {
    body,
    idempotencyKey,
  });
}

export async function arDunningLevelsUpdate(
  client: ApiClient,
  params: { id: string },
  body: ArDunningLevelUpdateRequest,
  idempotencyKey?: string,
): Promise<ArDunningLevelDTO> {
  return client.request<ArDunningLevelDTO>('PATCH', `/api/v1/ar-dunning-levels/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function arDunningLevelsDelete(
  client: ApiClient,
  params: { id: string },
  query: ArDeleteQuery,
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/ar-dunning-levels/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
    idempotencyKey,
  });
}

export async function arDunningRun(
  client: ApiClient,
  body: ArDunningRunRequest,
  idempotencyKey?: string,
): Promise<ArDunningRunResultDTO> {
  return client.request<ArDunningRunResultDTO>('POST', `/api/v1/ar-dunning/run`, {
    body,
    idempotencyKey,
  });
}

export async function arDunningEventsList(
  client: ApiClient,
  query: ArDunningEventListQuery,
): Promise<PageArDunningEventDTO> {
  return client.request<PageArDunningEventDTO>('GET', `/api/v1/ar-dunning-events`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arDunningEventsRecordResponse(
  client: ApiClient,
  params: { id: string },
  body: ArDunningResponseRequest,
  idempotencyKey?: string,
): Promise<ArDunningEventDTO> {
  return client.request<ArDunningEventDTO>('POST', `/api/v1/ar-dunning-events/${encodeURIComponent(params.id)}/response`, {
    body,
    idempotencyKey,
  });
}

export async function arReportsDunningEffectiveness(
  client: ApiClient,
  query: ArDunningEffectivenessQuery,
): Promise<ArDunningEffectivenessReport> {
  return client.request<ArDunningEffectivenessReport>('GET', `/api/v1/reports/dunning-effectiveness`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCollectionsWorklist(
  client: ApiClient,
  query: ArCollectionsWorklistQuery,
): Promise<PageArCollectionsItemDTO> {
  return client.request<PageArCollectionsItemDTO>('GET', `/api/v1/ar-collections/worklist`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arPromisesList(
  client: ApiClient,
  query: ArPromiseListQuery,
): Promise<PageArPromiseDTO> {
  return client.request<PageArPromiseDTO>('GET', `/api/v1/ar-promises`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arPromisesCreate(
  client: ApiClient,
  body: ArPromiseCreateRequest,
  idempotencyKey?: string,
): Promise<ArPromiseDTO> {
  return client.request<ArPromiseDTO>('POST', `/api/v1/ar-promises`, {
    body,
    idempotencyKey,
  });
}

export async function arPromisesUpdate(
  client: ApiClient,
  params: { id: string },
  body: ArPromiseUpdateRequest,
  idempotencyKey?: string,
): Promise<ArPromiseDTO> {
  return client.request<ArPromiseDTO>('PATCH', `/api/v1/ar-promises/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function arCollectionsSettingsGet(
  client: ApiClient,
  query: ArCollectionsSettingsQuery,
): Promise<ArCollectionsSettingsDTO> {
  return client.request<ArCollectionsSettingsDTO>('GET', `/api/v1/ar-collections-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCollectionsSettingsSet(
  client: ApiClient,
  body: ArCollectionsSettingsRequest,
  idempotencyKey?: string,
): Promise<ArCollectionsSettingsDTO> {
  return client.request<ArCollectionsSettingsDTO>('PUT', `/api/v1/ar-collections-settings`, {
    body,
    idempotencyKey,
  });
}

export async function arReportsCei(
  client: ApiClient,
  query: ArCeiQuery,
): Promise<ArCeiReport> {
  return client.request<ArCeiReport>('GET', `/api/v1/reports/cei`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arReceiptsAllocationProposal(
  client: ApiClient,
  params: { id: string },
): Promise<ArAllocationProposalDTO> {
  return client.request<ArAllocationProposalDTO>('GET', `/api/v1/ar-receipts/${encodeURIComponent(params.id)}/allocation-proposal`, {
  });
}

export async function arCreditNotesAllocationProposal(
  client: ApiClient,
  params: { id: string },
): Promise<ArAllocationProposalDTO> {
  return client.request<ArAllocationProposalDTO>('GET', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}/allocation-proposal`, {
  });
}

export async function arReportsUnallocatedReceipts(
  client: ApiClient,
  query: ArUnallocatedReceiptsQuery,
): Promise<PageArUnallocatedReceiptDTO> {
  return client.request<PageArUnallocatedReceiptDTO>('GET', `/api/v1/reports/unallocated-receipts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arWriteOffsList(
  client: ApiClient,
  query: ArWriteOffListQuery,
): Promise<PageArWriteOffDTO> {
  return client.request<PageArWriteOffDTO>('GET', `/api/v1/ar-write-offs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arWriteOffsCreate(
  client: ApiClient,
  body: ArWriteOffCreateRequest,
  idempotencyKey?: string,
): Promise<ArWriteOffDTO> {
  return client.request<ArWriteOffDTO>('POST', `/api/v1/ar-write-offs`, {
    body,
    idempotencyKey,
  });
}

export async function arWriteOffsPreview(
  client: ApiClient,
  body: ArWriteOffPreviewRequest,
): Promise<ArWriteOffPreviewDTO> {
  return client.request<ArWriteOffPreviewDTO>('POST', `/api/v1/ar-write-offs/preview`, {
    body,
  });
}

export async function arWriteOffsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ArWriteOffDTO> {
  return client.request<ArWriteOffDTO>('GET', `/api/v1/ar-write-offs/${encodeURIComponent(params.id)}`, {
  });
}

export async function arWriteOffsUpdate(
  client: ApiClient,
  params: { id: string },
  body: ArWriteOffUpdateRequest,
  idempotencyKey?: string,
): Promise<ArWriteOffDTO> {
  return client.request<ArWriteOffDTO>('PATCH', `/api/v1/ar-write-offs/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function arWriteOffsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ArWriteOffDTO> {
  return client.request<ArWriteOffDTO>('POST', `/api/v1/ar-write-offs/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function arWriteOffsPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ArWriteOffDTO> {
  return client.request<ArWriteOffDTO>('POST', `/api/v1/ar-write-offs/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function arWriteOffsReverse(
  client: ApiClient,
  params: { id: string },
  body: ArWriteOffReverseRequest,
  idempotencyKey?: string,
): Promise<ArWriteOffDTO> {
  return client.request<ArWriteOffDTO>('POST', `/api/v1/ar-write-offs/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function arReportsWriteOffs(
  client: ApiClient,
  query: ArWriteOffReportQuery,
): Promise<ArWriteOffReport> {
  return client.request<ArWriteOffReport>('GET', `/api/v1/reports/ar-write-offs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arWriteOffSettingsGet(
  client: ApiClient,
  query: ArWriteOffSettingsQuery,
): Promise<ArWriteOffSettingsDTO> {
  return client.request<ArWriteOffSettingsDTO>('GET', `/api/v1/ar-write-off-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arWriteOffSettingsSet(
  client: ApiClient,
  body: ArWriteOffSettingsRequest,
  idempotencyKey?: string,
): Promise<ArWriteOffSettingsDTO> {
  return client.request<ArWriteOffSettingsDTO>('PUT', `/api/v1/ar-write-off-settings`, {
    body,
    idempotencyKey,
  });
}

export async function arEclSegmentsList(
  client: ApiClient,
  query: ListECLSegmentsQuery,
): Promise<PageECLSegmentDTO> {
  return client.request<PageECLSegmentDTO>('GET', `/api/v1/ar-ecl-segments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arEclSegmentsCreate(
  client: ApiClient,
  body: CreateECLSegmentRequest,
  idempotencyKey?: string,
): Promise<ECLSegmentDTO> {
  return client.request<ECLSegmentDTO>('POST', `/api/v1/ar-ecl-segments`, {
    body,
    idempotencyKey,
  });
}

export async function arEclSegmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateECLSegmentRequest,
): Promise<ECLSegmentDTO> {
  return client.request<ECLSegmentDTO>('PATCH', `/api/v1/ar-ecl-segments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function arEclSegmentsDelete(
  client: ApiClient,
  params: { id: string },
  query: ArDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/ar-ecl-segments/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arEclMatricesList(
  client: ApiClient,
  query: ListECLMatricesQuery,
): Promise<PageECLMatrixDTO> {
  return client.request<PageECLMatrixDTO>('GET', `/api/v1/ar-ecl-matrices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arEclMatricesCreate(
  client: ApiClient,
  body: CreateECLMatrixRequest,
  idempotencyKey?: string,
): Promise<ECLMatrixDTO> {
  return client.request<ECLMatrixDTO>('POST', `/api/v1/ar-ecl-matrices`, {
    body,
    idempotencyKey,
  });
}

export async function arEclMatricesListRates(
  client: ApiClient,
  params: { id: string },
): Promise<ECLMatrixRateDTO[]> {
  return client.request<ECLMatrixRateDTO[]>('GET', `/api/v1/ar-ecl-matrices/${encodeURIComponent(params.id)}/rates`, {
  });
}

export async function arEclMatricesSetRates(
  client: ApiClient,
  params: { id: string },
  body: SetECLMatrixRatesRequest,
  idempotencyKey?: string,
): Promise<ECLMatrixDTO> {
  return client.request<ECLMatrixDTO>('PUT', `/api/v1/ar-ecl-matrices/${encodeURIComponent(params.id)}/rates`, {
    body,
    idempotencyKey,
  });
}

export async function arEclMatricesActivate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ECLMatrixDTO> {
  return client.request<ECLMatrixDTO>('POST', `/api/v1/ar-ecl-matrices/${encodeURIComponent(params.id)}/activate`, {
    idempotencyKey,
  });
}

export async function arEclRunsList(
  client: ApiClient,
  query: ListECLRunsQuery,
): Promise<PageECLRunDTO> {
  return client.request<PageECLRunDTO>('GET', `/api/v1/ar-ecl-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arEclRunsCalculate(
  client: ApiClient,
  body: CalculateECLRunRequest,
  idempotencyKey?: string,
): Promise<ECLRunDTO> {
  return client.request<ECLRunDTO>('POST', `/api/v1/ar-ecl-runs`, {
    body,
    idempotencyKey,
  });
}

export async function arEclRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ECLRunDTO> {
  return client.request<ECLRunDTO>('GET', `/api/v1/ar-ecl-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function arEclRunsListLines(
  client: ApiClient,
  params: { id: string },
): Promise<ECLRunLineDTO[]> {
  return client.request<ECLRunLineDTO[]>('GET', `/api/v1/ar-ecl-runs/${encodeURIComponent(params.id)}/lines`, {
  });
}

export async function arEclRunsPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ECLRunDTO> {
  return client.request<ECLRunDTO>('POST', `/api/v1/ar-ecl-runs/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function arEclAssessmentsList(
  client: ApiClient,
  query: ListECLAssessmentsQuery,
): Promise<PageECLAssessmentDTO> {
  return client.request<PageECLAssessmentDTO>('GET', `/api/v1/ar-ecl-assessments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arEclAssessmentsCreate(
  client: ApiClient,
  body: CreateECLAssessmentRequest,
  idempotencyKey?: string,
): Promise<ECLAssessmentDTO> {
  return client.request<ECLAssessmentDTO>('POST', `/api/v1/ar-ecl-assessments`, {
    body,
    idempotencyKey,
  });
}

export async function arEclSettingsGet(
  client: ApiClient,
  query: GetECLSettingsQuery,
): Promise<ECLSettingsDTO> {
  return client.request<ECLSettingsDTO>('GET', `/api/v1/ar-ecl-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arEclSettingsSet(
  client: ApiClient,
  body: SetECLSettingsRequest,
  idempotencyKey?: string,
): Promise<ECLSettingsDTO> {
  return client.request<ECLSettingsDTO>('PUT', `/api/v1/ar-ecl-settings`, {
    body,
    idempotencyKey,
  });
}

export async function arReportsEclMovement(
  client: ApiClient,
  query: ECLMovementQuery,
): Promise<ECLMovementReport> {
  return client.request<ECLMovementReport>('GET', `/api/v1/reports/ecl-movement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersPosition(
  client: ApiClient,
  params: { id: string },
): Promise<CustomerPositionDTO> {
  return client.request<CustomerPositionDTO>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/position`, {
  });
}

export async function cashBankAccountsList(
  client: ApiClient,
  query: CashListAccountsQuery,
): Promise<PageBankAccountDTO2> {
  return client.request<PageBankAccountDTO2>('GET', `/api/v1/bank-accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBankAccountsCreate(
  client: ApiClient,
  body: CreateBankAccountRequest,
  idempotencyKey?: string,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('POST', `/api/v1/bank-accounts`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankAccountsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('GET', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}`, {
  });
}

export async function cashBankAccountsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateBankAccountRequest,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('PATCH', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashBankAccountsReveal(
  client: ApiClient,
  params: { id: string },
  body: CashRevealRequest,
): Promise<CashRevealResponse> {
  return client.request<CashRevealResponse>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/reveal`, {
    body,
  });
}

export async function cashRecurringItemsList(
  client: ApiClient,
  query: ListRecurringQuery,
): Promise<PageRecurringItemDTO> {
  return client.request<PageRecurringItemDTO>('GET', `/api/v1/recurring-cash-items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashRecurringItemsCreate(
  client: ApiClient,
  body: CreateRecurringItemRequest,
  idempotencyKey?: string,
): Promise<RecurringItemDTO> {
  return client.request<RecurringItemDTO>('POST', `/api/v1/recurring-cash-items`, {
    body,
    idempotencyKey,
  });
}

export async function cashRecurringItemsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RecurringItemDTO> {
  return client.request<RecurringItemDTO>('GET', `/api/v1/recurring-cash-items/${encodeURIComponent(params.id)}`, {
  });
}

export async function cashRecurringItemsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRecurringItemRequest,
): Promise<RecurringItemDTO> {
  return client.request<RecurringItemDTO>('PATCH', `/api/v1/recurring-cash-items/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashRecurringItemsDelete(
  client: ApiClient,
  params: { id: string },
  query: CashDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/recurring-cash-items/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashTransferInstructionsList(
  client: ApiClient,
  query: ListInstructionsQuery,
): Promise<PageTransferInstructionDTO> {
  return client.request<PageTransferInstructionDTO>('GET', `/api/v1/transfer-instructions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashFundsInTransitList(
  client: ApiClient,
  query: FundsInTransitQuery,
): Promise<PageFundsInTransitDTO> {
  return client.request<PageFundsInTransitDTO>('GET', `/api/v1/funds-in-transit`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashFundsInTransitAging(
  client: ApiClient,
  query: FundsInTransitAgingQuery,
): Promise<FundsInTransitAgingDTO> {
  return client.request<FundsInTransitAgingDTO>('GET', `/api/v1/reports/funds-in-transit-aging`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashTransferInstructionsDraft(
  client: ApiClient,
  body: CreateInstructionRequest,
  idempotencyKey?: string,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions`, {
    body,
    idempotencyKey,
  });
}

export async function cashTransferInstructionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('GET', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}`, {
  });
}

export async function cashTransferInstructionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('PATCH', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashTransferInstructionsEvents(
  client: ApiClient,
  params: { id: string },
): Promise<TransferEventDTO[]> {
  return client.request<TransferEventDTO[]>('GET', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/events`, {
  });
}

export async function cashTransferInstructionsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function cashTransferInstructionsApprove(
  client: ApiClient,
  params: { id: string },
  body: ApproveInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/approve`, {
    body,
  });
}

export async function cashTransferInstructionsSign(
  client: ApiClient,
  params: { id: string },
  body: SignInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/sign`, {
    body,
  });
}

export async function cashTransferInstructionsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/release`, {
    body,
  });
}

export async function cashTransferInstructionsConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/confirm`, {
    body,
  });
}

export async function cashTransferInstructionsRecall(
  client: ApiClient,
  params: { id: string },
  body: RecallInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/recall`, {
    body,
  });
}

export async function cashTransferInstructionsRecallOutcome(
  client: ApiClient,
  params: { id: string },
  body: RecallOutcomeRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/recall-outcome`, {
    body,
  });
}

export async function cashTransferInstructionsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/reject`, {
    body,
  });
}

export async function cashTransferInstructionsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelInstructionRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function cashBankAccountsSubmit(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountSubmitRequest,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function cashBankAccountsVerify(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountVerifyRequest,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/verify`, {
    body,
  });
}

export async function cashBankAccountsActivate(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountActivateRequest,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/activate`, {
    body,
  });
}

export async function cashBankAccountsSuspend(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountSuspendRequest,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/suspend`, {
    body,
  });
}

export async function cashBankAccountsReactivate(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountReactivateRequest,
): Promise<CashBankAccountDTO> {
  return client.request<CashBankAccountDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/reactivate`, {
    body,
  });
}

export async function cashBankAccountsAmendmentsList(
  client: ApiClient,
  params: { id: string },
): Promise<CashBankAccountAmendmentDTO[]> {
  return client.request<CashBankAccountAmendmentDTO[]>('GET', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/amendments`, {
  });
}

export async function cashBankAccountsAmendmentsCreate(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountAmendmentCreateRequest,
  idempotencyKey?: string,
): Promise<CashBankAccountAmendmentDTO> {
  return client.request<CashBankAccountAmendmentDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/amendments`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankAccountAmendmentsDecide(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountAmendmentDecideRequest,
): Promise<CashBankAccountAmendmentDTO> {
  return client.request<CashBankAccountAmendmentDTO>('POST', `/api/v1/bank-account-amendments/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function cashBankAccountsCloseRequestsList(
  client: ApiClient,
  params: { id: string },
): Promise<CashBankAccountCloseRequestDTO[]> {
  return client.request<CashBankAccountCloseRequestDTO[]>('GET', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/close-requests`, {
  });
}

export async function cashBankAccountsCloseRequestsCreate(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountCloseCreateRequest,
  idempotencyKey?: string,
): Promise<CashBankAccountCloseRequestDTO> {
  return client.request<CashBankAccountCloseRequestDTO>('POST', `/api/v1/bank-accounts/${encodeURIComponent(params.id)}/close-requests`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankAccountCloseRequestsAuthorise(
  client: ApiClient,
  params: { id: string },
  body: CashBankAccountCloseAuthoriseRequest,
): Promise<CashBankAccountCloseRequestDTO> {
  return client.request<CashBankAccountCloseRequestDTO>('POST', `/api/v1/bank-account-close-requests/${encodeURIComponent(params.id)}/authorise`, {
    body,
  });
}

export async function cashBanksList(
  client: ApiClient,
  query: CashBankListQuery,
): Promise<PageCashBankDTO> {
  return client.request<PageCashBankDTO>('GET', `/api/v1/banks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksCreate(
  client: ApiClient,
  body: CashBankCreateRequest,
  idempotencyKey?: string,
): Promise<CashBankDTO> {
  return client.request<CashBankDTO>('POST', `/api/v1/banks`, {
    body,
    idempotencyKey,
  });
}

export async function cashBanksUpdate(
  client: ApiClient,
  params: { id: string },
  body: CashBankUpdateRequest,
): Promise<CashBankDTO> {
  return client.request<CashBankDTO>('PATCH', `/api/v1/banks/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashIcInstrumentsList(
  client: ApiClient,
  query: CashIcInstrumentListQuery,
): Promise<PageCashIcInstrumentDTO> {
  return client.request<PageCashIcInstrumentDTO>('GET', `/api/v1/intercompany-instruments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashIcInstrumentsCreate(
  client: ApiClient,
  body: CashIcInstrumentCreateRequest,
  idempotencyKey?: string,
): Promise<CashIcInstrumentDTO> {
  return client.request<CashIcInstrumentDTO>('POST', `/api/v1/intercompany-instruments`, {
    body,
    idempotencyKey,
  });
}

export async function cashIcInstrumentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CashIcInstrumentDTO> {
  return client.request<CashIcInstrumentDTO>('GET', `/api/v1/intercompany-instruments/${encodeURIComponent(params.id)}`, {
  });
}

export async function cashIcInstrumentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: CashIcInstrumentUpdateRequest,
): Promise<CashIcInstrumentDTO> {
  return client.request<CashIcInstrumentDTO>('PATCH', `/api/v1/intercompany-instruments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashIcInstrumentsSubmit(
  client: ApiClient,
  params: { id: string },
  body: CashIcInstrumentSubmitRequest,
  idempotencyKey?: string,
): Promise<CashIcInstrumentDTO> {
  return client.request<CashIcInstrumentDTO>('POST', `/api/v1/intercompany-instruments/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function cashIcInstrumentsClose(
  client: ApiClient,
  params: { id: string },
  body: CashIcInstrumentCloseRequest,
  idempotencyKey?: string,
): Promise<CashIcInstrumentDTO> {
  return client.request<CashIcInstrumentDTO>('POST', `/api/v1/intercompany-instruments/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function cashIcLoansReport(
  client: ApiClient,
  query: CashIcLoanReportQuery,
): Promise<CashIcLoanReportDTO> {
  return client.request<CashIcLoanReportDTO>('GET', `/api/v1/reports/intercompany-loans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashScreeningEntriesList(
  client: ApiClient,
  query: CashScreeningEntryListQuery,
): Promise<PageCashScreeningEntryDTO> {
  return client.request<PageCashScreeningEntryDTO>('GET', `/api/v1/screening-entries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashScreeningEntriesCreate(
  client: ApiClient,
  body: CashScreeningEntryCreateRequest,
  idempotencyKey?: string,
): Promise<CashScreeningEntryDTO> {
  return client.request<CashScreeningEntryDTO>('POST', `/api/v1/screening-entries`, {
    body,
    idempotencyKey,
  });
}

export async function cashScreeningEntriesImport(
  client: ApiClient,
  file: File | Blob,
  idempotencyKey?: string,
): Promise<CashScreeningImportResultDTO> {
  const form = new FormData();
  form.append('file', file);
  return client.request<CashScreeningImportResultDTO>('POST', `/api/v1/screening-entries/import`, {
    body: form,
    idempotencyKey,
  });
}

export async function cashScreeningEntriesUpdate(
  client: ApiClient,
  params: { id: string },
  body: CashScreeningEntryUpdateRequest,
): Promise<CashScreeningEntryDTO> {
  return client.request<CashScreeningEntryDTO>('PATCH', `/api/v1/screening-entries/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashExchangeControlsList(
  client: ApiClient,
): Promise<CashExchangeControlSetDTO> {
  return client.request<CashExchangeControlSetDTO>('GET', `/api/v1/exchange-control-rules`, {
  });
}

export async function cashExchangeControlsReplace(
  client: ApiClient,
  body: CashExchangeControlRulesRequest,
  idempotencyKey?: string,
): Promise<CashExchangeControlSetDTO> {
  return client.request<CashExchangeControlSetDTO>('PUT', `/api/v1/exchange-control-rules`, {
    body,
    idempotencyKey,
  });
}

export async function cashTransferInstructionsRegulatoryReportRecord(
  client: ApiClient,
  params: { id: string },
  body: CashTransferReportRecordRequest,
  idempotencyKey?: string,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/regulatory-report`, {
    body,
    idempotencyKey,
  });
}

export async function cashTransferInstructionsRegulatoryReportList(
  client: ApiClient,
  params: { id: string },
): Promise<CashTransferReportDTO[]> {
  return client.request<CashTransferReportDTO[]>('GET', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/regulatory-report`, {
  });
}

export async function cashTransferInstructionsScreeningClear(
  client: ApiClient,
  params: { id: string },
  body: CashTransferScreeningClearRequest,
): Promise<TransferInstructionDTO> {
  return client.request<TransferInstructionDTO>('POST', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/screening-clear`, {
    body,
  });
}

export async function cashTransferInstructionsApprovalRoute(
  client: ApiClient,
  params: { id: string },
): Promise<CashTransferApprovalRouteDTO> {
  return client.request<CashTransferApprovalRouteDTO>('GET', `/api/v1/transfer-instructions/${encodeURIComponent(params.id)}/approval-route`, {
  });
}

export async function cashBankRelationshipsList(
  client: ApiClient,
  query: CashBankRelationshipListQuery,
): Promise<PageCashBankRelationshipDTO> {
  return client.request<PageCashBankRelationshipDTO>('GET', `/api/v1/bank-relationships`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBankRelationshipsGet(
  client: ApiClient,
  params: { id: string },
  query: CashBankRelationshipGetQuery,
): Promise<CashBankRelationshipDTO> {
  return client.request<CashBankRelationshipDTO>('GET', `/api/v1/bank-relationships/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBankRelationshipsAccounts(
  client: ApiClient,
  params: { id: string },
  query: CashBankRelationshipAccountListQuery,
): Promise<PageCashBankRelationshipAccountDTO> {
  return client.request<PageCashBankRelationshipAccountDTO>('GET', `/api/v1/bank-relationships/${encodeURIComponent(params.id)}/accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBankRelationshipsFacilities(
  client: ApiClient,
  params: { id: string },
  query: CashBankRelationshipFacilityListQuery,
): Promise<PageCashBankRelationshipFacilityDTO> {
  return client.request<PageCashBankRelationshipFacilityDTO>('GET', `/api/v1/bank-relationships/${encodeURIComponent(params.id)}/facilities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksContactsList(
  client: ApiClient,
  params: { id: string },
  query: CashBankContactListQuery,
): Promise<PageCashBankContactDTO> {
  return client.request<PageCashBankContactDTO>('GET', `/api/v1/banks/${encodeURIComponent(params.id)}/contacts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksContactsCreate(
  client: ApiClient,
  params: { id: string },
  body: CashBankContactCreateRequest,
  idempotencyKey?: string,
): Promise<CashBankContactDTO> {
  return client.request<CashBankContactDTO>('POST', `/api/v1/banks/${encodeURIComponent(params.id)}/contacts`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankContactsUpdate(
  client: ApiClient,
  params: { id: string },
  body: CashBankContactUpdateRequest,
): Promise<CashBankContactDTO> {
  return client.request<CashBankContactDTO>('PATCH', `/api/v1/bank-contacts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashBankContactsDelete(
  client: ApiClient,
  params: { id: string },
  query: CashDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/bank-contacts/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksTariffsList(
  client: ApiClient,
  params: { id: string },
  query: CashBankTariffListQuery,
): Promise<PageCashBankTariffDTO> {
  return client.request<PageCashBankTariffDTO>('GET', `/api/v1/banks/${encodeURIComponent(params.id)}/tariffs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksTariffsCreate(
  client: ApiClient,
  params: { id: string },
  body: CashBankTariffCreateRequest,
  idempotencyKey?: string,
): Promise<CashBankTariffDTO> {
  return client.request<CashBankTariffDTO>('POST', `/api/v1/banks/${encodeURIComponent(params.id)}/tariffs`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankTariffsUpdate(
  client: ApiClient,
  params: { id: string },
  body: CashBankTariffUpdateRequest,
): Promise<CashBankTariffDTO> {
  return client.request<CashBankTariffDTO>('PATCH', `/api/v1/bank-tariffs/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function cashBankChargesList(
  client: ApiClient,
  query: CashBankChargeListQuery,
): Promise<PageCashBankChargeDTO> {
  return client.request<PageCashBankChargeDTO>('GET', `/api/v1/bank-charges`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBankChargesCreate(
  client: ApiClient,
  body: CashBankChargeCreateRequest,
  idempotencyKey?: string,
): Promise<CashBankChargeDTO> {
  return client.request<CashBankChargeDTO>('POST', `/api/v1/bank-charges`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankChargesDelete(
  client: ApiClient,
  params: { id: string },
  query: CashDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/bank-charges/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksLimitChangesList(
  client: ApiClient,
  params: { id: string },
  query: CashBankLimitChangeListQuery,
): Promise<PageCashBankLimitChangeDTO> {
  return client.request<PageCashBankLimitChangeDTO>('GET', `/api/v1/banks/${encodeURIComponent(params.id)}/limit-changes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBanksLimitChangesCreate(
  client: ApiClient,
  params: { id: string },
  body: CashBankLimitChangeRequest,
  idempotencyKey?: string,
): Promise<CashBankLimitChangeDTO> {
  return client.request<CashBankLimitChangeDTO>('POST', `/api/v1/banks/${encodeURIComponent(params.id)}/limit-changes`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankLimitChangesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CashBankLimitChangeDTO> {
  return client.request<CashBankLimitChangeDTO>('GET', `/api/v1/bank-limit-changes/${encodeURIComponent(params.id)}`, {
  });
}

export async function cashBankLimitChangesWithdraw(
  client: ApiClient,
  params: { id: string },
  body: CashBankLimitWithdrawRequest,
  idempotencyKey?: string,
): Promise<CashBankLimitChangeDTO> {
  return client.request<CashBankLimitChangeDTO>('POST', `/api/v1/bank-limit-changes/${encodeURIComponent(params.id)}/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function cashBankRelationshipsConcentration(
  client: ApiClient,
  query: CashBankConcentrationQuery,
): Promise<CashBankConcentrationReportDTO> {
  return client.request<CashBankConcentrationReportDTO>('GET', `/api/v1/reports/bank-relationship-concentration`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashBankChargesVariance(
  client: ApiClient,
  query: CashBankChargesVarianceQuery,
): Promise<CashBankChargesVarianceReportDTO> {
  return client.request<CashBankChargesVarianceReportDTO>('GET', `/api/v1/reports/bank-charges-variance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashMovementAnomaliesList(
  client: ApiClient,
  query: CashMovementAnomaliesQuery,
): Promise<PageCashMovementAnomalyDTO> {
  return client.request<PageCashMovementAnomalyDTO>('GET', `/api/v1/cash-movement-anomalies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function cashMovementAnomalyScansRun(
  client: ApiClient,
  body: CashMovementAnomalyScanRequest,
  idempotencyKey?: string,
): Promise<CashMovementAnomalyScanDTO> {
  return client.request<CashMovementAnomalyScanDTO>('POST', `/api/v1/cash-movement-anomaly-scans`, {
    body,
    idempotencyKey,
  });
}

export async function cashMovementAnomaliesDisposition(
  client: ApiClient,
  params: { id: string },
  body: CashMovementAnomalyDispositionRequest,
  idempotencyKey?: string,
): Promise<CashMovementAnomalyDTO> {
  return client.request<CashMovementAnomalyDTO>('POST', `/api/v1/cash-movement-anomalies/${encodeURIComponent(params.id)}/disposition`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecProfilesList(
  client: ApiClient,
  query: ListProfilesQuery,
): Promise<PageProfileDTO> {
  return client.request<PageProfileDTO>('GET', `/api/v1/bankrec/profiles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecProfilesCreate(
  client: ApiClient,
  body: CreateProfileRequest,
  idempotencyKey?: string,
): Promise<ProfileDTO> {
  return client.request<ProfileDTO>('POST', `/api/v1/bankrec/profiles`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecProfilesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProfileDTO> {
  return client.request<ProfileDTO>('GET', `/api/v1/bankrec/profiles/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecProfilesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProfileRequest,
): Promise<ProfileDTO> {
  return client.request<ProfileDTO>('PATCH', `/api/v1/bankrec/profiles/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function bankrecProfilesDelete(
  client: ApiClient,
  params: { id: string },
  query: BankrecDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/bankrec/profiles/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecStatementImportsCreate(
  client: ApiClient,
  file: File | Blob,
  fields: { bankAccountId: number; sourceFormat: string; profileId?: number; openingBalance?: string; expectedSha256?: string; overrideReasonCode?: string; overrideNote?: string; statementKind?: string; lateAcknowledgement?: string },
  idempotencyKey?: string,
): Promise<BankrecImportDTO> {
  const form = new FormData();
  form.append('file', file);
  form.append('bankAccountId', String(fields.bankAccountId));
  form.append('sourceFormat', String(fields.sourceFormat));
  if (fields.profileId !== undefined) {
    form.append('profileId', String(fields.profileId));
  }
  if (fields.openingBalance !== undefined) {
    form.append('openingBalance', String(fields.openingBalance));
  }
  if (fields.expectedSha256 !== undefined) {
    form.append('expectedSha256', String(fields.expectedSha256));
  }
  if (fields.overrideReasonCode !== undefined) {
    form.append('overrideReasonCode', String(fields.overrideReasonCode));
  }
  if (fields.overrideNote !== undefined) {
    form.append('overrideNote', String(fields.overrideNote));
  }
  if (fields.statementKind !== undefined) {
    form.append('statementKind', String(fields.statementKind));
  }
  if (fields.lateAcknowledgement !== undefined) {
    form.append('lateAcknowledgement', String(fields.lateAcknowledgement));
  }
  return client.request<BankrecImportDTO>('POST', `/api/v1/bankrec/statement-imports`, {
    body: form,
    idempotencyKey,
  });
}

export async function bankrecStatementImportsList(
  client: ApiClient,
  query: BankrecListImportsQuery,
): Promise<PageImportDTO2> {
  return client.request<PageImportDTO2>('GET', `/api/v1/bankrec/statement-imports`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecStatementImportsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecImportDTO> {
  return client.request<BankrecImportDTO>('GET', `/api/v1/bankrec/statement-imports/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecStatementImportsLines(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageStatementLineDTO> {
  return client.request<PageStatementLineDTO>('GET', `/api/v1/bankrec/statement-imports/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecStatementLinesList(
  client: ApiClient,
  query: BankrecListLinesQuery,
): Promise<PageStatementLineDTO> {
  return client.request<PageStatementLineDTO>('GET', `/api/v1/bankrec/statement-lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecStatementLinesSuggestions(
  client: ApiClient,
  params: { id: string },
): Promise<SuggestionsResponse> {
  return client.request<SuggestionsResponse>('GET', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/suggestions`, {
  });
}

export async function bankrecStatementLinesMatch(
  client: ApiClient,
  params: { id: string },
  body: ConfirmMatchRequest,
  idempotencyKey?: string,
): Promise<MatchDTO> {
  return client.request<MatchDTO>('POST', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/match`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementLinesMissingEntry(
  client: ApiClient,
  params: { id: string },
  body: MissingEntryRequest,
  idempotencyKey?: string,
): Promise<MatchDTO> {
  return client.request<MatchDTO>('POST', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/missing-entry`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecMatchesGet(
  client: ApiClient,
  params: { id: string },
): Promise<MatchDTO> {
  return client.request<MatchDTO>('GET', `/api/v1/bankrec/matches/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecMatchesRelease(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<MatchDTO> {
  return client.request<MatchDTO>('POST', `/api/v1/bankrec/matches/${encodeURIComponent(params.id)}/release`, {
    idempotencyKey,
  });
}

export async function bankrecSessionsList(
  client: ApiClient,
  query: ListSessionsQuery,
): Promise<PageSessionDTO> {
  return client.request<PageSessionDTO>('GET', `/api/v1/bankrec/sessions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecSessionsCreate(
  client: ApiClient,
  body: CreateSessionRequest,
  idempotencyKey?: string,
): Promise<BankrecSessionDTO> {
  return client.request<BankrecSessionDTO>('POST', `/api/v1/bankrec/sessions`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecSessionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecSessionDTO> {
  return client.request<BankrecSessionDTO>('GET', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecSessionsLedgerLines(
  client: ApiClient,
  params: { id: string },
  query: LedgerLinesQuery,
): Promise<LedgerLineDTO[]> {
  return client.request<LedgerLineDTO[]>('GET', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/ledger-lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecSessionsClose(
  client: ApiClient,
  params: { id: string },
  body: CloseSessionRequest,
  idempotencyKey?: string,
): Promise<BankrecSessionDTO> {
  return client.request<BankrecSessionDTO>('POST', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecContinuityGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecContinuityDTO> {
  return client.request<BankrecContinuityDTO>('GET', `/api/v1/bankrec/statement-imports/${encodeURIComponent(params.id)}/continuity`, {
  });
}

export async function bankrecMatchSetsList(
  client: ApiClient,
  query: ListMatchSetsQuery,
): Promise<PageBankrecMatchSetDTO> {
  return client.request<PageBankrecMatchSetDTO>('GET', `/api/v1/bankrec/match-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecMatchSetsCreate(
  client: ApiClient,
  body: CreateMatchSetRequest,
  idempotencyKey?: string,
): Promise<BankrecMatchSetDetailDTO> {
  return client.request<BankrecMatchSetDetailDTO>('POST', `/api/v1/bankrec/match-sets`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecMatchSetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecMatchSetDetailDTO> {
  return client.request<BankrecMatchSetDetailDTO>('GET', `/api/v1/bankrec/match-sets/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecMatchSetsRelease(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecMatchSetDTO> {
  return client.request<BankrecMatchSetDTO>('POST', `/api/v1/bankrec/match-sets/${encodeURIComponent(params.id)}/release`, {
  });
}

export async function bankrecStandingRulesList(
  client: ApiClient,
  query: ListStandingRulesQuery,
): Promise<PageBankrecStandingRuleDTO> {
  return client.request<PageBankrecStandingRuleDTO>('GET', `/api/v1/bankrec/standing-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecStandingRulesCreate(
  client: ApiClient,
  body: CreateStandingRuleRequest,
): Promise<BankrecStandingRuleDTO> {
  return client.request<BankrecStandingRuleDTO>('POST', `/api/v1/bankrec/standing-rules`, {
    body,
  });
}

export async function bankrecStandingRulesSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeStandingRuleRequest,
): Promise<BankrecStandingRuleDTO> {
  return client.request<BankrecStandingRuleDTO>('POST', `/api/v1/bankrec/standing-rules/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function bankrecStandingRulesDryRun(
  client: ApiClient,
  params: { id: string },
  query: DryRunStandingRuleQuery,
): Promise<StandingRuleDryRunDTO> {
  return client.request<StandingRuleDryRunDTO>('POST', `/api/v1/bankrec/standing-rules/${encodeURIComponent(params.id)}/dry-run`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecCertificatesCertify(
  client: ApiClient,
  params: { id: string },
  body: CertifySessionRequest,
  idempotencyKey?: string,
): Promise<ReconCertificateDTO> {
  return client.request<ReconCertificateDTO>('POST', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/certify`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecCertificatesDownload(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/certificate`, {
  });
}

export async function bankrecSessionReopensRequest(
  client: ApiClient,
  params: { id: string },
  body: RequestReopenRequest,
): Promise<BankrecSessionReopenDTO> {
  return client.request<BankrecSessionReopenDTO>('POST', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/reopen-requests`, {
    body,
  });
}

export async function bankrecSessionReopensList(
  client: ApiClient,
  query: BankrecSessionReopenListQuery,
): Promise<PageBankrecSessionReopenDTO> {
  return client.request<PageBankrecSessionReopenDTO>('GET', `/api/v1/bankrec/session-reopens`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecSessionReopensAuthorise(
  client: ApiClient,
  params: { id: string },
  body: AuthoriseReopenRequest,
): Promise<BankrecSessionReopenDTO> {
  return client.request<BankrecSessionReopenDTO>('POST', `/api/v1/bankrec/session-reopens/${encodeURIComponent(params.id)}/authorise`, {
    body,
  });
}

export async function bankrecConnectorsList(
  client: ApiClient,
): Promise<BankConnectorRefDTO[]> {
  return client.request<BankConnectorRefDTO[]>('GET', `/api/v1/bankrec/connectors`, {
  });
}

export async function bankrecConnectorsFetch(
  client: ApiClient,
  params: { id: string },
  body: FetchStatementsRequest,
): Promise<FetchStatementsResultDTO> {
  return client.request<FetchStatementsResultDTO>('POST', `/api/v1/bankrec/bank-accounts/${encodeURIComponent(params.id)}/fetch-statements`, {
    body,
  });
}

export async function bankrecConnectionsHealth(
  client: ApiClient,
): Promise<BankrecConnectionHealthDTO> {
  return client.request<BankrecConnectionHealthDTO>('GET', `/api/v1/bank-connections/health`, {
  });
}

export async function bankrecConnectionsList(
  client: ApiClient,
  query: BankrecConnectionListQuery,
): Promise<PageBankrecConnectionDTO> {
  return client.request<PageBankrecConnectionDTO>('GET', `/api/v1/bank-connections`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecConnectionsCreate(
  client: ApiClient,
  body: BankrecConnectionCreateRequest,
  idempotencyKey?: string,
): Promise<BankrecConnectionDTO> {
  return client.request<BankrecConnectionDTO>('POST', `/api/v1/bank-connections`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecConnectionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecConnectionDTO> {
  return client.request<BankrecConnectionDTO>('GET', `/api/v1/bank-connections/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecConnectionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: BankrecConnectionUpdateRequest,
  idempotencyKey?: string,
): Promise<BankrecConnectionDTO> {
  return client.request<BankrecConnectionDTO>('PATCH', `/api/v1/bank-connections/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecConnectionsTest(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<BankrecConnectionTestDTO> {
  return client.request<BankrecConnectionTestDTO>('POST', `/api/v1/bank-connections/${encodeURIComponent(params.id)}/test`, {
    idempotencyKey,
  });
}

export async function bankrecConnectionsFetch(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<BankrecConnectionFetchResult> {
  return client.request<BankrecConnectionFetchResult>('POST', `/api/v1/bank-connections/${encodeURIComponent(params.id)}/fetch`, {
    idempotencyKey,
  });
}

export async function bankrecConnectionsActivate(
  client: ApiClient,
  params: { id: string },
  body: BankrecConnectionTransitionRequest,
  idempotencyKey?: string,
): Promise<BankrecConnectionDTO> {
  return client.request<BankrecConnectionDTO>('POST', `/api/v1/bank-connections/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecConnectionsDisable(
  client: ApiClient,
  params: { id: string },
  body: BankrecConnectionTransitionRequest,
  idempotencyKey?: string,
): Promise<BankrecConnectionDTO> {
  return client.request<BankrecConnectionDTO>('POST', `/api/v1/bank-connections/${encodeURIComponent(params.id)}/disable`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementImportsRelease(
  client: ApiClient,
  params: { id: string },
  body: BankrecStatementReleaseRequest,
  idempotencyKey?: string,
): Promise<BankrecImportDTO> {
  return client.request<BankrecImportDTO>('POST', `/api/v1/bankrec/statement-imports/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementImportsReject(
  client: ApiClient,
  params: { id: string },
  body: BankrecStatementRejectRequest,
  idempotencyKey?: string,
): Promise<BankrecImportDTO> {
  return client.request<BankrecImportDTO>('POST', `/api/v1/bankrec/statement-imports/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementLinesRelease(
  client: ApiClient,
  params: { id: string },
  body: BankrecStatementLineDecisionRequest,
  idempotencyKey?: string,
): Promise<BankrecStatementLineDecisionDTO> {
  return client.request<BankrecStatementLineDecisionDTO>('POST', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementLinesReject(
  client: ApiClient,
  params: { id: string },
  body: BankrecStatementLineDecisionRequest,
  idempotencyKey?: string,
): Promise<BankrecStatementLineDecisionDTO> {
  return client.request<BankrecStatementLineDecisionDTO>('POST', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementGapsList(
  client: ApiClient,
  query: BankrecStatementGapListQuery,
): Promise<PageBankrecStatementGapDTO> {
  return client.request<PageBankrecStatementGapDTO>('GET', `/api/v1/bankrec/statements/gaps`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecStatementGapsResolve(
  client: ApiClient,
  params: { id: string },
  body: BankrecStatementGapResolveRequest,
  idempotencyKey?: string,
): Promise<BankrecStatementGapDTO> {
  return client.request<BankrecStatementGapDTO>('POST', `/api/v1/bankrec/statement-gaps/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementGapsWaive(
  client: ApiClient,
  params: { id: string },
  body: BankrecStatementGapWaiveRequest,
  idempotencyKey?: string,
): Promise<BankrecStatementGapDTO> {
  return client.request<BankrecStatementGapDTO>('POST', `/api/v1/bankrec/statement-gaps/${encodeURIComponent(params.id)}/waive`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementsStaleness(
  client: ApiClient,
  query: BankrecStatementStalenessQuery,
): Promise<BankrecStatementStalenessReportDTO> {
  return client.request<BankrecStatementStalenessReportDTO>('GET', `/api/v1/bankrec/statements/staleness`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecSettingsGet(
  client: ApiClient,
  query: BankrecSettingsQuery,
): Promise<BankrecSettingsDTO> {
  return client.request<BankrecSettingsDTO>('GET', `/api/v1/bankrec/settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecSettingsUpsert(
  client: ApiClient,
  body: BankrecSettingsUpsertRequest,
  idempotencyKey?: string,
): Promise<BankrecSettingsDTO> {
  return client.request<BankrecSettingsDTO>('PUT', `/api/v1/bankrec/settings`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStatementLinesAutoMatch(
  client: ApiClient,
  params: { id: string },
  body: BankrecAutoMatchRequest,
  idempotencyKey?: string,
): Promise<BankrecAutoMatchResultDTO> {
  return client.request<BankrecAutoMatchResultDTO>('POST', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/auto-match`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecSessionsAutoMatch(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<BankrecSessionAutoMatchSummaryDTO> {
  return client.request<BankrecSessionAutoMatchSummaryDTO>('POST', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/auto-match`, {
    idempotencyKey,
  });
}

export async function bankrecStatementLinesForceMatch(
  client: ApiClient,
  params: { id: string },
  body: BankrecForceMatchRequest,
  idempotencyKey?: string,
): Promise<BankrecForceMatchDTO> {
  return client.request<BankrecForceMatchDTO>('POST', `/api/v1/bankrec/statement-lines/${encodeURIComponent(params.id)}/force-match`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecForceMatchesList(
  client: ApiClient,
  query: BankrecForceMatchListQuery,
): Promise<PageBankrecForceMatchDTO> {
  return client.request<PageBankrecForceMatchDTO>('GET', `/api/v1/bankrec/force-matches`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecForceMatchesAuthorise(
  client: ApiClient,
  params: { id: string },
  body: BankrecForceMatchAuthoriseRequest,
  idempotencyKey?: string,
): Promise<BankrecForceMatchDTO> {
  return client.request<BankrecForceMatchDTO>('POST', `/api/v1/bankrec/force-matches/${encodeURIComponent(params.id)}/authorise`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStandingRulesApprove(
  client: ApiClient,
  params: { id: string },
  body: BankrecStandingRuleApproveRequest,
  idempotencyKey?: string,
): Promise<BankrecStandingRuleDTO> {
  return client.request<BankrecStandingRuleDTO>('POST', `/api/v1/bankrec/standing-rules/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecStandingRulesDecline(
  client: ApiClient,
  params: { id: string },
  body: BankrecStandingRuleDeclineRequest,
  idempotencyKey?: string,
): Promise<BankrecStandingRuleDTO> {
  return client.request<BankrecStandingRuleDTO>('POST', `/api/v1/bankrec/standing-rules/${encodeURIComponent(params.id)}/decline`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecReportsMatchTiers(
  client: ApiClient,
  query: BankrecMatchTiersQuery,
): Promise<BankrecMatchTiersReportDTO> {
  return client.request<BankrecMatchTiersReportDTO>('GET', `/api/v1/reports/bankrec-match-tiers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecCertificatesHistory(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecCertificateDTO[]> {
  return client.request<BankrecCertificateDTO[]>('GET', `/api/v1/bankrec/sessions/${encodeURIComponent(params.id)}/certificates`, {
  });
}

export async function bankrecExceptionsList(
  client: ApiClient,
  query: BankrecExceptionListQuery,
): Promise<PageBankrecExceptionDTO> {
  return client.request<PageBankrecExceptionDTO>('GET', `/api/v1/reconciliations/exceptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function bankrecExceptionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankrecExceptionDetailDTO> {
  return client.request<BankrecExceptionDetailDTO>('GET', `/api/v1/reconciliation-exceptions/${encodeURIComponent(params.id)}`, {
  });
}

export async function bankrecExceptionsAssign(
  client: ApiClient,
  params: { id: string },
  body: BankrecExceptionAssignRequest,
  idempotencyKey?: string,
): Promise<BankrecExceptionDetailDTO> {
  return client.request<BankrecExceptionDetailDTO>('POST', `/api/v1/reconciliation-exceptions/${encodeURIComponent(params.id)}/assign`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecExceptionsInvestigate(
  client: ApiClient,
  params: { id: string },
  body: BankrecExceptionInvestigateRequest,
  idempotencyKey?: string,
): Promise<BankrecExceptionDetailDTO> {
  return client.request<BankrecExceptionDetailDTO>('POST', `/api/v1/reconciliation-exceptions/${encodeURIComponent(params.id)}/investigate`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecExceptionsResolve(
  client: ApiClient,
  params: { id: string },
  body: BankrecExceptionResolveRequest,
  idempotencyKey?: string,
): Promise<BankrecExceptionDetailDTO> {
  return client.request<BankrecExceptionDetailDTO>('POST', `/api/v1/reconciliation-exceptions/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function bankrecReportsDashboard(
  client: ApiClient,
  query: BankrecDashboardQuery,
): Promise<BankrecDashboardDTO> {
  return client.request<BankrecDashboardDTO>('GET', `/api/v1/reports/bankrec-dashboard`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasurySettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<TreasurySettingsDTO> {
  return client.request<TreasurySettingsDTO>('GET', `/api/v1/treasury-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function treasurySettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdateTreasurySettingsRequest,
): Promise<TreasurySettingsDTO> {
  return client.request<TreasurySettingsDTO>('PUT', `/api/v1/treasury-settings/${encodeURIComponent(params.companyId)}`, {
    body,
  });
}

export async function treasuryAccountProfilesList(
  client: ApiClient,
  query: ListAccountProfilesQuery,
): Promise<PageTreasuryAccountProfileDTO> {
  return client.request<PageTreasuryAccountProfileDTO>('GET', `/api/v1/treasury-account-profiles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryAccountProfilesGet(
  client: ApiClient,
  params: { bankAccountId: string },
): Promise<TreasuryAccountProfileDTO> {
  return client.request<TreasuryAccountProfileDTO>('GET', `/api/v1/treasury-account-profiles/${encodeURIComponent(params.bankAccountId)}`, {
  });
}

export async function treasuryAccountProfilesUpdate(
  client: ApiClient,
  params: { bankAccountId: string },
  body: UpdateAccountProfileRequest,
): Promise<TreasuryAccountProfileDTO> {
  return client.request<TreasuryAccountProfileDTO>('PUT', `/api/v1/treasury-account-profiles/${encodeURIComponent(params.bankAccountId)}`, {
    body,
  });
}

export async function treasuryBalanceObservationsList(
  client: ApiClient,
  query: ListObservationsQuery,
): Promise<PageBankBalanceObservationDTO> {
  return client.request<PageBankBalanceObservationDTO>('GET', `/api/v1/bank-balance-observations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryBalanceObservationsRecord(
  client: ApiClient,
  body: RecordObservationRequest,
  idempotencyKey?: string,
): Promise<BankBalanceObservationDTO> {
  return client.request<BankBalanceObservationDTO>('POST', `/api/v1/bank-balance-observations`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryCoolingOffList(
  client: ApiClient,
  query: ListCoolingOffQuery,
): Promise<PageCoolingOffPolicyDTO> {
  return client.request<PageCoolingOffPolicyDTO>('GET', `/api/v1/cooling-off-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryCoolingOffUpdate(
  client: ApiClient,
  params: { subject: string },
  body: UpdateCoolingOffRequest,
): Promise<CoolingOffPolicyDTO> {
  return client.request<CoolingOffPolicyDTO>('PUT', `/api/v1/cooling-off-policies/${encodeURIComponent(params.subject)}`, {
    body,
  });
}

export async function treasuryPositionsList(
  client: ApiClient,
  query: ListTreasuryPositionsQuery,
): Promise<PageTreasuryPositionRunDTO> {
  return client.request<PageTreasuryPositionRunDTO>('GET', `/api/v1/treasury-positions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryPositionsGroup(
  client: ApiClient,
  query: TreasuryGroupPositionQuery,
): Promise<TreasuryGroupPositionDTO> {
  return client.request<TreasuryGroupPositionDTO>('GET', `/api/v1/treasury-positions/group`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryPositionsAvailableLiquidity(
  client: ApiClient,
  query: TreasuryLiquidityQuery,
): Promise<TreasuryLiquidityDTO> {
  return client.request<TreasuryLiquidityDTO>('GET', `/api/v1/treasury-positions/available-liquidity`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryPositionsRateToleranceSet(
  client: ApiClient,
  body: TreasuryPositionToleranceRequest,
  idempotencyKey?: string,
): Promise<TreasuryPositionToleranceDTO> {
  return client.request<TreasuryPositionToleranceDTO>('PUT', `/api/v1/treasury-positions/rate-tolerance`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryPositionsCurrent(
  client: ApiClient,
  query: CurrentPositionQuery,
): Promise<TreasuryPositionDetailDTO> {
  return client.request<TreasuryPositionDetailDTO>('GET', `/api/v1/treasury-positions/current`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryPositionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryPositionDetailDTO> {
  return client.request<TreasuryPositionDetailDTO>('GET', `/api/v1/treasury-positions/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryPositionsVersions(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryPositionRunDTO[]> {
  return client.request<TreasuryPositionRunDTO[]>('GET', `/api/v1/treasury-positions/${encodeURIComponent(params.id)}/versions`, {
  });
}

export async function treasuryPositionsDiff(
  client: ApiClient,
  params: { id: string },
  query: PositionDiffQuery,
): Promise<TreasuryPositionDiffDTO> {
  return client.request<TreasuryPositionDiffDTO>('GET', `/api/v1/treasury-positions/${encodeURIComponent(params.id)}/diff`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryPositionsPublish(
  client: ApiClient,
  body: PublishPositionRequest,
  idempotencyKey?: string,
): Promise<TreasuryPositionDetailDTO> {
  return client.request<TreasuryPositionDetailDTO>('POST', `/api/v1/treasury-positions`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryPositionsRepublish(
  client: ApiClient,
  params: { id: string },
  body: RepublishPositionRequest,
  idempotencyKey?: string,
): Promise<TreasuryPositionDetailDTO> {
  return client.request<TreasuryPositionDetailDTO>('POST', `/api/v1/treasury-positions/${encodeURIComponent(params.id)}/republish`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryPositionsVerify(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryPositionVerifyDTO> {
  return client.request<TreasuryPositionVerifyDTO>('POST', `/api/v1/treasury-positions/${encodeURIComponent(params.id)}/verify`, {
  });
}

export async function treasuryPositionsExport(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/treasury-positions/${encodeURIComponent(params.id)}/export`, {
  });
}

export async function treasuryPositionsPreview(
  client: ApiClient,
  query: PositionPreviewQuery,
): Promise<TreasuryPositionDetailDTO> {
  return client.request<TreasuryPositionDetailDTO>('GET', `/api/v1/treasury-position-preview`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryBeneficiariesList(
  client: ApiClient,
  query: ListBeneficiariesQuery,
): Promise<PagePaymentBeneficiaryDTO> {
  return client.request<PagePaymentBeneficiaryDTO>('GET', `/api/v1/payment-beneficiaries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryBeneficiariesRequest(
  client: ApiClient,
  body: CreateBeneficiaryRequest,
  idempotencyKey?: string,
): Promise<BeneficiaryChangeDTO> {
  return client.request<BeneficiaryChangeDTO>('POST', `/api/v1/payment-beneficiaries`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryBeneficiariesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PaymentBeneficiaryDTO> {
  return client.request<PaymentBeneficiaryDTO>('GET', `/api/v1/payment-beneficiaries/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryBeneficiariesChange(
  client: ApiClient,
  params: { id: string },
  body: UpdateBeneficiaryRequest,
): Promise<BeneficiaryChangeDTO> {
  return client.request<BeneficiaryChangeDTO>('PATCH', `/api/v1/payment-beneficiaries/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasuryBeneficiariesReveal(
  client: ApiClient,
  params: { id: string },
  body: RevealBeneficiaryRequest,
): Promise<BeneficiaryRevealDTO> {
  return client.request<BeneficiaryRevealDTO>('POST', `/api/v1/payment-beneficiaries/${encodeURIComponent(params.id)}/reveal`, {
    body,
  });
}

export async function treasuryBeneficiariesBlock(
  client: ApiClient,
  params: { id: string },
  body: BlockBeneficiaryRequest,
): Promise<PaymentBeneficiaryDTO> {
  return client.request<PaymentBeneficiaryDTO>('POST', `/api/v1/payment-beneficiaries/${encodeURIComponent(params.id)}/block`, {
    body,
  });
}

export async function treasuryBeneficiariesVerifications(
  client: ApiClient,
  params: { id: string },
): Promise<BeneficiaryVerificationDTO[]> {
  return client.request<BeneficiaryVerificationDTO[]>('GET', `/api/v1/payment-beneficiaries/${encodeURIComponent(params.id)}/verifications`, {
  });
}

export async function treasuryBeneficiariesStartVerification(
  client: ApiClient,
  params: { id: string },
  body: StartBeneficiaryVerificationRequest,
): Promise<BeneficiaryVerificationDTO> {
  return client.request<BeneficiaryVerificationDTO>('POST', `/api/v1/payment-beneficiaries/${encodeURIComponent(params.id)}/verifications`, {
    body,
  });
}

export async function treasuryBeneficiariesCompleteVerification(
  client: ApiClient,
  params: { id: string },
  body: CompleteBeneficiaryVerificationRequest,
): Promise<BeneficiaryVerificationDTO> {
  return client.request<BeneficiaryVerificationDTO>('POST', `/api/v1/beneficiary-verifications/${encodeURIComponent(params.id)}/complete`, {
    body,
  });
}

export async function treasuryBeneficiaryChangesList(
  client: ApiClient,
  query: ListBeneficiaryChangesQuery,
): Promise<PageBeneficiaryChangeDTO> {
  return client.request<PageBeneficiaryChangeDTO>('GET', `/api/v1/beneficiary-changes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryBeneficiaryChangesDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideBeneficiaryChangeRequest,
): Promise<BeneficiaryChangeDTO> {
  return client.request<BeneficiaryChangeDTO>('POST', `/api/v1/beneficiary-changes/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function treasuryBeneficiaryChangesReleaseEarly(
  client: ApiClient,
  params: { id: string },
  body: ReleaseEarlyRequest,
): Promise<BeneficiaryChangeDTO> {
  return client.request<BeneficiaryChangeDTO>('POST', `/api/v1/beneficiary-changes/${encodeURIComponent(params.id)}/release-early`, {
    body,
  });
}

export async function treasuryMandatesList(
  client: ApiClient,
  query: ListMandatesQuery,
): Promise<PageBankMandateDTO> {
  return client.request<PageBankMandateDTO>('GET', `/api/v1/bank-mandates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryMandatesResolve(
  client: ApiClient,
  query: ResolveMandateQuery,
): Promise<MandateResolutionDTO> {
  return client.request<MandateResolutionDTO>('GET', `/api/v1/bank-mandates/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryMandatesCreate(
  client: ApiClient,
  body: CreateMandateRequest,
  idempotencyKey?: string,
): Promise<BankMandateDTO> {
  return client.request<BankMandateDTO>('POST', `/api/v1/bank-mandates`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryMandatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<BankMandateDTO> {
  return client.request<BankMandateDTO>('GET', `/api/v1/bank-mandates/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryMandatesSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeMandateRequest,
): Promise<BankMandateDTO> {
  return client.request<BankMandateDTO>('POST', `/api/v1/bank-mandates/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function treasuryMandatesApprove(
  client: ApiClient,
  params: { id: string },
  body: TreasuryMandateApproveRequest,
): Promise<BankMandateDTO> {
  return client.request<BankMandateDTO>('POST', `/api/v1/bank-mandates/${encodeURIComponent(params.id)}/approve`, {
    body,
  });
}

export async function treasuryMandatesAddSignatory(
  client: ApiClient,
  params: { id: string },
  body: AddSignatoryRequest,
): Promise<BankMandateSignatoryDTO> {
  return client.request<BankMandateSignatoryDTO>('POST', `/api/v1/bank-mandates/${encodeURIComponent(params.id)}/signatories`, {
    body,
  });
}

export async function treasuryMandatesEndSignatory(
  client: ApiClient,
  params: { id: string },
  body: EndSignatoryRequest,
): Promise<BankMandateSignatoryDTO> {
  return client.request<BankMandateSignatoryDTO>('DELETE', `/api/v1/bank-mandate-signatories/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasuryWalletsList(
  client: ApiClient,
  query: ListWalletsQuery,
): Promise<PageTreasuryWalletDTO> {
  return client.request<PageTreasuryWalletDTO>('GET', `/api/v1/treasury-wallets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletsCreate(
  client: ApiClient,
  body: CreateWalletRequest,
  idempotencyKey?: string,
): Promise<TreasuryWalletDTO> {
  return client.request<TreasuryWalletDTO>('POST', `/api/v1/treasury-wallets`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryWalletsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryWalletDetailDTO> {
  return client.request<TreasuryWalletDetailDTO>('GET', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryWalletsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWalletRequest,
): Promise<TreasuryWalletDTO> {
  return client.request<TreasuryWalletDTO>('PATCH', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasuryWalletsClose(
  client: ApiClient,
  params: { id: string },
  body: CloseWalletRequest,
): Promise<TreasuryWalletDTO> {
  return client.request<TreasuryWalletDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function treasuryWalletsRecover(
  client: ApiClient,
  params: { id: string },
  body: TreasuryWalletRecoverRequest,
  idempotencyKey?: string,
): Promise<TreasuryWalletRecoveryDTO> {
  return client.request<TreasuryWalletRecoveryDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/recover`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryWalletsReconcile(
  client: ApiClient,
  params: { id: string },
  body: TreasuryWalletReconcileRequest,
  idempotencyKey?: string,
): Promise<TreasuryWalletReconciliationDTO> {
  return client.request<TreasuryWalletReconciliationDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/reconcile`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryWalletReconciliationsList(
  client: ApiClient,
  params: { id: string },
  query: TreasuryWalletReconciliationListQuery,
): Promise<PageTreasuryWalletReconciliationDTO> {
  return client.request<PageTreasuryWalletReconciliationDTO>('GET', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/reconciliations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletsRevealCustodian(
  client: ApiClient,
  params: { id: string },
  body: TreasuryWalletRevealRequest,
): Promise<TreasuryWalletRevealDTO> {
  return client.request<TreasuryWalletRevealDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/reveal-custodian`, {
    body,
  });
}

export async function treasuryWalletsTransactions(
  client: ApiClient,
  params: { id: string },
  query: ListWalletTxnsQuery,
): Promise<PageWalletTransactionDTO> {
  return client.request<PageWalletTransactionDTO>('GET', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/transactions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletsCapture(
  client: ApiClient,
  params: { id: string },
  body: WalletCaptureRequest,
  idempotencyKey?: string,
): Promise<WalletCaptureResultDTO> {
  return client.request<WalletCaptureResultDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/captures`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryWalletsSubmitCapture(
  client: ApiClient,
  params: { id: string },
): Promise<WalletTransactionDTO> {
  return client.request<WalletTransactionDTO>('POST', `/api/v1/wallet-transactions/${encodeURIComponent(params.id)}/submit`, {
  });
}

export async function treasuryWalletsDecideCapture(
  client: ApiClient,
  params: { id: string },
  body: DecideCaptureRequest,
): Promise<WalletTransactionDTO> {
  return client.request<WalletTransactionDTO>('POST', `/api/v1/wallet-transactions/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function treasuryWalletsReverseTxn(
  client: ApiClient,
  params: { id: string },
  body: ReverseTxnRequest,
): Promise<WalletTransactionDTO> {
  return client.request<WalletTransactionDTO>('POST', `/api/v1/wallet-transactions/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function treasuryWalletPoliciesList(
  client: ApiClient,
  query: ListCapturePoliciesQuery,
): Promise<PageWalletCapturePolicyDTO> {
  return client.request<PageWalletCapturePolicyDTO>('GET', `/api/v1/wallet-capture-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletPoliciesCreate(
  client: ApiClient,
  body: CreateCapturePolicyRequest,
): Promise<WalletCapturePolicyDTO> {
  return client.request<WalletCapturePolicyDTO>('POST', `/api/v1/wallet-capture-policies`, {
    body,
  });
}

export async function treasuryWalletPoliciesSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeCapturePolicyRequest,
): Promise<WalletCapturePolicyDTO> {
  return client.request<WalletCapturePolicyDTO>('POST', `/api/v1/wallet-capture-policies/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function treasuryWalletCountsOpen(
  client: ApiClient,
  params: { id: string },
  body: OpenWalletCountRequest,
): Promise<WalletCountDTO> {
  return client.request<WalletCountDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/counts`, {
    body,
  });
}

export async function treasuryWalletCountsSheet(
  client: ApiClient,
  params: { id: string },
): Promise<WalletCountSheetDTO> {
  return client.request<WalletCountSheetDTO>('GET', `/api/v1/wallet-counts/${encodeURIComponent(params.id)}/sheet`, {
  });
}

export async function treasuryWalletCountsRecord(
  client: ApiClient,
  params: { id: string },
  body: RecordCountLinesRequest,
): Promise<WalletCountDTO> {
  return client.request<WalletCountDTO>('PUT', `/api/v1/wallet-counts/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function treasuryWalletCountsPost(
  client: ApiClient,
  params: { id: string },
  body: PostWalletCountRequest,
): Promise<WalletCountDTO> {
  return client.request<WalletCountDTO>('POST', `/api/v1/wallet-counts/${encodeURIComponent(params.id)}/post`, {
    body,
  });
}

export async function treasuryWalletCountsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelCountRequest,
): Promise<WalletCountDTO> {
  return client.request<WalletCountDTO>('POST', `/api/v1/wallet-counts/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function treasuryWalletCountsList(
  client: ApiClient,
  query: ListWalletCountsQuery,
): Promise<PageWalletCountDTO> {
  return client.request<PageWalletCountDTO>('GET', `/api/v1/wallet-counts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletCustodyOpen(
  client: ApiClient,
  params: { id: string },
  body: OpenCustodyTransferRequest,
): Promise<WalletCustodyTransferDTO> {
  return client.request<WalletCustodyTransferDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/custody-transfers`, {
    body,
  });
}

export async function treasuryWalletsCustodyTransfers(
  client: ApiClient,
  params: { id: string },
): Promise<WalletCustodyTransferListDTO> {
  return client.request<WalletCustodyTransferListDTO>('GET', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/custody-transfers`, {
  });
}

export async function treasuryWalletHandoversAcknowledge(
  client: ApiClient,
  params: { id: string },
  body: TreasuryWalletHandoverAcknowledgeRequest,
): Promise<WalletCustodyTransferDTO> {
  return client.request<WalletCustodyTransferDTO>('POST', `/api/v1/wallet-handovers/${encodeURIComponent(params.id)}/acknowledge`, {
    body,
  });
}

export async function treasuryWalletReplenishmentsRequest(
  client: ApiClient,
  params: { id: string },
  body: RequestReplenishmentRequest,
): Promise<WalletReplenishmentDTO> {
  return client.request<WalletReplenishmentDTO>('POST', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/replenishments`, {
    body,
  });
}

export async function treasuryWalletReplenishmentsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideReplenishmentRequest,
): Promise<WalletReplenishmentDTO> {
  return client.request<WalletReplenishmentDTO>('POST', `/api/v1/wallet-replenishments/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function treasuryWalletReplenishmentsList(
  client: ApiClient,
  query: TreasuryWalletReplenishmentListQuery,
): Promise<PageWalletReplenishmentDTO> {
  return client.request<PageWalletReplenishmentDTO>('GET', `/api/v1/wallet-replenishments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletFundingGet(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryWalletFundingDTO> {
  return client.request<TreasuryWalletFundingDTO>('GET', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/funding`, {
  });
}

export async function treasuryWalletFundingSetTerms(
  client: ApiClient,
  params: { id: string },
  body: TreasuryWalletFundingTermsRequest,
): Promise<TreasuryWalletFundingDTO> {
  return client.request<TreasuryWalletFundingDTO>('PUT', `/api/v1/treasury-wallets/${encodeURIComponent(params.id)}/funding-terms`, {
    body,
  });
}

export async function treasuryWalletsBalanceReport(
  client: ApiClient,
  query: WalletBalanceReportQuery,
): Promise<WalletBalanceReportDTO> {
  return client.request<WalletBalanceReportDTO>('GET', `/api/v1/reports/wallet-balances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtFacilitiesList(
  client: ApiClient,
  query: ListFacilitiesQuery,
): Promise<PageDebtFacilityDTO> {
  return client.request<PageDebtFacilityDTO>('GET', `/api/v1/debt-facilities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtFacilitiesCreate(
  client: ApiClient,
  body: CreateFacilityRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesGet(
  client: ApiClient,
  params: { id: string },
): Promise<DebtFacilityDetailDTO> {
  return client.request<DebtFacilityDetailDTO>('GET', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryDebtFacilitiesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateFacilityRequest,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('PATCH', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasuryDebtFacilitiesSubmit(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtFacilitySubmitRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesActivate(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtFacilityActivateRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesReject(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtFacilityRejectRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesRequestLimitIncrease(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtLimitChangeRequest,
  idempotencyKey?: string,
): Promise<TreasuryDebtChangeRequestDTO> {
  return client.request<TreasuryDebtChangeRequestDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/limit-changes`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesRollover(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtFacilityRolloverRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/rollover`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesClose(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtFacilityCloseRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesWithdrawClose(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtFacilityCloseRequest,
  idempotencyKey?: string,
): Promise<DebtFacilityDTO> {
  return client.request<DebtFacilityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/close-request/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesPledgeSecurity(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtSecurityRequest,
  idempotencyKey?: string,
): Promise<TreasuryDebtSecurityDTO> {
  return client.request<TreasuryDebtSecurityDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/securities`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtSecuritiesRelease(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtSecurityReleaseRequest,
  idempotencyKey?: string,
): Promise<TreasuryDebtSecurityDTO> {
  return client.request<TreasuryDebtSecurityDTO>('POST', `/api/v1/debt-facility-securities/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtChangesList(
  client: ApiClient,
  query: TreasuryDebtChangeListQuery,
): Promise<PageTreasuryDebtChangeRequestDTO> {
  return client.request<PageTreasuryDebtChangeRequestDTO>('GET', `/api/v1/debt-change-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtChangesApprove(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtChangeDecisionRequest,
  idempotencyKey?: string,
): Promise<TreasuryDebtChangeRequestDTO> {
  return client.request<TreasuryDebtChangeRequestDTO>('POST', `/api/v1/debt-change-requests/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtChangesReject(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtChangeDecisionRequest,
  idempotencyKey?: string,
): Promise<TreasuryDebtChangeRequestDTO> {
  return client.request<TreasuryDebtChangeRequestDTO>('POST', `/api/v1/debt-change-requests/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtFacilitiesGenerateSchedule(
  client: ApiClient,
  params: { id: string },
  body: GenerateScheduleRequest,
): Promise<DebtScheduleDTO> {
  return client.request<DebtScheduleDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/schedule`, {
    body,
  });
}

export async function treasuryDebtFacilitiesSchedule(
  client: ApiClient,
  params: { id: string },
  query: ScheduleQuery,
): Promise<DebtScheduleDTO> {
  return client.request<DebtScheduleDTO>('GET', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/schedule`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDrawdownsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateDrawdownRequest,
  idempotencyKey?: string,
): Promise<DebtDrawdownDTO> {
  return client.request<DebtDrawdownDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/drawdowns`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDrawdownsConfirm(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtDrawdownConfirmRequest,
  idempotencyKey?: string,
): Promise<DebtDrawdownDTO> {
  return client.request<DebtDrawdownDTO>('POST', `/api/v1/debt-drawdowns/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDrawdownsApprove(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtMovementDecisionRequest,
  idempotencyKey?: string,
): Promise<DebtDrawdownDTO> {
  return client.request<DebtDrawdownDTO>('POST', `/api/v1/debt-drawdowns/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDrawdownsReject(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtMovementDecisionRequest,
  idempotencyKey?: string,
): Promise<DebtDrawdownDTO> {
  return client.request<DebtDrawdownDTO>('POST', `/api/v1/debt-drawdowns/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryRepaymentsApprove(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtMovementDecisionRequest,
  idempotencyKey?: string,
): Promise<DebtRepaymentDTO> {
  return client.request<DebtRepaymentDTO>('POST', `/api/v1/debt-repayments/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryRepaymentsReject(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtMovementDecisionRequest,
  idempotencyKey?: string,
): Promise<DebtRepaymentDTO> {
  return client.request<DebtRepaymentDTO>('POST', `/api/v1/debt-repayments/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDrawdownsWithdraw(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtMovementDecisionRequest,
  idempotencyKey?: string,
): Promise<DebtDrawdownDTO> {
  return client.request<DebtDrawdownDTO>('POST', `/api/v1/debt-drawdowns/${encodeURIComponent(params.id)}/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryRepaymentsWithdraw(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtMovementDecisionRequest,
  idempotencyKey?: string,
): Promise<DebtRepaymentDTO> {
  return client.request<DebtRepaymentDTO>('POST', `/api/v1/debt-repayments/${encodeURIComponent(params.id)}/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDrawdownsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseDrawdownRequest,
): Promise<DebtDrawdownDTO> {
  return client.request<DebtDrawdownDTO>('POST', `/api/v1/debt-drawdowns/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function treasuryRepaymentsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateRepaymentRequest,
  idempotencyKey?: string,
): Promise<DebtRepaymentDTO> {
  return client.request<DebtRepaymentDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/repayments`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryRepaymentsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseRepaymentRequest,
): Promise<DebtRepaymentDTO> {
  return client.request<DebtRepaymentDTO>('POST', `/api/v1/debt-repayments/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function treasuryDebtAccrualsRun(
  client: ApiClient,
  body: RunAccrualRequest,
  idempotencyKey?: string,
): Promise<DebtAccrualResult> {
  return client.request<DebtAccrualResult>('POST', `/api/v1/debt-accrual-runs`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryDebtAccrualsList(
  client: ApiClient,
  query: ListAccrualRunsQuery,
): Promise<PageDebtAccrualRunDTO> {
  return client.request<PageDebtAccrualRunDTO>('GET', `/api/v1/debt-accrual-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtAccrualsGet(
  client: ApiClient,
  params: { id: string },
): Promise<DebtAccrualRunDetailDTO> {
  return client.request<DebtAccrualRunDetailDTO>('GET', `/api/v1/debt-accrual-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryCovenantsList(
  client: ApiClient,
  params: { id: string },
): Promise<DebtCovenantDTO[]> {
  return client.request<DebtCovenantDTO[]>('GET', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/covenants`, {
  });
}

export async function treasuryCovenantsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateCovenantRequest,
  idempotencyKey?: string,
): Promise<DebtCovenantDTO> {
  return client.request<DebtCovenantDTO>('POST', `/api/v1/debt-facilities/${encodeURIComponent(params.id)}/covenants`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryCovenantsAmend(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtCovenantAmendRequest,
): Promise<DebtCovenantDTO> {
  return client.request<DebtCovenantDTO>('PATCH', `/api/v1/debt-covenants/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasuryCovenantsTest(
  client: ApiClient,
  params: { id: string },
  body: TreasuryDebtCovenantTestRequest,
  idempotencyKey?: string,
): Promise<DebtCovenantTestDTO> {
  return client.request<DebtCovenantTestDTO>('POST', `/api/v1/debt-covenants/${encodeURIComponent(params.id)}/test`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryCovenantsWaive(
  client: ApiClient,
  params: { id: string },
  body: WaiveCovenantRequest,
): Promise<DebtCovenantTestDTO> {
  return client.request<DebtCovenantTestDTO>('POST', `/api/v1/debt-covenant-tests/${encodeURIComponent(params.id)}/waive`, {
    body,
  });
}

export async function treasuryDebtMaturityReport(
  client: ApiClient,
  query: DebtMaturityQuery,
): Promise<DebtMaturityReportDTO> {
  return client.request<DebtMaturityReportDTO>('GET', `/api/v1/reports/debt-maturity`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtInterestExpenseReport(
  client: ApiClient,
  query: TreasuryDebtInterestExpenseQuery,
): Promise<TreasuryDebtInterestExpenseReportDTO> {
  return client.request<TreasuryDebtInterestExpenseReportDTO>('GET', `/api/v1/reports/debt-interest-expense`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtRatesList(
  client: ApiClient,
  query: ListRateObservationsQuery,
): Promise<PageDebtRateObservationDTO> {
  return client.request<PageDebtRateObservationDTO>('GET', `/api/v1/debt-rate-observations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryDebtRatesRecord(
  client: ApiClient,
  body: RecordRateObservationRequest,
): Promise<DebtRateObservationDTO> {
  return client.request<DebtRateObservationDTO>('POST', `/api/v1/debt-rate-observations`, {
    body,
  });
}

export async function treasuryForecastsList(
  client: ApiClient,
  query: ListForecastRunsQuery,
): Promise<PageCashForecastRunDTO> {
  return client.request<PageCashForecastRunDTO>('GET', `/api/v1/cash-forecast-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryForecastsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CashForecastRunDetailDTO> {
  return client.request<CashForecastRunDetailDTO>('GET', `/api/v1/cash-forecast-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryForecastsPublish(
  client: ApiClient,
  body: PublishForecastRequest,
  idempotencyKey?: string,
): Promise<CashForecastRunDetailDTO> {
  return client.request<CashForecastRunDetailDTO>('POST', `/api/v1/cash-forecast-runs`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryForecastsRevise(
  client: ApiClient,
  params: { id: string },
  body: ReviseForecastRequest,
): Promise<CashForecastRunDetailDTO> {
  return client.request<CashForecastRunDetailDTO>('POST', `/api/v1/cash-forecast-runs/${encodeURIComponent(params.id)}/revise`, {
    body,
  });
}

export async function treasuryForecastsBucketItems(
  client: ApiClient,
  params: { id: string; bucketId: string },
  query: BucketItemsQuery,
): Promise<PageCashForecastItemDTO> {
  return client.request<PageCashForecastItemDTO>('GET', `/api/v1/cash-forecast-runs/${encodeURIComponent(params.id)}/buckets/${encodeURIComponent(params.bucketId)}/items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryForecastsSeries(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryForecastSeriesDTO> {
  return client.request<TreasuryForecastSeriesDTO>('GET', `/api/v1/cash-forecast-runs/${encodeURIComponent(params.id)}/series`, {
  });
}

export async function treasuryForecastsOverrides(
  client: ApiClient,
  params: { id: string },
): Promise<TreasuryForecastRunOverridesDTO> {
  return client.request<TreasuryForecastRunOverridesDTO>('GET', `/api/v1/cash-forecast-runs/${encodeURIComponent(params.id)}/overrides`, {
  });
}

export async function treasuryForecastsOverrideItem(
  client: ApiClient,
  params: { id: string },
  body: TreasuryForecastOverrideRequest,
  idempotencyKey?: string,
): Promise<TreasuryForecastOverrideDTO> {
  return client.request<TreasuryForecastOverrideDTO>('POST', `/api/v1/cash-forecast-items/${encodeURIComponent(params.id)}/override`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryScenariosList(
  client: ApiClient,
  query: ListScenariosQuery,
): Promise<PageCashForecastScenarioDTO> {
  return client.request<PageCashForecastScenarioDTO>('GET', `/api/v1/cash-forecast-scenarios`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryScenariosCreate(
  client: ApiClient,
  body: CreateScenarioRequest,
): Promise<CashForecastScenarioDTO> {
  return client.request<CashForecastScenarioDTO>('POST', `/api/v1/cash-forecast-scenarios`, {
    body,
  });
}

export async function treasuryScenariosUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateScenarioRequest,
): Promise<CashForecastScenarioDTO> {
  return client.request<CashForecastScenarioDTO>('PATCH', `/api/v1/cash-forecast-scenarios/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasuryScenariosCompare(
  client: ApiClient,
  query: TreasuryScenarioCompareQuery,
): Promise<TreasuryScenarioComparisonDTO> {
  return client.request<TreasuryScenarioComparisonDTO>('GET', `/api/v1/cash-forecast-scenarios/compare`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryScenariosPromote(
  client: ApiClient,
  params: { id: string },
  body: TreasuryScenarioPromoteRequest,
  idempotencyKey?: string,
): Promise<TreasuryScenarioPromotionDTO> {
  return client.request<TreasuryScenarioPromotionDTO>('POST', `/api/v1/cash-forecast-scenarios/${encodeURIComponent(params.id)}/promote`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryScenariosPromotions(
  client: ApiClient,
  query: ListScenarioPromotionsQuery,
): Promise<PageTreasuryScenarioPromotionDTO> {
  return client.request<PageTreasuryScenarioPromotionDTO>('GET', `/api/v1/cash-forecast-scenario-promotions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryScenariosWithdrawPromotion(
  client: ApiClient,
  params: { id: string },
  body: TreasuryScenarioPromotionWithdrawRequest,
  idempotencyKey?: string,
): Promise<TreasuryScenarioPromotionDTO> {
  return client.request<TreasuryScenarioPromotionDTO>('POST', `/api/v1/cash-forecast-scenario-promotions/${encodeURIComponent(params.id)}/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryMinimumBalancesList(
  client: ApiClient,
  query: TreasuryMinimumBalanceListQuery,
): Promise<TreasuryMinimumBalanceListDTO> {
  return client.request<TreasuryMinimumBalanceListDTO>('GET', `/api/v1/treasury-minimum-balances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryMinimumBalancesChange(
  client: ApiClient,
  body: TreasuryMinimumBalanceChangeRequest,
  idempotencyKey?: string,
): Promise<TreasuryMinimumBalanceChangeDTO> {
  return client.request<TreasuryMinimumBalanceChangeDTO>('POST', `/api/v1/treasury-minimum-balances`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryMinimumBalancesChanges(
  client: ApiClient,
  query: ListMinimumBalanceChangesQuery,
): Promise<PageTreasuryMinimumBalanceChangeDTO> {
  return client.request<PageTreasuryMinimumBalanceChangeDTO>('GET', `/api/v1/treasury-minimum-balance-changes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryMinimumBalancesWithdrawChange(
  client: ApiClient,
  params: { id: string },
  body: TreasuryMinimumBalanceWithdrawRequest,
  idempotencyKey?: string,
): Promise<TreasuryMinimumBalanceChangeDTO> {
  return client.request<TreasuryMinimumBalanceChangeDTO>('POST', `/api/v1/treasury-minimum-balance-changes/${encodeURIComponent(params.id)}/withdraw`, {
    body,
    idempotencyKey,
  });
}

export async function treasuryShortfallsList(
  client: ApiClient,
  query: TreasuryShortfallsQuery,
): Promise<TreasuryShortfallsDTO> {
  return client.request<TreasuryShortfallsDTO>('GET', `/api/v1/treasury/shortfalls`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryAccuracyReport(
  client: ApiClient,
  query: ForecastAccuracyQuery,
): Promise<CashForecastAccuracyReportDTO> {
  return client.request<CashForecastAccuracyReportDTO>('GET', `/api/v1/reports/forecast-accuracy`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryForecastsLiquidityGap(
  client: ApiClient,
  query: LiquidityGapQuery,
): Promise<LiquidityGapReportDTO> {
  return client.request<LiquidityGapReportDTO>('GET', `/api/v1/reports/liquidity-gap`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasurySweepsList(
  client: ApiClient,
  query: TreasurySweepListQuery,
): Promise<PageTreasurySweepStructureDTO> {
  return client.request<PageTreasurySweepStructureDTO>('GET', `/api/v1/treasury-sweeps`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasurySweepsCreate(
  client: ApiClient,
  body: TreasurySweepCreateRequest,
  idempotencyKey?: string,
): Promise<TreasurySweepStructureDTO> {
  return client.request<TreasurySweepStructureDTO>('POST', `/api/v1/treasury-sweeps`, {
    body,
    idempotencyKey,
  });
}

export async function treasurySweepsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TreasurySweepStructureDTO> {
  return client.request<TreasurySweepStructureDTO>('GET', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasurySweepsUpdate(
  client: ApiClient,
  params: { id: string },
  body: TreasurySweepUpdateRequest,
): Promise<TreasurySweepStructureDTO> {
  return client.request<TreasurySweepStructureDTO>('PATCH', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function treasurySweepsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<TreasurySweepStructureDTO> {
  return client.request<TreasurySweepStructureDTO>('DELETE', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasurySweepsActivate(
  client: ApiClient,
  params: { id: string },
  body: TreasurySweepActivateRequest,
  idempotencyKey?: string,
): Promise<TreasurySweepStructureDTO> {
  return client.request<TreasurySweepStructureDTO>('POST', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function treasurySweepsSuspend(
  client: ApiClient,
  params: { id: string },
  body: TreasurySweepSuspendRequest,
  idempotencyKey?: string,
): Promise<TreasurySweepStructureDTO> {
  return client.request<TreasurySweepStructureDTO>('POST', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}/suspend`, {
    body,
    idempotencyKey,
  });
}

export async function treasurySweepsSimulate(
  client: ApiClient,
  params: { id: string },
  body: TreasurySweepSimulateRequest,
  idempotencyKey?: string,
): Promise<TreasurySweepSimulationDTO> {
  return client.request<TreasurySweepSimulationDTO>('POST', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}/simulate`, {
    body,
    idempotencyKey,
  });
}

export async function treasurySweepsExecute(
  client: ApiClient,
  params: { id: string },
  body: TreasurySweepExecuteRequest,
  idempotencyKey?: string,
): Promise<TreasurySweepExecutionDTO> {
  return client.request<TreasurySweepExecutionDTO>('POST', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}/execute`, {
    body,
    idempotencyKey,
  });
}

export async function treasurySweepsSimulations(
  client: ApiClient,
  params: { id: string },
  query: TreasurySweepSimulationListQuery,
): Promise<PageTreasurySweepSimulationDTO> {
  return client.request<PageTreasurySweepSimulationDTO>('GET', `/api/v1/treasury-sweeps/${encodeURIComponent(params.id)}/simulations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasurySweepSimulationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TreasurySweepSimulationDTO> {
  return client.request<TreasurySweepSimulationDTO>('GET', `/api/v1/treasury-sweep-simulations/${encodeURIComponent(params.id)}`, {
  });
}

export async function treasuryWalletAnalyticsFloatUtilisation(
  client: ApiClient,
  query: TreasuryWalletFloatUtilisationQuery,
): Promise<TreasuryWalletFloatUtilisationReportDTO> {
  return client.request<TreasuryWalletFloatUtilisationReportDTO>('GET', `/api/v1/reports/wallet-analytics/float-utilisation`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletAnalyticsRecordedNotApprovedAgeing(
  client: ApiClient,
  query: TreasuryWalletAgeingQuery,
): Promise<TreasuryWalletAgeingReportDTO> {
  return client.request<TreasuryWalletAgeingReportDTO>('GET', `/api/v1/reports/wallet-analytics/recorded-not-approved-ageing`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletAnalyticsCountVarianceHistory(
  client: ApiClient,
  query: TreasuryWalletCountVarianceQuery,
): Promise<TreasuryWalletCountVarianceReportDTO> {
  return client.request<TreasuryWalletCountVarianceReportDTO>('GET', `/api/v1/reports/wallet-analytics/count-variance-history`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletAnalyticsIdleBalances(
  client: ApiClient,
  query: TreasuryWalletIdleBalancesQuery,
): Promise<TreasuryWalletIdleBalancesReportDTO> {
  return client.request<TreasuryWalletIdleBalancesReportDTO>('GET', `/api/v1/reports/wallet-analytics/idle-balances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletAnalyticsExpenditureByCategory(
  client: ApiClient,
  query: TreasuryWalletExpenditureQuery,
): Promise<TreasuryWalletExpenditureReportDTO> {
  return client.request<TreasuryWalletExpenditureReportDTO>('GET', `/api/v1/reports/wallet-analytics/expenditure-by-category`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletAnalyticsLoadsInTransit(
  client: ApiClient,
  query: TreasuryWalletLoadsInTransitQuery,
): Promise<TreasuryWalletLoadsInTransitReportDTO> {
  return client.request<TreasuryWalletLoadsInTransitReportDTO>('GET', `/api/v1/reports/wallet-analytics/loads-in-transit`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryReportsIdleCash(
  client: ApiClient,
  query: TreasuryIdleCashQuery,
): Promise<TreasuryIdleCashReportDTO> {
  return client.request<TreasuryIdleCashReportDTO>('GET', `/api/v1/reports/idle-cash`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryReportsKpis(
  client: ApiClient,
  query: TreasuryKPIQuery,
): Promise<TreasuryKPIReportDTO> {
  return client.request<TreasuryKPIReportDTO>('GET', `/api/v1/reports/treasury-kpis`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function treasuryWalletSplitFlagsList(
  client: ApiClient,
  query: TreasuryWalletSplitFlagsQuery,
): Promise<PageTreasuryWalletSplitFlagDTO> {
  return client.request<PageTreasuryWalletSplitFlagDTO>('GET', `/api/v1/treasury-wallet-split-flags`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function attachmentsUpload(
  client: ApiClient,
  file: File | Blob,
  fields: { module: string; entity: string; entityId: number; note?: string },
  idempotencyKey?: string,
): Promise<AttachmentDTO> {
  const form = new FormData();
  form.append('file', file);
  form.append('module', String(fields.module));
  form.append('entity', String(fields.entity));
  form.append('entityId', String(fields.entityId));
  if (fields.note !== undefined) {
    form.append('note', String(fields.note));
  }
  return client.request<AttachmentDTO>('POST', `/api/v1/attachments`, {
    body: form,
    idempotencyKey,
  });
}

export async function attachmentsList(
  client: ApiClient,
  query: ListQuery,
): Promise<PageAttachmentDTO> {
  return client.request<PageAttachmentDTO>('GET', `/api/v1/attachments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function attachmentsDownload(
  client: ApiClient,
  params: { id: string },
): Promise<DownloadDTO> {
  return client.request<DownloadDTO>('GET', `/api/v1/attachments/${encodeURIComponent(params.id)}/download`, {
  });
}

export async function attachmentsSetPortalVisibility(
  client: ApiClient,
  params: { id: string },
  body: SetPortalVisibilityRequest,
): Promise<AttachmentDTO> {
  return client.request<AttachmentDTO>('PATCH', `/api/v1/attachments/${encodeURIComponent(params.id)}/portal-visibility`, {
    body,
  });
}

export async function attachmentsDelete(
  client: ApiClient,
  params: { id: string },
  query: AttachmentsDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/attachments/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetVersionsListAll(
  client: ApiClient,
  query: BudgetCycleVersionsQuery,
): Promise<PageBudgetCycleVersionDTO> {
  return client.request<PageBudgetCycleVersionDTO>('GET', `/api/v1/budget-versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetVersionsCompare(
  client: ApiClient,
  query: BudgetVersionCompareQuery,
): Promise<BudgetVersionCompareDTO> {
  return client.request<BudgetVersionCompareDTO>('GET', `/api/v1/budget-versions/compare`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetAvailabilityGet(
  client: ApiClient,
  query: BudgetAvailabilityQuery,
): Promise<BudgetAvailabilityDTO> {
  return client.request<BudgetAvailabilityDTO>('GET', `/api/v1/budget-availability`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetCyclesOpen(
  client: ApiClient,
  params: { id: string },
  body: BudgetCycleTransitionRequest,
  idempotencyKey?: string,
): Promise<BudgetModelDTO> {
  return client.request<BudgetModelDTO>('POST', `/api/v1/budget-models/${encodeURIComponent(params.id)}/open`, {
    body,
    idempotencyKey,
  });
}

export async function budgetCyclesClose(
  client: ApiClient,
  params: { id: string },
  body: BudgetCycleTransitionRequest,
): Promise<BudgetModelDTO> {
  return client.request<BudgetModelDTO>('POST', `/api/v1/budget-models/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function budgetCyclesSubmissionStatus(
  client: ApiClient,
  params: { id: string },
): Promise<BudgetCycleSubmissionDTO[]> {
  return client.request<BudgetCycleSubmissionDTO[]>('GET', `/api/v1/budget-models/${encodeURIComponent(params.id)}/submission-status`, {
  });
}

export async function budgetCyclesOwnersAssign(
  client: ApiClient,
  params: { id: string },
  body: BudgetCycleOwnerAssignRequest,
  idempotencyKey?: string,
): Promise<BudgetCycleSubmissionDTO> {
  return client.request<BudgetCycleSubmissionDTO>('POST', `/api/v1/budget-models/${encodeURIComponent(params.id)}/owners`, {
    body,
    idempotencyKey,
  });
}

export async function budgetCyclesOwnersReassign(
  client: ApiClient,
  params: { ownerId: string },
  body: BudgetCycleOwnerReassignRequest,
): Promise<BudgetCycleSubmissionDTO> {
  return client.request<BudgetCycleSubmissionDTO>('PATCH', `/api/v1/budget-cycle-owners/${encodeURIComponent(params.ownerId)}`, {
    body,
  });
}

export async function budgetCyclesOwnersRemove(
  client: ApiClient,
  params: { ownerId: string },
  query: BudgetCycleOwnerRemoveQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/budget-cycle-owners/${encodeURIComponent(params.ownerId)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetCyclesOwnersSubmit(
  client: ApiClient,
  params: { ownerId: string },
  body: BudgetCycleOwnerSubmitRequest,
  idempotencyKey?: string,
): Promise<BudgetCycleSubmissionDTO> {
  return client.request<BudgetCycleSubmissionDTO>('POST', `/api/v1/budget-cycle-owners/${encodeURIComponent(params.ownerId)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function budgetCyclesOwnersReopen(
  client: ApiClient,
  params: { ownerId: string },
  body: BudgetCycleOwnerReopenRequest,
): Promise<BudgetCycleSubmissionDTO> {
  return client.request<BudgetCycleSubmissionDTO>('POST', `/api/v1/budget-cycle-owners/${encodeURIComponent(params.ownerId)}/reopen`, {
    body,
  });
}

export async function budgetModelsList(
  client: ApiClient,
  query: ListModelsQuery,
): Promise<PageBudgetModelDTO> {
  return client.request<PageBudgetModelDTO>('GET', `/api/v1/budget-models`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetModelsCreate(
  client: ApiClient,
  body: CreateModelRequest,
  idempotencyKey?: string,
): Promise<BudgetModelDTO> {
  return client.request<BudgetModelDTO>('POST', `/api/v1/budget-models`, {
    body,
    idempotencyKey,
  });
}

export async function budgetModelsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BudgetModelDTO> {
  return client.request<BudgetModelDTO>('GET', `/api/v1/budget-models/${encodeURIComponent(params.id)}`, {
  });
}

export async function budgetModelsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateModelRequest,
): Promise<BudgetModelDTO> {
  return client.request<BudgetModelDTO>('PATCH', `/api/v1/budget-models/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function budgetVersionsList(
  client: ApiClient,
  params: { id: string },
  query: ListVersionsQuery,
): Promise<PageBudgetVersionDTO> {
  return client.request<PageBudgetVersionDTO>('GET', `/api/v1/budget-models/${encodeURIComponent(params.id)}/versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetVersionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateVersionRequest,
  idempotencyKey?: string,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('POST', `/api/v1/budget-models/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function budgetVersionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('GET', `/api/v1/budget-versions/${encodeURIComponent(params.id)}`, {
  });
}

export async function budgetVersionsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/budget-versions/${encodeURIComponent(params.id)}`, {
  });
}

export async function budgetVersionsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function budgetVersionsLock(
  client: ApiClient,
  params: { id: string },
  body: TransitionRequest,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/lock`, {
    body,
  });
}

export async function budgetVersionsUnlock(
  client: ApiClient,
  params: { id: string },
  body: TransitionRequest,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/unlock`, {
    body,
  });
}

export async function budgetVersionsSupersede(
  client: ApiClient,
  params: { id: string },
  body: TransitionRequest,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function budgetLinesList(
  client: ApiClient,
  params: { id: string },
  query: BudgetListLinesQuery,
): Promise<PageBudgetLineDTO> {
  return client.request<PageBudgetLineDTO>('GET', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetLinesCreate(
  client: ApiClient,
  params: { id: string },
  body: UpsertLineRequest,
  idempotencyKey?: string,
): Promise<BudgetLineDTO> {
  return client.request<BudgetLineDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/lines`, {
    body,
    idempotencyKey,
  });
}

export async function budgetLinesUpdate(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: UpsertLineRequest,
): Promise<BudgetLineDTO> {
  return client.request<BudgetLineDTO>('PATCH', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}`, {
    body,
  });
}

export async function budgetLinesDelete(
  client: ApiClient,
  params: { id: string; lineId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}`, {
  });
}

export async function budgetLinesImport(
  client: ApiClient,
  params: { id: string },
  file: File | Blob,
): Promise<BudgetImportResult> {
  const form = new FormData();
  form.append('file', file);
  return client.request<BudgetImportResult>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/import`, {
    body: form,
  });
}

export async function budgetLinesCopyPriorYear(
  client: ApiClient,
  params: { id: string },
  body: CopyPriorYearRequest,
  idempotencyKey?: string,
): Promise<CopyResult> {
  return client.request<CopyResult>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/copy-from`, {
    body,
    idempotencyKey,
  });
}

export async function budgetLinesSpread(
  client: ApiClient,
  params: { id: string },
  body: SpreadRequest,
  idempotencyKey?: string,
): Promise<SpreadResult> {
  return client.request<SpreadResult>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/spread`, {
    body,
    idempotencyKey,
  });
}

export async function budgetCommitmentsList(
  client: ApiClient,
  query: ListCommitmentsQuery,
): Promise<PageCommitmentDTO> {
  return client.request<PageCommitmentDTO>('GET', `/api/v1/budget-commitments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetCheck(
  client: ApiClient,
  body: CheckRequestDTO,
): Promise<CheckResultDTO> {
  return client.request<CheckResultDTO>('POST', `/api/v1/budget/check`, {
    body,
  });
}

export async function budgetVariance(
  client: ApiClient,
  query: VarianceQuery,
): Promise<VarianceReport> {
  return client.request<VarianceReport>('GET', `/api/v1/budget-variance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetControlLevelsGet(
  client: ApiClient,
  query: BudgetControlLevelsQuery,
): Promise<BudgetControlLevelsDTO> {
  return client.request<BudgetControlLevelsDTO>('GET', `/api/v1/budget-control-levels`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetControlLevelsSet(
  client: ApiClient,
  body: BudgetControlLevelSetRequest,
): Promise<BudgetControlLevelChangeDTO> {
  return client.request<BudgetControlLevelChangeDTO>('PUT', `/api/v1/budget-control-levels`, {
    body,
  });
}

export async function budgetTransfersList(
  client: ApiClient,
  query: BudgetTransferListQuery,
): Promise<PageBudgetTransferDTO> {
  return client.request<PageBudgetTransferDTO>('GET', `/api/v1/budget-transfers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetTransfersRequest(
  client: ApiClient,
  body: BudgetTransferRequest,
  idempotencyKey?: string,
): Promise<BudgetTransferDTO> {
  return client.request<BudgetTransferDTO>('POST', `/api/v1/budget-transfers`, {
    body,
    idempotencyKey,
  });
}

export async function budgetTransfersGet(
  client: ApiClient,
  params: { id: string },
): Promise<BudgetTransferDTO> {
  return client.request<BudgetTransferDTO>('GET', `/api/v1/budget-transfers/${encodeURIComponent(params.id)}`, {
  });
}

export async function budgetReportsTransferRegister(
  client: ApiClient,
  query: BudgetTransferRegisterQuery,
): Promise<BudgetTransferRegisterDTO> {
  return client.request<BudgetTransferRegisterDTO>('GET', `/api/v1/reports/budget-transfers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetSupplementaryList(
  client: ApiClient,
  query: BudgetSupplementaryListQuery,
): Promise<PageBudgetSupplementaryDTO> {
  return client.request<PageBudgetSupplementaryDTO>('GET', `/api/v1/budget-supplementary-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetSupplementaryRequest(
  client: ApiClient,
  body: BudgetSupplementaryRequest,
  idempotencyKey?: string,
): Promise<BudgetSupplementaryDTO> {
  return client.request<BudgetSupplementaryDTO>('POST', `/api/v1/budget-supplementary-requests`, {
    body,
    idempotencyKey,
  });
}

export async function budgetSupplementaryGet(
  client: ApiClient,
  params: { id: string },
): Promise<BudgetSupplementaryDTO> {
  return client.request<BudgetSupplementaryDTO>('GET', `/api/v1/budget-supplementary-requests/${encodeURIComponent(params.id)}`, {
  });
}

export async function budgetSettingsGet(
  client: ApiClient,
  query: BudgetSettingsQuery,
): Promise<BudgetSettingsDTO> {
  return client.request<BudgetSettingsDTO>('GET', `/api/v1/budget-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function budgetSettingsSet(
  client: ApiClient,
  body: BudgetSettingsSetRequest,
): Promise<BudgetSettingsDTO> {
  return client.request<BudgetSettingsDTO>('PUT', `/api/v1/budget-settings`, {
    body,
  });
}

export async function budgetVersionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: BudgetVersionUpdateRequest,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('PATCH', `/api/v1/budget-versions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function budgetForecastsRevise(
  client: ApiClient,
  params: { id: string },
  body: BudgetForecastReviseRequest,
  idempotencyKey?: string,
): Promise<BudgetVersionDTO> {
  return client.request<BudgetVersionDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/revise-forecast`, {
    body,
    idempotencyKey,
  });
}

export async function budgetVersionsBaseline(
  client: ApiClient,
  params: { id: string },
  body: BudgetVersionBaselineRequest,
  idempotencyKey?: string,
): Promise<BudgetVersionBaselineDTO> {
  return client.request<BudgetVersionBaselineDTO>('POST', `/api/v1/budget-versions/${encodeURIComponent(params.id)}/baseline`, {
    body,
    idempotencyKey,
  });
}

export async function budgetReportsForecastVsBudget(
  client: ApiClient,
  query: BudgetForecastVsBudgetQuery,
): Promise<BudgetForecastVsBudgetReportDTO> {
  return client.request<BudgetForecastVsBudgetReportDTO>('GET', `/api/v1/reports/forecast-vs-budget`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsClassesList(
  client: ApiClient,
  query: ListClassesQuery,
): Promise<PageClassDTO> {
  return client.request<PageClassDTO>('GET', `/api/v1/asset-classes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsClassesCreate(
  client: ApiClient,
  body: CreateClassRequest,
  idempotencyKey?: string,
): Promise<ClassDTO> {
  return client.request<ClassDTO>('POST', `/api/v1/asset-classes`, {
    body,
    idempotencyKey,
  });
}

export async function assetsClassesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ClassDTO> {
  return client.request<ClassDTO>('GET', `/api/v1/asset-classes/${encodeURIComponent(params.id)}`, {
  });
}

export async function assetsClassesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateClassRequest,
): Promise<ClassDTO> {
  return client.request<ClassDTO>('PATCH', `/api/v1/asset-classes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function assetsAssetsList(
  client: ApiClient,
  query: ListAssetsQuery,
): Promise<PageAssetDTO> {
  return client.request<PageAssetDTO>('GET', `/api/v1/assets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsAssetsCreate(
  client: ApiClient,
  body: CreateAssetRequest,
  idempotencyKey?: string,
): Promise<AssetDTO> {
  return client.request<AssetDTO>('POST', `/api/v1/assets`, {
    body,
    idempotencyKey,
  });
}

export async function assetsAssetsCapitalize(
  client: ApiClient,
  body: CapitalizeRequest,
  idempotencyKey?: string,
): Promise<AssetDTO> {
  return client.request<AssetDTO>('POST', `/api/v1/assets/capitalize`, {
    body,
    idempotencyKey,
  });
}

export async function assetsAssetsCapitalizableSources(
  client: ApiClient,
  query: AssetsCapitalizableSourcesQuery,
): Promise<PageAssetsCapitalizableSourceDTO> {
  return client.request<PageAssetsCapitalizableSourceDTO>('GET', `/api/v1/assets/capitalizable-sources`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsAssetsCapitalizableInvoiceLines(
  client: ApiClient,
  query: ListCapitalizableLinesQuery,
): Promise<PageCapitalizableLineDTO> {
  return client.request<PageCapitalizableLineDTO>('GET', `/api/v1/assets/capitalizable-invoice-lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsDepreciationRunsList(
  client: ApiClient,
  query: ListDepreciationRunsQuery,
): Promise<PageDepreciationRunDTO> {
  return client.request<PageDepreciationRunDTO>('GET', `/api/v1/assets/depreciation-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsAssetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<AssetDTO> {
  return client.request<AssetDTO>('GET', `/api/v1/assets/${encodeURIComponent(params.id)}`, {
  });
}

export async function assetsAssetsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateAssetRequest,
): Promise<AssetDTO> {
  return client.request<AssetDTO>('PATCH', `/api/v1/assets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function assetsAssetsTransfer(
  client: ApiClient,
  params: { id: string },
  body: TransferRequest,
): Promise<AssetDTO> {
  return client.request<AssetDTO>('POST', `/api/v1/assets/${encodeURIComponent(params.id)}/transfer`, {
    body,
  });
}

export async function assetsAssetsRevalue(
  client: ApiClient,
  params: { id: string },
  body: RevalueRequest,
): Promise<AssetDTO> {
  return client.request<AssetDTO>('POST', `/api/v1/assets/${encodeURIComponent(params.id)}/revalue`, {
    body,
  });
}

export async function assetsAssetsDispose(
  client: ApiClient,
  params: { id: string },
  body: DisposeRequest,
): Promise<AssetDTO> {
  return client.request<AssetDTO>('POST', `/api/v1/assets/${encodeURIComponent(params.id)}/dispose`, {
    body,
  });
}

export async function assetsAssetsDepreciationEntries(
  client: ApiClient,
  params: { id: string },
  query: ListDepreciationEntriesQuery,
): Promise<PageDepreciationEntryDTO> {
  return client.request<PageDepreciationEntryDTO>('GET', `/api/v1/assets/${encodeURIComponent(params.id)}/depreciation-entries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsAssetsDepreciationSchedule(
  client: ApiClient,
  params: { id: string },
): Promise<AssetDepreciationSchedule> {
  return client.request<AssetDepreciationSchedule>('GET', `/api/v1/assets/${encodeURIComponent(params.id)}/depreciation-schedule`, {
  });
}

export async function assetsDepreciationRun(
  client: ApiClient,
  body: RunDepreciationRequest,
  idempotencyKey?: string,
): Promise<DepreciationRunResult> {
  return client.request<DepreciationRunResult>('POST', `/api/v1/assets/depreciation-runs`, {
    body,
    idempotencyKey,
  });
}

export async function assetsReportsMovement(
  client: ApiClient,
  query: MovementReportQuery,
): Promise<MovementReport> {
  return client.request<MovementReport>('GET', `/api/v1/reports/asset-movement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsDisposalsList(
  client: ApiClient,
  query: AssetsDisposalListQuery,
): Promise<PageAssetsDisposalDTO> {
  return client.request<PageAssetsDisposalDTO>('GET', `/api/v1/asset-disposals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsDisposalsGet(
  client: ApiClient,
  params: { id: string },
): Promise<AssetsDisposalDTO> {
  return client.request<AssetsDisposalDTO>('GET', `/api/v1/asset-disposals/${encodeURIComponent(params.id)}`, {
  });
}

export async function assetsReportsDisposalGainLoss(
  client: ApiClient,
  query: AssetsDisposalGainLossQuery,
): Promise<AssetsDisposalGainLossReport> {
  return client.request<AssetsDisposalGainLossReport>('GET', `/api/v1/reports/disposal-gain-loss`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<AssetsSettingsDTO> {
  return client.request<AssetsSettingsDTO>('GET', `/api/v1/asset-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function assetsSettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: AssetsSettingsUpdateRequest,
): Promise<AssetsSettingsDTO> {
  return client.request<AssetsSettingsDTO>('PUT', `/api/v1/asset-settings/${encodeURIComponent(params.companyId)}`, {
    body,
  });
}

export async function assetsAssetsTransferIntercompany(
  client: ApiClient,
  params: { id: string },
  body: AssetsIntercompanyTransferRequest,
  idempotencyKey?: string,
): Promise<AssetsIntercompanyTransferDTO> {
  return client.request<AssetsIntercompanyTransferDTO>('POST', `/api/v1/assets/${encodeURIComponent(params.id)}/transfer-intercompany`, {
    body,
    idempotencyKey,
  });
}

export async function assetsAssetsEvents(
  client: ApiClient,
  params: { id: string },
  query: AssetsEventListQuery,
): Promise<PageAssetsEventDTO> {
  return client.request<PageAssetsEventDTO>('GET', `/api/v1/assets/${encodeURIComponent(params.id)}/events`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsDepreciationPreview(
  client: ApiClient,
  body: AssetsDepreciationPreviewRequest,
): Promise<AssetsDepreciationPreviewDTO> {
  return client.request<AssetsDepreciationPreviewDTO>('POST', `/api/v1/assets/depreciation-runs/preview`, {
    body,
  });
}

export async function assetsDepreciationSchedulePreview(
  client: ApiClient,
  body: AssetsDepreciationSchedulePreviewRequest,
): Promise<AssetsDepreciationSchedulePreviewDTO> {
  return client.request<AssetsDepreciationSchedulePreviewDTO>('POST', `/api/v1/assets/depreciation-schedule/preview`, {
    body,
  });
}

export async function assetsReportsDepreciationForecast(
  client: ApiClient,
  query: AssetsDepreciationForecastQuery,
): Promise<AssetsDepreciationForecastReport> {
  return client.request<AssetsDepreciationForecastReport>('GET', `/api/v1/reports/depreciation-forecast`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsReportsCapexVsBudget(
  client: ApiClient,
  query: AssetsCapexVsBudgetQuery,
): Promise<AssetsCapexVsBudgetReport> {
  return client.request<AssetsCapexVsBudgetReport>('GET', `/api/v1/reports/capex-vs-budget`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsBooksList(
  client: ApiClient,
  query: ListBooksQuery,
): Promise<PageFABookDTO> {
  return client.request<PageFABookDTO>('GET', `/api/v1/fa-books`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsBooksCreate(
  client: ApiClient,
  body: CreateBookRequest,
  idempotencyKey?: string,
): Promise<FABookDTO> {
  return client.request<FABookDTO>('POST', `/api/v1/fa-books`, {
    body,
    idempotencyKey,
  });
}

export async function assetsBooksGet(
  client: ApiClient,
  params: { id: string },
): Promise<FABookDTO> {
  return client.request<FABookDTO>('GET', `/api/v1/fa-books/${encodeURIComponent(params.id)}`, {
  });
}

export async function assetsBooksUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateBookRequest,
): Promise<FABookDTO> {
  return client.request<FABookDTO>('PATCH', `/api/v1/fa-books/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function assetsAssetsListBooks(
  client: ApiClient,
  params: { id: string },
  query: ListAssetBooksQuery,
): Promise<PageAssetBookDTO> {
  return client.request<PageAssetBookDTO>('GET', `/api/v1/assets/${encodeURIComponent(params.id)}/books`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsAssetsAddBook(
  client: ApiClient,
  params: { id: string },
  body: AddAssetBookRequest,
  idempotencyKey?: string,
): Promise<AssetBookDTO> {
  return client.request<AssetBookDTO>('POST', `/api/v1/assets/${encodeURIComponent(params.id)}/books`, {
    body,
    idempotencyKey,
  });
}

export async function assetsAssetBooksUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateAssetBookRequest,
): Promise<AssetBookDTO> {
  return client.request<AssetBookDTO>('PATCH', `/api/v1/asset-books/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function assetsAssetBooksChangeMethod(
  client: ApiClient,
  params: { id: string },
  body: ChangeBookMethodRequest,
): Promise<AssetBookDTO> {
  return client.request<AssetBookDTO>('POST', `/api/v1/asset-books/${encodeURIComponent(params.id)}/change-method`, {
    body,
  });
}

export async function assetsAssetBooksSchedule(
  client: ApiClient,
  params: { id: string },
): Promise<AssetBookScheduleDTO> {
  return client.request<AssetBookScheduleDTO>('GET', `/api/v1/asset-books/${encodeURIComponent(params.id)}/schedule`, {
  });
}

export async function assetsAssetsComponentise(
  client: ApiClient,
  params: { id: string },
  body: ComponentiseRequest,
  idempotencyKey?: string,
): Promise<FAComponentisationDTO> {
  return client.request<FAComponentisationDTO>('POST', `/api/v1/assets/${encodeURIComponent(params.id)}/componentise`, {
    body,
    idempotencyKey,
  });
}

export async function assetsImpairmentIndicatorsList(
  client: ApiClient,
  query: ListImpairmentIndicatorsQuery,
): Promise<PageFAImpairmentIndicatorDTO> {
  return client.request<PageFAImpairmentIndicatorDTO>('GET', `/api/v1/fa-impairment-indicators`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsImpairmentIndicatorsCreate(
  client: ApiClient,
  body: CreateImpairmentIndicatorRequest,
  idempotencyKey?: string,
): Promise<FAImpairmentIndicatorDTO> {
  return client.request<FAImpairmentIndicatorDTO>('POST', `/api/v1/fa-impairment-indicators`, {
    body,
    idempotencyKey,
  });
}

export async function assetsImpairmentIndicatorsAssess(
  client: ApiClient,
  params: { id: string },
  body: AssessImpairmentIndicatorRequest,
): Promise<FAImpairmentIndicatorDTO> {
  return client.request<FAImpairmentIndicatorDTO>('POST', `/api/v1/fa-impairment-indicators/${encodeURIComponent(params.id)}/assess`, {
    body,
  });
}

export async function assetsImpairmentsList(
  client: ApiClient,
  query: ListImpairmentsQuery,
): Promise<PageFAImpairmentDTO> {
  return client.request<PageFAImpairmentDTO>('GET', `/api/v1/fa-impairments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsImpairmentsCreate(
  client: ApiClient,
  body: CreateImpairmentRequest,
  idempotencyKey?: string,
): Promise<FAImpairmentDTO> {
  return client.request<FAImpairmentDTO>('POST', `/api/v1/fa-impairments`, {
    body,
    idempotencyKey,
  });
}

export async function assetsImpairmentsPost(
  client: ApiClient,
  params: { id: string },
): Promise<FAImpairmentDTO> {
  return client.request<FAImpairmentDTO>('POST', `/api/v1/fa-impairments/${encodeURIComponent(params.id)}/post`, {
  });
}

export async function assetsImpairmentsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseImpairmentRequest,
): Promise<FAImpairmentDTO> {
  return client.request<FAImpairmentDTO>('POST', `/api/v1/fa-impairments/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function assetsVerificationsList(
  client: ApiClient,
  query: ListVerificationRunsQuery,
): Promise<PageFAVerificationRunDTO> {
  return client.request<PageFAVerificationRunDTO>('GET', `/api/v1/fa-verification-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsVerificationsCreate(
  client: ApiClient,
  body: CreateVerificationRunRequest,
  idempotencyKey?: string,
): Promise<FAVerificationRunDTO> {
  return client.request<FAVerificationRunDTO>('POST', `/api/v1/fa-verification-runs`, {
    body,
    idempotencyKey,
  });
}

export async function assetsVerificationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<FAVerificationRunDTO> {
  return client.request<FAVerificationRunDTO>('GET', `/api/v1/fa-verification-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function assetsVerificationsListLines(
  client: ApiClient,
  params: { id: string },
  query: ListVerificationLinesQuery,
): Promise<PageFAVerificationLineDTO> {
  return client.request<PageFAVerificationLineDTO>('GET', `/api/v1/fa-verification-runs/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function assetsVerificationsRecordLines(
  client: ApiClient,
  params: { id: string },
  body: RecordVerificationLinesRequest,
): Promise<FAVerificationRunDTO> {
  return client.request<FAVerificationRunDTO>('PUT', `/api/v1/fa-verification-runs/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function assetsVerificationsComplete(
  client: ApiClient,
  params: { id: string },
  body: CompleteVerificationRequest,
): Promise<FAVerificationRunDTO> {
  return client.request<FAVerificationRunDTO>('POST', `/api/v1/fa-verification-runs/${encodeURIComponent(params.id)}/complete`, {
    body,
  });
}

export async function assetsReportsBookComparison(
  client: ApiClient,
  query: AssetBookComparisonQuery,
): Promise<AssetBookComparisonReport> {
  return client.request<AssetBookComparisonReport>('GET', `/api/v1/reports/asset-book-comparison`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryItemsList(
  client: ApiClient,
  query: InventoryListItemsQuery,
): Promise<PageItemDTO> {
  return client.request<PageItemDTO>('GET', `/api/v1/items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryItemsCreate(
  client: ApiClient,
  body: CreateItemRequest,
  idempotencyKey?: string,
): Promise<ItemDTO> {
  return client.request<ItemDTO>('POST', `/api/v1/items`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryItemsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ItemDTO> {
  return client.request<ItemDTO>('GET', `/api/v1/items/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryItemsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateItemRequest,
): Promise<ItemDTO> {
  return client.request<ItemDTO>('PATCH', `/api/v1/items/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryItemsUomsList(
  client: ApiClient,
  params: { id: string },
): Promise<ItemUOMDTO[]> {
  return client.request<ItemUOMDTO[]>('GET', `/api/v1/items/${encodeURIComponent(params.id)}/uoms`, {
  });
}

export async function inventoryItemsUomsUpsert(
  client: ApiClient,
  params: { id: string; uomCode: string },
  body: UpsertItemUOMRequest,
  idempotencyKey?: string,
): Promise<ItemUOMDTO> {
  return client.request<ItemUOMDTO>('PUT', `/api/v1/items/${encodeURIComponent(params.id)}/uoms/${encodeURIComponent(params.uomCode)}`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryItemsCompanySettingsGet(
  client: ApiClient,
  params: { id: string; companyId: string },
): Promise<ItemCompanySettingDTO> {
  return client.request<ItemCompanySettingDTO>('GET', `/api/v1/items/${encodeURIComponent(params.id)}/company-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function inventoryItemsCompanySettingsSet(
  client: ApiClient,
  params: { id: string; companyId: string },
  body: SetItemCompanySettingRequest,
  idempotencyKey?: string,
): Promise<ItemCompanySettingDTO> {
  return client.request<ItemCompanySettingDTO>('PUT', `/api/v1/items/${encodeURIComponent(params.id)}/company-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryWarehousesList(
  client: ApiClient,
  query: ListWarehousesQuery,
): Promise<PageWarehouseDTO> {
  return client.request<PageWarehouseDTO>('GET', `/api/v1/warehouses`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryWarehousesCreate(
  client: ApiClient,
  body: CreateWarehouseRequest,
  idempotencyKey?: string,
): Promise<WarehouseDTO> {
  return client.request<WarehouseDTO>('POST', `/api/v1/warehouses`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryWarehousesGet(
  client: ApiClient,
  params: { id: string },
): Promise<WarehouseDTO> {
  return client.request<WarehouseDTO>('GET', `/api/v1/warehouses/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryWarehousesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWarehouseRequest,
): Promise<WarehouseDTO> {
  return client.request<WarehouseDTO>('PATCH', `/api/v1/warehouses/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryWarehousesBinsList(
  client: ApiClient,
  params: { id: string },
  query: ListBinsQuery,
): Promise<PageBinDTO> {
  return client.request<PageBinDTO>('GET', `/api/v1/warehouses/${encodeURIComponent(params.id)}/bins`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryWarehousesBinsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateBinRequest,
  idempotencyKey?: string,
): Promise<BinDTO> {
  return client.request<BinDTO>('POST', `/api/v1/warehouses/${encodeURIComponent(params.id)}/bins`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryWarehousesBinsUpdate(
  client: ApiClient,
  params: { id: string; binId: string },
  body: UpdateBinRequest,
): Promise<BinDTO> {
  return client.request<BinDTO>('PATCH', `/api/v1/warehouses/${encodeURIComponent(params.id)}/bins/${encodeURIComponent(params.binId)}`, {
    body,
  });
}

export async function inventoryReceiptsCreate(
  client: ApiClient,
  body: ReceiveGoodsRequest,
  idempotencyKey?: string,
): Promise<ReceiptResultDTO> {
  return client.request<ReceiptResultDTO>('POST', `/api/v1/receipts`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReceiptsReverse(
  client: ApiClient,
  body: ReverseReceiptRequest,
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/receipts/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryIssuesCreate(
  client: ApiClient,
  body: IssueStockRequest,
  idempotencyKey?: string,
): Promise<IssueResultDTO> {
  return client.request<IssueResultDTO>('POST', `/api/v1/issues`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRevaluationsCreate(
  client: ApiClient,
  body: InventoryRevalueRequest,
  idempotencyKey?: string,
): Promise<StockLedgerEntryDTO> {
  return client.request<StockLedgerEntryDTO>('POST', `/api/v1/revaluations`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReservationsCreate(
  client: ApiClient,
  body: CreateReservationRequest,
  idempotencyKey?: string,
): Promise<ReservationDetailDTO> {
  return client.request<ReservationDetailDTO>('POST', `/api/v1/reservations`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReservationsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseReservationRequest,
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/reservations/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReservationsList(
  client: ApiClient,
  query: ListReservationsQuery,
): Promise<PageReservationDetailDTO> {
  return client.request<PageReservationDetailDTO>('GET', `/api/v1/reservations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryAtpGet(
  client: ApiClient,
  query: ATPRequestQuery,
): Promise<ATPLineDTO[]> {
  return client.request<ATPLineDTO[]>('GET', `/api/v1/atp`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryStockLedgerList(
  client: ApiClient,
  query: ListLedgerQuery,
): Promise<PageStockLedgerEntryDTO> {
  return client.request<PageStockLedgerEntryDTO>('GET', `/api/v1/stock-ledger`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryStockOnHandList(
  client: ApiClient,
  query: ListOnHandQuery,
): Promise<StockOnHandDTO[]> {
  return client.request<StockOnHandDTO[]>('GET', `/api/v1/stock-on-hand`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReasonCodesList(
  client: ApiClient,
  query: ListReasonCodesQuery,
): Promise<PageAdjustmentReasonCodeDTO> {
  return client.request<PageAdjustmentReasonCodeDTO>('GET', `/api/v1/adjustment-reason-codes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReasonCodesCreate(
  client: ApiClient,
  body: CreateReasonCodeRequest,
  idempotencyKey?: string,
): Promise<AdjustmentReasonCodeDTO> {
  return client.request<AdjustmentReasonCodeDTO>('POST', `/api/v1/adjustment-reason-codes`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReasonCodesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateReasonCodeRequest,
): Promise<AdjustmentReasonCodeDTO> {
  return client.request<AdjustmentReasonCodeDTO>('PATCH', `/api/v1/adjustment-reason-codes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryAdjustmentsList(
  client: ApiClient,
  query: ListAdjustmentsQuery,
): Promise<PageStockAdjustmentDTO> {
  return client.request<PageStockAdjustmentDTO>('GET', `/api/v1/adjustments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryAdjustmentsCreate(
  client: ApiClient,
  body: CreateAdjustmentRequest,
  idempotencyKey?: string,
): Promise<StockAdjustmentDTO> {
  return client.request<StockAdjustmentDTO>('POST', `/api/v1/adjustments`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryAdjustmentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<StockAdjustmentDTO> {
  return client.request<StockAdjustmentDTO>('GET', `/api/v1/adjustments/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryAdjustmentsApprove(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<StockAdjustmentDTO> {
  return client.request<StockAdjustmentDTO>('POST', `/api/v1/adjustments/${encodeURIComponent(params.id)}/approve`, {
    idempotencyKey,
  });
}

export async function inventoryAdjustmentsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectAdjustmentRequest,
  idempotencyKey?: string,
): Promise<StockAdjustmentDTO> {
  return client.request<StockAdjustmentDTO>('POST', `/api/v1/adjustments/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReportsValuation(
  client: ApiClient,
  query: ValuationReportQuery,
): Promise<ValuationReport> {
  return client.request<ValuationReport>('GET', `/api/v1/reports/valuation`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLotsList(
  client: ApiClient,
  query: ListStockLotsQuery,
): Promise<PageStockLotDTO> {
  return client.request<PageStockLotDTO>('GET', `/api/v1/stock-lots`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLotsCreate(
  client: ApiClient,
  body: CreateStockLotRequest,
  idempotencyKey?: string,
): Promise<StockLotDTO> {
  return client.request<StockLotDTO>('POST', `/api/v1/stock-lots`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLotsExpiring(
  client: ApiClient,
  query: ExpiringLotsQuery,
): Promise<PageExpiringLotDTO> {
  return client.request<PageExpiringLotDTO>('GET', `/api/v1/stock-lots/expiring`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLotsRepack(
  client: ApiClient,
  body: RepackLotRequest,
  idempotencyKey?: string,
): Promise<StockLotDTO> {
  return client.request<StockLotDTO>('POST', `/api/v1/stock-lots/repack`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLotsSplit(
  client: ApiClient,
  body: SplitLotRequest,
  idempotencyKey?: string,
): Promise<StockLotDTO[]> {
  return client.request<StockLotDTO[]>('POST', `/api/v1/stock-lots/split`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLotsMerge(
  client: ApiClient,
  body: MergeLotsRequest,
  idempotencyKey?: string,
): Promise<StockLotDTO> {
  return client.request<StockLotDTO>('POST', `/api/v1/stock-lots/merge`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLotsGet(
  client: ApiClient,
  params: { id: string },
): Promise<StockLotDetailDTO> {
  return client.request<StockLotDetailDTO>('GET', `/api/v1/stock-lots/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryLotsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateStockLotRequest,
): Promise<StockLotDTO> {
  return client.request<StockLotDTO>('PATCH', `/api/v1/stock-lots/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryLotsSetStatus(
  client: ApiClient,
  params: { id: string },
  body: SetLotStatusRequest,
): Promise<StockLotDTO> {
  return client.request<StockLotDTO>('POST', `/api/v1/stock-lots/${encodeURIComponent(params.id)}/status`, {
    body,
  });
}

export async function inventoryLotsBalances(
  client: ApiClient,
  params: { id: string },
): Promise<LotBalanceDTO[]> {
  return client.request<LotBalanceDTO[]>('GET', `/api/v1/stock-lots/${encodeURIComponent(params.id)}/balances`, {
  });
}

export async function inventoryLotsGenealogy(
  client: ApiClient,
  params: { id: string },
  query: LotGenealogyQuery,
): Promise<LotTraceResult> {
  return client.request<LotTraceResult>('GET', `/api/v1/stock-lots/${encodeURIComponent(params.id)}/genealogy`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventorySerialsList(
  client: ApiClient,
  query: ListStockSerialsQuery,
): Promise<PageStockSerialDTO> {
  return client.request<PageStockSerialDTO>('GET', `/api/v1/stock-serials`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRecallsList(
  client: ApiClient,
  query: ListLotRecallsQuery,
): Promise<PageLotRecallDTO> {
  return client.request<PageLotRecallDTO>('GET', `/api/v1/lot-recalls`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRecallsCreate(
  client: ApiClient,
  body: CreateLotRecallRequest,
  idempotencyKey?: string,
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('POST', `/api/v1/lot-recalls`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRecallsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('GET', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryRecallsTrace(
  client: ApiClient,
  params: { id: string },
  body: TraceRecallRequest,
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/trace`, {
    body,
  });
}

export async function inventoryRecallsTraces(
  client: ApiClient,
  params: { id: string },
  query: ListRecallTracesQuery,
): Promise<PageLotRecallTraceDTO> {
  return client.request<PageLotRecallTraceDTO>('GET', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/traces`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRecallsQuarantine(
  client: ApiClient,
  params: { id: string },
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/quarantine`, {
  });
}

export async function inventoryRecallsAddAction(
  client: ApiClient,
  params: { id: string },
  body: CreateRecallActionRequest,
  idempotencyKey?: string,
): Promise<LotRecallActionDTO> {
  return client.request<LotRecallActionDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/actions`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRecallsCompleteAction(
  client: ApiClient,
  params: { id: string; actionId: string },
  body: CompleteRecallActionRequest,
): Promise<LotRecallActionDTO> {
  return client.request<LotRecallActionDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/actions/${encodeURIComponent(params.actionId)}/complete`, {
    body,
  });
}

export async function inventoryRecallsContain(
  client: ApiClient,
  params: { id: string },
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/contain`, {
  });
}

export async function inventoryRecallsBuildEvidencePack(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<RecallEvidencePackDTO> {
  return client.request<RecallEvidencePackDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/evidence-pack`, {
    idempotencyKey,
  });
}

export async function inventoryRecallsEvidencePack(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/evidence-pack`, {
  });
}

export async function inventoryRecallsClose(
  client: ApiClient,
  params: { id: string },
  body: CloseRecallRequest,
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function inventoryRecallsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRecallRequest,
): Promise<LotRecallDetailDTO> {
  return client.request<LotRecallDetailDTO>('POST', `/api/v1/lot-recalls/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function inventoryZonesList(
  client: ApiClient,
  params: { id: string },
  query: ListZonesQuery,
): Promise<PageWarehouseZoneDTO> {
  return client.request<PageWarehouseZoneDTO>('GET', `/api/v1/warehouses/${encodeURIComponent(params.id)}/zones`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryZonesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateZoneRequest,
  idempotencyKey?: string,
): Promise<WarehouseZoneDTO> {
  return client.request<WarehouseZoneDTO>('POST', `/api/v1/warehouses/${encodeURIComponent(params.id)}/zones`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryZonesUpdate(
  client: ApiClient,
  params: { id: string; zoneId: string },
  body: UpdateZoneRequest,
): Promise<WarehouseZoneDTO> {
  return client.request<WarehouseZoneDTO>('PATCH', `/api/v1/warehouses/${encodeURIComponent(params.id)}/zones/${encodeURIComponent(params.zoneId)}`, {
    body,
  });
}

export async function inventoryZonesDelete(
  client: ApiClient,
  params: { id: string; zoneId: string },
  query: InventoryDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/warehouses/${encodeURIComponent(params.id)}/zones/${encodeURIComponent(params.zoneId)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryBalancesList(
  client: ApiClient,
  query: ListStockBalancesQuery,
): Promise<PageStockBalanceDTO> {
  return client.request<PageStockBalanceDTO>('GET', `/api/v1/stock-balances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryFixedBinsList(
  client: ApiClient,
  query: ListFixedBinsQuery,
): Promise<PageItemFixedBinDTO> {
  return client.request<PageItemFixedBinDTO>('GET', `/api/v1/item-fixed-bins`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryFixedBinsSet(
  client: ApiClient,
  body: SetFixedBinRequest,
  idempotencyKey?: string,
): Promise<ItemFixedBinDTO> {
  return client.request<ItemFixedBinDTO>('PUT', `/api/v1/item-fixed-bins`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryFixedBinsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/item-fixed-bins/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryPutawayPlan(
  client: ApiClient,
  body: PutawayPlanRequest,
): Promise<PutawayPlanDTO> {
  return client.request<PutawayPlanDTO>('POST', `/api/v1/putaway/plan`, {
    body,
  });
}

export async function inventoryPickingPlan(
  client: ApiClient,
  body: PickPlanRequest,
): Promise<PickPlanDTO> {
  return client.request<PickPlanDTO>('POST', `/api/v1/picking/plan`, {
    body,
  });
}

export async function inventoryCompanySettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<InventoryCompanySettingsDTO> {
  return client.request<InventoryCompanySettingsDTO>('GET', `/api/v1/inventory/company-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function inventoryCompanySettingsSet(
  client: ApiClient,
  params: { companyId: string },
  body: SetInventoryCompanySettingsRequest,
  idempotencyKey?: string,
): Promise<InventoryCompanySettingsDTO> {
  return client.request<InventoryCompanySettingsDTO>('PUT', `/api/v1/inventory/company-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryTransfersInTransit(
  client: ApiClient,
  query: InTransitQuery,
): Promise<InTransitSummaryDTO> {
  return client.request<InTransitSummaryDTO>('GET', `/api/v1/stock-transfers/in-transit`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryTransfersList(
  client: ApiClient,
  query: ListStockTransfersQuery,
): Promise<PageStockTransferDetailDTO> {
  return client.request<PageStockTransferDetailDTO>('GET', `/api/v1/stock-transfers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryTransfersDraft(
  client: ApiClient,
  body: DraftTransferRequest,
  idempotencyKey?: string,
): Promise<StockTransferDetailDTO> {
  return client.request<StockTransferDetailDTO>('POST', `/api/v1/stock-transfers`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryTransfersGet(
  client: ApiClient,
  params: { id: string },
): Promise<StockTransferDetailDTO> {
  return client.request<StockTransferDetailDTO>('GET', `/api/v1/stock-transfers/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryTransfersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateTransferRequest,
): Promise<StockTransferDetailDTO> {
  return client.request<StockTransferDetailDTO>('PATCH', `/api/v1/stock-transfers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryTransfersDespatch(
  client: ApiClient,
  params: { id: string },
  body: DespatchTransferRequest,
  idempotencyKey?: string,
): Promise<StockTransferDetailDTO> {
  return client.request<StockTransferDetailDTO>('POST', `/api/v1/stock-transfers/${encodeURIComponent(params.id)}/despatch`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryTransfersReceive(
  client: ApiClient,
  params: { id: string },
  body: ReceiveTransferRequest,
  idempotencyKey?: string,
): Promise<StockTransferDetailDTO> {
  return client.request<StockTransferDetailDTO>('POST', `/api/v1/stock-transfers/${encodeURIComponent(params.id)}/receive`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryTransfersCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelTransferRequest,
): Promise<StockTransferDetailDTO> {
  return client.request<StockTransferDetailDTO>('POST', `/api/v1/stock-transfers/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function inventoryReservationsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateReservationRequest,
): Promise<ReservationDetailDTO> {
  return client.request<ReservationDetailDTO>('PATCH', `/api/v1/reservations/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryShortagesList(
  client: ApiClient,
  query: ListShortagesQuery,
): Promise<PageStockShortageDTO> {
  return client.request<PageStockShortageDTO>('GET', `/api/v1/stock-shortages`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryShortagesSubstitute(
  client: ApiClient,
  params: { id: string },
  body: SubstituteShortageRequest,
  idempotencyKey?: string,
): Promise<StockShortageDTO> {
  return client.request<StockShortageDTO>('POST', `/api/v1/stock-shortages/${encodeURIComponent(params.id)}/substitute`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryShortagesResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveShortageRequest,
  idempotencyKey?: string,
): Promise<StockShortageDTO> {
  return client.request<StockShortageDTO>('POST', `/api/v1/stock-shortages/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function inventorySubstitutesList(
  client: ApiClient,
  query: ListSubstitutesQuery,
): Promise<PageItemSubstituteDTO> {
  return client.request<PageItemSubstituteDTO>('GET', `/api/v1/item-substitutes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventorySubstitutesCreate(
  client: ApiClient,
  body: CreateSubstituteRequest,
  idempotencyKey?: string,
): Promise<ItemSubstituteDTO> {
  return client.request<ItemSubstituteDTO>('POST', `/api/v1/item-substitutes`, {
    body,
    idempotencyKey,
  });
}

export async function inventorySubstitutesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSubstituteRequest,
): Promise<ItemSubstituteDTO> {
  return client.request<ItemSubstituteDTO>('PATCH', `/api/v1/item-substitutes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventorySubstitutesDelete(
  client: ApiClient,
  params: { id: string },
  query: InventoryDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/item-substitutes/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryCycleCountsListDetail(
  client: ApiClient,
  query: ListCountSessionsDetailQuery,
): Promise<PageCycleCountSessionDetailDTO> {
  return client.request<PageCycleCountSessionDetailDTO>('GET', `/api/v1/cycle-counts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryCycleCountsCreate(
  client: ApiClient,
  body: CountSessionRequest,
  idempotencyKey?: string,
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('POST', `/api/v1/cycle-counts`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryCycleCountsGetDetail(
  client: ApiClient,
  params: { id: string },
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('GET', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryCycleCountsRecordCounts(
  client: ApiClient,
  params: { id: string },
  body: InventoryRecordCountLinesRequest,
  idempotencyKey?: string,
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('POST', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/counts`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryCycleCountsRecordRecounts(
  client: ApiClient,
  params: { id: string },
  body: RecordRecountRequest,
  idempotencyKey?: string,
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('POST', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/recounts`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryCycleCountsExplainVariance(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: ExplainVarianceRequest,
): Promise<CycleCountLineDetailDTO> {
  return client.request<CycleCountLineDetailDTO>('POST', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/variance`, {
    body,
  });
}

export async function inventoryCycleCountsReview(
  client: ApiClient,
  params: { id: string },
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('POST', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/review`, {
  });
}

export async function inventoryCycleCountsPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('POST', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function inventoryCycleCountsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelCountSessionRequest,
): Promise<CycleCountSessionDetailDTO> {
  return client.request<CycleCountSessionDetailDTO>('POST', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function inventoryCycleCountsVarianceReport(
  client: ApiClient,
  params: { id: string },
): Promise<CountVarianceReportDTO> {
  return client.request<CountVarianceReportDTO>('GET', `/api/v1/cycle-counts/${encodeURIComponent(params.id)}/variance-report`, {
  });
}

export async function inventoryRootCauseCodesList(
  client: ApiClient,
  query: ListRootCauseCodesQuery,
): Promise<PageCountRootCauseCodeDTO> {
  return client.request<PageCountRootCauseCodeDTO>('GET', `/api/v1/count-root-cause-codes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRootCauseCodesCreate(
  client: ApiClient,
  body: CreateRootCauseCodeRequest,
  idempotencyKey?: string,
): Promise<CountRootCauseCodeDTO> {
  return client.request<CountRootCauseCodeDTO>('POST', `/api/v1/count-root-cause-codes`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRootCauseCodesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRootCauseCodeRequest,
): Promise<CountRootCauseCodeDTO> {
  return client.request<CountRootCauseCodeDTO>('PATCH', `/api/v1/count-root-cause-codes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryFreezesList(
  client: ApiClient,
  query: ListFreezesQuery,
): Promise<PageStockCountFreezeDTO> {
  return client.request<PageStockCountFreezeDTO>('GET', `/api/v1/stock-count-freezes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReasonCodesSetControls(
  client: ApiClient,
  params: { id: string },
  body: SetReasonCodeControlsRequest,
): Promise<AdjustmentReasonCodeDetailDTO> {
  return client.request<AdjustmentReasonCodeDetailDTO>('PATCH', `/api/v1/adjustment-reason-codes/${encodeURIComponent(params.id)}/controls`, {
    body,
  });
}

export async function inventoryAdjustmentsSetLineEvidence(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: SetAdjustmentLineEvidenceRequest,
): Promise<StockAdjustmentDetailDTO> {
  return client.request<StockAdjustmentDetailDTO>('PATCH', `/api/v1/stock-adjustments/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/evidence`, {
    body,
  });
}

export async function inventoryParLevelsList(
  client: ApiClient,
  query: ListParLevelsQuery,
): Promise<PageParLevelDTO> {
  return client.request<PageParLevelDTO>('GET', `/api/v1/par-levels`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryParLevelsSet(
  client: ApiClient,
  body: SetParLevelRequest,
  idempotencyKey?: string,
): Promise<ParLevelDTO> {
  return client.request<ParLevelDTO>('PUT', `/api/v1/par-levels`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryParLevelsTerminate(
  client: ApiClient,
  params: { id: string },
  body: TerminateParLevelRequest,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/par-levels/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryParLevelsRun(
  client: ApiClient,
  body: ParRunRequest,
): Promise<ParRunResultDTO> {
  return client.request<ParRunResultDTO>('POST', `/api/v1/par-levels/run`, {
    body,
  });
}

export async function inventoryParLevelsToRequisition(
  client: ApiClient,
  body: ParRunToRequisitionRequest,
  idempotencyKey?: string,
): Promise<StockRequisitionDTO> {
  return client.request<StockRequisitionDTO>('POST', `/api/v1/par-levels/run/requisition`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRequisitionsList(
  client: ApiClient,
  query: ListRequisitionsQuery,
): Promise<PageStockRequisitionDTO> {
  return client.request<PageStockRequisitionDTO>('GET', `/api/v1/stock-requisitions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRequisitionsCreate(
  client: ApiClient,
  body: CreateRequisitionRequest,
  idempotencyKey?: string,
): Promise<StockRequisitionDTO> {
  return client.request<StockRequisitionDTO>('POST', `/api/v1/stock-requisitions`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRequisitionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<StockRequisitionDetailDTO> {
  return client.request<StockRequisitionDetailDTO>('GET', `/api/v1/stock-requisitions/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryRequisitionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRequisitionRequest,
): Promise<StockRequisitionDTO> {
  return client.request<StockRequisitionDTO>('PATCH', `/api/v1/stock-requisitions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryRequisitionsSubmit(
  client: ApiClient,
  params: { id: string },
): Promise<StockRequisitionDTO> {
  return client.request<StockRequisitionDTO>('POST', `/api/v1/stock-requisitions/${encodeURIComponent(params.id)}/submit`, {
  });
}

export async function inventoryRequisitionsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideRequisitionRequest,
): Promise<StockRequisitionDTO> {
  return client.request<StockRequisitionDTO>('POST', `/api/v1/stock-requisitions/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function inventoryRequisitionsIssue(
  client: ApiClient,
  params: { id: string },
  body: IssueRequisitionRequest,
): Promise<StockRequisitionDetailDTO> {
  return client.request<StockRequisitionDetailDTO>('POST', `/api/v1/stock-requisitions/${encodeURIComponent(params.id)}/issue`, {
    body,
  });
}

export async function inventoryRequisitionsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRequisitionRequest,
): Promise<StockRequisitionDTO> {
  return client.request<StockRequisitionDTO>('POST', `/api/v1/stock-requisitions/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function inventoryWasteReasonsList(
  client: ApiClient,
  query: ListWasteReasonsQuery,
): Promise<PageWasteReasonCodeDTO> {
  return client.request<PageWasteReasonCodeDTO>('GET', `/api/v1/waste-reason-codes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryWasteReasonsCreate(
  client: ApiClient,
  body: CreateWasteReasonRequest,
  idempotencyKey?: string,
): Promise<WasteReasonCodeDTO> {
  return client.request<WasteReasonCodeDTO>('POST', `/api/v1/waste-reason-codes`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryWasteReasonsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWasteReasonRequest,
): Promise<WasteReasonCodeDTO> {
  return client.request<WasteReasonCodeDTO>('PATCH', `/api/v1/waste-reason-codes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryWasteList(
  client: ApiClient,
  query: ListWasteRecordsQuery,
): Promise<PageWasteRecordDTO> {
  return client.request<PageWasteRecordDTO>('GET', `/api/v1/waste-records`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryWasteCreate(
  client: ApiClient,
  body: CreateWasteRecordRequest,
  idempotencyKey?: string,
): Promise<WasteRecordDTO> {
  return client.request<WasteRecordDTO>('POST', `/api/v1/waste-records`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryWasteGet(
  client: ApiClient,
  params: { id: string },
): Promise<WasteRecordDTO> {
  return client.request<WasteRecordDTO>('GET', `/api/v1/waste-records/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryWasteApprove(
  client: ApiClient,
  params: { id: string },
): Promise<WasteRecordDTO> {
  return client.request<WasteRecordDTO>('POST', `/api/v1/waste-records/${encodeURIComponent(params.id)}/approve`, {
  });
}

export async function inventoryWasteReject(
  client: ApiClient,
  params: { id: string },
  body: RejectWasteRequest,
): Promise<WasteRecordDTO> {
  return client.request<WasteRecordDTO>('POST', `/api/v1/waste-records/${encodeURIComponent(params.id)}/reject`, {
    body,
  });
}

export async function inventoryReportsShrinkage(
  client: ApiClient,
  query: ShrinkageReportQuery,
): Promise<ShrinkageReportDTO> {
  return client.request<ShrinkageReportDTO>('GET', `/api/v1/reports/shrinkage`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReportsWasteAnalysis(
  client: ApiClient,
  query: WasteAnalysisQuery,
): Promise<WasteAnalysisDTO> {
  return client.request<WasteAnalysisDTO>('GET', `/api/v1/reports/waste-analysis`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRecipesList(
  client: ApiClient,
  query: ListRecipesQuery,
): Promise<PageRecipeDTO> {
  return client.request<PageRecipeDTO>('GET', `/api/v1/recipes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRecipesCreate(
  client: ApiClient,
  body: CreateRecipeRequest,
  idempotencyKey?: string,
): Promise<RecipeDetailDTO> {
  return client.request<RecipeDetailDTO>('POST', `/api/v1/recipes`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRecipesResolve(
  client: ApiClient,
  query: ResolveRecipeQuery,
): Promise<RecipeDetailDTO> {
  return client.request<RecipeDetailDTO>('GET', `/api/v1/recipes/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryRecipesGet(
  client: ApiClient,
  params: { id: string },
): Promise<RecipeDetailDTO> {
  return client.request<RecipeDetailDTO>('GET', `/api/v1/recipes/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryRecipesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRecipeRequest,
): Promise<RecipeDetailDTO> {
  return client.request<RecipeDetailDTO>('PATCH', `/api/v1/recipes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryRecipesSetLines(
  client: ApiClient,
  params: { id: string },
  body: SetRecipeLinesRequest,
): Promise<RecipeDetailDTO> {
  return client.request<RecipeDetailDTO>('PUT', `/api/v1/recipes/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function inventoryRecipesSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeRecipeRequest,
  idempotencyKey?: string,
): Promise<RecipeDetailDTO> {
  return client.request<RecipeDetailDTO>('POST', `/api/v1/recipes/${encodeURIComponent(params.id)}/supersede`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryRecipesCost(
  client: ApiClient,
  params: { id: string },
  query: RecipeCostQuery,
): Promise<RecipeCostDTO> {
  return client.request<RecipeCostDTO>('GET', `/api/v1/recipes/${encodeURIComponent(params.id)}/cost`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryDepletionCreate(
  client: ApiClient,
  body: DepletionRequest,
  idempotencyKey?: string,
): Promise<DepletionRunDTO> {
  return client.request<DepletionRunDTO>('POST', `/api/v1/depletions`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryDepletionUnresolved(
  client: ApiClient,
  query: UnresolvedDepletionQuery,
): Promise<PageUnresolvedDepletionDTO> {
  return client.request<PageUnresolvedDepletionDTO>('GET', `/api/v1/depletions/unresolved`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryDepletionList(
  client: ApiClient,
  query: ListDepletionRunsQuery,
): Promise<PageDepletionRunDTO> {
  return client.request<PageDepletionRunDTO>('GET', `/api/v1/depletions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryDepletionGet(
  client: ApiClient,
  params: { id: string },
): Promise<DepletionRunDetailDTO> {
  return client.request<DepletionRunDetailDTO>('GET', `/api/v1/depletions/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryDepletionReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseDepletionRequest,
): Promise<DepletionRunDTO> {
  return client.request<DepletionRunDTO>('POST', `/api/v1/depletions/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function inventoryUsageVarianceList(
  client: ApiClient,
  query: ListUsageVarianceQuery,
): Promise<PageUsageVarianceRunDTO> {
  return client.request<PageUsageVarianceRunDTO>('GET', `/api/v1/usage-variance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryUsageVarianceCompute(
  client: ApiClient,
  body: ComputeUsageVarianceRequest,
  idempotencyKey?: string,
): Promise<UsageVarianceRunDetailDTO> {
  return client.request<UsageVarianceRunDetailDTO>('POST', `/api/v1/usage-variance`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryUsageVarianceGet(
  client: ApiClient,
  params: { id: string },
): Promise<UsageVarianceRunDetailDTO> {
  return client.request<UsageVarianceRunDetailDTO>('GET', `/api/v1/usage-variance/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryHaccpListCheckpoints(
  client: ApiClient,
  query: ListCheckpointsQuery,
): Promise<PageHACCPCheckpointDTO> {
  return client.request<PageHACCPCheckpointDTO>('GET', `/api/v1/haccp-checkpoints`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryHaccpCreateCheckpoint(
  client: ApiClient,
  body: CreateCheckpointRequest,
  idempotencyKey?: string,
): Promise<HACCPCheckpointDTO> {
  return client.request<HACCPCheckpointDTO>('POST', `/api/v1/haccp-checkpoints`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryHaccpUpdateCheckpoint(
  client: ApiClient,
  params: { id: string },
  body: UpdateCheckpointRequest,
): Promise<HACCPCheckpointDTO> {
  return client.request<HACCPCheckpointDTO>('PATCH', `/api/v1/haccp-checkpoints/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryHaccpListRecords(
  client: ApiClient,
  query: ListHACCPRecordsQuery,
): Promise<PageHACCPRecordDTO> {
  return client.request<PageHACCPRecordDTO>('GET', `/api/v1/haccp-records`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryHaccpRecord(
  client: ApiClient,
  body: CreateHACCPRecordRequest,
  idempotencyKey?: string,
): Promise<HACCPRecordDTO> {
  return client.request<HACCPRecordDTO>('POST', `/api/v1/haccp-records`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryHaccpImportCsv(
  client: ApiClient,
  body: HACCPImportRequest,
): Promise<HACCPImportResultDTO> {
  return client.request<HACCPImportResultDTO>('POST', `/api/v1/haccp-records/import`, {
    body,
  });
}

export async function inventoryReportsHaccpCompliance(
  client: ApiClient,
  query: HACCPComplianceQuery,
): Promise<HACCPComplianceDTO> {
  return client.request<HACCPComplianceDTO>('GET', `/api/v1/reports/haccp-compliance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLandedCostListElements(
  client: ApiClient,
  query: ListLandedCostElementsQuery,
): Promise<PageLandedCostElementDTO> {
  return client.request<PageLandedCostElementDTO>('GET', `/api/v1/landed-cost-elements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLandedCostCreateElement(
  client: ApiClient,
  body: CreateLandedCostElementRequest,
  idempotencyKey?: string,
): Promise<LandedCostElementDTO> {
  return client.request<LandedCostElementDTO>('POST', `/api/v1/landed-cost-elements`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLandedCostUpdateElement(
  client: ApiClient,
  params: { id: string },
  body: UpdateLandedCostElementRequest,
): Promise<LandedCostElementDTO> {
  return client.request<LandedCostElementDTO>('PATCH', `/api/v1/landed-cost-elements/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryLandedCostListShipments(
  client: ApiClient,
  query: ListLandedCostShipmentsQuery,
): Promise<PageLandedCostShipmentDTO> {
  return client.request<PageLandedCostShipmentDTO>('GET', `/api/v1/landed-cost-shipments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLandedCostCreateShipment(
  client: ApiClient,
  body: CreateLandedCostShipmentRequest,
  idempotencyKey?: string,
): Promise<LandedCostShipmentDetailDTO> {
  return client.request<LandedCostShipmentDetailDTO>('POST', `/api/v1/landed-cost-shipments`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLandedCostGetShipment(
  client: ApiClient,
  params: { id: string },
): Promise<LandedCostShipmentDetailDTO> {
  return client.request<LandedCostShipmentDetailDTO>('GET', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryLandedCostUpdateShipment(
  client: ApiClient,
  params: { id: string },
  body: UpdateLandedCostShipmentRequest,
): Promise<LandedCostShipmentDTO> {
  return client.request<LandedCostShipmentDTO>('PATCH', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryLandedCostAttachReceipts(
  client: ApiClient,
  params: { id: string },
  body: AttachReceiptsRequest,
  idempotencyKey?: string,
): Promise<LandedCostShipmentDetailDTO> {
  return client.request<LandedCostShipmentDetailDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/receipts`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLandedCostDetachReceipt(
  client: ApiClient,
  params: { id: string; lineId: string },
  query: InventoryDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/receipts/${encodeURIComponent(params.lineId)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLandedCostAddCharge(
  client: ApiClient,
  params: { id: string },
  body: CreateLandedCostChargeRequest,
  idempotencyKey?: string,
): Promise<LandedCostChargeDTO> {
  return client.request<LandedCostChargeDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/charges`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLandedCostUpdateCharge(
  client: ApiClient,
  params: { id: string; chargeId: string },
  body: UpdateLandedCostChargeRequest,
): Promise<LandedCostChargeDTO> {
  return client.request<LandedCostChargeDTO>('PATCH', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/charges/${encodeURIComponent(params.chargeId)}`, {
    body,
  });
}

export async function inventoryLandedCostBillCharge(
  client: ApiClient,
  params: { id: string; chargeId: string },
  body: BillLandedCostChargeRequest,
  idempotencyKey?: string,
): Promise<LandedCostChargeDTO> {
  return client.request<LandedCostChargeDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/charges/${encodeURIComponent(params.chargeId)}/bill`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLandedCostDeleteCharge(
  client: ApiClient,
  params: { id: string; chargeId: string },
  query: InventoryDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/charges/${encodeURIComponent(params.chargeId)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryLandedCostAllocate(
  client: ApiClient,
  params: { id: string },
  body: AllocateLandedCostRequest,
): Promise<LandedCostAllocationPreviewDTO> {
  return client.request<LandedCostAllocationPreviewDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/allocate`, {
    body,
  });
}

export async function inventoryLandedCostAllocations(
  client: ApiClient,
  params: { id: string },
): Promise<LandedCostAllocationPreviewDTO> {
  return client.request<LandedCostAllocationPreviewDTO>('GET', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/allocations`, {
  });
}

export async function inventoryLandedCostAbsorb(
  client: ApiClient,
  params: { id: string },
  body: AbsorbLandedCostRequest,
  idempotencyKey?: string,
): Promise<LandedCostShipmentDetailDTO> {
  return client.request<LandedCostShipmentDetailDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/absorb`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryLandedCostClose(
  client: ApiClient,
  params: { id: string },
): Promise<LandedCostShipmentDetailDTO> {
  return client.request<LandedCostShipmentDetailDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/close`, {
  });
}

export async function inventoryLandedCostCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelShipmentRequest,
): Promise<LandedCostShipmentDTO> {
  return client.request<LandedCostShipmentDTO>('POST', `/api/v1/landed-cost-shipments/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function inventoryReportsLandedCostAging(
  client: ApiClient,
  query: LandedCostAgingQuery,
): Promise<LandedCostAgingDTO> {
  return client.request<LandedCostAgingDTO>('GET', `/api/v1/reports/landed-cost-aging`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryTariffsList(
  client: ApiClient,
  query: ListTariffsQuery,
): Promise<PageTariffClassificationDTO> {
  return client.request<PageTariffClassificationDTO>('GET', `/api/v1/tariff-classifications`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryTariffsCreate(
  client: ApiClient,
  body: CreateTariffRequest,
  idempotencyKey?: string,
): Promise<TariffClassificationDTO> {
  return client.request<TariffClassificationDTO>('POST', `/api/v1/tariff-classifications`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryTariffsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateTariffRequest,
): Promise<TariffClassificationDTO> {
  return client.request<TariffClassificationDTO>('PATCH', `/api/v1/tariff-classifications/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryItemsGetShippingMeasures(
  client: ApiClient,
  params: { id: string },
): Promise<ItemShippingMeasuresDTO> {
  return client.request<ItemShippingMeasuresDTO>('GET', `/api/v1/items/${encodeURIComponent(params.id)}/shipping-measures`, {
  });
}

export async function inventoryItemsSetShippingMeasures(
  client: ApiClient,
  params: { id: string },
  body: SetItemMeasuresRequest,
): Promise<ItemShippingMeasuresDTO> {
  return client.request<ItemShippingMeasuresDTO>('PATCH', `/api/v1/items/${encodeURIComponent(params.id)}/shipping-measures`, {
    body,
  });
}

export async function inventoryReplenishmentListPolicies(
  client: ApiClient,
  query: ListReplenishmentPoliciesQuery,
): Promise<PageReplenishmentPolicyDTO> {
  return client.request<PageReplenishmentPolicyDTO>('GET', `/api/v1/replenishment-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReplenishmentSetPolicy(
  client: ApiClient,
  body: SetReplenishmentPolicyRequest,
  idempotencyKey?: string,
): Promise<ReplenishmentPolicyDTO> {
  return client.request<ReplenishmentPolicyDTO>('PUT', `/api/v1/replenishment-policies`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReplenishmentTerminatePolicy(
  client: ApiClient,
  params: { id: string },
  body: TerminatePolicyRequest,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/replenishment-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function inventoryReplenishmentDemandHistory(
  client: ApiClient,
  query: DemandHistoryQuery,
): Promise<PageDemandPeriodDTO> {
  return client.request<PageDemandPeriodDTO>('GET', `/api/v1/demand-history`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReplenishmentRebuildDemand(
  client: ApiClient,
  body: RebuildDemandRequest,
): Promise<RebuildDemandResultDTO> {
  return client.request<RebuildDemandResultDTO>('POST', `/api/v1/demand-history/rebuild`, {
    body,
  });
}

export async function inventoryReorderSuggestionsList(
  client: ApiClient,
  query: ListReplenishmentSuggestionsQuery,
): Promise<PageReplenishmentSuggestionDTO> {
  return client.request<PageReplenishmentSuggestionDTO>('GET', `/api/v1/reorder-suggestions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReorderSuggestionsRun(
  client: ApiClient,
  body: RunReorderRequest,
): Promise<RunReorderResultDTO> {
  return client.request<RunReorderResultDTO>('POST', `/api/v1/reorder-suggestions/run`, {
    body,
  });
}

export async function inventoryReorderSuggestionsDismiss(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/reorder-suggestions/${encodeURIComponent(params.id)}/dismiss`, {
    idempotencyKey,
  });
}

export async function inventoryReorderSuggestionsConvert(
  client: ApiClient,
  body: ConvertSuggestionsRequest,
  idempotencyKey?: string,
): Promise<ReorderConversionResultDTO> {
  return client.request<ReorderConversionResultDTO>('POST', `/api/v1/reorder-suggestions/convert`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryClassificationList(
  client: ApiClient,
  query: ListClassificationQuery,
): Promise<PageInventoryClassificationLineDTO> {
  return client.request<PageInventoryClassificationLineDTO>('GET', `/api/v1/inventory-classifications`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryClassificationRun(
  client: ApiClient,
  body: RunClassificationRequest,
  idempotencyKey?: string,
): Promise<InventoryClassificationRunDTO> {
  return client.request<InventoryClassificationRunDTO>('POST', `/api/v1/inventory-classifications/run`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryClassificationSetDisposition(
  client: ApiClient,
  params: { id: string },
  body: SetDispositionRequest,
): Promise<InventoryClassificationLineDTO> {
  return client.request<InventoryClassificationLineDTO>('POST', `/api/v1/inventory-classifications/${encodeURIComponent(params.id)}/disposition`, {
    body,
  });
}

export async function inventoryClassificationAction(
  client: ApiClient,
  params: { id: string },
  body: ActionDispositionRequest,
): Promise<InventoryClassificationLineDTO> {
  return client.request<InventoryClassificationLineDTO>('POST', `/api/v1/inventory-classifications/${encodeURIComponent(params.id)}/action`, {
    body,
  });
}

export async function inventoryReconList(
  client: ApiClient,
  query: ListReconRunsQuery,
): Promise<PageInventoryReconRunDTO> {
  return client.request<PageInventoryReconRunDTO>('GET', `/api/v1/inventory-recon-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function inventoryReconRun(
  client: ApiClient,
  body: RunReconRequest,
  idempotencyKey?: string,
): Promise<InventoryReconRunDetailDTO> {
  return client.request<InventoryReconRunDetailDTO>('POST', `/api/v1/inventory-recon-runs`, {
    body,
    idempotencyKey,
  });
}

export async function inventoryReconGet(
  client: ApiClient,
  params: { id: string },
): Promise<InventoryReconRunDetailDTO> {
  return client.request<InventoryReconRunDetailDTO>('GET', `/api/v1/inventory-recon-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function inventoryReconExplain(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: ExplainReconRequest,
): Promise<InventoryReconLineDTO> {
  return client.request<InventoryReconLineDTO>('POST', `/api/v1/inventory-recon-runs/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/explain`, {
    body,
  });
}

export async function inventoryReconClear(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: ClearReconRequest,
): Promise<InventoryReconLineDTO> {
  return client.request<InventoryReconLineDTO>('POST', `/api/v1/inventory-recon-runs/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/clear`, {
    body,
  });
}

export async function procurementPrList(
  client: ApiClient,
  query: ListPRQuery,
): Promise<PagePRDTO> {
  return client.request<PagePRDTO>('GET', `/api/v1/purchase-requisitions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementPrCreate(
  client: ApiClient,
  body: CreatePRRequest,
  idempotencyKey?: string,
): Promise<PRDTO> {
  return client.request<PRDTO>('POST', `/api/v1/purchase-requisitions`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPrGet(
  client: ApiClient,
  params: { id: string },
): Promise<PRDTO> {
  return client.request<PRDTO>('GET', `/api/v1/purchase-requisitions/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementPrUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePRRequest,
): Promise<PRDTO> {
  return client.request<PRDTO>('PATCH', `/api/v1/purchase-requisitions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function procurementPrDelete(
  client: ApiClient,
  params: { id: string },
  query: ProcurementDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/purchase-requisitions/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementPrSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PRDTO> {
  return client.request<PRDTO>('POST', `/api/v1/purchase-requisitions/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function procurementPrApprove(
  client: ApiClient,
  params: { id: string },
  body: DecidePRRequest,
  idempotencyKey?: string,
): Promise<PRDTO> {
  return client.request<PRDTO>('POST', `/api/v1/purchase-requisitions/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPrCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRequest,
  idempotencyKey?: string,
): Promise<PRDTO> {
  return client.request<PRDTO>('POST', `/api/v1/purchase-requisitions/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqList(
  client: ApiClient,
  query: ListRFQQuery,
): Promise<PageRFQDTO> {
  return client.request<PageRFQDTO>('GET', `/api/v1/rfqs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementRfqCreate(
  client: ApiClient,
  body: CreateRFQRequest,
  idempotencyKey?: string,
): Promise<RFQDTO> {
  return client.request<RFQDTO>('POST', `/api/v1/rfqs`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqGet(
  client: ApiClient,
  params: { id: string },
): Promise<RFQDTO> {
  return client.request<RFQDTO>('GET', `/api/v1/rfqs/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementRfqInviteSuppliers(
  client: ApiClient,
  params: { id: string },
  body: InviteSuppliersRequest,
  idempotencyKey?: string,
): Promise<RFQDTO> {
  return client.request<RFQDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/suppliers`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqIssue(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<RFQDTO> {
  return client.request<RFQDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/issue`, {
    idempotencyKey,
  });
}

export async function procurementRfqSubmitQuote(
  client: ApiClient,
  params: { id: string },
  body: SubmitQuoteRequest,
  idempotencyKey?: string,
): Promise<QuoteDTO> {
  return client.request<QuoteDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/quotes`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqComparison(
  client: ApiClient,
  params: { id: string },
): Promise<ComparisonDTO> {
  return client.request<ComparisonDTO>('GET', `/api/v1/rfqs/${encodeURIComponent(params.id)}/comparison`, {
  });
}

export async function procurementRfqAward(
  client: ApiClient,
  params: { id: string },
  body: AwardRequest,
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/award`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPoList(
  client: ApiClient,
  query: ListPOQuery,
): Promise<PagePODTO> {
  return client.request<PagePODTO>('GET', `/api/v1/purchase-orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementPoCreate(
  client: ApiClient,
  body: CreatePORequest,
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPoGet(
  client: ApiClient,
  params: { id: string },
): Promise<PODTO> {
  return client.request<PODTO>('GET', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementPoUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePORequest,
): Promise<PODTO> {
  return client.request<PODTO>('PATCH', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function procurementPoSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function procurementPoApprove(
  client: ApiClient,
  params: { id: string },
  body: DecidePORequest,
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPoAmend(
  client: ApiClient,
  params: { id: string },
  body: AmendPORequest,
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/amend`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPoIssue(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/issue`, {
    idempotencyKey,
  });
}

export async function procurementPoConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmPORequest,
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPoCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRequest,
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function procurementPoCloseLine(
  client: ApiClient,
  params: { id: string },
  body: CloseLineRequest,
  idempotencyKey?: string,
): Promise<POLineDTO> {
  return client.request<POLineDTO>('POST', `/api/v1/purchase-order-lines/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function procurementGrnList(
  client: ApiClient,
  query: ListGRNQuery,
): Promise<PageGRNDTO> {
  return client.request<PageGRNDTO>('GET', `/api/v1/goods-receipts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementGrnCreate(
  client: ApiClient,
  body: CreateGRNRequest,
  idempotencyKey?: string,
): Promise<GRNDTO> {
  return client.request<GRNDTO>('POST', `/api/v1/goods-receipts`, {
    body,
    idempotencyKey,
  });
}

export async function procurementGrnGet(
  client: ApiClient,
  params: { id: string },
): Promise<GRNDTO> {
  return client.request<GRNDTO>('GET', `/api/v1/goods-receipts/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementGrnReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseGRNRequest,
  idempotencyKey?: string,
): Promise<GRNDTO> {
  return client.request<GRNDTO>('POST', `/api/v1/goods-receipts/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function procurementMatchList(
  client: ApiClient,
  query: ListMatchQuery,
): Promise<PageMatchDTO> {
  return client.request<PageMatchDTO>('GET', `/api/v1/invoice-matches`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementMatchCreate(
  client: ApiClient,
  body: matchInvoiceRequestDTO,
  idempotencyKey?: string,
): Promise<ProcurementMatchDTO> {
  return client.request<ProcurementMatchDTO>('POST', `/api/v1/invoice-matches`, {
    body,
    idempotencyKey,
  });
}

export async function procurementMatchGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProcurementMatchDTO> {
  return client.request<ProcurementMatchDTO>('GET', `/api/v1/invoice-matches/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementMatchResolveDispute(
  client: ApiClient,
  params: { id: string },
  body: ResolveDisputeRequest,
  idempotencyKey?: string,
): Promise<DisputeDTO> {
  return client.request<DisputeDTO>('POST', `/api/v1/match-disputes/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function procurementReportsGrIrAging(
  client: ApiClient,
  query: GRIRQuery,
): Promise<GRIRReport> {
  return client.request<GRIRReport>('GET', `/api/v1/reports/gr-ir-aging`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementScorecardsList(
  client: ApiClient,
  query: ListScorecardQuery,
): Promise<PageScorecardDTO> {
  return client.request<PageScorecardDTO>('GET', `/api/v1/vendor-scorecards`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementSettingsGet(
  client: ApiClient,
  query: ProcurementSettingsQuery,
): Promise<ProcurementSettingsDTO> {
  return client.request<ProcurementSettingsDTO>('GET', `/api/v1/procurement-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementSettingsUpsert(
  client: ApiClient,
  body: ProcurementUpsertSettingsRequest,
  idempotencyKey?: string,
): Promise<ProcurementSettingsDTO> {
  return client.request<ProcurementSettingsDTO>('PUT', `/api/v1/procurement-settings`, {
    body,
    idempotencyKey,
  });
}

export async function procurementGrnInspectionGet(
  client: ApiClient,
  params: { id: string },
): Promise<GRNLineDispositionDTO[]> {
  return client.request<GRNLineDispositionDTO[]>('GET', `/api/v1/goods-receipts/${encodeURIComponent(params.id)}/inspection`, {
  });
}

export async function procurementGrnInspectionRelease(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: ReleaseQuarantineRequest,
): Promise<GRNLineDispositionDTO> {
  return client.request<GRNLineDispositionDTO>('POST', `/api/v1/goods-receipts/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/release`, {
    body,
  });
}

export async function procurementGrnInspectionReject(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: RejectQuarantineRequest,
): Promise<GRNLineDispositionDTO> {
  return client.request<GRNLineDispositionDTO>('POST', `/api/v1/goods-receipts/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/reject`, {
    body,
  });
}

export async function procurementReturnsList(
  client: ApiClient,
  query: ListSupplierReturnsQuery,
): Promise<PageSupplierReturnDTO> {
  return client.request<PageSupplierReturnDTO>('GET', `/api/v1/supplier-returns`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementReturnsCreate(
  client: ApiClient,
  body: CreateSupplierReturnRequest,
  idempotencyKey?: string,
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('POST', `/api/v1/supplier-returns`, {
    body,
    idempotencyKey,
  });
}

export async function procurementReturnsGet(
  client: ApiClient,
  params: { id: string },
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('GET', `/api/v1/supplier-returns/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementReturnsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('POST', `/api/v1/supplier-returns/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function procurementReturnsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideRequest,
  idempotencyKey?: string,
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('POST', `/api/v1/supplier-returns/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function procurementReturnsDespatch(
  client: ApiClient,
  params: { id: string },
  body: DespatchReturnRequest,
  idempotencyKey?: string,
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('POST', `/api/v1/supplier-returns/${encodeURIComponent(params.id)}/despatch`, {
    body,
    idempotencyKey,
  });
}

export async function procurementReturnsDebitNote(
  client: ApiClient,
  params: { id: string },
  body: RaiseDebitNoteRequest,
  idempotencyKey?: string,
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('POST', `/api/v1/supplier-returns/${encodeURIComponent(params.id)}/debit-note`, {
    body,
    idempotencyKey,
  });
}

export async function procurementReturnsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRequest,
  idempotencyKey?: string,
): Promise<SupplierReturnDTO> {
  return client.request<SupplierReturnDTO>('POST', `/api/v1/supplier-returns/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function procurementSuppliersQualification(
  client: ApiClient,
  params: { vendorId: string },
): Promise<SupplierQualificationDTO> {
  return client.request<SupplierQualificationDTO>('GET', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/qualification`, {
  });
}

export async function procurementSuppliersQualify(
  client: ApiClient,
  params: { vendorId: string },
  body: TransitionQualificationRequest,
  idempotencyKey?: string,
): Promise<SupplierQualificationDTO> {
  return client.request<SupplierQualificationDTO>('POST', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/qualification`, {
    body,
    idempotencyKey,
  });
}

export async function procurementSupplierDocumentsList(
  client: ApiClient,
  params: { vendorId: string },
): Promise<SupplierComplianceDocDTO[]> {
  return client.request<SupplierComplianceDocDTO[]>('GET', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/documents`, {
  });
}

export async function procurementSupplierDocumentsCreate(
  client: ApiClient,
  params: { vendorId: string },
  body: CreateComplianceDocumentRequest,
  idempotencyKey?: string,
): Promise<SupplierComplianceDocDTO> {
  return client.request<SupplierComplianceDocDTO>('POST', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/documents`, {
    body,
    idempotencyKey,
  });
}

export async function procurementSupplierDocumentsVerify(
  client: ApiClient,
  params: { vendorId: string; docId: string },
  body: VerifyComplianceDocumentRequest,
  idempotencyKey?: string,
): Promise<SupplierComplianceDocDTO> {
  return client.request<SupplierComplianceDocDTO>('POST', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/documents/${encodeURIComponent(params.docId)}/verify`, {
    body,
    idempotencyKey,
  });
}

export async function procurementSupplierDocumentsDelete(
  client: ApiClient,
  params: { vendorId: string; docId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/documents/${encodeURIComponent(params.docId)}`, {
  });
}

export async function procurementSupplierDocumentsTypes(
  client: ApiClient,
): Promise<SupplierDocumentTypeDTO[]> {
  return client.request<SupplierDocumentTypeDTO[]>('GET', `/api/v1/supplier-document-types`, {
  });
}

export async function procurementSupplierDocumentsUpsertType(
  client: ApiClient,
  params: { code: string },
  body: UpsertDocumentTypeRequest,
  idempotencyKey?: string,
): Promise<SupplierDocumentTypeDTO> {
  return client.request<SupplierDocumentTypeDTO>('PUT', `/api/v1/supplier-document-types/${encodeURIComponent(params.code)}`, {
    body,
    idempotencyKey,
  });
}

export async function procurementScreeningsList(
  client: ApiClient,
  params: { vendorId: string },
  query: ListScreeningsQuery,
): Promise<PageSupplierScreeningDTO> {
  return client.request<PageSupplierScreeningDTO>('GET', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/screenings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementScreeningsCreate(
  client: ApiClient,
  params: { vendorId: string },
  body: CreateScreeningRequest,
  idempotencyKey?: string,
): Promise<SupplierScreeningDTO> {
  return client.request<SupplierScreeningDTO>('POST', `/api/v1/suppliers/${encodeURIComponent(params.vendorId)}/screenings`, {
    body,
    idempotencyKey,
  });
}

export async function procurementScreeningsReview(
  client: ApiClient,
  params: { id: string },
  body: ReviewScreeningRequest,
  idempotencyKey?: string,
): Promise<SupplierScreeningDTO> {
  return client.request<SupplierScreeningDTO>('POST', `/api/v1/screenings/${encodeURIComponent(params.id)}/review`, {
    body,
    idempotencyKey,
  });
}

export async function procurementSuppliersPolicy(
  client: ApiClient,
  params: { companyId: string },
): Promise<SupplierPolicyDTO> {
  return client.request<SupplierPolicyDTO>('GET', `/api/v1/supplier-policies/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function procurementSuppliersSetPolicy(
  client: ApiClient,
  params: { companyId: string },
  body: SetSupplierPolicyRequest,
  idempotencyKey?: string,
): Promise<SupplierPolicyDTO> {
  return client.request<SupplierPolicyDTO>('PUT', `/api/v1/supplier-policies/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function procurementSuppliersRiskReport(
  client: ApiClient,
  query: ListSupplierRiskQuery,
): Promise<PageSupplierRiskRowDTO> {
  return client.request<PageSupplierRiskRowDTO>('GET', `/api/v1/reports/supplier-risk`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementContractsList(
  client: ApiClient,
  query: ListSupplierContractsQuery,
): Promise<PageSupplierContractDTO> {
  return client.request<PageSupplierContractDTO>('GET', `/api/v1/supplier-contracts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementContractsCreate(
  client: ApiClient,
  body: CreateSupplierContractRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('POST', `/api/v1/supplier-contracts`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsGet(
  client: ApiClient,
  params: { id: string },
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('GET', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementContractsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSupplierContractRequest,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('PATCH', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function procurementContractsSetScopes(
  client: ApiClient,
  params: { id: string },
  body: SetContractScopesRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('PUT', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/scopes`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsSetLines(
  client: ApiClient,
  params: { id: string },
  body: SetContractLinesRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('PUT', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/lines`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsSetCommitments(
  client: ApiClient,
  params: { id: string },
  body: SetContractCommitmentsRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('PUT', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/commitments`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitContractRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('POST', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideContractRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('POST', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsSuspend(
  client: ApiClient,
  params: { id: string },
  body: SuspendContractRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('POST', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/suspend`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsTerminate(
  client: ApiClient,
  params: { id: string },
  body: TerminateContractRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('POST', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/terminate`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractReleasesList(
  client: ApiClient,
  params: { id: string },
  query: ListContractReleasesQuery,
): Promise<PageContractReleaseDTO> {
  return client.request<PageContractReleaseDTO>('GET', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/releases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementContractsConsumption(
  client: ApiClient,
  params: { id: string },
): Promise<ContractConsumptionDTO> {
  return client.request<ContractConsumptionDTO>('GET', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/consumption`, {
  });
}

export async function procurementRebatesSet(
  client: ApiClient,
  params: { id: string },
  body: SetContractRebatesRequest,
  idempotencyKey?: string,
): Promise<SupplierContractDetailDTO> {
  return client.request<SupplierContractDetailDTO>('PUT', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/rebates`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRebatesAccrue(
  client: ApiClient,
  params: { id: string; rid: string },
  body: AccrueRebateRequest,
  idempotencyKey?: string,
): Promise<SupplierRebateAccrualDTO> {
  return client.request<SupplierRebateAccrualDTO>('POST', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/rebates/${encodeURIComponent(params.rid)}/accrue`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRebatesSettle(
  client: ApiClient,
  params: { id: string; rid: string },
  body: SettleSupplierRebateRequest,
  idempotencyKey?: string,
): Promise<SupplierRebateAccrualDTO> {
  return client.request<SupplierRebateAccrualDTO>('POST', `/api/v1/supplier-contracts/${encodeURIComponent(params.id)}/rebates/${encodeURIComponent(params.rid)}/settle`, {
    body,
    idempotencyKey,
  });
}

export async function procurementContractsExpiryReport(
  client: ApiClient,
  query: ListContractExpiryQuery,
): Promise<PageContractExpiryRowDTO> {
  return client.request<PageContractExpiryRowDTO>('GET', `/api/v1/reports/contract-expiry`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementOrdersPrice(
  client: ApiClient,
  body: PricePORequest,
): Promise<POPriceResultDTO> {
  return client.request<POPriceResultDTO>('POST', `/api/v1/purchase-orders/price`, {
    body,
  });
}

export async function procurementSpendAnalysis(
  client: ApiClient,
  query: SpendAnalysisQuery,
): Promise<SpendAnalysisRowDTO[]> {
  return client.request<SpendAnalysisRowDTO[]>('GET', `/api/v1/spend-analysis`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementSpendMaverick(
  client: ApiClient,
  query: MaverickSpendQuery,
): Promise<MaverickSpendRowDTO[]> {
  return client.request<MaverickSpendRowDTO[]>('GET', `/api/v1/spend-analysis/maverick`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementSpendFacts(
  client: ApiClient,
  query: ListSpendFactsQuery,
): Promise<PageSpendFactDTO> {
  return client.request<PageSpendFactDTO>('GET', `/api/v1/spend-facts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementSpendExportCsv(
  client: ApiClient,
  query: SpendAnalysisQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/spend-analysis/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementMatchExceptionsList(
  client: ApiClient,
  query: ListMatchExceptionsQuery,
): Promise<PageMatchExceptionDTO> {
  return client.request<PageMatchExceptionDTO>('GET', `/api/v1/match-exceptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementMatchExceptionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<MatchExceptionDTO> {
  return client.request<MatchExceptionDTO>('GET', `/api/v1/match-exceptions/${encodeURIComponent(params.id)}`, {
  });
}

export async function procurementMatchExceptionsResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveMatchExceptionRequest,
  idempotencyKey?: string,
): Promise<MatchExceptionDTO> {
  return client.request<MatchExceptionDTO>('POST', `/api/v1/match-exceptions/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function procurementMatchExceptionsReassign(
  client: ApiClient,
  params: { id: string },
  body: ReassignMatchExceptionRequest,
  idempotencyKey?: string,
): Promise<MatchExceptionDTO> {
  return client.request<MatchExceptionDTO>('POST', `/api/v1/match-exceptions/${encodeURIComponent(params.id)}/reassign`, {
    body,
    idempotencyKey,
  });
}

export async function procurementMatchExceptionsCauses(
  client: ApiClient,
  query: ListMatchCausesQuery,
): Promise<MatchCauseDTO[]> {
  return client.request<MatchCauseDTO[]>('GET', `/api/v1/match-causes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function procurementMatchExceptionsUpsertCause(
  client: ApiClient,
  params: { code: string },
  body: UpsertMatchCauseRequest,
  idempotencyKey?: string,
): Promise<MatchCauseDTO> {
  return client.request<MatchCauseDTO>('PUT', `/api/v1/match-causes/${encodeURIComponent(params.code)}`, {
    body,
    idempotencyKey,
  });
}

export async function procurementAmendmentsList(
  client: ApiClient,
  params: { id: string },
): Promise<POAmendmentDTO[]> {
  return client.request<POAmendmentDTO[]>('GET', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/amendments`, {
  });
}

export async function procurementAmendmentsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateAmendmentRequest,
  idempotencyKey?: string,
): Promise<POAmendmentDTO> {
  return client.request<POAmendmentDTO>('POST', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/amendments`, {
    body,
    idempotencyKey,
  });
}

export async function procurementAmendmentsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<POAmendmentDTO> {
  return client.request<POAmendmentDTO>('POST', `/api/v1/purchase-order-amendments/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function procurementAmendmentsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideAmendmentRequest,
  idempotencyKey?: string,
): Promise<POAmendmentDTO> {
  return client.request<POAmendmentDTO>('POST', `/api/v1/purchase-order-amendments/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function procurementAmendmentsApply(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/api/v1/purchase-order-amendments/${encodeURIComponent(params.id)}/apply`, {
    idempotencyKey,
  });
}

export async function procurementDeliverySchedulesSet(
  client: ApiClient,
  params: { id: string },
  body: SetDeliveryScheduleRequest,
  idempotencyKey?: string,
): Promise<PODeliveryScheduleDTO[]> {
  return client.request<PODeliveryScheduleDTO[]>('PUT', `/api/v1/purchase-order-lines/${encodeURIComponent(params.id)}/delivery-schedule`, {
    body,
    idempotencyKey,
  });
}

export async function procurementDeliverySchedulesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PODeliveryScheduleDTO[]> {
  return client.request<PODeliveryScheduleDTO[]>('GET', `/api/v1/purchase-orders/${encodeURIComponent(params.id)}/delivery-schedule`, {
  });
}

export async function procurementIncotermsList(
  client: ApiClient,
): Promise<IncotermDTO[]> {
  return client.request<IncotermDTO[]>('GET', `/api/v1/incoterms`, {
  });
}

export async function procurementIncotermsUpsert(
  client: ApiClient,
  params: { code: string },
  body: UpsertIncotermRequest,
  idempotencyKey?: string,
): Promise<IncotermDTO> {
  return client.request<IncotermDTO>('PUT', `/api/v1/incoterms/${encodeURIComponent(params.code)}`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqCriteriaSet(
  client: ApiClient,
  params: { id: string },
  body: SetRFQCriteriaRequest,
  idempotencyKey?: string,
): Promise<RFQCriterionDTO[]> {
  return client.request<RFQCriterionDTO[]>('PUT', `/api/v1/rfqs/${encodeURIComponent(params.id)}/criteria`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqCriteriaList(
  client: ApiClient,
  params: { id: string },
): Promise<RFQCriterionDTO[]> {
  return client.request<RFQCriterionDTO[]>('GET', `/api/v1/rfqs/${encodeURIComponent(params.id)}/criteria`, {
  });
}

export async function procurementRfqEvaluatorsSet(
  client: ApiClient,
  params: { id: string },
  body: SetRFQEvaluatorsRequest,
  idempotencyKey?: string,
): Promise<RFQEvaluatorDTO[]> {
  return client.request<RFQEvaluatorDTO[]>('PUT', `/api/v1/rfqs/${encodeURIComponent(params.id)}/evaluators`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqEvaluatorsDeclareConflict(
  client: ApiClient,
  params: { id: string; uid: string },
  body: DeclareConflictRequest,
  idempotencyKey?: string,
): Promise<RFQEvaluatorDTO> {
  return client.request<RFQEvaluatorDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/evaluators/${encodeURIComponent(params.uid)}/declare-conflict`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqScoresRecord(
  client: ApiClient,
  params: { id: string; qid: string },
  body: RecordScoreRequest,
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/quotes/${encodeURIComponent(params.qid)}/scores`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqQuotesLogistics(
  client: ApiClient,
  params: { id: string; qid: string },
  body: RecordQuoteLogisticsRequest,
  idempotencyKey?: string,
): Promise<void> {
  return client.request<void>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/quotes/${encodeURIComponent(params.qid)}/logistics`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqQuotesList(
  client: ApiClient,
  params: { id: string },
): Promise<RFQQuoteViewDTO[]> {
  return client.request<RFQQuoteViewDTO[]>('GET', `/api/v1/rfqs/${encodeURIComponent(params.id)}/quotes`, {
  });
}

export async function procurementRfqsSeal(
  client: ApiClient,
  params: { id: string },
  body: SealRFQRequest,
  idempotencyKey?: string,
): Promise<RFQDTO> {
  return client.request<RFQDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/seal`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqsOpen(
  client: ApiClient,
  params: { id: string },
  body: OpenRFQRequest,
  idempotencyKey?: string,
): Promise<RFQDTO> {
  return client.request<RFQDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/open`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqClarificationsList(
  client: ApiClient,
  params: { id: string },
): Promise<RFQClarificationDTO[]> {
  return client.request<RFQClarificationDTO[]>('GET', `/api/v1/rfqs/${encodeURIComponent(params.id)}/clarifications`, {
  });
}

export async function procurementRfqClarificationsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateClarificationRequest,
  idempotencyKey?: string,
): Promise<RFQClarificationDTO> {
  return client.request<RFQClarificationDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/clarifications`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqClarificationsAnswer(
  client: ApiClient,
  params: { id: string; cid: string },
  body: AnswerClarificationRequest,
  idempotencyKey?: string,
): Promise<RFQClarificationDTO> {
  return client.request<RFQClarificationDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/clarifications/${encodeURIComponent(params.cid)}/answer`, {
    body,
    idempotencyKey,
  });
}

export async function procurementRfqClarificationsPublish(
  client: ApiClient,
  params: { id: string; cid: string },
  idempotencyKey?: string,
): Promise<RFQClarificationDTO> {
  return client.request<RFQClarificationDTO>('POST', `/api/v1/rfqs/${encodeURIComponent(params.id)}/clarifications/${encodeURIComponent(params.cid)}/publish`, {
    idempotencyKey,
  });
}

export async function procurementRfqAwardsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RFQAwardDTO> {
  return client.request<RFQAwardDTO>('GET', `/api/v1/rfqs/${encodeURIComponent(params.id)}/award-record`, {
  });
}

export async function crmLeadsList(
  client: ApiClient,
  query: ListLeadsQuery,
): Promise<PageLeadDTO> {
  return client.request<PageLeadDTO>('GET', `/api/v1/leads`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmLeadsCreate(
  client: ApiClient,
  body: CreateLeadRequest,
  idempotencyKey?: string,
): Promise<CreateLeadResult> {
  return client.request<CreateLeadResult>('POST', `/api/v1/leads`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LeadDTO> {
  return client.request<LeadDTO>('GET', `/api/v1/leads/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmLeadsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLeadRequest,
): Promise<LeadDTO> {
  return client.request<LeadDTO>('PATCH', `/api/v1/leads/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmLeadsDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/leads/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmLeadsDuplicates(
  client: ApiClient,
  params: { id: string },
): Promise<DuplicateCandidate[]> {
  return client.request<DuplicateCandidate[]>('GET', `/api/v1/leads/${encodeURIComponent(params.id)}/duplicates`, {
  });
}

export async function crmLeadsQualify(
  client: ApiClient,
  params: { id: string },
  body: QualifyLeadRequest,
  idempotencyKey?: string,
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('POST', `/api/v1/leads/${encodeURIComponent(params.id)}/qualify`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadsDisqualify(
  client: ApiClient,
  params: { id: string },
  body: DisqualifyLeadRequest,
): Promise<LeadDTO> {
  return client.request<LeadDTO>('POST', `/api/v1/leads/${encodeURIComponent(params.id)}/disqualify`, {
    body,
  });
}

export async function crmLeadsConvert(
  client: ApiClient,
  params: { id: string },
  body: ConvertLeadRequest,
  idempotencyKey?: string,
): Promise<ConvertLeadResult> {
  return client.request<ConvertLeadResult>('POST', `/api/v1/leads/${encodeURIComponent(params.id)}/convert`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadsMerge(
  client: ApiClient,
  params: { id: string },
  body: MergeLeadRequest,
  idempotencyKey?: string,
): Promise<LeadDTO> {
  return client.request<LeadDTO>('POST', `/api/v1/leads/${encodeURIComponent(params.id)}/merge`, {
    body,
    idempotencyKey,
  });
}

export async function crmOpportunityStagesList(
  client: ApiClient,
): Promise<OpportunityStageDTO[]> {
  return client.request<OpportunityStageDTO[]>('GET', `/api/v1/opportunity-stages`, {
  });
}

export async function crmOpportunitiesList(
  client: ApiClient,
  query: ListOpportunitiesQuery,
): Promise<PageOpportunityDTO> {
  return client.request<PageOpportunityDTO>('GET', `/api/v1/opportunities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmOpportunitiesCreate(
  client: ApiClient,
  body: CreateOpportunityRequest,
  idempotencyKey?: string,
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('POST', `/api/v1/opportunities`, {
    body,
    idempotencyKey,
  });
}

export async function crmOpportunitiesGet(
  client: ApiClient,
  params: { id: string },
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('GET', `/api/v1/opportunities/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmOpportunitiesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateOpportunityRequest,
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('PATCH', `/api/v1/opportunities/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmOpportunitiesDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/opportunities/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmOpportunitiesChangeStage(
  client: ApiClient,
  params: { id: string },
  body: ChangeStageRequest,
  idempotencyKey?: string,
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('POST', `/api/v1/opportunities/${encodeURIComponent(params.id)}/stage`, {
    body,
    idempotencyKey,
  });
}

export async function crmOpportunitiesWin(
  client: ApiClient,
  params: { id: string },
  body: WinOpportunityRequest,
  idempotencyKey?: string,
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('POST', `/api/v1/opportunities/${encodeURIComponent(params.id)}/win`, {
    body,
    idempotencyKey,
  });
}

export async function crmOpportunitiesLose(
  client: ApiClient,
  params: { id: string },
  body: LoseOpportunityRequest,
  idempotencyKey?: string,
): Promise<OpportunityDTO> {
  return client.request<OpportunityDTO>('POST', `/api/v1/opportunities/${encodeURIComponent(params.id)}/lose`, {
    body,
    idempotencyKey,
  });
}

export async function crmActivitiesList(
  client: ApiClient,
  query: ListActivitiesQuery,
): Promise<PageActivityDTO> {
  return client.request<PageActivityDTO>('GET', `/api/v1/activities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmActivitiesCreate(
  client: ApiClient,
  body: CreateActivityRequest,
  idempotencyKey?: string,
): Promise<ActivityDTO> {
  return client.request<ActivityDTO>('POST', `/api/v1/activities`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesList(
  client: ApiClient,
  query: ListCasesQuery,
): Promise<PageCaseDTO> {
  return client.request<PageCaseDTO>('GET', `/api/v1/cases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmCasesCreate(
  client: ApiClient,
  body: CreateCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CaseDTO> {
  return client.request<CaseDTO>('GET', `/api/v1/cases/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmCasesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCaseRequest,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('PATCH', `/api/v1/cases/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmCasesAssign(
  client: ApiClient,
  params: { id: string },
  body: AssignCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases/${encodeURIComponent(params.id)}/assign`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesRespond(
  client: ApiClient,
  params: { id: string },
  body: RespondCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases/${encodeURIComponent(params.id)}/respond`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesClose(
  client: ApiClient,
  params: { id: string },
  body: CloseCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesReopen(
  client: ApiClient,
  params: { id: string },
  body: ReopenCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases/${encodeURIComponent(params.id)}/reopen`, {
    body,
    idempotencyKey,
  });
}

export async function crmCasesCsat(
  client: ApiClient,
  params: { id: string },
  body: CSATRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/api/v1/cases/${encodeURIComponent(params.id)}/csat`, {
    body,
    idempotencyKey,
  });
}

export async function crmSlaPoliciesList(
  client: ApiClient,
  query: ListSLAPoliciesQuery,
): Promise<PageSLAPolicyDTO> {
  return client.request<PageSLAPolicyDTO>('GET', `/api/v1/crm-sla-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmSlaPoliciesCreate(
  client: ApiClient,
  body: CreateSLAPolicyRequest,
  idempotencyKey?: string,
): Promise<SLAPolicyDTO> {
  return client.request<SLAPolicyDTO>('POST', `/api/v1/crm-sla-policies`, {
    body,
    idempotencyKey,
  });
}

export async function crmSlaPoliciesGet(
  client: ApiClient,
  params: { id: string },
): Promise<SLAPolicyDTO> {
  return client.request<SLAPolicyDTO>('GET', `/api/v1/crm-sla-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmSlaPoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSLAPolicyRequest,
): Promise<SLAPolicyDTO> {
  return client.request<SLAPolicyDTO>('PATCH', `/api/v1/crm-sla-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmBusinessHoursGet(
  client: ApiClient,
): Promise<BusinessHourWindowInput[]> {
  return client.request<BusinessHourWindowInput[]>('GET', `/api/v1/crm-business-hours`, {
  });
}

export async function crmBusinessHoursSet(
  client: ApiClient,
  body: SetBusinessHoursRequest,
  idempotencyKey?: string,
): Promise<BusinessHourWindowInput[]> {
  return client.request<BusinessHourWindowInput[]>('PUT', `/api/v1/crm-business-hours`, {
    body,
    idempotencyKey,
  });
}

export async function crmSlaRun(
  client: ApiClient,
  body: RunSLASweepRequest,
  idempotencyKey?: string,
): Promise<SLASweepResult> {
  return client.request<SLASweepResult>('POST', `/api/v1/crm-sla/run`, {
    body,
    idempotencyKey,
  });
}

export async function crmKbArticlesList(
  client: ApiClient,
  query: ListKBArticlesQuery,
): Promise<PageKBArticleDTO> {
  return client.request<PageKBArticleDTO>('GET', `/api/v1/kb-articles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmKbArticlesCreate(
  client: ApiClient,
  body: CreateKBArticleRequest,
  idempotencyKey?: string,
): Promise<KBArticleDTO> {
  return client.request<KBArticleDTO>('POST', `/api/v1/kb-articles`, {
    body,
    idempotencyKey,
  });
}

export async function crmKbArticlesGet(
  client: ApiClient,
  params: { id: string },
): Promise<KBArticleDTO> {
  return client.request<KBArticleDTO>('GET', `/api/v1/kb-articles/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmKbArticlesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateKBArticleRequest,
): Promise<KBArticleDTO> {
  return client.request<KBArticleDTO>('PATCH', `/api/v1/kb-articles/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmKbArticlesDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/kb-articles/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmKbArticlesPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishKBArticleRequest,
  idempotencyKey?: string,
): Promise<KBArticleDTO> {
  return client.request<KBArticleDTO>('POST', `/api/v1/kb-articles/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function crmKbArticlesArchive(
  client: ApiClient,
  params: { id: string },
  body: ArchiveKBArticleRequest,
  idempotencyKey?: string,
): Promise<KBArticleDTO> {
  return client.request<KBArticleDTO>('POST', `/api/v1/kb-articles/${encodeURIComponent(params.id)}/archive`, {
    body,
    idempotencyKey,
  });
}

export async function crmReportsPipeline(
  client: ApiClient,
  query: PipelineQuery,
): Promise<PipelineReport> {
  return client.request<PipelineReport>('GET', `/api/v1/crm-reports/pipeline`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmReportsWinRate(
  client: ApiClient,
  query: DateRangeQuery,
): Promise<WinRateReport> {
  return client.request<WinRateReport>('GET', `/api/v1/crm-reports/win-rate`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmReportsConversion(
  client: ApiClient,
  query: DateRangeQuery,
): Promise<ConversionReport> {
  return client.request<ConversionReport>('GET', `/api/v1/crm-reports/conversion`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmReportsCaseVolume(
  client: ApiClient,
  query: DateRangeQuery,
): Promise<CaseVolumeReport> {
  return client.request<CaseVolumeReport>('GET', `/api/v1/crm-reports/cases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmReportsCsat(
  client: ApiClient,
  query: DateRangeQuery,
): Promise<CSATReport> {
  return client.request<CSATReport>('GET', `/api/v1/crm-reports/csat`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmAccountsList(
  client: ApiClient,
  query: ListCRMAccountsQuery,
): Promise<PageCRMAccountDTO> {
  return client.request<PageCRMAccountDTO>('GET', `/api/v1/crm-accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmAccountsCreate(
  client: ApiClient,
  body: CreateCRMAccountRequest,
  idempotencyKey?: string,
): Promise<CRMAccountDTO> {
  return client.request<CRMAccountDTO>('POST', `/api/v1/crm-accounts`, {
    body,
    idempotencyKey,
  });
}

export async function crmAccountsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CRMAccountDetailDTO> {
  return client.request<CRMAccountDetailDTO>('GET', `/api/v1/crm-accounts/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmAccountsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMAccountRequest,
): Promise<CRMAccountDTO> {
  return client.request<CRMAccountDTO>('PATCH', `/api/v1/crm-accounts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmAccountsDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/crm-accounts/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmAccountsTree(
  client: ApiClient,
  params: { id: string },
): Promise<CRMAccountTreeNodeDTO> {
  return client.request<CRMAccountTreeNodeDTO>('GET', `/api/v1/crm-accounts/${encodeURIComponent(params.id)}/tree`, {
  });
}

export async function crmAccountsContactsList(
  client: ApiClient,
  params: { id: string },
): Promise<CRMAccountContactDTO[]> {
  return client.request<CRMAccountContactDTO[]>('GET', `/api/v1/crm-accounts/${encodeURIComponent(params.id)}/contacts`, {
  });
}

export async function crmAccountsContactsAttach(
  client: ApiClient,
  params: { id: string },
  body: AttachCRMContactRequest,
  idempotencyKey?: string,
): Promise<CRMAccountContactDTO> {
  return client.request<CRMAccountContactDTO>('POST', `/api/v1/crm-accounts/${encodeURIComponent(params.id)}/contacts`, {
    body,
    idempotencyKey,
  });
}

export async function crmAccountsContactsDetach(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/crm-account-contacts/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmContactsList(
  client: ApiClient,
  query: ListCRMContactsQuery,
): Promise<PageCRMContactDTO> {
  return client.request<PageCRMContactDTO>('GET', `/api/v1/crm-contacts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmContactsCreate(
  client: ApiClient,
  body: CreateCRMContactRequest,
  idempotencyKey?: string,
): Promise<CRMContactDTO> {
  return client.request<CRMContactDTO>('POST', `/api/v1/crm-contacts`, {
    body,
    idempotencyKey,
  });
}

export async function crmContactsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CRMContactDTO> {
  return client.request<CRMContactDTO>('GET', `/api/v1/crm-contacts/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmContactsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMContactRequest,
): Promise<CRMContactDTO> {
  return client.request<CRMContactDTO>('PATCH', `/api/v1/crm-contacts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmContactsDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/crm-contacts/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmAccountSegmentsList(
  client: ApiClient,
): Promise<CRMAccountSegmentDTO[]> {
  return client.request<CRMAccountSegmentDTO[]>('GET', `/api/v1/crm-account-segments`, {
  });
}

export async function crmAccountSegmentsCreate(
  client: ApiClient,
  body: CreateCRMAccountSegmentRequest,
  idempotencyKey?: string,
): Promise<CRMAccountSegmentDTO> {
  return client.request<CRMAccountSegmentDTO>('POST', `/api/v1/crm-account-segments`, {
    body,
    idempotencyKey,
  });
}

export async function crmAccountSegmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMAccountSegmentRequest,
): Promise<CRMAccountSegmentDTO> {
  return client.request<CRMAccountSegmentDTO>('PATCH', `/api/v1/crm-account-segments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmContactRolesList(
  client: ApiClient,
): Promise<CRMContactRoleDTO[]> {
  return client.request<CRMContactRoleDTO[]>('GET', `/api/v1/crm-contact-roles`, {
  });
}

export async function crmContactRolesCreate(
  client: ApiClient,
  body: CreateCRMContactRoleRequest,
  idempotencyKey?: string,
): Promise<CRMContactRoleDTO> {
  return client.request<CRMContactRoleDTO>('POST', `/api/v1/crm-contact-roles`, {
    body,
    idempotencyKey,
  });
}

export async function crmContactRolesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMContactRoleRequest,
): Promise<CRMContactRoleDTO> {
  return client.request<CRMContactRoleDTO>('PATCH', `/api/v1/crm-contact-roles/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmSegmentsList(
  client: ApiClient,
  query: ListMarketingSegmentsQuery,
): Promise<PageMarketingSegmentDTO> {
  return client.request<PageMarketingSegmentDTO>('GET', `/api/v1/marketing-segments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmSegmentsCreate(
  client: ApiClient,
  body: CreateMarketingSegmentRequest,
  idempotencyKey?: string,
): Promise<MarketingSegmentDTO> {
  return client.request<MarketingSegmentDTO>('POST', `/api/v1/marketing-segments`, {
    body,
    idempotencyKey,
  });
}

export async function crmSegmentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<MarketingSegmentDTO> {
  return client.request<MarketingSegmentDTO>('GET', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmSegmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateMarketingSegmentRequest,
): Promise<MarketingSegmentDTO> {
  return client.request<MarketingSegmentDTO>('PATCH', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmSegmentsDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmSegmentsBuild(
  client: ApiClient,
  params: { id: string },
): Promise<SegmentBuildResult> {
  return client.request<SegmentBuildResult>('POST', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}/build`, {
  });
}

export async function crmSegmentsMembersList(
  client: ApiClient,
  params: { id: string },
  query: ListMarketingSegmentMembersQuery,
): Promise<PageMarketingSegmentMemberDTO> {
  return client.request<PageMarketingSegmentMemberDTO>('GET', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}/members`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmSegmentsMembersAdd(
  client: ApiClient,
  params: { id: string },
  body: AddMarketingSegmentMemberRequest,
  idempotencyKey?: string,
): Promise<MarketingSegmentMemberDTO> {
  return client.request<MarketingSegmentMemberDTO>('POST', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}/members`, {
    body,
    idempotencyKey,
  });
}

export async function crmSegmentsMembersRemove(
  client: ApiClient,
  params: { id: string; memberId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/marketing-segments/${encodeURIComponent(params.id)}/members/${encodeURIComponent(params.memberId)}`, {
  });
}

export async function crmCampaignsList(
  client: ApiClient,
  query: ListMarketingCampaignsQuery,
): Promise<PageMarketingCampaignDTO> {
  return client.request<PageMarketingCampaignDTO>('GET', `/api/v1/marketing-campaigns`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmCampaignsCreate(
  client: ApiClient,
  body: CreateMarketingCampaignRequest,
  idempotencyKey?: string,
): Promise<MarketingCampaignDTO> {
  return client.request<MarketingCampaignDTO>('POST', `/api/v1/marketing-campaigns`, {
    body,
    idempotencyKey,
  });
}

export async function crmCampaignsGet(
  client: ApiClient,
  params: { id: string },
): Promise<MarketingCampaignDetailDTO> {
  return client.request<MarketingCampaignDetailDTO>('GET', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmCampaignsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateMarketingCampaignRequest,
): Promise<MarketingCampaignDTO> {
  return client.request<MarketingCampaignDTO>('PATCH', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmCampaignsDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmCampaignsApprove(
  client: ApiClient,
  params: { id: string },
  body: ApproveMarketingCampaignRequest,
): Promise<MarketingCampaignDTO> {
  return client.request<MarketingCampaignDTO>('POST', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}/approve`, {
    body,
  });
}

export async function crmCampaignsSendPreview(
  client: ApiClient,
  params: { id: string },
  body: SendCampaignRequest,
): Promise<SendPreviewResult> {
  return client.request<SendPreviewResult>('POST', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}/send-preview`, {
    body,
  });
}

export async function crmCampaignsSend(
  client: ApiClient,
  params: { id: string },
  body: SendCampaignRequest,
  idempotencyKey?: string,
): Promise<CampaignSendDTO> {
  return client.request<CampaignSendDTO>('POST', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}/send`, {
    body,
    idempotencyKey,
  });
}

export async function crmCampaignSendsList(
  client: ApiClient,
  params: { id: string },
  query: ListCampaignSendsQuery,
): Promise<PageCampaignSendDTO> {
  return client.request<PageCampaignSendDTO>('GET', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}/sends`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmCampaignSendsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CampaignSendDTO> {
  return client.request<CampaignSendDTO>('GET', `/api/v1/campaign-sends/${encodeURIComponent(params.id)}`, {
  });
}

export async function crmCampaignSendsRecipients(
  client: ApiClient,
  params: { id: string },
  query: ListCampaignRecipientsQuery,
): Promise<PageCampaignRecipientDTO> {
  return client.request<PageCampaignRecipientDTO>('GET', `/api/v1/campaign-sends/${encodeURIComponent(params.id)}/recipients`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmCampaignResponsesList(
  client: ApiClient,
  query: ListCampaignResponsesQuery,
): Promise<PageCampaignResponseDTO> {
  return client.request<PageCampaignResponseDTO>('GET', `/api/v1/campaign-responses`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmCampaignResponsesCreate(
  client: ApiClient,
  body: CreateCampaignResponseRequest,
  idempotencyKey?: string,
): Promise<CampaignResponseDTO> {
  return client.request<CampaignResponseDTO>('POST', `/api/v1/campaign-responses`, {
    body,
    idempotencyKey,
  });
}

export async function crmAttributionGet(
  client: ApiClient,
  params: { id: string },
  query: CampaignAttributionQuery,
): Promise<CampaignAttributionReportDTO> {
  return client.request<CampaignAttributionReportDTO>('GET', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}/attribution`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmAttributionRoi(
  client: ApiClient,
  params: { id: string },
  query: CampaignAttributionQuery,
): Promise<CampaignROIDTO> {
  return client.request<CampaignROIDTO>('GET', `/api/v1/marketing-campaigns/${encodeURIComponent(params.id)}/roi`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmAttributionRecord(
  client: ApiClient,
  body: CreateCampaignAttributionRequest,
  idempotencyKey?: string,
): Promise<CampaignAttributionDTO> {
  return client.request<CampaignAttributionDTO>('POST', `/api/v1/campaign-attributions`, {
    body,
    idempotencyKey,
  });
}

export async function crmForecastCategoriesList(
  client: ApiClient,
): Promise<CRMForecastCategoryDTO[]> {
  return client.request<CRMForecastCategoryDTO[]>('GET', `/api/v1/crm-forecast-categories`, {
  });
}

export async function crmForecastCategoriesCreate(
  client: ApiClient,
  body: CreateCRMForecastCategoryRequest,
  idempotencyKey?: string,
): Promise<CRMForecastCategoryDTO> {
  return client.request<CRMForecastCategoryDTO>('POST', `/api/v1/crm-forecast-categories`, {
    body,
    idempotencyKey,
  });
}

export async function crmForecastCategoriesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMForecastCategoryRequest,
): Promise<CRMForecastCategoryDTO> {
  return client.request<CRMForecastCategoryDTO>('PATCH', `/api/v1/crm-forecast-categories/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmCloseReasonsList(
  client: ApiClient,
): Promise<CRMCloseReasonDTO[]> {
  return client.request<CRMCloseReasonDTO[]>('GET', `/api/v1/crm-close-reasons`, {
  });
}

export async function crmCloseReasonsCreate(
  client: ApiClient,
  body: CreateCRMCloseReasonRequest,
  idempotencyKey?: string,
): Promise<CRMCloseReasonDTO> {
  return client.request<CRMCloseReasonDTO>('POST', `/api/v1/crm-close-reasons`, {
    body,
    idempotencyKey,
  });
}

export async function crmCloseReasonsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMCloseReasonRequest,
): Promise<CRMCloseReasonDTO> {
  return client.request<CRMCloseReasonDTO>('PATCH', `/api/v1/crm-close-reasons/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmCompetitorsList(
  client: ApiClient,
): Promise<CRMCompetitorDTO[]> {
  return client.request<CRMCompetitorDTO[]>('GET', `/api/v1/crm-competitors`, {
  });
}

export async function crmCompetitorsCreate(
  client: ApiClient,
  body: CreateCRMCompetitorRequest,
  idempotencyKey?: string,
): Promise<CRMCompetitorDTO> {
  return client.request<CRMCompetitorDTO>('POST', `/api/v1/crm-competitors`, {
    body,
    idempotencyKey,
  });
}

export async function crmCompetitorsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCRMCompetitorRequest,
): Promise<CRMCompetitorDTO> {
  return client.request<CRMCompetitorDTO>('PATCH', `/api/v1/crm-competitors/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmOpportunitiesCompetitorsList(
  client: ApiClient,
  params: { id: string },
): Promise<OpportunityCompetitorDTO[]> {
  return client.request<OpportunityCompetitorDTO[]>('GET', `/api/v1/opportunities/${encodeURIComponent(params.id)}/competitors`, {
  });
}

export async function crmOpportunitiesCompetitorsCreate(
  client: ApiClient,
  params: { id: string },
  body: AttachOpportunityCompetitorRequest,
  idempotencyKey?: string,
): Promise<OpportunityCompetitorDTO> {
  return client.request<OpportunityCompetitorDTO>('POST', `/api/v1/opportunities/${encodeURIComponent(params.id)}/competitors`, {
    body,
    idempotencyKey,
  });
}

export async function crmOpportunitiesCompetitorsDelete(
  client: ApiClient,
  params: { id: string; cid: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/opportunities/${encodeURIComponent(params.id)}/competitors/${encodeURIComponent(params.cid)}`, {
  });
}

export async function crmForecastSnapshotsList(
  client: ApiClient,
  query: ListForecastSnapshotsQuery,
): Promise<PageCRMForecastSnapshotDTO> {
  return client.request<PageCRMForecastSnapshotDTO>('GET', `/api/v1/crm-forecast-snapshots`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmForecastSnapshotsCreate(
  client: ApiClient,
  body: CreateForecastSnapshotRequest,
  idempotencyKey?: string,
): Promise<CRMForecastSnapshotDTO> {
  return client.request<CRMForecastSnapshotDTO>('POST', `/api/v1/crm-forecast-snapshots`, {
    body,
    idempotencyKey,
  });
}

export async function crmForecastSnapshotsAccuracy(
  client: ApiClient,
  query: CrmForecastAccuracyQuery,
): Promise<ForecastAccuracyDTO> {
  return client.request<ForecastAccuracyDTO>('GET', `/api/v1/crm-forecast-accuracy`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmReportsWinLoss(
  client: ApiClient,
  query: DateRangeQuery,
): Promise<WinLossReportDTO> {
  return client.request<WinLossReportDTO>('GET', `/api/v1/crm-win-loss`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmLeadScoringModelsList(
  client: ApiClient,
): Promise<LeadScoreModelDTO[]> {
  return client.request<LeadScoreModelDTO[]>('GET', `/api/v1/lead-score-models`, {
  });
}

export async function crmLeadScoringModelsCreate(
  client: ApiClient,
  body: CreateLeadScoreModelRequest,
  idempotencyKey?: string,
): Promise<LeadScoreModelDTO> {
  return client.request<LeadScoreModelDTO>('POST', `/api/v1/lead-score-models`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadScoringModelsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLeadScoreModelRequest,
): Promise<LeadScoreModelDTO> {
  return client.request<LeadScoreModelDTO>('PATCH', `/api/v1/lead-score-models/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmLeadScoringActivate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<LeadScoreModelDTO> {
  return client.request<LeadScoreModelDTO>('POST', `/api/v1/lead-score-models/${encodeURIComponent(params.id)}/activate`, {
    idempotencyKey,
  });
}

export async function crmLeadScoringRulesList(
  client: ApiClient,
  params: { id: string },
): Promise<LeadScoreRuleDTO[]> {
  return client.request<LeadScoreRuleDTO[]>('GET', `/api/v1/lead-score-models/${encodeURIComponent(params.id)}/rules`, {
  });
}

export async function crmLeadScoringRulesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateLeadScoreRuleRequest,
  idempotencyKey?: string,
): Promise<LeadScoreRuleDTO> {
  return client.request<LeadScoreRuleDTO>('POST', `/api/v1/lead-score-models/${encodeURIComponent(params.id)}/rules`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadScoringRulesUpdate(
  client: ApiClient,
  params: { id: string; ruleId: string },
  body: UpdateLeadScoreRuleRequest,
): Promise<LeadScoreRuleDTO> {
  return client.request<LeadScoreRuleDTO>('PATCH', `/api/v1/lead-score-models/${encodeURIComponent(params.id)}/rules/${encodeURIComponent(params.ruleId)}`, {
    body,
  });
}

export async function crmLeadScoringRulesDelete(
  client: ApiClient,
  params: { id: string; ruleId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/lead-score-models/${encodeURIComponent(params.id)}/rules/${encodeURIComponent(params.ruleId)}`, {
  });
}

export async function crmLeadScoringRescore(
  client: ApiClient,
  params: { id: string },
  body: RescoreLeadRequest,
  idempotencyKey?: string,
): Promise<LeadDTO> {
  return client.request<LeadDTO>('POST', `/api/v1/leads/${encodeURIComponent(params.id)}/rescore`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadScoringRuns(
  client: ApiClient,
  params: { id: string },
  query: ListLeadScoreRunsQuery,
): Promise<PageLeadScoreRunDTO> {
  return client.request<PageLeadScoreRunDTO>('GET', `/api/v1/leads/${encodeURIComponent(params.id)}/score-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmLeadRoutingList(
  client: ApiClient,
): Promise<LeadRoutingRuleDTO[]> {
  return client.request<LeadRoutingRuleDTO[]>('GET', `/api/v1/lead-routing-rules`, {
  });
}

export async function crmLeadRoutingCreate(
  client: ApiClient,
  body: CreateLeadRoutingRuleRequest,
  idempotencyKey?: string,
): Promise<LeadRoutingRuleDTO> {
  return client.request<LeadRoutingRuleDTO>('POST', `/api/v1/lead-routing-rules`, {
    body,
    idempotencyKey,
  });
}

export async function crmLeadRoutingUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLeadRoutingRuleRequest,
): Promise<LeadRoutingRuleDTO> {
  return client.request<LeadRoutingRuleDTO>('PATCH', `/api/v1/lead-routing-rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function crmLeadRoutingDelete(
  client: ApiClient,
  params: { id: string },
  query: CrmDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/lead-routing-rules/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmLeadRoutingRoute(
  client: ApiClient,
  params: { id: string },
  body: RouteLeadRequest,
  idempotencyKey?: string,
): Promise<LeadRoutingResult> {
  return client.request<LeadRoutingResult>('POST', `/api/v1/leads/${encodeURIComponent(params.id)}/route`, {
    body,
    idempotencyKey,
  });
}

export async function crmCaseClockPause(
  client: ApiClient,
  params: { id: string },
  body: PauseCaseRequest,
  idempotencyKey?: string,
): Promise<CaseSLAStateDTO> {
  return client.request<CaseSLAStateDTO>('POST', `/api/v1/crm-cases/${encodeURIComponent(params.id)}/pause`, {
    body,
    idempotencyKey,
  });
}

export async function crmCaseClockResume(
  client: ApiClient,
  params: { id: string },
  body: ResumeCaseRequest,
  idempotencyKey?: string,
): Promise<CaseSLAStateDTO> {
  return client.request<CaseSLAStateDTO>('POST', `/api/v1/crm-cases/${encodeURIComponent(params.id)}/resume`, {
    body,
    idempotencyKey,
  });
}

export async function crmCaseClockGet(
  client: ApiClient,
  params: { id: string },
): Promise<CaseClockTrailDTO> {
  return client.request<CaseClockTrailDTO>('GET', `/api/v1/crm-cases/${encodeURIComponent(params.id)}/clock`, {
  });
}

export async function crmSlaEscalationLevelsGet(
  client: ApiClient,
  params: { id: string },
): Promise<SLAEscalationLevelDTO[]> {
  return client.request<SLAEscalationLevelDTO[]>('GET', `/api/v1/crm-sla-policies/${encodeURIComponent(params.id)}/escalation-levels`, {
  });
}

export async function crmSlaEscalationLevelsSet(
  client: ApiClient,
  params: { id: string },
  body: SetSLAEscalationLevelsRequest,
  idempotencyKey?: string,
): Promise<SLAEscalationLevelDTO[]> {
  return client.request<SLAEscalationLevelDTO[]>('PUT', `/api/v1/crm-sla-policies/${encodeURIComponent(params.id)}/escalation-levels`, {
    body,
    idempotencyKey,
  });
}

export async function crmSlaPredictionsList(
  client: ApiClient,
  query: ListSLABreachForecastsQuery,
): Promise<PageSLABreachForecastDTO> {
  return client.request<PageSLABreachForecastDTO>('GET', `/api/v1/crm-sla/predictions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function crmKbArticlesApplicabilityList(
  client: ApiClient,
  params: { id: string },
): Promise<KBApplicabilityDTO[]> {
  return client.request<KBApplicabilityDTO[]>('GET', `/api/v1/crm-kb-articles/${encodeURIComponent(params.id)}/applicability`, {
  });
}

export async function crmKbArticlesApplicabilitySet(
  client: ApiClient,
  params: { id: string },
  body: SetKBArticleApplicabilityRequest,
  idempotencyKey?: string,
): Promise<KBApplicabilityDTO[]> {
  return client.request<KBApplicabilityDTO[]>('PUT', `/api/v1/crm-kb-articles/${encodeURIComponent(params.id)}/applicability`, {
    body,
    idempotencyKey,
  });
}

export async function crmKbArticlesReview(
  client: ApiClient,
  params: { id: string },
  body: ReviewKBArticleRequest,
  idempotencyKey?: string,
): Promise<KBReviewDTO> {
  return client.request<KBReviewDTO>('POST', `/api/v1/crm-kb-articles/${encodeURIComponent(params.id)}/review`, {
    body,
    idempotencyKey,
  });
}

export async function partyConsentChannelsList(
  client: ApiClient,
  query: ListConsentChannelsQuery,
): Promise<PageConsentChannelDTO> {
  return client.request<PageConsentChannelDTO>('GET', `/api/v1/consent-channels`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyConsentChannelsCreate(
  client: ApiClient,
  body: CreateConsentChannelRequest,
  idempotencyKey?: string,
): Promise<ConsentChannelDTO> {
  return client.request<ConsentChannelDTO>('POST', `/api/v1/consent-channels`, {
    body,
    idempotencyKey,
  });
}

export async function partyConsentChannelsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateConsentChannelRequest,
): Promise<ConsentChannelDTO> {
  return client.request<ConsentChannelDTO>('PATCH', `/api/v1/consent-channels/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyConsentPurposesList(
  client: ApiClient,
  query: ListConsentPurposesQuery,
): Promise<PageConsentPurposeDTO> {
  return client.request<PageConsentPurposeDTO>('GET', `/api/v1/consent-purposes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyConsentPurposesCreate(
  client: ApiClient,
  body: CreateConsentPurposeRequest,
  idempotencyKey?: string,
): Promise<ConsentPurposeDTO> {
  return client.request<ConsentPurposeDTO>('POST', `/api/v1/consent-purposes`, {
    body,
    idempotencyKey,
  });
}

export async function partyConsentPurposesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateConsentPurposeRequest,
): Promise<ConsentPurposeDTO> {
  return client.request<ConsentPurposeDTO>('PATCH', `/api/v1/consent-purposes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyConsentsList(
  client: ApiClient,
  query: ListConsentsQuery,
): Promise<PagePartyConsentDTO> {
  return client.request<PagePartyConsentDTO>('GET', `/api/v1/party-consents`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyConsentsCapture(
  client: ApiClient,
  body: CaptureConsentRequest,
  idempotencyKey?: string,
): Promise<PartyConsentDTO> {
  return client.request<PartyConsentDTO>('POST', `/api/v1/party-consents`, {
    body,
    idempotencyKey,
  });
}

export async function partyConsentsWithdraw(
  client: ApiClient,
  params: { id: string },
  body: WithdrawConsentRequest,
): Promise<PartyConsentDTO> {
  return client.request<PartyConsentDTO>('POST', `/api/v1/party-consents/${encodeURIComponent(params.id)}/withdraw`, {
    body,
  });
}

export async function partyConsentsConfirm(
  client: ApiClient,
  body: ConfirmConsentRequest,
): Promise<PartyConsentDTO> {
  return client.request<PartyConsentDTO>('POST', `/api/v1/party-consents/confirm`, {
    body,
  });
}

export async function partyConsentsMatrix(
  client: ApiClient,
  params: { id: string },
  query: ConsentMatrixQuery,
): Promise<PartyConsentMatrixCellDTO[]> {
  return client.request<PartyConsentMatrixCellDTO[]>('GET', `/api/v1/parties/${encodeURIComponent(params.id)}/consent-matrix`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyConsentsImport(
  client: ApiClient,
  file: File | Blob,
): Promise<ImportConsentsResult> {
  const form = new FormData();
  form.append('file', file);
  return client.request<ImportConsentsResult>('POST', `/api/v1/party-consents/import`, {
    body: form,
  });
}

export async function partyConsentsExportEvidence(
  client: ApiClient,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/party-consents/export-evidence`, {
  });
}

export async function partySuppressionsList(
  client: ApiClient,
  query: ListSuppressionsQuery,
): Promise<PagePartySuppressionDTO> {
  return client.request<PagePartySuppressionDTO>('GET', `/api/v1/party-suppressions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partySuppressionsCreate(
  client: ApiClient,
  body: CreateSuppressionRequest,
  idempotencyKey?: string,
): Promise<PartySuppressionDTO> {
  return client.request<PartySuppressionDTO>('POST', `/api/v1/party-suppressions`, {
    body,
    idempotencyKey,
  });
}

export async function partySuppressionsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseSuppressionRequest,
): Promise<PartySuppressionDTO> {
  return client.request<PartySuppressionDTO>('POST', `/api/v1/party-suppressions/${encodeURIComponent(params.id)}/release`, {
    body,
  });
}

export async function partySuppressionsImport(
  client: ApiClient,
  file: File | Blob,
): Promise<ImportSuppressionsResult> {
  const form = new FormData();
  form.append('file', file);
  return client.request<ImportSuppressionsResult>('POST', `/api/v1/party-suppressions/import`, {
    body: form,
  });
}

export async function partyDsrList(
  client: ApiClient,
  query: ListDSRQuery,
): Promise<PagePartyDSRRequestDTO> {
  return client.request<PagePartyDSRRequestDTO>('GET', `/api/v1/data-subject-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyDsrCreate(
  client: ApiClient,
  body: CreateDSRRequest,
  idempotencyKey?: string,
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('POST', `/api/v1/data-subject-requests`, {
    body,
    idempotencyKey,
  });
}

export async function partyDsrGet(
  client: ApiClient,
  params: { id: string },
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('GET', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}`, {
  });
}

export async function partyDsrUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateDSRRequest,
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('PATCH', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyDsrVerifyIdentity(
  client: ApiClient,
  params: { id: string },
  body: VerifyIdentityRequest,
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/verify-identity`, {
    body,
  });
}

export async function partyDsrExtend(
  client: ApiClient,
  params: { id: string },
  body: ExtendDSRRequest,
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/extend`, {
    body,
  });
}

export async function partyDsrTasksList(
  client: ApiClient,
  params: { id: string },
): Promise<PartyDSRTaskDTO[]> {
  return client.request<PartyDSRTaskDTO[]>('GET', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/tasks`, {
  });
}

export async function partyDsrTasksComplete(
  client: ApiClient,
  params: { id: string; taskId: string },
  body: CompleteDSRTaskRequest,
): Promise<PartyDSRTaskDTO> {
  return client.request<PartyDSRTaskDTO>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/tasks/${encodeURIComponent(params.taskId)}/complete`, {
    body,
  });
}

export async function partyDsrTasksRegenerate(
  client: ApiClient,
  params: { id: string },
): Promise<PartyDSRTaskDTO[]> {
  return client.request<PartyDSRTaskDTO[]>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/regenerate-tasks`, {
  });
}

export async function partyDsrBuildExport(
  client: ApiClient,
  params: { id: string },
): Promise<DSRExportBundle> {
  return client.request<DSRExportBundle>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/export`, {
  });
}

export async function partyDsrExportBundle(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/export/download`, {
  });
}

export async function partyDsrErasurePreview(
  client: ApiClient,
  params: { id: string },
): Promise<DSRErasurePreview> {
  return client.request<DSRErasurePreview>('GET', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/erasure-preview`, {
  });
}

export async function partyDsrErase(
  client: ApiClient,
  params: { id: string },
  body: ExecuteErasureRequest,
  idempotencyKey?: string,
): Promise<DSRErasureReport> {
  return client.request<DSRErasureReport>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/erase`, {
    body,
    idempotencyKey,
  });
}

export async function partyDsrFulfil(
  client: ApiClient,
  params: { id: string },
  body: FulfilDSRRequest,
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/fulfil`, {
    body,
  });
}

export async function partyDsrRefuse(
  client: ApiClient,
  params: { id: string },
  body: RefuseDSRRequest,
): Promise<PartyDSRDetailDTO> {
  return client.request<PartyDSRDetailDTO>('POST', `/api/v1/data-subject-requests/${encodeURIComponent(params.id)}/refuse`, {
    body,
  });
}

export async function partyDsrRunClock(
  client: ApiClient,
  body: RunDSRClockRequest,
): Promise<RunDSRClockResult> {
  return client.request<RunDSRClockResult>('POST', `/api/v1/data-subject-requests/clock/run`, {
    body,
  });
}

export async function partyDsrPoliciesList(
  client: ApiClient,
  query: ListDSRPoliciesQuery,
): Promise<PagePartyDSRPolicyDTO> {
  return client.request<PagePartyDSRPolicyDTO>('GET', `/api/v1/party-dsr-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyDsrPoliciesCreate(
  client: ApiClient,
  body: CreateDSRPolicyRequest,
): Promise<PartyDSRPolicyDTO> {
  return client.request<PartyDSRPolicyDTO>('POST', `/api/v1/party-dsr-policies`, {
    body,
  });
}

export async function partyDsrPoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateDSRPolicyRequest,
): Promise<PartyDSRPolicyDTO> {
  return client.request<PartyDSRPolicyDTO>('PATCH', `/api/v1/party-dsr-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyRetentionRulesList(
  client: ApiClient,
  query: ListRetentionRulesQuery,
): Promise<PagePartyRetentionRuleDTO> {
  return client.request<PagePartyRetentionRuleDTO>('GET', `/api/v1/party-retention-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyRetentionRulesCreate(
  client: ApiClient,
  body: CreateRetentionRuleRequest,
): Promise<PartyRetentionRuleDTO> {
  return client.request<PartyRetentionRuleDTO>('POST', `/api/v1/party-retention-rules`, {
    body,
  });
}

export async function partyRetentionRulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRetentionRuleRequest,
): Promise<PartyRetentionRuleDTO> {
  return client.request<PartyRetentionRuleDTO>('PATCH', `/api/v1/party-retention-rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyRetentionRulesReconcile(
  client: ApiClient,
): Promise<ReconcileRetentionRulesResult> {
  return client.request<ReconcileRetentionRulesResult>('POST', `/api/v1/party-retention-rules/reconcile`, {
  });
}

export async function partyLoyaltyProgramsList(
  client: ApiClient,
  query: ListLoyaltyProgramsQuery,
): Promise<PageLoyaltyProgramDTO> {
  return client.request<PageLoyaltyProgramDTO>('GET', `/api/v1/loyalty-programs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyLoyaltyProgramsCreate(
  client: ApiClient,
  body: CreateLoyaltyProgramRequest,
  idempotencyKey?: string,
): Promise<LoyaltyProgramDTO> {
  return client.request<LoyaltyProgramDTO>('POST', `/api/v1/loyalty-programs`, {
    body,
    idempotencyKey,
  });
}

export async function partyLoyaltyProgramsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LoyaltyProgramDTO> {
  return client.request<LoyaltyProgramDTO>('GET', `/api/v1/loyalty-programs/${encodeURIComponent(params.id)}`, {
  });
}

export async function partyLoyaltyProgramsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLoyaltyProgramRequest,
): Promise<LoyaltyProgramDTO> {
  return client.request<LoyaltyProgramDTO>('PATCH', `/api/v1/loyalty-programs/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyLoyaltyTiersList(
  client: ApiClient,
  params: { id: string },
): Promise<LoyaltyTierDTO[]> {
  return client.request<LoyaltyTierDTO[]>('GET', `/api/v1/loyalty-programs/${encodeURIComponent(params.id)}/tiers`, {
  });
}

export async function partyLoyaltyTiersCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateLoyaltyTierRequest,
  idempotencyKey?: string,
): Promise<LoyaltyTierDTO> {
  return client.request<LoyaltyTierDTO>('POST', `/api/v1/loyalty-programs/${encodeURIComponent(params.id)}/tiers`, {
    body,
    idempotencyKey,
  });
}

export async function partyLoyaltyTiersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLoyaltyTierRequest,
): Promise<LoyaltyTierDTO> {
  return client.request<LoyaltyTierDTO>('PATCH', `/api/v1/loyalty-tiers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyLoyaltyTiersDelete(
  client: ApiClient,
  params: { id: string },
  query: PartyDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/loyalty-tiers/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyLoyaltyMembersList(
  client: ApiClient,
  query: ListLoyaltyMembersQuery,
): Promise<PageLoyaltyMemberDTO> {
  return client.request<PageLoyaltyMemberDTO>('GET', `/api/v1/loyalty-members`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyLoyaltyMembersEnrol(
  client: ApiClient,
  body: EnrolLoyaltyMemberRequest,
  idempotencyKey?: string,
): Promise<LoyaltyMemberDTO> {
  return client.request<LoyaltyMemberDTO>('POST', `/api/v1/loyalty-members`, {
    body,
    idempotencyKey,
  });
}

export async function partyLoyaltyMembersGet(
  client: ApiClient,
  params: { id: string },
): Promise<LoyaltyMemberDTO> {
  return client.request<LoyaltyMemberDTO>('GET', `/api/v1/loyalty-members/${encodeURIComponent(params.id)}`, {
  });
}

export async function partyLoyaltyAccrualsList(
  client: ApiClient,
  params: { id: string },
  query: ListLoyaltyAccrualsQuery,
): Promise<PageLoyaltyAccrualDTO> {
  return client.request<PageLoyaltyAccrualDTO>('GET', `/api/v1/loyalty-members/${encodeURIComponent(params.id)}/accruals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyLoyaltyAccrualsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateLoyaltyAccrualRequest,
  idempotencyKey?: string,
): Promise<LoyaltyAccrualDTO> {
  return client.request<LoyaltyAccrualDTO>('POST', `/api/v1/loyalty-members/${encodeURIComponent(params.id)}/accruals`, {
    body,
    idempotencyKey,
  });
}

export async function partyLoyaltyMembersReviewTier(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ReviewLoyaltyTierResult> {
  return client.request<ReviewLoyaltyTierResult>('POST', `/api/v1/loyalty-members/${encodeURIComponent(params.id)}/review-tier`, {
    idempotencyKey,
  });
}

export async function partyMergesList(
  client: ApiClient,
  query: ListPartyMergesQuery,
): Promise<PagePartyMergeDTO> {
  return client.request<PagePartyMergeDTO>('GET', `/api/v1/party-merges`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyMergesCreate(
  client: ApiClient,
  body: CreatePartyMergeRequest,
  idempotencyKey?: string,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('POST', `/api/v1/party-merges`, {
    body,
    idempotencyKey,
  });
}

export async function partyMergesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('GET', `/api/v1/party-merges/${encodeURIComponent(params.id)}`, {
  });
}

export async function partyMergesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePartyMergeRequest,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('PATCH', `/api/v1/party-merges/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyMergesSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitPartyMergeRequest,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('POST', `/api/v1/party-merges/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function partyMergesApprove(
  client: ApiClient,
  params: { id: string },
  body: ApprovePartyMergeRequest,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('POST', `/api/v1/party-merges/${encodeURIComponent(params.id)}/approve`, {
    body,
  });
}

export async function partyMergesReject(
  client: ApiClient,
  params: { id: string },
  body: RejectPartyMergeRequest,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('POST', `/api/v1/party-merges/${encodeURIComponent(params.id)}/reject`, {
    body,
  });
}

export async function partyMergesCommit(
  client: ApiClient,
  params: { id: string },
  body: CommitPartyMergeRequest,
  idempotencyKey?: string,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('POST', `/api/v1/party-merges/${encodeURIComponent(params.id)}/commit`, {
    body,
    idempotencyKey,
  });
}

export async function partyMergesReverse(
  client: ApiClient,
  params: { id: string },
  body: ReversePartyMergeRequest,
): Promise<ReverseMergeResult> {
  return client.request<ReverseMergeResult>('POST', `/api/v1/party-merges/${encodeURIComponent(params.id)}/reverse`, {
    body,
  });
}

export async function partyMergesCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelPartyMergeRequest,
): Promise<PartyMergeDetailDTO> {
  return client.request<PartyMergeDetailDTO>('POST', `/api/v1/party-merges/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function partyMergeSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<PartyMergeSettingsDTO> {
  return client.request<PartyMergeSettingsDTO>('GET', `/api/v1/party-merge-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function partyMergeSettingsSet(
  client: ApiClient,
  params: { companyId: string },
  body: SetPartyMergeSettingsRequest,
): Promise<PartyMergeSettingsDTO> {
  return client.request<PartyMergeSettingsDTO>('PUT', `/api/v1/party-merge-settings/${encodeURIComponent(params.companyId)}`, {
    body,
  });
}

export async function partyPartiesList(
  client: ApiClient,
  query: ListPartiesQuery,
): Promise<PagePartyDTO> {
  return client.request<PagePartyDTO>('GET', `/api/v1/parties`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyPartiesCreate(
  client: ApiClient,
  body: CreatePartyRequest,
  idempotencyKey?: string,
): Promise<PartyDTO> {
  return client.request<PartyDTO>('POST', `/api/v1/parties`, {
    body,
    idempotencyKey,
  });
}

export async function partyPartiesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PartyDetailDTO> {
  return client.request<PartyDetailDTO>('GET', `/api/v1/parties/${encodeURIComponent(params.id)}`, {
  });
}

export async function partyPartiesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePartyRequest,
): Promise<PartyDTO> {
  return client.request<PartyDTO>('PATCH', `/api/v1/parties/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function partyPartiesDelete(
  client: ApiClient,
  params: { id: string },
  query: PartyDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/parties/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyPartiesResolve(
  client: ApiClient,
  body: ResolveInput,
): Promise<ResolveResult> {
  return client.request<ResolveResult>('POST', `/api/v1/parties/resolve`, {
    body,
  });
}

export async function partyLinksList(
  client: ApiClient,
  params: { id: string },
): Promise<PartyLinkDTO[]> {
  return client.request<PartyLinkDTO[]>('GET', `/api/v1/parties/${encodeURIComponent(params.id)}/links`, {
  });
}

export async function partyLinksCreate(
  client: ApiClient,
  params: { id: string },
  body: CreatePartyLinkRequest,
  idempotencyKey?: string,
): Promise<PartyLinkDTO> {
  return client.request<PartyLinkDTO>('POST', `/api/v1/parties/${encodeURIComponent(params.id)}/links`, {
    body,
    idempotencyKey,
  });
}

export async function partyLinksDelete(
  client: ApiClient,
  params: { id: string; linkId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/parties/${encodeURIComponent(params.id)}/links/${encodeURIComponent(params.linkId)}`, {
  });
}

export async function partyLinksSetPrimary(
  client: ApiClient,
  params: { id: string; linkId: string },
): Promise<PartyLinkDTO> {
  return client.request<PartyLinkDTO>('POST', `/api/v1/parties/${encodeURIComponent(params.id)}/links/${encodeURIComponent(params.linkId)}/primary`, {
  });
}

export async function partyLinkTypesList(
  client: ApiClient,
): Promise<PartyLinkTypeDTO[]> {
  return client.request<PartyLinkTypeDTO[]>('GET', `/api/v1/party-link-types`, {
  });
}

export async function partyIdentifiersList(
  client: ApiClient,
  params: { id: string },
): Promise<PartyIdentifierDTO[]> {
  return client.request<PartyIdentifierDTO[]>('GET', `/api/v1/parties/${encodeURIComponent(params.id)}/identifiers`, {
  });
}

export async function partyIdentifiersCreate(
  client: ApiClient,
  params: { id: string },
  body: CreatePartyIdentifierRequest,
  idempotencyKey?: string,
): Promise<PartyIdentifierDTO> {
  return client.request<PartyIdentifierDTO>('POST', `/api/v1/parties/${encodeURIComponent(params.id)}/identifiers`, {
    body,
    idempotencyKey,
  });
}

export async function partyIdentifiersUpdate(
  client: ApiClient,
  params: { id: string; idfId: string },
  body: UpdatePartyIdentifierRequest,
): Promise<PartyIdentifierDTO> {
  return client.request<PartyIdentifierDTO>('PATCH', `/api/v1/parties/${encodeURIComponent(params.id)}/identifiers/${encodeURIComponent(params.idfId)}`, {
    body,
  });
}

export async function partyIdentifiersDelete(
  client: ApiClient,
  params: { id: string; idfId: string },
  query: PartyDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/parties/${encodeURIComponent(params.id)}/identifiers/${encodeURIComponent(params.idfId)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyIdentifiersReveal(
  client: ApiClient,
  params: { id: string; idfId: string },
  body: RevealIdentifierRequest,
): Promise<RevealIdentifierResult> {
  return client.request<RevealIdentifierResult>('POST', `/api/v1/parties/${encodeURIComponent(params.id)}/identifiers/${encodeURIComponent(params.idfId)}/reveal`, {
    body,
  });
}

export async function partyMatchesList(
  client: ApiClient,
  query: ListPartyMatchCandidatesQuery,
): Promise<PagePartyMatchCandidateDTO> {
  return client.request<PagePartyMatchCandidateDTO>('GET', `/api/v1/party-match-candidates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function partyMatchesDismiss(
  client: ApiClient,
  params: { id: string },
  body: DismissPartyMatchRequest,
): Promise<PartyMatchCandidateDTO> {
  return client.request<PartyMatchCandidateDTO>('POST', `/api/v1/party-match-candidates/${encodeURIComponent(params.id)}/dismiss`, {
    body,
  });
}

export async function partyMatchesRun(
  client: ApiClient,
  body: RunPartyMatchRequest,
  idempotencyKey?: string,
): Promise<RunPartyMatchResult> {
  return client.request<RunPartyMatchResult>('POST', `/api/v1/party-match-candidates/run`, {
    body,
    idempotencyKey,
  });
}

export async function partyProfileGet(
  client: ApiClient,
  params: { id: string },
): Promise<PartyProfileDTO> {
  return client.request<PartyProfileDTO>('GET', `/api/v1/parties/${encodeURIComponent(params.id)}/profile`, {
  });
}

export async function partyProfileTimeline(
  client: ApiClient,
  params: { id: string },
  query: ListPartyTimelineQuery,
): Promise<PagePartyTimelineEntryDTO> {
  return client.request<PagePartyTimelineEntryDTO>('GET', `/api/v1/parties/${encodeURIComponent(params.id)}/timeline`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesSettingsGet(
  client: ApiClient,
  query: SalesSettingsQuery,
): Promise<SalesSettingsDTO> {
  return client.request<SalesSettingsDTO>('GET', `/api/v1/sales-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesSettingsUpsert(
  client: ApiClient,
  body: SalesUpsertSettingsRequest,
  idempotencyKey?: string,
): Promise<SalesSettingsDTO> {
  return client.request<SalesSettingsDTO>('PUT', `/api/v1/sales-settings`, {
    body,
    idempotencyKey,
  });
}

export async function salesQuotesList(
  client: ApiClient,
  query: ListQuotesQuery,
): Promise<PageQuoteDTO> {
  return client.request<PageQuoteDTO>('GET', `/api/v1/sales-quotes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesQuotesCreate(
  client: ApiClient,
  body: CreateQuoteRequest,
  idempotencyKey?: string,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/api/v1/sales-quotes`, {
    body,
    idempotencyKey,
  });
}

export async function salesQuotesGet(
  client: ApiClient,
  params: { id: string },
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('GET', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesQuotesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateQuoteRequest,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('PATCH', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function salesQuotesDelete(
  client: ApiClient,
  params: { id: string },
  query: SalesDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesQuotesSend(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/send`, {
    idempotencyKey,
  });
}

export async function salesQuotesAccept(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/accept`, {
    idempotencyKey,
  });
}

export async function salesQuotesReject(
  client: ApiClient,
  params: { id: string },
  body: RejectQuoteRequest,
  idempotencyKey?: string,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function salesQuotesCancel(
  client: ApiClient,
  params: { id: string },
  body: SalesCancelRequest,
  idempotencyKey?: string,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function salesQuotesConvert(
  client: ApiClient,
  params: { id: string },
  body: ConvertQuoteRequest,
  idempotencyKey?: string,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/convert`, {
    body,
    idempotencyKey,
  });
}

export async function salesOrdersList(
  client: ApiClient,
  query: ListOrdersQuery,
): Promise<PageOrderDTO> {
  return client.request<PageOrderDTO>('GET', `/api/v1/sales-orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesOrdersCreate(
  client: ApiClient,
  body: CreateOrderRequest,
  idempotencyKey?: string,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/api/v1/sales-orders`, {
    body,
    idempotencyKey,
  });
}

export async function salesOrdersGet(
  client: ApiClient,
  params: { id: string },
): Promise<OrderDTO> {
  return client.request<OrderDTO>('GET', `/api/v1/sales-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesOrdersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateOrderRequest,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('PATCH', `/api/v1/sales-orders/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function salesOrdersConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmOrderRequest,
  idempotencyKey?: string,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function salesOrdersCancel(
  client: ApiClient,
  params: { id: string },
  body: SalesCancelRequest,
  idempotencyKey?: string,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function salesShipmentsList(
  client: ApiClient,
  query: ListShipmentsQuery,
): Promise<PageShipmentDTO> {
  return client.request<PageShipmentDTO>('GET', `/api/v1/shipments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesShipmentsCreate(
  client: ApiClient,
  body: CreateShipmentRequest,
  idempotencyKey?: string,
): Promise<ShipmentDTO> {
  return client.request<ShipmentDTO>('POST', `/api/v1/shipments`, {
    body,
    idempotencyKey,
  });
}

export async function salesShipmentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ShipmentDTO> {
  return client.request<ShipmentDTO>('GET', `/api/v1/shipments/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesShipmentsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseShipmentRequest,
  idempotencyKey?: string,
): Promise<ShipmentDTO> {
  return client.request<ShipmentDTO>('POST', `/api/v1/shipments/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function salesShipmentsInvoice(
  client: ApiClient,
  params: { id: string },
  body: CreateInvoiceFromShipmentRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/shipments/${encodeURIComponent(params.id)}/invoice`, {
    body,
    idempotencyKey,
  });
}

export async function salesBackordersList(
  client: ApiClient,
  query: ListBackordersQuery,
): Promise<PageBackorderDTO> {
  return client.request<PageBackorderDTO>('GET', `/api/v1/backorders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesReturnsList(
  client: ApiClient,
  query: SalesListReturnsQuery,
): Promise<PageReturnDTO> {
  return client.request<PageReturnDTO>('GET', `/api/v1/sales-returns`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesReturnsCreate(
  client: ApiClient,
  body: CreateReturnRequest,
  idempotencyKey?: string,
): Promise<ReturnDTO> {
  return client.request<ReturnDTO>('POST', `/api/v1/sales-returns`, {
    body,
    idempotencyKey,
  });
}

export async function salesReturnsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ReturnDTO> {
  return client.request<ReturnDTO>('GET', `/api/v1/sales-returns/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesSubscriptionPlansList(
  client: ApiClient,
  query: ListSubscriptionPlansQuery,
): Promise<PageSubscriptionPlanDTO> {
  return client.request<PageSubscriptionPlanDTO>('GET', `/api/v1/subscription-plans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesSubscriptionPlansCreate(
  client: ApiClient,
  body: CreateSubscriptionPlanRequest,
  idempotencyKey?: string,
): Promise<SubscriptionPlanDTO> {
  return client.request<SubscriptionPlanDTO>('POST', `/api/v1/subscription-plans`, {
    body,
    idempotencyKey,
  });
}

export async function salesSubscriptionPlansGet(
  client: ApiClient,
  params: { id: string },
): Promise<SubscriptionPlanDTO> {
  return client.request<SubscriptionPlanDTO>('GET', `/api/v1/subscription-plans/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesSubscriptionsList(
  client: ApiClient,
  query: ListSubscriptionsQuery,
): Promise<PageSubscriptionDTO2> {
  return client.request<PageSubscriptionDTO2>('GET', `/api/v1/subscriptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesSubscriptionsCreate(
  client: ApiClient,
  body: SalesCreateSubscriptionRequest,
  idempotencyKey?: string,
): Promise<SalesSubscriptionDTO> {
  return client.request<SalesSubscriptionDTO>('POST', `/api/v1/subscriptions`, {
    body,
    idempotencyKey,
  });
}

export async function salesSubscriptionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<SalesSubscriptionDTO> {
  return client.request<SalesSubscriptionDTO>('GET', `/api/v1/subscriptions/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesSubscriptionsChange(
  client: ApiClient,
  params: { id: string },
  body: ChangeSubscriptionRequest,
  idempotencyKey?: string,
): Promise<SalesSubscriptionDTO> {
  return client.request<SalesSubscriptionDTO>('POST', `/api/v1/subscriptions/${encodeURIComponent(params.id)}/change`, {
    body,
    idempotencyKey,
  });
}

export async function salesSubscriptionsRepriceReview(
  client: ApiClient,
  params: { id: string },
  body: RepriceReviewRequest,
  idempotencyKey?: string,
): Promise<RepriceReviewDTO> {
  return client.request<RepriceReviewDTO>('POST', `/api/v1/subscriptions/${encodeURIComponent(params.id)}/reprice-review`, {
    body,
    idempotencyKey,
  });
}

export async function salesSubscriptionsPause(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<SalesSubscriptionDTO> {
  return client.request<SalesSubscriptionDTO>('POST', `/api/v1/subscriptions/${encodeURIComponent(params.id)}/pause`, {
    idempotencyKey,
  });
}

export async function salesSubscriptionsResume(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<SalesSubscriptionDTO> {
  return client.request<SalesSubscriptionDTO>('POST', `/api/v1/subscriptions/${encodeURIComponent(params.id)}/resume`, {
    idempotencyKey,
  });
}

export async function salesSubscriptionsCancel(
  client: ApiClient,
  params: { id: string },
  body: SalesCancelRequest,
  idempotencyKey?: string,
): Promise<SalesSubscriptionDTO> {
  return client.request<SalesSubscriptionDTO>('POST', `/api/v1/subscriptions/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function salesRevrecList(
  client: ApiClient,
  query: ListRevRecSchedulesQuery,
): Promise<PageRevRecScheduleDTO> {
  return client.request<PageRevRecScheduleDTO>('GET', `/api/v1/revrec-schedules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesRevrecGet(
  client: ApiClient,
  params: { id: string },
): Promise<RevRecScheduleDTO> {
  return client.request<RevRecScheduleDTO>('GET', `/api/v1/revrec-schedules/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesReportsPipeline(
  client: ApiClient,
  query: SalesPipelineQuery,
): Promise<SalesPipelineReport> {
  return client.request<SalesPipelineReport>('GET', `/api/v1/reports/pipeline`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesReportsWinRate(
  client: ApiClient,
  query: WinRateQuery,
): Promise<SalesWinRateReport> {
  return client.request<SalesWinRateReport>('GET', `/api/v1/reports/win-rate`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesReportsForecast(
  client: ApiClient,
  query: ForecastQuery,
): Promise<ForecastReport> {
  return client.request<ForecastReport>('GET', `/api/v1/reports/forecast`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesReportsSalesBy(
  client: ApiClient,
  query: SalesByQuery,
): Promise<SalesByReport> {
  return client.request<SalesByReport>('GET', `/api/v1/reports/sales-by`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesReportsMrr(
  client: ApiClient,
  query: MRRQuery,
): Promise<MRRReport> {
  return client.request<MRRReport>('GET', `/api/v1/reports/mrr`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesQuotesRequestPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function salesQuotesPdfStatus(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function salesOrdersAmend(
  client: ApiClient,
  params: { id: string },
  body: AmendOrderRequest,
): Promise<OrderAmendmentDTO> {
  return client.request<OrderAmendmentDTO>('POST', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/amend`, {
    body,
  });
}

export async function salesOrdersAmendments(
  client: ApiClient,
  params: { id: string },
  query: ListAmendmentsQuery,
): Promise<PageOrderAmendmentDTO> {
  return client.request<PageOrderAmendmentDTO>('GET', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/amendments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesOrdersRepromise(
  client: ApiClient,
  params: { id: string },
  body: RepromiseRequest,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/repromise`, {
    body,
  });
}

export async function salesOrdersPromise(
  client: ApiClient,
  params: { id: string },
): Promise<PromiseDTO> {
  return client.request<PromiseDTO>('GET', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/promise`, {
  });
}

export async function salesQuotesRevise(
  client: ApiClient,
  params: { id: string },
  body: ReviseQuoteRequest,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/revise`, {
    body,
  });
}

export async function salesQuotesRevisions(
  client: ApiClient,
  params: { id: string },
  query: ListRevisionsQuery,
): Promise<PageQuoteRevisionDTO> {
  return client.request<PageQuoteRevisionDTO>('GET', `/api/v1/sales-quotes/${encodeURIComponent(params.id)}/revisions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesDiscountPoliciesList(
  client: ApiClient,
  query: ListDiscountPoliciesQuery,
): Promise<PageDiscountPolicyDTO> {
  return client.request<PageDiscountPolicyDTO>('GET', `/api/v1/sales-discount-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesDiscountPoliciesCreate(
  client: ApiClient,
  body: DiscountPolicyRequest,
  idempotencyKey?: string,
): Promise<DiscountPolicyDTO> {
  return client.request<DiscountPolicyDTO>('POST', `/api/v1/sales-discount-policies`, {
    body,
    idempotencyKey,
  });
}

export async function salesDiscountPoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: DiscountPolicyRequest,
): Promise<DiscountPolicyDTO> {
  return client.request<DiscountPolicyDTO>('PATCH', `/api/v1/sales-discount-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function salesDiscountPoliciesSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeDiscountPolicyRequest,
): Promise<DiscountPolicyDTO> {
  return client.request<DiscountPolicyDTO>('POST', `/api/v1/sales-discount-policies/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function salesReportsPromiseBreach(
  client: ApiClient,
  query: PromiseBreachQuery,
): Promise<PromiseBreachReportDTO> {
  return client.request<PromiseBreachReportDTO>('GET', `/api/v1/reports/promise-breach`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesAllocationsList(
  client: ApiClient,
  query: ListAllocationsQuery,
): Promise<PageStockAllocationDTO> {
  return client.request<PageStockAllocationDTO>('GET', `/api/v1/sales-allocations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesAllocationsAllocate(
  client: ApiClient,
  params: { id: string },
  body: AllocateStockRequest,
  idempotencyKey?: string,
): Promise<StockAllocationDTO[]> {
  return client.request<StockAllocationDTO[]>('POST', `/api/v1/sales-orders/${encodeURIComponent(params.id)}/allocate`, {
    body,
    idempotencyKey,
  });
}

export async function salesAllocationsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseAllocationRequest,
): Promise<StockAllocationDTO> {
  return client.request<StockAllocationDTO>('POST', `/api/v1/sales-allocations/${encodeURIComponent(params.id)}/release`, {
    body,
  });
}

export async function salesPicksList(
  client: ApiClient,
  query: ListPicksQuery,
): Promise<PagePickDTO> {
  return client.request<PagePickDTO>('GET', `/api/v1/sales-picks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesPicksCreate(
  client: ApiClient,
  body: CreatePickRequest,
  idempotencyKey?: string,
): Promise<PickDTO> {
  return client.request<PickDTO>('POST', `/api/v1/sales-picks`, {
    body,
    idempotencyKey,
  });
}

export async function salesPicksGet(
  client: ApiClient,
  params: { id: string },
): Promise<PickDTO> {
  return client.request<PickDTO>('GET', `/api/v1/sales-picks/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesPicksRecord(
  client: ApiClient,
  params: { id: string },
  body: RecordPickRequest,
): Promise<PickDTO> {
  return client.request<PickDTO>('POST', `/api/v1/sales-picks/${encodeURIComponent(params.id)}/record`, {
    body,
  });
}

export async function salesPicksComplete(
  client: ApiClient,
  params: { id: string },
  body: CompletePickRequest,
): Promise<PickDTO> {
  return client.request<PickDTO>('POST', `/api/v1/sales-picks/${encodeURIComponent(params.id)}/complete`, {
    body,
  });
}

export async function salesShipmentsDespatch(
  client: ApiClient,
  params: { id: string },
  body: DespatchRequest,
): Promise<ShipmentDTO> {
  return client.request<ShipmentDTO>('POST', `/api/v1/shipments/${encodeURIComponent(params.id)}/despatch`, {
    body,
  });
}

export async function salesShipmentsDeliver(
  client: ApiClient,
  params: { id: string },
  body: PODRequest,
): Promise<ShipmentDTO> {
  return client.request<ShipmentDTO>('POST', `/api/v1/shipments/${encodeURIComponent(params.id)}/deliver`, {
    body,
  });
}

export async function salesReturnsAuthorise(
  client: ApiClient,
  params: { id: string },
  body: AuthoriseReturnRequest,
): Promise<ReturnDTO> {
  return client.request<ReturnDTO>('POST', `/api/v1/sales-returns/${encodeURIComponent(params.id)}/authorise`, {
    body,
  });
}

export async function salesReturnsReceive(
  client: ApiClient,
  params: { id: string },
  body: ReceiveReturnRequest,
): Promise<ReturnDTO> {
  return client.request<ReturnDTO>('POST', `/api/v1/sales-returns/${encodeURIComponent(params.id)}/receive`, {
    body,
  });
}

export async function salesReturnsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectReturnRequest,
): Promise<ReturnDTO> {
  return client.request<ReturnDTO>('POST', `/api/v1/sales-returns/${encodeURIComponent(params.id)}/reject`, {
    body,
  });
}

export async function salesReturnsPost(
  client: ApiClient,
  params: { id: string },
  body: PostReturnRequest,
): Promise<ReturnDTO> {
  return client.request<ReturnDTO>('POST', `/api/v1/sales-returns/${encodeURIComponent(params.id)}/post`, {
    body,
  });
}

export async function salesObligationsList(
  client: ApiClient,
  query: ListObligationsQuery,
): Promise<PageObligationDTO> {
  return client.request<PageObligationDTO>('GET', `/api/v1/sales-obligations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesObligationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ObligationDetailDTO> {
  return client.request<ObligationDetailDTO>('GET', `/api/v1/sales-obligations/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesObligationsSatisfy(
  client: ApiClient,
  params: { id: string },
  body: SatisfyRequest,
): Promise<ObligationDTO> {
  return client.request<ObligationDTO>('POST', `/api/v1/sales-obligations/${encodeURIComponent(params.id)}/satisfy`, {
    body,
  });
}

export async function salesObligationsCertifyMilestone(
  client: ApiClient,
  params: { id: string; seq: string },
  body: CertifyRequest,
): Promise<ObligationDetailDTO> {
  return client.request<ObligationDetailDTO>('POST', `/api/v1/sales-obligations/${encodeURIComponent(params.id)}/milestones/${encodeURIComponent(params.seq)}/certify`, {
    body,
  });
}

export async function salesSspList(
  client: ApiClient,
  query: ListSSPQuery,
): Promise<PageSSPDTO> {
  return client.request<PageSSPDTO>('GET', `/api/v1/sales-ssp`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesSspCreate(
  client: ApiClient,
  body: SSPRequest,
  idempotencyKey?: string,
): Promise<SSPDTO> {
  return client.request<SSPDTO>('POST', `/api/v1/sales-ssp`, {
    body,
    idempotencyKey,
  });
}

export async function salesSspSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeSSPRequest,
): Promise<SSPDTO> {
  return client.request<SSPDTO>('POST', `/api/v1/sales-ssp/${encodeURIComponent(params.id)}/supersede`, {
    body,
  });
}

export async function salesAgreementsList(
  client: ApiClient,
  query: ListAgreementsQuery,
): Promise<PageTradeAgreementDTO> {
  return client.request<PageTradeAgreementDTO>('GET', `/api/v1/trade-agreements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesAgreementsCreate(
  client: ApiClient,
  body: TradeAgreementRequest,
): Promise<TradeAgreementDTO> {
  return client.request<TradeAgreementDTO>('POST', `/api/v1/trade-agreements`, {
    body,
  });
}

export async function salesAgreementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TradeAgreementDTO> {
  return client.request<TradeAgreementDTO>('GET', `/api/v1/trade-agreements/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesAgreementsUpdate(
  client: ApiClient,
  params: { id: string },
  body: TradeAgreementRequest,
): Promise<TradeAgreementDTO> {
  return client.request<TradeAgreementDTO>('PATCH', `/api/v1/trade-agreements/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function salesAgreementsSetLines(
  client: ApiClient,
  params: { id: string },
  body: SetAgreementLinesRequest,
): Promise<TradeAgreementDTO> {
  return client.request<TradeAgreementDTO>('PUT', `/api/v1/trade-agreements/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function salesAgreementsActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateAgreementRequest,
): Promise<TradeAgreementDTO> {
  return client.request<TradeAgreementDTO>('POST', `/api/v1/trade-agreements/${encodeURIComponent(params.id)}/activate`, {
    body,
  });
}

export async function salesRebateProgramsList(
  client: ApiClient,
  query: ListRebateProgramsQuery,
): Promise<PageRebateProgramDTO> {
  return client.request<PageRebateProgramDTO>('GET', `/api/v1/rebate-programs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesRebateProgramsCreate(
  client: ApiClient,
  body: RebateProgramRequest,
): Promise<RebateProgramDTO> {
  return client.request<RebateProgramDTO>('POST', `/api/v1/rebate-programs`, {
    body,
  });
}

export async function salesRebateProgramsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RebateProgramDTO> {
  return client.request<RebateProgramDTO>('GET', `/api/v1/rebate-programs/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesRebateProgramsUpdate(
  client: ApiClient,
  params: { id: string },
  body: RebateProgramRequest,
): Promise<RebateProgramDTO> {
  return client.request<RebateProgramDTO>('PATCH', `/api/v1/rebate-programs/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function salesRebateProgramsSetTiers(
  client: ApiClient,
  params: { id: string },
  body: SetTiersRequest,
): Promise<RebateProgramDTO> {
  return client.request<RebateProgramDTO>('PUT', `/api/v1/rebate-programs/${encodeURIComponent(params.id)}/tiers`, {
    body,
  });
}

export async function salesRebateAccrualsList(
  client: ApiClient,
  query: ListAccrualsQuery,
): Promise<PageRebateAccrualDTO> {
  return client.request<PageRebateAccrualDTO>('GET', `/api/v1/rebate-accruals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesRebateAccrualsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RebateAccrualDetailDTO> {
  return client.request<RebateAccrualDetailDTO>('GET', `/api/v1/rebate-accruals/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesRebateAccrualsClose(
  client: ApiClient,
  params: { id: string },
  body: CloseAccrualRequest,
): Promise<RebateAccrualDTO> {
  return client.request<RebateAccrualDTO>('POST', `/api/v1/rebate-accruals/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function salesRebateAccrualsSettle(
  client: ApiClient,
  params: { id: string },
  body: SettleAccrualRequest,
): Promise<RebateSettlementDTO> {
  return client.request<RebateSettlementDTO>('POST', `/api/v1/rebate-accruals/${encodeURIComponent(params.id)}/settle`, {
    body,
  });
}

export async function salesContractsList(
  client: ApiClient,
  query: ListContractsQuery,
): Promise<PageCustomerContractDTO> {
  return client.request<PageCustomerContractDTO>('GET', `/api/v1/customer-contracts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function salesContractsCreate(
  client: ApiClient,
  body: CustomerContractRequest,
): Promise<CustomerContractDTO> {
  return client.request<CustomerContractDTO>('POST', `/api/v1/customer-contracts`, {
    body,
  });
}

export async function salesContractsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CustomerContractDetailDTO> {
  return client.request<CustomerContractDetailDTO>('GET', `/api/v1/customer-contracts/${encodeURIComponent(params.id)}`, {
  });
}

export async function salesContractsUpdate(
  client: ApiClient,
  params: { id: string },
  body: CustomerContractRequest,
): Promise<CustomerContractDTO> {
  return client.request<CustomerContractDTO>('PATCH', `/api/v1/customer-contracts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function salesContractsSetObligations(
  client: ApiClient,
  params: { id: string },
  body: SetObligationsRequest,
): Promise<CustomerContractDetailDTO> {
  return client.request<CustomerContractDetailDTO>('PUT', `/api/v1/customer-contracts/${encodeURIComponent(params.id)}/obligations`, {
    body,
  });
}

export async function salesContractsActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateContractRequest,
): Promise<CustomerContractDTO> {
  return client.request<CustomerContractDTO>('POST', `/api/v1/customer-contracts/${encodeURIComponent(params.id)}/activate`, {
    body,
  });
}

export async function pricingResolve(
  client: ApiClient,
  query: ResolvePriceQuery,
): Promise<PriceResolutionDTO> {
  return client.request<PriceResolutionDTO>('GET', `/api/v1/pricing/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingResolveBatch(
  client: ApiClient,
  body: ResolveBatchRequest,
): Promise<ResolveBatchResponse> {
  return client.request<ResolveBatchResponse>('POST', `/api/v1/pricing/resolve-batch`, {
    body,
  });
}

export async function pricingExplanationsList(
  client: ApiClient,
  query: ListExplanationsQuery,
): Promise<PagePriceExplanationSummaryDTO> {
  return client.request<PagePriceExplanationSummaryDTO>('GET', `/api/v1/pricing/explanations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingExplanationsGet(
  client: ApiClient,
  params: { docType: string; docId: string; lineNo: string },
  query: GetExplanationQuery,
): Promise<PriceExplanationDTO> {
  return client.request<PriceExplanationDTO>('GET', `/api/v1/pricing/explanations/${encodeURIComponent(params.docType)}/${encodeURIComponent(params.docId)}/${encodeURIComponent(params.lineNo)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingExplanationsExport(
  client: ApiClient,
  query: ExportExplanationsQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/pricing/explanations/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingPrecedenceList(
  client: ApiClient,
  query: ListPrecedenceQuery,
): Promise<PagePrecedenceVersionDTO> {
  return client.request<PagePrecedenceVersionDTO>('GET', `/api/v1/pricing/precedence-versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingPrecedenceCreate(
  client: ApiClient,
  body: CreatePrecedenceRequest,
  idempotencyKey?: string,
): Promise<PrecedenceVersionDetailDTO> {
  return client.request<PrecedenceVersionDetailDTO>('POST', `/api/v1/pricing/precedence-versions`, {
    body,
    idempotencyKey,
  });
}

export async function pricingPrecedenceGet(
  client: ApiClient,
  params: { id: string },
): Promise<PrecedenceVersionDetailDTO> {
  return client.request<PrecedenceVersionDetailDTO>('GET', `/api/v1/pricing/precedence-versions/${encodeURIComponent(params.id)}`, {
  });
}

export async function pricingPrecedenceSetEntries(
  client: ApiClient,
  params: { id: string },
  body: SetPrecedenceEntriesRequest,
): Promise<PrecedenceVersionDetailDTO> {
  return client.request<PrecedenceVersionDetailDTO>('PUT', `/api/v1/pricing/precedence-versions/${encodeURIComponent(params.id)}/entries`, {
    body,
  });
}

export async function pricingPrecedenceSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitPrecedenceRequest,
): Promise<PrecedenceVersionDTO> {
  return client.request<PrecedenceVersionDTO>('POST', `/api/v1/pricing/precedence-versions/${encodeURIComponent(params.id)}/submit`, {
    body,
  });
}

export async function pricingPrecedenceActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivatePrecedenceRequest,
  idempotencyKey?: string,
): Promise<PrecedenceVersionDTO> {
  return client.request<PrecedenceVersionDTO>('POST', `/api/v1/pricing/precedence-versions/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function pricingPrecedenceSimulate(
  client: ApiClient,
  params: { id: string },
  body: SimulatePrecedenceRequest,
): Promise<SimulationReportDTO> {
  return client.request<SimulationReportDTO>('POST', `/api/v1/pricing/precedence-versions/${encodeURIComponent(params.id)}/simulate`, {
    body,
  });
}

export async function pricingPriceListsList(
  client: ApiClient,
  query: ListPriceListsQuery,
): Promise<PagePriceListDTO> {
  return client.request<PagePriceListDTO>('GET', `/api/v1/pricing/price-lists`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingPriceListsCreate(
  client: ApiClient,
  body: CreatePriceListRequest,
  idempotencyKey?: string,
): Promise<PriceListDTO> {
  return client.request<PriceListDTO>('POST', `/api/v1/pricing/price-lists`, {
    body,
    idempotencyKey,
  });
}

export async function pricingPriceListsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PriceListDTO> {
  return client.request<PriceListDTO>('GET', `/api/v1/pricing/price-lists/${encodeURIComponent(params.id)}`, {
  });
}

export async function pricingPriceListsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePriceListRequest,
): Promise<PriceListDTO> {
  return client.request<PriceListDTO>('PATCH', `/api/v1/pricing/price-lists/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pricingPromotionsList(
  client: ApiClient,
  query: ListPromotionsQuery,
): Promise<PagePromotionDTO> {
  return client.request<PagePromotionDTO>('GET', `/api/v1/pricing/promotions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingPromotionsCreate(
  client: ApiClient,
  body: CreatePromotionRequest,
  idempotencyKey?: string,
): Promise<PromotionDTO> {
  return client.request<PromotionDTO>('POST', `/api/v1/pricing/promotions`, {
    body,
    idempotencyKey,
  });
}

export async function pricingPromotionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PromotionDTO> {
  return client.request<PromotionDTO>('GET', `/api/v1/pricing/promotions/${encodeURIComponent(params.id)}`, {
  });
}

export async function pricingPromotionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePromotionRequest,
): Promise<PromotionDTO> {
  return client.request<PromotionDTO>('PATCH', `/api/v1/pricing/promotions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pricingItemSettingsUpsert(
  client: ApiClient,
  body: UpsertItemSettingRequest,
  idempotencyKey?: string,
): Promise<ItemSettingDTO> {
  return client.request<ItemSettingDTO>('PUT', `/api/v1/pricing/item-settings`, {
    body,
    idempotencyKey,
  });
}

export async function pricingItemSettingsGet(
  client: ApiClient,
  params: { companyId: string; itemId: string },
): Promise<ItemSettingDTO> {
  return client.request<ItemSettingDTO>('GET', `/api/v1/pricing/item-settings/${encodeURIComponent(params.companyId)}/${encodeURIComponent(params.itemId)}`, {
  });
}

export async function pricingCategoriesList(
  client: ApiClient,
  query: ListCategoriesQuery,
): Promise<PagePricingCategoryDTO> {
  return client.request<PagePricingCategoryDTO>('GET', `/api/v1/pricing/categories`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingCategoriesCreate(
  client: ApiClient,
  body: CategoryRequest,
  idempotencyKey?: string,
): Promise<PricingCategoryDTO> {
  return client.request<PricingCategoryDTO>('POST', `/api/v1/pricing/categories`, {
    body,
    idempotencyKey,
  });
}

export async function pricingCategoriesUpdate(
  client: ApiClient,
  params: { id: string },
  body: CategoryRequest,
): Promise<PricingCategoryDTO> {
  return client.request<PricingCategoryDTO>('PATCH', `/api/v1/pricing/categories/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pricingCategoriesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/pricing/categories/${encodeURIComponent(params.id)}`, {
  });
}

export async function pricingPriceSetsList(
  client: ApiClient,
  query: ListPriceSetsQuery,
): Promise<PagePriceSetDTO> {
  return client.request<PagePriceSetDTO>('GET', `/api/v1/pricing/price-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingPriceSetsCreate(
  client: ApiClient,
  body: PriceSetRequest,
  idempotencyKey?: string,
): Promise<PriceSetDTO> {
  return client.request<PriceSetDTO>('POST', `/api/v1/pricing/price-sets`, {
    body,
    idempotencyKey,
  });
}

export async function pricingPriceSetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PriceSetDTO> {
  return client.request<PriceSetDTO>('GET', `/api/v1/pricing/price-sets/${encodeURIComponent(params.id)}`, {
  });
}

export async function pricingPriceSetsUpdate(
  client: ApiClient,
  params: { id: string },
  body: PriceSetRequest,
): Promise<PriceSetDTO> {
  return client.request<PriceSetDTO>('PATCH', `/api/v1/pricing/price-sets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pricingPriceSetVersionsList(
  client: ApiClient,
  params: { id: string },
  query: ListSetVersionsQuery,
): Promise<PagePriceSetVersionDTO> {
  return client.request<PagePriceSetVersionDTO>('GET', `/api/v1/pricing/price-sets/${encodeURIComponent(params.id)}/versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pricingPriceSetVersionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateSetVersionRequest,
  idempotencyKey?: string,
): Promise<PriceSetVersionDTO> {
  return client.request<PriceSetVersionDTO>('POST', `/api/v1/pricing/price-sets/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function pricingPriceSetVersionsSetEntries(
  client: ApiClient,
  params: { id: string },
  body: SetPriceSetEntriesRequest,
): Promise<PriceSetVersionDTO> {
  return client.request<PriceSetVersionDTO>('PUT', `/api/v1/pricing/price-set-versions/${encodeURIComponent(params.id)}/entries`, {
    body,
  });
}

export async function pricingPriceSetVersionsBuild(
  client: ApiClient,
  params: { id: string },
  body: BuildPriceSetVersionRequest,
): Promise<PriceSetVersionDTO> {
  return client.request<PriceSetVersionDTO>('POST', `/api/v1/pricing/price-set-versions/${encodeURIComponent(params.id)}/build`, {
    body,
  });
}

export async function pricingPriceSetVersionsPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishPriceSetVersionRequest,
): Promise<PriceSetVersionDTO> {
  return client.request<PriceSetVersionDTO>('POST', `/api/v1/pricing/price-set-versions/${encodeURIComponent(params.id)}/publish`, {
    body,
  });
}

export async function pricingPriceSetVersionsWithdraw(
  client: ApiClient,
  params: { id: string },
  body: WithdrawPriceSetVersionRequest,
): Promise<PriceSetVersionDTO> {
  return client.request<PriceSetVersionDTO>('POST', `/api/v1/pricing/price-set-versions/${encodeURIComponent(params.id)}/withdraw`, {
    body,
  });
}

export async function pricingPriceSetVersionsManifest(
  client: ApiClient,
  params: { id: string },
): Promise<PriceSetManifestDTO> {
  return client.request<PriceSetManifestDTO>('GET', `/api/v1/pricing/price-set-versions/${encodeURIComponent(params.id)}/manifest`, {
  });
}

export async function costPoolsList(
  client: ApiClient,
  query: ListCostPoolsQuery,
): Promise<PageCostPoolDTO> {
  return client.request<PageCostPoolDTO>('GET', `/api/v1/cost-pools`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costPoolsCreate(
  client: ApiClient,
  body: CreateCostPoolRequest,
  idempotencyKey?: string,
): Promise<CostPoolDetailDTO> {
  return client.request<CostPoolDetailDTO>('POST', `/api/v1/cost-pools`, {
    body,
    idempotencyKey,
  });
}

export async function costPoolsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CostPoolDetailDTO> {
  return client.request<CostPoolDetailDTO>('GET', `/api/v1/cost-pools/${encodeURIComponent(params.id)}`, {
  });
}

export async function costPoolsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCostPoolRequest,
): Promise<CostPoolDetailDTO> {
  return client.request<CostPoolDetailDTO>('PATCH', `/api/v1/cost-pools/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function costPoolsDelete(
  client: ApiClient,
  params: { id: string },
  query: CostDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/cost-pools/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costPoolsSetSources(
  client: ApiClient,
  params: { id: string },
  body: SetCostPoolSourcesRequest,
): Promise<CostPoolDetailDTO> {
  return client.request<CostPoolDetailDTO>('PUT', `/api/v1/cost-pools/${encodeURIComponent(params.id)}/sources`, {
    body,
  });
}

export async function costPoolsBalance(
  client: ApiClient,
  params: { id: string },
  query: CostPoolBalanceQuery,
): Promise<CostPoolBalanceDTO> {
  return client.request<CostPoolBalanceDTO>('GET', `/api/v1/cost-pools/${encodeURIComponent(params.id)}/balance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costDriversList(
  client: ApiClient,
  query: ListCostDriversQuery,
): Promise<PageCostDriverDTO> {
  return client.request<PageCostDriverDTO>('GET', `/api/v1/cost-drivers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costDriversCreate(
  client: ApiClient,
  body: CreateCostDriverRequest,
  idempotencyKey?: string,
): Promise<CostDriverDTO> {
  return client.request<CostDriverDTO>('POST', `/api/v1/cost-drivers`, {
    body,
    idempotencyKey,
  });
}

export async function costDriversGet(
  client: ApiClient,
  params: { id: string },
): Promise<CostDriverDTO> {
  return client.request<CostDriverDTO>('GET', `/api/v1/cost-drivers/${encodeURIComponent(params.id)}`, {
  });
}

export async function costDriversUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCostDriverRequest,
): Promise<CostDriverDTO> {
  return client.request<CostDriverDTO>('PATCH', `/api/v1/cost-drivers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function costDriversDelete(
  client: ApiClient,
  params: { id: string },
  query: CostDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/cost-drivers/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costDriversListQuantities(
  client: ApiClient,
  params: { id: string },
  query: ListCostDriverQuantitiesQuery,
): Promise<PageCostDriverQuantityDTO> {
  return client.request<PageCostDriverQuantityDTO>('GET', `/api/v1/cost-drivers/${encodeURIComponent(params.id)}/quantities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costDriversSetQuantities(
  client: ApiClient,
  params: { id: string },
  body: SetCostDriverQuantitiesRequest,
): Promise<CostDriverQuantityDTO[]> {
  return client.request<CostDriverQuantityDTO[]>('PUT', `/api/v1/cost-drivers/${encodeURIComponent(params.id)}/quantities`, {
    body,
  });
}

export async function costDriversImportQuantities(
  client: ApiClient,
  params: { id: string },
  body: ImportCostDriverQuantitiesRequest,
  idempotencyKey?: string,
): Promise<CostQuantityImportResultDTO> {
  return client.request<CostQuantityImportResultDTO>('POST', `/api/v1/cost-drivers/${encodeURIComponent(params.id)}/quantities/import`, {
    body,
    idempotencyKey,
  });
}

export async function costCyclesList(
  client: ApiClient,
  query: ListCostCyclesQuery,
): Promise<PageCostCycleDTO> {
  return client.request<PageCostCycleDTO>('GET', `/api/v1/cost-cycles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costCyclesCreate(
  client: ApiClient,
  body: CreateCostCycleRequest,
  idempotencyKey?: string,
): Promise<CostCycleDetailDTO> {
  return client.request<CostCycleDetailDTO>('POST', `/api/v1/cost-cycles`, {
    body,
    idempotencyKey,
  });
}

export async function costCyclesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CostCycleDetailDTO> {
  return client.request<CostCycleDetailDTO>('GET', `/api/v1/cost-cycles/${encodeURIComponent(params.id)}`, {
  });
}

export async function costCyclesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCostCycleRequest,
): Promise<CostCycleDetailDTO> {
  return client.request<CostCycleDetailDTO>('PATCH', `/api/v1/cost-cycles/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function costCyclesDelete(
  client: ApiClient,
  params: { id: string },
  query: CostDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/cost-cycles/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costCyclesSetSteps(
  client: ApiClient,
  params: { id: string },
  body: SetCostCycleStepsRequest,
): Promise<CostCycleDetailDTO> {
  return client.request<CostCycleDetailDTO>('PUT', `/api/v1/cost-cycles/${encodeURIComponent(params.id)}/steps`, {
    body,
  });
}

export async function costCyclesValidate(
  client: ApiClient,
  params: { id: string },
  query: CostCycleValidateQuery,
): Promise<CostCycleValidationDTO> {
  return client.request<CostCycleValidationDTO>('POST', `/api/v1/cost-cycles/${encodeURIComponent(params.id)}/validate`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costCyclesActivate(
  client: ApiClient,
  params: { id: string },
  query: CostCycleValidateQuery,
  idempotencyKey?: string,
): Promise<CostCycleDetailDTO> {
  return client.request<CostCycleDetailDTO>('POST', `/api/v1/cost-cycles/${encodeURIComponent(params.id)}/activate`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
    idempotencyKey,
  });
}

export async function costRunsList(
  client: ApiClient,
  query: ListCostRunsQuery,
): Promise<PageCostRunDTO> {
  return client.request<PageCostRunDTO>('GET', `/api/v1/cost-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costRunsCalculate(
  client: ApiClient,
  body: CalculateCostRunRequest,
  idempotencyKey?: string,
): Promise<CostRunDetailDTO> {
  return client.request<CostRunDetailDTO>('POST', `/api/v1/cost-runs`, {
    body,
    idempotencyKey,
  });
}

export async function costRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CostRunDetailDTO> {
  return client.request<CostRunDetailDTO>('GET', `/api/v1/cost-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function costRunsPost(
  client: ApiClient,
  params: { id: string },
  body: PostCostRunRequest,
  idempotencyKey?: string,
): Promise<CostRunDetailDTO> {
  return client.request<CostRunDetailDTO>('POST', `/api/v1/cost-runs/${encodeURIComponent(params.id)}/post`, {
    body,
    idempotencyKey,
  });
}

export async function costRunsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseCostRunRequest,
  idempotencyKey?: string,
): Promise<CostRunDetailDTO> {
  return client.request<CostRunDetailDTO>('POST', `/api/v1/cost-runs/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function costRunsListAllocations(
  client: ApiClient,
  params: { id: string },
  query: ListCostRunAllocationsQuery,
): Promise<PageCostRunAllocationDTO> {
  return client.request<PageCostRunAllocationDTO>('GET', `/api/v1/cost-runs/${encodeURIComponent(params.id)}/allocations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costRunsListIterations(
  client: ApiClient,
  params: { id: string },
  query: ListCostRunIterationsQuery,
): Promise<PageCostRunIterationDTO> {
  return client.request<PageCostRunIterationDTO>('GET', `/api/v1/cost-runs/${encodeURIComponent(params.id)}/iterations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costRunsExport(
  client: ApiClient,
  params: { id: string },
  query: CostRunExportQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/cost-runs/${encodeURIComponent(params.id)}/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costRatesList(
  client: ApiClient,
  query: ListCostAbsorptionRatesQuery,
): Promise<PageCostAbsorptionRateDTO> {
  return client.request<PageCostAbsorptionRateDTO>('GET', `/api/v1/cost-absorption-rates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costRatesSet(
  client: ApiClient,
  body: SetCostAbsorptionRateRequest,
  idempotencyKey?: string,
): Promise<CostAbsorptionRateDTO> {
  return client.request<CostAbsorptionRateDTO>('PUT', `/api/v1/cost-absorption-rates`, {
    body,
    idempotencyKey,
  });
}

export async function costReportsProfitability(
  client: ApiClient,
  query: CostProfitabilityQuery,
): Promise<CostObjectProfitabilityDTO> {
  return client.request<CostObjectProfitabilityDTO>('GET', `/api/v1/reports/cost-object-profitability`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function costReportsAbsorptionVariance(
  client: ApiClient,
  query: CostVarianceReportQuery,
): Promise<CostAbsorptionVarianceReportDTO> {
  return client.request<CostAbsorptionVarianceReportDTO>('GET', `/api/v1/reports/cost-absorption-variance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function intercoPairsList(
  client: ApiClient,
  query: ListICReconPairsQuery,
): Promise<PageICPairDTO> {
  return client.request<PageICPairDTO>('GET', `/api/v1/ic-recon-pairs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function intercoPairsCreate(
  client: ApiClient,
  body: CreateICReconPairRequest,
  idempotencyKey?: string,
): Promise<ICPairDTO> {
  return client.request<ICPairDTO>('POST', `/api/v1/ic-recon-pairs`, {
    body,
    idempotencyKey,
  });
}

export async function intercoPairsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ICPairDTO> {
  return client.request<ICPairDTO>('GET', `/api/v1/ic-recon-pairs/${encodeURIComponent(params.id)}`, {
  });
}

export async function intercoPairsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateICReconPairRequest,
): Promise<ICPairDTO> {
  return client.request<ICPairDTO>('PATCH', `/api/v1/ic-recon-pairs/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function intercoTransactionsList(
  client: ApiClient,
  query: ListICTransactionsQuery,
): Promise<PageICTransactionDTO> {
  return client.request<PageICTransactionDTO>('GET', `/api/v1/ic-transactions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function intercoTransactionsMatch(
  client: ApiClient,
  params: { id: string },
  body: MatchTransactionRequest,
  idempotencyKey?: string,
): Promise<ICTransactionDTO> {
  return client.request<ICTransactionDTO>('POST', `/api/v1/ic-transactions/${encodeURIComponent(params.id)}/match`, {
    body,
    idempotencyKey,
  });
}

export async function intercoTransactionsUnmatch(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ICTransactionDTO> {
  return client.request<ICTransactionDTO>('POST', `/api/v1/ic-transactions/${encodeURIComponent(params.id)}/unmatch`, {
    idempotencyKey,
  });
}

export async function intercoTransactionsDispose(
  client: ApiClient,
  params: { id: string },
  body: DisposeTransactionRequest,
  idempotencyKey?: string,
): Promise<ICTransactionDTO> {
  return client.request<ICTransactionDTO>('POST', `/api/v1/ic-transactions/${encodeURIComponent(params.id)}/dispose`, {
    body,
    idempotencyKey,
  });
}

export async function intercoPairsAutoMatch(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<AutoMatchResultDTO> {
  return client.request<AutoMatchResultDTO>('POST', `/api/v1/ic-recon-pairs/${encodeURIComponent(params.id)}/auto-match`, {
    idempotencyKey,
  });
}

export async function intercoStatementsList(
  client: ApiClient,
  query: ListICStatementsQuery,
): Promise<PageICStatementDTO> {
  return client.request<PageICStatementDTO>('GET', `/api/v1/ic-recon-statements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function intercoStatementsGenerate(
  client: ApiClient,
  body: GenerateStatementRequest,
  idempotencyKey?: string,
): Promise<ICStatementDTO> {
  return client.request<ICStatementDTO>('POST', `/api/v1/ic-recon-statements`, {
    body,
    idempotencyKey,
  });
}

export async function intercoStatementsCertify(
  client: ApiClient,
  params: { id: string },
  body: CertifyStatementRequest,
  idempotencyKey?: string,
): Promise<ICStatementDTO> {
  return client.request<ICStatementDTO>('POST', `/api/v1/ic-recon-statements/${encodeURIComponent(params.id)}/certify`, {
    body,
    idempotencyKey,
  });
}

export async function intercoDifferencesList(
  client: ApiClient,
  query: ListICDifferencesQuery,
): Promise<PageICDifferenceDTO> {
  return client.request<PageICDifferenceDTO>('GET', `/api/v1/ic-differences`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function intercoDifferencesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ICDifferenceDTO> {
  return client.request<ICDifferenceDTO>('GET', `/api/v1/ic-differences/${encodeURIComponent(params.id)}`, {
  });
}

export async function intercoDifferencesAssign(
  client: ApiClient,
  params: { id: string },
  body: ICAssignDifferenceRequest,
  idempotencyKey?: string,
): Promise<ICDifferenceDTO> {
  return client.request<ICDifferenceDTO>('POST', `/api/v1/ic-differences/${encodeURIComponent(params.id)}/assign`, {
    body,
    idempotencyKey,
  });
}

export async function intercoDifferencesInvestigate(
  client: ApiClient,
  params: { id: string },
  body: ICInvestigateDifferenceRequest,
  idempotencyKey?: string,
): Promise<ICDifferenceDTO> {
  return client.request<ICDifferenceDTO>('POST', `/api/v1/ic-differences/${encodeURIComponent(params.id)}/investigate`, {
    body,
    idempotencyKey,
  });
}

export async function intercoDifferencesResolve(
  client: ApiClient,
  params: { id: string },
  body: ICResolveDifferenceRequest,
  idempotencyKey?: string,
): Promise<ICDifferenceDTO> {
  return client.request<ICDifferenceDTO>('POST', `/api/v1/ic-differences/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function intercoReportsAging(
  client: ApiClient,
  query: ICAgeingReportQuery,
): Promise<ICAgeingReportDTO> {
  return client.request<ICAgeingReportDTO>('GET', `/api/v1/reports/ic-ageing`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function tpPoliciesList(
  client: ApiClient,
  query: TpListPoliciesQuery,
): Promise<PageTpPolicyDTO> {
  return client.request<PageTpPolicyDTO>('GET', `/api/v1/tp-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function tpPoliciesCreate(
  client: ApiClient,
  body: TpCreatePolicyRequest,
  idempotencyKey?: string,
): Promise<TpPolicyDTO> {
  return client.request<TpPolicyDTO>('POST', `/api/v1/tp-policies`, {
    body,
    idempotencyKey,
  });
}

export async function tpPoliciesPrice(
  client: ApiClient,
  body: TpPriceRequest,
): Promise<TpPriceQuoteDTO> {
  return client.request<TpPriceQuoteDTO>('POST', `/api/v1/tp-policies/price`, {
    body,
  });
}

export async function tpPoliciesGet(
  client: ApiClient,
  params: { id: string },
): Promise<TpPolicyDTO> {
  return client.request<TpPolicyDTO>('GET', `/api/v1/tp-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function tpPoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: TpUpdatePolicyRequest,
): Promise<TpPolicyDTO> {
  return client.request<TpPolicyDTO>('PATCH', `/api/v1/tp-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function tpPoliciesSubmit(
  client: ApiClient,
  params: { id: string },
  body: TpSubmitPolicyRequest,
  idempotencyKey?: string,
): Promise<TpPolicyDTO> {
  return client.request<TpPolicyDTO>('POST', `/api/v1/tp-policies/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function tpPoliciesSupersede(
  client: ApiClient,
  params: { id: string },
  body: TpSupersedePolicyRequest,
  idempotencyKey?: string,
): Promise<TpPolicyDTO> {
  return client.request<TpPolicyDTO>('POST', `/api/v1/tp-policies/${encodeURIComponent(params.id)}/supersede`, {
    body,
    idempotencyKey,
  });
}

export async function tpPoliciesDiscard(
  client: ApiClient,
  params: { id: string },
  body: TpDiscardPolicyRequest,
  idempotencyKey?: string,
): Promise<TpPolicyDTO> {
  return client.request<TpPolicyDTO>('POST', `/api/v1/tp-policies/${encodeURIComponent(params.id)}/discard`, {
    body,
    idempotencyKey,
  });
}

export async function tpReportsDepartures(
  client: ApiClient,
  query: TpDeparturesQuery,
): Promise<TpDeparturesReportDTO> {
  return client.request<TpDeparturesReportDTO>('GET', `/api/v1/reports/tp-departures`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseCategoriesList(
  client: ApiClient,
  query: ListExpenseCategoriesQuery,
): Promise<PageExpenseCategoryDTO> {
  return client.request<PageExpenseCategoryDTO>('GET', `/api/v1/expense-categories`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseCategoriesCreate(
  client: ApiClient,
  body: CreateExpenseCategoryRequest,
  idempotencyKey?: string,
): Promise<ExpenseCategoryDTO> {
  return client.request<ExpenseCategoryDTO>('POST', `/api/v1/expense-categories`, {
    body,
    idempotencyKey,
  });
}

export async function expenseCategoriesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateExpenseCategoryRequest,
): Promise<ExpenseCategoryDTO> {
  return client.request<ExpenseCategoryDTO>('PATCH', `/api/v1/expense-categories/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function expensePoliciesList(
  client: ApiClient,
  query: ListExpensePoliciesQuery,
): Promise<PageExpensePolicyDTO> {
  return client.request<PageExpensePolicyDTO>('GET', `/api/v1/expense-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expensePoliciesCreate(
  client: ApiClient,
  body: CreateExpensePolicyRequest,
  idempotencyKey?: string,
): Promise<ExpensePolicyDTO> {
  return client.request<ExpensePolicyDTO>('POST', `/api/v1/expense-policies`, {
    body,
    idempotencyKey,
  });
}

export async function expenseMileageRatesList(
  client: ApiClient,
  query: ListExpenseMileageRatesQuery,
): Promise<PageExpenseMileageRateDTO> {
  return client.request<PageExpenseMileageRateDTO>('GET', `/api/v1/expense-mileage-rates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseMileageRatesCreate(
  client: ApiClient,
  body: CreateExpenseMileageRateRequest,
  idempotencyKey?: string,
): Promise<ExpenseMileageRateDTO> {
  return client.request<ExpenseMileageRateDTO>('POST', `/api/v1/expense-mileage-rates`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsList(
  client: ApiClient,
  query: ListExpenseClaimsQuery,
): Promise<PageExpenseClaimDTO> {
  return client.request<PageExpenseClaimDTO>('GET', `/api/v1/expense-claims`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseClaimsCreate(
  client: ApiClient,
  body: CreateExpenseClaimRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('POST', `/api/v1/expense-claims`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('GET', `/api/v1/expense-claims/${encodeURIComponent(params.id)}`, {
  });
}

export async function expenseClaimsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateExpenseClaimRequest,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('PATCH', `/api/v1/expense-claims/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function expenseClaimsDelete(
  client: ApiClient,
  params: { id: string },
  query: ExpenseClaimDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/expense-claims/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseClaimsSetLines(
  client: ApiClient,
  params: { id: string },
  body: SetExpenseClaimLinesRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('PUT', `/api/v1/expense-claims/${encodeURIComponent(params.id)}/lines`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitExpenseClaimRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('POST', `/api/v1/expense-claims/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsApprove(
  client: ApiClient,
  params: { id: string },
  body: DecideExpenseClaimRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('POST', `/api/v1/expense-claims/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsReject(
  client: ApiClient,
  params: { id: string },
  body: DecideExpenseClaimRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('POST', `/api/v1/expense-claims/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsPost(
  client: ApiClient,
  params: { id: string },
  body: PostExpenseClaimRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('POST', `/api/v1/expense-claims/${encodeURIComponent(params.id)}/post`, {
    body,
    idempotencyKey,
  });
}

export async function expenseClaimsSetSettlementMethod(
  client: ApiClient,
  params: { id: string },
  body: ExpensePayrollSettlementRequest,
  idempotencyKey?: string,
): Promise<ExpenseClaimDetailDTO> {
  return client.request<ExpenseClaimDetailDTO>('POST', `/api/v1/expense-claims/${encodeURIComponent(params.id)}/settlement-method`, {
    body,
    idempotencyKey,
  });
}

export async function expensePayrollInclusionsList(
  client: ApiClient,
  query: ListExpensePayrollInclusionsQuery,
): Promise<PageExpensePayrollInclusionDTO> {
  return client.request<PageExpensePayrollInclusionDTO>('GET', `/api/v1/expense-payroll-inclusions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseTravelRequestsList(
  client: ApiClient,
  query: ListTravelRequestsQuery,
): Promise<PageTravelRequestDTO> {
  return client.request<PageTravelRequestDTO>('GET', `/api/v1/travel-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseTravelRequestsCreate(
  client: ApiClient,
  body: CreateTravelRequestRequest,
  idempotencyKey?: string,
): Promise<TravelRequestDTO> {
  return client.request<TravelRequestDTO>('POST', `/api/v1/travel-requests`, {
    body,
    idempotencyKey,
  });
}

export async function expenseTravelRequestsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<TravelRequestDTO> {
  return client.request<TravelRequestDTO>('POST', `/api/v1/travel-requests/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function expenseTravelAdvancesList(
  client: ApiClient,
  query: ListTravelAdvancesQuery,
): Promise<PageTravelAdvanceDTO> {
  return client.request<PageTravelAdvanceDTO>('GET', `/api/v1/travel-advances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseTravelAdvancesCreate(
  client: ApiClient,
  body: CreateTravelAdvanceRequest,
  idempotencyKey?: string,
): Promise<TravelAdvanceDTO> {
  return client.request<TravelAdvanceDTO>('POST', `/api/v1/travel-advances`, {
    body,
    idempotencyKey,
  });
}

export async function expenseTravelAdvancesPay(
  client: ApiClient,
  params: { id: string },
  body: PayTravelAdvanceRequest,
  idempotencyKey?: string,
): Promise<TravelAdvanceDTO> {
  return client.request<TravelAdvanceDTO>('POST', `/api/v1/travel-advances/${encodeURIComponent(params.id)}/pay`, {
    body,
    idempotencyKey,
  });
}

export async function expenseTravelAdvancesSetPayrollRecovery(
  client: ApiClient,
  params: { id: string },
  body: ExpensePayrollRecoveryRequest,
  idempotencyKey?: string,
): Promise<TravelAdvanceDTO> {
  return client.request<TravelAdvanceDTO>('POST', `/api/v1/travel-advances/${encodeURIComponent(params.id)}/payroll-recovery`, {
    body,
    idempotencyKey,
  });
}

export async function expenseCardAccountsList(
  client: ApiClient,
  query: ListCardAccountsQuery,
): Promise<PageCardAccountDTO> {
  return client.request<PageCardAccountDTO>('GET', `/api/v1/card-accounts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseCardAccountsCreate(
  client: ApiClient,
  body: CreateCardAccountRequest,
  idempotencyKey?: string,
): Promise<CardAccountDTO> {
  return client.request<CardAccountDTO>('POST', `/api/v1/card-accounts`, {
    body,
    idempotencyKey,
  });
}

export async function expenseCardAccountsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCardAccountRequest,
): Promise<CardAccountDTO> {
  return client.request<CardAccountDTO>('PATCH', `/api/v1/card-accounts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function expenseCardStatementsList(
  client: ApiClient,
  query: ListCardStatementsQuery,
): Promise<PageCardStatementDTO> {
  return client.request<PageCardStatementDTO>('GET', `/api/v1/card-statements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseCardStatementsImport(
  client: ApiClient,
  body: ImportCardStatementRequest,
  idempotencyKey?: string,
): Promise<CardStatementDTO> {
  return client.request<CardStatementDTO>('POST', `/api/v1/card-statements`, {
    body,
    idempotencyKey,
  });
}

export async function expenseCardStatementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CardStatementDTO> {
  return client.request<CardStatementDTO>('GET', `/api/v1/card-statements/${encodeURIComponent(params.id)}`, {
  });
}

export async function expenseCardStatementsListLines(
  client: ApiClient,
  params: { id: string },
  query: ListCardStatementLinesQuery,
): Promise<PageCardStatementLineDTO> {
  return client.request<PageCardStatementLineDTO>('GET', `/api/v1/card-statements/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseCardStatementsAutoMatch(
  client: ApiClient,
  params: { id: string },
): Promise<AutoMatchCardStatementResponse> {
  return client.request<AutoMatchCardStatementResponse>('POST', `/api/v1/card-statements/${encodeURIComponent(params.id)}/auto-match`, {
  });
}

export async function expenseCardLinesMatch(
  client: ApiClient,
  params: { id: string },
  body: MatchCardLineRequest,
  idempotencyKey?: string,
): Promise<CardStatementLineDTO> {
  return client.request<CardStatementLineDTO>('POST', `/api/v1/card-statement-lines/${encodeURIComponent(params.id)}/match`, {
    body,
    idempotencyKey,
  });
}

export async function expenseCardLinesDispose(
  client: ApiClient,
  params: { id: string },
  body: DisposeCardLineRequest,
  idempotencyKey?: string,
): Promise<CardStatementLineDTO> {
  return client.request<CardStatementLineDTO>('POST', `/api/v1/card-statement-lines/${encodeURIComponent(params.id)}/dispose`, {
    body,
    idempotencyKey,
  });
}

export async function expenseCardStatementsClose(
  client: ApiClient,
  params: { id: string },
  body: CloseCardStatementRequest,
  idempotencyKey?: string,
): Promise<CardStatementDTO> {
  return client.request<CardStatementDTO>('POST', `/api/v1/card-statements/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function expenseSettingsGet(
  client: ApiClient,
  query: GetExpenseSettingsQuery,
): Promise<ExpenseSettingsDTO> {
  return client.request<ExpenseSettingsDTO>('GET', `/api/v1/expense-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function expenseSettingsSet(
  client: ApiClient,
  body: UpsertExpenseSettingsRequest,
  idempotencyKey?: string,
): Promise<ExpenseSettingsDTO> {
  return client.request<ExpenseSettingsDTO>('PUT', `/api/v1/expense-settings`, {
    body,
    idempotencyKey,
  });
}

export async function leaseContractsList(
  client: ApiClient,
  query: LeaseContractListQuery,
): Promise<PageLeaseContractDTO> {
  return client.request<PageLeaseContractDTO>('GET', `/api/v1/lease-contracts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function leaseContractsCreate(
  client: ApiClient,
  body: LeaseContractCreateRequest,
  idempotencyKey?: string,
): Promise<LeaseContractDTO> {
  return client.request<LeaseContractDTO>('POST', `/api/v1/lease-contracts`, {
    body,
    idempotencyKey,
  });
}

export async function leaseContractsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LeaseContractDTO> {
  return client.request<LeaseContractDTO>('GET', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}`, {
  });
}

export async function leaseContractsUpdate(
  client: ApiClient,
  params: { id: string },
  body: LeaseContractUpdateRequest,
  idempotencyKey?: string,
): Promise<LeaseContractDTO> {
  return client.request<LeaseContractDTO>('PATCH', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function leaseContractsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<LeaseContractDTO> {
  return client.request<LeaseContractDTO>('POST', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function leaseContractsSchedule(
  client: ApiClient,
  params: { id: string },
): Promise<LeaseScheduleDTO> {
  return client.request<LeaseScheduleDTO>('GET', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/schedule`, {
  });
}

export async function leaseContractsScheduleVersions(
  client: ApiClient,
  params: { id: string },
): Promise<LeaseScheduleVersionsDTO> {
  return client.request<LeaseScheduleVersionsDTO>('GET', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/schedule-versions`, {
  });
}

export async function leaseContractsScheduleVersion(
  client: ApiClient,
  params: { id: string; version: string },
): Promise<LeaseScheduleDTO> {
  return client.request<LeaseScheduleDTO>('GET', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/schedule-versions/${encodeURIComponent(params.version)}`, {
  });
}

export async function leaseContractsRemeasure(
  client: ApiClient,
  params: { id: string },
  body: LeaseRemeasureRequest,
  idempotencyKey?: string,
): Promise<LeaseChangeDTO> {
  return client.request<LeaseChangeDTO>('POST', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/remeasure`, {
    body,
    idempotencyKey,
  });
}

export async function leaseContractsTerminate(
  client: ApiClient,
  params: { id: string },
  body: LeaseTerminateRequest,
  idempotencyKey?: string,
): Promise<LeaseChangeDTO> {
  return client.request<LeaseChangeDTO>('POST', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/terminate`, {
    body,
    idempotencyKey,
  });
}

export async function leaseContractsChanges(
  client: ApiClient,
  params: { id: string },
  query: LeaseChangeListQuery,
): Promise<PageLeaseChangeDTO> {
  return client.request<PageLeaseChangeDTO>('GET', `/api/v1/lease-contracts/${encodeURIComponent(params.id)}/changes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function leaseChangesGet(
  client: ApiClient,
  params: { id: string },
): Promise<LeaseChangeDTO> {
  return client.request<LeaseChangeDTO>('GET', `/api/v1/lease-changes/${encodeURIComponent(params.id)}`, {
  });
}

export async function leaseReportsLiabilityMaturity(
  client: ApiClient,
  query: LeaseLiabilityMaturityQuery,
): Promise<LeaseLiabilityMaturityReport> {
  return client.request<LeaseLiabilityMaturityReport>('GET', `/api/v1/reports/lease-liability-maturity`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function leaseReportsRollForward(
  client: ApiClient,
  query: LeaseRollForwardQuery,
): Promise<LeaseRollForwardReport> {
  return client.request<LeaseRollForwardReport>('GET', `/api/v1/reports/lease-roll-forward`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function leaseRunsPost(
  client: ApiClient,
  body: LeaseRunRequest,
  idempotencyKey?: string,
): Promise<LeaseRunDTO> {
  return client.request<LeaseRunDTO>('POST', `/api/v1/lease-runs`, {
    body,
    idempotencyKey,
  });
}

export async function leaseRunsList(
  client: ApiClient,
  query: LeaseRunListQuery,
): Promise<PageLeaseRunDTO> {
  return client.request<PageLeaseRunDTO>('GET', `/api/v1/lease-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function revpolicyPoliciesList(
  client: ApiClient,
  query: RevpolicyListQuery,
): Promise<PageRevpolicyPolicyDTO> {
  return client.request<PageRevpolicyPolicyDTO>('GET', `/api/v1/revenue-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function revpolicyPoliciesCreate(
  client: ApiClient,
  body: RevpolicyCreateRequest,
  idempotencyKey?: string,
): Promise<RevpolicyPolicyDTO> {
  return client.request<RevpolicyPolicyDTO>('POST', `/api/v1/revenue-policies`, {
    body,
    idempotencyKey,
  });
}

export async function revpolicyPoliciesCurrent(
  client: ApiClient,
): Promise<RevpolicyCurrentDTO> {
  return client.request<RevpolicyCurrentDTO>('GET', `/api/v1/revenue-policies/current`, {
  });
}

export async function revpolicyPoliciesGet(
  client: ApiClient,
  params: { id: string },
): Promise<RevpolicyPolicyDTO> {
  return client.request<RevpolicyPolicyDTO>('GET', `/api/v1/revenue-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function revpolicyPoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: RevpolicyUpdateRequest,
  idempotencyKey?: string,
): Promise<RevpolicyPolicyDTO> {
  return client.request<RevpolicyPolicyDTO>('PATCH', `/api/v1/revenue-policies/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function revpolicyPoliciesSubmit(
  client: ApiClient,
  params: { id: string },
  body: RevpolicySubmitRequest,
  idempotencyKey?: string,
): Promise<RevpolicyPolicyDTO> {
  return client.request<RevpolicyPolicyDTO>('POST', `/api/v1/revenue-policies/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function revpolicyPoliciesSupersede(
  client: ApiClient,
  params: { id: string },
  body: RevpolicySupersedeRequest,
  idempotencyKey?: string,
): Promise<RevpolicyPolicyDTO> {
  return client.request<RevpolicyPolicyDTO>('POST', `/api/v1/revenue-policies/${encodeURIComponent(params.id)}/supersede`, {
    body,
    idempotencyKey,
  });
}

export async function revpolicyPoliciesDiscard(
  client: ApiClient,
  params: { id: string },
  body: RevpolicyDiscardRequest,
  idempotencyKey?: string,
): Promise<RevpolicyPolicyDTO> {
  return client.request<RevpolicyPolicyDTO>('POST', `/api/v1/revenue-policies/${encodeURIComponent(params.id)}/discard`, {
    body,
    idempotencyKey,
  });
}

export async function revpolicyReportsDepartures(
  client: ApiClient,
  query: RevpolicyDeparturesQuery,
): Promise<RevpolicyDeparturesReportDTO> {
  return client.request<RevpolicyDeparturesReportDTO>('GET', `/api/v1/reports/revenue-policy-departures`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function liabilityInstrumentsList(
  client: ApiClient,
  query: LiabilityInstrumentsQuery,
): Promise<PageLiabilityInstrumentDTO> {
  return client.request<PageLiabilityInstrumentDTO>('GET', `/api/v1/finance/liability-instruments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function liabilityReportsBreakage(
  client: ApiClient,
  query: LiabilityBreakageReportQuery,
): Promise<LiabilityBreakageReportDTO> {
  return client.request<LiabilityBreakageReportDTO>('GET', `/api/v1/reports/liability-breakage`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function liabilitySettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<LiabilitySettingsDTO> {
  return client.request<LiabilitySettingsDTO>('GET', `/api/v1/finance/liability-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function liabilitySettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: LiabilityUpdateSettingsRequest,
  idempotencyKey?: string,
): Promise<LiabilitySettingsDTO> {
  return client.request<LiabilitySettingsDTO>('PUT', `/api/v1/finance/liability-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function fxExposureReport(
  client: ApiClient,
  query: FxrevalExposureQuery,
): Promise<FxrevalExposureDTO> {
  return client.request<FxrevalExposureDTO>('GET', `/api/v1/reports/fx-exposure`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxRevaluationSettingsGet(
  client: ApiClient,
  query: FxrevalSettingsQuery,
): Promise<FxrevalSettingsDTO> {
  return client.request<FxrevalSettingsDTO>('GET', `/api/v1/fx-revaluation-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxRevaluationSettingsUpdate(
  client: ApiClient,
  body: FxrevalUpdateSettingsRequest,
  idempotencyKey?: string,
): Promise<FxrevalSettingsDTO> {
  return client.request<FxrevalSettingsDTO>('PUT', `/api/v1/fx-revaluation-settings`, {
    body,
    idempotencyKey,
  });
}

export async function fxPeriodRevaluationsList(
  client: ApiClient,
  query: FxrevalListRunsQuery,
): Promise<PageFxrevalRunDTO> {
  return client.request<PageFxrevalRunDTO>('GET', `/api/v1/fx-period-revaluations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxPeriodRevaluationsCreate(
  client: ApiClient,
  body: FxrevalCreateRunRequest,
  idempotencyKey?: string,
): Promise<FxrevalRunDTO> {
  return client.request<FxrevalRunDTO>('POST', `/api/v1/fx-period-revaluations`, {
    body,
    idempotencyKey,
  });
}

export async function fxPeriodRevaluationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<FxrevalRunDTO> {
  return client.request<FxrevalRunDTO>('GET', `/api/v1/fx-period-revaluations/${encodeURIComponent(params.id)}`, {
  });
}

export async function fxPeriodRevaluationsLines(
  client: ApiClient,
  params: { id: string },
  query: FxrevalRunLinesQuery,
): Promise<PageFxrevalRunLineDTO> {
  return client.request<PageFxrevalRunLineDTO>('GET', `/api/v1/fx-period-revaluations/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function fxPeriodRevaluationsPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<FxrevalRunDTO> {
  return client.request<FxrevalRunDTO>('POST', `/api/v1/fx-period-revaluations/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function fxPeriodRevaluationsDiscard(
  client: ApiClient,
  params: { id: string },
  body: FxrevalDiscardRunRequest,
  idempotencyKey?: string,
): Promise<FxrevalRunDTO> {
  return client.request<FxrevalRunDTO>('POST', `/api/v1/fx-period-revaluations/${encodeURIComponent(params.id)}/discard`, {
    body,
    idempotencyKey,
  });
}

export async function closeCalendarsList(
  client: ApiClient,
  query: ListCloseCalendarsQuery,
): Promise<PageCloseCalendarDTO> {
  return client.request<PageCloseCalendarDTO>('GET', `/api/v1/close-calendars`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeCalendarsCreate(
  client: ApiClient,
  body: CreateCloseCalendarRequest,
  idempotencyKey?: string,
): Promise<CloseCalendarDTO> {
  return client.request<CloseCalendarDTO>('POST', `/api/v1/close-calendars`, {
    body,
    idempotencyKey,
  });
}

export async function closeCalendarsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CloseCalendarDetailDTO> {
  return client.request<CloseCalendarDetailDTO>('GET', `/api/v1/close-calendars/${encodeURIComponent(params.id)}`, {
  });
}

export async function closeCalendarsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCloseCalendarRequest,
): Promise<CloseCalendarDTO> {
  return client.request<CloseCalendarDTO>('PATCH', `/api/v1/close-calendars/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function closeCalendarsSetTemplates(
  client: ApiClient,
  params: { id: string },
  body: SetCloseTemplatesRequest,
): Promise<CloseCalendarDetailDTO> {
  return client.request<CloseCalendarDetailDTO>('PUT', `/api/v1/close-calendars/${encodeURIComponent(params.id)}/task-templates`, {
    body,
  });
}

export async function closeCalendarsSeedDefaults(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<CloseCalendarDetailDTO> {
  return client.request<CloseCalendarDetailDTO>('POST', `/api/v1/close-calendars/${encodeURIComponent(params.id)}/seed-defaults`, {
    idempotencyKey,
  });
}

export async function closeRunsList(
  client: ApiClient,
  query: ListCloseRunsQuery,
): Promise<PageCloseRunDTO> {
  return client.request<PageCloseRunDTO>('GET', `/api/v1/close-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeRunsOpen(
  client: ApiClient,
  body: OpenCloseRunRequest,
  idempotencyKey?: string,
): Promise<CloseRunDetailDTO> {
  return client.request<CloseRunDetailDTO>('POST', `/api/v1/close-runs`, {
    body,
    idempotencyKey,
  });
}

export async function closeRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CloseRunDetailDTO> {
  return client.request<CloseRunDetailDTO>('GET', `/api/v1/close-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function closeRunsRefresh(
  client: ApiClient,
  params: { id: string },
): Promise<CloseRunDetailDTO> {
  return client.request<CloseRunDetailDTO>('POST', `/api/v1/close-runs/${encodeURIComponent(params.id)}/refresh`, {
  });
}

export async function closeRunsAbandon(
  client: ApiClient,
  params: { id: string },
  body: AbandonCloseRunRequest,
): Promise<CloseRunDTO> {
  return client.request<CloseRunDTO>('POST', `/api/v1/close-runs/${encodeURIComponent(params.id)}/abandon`, {
    body,
  });
}

export async function closeRunsCertify(
  client: ApiClient,
  params: { id: string },
  body: CertifyCloseRunRequest,
): Promise<CloseRunDetailDTO> {
  return client.request<CloseRunDetailDTO>('POST', `/api/v1/close-runs/${encodeURIComponent(params.id)}/certify`, {
    body,
  });
}

export async function closeRunsCertifyReconciliation(
  client: ApiClient,
  params: { id: string },
  body: CertifyCloseReconciliationRequest,
  idempotencyKey?: string,
): Promise<CloseCertificationDTO> {
  return client.request<CloseCertificationDTO>('POST', `/api/v1/close-runs/${encodeURIComponent(params.id)}/certify-reconciliation`, {
    body,
    idempotencyKey,
  });
}

export async function closeRunsAddTask(
  client: ApiClient,
  params: { id: string },
  body: AddCloseTaskRequest,
): Promise<CloseTaskDTO> {
  return client.request<CloseTaskDTO>('POST', `/api/v1/close-runs/${encodeURIComponent(params.id)}/tasks`, {
    body,
  });
}

export async function closeTasksList(
  client: ApiClient,
  params: { id: string },
  query: ListCloseTasksQuery,
): Promise<PageCloseTaskDTO> {
  return client.request<PageCloseTaskDTO>('GET', `/api/v1/close-runs/${encodeURIComponent(params.id)}/tasks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeEvidenceList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageCloseEvidenceDTO> {
  return client.request<PageCloseEvidenceDTO>('GET', `/api/v1/close-runs/${encodeURIComponent(params.id)}/evidence`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeRunsBuildEvidencePack(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<CloseEvidencePackDTO> {
  return client.request<CloseEvidencePackDTO>('POST', `/api/v1/close-runs/${encodeURIComponent(params.id)}/evidence-pack`, {
    idempotencyKey,
  });
}

export async function closeRunsEvidencePack(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/close-runs/${encodeURIComponent(params.id)}/evidence-pack`, {
  });
}

export async function closeTasksComplete(
  client: ApiClient,
  params: { id: string },
  body: CompleteCloseTaskRequest,
): Promise<CloseTaskDTO> {
  return client.request<CloseTaskDTO>('POST', `/api/v1/close-tasks/${encodeURIComponent(params.id)}/complete`, {
    body,
  });
}

export async function closeTasksAccept(
  client: ApiClient,
  params: { id: string },
  body: AcceptCloseTaskRequest,
): Promise<CloseTaskDTO> {
  return client.request<CloseTaskDTO>('POST', `/api/v1/close-tasks/${encodeURIComponent(params.id)}/accept`, {
    body,
  });
}

export async function closeTasksReopen(
  client: ApiClient,
  params: { id: string },
  body: ReopenCloseTaskRequest,
): Promise<CloseTaskDTO> {
  return client.request<CloseTaskDTO>('POST', `/api/v1/close-tasks/${encodeURIComponent(params.id)}/reopen`, {
    body,
  });
}

export async function closeTasksAssign(
  client: ApiClient,
  params: { id: string },
  body: AssignCloseTaskRequest,
): Promise<CloseTaskDTO> {
  return client.request<CloseTaskDTO>('POST', `/api/v1/close-tasks/${encodeURIComponent(params.id)}/assign`, {
    body,
  });
}

export async function closeTasksRunAction(
  client: ApiClient,
  params: { id: string },
  body: RunCloseActionRequest,
  idempotencyKey?: string,
): Promise<CloseTaskDTO> {
  return client.request<CloseTaskDTO>('POST', `/api/v1/close-tasks/${encodeURIComponent(params.id)}/run-action`, {
    body,
    idempotencyKey,
  });
}

export async function closeBoard(
  client: ApiClient,
  query: CloseBoardQuery,
): Promise<CloseBoardDTO> {
  return client.request<CloseBoardDTO>('GET', `/api/v1/close-board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeReadiness(
  client: ApiClient,
  query: CloseReadinessQuery,
): Promise<CloseReadinessDTO> {
  return client.request<CloseReadinessDTO>('GET', `/api/v1/close-readiness`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeCertificationsList(
  client: ApiClient,
  query: CloseCertificationsQuery,
): Promise<PageCloseCertificationDTO> {
  return client.request<PageCloseCertificationDTO>('GET', `/api/v1/close-certifications`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeReconciliation(
  client: ApiClient,
  query: CloseSubledgerReconQuery,
): Promise<CloseSubledgerReconDTO> {
  return client.request<CloseSubledgerReconDTO>('GET', `/api/v1/close-reconciliation`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeEvidencePacksList(
  client: ApiClient,
  query: CloseEvidencePacksQuery,
): Promise<PageCloseEvidencePackSummaryDTO> {
  return client.request<PageCloseEvidencePackSummaryDTO>('GET', `/api/v1/close-evidence-packs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeReportsCycleAnalysis(
  client: ApiClient,
  query: CloseCycleAnalysisQuery,
): Promise<CloseCycleAnalysisDTO> {
  return client.request<CloseCycleAnalysisDTO>('GET', `/api/v1/reports/close-cycle-analysis`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeStatementSetsList(
  client: ApiClient,
  query: CloseStatementSetsQuery,
): Promise<PageCloseStatementSetDTO> {
  return client.request<PageCloseStatementSetDTO>('GET', `/api/v1/close-statement-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeStatementSetsGenerate(
  client: ApiClient,
  body: CloseStatementSetGenerateRequest,
  idempotencyKey?: string,
): Promise<CloseStatementSetDTO> {
  return client.request<CloseStatementSetDTO>('POST', `/api/v1/close-statement-sets`, {
    body,
    idempotencyKey,
  });
}

export async function closeStatementSetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CloseStatementSetDTO> {
  return client.request<CloseStatementSetDTO>('GET', `/api/v1/close-statement-sets/${encodeURIComponent(params.id)}`, {
  });
}

export async function closeStatementSetsPublish(
  client: ApiClient,
  params: { id: string },
  body: CloseStatementSetPublishRequest,
  idempotencyKey?: string,
): Promise<CloseStatementSetDTO> {
  return client.request<CloseStatementSetDTO>('POST', `/api/v1/close-statement-sets/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function closeStatementSetsDownload(
  client: ApiClient,
  params: { id: string },
  query: CloseStatementSetDownloadQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/close-statement-sets/${encodeURIComponent(params.id)}/download`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeAnomalyScansRun(
  client: ApiClient,
  body: CloseAnomalyScanRequest,
  idempotencyKey?: string,
): Promise<CloseAnomalyScanDTO> {
  return client.request<CloseAnomalyScanDTO>('POST', `/api/v1/close-anomaly-scans`, {
    body,
    idempotencyKey,
  });
}

export async function closeAnomalyFindingsList(
  client: ApiClient,
  query: CloseAnomalyFindingsQuery,
): Promise<PageCloseAnomalyFindingDTO> {
  return client.request<PageCloseAnomalyFindingDTO>('GET', `/api/v1/close-anomaly-findings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function closeAnomalyFindingsDisposition(
  client: ApiClient,
  params: { id: string },
  body: CloseAnomalyDispositionRequest,
  idempotencyKey?: string,
): Promise<CloseAnomalyFindingDTO> {
  return client.request<CloseAnomalyFindingDTO>('POST', `/api/v1/close-anomaly-findings/${encodeURIComponent(params.id)}/disposition`, {
    body,
    idempotencyKey,
  });
}

export async function closeReportsTrialBalanceAnomalies(
  client: ApiClient,
  query: CloseTrialBalanceAnomaliesQuery,
): Promise<CloseTrialBalanceAnomaliesDTO> {
  return client.request<CloseTrialBalanceAnomaliesDTO>('GET', `/api/v1/reports/trial-balance-anomalies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrEmployeesList(
  client: ApiClient,
  query: ListEmployeesQuery,
): Promise<PageEmployeeDTO> {
  return client.request<PageEmployeeDTO>('GET', `/api/v1/employees`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrEmployeesCreate(
  client: ApiClient,
  body: CreateEmployeeRequest,
  idempotencyKey?: string,
): Promise<EmployeeDTO> {
  return client.request<EmployeeDTO>('POST', `/api/v1/employees`, {
    body,
    idempotencyKey,
  });
}

export async function hrEmployeesGet(
  client: ApiClient,
  params: { id: string },
): Promise<EmployeeDTO> {
  return client.request<EmployeeDTO>('GET', `/api/v1/employees/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrEmployeesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEmployeeRequest,
): Promise<EmployeeDTO> {
  return client.request<EmployeeDTO>('PATCH', `/api/v1/employees/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrEmployeesDelete(
  client: ApiClient,
  params: { id: string },
  query: HrDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/employees/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrEmployeesTerminate(
  client: ApiClient,
  params: { id: string },
  body: TerminateEmployeeRequest,
  idempotencyKey?: string,
): Promise<EmployeeDTO> {
  return client.request<EmployeeDTO>('POST', `/api/v1/employees/${encodeURIComponent(params.id)}/terminate`, {
    body,
    idempotencyKey,
  });
}

export async function hrEmployeesDetailChangesList(
  client: ApiClient,
  params: { id: string },
): Promise<DetailChangeDTO[]> {
  return client.request<DetailChangeDTO[]>('GET', `/api/v1/employees/${encodeURIComponent(params.id)}/detail-changes`, {
  });
}

export async function hrEmployeesDetailChangesRequest(
  client: ApiClient,
  params: { id: string },
  body: RequestDetailChangeRequest,
  idempotencyKey?: string,
): Promise<DetailChangeDTO> {
  return client.request<DetailChangeDTO>('POST', `/api/v1/employees/${encodeURIComponent(params.id)}/detail-changes`, {
    body,
    idempotencyKey,
  });
}

export async function hrEmployeeDetailChangesDecide(
  client: ApiClient,
  params: { changeId: string },
  body: DecideDetailChangeRequest,
  idempotencyKey?: string,
): Promise<DetailChangeDTO> {
  return client.request<DetailChangeDTO>('POST', `/api/v1/employee-detail-changes/${encodeURIComponent(params.changeId)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function hrEmployeesBankReveal(
  client: ApiClient,
  params: { id: string },
  body: RevealBankRequest,
): Promise<RevealBankResponse> {
  return client.request<RevealBankResponse>('POST', `/api/v1/employees/${encodeURIComponent(params.id)}/bank/reveal`, {
    body,
  });
}

export async function hrLeaveTypesList(
  client: ApiClient,
): Promise<LeaveTypeDTO[]> {
  return client.request<LeaveTypeDTO[]>('GET', `/api/v1/leave-types`, {
  });
}

export async function hrLeaveTypesCreate(
  client: ApiClient,
  body: CreateLeaveTypeRequest,
  idempotencyKey?: string,
): Promise<LeaveTypeDTO> {
  return client.request<LeaveTypeDTO>('POST', `/api/v1/leave-types`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveTypesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLeaveTypeRequest,
): Promise<LeaveTypeDTO> {
  return client.request<LeaveTypeDTO>('PATCH', `/api/v1/leave-types/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrEmployeesLeaveBalancesList(
  client: ApiClient,
  params: { id: string },
): Promise<LeaveBalanceDTO[]> {
  return client.request<LeaveBalanceDTO[]>('GET', `/api/v1/employees/${encodeURIComponent(params.id)}/leave-balances`, {
  });
}

export async function hrLeaveRequestsList(
  client: ApiClient,
  query: ListLeaveRequestsQuery,
): Promise<PageLeaveRequestDTO> {
  return client.request<PageLeaveRequestDTO>('GET', `/api/v1/leave-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrLeaveRequestsCreate(
  client: ApiClient,
  body: CreateLeaveRequestRequest,
  idempotencyKey?: string,
): Promise<LeaveRequestDTO> {
  return client.request<LeaveRequestDTO>('POST', `/api/v1/leave-requests`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveRequestsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LeaveRequestDTO> {
  return client.request<LeaveRequestDTO>('GET', `/api/v1/leave-requests/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrLeaveRequestsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideLeaveRequestRequest,
  idempotencyKey?: string,
): Promise<LeaveRequestDTO> {
  return client.request<LeaveRequestDTO>('POST', `/api/v1/leave-requests/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveRequestsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelLeaveRequestRequest,
  idempotencyKey?: string,
): Promise<LeaveRequestDTO> {
  return client.request<LeaveRequestDTO>('POST', `/api/v1/leave-requests/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveAccrualRun(
  client: ApiClient,
  body: HrRunAccrualRequest,
  idempotencyKey?: string,
): Promise<AccrualRunResult> {
  return client.request<AccrualRunResult>('POST', `/api/v1/leave/accrual/run`, {
    body,
    idempotencyKey,
  });
}

export async function hrOvertimeRulesList(
  client: ApiClient,
): Promise<OvertimeRuleDTO[]> {
  return client.request<OvertimeRuleDTO[]>('GET', `/api/v1/overtime-rules`, {
  });
}

export async function hrOvertimeRulesCreate(
  client: ApiClient,
  body: CreateOvertimeRuleRequest,
  idempotencyKey?: string,
): Promise<OvertimeRuleDTO> {
  return client.request<OvertimeRuleDTO>('POST', `/api/v1/overtime-rules`, {
    body,
    idempotencyKey,
  });
}

export async function hrOvertimeRulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateOvertimeRuleRequest,
): Promise<OvertimeRuleDTO> {
  return client.request<OvertimeRuleDTO>('PATCH', `/api/v1/overtime-rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrAttendanceList(
  client: ApiClient,
  query: ListAttendanceQuery,
): Promise<PageAttendanceDTO> {
  return client.request<PageAttendanceDTO>('GET', `/api/v1/attendance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrAttendanceCreate(
  client: ApiClient,
  body: CreateAttendanceRequest,
  idempotencyKey?: string,
): Promise<AttendanceDTO> {
  return client.request<AttendanceDTO>('POST', `/api/v1/attendance`, {
    body,
    idempotencyKey,
  });
}

export async function hrAttendanceUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateAttendanceRequest,
): Promise<AttendanceDTO> {
  return client.request<AttendanceDTO>('PATCH', `/api/v1/attendance/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrAttendanceApprove(
  client: ApiClient,
  params: { id: string },
  body: ApproveAttendanceRequest,
  idempotencyKey?: string,
): Promise<AttendanceDTO> {
  return client.request<AttendanceDTO>('POST', `/api/v1/attendance/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function hrAttendanceImport(
  client: ApiClient,
  body: ImportAttendanceRequest,
  idempotencyKey?: string,
): Promise<ImportAttendanceResult> {
  return client.request<ImportAttendanceResult>('POST', `/api/v1/attendance/import`, {
    body,
    idempotencyKey,
  });
}

export async function hrRequisitionsList(
  client: ApiClient,
  query: HrListRequisitionsQuery,
): Promise<PageRequisitionDTO> {
  return client.request<PageRequisitionDTO>('GET', `/api/v1/requisitions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrRequisitionsCreate(
  client: ApiClient,
  body: HrCreateRequisitionRequest,
  idempotencyKey?: string,
): Promise<RequisitionDTO> {
  return client.request<RequisitionDTO>('POST', `/api/v1/requisitions`, {
    body,
    idempotencyKey,
  });
}

export async function hrRequisitionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RequisitionDTO> {
  return client.request<RequisitionDTO>('GET', `/api/v1/requisitions/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrRequisitionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: HrUpdateRequisitionRequest,
): Promise<RequisitionDTO> {
  return client.request<RequisitionDTO>('PATCH', `/api/v1/requisitions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrRequisitionsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitRequisitionRequest,
  idempotencyKey?: string,
): Promise<RequisitionDTO> {
  return client.request<RequisitionDTO>('POST', `/api/v1/requisitions/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function hrCandidatesList(
  client: ApiClient,
  query: ListCandidatesQuery,
): Promise<PageCandidateDTO> {
  return client.request<PageCandidateDTO>('GET', `/api/v1/candidates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrCandidatesCreate(
  client: ApiClient,
  body: CreateCandidateRequest,
  idempotencyKey?: string,
): Promise<CandidateDTO> {
  return client.request<CandidateDTO>('POST', `/api/v1/candidates`, {
    body,
    idempotencyKey,
  });
}

export async function hrCandidatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CandidateDTO> {
  return client.request<CandidateDTO>('GET', `/api/v1/candidates/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrCandidatesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCandidateRequest,
): Promise<CandidateDTO> {
  return client.request<CandidateDTO>('PATCH', `/api/v1/candidates/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrCandidatesTimeline(
  client: ApiClient,
  params: { id: string },
): Promise<CandidateStageEventDTO[]> {
  return client.request<CandidateStageEventDTO[]>('GET', `/api/v1/candidates/${encodeURIComponent(params.id)}/timeline`, {
  });
}

export async function hrCandidatesAdvance(
  client: ApiClient,
  params: { id: string },
  body: AdvanceStageRequest,
  idempotencyKey?: string,
): Promise<CandidateDTO> {
  return client.request<CandidateDTO>('POST', `/api/v1/candidates/${encodeURIComponent(params.id)}/advance`, {
    body,
    idempotencyKey,
  });
}

export async function hrCandidatesConvert(
  client: ApiClient,
  params: { id: string },
  body: ConvertCandidateRequest,
  idempotencyKey?: string,
): Promise<ConvertCandidateResult> {
  return client.request<ConvertCandidateResult>('POST', `/api/v1/candidates/${encodeURIComponent(params.id)}/convert`, {
    body,
    idempotencyKey,
  });
}

export async function hrOffersList(
  client: ApiClient,
  query: ListOffersQuery,
): Promise<PageOfferDTO> {
  return client.request<PageOfferDTO>('GET', `/api/v1/offers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrOffersCreate(
  client: ApiClient,
  body: CreateOfferRequest,
  idempotencyKey?: string,
): Promise<OfferDTO> {
  return client.request<OfferDTO>('POST', `/api/v1/offers`, {
    body,
    idempotencyKey,
  });
}

export async function hrOffersGet(
  client: ApiClient,
  params: { id: string },
): Promise<OfferDTO> {
  return client.request<OfferDTO>('GET', `/api/v1/offers/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrOffersDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideOfferRequest,
  idempotencyKey?: string,
): Promise<OfferDTO> {
  return client.request<OfferDTO>('POST', `/api/v1/offers/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function hrMeEmployeeGet(
  client: ApiClient,
): Promise<EmployeeDTO> {
  return client.request<EmployeeDTO>('GET', `/api/v1/me/employee`, {
  });
}

export async function hrMeLeaveBalancesList(
  client: ApiClient,
): Promise<LeaveBalanceDTO[]> {
  return client.request<LeaveBalanceDTO[]>('GET', `/api/v1/me/leave-balances`, {
  });
}

export async function hrMeLeaveRequestsList(
  client: ApiClient,
  query: ListLeaveRequestsQuery,
): Promise<PageLeaveRequestDTO> {
  return client.request<PageLeaveRequestDTO>('GET', `/api/v1/me/leave-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrMeLeaveRequestsCreate(
  client: ApiClient,
  body: CreateLeaveRequestRequest,
  idempotencyKey?: string,
): Promise<LeaveRequestDTO> {
  return client.request<LeaveRequestDTO>('POST', `/api/v1/me/leave-requests`, {
    body,
    idempotencyKey,
  });
}

export async function hrMeLeaveRequestsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelLeaveRequestRequest,
  idempotencyKey?: string,
): Promise<LeaveRequestDTO> {
  return client.request<LeaveRequestDTO>('POST', `/api/v1/me/leave-requests/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function hrMeAttendanceList(
  client: ApiClient,
  query: ListAttendanceQuery,
): Promise<PageAttendanceDTO> {
  return client.request<PageAttendanceDTO>('GET', `/api/v1/me/attendance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrMeDocumentsList(
  client: ApiClient,
  query: CursorQuery,
): Promise<PageEmployeeDocumentDTO> {
  return client.request<PageEmployeeDocumentDTO>('GET', `/api/v1/me/documents`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrMeDocumentsDownload(
  client: ApiClient,
  params: { id: string },
): Promise<EmployeeDocumentDownloadDTO> {
  return client.request<EmployeeDocumentDownloadDTO>('GET', `/api/v1/me/documents/${encodeURIComponent(params.id)}/download`, {
  });
}

export async function hrAssignmentsList(
  client: ApiClient,
  params: { id: string },
  query: HrListAssignmentsQuery,
): Promise<PageEmploymentAssignmentDTO> {
  return client.request<PageEmploymentAssignmentDTO>('GET', `/api/v1/employees/${encodeURIComponent(params.id)}/assignments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrAssignmentsAsAt(
  client: ApiClient,
  params: { id: string },
  query: AsAtAssignmentQuery,
): Promise<EmploymentAssignmentDTO> {
  return client.request<EmploymentAssignmentDTO>('GET', `/api/v1/employees/${encodeURIComponent(params.id)}/assignments/as-at`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrAssignmentsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateAssignmentRequest,
  idempotencyKey?: string,
): Promise<EmploymentAssignmentDTO> {
  return client.request<EmploymentAssignmentDTO>('POST', `/api/v1/employees/${encodeURIComponent(params.id)}/assignments`, {
    body,
    idempotencyKey,
  });
}

export async function hrAssignmentsSupersede(
  client: ApiClient,
  params: { id: string },
  body: SupersedeAssignmentRequest,
  idempotencyKey?: string,
): Promise<SupersedeAssignmentResultDTO> {
  return client.request<SupersedeAssignmentResultDTO>('POST', `/api/v1/employees/${encodeURIComponent(params.id)}/assignments/supersede`, {
    body,
    idempotencyKey,
  });
}

export async function hrAssignmentsSupersedePreview(
  client: ApiClient,
  params: { id: string },
  body: SupersedeAssignmentRequest,
): Promise<SupersedeAssignmentPreviewDTO> {
  return client.request<SupersedeAssignmentPreviewDTO>('POST', `/api/v1/employees/${encodeURIComponent(params.id)}/assignments/supersede/preview`, {
    body,
  });
}

export async function hrAssignmentsCorrect(
  client: ApiClient,
  params: { id: string },
  body: CorrectAssignmentRequest,
): Promise<EmploymentAssignmentDTO> {
  return client.request<EmploymentAssignmentDTO>('PATCH', `/api/v1/employment-assignments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrAssignmentsEnd(
  client: ApiClient,
  params: { id: string },
  body: HrEndAssignmentRequest,
  idempotencyKey?: string,
): Promise<EndAssignmentResultDTO> {
  return client.request<EndAssignmentResultDTO>('POST', `/api/v1/employment-assignments/${encodeURIComponent(params.id)}/end`, {
    body,
    idempotencyKey,
  });
}

export async function hrMeAssignments(
  client: ApiClient,
  query: HrListAssignmentsQuery,
): Promise<PageEmploymentAssignmentDTO> {
  return client.request<PageEmploymentAssignmentDTO>('GET', `/api/v1/me/employment-assignments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrPayGroupsList(
  client: ApiClient,
  query: ListPayGroupsQuery,
): Promise<PagePayGroupDTO> {
  return client.request<PagePayGroupDTO>('GET', `/api/v1/pay-groups`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrPayGroupsCreate(
  client: ApiClient,
  body: CreatePayGroupRequest,
  idempotencyKey?: string,
): Promise<PayGroupDTO> {
  return client.request<PayGroupDTO>('POST', `/api/v1/pay-groups`, {
    body,
    idempotencyKey,
  });
}

export async function hrPayGroupsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PayGroupDTO> {
  return client.request<PayGroupDTO>('GET', `/api/v1/pay-groups/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrPayGroupsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePayGroupRequest,
): Promise<PayGroupDTO> {
  return client.request<PayGroupDTO>('PATCH', `/api/v1/pay-groups/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrPermitsList(
  client: ApiClient,
  query: ListWorkPermitsQuery,
): Promise<PageWorkPermitDTO> {
  return client.request<PageWorkPermitDTO>('GET', `/api/v1/work-permits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrPermitsCreate(
  client: ApiClient,
  body: CreateWorkPermitRequest,
  idempotencyKey?: string,
): Promise<WorkPermitDTO> {
  return client.request<WorkPermitDTO>('POST', `/api/v1/work-permits`, {
    body,
    idempotencyKey,
  });
}

export async function hrPermitsGet(
  client: ApiClient,
  params: { id: string },
): Promise<WorkPermitDTO> {
  return client.request<WorkPermitDTO>('GET', `/api/v1/work-permits/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrPermitsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWorkPermitRequest,
): Promise<WorkPermitDTO> {
  return client.request<WorkPermitDTO>('PATCH', `/api/v1/work-permits/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrPermitsReveal(
  client: ApiClient,
  params: { id: string },
  body: RevealPermitRequest,
): Promise<RevealPermitResponse> {
  return client.request<RevealPermitResponse>('POST', `/api/v1/work-permits/${encodeURIComponent(params.id)}/reveal`, {
    body,
  });
}

export async function hrPermitsRecordClearance(
  client: ApiClient,
  params: { id: string },
  body: RecordPermitClearanceRequest,
): Promise<WorkPermitDTO> {
  return client.request<WorkPermitDTO>('POST', `/api/v1/work-permits/${encodeURIComponent(params.id)}/clearance`, {
    body,
  });
}

export async function hrPermitQuotasList(
  client: ApiClient,
  query: ListPermitQuotasQuery,
): Promise<PagePermitQuotaDTO> {
  return client.request<PagePermitQuotaDTO>('GET', `/api/v1/permit-quotas`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrPermitQuotasCreate(
  client: ApiClient,
  body: CreatePermitQuotaRequest,
  idempotencyKey?: string,
): Promise<PermitQuotaDTO> {
  return client.request<PermitQuotaDTO>('POST', `/api/v1/permit-quotas`, {
    body,
    idempotencyKey,
  });
}

export async function hrPermitQuotasUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePermitQuotaRequest,
): Promise<PermitQuotaDTO> {
  return client.request<PermitQuotaDTO>('PATCH', `/api/v1/permit-quotas/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrMePermits(
  client: ApiClient,
  query: ListWorkPermitsQuery,
): Promise<PageWorkPermitDTO> {
  return client.request<PageWorkPermitDTO>('GET', `/api/v1/me/work-permits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrMeClock(
  client: ApiClient,
  body: RecordClockEventRequest,
  idempotencyKey?: string,
): Promise<ClockEventDTO> {
  return client.request<ClockEventDTO>('POST', `/api/v1/me/clock-events`, {
    body,
    idempotencyKey,
  });
}

export async function hrMeClockEvents(
  client: ApiClient,
): Promise<MyClockStateDTO> {
  return client.request<MyClockStateDTO>('GET', `/api/v1/me/clock-events`, {
  });
}

export async function hrClockEventsList(
  client: ApiClient,
  query: ListClockEventsQuery,
): Promise<PageClockEventDTO> {
  return client.request<PageClockEventDTO>('GET', `/api/v1/clock-events`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrClockEventsRecord(
  client: ApiClient,
  body: RecordClockEventForRequest,
  idempotencyKey?: string,
): Promise<ClockEventDTO> {
  return client.request<ClockEventDTO>('POST', `/api/v1/clock-events`, {
    body,
    idempotencyKey,
  });
}

export async function hrClockEventsCorrect(
  client: ApiClient,
  params: { id: string },
  body: CorrectClockEventRequest,
  idempotencyKey?: string,
): Promise<ClockEventDTO> {
  return client.request<ClockEventDTO>('POST', `/api/v1/clock-events/${encodeURIComponent(params.id)}/correct`, {
    body,
    idempotencyKey,
  });
}

export async function hrExceptionsList(
  client: ApiClient,
  query: ListAttendanceExceptionsQuery,
): Promise<PageAttendanceExceptionDTO> {
  return client.request<PageAttendanceExceptionDTO>('GET', `/api/v1/attendance-exceptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrExceptionsResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveAttendanceExceptionRequest,
): Promise<AttendanceExceptionDTO> {
  return client.request<AttendanceExceptionDTO>('POST', `/api/v1/attendance-exceptions/${encodeURIComponent(params.id)}/resolve`, {
    body,
  });
}

export async function hrExceptionsDismiss(
  client: ApiClient,
  params: { id: string },
  body: DismissAttendanceExceptionRequest,
): Promise<AttendanceExceptionDTO> {
  return client.request<AttendanceExceptionDTO>('POST', `/api/v1/attendance-exceptions/${encodeURIComponent(params.id)}/dismiss`, {
    body,
  });
}

export async function hrAttendanceLocksList(
  client: ApiClient,
  query: ListAttendanceLocksQuery,
): Promise<PageAttendanceLockDTO> {
  return client.request<PageAttendanceLockDTO>('GET', `/api/v1/attendance-locks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrAttendanceLocksRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseAttendanceLockRequest,
  idempotencyKey?: string,
): Promise<AttendanceLockDTO> {
  return client.request<AttendanceLockDTO>('POST', `/api/v1/attendance-locks/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveTypesGetPolicy(
  client: ApiClient,
  params: { id: string },
): Promise<LeaveTypePolicyDTO> {
  return client.request<LeaveTypePolicyDTO>('GET', `/api/v1/leave-types/${encodeURIComponent(params.id)}/policy`, {
  });
}

export async function hrLeaveTypesUpdatePolicy(
  client: ApiClient,
  params: { id: string },
  body: UpdateLeaveTypePolicyRequest,
): Promise<LeaveTypePolicyDTO> {
  return client.request<LeaveTypePolicyDTO>('PATCH', `/api/v1/leave-types/${encodeURIComponent(params.id)}/policy`, {
    body,
  });
}

export async function hrLeaveMovementsList(
  client: ApiClient,
  params: { employeeId: string; leaveTypeId: string },
  query: ListLeaveMovementsQuery,
): Promise<PageLeaveMovementDTO> {
  return client.request<PageLeaveMovementDTO>('GET', `/api/v1/leave-balances/${encodeURIComponent(params.employeeId)}/${encodeURIComponent(params.leaveTypeId)}/movements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrMeLeaveMovements(
  client: ApiClient,
  params: { leaveTypeId: string },
  query: ListLeaveMovementsQuery,
): Promise<PageLeaveMovementDTO> {
  return client.request<PageLeaveMovementDTO>('GET', `/api/v1/me/leave-balances/${encodeURIComponent(params.leaveTypeId)}/movements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrLeaveEncash(
  client: ApiClient,
  params: { employeeId: string; leaveTypeId: string },
  body: EncashLeaveRequest,
  idempotencyKey?: string,
): Promise<LeaveMovementDTO> {
  return client.request<LeaveMovementDTO>('POST', `/api/v1/leave-balances/${encodeURIComponent(params.employeeId)}/${encodeURIComponent(params.leaveTypeId)}/encash`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveCarryForwardRun(
  client: ApiClient,
  body: CarryForwardRunRequest,
): Promise<CarryForwardRunResult> {
  return client.request<CarryForwardRunResult>('POST', `/api/v1/leave/carry-forward/run`, {
    body,
  });
}

export async function hrLeaveBlackoutsList(
  client: ApiClient,
  query: ListLeaveBlackoutsQuery,
): Promise<PageLeaveBlackoutDTO> {
  return client.request<PageLeaveBlackoutDTO>('GET', `/api/v1/leave-blackouts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrLeaveBlackoutsCreate(
  client: ApiClient,
  body: CreateLeaveBlackoutRequest,
  idempotencyKey?: string,
): Promise<LeaveBlackoutDTO> {
  return client.request<LeaveBlackoutDTO>('POST', `/api/v1/leave-blackouts`, {
    body,
    idempotencyKey,
  });
}

export async function hrLeaveBlackoutsGet(
  client: ApiClient,
  params: { id: string },
): Promise<LeaveBlackoutDTO> {
  return client.request<LeaveBlackoutDTO>('GET', `/api/v1/leave-blackouts/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrLeaveBlackoutsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateLeaveBlackoutRequest,
): Promise<LeaveBlackoutDTO> {
  return client.request<LeaveBlackoutDTO>('PATCH', `/api/v1/leave-blackouts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrLeaveBlackoutsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/leave-blackouts/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrCoverageList(
  client: ApiClient,
  query: ListCoverageMinimumsQuery,
): Promise<PageCoverageMinimumDTO> {
  return client.request<PageCoverageMinimumDTO>('GET', `/api/v1/coverage-minimums`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrCoverageCreate(
  client: ApiClient,
  body: CreateCoverageMinimumRequest,
  idempotencyKey?: string,
): Promise<CoverageMinimumDTO> {
  return client.request<CoverageMinimumDTO>('POST', `/api/v1/coverage-minimums`, {
    body,
    idempotencyKey,
  });
}

export async function hrCoverageGet(
  client: ApiClient,
  params: { id: string },
): Promise<CoverageMinimumDTO> {
  return client.request<CoverageMinimumDTO>('GET', `/api/v1/coverage-minimums/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrCoverageUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCoverageMinimumRequest,
): Promise<CoverageMinimumDTO> {
  return client.request<CoverageMinimumDTO>('PATCH', `/api/v1/coverage-minimums/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrLeaveRequestsPreview(
  client: ApiClient,
  body: LeaveRequestPreviewRequest,
): Promise<LeaveRequestPreviewResponse> {
  return client.request<LeaveRequestPreviewResponse>('POST', `/api/v1/leave-requests/preview`, {
    body,
  });
}

export async function hrExitsList(
  client: ApiClient,
  query: ListExitsQuery,
): Promise<PageExitDTO> {
  return client.request<PageExitDTO>('GET', `/api/v1/employee-exits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrExitsInitiate(
  client: ApiClient,
  body: InitiateExitRequest,
  idempotencyKey?: string,
): Promise<ExitDTO> {
  return client.request<ExitDTO>('POST', `/api/v1/employee-exits`, {
    body,
    idempotencyKey,
  });
}

export async function hrExitsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ExitDTO> {
  return client.request<ExitDTO>('GET', `/api/v1/employee-exits/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrClearanceList(
  client: ApiClient,
  params: { id: string },
): Promise<ExitClearanceItemDTO[]> {
  return client.request<ExitClearanceItemDTO[]>('GET', `/api/v1/employee-exits/${encodeURIComponent(params.id)}/clearance`, {
  });
}

export async function hrClearanceAdd(
  client: ApiClient,
  params: { id: string },
  body: AddClearanceItemRequest,
  idempotencyKey?: string,
): Promise<ExitClearanceItemDTO> {
  return client.request<ExitClearanceItemDTO>('POST', `/api/v1/employee-exits/${encodeURIComponent(params.id)}/clearance`, {
    body,
    idempotencyKey,
  });
}

export async function hrClearanceClear(
  client: ApiClient,
  params: { id: string; itemId: string },
  body: ClearItemRequest,
): Promise<ExitClearanceItemDTO> {
  return client.request<ExitClearanceItemDTO>('PATCH', `/api/v1/employee-exits/${encodeURIComponent(params.id)}/clearance/${encodeURIComponent(params.itemId)}`, {
    body,
  });
}

export async function hrClearanceWaive(
  client: ApiClient,
  params: { id: string; itemId: string },
  body: WaiveItemRequest,
): Promise<ExitClearanceItemDTO> {
  return client.request<ExitClearanceItemDTO>('POST', `/api/v1/employee-exits/${encodeURIComponent(params.id)}/clearance/${encodeURIComponent(params.itemId)}/waive`, {
    body,
  });
}

export async function hrExitsFinalPay(
  client: ApiClient,
  params: { id: string },
  body: FinalPayRequest,
  idempotencyKey?: string,
): Promise<FinalPayResponse> {
  return client.request<FinalPayResponse>('POST', `/api/v1/employee-exits/${encodeURIComponent(params.id)}/final-pay`, {
    body,
    idempotencyKey,
  });
}

export async function hrExitsSettle(
  client: ApiClient,
  params: { id: string },
  body: SettleExitRequest,
): Promise<ExitDTO> {
  return client.request<ExitDTO>('POST', `/api/v1/employee-exits/${encodeURIComponent(params.id)}/settle`, {
    body,
  });
}

export async function hrScorecardsList(
  client: ApiClient,
  params: { id: string },
  query: ListScorecardsQuery,
): Promise<PageCandidateScorecardDTO> {
  return client.request<PageCandidateScorecardDTO>('GET', `/api/v1/candidates/${encodeURIComponent(params.id)}/scorecards`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrScorecardsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitScorecardRequest,
  idempotencyKey?: string,
): Promise<CandidateScorecardDTO> {
  return client.request<CandidateScorecardDTO>('POST', `/api/v1/candidates/${encodeURIComponent(params.id)}/scorecards`, {
    body,
    idempotencyKey,
  });
}

export async function hrScorecardsGet(
  client: ApiClient,
  params: { id: string; cardId: string },
): Promise<CandidateScorecardDTO> {
  return client.request<CandidateScorecardDTO>('GET', `/api/v1/candidates/${encodeURIComponent(params.id)}/scorecards/${encodeURIComponent(params.cardId)}`, {
  });
}

export async function hrReferencesList(
  client: ApiClient,
  params: { id: string },
  query: ListReferencesQuery,
): Promise<PageCandidateReferenceDTO> {
  return client.request<PageCandidateReferenceDTO>('GET', `/api/v1/candidates/${encodeURIComponent(params.id)}/references`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrReferencesAdd(
  client: ApiClient,
  params: { id: string },
  body: AddReferenceRequest,
  idempotencyKey?: string,
): Promise<CandidateReferenceDTO> {
  return client.request<CandidateReferenceDTO>('POST', `/api/v1/candidates/${encodeURIComponent(params.id)}/references`, {
    body,
    idempotencyKey,
  });
}

export async function hrReferencesUpdate(
  client: ApiClient,
  params: { id: string; refId: string },
  body: UpdateReferenceRequest,
): Promise<CandidateReferenceDTO> {
  return client.request<CandidateReferenceDTO>('PATCH', `/api/v1/candidates/${encodeURIComponent(params.id)}/references/${encodeURIComponent(params.refId)}`, {
    body,
  });
}

export async function hrRightToWorkVerify(
  client: ApiClient,
  params: { id: string },
  body: VerifyRightToWorkRequest,
): Promise<RightToWorkDTO> {
  return client.request<RightToWorkDTO>('POST', `/api/v1/candidates/${encodeURIComponent(params.id)}/right-to-work`, {
    body,
  });
}

export async function hrRequisitionsSetPosition(
  client: ApiClient,
  params: { id: string },
  body: SetRequisitionPositionRequest,
): Promise<RequisitionPositionDTO> {
  return client.request<RequisitionPositionDTO>('PATCH', `/api/v1/requisitions/${encodeURIComponent(params.id)}/position`, {
    body,
  });
}

export async function hrOffersSetPosition(
  client: ApiClient,
  params: { id: string },
  body: SetOfferPositionRequest,
): Promise<OfferPositionDTO> {
  return client.request<OfferPositionDTO>('PATCH', `/api/v1/offers/${encodeURIComponent(params.id)}/position`, {
    body,
  });
}

export async function hrOffersRangeCheck(
  client: ApiClient,
  params: { id: string },
  body: RangeCheckRequest,
  idempotencyKey?: string,
): Promise<RangeCheckResponse> {
  return client.request<RangeCheckResponse>('POST', `/api/v1/offers/${encodeURIComponent(params.id)}/range-check`, {
    body,
    idempotencyKey,
  });
}

export async function hrSkillsList(
  client: ApiClient,
  query: ListHRSkillsQuery,
): Promise<PageHRSkillDTO> {
  return client.request<PageHRSkillDTO>('GET', `/api/v1/hr-skills`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrSkillsCreate(
  client: ApiClient,
  body: CreateHRSkillRequest,
  idempotencyKey?: string,
): Promise<HRSkillDTO> {
  return client.request<HRSkillDTO>('POST', `/api/v1/hr-skills`, {
    body,
    idempotencyKey,
  });
}

export async function hrSkillsGet(
  client: ApiClient,
  params: { id: string },
): Promise<HRSkillDTO> {
  return client.request<HRSkillDTO>('GET', `/api/v1/hr-skills/${encodeURIComponent(params.id)}`, {
  });
}

export async function hrSkillsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateHRSkillRequest,
): Promise<HRSkillDTO> {
  return client.request<HRSkillDTO>('PATCH', `/api/v1/hr-skills/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function hrSkillsExpiring(
  client: ApiClient,
  query: ListExpiringHRSkillsQuery,
): Promise<PageHRPersonSkillDTO> {
  return client.request<PageHRPersonSkillDTO>('GET', `/api/v1/hr-skills/expiring`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrSkillsQualifiedFor(
  client: ApiClient,
  body: QualifiedForRequest,
): Promise<QualifiedForResponse> {
  return client.request<QualifiedForResponse>('POST', `/api/v1/hr-skills/qualified`, {
    body,
  });
}

export async function hrEmployeesSkillsList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageHRPersonSkillDTO> {
  return client.request<PageHRPersonSkillDTO>('GET', `/api/v1/hr-employees/${encodeURIComponent(params.id)}/skills`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function hrEmployeesSkillsGrant(
  client: ApiClient,
  params: { id: string },
  body: GrantHRSkillRequest,
  idempotencyKey?: string,
): Promise<HRPersonSkillDTO> {
  return client.request<HRPersonSkillDTO>('POST', `/api/v1/hr-employees/${encodeURIComponent(params.id)}/skills`, {
    body,
    idempotencyKey,
  });
}

export async function hrEmployeesSkillsRevoke(
  client: ApiClient,
  params: { id: string },
  query: RevokeHRSkillQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/hr-employees/${encodeURIComponent(params.id)}/skills`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterShiftDefinitionsList(
  client: ApiClient,
  query: ListShiftDefinitionsQuery,
): Promise<PageShiftDefinitionDTO> {
  return client.request<PageShiftDefinitionDTO>('GET', `/api/v1/shift-definitions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterShiftDefinitionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ShiftDefinitionDTO> {
  return client.request<ShiftDefinitionDTO>('GET', `/api/v1/shift-definitions/${encodeURIComponent(params.id)}`, {
  });
}

export async function rosterShiftDefinitionsCreate(
  client: ApiClient,
  body: CreateShiftDefinitionRequest,
  idempotencyKey?: string,
): Promise<ShiftDefinitionDTO> {
  return client.request<ShiftDefinitionDTO>('POST', `/api/v1/shift-definitions`, {
    body,
    idempotencyKey,
  });
}

export async function rosterShiftDefinitionsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateShiftDefinitionRequest,
): Promise<ShiftDefinitionDTO> {
  return client.request<ShiftDefinitionDTO>('PATCH', `/api/v1/shift-definitions/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rosterShiftDefinitionsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/shift-definitions/${encodeURIComponent(params.id)}`, {
  });
}

export async function rosterShiftPatternsList(
  client: ApiClient,
  query: ListShiftPatternsQuery,
): Promise<PageShiftPatternDTO> {
  return client.request<PageShiftPatternDTO>('GET', `/api/v1/shift-patterns`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterShiftPatternsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ShiftPatternDTO> {
  return client.request<ShiftPatternDTO>('GET', `/api/v1/shift-patterns/${encodeURIComponent(params.id)}`, {
  });
}

export async function rosterShiftPatternsCreate(
  client: ApiClient,
  body: CreateShiftPatternRequest,
  idempotencyKey?: string,
): Promise<ShiftPatternDTO> {
  return client.request<ShiftPatternDTO>('POST', `/api/v1/shift-patterns`, {
    body,
    idempotencyKey,
  });
}

export async function rosterShiftPatternsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateShiftPatternRequest,
): Promise<ShiftPatternDTO> {
  return client.request<ShiftPatternDTO>('PATCH', `/api/v1/shift-patterns/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rosterRostersList(
  client: ApiClient,
  query: ListRostersQuery,
): Promise<PageRosterDTO> {
  return client.request<PageRosterDTO>('GET', `/api/v1/rosters`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterRostersGet(
  client: ApiClient,
  params: { id: string },
): Promise<RosterDetailDTO> {
  return client.request<RosterDetailDTO>('GET', `/api/v1/rosters/${encodeURIComponent(params.id)}`, {
  });
}

export async function rosterRostersCreate(
  client: ApiClient,
  body: CreateRosterRequest,
  idempotencyKey?: string,
): Promise<RosterDTO> {
  return client.request<RosterDTO>('POST', `/api/v1/rosters`, {
    body,
    idempotencyKey,
  });
}

export async function rosterRostersReplaceLines(
  client: ApiClient,
  params: { id: string },
  body: ReplaceRosterLinesRequest,
): Promise<RosterDetailDTO> {
  return client.request<RosterDetailDTO>('PUT', `/api/v1/rosters/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function rosterRostersPropose(
  client: ApiClient,
  params: { id: string },
  body: ProposeRosterRequest,
): Promise<ProposeRosterResultDTO> {
  return client.request<ProposeRosterResultDTO>('POST', `/api/v1/rosters/${encodeURIComponent(params.id)}/propose`, {
    body,
  });
}

export async function rosterRostersEvaluate(
  client: ApiClient,
  params: { id: string },
): Promise<EvaluateRosterResultDTO> {
  return client.request<EvaluateRosterResultDTO>('POST', `/api/v1/rosters/${encodeURIComponent(params.id)}/evaluate`, {
  });
}

export async function rosterRostersPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishRosterRequest,
  idempotencyKey?: string,
): Promise<PublishRosterResultDTO> {
  return client.request<PublishRosterResultDTO>('POST', `/api/v1/rosters/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function rosterRostersLock(
  client: ApiClient,
  params: { id: string },
  body: LockRosterRequest,
): Promise<RosterDTO> {
  return client.request<RosterDTO>('POST', `/api/v1/rosters/${encodeURIComponent(params.id)}/lock`, {
    body,
  });
}

export async function rosterRostersUnlock(
  client: ApiClient,
  params: { id: string },
  body: UnlockRosterRequest,
): Promise<RosterDTO> {
  return client.request<RosterDTO>('POST', `/api/v1/rosters/${encodeURIComponent(params.id)}/unlock`, {
    body,
  });
}

export async function rosterRostersCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRosterRequest,
): Promise<RosterDTO> {
  return client.request<RosterDTO>('POST', `/api/v1/rosters/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function rosterEvaluationsList(
  client: ApiClient,
  query: ListRuleEvaluationsQuery,
): Promise<PageRuleEvaluationDTO> {
  return client.request<PageRuleEvaluationDTO>('GET', `/api/v1/roster-rule-evaluations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterWorkingTimeRulesList(
  client: ApiClient,
  query: ListWorkingTimeRulesQuery,
): Promise<PageWorkingTimeRuleDTO> {
  return client.request<PageWorkingTimeRuleDTO>('GET', `/api/v1/working-time-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterWorkingTimeRulesGet(
  client: ApiClient,
  params: { id: string },
): Promise<WorkingTimeRuleDTO> {
  return client.request<WorkingTimeRuleDTO>('GET', `/api/v1/working-time-rules/${encodeURIComponent(params.id)}`, {
  });
}

export async function rosterWorkingTimeRulesCreate(
  client: ApiClient,
  body: CreateWorkingTimeRuleRequest,
  idempotencyKey?: string,
): Promise<WorkingTimeRuleDTO> {
  return client.request<WorkingTimeRuleDTO>('POST', `/api/v1/working-time-rules`, {
    body,
    idempotencyKey,
  });
}

export async function rosterWorkingTimeRulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWorkingTimeRuleRequest,
): Promise<WorkingTimeRuleDTO> {
  return client.request<WorkingTimeRuleDTO>('PATCH', `/api/v1/working-time-rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rosterDemandList(
  client: ApiClient,
  query: ListDemandQuery,
): Promise<PageDemandInputDTO> {
  return client.request<PageDemandInputDTO>('GET', `/api/v1/roster-demand`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterDemandUpsert(
  client: ApiClient,
  body: UpsertDemandRequest,
  idempotencyKey?: string,
): Promise<DemandInputDTO> {
  return client.request<DemandInputDTO>('PUT', `/api/v1/roster-demand`, {
    body,
    idempotencyKey,
  });
}

export async function rosterDemandDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/roster-demand/${encodeURIComponent(params.id)}`, {
  });
}

export async function rosterDemandImport(
  client: ApiClient,
  body: ImportDemandRequest,
  idempotencyKey?: string,
): Promise<ImportDemandResultDTO> {
  return client.request<ImportDemandResultDTO>('POST', `/api/v1/roster-demand/import`, {
    body,
    idempotencyKey,
  });
}

export async function rosterStaffingRulesList(
  client: ApiClient,
  query: ListStaffingRulesQuery,
): Promise<PageStaffingRuleDTO> {
  return client.request<PageStaffingRuleDTO>('GET', `/api/v1/staffing-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterStaffingRulesCreate(
  client: ApiClient,
  body: CreateStaffingRuleRequest,
  idempotencyKey?: string,
): Promise<StaffingRuleDTO> {
  return client.request<StaffingRuleDTO>('POST', `/api/v1/staffing-rules`, {
    body,
    idempotencyKey,
  });
}

export async function rosterStaffingRulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateStaffingRuleRequest,
): Promise<StaffingRuleDTO> {
  return client.request<StaffingRuleDTO>('PATCH', `/api/v1/staffing-rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rosterLabourCostReport(
  client: ApiClient,
  query: LabourCostQuery,
): Promise<LabourCostReportDTO> {
  return client.request<LabourCostReportDTO>('GET', `/api/v1/labour-cost`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterSettingsGet(
  client: ApiClient,
  query: RosterSettingsQuery,
): Promise<RosterSettingsDTO> {
  return client.request<RosterSettingsDTO>('GET', `/api/v1/roster-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rosterSettingsUpdate(
  client: ApiClient,
  query: RosterSettingsQuery,
  body: UpdateRosterSettingsRequest,
): Promise<RosterSettingsDTO> {
  return client.request<RosterSettingsDTO>('PUT', `/api/v1/roster-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
    body,
  });
}

export async function rosterMeRoster(
  client: ApiClient,
  query: MyRosterQuery,
): Promise<MyRosterDTO> {
  return client.request<MyRosterDTO>('GET', `/api/v1/me/roster`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollComponentsList(
  client: ApiClient,
  query: ListComponentsQuery,
): Promise<PageComponentDTO> {
  return client.request<PageComponentDTO>('GET', `/api/v1/payroll-components`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollComponentsCreate(
  client: ApiClient,
  body: CreateComponentRequest,
  idempotencyKey?: string,
): Promise<ComponentDTO> {
  return client.request<ComponentDTO>('POST', `/api/v1/payroll-components`, {
    body,
    idempotencyKey,
  });
}

export async function payrollComponentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ComponentDTO> {
  return client.request<ComponentDTO>('GET', `/api/v1/payroll-components/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollComponentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateComponentRequest,
): Promise<ComponentDTO> {
  return client.request<ComponentDTO>('PATCH', `/api/v1/payroll-components/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function payrollBankFileProfilesList(
  client: ApiClient,
  query: PayrollListBankFileProfilesQuery,
): Promise<PageBankFileProfileDTO2> {
  return client.request<PageBankFileProfileDTO2>('GET', `/api/v1/payroll-bank-file-profiles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRunsList(
  client: ApiClient,
  query: PayrollListRunsQuery,
): Promise<PageRunDTO2> {
  return client.request<PageRunDTO2>('GET', `/api/v1/payroll-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRunsCreate(
  client: ApiClient,
  body: PayrollCreateRunRequest,
  idempotencyKey?: string,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs`, {
    body,
    idempotencyKey,
  });
}

export async function payrollRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollRunsCalculate(
  client: ApiClient,
  params: { id: string },
  body: CalculateRunRequest,
  idempotencyKey?: string,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/calculate`, {
    body,
    idempotencyKey,
  });
}

export async function payrollRunsPayslips(
  client: ApiClient,
  params: { id: string },
  query: ListPayslipsQuery,
): Promise<PagePayslipDTO> {
  return client.request<PagePayslipDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/payslips`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRunsManualLines(
  client: ApiClient,
  params: { id: string },
  query: ListManualLinesQuery,
): Promise<PageManualLineDTO> {
  return client.request<PageManualLineDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/manual-lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRunsSetManualLine(
  client: ApiClient,
  params: { id: string; employeeId: string; componentId: string },
  body: UpsertManualLineRequest,
): Promise<ManualLineDTO> {
  return client.request<ManualLineDTO>('PUT', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/manual-lines/${encodeURIComponent(params.employeeId)}/${encodeURIComponent(params.componentId)}`, {
    body,
  });
}

export async function payrollRunsDeleteManualLine(
  client: ApiClient,
  params: { id: string; employeeId: string; componentId: string },
): Promise<EmptyResponse> {
  return client.request<EmptyResponse>('DELETE', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/manual-lines/${encodeURIComponent(params.employeeId)}/${encodeURIComponent(params.componentId)}`, {
  });
}

export async function payrollRunsReview(
  client: ApiClient,
  params: { id: string },
  body: ReviewRunRequest,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/review`, {
    body,
  });
}

export async function payrollRunsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function payrollRunsDecide(
  client: ApiClient,
  params: { id: string },
  body: PayrollDecideRunRequest,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function payrollRunsPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function payrollRunsBankFile(
  client: ApiClient,
  params: { id: string },
): Promise<PayrollBankFileResponse> {
  return client.request<PayrollBankFileResponse>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/bank-file`, {
  });
}

export async function payrollRunsDisburse(
  client: ApiClient,
  params: { id: string },
  body: DisburseRunRequest,
  idempotencyKey?: string,
): Promise<PayrollRunDTO> {
  return client.request<PayrollRunDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/disburse`, {
    body,
    idempotencyKey,
  });
}

export async function payrollRunsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelRunRequest,
): Promise<EmptyResponse> {
  return client.request<EmptyResponse>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function payrollPayslipsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PayslipDTO> {
  return client.request<PayslipDTO>('GET', `/api/v1/payroll-payslips/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollMePayslipsList(
  client: ApiClient,
  query: CursorQuery,
): Promise<PagePayslipDTO> {
  return client.request<PagePayslipDTO>('GET', `/api/v1/me/payslips`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollMePayslipsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PayslipDTO> {
  return client.request<PayslipDTO>('GET', `/api/v1/me/payslips/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollWageBasesList(
  client: ApiClient,
  query: ListWageBasesQuery,
): Promise<PageWageBaseDTO> {
  return client.request<PageWageBaseDTO>('GET', `/api/v1/wage-bases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollWageBasesCreate(
  client: ApiClient,
  body: CreateWageBaseRequest,
  idempotencyKey?: string,
): Promise<WageBaseDTO> {
  return client.request<WageBaseDTO>('POST', `/api/v1/wage-bases`, {
    body,
    idempotencyKey,
  });
}

export async function payrollWageBasesGet(
  client: ApiClient,
  params: { id: string },
): Promise<WageBaseDTO> {
  return client.request<WageBaseDTO>('GET', `/api/v1/wage-bases/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollWageBasesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWageBaseRequest,
): Promise<WageBaseDTO> {
  return client.request<WageBaseDTO>('PATCH', `/api/v1/wage-bases/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function payrollStatutoryRulesList(
  client: ApiClient,
  query: ListStatutoryRulesQuery,
): Promise<PageStatutoryRuleDTO> {
  return client.request<PageStatutoryRuleDTO>('GET', `/api/v1/statutory-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollStatutoryRulesReviewDue(
  client: ApiClient,
  query: ListStatutoryRulesQuery,
): Promise<PageStatutoryRuleDTO> {
  return client.request<PageStatutoryRuleDTO>('GET', `/api/v1/statutory-rules/review-due`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollStatutoryRulesHistory(
  client: ApiClient,
  params: { code: string },
  query: StatutoryRuleHistoryQuery,
): Promise<StatutoryRuleDTO[]> {
  return client.request<StatutoryRuleDTO[]>('GET', `/api/v1/statutory-rules/${encodeURIComponent(params.code)}/history`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollStatutoryMappingsList(
  client: ApiClient,
  query: ListStatutoryMappingsQuery,
): Promise<PageStatutoryMappingDTO> {
  return client.request<PageStatutoryMappingDTO>('GET', `/api/v1/statutory-mappings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollStatutoryMappingsCreate(
  client: ApiClient,
  body: CreateStatutoryMappingRequest,
  idempotencyKey?: string,
): Promise<StatutoryMappingDTO> {
  return client.request<StatutoryMappingDTO>('POST', `/api/v1/statutory-mappings`, {
    body,
    idempotencyKey,
  });
}

export async function payrollStatutoryMappingsGet(
  client: ApiClient,
  params: { id: string },
): Promise<StatutoryMappingDTO> {
  return client.request<StatutoryMappingDTO>('GET', `/api/v1/statutory-mappings/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollStatutoryMappingsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateStatutoryMappingRequest,
): Promise<StatutoryMappingDTO> {
  return client.request<StatutoryMappingDTO>('PATCH', `/api/v1/statutory-mappings/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function payrollRunsProvenance(
  client: ApiClient,
  params: { id: string },
): Promise<RunProvenanceDTO> {
  return client.request<RunProvenanceDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/provenance`, {
  });
}

export async function payrollRetroList(
  client: ApiClient,
  query: ListRetroEventsQuery,
): Promise<PageRetroEventDTO> {
  return client.request<PageRetroEventDTO>('GET', `/api/v1/payroll-retro-events`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRetroDismiss(
  client: ApiClient,
  params: { id: string },
  body: DismissRetroEventRequest,
): Promise<RetroEventDTO> {
  return client.request<RetroEventDTO>('POST', `/api/v1/payroll-retro-events/${encodeURIComponent(params.id)}/dismiss`, {
    body,
  });
}

export async function payrollRetroLines(
  client: ApiClient,
  params: { id: string },
  query: ListRetroLinesQuery,
): Promise<PageRetroLineDTO> {
  return client.request<PageRetroLineDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/retro-lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollVariancesList(
  client: ApiClient,
  params: { id: string },
  query: ListVariancesQuery,
): Promise<PageRunVarianceDTO> {
  return client.request<PageRunVarianceDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/variances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollVariancesExplain(
  client: ApiClient,
  params: { id: string; varianceId: string },
  body: PayrollExplainVarianceRequest,
): Promise<RunVarianceDTO> {
  return client.request<RunVarianceDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/variances/${encodeURIComponent(params.varianceId)}/explain`, {
    body,
  });
}

export async function payrollVariancesExplainBulk(
  client: ApiClient,
  params: { id: string },
  body: ExplainVarianceBulkRequest,
): Promise<ExplainVarianceBulkResult> {
  return client.request<ExplainVarianceBulkResult>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/variances/explain-bulk`, {
    body,
  });
}

export async function payrollAnomaliesList(
  client: ApiClient,
  params: { id: string },
  query: ListAnomaliesQuery,
): Promise<PageRunAnomalyDTO> {
  return client.request<PageRunAnomalyDTO>('GET', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/anomalies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollAnomaliesAcknowledge(
  client: ApiClient,
  params: { id: string; anomalyId: string },
  body: AcknowledgeAnomalyRequest,
): Promise<RunAnomalyDTO> {
  return client.request<RunAnomalyDTO>('POST', `/api/v1/payroll-runs/${encodeURIComponent(params.id)}/anomalies/${encodeURIComponent(params.anomalyId)}/acknowledge`, {
    body,
  });
}

export async function payrollVariancePoliciesList(
  client: ApiClient,
  query: ListVariancePoliciesQuery,
): Promise<PageVariancePolicyDTO> {
  return client.request<PageVariancePolicyDTO>('GET', `/api/v1/payroll-variance-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollVariancePoliciesCreate(
  client: ApiClient,
  body: CreateVariancePolicyRequest,
  idempotencyKey?: string,
): Promise<VariancePolicyDTO> {
  return client.request<VariancePolicyDTO>('POST', `/api/v1/payroll-variance-policies`, {
    body,
    idempotencyKey,
  });
}

export async function payrollVariancePoliciesGet(
  client: ApiClient,
  params: { id: string },
): Promise<VariancePolicyDTO> {
  return client.request<VariancePolicyDTO>('GET', `/api/v1/payroll-variance-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollVariancePoliciesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateVariancePolicyRequest,
): Promise<VariancePolicyDTO> {
  return client.request<VariancePolicyDTO>('PATCH', `/api/v1/payroll-variance-policies/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function payrollServiceChargePoolsList(
  client: ApiClient,
  query: ListServiceChargePoolsQuery,
): Promise<PageServiceChargePoolDTO> {
  return client.request<PageServiceChargePoolDTO>('GET', `/api/v1/service-charge-pools`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollServiceChargePoolsCreate(
  client: ApiClient,
  body: CreateServiceChargePoolRequest,
  idempotencyKey?: string,
): Promise<ServiceChargePoolDTO> {
  return client.request<ServiceChargePoolDTO>('POST', `/api/v1/service-charge-pools`, {
    body,
    idempotencyKey,
  });
}

export async function payrollServiceChargePoolsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceChargePoolDTO> {
  return client.request<ServiceChargePoolDTO>('GET', `/api/v1/service-charge-pools/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollServiceChargePoolsCollect(
  client: ApiClient,
  params: { id: string },
  body: CollectPoolRequest,
): Promise<ServiceChargePoolDTO> {
  return client.request<ServiceChargePoolDTO>('POST', `/api/v1/service-charge-pools/${encodeURIComponent(params.id)}/collect`, {
    body,
  });
}

export async function payrollServiceChargePoolsAllocate(
  client: ApiClient,
  params: { id: string },
  body: RowVersionRequest,
): Promise<ServiceChargePoolDTO> {
  return client.request<ServiceChargePoolDTO>('POST', `/api/v1/service-charge-pools/${encodeURIComponent(params.id)}/allocate`, {
    body,
  });
}

export async function payrollServiceChargePoolsAllocations(
  client: ApiClient,
  params: { id: string },
  query: ListServiceChargeAllocationsQuery,
): Promise<PageServiceChargeAllocationDTO> {
  return client.request<PageServiceChargeAllocationDTO>('GET', `/api/v1/service-charge-pools/${encodeURIComponent(params.id)}/allocations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollServiceChargePoolsApprove(
  client: ApiClient,
  params: { id: string },
  body: ApprovePoolRequest,
): Promise<ServiceChargePoolDTO> {
  return client.request<ServiceChargePoolDTO>('POST', `/api/v1/service-charge-pools/${encodeURIComponent(params.id)}/approve`, {
    body,
  });
}

export async function payrollServiceChargePoolsDistribute(
  client: ApiClient,
  params: { id: string },
  body: DistributePoolRequest,
): Promise<ServiceChargePoolDTO> {
  return client.request<ServiceChargePoolDTO>('POST', `/api/v1/service-charge-pools/${encodeURIComponent(params.id)}/distribute`, {
    body,
  });
}

export async function payrollServiceChargeSchemesList(
  client: ApiClient,
  query: ListServiceChargeSchemesQuery,
): Promise<PageServiceChargeSchemeDTO> {
  return client.request<PageServiceChargeSchemeDTO>('GET', `/api/v1/service-charge-schemes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollServiceChargeSchemesCreate(
  client: ApiClient,
  body: CreateServiceChargeSchemeRequest,
  idempotencyKey?: string,
): Promise<ServiceChargeSchemeDTO> {
  return client.request<ServiceChargeSchemeDTO>('POST', `/api/v1/service-charge-schemes`, {
    body,
    idempotencyKey,
  });
}

export async function payrollServiceChargeSchemesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceChargeSchemeDTO> {
  return client.request<ServiceChargeSchemeDTO>('GET', `/api/v1/service-charge-schemes/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollServiceChargeSchemesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateServiceChargeSchemeRequest,
): Promise<ServiceChargeSchemeDTO> {
  return client.request<ServiceChargeSchemeDTO>('PATCH', `/api/v1/service-charge-schemes/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function payrollServiceChargePointsList(
  client: ApiClient,
  query: ListServiceChargePointsQuery,
): Promise<PageServiceChargePointDTO> {
  return client.request<PageServiceChargePointDTO>('GET', `/api/v1/service-charge-points`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollServiceChargePointsCreate(
  client: ApiClient,
  body: CreateServiceChargePointRequest,
  idempotencyKey?: string,
): Promise<ServiceChargePointDTO> {
  return client.request<ServiceChargePointDTO>('POST', `/api/v1/service-charge-points`, {
    body,
    idempotencyKey,
  });
}

export async function payrollServiceChargePointsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceChargePointDTO> {
  return client.request<ServiceChargePointDTO>('GET', `/api/v1/service-charge-points/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollServiceChargePointsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateServiceChargePointRequest,
): Promise<ServiceChargePointDTO> {
  return client.request<ServiceChargePointDTO>('PATCH', `/api/v1/service-charge-points/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function payrollServiceChargePointsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/service-charge-points/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollMeServiceCharge(
  client: ApiClient,
  query: ListMyServiceChargeAllocationsQuery,
): Promise<PageMyServiceChargeAllocationDTO> {
  return client.request<PageMyServiceChargeAllocationDTO>('GET', `/api/v1/me/service-charge-allocations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollStatutoryReturnsList(
  client: ApiClient,
  query: ListStatutoryReturnsQuery,
): Promise<PageStatutoryReturnDTO> {
  return client.request<PageStatutoryReturnDTO>('GET', `/api/v1/payroll-statutory-returns`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollStatutoryReturnsCreate(
  client: ApiClient,
  body: CreateStatutoryReturnRequest,
  idempotencyKey?: string,
): Promise<StatutoryReturnDTO> {
  return client.request<StatutoryReturnDTO>('POST', `/api/v1/payroll-statutory-returns`, {
    body,
    idempotencyKey,
  });
}

export async function payrollStatutoryReturnsGet(
  client: ApiClient,
  params: { id: string },
): Promise<StatutoryReturnDTO> {
  return client.request<StatutoryReturnDTO>('GET', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollStatutoryReturnsPrepare(
  client: ApiClient,
  params: { id: string },
): Promise<StatutoryReturnDTO> {
  return client.request<StatutoryReturnDTO>('POST', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}/prepare`, {
  });
}

export async function payrollStatutoryReturnsReconcile(
  client: ApiClient,
  params: { id: string },
): Promise<ReconcileReturnResponse> {
  return client.request<ReconcileReturnResponse>('POST', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}/reconcile`, {
  });
}

export async function payrollStatutoryReturnsApprove(
  client: ApiClient,
  params: { id: string },
): Promise<StatutoryReturnDTO> {
  return client.request<StatutoryReturnDTO>('POST', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}/approve`, {
  });
}

export async function payrollStatutoryReturnsFile(
  client: ApiClient,
  params: { id: string },
  body: PayrollFileReturnRequest,
): Promise<StatutoryReturnDTO> {
  return client.request<StatutoryReturnDTO>('POST', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}/file`, {
    body,
  });
}

export async function payrollStatutoryReturnsLines(
  client: ApiClient,
  params: { id: string },
  query: ListStatutoryReturnLinesQuery,
): Promise<PageStatutoryReturnLineDTO> {
  return client.request<PageStatutoryReturnLineDTO>('GET', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRemittancesList(
  client: ApiClient,
  query: ListRemittancesQuery,
): Promise<PageRemittanceDTO> {
  return client.request<PageRemittanceDTO>('GET', `/api/v1/payroll-remittances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollRemittancesCreate(
  client: ApiClient,
  body: CreateRemittanceRequest,
  idempotencyKey?: string,
): Promise<RemittanceDTO> {
  return client.request<RemittanceDTO>('POST', `/api/v1/payroll-remittances`, {
    body,
    idempotencyKey,
  });
}

export async function payrollRemittancesGet(
  client: ApiClient,
  params: { id: string },
): Promise<RemittanceDTO> {
  return client.request<RemittanceDTO>('GET', `/api/v1/payroll-remittances/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollRemittancesConfirm(
  client: ApiClient,
  params: { id: string },
  body: RowVersionRequest,
): Promise<RemittanceDTO> {
  return client.request<RemittanceDTO>('POST', `/api/v1/payroll-remittances/${encodeURIComponent(params.id)}/confirm`, {
    body,
  });
}

export async function payrollStatutoryReturnsExport(
  client: ApiClient,
  params: { id: string },
  query: ExportReturnFileQuery,
): Promise<RemittanceFileResponse> {
  return client.request<RemittanceFileResponse>('GET', `/api/v1/payroll-statutory-returns/${encodeURIComponent(params.id)}/file-export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollYearEndStatementsList(
  client: ApiClient,
  query: ListYearEndStatementsQuery,
): Promise<PageYearEndStatementDTO> {
  return client.request<PageYearEndStatementDTO>('GET', `/api/v1/payroll-year-end-statements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollYearEndStatementsIssue(
  client: ApiClient,
  body: IssueYearEndStatementRequest,
  idempotencyKey?: string,
): Promise<YearEndStatementDTO> {
  return client.request<YearEndStatementDTO>('POST', `/api/v1/payroll-year-end-statements`, {
    body,
    idempotencyKey,
  });
}

export async function payrollYearEndStatementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<YearEndStatementDTO> {
  return client.request<YearEndStatementDTO>('GET', `/api/v1/payroll-year-end-statements/${encodeURIComponent(params.id)}`, {
  });
}

export async function payrollMeYearEnd(
  client: ApiClient,
  query: CursorQuery,
): Promise<PageYearEndStatementDTO> {
  return client.request<PageYearEndStatementDTO>('GET', `/api/v1/me/year-end-statements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<ProjectsSettingsDTO> {
  return client.request<ProjectsSettingsDTO>('GET', `/api/v1/project-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function projectsSettingsUpsert(
  client: ApiClient,
  params: { companyId: string },
  body: ProjectsUpsertSettingsRequest,
  idempotencyKey?: string,
): Promise<ProjectsSettingsDTO> {
  return client.request<ProjectsSettingsDTO>('PUT', `/api/v1/project-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function projectsProjectsList(
  client: ApiClient,
  query: ListProjectsQuery,
): Promise<PageProjectDTO> {
  return client.request<PageProjectDTO>('GET', `/api/v1/projects`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsProjectsCreate(
  client: ApiClient,
  body: CreateProjectRequest,
  idempotencyKey?: string,
): Promise<ProjectDTO> {
  return client.request<ProjectDTO>('POST', `/api/v1/projects`, {
    body,
    idempotencyKey,
  });
}

export async function projectsProjectsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectDTO> {
  return client.request<ProjectDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsProjectsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProjectRequest,
): Promise<ProjectDTO> {
  return client.request<ProjectDTO>('PATCH', `/api/v1/projects/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function projectsProjectsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/projects/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsPhasesList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PagePhaseDTO> {
  return client.request<PagePhaseDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/phases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsPhasesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreatePhaseRequest,
  idempotencyKey?: string,
): Promise<PhaseDTO> {
  return client.request<PhaseDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/phases`, {
    body,
    idempotencyKey,
  });
}

export async function projectsPhasesUpdate(
  client: ApiClient,
  params: { id: string; phaseId: string },
  body: UpdatePhaseRequest,
): Promise<PhaseDTO> {
  return client.request<PhaseDTO>('PATCH', `/api/v1/projects/${encodeURIComponent(params.id)}/phases/${encodeURIComponent(params.phaseId)}`, {
    body,
  });
}

export async function projectsPhasesDelete(
  client: ApiClient,
  params: { id: string; phaseId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/projects/${encodeURIComponent(params.id)}/phases/${encodeURIComponent(params.phaseId)}`, {
  });
}

export async function projectsTasksList(
  client: ApiClient,
  params: { id: string },
  query: ListTasksQuery,
): Promise<PageTaskDTO> {
  return client.request<PageTaskDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsTasksCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateTaskRequest,
  idempotencyKey?: string,
): Promise<ProjectsTaskDTO> {
  return client.request<ProjectsTaskDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks`, {
    body,
    idempotencyKey,
  });
}

export async function projectsTasksGet(
  client: ApiClient,
  params: { id: string; taskId: string },
): Promise<ProjectsTaskDTO> {
  return client.request<ProjectsTaskDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks/${encodeURIComponent(params.taskId)}`, {
  });
}

export async function projectsTasksUpdate(
  client: ApiClient,
  params: { id: string; taskId: string },
  body: UpdateTaskRequest,
): Promise<ProjectsTaskDTO> {
  return client.request<ProjectsTaskDTO>('PATCH', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks/${encodeURIComponent(params.taskId)}`, {
    body,
  });
}

export async function projectsTasksDelete(
  client: ApiClient,
  params: { id: string; taskId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks/${encodeURIComponent(params.taskId)}`, {
  });
}

export async function projectsTasksAddDependency(
  client: ApiClient,
  params: { id: string; taskId: string },
  body: AddDependencyRequest,
  idempotencyKey?: string,
): Promise<DependencyDTO> {
  return client.request<DependencyDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks/${encodeURIComponent(params.taskId)}/dependencies`, {
    body,
    idempotencyKey,
  });
}

export async function projectsTasksRemoveDependency(
  client: ApiClient,
  params: { id: string; taskId: string; dependsOnTaskId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/projects/${encodeURIComponent(params.id)}/tasks/${encodeURIComponent(params.taskId)}/dependencies/${encodeURIComponent(params.dependsOnTaskId)}`, {
  });
}

export async function projectsMembersList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageMemberDTO> {
  return client.request<PageMemberDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/members`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsMembersUpsert(
  client: ApiClient,
  params: { id: string },
  body: UpsertMemberRequest,
  idempotencyKey?: string,
): Promise<MemberDTO> {
  return client.request<MemberDTO>('PUT', `/api/v1/projects/${encodeURIComponent(params.id)}/members`, {
    body,
    idempotencyKey,
  });
}

export async function projectsMembersRemove(
  client: ApiClient,
  params: { id: string; userId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/projects/${encodeURIComponent(params.id)}/members/${encodeURIComponent(params.userId)}`, {
  });
}

export async function projectsTimesheetsList(
  client: ApiClient,
  query: ListTimesheetsQuery,
): Promise<PageTimesheetDTO> {
  return client.request<PageTimesheetDTO>('GET', `/api/v1/timesheets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsTimesheetsCreate(
  client: ApiClient,
  body: CreateTimesheetRequest,
  idempotencyKey?: string,
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('POST', `/api/v1/timesheets`, {
    body,
    idempotencyKey,
  });
}

export async function projectsTimesheetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('GET', `/api/v1/timesheets/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsTimesheetsReplaceLines(
  client: ApiClient,
  params: { id: string },
  body: ReplaceLinesRequest,
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('PUT', `/api/v1/timesheets/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function projectsTimesheetsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/timesheets/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsTimesheetsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('POST', `/api/v1/timesheets/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function projectsTimesheetsApprove(
  client: ApiClient,
  params: { id: string },
  body: ApproveTimesheetRequest,
  idempotencyKey?: string,
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('POST', `/api/v1/timesheets/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function projectsTimesheetsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectTimesheetRequest,
  idempotencyKey?: string,
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('POST', `/api/v1/timesheets/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingMilestonesList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageMilestoneDTO> {
  return client.request<PageMilestoneDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/billing-milestones`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsBillingMilestonesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateMilestoneRequest,
  idempotencyKey?: string,
): Promise<MilestoneDTO> {
  return client.request<MilestoneDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/billing-milestones`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingMilestonesMarkReady(
  client: ApiClient,
  params: { id: string; milestoneId: string },
  idempotencyKey?: string,
): Promise<MilestoneDTO> {
  return client.request<MilestoneDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/billing-milestones/${encodeURIComponent(params.milestoneId)}/ready`, {
    idempotencyKey,
  });
}

export async function projectsBillingMilestonesInvoice(
  client: ApiClient,
  params: { id: string; milestoneId: string },
  body: InvoiceDateRequest,
  idempotencyKey?: string,
): Promise<MilestoneDTO> {
  return client.request<MilestoneDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/billing-milestones/${encodeURIComponent(params.milestoneId)}/invoice`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingTimeAndMaterials(
  client: ApiClient,
  params: { id: string },
  body: BillTimeAndMaterialsRequest,
  idempotencyKey?: string,
): Promise<BillingDTO> {
  return client.request<BillingDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/billing/time-and-materials`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingPercentComplete(
  client: ApiClient,
  params: { id: string },
  body: RecognizePercentCompleteRequest,
  idempotencyKey?: string,
): Promise<BillingDTO> {
  return client.request<BillingDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/billing/percent-complete`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingsList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageBillingDTO> {
  return client.request<PageBillingDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/billings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsReportsProfitability(
  client: ApiClient,
  params: { id: string },
): Promise<ProfitabilityDTO> {
  return client.request<ProfitabilityDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/profitability`, {
  });
}

export async function projectsReportsGantt(
  client: ApiClient,
  params: { id: string },
): Promise<GanttDTO> {
  return client.request<GanttDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/gantt`, {
  });
}

export async function projectsBaselinesList(
  client: ApiClient,
  params: { id: string },
  query: ListProjectBaselinesQuery,
): Promise<PageProjectBaselineDTO> {
  return client.request<PageProjectBaselineDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/baselines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsBaselinesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateProjectBaselineRequest,
  idempotencyKey?: string,
): Promise<ProjectBaselineDetailDTO> {
  return client.request<ProjectBaselineDetailDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/baselines`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBaselinesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectBaselineDetailDTO> {
  return client.request<ProjectBaselineDetailDTO>('GET', `/api/v1/project-baselines/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsBaselinesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/project-baselines/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsBaselinesSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ProjectBaselineDetailDTO> {
  return client.request<ProjectBaselineDetailDTO>('POST', `/api/v1/project-baselines/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function projectsBaselinesApprove(
  client: ApiClient,
  params: { id: string },
  body: DecideProjectBaselineRequest,
  idempotencyKey?: string,
): Promise<ProjectBaselineDetailDTO> {
  return client.request<ProjectBaselineDetailDTO>('POST', `/api/v1/project-baselines/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBaselinesReject(
  client: ApiClient,
  params: { id: string },
  body: DecideProjectBaselineRequest,
  idempotencyKey?: string,
): Promise<ProjectBaselineDetailDTO> {
  return client.request<ProjectBaselineDetailDTO>('POST', `/api/v1/project-baselines/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBaselinesComparison(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectBaselineComparisonDTO> {
  return client.request<ProjectBaselineComparisonDTO>('GET', `/api/v1/project-baselines/${encodeURIComponent(params.id)}/comparison`, {
  });
}

export async function projectsVariationsList(
  client: ApiClient,
  query: ListProjectVariationsQuery,
): Promise<PageProjectVariationDTO> {
  return client.request<PageProjectVariationDTO>('GET', `/api/v1/project-variations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsVariationsCreate(
  client: ApiClient,
  body: CreateProjectVariationRequest,
  idempotencyKey?: string,
): Promise<ProjectVariationDTO> {
  return client.request<ProjectVariationDTO>('POST', `/api/v1/project-variations`, {
    body,
    idempotencyKey,
  });
}

export async function projectsVariationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectVariationDTO> {
  return client.request<ProjectVariationDTO>('GET', `/api/v1/project-variations/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsVariationsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProjectVariationRequest,
): Promise<ProjectVariationDTO> {
  return client.request<ProjectVariationDTO>('PATCH', `/api/v1/project-variations/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function projectsVariationsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ProjectVariationDTO> {
  return client.request<ProjectVariationDTO>('POST', `/api/v1/project-variations/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function projectsVariationsAccept(
  client: ApiClient,
  params: { id: string },
  body: AcceptProjectVariationRequest,
  idempotencyKey?: string,
): Promise<ProjectVariationDTO> {
  return client.request<ProjectVariationDTO>('POST', `/api/v1/project-variations/${encodeURIComponent(params.id)}/accept`, {
    body,
    idempotencyKey,
  });
}

export async function projectsVariationsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectProjectVariationRequest,
  idempotencyKey?: string,
): Promise<ProjectVariationDTO> {
  return client.request<ProjectVariationDTO>('POST', `/api/v1/project-variations/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function projectsChargeabilityGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectChargeabilityDTO> {
  return client.request<ProjectChargeabilityDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/chargeability`, {
  });
}

export async function projectsChargeabilityOverride(
  client: ApiClient,
  params: { id: string },
  body: CreateProjectChargeabilityOverrideRequest,
  idempotencyKey?: string,
): Promise<ProjectChargeabilityOverrideDTO> {
  return client.request<ProjectChargeabilityOverrideDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/chargeability-override`, {
    body,
    idempotencyKey,
  });
}

export async function projectsChargeabilityRevokeOverride(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ProjectChargeabilityOverrideDTO> {
  return client.request<ProjectChargeabilityOverrideDTO>('POST', `/api/v1/project-chargeability-overrides/${encodeURIComponent(params.id)}/revoke`, {
    idempotencyKey,
  });
}

export async function projectsDependenciesList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageProjectDependencyDTO> {
  return client.request<PageProjectDependencyDTO>('GET', `/api/v1/project-tasks/${encodeURIComponent(params.id)}/dependencies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsDependenciesReplace(
  client: ApiClient,
  params: { id: string },
  body: SetProjectDependenciesRequest,
  idempotencyKey?: string,
): Promise<ProjectDependencyDTO[]> {
  return client.request<ProjectDependencyDTO[]>('PUT', `/api/v1/project-tasks/${encodeURIComponent(params.id)}/dependencies`, {
    body,
    idempotencyKey,
  });
}

export async function projectsScheduleSetTaskParent(
  client: ApiClient,
  params: { id: string },
  body: SetProjectTaskParentRequest,
  idempotencyKey?: string,
): Promise<ProjectScheduleDTO> {
  return client.request<ProjectScheduleDTO>('PUT', `/api/v1/project-tasks/${encodeURIComponent(params.id)}/parent`, {
    body,
    idempotencyKey,
  });
}

export async function projectsScheduleCompute(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ProjectCriticalPathDTO> {
  return client.request<ProjectCriticalPathDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/schedule/compute`, {
    idempotencyKey,
  });
}

export async function projectsCriticalPathGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectCriticalPathDTO> {
  return client.request<ProjectCriticalPathDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/critical-path`, {
  });
}

export async function projectsResourcesList(
  client: ApiClient,
  query: ListProjectResourcesQuery,
): Promise<PageProjectResourceDTO> {
  return client.request<PageProjectResourceDTO>('GET', `/api/v1/project-resources`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsResourcesCreate(
  client: ApiClient,
  body: CreateProjectResourceRequest,
  idempotencyKey?: string,
): Promise<ProjectResourceDTO> {
  return client.request<ProjectResourceDTO>('POST', `/api/v1/project-resources`, {
    body,
    idempotencyKey,
  });
}

export async function projectsResourcesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectResourceDTO> {
  return client.request<ProjectResourceDTO>('GET', `/api/v1/project-resources/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsResourcesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProjectResourceRequest,
): Promise<ProjectResourceDTO> {
  return client.request<ProjectResourceDTO>('PATCH', `/api/v1/project-resources/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function projectsCapacityList(
  client: ApiClient,
  params: { id: string },
  query: ListProjectCapacityQuery,
): Promise<ProjectCapacityDTO[]> {
  return client.request<ProjectCapacityDTO[]>('GET', `/api/v1/project-resources/${encodeURIComponent(params.id)}/capacity`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsCapacityUpsert(
  client: ApiClient,
  params: { id: string },
  body: UpsertProjectCapacityRequest,
  idempotencyKey?: string,
): Promise<ProjectCapacityDTO[]> {
  return client.request<ProjectCapacityDTO[]>('PUT', `/api/v1/project-resources/${encodeURIComponent(params.id)}/capacity`, {
    body,
    idempotencyKey,
  });
}

export async function projectsAllocationsList(
  client: ApiClient,
  query: ListProjectAllocationsQuery,
): Promise<PageProjectAllocationDTO> {
  return client.request<PageProjectAllocationDTO>('GET', `/api/v1/project-allocations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsAllocationsCreate(
  client: ApiClient,
  body: CreateProjectAllocationRequest,
  idempotencyKey?: string,
): Promise<ProjectAllocationDTO> {
  return client.request<ProjectAllocationDTO>('POST', `/api/v1/project-allocations`, {
    body,
    idempotencyKey,
  });
}

export async function projectsAllocationsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/project-allocations/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsAllocationsBoard(
  client: ApiClient,
  query: ProjectAllocationBoardQuery,
): Promise<ProjectAllocationBoardDTO> {
  return client.request<ProjectAllocationBoardDTO>('GET', `/api/v1/project-allocations/board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsReportsUtilisation(
  client: ApiClient,
  query: ProjectUtilisationQuery,
): Promise<ProjectUtilisationDTO[]> {
  return client.request<ProjectUtilisationDTO[]>('GET', `/api/v1/project-reports/utilisation`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsTaskSkillsList(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectTaskSkillDTO[]> {
  return client.request<ProjectTaskSkillDTO[]>('GET', `/api/v1/project-tasks/${encodeURIComponent(params.id)}/skills`, {
  });
}

export async function projectsTaskSkillsSet(
  client: ApiClient,
  params: { id: string },
  body: SetProjectTaskSkillsRequest,
  idempotencyKey?: string,
): Promise<ProjectTaskSkillDTO[]> {
  return client.request<ProjectTaskSkillDTO[]>('PUT', `/api/v1/project-tasks/${encodeURIComponent(params.id)}/skills`, {
    body,
    idempotencyKey,
  });
}

export async function projectsCostBreakdownGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectTaskCostDTO[]> {
  return client.request<ProjectTaskCostDTO[]>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/cost-breakdown`, {
  });
}

export async function projectsEvmList(
  client: ApiClient,
  params: { id: string },
  query: ListProjectEVMQuery,
): Promise<ProjectEVMDTO[]> {
  return client.request<ProjectEVMDTO[]>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/evm`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsEvmRefresh(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ProjectEVMDTO> {
  return client.request<ProjectEVMDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/evm/refresh`, {
    idempotencyKey,
  });
}

export async function projectsWipRunsList(
  client: ApiClient,
  query: ListProjectWIPRunsQuery,
): Promise<PageProjectWIPRunDTO> {
  return client.request<PageProjectWIPRunDTO>('GET', `/api/v1/project-wip-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsWipRunsCreate(
  client: ApiClient,
  body: CreateProjectWIPRunRequest,
  idempotencyKey?: string,
): Promise<ProjectWIPRunDetailDTO> {
  return client.request<ProjectWIPRunDetailDTO>('POST', `/api/v1/project-wip-runs`, {
    body,
    idempotencyKey,
  });
}

export async function projectsWipRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectWIPRunDetailDTO> {
  return client.request<ProjectWIPRunDetailDTO>('GET', `/api/v1/project-wip-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsWipRunsPost(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ProjectWIPRunDetailDTO> {
  return client.request<ProjectWIPRunDetailDTO>('POST', `/api/v1/project-wip-runs/${encodeURIComponent(params.id)}/post`, {
    idempotencyKey,
  });
}

export async function projectsWipRunsReverse(
  client: ApiClient,
  params: { id: string },
  body: ReverseProjectWIPRunRequest,
  idempotencyKey?: string,
): Promise<ProjectWIPRunDetailDTO> {
  return client.request<ProjectWIPRunDetailDTO>('POST', `/api/v1/project-wip-runs/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function projectsRetentionsList(
  client: ApiClient,
  query: ListProjectRetentionsQuery,
): Promise<PageProjectRetentionDTO> {
  return client.request<PageProjectRetentionDTO>('GET', `/api/v1/project-retentions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsRetentionsCreate(
  client: ApiClient,
  body: CreateProjectRetentionRequest,
  idempotencyKey?: string,
): Promise<ProjectRetentionDTO> {
  return client.request<ProjectRetentionDTO>('POST', `/api/v1/project-retentions`, {
    body,
    idempotencyKey,
  });
}

export async function projectsRetentionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectRetentionDTO> {
  return client.request<ProjectRetentionDTO>('GET', `/api/v1/project-retentions/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsRetentionsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseProjectRetentionRequest,
  idempotencyKey?: string,
): Promise<ProjectRetentionDTO> {
  return client.request<ProjectRetentionDTO>('POST', `/api/v1/project-retentions/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function projectsRetentionsForfeit(
  client: ApiClient,
  params: { id: string },
  body: ForfeitProjectRetentionRequest,
  idempotencyKey?: string,
): Promise<ProjectRetentionDTO> {
  return client.request<ProjectRetentionDTO>('POST', `/api/v1/project-retentions/${encodeURIComponent(params.id)}/forfeit`, {
    body,
    idempotencyKey,
  });
}

export async function projectsAdvancesList(
  client: ApiClient,
  query: ListProjectAdvancesQuery,
): Promise<PageProjectAdvanceDTO> {
  return client.request<PageProjectAdvanceDTO>('GET', `/api/v1/project-advances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsAdvancesCreate(
  client: ApiClient,
  body: CreateProjectAdvanceRequest,
  idempotencyKey?: string,
): Promise<ProjectAdvanceDTO> {
  return client.request<ProjectAdvanceDTO>('POST', `/api/v1/project-advances`, {
    body,
    idempotencyKey,
  });
}

export async function projectsAdvancesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectAdvanceDTO> {
  return client.request<ProjectAdvanceDTO>('GET', `/api/v1/project-advances/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsAdvancesRecoupments(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageProjectAdvanceRecoupmentDTO> {
  return client.request<PageProjectAdvanceRecoupmentDTO>('GET', `/api/v1/project-advances/${encodeURIComponent(params.id)}/recoupments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsAdvancesRecoup(
  client: ApiClient,
  params: { id: string },
  body: RecoupProjectAdvanceRequest,
  idempotencyKey?: string,
): Promise<ProjectAdvanceDTO> {
  return client.request<ProjectAdvanceDTO>('POST', `/api/v1/project-advances/${encodeURIComponent(params.id)}/recoup`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingCostPlus(
  client: ApiClient,
  params: { id: string },
  body: BillProjectCostPlusRequest,
  idempotencyKey?: string,
): Promise<BillingDTO> {
  return client.request<BillingDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/bill/cost-plus`, {
    body,
    idempotencyKey,
  });
}

export async function projectsBillingRetainer(
  client: ApiClient,
  params: { id: string },
  body: BillProjectRetainerRequest,
  idempotencyKey?: string,
): Promise<BillingDTO> {
  return client.request<BillingDTO>('POST', `/api/v1/projects/${encodeURIComponent(params.id)}/bill/retainer`, {
    body,
    idempotencyKey,
  });
}

export async function projectsRisksList(
  client: ApiClient,
  query: ListProjectRisksQuery,
): Promise<PageProjectRiskDTO> {
  return client.request<PageProjectRiskDTO>('GET', `/api/v1/project-risks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsRisksCreate(
  client: ApiClient,
  body: CreateProjectRiskRequest,
  idempotencyKey?: string,
): Promise<ProjectRiskDTO> {
  return client.request<ProjectRiskDTO>('POST', `/api/v1/project-risks`, {
    body,
    idempotencyKey,
  });
}

export async function projectsRisksGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectRiskDTO> {
  return client.request<ProjectRiskDTO>('GET', `/api/v1/project-risks/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsRisksUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProjectRiskRequest,
): Promise<ProjectRiskDTO> {
  return client.request<ProjectRiskDTO>('PATCH', `/api/v1/project-risks/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function projectsRisksDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/project-risks/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsSubcontractsList(
  client: ApiClient,
  query: ListProjectSubcontractsQuery,
): Promise<PageProjectSubcontractDTO> {
  return client.request<PageProjectSubcontractDTO>('GET', `/api/v1/project-subcontracts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function projectsSubcontractsCreate(
  client: ApiClient,
  body: CreateProjectSubcontractRequest,
  idempotencyKey?: string,
): Promise<ProjectSubcontractDTO> {
  return client.request<ProjectSubcontractDTO>('POST', `/api/v1/project-subcontracts`, {
    body,
    idempotencyKey,
  });
}

export async function projectsSubcontractsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectSubcontractDTO> {
  return client.request<ProjectSubcontractDTO>('GET', `/api/v1/project-subcontracts/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsSubcontractsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProjectSubcontractRequest,
): Promise<ProjectSubcontractDTO> {
  return client.request<ProjectSubcontractDTO>('PATCH', `/api/v1/project-subcontracts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function projectsSubcontractsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/project-subcontracts/${encodeURIComponent(params.id)}`, {
  });
}

export async function projectsMoneyGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProjectMoneyDTO> {
  return client.request<ProjectMoneyDTO>('GET', `/api/v1/projects/${encodeURIComponent(params.id)}/money`, {
  });
}

export async function projectsMoneyUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProjectMoneyRequest,
): Promise<ProjectMoneyDTO> {
  return client.request<ProjectMoneyDTO>('PATCH', `/api/v1/projects/${encodeURIComponent(params.id)}/money`, {
    body,
  });
}

export async function projectsMoneySettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<ProjectMoneySettingsDTO> {
  return client.request<ProjectMoneySettingsDTO>('GET', `/api/v1/project-money-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function projectsMoneySettingsUpsert(
  client: ApiClient,
  params: { companyId: string },
  body: UpsertProjectMoneySettingsRequest,
  idempotencyKey?: string,
): Promise<ProjectMoneySettingsDTO> {
  return client.request<ProjectMoneySettingsDTO>('PUT', `/api/v1/project-money-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function serviceSettingsGet(
  client: ApiClient,
  query: ServiceSettingsQuery,
): Promise<ServiceSettingsDTO> {
  return client.request<ServiceSettingsDTO>('GET', `/api/v1/service-settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceSettingsUpsert(
  client: ApiClient,
  body: ServiceUpsertSettingsRequest,
  idempotencyKey?: string,
): Promise<ServiceSettingsDTO> {
  return client.request<ServiceSettingsDTO>('PUT', `/api/v1/service-settings`, {
    body,
    idempotencyKey,
  });
}

export async function serviceCustomerAssetsList(
  client: ApiClient,
  query: ListCustomerAssetsQuery,
): Promise<PageCustomerAssetDTO> {
  return client.request<PageCustomerAssetDTO>('GET', `/api/v1/service-customer-assets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceCustomerAssetsCreate(
  client: ApiClient,
  body: CreateCustomerAssetRequest,
  idempotencyKey?: string,
): Promise<CustomerAssetDTO> {
  return client.request<CustomerAssetDTO>('POST', `/api/v1/service-customer-assets`, {
    body,
    idempotencyKey,
  });
}

export async function serviceCustomerAssetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<CustomerAssetDTO> {
  return client.request<CustomerAssetDTO>('GET', `/api/v1/service-customer-assets/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceCustomerAssetsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCustomerAssetRequest,
): Promise<CustomerAssetDTO> {
  return client.request<CustomerAssetDTO>('PATCH', `/api/v1/service-customer-assets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceCustomerAssetsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/service-customer-assets/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceCustomerAssetsHistory(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageTicketDTO> {
  return client.request<PageTicketDTO>('GET', `/api/v1/service-customer-assets/${encodeURIComponent(params.id)}/history`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceAssignmentRulesList(
  client: ApiClient,
  query: ListAssignmentRulesQuery,
): Promise<PageAssignmentRuleDTO> {
  return client.request<PageAssignmentRuleDTO>('GET', `/api/v1/service-assignment-rules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceAssignmentRulesCreate(
  client: ApiClient,
  body: CreateAssignmentRuleRequest,
  idempotencyKey?: string,
): Promise<AssignmentRuleDTO> {
  return client.request<AssignmentRuleDTO>('POST', `/api/v1/service-assignment-rules`, {
    body,
    idempotencyKey,
  });
}

export async function serviceAssignmentRulesGet(
  client: ApiClient,
  params: { id: string },
): Promise<AssignmentRuleDTO> {
  return client.request<AssignmentRuleDTO>('GET', `/api/v1/service-assignment-rules/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceAssignmentRulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateAssignmentRuleRequest,
): Promise<AssignmentRuleDTO> {
  return client.request<AssignmentRuleDTO>('PATCH', `/api/v1/service-assignment-rules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceAssignmentRulesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/service-assignment-rules/${encodeURIComponent(params.id)}`, {
  });
}

export async function servicePmSchedulesList(
  client: ApiClient,
  query: ListPMSchedulesQuery,
): Promise<PagePMScheduleDTO> {
  return client.request<PagePMScheduleDTO>('GET', `/api/v1/service-pm-schedules`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function servicePmSchedulesCreate(
  client: ApiClient,
  body: CreatePMScheduleRequest,
  idempotencyKey?: string,
): Promise<PMScheduleDTO> {
  return client.request<PMScheduleDTO>('POST', `/api/v1/service-pm-schedules`, {
    body,
    idempotencyKey,
  });
}

export async function servicePmSchedulesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMScheduleDTO> {
  return client.request<PMScheduleDTO>('GET', `/api/v1/service-pm-schedules/${encodeURIComponent(params.id)}`, {
  });
}

export async function servicePmSchedulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMScheduleRequest,
): Promise<PMScheduleDTO> {
  return client.request<PMScheduleDTO>('PATCH', `/api/v1/service-pm-schedules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function servicePmSchedulesRun(
  client: ApiClient,
  body: RunPMSchedulesRequest,
  idempotencyKey?: string,
): Promise<PMRunResult> {
  return client.request<PMRunResult>('POST', `/api/v1/service-pm-schedules/run`, {
    body,
    idempotencyKey,
  });
}

export async function serviceTicketsList(
  client: ApiClient,
  query: ListTicketsQuery,
): Promise<PageTicketDTO> {
  return client.request<PageTicketDTO>('GET', `/api/v1/service-tickets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceTicketsCreate(
  client: ApiClient,
  body: CreateTicketRequest,
  idempotencyKey?: string,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets`, {
    body,
    idempotencyKey,
  });
}

export async function serviceTicketsGet(
  client: ApiClient,
  params: { id: string },
): Promise<TicketDTO> {
  return client.request<TicketDTO>('GET', `/api/v1/service-tickets/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceTicketsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('PATCH', `/api/v1/service-tickets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceTicketsAssign(
  client: ApiClient,
  params: { id: string },
  body: AssignTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/assign`, {
    body,
  });
}

export async function serviceTicketsRespond(
  client: ApiClient,
  params: { id: string },
  body: RespondTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/respond`, {
    body,
  });
}

export async function serviceTicketsResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/resolve`, {
    body,
  });
}

export async function serviceTicketsClose(
  client: ApiClient,
  params: { id: string },
  body: CloseTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function serviceTicketsReopen(
  client: ApiClient,
  params: { id: string },
  body: ReopenTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/reopen`, {
    body,
  });
}

export async function serviceTicketsCsat(
  client: ApiClient,
  params: { id: string },
  body: CSATTicketRequest,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/csat`, {
    body,
  });
}

export async function serviceTicketsBill(
  client: ApiClient,
  params: { id: string },
  body: BillTicketRequest,
  idempotencyKey?: string,
): Promise<TicketDTO> {
  return client.request<TicketDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/bill`, {
    body,
    idempotencyKey,
  });
}

export async function serviceTicketsPartsList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageTicketPartDTO> {
  return client.request<PageTicketPartDTO>('GET', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/parts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceTicketsPartsAdd(
  client: ApiClient,
  params: { id: string },
  body: AddPartRequest,
  idempotencyKey?: string,
): Promise<TicketPartDTO> {
  return client.request<TicketPartDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/parts`, {
    body,
    idempotencyKey,
  });
}

export async function serviceTicketsPartsVoid(
  client: ApiClient,
  params: { id: string; partId: string },
  body: VoidPartRequest,
): Promise<TicketPartDTO> {
  return client.request<TicketPartDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/parts/${encodeURIComponent(params.partId)}/void`, {
    body,
  });
}

export async function serviceTicketsLaborList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageTicketLaborDTO> {
  return client.request<PageTicketLaborDTO>('GET', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/labor`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceTicketsLaborAdd(
  client: ApiClient,
  params: { id: string },
  body: AddLaborRequest,
  idempotencyKey?: string,
): Promise<TicketLaborDTO> {
  return client.request<TicketLaborDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/labor`, {
    body,
    idempotencyKey,
  });
}

export async function serviceTicketsLaborVoid(
  client: ApiClient,
  params: { id: string; laborId: string },
  body: VoidLaborRequest,
): Promise<TicketLaborDTO> {
  return client.request<TicketLaborDTO>('POST', `/api/v1/service-tickets/${encodeURIComponent(params.id)}/labor/${encodeURIComponent(params.laborId)}/void`, {
    body,
  });
}

export async function serviceReportsFirstTimeFix(
  client: ApiClient,
  query: ReportRangeQuery,
): Promise<FirstTimeFixReport> {
  return client.request<FirstTimeFixReport>('GET', `/api/v1/service-reports/first-time-fix`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceReportsSlaCompliance(
  client: ApiClient,
  query: ReportRangeQuery,
): Promise<SLAComplianceReport> {
  return client.request<SLAComplianceReport>('GET', `/api/v1/service-reports/sla-compliance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceReportsCsat(
  client: ApiClient,
  query: ReportRangeQuery,
): Promise<ServiceCSATReport> {
  return client.request<ServiceCSATReport>('GET', `/api/v1/service-reports/csat`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceWorkOrdersList(
  client: ApiClient,
  query: ListServiceWorkOrdersQuery,
): Promise<PageServiceWorkOrderDTO> {
  return client.request<PageServiceWorkOrderDTO>('GET', `/api/v1/service-work-orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceWorkOrdersCreate(
  client: ApiClient,
  body: CreateServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersGet(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceWorkOrderDetailDTO> {
  return client.request<ServiceWorkOrderDetailDTO>('GET', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceWorkOrdersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateServiceWorkOrderRequest,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('PATCH', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceWorkOrdersSchedule(
  client: ApiClient,
  params: { id: string },
  body: ScheduleServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/schedule`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersDispatch(
  client: ApiClient,
  params: { id: string },
  body: DispatchServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/dispatch`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersStart(
  client: ApiClient,
  params: { id: string },
  body: StartServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/start`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersComplete(
  client: ApiClient,
  params: { id: string },
  body: CompleteServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/complete`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersClose(
  client: ApiClient,
  params: { id: string },
  body: CloseServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function serviceVisitsList(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceVisitDTO[]> {
  return client.request<ServiceVisitDTO[]>('GET', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/visits`, {
  });
}

export async function serviceVisitsOpen(
  client: ApiClient,
  params: { id: string },
  body: OpenServiceVisitRequest,
  idempotencyKey?: string,
): Promise<ServiceVisitDTO> {
  return client.request<ServiceVisitDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/visits`, {
    body,
    idempotencyKey,
  });
}

export async function serviceVisitsComplete(
  client: ApiClient,
  params: { id: string },
  body: CompleteServiceVisitRequest,
  idempotencyKey?: string,
): Promise<ServiceVisitDTO> {
  return client.request<ServiceVisitDTO>('POST', `/api/v1/service-work-order-visits/${encodeURIComponent(params.id)}/complete`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrderSkillsList(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceWorkOrderSkillDTO[]> {
  return client.request<ServiceWorkOrderSkillDTO[]>('GET', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/skills`, {
  });
}

export async function serviceWorkOrderSkillsSet(
  client: ApiClient,
  params: { id: string },
  body: SetServiceWorkOrderSkillsRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderSkillDTO[]> {
  return client.request<ServiceWorkOrderSkillDTO[]>('PUT', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/skills`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersAddPart(
  client: ApiClient,
  params: { id: string },
  body: AddServiceWorkOrderPartRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderPartDTO> {
  return client.request<ServiceWorkOrderPartDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/parts`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersLogLabour(
  client: ApiClient,
  params: { id: string },
  body: LogServiceWorkOrderLabourRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderLabourDTO> {
  return client.request<ServiceWorkOrderLabourDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/labour`, {
    body,
    idempotencyKey,
  });
}

export async function serviceDispatchBoard(
  client: ApiClient,
  query: ServiceDispatchBoardQuery,
): Promise<ServiceDispatchBoardDTO> {
  return client.request<ServiceDispatchBoardDTO>('GET', `/api/v1/service-dispatch/board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceDispatchAssign(
  client: ApiClient,
  body: ServiceDispatchAssignRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-dispatch/assign`, {
    body,
    idempotencyKey,
  });
}

export async function serviceDeviceWorkOrders(
  client: ApiClient,
): Promise<ServiceDeviceWorkOrderListResponse> {
  return client.request<ServiceDeviceWorkOrderListResponse>('GET', `/device/api/v1/service/work-orders`, {
  });
}

export async function serviceDeviceWorkOrder(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceWorkOrderDetailDTO> {
  return client.request<ServiceWorkOrderDetailDTO>('GET', `/device/api/v1/service/work-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceDeviceVanStock(
  client: ApiClient,
): Promise<ServiceDeviceVanStockResponse> {
  return client.request<ServiceDeviceVanStockResponse>('GET', `/device/api/v1/service/van-stock`, {
  });
}

export async function serviceDeviceVocabularies(
  client: ApiClient,
): Promise<ServiceDeviceVocabulariesResponse> {
  return client.request<ServiceDeviceVocabulariesResponse>('GET', `/device/api/v1/service/vocabularies`, {
  });
}

export async function serviceDeviceCompleteWorkOrder(
  client: ApiClient,
  params: { id: string },
  body: ServiceDeviceCompleteRequest,
  idempotencyKey?: string,
): Promise<ServiceVisitDTO> {
  return client.request<ServiceVisitDTO>('POST', `/device/api/v1/service/work-orders/${encodeURIComponent(params.id)}/complete`, {
    body,
    idempotencyKey,
  });
}

export async function serviceDeviceConsumeVanStock(
  client: ApiClient,
  body: ServiceDeviceConsumeRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderPartDTO> {
  return client.request<ServiceWorkOrderPartDTO>('POST', `/device/api/v1/service/van-stock/consume`, {
    body,
    idempotencyKey,
  });
}

export async function serviceContractsList(
  client: ApiClient,
  query: ListServiceContractsQuery,
): Promise<PageServiceContractDTO> {
  return client.request<PageServiceContractDTO>('GET', `/api/v1/service-contracts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceContractsCreate(
  client: ApiClient,
  body: CreateServiceContractRequest,
  idempotencyKey?: string,
): Promise<ServiceContractDTO> {
  return client.request<ServiceContractDTO>('POST', `/api/v1/service-contracts`, {
    body,
    idempotencyKey,
  });
}

export async function serviceContractsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceContractDTO> {
  return client.request<ServiceContractDTO>('GET', `/api/v1/service-contracts/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceContractsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateServiceContractRequest,
): Promise<ServiceContractDTO> {
  return client.request<ServiceContractDTO>('PATCH', `/api/v1/service-contracts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceContractsActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivateServiceContractRequest,
  idempotencyKey?: string,
): Promise<ServiceContractDTO> {
  return client.request<ServiceContractDTO>('POST', `/api/v1/service-contracts/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function serviceContractsRenew(
  client: ApiClient,
  params: { id: string },
  body: RenewServiceContractRequest,
  idempotencyKey?: string,
): Promise<ServiceContractDTO> {
  return client.request<ServiceContractDTO>('POST', `/api/v1/service-contracts/${encodeURIComponent(params.id)}/renew`, {
    body,
    idempotencyKey,
  });
}

export async function serviceEntitlementsList(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceEntitlementDTO[]> {
  return client.request<ServiceEntitlementDTO[]>('GET', `/api/v1/service-contracts/${encodeURIComponent(params.id)}/entitlements`, {
  });
}

export async function serviceEntitlementsSet(
  client: ApiClient,
  params: { id: string },
  body: SetServiceEntitlementsRequest,
): Promise<ServiceEntitlementDTO[]> {
  return client.request<ServiceEntitlementDTO[]>('PUT', `/api/v1/service-contracts/${encodeURIComponent(params.id)}/entitlements`, {
    body,
  });
}

export async function serviceEntitlementDecisionsList(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceEntitlementDecisionDTO[]> {
  return client.request<ServiceEntitlementDecisionDTO[]>('GET', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/entitlement-decisions`, {
  });
}

export async function serviceEntitlementDecisionsEvaluate(
  client: ApiClient,
  params: { id: string },
  body: EvaluateServiceEntitlementRequest,
  idempotencyKey?: string,
): Promise<ServiceEntitlementDecisionDTO> {
  return client.request<ServiceEntitlementDecisionDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/entitlement/evaluate`, {
    body,
    idempotencyKey,
  });
}

export async function serviceWorkOrdersBill(
  client: ApiClient,
  params: { id: string },
  body: BillServiceWorkOrderRequest,
  idempotencyKey?: string,
): Promise<ServiceWorkOrderDTO> {
  return client.request<ServiceWorkOrderDTO>('POST', `/api/v1/service-work-orders/${encodeURIComponent(params.id)}/bill`, {
    body,
    idempotencyKey,
  });
}

export async function serviceCostToServeReport(
  client: ApiClient,
  query: ServiceCostToServeQuery,
): Promise<ServiceCostToServeDTO[]> {
  return client.request<ServiceCostToServeDTO[]>('GET', `/api/v1/service-reports/cost-to-serve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceRateCardsList(
  client: ApiClient,
  query: ListServiceRateCardsQuery,
): Promise<PageServiceRateCardDTO> {
  return client.request<PageServiceRateCardDTO>('GET', `/api/v1/service-rate-cards`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceRateCardsCreate(
  client: ApiClient,
  body: CreateServiceRateCardRequest,
  idempotencyKey?: string,
): Promise<ServiceRateCardDTO> {
  return client.request<ServiceRateCardDTO>('POST', `/api/v1/service-rate-cards`, {
    body,
    idempotencyKey,
  });
}

export async function serviceRateCardsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceRateCardDTO> {
  return client.request<ServiceRateCardDTO>('GET', `/api/v1/service-rate-cards/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceRateCardVersionsList(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceRateCardVersionDTO[]> {
  return client.request<ServiceRateCardVersionDTO[]>('GET', `/api/v1/service-rate-cards/${encodeURIComponent(params.id)}/versions`, {
  });
}

export async function serviceRateCardVersionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateServiceRateCardVersionRequest,
  idempotencyKey?: string,
): Promise<ServiceRateCardVersionDTO> {
  return client.request<ServiceRateCardVersionDTO>('POST', `/api/v1/service-rate-cards/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function serviceRateCardVersionsPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishServiceRateCardVersionRequest,
  idempotencyKey?: string,
): Promise<ServiceRateCardVersionDTO> {
  return client.request<ServiceRateCardVersionDTO>('POST', `/api/v1/service-rate-card-versions/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function serviceRateCardVersionsListLines(
  client: ApiClient,
  params: { id: string },
): Promise<ServiceRateCardLineDTO[]> {
  return client.request<ServiceRateCardLineDTO[]>('GET', `/api/v1/service-rate-card-versions/${encodeURIComponent(params.id)}/lines`, {
  });
}

export async function serviceRateCardVersionsSetLines(
  client: ApiClient,
  params: { id: string },
  body: SetServiceRateCardLinesRequest,
): Promise<ServiceRateCardLineDTO[]> {
  return client.request<ServiceRateCardLineDTO[]>('PUT', `/api/v1/service-rate-card-versions/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function serviceGoodwillList(
  client: ApiClient,
  query: ListServiceGoodwillQuery,
): Promise<PageServiceGoodwillDTO> {
  return client.request<PageServiceGoodwillDTO>('GET', `/api/v1/service-goodwill`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceGoodwillRequest(
  client: ApiClient,
  body: RequestServiceGoodwillRequest,
  idempotencyKey?: string,
): Promise<ServiceGoodwillDTO> {
  return client.request<ServiceGoodwillDTO>('POST', `/api/v1/service-goodwill`, {
    body,
    idempotencyKey,
  });
}

export async function serviceGoodwillApprove(
  client: ApiClient,
  params: { id: string },
  body: ApproveServiceGoodwillRequest,
  idempotencyKey?: string,
): Promise<ServiceGoodwillDTO> {
  return client.request<ServiceGoodwillDTO>('POST', `/api/v1/service-goodwill/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function serviceVansList(
  client: ApiClient,
  query: ListVansQuery,
): Promise<PageVanDTO> {
  return client.request<PageVanDTO>('GET', `/api/v1/service-vans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceVansCreate(
  client: ApiClient,
  body: CreateVanRequest,
  idempotencyKey?: string,
): Promise<VanDTO> {
  return client.request<VanDTO>('POST', `/api/v1/service-vans`, {
    body,
    idempotencyKey,
  });
}

export async function serviceVansGet(
  client: ApiClient,
  params: { id: string },
): Promise<VanDTO> {
  return client.request<VanDTO>('GET', `/api/v1/service-vans/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceVansUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateVanRequest,
): Promise<VanDTO> {
  return client.request<VanDTO>('PATCH', `/api/v1/service-vans/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceVansParLevelsList(
  client: ApiClient,
  params: { id: string },
): Promise<VanParLevelDTO[]> {
  return client.request<VanParLevelDTO[]>('GET', `/api/v1/service-vans/${encodeURIComponent(params.id)}/par-levels`, {
  });
}

export async function serviceVansParLevelsSet(
  client: ApiClient,
  params: { id: string },
  body: SetVanParLevelsRequest,
  idempotencyKey?: string,
): Promise<VanParLevelDTO[]> {
  return client.request<VanParLevelDTO[]>('PUT', `/api/v1/service-vans/${encodeURIComponent(params.id)}/par-levels`, {
    body,
    idempotencyKey,
  });
}

export async function serviceVansReplenish(
  client: ApiClient,
  params: { id: string },
  body: ProposeReplenishmentRequest,
  idempotencyKey?: string,
): Promise<VanReplenishmentDTO> {
  return client.request<VanReplenishmentDTO>('POST', `/api/v1/service-vans/${encodeURIComponent(params.id)}/replenishment`, {
    body,
    idempotencyKey,
  });
}

export async function serviceVansStock(
  client: ApiClient,
  params: { id: string },
): Promise<VanStockResponse> {
  return client.request<VanStockResponse>('GET', `/api/v1/service-vans/${encodeURIComponent(params.id)}/stock`, {
  });
}

export async function serviceProblemsList(
  client: ApiClient,
  query: ListProblemsQuery,
): Promise<PageProblemDTO> {
  return client.request<PageProblemDTO>('GET', `/api/v1/service-problems`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function serviceProblemsCreate(
  client: ApiClient,
  body: CreateProblemRequest,
  idempotencyKey?: string,
): Promise<ProblemDTO> {
  return client.request<ProblemDTO>('POST', `/api/v1/service-problems`, {
    body,
    idempotencyKey,
  });
}

export async function serviceProblemsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProblemDTO> {
  return client.request<ProblemDTO>('GET', `/api/v1/service-problems/${encodeURIComponent(params.id)}`, {
  });
}

export async function serviceProblemsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProblemRequest,
): Promise<ProblemDTO> {
  return client.request<ProblemDTO>('PATCH', `/api/v1/service-problems/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function serviceProblemsCasesLink(
  client: ApiClient,
  params: { id: string },
  body: LinkProblemCaseRequest,
  idempotencyKey?: string,
): Promise<ProblemDTO> {
  return client.request<ProblemDTO>('POST', `/api/v1/service-problems/${encodeURIComponent(params.id)}/cases`, {
    body,
    idempotencyKey,
  });
}

export async function serviceProblemsCasesUnlink(
  client: ApiClient,
  params: { id: string },
  body: LinkProblemCaseRequest,
): Promise<ProblemDTO> {
  return client.request<ProblemDTO>('DELETE', `/api/v1/service-problems/${encodeURIComponent(params.id)}/cases`, {
    body,
  });
}

export async function serviceReportsKbDeflection(
  client: ApiClient,
): Promise<KBDeflectionReport> {
  return client.request<KBDeflectionReport>('GET', `/api/v1/service-reports/kb-deflection`, {
  });
}

export async function mfgSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<MfgSettingsDTO> {
  return client.request<MfgSettingsDTO>('GET', `/api/v1/mfg/settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function mfgSettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdateSettingsRequest,
  idempotencyKey?: string,
): Promise<MfgSettingsDTO> {
  return client.request<MfgSettingsDTO>('PUT', `/api/v1/mfg/settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function mfgWorkCenterList(
  client: ApiClient,
  query: ListWorkCentersQuery,
): Promise<PageWorkCenterDTO> {
  return client.request<PageWorkCenterDTO>('GET', `/api/v1/mfg/work-centers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgWorkCenterCreate(
  client: ApiClient,
  body: CreateWorkCenterRequest,
  idempotencyKey?: string,
): Promise<WorkCenterDTO> {
  return client.request<WorkCenterDTO>('POST', `/api/v1/mfg/work-centers`, {
    body,
    idempotencyKey,
  });
}

export async function mfgWorkCenterGet(
  client: ApiClient,
  params: { id: string },
): Promise<WorkCenterDTO> {
  return client.request<WorkCenterDTO>('GET', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgWorkCenterUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWorkCenterRequest,
): Promise<WorkCenterDTO> {
  return client.request<WorkCenterDTO>('PATCH', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function mfgBomList(
  client: ApiClient,
  query: ListBOMQuery,
): Promise<PageBOMDTO> {
  return client.request<PageBOMDTO>('GET', `/api/v1/mfg/boms`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgBomCreate(
  client: ApiClient,
  body: CreateBOMRequest,
  idempotencyKey?: string,
): Promise<BOMDTO> {
  return client.request<BOMDTO>('POST', `/api/v1/mfg/boms`, {
    body,
    idempotencyKey,
  });
}

export async function mfgBomGet(
  client: ApiClient,
  params: { id: string },
): Promise<BOMDTO> {
  return client.request<BOMDTO>('GET', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgBomUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateBOMRequest,
): Promise<BOMDTO> {
  return client.request<BOMDTO>('PATCH', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function mfgBomDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgBomActivate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<BOMDTO> {
  return client.request<BOMDTO>('POST', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}/activate`, {
    idempotencyKey,
  });
}

export async function mfgRoutingList(
  client: ApiClient,
  query: ListRoutingQuery,
): Promise<PageRoutingDTO> {
  return client.request<PageRoutingDTO>('GET', `/api/v1/mfg/routings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgRoutingCreate(
  client: ApiClient,
  body: CreateRoutingRequest,
  idempotencyKey?: string,
): Promise<RoutingDTO> {
  return client.request<RoutingDTO>('POST', `/api/v1/mfg/routings`, {
    body,
    idempotencyKey,
  });
}

export async function mfgRoutingGet(
  client: ApiClient,
  params: { id: string },
): Promise<RoutingDTO> {
  return client.request<RoutingDTO>('GET', `/api/v1/mfg/routings/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgRoutingUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRoutingRequest,
): Promise<RoutingDTO> {
  return client.request<RoutingDTO>('PATCH', `/api/v1/mfg/routings/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function mfgRoutingDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/mfg/routings/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgRoutingActivate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<RoutingDTO> {
  return client.request<RoutingDTO>('POST', `/api/v1/mfg/routings/${encodeURIComponent(params.id)}/activate`, {
    idempotencyKey,
  });
}

export async function mfgStandardCostList(
  client: ApiClient,
  query: ListStandardCostsQuery,
): Promise<PageItemStandardCostDTO> {
  return client.request<PageItemStandardCostDTO>('GET', `/api/v1/mfg/standard-costs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgStandardCostSet(
  client: ApiClient,
  body: SetStandardCostRequest,
  idempotencyKey?: string,
): Promise<ItemStandardCostDTO> {
  return client.request<ItemStandardCostDTO>('POST', `/api/v1/mfg/standard-costs`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderList(
  client: ApiClient,
  query: ListProductionOrderQuery,
): Promise<PageProductionOrderDTO> {
  return client.request<PageProductionOrderDTO>('GET', `/api/v1/mfg/production-orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgProductionOrderCreate(
  client: ApiClient,
  body: CreateProductionOrderRequest,
  idempotencyKey?: string,
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('POST', `/api/v1/mfg/production-orders`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderGet(
  client: ApiClient,
  params: { id: string },
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgProductionOrderUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateProductionOrderRequest,
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('PATCH', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function mfgProductionOrderRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseProductionOrderRequest,
  idempotencyKey?: string,
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelProductionOrderRequest,
  idempotencyKey?: string,
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderIssueMaterials(
  client: ApiClient,
  params: { id: string },
  body: IssueMaterialsRequestC7,
  idempotencyKey?: string,
): Promise<IssueMaterialsResultC7> {
  return client.request<IssueMaterialsResultC7>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/issue-materials`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderStartOperation(
  client: ApiClient,
  params: { id: string; seq: string },
  idempotencyKey?: string,
): Promise<ProductionOrderOperationDTO> {
  return client.request<ProductionOrderOperationDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/operations/${encodeURIComponent(params.seq)}/start`, {
    idempotencyKey,
  });
}

export async function mfgProductionOrderStopOperation(
  client: ApiClient,
  params: { id: string; seq: string },
  body: StopOperationRequest,
  idempotencyKey?: string,
): Promise<ProductionOrderOperationDTO> {
  return client.request<ProductionOrderOperationDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/operations/${encodeURIComponent(params.seq)}/stop`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderRecordQC(
  client: ApiClient,
  params: { id: string },
  body: RecordQCInspectionRequest,
  idempotencyKey?: string,
): Promise<QCInspectionDTO> {
  return client.request<QCInspectionDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/qc-inspections`, {
    body,
    idempotencyKey,
  });
}

export async function mfgNcrResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveNCRRequest,
  idempotencyKey?: string,
): Promise<NCRDTO> {
  return client.request<NCRDTO>('POST', `/api/v1/mfg/ncrs/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderListNCRs(
  client: ApiClient,
  params: { id: string },
): Promise<NCRDTO[]> {
  return client.request<NCRDTO[]>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/ncrs`, {
  });
}

export async function mfgProductionOrderListQCInspections(
  client: ApiClient,
  params: { id: string },
): Promise<QCInspectionDTO[]> {
  return client.request<QCInspectionDTO[]>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/qc-inspections`, {
  });
}

export async function mfgProductionOrderReceive(
  client: ApiClient,
  params: { id: string },
  body: ReceiveFinishedGoodsRequestC7,
  idempotencyKey?: string,
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/receive`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderClose(
  client: ApiClient,
  params: { id: string },
  body: ShortCloseRequest,
  idempotencyKey?: string,
): Promise<ProductionOrderDTO> {
  return client.request<ProductionOrderDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function mfgProductionOrderListVariances(
  client: ApiClient,
  params: { id: string },
): Promise<VarianceDTO[]> {
  return client.request<VarianceDTO[]>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/variances`, {
  });
}

export async function mfgMrpRun(
  client: ApiClient,
  body: RunMRPRequest,
  idempotencyKey?: string,
): Promise<MRPRunDTO> {
  return client.request<MRPRunDTO>('POST', `/api/v1/mfg/mrp/run`, {
    body,
    idempotencyKey,
  });
}

export async function mfgMrpSuggestionList(
  client: ApiClient,
  query: ListMRPSuggestionsQuery,
): Promise<PageMRPSuggestionDTO> {
  return client.request<PageMRPSuggestionDTO>('GET', `/api/v1/mfg/mrp-suggestions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgMrpSuggestionDismiss(
  client: ApiClient,
  params: { id: string },
  body: DismissMRPSuggestionRequest,
  idempotencyKey?: string,
): Promise<MRPSuggestionDTO> {
  return client.request<MRPSuggestionDTO>('POST', `/api/v1/mfg/mrp-suggestions/${encodeURIComponent(params.id)}/dismiss`, {
    body,
    idempotencyKey,
  });
}

export async function mfgMrpSuggestionAction(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ActionMRPSuggestionResponse> {
  return client.request<ActionMRPSuggestionResponse>('POST', `/api/v1/mfg/mrp-suggestions/${encodeURIComponent(params.id)}/action`, {
    idempotencyKey,
  });
}

export async function mfgReportWipClearing(
  client: ApiClient,
  query: WIPClearingReportQuery,
): Promise<WIPClearingReportDTO> {
  return client.request<WIPClearingReportDTO>('GET', `/api/v1/mfg/reports/wip-clearing`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgReportCapacity(
  client: ApiClient,
  query: CapacityReportQuery,
): Promise<CapacityReportDTO> {
  return client.request<CapacityReportDTO>('GET', `/api/v1/mfg/reports/capacity`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgSubstitutionsOptions(
  client: ApiClient,
  params: { id: string; mid: string },
): Promise<SubstituteOption[]> {
  return client.request<SubstituteOption[]>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/materials/${encodeURIComponent(params.mid)}/substitutes`, {
  });
}

export async function mfgSubstitutionsList(
  client: ApiClient,
  params: { id: string },
): Promise<MaterialSubstitutionDTO[]> {
  return client.request<MaterialSubstitutionDTO[]>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/substitutions`, {
  });
}

export async function mfgGenealogyGet(
  client: ApiClient,
  params: { id: string },
): Promise<LotTraceResult> {
  return client.request<LotTraceResult>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/genealogy`, {
  });
}

export async function mfgMaterialSerialsList(
  client: ApiClient,
  params: { id: string },
): Promise<MaterialSerialDTO[]> {
  return client.request<MaterialSerialDTO[]>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/material-serials`, {
  });
}

export async function mfgEcoList(
  client: ApiClient,
  query: ListECOQuery,
): Promise<PageEngineeringChangeOrderDTO> {
  return client.request<PageEngineeringChangeOrderDTO>('GET', `/api/v1/mfg/engineering-change-orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgEcoCreate(
  client: ApiClient,
  body: CreateECORequest,
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders`, {
    body,
    idempotencyKey,
  });
}

export async function mfgEcoGet(
  client: ApiClient,
  params: { id: string },
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('GET', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgEcoUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateECORequest,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('PATCH', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function mfgEcoSetItems(
  client: ApiClient,
  params: { id: string },
  body: SetECOItemsRequest,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('PUT', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/items`, {
    body,
  });
}

export async function mfgEcoAnalyse(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/analyse`, {
    idempotencyKey,
  });
}

export async function mfgEcoSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitECORequest,
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function mfgEcoDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideECORequest,
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/decide`, {
    body,
    idempotencyKey,
  });
}

export async function mfgEcoApply(
  client: ApiClient,
  params: { id: string },
  body: ApplyECORequest,
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/apply`, {
    body,
    idempotencyKey,
  });
}

export async function mfgEcoCompleteDisposition(
  client: ApiClient,
  params: { id: string; did: string },
  body: CompleteECODispositionRequest,
): Promise<ECODispositionDTO> {
  return client.request<ECODispositionDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/dispositions/${encodeURIComponent(params.did)}/complete`, {
    body,
  });
}

export async function mfgEcoClose(
  client: ApiClient,
  params: { id: string },
  body: CloseECORequest,
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/close`, {
    body,
    idempotencyKey,
  });
}

export async function mfgEcoCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelECORequest,
  idempotencyKey?: string,
): Promise<EngineeringChangeOrderDetailDTO> {
  return client.request<EngineeringChangeOrderDetailDTO>('POST', `/api/v1/mfg/engineering-change-orders/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function mfgEffectivityList(
  client: ApiClient,
  params: { id: string },
): Promise<BOMLineEffectivityDTO[]> {
  return client.request<BOMLineEffectivityDTO[]>('GET', `/api/v1/mfg/bom-lines/${encodeURIComponent(params.id)}/effectivity`, {
  });
}

export async function mfgEffectivitySet(
  client: ApiClient,
  params: { id: string },
  body: SetBOMLineEffectivityRequest,
): Promise<BOMLineEffectivityDTO[]> {
  return client.request<BOMLineEffectivityDTO[]>('PUT', `/api/v1/mfg/bom-lines/${encodeURIComponent(params.id)}/effectivity`, {
    body,
  });
}

export async function mfgBomOutputsList(
  client: ApiClient,
  params: { id: string },
): Promise<BOMOutputDTO[]> {
  return client.request<BOMOutputDTO[]>('GET', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}/outputs`, {
  });
}

export async function mfgBomOutputsSet(
  client: ApiClient,
  params: { id: string },
  body: SetBOMOutputsRequest,
): Promise<BOMOutputDTO[]> {
  return client.request<BOMOutputDTO[]>('PUT', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}/outputs`, {
    body,
  });
}

export async function mfgBomOutputsReceive(
  client: ApiClient,
  params: { id: string },
  body: ReceiveJointOutputsRequest,
  idempotencyKey?: string,
): Promise<ReceiveJointOutputsResultDTO> {
  return client.request<ReceiveJointOutputsResultDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/receive-outputs`, {
    body,
    idempotencyKey,
  });
}

export async function mfgBomExplode(
  client: ApiClient,
  params: { id: string },
  query: ExplodeBOMQuery,
): Promise<BOMExplosionLine[]> {
  return client.request<BOMExplosionLine[]>('POST', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}/explode`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgCalendarsList(
  client: ApiClient,
  params: { id: string },
): Promise<WorkCenterCalendarDTO[]> {
  return client.request<WorkCenterCalendarDTO[]>('GET', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/calendars`, {
  });
}

export async function mfgCalendarsSet(
  client: ApiClient,
  params: { id: string },
  body: SetWorkCenterCalendarRequest,
  idempotencyKey?: string,
): Promise<WorkCenterCalendarDTO> {
  return client.request<WorkCenterCalendarDTO>('PUT', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/calendars`, {
    body,
    idempotencyKey,
  });
}

export async function mfgCalendarsSetShifts(
  client: ApiClient,
  params: { id: string; cid: string },
  body: SetWorkCenterShiftsRequest,
  idempotencyKey?: string,
): Promise<WorkCenterCalendarDTO> {
  return client.request<WorkCenterCalendarDTO>('PUT', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/calendars/${encodeURIComponent(params.cid)}/shifts`, {
    body,
    idempotencyKey,
  });
}

export async function mfgCalendarsExceptions(
  client: ApiClient,
  params: { id: string },
  query: ListWorkCenterExceptionsQuery,
): Promise<WorkCenterExceptionDTO[]> {
  return client.request<WorkCenterExceptionDTO[]>('GET', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/exceptions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgCalendarsAddException(
  client: ApiClient,
  params: { id: string },
  body: CreateWorkCenterExceptionRequest,
  idempotencyKey?: string,
): Promise<WorkCenterExceptionDTO> {
  return client.request<WorkCenterExceptionDTO>('POST', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/exceptions`, {
    body,
    idempotencyKey,
  });
}

export async function mfgCapacityLoad(
  client: ApiClient,
  params: { id: string },
  query: ListWorkCenterLoadQuery,
): Promise<WorkCenterLoadDTO> {
  return client.request<WorkCenterLoadDTO>('GET', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/load`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgCapacityBottlenecks(
  client: ApiClient,
  query: BottleneckReportQuery,
): Promise<BottleneckReportDTO> {
  return client.request<BottleneckReportDTO>('GET', `/api/v1/mfg/reports/bottlenecks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgScheduleRun(
  client: ApiClient,
  body: RunScheduleRequest,
  idempotencyKey?: string,
): Promise<ScheduleRunDTO> {
  return client.request<ScheduleRunDTO>('POST', `/api/v1/mfg/schedule-runs`, {
    body,
    idempotencyKey,
  });
}

export async function mfgScheduleList(
  client: ApiClient,
  query: ListScheduleRunsQuery,
): Promise<PageScheduleRunDTO> {
  return client.request<PageScheduleRunDTO>('GET', `/api/v1/mfg/schedule-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgScheduleGet(
  client: ApiClient,
  params: { id: string },
): Promise<ScheduleRunDTO> {
  return client.request<ScheduleRunDTO>('GET', `/api/v1/mfg/schedule-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgDispatchGet(
  client: ApiClient,
  params: { id: string },
): Promise<DispatchListDTO> {
  return client.request<DispatchListDTO>('GET', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/dispatch-list`, {
  });
}

export async function mfgDispatchResequence(
  client: ApiClient,
  params: { id: string },
  body: ResequenceDispatchListRequest,
  idempotencyKey?: string,
): Promise<DispatchListDTO> {
  return client.request<DispatchListDTO>('PUT', `/api/v1/mfg/work-centers/${encodeURIComponent(params.id)}/dispatch-list`, {
    body,
    idempotencyKey,
  });
}

export async function mfgPlanningParametersList(
  client: ApiClient,
  query: ListPlanningParametersQuery,
): Promise<PagePlanningParameterDTO> {
  return client.request<PagePlanningParameterDTO>('GET', `/api/v1/mfg/planning-parameters`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgPlanningParametersSet(
  client: ApiClient,
  params: { itemId: string },
  body: SetPlanningParameterRequest,
  idempotencyKey?: string,
): Promise<PlanningParameterDTO> {
  return client.request<PlanningParameterDTO>('PUT', `/api/v1/mfg/planning-parameters/${encodeURIComponent(params.itemId)}`, {
    body,
    idempotencyKey,
  });
}

export async function mfgMrpRunDepth(
  client: ApiClient,
  body: RunMRPDepthRequest,
  idempotencyKey?: string,
): Promise<MRPRunDTO> {
  return client.request<MRPRunDTO>('POST', `/api/v1/mfg/mrp-runs`, {
    body,
    idempotencyKey,
  });
}

export async function mfgMrpExceptions(
  client: ApiClient,
  params: { id: string },
): Promise<MRPExceptionDTO[]> {
  return client.request<MRPExceptionDTO[]>('GET', `/api/v1/mfg/mrp-runs/${encodeURIComponent(params.id)}/exceptions`, {
  });
}

export async function mfgPeggingGet(
  client: ApiClient,
  params: { id: string },
): Promise<MRPPeggingNodeDTO> {
  return client.request<MRPPeggingNodeDTO>('GET', `/api/v1/mfg/mrp-suggestions/${encodeURIComponent(params.id)}/pegging`, {
  });
}

export async function mfgToolsList(
  client: ApiClient,
  query: ListMfgToolsQuery,
): Promise<PageMfgToolDTO> {
  return client.request<PageMfgToolDTO>('GET', `/api/v1/mfg/tools`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgToolsCreate(
  client: ApiClient,
  body: CreateMfgToolRequest,
  idempotencyKey?: string,
): Promise<MfgToolDTO> {
  return client.request<MfgToolDTO>('POST', `/api/v1/mfg/tools`, {
    body,
    idempotencyKey,
  });
}

export async function mfgToolsGet(
  client: ApiClient,
  params: { id: string },
): Promise<MfgToolDTO> {
  return client.request<MfgToolDTO>('GET', `/api/v1/mfg/tools/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgToolsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateMfgToolRequest,
): Promise<MfgToolDTO> {
  return client.request<MfgToolDTO>('PATCH', `/api/v1/mfg/tools/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function mfgRoutingOperationToolsList(
  client: ApiClient,
  params: { id: string },
): Promise<RoutingOperationToolDTO[]> {
  return client.request<RoutingOperationToolDTO[]>('GET', `/api/v1/mfg/routing-operations/${encodeURIComponent(params.id)}/tools`, {
  });
}

export async function mfgRoutingOperationToolsSet(
  client: ApiClient,
  params: { id: string },
  body: SetRoutingOperationToolsRequest,
  idempotencyKey?: string,
): Promise<RoutingOperationToolDTO[]> {
  return client.request<RoutingOperationToolDTO[]>('PUT', `/api/v1/mfg/routing-operations/${encodeURIComponent(params.id)}/tools`, {
    body,
    idempotencyKey,
  });
}

export async function mfgSettlementsPreview(
  client: ApiClient,
  params: { id: string },
  body: ShortCloseRequest,
  idempotencyKey?: string,
): Promise<SettlementPreviewDTO> {
  return client.request<SettlementPreviewDTO>('POST', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/settlement-preview`, {
    body,
    idempotencyKey,
  });
}

export async function mfgSettlementsList(
  client: ApiClient,
  params: { id: string },
  query: ListSettlementsQuery,
): Promise<PageProductionSettlementDTO> {
  return client.request<PageProductionSettlementDTO>('GET', `/api/v1/mfg/production-orders/${encodeURIComponent(params.id)}/settlements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgRollupsList(
  client: ApiClient,
  query: ListStandardCostRollupsQuery,
): Promise<PageStandardCostRollupDTO> {
  return client.request<PageStandardCostRollupDTO>('GET', `/api/v1/mfg/standard-cost-rollups`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgRollupsCompute(
  client: ApiClient,
  body: ComputeStandardCostRollupRequest,
  idempotencyKey?: string,
): Promise<StandardCostRollupDTO[]> {
  return client.request<StandardCostRollupDTO[]>('POST', `/api/v1/mfg/standard-cost-rollups`, {
    body,
    idempotencyKey,
  });
}

export async function mfgRollupsApply(
  client: ApiClient,
  body: ApplyStandardCostRollupsRequest,
  idempotencyKey?: string,
): Promise<StandardCostRollupDTO[]> {
  return client.request<StandardCostRollupDTO[]>('POST', `/api/v1/mfg/standard-cost-rollups/apply`, {
    body,
    idempotencyKey,
  });
}

export async function mfgRollupsGet(
  client: ApiClient,
  params: { id: string },
): Promise<StandardCostRollupDTO> {
  return client.request<StandardCostRollupDTO>('GET', `/api/v1/mfg/standard-cost-rollups/${encodeURIComponent(params.id)}`, {
  });
}

export async function mfgReportsVariance(
  client: ApiClient,
  query: VarianceAnalysisQuery,
): Promise<VarianceAnalysisReportDTO> {
  return client.request<VarianceAnalysisReportDTO>('GET', `/api/v1/mfg/reports/variance-analysis`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function mfgBomsSetExpectedYield(
  client: ApiClient,
  params: { id: string },
  body: SetBOMExpectedYieldRequest,
  idempotencyKey?: string,
): Promise<BOMDTO> {
  return client.request<BOMDTO>('PUT', `/api/v1/mfg/boms/${encodeURIComponent(params.id)}/expected-yield`, {
    body,
    idempotencyKey,
  });
}

export async function mfgCostingAccountsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<MfgCostingAccountsDTO> {
  return client.request<MfgCostingAccountsDTO>('GET', `/api/v1/mfg/settings/${encodeURIComponent(params.companyId)}/costing-accounts`, {
  });
}

export async function mfgCostingAccountsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdateMfgCostingAccountsRequest,
  idempotencyKey?: string,
): Promise<MfgCostingAccountsDTO> {
  return client.request<MfgCostingAccountsDTO>('PUT', `/api/v1/mfg/settings/${encodeURIComponent(params.companyId)}/costing-accounts`, {
    body,
    idempotencyKey,
  });
}

export async function qmsPlansList(
  client: ApiClient,
  query: ListInspectionPlansQuery,
): Promise<PageInspectionPlanDTO> {
  return client.request<PageInspectionPlanDTO>('GET', `/api/v1/inspection-plans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function qmsPlansCreate(
  client: ApiClient,
  body: CreateInspectionPlanRequest,
  idempotencyKey?: string,
): Promise<InspectionPlanDTO> {
  return client.request<InspectionPlanDTO>('POST', `/api/v1/inspection-plans`, {
    body,
    idempotencyKey,
  });
}

export async function qmsPlansGet(
  client: ApiClient,
  params: { id: string },
): Promise<InspectionPlanDetailDTO> {
  return client.request<InspectionPlanDetailDTO>('GET', `/api/v1/inspection-plans/${encodeURIComponent(params.id)}`, {
  });
}

export async function qmsPlansUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateInspectionPlanRequest,
): Promise<InspectionPlanDTO> {
  return client.request<InspectionPlanDTO>('PATCH', `/api/v1/inspection-plans/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function qmsPlansActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivatePlanRequest,
): Promise<InspectionPlanDTO> {
  return client.request<InspectionPlanDTO>('POST', `/api/v1/inspection-plans/${encodeURIComponent(params.id)}/activate`, {
    body,
  });
}

export async function qmsCharacteristicsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateCharacteristicRequest,
  idempotencyKey?: string,
): Promise<InspectionCharacteristicDTO> {
  return client.request<InspectionCharacteristicDTO>('POST', `/api/v1/inspection-plans/${encodeURIComponent(params.id)}/characteristics`, {
    body,
    idempotencyKey,
  });
}

export async function qmsCharacteristicsUpdate(
  client: ApiClient,
  params: { id: string; cid: string },
  body: UpdateCharacteristicRequest,
): Promise<InspectionCharacteristicDTO> {
  return client.request<InspectionCharacteristicDTO>('PATCH', `/api/v1/inspection-plans/${encodeURIComponent(params.id)}/characteristics/${encodeURIComponent(params.cid)}`, {
    body,
  });
}

export async function qmsCharacteristicsDelete(
  client: ApiClient,
  params: { id: string; cid: string },
  query: DeleteCharacteristicQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/inspection-plans/${encodeURIComponent(params.id)}/characteristics/${encodeURIComponent(params.cid)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function qmsSamplingPlansList(
  client: ApiClient,
  query: ListSamplingPlansQuery,
): Promise<PageSamplingPlanDTO> {
  return client.request<PageSamplingPlanDTO>('GET', `/api/v1/sampling-plans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function qmsSamplingPlansCreate(
  client: ApiClient,
  body: CreateSamplingPlanRequest,
  idempotencyKey?: string,
): Promise<SamplingPlanDTO> {
  return client.request<SamplingPlanDTO>('POST', `/api/v1/sampling-plans`, {
    body,
    idempotencyKey,
  });
}

export async function qmsSamplingPlansSetLevels(
  client: ApiClient,
  params: { id: string },
  body: SetSamplingLevelsRequest,
): Promise<SamplingPlanDTO> {
  return client.request<SamplingPlanDTO>('PUT', `/api/v1/sampling-plans/${encodeURIComponent(params.id)}/levels`, {
    body,
  });
}

export async function qmsSamplingPlansPreview(
  client: ApiClient,
  params: { id: string },
  body: SamplePreviewRequest,
): Promise<SamplePreviewDTO> {
  return client.request<SamplePreviewDTO>('POST', `/api/v1/sampling-plans/${encodeURIComponent(params.id)}/preview`, {
    body,
  });
}

export async function qmsSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<QMSSettingsDTO> {
  return client.request<QMSSettingsDTO>('GET', `/api/v1/qms/company-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function qmsSettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdateQMSSettingsRequest,
): Promise<QMSSettingsDTO> {
  return client.request<QMSSettingsDTO>('PUT', `/api/v1/qms/company-settings/${encodeURIComponent(params.companyId)}`, {
    body,
  });
}

export async function qmsInspectionsList(
  client: ApiClient,
  query: ListInspectionLotsQuery,
): Promise<PageQualityInspectionDTO> {
  return client.request<PageQualityInspectionDTO>('GET', `/api/v1/inspection-lots`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function qmsInspectionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<QualityInspectionDetailDTO> {
  return client.request<QualityInspectionDetailDTO>('GET', `/api/v1/inspection-lots/${encodeURIComponent(params.id)}`, {
  });
}

export async function qmsResultsRecord(
  client: ApiClient,
  params: { id: string },
  body: RecordResultsRequest,
): Promise<QualityInspectionDetailDTO> {
  return client.request<QualityInspectionDetailDTO>('POST', `/api/v1/inspection-lots/${encodeURIComponent(params.id)}/results`, {
    body,
  });
}

export async function qmsInspectionsWaive(
  client: ApiClient,
  params: { id: string },
  body: WaiveInspectionRequest,
): Promise<QualityInspectionDTO> {
  return client.request<QualityInspectionDTO>('POST', `/api/v1/inspection-lots/${encodeURIComponent(params.id)}/waive`, {
    body,
  });
}

export async function qmsDispositionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateDispositionRequest,
  idempotencyKey?: string,
): Promise<QMSDispositionDTO> {
  return client.request<QMSDispositionDTO>('POST', `/api/v1/inspection-lots/${encodeURIComponent(params.id)}/dispositions`, {
    body,
    idempotencyKey,
  });
}

export async function qmsReportsSummary(
  client: ApiClient,
  query: QualitySummaryQuery,
): Promise<QualitySummaryDTO> {
  return client.request<QualitySummaryDTO>('GET', `/api/v1/reports/quality-summary`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function qmsCapaList(
  client: ApiClient,
  query: ListCAPAQuery,
): Promise<PageCAPADTO> {
  return client.request<PageCAPADTO>('GET', `/api/v1/capa`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function qmsCapaCreate(
  client: ApiClient,
  body: CreateCAPARequest,
  idempotencyKey?: string,
): Promise<CAPADTO> {
  return client.request<CAPADTO>('POST', `/api/v1/capa`, {
    body,
    idempotencyKey,
  });
}

export async function qmsCapaGet(
  client: ApiClient,
  params: { id: string },
): Promise<CAPADetailDTO> {
  return client.request<CAPADetailDTO>('GET', `/api/v1/capa/${encodeURIComponent(params.id)}`, {
  });
}

export async function qmsCapaUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateCAPARequest,
): Promise<CAPADTO> {
  return client.request<CAPADTO>('PATCH', `/api/v1/capa/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function qmsCapaAddAction(
  client: ApiClient,
  params: { id: string },
  body: AddCAPAActionRequest,
  idempotencyKey?: string,
): Promise<CAPAActionDTO> {
  return client.request<CAPAActionDTO>('POST', `/api/v1/capa/${encodeURIComponent(params.id)}/actions`, {
    body,
    idempotencyKey,
  });
}

export async function qmsCapaCompleteAction(
  client: ApiClient,
  params: { id: string; aid: string },
  body: CompleteCAPAActionRequest,
): Promise<CAPAActionDTO> {
  return client.request<CAPAActionDTO>('POST', `/api/v1/capa/${encodeURIComponent(params.id)}/actions/${encodeURIComponent(params.aid)}/complete`, {
    body,
  });
}

export async function qmsCapaVerify(
  client: ApiClient,
  params: { id: string },
  body: VerifyCAPARequest,
): Promise<CAPADTO> {
  return client.request<CAPADTO>('POST', `/api/v1/capa/${encodeURIComponent(params.id)}/verify`, {
    body,
  });
}

export async function consolGroupsCreate(
  client: ApiClient,
  body: ConsolCreateGroupRequest,
  idempotencyKey?: string,
): Promise<ConsolGroupDTO> {
  return client.request<ConsolGroupDTO>('POST', `/api/v1/consol-groups`, {
    body,
    idempotencyKey,
  });
}

export async function consolGroupsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateGroupRequest,
): Promise<ConsolGroupDTO> {
  return client.request<ConsolGroupDTO>('PATCH', `/api/v1/consol-groups/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function consolGroupsSetIcDifferenceThreshold(
  client: ApiClient,
  params: { id: string },
  body: ConsolIcThresholdRequest,
  idempotencyKey?: string,
): Promise<ConsolGroupDTO> {
  return client.request<ConsolGroupDTO>('PUT', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/ic-difference-threshold`, {
    body,
    idempotencyKey,
  });
}

export async function consolGroupsGet(
  client: ApiClient,
  params: { id: string },
): Promise<GroupDetail> {
  return client.request<GroupDetail>('GET', `/api/v1/consol-groups/${encodeURIComponent(params.id)}`, {
  });
}

export async function consolGroupsList(
  client: ApiClient,
  query: ConsolListGroupsQuery,
): Promise<PageGroupDTO2> {
  return client.request<PageGroupDTO2>('GET', `/api/v1/consol-groups`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function consolMembersAdd(
  client: ApiClient,
  params: { id: string },
  body: AddMemberRequest,
  idempotencyKey?: string,
): Promise<ConsolMemberDTO> {
  return client.request<ConsolMemberDTO>('POST', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/members`, {
    body,
    idempotencyKey,
  });
}

export async function consolMembersUpdate(
  client: ApiClient,
  params: { id: string; memberId: string },
  body: UpdateMemberRequest,
): Promise<ConsolMemberDTO> {
  return client.request<ConsolMemberDTO>('PATCH', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/members/${encodeURIComponent(params.memberId)}`, {
    body,
  });
}

export async function consolGroupsReadiness(
  client: ApiClient,
  params: { id: string },
  query: ConsolReadinessQuery,
): Promise<ConsolReadinessDTO> {
  return client.request<ConsolReadinessDTO>('GET', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/readiness`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function consolAccountMapsList(
  client: ApiClient,
  params: { id: string },
  query: ConsolAccountMapListQuery,
): Promise<PageConsolAccountMapDTO> {
  return client.request<PageConsolAccountMapDTO>('GET', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/account-maps`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function consolAccountMapsPut(
  client: ApiClient,
  params: { id: string },
  body: ConsolAccountMapsPutRequest,
  idempotencyKey?: string,
): Promise<ConsolAccountMapsPutResult> {
  return client.request<ConsolAccountMapsPutResult>('PUT', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/account-maps`, {
    body,
    idempotencyKey,
  });
}

export async function consolAccountMapsSuggest(
  client: ApiClient,
  params: { id: string },
  body: ConsolAccountMapSuggestRequest,
): Promise<ConsolAccountMapSuggestionsDTO> {
  return client.request<ConsolAccountMapSuggestionsDTO>('POST', `/api/v1/consol-groups/${encodeURIComponent(params.id)}/account-maps/suggest`, {
    body,
  });
}

export async function consolRunsGenerate(
  client: ApiClient,
  body: GenerateRunRequest,
  idempotencyKey?: string,
): Promise<ConsolRunDTO> {
  return client.request<ConsolRunDTO>('POST', `/api/v1/consol-runs`, {
    body,
    idempotencyKey,
  });
}

export async function consolRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolRunDTO> {
  return client.request<ConsolRunDTO>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function consolRunsList(
  client: ApiClient,
  query: ConsolListRunsQuery,
): Promise<PageRunDTO3> {
  return client.request<PageRunDTO3>('GET', `/api/v1/consol-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function consolRunsTrialBalance(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolTrialBalance> {
  return client.request<ConsolTrialBalance>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/trial-balance`, {
  });
}

export async function consolRunsTrialBalanceExport(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/trial-balance/export`, {
  });
}

export async function consolRunsIncomeStatement(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolStatement> {
  return client.request<ConsolStatement>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/income-statement`, {
  });
}

export async function consolRunsIncomeStatementExport(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/income-statement/export`, {
  });
}

export async function consolRunsBalanceSheet(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolStatement> {
  return client.request<ConsolStatement>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/balance-sheet`, {
  });
}

export async function consolRunsBalanceSheetExport(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/balance-sheet/export`, {
  });
}

export async function consolRunsStatements(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolStatementSetDTO> {
  return client.request<ConsolStatementSetDTO>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/statements`, {
  });
}

export async function consolRunsCurrencyTranslation(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolCurrencyTranslationDTO> {
  return client.request<ConsolCurrencyTranslationDTO>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/currency-translation`, {
  });
}

export async function consolRunsMinorityInterest(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolMinorityInterestDTO> {
  return client.request<ConsolMinorityInterestDTO>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/minority-interest`, {
  });
}

export async function consolRunsEntityContribution(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolEntityContributionDTO> {
  return client.request<ConsolEntityContributionDTO>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/entity-contribution`, {
  });
}

export async function consolRunsWorkbookExport(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/workbook/export`, {
  });
}

export async function consolRunsEliminations(
  client: ApiClient,
  params: { id: string },
  query: ListEliminationsQuery,
): Promise<PageEliminationDTO> {
  return client.request<PageEliminationDTO>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/eliminations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function consolRunsMismatchReport(
  client: ApiClient,
  params: { id: string },
): Promise<MismatchReport> {
  return client.request<MismatchReport>('GET', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/mismatch-report`, {
  });
}

export async function consolRunsPublish(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ConsolRunDTO> {
  return client.request<ConsolRunDTO>('POST', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/publish`, {
    idempotencyKey,
  });
}

export async function consolRunsRestate(
  client: ApiClient,
  params: { id: string },
  body: ConsolRunRestateRequest,
  idempotencyKey?: string,
): Promise<ConsolRunDTO> {
  return client.request<ConsolRunDTO>('POST', `/api/v1/consol-runs/${encodeURIComponent(params.id)}/restate`, {
    body,
    idempotencyKey,
  });
}

export async function consolAdjustmentsList(
  client: ApiClient,
  query: ConsolAdjustmentListQuery,
): Promise<PageConsolAdjustmentDTO> {
  return client.request<PageConsolAdjustmentDTO>('GET', `/api/v1/consol-adjustments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function consolAdjustmentsCreate(
  client: ApiClient,
  body: ConsolAdjustmentCreateRequest,
  idempotencyKey?: string,
): Promise<ConsolAdjustmentDTO> {
  return client.request<ConsolAdjustmentDTO>('POST', `/api/v1/consol-adjustments`, {
    body,
    idempotencyKey,
  });
}

export async function consolAdjustmentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ConsolAdjustmentDTO> {
  return client.request<ConsolAdjustmentDTO>('GET', `/api/v1/consol-adjustments/${encodeURIComponent(params.id)}`, {
  });
}

export async function consolAdjustmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: ConsolAdjustmentUpdateRequest,
): Promise<ConsolAdjustmentDTO> {
  return client.request<ConsolAdjustmentDTO>('PATCH', `/api/v1/consol-adjustments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function consolAdjustmentsSubmit(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ConsolAdjustmentDTO> {
  return client.request<ConsolAdjustmentDTO>('POST', `/api/v1/consol-adjustments/${encodeURIComponent(params.id)}/submit`, {
    idempotencyKey,
  });
}

export async function consolAdjustmentsWithdraw(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<ConsolAdjustmentDTO> {
  return client.request<ConsolAdjustmentDTO>('POST', `/api/v1/consol-adjustments/${encodeURIComponent(params.id)}/withdraw`, {
    idempotencyKey,
  });
}

export async function consolEliminationsAccept(
  client: ApiClient,
  params: { id: string },
  body: AcceptEliminationRequest,
  idempotencyKey?: string,
): Promise<EliminationDTO> {
  return client.request<EliminationDTO>('POST', `/api/v1/consol-eliminations/${encodeURIComponent(params.id)}/accept`, {
    body,
    idempotencyKey,
  });
}

export async function consolEliminationsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectEliminationRequest,
  idempotencyKey?: string,
): Promise<EliminationDTO> {
  return client.request<EliminationDTO>('POST', `/api/v1/consol-eliminations/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function aiAssistantConversationsCreate(
  client: ApiClient,
  body: CreateConversationRequest,
  idempotencyKey?: string,
): Promise<ConversationDTO> {
  return client.request<ConversationDTO>('POST', `/api/v1/ai/assistant/conversations`, {
    body,
    idempotencyKey,
  });
}

export async function aiAssistantConversationsList(
  client: ApiClient,
  query: ListConversationsQuery,
): Promise<PageConversationDTO> {
  return client.request<PageConversationDTO>('GET', `/api/v1/ai/assistant/conversations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiAssistantConversationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ConversationDTO> {
  return client.request<ConversationDTO>('GET', `/api/v1/ai/assistant/conversations/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiAssistantConversationsMessages(
  client: ApiClient,
  params: { id: string },
  query: ListMessagesQuery,
): Promise<PageMessageDTO> {
  return client.request<PageMessageDTO>('GET', `/api/v1/ai/assistant/conversations/${encodeURIComponent(params.id)}/messages`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiAssistantConversationsSend(
  client: ApiClient,
  params: { id: string },
  body: SendMessageRequest,
  idempotencyKey?: string,
): Promise<MessageDTO> {
  return client.request<MessageDTO>('POST', `/api/v1/ai/assistant/conversations/${encodeURIComponent(params.id)}/messages`, {
    body,
    idempotencyKey,
  });
}

export async function aiAssistantToolPreviewsList(
  client: ApiClient,
  query: ListPreviewsQuery,
): Promise<PagePreviewDTO> {
  return client.request<PagePreviewDTO>('GET', `/api/v1/ai/assistant/tool-previews`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiAssistantToolPreviewsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PreviewDTO> {
  return client.request<PreviewDTO>('GET', `/api/v1/ai/assistant/tool-previews/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiAssistantToolPreviewsConfirm(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PreviewDTO> {
  return client.request<PreviewDTO>('POST', `/api/v1/ai/assistant/tool-previews/${encodeURIComponent(params.id)}/confirm`, {
    idempotencyKey,
  });
}

export async function aiAssistantToolPreviewsDiscard(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PreviewDTO> {
  return client.request<PreviewDTO>('POST', `/api/v1/ai/assistant/tool-previews/${encodeURIComponent(params.id)}/discard`, {
    idempotencyKey,
  });
}

export async function aiExtractionsCreate(
  client: ApiClient,
  file: File | Blob,
  fields: { kind: string },
  idempotencyKey?: string,
): Promise<ExtractionDTO> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', String(fields.kind));
  return client.request<ExtractionDTO>('POST', `/api/v1/ai/extractions`, {
    body: form,
    idempotencyKey,
  });
}

export async function aiExtractionsList(
  client: ApiClient,
  query: ListExtractionsQuery,
): Promise<PageExtractionDTO> {
  return client.request<PageExtractionDTO>('GET', `/api/v1/ai/extractions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiExtractionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<ExtractionDTO> {
  return client.request<ExtractionDTO>('GET', `/api/v1/ai/extractions/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiExtractionsConfirm(
  client: ApiClient,
  params: { id: string },
  body: ReviewExtractionRequest,
  idempotencyKey?: string,
): Promise<ExtractionDTO> {
  return client.request<ExtractionDTO>('POST', `/api/v1/ai/extractions/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function aiExtractionsDiscard(
  client: ApiClient,
  params: { id: string },
  body: ReviewExtractionRequest,
  idempotencyKey?: string,
): Promise<ExtractionDTO> {
  return client.request<ExtractionDTO>('POST', `/api/v1/ai/extractions/${encodeURIComponent(params.id)}/discard`, {
    body,
    idempotencyKey,
  });
}

export async function aiCapabilitiesList(
  client: ApiClient,
  query: ListAICapabilitiesQuery,
): Promise<PageAICapabilityDTO> {
  return client.request<PageAICapabilityDTO>('GET', `/api/v1/ai/capabilities`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiCapabilitiesGet(
  client: ApiClient,
  params: { code: string },
): Promise<AICapabilityDetailDTO> {
  return client.request<AICapabilityDetailDTO>('GET', `/api/v1/ai/capabilities/${encodeURIComponent(params.code)}`, {
  });
}

export async function aiCapabilitiesPatch(
  client: ApiClient,
  params: { code: string },
  body: PatchAICapabilityRequest,
): Promise<AICapabilityDTO> {
  return client.request<AICapabilityDTO>('PATCH', `/api/v1/ai/capabilities/${encodeURIComponent(params.code)}`, {
    body,
  });
}

export async function aiCapabilitiesSetAutonomy(
  client: ApiClient,
  params: { code: string },
  body: SetAutonomyRequest,
  idempotencyKey?: string,
): Promise<AICapabilityDTO> {
  return client.request<AICapabilityDTO>('POST', `/api/v1/ai/capabilities/${encodeURIComponent(params.code)}/autonomy`, {
    body,
    idempotencyKey,
  });
}

export async function aiSettingsGet(
  client: ApiClient,
  query: AISettingsQuery,
): Promise<AISettingsDTO> {
  return client.request<AISettingsDTO>('GET', `/api/v1/ai/settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiSettingsPatch(
  client: ApiClient,
  body: PatchAISettingsRequest,
  idempotencyKey?: string,
): Promise<AISettingsDTO> {
  return client.request<AISettingsDTO>('PATCH', `/api/v1/ai/settings`, {
    body,
    idempotencyKey,
  });
}

export async function aiRecommendationsList(
  client: ApiClient,
  query: ListAIRecommendationsQuery,
): Promise<PageAIRecommendationDTO> {
  return client.request<PageAIRecommendationDTO>('GET', `/api/v1/ai/recommendations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiRecommendationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<AIRecommendationDetailDTO> {
  return client.request<AIRecommendationDetailDTO>('GET', `/api/v1/ai/recommendations/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiRecommendationsAccept(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<AIRecommendationDetailDTO> {
  return client.request<AIRecommendationDetailDTO>('POST', `/api/v1/ai/recommendations/${encodeURIComponent(params.id)}/accept`, {
    idempotencyKey,
  });
}

export async function aiRecommendationsReject(
  client: ApiClient,
  params: { id: string },
  body: RejectAIRecommendationRequest,
  idempotencyKey?: string,
): Promise<AIRecommendationDetailDTO> {
  return client.request<AIRecommendationDetailDTO>('POST', `/api/v1/ai/recommendations/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function aiRecommendationOutcomesList(
  client: ApiClient,
  query: ListAIRecommendationOutcomesQuery,
): Promise<PageAIRecommendationOutcomeDTO> {
  return client.request<PageAIRecommendationOutcomeDTO>('GET', `/api/v1/ai/recommendation-outcomes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiAccuracyByType(
  client: ApiClient,
  query: AIAccuracyQuery,
): Promise<AIAccuracyByTypeDTO> {
  return client.request<AIAccuracyByTypeDTO>('GET', `/api/v1/ai/accuracy`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiBacktestsCreate(
  client: ApiClient,
  body: CreateAIBacktestRequest,
  idempotencyKey?: string,
): Promise<AIBacktestRunDTO> {
  return client.request<AIBacktestRunDTO>('POST', `/api/v1/ai/backtests`, {
    body,
    idempotencyKey,
  });
}

export async function aiBacktestsList(
  client: ApiClient,
  query: ListAIBacktestRunsQuery,
): Promise<PageAIBacktestRunDTO> {
  return client.request<PageAIBacktestRunDTO>('GET', `/api/v1/ai/backtests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiBacktestsGet(
  client: ApiClient,
  params: { id: string },
): Promise<AIBacktestRunDTO> {
  return client.request<AIBacktestRunDTO>('GET', `/api/v1/ai/backtests/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiBacktestsPoints(
  client: ApiClient,
  params: { id: string },
  query: ListAIBacktestPointsQuery,
): Promise<PageAIBacktestPointDTO> {
  return client.request<PageAIBacktestPointDTO>('GET', `/api/v1/ai/backtests/${encodeURIComponent(params.id)}/points`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiAgentsList(
  client: ApiClient,
  query: ListAIAgentsQuery,
): Promise<PageAIAgentDTO> {
  return client.request<PageAIAgentDTO>('GET', `/api/v1/ai/agents`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiAgentsPatch(
  client: ApiClient,
  params: { code: string },
  body: PatchAIAgentRequest,
): Promise<AIAgentDTO> {
  return client.request<AIAgentDTO>('PATCH', `/api/v1/ai/agents/${encodeURIComponent(params.code)}`, {
    body,
  });
}

export async function aiAgentsRun(
  client: ApiClient,
  params: { code: string },
  body: RunAIAgentRequest,
  idempotencyKey?: string,
): Promise<AIBriefingDetailDTO> {
  return client.request<AIBriefingDetailDTO>('POST', `/api/v1/ai/agents/${encodeURIComponent(params.code)}/run`, {
    body,
    idempotencyKey,
  });
}

export async function aiAgentRunsList(
  client: ApiClient,
  query: ListAIAgentRunsQuery,
): Promise<PageAIAgentRunDTO> {
  return client.request<PageAIAgentRunDTO>('GET', `/api/v1/ai/agent-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiBriefingsList(
  client: ApiClient,
  query: ListAIBriefingsQuery,
): Promise<PageAIBriefingDTO> {
  return client.request<PageAIBriefingDTO>('GET', `/api/v1/ai/briefings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiBriefingsGet(
  client: ApiClient,
  params: { id: string },
): Promise<AIBriefingDetailDTO> {
  return client.request<AIBriefingDetailDTO>('GET', `/api/v1/ai/briefings/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiBriefingsRead(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<AIBriefingDTO> {
  return client.request<AIBriefingDTO>('POST', `/api/v1/ai/briefings/${encodeURIComponent(params.id)}/read`, {
    idempotencyKey,
  });
}

export async function aiConfigAnomaliesList(
  client: ApiClient,
  query: ListAIConfigAnomaliesQuery,
): Promise<PageAIConfigAnomalyDTO> {
  return client.request<PageAIConfigAnomalyDTO>('GET', `/api/v1/ai/config-anomalies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function aiConfigAnomaliesCoverage(
  client: ApiClient,
): Promise<AIConfigAnomalyCoverageDTO> {
  return client.request<AIConfigAnomalyCoverageDTO>('GET', `/api/v1/ai/config-anomalies/coverage`, {
  });
}

export async function aiConfigAnomaliesGet(
  client: ApiClient,
  params: { id: string },
): Promise<AIConfigAnomalyDTO> {
  return client.request<AIConfigAnomalyDTO>('GET', `/api/v1/ai/config-anomalies/${encodeURIComponent(params.id)}`, {
  });
}

export async function aiConfigAnomaliesAcknowledge(
  client: ApiClient,
  params: { id: string },
  body: AcknowledgeConfigAnomalyRequest,
  idempotencyKey?: string,
): Promise<AIConfigAnomalyDTO> {
  return client.request<AIConfigAnomalyDTO>('POST', `/api/v1/ai/config-anomalies/${encodeURIComponent(params.id)}/acknowledge`, {
    body,
    idempotencyKey,
  });
}

export async function aiConfigAnomaliesDismiss(
  client: ApiClient,
  params: { id: string },
  body: AcknowledgeConfigAnomalyRequest,
  idempotencyKey?: string,
): Promise<AIConfigAnomalyDTO> {
  return client.request<AIConfigAnomalyDTO>('POST', `/api/v1/ai/config-anomalies/${encodeURIComponent(params.id)}/dismiss`, {
    body,
    idempotencyKey,
  });
}

export async function aiConfigAnomaliesSweep(
  client: ApiClient,
  idempotencyKey?: string,
): Promise<AIAnomalySweepResult> {
  return client.request<AIAnomalySweepResult>('POST', `/api/v1/ai/config-anomalies/sweep`, {
    idempotencyKey,
  });
}

export async function aiConfigAnomalyRulesList(
  client: ApiClient,
): Promise<AIConfigAnomalyRuleDTO[]> {
  return client.request<AIConfigAnomalyRuleDTO[]>('GET', `/api/v1/ai/config-anomaly-rules`, {
  });
}

export async function aiConfigAnomalyRulesPatch(
  client: ApiClient,
  params: { code: string },
  body: PatchAIConfigAnomalyRuleRequest,
): Promise<AIConfigAnomalyRuleDTO> {
  return client.request<AIConfigAnomalyRuleDTO>('PATCH', `/api/v1/ai/config-anomaly-rules/${encodeURIComponent(params.code)}`, {
    body,
  });
}

export async function reportingDashboardsGet(
  client: ApiClient,
  params: { dashboard: string },
  query: DashboardQuery,
): Promise<ReportingDashboardResponse> {
  return client.request<ReportingDashboardResponse>('GET', `/api/v1/reporting/dashboards/${encodeURIComponent(params.dashboard)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingKpisList(
  client: ApiClient,
): Promise<KPIDefDTO[]> {
  return client.request<KPIDefDTO[]>('GET', `/api/v1/reporting/kpis`, {
  });
}

export async function reportingKpisDrill(
  client: ApiClient,
  params: { key: string },
  query: DrillQuery,
): Promise<DrillResponse> {
  return client.request<DrillResponse>('GET', `/api/v1/reporting/kpis/${encodeURIComponent(params.key)}/drill`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingRebuild(
  client: ApiClient,
  body: RebuildRequest,
  idempotencyKey?: string,
): Promise<RebuildResponse> {
  return client.request<RebuildResponse>('POST', `/api/v1/reporting/rebuild`, {
    body,
    idempotencyKey,
  });
}

export async function reportingExport(
  client: ApiClient,
  query: ReportingExportQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reporting/export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingSavedFiltersCreate(
  client: ApiClient,
  body: CreateSavedFilterRequest,
  idempotencyKey?: string,
): Promise<SavedFilterDTO> {
  return client.request<SavedFilterDTO>('POST', `/api/v1/reporting/saved-filters`, {
    body,
    idempotencyKey,
  });
}

export async function reportingSavedFiltersList(
  client: ApiClient,
  query: ListSavedFiltersQuery,
): Promise<SavedFilterDTO[]> {
  return client.request<SavedFilterDTO[]>('GET', `/api/v1/reporting/saved-filters`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingSavedFiltersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateSavedFilterRequest,
): Promise<SavedFilterDTO> {
  return client.request<SavedFilterDTO>('PATCH', `/api/v1/reporting/saved-filters/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function reportingSavedFiltersDelete(
  client: ApiClient,
  params: { id: string },
  query: ReportingDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/reporting/saved-filters/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingSchedulesCreate(
  client: ApiClient,
  body: CreateScheduleRequest,
  idempotencyKey?: string,
): Promise<ScheduleDTO> {
  return client.request<ScheduleDTO>('POST', `/api/v1/reporting/schedules`, {
    body,
    idempotencyKey,
  });
}

export async function reportingSchedulesList(
  client: ApiClient,
): Promise<ScheduleDTO[]> {
  return client.request<ScheduleDTO[]>('GET', `/api/v1/reporting/schedules`, {
  });
}

export async function reportingSchedulesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateScheduleRequest,
): Promise<ScheduleDTO> {
  return client.request<ScheduleDTO>('PATCH', `/api/v1/reporting/schedules/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function reportingSchedulesDelete(
  client: ApiClient,
  params: { id: string },
  query: ReportingDeleteQuery,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/reporting/schedules/${encodeURIComponent(params.id)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingMetricsList(
  client: ApiClient,
  query: ListMetricDefinitionsQuery,
): Promise<PageMetricDefinitionDTO> {
  return client.request<PageMetricDefinitionDTO>('GET', `/api/v1/reporting/metrics`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingMetricsSync(
  client: ApiClient,
  idempotencyKey?: string,
): Promise<MetricSyncResultDTO> {
  return client.request<MetricSyncResultDTO>('POST', `/api/v1/reporting/metrics/sync`, {
    idempotencyKey,
  });
}

export async function reportingMetricsGet(
  client: ApiClient,
  params: { code: string },
  query: MetricDetailQuery,
): Promise<MetricDefinitionDetailDTO> {
  return client.request<MetricDefinitionDetailDTO>('GET', `/api/v1/reporting/metrics/${encodeURIComponent(params.code)}`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingMetricsUpdate(
  client: ApiClient,
  params: { code: string },
  body: UpdateMetricDefinitionRequest,
): Promise<MetricDefinitionDTO> {
  return client.request<MetricDefinitionDTO>('PATCH', `/api/v1/reporting/metrics/${encodeURIComponent(params.code)}`, {
    body,
  });
}

export async function reportingMetricOwnersSet(
  client: ApiClient,
  params: { code: string },
  body: SetMetricOwnerRequest,
  idempotencyKey?: string,
): Promise<MetricOwnerDTO> {
  return client.request<MetricOwnerDTO>('POST', `/api/v1/reporting/metrics/${encodeURIComponent(params.code)}/owner`, {
    body,
    idempotencyKey,
  });
}

export async function reportingMetricOwnersList(
  client: ApiClient,
  params: { code: string },
): Promise<MetricOwnerDTO[]> {
  return client.request<MetricOwnerDTO[]>('GET', `/api/v1/reporting/metrics/${encodeURIComponent(params.code)}/owners`, {
  });
}

export async function reportingCertificationsCreate(
  client: ApiClient,
  params: { code: string },
  body: CertifyMetricRequest,
  idempotencyKey?: string,
): Promise<MetricCertificationDTO> {
  return client.request<MetricCertificationDTO>('POST', `/api/v1/reporting/metrics/${encodeURIComponent(params.code)}/certifications`, {
    body,
    idempotencyKey,
  });
}

export async function reportingCertificationsRevoke(
  client: ApiClient,
  params: { id: string },
  body: RevokeMetricCertificationRequest,
): Promise<MetricCertificationDTO> {
  return client.request<MetricCertificationDTO>('POST', `/api/v1/reporting/certifications/${encodeURIComponent(params.id)}/revoke`, {
    body,
  });
}

export async function reportingCertificationsList(
  client: ApiClient,
  query: ListMetricCertificationsQuery,
): Promise<PageMetricCertificationDTO> {
  return client.request<PageMetricCertificationDTO>('GET', `/api/v1/reporting/certifications`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingOutputsList(
  client: ApiClient,
  query: ListReportOutputsQuery,
): Promise<PageReportOutputDTO> {
  return client.request<PageReportOutputDTO>('GET', `/api/v1/reporting/outputs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingDailyOperatingList(
  client: ApiClient,
  query: ListDailyOperatingQuery,
): Promise<PageDailyOperatingReportDTO> {
  return client.request<PageDailyOperatingReportDTO>('GET', `/api/v1/reporting/daily-operating`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingDailyOperatingBoard(
  client: ApiClient,
  query: DailyOperatingBoardQuery,
): Promise<DailyOperatingBoardDTO> {
  return client.request<DailyOperatingBoardDTO>('GET', `/api/v1/reporting/daily-operating/board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingDailyOperatingBuild(
  client: ApiClient,
  body: BuildDailyOperatingRequest,
  idempotencyKey?: string,
): Promise<DailyOperatingDetailDTO> {
  return client.request<DailyOperatingDetailDTO>('POST', `/api/v1/reporting/daily-operating/build`, {
    body,
    idempotencyKey,
  });
}

export async function reportingDailyOperatingGet(
  client: ApiClient,
  params: { id: string },
): Promise<DailyOperatingDetailDTO> {
  return client.request<DailyOperatingDetailDTO>('GET', `/api/v1/reporting/daily-operating/${encodeURIComponent(params.id)}`, {
  });
}

export async function reportingDailyOperatingSectionsList(
  client: ApiClient,
  params: { id: string },
): Promise<DailyOperatingSectionDTO[]> {
  return client.request<DailyOperatingSectionDTO[]>('GET', `/api/v1/reporting/daily-operating/${encodeURIComponent(params.id)}/sections`, {
  });
}

export async function reportingDailyOperatingPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishDailyOperatingRequest,
): Promise<DailyOperatingDetailDTO> {
  return client.request<DailyOperatingDetailDTO>('POST', `/api/v1/reporting/daily-operating/${encodeURIComponent(params.id)}/publish`, {
    body,
  });
}

export async function reportingDailyOperatingSettingsGet(
  client: ApiClient,
  query: ReportingSettingsQuery,
): Promise<ReportingSettingsDTO> {
  return client.request<ReportingSettingsDTO>('GET', `/api/v1/reporting/settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingDailyOperatingSettingsUpdate(
  client: ApiClient,
  body: UpdateReportingSettingsRequest,
): Promise<ReportingSettingsDTO> {
  return client.request<ReportingSettingsDTO>('PATCH', `/api/v1/reporting/settings`, {
    body,
  });
}

export async function reportingExceptionViewsList(
  client: ApiClient,
  query: ListReportExceptionViewsQuery,
): Promise<ReportExceptionViewDTO[]> {
  return client.request<ReportExceptionViewDTO[]>('GET', `/api/v1/reporting/exception-views`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function reportingExceptionViewsCreate(
  client: ApiClient,
  body: CreateReportExceptionViewRequest,
  idempotencyKey?: string,
): Promise<ReportExceptionViewDTO> {
  return client.request<ReportExceptionViewDTO>('POST', `/api/v1/reporting/exception-views`, {
    body,
    idempotencyKey,
  });
}

export async function reportingExceptionViewsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateReportExceptionViewRequest,
): Promise<ReportExceptionViewDTO> {
  return client.request<ReportExceptionViewDTO>('PATCH', `/api/v1/reporting/exception-views/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function reportingExceptionViewsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/reporting/exception-views/${encodeURIComponent(params.id)}`, {
  });
}

export async function reportingExceptionViewsRows(
  client: ApiClient,
  params: { id: string },
  query: ReportExceptionRowsQuery,
): Promise<ReportExceptionRowsDTO> {
  return client.request<ReportExceptionRowsDTO>('GET', `/api/v1/reporting/exception-views/${encodeURIComponent(params.id)}/rows`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function webhooksEndpointsList(
  client: ApiClient,
  query: ListWebhookEndpointsQuery,
): Promise<PageWebhookEndpointDTO> {
  return client.request<PageWebhookEndpointDTO>('GET', `/api/v1/webhooks/endpoints`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function webhooksEndpointsCreate(
  client: ApiClient,
  body: CreateWebhookEndpointRequest,
  idempotencyKey?: string,
): Promise<CreateWebhookEndpointResponse> {
  return client.request<CreateWebhookEndpointResponse>('POST', `/api/v1/webhooks/endpoints`, {
    body,
    idempotencyKey,
  });
}

export async function webhooksEndpointsGet(
  client: ApiClient,
  params: { id: string },
): Promise<WebhookEndpointDTO> {
  return client.request<WebhookEndpointDTO>('GET', `/api/v1/webhooks/endpoints/${encodeURIComponent(params.id)}`, {
  });
}

export async function webhooksEndpointsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateWebhookEndpointRequest,
): Promise<WebhookEndpointDTO> {
  return client.request<WebhookEndpointDTO>('PATCH', `/api/v1/webhooks/endpoints/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function webhooksEndpointsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/webhooks/endpoints/${encodeURIComponent(params.id)}`, {
  });
}

export async function webhooksEndpointsRotateSecret(
  client: ApiClient,
  params: { id: string },
): Promise<RotateSecretResponse> {
  return client.request<RotateSecretResponse>('POST', `/api/v1/webhooks/endpoints/${encodeURIComponent(params.id)}/rotate-secret`, {
  });
}

export async function webhooksDeliveriesList(
  client: ApiClient,
  params: { id: string },
  query: ListWebhookDeliveriesQuery,
): Promise<PageWebhookDeliveryDTO> {
  return client.request<PageWebhookDeliveryDTO>('GET', `/api/v1/webhooks/endpoints/${encodeURIComponent(params.id)}/deliveries`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function webhooksDeliveriesRedeliver(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/webhooks/deliveries/${encodeURIComponent(params.id)}/redeliver`, {
  });
}

export async function portalBranding(
  client: ApiClient,
): Promise<BrandingResponse> {
  return client.request<BrandingResponse>('GET', `/portal/api/v1/branding`, {
  });
}

export async function portalAuthLogin(
  client: ApiClient,
  body: portalLoginRequest,
): Promise<PortalLoginResult> {
  return client.request<PortalLoginResult>('POST', `/portal/api/v1/auth/login`, {
    body,
  });
}

export async function portalAuthRefresh(
  client: ApiClient,
  body: PortalRefreshRequest,
): Promise<PortalLoginResult> {
  return client.request<PortalLoginResult>('POST', `/portal/api/v1/auth/refresh`, {
    body,
  });
}

export async function portalAuthForgotPassword(
  client: ApiClient,
  body: portalForgotPasswordRequest,
): Promise<void> {
  return client.request<void>('POST', `/portal/api/v1/auth/forgot-password`, {
    body,
  });
}

export async function portalAuthResetPassword(
  client: ApiClient,
  body: portalResetPasswordRequest,
): Promise<void> {
  return client.request<void>('POST', `/portal/api/v1/auth/reset-password`, {
    body,
  });
}

export async function portalAuthAcceptInvite(
  client: ApiClient,
  body: portalAcceptInviteRequest,
): Promise<PortalLoginResult> {
  return client.request<PortalLoginResult>('POST', `/portal/api/v1/auth/accept-invite`, {
    body,
  });
}

export async function portalAuthLogout(
  client: ApiClient,
): Promise<void> {
  return client.request<void>('POST', `/portal/api/v1/auth/logout`, {
  });
}

export async function portalMe(
  client: ApiClient,
): Promise<PortalMeResponse> {
  return client.request<PortalMeResponse>('GET', `/portal/api/v1/me`, {
  });
}

export async function portalCompaniesList(
  client: ApiClient,
): Promise<CompanyDTO[]> {
  return client.request<CompanyDTO[]>('GET', `/portal/api/v1/companies`, {
  });
}

export async function portalCatalogueList(
  client: ApiClient,
  query: ListCatalogueQuery,
): Promise<PageCatalogueItemDTO> {
  return client.request<PageCatalogueItemDTO>('GET', `/portal/api/v1/catalogue`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalCatalogueGet(
  client: ApiClient,
  params: { itemId: string },
): Promise<CatalogueItemDTO> {
  return client.request<CatalogueItemDTO>('GET', `/portal/api/v1/catalogue/${encodeURIComponent(params.itemId)}`, {
  });
}

export async function portalOrdersList(
  client: ApiClient,
  query: portalOrderListQuery,
): Promise<PageOrderDTO> {
  return client.request<PageOrderDTO>('GET', `/portal/api/v1/orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalOrdersCreate(
  client: ApiClient,
  body: CreatePortalOrderRequest,
  idempotencyKey?: string,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/portal/api/v1/orders`, {
    body,
    idempotencyKey,
  });
}

export async function portalOrdersGet(
  client: ApiClient,
  params: { id: string },
): Promise<OrderDTO> {
  return client.request<OrderDTO>('GET', `/portal/api/v1/orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function portalOrdersCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelOrderRequest,
): Promise<OrderDTO> {
  return client.request<OrderDTO>('POST', `/portal/api/v1/orders/${encodeURIComponent(params.id)}/cancel`, {
    body,
  });
}

export async function portalOrdersShipments(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageShipmentDTO> {
  return client.request<PageShipmentDTO>('GET', `/portal/api/v1/orders/${encodeURIComponent(params.id)}/shipments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalQuotesList(
  client: ApiClient,
  query: portalOrderListQuery,
): Promise<PageQuoteDTO> {
  return client.request<PageQuoteDTO>('GET', `/portal/api/v1/quotes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalQuotesGet(
  client: ApiClient,
  params: { id: string },
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('GET', `/portal/api/v1/quotes/${encodeURIComponent(params.id)}`, {
  });
}

export async function portalQuotesAccept(
  client: ApiClient,
  params: { id: string },
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/portal/api/v1/quotes/${encodeURIComponent(params.id)}/accept`, {
  });
}

export async function portalQuotesReject(
  client: ApiClient,
  params: { id: string },
  body: PortalRejectQuoteRequest,
): Promise<SalesQuoteDTO> {
  return client.request<SalesQuoteDTO>('POST', `/portal/api/v1/quotes/${encodeURIComponent(params.id)}/reject`, {
    body,
  });
}

export async function portalInvoicesList(
  client: ApiClient,
  query: portalCompanyListQuery,
): Promise<PageInvoiceDTO3> {
  return client.request<PageInvoiceDTO3>('GET', `/portal/api/v1/invoices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalInvoicesGet(
  client: ApiClient,
  params: { id: string },
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('GET', `/portal/api/v1/invoices/${encodeURIComponent(params.id)}`, {
  });
}

export async function portalInvoicesPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/portal/api/v1/invoices/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function portalStatementGet(
  client: ApiClient,
  query: portalStatementQuery,
): Promise<ArStatement> {
  return client.request<ArStatement>('GET', `/portal/api/v1/statement`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalStatementPdf(
  client: ApiClient,
  query: portalStatementQuery,
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/portal/api/v1/statement/pdf`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalOpenItemsGet(
  client: ApiClient,
  query: portalCompanyListQuery,
): Promise<PageInvoiceDTO3> {
  return client.request<PageInvoiceDTO3>('GET', `/portal/api/v1/open-items`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalPaymentClaimsCreate(
  client: ApiClient,
  body: SubmitPaymentClaimRequest,
  idempotencyKey?: string,
): Promise<PaymentClaimDTO> {
  return client.request<PaymentClaimDTO>('POST', `/portal/api/v1/payment-claims`, {
    body,
    idempotencyKey,
  });
}

export async function portalPaymentClaimsList(
  client: ApiClient,
  query: ListPaymentClaimsQuery,
): Promise<PagePaymentClaimDTO> {
  return client.request<PagePaymentClaimDTO>('GET', `/portal/api/v1/payment-claims`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalCasesList(
  client: ApiClient,
  query: CursorQuery,
): Promise<PageCaseDTO> {
  return client.request<PageCaseDTO>('GET', `/portal/api/v1/cases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalCasesCreate(
  client: ApiClient,
  body: PortalCreateCaseRequest,
  idempotencyKey?: string,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/portal/api/v1/cases`, {
    body,
    idempotencyKey,
  });
}

export async function portalCasesGet(
  client: ApiClient,
  params: { id: string },
): Promise<CaseDTO> {
  return client.request<CaseDTO>('GET', `/portal/api/v1/cases/${encodeURIComponent(params.id)}`, {
  });
}

export async function portalCasesRespond(
  client: ApiClient,
  params: { id: string },
  body: PortalRespondCaseRequest,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/portal/api/v1/cases/${encodeURIComponent(params.id)}/respond`, {
    body,
  });
}

export async function portalCasesCsat(
  client: ApiClient,
  params: { id: string },
  body: PortalCSATRequest,
): Promise<CaseDTO> {
  return client.request<CaseDTO>('POST', `/portal/api/v1/cases/${encodeURIComponent(params.id)}/csat`, {
    body,
  });
}

export async function portalRfqsList(
  client: ApiClient,
  query: CursorQuery,
): Promise<PageRFQDTO> {
  return client.request<PageRFQDTO>('GET', `/portal/api/v1/rfqs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalRfqsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RFQDTO> {
  return client.request<RFQDTO>('GET', `/portal/api/v1/rfqs/${encodeURIComponent(params.id)}`, {
  });
}

export async function portalRfqsQuote(
  client: ApiClient,
  params: { id: string },
  body: SubmitRFQQuoteRequest,
  idempotencyKey?: string,
): Promise<QuoteDTO> {
  return client.request<QuoteDTO>('POST', `/portal/api/v1/rfqs/${encodeURIComponent(params.id)}/quote`, {
    body,
    idempotencyKey,
  });
}

export async function portalRfqsDecline(
  client: ApiClient,
  params: { id: string },
  body: DeclineRFQRequest,
): Promise<void> {
  return client.request<void>('POST', `/portal/api/v1/rfqs/${encodeURIComponent(params.id)}/decline`, {
    body,
  });
}

export async function portalPurchaseOrdersList(
  client: ApiClient,
  query: portalOrderListQuery,
): Promise<PagePODTO> {
  return client.request<PagePODTO>('GET', `/portal/api/v1/purchase-orders`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalPurchaseOrdersGet(
  client: ApiClient,
  params: { id: string },
): Promise<PODTO> {
  return client.request<PODTO>('GET', `/portal/api/v1/purchase-orders/${encodeURIComponent(params.id)}`, {
  });
}

export async function portalPurchaseOrdersConfirm(
  client: ApiClient,
  params: { id: string },
  body: PortalConfirmPORequest,
): Promise<PODTO> {
  return client.request<PODTO>('POST', `/portal/api/v1/purchase-orders/${encodeURIComponent(params.id)}/confirm`, {
    body,
  });
}

export async function portalPurchaseOrdersAsn(
  client: ApiClient,
  params: { id: string },
  body: SubmitASNRequest,
  idempotencyKey?: string,
): Promise<ASNDTO> {
  return client.request<ASNDTO>('POST', `/portal/api/v1/purchase-orders/${encodeURIComponent(params.id)}/asn`, {
    body,
    idempotencyKey,
  });
}

export async function portalSupplierInvoicesCreate(
  client: ApiClient,
  body: SubmitSupplierInvoiceRequest,
  idempotencyKey?: string,
): Promise<ApInvoiceDTO> {
  return client.request<ApInvoiceDTO>('POST', `/portal/api/v1/supplier-invoices`, {
    body,
    idempotencyKey,
  });
}

export async function portalSupplierScorecard(
  client: ApiClient,
  query: portalCompanyListQuery,
): Promise<PageScorecardDTO> {
  return client.request<PageScorecardDTO>('GET', `/portal/api/v1/scorecard`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalSupplierInvoicesList(
  client: ApiClient,
  query: ListSupplierInvoicesQuery,
): Promise<PageInvoiceDTO2> {
  return client.request<PageInvoiceDTO2>('GET', `/portal/api/v1/supplier-invoices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalTasksList(
  client: ApiClient,
  query: CursorQuery,
): Promise<PageContractorTaskDTO> {
  return client.request<PageContractorTaskDTO>('GET', `/portal/api/v1/tasks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalTasksProgress(
  client: ApiClient,
  params: { id: string },
  body: TaskProgressRequest,
): Promise<ContractorTaskDTO> {
  return client.request<ContractorTaskDTO>('POST', `/portal/api/v1/tasks/${encodeURIComponent(params.id)}/progress`, {
    body,
  });
}

export async function portalTimesheetsList(
  client: ApiClient,
  query: ListContractorTimesheetsQuery,
): Promise<PageTimesheetDTO> {
  return client.request<PageTimesheetDTO>('GET', `/portal/api/v1/timesheets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalTimesheetsCreate(
  client: ApiClient,
  body: CreateContractorTimesheetRequest,
  idempotencyKey?: string,
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('POST', `/portal/api/v1/timesheets`, {
    body,
    idempotencyKey,
  });
}

export async function portalTimesheetsSubmit(
  client: ApiClient,
  params: { id: string },
): Promise<TimesheetDTO> {
  return client.request<TimesheetDTO>('POST', `/portal/api/v1/timesheets/${encodeURIComponent(params.id)}/submit`, {
  });
}

export async function portalContractorInvoicesCreate(
  client: ApiClient,
  body: SubmitContractorInvoiceRequest,
  idempotencyKey?: string,
): Promise<ContractorInvoiceDTO> {
  return client.request<ContractorInvoiceDTO>('POST', `/portal/api/v1/contractor-invoices`, {
    body,
    idempotencyKey,
  });
}

export async function portalPaymentStatus(
  client: ApiClient,
  query: portalPaymentStatusQuery,
): Promise<PaymentStatusResponse> {
  return client.request<PaymentStatusResponse>('GET', `/portal/api/v1/payment-status`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalAttachmentsUpload(
  client: ApiClient,
  idempotencyKey?: string,
): Promise<AttachmentDTO> {
  return client.request<AttachmentDTO>('POST', `/portal/api/v1/attachments`, {
    idempotencyKey,
  });
}

export async function portalAttachmentsList(
  client: ApiClient,
  query: ListQuery,
): Promise<PageAttachmentDTO> {
  return client.request<PageAttachmentDTO>('GET', `/portal/api/v1/attachments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalAttachmentsDownload(
  client: ApiClient,
  params: { id: string },
): Promise<DownloadDTO> {
  return client.request<DownloadDTO>('GET', `/portal/api/v1/attachments/${encodeURIComponent(params.id)}/download`, {
  });
}

export async function portalAdminUsersCreate(
  client: ApiClient,
  body: CreatePortalUserRequest,
  idempotencyKey?: string,
): Promise<PortalUserDTO> {
  return client.request<PortalUserDTO>('POST', `/api/v1/portal-users`, {
    body,
    idempotencyKey,
  });
}

export async function portalAdminUsersList(
  client: ApiClient,
  query: ListPortalUsersQuery,
): Promise<PageUserDTO2> {
  return client.request<PageUserDTO2>('GET', `/api/v1/portal-users`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function portalAdminUsersSuspend(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/portal-users/${encodeURIComponent(params.id)}/suspend`, {
  });
}

export async function portalAdminUsersReactivate(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/portal-users/${encodeURIComponent(params.id)}/reactivate`, {
  });
}

export async function portalAdminSettingsUpsert(
  client: ApiClient,
  body: UpsertPortalSettingsRequest,
): Promise<PortalSettingsDTO> {
  return client.request<PortalSettingsDTO>('PUT', `/api/v1/portal-settings`, {
    body,
  });
}

export async function portalAdminSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<PortalSettingsDTO> {
  return client.request<PortalSettingsDTO>('GET', `/api/v1/portal-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function posOutletsList(
  client: ApiClient,
  query: ListPOSOutletsQuery,
): Promise<PagePOSOutletDTO> {
  return client.request<PagePOSOutletDTO>('GET', `/api/v1/pos-outlets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posOutletsCreate(
  client: ApiClient,
  body: CreatePOSOutletRequest,
  idempotencyKey?: string,
): Promise<POSOutletDTO> {
  return client.request<POSOutletDTO>('POST', `/api/v1/pos-outlets`, {
    body,
    idempotencyKey,
  });
}

export async function posOutletsGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSOutletDTO> {
  return client.request<POSOutletDTO>('GET', `/api/v1/pos-outlets/${encodeURIComponent(params.id)}`, {
  });
}

export async function posOutletsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePOSOutletRequest,
): Promise<POSOutletDTO> {
  return client.request<POSOutletDTO>('PATCH', `/api/v1/pos-outlets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function posTerminalsList(
  client: ApiClient,
  query: ListPOSTerminalsQuery,
): Promise<PagePOSTerminalDTO> {
  return client.request<PagePOSTerminalDTO>('GET', `/api/v1/pos-terminals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posTerminalsCreate(
  client: ApiClient,
  body: CreatePOSTerminalRequest,
  idempotencyKey?: string,
): Promise<POSTerminalDTO> {
  return client.request<POSTerminalDTO>('POST', `/api/v1/pos-terminals`, {
    body,
    idempotencyKey,
  });
}

export async function posTerminalsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePOSTerminalRequest,
): Promise<POSTerminalDTO> {
  return client.request<POSTerminalDTO>('PATCH', `/api/v1/pos-terminals/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function posRevenueCentersList(
  client: ApiClient,
  query: ListPOSRevenueCentersQuery,
): Promise<PagePOSRevenueCenterDTO> {
  return client.request<PagePOSRevenueCenterDTO>('GET', `/api/v1/pos-revenue-centers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posRevenueCentersCreate(
  client: ApiClient,
  body: CreatePOSRevenueCenterRequest,
  idempotencyKey?: string,
): Promise<POSRevenueCenterDTO> {
  return client.request<POSRevenueCenterDTO>('POST', `/api/v1/pos-revenue-centers`, {
    body,
    idempotencyKey,
  });
}

export async function posRevenueCentersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePOSRevenueCenterRequest,
): Promise<POSRevenueCenterDTO> {
  return client.request<POSRevenueCenterDTO>('PATCH', `/api/v1/pos-revenue-centers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function posSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<POSSettingsDTO> {
  return client.request<POSSettingsDTO>('GET', `/api/v1/pos-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function posSettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdatePOSSettingsRequest,
  idempotencyKey?: string,
): Promise<POSSettingsDTO> {
  return client.request<POSSettingsDTO>('PUT', `/api/v1/pos-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function posTenderTypesList(
  client: ApiClient,
  query: ListPOSTenderTypesQuery,
): Promise<PagePOSTenderTypeDTO> {
  return client.request<PagePOSTenderTypeDTO>('GET', `/api/v1/pos-tender-types`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posTenderTypesCreate(
  client: ApiClient,
  body: CreatePOSTenderTypeRequest,
  idempotencyKey?: string,
): Promise<POSTenderTypeDTO> {
  return client.request<POSTenderTypeDTO>('POST', `/api/v1/pos-tender-types`, {
    body,
    idempotencyKey,
  });
}

export async function posTenderTypesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePOSTenderTypeRequest,
): Promise<POSTenderTypeDTO> {
  return client.request<POSTenderTypeDTO>('PATCH', `/api/v1/pos-tender-types/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function posDenominationsList(
  client: ApiClient,
  query: ListPOSDenominationsQuery,
): Promise<PagePOSCashDenominationDTO> {
  return client.request<PagePOSCashDenominationDTO>('GET', `/api/v1/pos-cash-denominations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posDenominationsSet(
  client: ApiClient,
  body: SetPOSDenominationsRequest,
  idempotencyKey?: string,
): Promise<POSCashDenominationDTO[]> {
  return client.request<POSCashDenominationDTO[]>('PUT', `/api/v1/pos-cash-denominations`, {
    body,
    idempotencyKey,
  });
}

export async function posMenusList(
  client: ApiClient,
  query: ListPOSMenusQuery,
): Promise<PagePOSMenuDTO> {
  return client.request<PagePOSMenuDTO>('GET', `/api/v1/pos-menus`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posMenusCreate(
  client: ApiClient,
  body: CreatePOSMenuRequest,
  idempotencyKey?: string,
): Promise<POSMenuDTO> {
  return client.request<POSMenuDTO>('POST', `/api/v1/pos-menus`, {
    body,
    idempotencyKey,
  });
}

export async function posMenusGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSMenuDTO> {
  return client.request<POSMenuDTO>('GET', `/api/v1/pos-menus/${encodeURIComponent(params.id)}`, {
  });
}

export async function posMenuVersionsList(
  client: ApiClient,
  query: ListPOSMenuVersionsQuery,
): Promise<PagePOSMenuVersionDTO> {
  return client.request<PagePOSMenuVersionDTO>('GET', `/api/v1/pos-menu-versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posMenuVersionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateMenuVersionRequest,
  idempotencyKey?: string,
): Promise<POSMenuVersionDTO> {
  return client.request<POSMenuVersionDTO>('POST', `/api/v1/pos-menus/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function posMenuVersionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSMenuVersionDetailDTO> {
  return client.request<POSMenuVersionDetailDTO>('GET', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}`, {
  });
}

export async function posMenuVersionsCheck(
  client: ApiClient,
  params: { id: string },
): Promise<POSMenuPublishCheckDTO> {
  return client.request<POSMenuPublishCheckDTO>('POST', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}/check`, {
  });
}

export async function posMenuVersionsPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishPOSMenuRequest,
): Promise<POSMenuVersionDTO> {
  return client.request<POSMenuVersionDTO>('POST', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}/publish`, {
    body,
  });
}

export async function posMenuVersionsWithdraw(
  client: ApiClient,
  params: { id: string },
): Promise<POSMenuVersionDTO> {
  return client.request<POSMenuVersionDTO>('POST', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}/withdraw`, {
  });
}

export async function posMenuSectionsSet(
  client: ApiClient,
  params: { id: string },
  body: SetPOSMenuSectionsRequest,
  idempotencyKey?: string,
): Promise<POSMenuSectionDTO[]> {
  return client.request<POSMenuSectionDTO[]>('PUT', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}/sections`, {
    body,
    idempotencyKey,
  });
}

export async function posMenuItemsSet(
  client: ApiClient,
  params: { id: string },
  body: SetPOSMenuItemsRequest,
  idempotencyKey?: string,
): Promise<POSMenuItemDTO[]> {
  return client.request<POSMenuItemDTO[]>('PUT', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}/items`, {
    body,
    idempotencyKey,
  });
}

export async function posModifiersSetGroups(
  client: ApiClient,
  params: { id: string },
  body: SetPOSModifierGroupsRequest,
  idempotencyKey?: string,
): Promise<POSModifierGroupDTO[]> {
  return client.request<POSModifierGroupDTO[]>('PUT', `/api/v1/pos-menu-versions/${encodeURIComponent(params.id)}/modifier-groups`, {
    body,
    idempotencyKey,
  });
}

export async function posAvailabilityList(
  client: ApiClient,
  query: ListPOSAvailabilityQuery,
): Promise<PagePOSItemAvailabilityDTO> {
  return client.request<PagePOSItemAvailabilityDTO>('GET', `/api/v1/pos-availability`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posAvailabilitySet(
  client: ApiClient,
  body: SetPOSAvailabilityRequest,
  idempotencyKey?: string,
): Promise<POSItemAvailabilityDTO> {
  return client.request<POSItemAvailabilityDTO>('PUT', `/api/v1/pos-availability`, {
    body,
    idempotencyKey,
  });
}

export async function posAllergensListCodes(
  client: ApiClient,
  query: ListPOSAllergenCodesQuery,
): Promise<PagePOSAllergenCodeDTO> {
  return client.request<PagePOSAllergenCodeDTO>('GET', `/api/v1/pos-allergen-codes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posAllergensList(
  client: ApiClient,
  query: ListPOSItemAllergensQuery,
): Promise<PagePOSItemAllergenDTO> {
  return client.request<PagePOSItemAllergenDTO>('GET', `/api/v1/pos-item-allergens`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posAllergensSet(
  client: ApiClient,
  params: { itemId: string },
  body: SetPOSItemAllergensRequest,
  idempotencyKey?: string,
): Promise<POSItemAllergenDTO[]> {
  return client.request<POSItemAllergenDTO[]>('PUT', `/api/v1/pos-item-allergens/${encodeURIComponent(params.itemId)}`, {
    body,
    idempotencyKey,
  });
}

export async function posSalesList(
  client: ApiClient,
  query: ListPOSSalesQuery,
): Promise<PagePOSSaleDTO> {
  return client.request<PagePOSSaleDTO>('GET', `/api/v1/pos-sales`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posSalesCreate(
  client: ApiClient,
  body: CreatePOSSaleRequest,
  idempotencyKey?: string,
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('POST', `/api/v1/pos-sales`, {
    body,
    idempotencyKey,
  });
}

export async function posSalesGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('GET', `/api/v1/pos-sales/${encodeURIComponent(params.id)}`, {
  });
}

export async function posSaleLinesAdd(
  client: ApiClient,
  params: { id: string },
  body: AddPOSSaleLineRequest,
  idempotencyKey?: string,
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/lines`, {
    body,
    idempotencyKey,
  });
}

export async function posSaleLinesUpdate(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: UpdatePOSSaleLineRequest,
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('PATCH', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}`, {
    body,
  });
}

export async function posSaleLinesVoid(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: VoidPOSLineRequest,
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('DELETE', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}`, {
    body,
  });
}

export async function posDiscountsApply(
  client: ApiClient,
  params: { id: string },
  body: ApplyPOSDiscountRequest,
  idempotencyKey?: string,
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/discounts`, {
    body,
    idempotencyKey,
  });
}

export async function posDiscountsRemove(
  client: ApiClient,
  params: { id: string; did: string },
): Promise<POSSaleDetailDTO> {
  return client.request<POSSaleDetailDTO>('DELETE', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/discounts/${encodeURIComponent(params.did)}`, {
  });
}

export async function posSalesReprice(
  client: ApiClient,
  params: { id: string },
  body: RepricePOSSaleRequest,
): Promise<POSSalePriceDTO> {
  return client.request<POSSalePriceDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/price`, {
    body,
  });
}

export async function posSalesAbandon(
  client: ApiClient,
  params: { id: string },
  body: AbandonPOSSaleRequest,
): Promise<POSSaleDTO> {
  return client.request<POSSaleDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/abandon`, {
    body,
  });
}

export async function posSalesWaterfall(
  client: ApiClient,
  params: { id: string },
): Promise<POSSaleWaterfallDTO> {
  return client.request<POSSaleWaterfallDTO>('GET', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/waterfall`, {
  });
}

export async function posTendersAdd(
  client: ApiClient,
  params: { id: string },
  body: AddPOSTenderRequest,
): Promise<POSSaleTenderStateDTO> {
  return client.request<POSSaleTenderStateDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/tenders`, {
    body,
  });
}

export async function posTendersVoid(
  client: ApiClient,
  params: { id: string; tid: string },
  body: VoidPOSTenderRequest,
): Promise<POSSaleTenderStateDTO> {
  return client.request<POSSaleTenderStateDTO>('DELETE', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/tenders/${encodeURIComponent(params.tid)}`, {
    body,
  });
}

export async function posInstrumentsList(
  client: ApiClient,
  query: ListPOSInstrumentsQuery,
): Promise<PagePOSInstrumentDTO> {
  return client.request<PagePOSInstrumentDTO>('GET', `/api/v1/pos-instruments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posInstrumentsLookup(
  client: ApiClient,
  body: LookupPOSInstrumentRequest,
): Promise<POSInstrumentDTO> {
  return client.request<POSInstrumentDTO>('POST', `/api/v1/pos-instruments/lookup`, {
    body,
  });
}

export async function posInstrumentsIssue(
  client: ApiClient,
  body: IssuePOSInstrumentRequest,
  idempotencyKey?: string,
): Promise<POSInstrumentDTO> {
  return client.request<POSInstrumentDTO>('POST', `/api/v1/pos-instruments`, {
    body,
    idempotencyKey,
  });
}

export async function posInstrumentsAdjust(
  client: ApiClient,
  params: { id: string },
  body: AdjustPOSInstrumentRequest,
): Promise<POSInstrumentDTO> {
  return client.request<POSInstrumentDTO>('POST', `/api/v1/pos-instruments/${encodeURIComponent(params.id)}/adjust`, {
    body,
  });
}

export async function posInstrumentsMovements(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PagePOSInstrumentMovementDTO> {
  return client.request<PagePOSInstrumentMovementDTO>('GET', `/api/v1/pos-instruments/${encodeURIComponent(params.id)}/movements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posTipsList(
  client: ApiClient,
  query: ListPOSTipsQuery,
): Promise<PagePOSTipDTO> {
  return client.request<PagePOSTipDTO>('GET', `/api/v1/pos-tips`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posReportsTipPool(
  client: ApiClient,
  query: POSTipPoolQuery,
): Promise<POSTipPoolDTO> {
  return client.request<POSTipPoolDTO>('GET', `/api/v1/reports/pos-tip-pool`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posCashSessionsList(
  client: ApiClient,
  query: ListPOSCashSessionsQuery,
): Promise<PagePOSCashSessionDTO> {
  return client.request<PagePOSCashSessionDTO>('GET', `/api/v1/pos-cash-sessions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posCashSessionsOpen(
  client: ApiClient,
  body: OpenPOSCashSessionRequest,
  idempotencyKey?: string,
): Promise<POSCashSessionDTO> {
  return client.request<POSCashSessionDTO>('POST', `/api/v1/pos-cash-sessions`, {
    body,
    idempotencyKey,
  });
}

export async function posCashSessionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSCashSessionDetailDTO> {
  return client.request<POSCashSessionDetailDTO>('GET', `/api/v1/pos-cash-sessions/${encodeURIComponent(params.id)}`, {
  });
}

export async function posCashSessionsMove(
  client: ApiClient,
  params: { id: string },
  body: RecordPOSCashMovementRequest,
): Promise<POSCashSessionDetailDTO> {
  return client.request<POSCashSessionDetailDTO>('POST', `/api/v1/pos-cash-sessions/${encodeURIComponent(params.id)}/movements`, {
    body,
  });
}

export async function posCashCountsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitPOSCashCountRequest,
): Promise<POSCashSessionDetailDTO> {
  return client.request<POSCashSessionDetailDTO>('POST', `/api/v1/pos-cash-sessions/${encodeURIComponent(params.id)}/count`, {
    body,
  });
}

export async function posCashSessionsClose(
  client: ApiClient,
  params: { id: string },
  body: ClosePOSCashSessionRequest,
): Promise<POSCashSessionDTO> {
  return client.request<POSCashSessionDTO>('POST', `/api/v1/pos-cash-sessions/${encodeURIComponent(params.id)}/close`, {
    body,
  });
}

export async function posDepositsList(
  client: ApiClient,
  query: ListPOSDepositsQuery,
): Promise<PagePOSDepositDTO> {
  return client.request<PagePOSDepositDTO>('GET', `/api/v1/pos-deposits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posDepositsCreate(
  client: ApiClient,
  body: CreatePOSDepositRequest,
  idempotencyKey?: string,
): Promise<POSDepositDTO> {
  return client.request<POSDepositDTO>('POST', `/api/v1/pos-deposits`, {
    body,
    idempotencyKey,
  });
}

export async function posDepositsBank(
  client: ApiClient,
  params: { id: string },
  body: BankPOSDepositRequest,
): Promise<POSDepositDTO> {
  return client.request<POSDepositDTO>('POST', `/api/v1/pos-deposits/${encodeURIComponent(params.id)}/bank`, {
    body,
  });
}

export async function posDayClosesList(
  client: ApiClient,
  query: ListPOSDayClosesQuery,
): Promise<PagePOSDayCloseDTO> {
  return client.request<PagePOSDayCloseDTO>('GET', `/api/v1/pos-day-closes`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posDayClosesPreview(
  client: ApiClient,
  body: PreviewPOSDayCloseRequest,
): Promise<POSDayCloseDetailDTO> {
  return client.request<POSDayCloseDetailDTO>('POST', `/api/v1/pos-day-closes/preview`, {
    body,
  });
}

export async function posDayClosesPost(
  client: ApiClient,
  body: PostPOSDayCloseRequest,
  idempotencyKey?: string,
): Promise<POSDayCloseDetailDTO> {
  return client.request<POSDayCloseDetailDTO>('POST', `/api/v1/pos-day-closes`, {
    body,
    idempotencyKey,
  });
}

export async function posDayClosesGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSDayCloseDetailDTO> {
  return client.request<POSDayCloseDetailDTO>('GET', `/api/v1/pos-day-closes/${encodeURIComponent(params.id)}`, {
  });
}

export async function posDayReconciliationGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSDayReconciliationDTO> {
  return client.request<POSDayReconciliationDTO>('GET', `/api/v1/pos-day-closes/${encodeURIComponent(params.id)}/reconciliation`, {
  });
}

export async function posDayClosesExportPack(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/pos-day-closes/${encodeURIComponent(params.id)}/pack`, {
  });
}

export async function posReportsDayBoard(
  client: ApiClient,
  query: POSDayBoardQuery,
): Promise<POSDayBoardDTO> {
  return client.request<POSDayBoardDTO>('GET', `/api/v1/reports/pos-day-board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posFloorAreasList(
  client: ApiClient,
  query: ListPOSFloorAreasQuery,
): Promise<PagePOSFloorAreaDTO> {
  return client.request<PagePOSFloorAreaDTO>('GET', `/api/v1/pos-floor-areas`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posFloorAreasCreate(
  client: ApiClient,
  body: CreatePOSFloorAreaRequest,
  idempotencyKey?: string,
): Promise<POSFloorAreaDTO> {
  return client.request<POSFloorAreaDTO>('POST', `/api/v1/pos-floor-areas`, {
    body,
    idempotencyKey,
  });
}

export async function posTablesSet(
  client: ApiClient,
  params: { id: string },
  body: SetPOSTablesRequest,
  idempotencyKey?: string,
): Promise<POSTableDTO[]> {
  return client.request<POSTableDTO[]>('PUT', `/api/v1/pos-floor-areas/${encodeURIComponent(params.id)}/tables`, {
    body,
    idempotencyKey,
  });
}

export async function posTablesList(
  client: ApiClient,
  query: ListPOSTablesQuery,
): Promise<PagePOSTableDTO> {
  return client.request<PagePOSTableDTO>('GET', `/api/v1/pos-tables`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posTablesSetStatus(
  client: ApiClient,
  params: { id: string },
  body: SetPOSTableStatusRequest,
): Promise<POSTableDTO> {
  return client.request<POSTableDTO>('PATCH', `/api/v1/pos-tables/${encodeURIComponent(params.id)}/status`, {
    body,
  });
}

export async function posStationsList(
  client: ApiClient,
  query: ListPOSStationsQuery,
): Promise<PagePOSStationDTO> {
  return client.request<PagePOSStationDTO>('GET', `/api/v1/pos-stations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posStationsCreate(
  client: ApiClient,
  body: CreatePOSStationRequest,
  idempotencyKey?: string,
): Promise<POSStationDTO> {
  return client.request<POSStationDTO>('POST', `/api/v1/pos-stations`, {
    body,
    idempotencyKey,
  });
}

export async function posChecksList(
  client: ApiClient,
  query: ListPOSChecksQuery,
): Promise<PagePOSCheckDTO> {
  return client.request<PagePOSCheckDTO>('GET', `/api/v1/pos-checks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posChecksOpen(
  client: ApiClient,
  body: OpenPOSCheckRequest,
  idempotencyKey?: string,
): Promise<POSCheckDetailDTO> {
  return client.request<POSCheckDetailDTO>('POST', `/api/v1/pos-checks`, {
    body,
    idempotencyKey,
  });
}

export async function posChecksGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSCheckDetailDTO> {
  return client.request<POSCheckDetailDTO>('GET', `/api/v1/pos-checks/${encodeURIComponent(params.id)}`, {
  });
}

export async function posChecksAddLine(
  client: ApiClient,
  params: { id: string },
  body: AddPOSCheckLineRequest,
): Promise<POSCheckDetailDTO> {
  return client.request<POSCheckDetailDTO>('POST', `/api/v1/pos-checks/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function posChecksSetAllergens(
  client: ApiClient,
  params: { id: string },
  body: SetPOSCheckAllergensRequest,
  idempotencyKey?: string,
): Promise<POSCheckDetailDTO> {
  return client.request<POSCheckDetailDTO>('PUT', `/api/v1/pos-checks/${encodeURIComponent(params.id)}/allergens`, {
    body,
    idempotencyKey,
  });
}

export async function posChecksFire(
  client: ApiClient,
  params: { id: string },
  body: FirePOSCourseRequest,
): Promise<POSCheckDetailDTO> {
  return client.request<POSCheckDetailDTO>('POST', `/api/v1/pos-checks/${encodeURIComponent(params.id)}/fire`, {
    body,
  });
}

export async function posCheckTransfersCreate(
  client: ApiClient,
  params: { id: string },
  body: TransferPOSCheckRequest,
): Promise<POSCheckDetailDTO> {
  return client.request<POSCheckDetailDTO>('POST', `/api/v1/pos-checks/${encodeURIComponent(params.id)}/transfer`, {
    body,
  });
}

export async function posCheckTransfersSplit(
  client: ApiClient,
  params: { id: string },
  body: SplitPOSCheckRequest,
): Promise<POSCheckSplitResultDTO> {
  return client.request<POSCheckSplitResultDTO>('POST', `/api/v1/pos-checks/${encodeURIComponent(params.id)}/split`, {
    body,
  });
}

export async function posChecksSettle(
  client: ApiClient,
  params: { id: string },
  body: SettlePOSCheckRequest,
  idempotencyKey?: string,
): Promise<POSSaleDetailDTO[]> {
  return client.request<POSSaleDetailDTO[]>('POST', `/api/v1/pos-checks/${encodeURIComponent(params.id)}/settle`, {
    body,
    idempotencyKey,
  });
}

export async function posKdsTicketsList(
  client: ApiClient,
  query: ListKDSTicketsQuery,
): Promise<PagePOSKDSTicketDTO> {
  return client.request<PagePOSKDSTicketDTO>('GET', `/api/v1/kds-tickets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posKdsTicketsStart(
  client: ApiClient,
  params: { id: string },
): Promise<POSKDSTicketDTO> {
  return client.request<POSKDSTicketDTO>('POST', `/api/v1/kds-tickets/${encodeURIComponent(params.id)}/start`, {
  });
}

export async function posKdsTicketsBump(
  client: ApiClient,
  params: { id: string },
): Promise<POSKDSTicketDTO> {
  return client.request<POSKDSTicketDTO>('POST', `/api/v1/kds-tickets/${encodeURIComponent(params.id)}/bump`, {
  });
}

export async function posKdsTicketsRecall(
  client: ApiClient,
  params: { id: string },
): Promise<POSKDSTicketDTO> {
  return client.request<POSKDSTicketDTO>('POST', `/api/v1/kds-tickets/${encodeURIComponent(params.id)}/recall`, {
  });
}

export async function posSettlementSettle(
  client: ApiClient,
  params: { id: string },
  body: SettlePOSSaleRequest,
  idempotencyKey?: string,
): Promise<POSSaleSettlementDTO> {
  return client.request<POSSaleSettlementDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/settle`, {
    body,
    idempotencyKey,
  });
}

export async function posSettlementVoid(
  client: ApiClient,
  params: { id: string },
  body: VoidPOSSaleRequest,
  idempotencyKey?: string,
): Promise<POSSaleSettlementDTO> {
  return client.request<POSSaleSettlementDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/void`, {
    body,
    idempotencyKey,
  });
}

export async function posRefundsCreate(
  client: ApiClient,
  body: CreatePOSRefundRequest,
  idempotencyKey?: string,
): Promise<POSSaleSettlementDTO> {
  return client.request<POSSaleSettlementDTO>('POST', `/api/v1/pos-refunds`, {
    body,
    idempotencyKey,
  });
}

export async function posDepletionGet(
  client: ApiClient,
  params: { id: string },
): Promise<POSSaleDepletionDTO> {
  return client.request<POSSaleDepletionDTO>('GET', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/depletion`, {
  });
}

export async function posDepletionPreview(
  client: ApiClient,
  params: { id: string },
): Promise<POSSaleDepletionDTO> {
  return client.request<POSSaleDepletionDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/depletion/preview`, {
  });
}

export async function posReceiptsList(
  client: ApiClient,
  query: ListPOSFiscalReceiptsQuery,
): Promise<PagePOSFiscalReceiptDTO> {
  return client.request<PagePOSFiscalReceiptDTO>('GET', `/api/v1/pos-fiscal-receipts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posReportsFiscalGaps(
  client: ApiClient,
  query: POSFiscalGapQuery,
): Promise<POSFiscalGapDTO> {
  return client.request<POSFiscalGapDTO>('GET', `/api/v1/reports/pos-fiscal-gaps`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posSerialsCapture(
  client: ApiClient,
  params: { id: string; lineId: string },
  body: CapturePOSSerialsRequest,
  idempotencyKey?: string,
): Promise<POSSaleLineDTO> {
  return client.request<POSSaleLineDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/lines/${encodeURIComponent(params.lineId)}/serials`, {
    body,
    idempotencyKey,
  });
}

export async function posSerialsList(
  client: ApiClient,
  query: ListPOSSerialSalesQuery,
): Promise<PagePOSSerialSaleDTO> {
  return client.request<PagePOSSerialSaleDTO>('GET', `/api/v1/pos-serial-sales`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posAgeChecksRecord(
  client: ApiClient,
  params: { id: string },
  body: RecordPOSAgeCheckRequest,
  idempotencyKey?: string,
): Promise<POSAgeVerificationDTO> {
  return client.request<POSAgeVerificationDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/age-check`, {
    body,
    idempotencyKey,
  });
}

export async function posEmployeePurchasesList(
  client: ApiClient,
  query: ListPOSEmployeePurchasesQuery,
): Promise<PagePOSEmployeePurchaseDTO> {
  return client.request<PagePOSEmployeePurchaseDTO>('GET', `/api/v1/pos-employee-purchases`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posEmployeePurchasesCreate(
  client: ApiClient,
  params: { id: string },
  body: CreatePOSEmployeePurchaseRequest,
  idempotencyKey?: string,
): Promise<POSEmployeePurchaseDTO> {
  return client.request<POSEmployeePurchaseDTO>('POST', `/api/v1/pos-sales/${encodeURIComponent(params.id)}/employee-purchase`, {
    body,
    idempotencyKey,
  });
}

export async function posCustomerDepositsList(
  client: ApiClient,
  query: ListPOSCustomerDepositsQuery,
): Promise<PagePOSCustomerDepositDTO> {
  return client.request<PagePOSCustomerDepositDTO>('GET', `/api/v1/pos-customer-deposits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posCustomerDepositsTake(
  client: ApiClient,
  body: TakePOSCustomerDepositRequest,
  idempotencyKey?: string,
): Promise<POSCustomerDepositDTO> {
  return client.request<POSCustomerDepositDTO>('POST', `/api/v1/pos-customer-deposits`, {
    body,
    idempotencyKey,
  });
}

export async function posCustomerDepositsApply(
  client: ApiClient,
  params: { id: string },
  body: ApplyPOSDepositRequest,
  idempotencyKey?: string,
): Promise<POSCustomerDepositDTO> {
  return client.request<POSCustomerDepositDTO>('POST', `/api/v1/pos-customer-deposits/${encodeURIComponent(params.id)}/apply`, {
    body,
    idempotencyKey,
  });
}

export async function posCustomerDepositsRefund(
  client: ApiClient,
  params: { id: string },
  body: RefundPOSDepositRequest,
  idempotencyKey?: string,
): Promise<POSCustomerDepositDTO> {
  return client.request<POSCustomerDepositDTO>('POST', `/api/v1/pos-customer-deposits/${encodeURIComponent(params.id)}/refund`, {
    body,
    idempotencyKey,
  });
}

export async function posOverridesAuthorise(
  client: ApiClient,
  body: AuthorisePOSOverrideRequest,
  idempotencyKey?: string,
): Promise<POSOverrideGrantDTO> {
  return client.request<POSOverrideGrantDTO>('POST', `/api/v1/pos-overrides/authorise`, {
    body,
    idempotencyKey,
  });
}

export async function posOperatorStatsList(
  client: ApiClient,
  query: ListPOSOperatorStatsQuery,
): Promise<PagePOSOperatorDayStatsDTO> {
  return client.request<PagePOSOperatorDayStatsDTO>('GET', `/api/v1/pos-operator-stats`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posAnomaliesList(
  client: ApiClient,
  query: ListPOSAnomaliesQuery,
): Promise<PagePOSAnomalyExceptionDTO> {
  return client.request<PagePOSAnomalyExceptionDTO>('GET', `/api/v1/pos-anomalies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posAnomaliesResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolvePOSAnomalyRequest,
): Promise<POSAnomalyExceptionDTO> {
  return client.request<POSAnomalyExceptionDTO>('POST', `/api/v1/pos-anomalies/${encodeURIComponent(params.id)}/resolve`, {
    body,
  });
}

export async function posReportsSalesSummary(
  client: ApiClient,
  query: POSSalesSummaryQuery,
): Promise<POSSalesSummaryDTO> {
  return client.request<POSSalesSummaryDTO>('GET', `/api/v1/reports/pos-sales-summary`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posReportsVoidDiscount(
  client: ApiClient,
  query: POSVoidDiscountQuery,
): Promise<POSVoidDiscountDTO> {
  return client.request<POSVoidDiscountDTO>('GET', `/api/v1/reports/pos-void-discount`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posReportsSupersededPricing(
  client: ApiClient,
  query: POSSupersededPricingQuery,
): Promise<POSSupersededPricingDTO> {
  return client.request<POSSupersededPricingDTO>('GET', `/api/v1/reports/pos-superseded-pricing`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function posReportsExportCsv(
  client: ApiClient,
  query: POSExportQuery,
): Promise<void> {
  return client.request<void>('GET', `/api/v1/reports/pos-export`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceDevicesList(
  client: ApiClient,
  query: ListDevicesQuery,
): Promise<PageDeviceDTO> {
  return client.request<PageDeviceDTO>('GET', `/api/v1/devices`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceDevicesCreate(
  client: ApiClient,
  body: CreateDeviceRequest,
  idempotencyKey?: string,
): Promise<DeviceDTO> {
  return client.request<DeviceDTO>('POST', `/api/v1/devices`, {
    body,
    idempotencyKey,
  });
}

export async function deviceDevicesGet(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceDetailDTO> {
  return client.request<DeviceDetailDTO>('GET', `/api/v1/devices/${encodeURIComponent(params.id)}`, {
  });
}

export async function deviceEnrolmentsMint(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceEnrolmentCodeResponse> {
  return client.request<DeviceEnrolmentCodeResponse>('POST', `/api/v1/devices/${encodeURIComponent(params.id)}/enrolment-code`, {
  });
}

export async function deviceDevicesSuspend(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceDTO> {
  return client.request<DeviceDTO>('POST', `/api/v1/devices/${encodeURIComponent(params.id)}/suspend`, {
  });
}

export async function deviceDevicesRevoke(
  client: ApiClient,
  params: { id: string },
  body: RevokeDeviceRequest,
): Promise<DeviceDTO> {
  return client.request<DeviceDTO>('POST', `/api/v1/devices/${encodeURIComponent(params.id)}/revoke`, {
    body,
  });
}

export async function deviceContentSetsList(
  client: ApiClient,
  query: ListContentSetsQuery,
): Promise<PageDeviceContentSetDTO> {
  return client.request<PageDeviceContentSetDTO>('GET', `/api/v1/content-sets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceContentSetsCreate(
  client: ApiClient,
  body: DeviceContentSetDTO,
  idempotencyKey?: string,
): Promise<DeviceContentSetDTO> {
  return client.request<DeviceContentSetDTO>('POST', `/api/v1/content-sets`, {
    body,
    idempotencyKey,
  });
}

export async function deviceContentVersionsList(
  client: ApiClient,
  params: { id: string },
  query: CursorQuery,
): Promise<PageDeviceContentSetVersionDTO> {
  return client.request<PageDeviceContentSetVersionDTO>('GET', `/api/v1/content-sets/${encodeURIComponent(params.id)}/versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceContentVersionsCreate(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<DeviceContentSetVersionDTO> {
  return client.request<DeviceContentSetVersionDTO>('POST', `/api/v1/content-sets/${encodeURIComponent(params.id)}/versions`, {
    idempotencyKey,
  });
}

export async function deviceContentVersionsSetParts(
  client: ApiClient,
  params: { id: string },
  body: SetContentPartsRequest,
): Promise<DeviceContentSetVersionDTO> {
  return client.request<DeviceContentSetVersionDTO>('PUT', `/api/v1/content-set-versions/${encodeURIComponent(params.id)}/parts`, {
    body,
  });
}

export async function deviceContentVersionsCheck(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceContentCheckDTO> {
  return client.request<DeviceContentCheckDTO>('POST', `/api/v1/content-set-versions/${encodeURIComponent(params.id)}/check`, {
  });
}

export async function deviceContentVersionsPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishContentRequest,
  idempotencyKey?: string,
): Promise<DeviceContentSetVersionDTO> {
  return client.request<DeviceContentSetVersionDTO>('POST', `/api/v1/content-set-versions/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function deviceContentVersionsWithdraw(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceContentSetVersionDTO> {
  return client.request<DeviceContentSetVersionDTO>('POST', `/api/v1/content-set-versions/${encodeURIComponent(params.id)}/withdraw`, {
  });
}

export async function deviceContentSetsAssignments(
  client: ApiClient,
  query: ListContentAssignmentsQuery,
): Promise<PageDeviceContentAssignmentDTO> {
  return client.request<PageDeviceContentAssignmentDTO>('GET', `/api/v1/content-assignments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function devicePoliciesList(
  client: ApiClient,
  query: ListOfflinePoliciesQuery,
): Promise<PageDeviceOfflinePolicyDTO> {
  return client.request<PageDeviceOfflinePolicyDTO>('GET', `/api/v1/device-offline-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function devicePoliciesSet(
  client: ApiClient,
  body: SetOfflinePoliciesRequest,
): Promise<DeviceOfflinePolicyDTO[]> {
  return client.request<DeviceOfflinePolicyDTO[]>('PUT', `/api/v1/device-offline-policies`, {
    body,
  });
}

export async function deviceRostersPublish(
  client: ApiClient,
  body: DevicePublishRosterRequest,
  idempotencyKey?: string,
): Promise<DeviceOperatorRosterDTO> {
  return client.request<DeviceOperatorRosterDTO>('POST', `/api/v1/device-operator-rosters`, {
    body,
    idempotencyKey,
  });
}

export async function devicePinsSet(
  client: ApiClient,
  body: SetOperatorPinRequest,
): Promise<void> {
  return client.request<void>('PUT', `/api/v1/pos-operator-pin`, {
    body,
  });
}

export async function devicePinsReset(
  client: ApiClient,
  params: { userId: string },
): Promise<void> {
  return client.request<void>('POST', `/api/v1/pos-operator-pin/${encodeURIComponent(params.userId)}/reset`, {
  });
}

export async function deviceSyncBatchesList(
  client: ApiClient,
  query: ListSyncBatchesQuery,
): Promise<PageDeviceSyncBatchDTO> {
  return client.request<PageDeviceSyncBatchDTO>('GET', `/api/v1/device-sync-batches`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceSyncBatchesGet(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceSyncBatchDetailDTO> {
  return client.request<DeviceSyncBatchDetailDTO>('GET', `/api/v1/device-sync-batches/${encodeURIComponent(params.id)}`, {
  });
}

export async function deviceConflictsList(
  client: ApiClient,
  query: ListSyncConflictsQuery,
): Promise<PageDeviceSyncConflictDTO> {
  return client.request<PageDeviceSyncConflictDTO>('GET', `/api/v1/sync-conflicts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceConflictsResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolveSyncConflictRequest,
): Promise<DeviceSyncConflictDTO> {
  return client.request<DeviceSyncConflictDTO>('POST', `/api/v1/sync-conflicts/${encodeURIComponent(params.id)}/resolve`, {
    body,
  });
}

export async function deviceRatificationsList(
  client: ApiClient,
  query: ListRatificationsQuery,
): Promise<PageDeviceRatificationDTO> {
  return client.request<PageDeviceRatificationDTO>('GET', `/api/v1/device-ratifications`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function deviceRatificationsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideRatificationRequest,
): Promise<DeviceRatificationDTO> {
  return client.request<DeviceRatificationDTO>('POST', `/api/v1/device-ratifications/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function deviceRealmEnrol(
  client: ApiClient,
  body: DeviceEnrolRequest,
): Promise<DeviceEnrolResponse> {
  return client.request<DeviceEnrolResponse>('POST', `/device/api/v1/enrol`, {
    body,
  });
}

export async function deviceRealmToken(
  client: ApiClient,
  body: DeviceTokenRequest,
): Promise<DeviceTokenResponse> {
  return client.request<DeviceTokenResponse>('POST', `/device/api/v1/token`, {
    body,
  });
}

export async function deviceRealmHeartbeat(
  client: ApiClient,
  body: DeviceHeartbeatRequest,
): Promise<DeviceHeartbeatResponse> {
  return client.request<DeviceHeartbeatResponse>('POST', `/device/api/v1/heartbeat`, {
    body,
  });
}

export async function deviceRealmContent(
  client: ApiClient,
): Promise<DeviceContentManifestDTO> {
  return client.request<DeviceContentManifestDTO>('GET', `/device/api/v1/content`, {
  });
}

export async function deviceRealmContentPart(
  client: ApiClient,
  params: { id: string },
): Promise<DeviceContentPartResponse> {
  return client.request<DeviceContentPartResponse>('GET', `/device/api/v1/content/parts/${encodeURIComponent(params.id)}`, {
  });
}

export async function deviceRealmRoster(
  client: ApiClient,
): Promise<DeviceOperatorRosterDTO> {
  return client.request<DeviceOperatorRosterDTO>('GET', `/device/api/v1/roster`, {
  });
}

export async function deviceRealmSync(
  client: ApiClient,
  body: DeviceSyncRequest,
  idempotencyKey?: string,
): Promise<DeviceSyncResultDTO> {
  return client.request<DeviceSyncResultDTO>('POST', `/device/api/v1/sync`, {
    body,
    idempotencyKey,
  });
}

export async function deviceRealmSyncResult(
  client: ApiClient,
  params: { batchUuid: string },
): Promise<DeviceSyncResultDTO> {
  return client.request<DeviceSyncResultDTO>('GET', `/device/api/v1/sync/results/${encodeURIComponent(params.batchUuid)}`, {
  });
}

export async function deviceRealmMenu(
  client: ApiClient,
): Promise<DeviceMenuResponse> {
  return client.request<DeviceMenuResponse>('GET', `/device/api/v1/menu`, {
  });
}

export async function deviceRealmPrices(
  client: ApiClient,
): Promise<DevicePriceManifestResponse> {
  return client.request<DevicePriceManifestResponse>('GET', `/device/api/v1/prices`, {
  });
}

export async function deviceRealmTelemetry(
  client: ApiClient,
  body: DeviceTelemetryRequest,
): Promise<void> {
  return client.request<void>('POST', `/device/api/v1/telemetry`, {
    body,
  });
}

export async function pmsPropertiesList(
  client: ApiClient,
  query: ListPMSPropertiesQuery,
): Promise<PagePMSPropertyDTO> {
  return client.request<PagePMSPropertyDTO>('GET', `/api/v1/pms-properties`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsPropertiesCreate(
  client: ApiClient,
  body: CreatePMSPropertyRequest,
  idempotencyKey?: string,
): Promise<PMSPropertyDTO> {
  return client.request<PMSPropertyDTO>('POST', `/api/v1/pms-properties`, {
    body,
    idempotencyKey,
  });
}

export async function pmsPropertiesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSPropertyDTO> {
  return client.request<PMSPropertyDTO>('GET', `/api/v1/pms-properties/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsPropertiesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSPropertyRequest,
): Promise<PMSPropertyDTO> {
  return client.request<PMSPropertyDTO>('PATCH', `/api/v1/pms-properties/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsRoomTypesList(
  client: ApiClient,
  query: ListPMSRoomTypesQuery,
): Promise<PagePMSRoomTypeDTO> {
  return client.request<PagePMSRoomTypeDTO>('GET', `/api/v1/pms-room-types`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRoomTypesCreate(
  client: ApiClient,
  body: CreatePMSRoomTypeRequest,
  idempotencyKey?: string,
): Promise<PMSRoomTypeDTO> {
  return client.request<PMSRoomTypeDTO>('POST', `/api/v1/pms-room-types`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomTypesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSRoomTypeDTO> {
  return client.request<PMSRoomTypeDTO>('GET', `/api/v1/pms-room-types/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsRoomTypesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSRoomTypeRequest,
): Promise<PMSRoomTypeDTO> {
  return client.request<PMSRoomTypeDTO>('PATCH', `/api/v1/pms-room-types/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsRoomsList(
  client: ApiClient,
  query: ListPMSRoomsQuery,
): Promise<PagePMSRoomDTO> {
  return client.request<PagePMSRoomDTO>('GET', `/api/v1/pms-rooms`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRoomsCreate(
  client: ApiClient,
  body: CreatePMSRoomRequest,
  idempotencyKey?: string,
): Promise<PMSRoomDTO> {
  return client.request<PMSRoomDTO>('POST', `/api/v1/pms-rooms`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSRoomDTO> {
  return client.request<PMSRoomDTO>('GET', `/api/v1/pms-rooms/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsRoomsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSRoomRequest,
): Promise<PMSRoomDTO> {
  return client.request<PMSRoomDTO>('PATCH', `/api/v1/pms-rooms/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsRoomsConnect(
  client: ApiClient,
  params: { id: string },
  body: ConnectRoomRequest,
  idempotencyKey?: string,
): Promise<PMSRoomConnectionDTO> {
  return client.request<PMSRoomConnectionDTO>('POST', `/api/v1/pms-rooms/${encodeURIComponent(params.id)}/connections`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomsDisconnect(
  client: ApiClient,
  params: { id: string },
  body: ConnectRoomRequest,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/pms-rooms/${encodeURIComponent(params.id)}/connections`, {
    body,
  });
}

export async function pmsOutOfOrderList(
  client: ApiClient,
  query: ListPMSOutOfOrderQuery,
): Promise<PagePMSOutOfOrderDTO> {
  return client.request<PagePMSOutOfOrderDTO>('GET', `/api/v1/pms-out-of-order`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsOutOfOrderCreate(
  client: ApiClient,
  body: CreatePMSOutOfOrderRequest,
  idempotencyKey?: string,
): Promise<PMSOutOfOrderDTO> {
  return client.request<PMSOutOfOrderDTO>('POST', `/api/v1/pms-out-of-order`, {
    body,
    idempotencyKey,
  });
}

export async function pmsOutOfOrderRelease(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<PMSOutOfOrderDTO> {
  return client.request<PMSOutOfOrderDTO>('POST', `/api/v1/pms-out-of-order/${encodeURIComponent(params.id)}/release`, {
    idempotencyKey,
  });
}

export async function pmsRevenueCentersList(
  client: ApiClient,
  query: ListPMSRevenueCentersQuery,
): Promise<PagePMSRevenueCenterDTO> {
  return client.request<PagePMSRevenueCenterDTO>('GET', `/api/v1/pms-revenue-centers`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRevenueCentersCreate(
  client: ApiClient,
  body: CreatePMSRevenueCenterRequest,
  idempotencyKey?: string,
): Promise<PMSRevenueCenterDTO> {
  return client.request<PMSRevenueCenterDTO>('POST', `/api/v1/pms-revenue-centers`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRevenueCentersUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSRevenueCenterRequest,
): Promise<PMSRevenueCenterDTO> {
  return client.request<PMSRevenueCenterDTO>('PATCH', `/api/v1/pms-revenue-centers/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<PMSSettingsDTO> {
  return client.request<PMSSettingsDTO>('GET', `/api/v1/pms-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function pmsSettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdatePMSSettingsRequest,
  idempotencyKey?: string,
): Promise<PMSSettingsDTO> {
  return client.request<PMSSettingsDTO>('PUT', `/api/v1/pms-settings/${encodeURIComponent(params.companyId)}`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGuestsList(
  client: ApiClient,
  query: ListPMSGuestsQuery,
): Promise<PagePMSGuestDTO> {
  return client.request<PagePMSGuestDTO>('GET', `/api/v1/pms-guests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsGuestsCreate(
  client: ApiClient,
  body: CreatePMSGuestRequest,
  idempotencyKey?: string,
): Promise<PMSGuestDTO> {
  return client.request<PMSGuestDTO>('POST', `/api/v1/pms-guests`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGuestsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSGuestDetailDTO> {
  return client.request<PMSGuestDetailDTO>('GET', `/api/v1/pms-guests/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsGuestsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSGuestRequest,
): Promise<PMSGuestDTO> {
  return client.request<PMSGuestDTO>('PATCH', `/api/v1/pms-guests/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsGuestsRevealDocument(
  client: ApiClient,
  params: { id: string },
  body: RevealPMSGuestDocumentRequest,
): Promise<PMSGuestDocumentRevealDTO> {
  return client.request<PMSGuestDocumentRevealDTO>('POST', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/document/reveal`, {
    body,
  });
}

export async function pmsGuestsExtractDocument(
  client: ApiClient,
  params: { id: string },
  file: File | Blob,
): Promise<PMSGuestDocumentExtractionDTO> {
  const form = new FormData();
  form.append('file', file);
  return client.request<PMSGuestDocumentExtractionDTO>('POST', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/document/extract`, {
    body: form,
  });
}

export async function pmsGuestPreferencesList(
  client: ApiClient,
  params: { id: string },
): Promise<PMSGuestPreferenceDTO[]> {
  return client.request<PMSGuestPreferenceDTO[]>('GET', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/preferences`, {
  });
}

export async function pmsGuestPreferencesSet(
  client: ApiClient,
  params: { id: string },
  body: SetPMSGuestPreferenceRequest,
  idempotencyKey?: string,
): Promise<PMSGuestPreferenceDTO> {
  return client.request<PMSGuestPreferenceDTO>('POST', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/preferences`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGuestPreferencesDelete(
  client: ApiClient,
  params: { id: string },
  body: DeletePMSGuestPreferenceRequest,
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/preferences`, {
    body,
  });
}

export async function pmsRestrictedListAdd(
  client: ApiClient,
  params: { id: string },
  body: RestrictPMSGuestRequest,
): Promise<PMSGuestDTO> {
  return client.request<PMSGuestDTO>('POST', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/restriction`, {
    body,
  });
}

export async function pmsRestrictedListRemove(
  client: ApiClient,
  params: { id: string },
  body: UnrestrictPMSGuestRequest,
): Promise<PMSGuestDTO> {
  return client.request<PMSGuestDTO>('DELETE', `/api/v1/pms-guests/${encodeURIComponent(params.id)}/restriction`, {
    body,
  });
}

export async function pmsGuestMessagesList(
  client: ApiClient,
  query: ListPMSGuestMessagesQuery,
): Promise<PagePMSGuestMessageDTO> {
  return client.request<PagePMSGuestMessageDTO>('GET', `/api/v1/pms-guest-messages`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsGuestMessagesCreate(
  client: ApiClient,
  body: CreatePMSGuestMessageRequest,
  idempotencyKey?: string,
): Promise<PMSGuestMessageDTO> {
  return client.request<PMSGuestMessageDTO>('POST', `/api/v1/pms-guest-messages`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGuestMessagesMarkSent(
  client: ApiClient,
  params: { id: string },
): Promise<PMSGuestMessageDTO> {
  return client.request<PMSGuestMessageDTO>('POST', `/api/v1/pms-guest-messages/${encodeURIComponent(params.id)}/sent`, {
  });
}

export async function pmsRatePlansList(
  client: ApiClient,
  query: ListPMSRatePlansQuery,
): Promise<PMSRatePlanDTO[]> {
  return client.request<PMSRatePlanDTO[]>('GET', `/api/v1/pms-rate-plans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRatePlansCreate(
  client: ApiClient,
  body: CreatePMSRatePlanRequest,
  idempotencyKey?: string,
): Promise<PMSRatePlanDTO> {
  return client.request<PMSRatePlanDTO>('POST', `/api/v1/pms-rate-plans`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRatePlansGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSRatePlanDTO> {
  return client.request<PMSRatePlanDTO>('GET', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsRatePlansUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSRatePlanRequest,
): Promise<PMSRatePlanDTO> {
  return client.request<PMSRatePlanDTO>('PATCH', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsRatePlansActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivatePMSRatePlanRequest,
  idempotencyKey?: string,
): Promise<PMSRatePlanDTO> {
  return client.request<PMSRatePlanDTO>('POST', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRatePlanRatesList(
  client: ApiClient,
  params: { id: string },
  query: ListPMSRatePlanRatesQuery,
): Promise<PMSRatePlanRateDTO[]> {
  return client.request<PMSRatePlanRateDTO[]>('GET', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}/rates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRatePlanRatesReplace(
  client: ApiClient,
  params: { id: string },
  body: ReplacePMSRatePlanRatesRequest,
  idempotencyKey?: string,
): Promise<PMSRatePlanRateDTO[]> {
  return client.request<PMSRatePlanRateDTO[]>('PUT', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}/rates`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRateDerivationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSRateDerivationDTO> {
  return client.request<PMSRateDerivationDTO>('GET', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}/derivation`, {
  });
}

export async function pmsRateDerivationsPut(
  client: ApiClient,
  params: { id: string },
  body: PutPMSRateDerivationRequest,
  idempotencyKey?: string,
): Promise<PMSRateDerivationDTO> {
  return client.request<PMSRateDerivationDTO>('PUT', `/api/v1/pms-rate-plans/${encodeURIComponent(params.id)}/derivation`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRateRestrictionsList(
  client: ApiClient,
  query: ListPMSRateRestrictionsQuery,
): Promise<PMSRateRestrictionDTO[]> {
  return client.request<PMSRateRestrictionDTO[]>('GET', `/api/v1/pms-rate-restrictions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRateRestrictionsPut(
  client: ApiClient,
  body: PutPMSRateRestrictionsRequest,
  idempotencyKey?: string,
): Promise<PMSRateRestrictionDTO[]> {
  return client.request<PMSRateRestrictionDTO[]>('PUT', `/api/v1/pms-rate-restrictions`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCancellationPoliciesList(
  client: ApiClient,
  query: ListPMSCancellationPoliciesQuery,
): Promise<PagePMSCancellationPolicyDTO> {
  return client.request<PagePMSCancellationPolicyDTO>('GET', `/api/v1/pms-cancellation-policies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsCancellationPoliciesCreate(
  client: ApiClient,
  body: CreatePMSCancellationPolicyRequest,
  idempotencyKey?: string,
): Promise<PMSCancellationPolicyDTO> {
  return client.request<PMSCancellationPolicyDTO>('POST', `/api/v1/pms-cancellation-policies`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCancellationPoliciesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSCancellationPolicyDTO> {
  return client.request<PMSCancellationPolicyDTO>('GET', `/api/v1/pms-cancellation-policies/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsAvailabilityGet(
  client: ApiClient,
  query: ListPMSAvailabilityQuery,
): Promise<PMSAvailabilityCalendarDTO> {
  return client.request<PMSAvailabilityCalendarDTO>('GET', `/api/v1/pms-availability`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsQuotesCreate(
  client: ApiClient,
  body: QuotePMSStayRequest,
): Promise<PMSQuoteDTO> {
  return client.request<PMSQuoteDTO>('POST', `/api/v1/pms-quotes`, {
    body,
  });
}

export async function pmsReservationsList(
  client: ApiClient,
  query: ListPMSReservationsQuery,
): Promise<PagePMSReservationDTO> {
  return client.request<PagePMSReservationDTO>('GET', `/api/v1/pms-reservations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsReservationsCreate(
  client: ApiClient,
  body: CreatePMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSReservationDetailDTO> {
  return client.request<PMSReservationDetailDTO>('POST', `/api/v1/pms-reservations`, {
    body,
    idempotencyKey,
  });
}

export async function pmsReservationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSReservationDetailDTO> {
  return client.request<PMSReservationDetailDTO>('GET', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsReservationsHold(
  client: ApiClient,
  params: { id: string },
  body: HoldPMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSReservationDetailDTO> {
  return client.request<PMSReservationDetailDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/hold`, {
    body,
    idempotencyKey,
  });
}

export async function pmsReservationsConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmPMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSReservationDetailDTO> {
  return client.request<PMSReservationDetailDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function pmsReservationsAmend(
  client: ApiClient,
  params: { id: string },
  body: AmendPMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSAmendmentDTO> {
  return client.request<PMSAmendmentDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/amend`, {
    body,
    idempotencyKey,
  });
}

export async function pmsReservationsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelPMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSReservationDetailDTO> {
  return client.request<PMSReservationDetailDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function pmsWaitlistList(
  client: ApiClient,
  query: ListPMSWaitlistQuery,
): Promise<PagePMSWaitlistEntryDTO> {
  return client.request<PagePMSWaitlistEntryDTO>('GET', `/api/v1/pms-waitlist`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsWaitlistCreate(
  client: ApiClient,
  body: CreatePMSWaitlistEntryRequest,
  idempotencyKey?: string,
): Promise<PMSWaitlistEntryDTO> {
  return client.request<PMSWaitlistEntryDTO>('POST', `/api/v1/pms-waitlist`, {
    body,
    idempotencyKey,
  });
}

export async function pmsWaitlistOffer(
  client: ApiClient,
  params: { id: string },
  body: OfferPMSWaitlistEntryRequest,
  idempotencyKey?: string,
): Promise<PMSWaitlistEntryDTO> {
  return client.request<PMSWaitlistEntryDTO>('POST', `/api/v1/pms-waitlist/${encodeURIComponent(params.id)}/offer`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGroupBlocksList(
  client: ApiClient,
  query: ListPMSGroupBlocksQuery,
): Promise<PagePMSGroupBlockDTO> {
  return client.request<PagePMSGroupBlockDTO>('GET', `/api/v1/pms-group-blocks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsGroupBlocksCreate(
  client: ApiClient,
  body: CreatePMSGroupBlockRequest,
  idempotencyKey?: string,
): Promise<PMSGroupBlockDetailDTO> {
  return client.request<PMSGroupBlockDetailDTO>('POST', `/api/v1/pms-group-blocks`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGroupBlocksGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSGroupBlockDetailDTO> {
  return client.request<PMSGroupBlockDetailDTO>('GET', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsGroupBlocksUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSGroupBlockRequest,
): Promise<PMSGroupBlockDetailDTO> {
  return client.request<PMSGroupBlockDetailDTO>('PATCH', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsGroupBlocksConfirm(
  client: ApiClient,
  params: { id: string },
  body: ConfirmPMSGroupBlockRequest,
  idempotencyKey?: string,
): Promise<PMSGroupBlockDetailDTO> {
  return client.request<PMSGroupBlockDetailDTO>('POST', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}/confirm`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGroupBlocksAllotmentsList(
  client: ApiClient,
  params: { id: string },
): Promise<PMSAllotmentDTO[]> {
  return client.request<PMSAllotmentDTO[]>('GET', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}/allotments`, {
  });
}

export async function pmsGroupBlocksAllotmentsPut(
  client: ApiClient,
  params: { id: string },
  body: PutPMSAllotmentsRequest,
): Promise<PMSAllotmentDTO[]> {
  return client.request<PMSAllotmentDTO[]>('PUT', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}/allotments`, {
    body,
  });
}

export async function pmsGroupBlocksRoomingListImport(
  client: ApiClient,
  params: { id: string },
  body: ImportPMSRoomingListRequest,
  idempotencyKey?: string,
): Promise<PMSRoomingListDTO> {
  return client.request<PMSRoomingListDTO>('POST', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}/rooming-list`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomingListsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSRoomingListDTO> {
  return client.request<PMSRoomingListDTO>('GET', `/api/v1/pms-rooming-lists/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsGroupBlocksCutoff(
  client: ApiClient,
  params: { id: string },
  body: CutoffPMSGroupBlockRequest,
  idempotencyKey?: string,
): Promise<PMSGroupBlockDetailDTO> {
  return client.request<PMSGroupBlockDetailDTO>('POST', `/api/v1/pms-group-blocks/${encodeURIComponent(params.id)}/cutoff`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFoliosList(
  client: ApiClient,
  query: ListPMSFoliosQuery,
): Promise<PagePMSFolioDTO> {
  return client.request<PagePMSFolioDTO>('GET', `/api/v1/pms-folios`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsFoliosCreate(
  client: ApiClient,
  body: CreatePMSFolioRequest,
  idempotencyKey?: string,
): Promise<PMSFolioDetailDTO> {
  return client.request<PMSFolioDetailDTO>('POST', `/api/v1/pms-folios`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFoliosGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSFolioDetailDTO> {
  return client.request<PMSFolioDetailDTO>('GET', `/api/v1/pms-folios/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsFoliosInvoice(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/invoice`, {
  });
}

export async function pmsFolioWindowsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreatePMSFolioWindowRequest,
  idempotencyKey?: string,
): Promise<PMSFolioWindowDTO> {
  return client.request<PMSFolioWindowDTO>('POST', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/windows`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFolioRoutingList(
  client: ApiClient,
  params: { id: string },
): Promise<PMSFolioRoutingRuleDTO[]> {
  return client.request<PMSFolioRoutingRuleDTO[]>('GET', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/routing-rules`, {
  });
}

export async function pmsFolioRoutingSet(
  client: ApiClient,
  params: { id: string },
  body: SetPMSFolioRoutingRulesRequest,
): Promise<PMSFolioRoutingRuleDTO[]> {
  return client.request<PMSFolioRoutingRuleDTO[]>('PUT', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/routing-rules`, {
    body,
  });
}

export async function pmsFolioChargesPost(
  client: ApiClient,
  params: { id: string },
  body: PostPMSFolioChargeRequest,
  idempotencyKey?: string,
): Promise<PMSFolioChargeDTO[]> {
  return client.request<PMSFolioChargeDTO[]>('POST', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/charges`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFolioChargesReverse(
  client: ApiClient,
  params: { id: string },
  body: ReversePMSFolioChargeRequest,
  idempotencyKey?: string,
): Promise<PMSFolioChargeDTO> {
  return client.request<PMSFolioChargeDTO>('POST', `/api/v1/pms-folio-charges/${encodeURIComponent(params.id)}/reverse`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFolioSettlementsSettle(
  client: ApiClient,
  params: { id: string },
  body: SettlePMSFolioRequest,
  idempotencyKey?: string,
): Promise<PMSFolioDetailDTO> {
  return client.request<PMSFolioDetailDTO>('POST', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/settle`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFolioSettlementsCityLedger(
  client: ApiClient,
  params: { id: string },
  body: CityLedgerPMSFolioRequest,
  idempotencyKey?: string,
): Promise<PMSFolioDetailDTO> {
  return client.request<PMSFolioDetailDTO>('POST', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/city-ledger`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFolioSettlementsAllowance(
  client: ApiClient,
  params: { id: string },
  body: AllowancePMSFolioRequest,
  idempotencyKey?: string,
): Promise<PMSFolioDetailDTO> {
  return client.request<PMSFolioDetailDTO>('POST', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/allowance`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFolioSettlementsTransfer(
  client: ApiClient,
  params: { id: string },
  body: TransferPMSFolioRequest,
  idempotencyKey?: string,
): Promise<PMSFolioDetailDTO> {
  return client.request<PMSFolioDetailDTO>('POST', `/api/v1/pms-folios/${encodeURIComponent(params.id)}/transfer`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDepositsList(
  client: ApiClient,
  query: ListPMSDepositsQuery,
): Promise<PagePMSDepositDTO> {
  return client.request<PagePMSDepositDTO>('GET', `/api/v1/pms-deposits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsDepositsCreate(
  client: ApiClient,
  body: CreatePMSDepositRequest,
  idempotencyKey?: string,
): Promise<PMSDepositDTO> {
  return client.request<PMSDepositDTO>('POST', `/api/v1/pms-deposits`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDepositsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSDepositDTO> {
  return client.request<PMSDepositDTO>('GET', `/api/v1/pms-deposits/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsDepositsApply(
  client: ApiClient,
  params: { id: string },
  body: ApplyPMSDepositRequest,
  idempotencyKey?: string,
): Promise<PMSDepositDTO> {
  return client.request<PMSDepositDTO>('POST', `/api/v1/pms-deposits/${encodeURIComponent(params.id)}/apply`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDepositsRefund(
  client: ApiClient,
  params: { id: string },
  body: RefundPMSDepositRequest,
  idempotencyKey?: string,
): Promise<PMSDepositDTO> {
  return client.request<PMSDepositDTO>('POST', `/api/v1/pms-deposits/${encodeURIComponent(params.id)}/refund`, {
    body,
    idempotencyKey,
  });
}

export async function pmsFrontDeskBoardsArrivals(
  client: ApiClient,
  query: ListPMSFrontDeskQuery,
): Promise<PMSFrontDeskBoardDTO> {
  return client.request<PMSFrontDeskBoardDTO>('GET', `/api/v1/pms-front-desk/arrivals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsFrontDeskBoardsDepartures(
  client: ApiClient,
  query: ListPMSFrontDeskQuery,
): Promise<PMSFrontDeskBoardDTO> {
  return client.request<PMSFrontDeskBoardDTO>('GET', `/api/v1/pms-front-desk/departures`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsFrontDeskBoardsInHouse(
  client: ApiClient,
  query: ListPMSFrontDeskQuery,
): Promise<PMSFrontDeskBoardDTO> {
  return client.request<PMSFrontDeskBoardDTO>('GET', `/api/v1/pms-front-desk/in-house`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsCheckInAssignRoom(
  client: ApiClient,
  params: { id: string },
  body: AssignPMSRoomRequest,
  idempotencyKey?: string,
): Promise<PMSReservationDetailDTO> {
  return client.request<PMSReservationDetailDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/assign-room`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCheckInPerform(
  client: ApiClient,
  params: { id: string },
  body: CheckInPMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSCheckInDTO> {
  return client.request<PMSCheckInDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/check-in`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCheckOutPerform(
  client: ApiClient,
  params: { id: string },
  body: CheckOutPMSReservationRequest,
  idempotencyKey?: string,
): Promise<PMSCheckOutDTO> {
  return client.request<PMSCheckOutDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/check-out`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomMovesMove(
  client: ApiClient,
  params: { id: string },
  body: MovePMSRoomRequest,
  idempotencyKey?: string,
): Promise<PMSRoomMoveDTO> {
  return client.request<PMSRoomMoveDTO>('POST', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/room-move`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomMovesList(
  client: ApiClient,
  params: { id: string },
): Promise<PMSRoomMoveDTO[]> {
  return client.request<PMSRoomMoveDTO[]>('GET', `/api/v1/pms-reservations/${encodeURIComponent(params.id)}/room-moves`, {
  });
}

export async function pmsAuthHoldsList(
  client: ApiClient,
  query: ListPMSAuthHoldsQuery,
): Promise<PagePMSAuthHoldDTO> {
  return client.request<PagePMSAuthHoldDTO>('GET', `/api/v1/pms-auth-holds`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsAuthHoldsCreate(
  client: ApiClient,
  body: CreatePMSAuthHoldRequest,
  idempotencyKey?: string,
): Promise<PMSAuthHoldDTO> {
  return client.request<PMSAuthHoldDTO>('POST', `/api/v1/pms-auth-holds`, {
    body,
    idempotencyKey,
  });
}

export async function pmsAuthHoldsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSAuthHoldDTO> {
  return client.request<PMSAuthHoldDTO>('GET', `/api/v1/pms-auth-holds/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsAuthHoldsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleasePMSAuthHoldRequest,
  idempotencyKey?: string,
): Promise<PMSAuthHoldDTO> {
  return client.request<PMSAuthHoldDTO>('POST', `/api/v1/pms-auth-holds/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function pmsKeyIssuesList(
  client: ApiClient,
  query: ListPMSKeyIssuesQuery,
): Promise<PagePMSKeyIssueDTO> {
  return client.request<PagePMSKeyIssueDTO>('GET', `/api/v1/pms-key-issues`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsKeyIssuesCreate(
  client: ApiClient,
  body: CreatePMSKeyIssueRequest,
  idempotencyKey?: string,
): Promise<PMSKeyIssueDTO> {
  return client.request<PMSKeyIssueDTO>('POST', `/api/v1/pms-key-issues`, {
    body,
    idempotencyKey,
  });
}

export async function pmsKeyIssuesCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelPMSKeyIssueRequest,
  idempotencyKey?: string,
): Promise<PMSKeyIssueDTO> {
  return client.request<PMSKeyIssueDTO>('POST', `/api/v1/pms-key-issues/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function pmsHousekeepingBoardsList(
  client: ApiClient,
  query: ListPMSHousekeepingBoardsQuery,
): Promise<PagePMSHousekeepingBoardDTO> {
  return client.request<PagePMSHousekeepingBoardDTO>('GET', `/api/v1/pms-housekeeping-boards`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsHousekeepingBoardsCreate(
  client: ApiClient,
  body: CreatePMSHousekeepingBoardRequest,
  idempotencyKey?: string,
): Promise<PMSHousekeepingBoardDTO> {
  return client.request<PMSHousekeepingBoardDTO>('POST', `/api/v1/pms-housekeeping-boards`, {
    body,
    idempotencyKey,
  });
}

export async function pmsHousekeepingBoardsRebalance(
  client: ApiClient,
  params: { id: string },
  body: RebalancePMSHousekeepingBoardRequest,
  idempotencyKey?: string,
): Promise<PMSHousekeepingBoardRebalanceResultDTO> {
  return client.request<PMSHousekeepingBoardRebalanceResultDTO>('POST', `/api/v1/pms-housekeeping-boards/${encodeURIComponent(params.id)}/rebalance`, {
    body,
    idempotencyKey,
  });
}

export async function pmsHousekeepingBoardsRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleasePMSHousekeepingBoardRequest,
  idempotencyKey?: string,
): Promise<PMSHousekeepingBoardDTO> {
  return client.request<PMSHousekeepingBoardDTO>('POST', `/api/v1/pms-housekeeping-boards/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function pmsHousekeepingTasksList(
  client: ApiClient,
  query: ListPMSHousekeepingTasksQuery,
): Promise<PagePMSHousekeepingTaskDTO> {
  return client.request<PagePMSHousekeepingTaskDTO>('GET', `/api/v1/pms-housekeeping-tasks`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsHousekeepingTasksSetStatus(
  client: ApiClient,
  params: { id: string },
  body: SetPMSHousekeepingTaskStatusRequest,
  idempotencyKey?: string,
): Promise<PMSHousekeepingTaskDTO> {
  return client.request<PMSHousekeepingTaskDTO>('POST', `/api/v1/pms-housekeeping-tasks/${encodeURIComponent(params.id)}/status`, {
    body,
    idempotencyKey,
  });
}

export async function pmsHousekeepingTasksInspect(
  client: ApiClient,
  params: { id: string },
  body: InspectPMSHousekeepingTaskRequest,
  idempotencyKey?: string,
): Promise<PMSHousekeepingTaskDTO> {
  return client.request<PMSHousekeepingTaskDTO>('POST', `/api/v1/pms-housekeeping-tasks/${encodeURIComponent(params.id)}/inspect`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomsSetStatus(
  client: ApiClient,
  params: { id: string },
  body: SetPMSRoomStatusRequest,
  idempotencyKey?: string,
): Promise<PMSRoomStatusResultDTO> {
  return client.request<PMSRoomStatusResultDTO>('POST', `/api/v1/pms-rooms/${encodeURIComponent(params.id)}/status`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomFaultsList(
  client: ApiClient,
  query: ListPMSRoomFaultsQuery,
): Promise<PagePMSRoomFaultDTO> {
  return client.request<PagePMSRoomFaultDTO>('GET', `/api/v1/pms-room-faults`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsRoomFaultsCreate(
  client: ApiClient,
  body: CreatePMSRoomFaultRequest,
  idempotencyKey?: string,
): Promise<PMSRoomFaultDTO> {
  return client.request<PMSRoomFaultDTO>('POST', `/api/v1/pms-room-faults`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomFaultsTriage(
  client: ApiClient,
  params: { id: string },
  body: TriagePMSRoomFaultRequest,
  idempotencyKey?: string,
): Promise<PMSRoomFaultDTO> {
  return client.request<PMSRoomFaultDTO>('POST', `/api/v1/pms-room-faults/${encodeURIComponent(params.id)}/triage`, {
    body,
    idempotencyKey,
  });
}

export async function pmsRoomFaultsResolve(
  client: ApiClient,
  params: { id: string },
  body: ResolvePMSRoomFaultRequest,
  idempotencyKey?: string,
): Promise<PMSRoomFaultDTO> {
  return client.request<PMSRoomFaultDTO>('POST', `/api/v1/pms-room-faults/${encodeURIComponent(params.id)}/resolve`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDeviceBoard(
  client: ApiClient,
  query: PMSDeviceOperatorQuery,
): Promise<PMSDeviceBoardResponse> {
  return client.request<PMSDeviceBoardResponse>('GET', `/device/api/v1/pms/board`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsDeviceRooms(
  client: ApiClient,
): Promise<PMSDeviceRoomsResponse> {
  return client.request<PMSDeviceRoomsResponse>('GET', `/device/api/v1/pms/rooms`, {
  });
}

export async function pmsDeviceFaultReasons(
  client: ApiClient,
): Promise<PMSDeviceFaultReasonsResponse> {
  return client.request<PMSDeviceFaultReasonsResponse>('GET', `/device/api/v1/pms/fault-reasons`, {
  });
}

export async function pmsDeviceSetRoomStatus(
  client: ApiClient,
  body: PMSDeviceSetRoomStatusRequest,
  idempotencyKey?: string,
): Promise<PMSRoomStatusResultDTO> {
  return client.request<PMSRoomStatusResultDTO>('POST', `/device/api/v1/pms/room-status`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDeviceReportFault(
  client: ApiClient,
  body: PMSDeviceReportFaultRequest,
  idempotencyKey?: string,
): Promise<PMSRoomFaultDTO> {
  return client.request<PMSRoomFaultDTO>('POST', `/device/api/v1/pms/faults`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDeviceSummary(
  client: ApiClient,
  query: PMSDeviceOperatorQuery,
): Promise<PMSDeviceSummaryResponse> {
  return client.request<PMSDeviceSummaryResponse>('GET', `/device/api/v1/pms/summary`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsServicesList(
  client: ApiClient,
  query: ListPMSServicesQuery,
): Promise<PagePMSServiceDTO> {
  return client.request<PagePMSServiceDTO>('GET', `/api/v1/pms-services`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsServicesCreate(
  client: ApiClient,
  body: CreatePMSServiceRequest,
  idempotencyKey?: string,
): Promise<PMSServiceDTO> {
  return client.request<PMSServiceDTO>('POST', `/api/v1/pms-services`, {
    body,
    idempotencyKey,
  });
}

export async function pmsServicesGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSServiceDTO> {
  return client.request<PMSServiceDTO>('GET', `/api/v1/pms-services/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsServicesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSServiceRequest,
  idempotencyKey?: string,
): Promise<PMSServiceDTO> {
  return client.request<PMSServiceDTO>('PATCH', `/api/v1/pms-services/${encodeURIComponent(params.id)}`, {
    body,
    idempotencyKey,
  });
}

export async function pmsServiceResourcesList(
  client: ApiClient,
  query: ListPMSServiceResourcesQuery,
): Promise<PagePMSServiceResourceDTO> {
  return client.request<PagePMSServiceResourceDTO>('GET', `/api/v1/pms-service-resources`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsServiceResourcesCreate(
  client: ApiClient,
  body: CreatePMSServiceResourceRequest,
  idempotencyKey?: string,
): Promise<PMSServiceResourceDTO> {
  return client.request<PMSServiceResourceDTO>('POST', `/api/v1/pms-service-resources`, {
    body,
    idempotencyKey,
  });
}

export async function pmsServiceBookingsList(
  client: ApiClient,
  query: ListPMSServiceBookingsQuery,
): Promise<PagePMSServiceBookingDTO> {
  return client.request<PagePMSServiceBookingDTO>('GET', `/api/v1/pms-service-bookings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsServiceBookingsCreate(
  client: ApiClient,
  body: CreatePMSServiceBookingRequest,
  idempotencyKey?: string,
): Promise<PMSServiceBookingDTO> {
  return client.request<PMSServiceBookingDTO>('POST', `/api/v1/pms-service-bookings`, {
    body,
    idempotencyKey,
  });
}

export async function pmsServiceBookingsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSServiceBookingDTO> {
  return client.request<PMSServiceBookingDTO>('GET', `/api/v1/pms-service-bookings/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsServiceBookingsDeliver(
  client: ApiClient,
  params: { id: string },
  body: DeliverPMSServiceBookingRequest,
  idempotencyKey?: string,
): Promise<PMSServiceBookingDTO> {
  return client.request<PMSServiceBookingDTO>('POST', `/api/v1/pms-service-bookings/${encodeURIComponent(params.id)}/deliver`, {
    body,
    idempotencyKey,
  });
}

export async function pmsServiceBookingsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelPMSServiceBookingRequest,
  idempotencyKey?: string,
): Promise<PMSServiceBookingDTO> {
  return client.request<PMSServiceBookingDTO>('POST', `/api/v1/pms-service-bookings/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function pmsItinerariesGet(
  client: ApiClient,
  params: { reservationId: string },
): Promise<PMSItineraryDTO> {
  return client.request<PMSItineraryDTO>('GET', `/api/v1/pms-itineraries/${encodeURIComponent(params.reservationId)}`, {
  });
}

export async function pmsBoardPlansList(
  client: ApiClient,
  query: ListPMSBoardPlansQuery,
): Promise<PagePMSBoardPlanDTO> {
  return client.request<PagePMSBoardPlanDTO>('GET', `/api/v1/pms-board-plans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsBoardPlansCreate(
  client: ApiClient,
  body: CreatePMSBoardPlanRequest,
  idempotencyKey?: string,
): Promise<PMSBoardPlanDTO> {
  return client.request<PMSBoardPlanDTO>('POST', `/api/v1/pms-board-plans`, {
    body,
    idempotencyKey,
  });
}

export async function pmsBoardPlansGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSBoardPlanDTO> {
  return client.request<PMSBoardPlanDTO>('GET', `/api/v1/pms-board-plans/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsBoardPlansSetEntitlements(
  client: ApiClient,
  params: { id: string },
  body: SetPMSBoardEntitlementsRequest,
  idempotencyKey?: string,
): Promise<PMSBoardPlanDTO> {
  return client.request<PMSBoardPlanDTO>('PUT', `/api/v1/pms-board-plans/${encodeURIComponent(params.id)}/entitlements`, {
    body,
    idempotencyKey,
  });
}

export async function pmsBoardPlansActivate(
  client: ApiClient,
  params: { id: string },
  body: ActivatePMSBoardPlanRequest,
  idempotencyKey?: string,
): Promise<PMSBoardPlanDTO> {
  return client.request<PMSBoardPlanDTO>('POST', `/api/v1/pms-board-plans/${encodeURIComponent(params.id)}/activate`, {
    body,
    idempotencyKey,
  });
}

export async function pmsBoardAllowancesList(
  client: ApiClient,
  query: ListPMSBoardAllowancesQuery,
): Promise<PMSBoardAllowanceDTO[]> {
  return client.request<PMSBoardAllowanceDTO[]>('GET', `/api/v1/pms-board-allowances`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsBoardAllowancesUnconsumed(
  client: ApiClient,
  query: ListPMSUnconsumedBoardAllowancesQuery,
): Promise<PagePMSBoardAllowanceDTO> {
  return client.request<PagePMSBoardAllowanceDTO>('GET', `/api/v1/pms-board-allowances/unconsumed`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsTransferLegsList(
  client: ApiClient,
  query: ListPMSTransferLegsQuery,
): Promise<PagePMSTransferLegDTO> {
  return client.request<PagePMSTransferLegDTO>('GET', `/api/v1/pms-transfer-legs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsTransferLegsCreate(
  client: ApiClient,
  body: CreatePMSTransferLegRequest,
  idempotencyKey?: string,
): Promise<PMSTransferLegDTO> {
  return client.request<PMSTransferLegDTO>('POST', `/api/v1/pms-transfer-legs`, {
    body,
    idempotencyKey,
  });
}

export async function pmsTransferLegsAddBookings(
  client: ApiClient,
  params: { id: string },
  body: CreatePMSTransferLegBookingsRequest,
  idempotencyKey?: string,
): Promise<PMSTransferBookingDTO[]> {
  return client.request<PMSTransferBookingDTO[]>('POST', `/api/v1/pms-transfer-legs/${encodeURIComponent(params.id)}/bookings`, {
    body,
    idempotencyKey,
  });
}

export async function pmsTransferLegsManifest(
  client: ApiClient,
  params: { id: string },
): Promise<PMSManifestDTO> {
  return client.request<PMSManifestDTO>('GET', `/api/v1/pms-transfer-legs/${encodeURIComponent(params.id)}/manifest`, {
  });
}

export async function pmsGuestRequestsList(
  client: ApiClient,
  query: ListPMSGuestRequestsQuery,
): Promise<PagePMSGuestRequestDTO> {
  return client.request<PagePMSGuestRequestDTO>('GET', `/api/v1/pms-guest-requests`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsGuestRequestsCreate(
  client: ApiClient,
  body: CreatePMSGuestRequestRequest,
  idempotencyKey?: string,
): Promise<PMSGuestRequestDTO> {
  return client.request<PMSGuestRequestDTO>('POST', `/api/v1/pms-guest-requests`, {
    body,
    idempotencyKey,
  });
}

export async function pmsGuestRequestsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSGuestRequestDTO> {
  return client.request<PMSGuestRequestDTO>('GET', `/api/v1/pms-guest-requests/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsGuestRequestsRecover(
  client: ApiClient,
  params: { id: string },
  body: RecoverPMSGuestRequestRequest,
  idempotencyKey?: string,
): Promise<PMSGuestRequestDTO> {
  return client.request<PMSGuestRequestDTO>('POST', `/api/v1/pms-guest-requests/${encodeURIComponent(params.id)}/recover`, {
    body,
    idempotencyKey,
  });
}

export async function pmsChannelsList(
  client: ApiClient,
  query: ListPMSChannelsQuery,
): Promise<PagePMSChannelDTO> {
  return client.request<PagePMSChannelDTO>('GET', `/api/v1/pms-channels`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsChannelsCreate(
  client: ApiClient,
  body: CreatePMSChannelRequest,
  idempotencyKey?: string,
): Promise<PMSChannelDTO> {
  return client.request<PMSChannelDTO>('POST', `/api/v1/pms-channels`, {
    body,
    idempotencyKey,
  });
}

export async function pmsChannelsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSChannelDTO> {
  return client.request<PMSChannelDTO>('GET', `/api/v1/pms-channels/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsChannelsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdatePMSChannelRequest,
): Promise<PMSChannelDTO> {
  return client.request<PMSChannelDTO>('PATCH', `/api/v1/pms-channels/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function pmsChannelsPush(
  client: ApiClient,
  params: { id: string },
  body: PushPMSChannelRequest,
  idempotencyKey?: string,
): Promise<PMSChannelExchangeDTO> {
  return client.request<PMSChannelExchangeDTO>('POST', `/api/v1/pms-channels/${encodeURIComponent(params.id)}/push`, {
    body,
    idempotencyKey,
  });
}

export async function pmsChannelsPull(
  client: ApiClient,
  params: { id: string },
  file: File | Blob | null,
  fields: { payloadKind: string; statementNo?: string; periodFrom?: string; periodTo?: string; currency?: string },
  idempotencyKey?: string,
): Promise<PMSChannelExchangeDTO> {
  const form = new FormData();
  if (file !== null) {
    form.append('file', file);
  }
  form.append('payloadKind', String(fields.payloadKind));
  if (fields.statementNo !== undefined) {
    form.append('statementNo', String(fields.statementNo));
  }
  if (fields.periodFrom !== undefined) {
    form.append('periodFrom', String(fields.periodFrom));
  }
  if (fields.periodTo !== undefined) {
    form.append('periodTo', String(fields.periodTo));
  }
  if (fields.currency !== undefined) {
    form.append('currency', String(fields.currency));
  }
  return client.request<PMSChannelExchangeDTO>('POST', `/api/v1/pms-channels/${encodeURIComponent(params.id)}/pull`, {
    body: form,
    idempotencyKey,
  });
}

export async function pmsChannelExchangesList(
  client: ApiClient,
  query: ListPMSChannelExchangesQuery,
): Promise<PagePMSChannelExchangeDTO> {
  return client.request<PagePMSChannelExchangeDTO>('GET', `/api/v1/pms-channel-exchanges`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsParityObservationsList(
  client: ApiClient,
  query: ListPMSParityObservationsQuery,
): Promise<PagePMSParityObservationDTO> {
  return client.request<PagePMSParityObservationDTO>('GET', `/api/v1/pms-parity-observations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsParityObservationsCreate(
  client: ApiClient,
  body: CreatePMSParityObservationRequest,
  idempotencyKey?: string,
): Promise<PMSParityObservationDTO> {
  return client.request<PMSParityObservationDTO>('POST', `/api/v1/pms-parity-observations`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCommissionAgreementsList(
  client: ApiClient,
  query: ListPMSCommissionAgreementsQuery,
): Promise<PagePMSCommissionAgreementDTO> {
  return client.request<PagePMSCommissionAgreementDTO>('GET', `/api/v1/pms-commission-agreements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsCommissionAgreementsCreate(
  client: ApiClient,
  body: CreatePMSCommissionAgreementRequest,
  idempotencyKey?: string,
): Promise<PMSCommissionAgreementDTO> {
  return client.request<PMSCommissionAgreementDTO>('POST', `/api/v1/pms-commission-agreements`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCommissionAgreementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSCommissionAgreementDTO> {
  return client.request<PMSCommissionAgreementDTO>('GET', `/api/v1/pms-commission-agreements/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsCommissionAgreementsListVersions(
  client: ApiClient,
  params: { id: string },
): Promise<PMSCommissionVersionDTO[]> {
  return client.request<PMSCommissionVersionDTO[]>('GET', `/api/v1/pms-commission-agreements/${encodeURIComponent(params.id)}/versions`, {
  });
}

export async function pmsCommissionAgreementsCreateVersion(
  client: ApiClient,
  params: { id: string },
  body: CreatePMSCommissionVersionRequest,
  idempotencyKey?: string,
): Promise<PMSCommissionVersionDTO> {
  return client.request<PMSCommissionVersionDTO>('POST', `/api/v1/pms-commission-agreements/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCommissionVersionsPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishPMSCommissionVersionRequest,
  idempotencyKey?: string,
): Promise<PMSCommissionVersionDTO> {
  return client.request<PMSCommissionVersionDTO>('POST', `/api/v1/pms-commission-versions/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCommissionAccrualsList(
  client: ApiClient,
  query: ListPMSCommissionAccrualsQuery,
): Promise<PagePMSCommissionAccrualDTO> {
  return client.request<PagePMSCommissionAccrualDTO>('GET', `/api/v1/pms-commission-accruals`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsCommissionStatementsList(
  client: ApiClient,
  query: ListPMSCommissionStatementsQuery,
): Promise<PagePMSCommissionStatementDTO> {
  return client.request<PagePMSCommissionStatementDTO>('GET', `/api/v1/pms-commission-statements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsCommissionStatementsCreate(
  client: ApiClient,
  body: CreatePMSCommissionStatementRequest,
  idempotencyKey?: string,
): Promise<PMSCommissionStatementDTO> {
  return client.request<PMSCommissionStatementDTO>('POST', `/api/v1/pms-commission-statements`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCommissionStatementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSCommissionStatementDTO> {
  return client.request<PMSCommissionStatementDTO>('GET', `/api/v1/pms-commission-statements/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsCommissionStatementsSuggestions(
  client: ApiClient,
  params: { id: string },
): Promise<PMSCommissionMatchDTO[]> {
  return client.request<PMSCommissionMatchDTO[]>('GET', `/api/v1/pms-commission-statements/${encodeURIComponent(params.id)}/suggestions`, {
  });
}

export async function pmsCommissionStatementLinesMatch(
  client: ApiClient,
  params: { id: string },
  body: MatchPMSCommissionStatementLineRequest,
  idempotencyKey?: string,
): Promise<PMSCommissionStatementLineDTO> {
  return client.request<PMSCommissionStatementLineDTO>('POST', `/api/v1/pms-commission-statement-lines/${encodeURIComponent(params.id)}/match`, {
    body,
    idempotencyKey,
  });
}

export async function pmsCommissionStatementLinesWriteOff(
  client: ApiClient,
  params: { id: string },
  body: WriteOffPMSCommissionStatementLineRequest,
  idempotencyKey?: string,
): Promise<PMSCommissionStatementLineDTO> {
  return client.request<PMSCommissionStatementLineDTO>('POST', `/api/v1/pms-commission-statement-lines/${encodeURIComponent(params.id)}/write-off`, {
    body,
    idempotencyKey,
  });
}

export async function pmsNightAuditRunsReadiness(
  client: ApiClient,
  query: PMSNightAuditReadinessQuery,
): Promise<PMSNightAuditReadinessDTO> {
  return client.request<PMSNightAuditReadinessDTO>('GET', `/api/v1/pms-night-audit/readiness`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsNightAuditRunsList(
  client: ApiClient,
  query: ListPMSNightAuditRunsQuery,
): Promise<PagePMSNightAuditRunDTO> {
  return client.request<PagePMSNightAuditRunDTO>('GET', `/api/v1/pms-night-audit-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsNightAuditRunsCreate(
  client: ApiClient,
  body: CreatePMSNightAuditRunRequest,
  idempotencyKey?: string,
): Promise<PMSNightAuditRunDetailDTO> {
  return client.request<PMSNightAuditRunDetailDTO>('POST', `/api/v1/pms-night-audit-runs`, {
    body,
    idempotencyKey,
  });
}

export async function pmsNightAuditRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSNightAuditRunDetailDTO> {
  return client.request<PMSNightAuditRunDetailDTO>('GET', `/api/v1/pms-night-audit-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function pmsNightAuditStepsAdvance(
  client: ApiClient,
  params: { id: string },
  body: AdvancePMSNightAuditRunRequest,
  idempotencyKey?: string,
): Promise<PMSNightAuditRunDetailDTO> {
  return client.request<PMSNightAuditRunDetailDTO>('POST', `/api/v1/pms-night-audit-runs/${encodeURIComponent(params.id)}/advance`, {
    body,
    idempotencyKey,
  });
}

export async function pmsNightAuditRunsForce(
  client: ApiClient,
  params: { id: string },
  body: ForcePMSNightAuditRunRequest,
  idempotencyKey?: string,
): Promise<PMSNightAuditRunDetailDTO> {
  return client.request<PMSNightAuditRunDetailDTO>('POST', `/api/v1/pms-night-audit-runs/${encodeURIComponent(params.id)}/force`, {
    body,
    idempotencyKey,
  });
}

export async function pmsNightAuditRunsAbandon(
  client: ApiClient,
  params: { id: string },
  body: AbandonPMSNightAuditRunRequest,
  idempotencyKey?: string,
): Promise<PMSNightAuditRunDetailDTO> {
  return client.request<PMSNightAuditRunDetailDTO>('POST', `/api/v1/pms-night-audit-runs/${encodeURIComponent(params.id)}/abandon`, {
    body,
    idempotencyKey,
  });
}

export async function pmsDayPackGet(
  client: ApiClient,
  params: { id: string },
): Promise<PMSDayPackDTO> {
  return client.request<PMSDayPackDTO>('GET', `/api/v1/pms-night-audit-runs/${encodeURIComponent(params.id)}/day-pack`, {
  });
}

export async function pmsDayPackDownload(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('GET', `/api/v1/pms-night-audit-runs/${encodeURIComponent(params.id)}/day-pack/download`, {
  });
}

export async function pmsReportsOccupancy(
  client: ApiClient,
  query: PMSReportQuery,
): Promise<PMSOccupancyReportDTO> {
  return client.request<PMSOccupancyReportDTO>('GET', `/api/v1/pms-reports/occupancy`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsReportsAdrRevpar(
  client: ApiClient,
  query: PMSReportQuery,
): Promise<PMSOccupancyReportDTO> {
  return client.request<PMSOccupancyReportDTO>('GET', `/api/v1/pms-reports/adr-revpar`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsReportsInHouse(
  client: ApiClient,
  query: PMSReportQuery,
): Promise<PMSInHouseRowDTO[]> {
  return client.request<PMSInHouseRowDTO[]>('GET', `/api/v1/pms-reports/in-house`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsReportsGuestLedger(
  client: ApiClient,
  query: PMSReportQuery,
): Promise<PMSLedgerRowDTO[]> {
  return client.request<PMSLedgerRowDTO[]>('GET', `/api/v1/pms-reports/guest-ledger`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function pmsReportsDailyOperating(
  client: ApiClient,
  query: PMSReportQuery,
): Promise<PMSRevenueReportDTO> {
  return client.request<PMSRevenueReportDTO>('GET', `/api/v1/pms-reports/daily-operating`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmSettingsGet(
  client: ApiClient,
  query: RVMSettingsQuery,
): Promise<RVMSettingsDTO> {
  return client.request<RVMSettingsDTO>('GET', `/api/v1/rvm/settings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmSettingsUpdate(
  client: ApiClient,
  body: UpdateRVMSettingsRequest,
): Promise<RVMSettingsDTO> {
  return client.request<RVMSettingsDTO>('PATCH', `/api/v1/rvm/settings`, {
    body,
  });
}

export async function rvmSegmentsList(
  client: ApiClient,
  query: ListRVMSegmentsQuery,
): Promise<PageRVMSegmentDTO> {
  return client.request<PageRVMSegmentDTO>('GET', `/api/v1/rvm/segments`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmSegmentsCreate(
  client: ApiClient,
  body: CreateRVMSegmentRequest,
  idempotencyKey?: string,
): Promise<RVMSegmentDTO> {
  return client.request<RVMSegmentDTO>('POST', `/api/v1/rvm/segments`, {
    body,
    idempotencyKey,
  });
}

export async function rvmSegmentsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMSegmentDTO> {
  return client.request<RVMSegmentDTO>('GET', `/api/v1/rvm/segments/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmSegmentsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRVMSegmentRequest,
): Promise<RVMSegmentDTO> {
  return client.request<RVMSegmentDTO>('PATCH', `/api/v1/rvm/segments/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rvmSegmentsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/rvm/segments/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmSegmentMappingsList(
  client: ApiClient,
  query: ListRVMSegmentMappingsQuery,
): Promise<PageRVMSegmentMappingDTO> {
  return client.request<PageRVMSegmentMappingDTO>('GET', `/api/v1/rvm/segment-mappings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmSegmentMappingsCreate(
  client: ApiClient,
  body: CreateRVMSegmentMappingRequest,
  idempotencyKey?: string,
): Promise<RVMSegmentMappingDTO> {
  return client.request<RVMSegmentMappingDTO>('POST', `/api/v1/rvm/segment-mappings`, {
    body,
    idempotencyKey,
  });
}

export async function rvmSegmentMappingsResolve(
  client: ApiClient,
  query: ResolveRVMSegmentQuery,
): Promise<RVMSegmentResolutionDTO> {
  return client.request<RVMSegmentResolutionDTO>('GET', `/api/v1/rvm/segment-mappings/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmSegmentMappingsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRVMSegmentMappingRequest,
): Promise<RVMSegmentMappingDTO> {
  return client.request<RVMSegmentMappingDTO>('PATCH', `/api/v1/rvm/segment-mappings/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rvmSegmentMappingsDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/rvm/segment-mappings/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmStreamsList(
  client: ApiClient,
  query: ListRVMStreamsQuery,
): Promise<PageRVMStreamDTO> {
  return client.request<PageRVMStreamDTO>('GET', `/api/v1/rvm/streams`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmStreamsCreate(
  client: ApiClient,
  body: CreateRVMStreamRequest,
  idempotencyKey?: string,
): Promise<RVMStreamDTO> {
  return client.request<RVMStreamDTO>('POST', `/api/v1/rvm/streams`, {
    body,
    idempotencyKey,
  });
}

export async function rvmStreamsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMStreamDTO> {
  return client.request<RVMStreamDTO>('GET', `/api/v1/rvm/streams/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmStreamsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRVMStreamRequest,
): Promise<RVMStreamDTO> {
  return client.request<RVMStreamDTO>('PATCH', `/api/v1/rvm/streams/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rvmForecastsList(
  client: ApiClient,
  query: ListRVMForecastsQuery,
): Promise<PageRVMForecastDTO> {
  return client.request<PageRVMForecastDTO>('GET', `/api/v1/rvm/forecasts`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmForecastsCreate(
  client: ApiClient,
  body: CreateRVMForecastRequest,
  idempotencyKey?: string,
): Promise<RVMForecastDTO> {
  return client.request<RVMForecastDTO>('POST', `/api/v1/rvm/forecasts`, {
    body,
    idempotencyKey,
  });
}

export async function rvmForecastsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMForecastDTO> {
  return client.request<RVMForecastDTO>('GET', `/api/v1/rvm/forecasts/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmForecastsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRVMForecastRequest,
): Promise<RVMForecastDTO> {
  return client.request<RVMForecastDTO>('PATCH', `/api/v1/rvm/forecasts/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rvmForecastVersionsList(
  client: ApiClient,
  params: { id: string },
  query: ListRVMForecastVersionsQuery,
): Promise<PageRVMForecastVersionDTO> {
  return client.request<PageRVMForecastVersionDTO>('GET', `/api/v1/rvm/forecasts/${encodeURIComponent(params.id)}/versions`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmForecastVersionsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateRVMForecastVersionRequest,
  idempotencyKey?: string,
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('POST', `/api/v1/rvm/forecasts/${encodeURIComponent(params.id)}/versions`, {
    body,
    idempotencyKey,
  });
}

export async function rvmForecastVersionsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('GET', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmForecastLinesList(
  client: ApiClient,
  params: { id: string },
): Promise<RVMForecastLinesDTO> {
  return client.request<RVMForecastLinesDTO>('GET', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/lines`, {
  });
}

export async function rvmForecastLinesReplace(
  client: ApiClient,
  params: { id: string },
  body: ReplaceRVMForecastLinesRequest,
): Promise<RVMForecastLinesDTO> {
  return client.request<RVMForecastLinesDTO>('PUT', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/lines`, {
    body,
  });
}

export async function rvmForecastVersionsSubmit(
  client: ApiClient,
  params: { id: string },
  body: SubmitRVMForecastVersionRequest,
  idempotencyKey?: string,
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('POST', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/submit`, {
    body,
    idempotencyKey,
  });
}

export async function rvmForecastVersionsApprove(
  client: ApiClient,
  params: { id: string },
  body: DecideRVMForecastVersionRequest,
  idempotencyKey?: string,
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('POST', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/approve`, {
    body,
    idempotencyKey,
  });
}

export async function rvmForecastVersionsReject(
  client: ApiClient,
  params: { id: string },
  body: DecideRVMForecastVersionRequest,
  idempotencyKey?: string,
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('POST', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/reject`, {
    body,
    idempotencyKey,
  });
}

export async function rvmForecastVersionsPublish(
  client: ApiClient,
  params: { id: string },
  body: PublishRVMForecastVersionRequest,
  idempotencyKey?: string,
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('POST', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/publish`, {
    body,
    idempotencyKey,
  });
}

export async function rvmForecastVersionsLock(
  client: ApiClient,
  params: { id: string },
  idempotencyKey?: string,
): Promise<RVMForecastVersionDTO> {
  return client.request<RVMForecastVersionDTO>('POST', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/lock`, {
    idempotencyKey,
  });
}

export async function rvmSnapshotsList(
  client: ApiClient,
  query: ListRVMSnapshotsQuery,
): Promise<PageRVMOnTheBooksSnapshotDTO> {
  return client.request<PageRVMOnTheBooksSnapshotDTO>('GET', `/api/v1/rvm/snapshots`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmSnapshotsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMOnTheBooksSnapshotDTO> {
  return client.request<RVMOnTheBooksSnapshotDTO>('GET', `/api/v1/rvm/snapshots/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmSnapshotsTake(
  client: ApiClient,
  body: TakeRVMSnapshotRequest,
  idempotencyKey?: string,
): Promise<RVMOnTheBooksSnapshotDTO> {
  return client.request<RVMOnTheBooksSnapshotDTO>('POST', `/api/v1/rvm/snapshots`, {
    body,
    idempotencyKey,
  });
}

export async function rvmOnTheBooksGet(
  client: ApiClient,
  query: RVMOnTheBooksQuery,
): Promise<RVMOnTheBooksSurfaceDTO> {
  return client.request<RVMOnTheBooksSurfaceDTO>('GET', `/api/v1/rvm/on-the-books`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmCompetitorRatesList(
  client: ApiClient,
  query: ListRVMCompetitorRatesQuery,
): Promise<PageRVMCompetitorRateDTO> {
  return client.request<PageRVMCompetitorRateDTO>('GET', `/api/v1/rvm/competitor-rates`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmCompetitorRatesCreate(
  client: ApiClient,
  body: CreateRVMCompetitorRateRequest,
  idempotencyKey?: string,
): Promise<RVMCompetitorRateDTO> {
  return client.request<RVMCompetitorRateDTO>('POST', `/api/v1/rvm/competitor-rates`, {
    body,
    idempotencyKey,
  });
}

export async function rvmCompetitorRatesImport(
  client: ApiClient,
  body: ImportRVMCompetitorRatesRequest,
  idempotencyKey?: string,
): Promise<RVMCompetitorImportResultDTO> {
  return client.request<RVMCompetitorImportResultDTO>('POST', `/api/v1/rvm/competitor-rates/import`, {
    body,
    idempotencyKey,
  });
}

export async function rvmCompetitorRatesGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMCompetitorRateDTO> {
  return client.request<RVMCompetitorRateDTO>('GET', `/api/v1/rvm/competitor-rates/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmCompetitorRatesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRVMCompetitorRateRequest,
): Promise<RVMCompetitorRateDTO> {
  return client.request<RVMCompetitorRateDTO>('PATCH', `/api/v1/rvm/competitor-rates/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rvmCompetitorRatesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/rvm/competitor-rates/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmPaceGet(
  client: ApiClient,
  query: RVMPaceQuery,
): Promise<RVMPaceDTO> {
  return client.request<RVMPaceDTO>('GET', `/api/v1/rvm/pace`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmPickupGet(
  client: ApiClient,
  query: RVMPickupQuery,
): Promise<RVMPickupDTO> {
  return client.request<RVMPickupDTO>('GET', `/api/v1/rvm/pickup`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmVarianceGet(
  client: ApiClient,
  query: RVMVarianceQuery,
): Promise<RVMVarianceDTO> {
  return client.request<RVMVarianceDTO>('GET', `/api/v1/rvm/variance`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmVarianceExplanationsCreate(
  client: ApiClient,
  params: { id: string },
  body: ExplainRVMVarianceRequest,
): Promise<RVMVarianceExplanationDTO> {
  return client.request<RVMVarianceExplanationDTO>('POST', `/api/v1/rvm/forecast-versions/${encodeURIComponent(params.id)}/explanations`, {
    body,
  });
}

export async function rvmAccuracyRunsList(
  client: ApiClient,
  query: ListRVMAccuracyRunsQuery,
): Promise<PageRVMAccuracyRunDTO> {
  return client.request<PageRVMAccuracyRunDTO>('GET', `/api/v1/rvm/accuracy-runs`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmAccuracyRunsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMAccuracyRunDTO> {
  return client.request<RVMAccuracyRunDTO>('GET', `/api/v1/rvm/accuracy-runs/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmAccuracyRunsLines(
  client: ApiClient,
  params: { id: string },
  query: ListRVMAccuracyLinesQuery,
): Promise<RVMAccuracyLineDTO[]> {
  return client.request<RVMAccuracyLineDTO[]>('GET', `/api/v1/rvm/accuracy-runs/${encodeURIComponent(params.id)}/lines`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmAccuracyRunsCreate(
  client: ApiClient,
  body: CreateRVMAccuracyRunRequest,
  idempotencyKey?: string,
): Promise<RVMAccuracyRunDTO> {
  return client.request<RVMAccuracyRunDTO>('POST', `/api/v1/rvm/accuracy-runs`, {
    body,
    idempotencyKey,
  });
}

export async function rvmCommercialReportGet(
  client: ApiClient,
  query: RVMCommercialReportQuery,
): Promise<RVMCommercialReportDTO> {
  return client.request<RVMCommercialReportDTO>('GET', `/api/v1/rvm/commercial-report`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmHurdlesList(
  client: ApiClient,
  query: ListRVMHurdlesQuery,
): Promise<PageRVMHurdleDTO> {
  return client.request<PageRVMHurdleDTO>('GET', `/api/v1/rvm/hurdles`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmHurdlesResolve(
  client: ApiClient,
  query: ResolveRVMHurdleQuery,
): Promise<RVMHurdleResolutionDTO> {
  return client.request<RVMHurdleResolutionDTO>('GET', `/api/v1/rvm/hurdles/resolve`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmHurdlesGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMHurdleDTO> {
  return client.request<RVMHurdleDTO>('GET', `/api/v1/rvm/hurdles/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmHurdlesCreate(
  client: ApiClient,
  body: CreateRVMHurdleRequest,
  idempotencyKey?: string,
): Promise<RVMHurdleDTO> {
  return client.request<RVMHurdleDTO>('POST', `/api/v1/rvm/hurdles`, {
    body,
    idempotencyKey,
  });
}

export async function rvmHurdlesUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateRVMHurdleRequest,
): Promise<RVMHurdleDTO> {
  return client.request<RVMHurdleDTO>('PATCH', `/api/v1/rvm/hurdles/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function rvmHurdlesDelete(
  client: ApiClient,
  params: { id: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/rvm/hurdles/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmDisplacementsList(
  client: ApiClient,
  query: ListRVMDisplacementsQuery,
): Promise<PageRVMDisplacementDTO> {
  return client.request<PageRVMDisplacementDTO>('GET', `/api/v1/rvm/displacements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function rvmDisplacementOutcomesMeasure(
  client: ApiClient,
  body: MeasureRVMDisplacementOutcomesRequest,
  idempotencyKey?: string,
): Promise<RVMDisplacementSweepDTO> {
  return client.request<RVMDisplacementSweepDTO>('POST', `/api/v1/rvm/displacements/measure-outcomes`, {
    body,
    idempotencyKey,
  });
}

export async function rvmDisplacementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMDisplacementDetailDTO> {
  return client.request<RVMDisplacementDetailDTO>('GET', `/api/v1/rvm/displacements/${encodeURIComponent(params.id)}`, {
  });
}

export async function rvmDisplacementOutcomesGet(
  client: ApiClient,
  params: { id: string },
): Promise<RVMDisplacementOutcomeDTO> {
  return client.request<RVMDisplacementOutcomeDTO>('GET', `/api/v1/rvm/displacements/${encodeURIComponent(params.id)}/outcome`, {
  });
}

export async function rvmDisplacementsEvaluate(
  client: ApiClient,
  body: EvaluateRVMDisplacementRequest,
  idempotencyKey?: string,
): Promise<RVMDisplacementDetailDTO> {
  return client.request<RVMDisplacementDetailDTO>('POST', `/api/v1/rvm/displacements`, {
    body,
    idempotencyKey,
  });
}

export async function rvmDisplacementsReEvaluate(
  client: ApiClient,
  params: { id: string },
): Promise<RVMDisplacementDetailDTO> {
  return client.request<RVMDisplacementDetailDTO>('POST', `/api/v1/rvm/displacements/${encodeURIComponent(params.id)}/re-evaluate`, {
  });
}

export async function rvmDisplacementsDecide(
  client: ApiClient,
  params: { id: string },
  body: DecideRVMDisplacementRequest,
): Promise<RVMDisplacementDetailDTO> {
  return client.request<RVMDisplacementDetailDTO>('POST', `/api/v1/rvm/displacements/${encodeURIComponent(params.id)}/decide`, {
    body,
  });
}

export async function eamFunctionalLocationsList(
  client: ApiClient,
  query: ListEAMFunctionalLocationsQuery,
): Promise<PageEAMFunctionalLocationDTO> {
  return client.request<PageEAMFunctionalLocationDTO>('GET', `/api/v1/eam-functional-locations`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamFunctionalLocationsCreate(
  client: ApiClient,
  body: CreateEAMFunctionalLocationRequest,
  idempotencyKey?: string,
): Promise<EAMFunctionalLocationDTO> {
  return client.request<EAMFunctionalLocationDTO>('POST', `/api/v1/eam-functional-locations`, {
    body,
    idempotencyKey,
  });
}

export async function eamFunctionalLocationsTree(
  client: ApiClient,
  query: TreeEAMFunctionalLocationsQuery,
): Promise<EAMFunctionalLocationTreeDTO[]> {
  return client.request<EAMFunctionalLocationTreeDTO[]>('GET', `/api/v1/eam-functional-locations/tree`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamFunctionalLocationsGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMFunctionalLocationDTO> {
  return client.request<EAMFunctionalLocationDTO>('GET', `/api/v1/eam-functional-locations/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamFunctionalLocationsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEAMFunctionalLocationRequest,
): Promise<EAMFunctionalLocationDTO> {
  return client.request<EAMFunctionalLocationDTO>('PATCH', `/api/v1/eam-functional-locations/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function eamAssetsList(
  client: ApiClient,
  query: ListEAMAssetsQuery,
): Promise<PageEAMAssetDTO> {
  return client.request<PageEAMAssetDTO>('GET', `/api/v1/eam-assets`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamAssetsCreate(
  client: ApiClient,
  body: CreateEAMAssetRequest,
  idempotencyKey?: string,
): Promise<EAMAssetDTO> {
  return client.request<EAMAssetDTO>('POST', `/api/v1/eam-assets`, {
    body,
    idempotencyKey,
  });
}

export async function eamAssetsGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMAssetDetailDTO> {
  return client.request<EAMAssetDetailDTO>('GET', `/api/v1/eam-assets/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamAssetsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEAMAssetRequest,
): Promise<EAMAssetDTO> {
  return client.request<EAMAssetDTO>('PATCH', `/api/v1/eam-assets/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function eamAssetsCommission(
  client: ApiClient,
  params: { id: string },
  body: CommissionEAMAssetRequest,
  idempotencyKey?: string,
): Promise<EAMAssetDTO> {
  return client.request<EAMAssetDTO>('POST', `/api/v1/eam-assets/${encodeURIComponent(params.id)}/commission`, {
    body,
    idempotencyKey,
  });
}

export async function eamAssetsDecommission(
  client: ApiClient,
  params: { id: string },
  body: DecommissionEAMAssetRequest,
  idempotencyKey?: string,
): Promise<EAMAssetDTO> {
  return client.request<EAMAssetDTO>('POST', `/api/v1/eam-assets/${encodeURIComponent(params.id)}/decommission`, {
    body,
    idempotencyKey,
  });
}

export async function eamAssetsLifecycleCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateEAMLifecycleEventRequest,
  idempotencyKey?: string,
): Promise<EAMLifecycleEventDTO> {
  return client.request<EAMLifecycleEventDTO>('POST', `/api/v1/eam-assets/${encodeURIComponent(params.id)}/lifecycle-events`, {
    body,
    idempotencyKey,
  });
}

export async function eamAssetsLinksList(
  client: ApiClient,
  params: { id: string },
): Promise<EAMAssetLinkDTO[]> {
  return client.request<EAMAssetLinkDTO[]>('GET', `/api/v1/eam-assets/${encodeURIComponent(params.id)}/links`, {
  });
}

export async function eamAssetsLinksCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateEAMAssetLinkRequest,
  idempotencyKey?: string,
): Promise<EAMAssetLinkDTO> {
  return client.request<EAMAssetLinkDTO>('POST', `/api/v1/eam-assets/${encodeURIComponent(params.id)}/links`, {
    body,
    idempotencyKey,
  });
}

export async function eamAssetsLinksDelete(
  client: ApiClient,
  params: { id: string; linkId: string },
): Promise<void> {
  return client.request<void>('DELETE', `/api/v1/eam-assets/${encodeURIComponent(params.id)}/links/${encodeURIComponent(params.linkId)}`, {
  });
}

export async function eamReportsAssetReconciliation(
  client: ApiClient,
  query: ListEAMAssetReconciliationQuery,
): Promise<EAMReconciliationResponse> {
  return client.request<EAMReconciliationResponse>('GET', `/api/v1/eam-reports/asset-reconciliation`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamSettingsGet(
  client: ApiClient,
  params: { companyId: string },
): Promise<EAMSettingsDTO> {
  return client.request<EAMSettingsDTO>('GET', `/api/v1/eam-settings/${encodeURIComponent(params.companyId)}`, {
  });
}

export async function eamSettingsUpdate(
  client: ApiClient,
  params: { companyId: string },
  body: UpdateEAMSettingsRequest,
): Promise<EAMSettingsDTO> {
  return client.request<EAMSettingsDTO>('PUT', `/api/v1/eam-settings/${encodeURIComponent(params.companyId)}`, {
    body,
  });
}

export async function eamStrategiesList(
  client: ApiClient,
  query: ListEAMStrategiesQuery,
): Promise<PageEAMStrategyDTO> {
  return client.request<PageEAMStrategyDTO>('GET', `/api/v1/eam-strategies`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamStrategiesCreate(
  client: ApiClient,
  body: CreateEAMStrategyRequest,
  idempotencyKey?: string,
): Promise<EAMStrategyDTO> {
  return client.request<EAMStrategyDTO>('POST', `/api/v1/eam-strategies`, {
    body,
    idempotencyKey,
  });
}

export async function eamTaskListsList(
  client: ApiClient,
  query: ListEAMTaskListsQuery,
): Promise<PageEAMTaskListDTO> {
  return client.request<PageEAMTaskListDTO>('GET', `/api/v1/eam-task-lists`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamTaskListsCreate(
  client: ApiClient,
  body: CreateEAMTaskListRequest,
  idempotencyKey?: string,
): Promise<EAMTaskListDTO> {
  return client.request<EAMTaskListDTO>('POST', `/api/v1/eam-task-lists`, {
    body,
    idempotencyKey,
  });
}

export async function eamTaskListsGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMTaskListDetailDTO> {
  return client.request<EAMTaskListDetailDTO>('GET', `/api/v1/eam-task-lists/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamTaskListsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEAMTaskListRequest,
): Promise<EAMTaskListDTO> {
  return client.request<EAMTaskListDTO>('PATCH', `/api/v1/eam-task-lists/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function eamTaskListsStepsList(
  client: ApiClient,
  params: { id: string },
): Promise<EAMTaskListStepDTO[]> {
  return client.request<EAMTaskListStepDTO[]>('GET', `/api/v1/eam-task-lists/${encodeURIComponent(params.id)}/steps`, {
  });
}

export async function eamTaskListsStepsSet(
  client: ApiClient,
  params: { id: string },
  body: SetEAMTaskListStepsRequest,
): Promise<EAMTaskListStepDTO[]> {
  return client.request<EAMTaskListStepDTO[]>('PUT', `/api/v1/eam-task-lists/${encodeURIComponent(params.id)}/steps`, {
    body,
  });
}

export async function eamTaskListsSkillsList(
  client: ApiClient,
  params: { id: string },
): Promise<EAMTaskListSkillDTO[]> {
  return client.request<EAMTaskListSkillDTO[]>('GET', `/api/v1/eam-task-lists/${encodeURIComponent(params.id)}/skills`, {
  });
}

export async function eamTaskListsSkillsSet(
  client: ApiClient,
  params: { id: string },
  body: SetEAMTaskListSkillsRequest,
): Promise<EAMTaskListSkillDTO[]> {
  return client.request<EAMTaskListSkillDTO[]>('PUT', `/api/v1/eam-task-lists/${encodeURIComponent(params.id)}/skills`, {
    body,
  });
}

export async function eamPmPlansList(
  client: ApiClient,
  query: ListEAMPMPlansQuery,
): Promise<PageEAMPMPlanDTO> {
  return client.request<PageEAMPMPlanDTO>('GET', `/api/v1/eam-pm-plans`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamPmPlansCreate(
  client: ApiClient,
  body: CreateEAMPMPlanRequest,
  idempotencyKey?: string,
): Promise<EAMPMPlanDTO> {
  return client.request<EAMPMPlanDTO>('POST', `/api/v1/eam-pm-plans`, {
    body,
    idempotencyKey,
  });
}

export async function eamPmPlansGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMPMPlanDTO> {
  return client.request<EAMPMPlanDTO>('GET', `/api/v1/eam-pm-plans/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamPmPlansUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEAMPMPlanRequest,
): Promise<EAMPMPlanDTO> {
  return client.request<EAMPMPlanDTO>('PATCH', `/api/v1/eam-pm-plans/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function eamPmPlansPreview(
  client: ApiClient,
  params: { id: string },
): Promise<EAMPMPlanPreviewDTO> {
  return client.request<EAMPMPlanPreviewDTO>('GET', `/api/v1/eam-pm-plans/${encodeURIComponent(params.id)}/preview`, {
  });
}

export async function eamPmPlansGenerate(
  client: ApiClient,
  body: GenerateEAMPMPlansRequest,
  idempotencyKey?: string,
): Promise<GenerateEAMPMPlansResult> {
  return client.request<GenerateEAMPMPlansResult>('POST', `/api/v1/eam-pm-plans/generate`, {
    body,
    idempotencyKey,
  });
}

export async function eamMetersList(
  client: ApiClient,
  query: ListEAMMetersQuery,
): Promise<PageEAMMeterDTO> {
  return client.request<PageEAMMeterDTO>('GET', `/api/v1/eam-meters`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamMetersCreate(
  client: ApiClient,
  body: CreateEAMMeterRequest,
  idempotencyKey?: string,
): Promise<EAMMeterDTO> {
  return client.request<EAMMeterDTO>('POST', `/api/v1/eam-meters`, {
    body,
    idempotencyKey,
  });
}

export async function eamReadingsList(
  client: ApiClient,
  params: { id: string },
  query: ListEAMMeterReadingsQuery,
): Promise<PageEAMMeterReadingDTO> {
  return client.request<PageEAMMeterReadingDTO>('GET', `/api/v1/eam-meters/${encodeURIComponent(params.id)}/readings`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamReadingsCreate(
  client: ApiClient,
  params: { id: string },
  body: CreateEAMMeterReadingRequest,
  idempotencyKey?: string,
): Promise<EAMMeterReadingDTO> {
  return client.request<EAMMeterReadingDTO>('POST', `/api/v1/eam-meters/${encodeURIComponent(params.id)}/readings`, {
    body,
    idempotencyKey,
  });
}

export async function eamReadingsImport(
  client: ApiClient,
  body: ImportEAMMeterReadingsRequest,
): Promise<ImportEAMMeterReadingsResult> {
  return client.request<ImportEAMMeterReadingsResult>('POST', `/api/v1/eam-meter-readings/import`, {
    body,
  });
}

export async function eamFailuresList(
  client: ApiClient,
  query: ListEAMFailuresQuery,
): Promise<PageEAMFailureDTO> {
  return client.request<PageEAMFailureDTO>('GET', `/api/v1/eam-failures`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamFailuresCreate(
  client: ApiClient,
  body: CreateEAMFailureRequest,
  idempotencyKey?: string,
): Promise<EAMFailureDTO> {
  return client.request<EAMFailureDTO>('POST', `/api/v1/eam-failures`, {
    body,
    idempotencyKey,
  });
}

export async function eamFailuresRestore(
  client: ApiClient,
  params: { id: string },
  body: RestoreEAMFailureRequest,
  idempotencyKey?: string,
): Promise<EAMFailureDTO> {
  return client.request<EAMFailureDTO>('POST', `/api/v1/eam-failures/${encodeURIComponent(params.id)}/restore`, {
    body,
    idempotencyKey,
  });
}

export async function eamComplianceRequirementsList(
  client: ApiClient,
  query: ListEAMComplianceRequirementsQuery,
): Promise<PageEAMComplianceRequirementDTO> {
  return client.request<PageEAMComplianceRequirementDTO>('GET', `/api/v1/eam-compliance-requirements`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamComplianceRequirementsCreate(
  client: ApiClient,
  body: CreateEAMComplianceRequirementRequest,
  idempotencyKey?: string,
): Promise<EAMComplianceRequirementDTO> {
  return client.request<EAMComplianceRequirementDTO>('POST', `/api/v1/eam-compliance-requirements`, {
    body,
    idempotencyKey,
  });
}

export async function eamComplianceCalendar(
  client: ApiClient,
  query: EAMComplianceCalendarQuery,
): Promise<EAMComplianceCalendarDTO> {
  return client.request<EAMComplianceCalendarDTO>('GET', `/api/v1/eam-compliance/calendar`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamComplianceRequirementsGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMComplianceRequirementDTO> {
  return client.request<EAMComplianceRequirementDTO>('GET', `/api/v1/eam-compliance-requirements/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamComplianceRequirementsUpdate(
  client: ApiClient,
  params: { id: string },
  body: UpdateEAMComplianceRequirementRequest,
): Promise<EAMComplianceRequirementDTO> {
  return client.request<EAMComplianceRequirementDTO>('PATCH', `/api/v1/eam-compliance-requirements/${encodeURIComponent(params.id)}`, {
    body,
  });
}

export async function eamInspectionsList(
  client: ApiClient,
  params: { id: string },
  query: ListEAMInspectionsQuery,
): Promise<PageEAMInspectionDTO> {
  return client.request<PageEAMInspectionDTO>('GET', `/api/v1/eam-compliance-requirements/${encodeURIComponent(params.id)}/inspections`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamInspectionsRecord(
  client: ApiClient,
  params: { id: string },
  body: RecordEAMInspectionRequest,
  idempotencyKey?: string,
): Promise<EAMInspectionDTO> {
  return client.request<EAMInspectionDTO>('POST', `/api/v1/eam-compliance-requirements/${encodeURIComponent(params.id)}/inspections`, {
    body,
    idempotencyKey,
  });
}

export async function eamComplianceSweep(
  client: ApiClient,
  body: RunEAMComplianceSweepRequest,
  idempotencyKey?: string,
): Promise<EAMComplianceSweepResult> {
  return client.request<EAMComplianceSweepResult>('POST', `/api/v1/eam-compliance/sweep`, {
    body,
    idempotencyKey,
  });
}

export async function eamPermitsList(
  client: ApiClient,
  query: ListEAMPermitsQuery,
): Promise<PageEAMPermitDTO> {
  return client.request<PageEAMPermitDTO>('GET', `/api/v1/eam-permits`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamPermitsRequest(
  client: ApiClient,
  body: CreateEAMPermitRequest,
  idempotencyKey?: string,
): Promise<EAMPermitDTO> {
  return client.request<EAMPermitDTO>('POST', `/api/v1/eam-permits`, {
    body,
    idempotencyKey,
  });
}

export async function eamPermitsGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMPermitDTO> {
  return client.request<EAMPermitDTO>('GET', `/api/v1/eam-permits/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamPermitsIssue(
  client: ApiClient,
  params: { id: string },
  body: IssueEAMPermitRequest,
  idempotencyKey?: string,
): Promise<EAMPermitDTO> {
  return client.request<EAMPermitDTO>('POST', `/api/v1/eam-permits/${encodeURIComponent(params.id)}/issue`, {
    body,
    idempotencyKey,
  });
}

export async function eamPermitsAcceptReturn(
  client: ApiClient,
  params: { id: string },
  body: AcceptEAMPermitReturnRequest,
  idempotencyKey?: string,
): Promise<EAMPermitDTO> {
  return client.request<EAMPermitDTO>('POST', `/api/v1/eam-permits/${encodeURIComponent(params.id)}/accept-return`, {
    body,
    idempotencyKey,
  });
}

export async function eamPermitsCancel(
  client: ApiClient,
  params: { id: string },
  body: CancelEAMPermitRequest,
  idempotencyKey?: string,
): Promise<EAMPermitDTO> {
  return client.request<EAMPermitDTO>('POST', `/api/v1/eam-permits/${encodeURIComponent(params.id)}/cancel`, {
    body,
    idempotencyKey,
  });
}

export async function eamDowntimeList(
  client: ApiClient,
  query: ListEAMDowntimeQuery,
): Promise<PageEAMDowntimeDTO> {
  return client.request<PageEAMDowntimeDTO>('GET', `/api/v1/eam-downtime`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamDowntimeOpen(
  client: ApiClient,
  body: CreateEAMDowntimeRequest,
  idempotencyKey?: string,
): Promise<EAMDowntimeDTO> {
  return client.request<EAMDowntimeDTO>('POST', `/api/v1/eam-downtime`, {
    body,
    idempotencyKey,
  });
}

export async function eamDowntimeGet(
  client: ApiClient,
  params: { id: string },
): Promise<EAMDowntimeDTO> {
  return client.request<EAMDowntimeDTO>('GET', `/api/v1/eam-downtime/${encodeURIComponent(params.id)}`, {
  });
}

export async function eamDowntimeRelease(
  client: ApiClient,
  params: { id: string },
  body: ReleaseEAMDowntimeRequest,
  idempotencyKey?: string,
): Promise<EAMDowntimeDTO> {
  return client.request<EAMDowntimeDTO>('POST', `/api/v1/eam-downtime/${encodeURIComponent(params.id)}/release`, {
    body,
    idempotencyKey,
  });
}

export async function eamAvailabilityGet(
  client: ApiClient,
  query: EAMAvailabilityQuery,
): Promise<EAMAvailabilityDTO[]> {
  return client.request<EAMAvailabilityDTO[]>('GET', `/api/v1/eam-availability`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamAvailabilityEventsList(
  client: ApiClient,
  query: ListEAMAvailabilityEventsQuery,
): Promise<PageEAMAvailabilityEventDTO> {
  return client.request<PageEAMAvailabilityEventDTO>('GET', `/api/v1/eam-availability-events`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function eamReportsReliability(
  client: ApiClient,
  query: ListEAMReliabilityQuery,
): Promise<PageEAMReliabilityDTO> {
  return client.request<PageEAMReliabilityDTO>('GET', `/api/v1/eam-reports/reliability`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function apInvoicesRequestPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function apInvoicesPdfStatus(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/ap-invoices/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function apPaymentRunsRequestRemittance(
  client: ApiClient,
  params: { id: string; paymentId: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/payment-runs/${encodeURIComponent(params.id)}/payments/${encodeURIComponent(params.paymentId)}/remittance`, {
  });
}

export async function apPaymentRunsRemittanceStatus(
  client: ApiClient,
  params: { id: string; paymentId: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/payment-runs/${encodeURIComponent(params.id)}/payments/${encodeURIComponent(params.paymentId)}/remittance`, {
  });
}

export async function arInvoicesRequestPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function arInvoicesPdfStatus(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function arInvoicesDeliver(
  client: ApiClient,
  params: { id: string },
  body: ArInvoiceDeliverRequest,
  idempotencyKey?: string,
): Promise<ArInvoiceDTO> {
  return client.request<ArInvoiceDTO>('POST', `/api/v1/ar-invoices/${encodeURIComponent(params.id)}/deliver`, {
    body,
    idempotencyKey,
  });
}

export async function arCreditNotesRequestPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function arCreditNotesPdfStatus(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/ar-credit-notes/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function arCustomersRequestStatementPdf(
  client: ApiClient,
  params: { id: string },
  query: StatementQuery,
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/customers/${encodeURIComponent(params.id)}/statement/pdf`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function arCustomersStatementPdfStatus(
  client: ApiClient,
  params: { id: string },
  query: StatementQuery,
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/customers/${encodeURIComponent(params.id)}/statement/pdf`, {
    query: query as unknown as Record<string, string | number | boolean | undefined>,
  });
}

export async function payrollPayslipsRequestPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/payroll-payslips/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function payrollPayslipsPdfStatus(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/payroll-payslips/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function payrollMePayslipsRequestPdf(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('POST', `/api/v1/me/payslips/${encodeURIComponent(params.id)}/pdf`, {
  });
}

export async function payrollMePayslipsPdfStatus(
  client: ApiClient,
  params: { id: string },
): Promise<RenderStatusDTO> {
  return client.request<RenderStatusDTO>('GET', `/api/v1/me/payslips/${encodeURIComponent(params.id)}/pdf`, {
  });
}


